/**
 * @license
 * Lodash <https://lodash.com/>
 * Copyright OpenJS Foundation and other contributors <https://openjsf.org/>
 * Released under MIT license <https://lodash.com/license>
 * Based on Underscore.js 1.8.3 <http://underscorejs.org/LICENSE>
 * Copyright Jeremy Ashkenas, DocumentCloud and Investigative Reporters & Editors
 */
;(function() {

  /** Used as a safe reference for `undefined` in pre-ES5 environments. */
  var undefined;

  /** Used as the semantic version number. */
  var VERSION = '4.17.21';

  /** Used as the size to enable large array optimizations. */
  var LARGE_ARRAY_SIZE = 200;

  /** Error message constants. */
  var CORE_ERROR_TEXT = 'Unsupported core-js use. Try https://npms.io/search?q=ponyfill.',
      FUNC_ERROR_TEXT = 'Expected a function',
      INVALID_TEMPL_VAR_ERROR_TEXT = 'Invalid `variable` option passed into `_.template`';

  /** Used to stand-in for `undefined` hash values. */
  var HASH_UNDEFINED = '__lodash_hash_undefined__';

  /** Used as the maximum memoize cache size. */
  var MAX_MEMOIZE_SIZE = 500;

  /** Used as the internal argument placeholder. */
  var PLACEHOLDER = '__lodash_placeholder__';

  /** Used to compose bitmasks for cloning. */
  var CLONE_DEEP_FLAG = 1,
      CLONE_FLAT_FLAG = 2,
      CLONE_SYMBOLS_FLAG = 4;

  /** Used to compose bitmasks for value comparisons. */
  var COMPARE_PARTIAL_FLAG = 1,
      COMPARE_UNORDERED_FLAG = 2;

  /** Used to compose bitmasks for function metadata. */
  var WRAP_BIND_FLAG = 1,
      WRAP_BIND_KEY_FLAG = 2,
      WRAP_CURRY_BOUND_FLAG = 4,
      WRAP_CURRY_FLAG = 8,
      WRAP_CURRY_RIGHT_FLAG = 16,
      WRAP_PARTIAL_FLAG = 32,
      WRAP_PARTIAL_RIGHT_FLAG = 64,
      WRAP_ARY_FLAG = 128,
      WRAP_REARG_FLAG = 256,
      WRAP_FLIP_FLAG = 512;

  /** Used as default options for `_.truncate`. */
  var DEFAULT_TRUNC_LENGTH = 30,
      DEFAULT_TRUNC_OMISSION = '...';

  /** Used to detect hot functions by number of calls within a span of milliseconds. */
  var HOT_COUNT = 800,
      HOT_SPAN = 16;

  /** Used to indicate the type of lazy iteratees. */
  var LAZY_FILTER_FLAG = 1,
      LAZY_MAP_FLAG = 2,
      LAZY_WHILE_FLAG = 3;

  /** Used as references for various `Number` constants. */
  var INFINITY = 1 / 0,
      MAX_SAFE_INTEGER = 9007199254740991,
      MAX_INTEGER = 1.7976931348623157e+308,
      NAN = 0 / 0;

  /** Used as references for the maximum length and index of an array. */
  var MAX_ARRAY_LENGTH = 4294967295,
      MAX_ARRAY_INDEX = MAX_ARRAY_LENGTH - 1,
      HALF_MAX_ARRAY_LENGTH = MAX_ARRAY_LENGTH >>> 1;

  /** Used to associate wrap methods with their bit flags. */
  var wrapFlags = [
    ['ary', WRAP_ARY_FLAG],
    ['bind', WRAP_BIND_FLAG],
    ['bindKey', WRAP_BIND_KEY_FLAG],
    ['curry', WRAP_CURRY_FLAG],
    ['curryRight', WRAP_CURRY_RIGHT_FLAG],
    ['flip', WRAP_FLIP_FLAG],
    ['partial', WRAP_PARTIAL_FLAG],
    ['partialRight', WRAP_PARTIAL_RIGHT_FLAG],
    ['rearg', WRAP_REARG_FLAG]
  ];

  /** `Object#toString` result references. */
  var argsTag = '[object Arguments]',
      arrayTag = '[object Array]',
      asyncTag = '[object AsyncFunction]',
      boolTag = '[object Boolean]',
      dateTag = '[object Date]',
      domExcTag = '[object DOMException]',
      errorTag = '[object Error]',
      funcTag = '[object Function]',
      genTag = '[object GeneratorFunction]',
      mapTag = '[object Map]',
      numberTag = '[object Number]',
      nullTag = '[object Null]',
      objectTag = '[object Object]',
      promiseTag = '[object Promise]',
      proxyTag = '[object Proxy]',
      regexpTag = '[object RegExp]',
      setTag = '[object Set]',
      stringTag = '[object String]',
      symbolTag = '[object Symbol]',
      undefinedTag = '[object Undefined]',
      weakMapTag = '[object WeakMap]',
      weakSetTag = '[object WeakSet]';

  var arrayBufferTag = '[object ArrayBuffer]',
      dataViewTag = '[object DataView]',
      float32Tag = '[object Float32Array]',
      float64Tag = '[object Float64Array]',
      int8Tag = '[object Int8Array]',
      int16Tag = '[object Int16Array]',
      int32Tag = '[object Int32Array]',
      uint8Tag = '[object Uint8Array]',
      uint8ClampedTag = '[object Uint8ClampedArray]',
      uint16Tag = '[object Uint16Array]',
      uint32Tag = '[object Uint32Array]';

  /** Used to match empty string literals in compiled template source. */
  var reEmptyStringLeading = /\b__p \+= '';/g,
      reEmptyStringMiddle = /\b(__p \+=) '' \+/g,
      reEmptyStringTrailing = /(__e\(.*?\)|\b__t\)) \+\n'';/g;

  /** Used to match HTML entities and HTML characters. */
  var reEscapedHtml = /&(?:amp|lt|gt|quot|#39);/g,
      reUnescapedHtml = /[&<>"']/g,
      reHasEscapedHtml = RegExp(reEscapedHtml.source),
      reHasUnescapedHtml = RegExp(reUnescapedHtml.source);

  /** Used to match template delimiters. */
  var reEscape = /<%-([\s\S]+?)%>/g,
      reEvaluate = /<%([\s\S]+?)%>/g,
      reInterpolate = /<%=([\s\S]+?)%>/g;

  /** Used to match property names within property paths. */
  var reIsDeepProp = /\.|\[(?:[^[\]]*|(["'])(?:(?!\1)[^\\]|\\.)*?\1)\]/,
      reIsPlainProp = /^\w*$/,
      rePropName = /[^.[\]]+|\[(?:(-?\d+(?:\.\d+)?)|(["'])((?:(?!\2)[^\\]|\\.)*?)\2)\]|(?=(?:\.|\[\])(?:\.|\[\]|$))/g;

  /**
   * Used to match `RegExp`
   * [syntax characters](http://ecma-international.org/ecma-262/7.0/#sec-patterns).
   */
  var reRegExpChar = /[\\^$.*+?()[\]{}|]/g,
      reHasRegExpChar = RegExp(reRegExpChar.source);

  /** Used to match leading whitespace. */
  var reTrimStart = /^\s+/;

  /** Used to match a single whitespace character. */
  var reWhitespace = /\s/;

  /** Used to match wrap detail comments. */
  var reWrapComment = /\{(?:\n\/\* \[wrapped with .+\] \*\/)?\n?/,
      reWrapDetails = /\{\n\/\* \[wrapped with (.+)\] \*/,
      reSplitDetails = /,? & /;

  /** Used to match words composed of alphanumeric characters. */
  var reAsciiWord = /[^\x00-\x2f\x3a-\x40\x5b-\x60\x7b-\x7f]+/g;

  /**
   * Used to validate the `validate` option in `_.template` variable.
   *
   * Forbids characters which could potentially change the meaning of the function argument definition:
   * - "()," (modification of function parameters)
   * - "=" (default value)
   * - "[]{}" (destructuring of function parameters)
   * - "/" (beginning of a comment)
   * - whitespace
   */
  var reForbiddenIdentifierChars = /[()=,{}\[\]\/\s]/;

  /** Used to match backslashes in property paths. */
  var reEscapeChar = /\\(\\)?/g;

  /**
   * Used to match
   * [ES template delimiters](http://ecma-international.org/ecma-262/7.0/#sec-template-literal-lexical-components).
   */
  var reEsTemplate = /\$\{([^\\}]*(?:\\.[^\\}]*)*)\}/g;

  /** Used to match `RegExp` flags from their coerced string values. */
  var reFlags = /\w*$/;

  /** Used to detect bad signed hexadecimal string values. */
  var reIsBadHex = /^[-+]0x[0-9a-f]+$/i;

  /** Used to detect binary string values. */
  var reIsBinary = /^0b[01]+$/i;

  /** Used to detect host constructors (Safari). */
  var reIsHostCtor = /^\[object .+?Constructor\]$/;

  /** Used to detect octal string values. */
  var reIsOctal = /^0o[0-7]+$/i;

  /** Used to detect unsigned integer values. */
  var reIsUint = /^(?:0|[1-9]\d*)$/;

  /** Used to match Latin Unicode letters (excluding mathematical operators). */
  var reLatin = /[\xc0-\xd6\xd8-\xf6\xf8-\xff\u0100-\u017f]/g;

  /** Used to ensure capturing order of template delimiters. */
  var reNoMatch = /($^)/;

  /** Used to match unescaped characters in compiled string literals. */
  var reUnescapedString = /['\n\r\u2028\u2029\\]/g;

  /** Used to compose unicode character classes. */
  var rsAstralRange = '\\ud800-\\udfff',
      rsComboMarksRange = '\\u0300-\\u036f',
      reComboHalfMarksRange = '\\ufe20-\\ufe2f',
      rsComboSymbolsRange = '\\u20d0-\\u20ff',
      rsComboRange = rsComboMarksRange + reComboHalfMarksRange + rsComboSymbolsRange,
      rsDingbatRange = '\\u2700-\\u27bf',
      rsLowerRange = 'a-z\\xdf-\\xf6\\xf8-\\xff',
      rsMathOpRange = '\\xac\\xb1\\xd7\\xf7',
      rsNonCharRange = '\\x00-\\x2f\\x3a-\\x40\\x5b-\\x60\\x7b-\\xbf',
      rsPunctuationRange = '\\u2000-\\u206f',
      rsSpaceRange = ' \\t\\x0b\\f\\xa0\\ufeff\\n\\r\\u2028\\u2029\\u1680\\u180e\\u2000\\u2001\\u2002\\u2003\\u2004\\u2005\\u2006\\u2007\\u2008\\u2009\\u200a\\u202f\\u205f\\u3000',
      rsUpperRange = 'A-Z\\xc0-\\xd6\\xd8-\\xde',
      rsVarRange = '\\ufe0e\\ufe0f',
      rsBreakRange = rsMathOpRange + rsNonCharRange + rsPunctuationRange + rsSpaceRange;

  /** Used to compose unicode capture groups. */
  var rsApos = "['\u2019]",
      rsAstral = '[' + rsAstralRange + ']',
      rsBreak = '[' + rsBreakRange + ']',
      rsCombo = '[' + rsComboRange + ']',
      rsDigits = '\\d+',
      rsDingbat = '[' + rsDingbatRange + ']',
      rsLower = '[' + rsLowerRange + ']',
      rsMisc = '[^' + rsAstralRange + rsBreakRange + rsDigits + rsDingbatRange + rsLowerRange + rsUpperRange + ']',
      rsFitz = '\\ud83c[\\udffb-\\udfff]',
      rsModifier = '(?:' + rsCombo + '|' + rsFitz + ')',
      rsNonAstral = '[^' + rsAstralRange + ']',
      rsRegional = '(?:\\ud83c[\\udde6-\\uddff]){2}',
      rsSurrPair = '[\\ud800-\\udbff][\\udc00-\\udfff]',
      rsUpper = '[' + rsUpperRange + ']',
      rsZWJ = '\\u200d';

  /** Used to compose unicode regexes. */
  var rsMiscLower = '(?:' + rsLower + '|' + rsMisc + ')',
      rsMiscUpper = '(?:' + rsUpper + '|' + rsMisc + ')',
      rsOptContrLower = '(?:' + rsApos + '(?:d|ll|m|re|s|t|ve))?',
      rsOptContrUpper = '(?:' + rsApos + '(?:D|LL|M|RE|S|T|VE))?',
      reOptMod = rsModifier + '?',
      rsOptVar = '[' + rsVarRange + ']?',
      rsOptJoin = '(?:' + rsZWJ + '(?:' + [rsNonAstral, rsRegional, rsSurrPair].join('|') + ')' + rsOptVar + reOptMod + ')*',
      rsOrdLower = '\\d*(?:1st|2nd|3rd|(?![123])\\dth)(?=\\b|[A-Z_])',
      rsOrdUpper = '\\d*(?:1ST|2ND|3RD|(?![123])\\dTH)(?=\\b|[a-z_])',
      rsSeq = rsOptVar + reOptMod + rsOptJoin,
      rsEmoji = '(?:' + [rsDingbat, rsRegional, rsSurrPair].join('|') + ')' + rsSeq,
      rsSymbol = '(?:' + [rsNonAstral + rsCombo + '?', rsCombo, rsRegional, rsSurrPair, rsAstral].join('|') + ')';

  /** Used to match apostrophes. */
  var reApos = RegExp(rsApos, 'g');

  /**
   * Used to match [combining diacritical marks](https://en.wikipedia.org/wiki/Combining_Diacritical_Marks) and
   * [combining diacritical marks for symbols](https://en.wikipedia.org/wiki/Combining_Diacritical_Marks_for_Symbols).
   */
  var reComboMark = RegExp(rsCombo, 'g');

  /** Used to match [string symbols](https://mathiasbynens.be/notes/javascript-unicode). */
  var reUnicode = RegExp(rsFitz + '(?=' + rsFitz + ')|' + rsSymbol + rsSeq, 'g');

  /** Used to match complex or compound words. */
  var reUnicodeWord = RegExp([
    rsUpper + '?' + rsLower + '+' + rsOptContrLower + '(?=' + [rsBreak, rsUpper, '$'].join('|') + ')',
    rsMiscUpper + '+' + rsOptContrUpper + '(?=' + [rsBreak, rsUpper + rsMiscLower, '$'].join('|') + ')',
    rsUpper + '?' + rsMiscLower + '+' + rsOptContrLower,
    rsUpper + '+' + rsOptContrUpper,
    rsOrdUpper,
    rsOrdLower,
    rsDigits,
    rsEmoji
  ].join('|'), 'g');

  /** Used to detect strings with [zero-width joiners or code points from the astral planes](http://eev.ee/blog/2015/09/12/dark-corners-of-unicode/). */
  var reHasUnicode = RegExp('[' + rsZWJ + rsAstralRange  + rsComboRange + rsVarRange + ']');

  /** Used to detect strings that need a more robust regexp to match words. */
  var reHasUnicodeWord = /[a-z][A-Z]|[A-Z]{2}[a-z]|[0-9][a-zA-Z]|[a-zA-Z][0-9]|[^a-zA-Z0-9 ]/;

  /** Used to assign default `context` object properties. */
  var contextProps = [
    'Array', 'Buffer', 'DataView', 'Date', 'Error', 'Float32Array', 'Float64Array',
    'Function', 'Int8Array', 'Int16Array', 'Int32Array', 'Map', 'Math', 'Object',
    'Promise', 'RegExp', 'Set', 'String', 'Symbol', 'TypeError', 'Uint8Array',
    'Uint8ClampedArray', 'Uint16Array', 'Uint32Array', 'WeakMap',
    '_', 'clearTimeout', 'isFinite', 'parseInt', 'setTimeout'
  ];

  /** Used to make template sourceURLs easier to identify. */
  var templateCounter = -1;

  /** Used to identify `toStringTag` values of typed arrays. */
  var typedArrayTags = {};
  typedArrayTags[float32Tag] = typedArrayTags[float64Tag] =
  typedArrayTags[int8Tag] = typedArrayTags[int16Tag] =
  typedArrayTags[int32Tag] = typedArrayTags[uint8Tag] =
  typedArrayTags[uint8ClampedTag] = typedArrayTags[uint16Tag] =
  typedArrayTags[uint32Tag] = true;
  typedArrayTags[argsTag] = typedArrayTags[arrayTag] =
  typedArrayTags[arrayBufferTag] = typedArrayTags[boolTag] =
  typedArrayTags[dataViewTag] = typedArrayTags[dateTag] =
  typedArrayTags[errorTag] = typedArrayTags[funcTag] =
  typedArrayTags[mapTag] = typedArrayTags[numberTag] =
  typedArrayTags[objectTag] = typedArrayTags[regexpTag] =
  typedArrayTags[setTag] = typedArrayTags[stringTag] =
  typedArrayTags[weakMapTag] = false;

  /** Used to identify `toStringTag` values supported by `_.clone`. */
  var cloneableTags = {};
  cloneableTags[argsTag] = cloneableTags[arrayTag] =
  cloneableTags[arrayBufferTag] = cloneableTags[dataViewTag] =
  cloneableTags[boolTag] = cloneableTags[dateTag] =
  cloneableTags[float32Tag] = cloneableTags[float64Tag] =
  cloneableTags[int8Tag] = cloneableTags[int16Tag] =
  cloneableTags[int32Tag] = cloneableTags[mapTag] =
  cloneableTags[numberTag] = cloneableTags[objectTag] =
  cloneableTags[regexpTag] = cloneableTags[setTag] =
  cloneableTags[stringTag] = cloneableTags[symbolTag] =
  cloneableTags[uint8Tag] = cloneableTags[uint8ClampedTag] =
  cloneableTags[uint16Tag] = cloneableTags[uint32Tag] = true;
  cloneableTags[errorTag] = cloneableTags[funcTag] =
  cloneableTags[weakMapTag] = false;

  /** Used to map Latin Unicode letters to basic Latin letters. */
  var deburredLetters = {
    // Latin-1 Supplement block.
    '\xc0': 'A',  '\xc1': 'A', '\xc2': 'A', '\xc3': 'A', '\xc4': 'A', '\xc5': 'A',
    '\xe0': 'a',  '\xe1': 'a', '\xe2': 'a', '\xe3': 'a', '\xe4': 'a', '\xe5': 'a',
    '\xc7': 'C',  '\xe7': 'c',
    '\xd0': 'D',  '\xf0': 'd',
    '\xc8': 'E',  '\xc9': 'E', '\xca': 'E', '\xcb': 'E',
    '\xe8': 'e',  '\xe9': 'e', '\xea': 'e', '\xeb': 'e',
    '\xcc': 'I',  '\xcd': 'I', '\xce': 'I', '\xcf': 'I',
    '\xec': 'i',  '\xed': 'i', '\xee': 'i', '\xef': 'i',
    '\xd1': 'N',  '\xf1': 'n',
    '\xd2': 'O',  '\xd3': 'O', '\xd4': 'O', '\xd5': 'O', '\xd6': 'O', '\xd8': 'O',
    '\xf2': 'o',  '\xf3': 'o', '\xf4': 'o', '\xf5': 'o', '\xf6': 'o', '\xf8': 'o',
    '\xd9': 'U',  '\xda': 'U', '\xdb': 'U', '\xdc': 'U',
    '\xf9': 'u',  '\xfa': 'u', '\xfb': 'u', '\xfc': 'u',
    '\xdd': 'Y',  '\xfd': 'y', '\xff': 'y',
    '\xc6': 'Ae', '\xe6': 'ae',
    '\xde': 'Th', '\xfe': 'th',
    '\xdf': 'ss',
    // Latin Extended-A block.
    '\u0100': 'A',  '\u0102': 'A', '\u0104': 'A',
    '\u0101': 'a',  '\u0103': 'a', '\u0105': 'a',
    '\u0106': 'C',  '\u0108': 'C', '\u010a': 'C', '\u010c': 'C',
    '\u0107': 'c',  '\u0109': 'c', '\u010b': 'c', '\u010d': 'c',
    '\u010e': 'D',  '\u0110': 'D', '\u010f': 'd', '\u0111': 'd',
    '\u0112': 'E',  '\u0114': 'E', '\u0116': 'E', '\u0118': 'E', '\u011a': 'E',
    '\u0113': 'e',  '\u0115': 'e', '\u0117': 'e', '\u0119': 'e', '\u011b': 'e',
    '\u011c': 'G',  '\u011e': 'G', '\u0120': 'G', '\u0122': 'G',
    '\u011d': 'g',  '\u011f': 'g', '\u0121': 'g', '\u0123': 'g',
    '\u0124': 'H',  '\u0126': 'H', '\u0125': 'h', '\u0127': 'h',
    '\u0128': 'I',  '\u012a': 'I', '\u012c': 'I', '\u012e': 'I', '\u0130': 'I',
    '\u0129': 'i',  '\u012b': 'i', '\u012d': 'i', '\u012f': 'i', '\u0131': 'i',
    '\u0134': 'J',  '\u0135': 'j',
    '\u0136': 'K',  '\u0137': 'k', '\u0138': 'k',
    '\u0139': 'L',  '\u013b': 'L', '\u013d': 'L', '\u013f': 'L', '\u0141': 'L',
    '\u013a': 'l',  '\u013c': 'l', '\u013e': 'l', '\u0140': 'l', '\u0142': 'l',
    '\u0143': 'N',  '\u0145': 'N', '\u0147': 'N', '\u014a': 'N',
    '\u0144': 'n',  '\u0146': 'n', '\u0148': 'n', '\u014b': 'n',
    '\u014c': 'O',  '\u014e': 'O', '\u0150': 'O',
    '\u014d': 'o',  '\u014f': 'o', '\u0151': 'o',
    '\u0154': 'R',  '\u0156': 'R', '\u0158': 'R',
    '\u0155': 'r',  '\u0157': 'r', '\u0159': 'r',
    '\u015a': 'S',  '\u015c': 'S', '\u015e': 'S', '\u0160': 'S',
    '\u015b': 's',  '\u015d': 's', '\u015f': 's', '\u0161': 's',
    '\u0162': 'T',  '\u0164': 'T', '\u0166': 'T',
    '\u0163': 't',  '\u0165': 't', '\u0167': 't',
    '\u0168': 'U',  '\u016a': 'U', '\u016c': 'U', '\u016e': 'U', '\u0170': 'U', '\u0172': 'U',
    '\u0169': 'u',  '\u016b': 'u', '\u016d': 'u', '\u016f': 'u', '\u0171': 'u', '\u0173': 'u',
    '\u0174': 'W',  '\u0175': 'w',
    '\u0176': 'Y',  '\u0177': 'y', '\u0178': 'Y',
    '\u0179': 'Z',  '\u017b': 'Z', '\u017d': 'Z',
    '\u017a': 'z',  '\u017c': 'z', '\u017e': 'z',
    '\u0132': 'IJ', '\u0133': 'ij',
    '\u0152': 'Oe', '\u0153': 'oe',
    '\u0149': "'n", '\u017f': 's'
  };

  /** Used to map characters to HTML entities. */
  var htmlEscapes = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;'
  };

  /** Used to map HTML entities to characters. */
  var htmlUnescapes = {
    '&amp;': '&',
    '&lt;': '<',
    '&gt;': '>',
    '&quot;': '"',
    '&#39;': "'"
  };

  /** Used to escape characters for inclusion in compiled string literals. */
  var stringEscapes = {
    '\\': '\\',
    "'": "'",
    '\n': 'n',
    '\r': 'r',
    '\u2028': 'u2028',
    '\u2029': 'u2029'
  };

  /** Built-in method references without a dependency on `root`. */
  var freeParseFloat = parseFloat,
      freeParseInt = parseInt;

  /** Detect free variable `global` from Node.js. */
  var freeGlobal = typeof global == 'object' && global && global.Object === Object && global;

  /** Detect free variable `self`. */
  var freeSelf = typeof self == 'object' && self && self.Object === Object && self;

  /** Used as a reference to the global object. */
  var root = freeGlobal || freeSelf || Function('return this')();

  /** Detect free variable `exports`. */
  var freeExports = typeof exports == 'object' && exports && !exports.nodeType && exports;

  /** Detect free variable `module`. */
  var freeModule = freeExports && typeof module == 'object' && module && !module.nodeType && module;

  /** Detect the popular CommonJS extension `module.exports`. */
  var moduleExports = freeModule && freeModule.exports === freeExports;

  /** Detect free variable `process` from Node.js. */
  var freeProcess = moduleExports && freeGlobal.process;

  /** Used to access faster Node.js helpers. */
  var nodeUtil = (function() {
    try {
      // Use `util.types` for Node.js 10+.
      var types = freeModule && freeModule.require && freeModule.require('util').types;

      if (types) {
        return types;
      }

      // Legacy `process.binding('util')` for Node.js < 10.
      return freeProcess && freeProcess.binding && freeProcess.binding('util');
    } catch (e) {}
  }());

  /* Node.js helper references. */
  var nodeIsArrayBuffer = nodeUtil && nodeUtil.isArrayBuffer,
      nodeIsDate = nodeUtil && nodeUtil.isDate,
      nodeIsMap = nodeUtil && nodeUtil.isMap,
      nodeIsRegExp = nodeUtil && nodeUtil.isRegExp,
      nodeIsSet = nodeUtil && nodeUtil.isSet,
      nodeIsTypedArray = nodeUtil && nodeUtil.isTypedArray;

  /*--------------------------------------------------------------------------*/

  /**
   * A faster alternative to `Function#apply`, this function invokes `func`
   * with the `this` binding of `thisArg` and the arguments of `args`.
   *
   * @private
   * @param {Function} func The function to invoke.
   * @param {*} thisArg The `this` binding of `func`.
   * @param {Array} args The arguments to invoke `func` with.
   * @returns {*} Returns the result of `func`.
   */
  function apply(func, thisArg, args) {
    switch (args.length) {
      case 0: return func.call(thisArg);
      case 1: return func.call(thisArg, args[0]);
      case 2: return func.call(thisArg, args[0], args[1]);
      case 3: return func.call(thisArg, args[0], args[1], args[2]);
    }
    return func.apply(thisArg, args);
  }

  /**
   * A specialized version of `baseAggregator` for arrays.
   *
   * @private
   * @param {Array} [array] The array to iterate over.
   * @param {Function} setter The function to set `accumulator` values.
   * @param {Function} iteratee The iteratee to transform keys.
   * @param {Object} accumulator The initial aggregated object.
   * @returns {Function} Returns `accumulator`.
   */
  function arrayAggregator(array, setter, iteratee, accumulator) {
    var index = -1,
        length = array == null ? 0 : array.length;

    while (++index < length) {
      var value = array[index];
      setter(accumulator, value, iteratee(value), array);
    }
    return accumulator;
  }

  /**
   * A specialized version of `_.forEach` for arrays without support for
   * iteratee shorthands.
   *
   * @private
   * @param {Array} [array] The array to iterate over.
   * @param {Function} iteratee The function invoked per iteration.
   * @returns {Array} Returns `array`.
   */
  function arrayEach(array, iteratee) {
    var index = -1,
        length = array == null ? 0 : array.length;

    while (++index < length) {
      if (iteratee(array[index], index, array) === false) {
        break;
      }
    }
    return array;
  }

  /**
   * A specialized version of `_.forEachRight` for arrays without support for
   * iteratee shorthands.
   *
   * @private
   * @param {Array} [array] The array to iterate over.
   * @param {Function} iteratee The function invoked per iteration.
   * @returns {Array} Returns `array`.
   */
  function arrayEachRight(array, iteratee) {
    var length = array == null ? 0 : array.length;

    while (length--) {
      if (iteratee(array[length], length, array) === false) {
        break;
      }
    }
    return array;
  }

  /**
   * A specialized version of `_.every` for arrays without support for
   * iteratee shorthands.
   *
   * @private
   * @param {Array} [array] The array to iterate over.
   * @param {Function} predicate The function invoked per iteration.
   * @returns {boolean} Returns `true` if all elements pass the predicate check,
   *  else `false`.
   */
  function arrayEvery(array, predicate) {
    var index = -1,
        length = array == null ? 0 : array.length;

    while (++index < length) {
      if (!predicate(array[index], index, array)) {
        return false;
      }
    }
    return true;
  }

  /**
   * A specialized version of `_.filter` for arrays without support for
   * iteratee shorthands.
   *
   * @private
   * @param {Array} [array] The array to iterate over.
   * @param {Function} predicate The function invoked per iteration.
   * @returns {Array} Returns the new filtered array.
   */
  function arrayFilter(array, predicate) {
    var index = -1,
        length = array == null ? 0 : array.length,
        resIndex = 0,
        result = [];

    while (++index < length) {
      var value = array[index];
      if (predicate(value, index, array)) {
        result[resIndex++] = value;
      }
    }
    return result;
  }

  /**
   * A specialized version of `_.includes` for arrays without support for
   * specifying an index to search from.
   *
   * @private
   * @param {Array} [array] The array to inspect.
   * @param {*} target The value to search for.
   * @returns {boolean} Returns `true` if `target` is found, else `false`.
   */
  function arrayIncludes(array, value) {
    var length = array == null ? 0 : array.length;
    return !!length && baseIndexOf(array, value, 0) > -1;
  }

  /**
   * This function is like `arrayIncludes` except that it accepts a comparator.
   *
   * @private
   * @param {Array} [array] The array to inspect.
   * @param {*} target The value to search for.
   * @param {Function} comparator The comparator invoked per element.
   * @returns {boolean} Returns `true` if `target` is found, else `false`.
   */
  function arrayIncludesWith(array, value, comparator) {
    var index = -1,
        length = array == null ? 0 : array.length;

    while (++index < length) {
      if (comparator(value, array[index])) {
        return true;
      }
    }
    return false;
  }

  /**
   * A specialized version of `_.map` for arrays without support for iteratee
   * shorthands.
   *
   * @private
   * @param {Array} [array] The array to iterate over.
   * @param {Function} iteratee The function invoked per iteration.
   * @returns {Array} Returns the new mapped array.
   */
  function arrayMap(array, iteratee) {
    var index = -1,
        length = array == null ? 0 : array.length,
        result = Array(length);

    while (++index < length) {
      result[index] = iteratee(array[index], index, array);
    }
    return result;
  }

  /**
   * Appends the elements of `values` to `array`.
   *
   * @private
   * @param {Array} array The array to modify.
   * @param {Array} values The values to append.
   * @returns {Array} Returns `array`.
   */
  function arrayPush(array, values) {
    var index = -1,
        length = values.length,
        offset = array.length;

    while (++index < length) {
      array[offset + index] = values[index];
    }
    return array;
  }

  /**
   * A specialized version of `_.reduce` for arrays without support for
   * iteratee shorthands.
   *
   * @private
   * @param {Array} [array] The array to iterate over.
   * @param {Function} iteratee The function invoked per iteration.
   * @param {*} [accumulator] The initial value.
   * @param {boolean} [initAccum] Specify using the first element of `array` as
   *  the initial value.
   * @returns {*} Returns the accumulated value.
   */
  function arrayReduce(array, iteratee, accumulator, initAccum) {
    var index = -1,
        length = array == null ? 0 : array.length;

    if (initAccum && length) {
      accumulator = array[++index];
    }
    while (++index < length) {
      accumulator = iteratee(accumulator, array[index], index, array);
    }
    return accumulator;
  }

  /**
   * A specialized version of `_.reduceRight` for arrays without support for
   * iteratee shorthands.
   *
   * @private
   * @param {Array} [array] The array to iterate over.
   * @param {Function} iteratee The function invoked per iteration.
   * @param {*} [accumulator] The initial value.
   * @param {boolean} [initAccum] Specify using the last element of `array` as
   *  the initial value.
   * @returns {*} Returns the accumulated value.
   */
  function arrayReduceRight(array, iteratee, accumulator, initAccum) {
    var length = array == null ? 0 : array.length;
    if (initAccum && length) {
      accumulator = array[--length];
    }
    while (length--) {
      accumulator = iteratee(accumulator, array[length], length, array);
    }
    return accumulator;
  }

  /**
   * A specialized version of `_.some` for arrays without support for iteratee
   * shorthands.
   *
   * @private
   * @param {Array} [array] The array to iterate over.
   * @param {Function} predicate The function invoked per iteration.
   * @returns {boolean} Returns `true` if any element passes the predicate check,
   *  else `false`.
   */
  function arraySome(array, predicate) {
    var index = -1,
        length = array == null ? 0 : array.length;

    while (++index < length) {
      if (predicate(array[index], index, array)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Gets the size of an ASCII `string`.
   *
   * @private
   * @param {string} string The string inspect.
   * @returns {number} Returns the string size.
   */
  var asciiSize = baseProperty('length');

  /**
   * Converts an ASCII `string` to an array.
   *
   * @private
   * @param {string} string The string to convert.
   * @returns {Array} Returns the converted array.
   */
  function asciiToArray(string) {
    return string.split('');
  }

  /**
   * Splits an ASCII `string` into an array of its words.
   *
   * @private
   * @param {string} The string to inspect.
   * @returns {Array} Returns the words of `string`.
   */
  function asciiWords(string) {
    return string.match(reAsciiWord) || [];
  }

  /**
   * The base implementation of methods like `_.findKey` and `_.findLastKey`,
   * without support for iteratee shorthands, which iterates over `collection`
   * using `eachFunc`.
   *
   * @private
   * @param {Array|Object} collection The collection to inspect.
   * @param {Function} predicate The function invoked per iteration.
   * @param {Function} eachFunc The function to iterate over `collection`.
   * @returns {*} Returns the found element or its key, else `undefined`.
   */
  function baseFindKey(collection, predicate, eachFunc) {
    var result;
    eachFunc(collection, function(value, key, collection) {
      if (predicate(value, key, collection)) {
        result = key;
        return false;
      }
    });
    return result;
  }

  /**
   * The base implementation of `_.findIndex` and `_.findLastIndex` without
   * support for iteratee shorthands.
   *
   * @private
   * @param {Array} array The array to inspect.
   * @param {Function} predicate The function invoked per iteration.
   * @param {number} fromIndex The index to search from.
   * @param {boolean} [fromRight] Specify iterating from right to left.
   * @returns {number} Returns the index of the matched value, else `-1`.
   */
  function baseFindIndex(array, predicate, fromIndex, fromRight) {
    var length = array.length,
        index = fromIndex + (fromRight ? 1 : -1);

    while ((fromRight ? index-- : ++index < length)) {
      if (predicate(array[index], index, array)) {
        return index;
      }
    }
    return -1;
  }

  /**
   * The base implementation of `_.indexOf` without `fromIndex` bounds checks.
   *
   * @private
   * @param {Array} array The array to inspect.
   * @param {*} value The value to search for.
   * @param {number} fromIndex The index to search from.
   * @returns {number} Returns the index of the matched value, else `-1`.
   */
  function baseIndexOf(array, value, fromIndex) {
    return value === value
      ? strictIndexOf(array, value, fromIndex)
      : baseFindIndex(array, baseIsNaN, fromIndex);
  }

  /**
   * This function is like `baseIndexOf` except that it accepts a comparator.
   *
   * @private
   * @param {Array} array The array to inspect.
   * @param {*} value The value to search for.
   * @param {number} fromIndex The index to search from.
   * @param {Function} comparator The comparator invoked per element.
   * @returns {number} Returns the index of the matched value, else `-1`.
   */
  function baseIndexOfWith(array, value, fromIndex, comparator) {
    var index = fromIndex - 1,
        length = array.length;

    while (++index < length) {
      if (comparator(array[index], value)) {
        return index;
      }
    }
    return -1;
  }

  /**
   * The base implementation of `_.isNaN` without support for number objects.
   *
   * @private
   * @param {*} value The value to check.
   * @returns {boolean} Returns `true` if `value` is `NaN`, else `false`.
   */
  function baseIsNaN(value) {
    return value !== value;
  }

  /**
   * The base implementation of `_.mean` and `_.meanBy` without support for
   * iteratee shorthands.
   *
   * @private
   * @param {Array} array The array to iterate over.
   * @param {Function} iteratee The function invoked per iteration.
   * @returns {number} Returns the mean.
   */
  function baseMean(array, iteratee) {
    var length = array == null ? 0 : array.length;
    return length ? (baseSum(array, iteratee) / length) : NAN;
  }

  /**
   * The base implementation of `_.property` without support for deep paths.
   *
   * @private
   * @param {string} key The key of the property to get.
   * @returns {Function} Returns the new accessor function.
   */
  function baseProperty(key) {
    return function(object) {
      return object == null ? undefined : object[key];
    };
  }

  /**
   * The base implementation of `_.propertyOf` without support for deep paths.
   *
   * @private
   * @param {Object} object The object to query.
   * @returns {Function} Returns the new accessor function.
   */
  function basePropertyOf(object) {
    return function(key) {
      return object == null ? undefined : object[key];
    };
  }

  /**
   * The base implementation of `_.reduce` and `_.reduceRight`, without support
   * for iteratee shorthands, which iterates over `collection` using `eachFunc`.
   *
   * @private
   * @param {Array|Object} collection The collection to iterate over.
   * @param {Function} iteratee The function invoked per iteration.
   * @param {*} accumulator The initial value.
   * @param {boolean} initAccum Specify using the first or last element of
   *  `collection` as the initial value.
   * @param {Function} eachFunc The function to iterate over `collection`.
   * @returns {*} Returns the accumulated value.
   */
  function baseReduce(collection, iteratee, accumulator, initAccum, eachFunc) {
    eachFunc(collection, function(value, index, collection) {
      accumulator = initAccum
        ? (initAccum = false, value)
        : iteratee(accumulator, value, index, collection);
    });
    return accumulator;
  }

  /**
   * The base implementation of `_.sortBy` which uses `comparer` to define the
   * sort order of `array` and replaces criteria objects with their corresponding
   * values.
   *
   * @private
   * @param {Array} array The array to sort.
   * @param {Function} comparer The function to define sort order.
   * @returns {Array} Returns `array`.
   */
  function baseSortBy(array, comparer) {
    var length = array.length;

    array.sort(comparer);
    while (length--) {
      array[length] = array[length].value;
    }
    return array;
  }

  /**
   * The base implementation of `_.sum` and `_.sumBy` without support for
   * iteratee shorthands.
   *
   * @private
   * @param {Array} array The array to iterate over.
   * @param {Function} iteratee The function invoked per iteration.
   * @returns {number} Returns the sum.
   */
  function baseSum(array, iteratee) {
    var result,
        index = -1,
        length = array.length;

    while (++index < length) {
      var current = iteratee(array[index]);
      if (current !== undefined) {
        result = result === undefined ? current : (result + current);
      }
    }
    return result;
  }

  /**
   * The base implementation of `_.times` without support for iteratee shorthands
   * or max array length checks.
   *
   * @private
   * @param {number} n The number of times to invoke `iteratee`.
   * @param {Function} iteratee The function invoked per iteration.
   * @returns {Array} Returns the array of results.
   */
  function baseTimes(n, iteratee) {
    var index = -1,
        result = Array(n);

    while (++index < n) {
      result[index] = iteratee(index);
    }
    return result;
  }

  /**
   * The base implementation of `_.toPairs` and `_.toPairsIn` which creates an array
   * of key-value pairs for `object` corresponding to the property names of `props`.
   *
   * @private
   * @param {Object} object The object to query.
   * @param {Array} props The property names to get values for.
   * @returns {Object} Returns the key-value pairs.
   */
  function baseToPairs(object, props) {
    return arrayMap(props, function(key) {
      return [key, object[key]];
    });
  }

  /**
   * The base implementation of `_.trim`.
   *
   * @private
   * @param {string} string The string to trim.
   * @returns {string} Returns the trimmed string.
   */
  function baseTrim(string) {
    return string
      ? string.slice(0, trimmedEndIndex(string) + 1).replace(reTrimStart, '')
      : string;
  }

  /**
   * The base implementation of `_.unary` without support for storing metadata.
   *
   * @private
   * @param {Function} func The function to cap arguments for.
   * @returns {Function} Returns the new capped function.
   */
  function baseUnary(func) {
    return function(value) {
      return func(value);
    };
  }

  /**
   * The base implementation of `_.values` and `_.valuesIn` which creates an
   * array of `object` property values corresponding to the property names
   * of `props`.
   *
   * @private
   * @param {Object} object The object to query.
   * @param {Array} props The property names to get values for.
   * @returns {Object} Returns the array of property values.
   */
  function baseValues(object, props) {
    return arrayMap(props, function(key) {
      return object[key];
    });
  }

  /**
   * Checks if a `cache` value for `key` exists.
   *
   * @private
   * @param {Object} cache The cache to query.
   * @param {string} key The key of the entry to check.
   * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
   */
  function cacheHas(cache, key) {
    return cache.has(key);
  }

  /**
   * Used by `_.trim` and `_.trimStart` to get the index of the first string symbol
   * that is not found in the character symbols.
   *
   * @private
   * @param {Array} strSymbols The string symbols to inspect.
   * @param {Array} chrSymbols The character symbols to find.
   * @returns {number} Returns the index of the first unmatched string symbol.
   */
  function charsStartIndex(strSymbols, chrSymbols) {
    var index = -1,
        length = strSymbols.length;

    while (++index < length && baseIndexOf(chrSymbols, strSymbols[index], 0) > -1) {}
    return index;
  }

  /**
   * Used by `_.trim` and `_.trimEnd` to get the index of the last string symbol
   * that is not found in the character symbols.
   *
   * @private
   * @param {Array} strSymbols The string symbols to inspect.
   * @param {Array} chrSymbols The character symbols to find.
   * @returns {number} Returns the index of the last unmatched string symbol.
   */
  function charsEndIndex(strSymbols, chrSymbols) {
    var index = strSymbols.length;

    while (index-- && baseIndexOf(chrSymbols, strSymbols[index], 0) > -1) {}
    return index;
  }

  /**
   * Gets the number of `placeholder` occurrences in `array`.
   *
   * @private
   * @param {Array} array The array to inspect.
   * @param {*} placeholder The placeholder to search for.
   * @returns {number} Returns the placeholder count.
   */
  function countHolders(array, placeholder) {
    var length = array.length,
        result = 0;

    while (length--) {
      if (array[length] === placeholder) {
        ++result;
      }
    }
    return result;
  }

  /**
   * Used by `_.deburr` to convert Latin-1 Supplement and Latin Extended-A
   * letters to basic Latin letters.
   *
   * @private
   * @param {string} letter The matched letter to deburr.
   * @returns {string} Returns the deburred letter.
   */
  var deburrLetter = basePropertyOf(deburredLetters);

  /**
   * Used by `_.escape` to convert characters to HTML entities.
   *
   * @private
   * @param {string} chr The matched character to escape.
   * @returns {string} Returns the escaped character.
   */
  var escapeHtmlChar = basePropertyOf(htmlEscapes);

  /**
   * Used by `_.template` to escape characters for inclusion in compiled string literals.
   *
   * @private
   * @param {string} chr The matched character to escape.
   * @returns {string} Returns the escaped character.
   */
  function escapeStringChar(chr) {
    return '\\' + stringEscapes[chr];
  }

  /**
   * Gets the value at `key` of `object`.
   *
   * @private
   * @param {Object} [object] The object to query.
   * @param {string} key The key of the property to get.
   * @returns {*} Returns the property value.
   */
  function getValue(object, key) {
    return object == null ? undefined : object[key];
  }

  /**
   * Checks if `string` contains Unicode symbols.
   *
   * @private
   * @param {string} string The string to inspect.
   * @returns {boolean} Returns `true` if a symbol is found, else `false`.
   */
  function hasUnicode(string) {
    return reHasUnicode.test(string);
  }

  /**
   * Checks if `string` contains a word composed of Unicode symbols.
   *
   * @private
   * @param {string} string The string to inspect.
   * @returns {boolean} Returns `true` if a word is found, else `false`.
   */
  function hasUnicodeWord(string) {
    return reHasUnicodeWord.test(string);
  }

  /**
   * Converts `iterator` to an array.
   *
   * @private
   * @param {Object} iterator The iterator to convert.
   * @returns {Array} Returns the converted array.
   */
  function iteratorToArray(iterator) {
    var data,
        result = [];

    while (!(data = iterator.next()).done) {
      result.push(data.value);
    }
    return result;
  }

  /**
   * Converts `map` to its key-value pairs.
   *
   * @private
   * @param {Object} map The map to convert.
   * @returns {Array} Returns the key-value pairs.
   */
  function mapToArray(map) {
    var index = -1,
        result = Array(map.size);

    map.forEach(function(value, key) {
      result[++index] = [key, value];
    });
    return result;
  }

  /**
   * Creates a unary function that invokes `func` with its argument transformed.
   *
   * @private
   * @param {Function} func The function to wrap.
   * @param {Function} transform The argument transform.
   * @returns {Function} Returns the new function.
   */
  function overArg(func, transform) {
    return function(arg) {
      return func(transform(arg));
    };
  }

  /**
   * Replaces all `placeholder` elements in `array` with an internal placeholder
   * and returns an array of their indexes.
   *
   * @private
   * @param {Array} array The array to modify.
   * @param {*} placeholder The placeholder to replace.
   * @returns {Array} Returns the new array of placeholder indexes.
   */
  function replaceHolders(array, placeholder) {
    var index = -1,
        length = array.length,
        resIndex = 0,
        result = [];

    while (++index < length) {
      var value = array[index];
      if (value === placeholder || value === PLACEHOLDER) {
        array[index] = PLACEHOLDER;
        result[resIndex++] = index;
      }
    }
    return result;
  }

  /**
   * Converts `set` to an array of its values.
   *
   * @private
   * @param {Object} set The set to convert.
   * @returns {Array} Returns the values.
   */
  function setToArray(set) {
    var index = -1,
        result = Array(set.size);

    set.forEach(function(value) {
      result[++index] = value;
    });
    return result;
  }

  /**
   * Converts `set` to its value-value pairs.
   *
   * @private
   * @param {Object} set The set to convert.
   * @returns {Array} Returns the value-value pairs.
   */
  function setToPairs(set) {
    var index = -1,
        result = Array(set.size);

    set.forEach(function(value) {
      result[++index] = [value, value];
    });
    return result;
  }

  /**
   * A specialized version of `_.indexOf` which performs strict equality
   * comparisons of values, i.e. `===`.
   *
   * @private
   * @param {Array} array The array to inspect.
   * @param {*} value The value to search for.
   * @param {number} fromIndex The index to search from.
   * @returns {number} Returns the index of the matched value, else `-1`.
   */
  function strictIndexOf(array, value, fromIndex) {
    var index = fromIndex - 1,
        length = array.length;

    while (++index < length) {
      if (array[index] === value) {
        return index;
      }
    }
    return -1;
  }

  /**
   * A specialized version of `_.lastIndexOf` which performs strict equality
   * comparisons of values, i.e. `===`.
   *
   * @private
   * @param {Array} array The array to inspect.
   * @param {*} value The value to search for.
   * @param {number} fromIndex The index to search from.
   * @returns {number} Returns the index of the matched value, else `-1`.
   */
  function strictLastIndexOf(array, value, fromIndex) {
    var index = fromIndex + 1;
    while (index--) {
      if (array[index] === value) {
        return index;
      }
    }
    return index;
  }

  /**
   * Gets the number of symbols in `string`.
   *
   * @private
   * @param {string} string The string to inspect.
   * @returns {number} Returns the string size.
   */
  function stringSize(string) {
    return hasUnicode(string)
      ? unicodeSize(string)
      : asciiSize(string);
  }

  /**
   * Converts `string` to an array.
   *
   * @private
   * @param {string} string The string to convert.
   * @returns {Array} Returns the converted array.
   */
  function stringToArray(string) {
    return hasUnicode(string)
      ? unicodeToArray(string)
      : asciiToArray(string);
  }

  /**
   * Used by `_.trim` and `_.trimEnd` to get the index of the last non-whitespace
   * character of `string`.
   *
   * @private
   * @param {string} string The string to inspect.
   * @returns {number} Returns the index of the last non-whitespace character.
   */
  function trimmedEndIndex(string) {
    var index = string.length;

    while (index-- && reWhitespace.test(string.charAt(index))) {}
    return index;
  }

  /**
   * Used by `_.unescape` to convert HTML entities to characters.
   *
   * @private
   * @param {string} chr The matched character to unescape.
   * @returns {string} Returns the unescaped character.
   */
  var unescapeHtmlChar = basePropertyOf(htmlUnescapes);

  /**
   * Gets the size of a Unicode `string`.
   *
   * @private
   * @param {string} string The string inspect.
   * @returns {number} Returns the string size.
   */
  function unicodeSize(string) {
    var result = reUnicode.lastIndex = 0;
    while (reUnicode.test(string)) {
      ++result;
    }
    return result;
  }

  /**
   * Converts a Unicode `string` to an array.
   *
   * @private
   * @param {string} string The string to convert.
   * @returns {Array} Returns the converted array.
   */
  function unicodeToArray(string) {
    return string.match(reUnicode) || [];
  }

  /**
   * Splits a Unicode `string` into an array of its words.
   *
   * @private
   * @param {string} The string to inspect.
   * @returns {Array} Returns the words of `string`.
   */
  function unicodeWords(string) {
    return string.match(reUnicodeWord) || [];
  }

  /*--------------------------------------------------------------------------*/

  /**
   * Create a new pristine `lodash` function using the `context` object.
   *
   * @static
   * @memberOf _
   * @since 1.1.0
   * @category Util
   * @param {Object} [context=root] The context object.
   * @returns {Function} Returns a new `lodash` function.
   * @example
   *
   * _.mixin({ 'foo': _.constant('foo') });
   *
   * var lodash = _.runInContext();
   * lodash.mixin({ 'bar': lodash.constant('bar') });
   *
   * _.isFunction(_.foo);
   * // => true
   * _.isFunction(_.bar);
   * // => false
   *
   * lodash.isFunction(lodash.foo);
   * // => false
   * lodash.isFunction(lodash.bar);
   * // => true
   *
   * // Create a suped-up `defer` in Node.js.
   * var defer = _.runInContext({ 'setTimeout': setImmediate }).defer;
   */
  var runInContext = (function runInContext(context) {
    context = context == null ? root : _.defaults(root.Object(), context, _.pick(root, contextProps));

    /** Built-in constructor references. */
    var Array = context.Array,
        Date = context.Date,
        Error = context.Error,
        Function = context.Function,
        Math = context.Math,
        Object = context.Object,
        RegExp = context.RegExp,
        String = context.String,
        TypeError = context.TypeError;

    /** Used for built-in method references. */
    var arrayProto = Array.prototype,
        funcProto = Function.prototype,
        objectProto = Object.prototype;

    /** Used to detect overreaching core-js shims. */
    var coreJsData = context['__core-js_shared__'];

    /** Used to resolve the decompiled source of functions. */
    var funcToString = funcProto.toString;

    /** Used to check objects for own properties. */
    var hasOwnProperty = objectProto.hasOwnProperty;

    /** Used to generate unique IDs. */
    var idCounter = 0;

    /** Used to detect methods masquerading as native. */
    var maskSrcKey = (function() {
      var uid = /[^.]+$/.exec(coreJsData && coreJsData.keys && coreJsData.keys.IE_PROTO || '');
      return uid ? ('Symbol(src)_1.' + uid) : '';
    }());

    /**
     * Used to resolve the
     * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
     * of values.
     */
    var nativeObjectToString = objectProto.toString;

    /** Used to infer the `Object` constructor. */
    var objectCtorString = funcToString.call(Object);

    /** Used to restore the original `_` reference in `_.noConflict`. */
    var oldDash = root._;

    /** Used to detect if a method is native. */
    var reIsNative = RegExp('^' +
      funcToString.call(hasOwnProperty).replace(reRegExpChar, '\\$&')
      .replace(/hasOwnProperty|(function).*?(?=\\\()| for .+?(?=\\\])/g, '$1.*?') + '$'
    );

    /** Built-in value references. */
    var Buffer = moduleExports ? context.Buffer : undefined,
        Symbol = context.Symbol,
        Uint8Array = context.Uint8Array,
        allocUnsafe = Buffer ? Buffer.allocUnsafe : undefined,
        getPrototype = overArg(Object.getPrototypeOf, Object),
        objectCreate = Object.create,
        propertyIsEnumerable = objectProto.propertyIsEnumerable,
        splice = arrayProto.splice,
        spreadableSymbol = Symbol ? Symbol.isConcatSpreadable : undefined,
        symIterator = Symbol ? Symbol.iterator : undefined,
        symToStringTag = Symbol ? Symbol.toStringTag : undefined;

    var defineProperty = (function() {
      try {
        var func = getNative(Object, 'defineProperty');
        func({}, '', {});
        return func;
      } catch (e) {}
    }());

    /** Mocked built-ins. */
    var ctxClearTimeout = context.clearTimeout !== root.clearTimeout && context.clearTimeout,
        ctxNow = Date && Date.now !== root.Date.now && Date.now,
        ctxSetTimeout = context.setTimeout !== root.setTimeout && context.setTimeout;

    /* Built-in method references for those with the same name as other `lodash` methods. */
    var nativeCeil = Math.ceil,
        nativeFloor = Math.floor,
        nativeGetSymbols = Object.getOwnPropertySymbols,
        nativeIsBuffer = Buffer ? Buffer.isBuffer : undefined,
        nativeIsFinite = context.isFinite,
        nativeJoin = arrayProto.join,
        nativeKeys = overArg(Object.keys, Object),
        nativeMax = Math.max,
        nativeMin = Math.min,
        nativeNow = Date.now,
        nativeParseInt = context.parseInt,
        nativeRandom = Math.random,
        nativeReverse = arrayProto.reverse;

    /* Built-in method references that are verified to be native. */
    var DataView = getNative(context, 'DataView'),
        Map = getNative(context, 'Map'),
        Promise = getNative(context, 'Promise'),
        Set = getNative(context, 'Set'),
        WeakMap = getNative(context, 'WeakMap'),
        nativeCreate = getNative(Object, 'create');

    /** Used to store function metadata. */
    var metaMap = WeakMap && new WeakMap;

    /** Used to lookup unminified function names. */
    var realNames = {};

    /** Used to detect maps, sets, and weakmaps. */
    var dataViewCtorString = toSource(DataView),
        mapCtorString = toSource(Map),
        promiseCtorString = toSource(Promise),
        setCtorString = toSource(Set),
        weakMapCtorString = toSource(WeakMap);

    /** Used to convert symbols to primitives and strings. */
    var symbolProto = Symbol ? Symbol.prototype : undefined,
        symbolValueOf = symbolProto ? symbolProto.valueOf : undefined,
        symbolToString = symbolProto ? symbolProto.toString : undefined;

    /*------------------------------------------------------------------------*/

    /**
     * Creates a `lodash` object which wraps `value` to enable implicit method
     * chain sequences. Methods that operate on and return arrays, collections,
     * and functions can be chained together. Methods that retrieve a single value
     * or may return a primitive value will automatically end the chain sequence
     * and return the unwrapped value. Otherwise, the value must be unwrapped
     * with `_#value`.
     *
     * Explicit chain sequences, which must be unwrapped with `_#value`, may be
     * enabled using `_.chain`.
     *
     * The execution of chained methods is lazy, that is, it's deferred until
     * `_#value` is implicitly or explicitly called.
     *
     * Lazy evaluation allows several methods to support shortcut fusion.
     * Shortcut fusion is an optimization to merge iteratee calls; this avoids
     * the creation of intermediate arrays and can greatly reduce the number of
     * iteratee executions. Sections of a chain sequence qualify for shortcut
     * fusion if the section is applied to an array and iteratees accept only
     * one argument. The heuristic for whether a section qualifies for shortcut
     * fusion is subject to change.
     *
     * Chaining is supported in custom builds as long as the `_#value` method is
     * directly or indirectly included in the build.
     *
     * In addition to lodash methods, wrappers have `Array` and `String` methods.
     *
     * The wrapper `Array` methods are:
     * `concat`, `join`, `pop`, `push`, `shift`, `sort`, `splice`, and `unshift`
     *
     * The wrapper `String` methods are:
     * `replace` and `split`
     *
     * The wrapper methods that support shortcut fusion are:
     * `at`, `compact`, `drop`, `dropRight`, `dropWhile`, `filter`, `find`,
     * `findLast`, `head`, `initial`, `last`, `map`, `reject`, `reverse`, `slice`,
     * `tail`, `take`, `takeRight`, `takeRightWhile`, `takeWhile`, and `toArray`
     *
     * The chainable wrapper methods are:
     * `after`, `ary`, `assign`, `assignIn`, `assignInWith`, `assignWith`, `at`,
     * `before`, `bind`, `bindAll`, `bindKey`, `castArray`, `chain`, `chunk`,
     * `commit`, `compact`, `concat`, `conforms`, `constant`, `countBy`, `create`,
     * `curry`, `debounce`, `defaults`, `defaultsDeep`, `defer`, `delay`,
     * `difference`, `differenceBy`, `differenceWith`, `drop`, `dropRight`,
     * `dropRightWhile`, `dropWhile`, `extend`, `extendWith`, `fill`, `filter`,
     * `flatMap`, `flatMapDeep`, `flatMapDepth`, `flatten`, `flattenDeep`,
     * `flattenDepth`, `flip`, `flow`, `flowRight`, `fromPairs`, `functions`,
     * `functionsIn`, `groupBy`, `initial`, `intersection`, `intersectionBy`,
     * `intersectionWith`, `invert`, `invertBy`, `invokeMap`, `iteratee`, `keyBy`,
     * `keys`, `keysIn`, `map`, `mapKeys`, `mapValues`, `matches`, `matchesProperty`,
     * `memoize`, `merge`, `mergeWith`, `method`, `methodOf`, `mixin`, `negate`,
     * `nthArg`, `omit`, `omitBy`, `once`, `orderBy`, `over`, `overArgs`,
     * `overEvery`, `overSome`, `partial`, `partialRight`, `partition`, `pick`,
     * `pickBy`, `plant`, `property`, `propertyOf`, `pull`, `pullAll`, `pullAllBy`,
     * `pullAllWith`, `pullAt`, `push`, `range`, `rangeRight`, `rearg`, `reject`,
     * `remove`, `rest`, `reverse`, `sampleSize`, `set`, `setWith`, `shuffle`,
     * `slice`, `sort`, `sortBy`, `splice`, `spread`, `tail`, `take`, `takeRight`,
     * `takeRightWhile`, `takeWhile`, `tap`, `throttle`, `thru`, `toArray`,
     * `toPairs`, `toPairsIn`, `toPath`, `toPlainObject`, `transform`, `unary`,
     * `union`, `unionBy`, `unionWith`, `uniq`, `uniqBy`, `uniqWith`, `unset`,
     * `unshift`, `unzip`, `unzipWith`, `update`, `updateWith`, `values`,
     * `valuesIn`, `without`, `wrap`, `xor`, `xorBy`, `xorWith`, `zip`,
     * `zipObject`, `zipObjectDeep`, and `zipWith`
     *
     * The wrapper methods that are **not** chainable by default are:
     * `add`, `attempt`, `camelCase`, `capitalize`, `ceil`, `clamp`, `clone`,
     * `cloneDeep`, `cloneDeepWith`, `cloneWith`, `conformsTo`, `deburr`,
     * `defaultTo`, `divide`, `each`, `eachRight`, `endsWith`, `eq`, `escape`,
     * `escapeRegExp`, `every`, `find`, `findIndex`, `findKey`, `findLast`,
     * `findLastIndex`, `findLastKey`, `first`, `floor`, `forEach`, `forEachRight`,
     * `forIn`, `forInRight`, `forOwn`, `forOwnRight`, `get`, `gt`, `gte`, `has`,
     * `hasIn`, `head`, `identity`, `includes`, `indexOf`, `inRange`, `invoke`,
     * `isArguments`, `isArray`, `isArrayBuffer`, `isArrayLike`, `isArrayLikeObject`,
     * `isBoolean`, `isBuffer`, `isDate`, `isElement`, `isEmpty`, `isEqual`,
     * `isEqualWith`, `isError`, `isFinite`, `isFunction`, `isInteger`, `isLength`,
     * `isMap`, `isMatch`, `isMatchWith`, `isNaN`, `isNative`, `isNil`, `isNull`,
     * `isNumber`, `isObject`, `isObjectLike`, `isPlainObject`, `isRegExp`,
     * `isSafeInteger`, `isSet`, `isString`, `isUndefined`, `isTypedArray`,
     * `isWeakMap`, `isWeakSet`, `join`, `kebabCase`, `last`, `lastIndexOf`,
     * `lowerCase`, `lowerFirst`, `lt`, `lte`, `max`, `maxBy`, `mean`, `meanBy`,
     * `min`, `minBy`, `multiply`, `noConflict`, `noop`, `now`, `nth`, `pad`,
     * `padEnd`, `padStart`, `parseInt`, `pop`, `random`, `reduce`, `reduceRight`,
     * `repeat`, `result`, `round`, `runInContext`, `sample`, `shift`, `size`,
     * `snakeCase`, `some`, `sortedIndex`, `sortedIndexBy`, `sortedLastIndex`,
     * `sortedLastIndexBy`, `startCase`, `startsWith`, `stubArray`, `stubFalse`,
     * `stubObject`, `stubString`, `stubTrue`, `subtract`, `sum`, `sumBy`,
     * `template`, `times`, `toFinite`, `toInteger`, `toJSON`, `toLength`,
     * `toLower`, `toNumber`, `toSafeInteger`, `toString`, `toUpper`, `trim`,
     * `trimEnd`, `trimStart`, `truncate`, `unescape`, `uniqueId`, `upperCase`,
     * `upperFirst`, `value`, and `words`
     *
     * @name _
     * @constructor
     * @category Seq
     * @param {*} value The value to wrap in a `lodash` instance.
     * @returns {Object} Returns the new `lodash` wrapper instance.
     * @example
     *
     * function square(n) {
     *   return n * n;
     * }
     *
     * var wrapped = _([1, 2, 3]);
     *
     * // Returns an unwrapped value.
     * wrapped.reduce(_.add);
     * // => 6
     *
     * // Returns a wrapped value.
     * var squares = wrapped.map(square);
     *
     * _.isArray(squares);
     * // => false
     *
     * _.isArray(squares.value());
     * // => true
     */
    function lodash(value) {
      if (isObjectLike(value) && !isArray(value) && !(value instanceof LazyWrapper)) {
        if (value instanceof LodashWrapper) {
          return value;
        }
        if (hasOwnProperty.call(value, '__wrapped__')) {
          return wrapperClone(value);
        }
      }
      return new LodashWrapper(value);
    }

    /**
     * The base implementation of `_.create` without support for assigning
     * properties to the created object.
     *
     * @private
     * @param {Object} proto The object to inherit from.
     * @returns {Object} Returns the new object.
     */
    var baseCreate = (function() {
      function object() {}
      return function(proto) {
        if (!isObject(proto)) {
          return {};
        }
        if (objectCreate) {
          return objectCreate(proto);
        }
        object.prototype = proto;
        var result = new object;
        object.prototype = undefined;
        return result;
      };
    }());

    /**
     * The function whose prototype chain sequence wrappers inherit from.
     *
     * @private
     */
    function baseLodash() {
      // No operation performed.
    }

    /**
     * The base constructor for creating `lodash` wrapper objects.
     *
     * @private
     * @param {*} value The value to wrap.
     * @param {boolean} [chainAll] Enable explicit method chain sequences.
     */
    function LodashWrapper(value, chainAll) {
      this.__wrapped__ = value;
      this.__actions__ = [];
      this.__chain__ = !!chainAll;
      this.__index__ = 0;
      this.__values__ = undefined;
    }

    /**
     * By default, the template delimiters used by lodash are like those in
     * embedded Ruby (ERB) as well as ES2015 template strings. Change the
     * following template settings to use alternative delimiters.
     *
     * @static
     * @memberOf _
     * @type {Object}
     */
    lodash.templateSettings = {

      /**
       * Used to detect `data` property values to be HTML-escaped.
       *
       * @memberOf _.templateSettings
       * @type {RegExp}
       */
      'escape': reEscape,

      /**
       * Used to detect code to be evaluated.
       *
       * @memberOf _.templateSettings
       * @type {RegExp}
       */
      'evaluate': reEvaluate,

      /**
       * Used to detect `data` property values to inject.
       *
       * @memberOf _.templateSettings
       * @type {RegExp}
       */
      'interpolate': reInterpolate,

      /**
       * Used to reference the data object in the template text.
       *
       * @memberOf _.templateSettings
       * @type {string}
       */
      'variable': '',

      /**
       * Used to import variables into the compiled template.
       *
       * @memberOf _.templateSettings
       * @type {Object}
       */
      'imports': {

        /**
         * A reference to the `lodash` function.
         *
         * @memberOf _.templateSettings.imports
         * @type {Function}
         */
        '_': lodash
      }
    };

    // Ensure wrappers are instances of `baseLodash`.
    lodash.prototype = baseLodash.prototype;
    lodash.prototype.constructor = lodash;

    LodashWrapper.prototype = baseCreate(baseLodash.prototype);
    LodashWrapper.prototype.constructor = LodashWrapper;

    /*------------------------------------------------------------------------*/

    /**
     * Creates a lazy wrapper object which wraps `value` to enable lazy evaluation.
     *
     * @private
     * @constructor
     * @param {*} value The value to wrap.
     */
    function LazyWrapper(value) {
      this.__wrapped__ = value;
      this.__actions__ = [];
      this.__dir__ = 1;
      this.__filtered__ = false;
      this.__iteratees__ = [];
      this.__takeCount__ = MAX_ARRAY_LENGTH;
      this.__views__ = [];
    }

    /**
     * Creates a clone of the lazy wrapper object.
     *
     * @private
     * @name clone
     * @memberOf LazyWrapper
     * @returns {Object} Returns the cloned `LazyWrapper` object.
     */
    function lazyClone() {
      var result = new LazyWrapper(this.__wrapped__);
      result.__actions__ = copyArray(this.__actions__);
      result.__dir__ = this.__dir__;
      result.__filtered__ = this.__filtered__;
      result.__iteratees__ = copyArray(this.__iteratees__);
      result.__takeCount__ = this.__takeCount__;
      result.__views__ = copyArray(this.__views__);
      return result;
    }

    /**
     * Reverses the direction of lazy iteration.
     *
     * @private
     * @name reverse
     * @memberOf LazyWrapper
     * @returns {Object} Returns the new reversed `LazyWrapper` object.
     */
    function lazyReverse() {
      if (this.__filtered__) {
        var result = new LazyWrapper(this);
        result.__dir__ = -1;
        result.__filtered__ = true;
      } else {
        result = this.clone();
        result.__dir__ *= -1;
      }
      return result;
    }

    /**
     * Extracts the unwrapped value from its lazy wrapper.
     *
     * @private
     * @name value
     * @memberOf LazyWrapper
     * @returns {*} Returns the unwrapped value.
     */
    function lazyValue() {
      var array = this.__wrapped__.value(),
          dir = this.__dir__,
          isArr = isArray(array),
          isRight = dir < 0,
          arrLength = isArr ? array.length : 0,
          view = getView(0, arrLength, this.__views__),
          start = view.start,
          end = view.end,
          length = end - start,
          index = isRight ? end : (start - 1),
          iteratees = this.__iteratees__,
          iterLength = iteratees.length,
          resIndex = 0,
          takeCount = nativeMin(length, this.__takeCount__);

      if (!isArr || (!isRight && arrLength == length && takeCount == length)) {
        return baseWrapperValue(array, this.__actions__);
      }
      var result = [];

      outer:
      while (length-- && resIndex < takeCount) {
        index += dir;

        var iterIndex = -1,
            value = array[index];

        while (++iterIndex < iterLength) {
          var data = iteratees[iterIndex],
              iteratee = data.iteratee,
              type = data.type,
              computed = iteratee(value);

          if (type == LAZY_MAP_FLAG) {
            value = computed;
          } else if (!computed) {
            if (type == LAZY_FILTER_FLAG) {
              continue outer;
            } else {
              break outer;
            }
          }
        }
        result[resIndex++] = value;
      }
      return result;
    }

    // Ensure `LazyWrapper` is an instance of `baseLodash`.
    LazyWrapper.prototype = baseCreate(baseLodash.prototype);
    LazyWrapper.prototype.constructor = LazyWrapper;

    /*------------------------------------------------------------------------*/

    /**
     * Creates a hash object.
     *
     * @private
     * @constructor
     * @param {Array} [entries] The key-value pairs to cache.
     */
    function Hash(entries) {
      var index = -1,
          length = entries == null ? 0 : entries.length;

      this.clear();
      while (++index < length) {
        var entry = entries[index];
        this.set(entry[0], entry[1]);
      }
    }

    /**
     * Removes all key-value entries from the hash.
     *
     * @private
     * @name clear
     * @memberOf Hash
     */
    function hashClear() {
      this.__data__ = nativeCreate ? nativeCreate(null) : {};
      this.size = 0;
    }

    /**
     * Removes `key` and its value from the hash.
     *
     * @private
     * @name delete
     * @memberOf Hash
     * @param {Object} hash The hash to modify.
     * @param {string} key The key of the value to remove.
     * @returns {boolean} Returns `true` if the entry was removed, else `false`.
     */
    function hashDelete(key) {
      var result = this.has(key) && delete this.__data__[key];
      this.size -= result ? 1 : 0;
      return result;
    }

    /**
     * Gets the hash value for `key`.
     *
     * @private
     * @name get
     * @memberOf Hash
     * @param {string} key The key of the value to get.
     * @returns {*} Returns the entry value.
     */
    function hashGet(key) {
      var data = this.__data__;
      if (nativeCreate) {
        var result = data[key];
        return result === HASH_UNDEFINED ? undefined : result;
      }
      return hasOwnProperty.call(data, key) ? data[key] : undefined;
    }

    /**
     * Checks if a hash value for `key` exists.
     *
     * @private
     * @name has
     * @memberOf Hash
     * @param {string} key The key of the entry to check.
     * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
     */
    function hashHas(key) {
      var data = this.__data__;
      return nativeCreate ? (data[key] !== undefined) : hasOwnProperty.call(data, key);
    }

    /**
     * Sets the hash `key` to `value`.
     *
     * @private
     * @name set
     * @memberOf Hash
     * @param {string} key The key of the value to set.
     * @param {*} value The value to set.
     * @returns {Object} Returns the hash instance.
     */
    function hashSet(key, value) {
      var data = this.__data__;
      this.size += this.has(key) ? 0 : 1;
      data[key] = (nativeCreate && value === undefined) ? HASH_UNDEFINED : value;
      return this;
    }

    // Add methods to `Hash`.
    Hash.prototype.clear = hashClear;
    Hash.prototype['delete'] = hashDelete;
    Hash.prototype.get = hashGet;
    Hash.prototype.has = hashHas;
    Hash.prototype.set = hashSet;

    /*------------------------------------------------------------------------*/

    /**
     * Creates an list cache object.
     *
     * @private
     * @constructor
     * @param {Array} [entries] The key-value pairs to cache.
     */
    function ListCache(entries) {
      var index = -1,
          length = entries == null ? 0 : entries.length;

      this.clear();
      while (++index < length) {
        var entry = entries[index];
        this.set(entry[0], entry[1]);
      }
    }

    /**
     * Removes all key-value entries from the list cache.
     *
     * @private
     * @name clear
     * @memberOf ListCache
     */
    function listCacheClear() {
      this.__data__ = [];
      this.size = 0;
    }

    /**
     * Removes `key` and its value from the list cache.
     *
     * @private
     * @name delete
     * @memberOf ListCache
     * @param {string} key The key of the value to remove.
     * @returns {boolean} Returns `true` if the entry was removed, else `false`.
     */
    function listCacheDelete(key) {
      var data = this.__data__,
          index = assocIndexOf(data, key);

      if (index < 0) {
        return false;
      }
      var lastIndex = data.length - 1;
      if (index == lastIndex) {
        data.pop();
      } else {
        splice.call(data, index, 1);
      }
      --this.size;
      return true;
    }

    /**
     * Gets the list cache value for `key`.
     *
     * @private
     * @name get
     * @memberOf ListCache
     * @param {string} key The key of the value to get.
     * @returns {*} Returns the entry value.
     */
    function listCacheGet(key) {
      var data = this.__data__,
          index = assocIndexOf(data, key);

      return index < 0 ? undefined : data[index][1];
    }

    /**
     * Checks if a list cache value for `key` exists.
     *
     * @private
     * @name has
     * @memberOf ListCache
     * @param {string} key The key of the entry to check.
     * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
     */
    function listCacheHas(key) {
      return assocIndexOf(this.__data__, key) > -1;
    }

    /**
     * Sets the list cache `key` to `value`.
     *
     * @private
     * @name set
     * @memberOf ListCache
     * @param {string} key The key of the value to set.
     * @param {*} value The value to set.
     * @returns {Object} Returns the list cache instance.
     */
    function listCacheSet(key, value) {
      var data = this.__data__,
          index = assocIndexOf(data, key);

      if (index < 0) {
        ++this.size;
        data.push([key, value]);
      } else {
        data[index][1] = value;
      }
      return this;
    }

    // Add methods to `ListCache`.
    ListCache.prototype.clear = listCacheClear;
    ListCache.prototype['delete'] = listCacheDelete;
    ListCache.prototype.get = listCacheGet;
    ListCache.prototype.has = listCacheHas;
    ListCache.prototype.set = listCacheSet;

    /*------------------------------------------------------------------------*/

    /**
     * Creates a map cache object to store key-value pairs.
     *
     * @private
     * @constructor
     * @param {Array} [entries] The key-value pairs to cache.
     */
    function MapCache(entries) {
      var index = -1,
          length = entries == null ? 0 : entries.length;

      this.clear();
      while (++index < length) {
        var entry = entries[index];
        this.set(entry[0], entry[1]);
      }
    }

    /**
     * Removes all key-value entries from the map.
     *
     * @private
     * @name clear
     * @memberOf MapCache
     */
    function mapCacheClear() {
      this.size = 0;
      this.__data__ = {
        'hash': new Hash,
        'map': new (Map || ListCache),
        'string': new Hash
      };
    }

    /**
     * Removes `key` and its value from the map.
     *
     * @private
     * @name delete
     * @memberOf MapCache
     * @param {string} key The key of the value to remove.
     * @returns {boolean} Returns `true` if the entry was removed, else `false`.
     */
    function mapCacheDelete(key) {
      var result = getMapData(this, key)['delete'](key);
      this.size -= result ? 1 : 0;
      return result;
    }

    /**
     * Gets the map value for `key`.
     *
     * @private
     * @name get
     * @memberOf MapCache
     * @param {string} key The key of the value to get.
     * @returns {*} Returns the entry value.
     */
    function mapCacheGet(key) {
      return getMapData(this, key).get(key);
    }

    /**
     * Checks if a map value for `key` exists.
     *
     * @private
     * @name has
     * @memberOf MapCache
     * @param {string} key The key of the entry to check.
     * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
     */
    function mapCacheHas(key) {
      return getMapData(this, key).has(key);
    }

    /**
     * Sets the map `key` to `value`.
     *
     * @private
     * @name set
     * @memberOf MapCache
     * @param {string} key The key of the value to set.
     * @param {*} value The value to set.
     * @returns {Object} Returns the map cache instance.
     */
    function mapCacheSet(key, value) {
      var data = getMapData(this, key),
          size = data.size;

      data.set(key, value);
      this.size += data.size == size ? 0 : 1;
      return this;
    }

    // Add methods to `MapCache`.
    MapCache.prototype.clear = mapCacheClear;
    MapCache.prototype['delete'] = mapCacheDelete;
    MapCache.prototype.get = mapCacheGet;
    MapCache.prototype.has = mapCacheHas;
    MapCache.prototype.set = mapCacheSet;

    /*------------------------------------------------------------------------*/

    /**
     *
     * Creates an array cache object to store unique values.
     *
     * @private
     * @constructor
     * @param {Array} [values] The values to cache.
     */
    function SetCache(values) {
      var index = -1,
          length = values == null ? 0 : values.length;

      this.__data__ = new MapCache;
      while (++index < length) {
        this.add(values[index]);
      }
    }

    /**
     * Adds `value` to the array cache.
     *
     * @private
     * @name add
     * @memberOf SetCache
     * @alias push
     * @param {*} value The value to cache.
     * @returns {Object} Returns the cache instance.
     */
    function setCacheAdd(value) {
      this.__data__.set(value, HASH_UNDEFINED);
      return this;
    }

    /**
     * Checks if `value` is in the array cache.
     *
     * @private
     * @name has
     * @memberOf SetCache
     * @param {*} value The value to search for.
     * @returns {number} Returns `true` if `value` is found, else `false`.
     */
    function setCacheHas(value) {
      return this.__data__.has(value);
    }

    // Add methods to `SetCache`.
    SetCache.prototype.add = SetCache.prototype.push = setCacheAdd;
    SetCache.prototype.has = setCacheHas;

    /*------------------------------------------------------------------------*/

    /**
     * Creates a stack cache object to store key-value pairs.
     *
     * @private
     * @constructor
     * @param {Array} [entries] The key-value pairs to cache.
     */
    function Stack(entries) {
      var data = this.__data__ = new ListCache(entries);
      this.size = data.size;
    }

    /**
     * Removes all key-value entries from the stack.
     *
     * @private
     * @name clear
     * @memberOf Stack
     */
    function stackClear() {
      this.__data__ = new ListCache;
      this.size = 0;
    }

    /**
     * Removes `key` and its value from the stack.
     *
     * @private
     * @name delete
     * @memberOf Stack
     * @param {string} key The key of the value to remove.
     * @returns {boolean} Returns `true` if the entry was removed, else `false`.
     */
    function stackDelete(key) {
      var data = this.__data__,
          result = data['delete'](key);

      this.size = data.size;
      return result;
    }

    /**
     * Gets the stack value for `key`.
     *
     * @private
     * @name get
     * @memberOf Stack
     * @param {string} key The key of the value to get.
     * @returns {*} Returns the entry value.
     */
    function stackGet(key) {
      return this.__data__.get(key);
    }

    /**
     * Checks if a stack value for `key` exists.
     *
     * @private
     * @name has
     * @memberOf Stack
     * @param {string} key The key of the entry to check.
     * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
     */
    function stackHas(key) {
      return this.__data__.has(key);
    }

    /**
     * Sets the stack `key` to `value`.
     *
     * @private
     * @name set
     * @memberOf Stack
     * @param {string} key The key of the value to set.
     * @param {*} value The value to set.
     * @returns {Object} Returns the stack cache instance.
     */
    function stackSet(key, value) {
      var data = this.__data__;
      if (data instanceof ListCache) {
        var pairs = data.__data__;
        if (!Map || (pairs.length < LARGE_ARRAY_SIZE - 1)) {
          pairs.push([key, value]);
          this.size = ++data.size;
          return this;
        }
        data = this.__data__ = new MapCache(pairs);
      }
      data.set(key, value);
      this.size = data.size;
      return this;
    }

    // Add methods to `Stack`.
    Stack.prototype.clear = stackClear;
    Stack.prototype['delete'] = stackDelete;
    Stack.prototype.get = stackGet;
    Stack.prototype.has = stackHas;
    Stack.prototype.set = stackSet;

    /*------------------------------------------------------------------------*/

    /**
     * Creates an array of the enumerable property names of the array-like `value`.
     *
     * @private
     * @param {*} value The value to query.
     * @param {boolean} inherited Specify returning inherited property names.
     * @returns {Array} Returns the array of property names.
     */
    function arrayLikeKeys(value, inherited) {
      var isArr = isArray(value),
          isArg = !isArr && isArguments(value),
          isBuff = !isArr && !isArg && isBuffer(value),
          isType = !isArr && !isArg && !isBuff && isTypedArray(value),
          skipIndexes = isArr || isArg || isBuff || isType,
          result = skipIndexes ? baseTimes(value.length, String) : [],
          length = result.length;

      for (var key in value) {
        if ((inherited || hasOwnProperty.call(value, key)) &&
            !(skipIndexes && (
               // Safari 9 has enumerable `arguments.length` in strict mode.
               key == 'length' ||
               // Node.js 0.10 has enumerable non-index properties on buffers.
               (isBuff && (key == 'offset' || key == 'parent')) ||
               // PhantomJS 2 has enumerable non-index properties on typed arrays.
               (isType && (key == 'buffer' || key == 'byteLength' || key == 'byteOffset')) ||
               // Skip index properties.
               isIndex(key, length)
            ))) {
          result.push(key);
        }
      }
      return result;
    }

    /**
     * A specialized version of `_.sample` for arrays.
     *
     * @private
     * @param {Array} array The array to sample.
     * @returns {*} Returns the random element.
     */
    function arraySample(array) {
      var length = array.length;
      return length ? array[baseRandom(0, length - 1)] : undefined;
    }

    /**
     * A specialized version of `_.sampleSize` for arrays.
     *
     * @private
     * @param {Array} array The array to sample.
     * @param {number} n The number of elements to sample.
     * @returns {Array} Returns the random elements.
     */
    function arraySampleSize(array, n) {
      return shuffleSelf(copyArray(array), baseClamp(n, 0, array.length));
    }

    /**
     * A specialized version of `_.shuffle` for arrays.
     *
     * @private
     * @param {Array} array The array to shuffle.
     * @returns {Array} Returns the new shuffled array.
     */
    function arrayShuffle(array) {
      return shuffleSelf(copyArray(array));
    }

    /**
     * This function is like `assignValue` except that it doesn't assign
     * `undefined` values.
     *
     * @private
     * @param {Object} object The object to modify.
     * @param {string} key The key of the property to assign.
     * @param {*} value The value to assign.
     */
    function assignMergeValue(object, key, value) {
      if ((value !== undefined && !eq(object[key], value)) ||
          (value === undefined && !(key in object))) {
        baseAssignValue(object, key, value);
      }
    }

    /**
     * Assigns `value` to `key` of `object` if the existing value is not equivalent
     * using [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
     * for equality comparisons.
     *
     * @private
     * @param {Object} object The object to modify.
     * @param {string} key The key of the property to assign.
     * @param {*} value The value to assign.
     */
    function assignValue(object, key, value) {
      var objValue = object[key];
      if (!(hasOwnProperty.call(object, key) && eq(objValue, value)) ||
          (value === undefined && !(key in object))) {
        baseAssignValue(object, key, value);
      }
    }

    /**
     * Gets the index at which the `key` is found in `array` of key-value pairs.
     *
     * @private
     * @param {Array} array The array to inspect.
     * @param {*} key The key to search for.
     * @returns {number} Returns the index of the matched value, else `-1`.
     */
    function assocIndexOf(array, key) {
      var length = array.length;
      while (length--) {
        if (eq(array[length][0], key)) {
          return length;
        }
      }
      return -1;
    }

    /**
     * Aggregates elements of `collection` on `accumulator` with keys transformed
     * by `iteratee` and values set by `setter`.
     *
     * @private
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Function} setter The function to set `accumulator` values.
     * @param {Function} iteratee The iteratee to transform keys.
     * @param {Object} accumulator The initial aggregated object.
     * @returns {Function} Returns `accumulator`.
     */
    function baseAggregator(collection, setter, iteratee, accumulator) {
      baseEach(collection, function(value, key, collection) {
        setter(accumulator, value, iteratee(value), collection);
      });
      return accumulator;
    }

    /**
     * The base implementation of `_.assign` without support for multiple sources
     * or `customizer` functions.
     *
     * @private
     * @param {Object} object The destination object.
     * @param {Object} source The source object.
     * @returns {Object} Returns `object`.
     */
    function baseAssign(object, source) {
      return object && copyObject(source, keys(source), object);
    }

    /**
     * The base implementation of `_.assignIn` without support for multiple sources
     * or `customizer` functions.
     *
     * @private
     * @param {Object} object The destination object.
     * @param {Object} source The source object.
     * @returns {Object} Returns `object`.
     */
    function baseAssignIn(object, source) {
      return object && copyObject(source, keysIn(source), object);
    }

    /**
     * The base implementation of `assignValue` and `assignMergeValue` without
     * value checks.
     *
     * @private
     * @param {Object} object The object to modify.
     * @param {string} key The key of the property to assign.
     * @param {*} value The value to assign.
     */
    function baseAssignValue(object, key, value) {
      if (key == '__proto__' && defineProperty) {
        defineProperty(object, key, {
          'configurable': true,
          'enumerable': true,
          'value': value,
          'writable': true
        });
      } else {
        object[key] = value;
      }
    }

    /**
     * The base implementation of `_.at` without support for individual paths.
     *
     * @private
     * @param {Object} object The object to iterate over.
     * @param {string[]} paths The property paths to pick.
     * @returns {Array} Returns the picked elements.
     */
    function baseAt(object, paths) {
      var index = -1,
          length = paths.length,
          result = Array(length),
          skip = object == null;

      while (++index < length) {
        result[index] = skip ? undefined : get(object, paths[index]);
      }
      return result;
    }

    /**
     * The base implementation of `_.clamp` which doesn't coerce arguments.
     *
     * @private
     * @param {number} number The number to clamp.
     * @param {number} [lower] The lower bound.
     * @param {number} upper The upper bound.
     * @returns {number} Returns the clamped number.
     */
    function baseClamp(number, lower, upper) {
      if (number === number) {
        if (upper !== undefined) {
          number = number <= upper ? number : upper;
        }
        if (lower !== undefined) {
          number = number >= lower ? number : lower;
        }
      }
      return number;
    }

    /**
     * The base implementation of `_.clone` and `_.cloneDeep` which tracks
     * traversed objects.
     *
     * @private
     * @param {*} value The value to clone.
     * @param {boolean} bitmask The bitmask flags.
     *  1 - Deep clone
     *  2 - Flatten inherited properties
     *  4 - Clone symbols
     * @param {Function} [customizer] The function to customize cloning.
     * @param {string} [key] The key of `value`.
     * @param {Object} [object] The parent object of `value`.
     * @param {Object} [stack] Tracks traversed objects and their clone counterparts.
     * @returns {*} Returns the cloned value.
     */
    function baseClone(value, bitmask, customizer, key, object, stack) {
      var result,
          isDeep = bitmask & CLONE_DEEP_FLAG,
          isFlat = bitmask & CLONE_FLAT_FLAG,
          isFull = bitmask & CLONE_SYMBOLS_FLAG;

      if (customizer) {
        result = object ? customizer(value, key, object, stack) : customizer(value);
      }
      if (result !== undefined) {
        return result;
      }
      if (!isObject(value)) {
        return value;
      }
      var isArr = isArray(value);
      if (isArr) {
        result = initCloneArray(value);
        if (!isDeep) {
          return copyArray(value, result);
        }
      } else {
        var tag = getTag(value),
            isFunc = tag == funcTag || tag == genTag;

        if (isBuffer(value)) {
          return cloneBuffer(value, isDeep);
        }
        if (tag == objectTag || tag == argsTag || (isFunc && !object)) {
          result = (isFlat || isFunc) ? {} : initCloneObject(value);
          if (!isDeep) {
            return isFlat
              ? copySymbolsIn(value, baseAssignIn(result, value))
              : copySymbols(value, baseAssign(result, value));
          }
        } else {
          if (!cloneableTags[tag]) {
            return object ? value : {};
          }
          result = initCloneByTag(value, tag, isDeep);
        }
      }
      // Check for circular references and return its corresponding clone.
      stack || (stack = new Stack);
      var stacked = stack.get(value);
      if (stacked) {
        return stacked;
      }
      stack.set(value, result);

      if (isSet(value)) {
        value.forEach(function(subValue) {
          result.add(baseClone(subValue, bitmask, customizer, subValue, value, stack));
        });
      } else if (isMap(value)) {
        value.forEach(function(subValue, key) {
          result.set(key, baseClone(subValue, bitmask, customizer, key, value, stack));
        });
      }

      var keysFunc = isFull
        ? (isFlat ? getAllKeysIn : getAllKeys)
        : (isFlat ? keysIn : keys);

      var props = isArr ? undefined : keysFunc(value);
      arrayEach(props || value, function(subValue, key) {
        if (props) {
          key = subValue;
          subValue = value[key];
        }
        // Recursively populate clone (susceptible to call stack limits).
        assignValue(result, key, baseClone(subValue, bitmask, customizer, key, value, stack));
      });
      return result;
    }

    /**
     * The base implementation of `_.conforms` which doesn't clone `source`.
     *
     * @private
     * @param {Object} source The object of property predicates to conform to.
     * @returns {Function} Returns the new spec function.
     */
    function baseConforms(source) {
      var props = keys(source);
      return function(object) {
        return baseConformsTo(object, source, props);
      };
    }

    /**
     * The base implementation of `_.conformsTo` which accepts `props` to check.
     *
     * @private
     * @param {Object} object The object to inspect.
     * @param {Object} source The object of property predicates to conform to.
     * @returns {boolean} Returns `true` if `object` conforms, else `false`.
     */
    function baseConformsTo(object, source, props) {
      var length = props.length;
      if (object == null) {
        return !length;
      }
      object = Object(object);
      while (length--) {
        var key = props[length],
            predicate = source[key],
            value = object[key];

        if ((value === undefined && !(key in object)) || !predicate(value)) {
          return false;
        }
      }
      return true;
    }

    /**
     * The base implementation of `_.delay` and `_.defer` which accepts `args`
     * to provide to `func`.
     *
     * @private
     * @param {Function} func The function to delay.
     * @param {number} wait The number of milliseconds to delay invocation.
     * @param {Array} args The arguments to provide to `func`.
     * @returns {number|Object} Returns the timer id or timeout object.
     */
    function baseDelay(func, wait, args) {
      if (typeof func != 'function') {
        throw new TypeError(FUNC_ERROR_TEXT);
      }
      return setTimeout(function() { func.apply(undefined, args); }, wait);
    }

    /**
     * The base implementation of methods like `_.difference` without support
     * for excluding multiple arrays or iteratee shorthands.
     *
     * @private
     * @param {Array} array The array to inspect.
     * @param {Array} values The values to exclude.
     * @param {Function} [iteratee] The iteratee invoked per element.
     * @param {Function} [comparator] The comparator invoked per element.
     * @returns {Array} Returns the new array of filtered values.
     */
    function baseDifference(array, values, iteratee, comparator) {
      var index = -1,
          includes = arrayIncludes,
          isCommon = true,
          length = array.length,
          result = [],
          valuesLength = values.length;

      if (!length) {
        return result;
      }
      if (iteratee) {
        values = arrayMap(values, baseUnary(iteratee));
      }
      if (comparator) {
        includes = arrayIncludesWith;
        isCommon = false;
      }
      else if (values.length >= LARGE_ARRAY_SIZE) {
        includes = cacheHas;
        isCommon = false;
        values = new SetCache(values);
      }
      outer:
      while (++index < length) {
        var value = array[index],
            computed = iteratee == null ? value : iteratee(value);

        value = (comparator || value !== 0) ? value : 0;
        if (isCommon && computed === computed) {
          var valuesIndex = valuesLength;
          while (valuesIndex--) {
            if (values[valuesIndex] === computed) {
              continue outer;
            }
          }
          result.push(value);
        }
        else if (!includes(values, computed, comparator)) {
          result.push(value);
        }
      }
      return result;
    }

    /**
     * The base implementation of `_.forEach` without support for iteratee shorthands.
     *
     * @private
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Function} iteratee The function invoked per iteration.
     * @returns {Array|Object} Returns `collection`.
     */
    var baseEach = createBaseEach(baseForOwn);

    /**
     * The base implementation of `_.forEachRight` without support for iteratee shorthands.
     *
     * @private
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Function} iteratee The function invoked per iteration.
     * @returns {Array|Object} Returns `collection`.
     */
    var baseEachRight = createBaseEach(baseForOwnRight, true);

    /**
     * The base implementation of `_.every` without support for iteratee shorthands.
     *
     * @private
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Function} predicate The function invoked per iteration.
     * @returns {boolean} Returns `true` if all elements pass the predicate check,
     *  else `false`
     */
    function baseEvery(collection, predicate) {
      var result = true;
      baseEach(collection, function(value, index, collection) {
        result = !!predicate(value, index, collection);
        return result;
      });
      return result;
    }

    /**
     * The base implementation of methods like `_.max` and `_.min` which accepts a
     * `comparator` to determine the extremum value.
     *
     * @private
     * @param {Array} array The array to iterate over.
     * @param {Function} iteratee The iteratee invoked per iteration.
     * @param {Function} comparator The comparator used to compare values.
     * @returns {*} Returns the extremum value.
     */
    function baseExtremum(array, iteratee, comparator) {
      var index = -1,
          length = array.length;

      while (++index < length) {
        var value = array[index],
            current = iteratee(value);

        if (current != null && (computed === undefined
              ? (current === current && !isSymbol(current))
              : comparator(current, computed)
            )) {
          var computed = current,
              result = value;
        }
      }
      return result;
    }

    /**
     * The base implementation of `_.fill` without an iteratee call guard.
     *
     * @private
     * @param {Array} array The array to fill.
     * @param {*} value The value to fill `array` with.
     * @param {number} [start=0] The start position.
     * @param {number} [end=array.length] The end position.
     * @returns {Array} Returns `array`.
     */
    function baseFill(array, value, start, end) {
      var length = array.length;

      start = toInteger(start);
      if (start < 0) {
        start = -start > length ? 0 : (length + start);
      }
      end = (end === undefined || end > length) ? length : toInteger(end);
      if (end < 0) {
        end += length;
      }
      end = start > end ? 0 : toLength(end);
      while (start < end) {
        array[start++] = value;
      }
      return array;
    }

    /**
     * The base implementation of `_.filter` without support for iteratee shorthands.
     *
     * @private
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Function} predicate The function invoked per iteration.
     * @returns {Array} Returns the new filtered array.
     */
    function baseFilter(collection, predicate) {
      var result = [];
      baseEach(collection, function(value, index, collection) {
        if (predicate(value, index, collection)) {
          result.push(value);
        }
      });
      return result;
    }

    /**
     * The base implementation of `_.flatten` with support for restricting flattening.
     *
     * @private
     * @param {Array} array The array to flatten.
     * @param {number} depth The maximum recursion depth.
     * @param {boolean} [predicate=isFlattenable] The function invoked per iteration.
     * @param {boolean} [isStrict] Restrict to values that pass `predicate` checks.
     * @param {Array} [result=[]] The initial result value.
     * @returns {Array} Returns the new flattened array.
     */
    function baseFlatten(array, depth, predicate, isStrict, result) {
      var index = -1,
          length = array.length;

      predicate || (predicate = isFlattenable);
      result || (result = []);

      while (++index < length) {
        var value = array[index];
        if (depth > 0 && predicate(value)) {
          if (depth > 1) {
            // Recursively flatten arrays (susceptible to call stack limits).
            baseFlatten(value, depth - 1, predicate, isStrict, result);
          } else {
            arrayPush(result, value);
          }
        } else if (!isStrict) {
          result[result.length] = value;
        }
      }
      return result;
    }

    /**
     * The base implementation of `baseForOwn` which iterates over `object`
     * properties returned by `keysFunc` and invokes `iteratee` for each property.
     * Iteratee functions may exit iteration early by explicitly returning `false`.
     *
     * @private
     * @param {Object} object The object to iterate over.
     * @param {Function} iteratee The function invoked per iteration.
     * @param {Function} keysFunc The function to get the keys of `object`.
     * @returns {Object} Returns `object`.
     */
    var baseFor = createBaseFor();

    /**
     * This function is like `baseFor` except that it iterates over properties
     * in the opposite order.
     *
     * @private
     * @param {Object} object The object to iterate over.
     * @param {Function} iteratee The function invoked per iteration.
     * @param {Function} keysFunc The function to get the keys of `object`.
     * @returns {Object} Returns `object`.
     */
    var baseForRight = createBaseFor(true);

    /**
     * The base implementation of `_.forOwn` without support for iteratee shorthands.
     *
     * @private
     * @param {Object} object The object to iterate over.
     * @param {Function} iteratee The function invoked per iteration.
     * @returns {Object} Returns `object`.
     */
    function baseForOwn(object, iteratee) {
      return object && baseFor(object, iteratee, keys);
    }

    /**
     * The base implementation of `_.forOwnRight` without support for iteratee shorthands.
     *
     * @private
     * @param {Object} object The object to iterate over.
     * @param {Function} iteratee The function invoked per iteration.
     * @returns {Object} Returns `object`.
     */
    function baseForOwnRight(object, iteratee) {
      return object && baseForRight(object, iteratee, keys);
    }

    /**
     * The base implementation of `_.functions` which creates an array of
     * `object` function property names filtered from `props`.
     *
     * @private
     * @param {Object} object The object to inspect.
     * @param {Array} props The property names to filter.
     * @returns {Array} Returns the function names.
     */
    function baseFunctions(object, props) {
      return arrayFilter(props, function(key) {
        return isFunction(object[key]);
      });
    }

    /**
     * The base implementation of `_.get` without support for default values.
     *
     * @private
     * @param {Object} object The object to query.
     * @param {Array|string} path The path of the property to get.
     * @returns {*} Returns the resolved value.
     */
    function baseGet(object, path) {
      path = castPath(path, object);

      var index = 0,
          length = path.length;

      while (object != null && index < length) {
        object = object[toKey(path[index++])];
      }
      return (index && index == length) ? object : undefined;
    }

    /**
     * The base implementation of `getAllKeys` and `getAllKeysIn` which uses
     * `keysFunc` and `symbolsFunc` to get the enumerable property names and
     * symbols of `object`.
     *
     * @private
     * @param {Object} object The object to query.
     * @param {Function} keysFunc The function to get the keys of `object`.
     * @param {Function} symbolsFunc The function to get the symbols of `object`.
     * @returns {Array} Returns the array of property names and symbols.
     */
    function baseGetAllKeys(object, keysFunc, symbolsFunc) {
      var result = keysFunc(object);
      return isArray(object) ? result : arrayPush(result, symbolsFunc(object));
    }

    /**
     * The base implementation of `getTag` without fallbacks for buggy environments.
     *
     * @private
     * @param {*} value The value to query.
     * @returns {string} Returns the `toStringTag`.
     */
    function baseGetTag(value) {
      if (value == null) {
        return value === undefined ? undefinedTag : nullTag;
      }
      return (symToStringTag && symToStringTag in Object(value))
        ? getRawTag(value)
        : objectToString(value);
    }

    /**
     * The base implementation of `_.gt` which doesn't coerce arguments.
     *
     * @private
     * @param {*} value The value to compare.
     * @param {*} other The other value to compare.
     * @returns {boolean} Returns `true` if `value` is greater than `other`,
     *  else `false`.
     */
    function baseGt(value, other) {
      return value > other;
    }

    /**
     * The base implementation of `_.has` without support for deep paths.
     *
     * @private
     * @param {Object} [object] The object to query.
     * @param {Array|string} key The key to check.
     * @returns {boolean} Returns `true` if `key` exists, else `false`.
     */
    function baseHas(object, key) {
      return object != null && hasOwnProperty.call(object, key);
    }

    /**
     * The base implementation of `_.hasIn` without support for deep paths.
     *
     * @private
     * @param {Object} [object] The object to query.
     * @param {Array|string} key The key to check.
     * @returns {boolean} Returns `true` if `key` exists, else `false`.
     */
    function baseHasIn(object, key) {
      return object != null && key in Object(object);
    }

    /**
     * The base implementation of `_.inRange` which doesn't coerce arguments.
     *
     * @private
     * @param {number} number The number to check.
     * @param {number} start The start of the range.
     * @param {number} end The end of the range.
     * @returns {boolean} Returns `true` if `number` is in the range, else `false`.
     */
    function baseInRange(number, start, end) {
      return number >= nativeMin(start, end) && number < nativeMax(start, end);
    }

    /**
     * The base implementation of methods like `_.intersection`, without support
     * for iteratee shorthands, that accepts an array of arrays to inspect.
     *
     * @private
     * @param {Array} arrays The arrays to inspect.
     * @param {Function} [iteratee] The iteratee invoked per element.
     * @param {Function} [comparator] The comparator invoked per element.
     * @returns {Array} Returns the new array of shared values.
     */
    function baseIntersection(arrays, iteratee, comparator) {
      var includes = comparator ? arrayIncludesWith : arrayIncludes,
          length = arrays[0].length,
          othLength = arrays.length,
          othIndex = othLength,
          caches = Array(othLength),
          maxLength = Infinity,
          result = [];

      while (othIndex--) {
        var array = arrays[othIndex];
        if (othIndex && iteratee) {
          array = arrayMap(array, baseUnary(iteratee));
        }
        maxLength = nativeMin(array.length, maxLength);
        caches[othIndex] = !comparator && (iteratee || (length >= 120 && array.length >= 120))
          ? new SetCache(othIndex && array)
          : undefined;
      }
      array = arrays[0];

      var index = -1,
          seen = caches[0];

      outer:
      while (++index < length && result.length < maxLength) {
        var value = array[index],
            computed = iteratee ? iteratee(value) : value;

        value = (comparator || value !== 0) ? value : 0;
        if (!(seen
              ? cacheHas(seen, computed)
              : includes(result, computed, comparator)
            )) {
          othIndex = othLength;
          while (--othIndex) {
            var cache = caches[othIndex];
            if (!(cache
                  ? cacheHas(cache, computed)
                  : includes(arrays[othIndex], computed, comparator))
                ) {
              continue outer;
            }
          }
          if (seen) {
            seen.push(computed);
          }
          result.push(value);
        }
      }
      return result;
    }

    /**
     * The base implementation of `_.invert` and `_.invertBy` which inverts
     * `object` with values transformed by `iteratee` and set by `setter`.
     *
     * @private
     * @param {Object} object The object to iterate over.
     * @param {Function} setter The function to set `accumulator` values.
     * @param {Function} iteratee The iteratee to transform values.
     * @param {Object} accumulator The initial inverted object.
     * @returns {Function} Returns `accumulator`.
     */
    function baseInverter(object, setter, iteratee, accumulator) {
      baseForOwn(object, function(value, key, object) {
        setter(accumulator, iteratee(value), key, object);
      });
      return accumulator;
    }

    /**
     * The base implementation of `_.invoke` without support for individual
     * method arguments.
     *
     * @private
     * @param {Object} object The object to query.
     * @param {Array|string} path The path of the method to invoke.
     * @param {Array} args The arguments to invoke the method with.
     * @returns {*} Returns the result of the invoked method.
     */
    function baseInvoke(object, path, args) {
      path = castPath(path, object);
      object = parent(object, path);
      var func = object == null ? object : object[toKey(last(path))];
      return func == null ? undefined : apply(func, object, args);
    }

    /**
     * The base implementation of `_.isArguments`.
     *
     * @private
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is an `arguments` object,
     */
    function baseIsArguments(value) {
      return isObjectLike(value) && baseGetTag(value) == argsTag;
    }

    /**
     * The base implementation of `_.isArrayBuffer` without Node.js optimizations.
     *
     * @private
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is an array buffer, else `false`.
     */
    function baseIsArrayBuffer(value) {
      return isObjectLike(value) && baseGetTag(value) == arrayBufferTag;
    }

    /**
     * The base implementation of `_.isDate` without Node.js optimizations.
     *
     * @private
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is a date object, else `false`.
     */
    function baseIsDate(value) {
      return isObjectLike(value) && baseGetTag(value) == dateTag;
    }

    /**
     * The base implementation of `_.isEqual` which supports partial comparisons
     * and tracks traversed objects.
     *
     * @private
     * @param {*} value The value to compare.
     * @param {*} other The other value to compare.
     * @param {boolean} bitmask The bitmask flags.
     *  1 - Unordered comparison
     *  2 - Partial comparison
     * @param {Function} [customizer] The function to customize comparisons.
     * @param {Object} [stack] Tracks traversed `value` and `other` objects.
     * @returns {boolean} Returns `true` if the values are equivalent, else `false`.
     */
    function baseIsEqual(value, other, bitmask, customizer, stack) {
      if (value === other) {
        return true;
      }
      if (value == null || other == null || (!isObjectLike(value) && !isObjectLike(other))) {
        return value !== value && other !== other;
      }
      return baseIsEqualDeep(value, other, bitmask, customizer, baseIsEqual, stack);
    }

    /**
     * A specialized version of `baseIsEqual` for arrays and objects which performs
     * deep comparisons and tracks traversed objects enabling objects with circular
     * references to be compared.
     *
     * @private
     * @param {Object} object The object to compare.
     * @param {Object} other The other object to compare.
     * @param {number} bitmask The bitmask flags. See `baseIsEqual` for more details.
     * @param {Function} customizer The function to customize comparisons.
     * @param {Function} equalFunc The function to determine equivalents of values.
     * @param {Object} [stack] Tracks traversed `object` and `other` objects.
     * @returns {boolean} Returns `true` if the objects are equivalent, else `false`.
     */
    function baseIsEqualDeep(object, other, bitmask, customizer, equalFunc, stack) {
      var objIsArr = isArray(object),
          othIsArr = isArray(other),
          objTag = objIsArr ? arrayTag : getTag(object),
          othTag = othIsArr ? arrayTag : getTag(other);

      objTag = objTag == argsTag ? objectTag : objTag;
      othTag = othTag == argsTag ? objectTag : othTag;

      var objIsObj = objTag == objectTag,
          othIsObj = othTag == objectTag,
          isSameTag = objTag == othTag;

      if (isSameTag && isBuffer(object)) {
        if (!isBuffer(other)) {
          return false;
        }
        objIsArr = true;
        objIsObj = false;
      }
      if (isSameTag && !objIsObj) {
        stack || (stack = new Stack);
        return (objIsArr || isTypedArray(object))
          ? equalArrays(object, other, bitmask, customizer, equalFunc, stack)
          : equalByTag(object, other, objTag, bitmask, customizer, equalFunc, stack);
      }
      if (!(bitmask & COMPARE_PARTIAL_FLAG)) {
        var objIsWrapped = objIsObj && hasOwnProperty.call(object, '__wrapped__'),
            othIsWrapped = othIsObj && hasOwnProperty.call(other, '__wrapped__');

        if (objIsWrapped || othIsWrapped) {
          var objUnwrapped = objIsWrapped ? object.value() : object,
              othUnwrapped = othIsWrapped ? other.value() : other;

          stack || (stack = new Stack);
          return equalFunc(objUnwrapped, othUnwrapped, bitmask, customizer, stack);
        }
      }
      if (!isSameTag) {
        return false;
      }
      stack || (stack = new Stack);
      return equalObjects(object, other, bitmask, customizer, equalFunc, stack);
    }

    /**
     * The base implementation of `_.isMap` without Node.js optimizations.
     *
     * @private
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is a map, else `false`.
     */
    function baseIsMap(value) {
      return isObjectLike(value) && getTag(value) == mapTag;
    }

    /**
     * The base implementation of `_.isMatch` without support for iteratee shorthands.
     *
     * @private
     * @param {Object} object The object to inspect.
     * @param {Object} source The object of property values to match.
     * @param {Array} matchData The property names, values, and compare flags to match.
     * @param {Function} [customizer] The function to customize comparisons.
     * @returns {boolean} Returns `true` if `object` is a match, else `false`.
     */
    function baseIsMatch(object, source, matchData, customizer) {
      var index = matchData.length,
          length = index,
          noCustomizer = !customizer;

      if (object == null) {
        return !length;
      }
      object = Object(object);
      while (index--) {
        var data = matchData[index];
        if ((noCustomizer && data[2])
              ? data[1] !== object[data[0]]
              : !(data[0] in object)
            ) {
          return false;
        }
      }
      while (++index < length) {
        data = matchData[index];
        var key = data[0],
            objValue = object[key],
            srcValue = data[1];

        if (noCustomizer && data[2]) {
          if (objValue === undefined && !(key in object)) {
            return false;
          }
        } else {
          var stack = new Stack;
          if (customizer) {
            var result = customizer(objValue, srcValue, key, object, source, stack);
          }
          if (!(result === undefined
                ? baseIsEqual(srcValue, objValue, COMPARE_PARTIAL_FLAG | COMPARE_UNORDERED_FLAG, customizer, stack)
                : result
              )) {
            return false;
          }
        }
      }
      return true;
    }

    /**
     * The base implementation of `_.isNative` without bad shim checks.
     *
     * @private
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is a native function,
     *  else `false`.
     */
    function baseIsNative(value) {
      if (!isObject(value) || isMasked(value)) {
        return false;
      }
      var pattern = isFunction(value) ? reIsNative : reIsHostCtor;
      return pattern.test(toSource(value));
    }

    /**
     * The base implementation of `_.isRegExp` without Node.js optimizations.
     *
     * @private
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is a regexp, else `false`.
     */
    function baseIsRegExp(value) {
      return isObjectLike(value) && baseGetTag(value) == regexpTag;
    }

    /**
     * The base implementation of `_.isSet` without Node.js optimizations.
     *
     * @private
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is a set, else `false`.
     */
    function baseIsSet(value) {
      return isObjectLike(value) && getTag(value) == setTag;
    }

    /**
     * The base implementation of `_.isTypedArray` without Node.js optimizations.
     *
     * @private
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is a typed array, else `false`.
     */
    function baseIsTypedArray(value) {
      return isObjectLike(value) &&
        isLength(value.length) && !!typedArrayTags[baseGetTag(value)];
    }

    /**
     * The base implementation of `_.iteratee`.
     *
     * @private
     * @param {*} [value=_.identity] The value to convert to an iteratee.
     * @returns {Function} Returns the iteratee.
     */
    function baseIteratee(value) {
      // Don't store the `typeof` result in a variable to avoid a JIT bug in Safari 9.
      // See https://bugs.webkit.org/show_bug.cgi?id=156034 for more details.
      if (typeof value == 'function') {
        return value;
      }
      if (value == null) {
        return identity;
      }
      if (typeof value == 'object') {
        return isArray(value)
          ? baseMatchesProperty(value[0], value[1])
          : baseMatches(value);
      }
      return property(value);
    }

    /**
     * The base implementation of `_.keys` which doesn't treat sparse arrays as dense.
     *
     * @private
     * @param {Object} object The object to query.
     * @returns {Array} Returns the array of property names.
     */
    function baseKeys(object) {
      if (!isPrototype(object)) {
        return nativeKeys(object);
      }
      var result = [];
      for (var key in Object(object)) {
        if (hasOwnProperty.call(object, key) && key != 'constructor') {
          result.push(key);
        }
      }
      return result;
    }

    /**
     * The base implementation of `_.keysIn` which doesn't treat sparse arrays as dense.
     *
     * @private
     * @param {Object} object The object to query.
     * @returns {Array} Returns the array of property names.
     */
    function baseKeysIn(object) {
      if (!isObject(object)) {
        return nativeKeysIn(object);
      }
      var isProto = isPrototype(object),
          result = [];

      for (var key in object) {
        if (!(key == 'constructor' && (isProto || !hasOwnProperty.call(object, key)))) {
          result.push(key);
        }
      }
      return result;
    }

    /**
     * The base implementation of `_.lt` which doesn't coerce arguments.
     *
     * @private
     * @param {*} value The value to compare.
     * @param {*} other The other value to compare.
     * @returns {boolean} Returns `true` if `value` is less than `other`,
     *  else `false`.
     */
    function baseLt(value, other) {
      return value < other;
    }

    /**
     * The base implementation of `_.map` without support for iteratee shorthands.
     *
     * @private
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Function} iteratee The function invoked per iteration.
     * @returns {Array} Returns the new mapped array.
     */
    function baseMap(collection, iteratee) {
      var index = -1,
          result = isArrayLike(collection) ? Array(collection.length) : [];

      baseEach(collection, function(value, key, collection) {
        result[++index] = iteratee(value, key, collection);
      });
      return result;
    }

    /**
     * The base implementation of `_.matches` which doesn't clone `source`.
     *
     * @private
     * @param {Object} source The object of property values to match.
     * @returns {Function} Returns the new spec function.
     */
    function baseMatches(source) {
      var matchData = getMatchData(source);
      if (matchData.length == 1 && matchData[0][2]) {
        return matchesStrictComparable(matchData[0][0], matchData[0][1]);
      }
      return function(object) {
        return object === source || baseIsMatch(object, source, matchData);
      };
    }

    /**
     * The base implementation of `_.matchesProperty` which doesn't clone `srcValue`.
     *
     * @private
     * @param {string} path The path of the property to get.
     * @param {*} srcValue The value to match.
     * @returns {Function} Returns the new spec function.
     */
    function baseMatchesProperty(path, srcValue) {
      if (isKey(path) && isStrictComparable(srcValue)) {
        return matchesStrictComparable(toKey(path), srcValue);
      }
      return function(object) {
        var objValue = get(object, path);
        return (objValue === undefined && objValue === srcValue)
          ? hasIn(object, path)
          : baseIsEqual(srcValue, objValue, COMPARE_PARTIAL_FLAG | COMPARE_UNORDERED_FLAG);
      };
    }

    /**
     * The base implementation of `_.merge` without support for multiple sources.
     *
     * @private
     * @param {Object} object The destination object.
     * @param {Object} source The source object.
     * @param {number} srcIndex The index of `source`.
     * @param {Function} [customizer] The function to customize merged values.
     * @param {Object} [stack] Tracks traversed source values and their merged
     *  counterparts.
     */
    function baseMerge(object, source, srcIndex, customizer, stack) {
      if (object === source) {
        return;
      }
      baseFor(source, function(srcValue, key) {
        stack || (stack = new Stack);
        if (isObject(srcValue)) {
          baseMergeDeep(object, source, key, srcIndex, baseMerge, customizer, stack);
        }
        else {
          var newValue = customizer
            ? customizer(safeGet(object, key), srcValue, (key + ''), object, source, stack)
            : undefined;

          if (newValue === undefined) {
            newValue = srcValue;
          }
          assignMergeValue(object, key, newValue);
        }
      }, keysIn);
    }

    /**
     * A specialized version of `baseMerge` for arrays and objects which performs
     * deep merges and tracks traversed objects enabling objects with circular
     * references to be merged.
     *
     * @private
     * @param {Object} object The destination object.
     * @param {Object} source The source object.
     * @param {string} key The key of the value to merge.
     * @param {number} srcIndex The index of `source`.
     * @param {Function} mergeFunc The function to merge values.
     * @param {Function} [customizer] The function to customize assigned values.
     * @param {Object} [stack] Tracks traversed source values and their merged
     *  counterparts.
     */
    function baseMergeDeep(object, source, key, srcIndex, mergeFunc, customizer, stack) {
      var objValue = safeGet(object, key),
          srcValue = safeGet(source, key),
          stacked = stack.get(srcValue);

      if (stacked) {
        assignMergeValue(object, key, stacked);
        return;
      }
      var newValue = customizer
        ? customizer(objValue, srcValue, (key + ''), object, source, stack)
        : undefined;

      var isCommon = newValue === undefined;

      if (isCommon) {
        var isArr = isArray(srcValue),
            isBuff = !isArr && isBuffer(srcValue),
            isTyped = !isArr && !isBuff && isTypedArray(srcValue);

        newValue = srcValue;
        if (isArr || isBuff || isTyped) {
          if (isArray(objValue)) {
            newValue = objValue;
          }
          else if (isArrayLikeObject(objValue)) {
            newValue = copyArray(objValue);
          }
          else if (isBuff) {
            isCommon = false;
            newValue = cloneBuffer(srcValue, true);
          }
          else if (isTyped) {
            isCommon = false;
            newValue = cloneTypedArray(srcValue, true);
          }
          else {
            newValue = [];
          }
        }
        else if (isPlainObject(srcValue) || isArguments(srcValue)) {
          newValue = objValue;
          if (isArguments(objValue)) {
            newValue = toPlainObject(objValue);
          }
          else if (!isObject(objValue) || isFunction(objValue)) {
            newValue = initCloneObject(srcValue);
          }
        }
        else {
          isCommon = false;
        }
      }
      if (isCommon) {
        // Recursively merge objects and arrays (susceptible to call stack limits).
        stack.set(srcValue, newValue);
        mergeFunc(newValue, srcValue, srcIndex, customizer, stack);
        stack['delete'](srcValue);
      }
      assignMergeValue(object, key, newValue);
    }

    /**
     * The base implementation of `_.nth` which doesn't coerce arguments.
     *
     * @private
     * @param {Array} array The array to query.
     * @param {number} n The index of the element to return.
     * @returns {*} Returns the nth element of `array`.
     */
    function baseNth(array, n) {
      var length = array.length;
      if (!length) {
        return;
      }
      n += n < 0 ? length : 0;
      return isIndex(n, length) ? array[n] : undefined;
    }

    /**
     * The base implementation of `_.orderBy` without param guards.
     *
     * @private
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Function[]|Object[]|string[]} iteratees The iteratees to sort by.
     * @param {string[]} orders The sort orders of `iteratees`.
     * @returns {Array} Returns the new sorted array.
     */
    function baseOrderBy(collection, iteratees, orders) {
      if (iteratees.length) {
        iteratees = arrayMap(iteratees, function(iteratee) {
          if (isArray(iteratee)) {
            return function(value) {
              return baseGet(value, iteratee.length === 1 ? iteratee[0] : iteratee);
            }
          }
          return iteratee;
        });
      } else {
        iteratees = [identity];
      }

      var index = -1;
      iteratees = arrayMap(iteratees, baseUnary(getIteratee()));

      var result = baseMap(collection, function(value, key, collection) {
        var criteria = arrayMap(iteratees, function(iteratee) {
          return iteratee(value);
        });
        return { 'criteria': criteria, 'index': ++index, 'value': value };
      });

      return baseSortBy(result, function(object, other) {
        return compareMultiple(object, other, orders);
      });
    }

    /**
     * The base implementation of `_.pick` without support for individual
     * property identifiers.
     *
     * @private
     * @param {Object} object The source object.
     * @param {string[]} paths The property paths to pick.
     * @returns {Object} Returns the new object.
     */
    function basePick(object, paths) {
      return basePickBy(object, paths, function(value, path) {
        return hasIn(object, path);
      });
    }

    /**
     * The base implementation of  `_.pickBy` without support for iteratee shorthands.
     *
     * @private
     * @param {Object} object The source object.
     * @param {string[]} paths The property paths to pick.
     * @param {Function} predicate The function invoked per property.
     * @returns {Object} Returns the new object.
     */
    function basePickBy(object, paths, predicate) {
      var index = -1,
          length = paths.length,
          result = {};

      while (++index < length) {
        var path = paths[index],
            value = baseGet(object, path);

        if (predicate(value, path)) {
          baseSet(result, castPath(path, object), value);
        }
      }
      return result;
    }

    /**
     * A specialized version of `baseProperty` which supports deep paths.
     *
     * @private
     * @param {Array|string} path The path of the property to get.
     * @returns {Function} Returns the new accessor function.
     */
    function basePropertyDeep(path) {
      return function(object) {
        return baseGet(object, path);
      };
    }

    /**
     * The base implementation of `_.pullAllBy` without support for iteratee
     * shorthands.
     *
     * @private
     * @param {Array} array The array to modify.
     * @param {Array} values The values to remove.
     * @param {Function} [iteratee] The iteratee invoked per element.
     * @param {Function} [comparator] The comparator invoked per element.
     * @returns {Array} Returns `array`.
     */
    function basePullAll(array, values, iteratee, comparator) {
      var indexOf = comparator ? baseIndexOfWith : baseIndexOf,
          index = -1,
          length = values.length,
          seen = array;

      if (array === values) {
        values = copyArray(values);
      }
      if (iteratee) {
        seen = arrayMap(array, baseUnary(iteratee));
      }
      while (++index < length) {
        var fromIndex = 0,
            value = values[index],
            computed = iteratee ? iteratee(value) : value;

        while ((fromIndex = indexOf(seen, computed, fromIndex, comparator)) > -1) {
          if (seen !== array) {
            splice.call(seen, fromIndex, 1);
          }
          splice.call(array, fromIndex, 1);
        }
      }
      return array;
    }

    /**
     * The base implementation of `_.pullAt` without support for individual
     * indexes or capturing the removed elements.
     *
     * @private
     * @param {Array} array The array to modify.
     * @param {number[]} indexes The indexes of elements to remove.
     * @returns {Array} Returns `array`.
     */
    function basePullAt(array, indexes) {
      var length = array ? indexes.length : 0,
          lastIndex = length - 1;

      while (length--) {
        var index = indexes[length];
        if (length == lastIndex || index !== previous) {
          var previous = index;
          if (isIndex(index)) {
            splice.call(array, index, 1);
          } else {
            baseUnset(array, index);
          }
        }
      }
      return array;
    }

    /**
     * The base implementation of `_.random` without support for returning
     * floating-point numbers.
     *
     * @private
     * @param {number} lower The lower bound.
     * @param {number} upper The upper bound.
     * @returns {number} Returns the random number.
     */
    function baseRandom(lower, upper) {
      return lower + nativeFloor(nativeRandom() * (upper - lower + 1));
    }

    /**
     * The base implementation of `_.range` and `_.rangeRight` which doesn't
     * coerce arguments.
     *
     * @private
     * @param {number} start The start of the range.
     * @param {number} end The end of the range.
     * @param {number} step The value to increment or decrement by.
     * @param {boolean} [fromRight] Specify iterating from right to left.
     * @returns {Array} Returns the range of numbers.
     */
    function baseRange(start, end, step, fromRight) {
      var index = -1,
          length = nativeMax(nativeCeil((end - start) / (step || 1)), 0),
          result = Array(length);

      while (length--) {
        result[fromRight ? length : ++index] = start;
        start += step;
      }
      return result;
    }

    /**
     * The base implementation of `_.repeat` which doesn't coerce arguments.
     *
     * @private
     * @param {string} string The string to repeat.
     * @param {number} n The number of times to repeat the string.
     * @returns {string} Returns the repeated string.
     */
    function baseRepeat(string, n) {
      var result = '';
      if (!string || n < 1 || n > MAX_SAFE_INTEGER) {
        return result;
      }
      // Leverage the exponentiation by squaring algorithm for a faster repeat.
      // See https://en.wikipedia.org/wiki/Exponentiation_by_squaring for more details.
      do {
        if (n % 2) {
          result += string;
        }
        n = nativeFloor(n / 2);
        if (n) {
          string += string;
        }
      } while (n);

      return result;
    }

    /**
     * The base implementation of `_.rest` which doesn't validate or coerce arguments.
     *
     * @private
     * @param {Function} func The function to apply a rest parameter to.
     * @param {number} [start=func.length-1] The start position of the rest parameter.
     * @returns {Function} Returns the new function.
     */
    function baseRest(func, start) {
      return setToString(overRest(func, start, identity), func + '');
    }

    /**
     * The base implementation of `_.sample`.
     *
     * @private
     * @param {Array|Object} collection The collection to sample.
     * @returns {*} Returns the random element.
     */
    function baseSample(collection) {
      return arraySample(values(collection));
    }

    /**
     * The base implementation of `_.sampleSize` without param guards.
     *
     * @private
     * @param {Array|Object} collection The collection to sample.
     * @param {number} n The number of elements to sample.
     * @returns {Array} Returns the random elements.
     */
    function baseSampleSize(collection, n) {
      var array = values(collection);
      return shuffleSelf(array, baseClamp(n, 0, array.length));
    }

    /**
     * The base implementation of `_.set`.
     *
     * @private
     * @param {Object} object The object to modify.
     * @param {Array|string} path The path of the property to set.
     * @param {*} value The value to set.
     * @param {Function} [customizer] The function to customize path creation.
     * @returns {Object} Returns `object`.
     */
    function baseSet(object, path, value, customizer) {
      if (!isObject(object)) {
        return object;
      }
      path = castPath(path, object);

      var index = -1,
          length = path.length,
          lastIndex = length - 1,
          nested = object;

      while (nested != null && ++index < length) {
        var key = toKey(path[index]),
            newValue = value;

        if (key === '__proto__' || key === 'constructor' || key === 'prototype') {
          return object;
        }

        if (index != lastIndex) {
          var objValue = nested[key];
          newValue = customizer ? customizer(objValue, key, nested) : undefined;
          if (newValue === undefined) {
            newValue = isObject(objValue)
              ? objValue
              : (isIndex(path[index + 1]) ? [] : {});
          }
        }
        assignValue(nested, key, newValue);
        nested = nested[key];
      }
      return object;
    }

    /**
     * The base implementation of `setData` without support for hot loop shorting.
     *
     * @private
     * @param {Function} func The function to associate metadata with.
     * @param {*} data The metadata.
     * @returns {Function} Returns `func`.
     */
    var baseSetData = !metaMap ? identity : function(func, data) {
      metaMap.set(func, data);
      return func;
    };

    /**
     * The base implementation of `setToString` without support for hot loop shorting.
     *
     * @private
     * @param {Function} func The function to modify.
     * @param {Function} string The `toString` result.
     * @returns {Function} Returns `func`.
     */
    var baseSetToString = !defineProperty ? identity : function(func, string) {
      return defineProperty(func, 'toString', {
        'configurable': true,
        'enumerable': false,
        'value': constant(string),
        'writable': true
      });
    };

    /**
     * The base implementation of `_.shuffle`.
     *
     * @private
     * @param {Array|Object} collection The collection to shuffle.
     * @returns {Array} Returns the new shuffled array.
     */
    function baseShuffle(collection) {
      return shuffleSelf(values(collection));
    }

    /**
     * The base implementation of `_.slice` without an iteratee call guard.
     *
     * @private
     * @param {Array} array The array to slice.
     * @param {number} [start=0] The start position.
     * @param {number} [end=array.length] The end position.
     * @returns {Array} Returns the slice of `array`.
     */
    function baseSlice(array, start, end) {
      var index = -1,
          length = array.length;

      if (start < 0) {
        start = -start > length ? 0 : (length + start);
      }
      end = end > length ? length : end;
      if (end < 0) {
        end += length;
      }
      length = start > end ? 0 : ((end - start) >>> 0);
      start >>>= 0;

      var result = Array(length);
      while (++index < length) {
        result[index] = array[index + start];
      }
      return result;
    }

    /**
     * The base implementation of `_.some` without support for iteratee shorthands.
     *
     * @private
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Function} predicate The function invoked per iteration.
     * @returns {boolean} Returns `true` if any element passes the predicate check,
     *  else `false`.
     */
    function baseSome(collection, predicate) {
      var result;

      baseEach(collection, function(value, index, collection) {
        result = predicate(value, index, collection);
        return !result;
      });
      return !!result;
    }

    /**
     * The base implementation of `_.sortedIndex` and `_.sortedLastIndex` which
     * performs a binary search of `array` to determine the index at which `value`
     * should be inserted into `array` in order to maintain its sort order.
     *
     * @private
     * @param {Array} array The sorted array to inspect.
     * @param {*} value The value to evaluate.
     * @param {boolean} [retHighest] Specify returning the highest qualified index.
     * @returns {number} Returns the index at which `value` should be inserted
     *  into `array`.
     */
    function baseSortedIndex(array, value, retHighest) {
      var low = 0,
          high = array == null ? low : array.length;

      if (typeof value == 'number' && value === value && high <= HALF_MAX_ARRAY_LENGTH) {
        while (low < high) {
          var mid = (low + high) >>> 1,
              computed = array[mid];

          if (computed !== null && !isSymbol(computed) &&
              (retHighest ? (computed <= value) : (computed < value))) {
            low = mid + 1;
          } else {
            high = mid;
          }
        }
        return high;
      }
      return baseSortedIndexBy(array, value, identity, retHighest);
    }

    /**
     * The base implementation of `_.sortedIndexBy` and `_.sortedLastIndexBy`
     * which invokes `iteratee` for `value` and each element of `array` to compute
     * their sort ranking. The iteratee is invoked with one argument; (value).
     *
     * @private
     * @param {Array} array The sorted array to inspect.
     * @param {*} value The value to evaluate.
     * @param {Function} iteratee The iteratee invoked per element.
     * @param {boolean} [retHighest] Specify returning the highest qualified index.
     * @returns {number} Returns the index at which `value` should be inserted
     *  into `array`.
     */
    function baseSortedIndexBy(array, value, iteratee, retHighest) {
      var low = 0,
          high = array == null ? 0 : array.length;
      if (high === 0) {
        return 0;
      }

      value = iteratee(value);
      var valIsNaN = value !== value,
          valIsNull = value === null,
          valIsSymbol = isSymbol(value),
          valIsUndefined = value === undefined;

      while (low < high) {
        var mid = nativeFloor((low + high) / 2),
            computed = iteratee(array[mid]),
            othIsDefined = computed !== undefined,
            othIsNull = computed === null,
            othIsReflexive = computed === computed,
            othIsSymbol = isSymbol(computed);

        if (valIsNaN) {
          var setLow = retHighest || othIsReflexive;
        } else if (valIsUndefined) {
          setLow = othIsReflexive && (retHighest || othIsDefined);
        } else if (valIsNull) {
          setLow = othIsReflexive && othIsDefined && (retHighest || !othIsNull);
        } else if (valIsSymbol) {
          setLow = othIsReflexive && othIsDefined && !othIsNull && (retHighest || !othIsSymbol);
        } else if (othIsNull || othIsSymbol) {
          setLow = false;
        } else {
          setLow = retHighest ? (computed <= value) : (computed < value);
        }
        if (setLow) {
          low = mid + 1;
        } else {
          high = mid;
        }
      }
      return nativeMin(high, MAX_ARRAY_INDEX);
    }

    /**
     * The base implementation of `_.sortedUniq` and `_.sortedUniqBy` without
     * support for iteratee shorthands.
     *
     * @private
     * @param {Array} array The array to inspect.
     * @param {Function} [iteratee] The iteratee invoked per element.
     * @returns {Array} Returns the new duplicate free array.
     */
    function baseSortedUniq(array, iteratee) {
      var index = -1,
          length = array.length,
          resIndex = 0,
          result = [];

      while (++index < length) {
        var value = array[index],
            computed = iteratee ? iteratee(value) : value;

        if (!index || !eq(computed, seen)) {
          var seen = computed;
          result[resIndex++] = value === 0 ? 0 : value;
        }
      }
      return result;
    }

    /**
     * The base implementation of `_.toNumber` which doesn't ensure correct
     * conversions of binary, hexadecimal, or octal string values.
     *
     * @private
     * @param {*} value The value to process.
     * @returns {number} Returns the number.
     */
    function baseToNumber(value) {
      if (typeof value == 'number') {
        return value;
      }
      if (isSymbol(value)) {
        return NAN;
      }
      return +value;
    }

    /**
     * The base implementation of `_.toString` which doesn't convert nullish
     * values to empty strings.
     *
     * @private
     * @param {*} value The value to process.
     * @returns {string} Returns the string.
     */
    function baseToString(value) {
      // Exit early for strings to avoid a performance hit in some environments.
      if (typeof value == 'string') {
        return value;
      }
      if (isArray(value)) {
        // Recursively convert values (susceptible to call stack limits).
        return arrayMap(value, baseToString) + '';
      }
      if (isSymbol(value)) {
        return symbolToString ? symbolToString.call(value) : '';
      }
      var result = (value + '');
      return (result == '0' && (1 / value) == -INFINITY) ? '-0' : result;
    }

    /**
     * The base implementation of `_.uniqBy` without support for iteratee shorthands.
     *
     * @private
     * @param {Array} array The array to inspect.
     * @param {Function} [iteratee] The iteratee invoked per element.
     * @param {Function} [comparator] The comparator invoked per element.
     * @returns {Array} Returns the new duplicate free array.
     */
    function baseUniq(array, iteratee, comparator) {
      var index = -1,
          includes = arrayIncludes,
          length = array.length,
          isCommon = true,
          result = [],
          seen = result;

      if (comparator) {
        isCommon = false;
        includes = arrayIncludesWith;
      }
      else if (length >= LARGE_ARRAY_SIZE) {
        var set = iteratee ? null : createSet(array);
        if (set) {
          return setToArray(set);
        }
        isCommon = false;
        includes = cacheHas;
        seen = new SetCache;
      }
      else {
        seen = iteratee ? [] : result;
      }
      outer:
      while (++index < length) {
        var value = array[index],
            computed = iteratee ? iteratee(value) : value;

        value = (comparator || value !== 0) ? value : 0;
        if (isCommon && computed === computed) {
          var seenIndex = seen.length;
          while (seenIndex--) {
            if (seen[seenIndex] === computed) {
              continue outer;
            }
          }
          if (iteratee) {
            seen.push(computed);
          }
          result.push(value);
        }
        else if (!includes(seen, computed, comparator)) {
          if (seen !== result) {
            seen.push(computed);
          }
          result.push(value);
        }
      }
      return result;
    }

    /**
     * The base implementation of `_.unset`.
     *
     * @private
     * @param {Object} object The object to modify.
     * @param {Array|string} path The property path to unset.
     * @returns {boolean} Returns `true` if the property is deleted, else `false`.
     */
    function baseUnset(object, path) {
      path = castPath(path, object);
      object = parent(object, path);
      return object == null || delete object[toKey(last(path))];
    }

    /**
     * The base implementation of `_.update`.
     *
     * @private
     * @param {Object} object The object to modify.
     * @param {Array|string} path The path of the property to update.
     * @param {Function} updater The function to produce the updated value.
     * @param {Function} [customizer] The function to customize path creation.
     * @returns {Object} Returns `object`.
     */
    function baseUpdate(object, path, updater, customizer) {
      return baseSet(object, path, updater(baseGet(object, path)), customizer);
    }

    /**
     * The base implementation of methods like `_.dropWhile` and `_.takeWhile`
     * without support for iteratee shorthands.
     *
     * @private
     * @param {Array} array The array to query.
     * @param {Function} predicate The function invoked per iteration.
     * @param {boolean} [isDrop] Specify dropping elements instead of taking them.
     * @param {boolean} [fromRight] Specify iterating from right to left.
     * @returns {Array} Returns the slice of `array`.
     */
    function baseWhile(array, predicate, isDrop, fromRight) {
      var length = array.length,
          index = fromRight ? length : -1;

      while ((fromRight ? index-- : ++index < length) &&
        predicate(array[index], index, array)) {}

      return isDrop
        ? baseSlice(array, (fromRight ? 0 : index), (fromRight ? index + 1 : length))
        : baseSlice(array, (fromRight ? index + 1 : 0), (fromRight ? length : index));
    }

    /**
     * The base implementation of `wrapperValue` which returns the result of
     * performing a sequence of actions on the unwrapped `value`, where each
     * successive action is supplied the return value of the previous.
     *
     * @private
     * @param {*} value The unwrapped value.
     * @param {Array} actions Actions to perform to resolve the unwrapped value.
     * @returns {*} Returns the resolved value.
     */
    function baseWrapperValue(value, actions) {
      var result = value;
      if (result instanceof LazyWrapper) {
        result = result.value();
      }
      return arrayReduce(actions, function(result, action) {
        return action.func.apply(action.thisArg, arrayPush([result], action.args));
      }, result);
    }

    /**
     * The base implementation of methods like `_.xor`, without support for
     * iteratee shorthands, that accepts an array of arrays to inspect.
     *
     * @private
     * @param {Array} arrays The arrays to inspect.
     * @param {Function} [iteratee] The iteratee invoked per element.
     * @param {Function} [comparator] The comparator invoked per element.
     * @returns {Array} Returns the new array of values.
     */
    function baseXor(arrays, iteratee, comparator) {
      var length = arrays.length;
      if (length < 2) {
        return length ? baseUniq(arrays[0]) : [];
      }
      var index = -1,
          result = Array(length);

      while (++index < length) {
        var array = arrays[index],
            othIndex = -1;

        while (++othIndex < length) {
          if (othIndex != index) {
            result[index] = baseDifference(result[index] || array, arrays[othIndex], iteratee, comparator);
          }
        }
      }
      return baseUniq(baseFlatten(result, 1), iteratee, comparator);
    }

    /**
     * This base implementation of `_.zipObject` which assigns values using `assignFunc`.
     *
     * @private
     * @param {Array} props The property identifiers.
     * @param {Array} values The property values.
     * @param {Function} assignFunc The function to assign values.
     * @returns {Object} Returns the new object.
     */
    function baseZipObject(props, values, assignFunc) {
      var index = -1,
          length = props.length,
          valsLength = values.length,
          result = {};

      while (++index < length) {
        var value = index < valsLength ? values[index] : undefined;
        assignFunc(result, props[index], value);
      }
      return result;
    }

    /**
     * Casts `value` to an empty array if it's not an array like object.
     *
     * @private
     * @param {*} value The value to inspect.
     * @returns {Array|Object} Returns the cast array-like object.
     */
    function castArrayLikeObject(value) {
      return isArrayLikeObject(value) ? value : [];
    }

    /**
     * Casts `value` to `identity` if it's not a function.
     *
     * @private
     * @param {*} value The value to inspect.
     * @returns {Function} Returns cast function.
     */
    function castFunction(value) {
      return typeof value == 'function' ? value : identity;
    }

    /**
     * Casts `value` to a path array if it's not one.
     *
     * @private
     * @param {*} value The value to inspect.
     * @param {Object} [object] The object to query keys on.
     * @returns {Array} Returns the cast property path array.
     */
    function castPath(value, object) {
      if (isArray(value)) {
        return value;
      }
      return isKey(value, object) ? [value] : stringToPath(toString(value));
    }

    /**
     * A `baseRest` alias which can be replaced with `identity` by module
     * replacement plugins.
     *
     * @private
     * @type {Function}
     * @param {Function} func The function to apply a rest parameter to.
     * @returns {Function} Returns the new function.
     */
    var castRest = baseRest;

    /**
     * Casts `array` to a slice if it's needed.
     *
     * @private
     * @param {Array} array The array to inspect.
     * @param {number} start The start position.
     * @param {number} [end=array.length] The end position.
     * @returns {Array} Returns the cast slice.
     */
    function castSlice(array, start, end) {
      var length = array.length;
      end = end === undefined ? length : end;
      return (!start && end >= length) ? array : baseSlice(array, start, end);
    }

    /**
     * A simple wrapper around the global [`clearTimeout`](https://mdn.io/clearTimeout).
     *
     * @private
     * @param {number|Object} id The timer id or timeout object of the timer to clear.
     */
    var clearTimeout = ctxClearTimeout || function(id) {
      return root.clearTimeout(id);
    };

    /**
     * Creates a clone of  `buffer`.
     *
     * @private
     * @param {Buffer} buffer The buffer to clone.
     * @param {boolean} [isDeep] Specify a deep clone.
     * @returns {Buffer} Returns the cloned buffer.
     */
    function cloneBuffer(buffer, isDeep) {
      if (isDeep) {
        return buffer.slice();
      }
      var length = buffer.length,
          result = allocUnsafe ? allocUnsafe(length) : new buffer.constructor(length);

      buffer.copy(result);
      return result;
    }

    /**
     * Creates a clone of `arrayBuffer`.
     *
     * @private
     * @param {ArrayBuffer} arrayBuffer The array buffer to clone.
     * @returns {ArrayBuffer} Returns the cloned array buffer.
     */
    function cloneArrayBuffer(arrayBuffer) {
      var result = new arrayBuffer.constructor(arrayBuffer.byteLength);
      new Uint8Array(result).set(new Uint8Array(arrayBuffer));
      return result;
    }

    /**
     * Creates a clone of `dataView`.
     *
     * @private
     * @param {Object} dataView The data view to clone.
     * @param {boolean} [isDeep] Specify a deep clone.
     * @returns {Object} Returns the cloned data view.
     */
    function cloneDataView(dataView, isDeep) {
      var buffer = isDeep ? cloneArrayBuffer(dataView.buffer) : dataView.buffer;
      return new dataView.constructor(buffer, dataView.byteOffset, dataView.byteLength);
    }

    /**
     * Creates a clone of `regexp`.
     *
     * @private
     * @param {Object} regexp The regexp to clone.
     * @returns {Object} Returns the cloned regexp.
     */
    function cloneRegExp(regexp) {
      var result = new regexp.constructor(regexp.source, reFlags.exec(regexp));
      result.lastIndex = regexp.lastIndex;
      return result;
    }

    /**
     * Creates a clone of the `symbol` object.
     *
     * @private
     * @param {Object} symbol The symbol object to clone.
     * @returns {Object} Returns the cloned symbol object.
     */
    function cloneSymbol(symbol) {
      return symbolValueOf ? Object(symbolValueOf.call(symbol)) : {};
    }

    /**
     * Creates a clone of `typedArray`.
     *
     * @private
     * @param {Object} typedArray The typed array to clone.
     * @param {boolean} [isDeep] Specify a deep clone.
     * @returns {Object} Returns the cloned typed array.
     */
    function cloneTypedArray(typedArray, isDeep) {
      var buffer = isDeep ? cloneArrayBuffer(typedArray.buffer) : typedArray.buffer;
      return new typedArray.constructor(buffer, typedArray.byteOffset, typedArray.length);
    }

    /**
     * Compares values to sort them in ascending order.
     *
     * @private
     * @param {*} value The value to compare.
     * @param {*} other The other value to compare.
     * @returns {number} Returns the sort order indicator for `value`.
     */
    function compareAscending(value, other) {
      if (value !== other) {
        var valIsDefined = value !== undefined,
            valIsNull = value === null,
            valIsReflexive = value === value,
            valIsSymbol = isSymbol(value);

        var othIsDefined = other !== undefined,
            othIsNull = other === null,
            othIsReflexive = other === other,
            othIsSymbol = isSymbol(other);

        if ((!othIsNull && !othIsSymbol && !valIsSymbol && value > other) ||
            (valIsSymbol && othIsDefined && othIsReflexive && !othIsNull && !othIsSymbol) ||
            (valIsNull && othIsDefined && othIsReflexive) ||
            (!valIsDefined && othIsReflexive) ||
            !valIsReflexive) {
          return 1;
        }
        if ((!valIsNull && !valIsSymbol && !othIsSymbol && value < other) ||
            (othIsSymbol && valIsDefined && valIsReflexive && !valIsNull && !valIsSymbol) ||
            (othIsNull && valIsDefined && valIsReflexive) ||
            (!othIsDefined && valIsReflexive) ||
            !othIsReflexive) {
          return -1;
        }
      }
      return 0;
    }

    /**
     * Used by `_.orderBy` to compare multiple properties of a value to another
     * and stable sort them.
     *
     * If `orders` is unspecified, all values are sorted in ascending order. Otherwise,
     * specify an order of "desc" for descending or "asc" for ascending sort order
     * of corresponding values.
     *
     * @private
     * @param {Object} object The object to compare.
     * @param {Object} other The other object to compare.
     * @param {boolean[]|string[]} orders The order to sort by for each property.
     * @returns {number} Returns the sort order indicator for `object`.
     */
    function compareMultiple(object, other, orders) {
      var index = -1,
          objCriteria = object.criteria,
          othCriteria = other.criteria,
          length = objCriteria.length,
          ordersLength = orders.length;

      while (++index < length) {
        var result = compareAscending(objCriteria[index], othCriteria[index]);
        if (result) {
          if (index >= ordersLength) {
            return result;
          }
          var order = orders[index];
          return result * (order == 'desc' ? -1 : 1);
        }
      }
      // Fixes an `Array#sort` bug in the JS engine embedded in Adobe applications
      // that causes it, under certain circumstances, to provide the same value for
      // `object` and `other`. See https://github.com/jashkenas/underscore/pull/1247
      // for more details.
      //
      // This also ensures a stable sort in V8 and other engines.
      // See https://bugs.chromium.org/p/v8/issues/detail?id=90 for more details.
      return object.index - other.index;
    }

    /**
     * Creates an array that is the composition of partially applied arguments,
     * placeholders, and provided arguments into a single array of arguments.
     *
     * @private
     * @param {Array} args The provided arguments.
     * @param {Array} partials The arguments to prepend to those provided.
     * @param {Array} holders The `partials` placeholder indexes.
     * @params {boolean} [isCurried] Specify composing for a curried function.
     * @returns {Array} Returns the new array of composed arguments.
     */
    function composeArgs(args, partials, holders, isCurried) {
      var argsIndex = -1,
          argsLength = args.length,
          holdersLength = holders.length,
          leftIndex = -1,
          leftLength = partials.length,
          rangeLength = nativeMax(argsLength - holdersLength, 0),
          result = Array(leftLength + rangeLength),
          isUncurried = !isCurried;

      while (++leftIndex < leftLength) {
        result[leftIndex] = partials[leftIndex];
      }
      while (++argsIndex < holdersLength) {
        if (isUncurried || argsIndex < argsLength) {
          result[holders[argsIndex]] = args[argsIndex];
        }
      }
      while (rangeLength--) {
        result[leftIndex++] = args[argsIndex++];
      }
      return result;
    }

    /**
     * This function is like `composeArgs` except that the arguments composition
     * is tailored for `_.partialRight`.
     *
     * @private
     * @param {Array} args The provided arguments.
     * @param {Array} partials The arguments to append to those provided.
     * @param {Array} holders The `partials` placeholder indexes.
     * @params {boolean} [isCurried] Specify composing for a curried function.
     * @returns {Array} Returns the new array of composed arguments.
     */
    function composeArgsRight(args, partials, holders, isCurried) {
      var argsIndex = -1,
          argsLength = args.length,
          holdersIndex = -1,
          holdersLength = holders.length,
          rightIndex = -1,
          rightLength = partials.length,
          rangeLength = nativeMax(argsLength - holdersLength, 0),
          result = Array(rangeLength + rightLength),
          isUncurried = !isCurried;

      while (++argsIndex < rangeLength) {
        result[argsIndex] = args[argsIndex];
      }
      var offset = argsIndex;
      while (++rightIndex < rightLength) {
        result[offset + rightIndex] = partials[rightIndex];
      }
      while (++holdersIndex < holdersLength) {
        if (isUncurried || argsIndex < argsLength) {
          result[offset + holders[holdersIndex]] = args[argsIndex++];
        }
      }
      return result;
    }

    /**
     * Copies the values of `source` to `array`.
     *
     * @private
     * @param {Array} source The array to copy values from.
     * @param {Array} [array=[]] The array to copy values to.
     * @returns {Array} Returns `array`.
     */
    function copyArray(source, array) {
      var index = -1,
          length = source.length;

      array || (array = Array(length));
      while (++index < length) {
        array[index] = source[index];
      }
      return array;
    }

    /**
     * Copies properties of `source` to `object`.
     *
     * @private
     * @param {Object} source The object to copy properties from.
     * @param {Array} props The property identifiers to copy.
     * @param {Object} [object={}] The object to copy properties to.
     * @param {Function} [customizer] The function to customize copied values.
     * @returns {Object} Returns `object`.
     */
    function copyObject(source, props, object, customizer) {
      var isNew = !object;
      object || (object = {});

      var index = -1,
          length = props.length;

      while (++index < length) {
        var key = props[index];

        var newValue = customizer
          ? customizer(object[key], source[key], key, object, source)
          : undefined;

        if (newValue === undefined) {
          newValue = source[key];
        }
        if (isNew) {
          baseAssignValue(object, key, newValue);
        } else {
          assignValue(object, key, newValue);
        }
      }
      return object;
    }

    /**
     * Copies own symbols of `source` to `object`.
     *
     * @private
     * @param {Object} source The object to copy symbols from.
     * @param {Object} [object={}] The object to copy symbols to.
     * @returns {Object} Returns `object`.
     */
    function copySymbols(source, object) {
      return copyObject(source, getSymbols(source), object);
    }

    /**
     * Copies own and inherited symbols of `source` to `object`.
     *
     * @private
     * @param {Object} source The object to copy symbols from.
     * @param {Object} [object={}] The object to copy symbols to.
     * @returns {Object} Returns `object`.
     */
    function copySymbolsIn(source, object) {
      return copyObject(source, getSymbolsIn(source), object);
    }

    /**
     * Creates a function like `_.groupBy`.
     *
     * @private
     * @param {Function} setter The function to set accumulator values.
     * @param {Function} [initializer] The accumulator object initializer.
     * @returns {Function} Returns the new aggregator function.
     */
    function createAggregator(setter, initializer) {
      return function(collection, iteratee) {
        var func = isArray(collection) ? arrayAggregator : baseAggregator,
            accumulator = initializer ? initializer() : {};

        return func(collection, setter, getIteratee(iteratee, 2), accumulator);
      };
    }

    /**
     * Creates a function like `_.assign`.
     *
     * @private
     * @param {Function} assigner The function to assign values.
     * @returns {Function} Returns the new assigner function.
     */
    function createAssigner(assigner) {
      return baseRest(function(object, sources) {
        var index = -1,
            length = sources.length,
            customizer = length > 1 ? sources[length - 1] : undefined,
            guard = length > 2 ? sources[2] : undefined;

        customizer = (assigner.length > 3 && typeof customizer == 'function')
          ? (length--, customizer)
          : undefined;

        if (guard && isIterateeCall(sources[0], sources[1], guard)) {
          customizer = length < 3 ? undefined : customizer;
          length = 1;
        }
        object = Object(object);
        while (++index < length) {
          var source = sources[index];
          if (source) {
            assigner(object, source, index, customizer);
          }
        }
        return object;
      });
    }

    /**
     * Creates a `baseEach` or `baseEachRight` function.
     *
     * @private
     * @param {Function} eachFunc The function to iterate over a collection.
     * @param {boolean} [fromRight] Specify iterating from right to left.
     * @returns {Function} Returns the new base function.
     */
    function createBaseEach(eachFunc, fromRight) {
      return function(collection, iteratee) {
        if (collection == null) {
          return collection;
        }
        if (!isArrayLike(collection)) {
          return eachFunc(collection, iteratee);
        }
        var length = collection.length,
            index = fromRight ? length : -1,
            iterable = Object(collection);

        while ((fromRight ? index-- : ++index < length)) {
          if (iteratee(iterable[index], index, iterable) === false) {
            break;
          }
        }
        return collection;
      };
    }

    /**
     * Creates a base function for methods like `_.forIn` and `_.forOwn`.
     *
     * @private
     * @param {boolean} [fromRight] Specify iterating from right to left.
     * @returns {Function} Returns the new base function.
     */
    function createBaseFor(fromRight) {
      return function(object, iteratee, keysFunc) {
        var index = -1,
            iterable = Object(object),
            props = keysFunc(object),
            length = props.length;

        while (length--) {
          var key = props[fromRight ? length : ++index];
          if (iteratee(iterable[key], key, iterable) === false) {
            break;
          }
        }
        return object;
      };
    }

    /**
     * Creates a function that wraps `func` to invoke it with the optional `this`
     * binding of `thisArg`.
     *
     * @private
     * @param {Function} func The function to wrap.
     * @param {number} bitmask The bitmask flags. See `createWrap` for more details.
     * @param {*} [thisArg] The `this` binding of `func`.
     * @returns {Function} Returns the new wrapped function.
     */
    function createBind(func, bitmask, thisArg) {
      var isBind = bitmask & WRAP_BIND_FLAG,
          Ctor = createCtor(func);

      function wrapper() {
        var fn = (this && this !== root && this instanceof wrapper) ? Ctor : func;
        return fn.apply(isBind ? thisArg : this, arguments);
      }
      return wrapper;
    }

    /**
     * Creates a function like `_.lowerFirst`.
     *
     * @private
     * @param {string} methodName The name of the `String` case method to use.
     * @returns {Function} Returns the new case function.
     */
    function createCaseFirst(methodName) {
      return function(string) {
        string = toString(string);

        var strSymbols = hasUnicode(string)
          ? stringToArray(string)
          : undefined;

        var chr = strSymbols
          ? strSymbols[0]
          : string.charAt(0);

        var trailing = strSymbols
          ? castSlice(strSymbols, 1).join('')
          : string.slice(1);

        return chr[methodName]() + trailing;
      };
    }

    /**
     * Creates a function like `_.camelCase`.
     *
     * @private
     * @param {Function} callback The function to combine each word.
     * @returns {Function} Returns the new compounder function.
     */
    function createCompounder(callback) {
      return function(string) {
        return arrayReduce(words(deburr(string).replace(reApos, '')), callback, '');
      };
    }

    /**
     * Creates a function that produces an instance of `Ctor` regardless of
     * whether it was invoked as part of a `new` expression or by `call` or `apply`.
     *
     * @private
     * @param {Function} Ctor The constructor to wrap.
     * @returns {Function} Returns the new wrapped function.
     */
    function createCtor(Ctor) {
      return function() {
        // Use a `switch` statement to work with class constructors. See
        // http://ecma-international.org/ecma-262/7.0/#sec-ecmascript-function-objects-call-thisargument-argumentslist
        // for more details.
        var args = arguments;
        switch (args.length) {
          case 0: return new Ctor;
          case 1: return new Ctor(args[0]);
          case 2: return new Ctor(args[0], args[1]);
          case 3: return new Ctor(args[0], args[1], args[2]);
          case 4: return new Ctor(args[0], args[1], args[2], args[3]);
          case 5: return new Ctor(args[0], args[1], args[2], args[3], args[4]);
          case 6: return new Ctor(args[0], args[1], args[2], args[3], args[4], args[5]);
          case 7: return new Ctor(args[0], args[1], args[2], args[3], args[4], args[5], args[6]);
        }
        var thisBinding = baseCreate(Ctor.prototype),
            result = Ctor.apply(thisBinding, args);

        // Mimic the constructor's `return` behavior.
        // See https://es5.github.io/#x13.2.2 for more details.
        return isObject(result) ? result : thisBinding;
      };
    }

    /**
     * Creates a function that wraps `func` to enable currying.
     *
     * @private
     * @param {Function} func The function to wrap.
     * @param {number} bitmask The bitmask flags. See `createWrap` for more details.
     * @param {number} arity The arity of `func`.
     * @returns {Function} Returns the new wrapped function.
     */
    function createCurry(func, bitmask, arity) {
      var Ctor = createCtor(func);

      function wrapper() {
        var length = arguments.length,
            args = Array(length),
            index = length,
            placeholder = getHolder(wrapper);

        while (index--) {
          args[index] = arguments[index];
        }
        var holders = (length < 3 && args[0] !== placeholder && args[length - 1] !== placeholder)
          ? []
          : replaceHolders(args, placeholder);

        length -= holders.length;
        if (length < arity) {
          return createRecurry(
            func, bitmask, createHybrid, wrapper.placeholder, undefined,
            args, holders, undefined, undefined, arity - length);
        }
        var fn = (this && this !== root && this instanceof wrapper) ? Ctor : func;
        return apply(fn, this, args);
      }
      return wrapper;
    }

    /**
     * Creates a `_.find` or `_.findLast` function.
     *
     * @private
     * @param {Function} findIndexFunc The function to find the collection index.
     * @returns {Function} Returns the new find function.
     */
    function createFind(findIndexFunc) {
      return function(collection, predicate, fromIndex) {
        var iterable = Object(collection);
        if (!isArrayLike(collection)) {
          var iteratee = getIteratee(predicate, 3);
          collection = keys(collection);
          predicate = function(key) { return iteratee(iterable[key], key, iterable); };
        }
        var index = findIndexFunc(collection, predicate, fromIndex);
        return index > -1 ? iterable[iteratee ? collection[index] : index] : undefined;
      };
    }

    /**
     * Creates a `_.flow` or `_.flowRight` function.
     *
     * @private
     * @param {boolean} [fromRight] Specify iterating from right to left.
     * @returns {Function} Returns the new flow function.
     */
    function createFlow(fromRight) {
      return flatRest(function(funcs) {
        var length = funcs.length,
            index = length,
            prereq = LodashWrapper.prototype.thru;

        if (fromRight) {
          funcs.reverse();
        }
        while (index--) {
          var func = funcs[index];
          if (typeof func != 'function') {
            throw new TypeError(FUNC_ERROR_TEXT);
          }
          if (prereq && !wrapper && getFuncName(func) == 'wrapper') {
            var wrapper = new LodashWrapper([], true);
          }
        }
        index = wrapper ? index : length;
        while (++index < length) {
          func = funcs[index];

          var funcName = getFuncName(func),
              data = funcName == 'wrapper' ? getData(func) : undefined;

          if (data && isLaziable(data[0]) &&
                data[1] == (WRAP_ARY_FLAG | WRAP_CURRY_FLAG | WRAP_PARTIAL_FLAG | WRAP_REARG_FLAG) &&
                !data[4].length && data[9] == 1
              ) {
            wrapper = wrapper[getFuncName(data[0])].apply(wrapper, data[3]);
          } else {
            wrapper = (func.length == 1 && isLaziable(func))
              ? wrapper[funcName]()
              : wrapper.thru(func);
          }
        }
        return function() {
          var args = arguments,
              value = args[0];

          if (wrapper && args.length == 1 && isArray(value)) {
            return wrapper.plant(value).value();
          }
          var index = 0,
              result = length ? funcs[index].apply(this, args) : value;

          while (++index < length) {
            result = funcs[index].call(this, result);
          }
          return result;
        };
      });
    }

    /**
     * Creates a function that wraps `func` to invoke it with optional `this`
     * binding of `thisArg`, partial application, and currying.
     *
     * @private
     * @param {Function|string} func The function or method name to wrap.
     * @param {number} bitmask The bitmask flags. See `createWrap` for more details.
     * @param {*} [thisArg] The `this` binding of `func`.
     * @param {Array} [partials] The arguments to prepend to those provided to
     *  the new function.
     * @param {Array} [holders] The `partials` placeholder indexes.
     * @param {Array} [partialsRight] The arguments to append to those provided
     *  to the new function.
     * @param {Array} [holdersRight] The `partialsRight` placeholder indexes.
     * @param {Array} [argPos] The argument positions of the new function.
     * @param {number} [ary] The arity cap of `func`.
     * @param {number} [arity] The arity of `func`.
     * @returns {Function} Returns the new wrapped function.
     */
    function createHybrid(func, bitmask, thisArg, partials, holders, partialsRight, holdersRight, argPos, ary, arity) {
      var isAry = bitmask & WRAP_ARY_FLAG,
          isBind = bitmask & WRAP_BIND_FLAG,
          isBindKey = bitmask & WRAP_BIND_KEY_FLAG,
          isCurried = bitmask & (WRAP_CURRY_FLAG | WRAP_CURRY_RIGHT_FLAG),
          isFlip = bitmask & WRAP_FLIP_FLAG,
          Ctor = isBindKey ? undefined : createCtor(func);

      function wrapper() {
        var length = arguments.length,
            args = Array(length),
            index = length;

        while (index--) {
          args[index] = arguments[index];
        }
        if (isCurried) {
          var placeholder = getHolder(wrapper),
              holdersCount = countHolders(args, placeholder);
        }
        if (partials) {
          args = composeArgs(args, partials, holders, isCurried);
        }
        if (partialsRight) {
          args = composeArgsRight(args, partialsRight, holdersRight, isCurried);
        }
        length -= holdersCount;
        if (isCurried && length < arity) {
          var newHolders = replaceHolders(args, placeholder);
          return createRecurry(
            func, bitmask, createHybrid, wrapper.placeholder, thisArg,
            args, newHolders, argPos, ary, arity - length
          );
        }
        var thisBinding = isBind ? thisArg : this,
            fn = isBindKey ? thisBinding[func] : func;

        length = args.length;
        if (argPos) {
          args = reorder(args, argPos);
        } else if (isFlip && length > 1) {
          args.reverse();
        }
        if (isAry && ary < length) {
          args.length = ary;
        }
        if (this && this !== root && this instanceof wrapper) {
          fn = Ctor || createCtor(fn);
        }
        return fn.apply(thisBinding, args);
      }
      return wrapper;
    }

    /**
     * Creates a function like `_.invertBy`.
     *
     * @private
     * @param {Function} setter The function to set accumulator values.
     * @param {Function} toIteratee The function to resolve iteratees.
     * @returns {Function} Returns the new inverter function.
     */
    function createInverter(setter, toIteratee) {
      return function(object, iteratee) {
        return baseInverter(object, setter, toIteratee(iteratee), {});
      };
    }

    /**
     * Creates a function that performs a mathematical operation on two values.
     *
     * @private
     * @param {Function} operator The function to perform the operation.
     * @param {number} [defaultValue] The value used for `undefined` arguments.
     * @returns {Function} Returns the new mathematical operation function.
     */
    function createMathOperation(operator, defaultValue) {
      return function(value, other) {
        var result;
        if (value === undefined && other === undefined) {
          return defaultValue;
        }
        if (value !== undefined) {
          result = value;
        }
        if (other !== undefined) {
          if (result === undefined) {
            return other;
          }
          if (typeof value == 'string' || typeof other == 'string') {
            value = baseToString(value);
            other = baseToString(other);
          } else {
            value = baseToNumber(value);
            other = baseToNumber(other);
          }
          result = operator(value, other);
        }
        return result;
      };
    }

    /**
     * Creates a function like `_.over`.
     *
     * @private
     * @param {Function} arrayFunc The function to iterate over iteratees.
     * @returns {Function} Returns the new over function.
     */
    function createOver(arrayFunc) {
      return flatRest(function(iteratees) {
        iteratees = arrayMap(iteratees, baseUnary(getIteratee()));
        return baseRest(function(args) {
          var thisArg = this;
          return arrayFunc(iteratees, function(iteratee) {
            return apply(iteratee, thisArg, args);
          });
        });
      });
    }

    /**
     * Creates the padding for `string` based on `length`. The `chars` string
     * is truncated if the number of characters exceeds `length`.
     *
     * @private
     * @param {number} length The padding length.
     * @param {string} [chars=' '] The string used as padding.
     * @returns {string} Returns the padding for `string`.
     */
    function createPadding(length, chars) {
      chars = chars === undefined ? ' ' : baseToString(chars);

      var charsLength = chars.length;
      if (charsLength < 2) {
        return charsLength ? baseRepeat(chars, length) : chars;
      }
      var result = baseRepeat(chars, nativeCeil(length / stringSize(chars)));
      return hasUnicode(chars)
        ? castSlice(stringToArray(result), 0, length).join('')
        : result.slice(0, length);
    }

    /**
     * Creates a function that wraps `func` to invoke it with the `this` binding
     * of `thisArg` and `partials` prepended to the arguments it receives.
     *
     * @private
     * @param {Function} func The function to wrap.
     * @param {number} bitmask The bitmask flags. See `createWrap` for more details.
     * @param {*} thisArg The `this` binding of `func`.
     * @param {Array} partials The arguments to prepend to those provided to
     *  the new function.
     * @returns {Function} Returns the new wrapped function.
     */
    function createPartial(func, bitmask, thisArg, partials) {
      var isBind = bitmask & WRAP_BIND_FLAG,
          Ctor = createCtor(func);

      function wrapper() {
        var argsIndex = -1,
            argsLength = arguments.length,
            leftIndex = -1,
            leftLength = partials.length,
            args = Array(leftLength + argsLength),
            fn = (this && this !== root && this instanceof wrapper) ? Ctor : func;

        while (++leftIndex < leftLength) {
          args[leftIndex] = partials[leftIndex];
        }
        while (argsLength--) {
          args[leftIndex++] = arguments[++argsIndex];
        }
        return apply(fn, isBind ? thisArg : this, args);
      }
      return wrapper;
    }

    /**
     * Creates a `_.range` or `_.rangeRight` function.
     *
     * @private
     * @param {boolean} [fromRight] Specify iterating from right to left.
     * @returns {Function} Returns the new range function.
     */
    function createRange(fromRight) {
      return function(start, end, step) {
        if (step && typeof step != 'number' && isIterateeCall(start, end, step)) {
          end = step = undefined;
        }
        // Ensure the sign of `-0` is preserved.
        start = toFinite(start);
        if (end === undefined) {
          end = start;
          start = 0;
        } else {
          end = toFinite(end);
        }
        step = step === undefined ? (start < end ? 1 : -1) : toFinite(step);
        return baseRange(start, end, step, fromRight);
      };
    }

    /**
     * Creates a function that performs a relational operation on two values.
     *
     * @private
     * @param {Function} operator The function to perform the operation.
     * @returns {Function} Returns the new relational operation function.
     */
    function createRelationalOperation(operator) {
      return function(value, other) {
        if (!(typeof value == 'string' && typeof other == 'string')) {
          value = toNumber(value);
          other = toNumber(other);
        }
        return operator(value, other);
      };
    }

    /**
     * Creates a function that wraps `func` to continue currying.
     *
     * @private
     * @param {Function} func The function to wrap.
     * @param {number} bitmask The bitmask flags. See `createWrap` for more details.
     * @param {Function} wrapFunc The function to create the `func` wrapper.
     * @param {*} placeholder The placeholder value.
     * @param {*} [thisArg] The `this` binding of `func`.
     * @param {Array} [partials] The arguments to prepend to those provided to
     *  the new function.
     * @param {Array} [holders] The `partials` placeholder indexes.
     * @param {Array} [argPos] The argument positions of the new function.
     * @param {number} [ary] The arity cap of `func`.
     * @param {number} [arity] The arity of `func`.
     * @returns {Function} Returns the new wrapped function.
     */
    function createRecurry(func, bitmask, wrapFunc, placeholder, thisArg, partials, holders, argPos, ary, arity) {
      var isCurry = bitmask & WRAP_CURRY_FLAG,
          newHolders = isCurry ? holders : undefined,
          newHoldersRight = isCurry ? undefined : holders,
          newPartials = isCurry ? partials : undefined,
          newPartialsRight = isCurry ? undefined : partials;

      bitmask |= (isCurry ? WRAP_PARTIAL_FLAG : WRAP_PARTIAL_RIGHT_FLAG);
      bitmask &= ~(isCurry ? WRAP_PARTIAL_RIGHT_FLAG : WRAP_PARTIAL_FLAG);

      if (!(bitmask & WRAP_CURRY_BOUND_FLAG)) {
        bitmask &= ~(WRAP_BIND_FLAG | WRAP_BIND_KEY_FLAG);
      }
      var newData = [
        func, bitmask, thisArg, newPartials, newHolders, newPartialsRight,
        newHoldersRight, argPos, ary, arity
      ];

      var result = wrapFunc.apply(undefined, newData);
      if (isLaziable(func)) {
        setData(result, newData);
      }
      result.placeholder = placeholder;
      return setWrapToString(result, func, bitmask);
    }

    /**
     * Creates a function like `_.round`.
     *
     * @private
     * @param {string} methodName The name of the `Math` method to use when rounding.
     * @returns {Function} Returns the new round function.
     */
    function createRound(methodName) {
      var func = Math[methodName];
      return function(number, precision) {
        number = toNumber(number);
        precision = precision == null ? 0 : nativeMin(toInteger(precision), 292);
        if (precision && nativeIsFinite(number)) {
          // Shift with exponential notation to avoid floating-point issues.
          // See [MDN](https://mdn.io/round#Examples) for more details.
          var pair = (toString(number) + 'e').split('e'),
              value = func(pair[0] + 'e' + (+pair[1] + precision));

          pair = (toString(value) + 'e').split('e');
          return +(pair[0] + 'e' + (+pair[1] - precision));
        }
        return func(number);
      };
    }

    /**
     * Creates a set object of `values`.
     *
     * @private
     * @param {Array} values The values to add to the set.
     * @returns {Object} Returns the new set.
     */
    var createSet = !(Set && (1 / setToArray(new Set([,-0]))[1]) == INFINITY) ? noop : function(values) {
      return new Set(values);
    };

    /**
     * Creates a `_.toPairs` or `_.toPairsIn` function.
     *
     * @private
     * @param {Function} keysFunc The function to get the keys of a given object.
     * @returns {Function} Returns the new pairs function.
     */
    function createToPairs(keysFunc) {
      return function(object) {
        var tag = getTag(object);
        if (tag == mapTag) {
          return mapToArray(object);
        }
        if (tag == setTag) {
          return setToPairs(object);
        }
        return baseToPairs(object, keysFunc(object));
      };
    }

    /**
     * Creates a function that either curries or invokes `func` with optional
     * `this` binding and partially applied arguments.
     *
     * @private
     * @param {Function|string} func The function or method name to wrap.
     * @param {number} bitmask The bitmask flags.
     *    1 - `_.bind`
     *    2 - `_.bindKey`
     *    4 - `_.curry` or `_.curryRight` of a bound function
     *    8 - `_.curry`
     *   16 - `_.curryRight`
     *   32 - `_.partial`
     *   64 - `_.partialRight`
     *  128 - `_.rearg`
     *  256 - `_.ary`
     *  512 - `_.flip`
     * @param {*} [thisArg] The `this` binding of `func`.
     * @param {Array} [partials] The arguments to be partially applied.
     * @param {Array} [holders] The `partials` placeholder indexes.
     * @param {Array} [argPos] The argument positions of the new function.
     * @param {number} [ary] The arity cap of `func`.
     * @param {number} [arity] The arity of `func`.
     * @returns {Function} Returns the new wrapped function.
     */
    function createWrap(func, bitmask, thisArg, partials, holders, argPos, ary, arity) {
      var isBindKey = bitmask & WRAP_BIND_KEY_FLAG;
      if (!isBindKey && typeof func != 'function') {
        throw new TypeError(FUNC_ERROR_TEXT);
      }
      var length = partials ? partials.length : 0;
      if (!length) {
        bitmask &= ~(WRAP_PARTIAL_FLAG | WRAP_PARTIAL_RIGHT_FLAG);
        partials = holders = undefined;
      }
      ary = ary === undefined ? ary : nativeMax(toInteger(ary), 0);
      arity = arity === undefined ? arity : toInteger(arity);
      length -= holders ? holders.length : 0;

      if (bitmask & WRAP_PARTIAL_RIGHT_FLAG) {
        var partialsRight = partials,
            holdersRight = holders;

        partials = holders = undefined;
      }
      var data = isBindKey ? undefined : getData(func);

      var newData = [
        func, bitmask, thisArg, partials, holders, partialsRight, holdersRight,
        argPos, ary, arity
      ];

      if (data) {
        mergeData(newData, data);
      }
      func = newData[0];
      bitmask = newData[1];
      thisArg = newData[2];
      partials = newData[3];
      holders = newData[4];
      arity = newData[9] = newData[9] === undefined
        ? (isBindKey ? 0 : func.length)
        : nativeMax(newData[9] - length, 0);

      if (!arity && bitmask & (WRAP_CURRY_FLAG | WRAP_CURRY_RIGHT_FLAG)) {
        bitmask &= ~(WRAP_CURRY_FLAG | WRAP_CURRY_RIGHT_FLAG);
      }
      if (!bitmask || bitmask == WRAP_BIND_FLAG) {
        var result = createBind(func, bitmask, thisArg);
      } else if (bitmask == WRAP_CURRY_FLAG || bitmask == WRAP_CURRY_RIGHT_FLAG) {
        result = createCurry(func, bitmask, arity);
      } else if ((bitmask == WRAP_PARTIAL_FLAG || bitmask == (WRAP_BIND_FLAG | WRAP_PARTIAL_FLAG)) && !holders.length) {
        result = createPartial(func, bitmask, thisArg, partials);
      } else {
        result = createHybrid.apply(undefined, newData);
      }
      var setter = data ? baseSetData : setData;
      return setWrapToString(setter(result, newData), func, bitmask);
    }

    /**
     * Used by `_.defaults` to customize its `_.assignIn` use to assign properties
     * of source objects to the destination object for all destination properties
     * that resolve to `undefined`.
     *
     * @private
     * @param {*} objValue The destination value.
     * @param {*} srcValue The source value.
     * @param {string} key The key of the property to assign.
     * @param {Object} object The parent object of `objValue`.
     * @returns {*} Returns the value to assign.
     */
    function customDefaultsAssignIn(objValue, srcValue, key, object) {
      if (objValue === undefined ||
          (eq(objValue, objectProto[key]) && !hasOwnProperty.call(object, key))) {
        return srcValue;
      }
      return objValue;
    }

    /**
     * Used by `_.defaultsDeep` to customize its `_.merge` use to merge source
     * objects into destination objects that are passed thru.
     *
     * @private
     * @param {*} objValue The destination value.
     * @param {*} srcValue The source value.
     * @param {string} key The key of the property to merge.
     * @param {Object} object The parent object of `objValue`.
     * @param {Object} source The parent object of `srcValue`.
     * @param {Object} [stack] Tracks traversed source values and their merged
     *  counterparts.
     * @returns {*} Returns the value to assign.
     */
    function customDefaultsMerge(objValue, srcValue, key, object, source, stack) {
      if (isObject(objValue) && isObject(srcValue)) {
        // Recursively merge objects and arrays (susceptible to call stack limits).
        stack.set(srcValue, objValue);
        baseMerge(objValue, srcValue, undefined, customDefaultsMerge, stack);
        stack['delete'](srcValue);
      }
      return objValue;
    }

    /**
     * Used by `_.omit` to customize its `_.cloneDeep` use to only clone plain
     * objects.
     *
     * @private
     * @param {*} value The value to inspect.
     * @param {string} key The key of the property to inspect.
     * @returns {*} Returns the uncloned value or `undefined` to defer cloning to `_.cloneDeep`.
     */
    function customOmitClone(value) {
      return isPlainObject(value) ? undefined : value;
    }

    /**
     * A specialized version of `baseIsEqualDeep` for arrays with support for
     * partial deep comparisons.
     *
     * @private
     * @param {Array} array The array to compare.
     * @param {Array} other The other array to compare.
     * @param {number} bitmask The bitmask flags. See `baseIsEqual` for more details.
     * @param {Function} customizer The function to customize comparisons.
     * @param {Function} equalFunc The function to determine equivalents of values.
     * @param {Object} stack Tracks traversed `array` and `other` objects.
     * @returns {boolean} Returns `true` if the arrays are equivalent, else `false`.
     */
    function equalArrays(array, other, bitmask, customizer, equalFunc, stack) {
      var isPartial = bitmask & COMPARE_PARTIAL_FLAG,
          arrLength = array.length,
          othLength = other.length;

      if (arrLength != othLength && !(isPartial && othLength > arrLength)) {
        return false;
      }
      // Check that cyclic values are equal.
      var arrStacked = stack.get(array);
      var othStacked = stack.get(other);
      if (arrStacked && othStacked) {
        return arrStacked == other && othStacked == array;
      }
      var index = -1,
          result = true,
          seen = (bitmask & COMPARE_UNORDERED_FLAG) ? new SetCache : undefined;

      stack.set(array, other);
      stack.set(other, array);

      // Ignore non-index properties.
      while (++index < arrLength) {
        var arrValue = array[index],
            othValue = other[index];

        if (customizer) {
          var compared = isPartial
            ? customizer(othValue, arrValue, index, other, array, stack)
            : customizer(arrValue, othValue, index, array, other, stack);
        }
        if (compared !== undefined) {
          if (compared) {
            continue;
          }
          result = false;
          break;
        }
        // Recursively compare arrays (susceptible to call stack limits).
        if (seen) {
          if (!arraySome(other, function(othValue, othIndex) {
                if (!cacheHas(seen, othIndex) &&
                    (arrValue === othValue || equalFunc(arrValue, othValue, bitmask, customizer, stack))) {
                  return seen.push(othIndex);
                }
              })) {
            result = false;
            break;
          }
        } else if (!(
              arrValue === othValue ||
                equalFunc(arrValue, othValue, bitmask, customizer, stack)
            )) {
          result = false;
          break;
        }
      }
      stack['delete'](array);
      stack['delete'](other);
      return result;
    }

    /**
     * A specialized version of `baseIsEqualDeep` for comparing objects of
     * the same `toStringTag`.
     *
     * **Note:** This function only supports comparing values with tags of
     * `Boolean`, `Date`, `Error`, `Number`, `RegExp`, or `String`.
     *
     * @private
     * @param {Object} object The object to compare.
     * @param {Object} other The other object to compare.
     * @param {string} tag The `toStringTag` of the objects to compare.
     * @param {number} bitmask The bitmask flags. See `baseIsEqual` for more details.
     * @param {Function} customizer The function to customize comparisons.
     * @param {Function} equalFunc The function to determine equivalents of values.
     * @param {Object} stack Tracks traversed `object` and `other` objects.
     * @returns {boolean} Returns `true` if the objects are equivalent, else `false`.
     */
    function equalByTag(object, other, tag, bitmask, customizer, equalFunc, stack) {
      switch (tag) {
        case dataViewTag:
          if ((object.byteLength != other.byteLength) ||
              (object.byteOffset != other.byteOffset)) {
            return false;
          }
          object = object.buffer;
          other = other.buffer;

        case arrayBufferTag:
          if ((object.byteLength != other.byteLength) ||
              !equalFunc(new Uint8Array(object), new Uint8Array(other))) {
            return false;
          }
          return true;

        case boolTag:
        case dateTag:
        case numberTag:
          // Coerce booleans to `1` or `0` and dates to milliseconds.
          // Invalid dates are coerced to `NaN`.
          return eq(+object, +other);

        case errorTag:
          return object.name == other.name && object.message == other.message;

        case regexpTag:
        case stringTag:
          // Coerce regexes to strings and treat strings, primitives and objects,
          // as equal. See http://www.ecma-international.org/ecma-262/7.0/#sec-regexp.prototype.tostring
          // for more details.
          return object == (other + '');

        case mapTag:
          var convert = mapToArray;

        case setTag:
          var isPartial = bitmask & COMPARE_PARTIAL_FLAG;
          convert || (convert = setToArray);

          if (object.size != other.size && !isPartial) {
            return false;
          }
          // Assume cyclic values are equal.
          var stacked = stack.get(object);
          if (stacked) {
            return stacked == other;
          }
          bitmask |= COMPARE_UNORDERED_FLAG;

          // Recursively compare objects (susceptible to call stack limits).
          stack.set(object, other);
          var result = equalArrays(convert(object), convert(other), bitmask, customizer, equalFunc, stack);
          stack['delete'](object);
          return result;

        case symbolTag:
          if (symbolValueOf) {
            return symbolValueOf.call(object) == symbolValueOf.call(other);
          }
      }
      return false;
    }

    /**
     * A specialized version of `baseIsEqualDeep` for objects with support for
     * partial deep comparisons.
     *
     * @private
     * @param {Object} object The object to compare.
     * @param {Object} other The other object to compare.
     * @param {number} bitmask The bitmask flags. See `baseIsEqual` for more details.
     * @param {Function} customizer The function to customize comparisons.
     * @param {Function} equalFunc The function to determine equivalents of values.
     * @param {Object} stack Tracks traversed `object` and `other` objects.
     * @returns {boolean} Returns `true` if the objects are equivalent, else `false`.
     */
    function equalObjects(object, other, bitmask, customizer, equalFunc, stack) {
      var isPartial = bitmask & COMPARE_PARTIAL_FLAG,
          objProps = getAllKeys(object),
          objLength = objProps.length,
          othProps = getAllKeys(other),
          othLength = othProps.length;

      if (objLength != othLength && !isPartial) {
        return false;
      }
      var index = objLength;
      while (index--) {
        var key = objProps[index];
        if (!(isPartial ? key in other : hasOwnProperty.call(other, key))) {
          return false;
        }
      }
      // Check that cyclic values are equal.
      var objStacked = stack.get(object);
      var othStacked = stack.get(other);
      if (objStacked && othStacked) {
        return objStacked == other && othStacked == object;
      }
      var result = true;
      stack.set(object, other);
      stack.set(other, object);

      var skipCtor = isPartial;
      while (++index < objLength) {
        key = objProps[index];
        var objValue = object[key],
            othValue = other[key];

        if (customizer) {
          var compared = isPartial
            ? customizer(othValue, objValue, key, other, object, stack)
            : customizer(objValue, othValue, key, object, other, stack);
        }
        // Recursively compare objects (susceptible to call stack limits).
        if (!(compared === undefined
              ? (objValue === othValue || equalFunc(objValue, othValue, bitmask, customizer, stack))
              : compared
            )) {
          result = false;
          break;
        }
        skipCtor || (skipCtor = key == 'constructor');
      }
      if (result && !skipCtor) {
        var objCtor = object.constructor,
            othCtor = other.constructor;

        // Non `Object` object instances with different constructors are not equal.
        if (objCtor != othCtor &&
            ('constructor' in object && 'constructor' in other) &&
            !(typeof objCtor == 'function' && objCtor instanceof objCtor &&
              typeof othCtor == 'function' && othCtor instanceof othCtor)) {
          result = false;
        }
      }
      stack['delete'](object);
      stack['delete'](other);
      return result;
    }

    /**
     * A specialized version of `baseRest` which flattens the rest array.
     *
     * @private
     * @param {Function} func The function to apply a rest parameter to.
     * @returns {Function} Returns the new function.
     */
    function flatRest(func) {
      return setToString(overRest(func, undefined, flatten), func + '');
    }

    /**
     * Creates an array of own enumerable property names and symbols of `object`.
     *
     * @private
     * @param {Object} object The object to query.
     * @returns {Array} Returns the array of property names and symbols.
     */
    function getAllKeys(object) {
      return baseGetAllKeys(object, keys, getSymbols);
    }

    /**
     * Creates an array of own and inherited enumerable property names and
     * symbols of `object`.
     *
     * @private
     * @param {Object} object The object to query.
     * @returns {Array} Returns the array of property names and symbols.
     */
    function getAllKeysIn(object) {
      return baseGetAllKeys(object, keysIn, getSymbolsIn);
    }

    /**
     * Gets metadata for `func`.
     *
     * @private
     * @param {Function} func The function to query.
     * @returns {*} Returns the metadata for `func`.
     */
    var getData = !metaMap ? noop : function(func) {
      return metaMap.get(func);
    };

    /**
     * Gets the name of `func`.
     *
     * @private
     * @param {Function} func The function to query.
     * @returns {string} Returns the function name.
     */
    function getFuncName(func) {
      var result = (func.name + ''),
          array = realNames[result],
          length = hasOwnProperty.call(realNames, result) ? array.length : 0;

      while (length--) {
        var data = array[length],
            otherFunc = data.func;
        if (otherFunc == null || otherFunc == func) {
          return data.name;
        }
      }
      return result;
    }

    /**
     * Gets the argument placeholder value for `func`.
     *
     * @private
     * @param {Function} func The function to inspect.
     * @returns {*} Returns the placeholder value.
     */
    function getHolder(func) {
      var object = hasOwnProperty.call(lodash, 'placeholder') ? lodash : func;
      return object.placeholder;
    }

    /**
     * Gets the appropriate "iteratee" function. If `_.iteratee` is customized,
     * this function returns the custom method, otherwise it returns `baseIteratee`.
     * If arguments are provided, the chosen function is invoked with them and
     * its result is returned.
     *
     * @private
     * @param {*} [value] The value to convert to an iteratee.
     * @param {number} [arity] The arity of the created iteratee.
     * @returns {Function} Returns the chosen function or its result.
     */
    function getIteratee() {
      var result = lodash.iteratee || iteratee;
      result = result === iteratee ? baseIteratee : result;
      return arguments.length ? result(arguments[0], arguments[1]) : result;
    }

    /**
     * Gets the data for `map`.
     *
     * @private
     * @param {Object} map The map to query.
     * @param {string} key The reference key.
     * @returns {*} Returns the map data.
     */
    function getMapData(map, key) {
      var data = map.__data__;
      return isKeyable(key)
        ? data[typeof key == 'string' ? 'string' : 'hash']
        : data.map;
    }

    /**
     * Gets the property names, values, and compare flags of `object`.
     *
     * @private
     * @param {Object} object The object to query.
     * @returns {Array} Returns the match data of `object`.
     */
    function getMatchData(object) {
      var result = keys(object),
          length = result.length;

      while (length--) {
        var key = result[length],
            value = object[key];

        result[length] = [key, value, isStrictComparable(value)];
      }
      return result;
    }

    /**
     * Gets the native function at `key` of `object`.
     *
     * @private
     * @param {Object} object The object to query.
     * @param {string} key The key of the method to get.
     * @returns {*} Returns the function if it's native, else `undefined`.
     */
    function getNative(object, key) {
      var value = getValue(object, key);
      return baseIsNative(value) ? value : undefined;
    }

    /**
     * A specialized version of `baseGetTag` which ignores `Symbol.toStringTag` values.
     *
     * @private
     * @param {*} value The value to query.
     * @returns {string} Returns the raw `toStringTag`.
     */
    function getRawTag(value) {
      var isOwn = hasOwnProperty.call(value, symToStringTag),
          tag = value[symToStringTag];

      try {
        value[symToStringTag] = undefined;
        var unmasked = true;
      } catch (e) {}

      var result = nativeObjectToString.call(value);
      if (unmasked) {
        if (isOwn) {
          value[symToStringTag] = tag;
        } else {
          delete value[symToStringTag];
        }
      }
      return result;
    }

    /**
     * Creates an array of the own enumerable symbols of `object`.
     *
     * @private
     * @param {Object} object The object to query.
     * @returns {Array} Returns the array of symbols.
     */
    var getSymbols = !nativeGetSymbols ? stubArray : function(object) {
      if (object == null) {
        return [];
      }
      object = Object(object);
      return arrayFilter(nativeGetSymbols(object), function(symbol) {
        return propertyIsEnumerable.call(object, symbol);
      });
    };

    /**
     * Creates an array of the own and inherited enumerable symbols of `object`.
     *
     * @private
     * @param {Object} object The object to query.
     * @returns {Array} Returns the array of symbols.
     */
    var getSymbolsIn = !nativeGetSymbols ? stubArray : function(object) {
      var result = [];
      while (object) {
        arrayPush(result, getSymbols(object));
        object = getPrototype(object);
      }
      return result;
    };

    /**
     * Gets the `toStringTag` of `value`.
     *
     * @private
     * @param {*} value The value to query.
     * @returns {string} Returns the `toStringTag`.
     */
    var getTag = baseGetTag;

    // Fallback for data views, maps, sets, and weak maps in IE 11 and promises in Node.js < 6.
    if ((DataView && getTag(new DataView(new ArrayBuffer(1))) != dataViewTag) ||
        (Map && getTag(new Map) != mapTag) ||
        (Promise && getTag(Promise.resolve()) != promiseTag) ||
        (Set && getTag(new Set) != setTag) ||
        (WeakMap && getTag(new WeakMap) != weakMapTag)) {
      getTag = function(value) {
        var result = baseGetTag(value),
            Ctor = result == objectTag ? value.constructor : undefined,
            ctorString = Ctor ? toSource(Ctor) : '';

        if (ctorString) {
          switch (ctorString) {
            case dataViewCtorString: return dataViewTag;
            case mapCtorString: return mapTag;
            case promiseCtorString: return promiseTag;
            case setCtorString: return setTag;
            case weakMapCtorString: return weakMapTag;
          }
        }
        return result;
      };
    }

    /**
     * Gets the view, applying any `transforms` to the `start` and `end` positions.
     *
     * @private
     * @param {number} start The start of the view.
     * @param {number} end The end of the view.
     * @param {Array} transforms The transformations to apply to the view.
     * @returns {Object} Returns an object containing the `start` and `end`
     *  positions of the view.
     */
    function getView(start, end, transforms) {
      var index = -1,
          length = transforms.length;

      while (++index < length) {
        var data = transforms[index],
            size = data.size;

        switch (data.type) {
          case 'drop':      start += size; break;
          case 'dropRight': end -= size; break;
          case 'take':      end = nativeMin(end, start + size); break;
          case 'takeRight': start = nativeMax(start, end - size); break;
        }
      }
      return { 'start': start, 'end': end };
    }

    /**
     * Extracts wrapper details from the `source` body comment.
     *
     * @private
     * @param {string} source The source to inspect.
     * @returns {Array} Returns the wrapper details.
     */
    function getWrapDetails(source) {
      var match = source.match(reWrapDetails);
      return match ? match[1].split(reSplitDetails) : [];
    }

    /**
     * Checks if `path` exists on `object`.
     *
     * @private
     * @param {Object} object The object to query.
     * @param {Array|string} path The path to check.
     * @param {Function} hasFunc The function to check properties.
     * @returns {boolean} Returns `true` if `path` exists, else `false`.
     */
    function hasPath(object, path, hasFunc) {
      path = castPath(path, object);

      var index = -1,
          length = path.length,
          result = false;

      while (++index < length) {
        var key = toKey(path[index]);
        if (!(result = object != null && hasFunc(object, key))) {
          break;
        }
        object = object[key];
      }
      if (result || ++index != length) {
        return result;
      }
      length = object == null ? 0 : object.length;
      return !!length && isLength(length) && isIndex(key, length) &&
        (isArray(object) || isArguments(object));
    }

    /**
     * Initializes an array clone.
     *
     * @private
     * @param {Array} array The array to clone.
     * @returns {Array} Returns the initialized clone.
     */
    function initCloneArray(array) {
      var length = array.length,
          result = new array.constructor(length);

      // Add properties assigned by `RegExp#exec`.
      if (length && typeof array[0] == 'string' && hasOwnProperty.call(array, 'index')) {
        result.index = array.index;
        result.input = array.input;
      }
      return result;
    }

    /**
     * Initializes an object clone.
     *
     * @private
     * @param {Object} object The object to clone.
     * @returns {Object} Returns the initialized clone.
     */
    function initCloneObject(object) {
      return (typeof object.constructor == 'function' && !isPrototype(object))
        ? baseCreate(getPrototype(object))
        : {};
    }

    /**
     * Initializes an object clone based on its `toStringTag`.
     *
     * **Note:** This function only supports cloning values with tags of
     * `Boolean`, `Date`, `Error`, `Map`, `Number`, `RegExp`, `Set`, or `String`.
     *
     * @private
     * @param {Object} object The object to clone.
     * @param {string} tag The `toStringTag` of the object to clone.
     * @param {boolean} [isDeep] Specify a deep clone.
     * @returns {Object} Returns the initialized clone.
     */
    function initCloneByTag(object, tag, isDeep) {
      var Ctor = object.constructor;
      switch (tag) {
        case arrayBufferTag:
          return cloneArrayBuffer(object);

        case boolTag:
        case dateTag:
          return new Ctor(+object);

        case dataViewTag:
          return cloneDataView(object, isDeep);

        case float32Tag: case float64Tag:
        case int8Tag: case int16Tag: case int32Tag:
        case uint8Tag: case uint8ClampedTag: case uint16Tag: case uint32Tag:
          return cloneTypedArray(object, isDeep);

        case mapTag:
          return new Ctor;

        case numberTag:
        case stringTag:
          return new Ctor(object);

        case regexpTag:
          return cloneRegExp(object);

        case setTag:
          return new Ctor;

        case symbolTag:
          return cloneSymbol(object);
      }
    }

    /**
     * Inserts wrapper `details` in a comment at the top of the `source` body.
     *
     * @private
     * @param {string} source The source to modify.
     * @returns {Array} details The details to insert.
     * @returns {string} Returns the modified source.
     */
    function insertWrapDetails(source, details) {
      var length = details.length;
      if (!length) {
        return source;
      }
      var lastIndex = length - 1;
      details[lastIndex] = (length > 1 ? '& ' : '') + details[lastIndex];
      details = details.join(length > 2 ? ', ' : ' ');
      return source.replace(reWrapComment, '{\n/* [wrapped with ' + details + '] */\n');
    }

    /**
     * Checks if `value` is a flattenable `arguments` object or array.
     *
     * @private
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is flattenable, else `false`.
     */
    function isFlattenable(value) {
      return isArray(value) || isArguments(value) ||
        !!(spreadableSymbol && value && value[spreadableSymbol]);
    }

    /**
     * Checks if `value` is a valid array-like index.
     *
     * @private
     * @param {*} value The value to check.
     * @param {number} [length=MAX_SAFE_INTEGER] The upper bounds of a valid index.
     * @returns {boolean} Returns `true` if `value` is a valid index, else `false`.
     */
    function isIndex(value, length) {
      var type = typeof value;
      length = length == null ? MAX_SAFE_INTEGER : length;

      return !!length &&
        (type == 'number' ||
          (type != 'symbol' && reIsUint.test(value))) &&
            (value > -1 && value % 1 == 0 && value < length);
    }

    /**
     * Checks if the given arguments are from an iteratee call.
     *
     * @private
     * @param {*} value The potential iteratee value argument.
     * @param {*} index The potential iteratee index or key argument.
     * @param {*} object The potential iteratee object argument.
     * @returns {boolean} Returns `true` if the arguments are from an iteratee call,
     *  else `false`.
     */
    function isIterateeCall(value, index, object) {
      if (!isObject(object)) {
        return false;
      }
      var type = typeof index;
      if (type == 'number'
            ? (isArrayLike(object) && isIndex(index, object.length))
            : (type == 'string' && index in object)
          ) {
        return eq(object[index], value);
      }
      return false;
    }

    /**
     * Checks if `value` is a property name and not a property path.
     *
     * @private
     * @param {*} value The value to check.
     * @param {Object} [object] The object to query keys on.
     * @returns {boolean} Returns `true` if `value` is a property name, else `false`.
     */
    function isKey(value, object) {
      if (isArray(value)) {
        return false;
      }
      var type = typeof value;
      if (type == 'number' || type == 'symbol' || type == 'boolean' ||
          value == null || isSymbol(value)) {
        return true;
      }
      return reIsPlainProp.test(value) || !reIsDeepProp.test(value) ||
        (object != null && value in Object(object));
    }

    /**
     * Checks if `value` is suitable for use as unique object key.
     *
     * @private
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is suitable, else `false`.
     */
    function isKeyable(value) {
      var type = typeof value;
      return (type == 'string' || type == 'number' || type == 'symbol' || type == 'boolean')
        ? (value !== '__proto__')
        : (value === null);
    }

    /**
     * Checks if `func` has a lazy counterpart.
     *
     * @private
     * @param {Function} func The function to check.
     * @returns {boolean} Returns `true` if `func` has a lazy counterpart,
     *  else `false`.
     */
    function isLaziable(func) {
      var funcName = getFuncName(func),
          other = lodash[funcName];

      if (typeof other != 'function' || !(funcName in LazyWrapper.prototype)) {
        return false;
      }
      if (func === other) {
        return true;
      }
      var data = getData(other);
      return !!data && func === data[0];
    }

    /**
     * Checks if `func` has its source masked.
     *
     * @private
     * @param {Function} func The function to check.
     * @returns {boolean} Returns `true` if `func` is masked, else `false`.
     */
    function isMasked(func) {
      return !!maskSrcKey && (maskSrcKey in func);
    }

    /**
     * Checks if `func` is capable of being masked.
     *
     * @private
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `func` is maskable, else `false`.
     */
    var isMaskable = coreJsData ? isFunction : stubFalse;

    /**
     * Checks if `value` is likely a prototype object.
     *
     * @private
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is a prototype, else `false`.
     */
    function isPrototype(value) {
      var Ctor = value && value.constructor,
          proto = (typeof Ctor == 'function' && Ctor.prototype) || objectProto;

      return value === proto;
    }

    /**
     * Checks if `value` is suitable for strict equality comparisons, i.e. `===`.
     *
     * @private
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` if suitable for strict
     *  equality comparisons, else `false`.
     */
    function isStrictComparable(value) {
      return value === value && !isObject(value);
    }

    /**
     * A specialized version of `matchesProperty` for source values suitable
     * for strict equality comparisons, i.e. `===`.
     *
     * @private
     * @param {string} key The key of the property to get.
     * @param {*} srcValue The value to match.
     * @returns {Function} Returns the new spec function.
     */
    function matchesStrictComparable(key, srcValue) {
      return function(object) {
        if (object == null) {
          return false;
        }
        return object[key] === srcValue &&
          (srcValue !== undefined || (key in Object(object)));
      };
    }

    /**
     * A specialized version of `_.memoize` which clears the memoized function's
     * cache when it exceeds `MAX_MEMOIZE_SIZE`.
     *
     * @private
     * @param {Function} func The function to have its output memoized.
     * @returns {Function} Returns the new memoized function.
     */
    function memoizeCapped(func) {
      var result = memoize(func, function(key) {
        if (cache.size === MAX_MEMOIZE_SIZE) {
          cache.clear();
        }
        return key;
      });

      var cache = result.cache;
      return result;
    }

    /**
     * Merges the function metadata of `source` into `data`.
     *
     * Merging metadata reduces the number of wrappers used to invoke a function.
     * This is possible because methods like `_.bind`, `_.curry`, and `_.partial`
     * may be applied regardless of execution order. Methods like `_.ary` and
     * `_.rearg` modify function arguments, making the order in which they are
     * executed important, preventing the merging of metadata. However, we make
     * an exception for a safe combined case where curried functions have `_.ary`
     * and or `_.rearg` applied.
     *
     * @private
     * @param {Array} data The destination metadata.
     * @param {Array} source The source metadata.
     * @returns {Array} Returns `data`.
     */
    function mergeData(data, source) {
      var bitmask = data[1],
          srcBitmask = source[1],
          newBitmask = bitmask | srcBitmask,
          isCommon = newBitmask < (WRAP_BIND_FLAG | WRAP_BIND_KEY_FLAG | WRAP_ARY_FLAG);

      var isCombo =
        ((srcBitmask == WRAP_ARY_FLAG) && (bitmask == WRAP_CURRY_FLAG)) ||
        ((srcBitmask == WRAP_ARY_FLAG) && (bitmask == WRAP_REARG_FLAG) && (data[7].length <= source[8])) ||
        ((srcBitmask == (WRAP_ARY_FLAG | WRAP_REARG_FLAG)) && (source[7].length <= source[8]) && (bitmask == WRAP_CURRY_FLAG));

      // Exit early if metadata can't be merged.
      if (!(isCommon || isCombo)) {
        return data;
      }
      // Use source `thisArg` if available.
      if (srcBitmask & WRAP_BIND_FLAG) {
        data[2] = source[2];
        // Set when currying a bound function.
        newBitmask |= bitmask & WRAP_BIND_FLAG ? 0 : WRAP_CURRY_BOUND_FLAG;
      }
      // Compose partial arguments.
      var value = source[3];
      if (value) {
        var partials = data[3];
        data[3] = partials ? composeArgs(partials, value, source[4]) : value;
        data[4] = partials ? replaceHolders(data[3], PLACEHOLDER) : source[4];
      }
      // Compose partial right arguments.
      value = source[5];
      if (value) {
        partials = data[5];
        data[5] = partials ? composeArgsRight(partials, value, source[6]) : value;
        data[6] = partials ? replaceHolders(data[5], PLACEHOLDER) : source[6];
      }
      // Use source `argPos` if available.
      value = source[7];
      if (value) {
        data[7] = value;
      }
      // Use source `ary` if it's smaller.
      if (srcBitmask & WRAP_ARY_FLAG) {
        data[8] = data[8] == null ? source[8] : nativeMin(data[8], source[8]);
      }
      // Use source `arity` if one is not provided.
      if (data[9] == null) {
        data[9] = source[9];
      }
      // Use source `func` and merge bitmasks.
      data[0] = source[0];
      data[1] = newBitmask;

      return data;
    }

    /**
     * This function is like
     * [`Object.keys`](http://ecma-international.org/ecma-262/7.0/#sec-object.keys)
     * except that it includes inherited enumerable properties.
     *
     * @private
     * @param {Object} object The object to query.
     * @returns {Array} Returns the array of property names.
     */
    function nativeKeysIn(object) {
      var result = [];
      if (object != null) {
        for (var key in Object(object)) {
          result.push(key);
        }
      }
      return result;
    }

    /**
     * Converts `value` to a string using `Object.prototype.toString`.
     *
     * @private
     * @param {*} value The value to convert.
     * @returns {string} Returns the converted string.
     */
    function objectToString(value) {
      return nativeObjectToString.call(value);
    }

    /**
     * A specialized version of `baseRest` which transforms the rest array.
     *
     * @private
     * @param {Function} func The function to apply a rest parameter to.
     * @param {number} [start=func.length-1] The start position of the rest parameter.
     * @param {Function} transform The rest array transform.
     * @returns {Function} Returns the new function.
     */
    function overRest(func, start, transform) {
      start = nativeMax(start === undefined ? (func.length - 1) : start, 0);
      return function() {
        var args = arguments,
            index = -1,
            length = nativeMax(args.length - start, 0),
            array = Array(length);

        while (++index < length) {
          array[index] = args[start + index];
        }
        index = -1;
        var otherArgs = Array(start + 1);
        while (++index < start) {
          otherArgs[index] = args[index];
        }
        otherArgs[start] = transform(array);
        return apply(func, this, otherArgs);
      };
    }

    /**
     * Gets the parent value at `path` of `object`.
     *
     * @private
     * @param {Object} object The object to query.
     * @param {Array} path The path to get the parent value of.
     * @returns {*} Returns the parent value.
     */
    function parent(object, path) {
      return path.length < 2 ? object : baseGet(object, baseSlice(path, 0, -1));
    }

    /**
     * Reorder `array` according to the specified indexes where the element at
     * the first index is assigned as the first element, the element at
     * the second index is assigned as the second element, and so on.
     *
     * @private
     * @param {Array} array The array to reorder.
     * @param {Array} indexes The arranged array indexes.
     * @returns {Array} Returns `array`.
     */
    function reorder(array, indexes) {
      var arrLength = array.length,
          length = nativeMin(indexes.length, arrLength),
          oldArray = copyArray(array);

      while (length--) {
        var index = indexes[length];
        array[length] = isIndex(index, arrLength) ? oldArray[index] : undefined;
      }
      return array;
    }

    /**
     * Gets the value at `key`, unless `key` is "__proto__" or "constructor".
     *
     * @private
     * @param {Object} object The object to query.
     * @param {string} key The key of the property to get.
     * @returns {*} Returns the property value.
     */
    function safeGet(object, key) {
      if (key === 'constructor' && typeof object[key] === 'function') {
        return;
      }

      if (key == '__proto__') {
        return;
      }

      return object[key];
    }

    /**
     * Sets metadata for `func`.
     *
     * **Note:** If this function becomes hot, i.e. is invoked a lot in a short
     * period of time, it will trip its breaker and transition to an identity
     * function to avoid garbage collection pauses in V8. See
     * [V8 issue 2070](https://bugs.chromium.org/p/v8/issues/detail?id=2070)
     * for more details.
     *
     * @private
     * @param {Function} func The function to associate metadata with.
     * @param {*} data The metadata.
     * @returns {Function} Returns `func`.
     */
    var setData = shortOut(baseSetData);

    /**
     * A simple wrapper around the global [`setTimeout`](https://mdn.io/setTimeout).
     *
     * @private
     * @param {Function} func The function to delay.
     * @param {number} wait The number of milliseconds to delay invocation.
     * @returns {number|Object} Returns the timer id or timeout object.
     */
    var setTimeout = ctxSetTimeout || function(func, wait) {
      return root.setTimeout(func, wait);
    };

    /**
     * Sets the `toString` method of `func` to return `string`.
     *
     * @private
     * @param {Function} func The function to modify.
     * @param {Function} string The `toString` result.
     * @returns {Function} Returns `func`.
     */
    var setToString = shortOut(baseSetToString);

    /**
     * Sets the `toString` method of `wrapper` to mimic the source of `reference`
     * with wrapper details in a comment at the top of the source body.
     *
     * @private
     * @param {Function} wrapper The function to modify.
     * @param {Function} reference The reference function.
     * @param {number} bitmask The bitmask flags. See `createWrap` for more details.
     * @returns {Function} Returns `wrapper`.
     */
    function setWrapToString(wrapper, reference, bitmask) {
      var source = (reference + '');
      return setToString(wrapper, insertWrapDetails(source, updateWrapDetails(getWrapDetails(source), bitmask)));
    }

    /**
     * Creates a function that'll short out and invoke `identity` instead
     * of `func` when it's called `HOT_COUNT` or more times in `HOT_SPAN`
     * milliseconds.
     *
     * @private
     * @param {Function} func The function to restrict.
     * @returns {Function} Returns the new shortable function.
     */
    function shortOut(func) {
      var count = 0,
          lastCalled = 0;

      return function() {
        var stamp = nativeNow(),
            remaining = HOT_SPAN - (stamp - lastCalled);

        lastCalled = stamp;
        if (remaining > 0) {
          if (++count >= HOT_COUNT) {
            return arguments[0];
          }
        } else {
          count = 0;
        }
        return func.apply(undefined, arguments);
      };
    }

    /**
     * A specialized version of `_.shuffle` which mutates and sets the size of `array`.
     *
     * @private
     * @param {Array} array The array to shuffle.
     * @param {number} [size=array.length] The size of `array`.
     * @returns {Array} Returns `array`.
     */
    function shuffleSelf(array, size) {
      var index = -1,
          length = array.length,
          lastIndex = length - 1;

      size = size === undefined ? length : size;
      while (++index < size) {
        var rand = baseRandom(index, lastIndex),
            value = array[rand];

        array[rand] = array[index];
        array[index] = value;
      }
      array.length = size;
      return array;
    }

    /**
     * Converts `string` to a property path array.
     *
     * @private
     * @param {string} string The string to convert.
     * @returns {Array} Returns the property path array.
     */
    var stringToPath = memoizeCapped(function(string) {
      var result = [];
      if (string.charCodeAt(0) === 46 /* . */) {
        result.push('');
      }
      string.replace(rePropName, function(match, number, quote, subString) {
        result.push(quote ? subString.replace(reEscapeChar, '$1') : (number || match));
      });
      return result;
    });

    /**
     * Converts `value` to a string key if it's not a string or symbol.
     *
     * @private
     * @param {*} value The value to inspect.
     * @returns {string|symbol} Returns the key.
     */
    function toKey(value) {
      if (typeof value == 'string' || isSymbol(value)) {
        return value;
      }
      var result = (value + '');
      return (result == '0' && (1 / value) == -INFINITY) ? '-0' : result;
    }

    /**
     * Converts `func` to its source code.
     *
     * @private
     * @param {Function} func The function to convert.
     * @returns {string} Returns the source code.
     */
    function toSource(func) {
      if (func != null) {
        try {
          return funcToString.call(func);
        } catch (e) {}
        try {
          return (func + '');
        } catch (e) {}
      }
      return '';
    }

    /**
     * Updates wrapper `details` based on `bitmask` flags.
     *
     * @private
     * @returns {Array} details The details to modify.
     * @param {number} bitmask The bitmask flags. See `createWrap` for more details.
     * @returns {Array} Returns `details`.
     */
    function updateWrapDetails(details, bitmask) {
      arrayEach(wrapFlags, function(pair) {
        var value = '_.' + pair[0];
        if ((bitmask & pair[1]) && !arrayIncludes(details, value)) {
          details.push(value);
        }
      });
      return details.sort();
    }

    /**
     * Creates a clone of `wrapper`.
     *
     * @private
     * @param {Object} wrapper The wrapper to clone.
     * @returns {Object} Returns the cloned wrapper.
     */
    function wrapperClone(wrapper) {
      if (wrapper instanceof LazyWrapper) {
        return wrapper.clone();
      }
      var result = new LodashWrapper(wrapper.__wrapped__, wrapper.__chain__);
      result.__actions__ = copyArray(wrapper.__actions__);
      result.__index__  = wrapper.__index__;
      result.__values__ = wrapper.__values__;
      return result;
    }

    /*------------------------------------------------------------------------*/

    /**
     * Creates an array of elements split into groups the length of `size`.
     * If `array` can't be split evenly, the final chunk will be the remaining
     * elements.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Array
     * @param {Array} array The array to process.
     * @param {number} [size=1] The length of each chunk
     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
     * @returns {Array} Returns the new array of chunks.
     * @example
     *
     * _.chunk(['a', 'b', 'c', 'd'], 2);
     * // => [['a', 'b'], ['c', 'd']]
     *
     * _.chunk(['a', 'b', 'c', 'd'], 3);
     * // => [['a', 'b', 'c'], ['d']]
     */
    function chunk(array, size, guard) {
      if ((guard ? isIterateeCall(array, size, guard) : size === undefined)) {
        size = 1;
      } else {
        size = nativeMax(toInteger(size), 0);
      }
      var length = array == null ? 0 : array.length;
      if (!length || size < 1) {
        return [];
      }
      var index = 0,
          resIndex = 0,
          result = Array(nativeCeil(length / size));

      while (index < length) {
        result[resIndex++] = baseSlice(array, index, (index += size));
      }
      return result;
    }

    /**
     * Creates an array with all falsey values removed. The values `false`, `null`,
     * `0`, `""`, `undefined`, and `NaN` are falsey.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Array
     * @param {Array} array The array to compact.
     * @returns {Array} Returns the new array of filtered values.
     * @example
     *
     * _.compact([0, 1, false, 2, '', 3]);
     * // => [1, 2, 3]
     */
    function compact(array) {
      var index = -1,
          length = array == null ? 0 : array.length,
          resIndex = 0,
          result = [];

      while (++index < length) {
        var value = array[index];
        if (value) {
          result[resIndex++] = value;
        }
      }
      return result;
    }

    /**
     * Creates a new array concatenating `array` with any additional arrays
     * and/or values.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Array
     * @param {Array} array The array to concatenate.
     * @param {...*} [values] The values to concatenate.
     * @returns {Array} Returns the new concatenated array.
     * @example
     *
     * var array = [1];
     * var other = _.concat(array, 2, [3], [[4]]);
     *
     * console.log(other);
     * // => [1, 2, 3, [4]]
     *
     * console.log(array);
     * // => [1]
     */
    function concat() {
      var length = arguments.length;
      if (!length) {
        return [];
      }
      var args = Array(length - 1),
          array = arguments[0],
          index = length;

      while (index--) {
        args[index - 1] = arguments[index];
      }
      return arrayPush(isArray(array) ? copyArray(array) : [array], baseFlatten(args, 1));
    }

    /**
     * Creates an array of `array` values not included in the other given arrays
     * using [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
     * for equality comparisons. The order and references of result values are
     * determined by the first array.
     *
     * **Note:** Unlike `_.pullAll`, this method returns a new array.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Array
     * @param {Array} array The array to inspect.
     * @param {...Array} [values] The values to exclude.
     * @returns {Array} Returns the new array of filtered values.
     * @see _.without, _.xor
     * @example
     *
     * _.difference([2, 1], [2, 3]);
     * // => [1]
     */
    var difference = baseRest(function(array, values) {
      return isArrayLikeObject(array)
        ? baseDifference(array, baseFlatten(values, 1, isArrayLikeObject, true))
        : [];
    });

    /**
     * This method is like `_.difference` except that it accepts `iteratee` which
     * is invoked for each element of `array` and `values` to generate the criterion
     * by which they're compared. The order and references of result values are
     * determined by the first array. The iteratee is invoked with one argument:
     * (value).
     *
     * **Note:** Unlike `_.pullAllBy`, this method returns a new array.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Array
     * @param {Array} array The array to inspect.
     * @param {...Array} [values] The values to exclude.
     * @param {Function} [iteratee=_.identity] The iteratee invoked per element.
     * @returns {Array} Returns the new array of filtered values.
     * @example
     *
     * _.differenceBy([2.1, 1.2], [2.3, 3.4], Math.floor);
     * // => [1.2]
     *
     * // The `_.property` iteratee shorthand.
     * _.differenceBy([{ 'x': 2 }, { 'x': 1 }], [{ 'x': 1 }], 'x');
     * // => [{ 'x': 2 }]
     */
    var differenceBy = baseRest(function(array, values) {
      var iteratee = last(values);
      if (isArrayLikeObject(iteratee)) {
        iteratee = undefined;
      }
      return isArrayLikeObject(array)
        ? baseDifference(array, baseFlatten(values, 1, isArrayLikeObject, true), getIteratee(iteratee, 2))
        : [];
    });

    /**
     * This method is like `_.difference` except that it accepts `comparator`
     * which is invoked to compare elements of `array` to `values`. The order and
     * references of result values are determined by the first array. The comparator
     * is invoked with two arguments: (arrVal, othVal).
     *
     * **Note:** Unlike `_.pullAllWith`, this method returns a new array.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Array
     * @param {Array} array The array to inspect.
     * @param {...Array} [values] The values to exclude.
     * @param {Function} [comparator] The comparator invoked per element.
     * @returns {Array} Returns the new array of filtered values.
     * @example
     *
     * var objects = [{ 'x': 1, 'y': 2 }, { 'x': 2, 'y': 1 }];
     *
     * _.differenceWith(objects, [{ 'x': 1, 'y': 2 }], _.isEqual);
     * // => [{ 'x': 2, 'y': 1 }]
     */
    var differenceWith = baseRest(function(array, values) {
      var comparator = last(values);
      if (isArrayLikeObject(comparator)) {
        comparator = undefined;
      }
      return isArrayLikeObject(array)
        ? baseDifference(array, baseFlatten(values, 1, isArrayLikeObject, true), undefined, comparator)
        : [];
    });

    /**
     * Creates a slice of `array` with `n` elements dropped from the beginning.
     *
     * @static
     * @memberOf _
     * @since 0.5.0
     * @category Array
     * @param {Array} array The array to query.
     * @param {number} [n=1] The number of elements to drop.
     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
     * @returns {Array} Returns the slice of `array`.
     * @example
     *
     * _.drop([1, 2, 3]);
     * // => [2, 3]
     *
     * _.drop([1, 2, 3], 2);
     * // => [3]
     *
     * _.drop([1, 2, 3], 5);
     * // => []
     *
     * _.drop([1, 2, 3], 0);
     * // => [1, 2, 3]
     */
    function drop(array, n, guard) {
      var length = array == null ? 0 : array.length;
      if (!length) {
        return [];
      }
      n = (guard || n === undefined) ? 1 : toInteger(n);
      return baseSlice(array, n < 0 ? 0 : n, length);
    }

    /**
     * Creates a slice of `array` with `n` elements dropped from the end.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Array
     * @param {Array} array The array to query.
     * @param {number} [n=1] The number of elements to drop.
     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
     * @returns {Array} Returns the slice of `array`.
     * @example
     *
     * _.dropRight([1, 2, 3]);
     * // => [1, 2]
     *
     * _.dropRight([1, 2, 3], 2);
     * // => [1]
     *
     * _.dropRight([1, 2, 3], 5);
     * // => []
     *
     * _.dropRight([1, 2, 3], 0);
     * // => [1, 2, 3]
     */
    function dropRight(array, n, guard) {
      var length = array == null ? 0 : array.length;
      if (!length) {
        return [];
      }
      n = (guard || n === undefined) ? 1 : toInteger(n);
      n = length - n;
      return baseSlice(array, 0, n < 0 ? 0 : n);
    }

    /**
     * Creates a slice of `array` excluding elements dropped from the end.
     * Elements are dropped until `predicate` returns falsey. The predicate is
     * invoked with three arguments: (value, index, array).
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Array
     * @param {Array} array The array to query.
     * @param {Function} [predicate=_.identity] The function invoked per iteration.
     * @returns {Array} Returns the slice of `array`.
     * @example
     *
     * var users = [
     *   { 'user': 'barney',  'active': true },
     *   { 'user': 'fred',    'active': false },
     *   { 'user': 'pebbles', 'active': false }
     * ];
     *
     * _.dropRightWhile(users, function(o) { return !o.active; });
     * // => objects for ['barney']
     *
     * // The `_.matches` iteratee shorthand.
     * _.dropRightWhile(users, { 'user': 'pebbles', 'active': false });
     * // => objects for ['barney', 'fred']
     *
     * // The `_.matchesProperty` iteratee shorthand.
     * _.dropRightWhile(users, ['active', false]);
     * // => objects for ['barney']
     *
     * // The `_.property` iteratee shorthand.
     * _.dropRightWhile(users, 'active');
     * // => objects for ['barney', 'fred', 'pebbles']
     */
    function dropRightWhile(array, predicate) {
      return (array && array.length)
        ? baseWhile(array, getIteratee(predicate, 3), true, true)
        : [];
    }

    /**
     * Creates a slice of `array` excluding elements dropped from the beginning.
     * Elements are dropped until `predicate` returns falsey. The predicate is
     * invoked with three arguments: (value, index, array).
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Array
     * @param {Array} array The array to query.
     * @param {Function} [predicate=_.identity] The function invoked per iteration.
     * @returns {Array} Returns the slice of `array`.
     * @example
     *
     * var users = [
     *   { 'user': 'barney',  'active': false },
     *   { 'user': 'fred',    'active': false },
     *   { 'user': 'pebbles', 'active': true }
     * ];
     *
     * _.dropWhile(users, function(o) { return !o.active; });
     * // => objects for ['pebbles']
     *
     * // The `_.matches` iteratee shorthand.
     * _.dropWhile(users, { 'user': 'barney', 'active': false });
     * // => objects for ['fred', 'pebbles']
     *
     * // The `_.matchesProperty` iteratee shorthand.
     * _.dropWhile(users, ['active', false]);
     * // => objects for ['pebbles']
     *
     * // The `_.property` iteratee shorthand.
     * _.dropWhile(users, 'active');
     * // => objects for ['barney', 'fred', 'pebbles']
     */
    function dropWhile(array, predicate) {
      return (array && array.length)
        ? baseWhile(array, getIteratee(predicate, 3), true)
        : [];
    }

    /**
     * Fills elements of `array` with `value` from `start` up to, but not
     * including, `end`.
     *
     * **Note:** This method mutates `array`.
     *
     * @static
     * @memberOf _
     * @since 3.2.0
     * @category Array
     * @param {Array} array The array to fill.
     * @param {*} value The value to fill `array` with.
     * @param {number} [start=0] The start position.
     * @param {number} [end=array.length] The end position.
     * @returns {Array} Returns `array`.
     * @example
     *
     * var array = [1, 2, 3];
     *
     * _.fill(array, 'a');
     * console.log(array);
     * // => ['a', 'a', 'a']
     *
     * _.fill(Array(3), 2);
     * // => [2, 2, 2]
     *
     * _.fill([4, 6, 8, 10], '*', 1, 3);
     * // => [4, '*', '*', 10]
     */
    function fill(array, value, start, end) {
      var length = array == null ? 0 : array.length;
      if (!length) {
        return [];
      }
      if (start && typeof start != 'number' && isIterateeCall(array, value, start)) {
        start = 0;
        end = length;
      }
      return baseFill(array, value, start, end);
    }

    /**
     * This method is like `_.find` except that it returns the index of the first
     * element `predicate` returns truthy for instead of the element itself.
     *
     * @static
     * @memberOf _
     * @since 1.1.0
     * @category Array
     * @param {Array} array The array to inspect.
     * @param {Function} [predicate=_.identity] The function invoked per iteration.
     * @param {number} [fromIndex=0] The index to search from.
     * @returns {number} Returns the index of the found element, else `-1`.
     * @example
     *
     * var users = [
     *   { 'user': 'barney',  'active': false },
     *   { 'user': 'fred',    'active': false },
     *   { 'user': 'pebbles', 'active': true }
     * ];
     *
     * _.findIndex(users, function(o) { return o.user == 'barney'; });
     * // => 0
     *
     * // The `_.matches` iteratee shorthand.
     * _.findIndex(users, { 'user': 'fred', 'active': false });
     * // => 1
     *
     * // The `_.matchesProperty` iteratee shorthand.
     * _.findIndex(users, ['active', false]);
     * // => 0
     *
     * // The `_.property` iteratee shorthand.
     * _.findIndex(users, 'active');
     * // => 2
     */
    function findIndex(array, predicate, fromIndex) {
      var length = array == null ? 0 : array.length;
      if (!length) {
        return -1;
      }
      var index = fromIndex == null ? 0 : toInteger(fromIndex);
      if (index < 0) {
        index = nativeMax(length + index, 0);
      }
      return baseFindIndex(array, getIteratee(predicate, 3), index);
    }

    /**
     * This method is like `_.findIndex` except that it iterates over elements
     * of `collection` from right to left.
     *
     * @static
     * @memberOf _
     * @since 2.0.0
     * @category Array
     * @param {Array} array The array to inspect.
     * @param {Function} [predicate=_.identity] The function invoked per iteration.
     * @param {number} [fromIndex=array.length-1] The index to search from.
     * @returns {number} Returns the index of the found element, else `-1`.
     * @example
     *
     * var users = [
     *   { 'user': 'barney',  'active': true },
     *   { 'user': 'fred',    'active': false },
     *   { 'user': 'pebbles', 'active': false }
     * ];
     *
     * _.findLastIndex(users, function(o) { return o.user == 'pebbles'; });
     * // => 2
     *
     * // The `_.matches` iteratee shorthand.
     * _.findLastIndex(users, { 'user': 'barney', 'active': true });
     * // => 0
     *
     * // The `_.matchesProperty` iteratee shorthand.
     * _.findLastIndex(users, ['active', false]);
     * // => 2
     *
     * // The `_.property` iteratee shorthand.
     * _.findLastIndex(users, 'active');
     * // => 0
     */
    function findLastIndex(array, predicate, fromIndex) {
      var length = array == null ? 0 : array.length;
      if (!length) {
        return -1;
      }
      var index = length - 1;
      if (fromIndex !== undefined) {
        index = toInteger(fromIndex);
        index = fromIndex < 0
          ? nativeMax(length + index, 0)
          : nativeMin(index, length - 1);
      }
      return baseFindIndex(array, getIteratee(predicate, 3), index, true);
    }

    /**
     * Flattens `array` a single level deep.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Array
     * @param {Array} array The array to flatten.
     * @returns {Array} Returns the new flattened array.
     * @example
     *
     * _.flatten([1, [2, [3, [4]], 5]]);
     * // => [1, 2, [3, [4]], 5]
     */
    function flatten(array) {
      var length = array == null ? 0 : array.length;
      return length ? baseFlatten(array, 1) : [];
    }

    /**
     * Recursively flattens `array`.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Array
     * @param {Array} array The array to flatten.
     * @returns {Array} Returns the new flattened array.
     * @example
     *
     * _.flattenDeep([1, [2, [3, [4]], 5]]);
     * // => [1, 2, 3, 4, 5]
     */
    function flattenDeep(array) {
      var length = array == null ? 0 : array.length;
      return length ? baseFlatten(array, INFINITY) : [];
    }

    /**
     * Recursively flatten `array` up to `depth` times.
     *
     * @static
     * @memberOf _
     * @since 4.4.0
     * @category Array
     * @param {Array} array The array to flatten.
     * @param {number} [depth=1] The maximum recursion depth.
     * @returns {Array} Returns the new flattened array.
     * @example
     *
     * var array = [1, [2, [3, [4]], 5]];
     *
     * _.flattenDepth(array, 1);
     * // => [1, 2, [3, [4]], 5]
     *
     * _.flattenDepth(array, 2);
     * // => [1, 2, 3, [4], 5]
     */
    function flattenDepth(array, depth) {
      var length = array == null ? 0 : array.length;
      if (!length) {
        return [];
      }
      depth = depth === undefined ? 1 : toInteger(depth);
      return baseFlatten(array, depth);
    }

    /**
     * The inverse of `_.toPairs`; this method returns an object composed
     * from key-value `pairs`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Array
     * @param {Array} pairs The key-value pairs.
     * @returns {Object} Returns the new object.
     * @example
     *
     * _.fromPairs([['a', 1], ['b', 2]]);
     * // => { 'a': 1, 'b': 2 }
     */
    function fromPairs(pairs) {
      var index = -1,
          length = pairs == null ? 0 : pairs.length,
          result = {};

      while (++index < length) {
        var pair = pairs[index];
        result[pair[0]] = pair[1];
      }
      return result;
    }

    /**
     * Gets the first element of `array`.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @alias first
     * @category Array
     * @param {Array} array The array to query.
     * @returns {*} Returns the first element of `array`.
     * @example
     *
     * _.head([1, 2, 3]);
     * // => 1
     *
     * _.head([]);
     * // => undefined
     */
    function head(array) {
      return (array && array.length) ? array[0] : undefined;
    }

    /**
     * Gets the index at which the first occurrence of `value` is found in `array`
     * using [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
     * for equality comparisons. If `fromIndex` is negative, it's used as the
     * offset from the end of `array`.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Array
     * @param {Array} array The array to inspect.
     * @param {*} value The value to search for.
     * @param {number} [fromIndex=0] The index to search from.
     * @returns {number} Returns the index of the matched value, else `-1`.
     * @example
     *
     * _.indexOf([1, 2, 1, 2], 2);
     * // => 1
     *
     * // Search from the `fromIndex`.
     * _.indexOf([1, 2, 1, 2], 2, 2);
     * // => 3
     */
    function indexOf(array, value, fromIndex) {
      var length = array == null ? 0 : array.length;
      if (!length) {
        return -1;
      }
      var index = fromIndex == null ? 0 : toInteger(fromIndex);
      if (index < 0) {
        index = nativeMax(length + index, 0);
      }
      return baseIndexOf(array, value, index);
    }

    /**
     * Gets all but the last element of `array`.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Array
     * @param {Array} array The array to query.
     * @returns {Array} Returns the slice of `array`.
     * @example
     *
     * _.initial([1, 2, 3]);
     * // => [1, 2]
     */
    function initial(array) {
      var length = array == null ? 0 : array.length;
      return length ? baseSlice(array, 0, -1) : [];
    }

    /**
     * Creates an array of unique values that are included in all given arrays
     * using [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
     * for equality comparisons. The order and references of result values are
     * determined by the first array.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Array
     * @param {...Array} [arrays] The arrays to inspect.
     * @returns {Array} Returns the new array of intersecting values.
     * @example
     *
     * _.intersection([2, 1], [2, 3]);
     * // => [2]
     */
    var intersection = baseRest(function(arrays) {
      var mapped = arrayMap(arrays, castArrayLikeObject);
      return (mapped.length && mapped[0] === arrays[0])
        ? baseIntersection(mapped)
        : [];
    });

    /**
     * This method is like `_.intersection` except that it accepts `iteratee`
     * which is invoked for each element of each `arrays` to generate the criterion
     * by which they're compared. The order and references of result values are
     * determined by the first array. The iteratee is invoked with one argument:
     * (value).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Array
     * @param {...Array} [arrays] The arrays to inspect.
     * @param {Function} [iteratee=_.identity] The iteratee invoked per element.
     * @returns {Array} Returns the new array of intersecting values.
     * @example
     *
     * _.intersectionBy([2.1, 1.2], [2.3, 3.4], Math.floor);
     * // => [2.1]
     *
     * // The `_.property` iteratee shorthand.
     * _.intersectionBy([{ 'x': 1 }], [{ 'x': 2 }, { 'x': 1 }], 'x');
     * // => [{ 'x': 1 }]
     */
    var intersectionBy = baseRest(function(arrays) {
      var iteratee = last(arrays),
          mapped = arrayMap(arrays, castArrayLikeObject);

      if (iteratee === last(mapped)) {
        iteratee = undefined;
      } else {
        mapped.pop();
      }
      return (mapped.length && mapped[0] === arrays[0])
        ? baseIntersection(mapped, getIteratee(iteratee, 2))
        : [];
    });

    /**
     * This method is like `_.intersection` except that it accepts `comparator`
     * which is invoked to compare elements of `arrays`. The order and references
     * of result values are determined by the first array. The comparator is
     * invoked with two arguments: (arrVal, othVal).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Array
     * @param {...Array} [arrays] The arrays to inspect.
     * @param {Function} [comparator] The comparator invoked per element.
     * @returns {Array} Returns the new array of intersecting values.
     * @example
     *
     * var objects = [{ 'x': 1, 'y': 2 }, { 'x': 2, 'y': 1 }];
     * var others = [{ 'x': 1, 'y': 1 }, { 'x': 1, 'y': 2 }];
     *
     * _.intersectionWith(objects, others, _.isEqual);
     * // => [{ 'x': 1, 'y': 2 }]
     */
    var intersectionWith = baseRest(function(arrays) {
      var comparator = last(arrays),
          mapped = arrayMap(arrays, castArrayLikeObject);

      comparator = typeof comparator == 'function' ? comparator : undefined;
      if (comparator) {
        mapped.pop();
      }
      return (mapped.length && mapped[0] === arrays[0])
        ? baseIntersection(mapped, undefined, comparator)
        : [];
    });

    /**
     * Converts all elements in `array` into a string separated by `separator`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Array
     * @param {Array} array The array to convert.
     * @param {string} [separator=','] The element separator.
     * @returns {string} Returns the joined string.
     * @example
     *
     * _.join(['a', 'b', 'c'], '~');
     * // => 'a~b~c'
     */
    function join(array, separator) {
      return array == null ? '' : nativeJoin.call(array, separator);
    }

    /**
     * Gets the last element of `array`.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Array
     * @param {Array} array The array to query.
     * @returns {*} Returns the last element of `array`.
     * @example
     *
     * _.last([1, 2, 3]);
     * // => 3
     */
    function last(array) {
      var length = array == null ? 0 : array.length;
      return length ? array[length - 1] : undefined;
    }

    /**
     * This method is like `_.indexOf` except that it iterates over elements of
     * `array` from right to left.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Array
     * @param {Array} array The array to inspect.
     * @param {*} value The value to search for.
     * @param {number} [fromIndex=array.length-1] The index to search from.
     * @returns {number} Returns the index of the matched value, else `-1`.
     * @example
     *
     * _.lastIndexOf([1, 2, 1, 2], 2);
     * // => 3
     *
     * // Search from the `fromIndex`.
     * _.lastIndexOf([1, 2, 1, 2], 2, 2);
     * // => 1
     */
    function lastIndexOf(array, value, fromIndex) {
      var length = array == null ? 0 : array.length;
      if (!length) {
        return -1;
      }
      var index = length;
      if (fromIndex !== undefined) {
        index = toInteger(fromIndex);
        index = index < 0 ? nativeMax(length + index, 0) : nativeMin(index, length - 1);
      }
      return value === value
        ? strictLastIndexOf(array, value, index)
        : baseFindIndex(array, baseIsNaN, index, true);
    }

    /**
     * Gets the element at index `n` of `array`. If `n` is negative, the nth
     * element from the end is returned.
     *
     * @static
     * @memberOf _
     * @since 4.11.0
     * @category Array
     * @param {Array} array The array to query.
     * @param {number} [n=0] The index of the element to return.
     * @returns {*} Returns the nth element of `array`.
     * @example
     *
     * var array = ['a', 'b', 'c', 'd'];
     *
     * _.nth(array, 1);
     * // => 'b'
     *
     * _.nth(array, -2);
     * // => 'c';
     */
    function nth(array, n) {
      return (array && array.length) ? baseNth(array, toInteger(n)) : undefined;
    }

    /**
     * Removes all given values from `array` using
     * [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
     * for equality comparisons.
     *
     * **Note:** Unlike `_.without`, this method mutates `array`. Use `_.remove`
     * to remove elements from an array by predicate.
     *
     * @static
     * @memberOf _
     * @since 2.0.0
     * @category Array
     * @param {Array} array The array to modify.
     * @param {...*} [values] The values to remove.
     * @returns {Array} Returns `array`.
     * @example
     *
     * var array = ['a', 'b', 'c', 'a', 'b', 'c'];
     *
     * _.pull(array, 'a', 'c');
     * console.log(array);
     * // => ['b', 'b']
     */
    var pull = baseRest(pullAll);

    /**
     * This method is like `_.pull` except that it accepts an array of values to remove.
     *
     * **Note:** Unlike `_.difference`, this method mutates `array`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Array
     * @param {Array} array The array to modify.
     * @param {Array} values The values to remove.
     * @returns {Array} Returns `array`.
     * @example
     *
     * var array = ['a', 'b', 'c', 'a', 'b', 'c'];
     *
     * _.pullAll(array, ['a', 'c']);
     * console.log(array);
     * // => ['b', 'b']
     */
    function pullAll(array, values) {
      return (array && array.length && values && values.length)
        ? basePullAll(array, values)
        : array;
    }

    /**
     * This method is like `_.pullAll` except that it accepts `iteratee` which is
     * invoked for each element of `array` and `values` to generate the criterion
     * by which they're compared. The iteratee is invoked with one argument: (value).
     *
     * **Note:** Unlike `_.differenceBy`, this method mutates `array`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Array
     * @param {Array} array The array to modify.
     * @param {Array} values The values to remove.
     * @param {Function} [iteratee=_.identity] The iteratee invoked per element.
     * @returns {Array} Returns `array`.
     * @example
     *
     * var array = [{ 'x': 1 }, { 'x': 2 }, { 'x': 3 }, { 'x': 1 }];
     *
     * _.pullAllBy(array, [{ 'x': 1 }, { 'x': 3 }], 'x');
     * console.log(array);
     * // => [{ 'x': 2 }]
     */
    function pullAllBy(array, values, iteratee) {
      return (array && array.length && values && values.length)
        ? basePullAll(array, values, getIteratee(iteratee, 2))
        : array;
    }

    /**
     * This method is like `_.pullAll` except that it accepts `comparator` which
     * is invoked to compare elements of `array` to `values`. The comparator is
     * invoked with two arguments: (arrVal, othVal).
     *
     * **Note:** Unlike `_.differenceWith`, this method mutates `array`.
     *
     * @static
     * @memberOf _
     * @since 4.6.0
     * @category Array
     * @param {Array} array The array to modify.
     * @param {Array} values The values to remove.
     * @param {Function} [comparator] The comparator invoked per element.
     * @returns {Array} Returns `array`.
     * @example
     *
     * var array = [{ 'x': 1, 'y': 2 }, { 'x': 3, 'y': 4 }, { 'x': 5, 'y': 6 }];
     *
     * _.pullAllWith(array, [{ 'x': 3, 'y': 4 }], _.isEqual);
     * console.log(array);
     * // => [{ 'x': 1, 'y': 2 }, { 'x': 5, 'y': 6 }]
     */
    function pullAllWith(array, values, comparator) {
      return (array && array.length && values && values.length)
        ? basePullAll(array, values, undefined, comparator)
        : array;
    }

    /**
     * Removes elements from `array` corresponding to `indexes` and returns an
     * array of removed elements.
     *
     * **Note:** Unlike `_.at`, this method mutates `array`.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Array
     * @param {Array} array The array to modify.
     * @param {...(number|number[])} [indexes] The indexes of elements to remove.
     * @returns {Array} Returns the new array of removed elements.
     * @example
     *
     * var array = ['a', 'b', 'c', 'd'];
     * var pulled = _.pullAt(array, [1, 3]);
     *
     * console.log(array);
     * // => ['a', 'c']
     *
     * console.log(pulled);
     * // => ['b', 'd']
     */
    var pullAt = flatRest(function(array, indexes) {
      var length = array == null ? 0 : array.length,
          result = baseAt(array, indexes);

      basePullAt(array, arrayMap(indexes, function(index) {
        return isIndex(index, length) ? +index : index;
      }).sort(compareAscending));

      return result;
    });

    /**
     * Removes all elements from `array` that `predicate` returns truthy for
     * and returns an array of the removed elements. The predicate is invoked
     * with three arguments: (value, index, array).
     *
     * **Note:** Unlike `_.filter`, this method mutates `array`. Use `_.pull`
     * to pull elements from an array by value.
     *
     * @static
     * @memberOf _
     * @since 2.0.0
     * @category Array
     * @param {Array} array The array to modify.
     * @param {Function} [predicate=_.identity] The function invoked per iteration.
     * @returns {Array} Returns the new array of removed elements.
     * @example
     *
     * var array = [1, 2, 3, 4];
     * var evens = _.remove(array, function(n) {
     *   return n % 2 == 0;
     * });
     *
     * console.log(array);
     * // => [1, 3]
     *
     * console.log(evens);
     * // => [2, 4]
     */
    function remove(array, predicate) {
      var result = [];
      if (!(array && array.length)) {
        return result;
      }
      var index = -1,
          indexes = [],
          length = array.length;

      predicate = getIteratee(predicate, 3);
      while (++index < length) {
        var value = array[index];
        if (predicate(value, index, array)) {
          result.push(value);
          indexes.push(index);
        }
      }
      basePullAt(array, indexes);
      return result;
    }

    /**
     * Reverses `array` so that the first element becomes the last, the second
     * element becomes the second to last, and so on.
     *
     * **Note:** This method mutates `array` and is based on
     * [`Array#reverse`](https://mdn.io/Array/reverse).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Array
     * @param {Array} array The array to modify.
     * @returns {Array} Returns `array`.
     * @example
     *
     * var array = [1, 2, 3];
     *
     * _.reverse(array);
     * // => [3, 2, 1]
     *
     * console.log(array);
     * // => [3, 2, 1]
     */
    function reverse(array) {
      return array == null ? array : nativeReverse.call(array);
    }

    /**
     * Creates a slice of `array` from `start` up to, but not including, `end`.
     *
     * **Note:** This method is used instead of
     * [`Array#slice`](https://mdn.io/Array/slice) to ensure dense arrays are
     * returned.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Array
     * @param {Array} array The array to slice.
     * @param {number} [start=0] The start position.
     * @param {number} [end=array.length] The end position.
     * @returns {Array} Returns the slice of `array`.
     */
    function slice(array, start, end) {
      var length = array == null ? 0 : array.length;
      if (!length) {
        return [];
      }
      if (end && typeof end != 'number' && isIterateeCall(array, start, end)) {
        start = 0;
        end = length;
      }
      else {
        start = start == null ? 0 : toInteger(start);
        end = end === undefined ? length : toInteger(end);
      }
      return baseSlice(array, start, end);
    }

    /**
     * Uses a binary search to determine the lowest index at which `value`
     * should be inserted into `array` in order to maintain its sort order.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Array
     * @param {Array} array The sorted array to inspect.
     * @param {*} value The value to evaluate.
     * @returns {number} Returns the index at which `value` should be inserted
     *  into `array`.
     * @example
     *
     * _.sortedIndex([30, 50], 40);
     * // => 1
     */
    function sortedIndex(array, value) {
      return baseSortedIndex(array, value);
    }

    /**
     * This method is like `_.sortedIndex` except that it accepts `iteratee`
     * which is invoked for `value` and each element of `array` to compute their
     * sort ranking. The iteratee is invoked with one argument: (value).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Array
     * @param {Array} array The sorted array to inspect.
     * @param {*} value The value to evaluate.
     * @param {Function} [iteratee=_.identity] The iteratee invoked per element.
     * @returns {number} Returns the index at which `value` should be inserted
     *  into `array`.
     * @example
     *
     * var objects = [{ 'x': 4 }, { 'x': 5 }];
     *
     * _.sortedIndexBy(objects, { 'x': 4 }, function(o) { return o.x; });
     * // => 0
     *
     * // The `_.property` iteratee shorthand.
     * _.sortedIndexBy(objects, { 'x': 4 }, 'x');
     * // => 0
     */
    function sortedIndexBy(array, value, iteratee) {
      return baseSortedIndexBy(array, value, getIteratee(iteratee, 2));
    }

    /**
     * This method is like `_.indexOf` except that it performs a binary
     * search on a sorted `array`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Array
     * @param {Array} array The array to inspect.
     * @param {*} value The value to search for.
     * @returns {number} Returns the index of the matched value, else `-1`.
     * @example
     *
     * _.sortedIndexOf([4, 5, 5, 5, 6], 5);
     * // => 1
     */
    function sortedIndexOf(array, value) {
      var length = array == null ? 0 : array.length;
      if (length) {
        var index = baseSortedIndex(array, value);
        if (index < length && eq(array[index], value)) {
          return index;
        }
      }
      return -1;
    }

    /**
     * This method is like `_.sortedIndex` except that it returns the highest
     * index at which `value` should be inserted into `array` in order to
     * maintain its sort order.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Array
     * @param {Array} array The sorted array to inspect.
     * @param {*} value The value to evaluate.
     * @returns {number} Returns the index at which `value` should be inserted
     *  into `array`.
     * @example
     *
     * _.sortedLastIndex([4, 5, 5, 5, 6], 5);
     * // => 4
     */
    function sortedLastIndex(array, value) {
      return baseSortedIndex(array, value, true);
    }

    /**
     * This method is like `_.sortedLastIndex` except that it accepts `iteratee`
     * which is invoked for `value` and each element of `array` to compute their
     * sort ranking. The iteratee is invoked with one argument: (value).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Array
     * @param {Array} array The sorted array to inspect.
     * @param {*} value The value to evaluate.
     * @param {Function} [iteratee=_.identity] The iteratee invoked per element.
     * @returns {number} Returns the index at which `value` should be inserted
     *  into `array`.
     * @example
     *
     * var objects = [{ 'x': 4 }, { 'x': 5 }];
     *
     * _.sortedLastIndexBy(objects, { 'x': 4 }, function(o) { return o.x; });
     * // => 1
     *
     * // The `_.property` iteratee shorthand.
     * _.sortedLastIndexBy(objects, { 'x': 4 }, 'x');
     * // => 1
     */
    function sortedLastIndexBy(array, value, iteratee) {
      return baseSortedIndexBy(array, value, getIteratee(iteratee, 2), true);
    }

    /**
     * This method is like `_.lastIndexOf` except that it performs a binary
     * search on a sorted `array`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Array
     * @param {Array} array The array to inspect.
     * @param {*} value The value to search for.
     * @returns {number} Returns the index of the matched value, else `-1`.
     * @example
     *
     * _.sortedLastIndexOf([4, 5, 5, 5, 6], 5);
     * // => 3
     */
    function sortedLastIndexOf(array, value) {
      var length = array == null ? 0 : array.length;
      if (length) {
        var index = baseSortedIndex(array, value, true) - 1;
        if (eq(array[index], value)) {
          return index;
        }
      }
      return -1;
    }

    /**
     * This method is like `_.uniq` except that it's designed and optimized
     * for sorted arrays.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Array
     * @param {Array} array The array to inspect.
     * @returns {Array} Returns the new duplicate free array.
     * @example
     *
     * _.sortedUniq([1, 1, 2]);
     * // => [1, 2]
     */
    function sortedUniq(array) {
      return (array && array.length)
        ? baseSortedUniq(array)
        : [];
    }

    /**
     * This method is like `_.uniqBy` except that it's designed and optimized
     * for sorted arrays.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Array
     * @param {Array} array The array to inspect.
     * @param {Function} [iteratee] The iteratee invoked per element.
     * @returns {Array} Returns the new duplicate free array.
     * @example
     *
     * _.sortedUniqBy([1.1, 1.2, 2.3, 2.4], Math.floor);
     * // => [1.1, 2.3]
     */
    function sortedUniqBy(array, iteratee) {
      return (array && array.length)
        ? baseSortedUniq(array, getIteratee(iteratee, 2))
        : [];
    }

    /**
     * Gets all but the first element of `array`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Array
     * @param {Array} array The array to query.
     * @returns {Array} Returns the slice of `array`.
     * @example
     *
     * _.tail([1, 2, 3]);
     * // => [2, 3]
     */
    function tail(array) {
      var length = array == null ? 0 : array.length;
      return length ? baseSlice(array, 1, length) : [];
    }

    /**
     * Creates a slice of `array` with `n` elements taken from the beginning.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Array
     * @param {Array} array The array to query.
     * @param {number} [n=1] The number of elements to take.
     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
     * @returns {Array} Returns the slice of `array`.
     * @example
     *
     * _.take([1, 2, 3]);
     * // => [1]
     *
     * _.take([1, 2, 3], 2);
     * // => [1, 2]
     *
     * _.take([1, 2, 3], 5);
     * // => [1, 2, 3]
     *
     * _.take([1, 2, 3], 0);
     * // => []
     */
    function take(array, n, guard) {
      if (!(array && array.length)) {
        return [];
      }
      n = (guard || n === undefined) ? 1 : toInteger(n);
      return baseSlice(array, 0, n < 0 ? 0 : n);
    }

    /**
     * Creates a slice of `array` with `n` elements taken from the end.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Array
     * @param {Array} array The array to query.
     * @param {number} [n=1] The number of elements to take.
     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
     * @returns {Array} Returns the slice of `array`.
     * @example
     *
     * _.takeRight([1, 2, 3]);
     * // => [3]
     *
     * _.takeRight([1, 2, 3], 2);
     * // => [2, 3]
     *
     * _.takeRight([1, 2, 3], 5);
     * // => [1, 2, 3]
     *
     * _.takeRight([1, 2, 3], 0);
     * // => []
     */
    function takeRight(array, n, guard) {
      var length = array == null ? 0 : array.length;
      if (!length) {
        return [];
      }
      n = (guard || n === undefined) ? 1 : toInteger(n);
      n = length - n;
      return baseSlice(array, n < 0 ? 0 : n, length);
    }

    /**
     * Creates a slice of `array` with elements taken from the end. Elements are
     * taken until `predicate` returns falsey. The predicate is invoked with
     * three arguments: (value, index, array).
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Array
     * @param {Array} array The array to query.
     * @param {Function} [predicate=_.identity] The function invoked per iteration.
     * @returns {Array} Returns the slice of `array`.
     * @example
     *
     * var users = [
     *   { 'user': 'barney',  'active': true },
     *   { 'user': 'fred',    'active': false },
     *   { 'user': 'pebbles', 'active': false }
     * ];
     *
     * _.takeRightWhile(users, function(o) { return !o.active; });
     * // => objects for ['fred', 'pebbles']
     *
     * // The `_.matches` iteratee shorthand.
     * _.takeRightWhile(users, { 'user': 'pebbles', 'active': false });
     * // => objects for ['pebbles']
     *
     * // The `_.matchesProperty` iteratee shorthand.
     * _.takeRightWhile(users, ['active', false]);
     * // => objects for ['fred', 'pebbles']
     *
     * // The `_.property` iteratee shorthand.
     * _.takeRightWhile(users, 'active');
     * // => []
     */
    function takeRightWhile(array, predicate) {
      return (array && array.length)
        ? baseWhile(array, getIteratee(predicate, 3), false, true)
        : [];
    }

    /**
     * Creates a slice of `array` with elements taken from the beginning. Elements
     * are taken until `predicate` returns falsey. The predicate is invoked with
     * three arguments: (value, index, array).
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Array
     * @param {Array} array The array to query.
     * @param {Function} [predicate=_.identity] The function invoked per iteration.
     * @returns {Array} Returns the slice of `array`.
     * @example
     *
     * var users = [
     *   { 'user': 'barney',  'active': false },
     *   { 'user': 'fred',    'active': false },
     *   { 'user': 'pebbles', 'active': true }
     * ];
     *
     * _.takeWhile(users, function(o) { return !o.active; });
     * // => objects for ['barney', 'fred']
     *
     * // The `_.matches` iteratee shorthand.
     * _.takeWhile(users, { 'user': 'barney', 'active': false });
     * // => objects for ['barney']
     *
     * // The `_.matchesProperty` iteratee shorthand.
     * _.takeWhile(users, ['active', false]);
     * // => objects for ['barney', 'fred']
     *
     * // The `_.property` iteratee shorthand.
     * _.takeWhile(users, 'active');
     * // => []
     */
    function takeWhile(array, predicate) {
      return (array && array.length)
        ? baseWhile(array, getIteratee(predicate, 3))
        : [];
    }

    /**
     * Creates an array of unique values, in order, from all given arrays using
     * [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
     * for equality comparisons.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Array
     * @param {...Array} [arrays] The arrays to inspect.
     * @returns {Array} Returns the new array of combined values.
     * @example
     *
     * _.union([2], [1, 2]);
     * // => [2, 1]
     */
    var union = baseRest(function(arrays) {
      return baseUniq(baseFlatten(arrays, 1, isArrayLikeObject, true));
    });

    /**
     * This method is like `_.union` except that it accepts `iteratee` which is
     * invoked for each element of each `arrays` to generate the criterion by
     * which uniqueness is computed. Result values are chosen from the first
     * array in which the value occurs. The iteratee is invoked with one argument:
     * (value).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Array
     * @param {...Array} [arrays] The arrays to inspect.
     * @param {Function} [iteratee=_.identity] The iteratee invoked per element.
     * @returns {Array} Returns the new array of combined values.
     * @example
     *
     * _.unionBy([2.1], [1.2, 2.3], Math.floor);
     * // => [2.1, 1.2]
     *
     * // The `_.property` iteratee shorthand.
     * _.unionBy([{ 'x': 1 }], [{ 'x': 2 }, { 'x': 1 }], 'x');
     * // => [{ 'x': 1 }, { 'x': 2 }]
     */
    var unionBy = baseRest(function(arrays) {
      var iteratee = last(arrays);
      if (isArrayLikeObject(iteratee)) {
        iteratee = undefined;
      }
      return baseUniq(baseFlatten(arrays, 1, isArrayLikeObject, true), getIteratee(iteratee, 2));
    });

    /**
     * This method is like `_.union` except that it accepts `comparator` which
     * is invoked to compare elements of `arrays`. Result values are chosen from
     * the first array in which the value occurs. The comparator is invoked
     * with two arguments: (arrVal, othVal).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Array
     * @param {...Array} [arrays] The arrays to inspect.
     * @param {Function} [comparator] The comparator invoked per element.
     * @returns {Array} Returns the new array of combined values.
     * @example
     *
     * var objects = [{ 'x': 1, 'y': 2 }, { 'x': 2, 'y': 1 }];
     * var others = [{ 'x': 1, 'y': 1 }, { 'x': 1, 'y': 2 }];
     *
     * _.unionWith(objects, others, _.isEqual);
     * // => [{ 'x': 1, 'y': 2 }, { 'x': 2, 'y': 1 }, { 'x': 1, 'y': 1 }]
     */
    var unionWith = baseRest(function(arrays) {
      var comparator = last(arrays);
      comparator = typeof comparator == 'function' ? comparator : undefined;
      return baseUniq(baseFlatten(arrays, 1, isArrayLikeObject, true), undefined, comparator);
    });

    /**
     * Creates a duplicate-free version of an array, using
     * [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
     * for equality comparisons, in which only the first occurrence of each element
     * is kept. The order of result values is determined by the order they occur
     * in the array.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Array
     * @param {Array} array The array to inspect.
     * @returns {Array} Returns the new duplicate free array.
     * @example
     *
     * _.uniq([2, 1, 2]);
     * // => [2, 1]
     */
    function uniq(array) {
      return (array && array.length) ? baseUniq(array) : [];
    }

    /**
     * This method is like `_.uniq` except that it accepts `iteratee` which is
     * invoked for each element in `array` to generate the criterion by which
     * uniqueness is computed. The order of result values is determined by the
     * order they occur in the array. The iteratee is invoked with one argument:
     * (value).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Array
     * @param {Array} array The array to inspect.
     * @param {Function} [iteratee=_.identity] The iteratee invoked per element.
     * @returns {Array} Returns the new duplicate free array.
     * @example
     *
     * _.uniqBy([2.1, 1.2, 2.3], Math.floor);
     * // => [2.1, 1.2]
     *
     * // The `_.property` iteratee shorthand.
     * _.uniqBy([{ 'x': 1 }, { 'x': 2 }, { 'x': 1 }], 'x');
     * // => [{ 'x': 1 }, { 'x': 2 }]
     */
    function uniqBy(array, iteratee) {
      return (array && array.length) ? baseUniq(array, getIteratee(iteratee, 2)) : [];
    }

    /**
     * This method is like `_.uniq` except that it accepts `comparator` which
     * is invoked to compare elements of `array`. The order of result values is
     * determined by the order they occur in the array.The comparator is invoked
     * with two arguments: (arrVal, othVal).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Array
     * @param {Array} array The array to inspect.
     * @param {Function} [comparator] The comparator invoked per element.
     * @returns {Array} Returns the new duplicate free array.
     * @example
     *
     * var objects = [{ 'x': 1, 'y': 2 }, { 'x': 2, 'y': 1 }, { 'x': 1, 'y': 2 }];
     *
     * _.uniqWith(objects, _.isEqual);
     * // => [{ 'x': 1, 'y': 2 }, { 'x': 2, 'y': 1 }]
     */
    function uniqWith(array, comparator) {
      comparator = typeof comparator == 'function' ? comparator : undefined;
      return (array && array.length) ? baseUniq(array, undefined, comparator) : [];
    }

    /**
     * This method is like `_.zip` except that it accepts an array of grouped
     * elements and creates an array regrouping the elements to their pre-zip
     * configuration.
     *
     * @static
     * @memberOf _
     * @since 1.2.0
     * @category Array
     * @param {Array} array The array of grouped elements to process.
     * @returns {Array} Returns the new array of regrouped elements.
     * @example
     *
     * var zipped = _.zip(['a', 'b'], [1, 2], [true, false]);
     * // => [['a', 1, true], ['b', 2, false]]
     *
     * _.unzip(zipped);
     * // => [['a', 'b'], [1, 2], [true, false]]
     */
    function unzip(array) {
      if (!(array && array.length)) {
        return [];
      }
      var length = 0;
      array = arrayFilter(array, function(group) {
        if (isArrayLikeObject(group)) {
          length = nativeMax(group.length, length);
          return true;
        }
      });
      return baseTimes(length, function(index) {
        return arrayMap(array, baseProperty(index));
      });
    }

    /**
     * This method is like `_.unzip` except that it accepts `iteratee` to specify
     * how regrouped values should be combined. The iteratee is invoked with the
     * elements of each group: (...group).
     *
     * @static
     * @memberOf _
     * @since 3.8.0
     * @category Array
     * @param {Array} array The array of grouped elements to process.
     * @param {Function} [iteratee=_.identity] The function to combine
     *  regrouped values.
     * @returns {Array} Returns the new array of regrouped elements.
     * @example
     *
     * var zipped = _.zip([1, 2], [10, 20], [100, 200]);
     * // => [[1, 10, 100], [2, 20, 200]]
     *
     * _.unzipWith(zipped, _.add);
     * // => [3, 30, 300]
     */
    function unzipWith(array, iteratee) {
      if (!(array && array.length)) {
        return [];
      }
      var result = unzip(array);
      if (iteratee == null) {
        return result;
      }
      return arrayMap(result, function(group) {
        return apply(iteratee, undefined, group);
      });
    }

    /**
     * Creates an array excluding all given values using
     * [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
     * for equality comparisons.
     *
     * **Note:** Unlike `_.pull`, this method returns a new array.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Array
     * @param {Array} array The array to inspect.
     * @param {...*} [values] The values to exclude.
     * @returns {Array} Returns the new array of filtered values.
     * @see _.difference, _.xor
     * @example
     *
     * _.without([2, 1, 2, 3], 1, 2);
     * // => [3]
     */
    var without = baseRest(function(array, values) {
      return isArrayLikeObject(array)
        ? baseDifference(array, values)
        : [];
    });

    /**
     * Creates an array of unique values that is the
     * [symmetric difference](https://en.wikipedia.org/wiki/Symmetric_difference)
     * of the given arrays. The order of result values is determined by the order
     * they occur in the arrays.
     *
     * @static
     * @memberOf _
     * @since 2.4.0
     * @category Array
     * @param {...Array} [arrays] The arrays to inspect.
     * @returns {Array} Returns the new array of filtered values.
     * @see _.difference, _.without
     * @example
     *
     * _.xor([2, 1], [2, 3]);
     * // => [1, 3]
     */
    var xor = baseRest(function(arrays) {
      return baseXor(arrayFilter(arrays, isArrayLikeObject));
    });

    /**
     * This method is like `_.xor` except that it accepts `iteratee` which is
     * invoked for each element of each `arrays` to generate the criterion by
     * which by which they're compared. The order of result values is determined
     * by the order they occur in the arrays. The iteratee is invoked with one
     * argument: (value).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Array
     * @param {...Array} [arrays] The arrays to inspect.
     * @param {Function} [iteratee=_.identity] The iteratee invoked per element.
     * @returns {Array} Returns the new array of filtered values.
     * @example
     *
     * _.xorBy([2.1, 1.2], [2.3, 3.4], Math.floor);
     * // => [1.2, 3.4]
     *
     * // The `_.property` iteratee shorthand.
     * _.xorBy([{ 'x': 1 }], [{ 'x': 2 }, { 'x': 1 }], 'x');
     * // => [{ 'x': 2 }]
     */
    var xorBy = baseRest(function(arrays) {
      var iteratee = last(arrays);
      if (isArrayLikeObject(iteratee)) {
        iteratee = undefined;
      }
      return baseXor(arrayFilter(arrays, isArrayLikeObject), getIteratee(iteratee, 2));
    });

    /**
     * This method is like `_.xor` except that it accepts `comparator` which is
     * invoked to compare elements of `arrays`. The order of result values is
     * determined by the order they occur in the arrays. The comparator is invoked
     * with two arguments: (arrVal, othVal).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Array
     * @param {...Array} [arrays] The arrays to inspect.
     * @param {Function} [comparator] The comparator invoked per element.
     * @returns {Array} Returns the new array of filtered values.
     * @example
     *
     * var objects = [{ 'x': 1, 'y': 2 }, { 'x': 2, 'y': 1 }];
     * var others = [{ 'x': 1, 'y': 1 }, { 'x': 1, 'y': 2 }];
     *
     * _.xorWith(objects, others, _.isEqual);
     * // => [{ 'x': 2, 'y': 1 }, { 'x': 1, 'y': 1 }]
     */
    var xorWith = baseRest(function(arrays) {
      var comparator = last(arrays);
      comparator = typeof comparator == 'function' ? comparator : undefined;
      return baseXor(arrayFilter(arrays, isArrayLikeObject), undefined, comparator);
    });

    /**
     * Creates an array of grouped elements, the first of which contains the
     * first elements of the given arrays, the second of which contains the
     * second elements of the given arrays, and so on.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Array
     * @param {...Array} [arrays] The arrays to process.
     * @returns {Array} Returns the new array of grouped elements.
     * @example
     *
     * _.zip(['a', 'b'], [1, 2], [true, false]);
     * // => [['a', 1, true], ['b', 2, false]]
     */
    var zip = baseRest(unzip);

    /**
     * This method is like `_.fromPairs` except that it accepts two arrays,
     * one of property identifiers and one of corresponding values.
     *
     * @static
     * @memberOf _
     * @since 0.4.0
     * @category Array
     * @param {Array} [props=[]] The property identifiers.
     * @param {Array} [values=[]] The property values.
     * @returns {Object} Returns the new object.
     * @example
     *
     * _.zipObject(['a', 'b'], [1, 2]);
     * // => { 'a': 1, 'b': 2 }
     */
    function zipObject(props, values) {
      return baseZipObject(props || [], values || [], assignValue);
    }

    /**
     * This method is like `_.zipObject` except that it supports property paths.
     *
     * @static
     * @memberOf _
     * @since 4.1.0
     * @category Array
     * @param {Array} [props=[]] The property identifiers.
     * @param {Array} [values=[]] The property values.
     * @returns {Object} Returns the new object.
     * @example
     *
     * _.zipObjectDeep(['a.b[0].c', 'a.b[1].d'], [1, 2]);
     * // => { 'a': { 'b': [{ 'c': 1 }, { 'd': 2 }] } }
     */
    function zipObjectDeep(props, values) {
      return baseZipObject(props || [], values || [], baseSet);
    }

    /**
     * This method is like `_.zip` except that it accepts `iteratee` to specify
     * how grouped values should be combined. The iteratee is invoked with the
     * elements of each group: (...group).
     *
     * @static
     * @memberOf _
     * @since 3.8.0
     * @category Array
     * @param {...Array} [arrays] The arrays to process.
     * @param {Function} [iteratee=_.identity] The function to combine
     *  grouped values.
     * @returns {Array} Returns the new array of grouped elements.
     * @example
     *
     * _.zipWith([1, 2], [10, 20], [100, 200], function(a, b, c) {
     *   return a + b + c;
     * });
     * // => [111, 222]
     */
    var zipWith = baseRest(function(arrays) {
      var length = arrays.length,
          iteratee = length > 1 ? arrays[length - 1] : undefined;

      iteratee = typeof iteratee == 'function' ? (arrays.pop(), iteratee) : undefined;
      return unzipWith(arrays, iteratee);
    });

    /*------------------------------------------------------------------------*/

    /**
     * Creates a `lodash` wrapper instance that wraps `value` with explicit method
     * chain sequences enabled. The result of such sequences must be unwrapped
     * with `_#value`.
     *
     * @static
     * @memberOf _
     * @since 1.3.0
     * @category Seq
     * @param {*} value The value to wrap.
     * @returns {Object} Returns the new `lodash` wrapper instance.
     * @example
     *
     * var users = [
     *   { 'user': 'barney',  'age': 36 },
     *   { 'user': 'fred',    'age': 40 },
     *   { 'user': 'pebbles', 'age': 1 }
     * ];
     *
     * var youngest = _
     *   .chain(users)
     *   .sortBy('age')
     *   .map(function(o) {
     *     return o.user + ' is ' + o.age;
     *   })
     *   .head()
     *   .value();
     * // => 'pebbles is 1'
     */
    function chain(value) {
      var result = lodash(value);
      result.__chain__ = true;
      return result;
    }

    /**
     * This method invokes `interceptor` and returns `value`. The interceptor
     * is invoked with one argument; (value). The purpose of this method is to
     * "tap into" a method chain sequence in order to modify intermediate results.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Seq
     * @param {*} value The value to provide to `interceptor`.
     * @param {Function} interceptor The function to invoke.
     * @returns {*} Returns `value`.
     * @example
     *
     * _([1, 2, 3])
     *  .tap(function(array) {
     *    // Mutate input array.
     *    array.pop();
     *  })
     *  .reverse()
     *  .value();
     * // => [2, 1]
     */
    function tap(value, interceptor) {
      interceptor(value);
      return value;
    }

    /**
     * This method is like `_.tap` except that it returns the result of `interceptor`.
     * The purpose of this method is to "pass thru" values replacing intermediate
     * results in a method chain sequence.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Seq
     * @param {*} value The value to provide to `interceptor`.
     * @param {Function} interceptor The function to invoke.
     * @returns {*} Returns the result of `interceptor`.
     * @example
     *
     * _('  abc  ')
     *  .chain()
     *  .trim()
     *  .thru(function(value) {
     *    return [value];
     *  })
     *  .value();
     * // => ['abc']
     */
    function thru(value, interceptor) {
      return interceptor(value);
    }

    /**
     * This method is the wrapper version of `_.at`.
     *
     * @name at
     * @memberOf _
     * @since 1.0.0
     * @category Seq
     * @param {...(string|string[])} [paths] The property paths to pick.
     * @returns {Object} Returns the new `lodash` wrapper instance.
     * @example
     *
     * var object = { 'a': [{ 'b': { 'c': 3 } }, 4] };
     *
     * _(object).at(['a[0].b.c', 'a[1]']).value();
     * // => [3, 4]
     */
    var wrapperAt = flatRest(function(paths) {
      var length = paths.length,
          start = length ? paths[0] : 0,
          value = this.__wrapped__,
          interceptor = function(object) { return baseAt(object, paths); };

      if (length > 1 || this.__actions__.length ||
          !(value instanceof LazyWrapper) || !isIndex(start)) {
        return this.thru(interceptor);
      }
      value = value.slice(start, +start + (length ? 1 : 0));
      value.__actions__.push({
        'func': thru,
        'args': [interceptor],
        'thisArg': undefined
      });
      return new LodashWrapper(value, this.__chain__).thru(function(array) {
        if (length && !array.length) {
          array.push(undefined);
        }
        return array;
      });
    });

    /**
     * Creates a `lodash` wrapper instance with explicit method chain sequences enabled.
     *
     * @name chain
     * @memberOf _
     * @since 0.1.0
     * @category Seq
     * @returns {Object} Returns the new `lodash` wrapper instance.
     * @example
     *
     * var users = [
     *   { 'user': 'barney', 'age': 36 },
     *   { 'user': 'fred',   'age': 40 }
     * ];
     *
     * // A sequence without explicit chaining.
     * _(users).head();
     * // => { 'user': 'barney', 'age': 36 }
     *
     * // A sequence with explicit chaining.
     * _(users)
     *   .chain()
     *   .head()
     *   .pick('user')
     *   .value();
     * // => { 'user': 'barney' }
     */
    function wrapperChain() {
      return chain(this);
    }

    /**
     * Executes the chain sequence and returns the wrapped result.
     *
     * @name commit
     * @memberOf _
     * @since 3.2.0
     * @category Seq
     * @returns {Object} Returns the new `lodash` wrapper instance.
     * @example
     *
     * var array = [1, 2];
     * var wrapped = _(array).push(3);
     *
     * console.log(array);
     * // => [1, 2]
     *
     * wrapped = wrapped.commit();
     * console.log(array);
     * // => [1, 2, 3]
     *
     * wrapped.last();
     * // => 3
     *
     * console.log(array);
     * // => [1, 2, 3]
     */
    function wrapperCommit() {
      return new LodashWrapper(this.value(), this.__chain__);
    }

    /**
     * Gets the next value on a wrapped object following the
     * [iterator protocol](https://mdn.io/iteration_protocols#iterator).
     *
     * @name next
     * @memberOf _
     * @since 4.0.0
     * @category Seq
     * @returns {Object} Returns the next iterator value.
     * @example
     *
     * var wrapped = _([1, 2]);
     *
     * wrapped.next();
     * // => { 'done': false, 'value': 1 }
     *
     * wrapped.next();
     * // => { 'done': false, 'value': 2 }
     *
     * wrapped.next();
     * // => { 'done': true, 'value': undefined }
     */
    function wrapperNext() {
      if (this.__values__ === undefined) {
        this.__values__ = toArray(this.value());
      }
      var done = this.__index__ >= this.__values__.length,
          value = done ? undefined : this.__values__[this.__index__++];

      return { 'done': done, 'value': value };
    }

    /**
     * Enables the wrapper to be iterable.
     *
     * @name Symbol.iterator
     * @memberOf _
     * @since 4.0.0
     * @category Seq
     * @returns {Object} Returns the wrapper object.
     * @example
     *
     * var wrapped = _([1, 2]);
     *
     * wrapped[Symbol.iterator]() === wrapped;
     * // => true
     *
     * Array.from(wrapped);
     * // => [1, 2]
     */
    function wrapperToIterator() {
      return this;
    }

    /**
     * Creates a clone of the chain sequence planting `value` as the wrapped value.
     *
     * @name plant
     * @memberOf _
     * @since 3.2.0
     * @category Seq
     * @param {*} value The value to plant.
     * @returns {Object} Returns the new `lodash` wrapper instance.
     * @example
     *
     * function square(n) {
     *   return n * n;
     * }
     *
     * var wrapped = _([1, 2]).map(square);
     * var other = wrapped.plant([3, 4]);
     *
     * other.value();
     * // => [9, 16]
     *
     * wrapped.value();
     * // => [1, 4]
     */
    function wrapperPlant(value) {
      var result,
          parent = this;

      while (parent instanceof baseLodash) {
        var clone = wrapperClone(parent);
        clone.__index__ = 0;
        clone.__values__ = undefined;
        if (result) {
          previous.__wrapped__ = clone;
        } else {
          result = clone;
        }
        var previous = clone;
        parent = parent.__wrapped__;
      }
      previous.__wrapped__ = value;
      return result;
    }

    /**
     * This method is the wrapper version of `_.reverse`.
     *
     * **Note:** This method mutates the wrapped array.
     *
     * @name reverse
     * @memberOf _
     * @since 0.1.0
     * @category Seq
     * @returns {Object} Returns the new `lodash` wrapper instance.
     * @example
     *
     * var array = [1, 2, 3];
     *
     * _(array).reverse().value()
     * // => [3, 2, 1]
     *
     * console.log(array);
     * // => [3, 2, 1]
     */
    function wrapperReverse() {
      var value = this.__wrapped__;
      if (value instanceof LazyWrapper) {
        var wrapped = value;
        if (this.__actions__.length) {
          wrapped = new LazyWrapper(this);
        }
        wrapped = wrapped.reverse();
        wrapped.__actions__.push({
          'func': thru,
          'args': [reverse],
          'thisArg': undefined
        });
        return new LodashWrapper(wrapped, this.__chain__);
      }
      return this.thru(reverse);
    }

    /**
     * Executes the chain sequence to resolve the unwrapped value.
     *
     * @name value
     * @memberOf _
     * @since 0.1.0
     * @alias toJSON, valueOf
     * @category Seq
     * @returns {*} Returns the resolved unwrapped value.
     * @example
     *
     * _([1, 2, 3]).value();
     * // => [1, 2, 3]
     */
    function wrapperValue() {
      return baseWrapperValue(this.__wrapped__, this.__actions__);
    }

    /*------------------------------------------------------------------------*/

    /**
     * Creates an object composed of keys generated from the results of running
     * each element of `collection` thru `iteratee`. The corresponding value of
     * each key is the number of times the key was returned by `iteratee`. The
     * iteratee is invoked with one argument: (value).
     *
     * @static
     * @memberOf _
     * @since 0.5.0
     * @category Collection
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Function} [iteratee=_.identity] The iteratee to transform keys.
     * @returns {Object} Returns the composed aggregate object.
     * @example
     *
     * _.countBy([6.1, 4.2, 6.3], Math.floor);
     * // => { '4': 1, '6': 2 }
     *
     * // The `_.property` iteratee shorthand.
     * _.countBy(['one', 'two', 'three'], 'length');
     * // => { '3': 2, '5': 1 }
     */
    var countBy = createAggregator(function(result, value, key) {
      if (hasOwnProperty.call(result, key)) {
        ++result[key];
      } else {
        baseAssignValue(result, key, 1);
      }
    });

    /**
     * Checks if `predicate` returns truthy for **all** elements of `collection`.
     * Iteration is stopped once `predicate` returns falsey. The predicate is
     * invoked with three arguments: (value, index|key, collection).
     *
     * **Note:** This method returns `true` for
     * [empty collections](https://en.wikipedia.org/wiki/Empty_set) because
     * [everything is true](https://en.wikipedia.org/wiki/Vacuous_truth) of
     * elements of empty collections.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Collection
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Function} [predicate=_.identity] The function invoked per iteration.
     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
     * @returns {boolean} Returns `true` if all elements pass the predicate check,
     *  else `false`.
     * @example
     *
     * _.every([true, 1, null, 'yes'], Boolean);
     * // => false
     *
     * var users = [
     *   { 'user': 'barney', 'age': 36, 'active': false },
     *   { 'user': 'fred',   'age': 40, 'active': false }
     * ];
     *
     * // The `_.matches` iteratee shorthand.
     * _.every(users, { 'user': 'barney', 'active': false });
     * // => false
     *
     * // The `_.matchesProperty` iteratee shorthand.
     * _.every(users, ['active', false]);
     * // => true
     *
     * // The `_.property` iteratee shorthand.
     * _.every(users, 'active');
     * // => false
     */
    function every(collection, predicate, guard) {
      var func = isArray(collection) ? arrayEvery : baseEvery;
      if (guard && isIterateeCall(collection, predicate, guard)) {
        predicate = undefined;
      }
      return func(collection, getIteratee(predicate, 3));
    }

    /**
     * Iterates over elements of `collection`, returning an array of all elements
     * `predicate` returns truthy for. The predicate is invoked with three
     * arguments: (value, index|key, collection).
     *
     * **Note:** Unlike `_.remove`, this method returns a new array.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Collection
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Function} [predicate=_.identity] The function invoked per iteration.
     * @returns {Array} Returns the new filtered array.
     * @see _.reject
     * @example
     *
     * var users = [
     *   { 'user': 'barney', 'age': 36, 'active': true },
     *   { 'user': 'fred',   'age': 40, 'active': false }
     * ];
     *
     * _.filter(users, function(o) { return !o.active; });
     * // => objects for ['fred']
     *
     * // The `_.matches` iteratee shorthand.
     * _.filter(users, { 'age': 36, 'active': true });
     * // => objects for ['barney']
     *
     * // The `_.matchesProperty` iteratee shorthand.
     * _.filter(users, ['active', false]);
     * // => objects for ['fred']
     *
     * // The `_.property` iteratee shorthand.
     * _.filter(users, 'active');
     * // => objects for ['barney']
     *
     * // Combining several predicates using `_.overEvery` or `_.overSome`.
     * _.filter(users, _.overSome([{ 'age': 36 }, ['age', 40]]));
     * // => objects for ['fred', 'barney']
     */
    function filter(collection, predicate) {
      var func = isArray(collection) ? arrayFilter : baseFilter;
      return func(collection, getIteratee(predicate, 3));
    }

    /**
     * Iterates over elements of `collection`, returning the first element
     * `predicate` returns truthy for. The predicate is invoked with three
     * arguments: (value, index|key, collection).
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Collection
     * @param {Array|Object} collection The collection to inspect.
     * @param {Function} [predicate=_.identity] The function invoked per iteration.
     * @param {number} [fromIndex=0] The index to search from.
     * @returns {*} Returns the matched element, else `undefined`.
     * @example
     *
     * var users = [
     *   { 'user': 'barney',  'age': 36, 'active': true },
     *   { 'user': 'fred',    'age': 40, 'active': false },
     *   { 'user': 'pebbles', 'age': 1,  'active': true }
     * ];
     *
     * _.find(users, function(o) { return o.age < 40; });
     * // => object for 'barney'
     *
     * // The `_.matches` iteratee shorthand.
     * _.find(users, { 'age': 1, 'active': true });
     * // => object for 'pebbles'
     *
     * // The `_.matchesProperty` iteratee shorthand.
     * _.find(users, ['active', false]);
     * // => object for 'fred'
     *
     * // The `_.property` iteratee shorthand.
     * _.find(users, 'active');
     * // => object for 'barney'
     */
    var find = createFind(findIndex);

    /**
     * This method is like `_.find` except that it iterates over elements of
     * `collection` from right to left.
     *
     * @static
     * @memberOf _
     * @since 2.0.0
     * @category Collection
     * @param {Array|Object} collection The collection to inspect.
     * @param {Function} [predicate=_.identity] The function invoked per iteration.
     * @param {number} [fromIndex=collection.length-1] The index to search from.
     * @returns {*} Returns the matched element, else `undefined`.
     * @example
     *
     * _.findLast([1, 2, 3, 4], function(n) {
     *   return n % 2 == 1;
     * });
     * // => 3
     */
    var findLast = createFind(findLastIndex);

    /**
     * Creates a flattened array of values by running each element in `collection`
     * thru `iteratee` and flattening the mapped results. The iteratee is invoked
     * with three arguments: (value, index|key, collection).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Collection
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Function} [iteratee=_.identity] The function invoked per iteration.
     * @returns {Array} Returns the new flattened array.
     * @example
     *
     * function duplicate(n) {
     *   return [n, n];
     * }
     *
     * _.flatMap([1, 2], duplicate);
     * // => [1, 1, 2, 2]
     */
    function flatMap(collection, iteratee) {
      return baseFlatten(map(collection, iteratee), 1);
    }

    /**
     * This method is like `_.flatMap` except that it recursively flattens the
     * mapped results.
     *
     * @static
     * @memberOf _
     * @since 4.7.0
     * @category Collection
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Function} [iteratee=_.identity] The function invoked per iteration.
     * @returns {Array} Returns the new flattened array.
     * @example
     *
     * function duplicate(n) {
     *   return [[[n, n]]];
     * }
     *
     * _.flatMapDeep([1, 2], duplicate);
     * // => [1, 1, 2, 2]
     */
    function flatMapDeep(collection, iteratee) {
      return baseFlatten(map(collection, iteratee), INFINITY);
    }

    /**
     * This method is like `_.flatMap` except that it recursively flattens the
     * mapped results up to `depth` times.
     *
     * @static
     * @memberOf _
     * @since 4.7.0
     * @category Collection
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Function} [iteratee=_.identity] The function invoked per iteration.
     * @param {number} [depth=1] The maximum recursion depth.
     * @returns {Array} Returns the new flattened array.
     * @example
     *
     * function duplicate(n) {
     *   return [[[n, n]]];
     * }
     *
     * _.flatMapDepth([1, 2], duplicate, 2);
     * // => [[1, 1], [2, 2]]
     */
    function flatMapDepth(collection, iteratee, depth) {
      depth = depth === undefined ? 1 : toInteger(depth);
      return baseFlatten(map(collection, iteratee), depth);
    }

    /**
     * Iterates over elements of `collection` and invokes `iteratee` for each element.
     * The iteratee is invoked with three arguments: (value, index|key, collection).
     * Iteratee functions may exit iteration early by explicitly returning `false`.
     *
     * **Note:** As with other "Collections" methods, objects with a "length"
     * property are iterated like arrays. To avoid this behavior use `_.forIn`
     * or `_.forOwn` for object iteration.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @alias each
     * @category Collection
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Function} [iteratee=_.identity] The function invoked per iteration.
     * @returns {Array|Object} Returns `collection`.
     * @see _.forEachRight
     * @example
     *
     * _.forEach([1, 2], function(value) {
     *   console.log(value);
     * });
     * // => Logs `1` then `2`.
     *
     * _.forEach({ 'a': 1, 'b': 2 }, function(value, key) {
     *   console.log(key);
     * });
     * // => Logs 'a' then 'b' (iteration order is not guaranteed).
     */
    function forEach(collection, iteratee) {
      var func = isArray(collection) ? arrayEach : baseEach;
      return func(collection, getIteratee(iteratee, 3));
    }

    /**
     * This method is like `_.forEach` except that it iterates over elements of
     * `collection` from right to left.
     *
     * @static
     * @memberOf _
     * @since 2.0.0
     * @alias eachRight
     * @category Collection
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Function} [iteratee=_.identity] The function invoked per iteration.
     * @returns {Array|Object} Returns `collection`.
     * @see _.forEach
     * @example
     *
     * _.forEachRight([1, 2], function(value) {
     *   console.log(value);
     * });
     * // => Logs `2` then `1`.
     */
    function forEachRight(collection, iteratee) {
      var func = isArray(collection) ? arrayEachRight : baseEachRight;
      return func(collection, getIteratee(iteratee, 3));
    }

    /**
     * Creates an object composed of keys generated from the results of running
     * each element of `collection` thru `iteratee`. The order of grouped values
     * is determined by the order they occur in `collection`. The corresponding
     * value of each key is an array of elements responsible for generating the
     * key. The iteratee is invoked with one argument: (value).
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Collection
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Function} [iteratee=_.identity] The iteratee to transform keys.
     * @returns {Object} Returns the composed aggregate object.
     * @example
     *
     * _.groupBy([6.1, 4.2, 6.3], Math.floor);
     * // => { '4': [4.2], '6': [6.1, 6.3] }
     *
     * // The `_.property` iteratee shorthand.
     * _.groupBy(['one', 'two', 'three'], 'length');
     * // => { '3': ['one', 'two'], '5': ['three'] }
     */
    var groupBy = createAggregator(function(result, value, key) {
      if (hasOwnProperty.call(result, key)) {
        result[key].push(value);
      } else {
        baseAssignValue(result, key, [value]);
      }
    });

    /**
     * Checks if `value` is in `collection`. If `collection` is a string, it's
     * checked for a substring of `value`, otherwise
     * [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
     * is used for equality comparisons. If `fromIndex` is negative, it's used as
     * the offset from the end of `collection`.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Collection
     * @param {Array|Object|string} collection The collection to inspect.
     * @param {*} value The value to search for.
     * @param {number} [fromIndex=0] The index to search from.
     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.reduce`.
     * @returns {boolean} Returns `true` if `value` is found, else `false`.
     * @example
     *
     * _.includes([1, 2, 3], 1);
     * // => true
     *
     * _.includes([1, 2, 3], 1, 2);
     * // => false
     *
     * _.includes({ 'a': 1, 'b': 2 }, 1);
     * // => true
     *
     * _.includes('abcd', 'bc');
     * // => true
     */
    function includes(collection, value, fromIndex, guard) {
      collection = isArrayLike(collection) ? collection : values(collection);
      fromIndex = (fromIndex && !guard) ? toInteger(fromIndex) : 0;

      var length = collection.length;
      if (fromIndex < 0) {
        fromIndex = nativeMax(length + fromIndex, 0);
      }
      return isString(collection)
        ? (fromIndex <= length && collection.indexOf(value, fromIndex) > -1)
        : (!!length && baseIndexOf(collection, value, fromIndex) > -1);
    }

    /**
     * Invokes the method at `path` of each element in `collection`, returning
     * an array of the results of each invoked method. Any additional arguments
     * are provided to each invoked method. If `path` is a function, it's invoked
     * for, and `this` bound to, each element in `collection`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Collection
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Array|Function|string} path The path of the method to invoke or
     *  the function invoked per iteration.
     * @param {...*} [args] The arguments to invoke each method with.
     * @returns {Array} Returns the array of results.
     * @example
     *
     * _.invokeMap([[5, 1, 7], [3, 2, 1]], 'sort');
     * // => [[1, 5, 7], [1, 2, 3]]
     *
     * _.invokeMap([123, 456], String.prototype.split, '');
     * // => [['1', '2', '3'], ['4', '5', '6']]
     */
    var invokeMap = baseRest(function(collection, path, args) {
      var index = -1,
          isFunc = typeof path == 'function',
          result = isArrayLike(collection) ? Array(collection.length) : [];

      baseEach(collection, function(value) {
        result[++index] = isFunc ? apply(path, value, args) : baseInvoke(value, path, args);
      });
      return result;
    });

    /**
     * Creates an object composed of keys generated from the results of running
     * each element of `collection` thru `iteratee`. The corresponding value of
     * each key is the last element responsible for generating the key. The
     * iteratee is invoked with one argument: (value).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Collection
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Function} [iteratee=_.identity] The iteratee to transform keys.
     * @returns {Object} Returns the composed aggregate object.
     * @example
     *
     * var array = [
     *   { 'dir': 'left', 'code': 97 },
     *   { 'dir': 'right', 'code': 100 }
     * ];
     *
     * _.keyBy(array, function(o) {
     *   return String.fromCharCode(o.code);
     * });
     * // => { 'a': { 'dir': 'left', 'code': 97 }, 'd': { 'dir': 'right', 'code': 100 } }
     *
     * _.keyBy(array, 'dir');
     * // => { 'left': { 'dir': 'left', 'code': 97 }, 'right': { 'dir': 'right', 'code': 100 } }
     */
    var keyBy = createAggregator(function(result, value, key) {
      baseAssignValue(result, key, value);
    });

    /**
     * Creates an array of values by running each element in `collection` thru
     * `iteratee`. The iteratee is invoked with three arguments:
     * (value, index|key, collection).
     *
     * Many lodash methods are guarded to work as iteratees for methods like
     * `_.every`, `_.filter`, `_.map`, `_.mapValues`, `_.reject`, and `_.some`.
     *
     * The guarded methods are:
     * `ary`, `chunk`, `curry`, `curryRight`, `drop`, `dropRight`, `every`,
     * `fill`, `invert`, `parseInt`, `random`, `range`, `rangeRight`, `repeat`,
     * `sampleSize`, `slice`, `some`, `sortBy`, `split`, `take`, `takeRight`,
     * `template`, `trim`, `trimEnd`, `trimStart`, and `words`
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Collection
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Function} [iteratee=_.identity] The function invoked per iteration.
     * @returns {Array} Returns the new mapped array.
     * @example
     *
     * function square(n) {
     *   return n * n;
     * }
     *
     * _.map([4, 8], square);
     * // => [16, 64]
     *
     * _.map({ 'a': 4, 'b': 8 }, square);
     * // => [16, 64] (iteration order is not guaranteed)
     *
     * var users = [
     *   { 'user': 'barney' },
     *   { 'user': 'fred' }
     * ];
     *
     * // The `_.property` iteratee shorthand.
     * _.map(users, 'user');
     * // => ['barney', 'fred']
     */
    function map(collection, iteratee) {
      var func = isArray(collection) ? arrayMap : baseMap;
      return func(collection, getIteratee(iteratee, 3));
    }

    /**
     * This method is like `_.sortBy` except that it allows specifying the sort
     * orders of the iteratees to sort by. If `orders` is unspecified, all values
     * are sorted in ascending order. Otherwise, specify an order of "desc" for
     * descending or "asc" for ascending sort order of corresponding values.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Collection
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Array[]|Function[]|Object[]|string[]} [iteratees=[_.identity]]
     *  The iteratees to sort by.
     * @param {string[]} [orders] The sort orders of `iteratees`.
     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.reduce`.
     * @returns {Array} Returns the new sorted array.
     * @example
     *
     * var users = [
     *   { 'user': 'fred',   'age': 48 },
     *   { 'user': 'barney', 'age': 34 },
     *   { 'user': 'fred',   'age': 40 },
     *   { 'user': 'barney', 'age': 36 }
     * ];
     *
     * // Sort by `user` in ascending order and by `age` in descending order.
     * _.orderBy(users, ['user', 'age'], ['asc', 'desc']);
     * // => objects for [['barney', 36], ['barney', 34], ['fred', 48], ['fred', 40]]
     */
    function orderBy(collection, iteratees, orders, guard) {
      if (collection == null) {
        return [];
      }
      if (!isArray(iteratees)) {
        iteratees = iteratees == null ? [] : [iteratees];
      }
      orders = guard ? undefined : orders;
      if (!isArray(orders)) {
        orders = orders == null ? [] : [orders];
      }
      return baseOrderBy(collection, iteratees, orders);
    }

    /**
     * Creates an array of elements split into two groups, the first of which
     * contains elements `predicate` returns truthy for, the second of which
     * contains elements `predicate` returns falsey for. The predicate is
     * invoked with one argument: (value).
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Collection
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Function} [predicate=_.identity] The function invoked per iteration.
     * @returns {Array} Returns the array of grouped elements.
     * @example
     *
     * var users = [
     *   { 'user': 'barney',  'age': 36, 'active': false },
     *   { 'user': 'fred',    'age': 40, 'active': true },
     *   { 'user': 'pebbles', 'age': 1,  'active': false }
     * ];
     *
     * _.partition(users, function(o) { return o.active; });
     * // => objects for [['fred'], ['barney', 'pebbles']]
     *
     * // The `_.matches` iteratee shorthand.
     * _.partition(users, { 'age': 1, 'active': false });
     * // => objects for [['pebbles'], ['barney', 'fred']]
     *
     * // The `_.matchesProperty` iteratee shorthand.
     * _.partition(users, ['active', false]);
     * // => objects for [['barney', 'pebbles'], ['fred']]
     *
     * // The `_.property` iteratee shorthand.
     * _.partition(users, 'active');
     * // => objects for [['fred'], ['barney', 'pebbles']]
     */
    var partition = createAggregator(function(result, value, key) {
      result[key ? 0 : 1].push(value);
    }, function() { return [[], []]; });

    /**
     * Reduces `collection` to a value which is the accumulated result of running
     * each element in `collection` thru `iteratee`, where each successive
     * invocation is supplied the return value of the previous. If `accumulator`
     * is not given, the first element of `collection` is used as the initial
     * value. The iteratee is invoked with four arguments:
     * (accumulator, value, index|key, collection).
     *
     * Many lodash methods are guarded to work as iteratees for methods like
     * `_.reduce`, `_.reduceRight`, and `_.transform`.
     *
     * The guarded methods are:
     * `assign`, `defaults`, `defaultsDeep`, `includes`, `merge`, `orderBy`,
     * and `sortBy`
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Collection
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Function} [iteratee=_.identity] The function invoked per iteration.
     * @param {*} [accumulator] The initial value.
     * @returns {*} Returns the accumulated value.
     * @see _.reduceRight
     * @example
     *
     * _.reduce([1, 2], function(sum, n) {
     *   return sum + n;
     * }, 0);
     * // => 3
     *
     * _.reduce({ 'a': 1, 'b': 2, 'c': 1 }, function(result, value, key) {
     *   (result[value] || (result[value] = [])).push(key);
     *   return result;
     * }, {});
     * // => { '1': ['a', 'c'], '2': ['b'] } (iteration order is not guaranteed)
     */
    function reduce(collection, iteratee, accumulator) {
      var func = isArray(collection) ? arrayReduce : baseReduce,
          initAccum = arguments.length < 3;

      return func(collection, getIteratee(iteratee, 4), accumulator, initAccum, baseEach);
    }

    /**
     * This method is like `_.reduce` except that it iterates over elements of
     * `collection` from right to left.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Collection
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Function} [iteratee=_.identity] The function invoked per iteration.
     * @param {*} [accumulator] The initial value.
     * @returns {*} Returns the accumulated value.
     * @see _.reduce
     * @example
     *
     * var array = [[0, 1], [2, 3], [4, 5]];
     *
     * _.reduceRight(array, function(flattened, other) {
     *   return flattened.concat(other);
     * }, []);
     * // => [4, 5, 2, 3, 0, 1]
     */
    function reduceRight(collection, iteratee, accumulator) {
      var func = isArray(collection) ? arrayReduceRight : baseReduce,
          initAccum = arguments.length < 3;

      return func(collection, getIteratee(iteratee, 4), accumulator, initAccum, baseEachRight);
    }

    /**
     * The opposite of `_.filter`; this method returns the elements of `collection`
     * that `predicate` does **not** return truthy for.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Collection
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Function} [predicate=_.identity] The function invoked per iteration.
     * @returns {Array} Returns the new filtered array.
     * @see _.filter
     * @example
     *
     * var users = [
     *   { 'user': 'barney', 'age': 36, 'active': false },
     *   { 'user': 'fred',   'age': 40, 'active': true }
     * ];
     *
     * _.reject(users, function(o) { return !o.active; });
     * // => objects for ['fred']
     *
     * // The `_.matches` iteratee shorthand.
     * _.reject(users, { 'age': 40, 'active': true });
     * // => objects for ['barney']
     *
     * // The `_.matchesProperty` iteratee shorthand.
     * _.reject(users, ['active', false]);
     * // => objects for ['fred']
     *
     * // The `_.property` iteratee shorthand.
     * _.reject(users, 'active');
     * // => objects for ['barney']
     */
    function reject(collection, predicate) {
      var func = isArray(collection) ? arrayFilter : baseFilter;
      return func(collection, negate(getIteratee(predicate, 3)));
    }

    /**
     * Gets a random element from `collection`.
     *
     * @static
     * @memberOf _
     * @since 2.0.0
     * @category Collection
     * @param {Array|Object} collection The collection to sample.
     * @returns {*} Returns the random element.
     * @example
     *
     * _.sample([1, 2, 3, 4]);
     * // => 2
     */
    function sample(collection) {
      var func = isArray(collection) ? arraySample : baseSample;
      return func(collection);
    }

    /**
     * Gets `n` random elements at unique keys from `collection` up to the
     * size of `collection`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Collection
     * @param {Array|Object} collection The collection to sample.
     * @param {number} [n=1] The number of elements to sample.
     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
     * @returns {Array} Returns the random elements.
     * @example
     *
     * _.sampleSize([1, 2, 3], 2);
     * // => [3, 1]
     *
     * _.sampleSize([1, 2, 3], 4);
     * // => [2, 3, 1]
     */
    function sampleSize(collection, n, guard) {
      if ((guard ? isIterateeCall(collection, n, guard) : n === undefined)) {
        n = 1;
      } else {
        n = toInteger(n);
      }
      var func = isArray(collection) ? arraySampleSize : baseSampleSize;
      return func(collection, n);
    }

    /**
     * Creates an array of shuffled values, using a version of the
     * [Fisher-Yates shuffle](https://en.wikipedia.org/wiki/Fisher-Yates_shuffle).
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Collection
     * @param {Array|Object} collection The collection to shuffle.
     * @returns {Array} Returns the new shuffled array.
     * @example
     *
     * _.shuffle([1, 2, 3, 4]);
     * // => [4, 1, 3, 2]
     */
    function shuffle(collection) {
      var func = isArray(collection) ? arrayShuffle : baseShuffle;
      return func(collection);
    }

    /**
     * Gets the size of `collection` by returning its length for array-like
     * values or the number of own enumerable string keyed properties for objects.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Collection
     * @param {Array|Object|string} collection The collection to inspect.
     * @returns {number} Returns the collection size.
     * @example
     *
     * _.size([1, 2, 3]);
     * // => 3
     *
     * _.size({ 'a': 1, 'b': 2 });
     * // => 2
     *
     * _.size('pebbles');
     * // => 7
     */
    function size(collection) {
      if (collection == null) {
        return 0;
      }
      if (isArrayLike(collection)) {
        return isString(collection) ? stringSize(collection) : collection.length;
      }
      var tag = getTag(collection);
      if (tag == mapTag || tag == setTag) {
        return collection.size;
      }
      return baseKeys(collection).length;
    }

    /**
     * Checks if `predicate` returns truthy for **any** element of `collection`.
     * Iteration is stopped once `predicate` returns truthy. The predicate is
     * invoked with three arguments: (value, index|key, collection).
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Collection
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Function} [predicate=_.identity] The function invoked per iteration.
     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
     * @returns {boolean} Returns `true` if any element passes the predicate check,
     *  else `false`.
     * @example
     *
     * _.some([null, 0, 'yes', false], Boolean);
     * // => true
     *
     * var users = [
     *   { 'user': 'barney', 'active': true },
     *   { 'user': 'fred',   'active': false }
     * ];
     *
     * // The `_.matches` iteratee shorthand.
     * _.some(users, { 'user': 'barney', 'active': false });
     * // => false
     *
     * // The `_.matchesProperty` iteratee shorthand.
     * _.some(users, ['active', false]);
     * // => true
     *
     * // The `_.property` iteratee shorthand.
     * _.some(users, 'active');
     * // => true
     */
    function some(collection, predicate, guard) {
      var func = isArray(collection) ? arraySome : baseSome;
      if (guard && isIterateeCall(collection, predicate, guard)) {
        predicate = undefined;
      }
      return func(collection, getIteratee(predicate, 3));
    }

    /**
     * Creates an array of elements, sorted in ascending order by the results of
     * running each element in a collection thru each iteratee. This method
     * performs a stable sort, that is, it preserves the original sort order of
     * equal elements. The iteratees are invoked with one argument: (value).
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Collection
     * @param {Array|Object} collection The collection to iterate over.
     * @param {...(Function|Function[])} [iteratees=[_.identity]]
     *  The iteratees to sort by.
     * @returns {Array} Returns the new sorted array.
     * @example
     *
     * var users = [
     *   { 'user': 'fred',   'age': 48 },
     *   { 'user': 'barney', 'age': 36 },
     *   { 'user': 'fred',   'age': 30 },
     *   { 'user': 'barney', 'age': 34 }
     * ];
     *
     * _.sortBy(users, [function(o) { return o.user; }]);
     * // => objects for [['barney', 36], ['barney', 34], ['fred', 48], ['fred', 30]]
     *
     * _.sortBy(users, ['user', 'age']);
     * // => objects for [['barney', 34], ['barney', 36], ['fred', 30], ['fred', 48]]
     */
    var sortBy = baseRest(function(collection, iteratees) {
      if (collection == null) {
        return [];
      }
      var length = iteratees.length;
      if (length > 1 && isIterateeCall(collection, iteratees[0], iteratees[1])) {
        iteratees = [];
      } else if (length > 2 && isIterateeCall(iteratees[0], iteratees[1], iteratees[2])) {
        iteratees = [iteratees[0]];
      }
      return baseOrderBy(collection, baseFlatten(iteratees, 1), []);
    });

    /*------------------------------------------------------------------------*/

    /**
     * Gets the timestamp of the number of milliseconds that have elapsed since
     * the Unix epoch (1 January 1970 00:00:00 UTC).
     *
     * @static
     * @memberOf _
     * @since 2.4.0
     * @category Date
     * @returns {number} Returns the timestamp.
     * @example
     *
     * _.defer(function(stamp) {
     *   console.log(_.now() - stamp);
     * }, _.now());
     * // => Logs the number of milliseconds it took for the deferred invocation.
     */
    var now = ctxNow || function() {
      return root.Date.now();
    };

    /*------------------------------------------------------------------------*/

    /**
     * The opposite of `_.before`; this method creates a function that invokes
     * `func` once it's called `n` or more times.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Function
     * @param {number} n The number of calls before `func` is invoked.
     * @param {Function} func The function to restrict.
     * @returns {Function} Returns the new restricted function.
     * @example
     *
     * var saves = ['profile', 'settings'];
     *
     * var done = _.after(saves.length, function() {
     *   console.log('done saving!');
     * });
     *
     * _.forEach(saves, function(type) {
     *   asyncSave({ 'type': type, 'complete': done });
     * });
     * // => Logs 'done saving!' after the two async saves have completed.
     */
    function after(n, func) {
      if (typeof func != 'function') {
        throw new TypeError(FUNC_ERROR_TEXT);
      }
      n = toInteger(n);
      return function() {
        if (--n < 1) {
          return func.apply(this, arguments);
        }
      };
    }

    /**
     * Creates a function that invokes `func`, with up to `n` arguments,
     * ignoring any additional arguments.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Function
     * @param {Function} func The function to cap arguments for.
     * @param {number} [n=func.length] The arity cap.
     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
     * @returns {Function} Returns the new capped function.
     * @example
     *
     * _.map(['6', '8', '10'], _.ary(parseInt, 1));
     * // => [6, 8, 10]
     */
    function ary(func, n, guard) {
      n = guard ? undefined : n;
      n = (func && n == null) ? func.length : n;
      return createWrap(func, WRAP_ARY_FLAG, undefined, undefined, undefined, undefined, n);
    }

    /**
     * Creates a function that invokes `func`, with the `this` binding and arguments
     * of the created function, while it's called less than `n` times. Subsequent
     * calls to the created function return the result of the last `func` invocation.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Function
     * @param {number} n The number of calls at which `func` is no longer invoked.
     * @param {Function} func The function to restrict.
     * @returns {Function} Returns the new restricted function.
     * @example
     *
     * jQuery(element).on('click', _.before(5, addContactToList));
     * // => Allows adding up to 4 contacts to the list.
     */
    function before(n, func) {
      var result;
      if (typeof func != 'function') {
        throw new TypeError(FUNC_ERROR_TEXT);
      }
      n = toInteger(n);
      return function() {
        if (--n > 0) {
          result = func.apply(this, arguments);
        }
        if (n <= 1) {
          func = undefined;
        }
        return result;
      };
    }

    /**
     * Creates a function that invokes `func` with the `this` binding of `thisArg`
     * and `partials` prepended to the arguments it receives.
     *
     * The `_.bind.placeholder` value, which defaults to `_` in monolithic builds,
     * may be used as a placeholder for partially applied arguments.
     *
     * **Note:** Unlike native `Function#bind`, this method doesn't set the "length"
     * property of bound functions.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Function
     * @param {Function} func The function to bind.
     * @param {*} thisArg The `this` binding of `func`.
     * @param {...*} [partials] The arguments to be partially applied.
     * @returns {Function} Returns the new bound function.
     * @example
     *
     * function greet(greeting, punctuation) {
     *   return greeting + ' ' + this.user + punctuation;
     * }
     *
     * var object = { 'user': 'fred' };
     *
     * var bound = _.bind(greet, object, 'hi');
     * bound('!');
     * // => 'hi fred!'
     *
     * // Bound with placeholders.
     * var bound = _.bind(greet, object, _, '!');
     * bound('hi');
     * // => 'hi fred!'
     */
    var bind = baseRest(function(func, thisArg, partials) {
      var bitmask = WRAP_BIND_FLAG;
      if (partials.length) {
        var holders = replaceHolders(partials, getHolder(bind));
        bitmask |= WRAP_PARTIAL_FLAG;
      }
      return createWrap(func, bitmask, thisArg, partials, holders);
    });

    /**
     * Creates a function that invokes the method at `object[key]` with `partials`
     * prepended to the arguments it receives.
     *
     * This method differs from `_.bind` by allowing bound functions to reference
     * methods that may be redefined or don't yet exist. See
     * [Peter Michaux's article](http://peter.michaux.ca/articles/lazy-function-definition-pattern)
     * for more details.
     *
     * The `_.bindKey.placeholder` value, which defaults to `_` in monolithic
     * builds, may be used as a placeholder for partially applied arguments.
     *
     * @static
     * @memberOf _
     * @since 0.10.0
     * @category Function
     * @param {Object} object The object to invoke the method on.
     * @param {string} key The key of the method.
     * @param {...*} [partials] The arguments to be partially applied.
     * @returns {Function} Returns the new bound function.
     * @example
     *
     * var object = {
     *   'user': 'fred',
     *   'greet': function(greeting, punctuation) {
     *     return greeting + ' ' + this.user + punctuation;
     *   }
     * };
     *
     * var bound = _.bindKey(object, 'greet', 'hi');
     * bound('!');
     * // => 'hi fred!'
     *
     * object.greet = function(greeting, punctuation) {
     *   return greeting + 'ya ' + this.user + punctuation;
     * };
     *
     * bound('!');
     * // => 'hiya fred!'
     *
     * // Bound with placeholders.
     * var bound = _.bindKey(object, 'greet', _, '!');
     * bound('hi');
     * // => 'hiya fred!'
     */
    var bindKey = baseRest(function(object, key, partials) {
      var bitmask = WRAP_BIND_FLAG | WRAP_BIND_KEY_FLAG;
      if (partials.length) {
        var holders = replaceHolders(partials, getHolder(bindKey));
        bitmask |= WRAP_PARTIAL_FLAG;
      }
      return createWrap(key, bitmask, object, partials, holders);
    });

    /**
     * Creates a function that accepts arguments of `func` and either invokes
     * `func` returning its result, if at least `arity` number of arguments have
     * been provided, or returns a function that accepts the remaining `func`
     * arguments, and so on. The arity of `func` may be specified if `func.length`
     * is not sufficient.
     *
     * The `_.curry.placeholder` value, which defaults to `_` in monolithic builds,
     * may be used as a placeholder for provided arguments.
     *
     * **Note:** This method doesn't set the "length" property of curried functions.
     *
     * @static
     * @memberOf _
     * @since 2.0.0
     * @category Function
     * @param {Function} func The function to curry.
     * @param {number} [arity=func.length] The arity of `func`.
     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
     * @returns {Function} Returns the new curried function.
     * @example
     *
     * var abc = function(a, b, c) {
     *   return [a, b, c];
     * };
     *
     * var curried = _.curry(abc);
     *
     * curried(1)(2)(3);
     * // => [1, 2, 3]
     *
     * curried(1, 2)(3);
     * // => [1, 2, 3]
     *
     * curried(1, 2, 3);
     * // => [1, 2, 3]
     *
     * // Curried with placeholders.
     * curried(1)(_, 3)(2);
     * // => [1, 2, 3]
     */
    function curry(func, arity, guard) {
      arity = guard ? undefined : arity;
      var result = createWrap(func, WRAP_CURRY_FLAG, undefined, undefined, undefined, undefined, undefined, arity);
      result.placeholder = curry.placeholder;
      return result;
    }

    /**
     * This method is like `_.curry` except that arguments are applied to `func`
     * in the manner of `_.partialRight` instead of `_.partial`.
     *
     * The `_.curryRight.placeholder` value, which defaults to `_` in monolithic
     * builds, may be used as a placeholder for provided arguments.
     *
     * **Note:** This method doesn't set the "length" property of curried functions.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Function
     * @param {Function} func The function to curry.
     * @param {number} [arity=func.length] The arity of `func`.
     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
     * @returns {Function} Returns the new curried function.
     * @example
     *
     * var abc = function(a, b, c) {
     *   return [a, b, c];
     * };
     *
     * var curried = _.curryRight(abc);
     *
     * curried(3)(2)(1);
     * // => [1, 2, 3]
     *
     * curried(2, 3)(1);
     * // => [1, 2, 3]
     *
     * curried(1, 2, 3);
     * // => [1, 2, 3]
     *
     * // Curried with placeholders.
     * curried(3)(1, _)(2);
     * // => [1, 2, 3]
     */
    function curryRight(func, arity, guard) {
      arity = guard ? undefined : arity;
      var result = createWrap(func, WRAP_CURRY_RIGHT_FLAG, undefined, undefined, undefined, undefined, undefined, arity);
      result.placeholder = curryRight.placeholder;
      return result;
    }

    /**
     * Creates a debounced function that delays invoking `func` until after `wait`
     * milliseconds have elapsed since the last time the debounced function was
     * invoked. The debounced function comes with a `cancel` method to cancel
     * delayed `func` invocations and a `flush` method to immediately invoke them.
     * Provide `options` to indicate whether `func` should be invoked on the
     * leading and/or trailing edge of the `wait` timeout. The `func` is invoked
     * with the last arguments provided to the debounced function. Subsequent
     * calls to the debounced function return the result of the last `func`
     * invocation.
     *
     * **Note:** If `leading` and `trailing` options are `true`, `func` is
     * invoked on the trailing edge of the timeout only if the debounced function
     * is invoked more than once during the `wait` timeout.
     *
     * If `wait` is `0` and `leading` is `false`, `func` invocation is deferred
     * until to the next tick, similar to `setTimeout` with a timeout of `0`.
     *
     * See [David Corbacho's article](https://css-tricks.com/debouncing-throttling-explained-examples/)
     * for details over the differences between `_.debounce` and `_.throttle`.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Function
     * @param {Function} func The function to debounce.
     * @param {number} [wait=0] The number of milliseconds to delay.
     * @param {Object} [options={}] The options object.
     * @param {boolean} [options.leading=false]
     *  Specify invoking on the leading edge of the timeout.
     * @param {number} [options.maxWait]
     *  The maximum time `func` is allowed to be delayed before it's invoked.
     * @param {boolean} [options.trailing=true]
     *  Specify invoking on the trailing edge of the timeout.
     * @returns {Function} Returns the new debounced function.
     * @example
     *
     * // Avoid costly calculations while the window size is in flux.
     * jQuery(window).on('resize', _.debounce(calculateLayout, 150));
     *
     * // Invoke `sendMail` when clicked, debouncing subsequent calls.
     * jQuery(element).on('click', _.debounce(sendMail, 300, {
     *   'leading': true,
     *   'trailing': false
     * }));
     *
     * // Ensure `batchLog` is invoked once after 1 second of debounced calls.
     * var debounced = _.debounce(batchLog, 250, { 'maxWait': 1000 });
     * var source = new EventSource('/stream');
     * jQuery(source).on('message', debounced);
     *
     * // Cancel the trailing debounced invocation.
     * jQuery(window).on('popstate', debounced.cancel);
     */
    function debounce(func, wait, options) {
      var lastArgs,
          lastThis,
          maxWait,
          result,
          timerId,
          lastCallTime,
          lastInvokeTime = 0,
          leading = false,
          maxing = false,
          trailing = true;

      if (typeof func != 'function') {
        throw new TypeError(FUNC_ERROR_TEXT);
      }
      wait = toNumber(wait) || 0;
      if (isObject(options)) {
        leading = !!options.leading;
        maxing = 'maxWait' in options;
        maxWait = maxing ? nativeMax(toNumber(options.maxWait) || 0, wait) : maxWait;
        trailing = 'trailing' in options ? !!options.trailing : trailing;
      }

      function invokeFunc(time) {
        var args = lastArgs,
            thisArg = lastThis;

        lastArgs = lastThis = undefined;
        lastInvokeTime = time;
        result = func.apply(thisArg, args);
        return result;
      }

      function leadingEdge(time) {
        // Reset any `maxWait` timer.
        lastInvokeTime = time;
        // Start the timer for the trailing edge.
        timerId = setTimeout(timerExpired, wait);
        // Invoke the leading edge.
        return leading ? invokeFunc(time) : result;
      }

      function remainingWait(time) {
        var timeSinceLastCall = time - lastCallTime,
            timeSinceLastInvoke = time - lastInvokeTime,
            timeWaiting = wait - timeSinceLastCall;

        return maxing
          ? nativeMin(timeWaiting, maxWait - timeSinceLastInvoke)
          : timeWaiting;
      }

      function shouldInvoke(time) {
        var timeSinceLastCall = time - lastCallTime,
            timeSinceLastInvoke = time - lastInvokeTime;

        // Either this is the first call, activity has stopped and we're at the
        // trailing edge, the system time has gone backwards and we're treating
        // it as the trailing edge, or we've hit the `maxWait` limit.
        return (lastCallTime === undefined || (timeSinceLastCall >= wait) ||
          (timeSinceLastCall < 0) || (maxing && timeSinceLastInvoke >= maxWait));
      }

      function timerExpired() {
        var time = now();
        if (shouldInvoke(time)) {
          return trailingEdge(time);
        }
        // Restart the timer.
        timerId = setTimeout(timerExpired, remainingWait(time));
      }

      function trailingEdge(time) {
        timerId = undefined;

        // Only invoke if we have `lastArgs` which means `func` has been
        // debounced at least once.
        if (trailing && lastArgs) {
          return invokeFunc(time);
        }
        lastArgs = lastThis = undefined;
        return result;
      }

      function cancel() {
        if (timerId !== undefined) {
          clearTimeout(timerId);
        }
        lastInvokeTime = 0;
        lastArgs = lastCallTime = lastThis = timerId = undefined;
      }

      function flush() {
        return timerId === undefined ? result : trailingEdge(now());
      }

      function debounced() {
        var time = now(),
            isInvoking = shouldInvoke(time);

        lastArgs = arguments;
        lastThis = this;
        lastCallTime = time;

        if (isInvoking) {
          if (timerId === undefined) {
            return leadingEdge(lastCallTime);
          }
          if (maxing) {
            // Handle invocations in a tight loop.
            clearTimeout(timerId);
            timerId = setTimeout(timerExpired, wait);
            return invokeFunc(lastCallTime);
          }
        }
        if (timerId === undefined) {
          timerId = setTimeout(timerExpired, wait);
        }
        return result;
      }
      debounced.cancel = cancel;
      debounced.flush = flush;
      return debounced;
    }

    /**
     * Defers invoking the `func` until the current call stack has cleared. Any
     * additional arguments are provided to `func` when it's invoked.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Function
     * @param {Function} func The function to defer.
     * @param {...*} [args] The arguments to invoke `func` with.
     * @returns {number} Returns the timer id.
     * @example
     *
     * _.defer(function(text) {
     *   console.log(text);
     * }, 'deferred');
     * // => Logs 'deferred' after one millisecond.
     */
    var defer = baseRest(function(func, args) {
      return baseDelay(func, 1, args);
    });

    /**
     * Invokes `func` after `wait` milliseconds. Any additional arguments are
     * provided to `func` when it's invoked.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Function
     * @param {Function} func The function to delay.
     * @param {number} wait The number of milliseconds to delay invocation.
     * @param {...*} [args] The arguments to invoke `func` with.
     * @returns {number} Returns the timer id.
     * @example
     *
     * _.delay(function(text) {
     *   console.log(text);
     * }, 1000, 'later');
     * // => Logs 'later' after one second.
     */
    var delay = baseRest(function(func, wait, args) {
      return baseDelay(func, toNumber(wait) || 0, args);
    });

    /**
     * Creates a function that invokes `func` with arguments reversed.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Function
     * @param {Function} func The function to flip arguments for.
     * @returns {Function} Returns the new flipped function.
     * @example
     *
     * var flipped = _.flip(function() {
     *   return _.toArray(arguments);
     * });
     *
     * flipped('a', 'b', 'c', 'd');
     * // => ['d', 'c', 'b', 'a']
     */
    function flip(func) {
      return createWrap(func, WRAP_FLIP_FLAG);
    }

    /**
     * Creates a function that memoizes the result of `func`. If `resolver` is
     * provided, it determines the cache key for storing the result based on the
     * arguments provided to the memoized function. By default, the first argument
     * provided to the memoized function is used as the map cache key. The `func`
     * is invoked with the `this` binding of the memoized function.
     *
     * **Note:** The cache is exposed as the `cache` property on the memoized
     * function. Its creation may be customized by replacing the `_.memoize.Cache`
     * constructor with one whose instances implement the
     * [`Map`](http://ecma-international.org/ecma-262/7.0/#sec-properties-of-the-map-prototype-object)
     * method interface of `clear`, `delete`, `get`, `has`, and `set`.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Function
     * @param {Function} func The function to have its output memoized.
     * @param {Function} [resolver] The function to resolve the cache key.
     * @returns {Function} Returns the new memoized function.
     * @example
     *
     * var object = { 'a': 1, 'b': 2 };
     * var other = { 'c': 3, 'd': 4 };
     *
     * var values = _.memoize(_.values);
     * values(object);
     * // => [1, 2]
     *
     * values(other);
     * // => [3, 4]
     *
     * object.a = 2;
     * values(object);
     * // => [1, 2]
     *
     * // Modify the result cache.
     * values.cache.set(object, ['a', 'b']);
     * values(object);
     * // => ['a', 'b']
     *
     * // Replace `_.memoize.Cache`.
     * _.memoize.Cache = WeakMap;
     */
    function memoize(func, resolver) {
      if (typeof func != 'function' || (resolver != null && typeof resolver != 'function')) {
        throw new TypeError(FUNC_ERROR_TEXT);
      }
      var memoized = function() {
        var args = arguments,
            key = resolver ? resolver.apply(this, args) : args[0],
            cache = memoized.cache;

        if (cache.has(key)) {
          return cache.get(key);
        }
        var result = func.apply(this, args);
        memoized.cache = cache.set(key, result) || cache;
        return result;
      };
      memoized.cache = new (memoize.Cache || MapCache);
      return memoized;
    }

    // Expose `MapCache`.
    memoize.Cache = MapCache;

    /**
     * Creates a function that negates the result of the predicate `func`. The
     * `func` predicate is invoked with the `this` binding and arguments of the
     * created function.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Function
     * @param {Function} predicate The predicate to negate.
     * @returns {Function} Returns the new negated function.
     * @example
     *
     * function isEven(n) {
     *   return n % 2 == 0;
     * }
     *
     * _.filter([1, 2, 3, 4, 5, 6], _.negate(isEven));
     * // => [1, 3, 5]
     */
    function negate(predicate) {
      if (typeof predicate != 'function') {
        throw new TypeError(FUNC_ERROR_TEXT);
      }
      return function() {
        var args = arguments;
        switch (args.length) {
          case 0: return !predicate.call(this);
          case 1: return !predicate.call(this, args[0]);
          case 2: return !predicate.call(this, args[0], args[1]);
          case 3: return !predicate.call(this, args[0], args[1], args[2]);
        }
        return !predicate.apply(this, args);
      };
    }

    /**
     * Creates a function that is restricted to invoking `func` once. Repeat calls
     * to the function return the value of the first invocation. The `func` is
     * invoked with the `this` binding and arguments of the created function.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Function
     * @param {Function} func The function to restrict.
     * @returns {Function} Returns the new restricted function.
     * @example
     *
     * var initialize = _.once(createApplication);
     * initialize();
     * initialize();
     * // => `createApplication` is invoked once
     */
    function once(func) {
      return before(2, func);
    }

    /**
     * Creates a function that invokes `func` with its arguments transformed.
     *
     * @static
     * @since 4.0.0
     * @memberOf _
     * @category Function
     * @param {Function} func The function to wrap.
     * @param {...(Function|Function[])} [transforms=[_.identity]]
     *  The argument transforms.
     * @returns {Function} Returns the new function.
     * @example
     *
     * function doubled(n) {
     *   return n * 2;
     * }
     *
     * function square(n) {
     *   return n * n;
     * }
     *
     * var func = _.overArgs(function(x, y) {
     *   return [x, y];
     * }, [square, doubled]);
     *
     * func(9, 3);
     * // => [81, 6]
     *
     * func(10, 5);
     * // => [100, 10]
     */
    var overArgs = castRest(function(func, transforms) {
      transforms = (transforms.length == 1 && isArray(transforms[0]))
        ? arrayMap(transforms[0], baseUnary(getIteratee()))
        : arrayMap(baseFlatten(transforms, 1), baseUnary(getIteratee()));

      var funcsLength = transforms.length;
      return baseRest(function(args) {
        var index = -1,
            length = nativeMin(args.length, funcsLength);

        while (++index < length) {
          args[index] = transforms[index].call(this, args[index]);
        }
        return apply(func, this, args);
      });
    });

    /**
     * Creates a function that invokes `func` with `partials` prepended to the
     * arguments it receives. This method is like `_.bind` except it does **not**
     * alter the `this` binding.
     *
     * The `_.partial.placeholder` value, which defaults to `_` in monolithic
     * builds, may be used as a placeholder for partially applied arguments.
     *
     * **Note:** This method doesn't set the "length" property of partially
     * applied functions.
     *
     * @static
     * @memberOf _
     * @since 0.2.0
     * @category Function
     * @param {Function} func The function to partially apply arguments to.
     * @param {...*} [partials] The arguments to be partially applied.
     * @returns {Function} Returns the new partially applied function.
     * @example
     *
     * function greet(greeting, name) {
     *   return greeting + ' ' + name;
     * }
     *
     * var sayHelloTo = _.partial(greet, 'hello');
     * sayHelloTo('fred');
     * // => 'hello fred'
     *
     * // Partially applied with placeholders.
     * var greetFred = _.partial(greet, _, 'fred');
     * greetFred('hi');
     * // => 'hi fred'
     */
    var partial = baseRest(function(func, partials) {
      var holders = replaceHolders(partials, getHolder(partial));
      return createWrap(func, WRAP_PARTIAL_FLAG, undefined, partials, holders);
    });

    /**
     * This method is like `_.partial` except that partially applied arguments
     * are appended to the arguments it receives.
     *
     * The `_.partialRight.placeholder` value, which defaults to `_` in monolithic
     * builds, may be used as a placeholder for partially applied arguments.
     *
     * **Note:** This method doesn't set the "length" property of partially
     * applied functions.
     *
     * @static
     * @memberOf _
     * @since 1.0.0
     * @category Function
     * @param {Function} func The function to partially apply arguments to.
     * @param {...*} [partials] The arguments to be partially applied.
     * @returns {Function} Returns the new partially applied function.
     * @example
     *
     * function greet(greeting, name) {
     *   return greeting + ' ' + name;
     * }
     *
     * var greetFred = _.partialRight(greet, 'fred');
     * greetFred('hi');
     * // => 'hi fred'
     *
     * // Partially applied with placeholders.
     * var sayHelloTo = _.partialRight(greet, 'hello', _);
     * sayHelloTo('fred');
     * // => 'hello fred'
     */
    var partialRight = baseRest(function(func, partials) {
      var holders = replaceHolders(partials, getHolder(partialRight));
      return createWrap(func, WRAP_PARTIAL_RIGHT_FLAG, undefined, partials, holders);
    });

    /**
     * Creates a function that invokes `func` with arguments arranged according
     * to the specified `indexes` where the argument value at the first index is
     * provided as the first argument, the argument value at the second index is
     * provided as the second argument, and so on.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Function
     * @param {Function} func The function to rearrange arguments for.
     * @param {...(number|number[])} indexes The arranged argument indexes.
     * @returns {Function} Returns the new function.
     * @example
     *
     * var rearged = _.rearg(function(a, b, c) {
     *   return [a, b, c];
     * }, [2, 0, 1]);
     *
     * rearged('b', 'c', 'a')
     * // => ['a', 'b', 'c']
     */
    var rearg = flatRest(function(func, indexes) {
      return createWrap(func, WRAP_REARG_FLAG, undefined, undefined, undefined, indexes);
    });

    /**
     * Creates a function that invokes `func` with the `this` binding of the
     * created function and arguments from `start` and beyond provided as
     * an array.
     *
     * **Note:** This method is based on the
     * [rest parameter](https://mdn.io/rest_parameters).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Function
     * @param {Function} func The function to apply a rest parameter to.
     * @param {number} [start=func.length-1] The start position of the rest parameter.
     * @returns {Function} Returns the new function.
     * @example
     *
     * var say = _.rest(function(what, names) {
     *   return what + ' ' + _.initial(names).join(', ') +
     *     (_.size(names) > 1 ? ', & ' : '') + _.last(names);
     * });
     *
     * say('hello', 'fred', 'barney', 'pebbles');
     * // => 'hello fred, barney, & pebbles'
     */
    function rest(func, start) {
      if (typeof func != 'function') {
        throw new TypeError(FUNC_ERROR_TEXT);
      }
      start = start === undefined ? start : toInteger(start);
      return baseRest(func, start);
    }

    /**
     * Creates a function that invokes `func` with the `this` binding of the
     * create function and an array of arguments much like
     * [`Function#apply`](http://www.ecma-international.org/ecma-262/7.0/#sec-function.prototype.apply).
     *
     * **Note:** This method is based on the
     * [spread operator](https://mdn.io/spread_operator).
     *
     * @static
     * @memberOf _
     * @since 3.2.0
     * @category Function
     * @param {Function} func The function to spread arguments over.
     * @param {number} [start=0] The start position of the spread.
     * @returns {Function} Returns the new function.
     * @example
     *
     * var say = _.spread(function(who, what) {
     *   return who + ' says ' + what;
     * });
     *
     * say(['fred', 'hello']);
     * // => 'fred says hello'
     *
     * var numbers = Promise.all([
     *   Promise.resolve(40),
     *   Promise.resolve(36)
     * ]);
     *
     * numbers.then(_.spread(function(x, y) {
     *   return x + y;
     * }));
     * // => a Promise of 76
     */
    function spread(func, start) {
      if (typeof func != 'function') {
        throw new TypeError(FUNC_ERROR_TEXT);
      }
      start = start == null ? 0 : nativeMax(toInteger(start), 0);
      return baseRest(function(args) {
        var array = args[start],
            otherArgs = castSlice(args, 0, start);

        if (array) {
          arrayPush(otherArgs, array);
        }
        return apply(func, this, otherArgs);
      });
    }

    /**
     * Creates a throttled function that only invokes `func` at most once per
     * every `wait` milliseconds. The throttled function comes with a `cancel`
     * method to cancel delayed `func` invocations and a `flush` method to
     * immediately invoke them. Provide `options` to indicate whether `func`
     * should be invoked on the leading and/or trailing edge of the `wait`
     * timeout. The `func` is invoked with the last arguments provided to the
     * throttled function. Subsequent calls to the throttled function return the
     * result of the last `func` invocation.
     *
     * **Note:** If `leading` and `trailing` options are `true`, `func` is
     * invoked on the trailing edge of the timeout only if the throttled function
     * is invoked more than once during the `wait` timeout.
     *
     * If `wait` is `0` and `leading` is `false`, `func` invocation is deferred
     * until to the next tick, similar to `setTimeout` with a timeout of `0`.
     *
     * See [David Corbacho's article](https://css-tricks.com/debouncing-throttling-explained-examples/)
     * for details over the differences between `_.throttle` and `_.debounce`.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Function
     * @param {Function} func The function to throttle.
     * @param {number} [wait=0] The number of milliseconds to throttle invocations to.
     * @param {Object} [options={}] The options object.
     * @param {boolean} [options.leading=true]
     *  Specify invoking on the leading edge of the timeout.
     * @param {boolean} [options.trailing=true]
     *  Specify invoking on the trailing edge of the timeout.
     * @returns {Function} Returns the new throttled function.
     * @example
     *
     * // Avoid excessively updating the position while scrolling.
     * jQuery(window).on('scroll', _.throttle(updatePosition, 100));
     *
     * // Invoke `renewToken` when the click event is fired, but not more than once every 5 minutes.
     * var throttled = _.throttle(renewToken, 300000, { 'trailing': false });
     * jQuery(element).on('click', throttled);
     *
     * // Cancel the trailing throttled invocation.
     * jQuery(window).on('popstate', throttled.cancel);
     */
    function throttle(func, wait, options) {
      var leading = true,
          trailing = true;

      if (typeof func != 'function') {
        throw new TypeError(FUNC_ERROR_TEXT);
      }
      if (isObject(options)) {
        leading = 'leading' in options ? !!options.leading : leading;
        trailing = 'trailing' in options ? !!options.trailing : trailing;
      }
      return debounce(func, wait, {
        'leading': leading,
        'maxWait': wait,
        'trailing': trailing
      });
    }

    /**
     * Creates a function that accepts up to one argument, ignoring any
     * additional arguments.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Function
     * @param {Function} func The function to cap arguments for.
     * @returns {Function} Returns the new capped function.
     * @example
     *
     * _.map(['6', '8', '10'], _.unary(parseInt));
     * // => [6, 8, 10]
     */
    function unary(func) {
      return ary(func, 1);
    }

    /**
     * Creates a function that provides `value` to `wrapper` as its first
     * argument. Any additional arguments provided to the function are appended
     * to those provided to the `wrapper`. The wrapper is invoked with the `this`
     * binding of the created function.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Function
     * @param {*} value The value to wrap.
     * @param {Function} [wrapper=identity] The wrapper function.
     * @returns {Function} Returns the new function.
     * @example
     *
     * var p = _.wrap(_.escape, function(func, text) {
     *   return '<p>' + func(text) + '</p>';
     * });
     *
     * p('fred, barney, & pebbles');
     * // => '<p>fred, barney, &amp; pebbles</p>'
     */
    function wrap(value, wrapper) {
      return partial(castFunction(wrapper), value);
    }

    /*------------------------------------------------------------------------*/

    /**
     * Casts `value` as an array if it's not one.
     *
     * @static
     * @memberOf _
     * @since 4.4.0
     * @category Lang
     * @param {*} value The value to inspect.
     * @returns {Array} Returns the cast array.
     * @example
     *
     * _.castArray(1);
     * // => [1]
     *
     * _.castArray({ 'a': 1 });
     * // => [{ 'a': 1 }]
     *
     * _.castArray('abc');
     * // => ['abc']
     *
     * _.castArray(null);
     * // => [null]
     *
     * _.castArray(undefined);
     * // => [undefined]
     *
     * _.castArray();
     * // => []
     *
     * var array = [1, 2, 3];
     * console.log(_.castArray(array) === array);
     * // => true
     */
    function castArray() {
      if (!arguments.length) {
        return [];
      }
      var value = arguments[0];
      return isArray(value) ? value : [value];
    }

    /**
     * Creates a shallow clone of `value`.
     *
     * **Note:** This method is loosely based on the
     * [structured clone algorithm](https://mdn.io/Structured_clone_algorithm)
     * and supports cloning arrays, array buffers, booleans, date objects, maps,
     * numbers, `Object` objects, regexes, sets, strings, symbols, and typed
     * arrays. The own enumerable properties of `arguments` objects are cloned
     * as plain objects. An empty object is returned for uncloneable values such
     * as error objects, functions, DOM nodes, and WeakMaps.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Lang
     * @param {*} value The value to clone.
     * @returns {*} Returns the cloned value.
     * @see _.cloneDeep
     * @example
     *
     * var objects = [{ 'a': 1 }, { 'b': 2 }];
     *
     * var shallow = _.clone(objects);
     * console.log(shallow[0] === objects[0]);
     * // => true
     */
    function clone(value) {
      return baseClone(value, CLONE_SYMBOLS_FLAG);
    }

    /**
     * This method is like `_.clone` except that it accepts `customizer` which
     * is invoked to produce the cloned value. If `customizer` returns `undefined`,
     * cloning is handled by the method instead. The `customizer` is invoked with
     * up to four arguments; (value [, index|key, object, stack]).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Lang
     * @param {*} value The value to clone.
     * @param {Function} [customizer] The function to customize cloning.
     * @returns {*} Returns the cloned value.
     * @see _.cloneDeepWith
     * @example
     *
     * function customizer(value) {
     *   if (_.isElement(value)) {
     *     return value.cloneNode(false);
     *   }
     * }
     *
     * var el = _.cloneWith(document.body, customizer);
     *
     * console.log(el === document.body);
     * // => false
     * console.log(el.nodeName);
     * // => 'BODY'
     * console.log(el.childNodes.length);
     * // => 0
     */
    function cloneWith(value, customizer) {
      customizer = typeof customizer == 'function' ? customizer : undefined;
      return baseClone(value, CLONE_SYMBOLS_FLAG, customizer);
    }

    /**
     * This method is like `_.clone` except that it recursively clones `value`.
     *
     * @static
     * @memberOf _
     * @since 1.0.0
     * @category Lang
     * @param {*} value The value to recursively clone.
     * @returns {*} Returns the deep cloned value.
     * @see _.clone
     * @example
     *
     * var objects = [{ 'a': 1 }, { 'b': 2 }];
     *
     * var deep = _.cloneDeep(objects);
     * console.log(deep[0] === objects[0]);
     * // => false
     */
    function cloneDeep(value) {
      return baseClone(value, CLONE_DEEP_FLAG | CLONE_SYMBOLS_FLAG);
    }

    /**
     * This method is like `_.cloneWith` except that it recursively clones `value`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Lang
     * @param {*} value The value to recursively clone.
     * @param {Function} [customizer] The function to customize cloning.
     * @returns {*} Returns the deep cloned value.
     * @see _.cloneWith
     * @example
     *
     * function customizer(value) {
     *   if (_.isElement(value)) {
     *     return value.cloneNode(true);
     *   }
     * }
     *
     * var el = _.cloneDeepWith(document.body, customizer);
     *
     * console.log(el === document.body);
     * // => false
     * console.log(el.nodeName);
     * // => 'BODY'
     * console.log(el.childNodes.length);
     * // => 20
     */
    function cloneDeepWith(value, customizer) {
      customizer = typeof customizer == 'function' ? customizer : undefined;
      return baseClone(value, CLONE_DEEP_FLAG | CLONE_SYMBOLS_FLAG, customizer);
    }

    /**
     * Checks if `object` conforms to `source` by invoking the predicate
     * properties of `source` with the corresponding property values of `object`.
     *
     * **Note:** This method is equivalent to `_.conforms` when `source` is
     * partially applied.
     *
     * @static
     * @memberOf _
     * @since 4.14.0
     * @category Lang
     * @param {Object} object The object to inspect.
     * @param {Object} source The object of property predicates to conform to.
     * @returns {boolean} Returns `true` if `object` conforms, else `false`.
     * @example
     *
     * var object = { 'a': 1, 'b': 2 };
     *
     * _.conformsTo(object, { 'b': function(n) { return n > 1; } });
     * // => true
     *
     * _.conformsTo(object, { 'b': function(n) { return n > 2; } });
     * // => false
     */
    function conformsTo(object, source) {
      return source == null || baseConformsTo(object, source, keys(source));
    }

    /**
     * Performs a
     * [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
     * comparison between two values to determine if they are equivalent.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Lang
     * @param {*} value The value to compare.
     * @param {*} other The other value to compare.
     * @returns {boolean} Returns `true` if the values are equivalent, else `false`.
     * @example
     *
     * var object = { 'a': 1 };
     * var other = { 'a': 1 };
     *
     * _.eq(object, object);
     * // => true
     *
     * _.eq(object, other);
     * // => false
     *
     * _.eq('a', 'a');
     * // => true
     *
     * _.eq('a', Object('a'));
     * // => false
     *
     * _.eq(NaN, NaN);
     * // => true
     */
    function eq(value, other) {
      return value === other || (value !== value && other !== other);
    }

    /**
     * Checks if `value` is greater than `other`.
     *
     * @static
     * @memberOf _
     * @since 3.9.0
     * @category Lang
     * @param {*} value The value to compare.
     * @param {*} other The other value to compare.
     * @returns {boolean} Returns `true` if `value` is greater than `other`,
     *  else `false`.
     * @see _.lt
     * @example
     *
     * _.gt(3, 1);
     * // => true
     *
     * _.gt(3, 3);
     * // => false
     *
     * _.gt(1, 3);
     * // => false
     */
    var gt = createRelationalOperation(baseGt);

    /**
     * Checks if `value` is greater than or equal to `other`.
     *
     * @static
     * @memberOf _
     * @since 3.9.0
     * @category Lang
     * @param {*} value The value to compare.
     * @param {*} other The other value to compare.
     * @returns {boolean} Returns `true` if `value` is greater than or equal to
     *  `other`, else `false`.
     * @see _.lte
     * @example
     *
     * _.gte(3, 1);
     * // => true
     *
     * _.gte(3, 3);
     * // => true
     *
     * _.gte(1, 3);
     * // => false
     */
    var gte = createRelationalOperation(function(value, other) {
      return value >= other;
    });

    /**
     * Checks if `value` is likely an `arguments` object.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is an `arguments` object,
     *  else `false`.
     * @example
     *
     * _.isArguments(function() { return arguments; }());
     * // => true
     *
     * _.isArguments([1, 2, 3]);
     * // => false
     */
    var isArguments = baseIsArguments(function() { return arguments; }()) ? baseIsArguments : function(value) {
      return isObjectLike(value) && hasOwnProperty.call(value, 'callee') &&
        !propertyIsEnumerable.call(value, 'callee');
    };

    /**
     * Checks if `value` is classified as an `Array` object.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is an array, else `false`.
     * @example
     *
     * _.isArray([1, 2, 3]);
     * // => true
     *
     * _.isArray(document.body.children);
     * // => false
     *
     * _.isArray('abc');
     * // => false
     *
     * _.isArray(_.noop);
     * // => false
     */
    var isArray = Array.isArray;

    /**
     * Checks if `value` is classified as an `ArrayBuffer` object.
     *
     * @static
     * @memberOf _
     * @since 4.3.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is an array buffer, else `false`.
     * @example
     *
     * _.isArrayBuffer(new ArrayBuffer(2));
     * // => true
     *
     * _.isArrayBuffer(new Array(2));
     * // => false
     */
    var isArrayBuffer = nodeIsArrayBuffer ? baseUnary(nodeIsArrayBuffer) : baseIsArrayBuffer;

    /**
     * Checks if `value` is array-like. A value is considered array-like if it's
     * not a function and has a `value.length` that's an integer greater than or
     * equal to `0` and less than or equal to `Number.MAX_SAFE_INTEGER`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is array-like, else `false`.
     * @example
     *
     * _.isArrayLike([1, 2, 3]);
     * // => true
     *
     * _.isArrayLike(document.body.children);
     * // => true
     *
     * _.isArrayLike('abc');
     * // => true
     *
     * _.isArrayLike(_.noop);
     * // => false
     */
    function isArrayLike(value) {
      return value != null && isLength(value.length) && !isFunction(value);
    }

    /**
     * This method is like `_.isArrayLike` except that it also checks if `value`
     * is an object.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is an array-like object,
     *  else `false`.
     * @example
     *
     * _.isArrayLikeObject([1, 2, 3]);
     * // => true
     *
     * _.isArrayLikeObject(document.body.children);
     * // => true
     *
     * _.isArrayLikeObject('abc');
     * // => false
     *
     * _.isArrayLikeObject(_.noop);
     * // => false
     */
    function isArrayLikeObject(value) {
      return isObjectLike(value) && isArrayLike(value);
    }

    /**
     * Checks if `value` is classified as a boolean primitive or object.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is a boolean, else `false`.
     * @example
     *
     * _.isBoolean(false);
     * // => true
     *
     * _.isBoolean(null);
     * // => false
     */
    function isBoolean(value) {
      return value === true || value === false ||
        (isObjectLike(value) && baseGetTag(value) == boolTag);
    }

    /**
     * Checks if `value` is a buffer.
     *
     * @static
     * @memberOf _
     * @since 4.3.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is a buffer, else `false`.
     * @example
     *
     * _.isBuffer(new Buffer(2));
     * // => true
     *
     * _.isBuffer(new Uint8Array(2));
     * // => false
     */
    var isBuffer = nativeIsBuffer || stubFalse;

    /**
     * Checks if `value` is classified as a `Date` object.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is a date object, else `false`.
     * @example
     *
     * _.isDate(new Date);
     * // => true
     *
     * _.isDate('Mon April 23 2012');
     * // => false
     */
    var isDate = nodeIsDate ? baseUnary(nodeIsDate) : baseIsDate;

    /**
     * Checks if `value` is likely a DOM element.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is a DOM element, else `false`.
     * @example
     *
     * _.isElement(document.body);
     * // => true
     *
     * _.isElement('<body>');
     * // => false
     */
    function isElement(value) {
      return isObjectLike(value) && value.nodeType === 1 && !isPlainObject(value);
    }

    /**
     * Checks if `value` is an empty object, collection, map, or set.
     *
     * Objects are considered empty if they have no own enumerable string keyed
     * properties.
     *
     * Array-like values such as `arguments` objects, arrays, buffers, strings, or
     * jQuery-like collections are considered empty if they have a `length` of `0`.
     * Similarly, maps and sets are considered empty if they have a `size` of `0`.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is empty, else `false`.
     * @example
     *
     * _.isEmpty(null);
     * // => true
     *
     * _.isEmpty(true);
     * // => true
     *
     * _.isEmpty(1);
     * // => true
     *
     * _.isEmpty([1, 2, 3]);
     * // => false
     *
     * _.isEmpty({ 'a': 1 });
     * // => false
     */
    function isEmpty(value) {
      if (value == null) {
        return true;
      }
      if (isArrayLike(value) &&
          (isArray(value) || typeof value == 'string' || typeof value.splice == 'function' ||
            isBuffer(value) || isTypedArray(value) || isArguments(value))) {
        return !value.length;
      }
      var tag = getTag(value);
      if (tag == mapTag || tag == setTag) {
        return !value.size;
      }
      if (isPrototype(value)) {
        return !baseKeys(value).length;
      }
      for (var key in value) {
        if (hasOwnProperty.call(value, key)) {
          return false;
        }
      }
      return true;
    }

    /**
     * Performs a deep comparison between two values to determine if they are
     * equivalent.
     *
     * **Note:** This method supports comparing arrays, array buffers, booleans,
     * date objects, error objects, maps, numbers, `Object` objects, regexes,
     * sets, strings, symbols, and typed arrays. `Object` objects are compared
     * by their own, not inherited, enumerable properties. Functions and DOM
     * nodes are compared by strict equality, i.e. `===`.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Lang
     * @param {*} value The value to compare.
     * @param {*} other The other value to compare.
     * @returns {boolean} Returns `true` if the values are equivalent, else `false`.
     * @example
     *
     * var object = { 'a': 1 };
     * var other = { 'a': 1 };
     *
     * _.isEqual(object, other);
     * // => true
     *
     * object === other;
     * // => false
     */
    function isEqual(value, other) {
      return baseIsEqual(value, other);
    }

    /**
     * This method is like `_.isEqual` except that it accepts `customizer` which
     * is invoked to compare values. If `customizer` returns `undefined`, comparisons
     * are handled by the method instead. The `customizer` is invoked with up to
     * six arguments: (objValue, othValue [, index|key, object, other, stack]).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Lang
     * @param {*} value The value to compare.
     * @param {*} other The other value to compare.
     * @param {Function} [customizer] The function to customize comparisons.
     * @returns {boolean} Returns `true` if the values are equivalent, else `false`.
     * @example
     *
     * function isGreeting(value) {
     *   return /^h(?:i|ello)$/.test(value);
     * }
     *
     * function customizer(objValue, othValue) {
     *   if (isGreeting(objValue) && isGreeting(othValue)) {
     *     return true;
     *   }
     * }
     *
     * var array = ['hello', 'goodbye'];
     * var other = ['hi', 'goodbye'];
     *
     * _.isEqualWith(array, other, customizer);
     * // => true
     */
    function isEqualWith(value, other, customizer) {
      customizer = typeof customizer == 'function' ? customizer : undefined;
      var result = customizer ? customizer(value, other) : undefined;
      return result === undefined ? baseIsEqual(value, other, undefined, customizer) : !!result;
    }

    /**
     * Checks if `value` is an `Error`, `EvalError`, `RangeError`, `ReferenceError`,
     * `SyntaxError`, `TypeError`, or `URIError` object.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is an error object, else `false`.
     * @example
     *
     * _.isError(new Error);
     * // => true
     *
     * _.isError(Error);
     * // => false
     */
    function isError(value) {
      if (!isObjectLike(value)) {
        return false;
      }
      var tag = baseGetTag(value);
      return tag == errorTag || tag == domExcTag ||
        (typeof value.message == 'string' && typeof value.name == 'string' && !isPlainObject(value));
    }

    /**
     * Checks if `value` is a finite primitive number.
     *
     * **Note:** This method is based on
     * [`Number.isFinite`](https://mdn.io/Number/isFinite).
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is a finite number, else `false`.
     * @example
     *
     * _.isFinite(3);
     * // => true
     *
     * _.isFinite(Number.MIN_VALUE);
     * // => true
     *
     * _.isFinite(Infinity);
     * // => false
     *
     * _.isFinite('3');
     * // => false
     */
    function isFinite(value) {
      return typeof value == 'number' && nativeIsFinite(value);
    }

    /**
     * Checks if `value` is classified as a `Function` object.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is a function, else `false`.
     * @example
     *
     * _.isFunction(_);
     * // => true
     *
     * _.isFunction(/abc/);
     * // => false
     */
    function isFunction(value) {
      if (!isObject(value)) {
        return false;
      }
      // The use of `Object#toString` avoids issues with the `typeof` operator
      // in Safari 9 which returns 'object' for typed arrays and other constructors.
      var tag = baseGetTag(value);
      return tag == funcTag || tag == genTag || tag == asyncTag || tag == proxyTag;
    }

    /**
     * Checks if `value` is an integer.
     *
     * **Note:** This method is based on
     * [`Number.isInteger`](https://mdn.io/Number/isInteger).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is an integer, else `false`.
     * @example
     *
     * _.isInteger(3);
     * // => true
     *
     * _.isInteger(Number.MIN_VALUE);
     * // => false
     *
     * _.isInteger(Infinity);
     * // => false
     *
     * _.isInteger('3');
     * // => false
     */
    function isInteger(value) {
      return typeof value == 'number' && value == toInteger(value);
    }

    /**
     * Checks if `value` is a valid array-like length.
     *
     * **Note:** This method is loosely based on
     * [`ToLength`](http://ecma-international.org/ecma-262/7.0/#sec-tolength).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is a valid length, else `false`.
     * @example
     *
     * _.isLength(3);
     * // => true
     *
     * _.isLength(Number.MIN_VALUE);
     * // => false
     *
     * _.isLength(Infinity);
     * // => false
     *
     * _.isLength('3');
     * // => false
     */
    function isLength(value) {
      return typeof value == 'number' &&
        value > -1 && value % 1 == 0 && value <= MAX_SAFE_INTEGER;
    }

    /**
     * Checks if `value` is the
     * [language type](http://www.ecma-international.org/ecma-262/7.0/#sec-ecmascript-language-types)
     * of `Object`. (e.g. arrays, functions, objects, regexes, `new Number(0)`, and `new String('')`)
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is an object, else `false`.
     * @example
     *
     * _.isObject({});
     * // => true
     *
     * _.isObject([1, 2, 3]);
     * // => true
     *
     * _.isObject(_.noop);
     * // => true
     *
     * _.isObject(null);
     * // => false
     */
    function isObject(value) {
      var type = typeof value;
      return value != null && (type == 'object' || type == 'function');
    }

    /**
     * Checks if `value` is object-like. A value is object-like if it's not `null`
     * and has a `typeof` result of "object".
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is object-like, else `false`.
     * @example
     *
     * _.isObjectLike({});
     * // => true
     *
     * _.isObjectLike([1, 2, 3]);
     * // => true
     *
     * _.isObjectLike(_.noop);
     * // => false
     *
     * _.isObjectLike(null);
     * // => false
     */
    function isObjectLike(value) {
      return value != null && typeof value == 'object';
    }

    /**
     * Checks if `value` is classified as a `Map` object.
     *
     * @static
     * @memberOf _
     * @since 4.3.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is a map, else `false`.
     * @example
     *
     * _.isMap(new Map);
     * // => true
     *
     * _.isMap(new WeakMap);
     * // => false
     */
    var isMap = nodeIsMap ? baseUnary(nodeIsMap) : baseIsMap;

    /**
     * Performs a partial deep comparison between `object` and `source` to
     * determine if `object` contains equivalent property values.
     *
     * **Note:** This method is equivalent to `_.matches` when `source` is
     * partially applied.
     *
     * Partial comparisons will match empty array and empty object `source`
     * values against any array or object value, respectively. See `_.isEqual`
     * for a list of supported value comparisons.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Lang
     * @param {Object} object The object to inspect.
     * @param {Object} source The object of property values to match.
     * @returns {boolean} Returns `true` if `object` is a match, else `false`.
     * @example
     *
     * var object = { 'a': 1, 'b': 2 };
     *
     * _.isMatch(object, { 'b': 2 });
     * // => true
     *
     * _.isMatch(object, { 'b': 1 });
     * // => false
     */
    function isMatch(object, source) {
      return object === source || baseIsMatch(object, source, getMatchData(source));
    }

    /**
     * This method is like `_.isMatch` except that it accepts `customizer` which
     * is invoked to compare values. If `customizer` returns `undefined`, comparisons
     * are handled by the method instead. The `customizer` is invoked with five
     * arguments: (objValue, srcValue, index|key, object, source).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Lang
     * @param {Object} object The object to inspect.
     * @param {Object} source The object of property values to match.
     * @param {Function} [customizer] The function to customize comparisons.
     * @returns {boolean} Returns `true` if `object` is a match, else `false`.
     * @example
     *
     * function isGreeting(value) {
     *   return /^h(?:i|ello)$/.test(value);
     * }
     *
     * function customizer(objValue, srcValue) {
     *   if (isGreeting(objValue) && isGreeting(srcValue)) {
     *     return true;
     *   }
     * }
     *
     * var object = { 'greeting': 'hello' };
     * var source = { 'greeting': 'hi' };
     *
     * _.isMatchWith(object, source, customizer);
     * // => true
     */
    function isMatchWith(object, source, customizer) {
      customizer = typeof customizer == 'function' ? customizer : undefined;
      return baseIsMatch(object, source, getMatchData(source), customizer);
    }

    /**
     * Checks if `value` is `NaN`.
     *
     * **Note:** This method is based on
     * [`Number.isNaN`](https://mdn.io/Number/isNaN) and is not the same as
     * global [`isNaN`](https://mdn.io/isNaN) which returns `true` for
     * `undefined` and other non-number values.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is `NaN`, else `false`.
     * @example
     *
     * _.isNaN(NaN);
     * // => true
     *
     * _.isNaN(new Number(NaN));
     * // => true
     *
     * isNaN(undefined);
     * // => true
     *
     * _.isNaN(undefined);
     * // => false
     */
    function isNaN(value) {
      // An `NaN` primitive is the only value that is not equal to itself.
      // Perform the `toStringTag` check first to avoid errors with some
      // ActiveX objects in IE.
      return isNumber(value) && value != +value;
    }

    /**
     * Checks if `value` is a pristine native function.
     *
     * **Note:** This method can't reliably detect native functions in the presence
     * of the core-js package because core-js circumvents this kind of detection.
     * Despite multiple requests, the core-js maintainer has made it clear: any
     * attempt to fix the detection will be obstructed. As a result, we're left
     * with little choice but to throw an error. Unfortunately, this also affects
     * packages, like [babel-polyfill](https://www.npmjs.com/package/babel-polyfill),
     * which rely on core-js.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is a native function,
     *  else `false`.
     * @example
     *
     * _.isNative(Array.prototype.push);
     * // => true
     *
     * _.isNative(_);
     * // => false
     */
    function isNative(value) {
      if (isMaskable(value)) {
        throw new Error(CORE_ERROR_TEXT);
      }
      return baseIsNative(value);
    }

    /**
     * Checks if `value` is `null`.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is `null`, else `false`.
     * @example
     *
     * _.isNull(null);
     * // => true
     *
     * _.isNull(void 0);
     * // => false
     */
    function isNull(value) {
      return value === null;
    }

    /**
     * Checks if `value` is `null` or `undefined`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is nullish, else `false`.
     * @example
     *
     * _.isNil(null);
     * // => true
     *
     * _.isNil(void 0);
     * // => true
     *
     * _.isNil(NaN);
     * // => false
     */
    function isNil(value) {
      return value == null;
    }

    /**
     * Checks if `value` is classified as a `Number` primitive or object.
     *
     * **Note:** To exclude `Infinity`, `-Infinity`, and `NaN`, which are
     * classified as numbers, use the `_.isFinite` method.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is a number, else `false`.
     * @example
     *
     * _.isNumber(3);
     * // => true
     *
     * _.isNumber(Number.MIN_VALUE);
     * // => true
     *
     * _.isNumber(Infinity);
     * // => true
     *
     * _.isNumber('3');
     * // => false
     */
    function isNumber(value) {
      return typeof value == 'number' ||
        (isObjectLike(value) && baseGetTag(value) == numberTag);
    }

    /**
     * Checks if `value` is a plain object, that is, an object created by the
     * `Object` constructor or one with a `[[Prototype]]` of `null`.
     *
     * @static
     * @memberOf _
     * @since 0.8.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is a plain object, else `false`.
     * @example
     *
     * function Foo() {
     *   this.a = 1;
     * }
     *
     * _.isPlainObject(new Foo);
     * // => false
     *
     * _.isPlainObject([1, 2, 3]);
     * // => false
     *
     * _.isPlainObject({ 'x': 0, 'y': 0 });
     * // => true
     *
     * _.isPlainObject(Object.create(null));
     * // => true
     */
    function isPlainObject(value) {
      if (!isObjectLike(value) || baseGetTag(value) != objectTag) {
        return false;
      }
      var proto = getPrototype(value);
      if (proto === null) {
        return true;
      }
      var Ctor = hasOwnProperty.call(proto, 'constructor') && proto.constructor;
      return typeof Ctor == 'function' && Ctor instanceof Ctor &&
        funcToString.call(Ctor) == objectCtorString;
    }

    /**
     * Checks if `value` is classified as a `RegExp` object.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is a regexp, else `false`.
     * @example
     *
     * _.isRegExp(/abc/);
     * // => true
     *
     * _.isRegExp('/abc/');
     * // => false
     */
    var isRegExp = nodeIsRegExp ? baseUnary(nodeIsRegExp) : baseIsRegExp;

    /**
     * Checks if `value` is a safe integer. An integer is safe if it's an IEEE-754
     * double precision number which isn't the result of a rounded unsafe integer.
     *
     * **Note:** This method is based on
     * [`Number.isSafeInteger`](https://mdn.io/Number/isSafeInteger).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is a safe integer, else `false`.
     * @example
     *
     * _.isSafeInteger(3);
     * // => true
     *
     * _.isSafeInteger(Number.MIN_VALUE);
     * // => false
     *
     * _.isSafeInteger(Infinity);
     * // => false
     *
     * _.isSafeInteger('3');
     * // => false
     */
    function isSafeInteger(value) {
      return isInteger(value) && value >= -MAX_SAFE_INTEGER && value <= MAX_SAFE_INTEGER;
    }

    /**
     * Checks if `value` is classified as a `Set` object.
     *
     * @static
     * @memberOf _
     * @since 4.3.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is a set, else `false`.
     * @example
     *
     * _.isSet(new Set);
     * // => true
     *
     * _.isSet(new WeakSet);
     * // => false
     */
    var isSet = nodeIsSet ? baseUnary(nodeIsSet) : baseIsSet;

    /**
     * Checks if `value` is classified as a `String` primitive or object.
     *
     * @static
     * @since 0.1.0
     * @memberOf _
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is a string, else `false`.
     * @example
     *
     * _.isString('abc');
     * // => true
     *
     * _.isString(1);
     * // => false
     */
    function isString(value) {
      return typeof value == 'string' ||
        (!isArray(value) && isObjectLike(value) && baseGetTag(value) == stringTag);
    }

    /**
     * Checks if `value` is classified as a `Symbol` primitive or object.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is a symbol, else `false`.
     * @example
     *
     * _.isSymbol(Symbol.iterator);
     * // => true
     *
     * _.isSymbol('abc');
     * // => false
     */
    function isSymbol(value) {
      return typeof value == 'symbol' ||
        (isObjectLike(value) && baseGetTag(value) == symbolTag);
    }

    /**
     * Checks if `value` is classified as a typed array.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is a typed array, else `false`.
     * @example
     *
     * _.isTypedArray(new Uint8Array);
     * // => true
     *
     * _.isTypedArray([]);
     * // => false
     */
    var isTypedArray = nodeIsTypedArray ? baseUnary(nodeIsTypedArray) : baseIsTypedArray;

    /**
     * Checks if `value` is `undefined`.
     *
     * @static
     * @since 0.1.0
     * @memberOf _
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is `undefined`, else `false`.
     * @example
     *
     * _.isUndefined(void 0);
     * // => true
     *
     * _.isUndefined(null);
     * // => false
     */
    function isUndefined(value) {
      return value === undefined;
    }

    /**
     * Checks if `value` is classified as a `WeakMap` object.
     *
     * @static
     * @memberOf _
     * @since 4.3.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is a weak map, else `false`.
     * @example
     *
     * _.isWeakMap(new WeakMap);
     * // => true
     *
     * _.isWeakMap(new Map);
     * // => false
     */
    function isWeakMap(value) {
      return isObjectLike(value) && getTag(value) == weakMapTag;
    }

    /**
     * Checks if `value` is classified as a `WeakSet` object.
     *
     * @static
     * @memberOf _
     * @since 4.3.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is a weak set, else `false`.
     * @example
     *
     * _.isWeakSet(new WeakSet);
     * // => true
     *
     * _.isWeakSet(new Set);
     * // => false
     */
    function isWeakSet(value) {
      return isObjectLike(value) && baseGetTag(value) == weakSetTag;
    }

    /**
     * Checks if `value` is less than `other`.
     *
     * @static
     * @memberOf _
     * @since 3.9.0
     * @category Lang
     * @param {*} value The value to compare.
     * @param {*} other The other value to compare.
     * @returns {boolean} Returns `true` if `value` is less than `other`,
     *  else `false`.
     * @see _.gt
     * @example
     *
     * _.lt(1, 3);
     * // => true
     *
     * _.lt(3, 3);
     * // => false
     *
     * _.lt(3, 1);
     * // => false
     */
    var lt = createRelationalOperation(baseLt);

    /**
     * Checks if `value` is less than or equal to `other`.
     *
     * @static
     * @memberOf _
     * @since 3.9.0
     * @category Lang
     * @param {*} value The value to compare.
     * @param {*} other The other value to compare.
     * @returns {boolean} Returns `true` if `value` is less than or equal to
     *  `other`, else `false`.
     * @see _.gte
     * @example
     *
     * _.lte(1, 3);
     * // => true
     *
     * _.lte(3, 3);
     * // => true
     *
     * _.lte(3, 1);
     * // => false
     */
    var lte = createRelationalOperation(function(value, other) {
      return value <= other;
    });

    /**
     * Converts `value` to an array.
     *
     * @static
     * @since 0.1.0
     * @memberOf _
     * @category Lang
     * @param {*} value The value to convert.
     * @returns {Array} Returns the converted array.
     * @example
     *
     * _.toArray({ 'a': 1, 'b': 2 });
     * // => [1, 2]
     *
     * _.toArray('abc');
     * // => ['a', 'b', 'c']
     *
     * _.toArray(1);
     * // => []
     *
     * _.toArray(null);
     * // => []
     */
    function toArray(value) {
      if (!value) {
        return [];
      }
      if (isArrayLike(value)) {
        return isString(value) ? stringToArray(value) : copyArray(value);
      }
      if (symIterator && value[symIterator]) {
        return iteratorToArray(value[symIterator]());
      }
      var tag = getTag(value),
          func = tag == mapTag ? mapToArray : (tag == setTag ? setToArray : values);

      return func(value);
    }

    /**
     * Converts `value` to a finite number.
     *
     * @static
     * @memberOf _
     * @since 4.12.0
     * @category Lang
     * @param {*} value The value to convert.
     * @returns {number} Returns the converted number.
     * @example
     *
     * _.toFinite(3.2);
     * // => 3.2
     *
     * _.toFinite(Number.MIN_VALUE);
     * // => 5e-324
     *
     * _.toFinite(Infinity);
     * // => 1.7976931348623157e+308
     *
     * _.toFinite('3.2');
     * // => 3.2
     */
    function toFinite(value) {
      if (!value) {
        return value === 0 ? value : 0;
      }
      value = toNumber(value);
      if (value === INFINITY || value === -INFINITY) {
        var sign = (value < 0 ? -1 : 1);
        return sign * MAX_INTEGER;
      }
      return value === value ? value : 0;
    }

    /**
     * Converts `value` to an integer.
     *
     * **Note:** This method is loosely based on
     * [`ToInteger`](http://www.ecma-international.org/ecma-262/7.0/#sec-tointeger).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Lang
     * @param {*} value The value to convert.
     * @returns {number} Returns the converted integer.
     * @example
     *
     * _.toInteger(3.2);
     * // => 3
     *
     * _.toInteger(Number.MIN_VALUE);
     * // => 0
     *
     * _.toInteger(Infinity);
     * // => 1.7976931348623157e+308
     *
     * _.toInteger('3.2');
     * // => 3
     */
    function toInteger(value) {
      var result = toFinite(value),
          remainder = result % 1;

      return result === result ? (remainder ? result - remainder : result) : 0;
    }

    /**
     * Converts `value` to an integer suitable for use as the length of an
     * array-like object.
     *
     * **Note:** This method is based on
     * [`ToLength`](http://ecma-international.org/ecma-262/7.0/#sec-tolength).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Lang
     * @param {*} value The value to convert.
     * @returns {number} Returns the converted integer.
     * @example
     *
     * _.toLength(3.2);
     * // => 3
     *
     * _.toLength(Number.MIN_VALUE);
     * // => 0
     *
     * _.toLength(Infinity);
     * // => 4294967295
     *
     * _.toLength('3.2');
     * // => 3
     */
    function toLength(value) {
      return value ? baseClamp(toInteger(value), 0, MAX_ARRAY_LENGTH) : 0;
    }

    /**
     * Converts `value` to a number.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Lang
     * @param {*} value The value to process.
     * @returns {number} Returns the number.
     * @example
     *
     * _.toNumber(3.2);
     * // => 3.2
     *
     * _.toNumber(Number.MIN_VALUE);
     * // => 5e-324
     *
     * _.toNumber(Infinity);
     * // => Infinity
     *
     * _.toNumber('3.2');
     * // => 3.2
     */
    function toNumber(value) {
      if (typeof value == 'number') {
        return value;
      }
      if (isSymbol(value)) {
        return NAN;
      }
      if (isObject(value)) {
        var other = typeof value.valueOf == 'function' ? value.valueOf() : value;
        value = isObject(other) ? (other + '') : other;
      }
      if (typeof value != 'string') {
        return value === 0 ? value : +value;
      }
      value = baseTrim(value);
      var isBinary = reIsBinary.test(value);
      return (isBinary || reIsOctal.test(value))
        ? freeParseInt(value.slice(2), isBinary ? 2 : 8)
        : (reIsBadHex.test(value) ? NAN : +value);
    }

    /**
     * Converts `value` to a plain object flattening inherited enumerable string
     * keyed properties of `value` to own properties of the plain object.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Lang
     * @param {*} value The value to convert.
     * @returns {Object} Returns the converted plain object.
     * @example
     *
     * function Foo() {
     *   this.b = 2;
     * }
     *
     * Foo.prototype.c = 3;
     *
     * _.assign({ 'a': 1 }, new Foo);
     * // => { 'a': 1, 'b': 2 }
     *
     * _.assign({ 'a': 1 }, _.toPlainObject(new Foo));
     * // => { 'a': 1, 'b': 2, 'c': 3 }
     */
    function toPlainObject(value) {
      return copyObject(value, keysIn(value));
    }

    /**
     * Converts `value` to a safe integer. A safe integer can be compared and
     * represented correctly.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Lang
     * @param {*} value The value to convert.
     * @returns {number} Returns the converted integer.
     * @example
     *
     * _.toSafeInteger(3.2);
     * // => 3
     *
     * _.toSafeInteger(Number.MIN_VALUE);
     * // => 0
     *
     * _.toSafeInteger(Infinity);
     * // => 9007199254740991
     *
     * _.toSafeInteger('3.2');
     * // => 3
     */
    function toSafeInteger(value) {
      return value
        ? baseClamp(toInteger(value), -MAX_SAFE_INTEGER, MAX_SAFE_INTEGER)
        : (value === 0 ? value : 0);
    }

    /**
     * Converts `value` to a string. An empty string is returned for `null`
     * and `undefined` values. The sign of `-0` is preserved.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Lang
     * @param {*} value The value to convert.
     * @returns {string} Returns the converted string.
     * @example
     *
     * _.toString(null);
     * // => ''
     *
     * _.toString(-0);
     * // => '-0'
     *
     * _.toString([1, 2, 3]);
     * // => '1,2,3'
     */
    function toString(value) {
      return value == null ? '' : baseToString(value);
    }

    /*------------------------------------------------------------------------*/

    /**
     * Assigns own enumerable string keyed properties of source objects to the
     * destination object. Source objects are applied from left to right.
     * Subsequent sources overwrite property assignments of previous sources.
     *
     * **Note:** This method mutates `object` and is loosely based on
     * [`Object.assign`](https://mdn.io/Object/assign).
     *
     * @static
     * @memberOf _
     * @since 0.10.0
     * @category Object
     * @param {Object} object The destination object.
     * @param {...Object} [sources] The source objects.
     * @returns {Object} Returns `object`.
     * @see _.assignIn
     * @example
     *
     * function Foo() {
     *   this.a = 1;
     * }
     *
     * function Bar() {
     *   this.c = 3;
     * }
     *
     * Foo.prototype.b = 2;
     * Bar.prototype.d = 4;
     *
     * _.assign({ 'a': 0 }, new Foo, new Bar);
     * // => { 'a': 1, 'c': 3 }
     */
    var assign = createAssigner(function(object, source) {
      if (isPrototype(source) || isArrayLike(source)) {
        copyObject(source, keys(source), object);
        return;
      }
      for (var key in source) {
        if (hasOwnProperty.call(source, key)) {
          assignValue(object, key, source[key]);
        }
      }
    });

    /**
     * This method is like `_.assign` except that it iterates over own and
     * inherited source properties.
     *
     * **Note:** This method mutates `object`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @alias extend
     * @category Object
     * @param {Object} object The destination object.
     * @param {...Object} [sources] The source objects.
     * @returns {Object} Returns `object`.
     * @see _.assign
     * @example
     *
     * function Foo() {
     *   this.a = 1;
     * }
     *
     * function Bar() {
     *   this.c = 3;
     * }
     *
     * Foo.prototype.b = 2;
     * Bar.prototype.d = 4;
     *
     * _.assignIn({ 'a': 0 }, new Foo, new Bar);
     * // => { 'a': 1, 'b': 2, 'c': 3, 'd': 4 }
     */
    var assignIn = createAssigner(function(object, source) {
      copyObject(source, keysIn(source), object);
    });

    /**
     * This method is like `_.assignIn` except that it accepts `customizer`
     * which is invoked to produce the assigned values. If `customizer` returns
     * `undefined`, assignment is handled by the method instead. The `customizer`
     * is invoked with five arguments: (objValue, srcValue, key, object, source).
     *
     * **Note:** This method mutates `object`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @alias extendWith
     * @category Object
     * @param {Object} object The destination object.
     * @param {...Object} sources The source objects.
     * @param {Function} [customizer] The function to customize assigned values.
     * @returns {Object} Returns `object`.
     * @see _.assignWith
     * @example
     *
     * function customizer(objValue, srcValue) {
     *   return _.isUndefined(objValue) ? srcValue : objValue;
     * }
     *
     * var defaults = _.partialRight(_.assignInWith, customizer);
     *
     * defaults({ 'a': 1 }, { 'b': 2 }, { 'a': 3 });
     * // => { 'a': 1, 'b': 2 }
     */
    var assignInWith = createAssigner(function(object, source, srcIndex, customizer) {
      copyObject(source, keysIn(source), object, customizer);
    });

    /**
     * This method is like `_.assign` except that it accepts `customizer`
     * which is invoked to produce the assigned values. If `customizer` returns
     * `undefined`, assignment is handled by the method instead. The `customizer`
     * is invoked with five arguments: (objValue, srcValue, key, object, source).
     *
     * **Note:** This method mutates `object`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Object
     * @param {Object} object The destination object.
     * @param {...Object} sources The source objects.
     * @param {Function} [customizer] The function to customize assigned values.
     * @returns {Object} Returns `object`.
     * @see _.assignInWith
     * @example
     *
     * function customizer(objValue, srcValue) {
     *   return _.isUndefined(objValue) ? srcValue : objValue;
     * }
     *
     * var defaults = _.partialRight(_.assignWith, customizer);
     *
     * defaults({ 'a': 1 }, { 'b': 2 }, { 'a': 3 });
     * // => { 'a': 1, 'b': 2 }
     */
    var assignWith = createAssigner(function(object, source, srcIndex, customizer) {
      copyObject(source, keys(source), object, customizer);
    });

    /**
     * Creates an array of values corresponding to `paths` of `object`.
     *
     * @static
     * @memberOf _
     * @since 1.0.0
     * @category Object
     * @param {Object} object The object to iterate over.
     * @param {...(string|string[])} [paths] The property paths to pick.
     * @returns {Array} Returns the picked values.
     * @example
     *
     * var object = { 'a': [{ 'b': { 'c': 3 } }, 4] };
     *
     * _.at(object, ['a[0].b.c', 'a[1]']);
     * // => [3, 4]
     */
    var at = flatRest(baseAt);

    /**
     * Creates an object that inherits from the `prototype` object. If a
     * `properties` object is given, its own enumerable string keyed properties
     * are assigned to the created object.
     *
     * @static
     * @memberOf _
     * @since 2.3.0
     * @category Object
     * @param {Object} prototype The object to inherit from.
     * @param {Object} [properties] The properties to assign to the object.
     * @returns {Object} Returns the new object.
     * @example
     *
     * function Shape() {
     *   this.x = 0;
     *   this.y = 0;
     * }
     *
     * function Circle() {
     *   Shape.call(this);
     * }
     *
     * Circle.prototype = _.create(Shape.prototype, {
     *   'constructor': Circle
     * });
     *
     * var circle = new Circle;
     * circle instanceof Circle;
     * // => true
     *
     * circle instanceof Shape;
     * // => true
     */
    function create(prototype, properties) {
      var result = baseCreate(prototype);
      return properties == null ? result : baseAssign(result, properties);
    }

    /**
     * Assigns own and inherited enumerable string keyed properties of source
     * objects to the destination object for all destination properties that
     * resolve to `undefined`. Source objects are applied from left to right.
     * Once a property is set, additional values of the same property are ignored.
     *
     * **Note:** This method mutates `object`.
     *
     * @static
     * @since 0.1.0
     * @memberOf _
     * @category Object
     * @param {Object} object The destination object.
     * @param {...Object} [sources] The source objects.
     * @returns {Object} Returns `object`.
     * @see _.defaultsDeep
     * @example
     *
     * _.defaults({ 'a': 1 }, { 'b': 2 }, { 'a': 3 });
     * // => { 'a': 1, 'b': 2 }
     */
    var defaults = baseRest(function(object, sources) {
      object = Object(object);

      var index = -1;
      var length = sources.length;
      var guard = length > 2 ? sources[2] : undefined;

      if (guard && isIterateeCall(sources[0], sources[1], guard)) {
        length = 1;
      }

      while (++index < length) {
        var source = sources[index];
        var props = keysIn(source);
        var propsIndex = -1;
        var propsLength = props.length;

        while (++propsIndex < propsLength) {
          var key = props[propsIndex];
          var value = object[key];

          if (value === undefined ||
              (eq(value, objectProto[key]) && !hasOwnProperty.call(object, key))) {
            object[key] = source[key];
          }
        }
      }

      return object;
    });

    /**
     * This method is like `_.defaults` except that it recursively assigns
     * default properties.
     *
     * **Note:** This method mutates `object`.
     *
     * @static
     * @memberOf _
     * @since 3.10.0
     * @category Object
     * @param {Object} object The destination object.
     * @param {...Object} [sources] The source objects.
     * @returns {Object} Returns `object`.
     * @see _.defaults
     * @example
     *
     * _.defaultsDeep({ 'a': { 'b': 2 } }, { 'a': { 'b': 1, 'c': 3 } });
     * // => { 'a': { 'b': 2, 'c': 3 } }
     */
    var defaultsDeep = baseRest(function(args) {
      args.push(undefined, customDefaultsMerge);
      return apply(mergeWith, undefined, args);
    });

    /**
     * This method is like `_.find` except that it returns the key of the first
     * element `predicate` returns truthy for instead of the element itself.
     *
     * @static
     * @memberOf _
     * @since 1.1.0
     * @category Object
     * @param {Object} object The object to inspect.
     * @param {Function} [predicate=_.identity] The function invoked per iteration.
     * @returns {string|undefined} Returns the key of the matched element,
     *  else `undefined`.
     * @example
     *
     * var users = {
     *   'barney':  { 'age': 36, 'active': true },
     *   'fred':    { 'age': 40, 'active': false },
     *   'pebbles': { 'age': 1,  'active': true }
     * };
     *
     * _.findKey(users, function(o) { return o.age < 40; });
     * // => 'barney' (iteration order is not guaranteed)
     *
     * // The `_.matches` iteratee shorthand.
     * _.findKey(users, { 'age': 1, 'active': true });
     * // => 'pebbles'
     *
     * // The `_.matchesProperty` iteratee shorthand.
     * _.findKey(users, ['active', false]);
     * // => 'fred'
     *
     * // The `_.property` iteratee shorthand.
     * _.findKey(users, 'active');
     * // => 'barney'
     */
    function findKey(object, predicate) {
      return baseFindKey(object, getIteratee(predicate, 3), baseForOwn);
    }

    /**
     * This method is like `_.findKey` except that it iterates over elements of
     * a collection in the opposite order.
     *
     * @static
     * @memberOf _
     * @since 2.0.0
     * @category Object
     * @param {Object} object The object to inspect.
     * @param {Function} [predicate=_.identity] The function invoked per iteration.
     * @returns {string|undefined} Returns the key of the matched element,
     *  else `undefined`.
     * @example
     *
     * var users = {
     *   'barney':  { 'age': 36, 'active': true },
     *   'fred':    { 'age': 40, 'active': false },
     *   'pebbles': { 'age': 1,  'active': true }
     * };
     *
     * _.findLastKey(users, function(o) { return o.age < 40; });
     * // => returns 'pebbles' assuming `_.findKey` returns 'barney'
     *
     * // The `_.matches` iteratee shorthand.
     * _.findLastKey(users, { 'age': 36, 'active': true });
     * // => 'barney'
     *
     * // The `_.matchesProperty` iteratee shorthand.
     * _.findLastKey(users, ['active', false]);
     * // => 'fred'
     *
     * // The `_.property` iteratee shorthand.
     * _.findLastKey(users, 'active');
     * // => 'pebbles'
     */
    function findLastKey(object, predicate) {
      return baseFindKey(object, getIteratee(predicate, 3), baseForOwnRight);
    }

    /**
     * Iterates over own and inherited enumerable string keyed properties of an
     * object and invokes `iteratee` for each property. The iteratee is invoked
     * with three arguments: (value, key, object). Iteratee functions may exit
     * iteration early by explicitly returning `false`.
     *
     * @static
     * @memberOf _
     * @since 0.3.0
     * @category Object
     * @param {Object} object The object to iterate over.
     * @param {Function} [iteratee=_.identity] The function invoked per iteration.
     * @returns {Object} Returns `object`.
     * @see _.forInRight
     * @example
     *
     * function Foo() {
     *   this.a = 1;
     *   this.b = 2;
     * }
     *
     * Foo.prototype.c = 3;
     *
     * _.forIn(new Foo, function(value, key) {
     *   console.log(key);
     * });
     * // => Logs 'a', 'b', then 'c' (iteration order is not guaranteed).
     */
    function forIn(object, iteratee) {
      return object == null
        ? object
        : baseFor(object, getIteratee(iteratee, 3), keysIn);
    }

    /**
     * This method is like `_.forIn` except that it iterates over properties of
     * `object` in the opposite order.
     *
     * @static
     * @memberOf _
     * @since 2.0.0
     * @category Object
     * @param {Object} object The object to iterate over.
     * @param {Function} [iteratee=_.identity] The function invoked per iteration.
     * @returns {Object} Returns `object`.
     * @see _.forIn
     * @example
     *
     * function Foo() {
     *   this.a = 1;
     *   this.b = 2;
     * }
     *
     * Foo.prototype.c = 3;
     *
     * _.forInRight(new Foo, function(value, key) {
     *   console.log(key);
     * });
     * // => Logs 'c', 'b', then 'a' assuming `_.forIn` logs 'a', 'b', then 'c'.
     */
    function forInRight(object, iteratee) {
      return object == null
        ? object
        : baseForRight(object, getIteratee(iteratee, 3), keysIn);
    }

    /**
     * Iterates over own enumerable string keyed properties of an object and
     * invokes `iteratee` for each property. The iteratee is invoked with three
     * arguments: (value, key, object). Iteratee functions may exit iteration
     * early by explicitly returning `false`.
     *
     * @static
     * @memberOf _
     * @since 0.3.0
     * @category Object
     * @param {Object} object The object to iterate over.
     * @param {Function} [iteratee=_.identity] The function invoked per iteration.
     * @returns {Object} Returns `object`.
     * @see _.forOwnRight
     * @example
     *
     * function Foo() {
     *   this.a = 1;
     *   this.b = 2;
     * }
     *
     * Foo.prototype.c = 3;
     *
     * _.forOwn(new Foo, function(value, key) {
     *   console.log(key);
     * });
     * // => Logs 'a' then 'b' (iteration order is not guaranteed).
     */
    function forOwn(object, iteratee) {
      return object && baseForOwn(object, getIteratee(iteratee, 3));
    }

    /**
     * This method is like `_.forOwn` except that it iterates over properties of
     * `object` in the opposite order.
     *
     * @static
     * @memberOf _
     * @since 2.0.0
     * @category Object
     * @param {Object} object The object to iterate over.
     * @param {Function} [iteratee=_.identity] The function invoked per iteration.
     * @returns {Object} Returns `object`.
     * @see _.forOwn
     * @example
     *
     * function Foo() {
     *   this.a = 1;
     *   this.b = 2;
     * }
     *
     * Foo.prototype.c = 3;
     *
     * _.forOwnRight(new Foo, function(value, key) {
     *   console.log(key);
     * });
     * // => Logs 'b' then 'a' assuming `_.forOwn` logs 'a' then 'b'.
     */
    function forOwnRight(object, iteratee) {
      return object && baseForOwnRight(object, getIteratee(iteratee, 3));
    }

    /**
     * Creates an array of function property names from own enumerable properties
     * of `object`.
     *
     * @static
     * @since 0.1.0
     * @memberOf _
     * @category Object
     * @param {Object} object The object to inspect.
     * @returns {Array} Returns the function names.
     * @see _.functionsIn
     * @example
     *
     * function Foo() {
     *   this.a = _.constant('a');
     *   this.b = _.constant('b');
     * }
     *
     * Foo.prototype.c = _.constant('c');
     *
     * _.functions(new Foo);
     * // => ['a', 'b']
     */
    function functions(object) {
      return object == null ? [] : baseFunctions(object, keys(object));
    }

    /**
     * Creates an array of function property names from own and inherited
     * enumerable properties of `object`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Object
     * @param {Object} object The object to inspect.
     * @returns {Array} Returns the function names.
     * @see _.functions
     * @example
     *
     * function Foo() {
     *   this.a = _.constant('a');
     *   this.b = _.constant('b');
     * }
     *
     * Foo.prototype.c = _.constant('c');
     *
     * _.functionsIn(new Foo);
     * // => ['a', 'b', 'c']
     */
    function functionsIn(object) {
      return object == null ? [] : baseFunctions(object, keysIn(object));
    }

    /**
     * Gets the value at `path` of `object`. If the resolved value is
     * `undefined`, the `defaultValue` is returned in its place.
     *
     * @static
     * @memberOf _
     * @since 3.7.0
     * @category Object
     * @param {Object} object The object to query.
     * @param {Array|string} path The path of the property to get.
     * @param {*} [defaultValue] The value returned for `undefined` resolved values.
     * @returns {*} Returns the resolved value.
     * @example
     *
     * var object = { 'a': [{ 'b': { 'c': 3 } }] };
     *
     * _.get(object, 'a[0].b.c');
     * // => 3
     *
     * _.get(object, ['a', '0', 'b', 'c']);
     * // => 3
     *
     * _.get(object, 'a.b.c', 'default');
     * // => 'default'
     */
    function get(object, path, defaultValue) {
      var result = object == null ? undefined : baseGet(object, path);
      return result === undefined ? defaultValue : result;
    }

    /**
     * Checks if `path` is a direct property of `object`.
     *
     * @static
     * @since 0.1.0
     * @memberOf _
     * @category Object
     * @param {Object} object The object to query.
     * @param {Array|string} path The path to check.
     * @returns {boolean} Returns `true` if `path` exists, else `false`.
     * @example
     *
     * var object = { 'a': { 'b': 2 } };
     * var other = _.create({ 'a': _.create({ 'b': 2 }) });
     *
     * _.has(object, 'a');
     * // => true
     *
     * _.has(object, 'a.b');
     * // => true
     *
     * _.has(object, ['a', 'b']);
     * // => true
     *
     * _.has(other, 'a');
     * // => false
     */
    function has(object, path) {
      return object != null && hasPath(object, path, baseHas);
    }

    /**
     * Checks if `path` is a direct or inherited property of `object`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Object
     * @param {Object} object The object to query.
     * @param {Array|string} path The path to check.
     * @returns {boolean} Returns `true` if `path` exists, else `false`.
     * @example
     *
     * var object = _.create({ 'a': _.create({ 'b': 2 }) });
     *
     * _.hasIn(object, 'a');
     * // => true
     *
     * _.hasIn(object, 'a.b');
     * // => true
     *
     * _.hasIn(object, ['a', 'b']);
     * // => true
     *
     * _.hasIn(object, 'b');
     * // => false
     */
    function hasIn(object, path) {
      return object != null && hasPath(object, path, baseHasIn);
    }

    /**
     * Creates an object composed of the inverted keys and values of `object`.
     * If `object` contains duplicate values, subsequent values overwrite
     * property assignments of previous values.
     *
     * @static
     * @memberOf _
     * @since 0.7.0
     * @category Object
     * @param {Object} object The object to invert.
     * @returns {Object} Returns the new inverted object.
     * @example
     *
     * var object = { 'a': 1, 'b': 2, 'c': 1 };
     *
     * _.invert(object);
     * // => { '1': 'c', '2': 'b' }
     */
    var invert = createInverter(function(result, value, key) {
      if (value != null &&
          typeof value.toString != 'function') {
        value = nativeObjectToString.call(value);
      }

      result[value] = key;
    }, constant(identity));

    /**
     * This method is like `_.invert` except that the inverted object is generated
     * from the results of running each element of `object` thru `iteratee`. The
     * corresponding inverted value of each inverted key is an array of keys
     * responsible for generating the inverted value. The iteratee is invoked
     * with one argument: (value).
     *
     * @static
     * @memberOf _
     * @since 4.1.0
     * @category Object
     * @param {Object} object The object to invert.
     * @param {Function} [iteratee=_.identity] The iteratee invoked per element.
     * @returns {Object} Returns the new inverted object.
     * @example
     *
     * var object = { 'a': 1, 'b': 2, 'c': 1 };
     *
     * _.invertBy(object);
     * // => { '1': ['a', 'c'], '2': ['b'] }
     *
     * _.invertBy(object, function(value) {
     *   return 'group' + value;
     * });
     * // => { 'group1': ['a', 'c'], 'group2': ['b'] }
     */
    var invertBy = createInverter(function(result, value, key) {
      if (value != null &&
          typeof value.toString != 'function') {
        value = nativeObjectToString.call(value);
      }

      if (hasOwnProperty.call(result, value)) {
        result[value].push(key);
      } else {
        result[value] = [key];
      }
    }, getIteratee);

    /**
     * Invokes the method at `path` of `object`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Object
     * @param {Object} object The object to query.
     * @param {Array|string} path The path of the method to invoke.
     * @param {...*} [args] The arguments to invoke the method with.
     * @returns {*} Returns the result of the invoked method.
     * @example
     *
     * var object = { 'a': [{ 'b': { 'c': [1, 2, 3, 4] } }] };
     *
     * _.invoke(object, 'a[0].b.c.slice', 1, 3);
     * // => [2, 3]
     */
    var invoke = baseRest(baseInvoke);

    /**
     * Creates an array of the own enumerable property names of `object`.
     *
     * **Note:** Non-object values are coerced to objects. See the
     * [ES spec](http://ecma-international.org/ecma-262/7.0/#sec-object.keys)
     * for more details.
     *
     * @static
     * @since 0.1.0
     * @memberOf _
     * @category Object
     * @param {Object} object The object to query.
     * @returns {Array} Returns the array of property names.
     * @example
     *
     * function Foo() {
     *   this.a = 1;
     *   this.b = 2;
     * }
     *
     * Foo.prototype.c = 3;
     *
     * _.keys(new Foo);
     * // => ['a', 'b'] (iteration order is not guaranteed)
     *
     * _.keys('hi');
     * // => ['0', '1']
     */
    function keys(object) {
      return isArrayLike(object) ? arrayLikeKeys(object) : baseKeys(object);
    }

    /**
     * Creates an array of the own and inherited enumerable property names of `object`.
     *
     * **Note:** Non-object values are coerced to objects.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Object
     * @param {Object} object The object to query.
     * @returns {Array} Returns the array of property names.
     * @example
     *
     * function Foo() {
     *   this.a = 1;
     *   this.b = 2;
     * }
     *
     * Foo.prototype.c = 3;
     *
     * _.keysIn(new Foo);
     * // => ['a', 'b', 'c'] (iteration order is not guaranteed)
     */
    function keysIn(object) {
      return isArrayLike(object) ? arrayLikeKeys(object, true) : baseKeysIn(object);
    }

    /**
     * The opposite of `_.mapValues`; this method creates an object with the
     * same values as `object` and keys generated by running each own enumerable
     * string keyed property of `object` thru `iteratee`. The iteratee is invoked
     * with three arguments: (value, key, object).
     *
     * @static
     * @memberOf _
     * @since 3.8.0
     * @category Object
     * @param {Object} object The object to iterate over.
     * @param {Function} [iteratee=_.identity] The function invoked per iteration.
     * @returns {Object} Returns the new mapped object.
     * @see _.mapValues
     * @example
     *
     * _.mapKeys({ 'a': 1, 'b': 2 }, function(value, key) {
     *   return key + value;
     * });
     * // => { 'a1': 1, 'b2': 2 }
     */
    function mapKeys(object, iteratee) {
      var result = {};
      iteratee = getIteratee(iteratee, 3);

      baseForOwn(object, function(value, key, object) {
        baseAssignValue(result, iteratee(value, key, object), value);
      });
      return result;
    }

    /**
     * Creates an object with the same keys as `object` and values generated
     * by running each own enumerable string keyed property of `object` thru
     * `iteratee`. The iteratee is invoked with three arguments:
     * (value, key, object).
     *
     * @static
     * @memberOf _
     * @since 2.4.0
     * @category Object
     * @param {Object} object The object to iterate over.
     * @param {Function} [iteratee=_.identity] The function invoked per iteration.
     * @returns {Object} Returns the new mapped object.
     * @see _.mapKeys
     * @example
     *
     * var users = {
     *   'fred':    { 'user': 'fred',    'age': 40 },
     *   'pebbles': { 'user': 'pebbles', 'age': 1 }
     * };
     *
     * _.mapValues(users, function(o) { return o.age; });
     * // => { 'fred': 40, 'pebbles': 1 } (iteration order is not guaranteed)
     *
     * // The `_.property` iteratee shorthand.
     * _.mapValues(users, 'age');
     * // => { 'fred': 40, 'pebbles': 1 } (iteration order is not guaranteed)
     */
    function mapValues(object, iteratee) {
      var result = {};
      iteratee = getIteratee(iteratee, 3);

      baseForOwn(object, function(value, key, object) {
        baseAssignValue(result, key, iteratee(value, key, object));
      });
      return result;
    }

    /**
     * This method is like `_.assign` except that it recursively merges own and
     * inherited enumerable string keyed properties of source objects into the
     * destination object. Source properties that resolve to `undefined` are
     * skipped if a destination value exists. Array and plain object properties
     * are merged recursively. Other objects and value types are overridden by
     * assignment. Source objects are applied from left to right. Subsequent
     * sources overwrite property assignments of previous sources.
     *
     * **Note:** This method mutates `object`.
     *
     * @static
     * @memberOf _
     * @since 0.5.0
     * @category Object
     * @param {Object} object The destination object.
     * @param {...Object} [sources] The source objects.
     * @returns {Object} Returns `object`.
     * @example
     *
     * var object = {
     *   'a': [{ 'b': 2 }, { 'd': 4 }]
     * };
     *
     * var other = {
     *   'a': [{ 'c': 3 }, { 'e': 5 }]
     * };
     *
     * _.merge(object, other);
     * // => { 'a': [{ 'b': 2, 'c': 3 }, { 'd': 4, 'e': 5 }] }
     */
    var merge = createAssigner(function(object, source, srcIndex) {
      baseMerge(object, source, srcIndex);
    });

    /**
     * This method is like `_.merge` except that it accepts `customizer` which
     * is invoked to produce the merged values of the destination and source
     * properties. If `customizer` returns `undefined`, merging is handled by the
     * method instead. The `customizer` is invoked with six arguments:
     * (objValue, srcValue, key, object, source, stack).
     *
     * **Note:** This method mutates `object`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Object
     * @param {Object} object The destination object.
     * @param {...Object} sources The source objects.
     * @param {Function} customizer The function to customize assigned values.
     * @returns {Object} Returns `object`.
     * @example
     *
     * function customizer(objValue, srcValue) {
     *   if (_.isArray(objValue)) {
     *     return objValue.concat(srcValue);
     *   }
     * }
     *
     * var object = { 'a': [1], 'b': [2] };
     * var other = { 'a': [3], 'b': [4] };
     *
     * _.mergeWith(object, other, customizer);
     * // => { 'a': [1, 3], 'b': [2, 4] }
     */
    var mergeWith = createAssigner(function(object, source, srcIndex, customizer) {
      baseMerge(object, source, srcIndex, customizer);
    });

    /**
     * The opposite of `_.pick`; this method creates an object composed of the
     * own and inherited enumerable property paths of `object` that are not omitted.
     *
     * **Note:** This method is considerably slower than `_.pick`.
     *
     * @static
     * @since 0.1.0
     * @memberOf _
     * @category Object
     * @param {Object} object The source object.
     * @param {...(string|string[])} [paths] The property paths to omit.
     * @returns {Object} Returns the new object.
     * @example
     *
     * var object = { 'a': 1, 'b': '2', 'c': 3 };
     *
     * _.omit(object, ['a', 'c']);
     * // => { 'b': '2' }
     */
    var omit = flatRest(function(object, paths) {
      var result = {};
      if (object == null) {
        return result;
      }
      var isDeep = false;
      paths = arrayMap(paths, function(path) {
        path = castPath(path, object);
        isDeep || (isDeep = path.length > 1);
        return path;
      });
      copyObject(object, getAllKeysIn(object), result);
      if (isDeep) {
        result = baseClone(result, CLONE_DEEP_FLAG | CLONE_FLAT_FLAG | CLONE_SYMBOLS_FLAG, customOmitClone);
      }
      var length = paths.length;
      while (length--) {
        baseUnset(result, paths[length]);
      }
      return result;
    });

    /**
     * The opposite of `_.pickBy`; this method creates an object composed of
     * the own and inherited enumerable string keyed properties of `object` that
     * `predicate` doesn't return truthy for. The predicate is invoked with two
     * arguments: (value, key).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Object
     * @param {Object} object The source object.
     * @param {Function} [predicate=_.identity] The function invoked per property.
     * @returns {Object} Returns the new object.
     * @example
     *
     * var object = { 'a': 1, 'b': '2', 'c': 3 };
     *
     * _.omitBy(object, _.isNumber);
     * // => { 'b': '2' }
     */
    function omitBy(object, predicate) {
      return pickBy(object, negate(getIteratee(predicate)));
    }

    /**
     * Creates an object composed of the picked `object` properties.
     *
     * @static
     * @since 0.1.0
     * @memberOf _
     * @category Object
     * @param {Object} object The source object.
     * @param {...(string|string[])} [paths] The property paths to pick.
     * @returns {Object} Returns the new object.
     * @example
     *
     * var object = { 'a': 1, 'b': '2', 'c': 3 };
     *
     * _.pick(object, ['a', 'c']);
     * // => { 'a': 1, 'c': 3 }
     */
    var pick = flatRest(function(object, paths) {
      return object == null ? {} : basePick(object, paths);
    });

    /**
     * Creates an object composed of the `object` properties `predicate` returns
     * truthy for. The predicate is invoked with two arguments: (value, key).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Object
     * @param {Object} object The source object.
     * @param {Function} [predicate=_.identity] The function invoked per property.
     * @returns {Object} Returns the new object.
     * @example
     *
     * var object = { 'a': 1, 'b': '2', 'c': 3 };
     *
     * _.pickBy(object, _.isNumber);
     * // => { 'a': 1, 'c': 3 }
     */
    function pickBy(object, predicate) {
      if (object == null) {
        return {};
      }
      var props = arrayMap(getAllKeysIn(object), function(prop) {
        return [prop];
      });
      predicate = getIteratee(predicate);
      return basePickBy(object, props, function(value, path) {
        return predicate(value, path[0]);
      });
    }

    /**
     * This method is like `_.get` except that if the resolved value is a
     * function it's invoked with the `this` binding of its parent object and
     * its result is returned.
     *
     * @static
     * @since 0.1.0
     * @memberOf _
     * @category Object
     * @param {Object} object The object to query.
     * @param {Array|string} path The path of the property to resolve.
     * @param {*} [defaultValue] The value returned for `undefined` resolved values.
     * @returns {*} Returns the resolved value.
     * @example
     *
     * var object = { 'a': [{ 'b': { 'c1': 3, 'c2': _.constant(4) } }] };
     *
     * _.result(object, 'a[0].b.c1');
     * // => 3
     *
     * _.result(object, 'a[0].b.c2');
     * // => 4
     *
     * _.result(object, 'a[0].b.c3', 'default');
     * // => 'default'
     *
     * _.result(object, 'a[0].b.c3', _.constant('default'));
     * // => 'default'
     */
    function result(object, path, defaultValue) {
      path = castPath(path, object);

      var index = -1,
          length = path.length;

      // Ensure the loop is entered when path is empty.
      if (!length) {
        length = 1;
        object = undefined;
      }
      while (++index < length) {
        var value = object == null ? undefined : object[toKey(path[index])];
        if (value === undefined) {
          index = length;
          value = defaultValue;
        }
        object = isFunction(value) ? value.call(object) : value;
      }
      return object;
    }

    /**
     * Sets the value at `path` of `object`. If a portion of `path` doesn't exist,
     * it's created. Arrays are created for missing index properties while objects
     * are created for all other missing properties. Use `_.setWith` to customize
     * `path` creation.
     *
     * **Note:** This method mutates `object`.
     *
     * @static
     * @memberOf _
     * @since 3.7.0
     * @category Object
     * @param {Object} object The object to modify.
     * @param {Array|string} path The path of the property to set.
     * @param {*} value The value to set.
     * @returns {Object} Returns `object`.
     * @example
     *
     * var object = { 'a': [{ 'b': { 'c': 3 } }] };
     *
     * _.set(object, 'a[0].b.c', 4);
     * console.log(object.a[0].b.c);
     * // => 4
     *
     * _.set(object, ['x', '0', 'y', 'z'], 5);
     * console.log(object.x[0].y.z);
     * // => 5
     */
    function set(object, path, value) {
      return object == null ? object : baseSet(object, path, value);
    }

    /**
     * This method is like `_.set` except that it accepts `customizer` which is
     * invoked to produce the objects of `path`.  If `customizer` returns `undefined`
     * path creation is handled by the method instead. The `customizer` is invoked
     * with three arguments: (nsValue, key, nsObject).
     *
     * **Note:** This method mutates `object`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Object
     * @param {Object} object The object to modify.
     * @param {Array|string} path The path of the property to set.
     * @param {*} value The value to set.
     * @param {Function} [customizer] The function to customize assigned values.
     * @returns {Object} Returns `object`.
     * @example
     *
     * var object = {};
     *
     * _.setWith(object, '[0][1]', 'a', Object);
     * // => { '0': { '1': 'a' } }
     */
    function setWith(object, path, value, customizer) {
      customizer = typeof customizer == 'function' ? customizer : undefined;
      return object == null ? object : baseSet(object, path, value, customizer);
    }

    /**
     * Creates an array of own enumerable string keyed-value pairs for `object`
     * which can be consumed by `_.fromPairs`. If `object` is a map or set, its
     * entries are returned.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @alias entries
     * @category Object
     * @param {Object} object The object to query.
     * @returns {Array} Returns the key-value pairs.
     * @example
     *
     * function Foo() {
     *   this.a = 1;
     *   this.b = 2;
     * }
     *
     * Foo.prototype.c = 3;
     *
     * _.toPairs(new Foo);
     * // => [['a', 1], ['b', 2]] (iteration order is not guaranteed)
     */
    var toPairs = createToPairs(keys);

    /**
     * Creates an array of own and inherited enumerable string keyed-value pairs
     * for `object` which can be consumed by `_.fromPairs`. If `object` is a map
     * or set, its entries are returned.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @alias entriesIn
     * @category Object
     * @param {Object} object The object to query.
     * @returns {Array} Returns the key-value pairs.
     * @example
     *
     * function Foo() {
     *   this.a = 1;
     *   this.b = 2;
     * }
     *
     * Foo.prototype.c = 3;
     *
     * _.toPairsIn(new Foo);
     * // => [['a', 1], ['b', 2], ['c', 3]] (iteration order is not guaranteed)
     */
    var toPairsIn = createToPairs(keysIn);

    /**
     * An alternative to `_.reduce`; this method transforms `object` to a new
     * `accumulator` object which is the result of running each of its own
     * enumerable string keyed properties thru `iteratee`, with each invocation
     * potentially mutating the `accumulator` object. If `accumulator` is not
     * provided, a new object with the same `[[Prototype]]` will be used. The
     * iteratee is invoked with four arguments: (accumulator, value, key, object).
     * Iteratee functions may exit iteration early by explicitly returning `false`.
     *
     * @static
     * @memberOf _
     * @since 1.3.0
     * @category Object
     * @param {Object} object The object to iterate over.
     * @param {Function} [iteratee=_.identity] The function invoked per iteration.
     * @param {*} [accumulator] The custom accumulator value.
     * @returns {*} Returns the accumulated value.
     * @example
     *
     * _.transform([2, 3, 4], function(result, n) {
     *   result.push(n *= n);
     *   return n % 2 == 0;
     * }, []);
     * // => [4, 9]
     *
     * _.transform({ 'a': 1, 'b': 2, 'c': 1 }, function(result, value, key) {
     *   (result[value] || (result[value] = [])).push(key);
     * }, {});
     * // => { '1': ['a', 'c'], '2': ['b'] }
     */
    function transform(object, iteratee, accumulator) {
      var isArr = isArray(object),
          isArrLike = isArr || isBuffer(object) || isTypedArray(object);

      iteratee = getIteratee(iteratee, 4);
      if (accumulator == null) {
        var Ctor = object && object.constructor;
        if (isArrLike) {
          accumulator = isArr ? new Ctor : [];
        }
        else if (isObject(object)) {
          accumulator = isFunction(Ctor) ? baseCreate(getPrototype(object)) : {};
        }
        else {
          accumulator = {};
        }
      }
      (isArrLike ? arrayEach : baseForOwn)(object, function(value, index, object) {
        return iteratee(accumulator, value, index, object);
      });
      return accumulator;
    }

    /**
     * Removes the property at `path` of `object`.
     *
     * **Note:** This method mutates `object`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Object
     * @param {Object} object The object to modify.
     * @param {Array|string} path The path of the property to unset.
     * @returns {boolean} Returns `true` if the property is deleted, else `false`.
     * @example
     *
     * var object = { 'a': [{ 'b': { 'c': 7 } }] };
     * _.unset(object, 'a[0].b.c');
     * // => true
     *
     * console.log(object);
     * // => { 'a': [{ 'b': {} }] };
     *
     * _.unset(object, ['a', '0', 'b', 'c']);
     * // => true
     *
     * console.log(object);
     * // => { 'a': [{ 'b': {} }] };
     */
    function unset(object, path) {
      return object == null ? true : baseUnset(object, path);
    }

    /**
     * This method is like `_.set` except that accepts `updater` to produce the
     * value to set. Use `_.updateWith` to customize `path` creation. The `updater`
     * is invoked with one argument: (value).
     *
     * **Note:** This method mutates `object`.
     *
     * @static
     * @memberOf _
     * @since 4.6.0
     * @category Object
     * @param {Object} object The object to modify.
     * @param {Array|string} path The path of the property to set.
     * @param {Function} updater The function to produce the updated value.
     * @returns {Object} Returns `object`.
     * @example
     *
     * var object = { 'a': [{ 'b': { 'c': 3 } }] };
     *
     * _.update(object, 'a[0].b.c', function(n) { return n * n; });
     * console.log(object.a[0].b.c);
     * // => 9
     *
     * _.update(object, 'x[0].y.z', function(n) { return n ? n + 1 : 0; });
     * console.log(object.x[0].y.z);
     * // => 0
     */
    function update(object, path, updater) {
      return object == null ? object : baseUpdate(object, path, castFunction(updater));
    }

    /**
     * This method is like `_.update` except that it accepts `customizer` which is
     * invoked to produce the objects of `path`.  If `customizer` returns `undefined`
     * path creation is handled by the method instead. The `customizer` is invoked
     * with three arguments: (nsValue, key, nsObject).
     *
     * **Note:** This method mutates `object`.
     *
     * @static
     * @memberOf _
     * @since 4.6.0
     * @category Object
     * @param {Object} object The object to modify.
     * @param {Array|string} path The path of the property to set.
     * @param {Function} updater The function to produce the updated value.
     * @param {Function} [customizer] The function to customize assigned values.
     * @returns {Object} Returns `object`.
     * @example
     *
     * var object = {};
     *
     * _.updateWith(object, '[0][1]', _.constant('a'), Object);
     * // => { '0': { '1': 'a' } }
     */
    function updateWith(object, path, updater, customizer) {
      customizer = typeof customizer == 'function' ? customizer : undefined;
      return object == null ? object : baseUpdate(object, path, castFunction(updater), customizer);
    }

    /**
     * Creates an array of the own enumerable string keyed property values of `object`.
     *
     * **Note:** Non-object values are coerced to objects.
     *
     * @static
     * @since 0.1.0
     * @memberOf _
     * @category Object
     * @param {Object} object The object to query.
     * @returns {Array} Returns the array of property values.
     * @example
     *
     * function Foo() {
     *   this.a = 1;
     *   this.b = 2;
     * }
     *
     * Foo.prototype.c = 3;
     *
     * _.values(new Foo);
     * // => [1, 2] (iteration order is not guaranteed)
     *
     * _.values('hi');
     * // => ['h', 'i']
     */
    function values(object) {
      return object == null ? [] : baseValues(object, keys(object));
    }

    /**
     * Creates an array of the own and inherited enumerable string keyed property
     * values of `object`.
     *
     * **Note:** Non-object values are coerced to objects.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Object
     * @param {Object} object The object to query.
     * @returns {Array} Returns the array of property values.
     * @example
     *
     * function Foo() {
     *   this.a = 1;
     *   this.b = 2;
     * }
     *
     * Foo.prototype.c = 3;
     *
     * _.valuesIn(new Foo);
     * // => [1, 2, 3] (iteration order is not guaranteed)
     */
    function valuesIn(object) {
      return object == null ? [] : baseValues(object, keysIn(object));
    }

    /*------------------------------------------------------------------------*/

    /**
     * Clamps `number` within the inclusive `lower` and `upper` bounds.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Number
     * @param {number} number The number to clamp.
     * @param {number} [lower] The lower bound.
     * @param {number} upper The upper bound.
     * @returns {number} Returns the clamped number.
     * @example
     *
     * _.clamp(-10, -5, 5);
     * // => -5
     *
     * _.clamp(10, -5, 5);
     * // => 5
     */
    function clamp(number, lower, upper) {
      if (upper === undefined) {
        upper = lower;
        lower = undefined;
      }
      if (upper !== undefined) {
        upper = toNumber(upper);
        upper = upper === upper ? upper : 0;
      }
      if (lower !== undefined) {
        lower = toNumber(lower);
        lower = lower === lower ? lower : 0;
      }
      return baseClamp(toNumber(number), lower, upper);
    }

    /**
     * Checks if `n` is between `start` and up to, but not including, `end`. If
     * `end` is not specified, it's set to `start` with `start` then set to `0`.
     * If `start` is greater than `end` the params are swapped to support
     * negative ranges.
     *
     * @static
     * @memberOf _
     * @since 3.3.0
     * @category Number
     * @param {number} number The number to check.
     * @param {number} [start=0] The start of the range.
     * @param {number} end The end of the range.
     * @returns {boolean} Returns `true` if `number` is in the range, else `false`.
     * @see _.range, _.rangeRight
     * @example
     *
     * _.inRange(3, 2, 4);
     * // => true
     *
     * _.inRange(4, 8);
     * // => true
     *
     * _.inRange(4, 2);
     * // => false
     *
     * _.inRange(2, 2);
     * // => false
     *
     * _.inRange(1.2, 2);
     * // => true
     *
     * _.inRange(5.2, 4);
     * // => false
     *
     * _.inRange(-3, -2, -6);
     * // => true
     */
    function inRange(number, start, end) {
      start = toFinite(start);
      if (end === undefined) {
        end = start;
        start = 0;
      } else {
        end = toFinite(end);
      }
      number = toNumber(number);
      return baseInRange(number, start, end);
    }

    /**
     * Produces a random number between the inclusive `lower` and `upper` bounds.
     * If only one argument is provided a number between `0` and the given number
     * is returned. If `floating` is `true`, or either `lower` or `upper` are
     * floats, a floating-point number is returned instead of an integer.
     *
     * **Note:** JavaScript follows the IEEE-754 standard for resolving
     * floating-point values which can produce unexpected results.
     *
     * @static
     * @memberOf _
     * @since 0.7.0
     * @category Number
     * @param {number} [lower=0] The lower bound.
     * @param {number} [upper=1] The upper bound.
     * @param {boolean} [floating] Specify returning a floating-point number.
     * @returns {number} Returns the random number.
     * @example
     *
     * _.random(0, 5);
     * // => an integer between 0 and 5
     *
     * _.random(5);
     * // => also an integer between 0 and 5
     *
     * _.random(5, true);
     * // => a floating-point number between 0 and 5
     *
     * _.random(1.2, 5.2);
     * // => a floating-point number between 1.2 and 5.2
     */
    function random(lower, upper, floating) {
      if (floating && typeof floating != 'boolean' && isIterateeCall(lower, upper, floating)) {
        upper = floating = undefined;
      }
      if (floating === undefined) {
        if (typeof upper == 'boolean') {
          floating = upper;
          upper = undefined;
        }
        else if (typeof lower == 'boolean') {
          floating = lower;
          lower = undefined;
        }
      }
      if (lower === undefined && upper === undefined) {
        lower = 0;
        upper = 1;
      }
      else {
        lower = toFinite(lower);
        if (upper === undefined) {
          upper = lower;
          lower = 0;
        } else {
          upper = toFinite(upper);
        }
      }
      if (lower > upper) {
        var temp = lower;
        lower = upper;
        upper = temp;
      }
      if (floating || lower % 1 || upper % 1) {
        var rand = nativeRandom();
        return nativeMin(lower + (rand * (upper - lower + freeParseFloat('1e-' + ((rand + '').length - 1)))), upper);
      }
      return baseRandom(lower, upper);
    }

    /*------------------------------------------------------------------------*/

    /**
     * Converts `string` to [camel case](https://en.wikipedia.org/wiki/CamelCase).
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category String
     * @param {string} [string=''] The string to convert.
     * @returns {string} Returns the camel cased string.
     * @example
     *
     * _.camelCase('Foo Bar');
     * // => 'fooBar'
     *
     * _.camelCase('--foo-bar--');
     * // => 'fooBar'
     *
     * _.camelCase('__FOO_BAR__');
     * // => 'fooBar'
     */
    var camelCase = createCompounder(function(result, word, index) {
      word = word.toLowerCase();
      return result + (index ? capitalize(word) : word);
    });

    /**
     * Converts the first character of `string` to upper case and the remaining
     * to lower case.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category String
     * @param {string} [string=''] The string to capitalize.
     * @returns {string} Returns the capitalized string.
     * @example
     *
     * _.capitalize('FRED');
     * // => 'Fred'
     */
    function capitalize(string) {
      return upperFirst(toString(string).toLowerCase());
    }

    /**
     * Deburrs `string` by converting
     * [Latin-1 Supplement](https://en.wikipedia.org/wiki/Latin-1_Supplement_(Unicode_block)#Character_table)
     * and [Latin Extended-A](https://en.wikipedia.org/wiki/Latin_Extended-A)
     * letters to basic Latin letters and removing
     * [combining diacritical marks](https://en.wikipedia.org/wiki/Combining_Diacritical_Marks).
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category String
     * @param {string} [string=''] The string to deburr.
     * @returns {string} Returns the deburred string.
     * @example
     *
     * _.deburr('déjà vu');
     * // => 'deja vu'
     */
    function deburr(string) {
      string = toString(string);
      return string && string.replace(reLatin, deburrLetter).replace(reComboMark, '');
    }

    /**
     * Checks if `string` ends with the given target string.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category String
     * @param {string} [string=''] The string to inspect.
     * @param {string} [target] The string to search for.
     * @param {number} [position=string.length] The position to search up to.
     * @returns {boolean} Returns `true` if `string` ends with `target`,
     *  else `false`.
     * @example
     *
     * _.endsWith('abc', 'c');
     * // => true
     *
     * _.endsWith('abc', 'b');
     * // => false
     *
     * _.endsWith('abc', 'b', 2);
     * // => true
     */
    function endsWith(string, target, position) {
      string = toString(string);
      target = baseToString(target);

      var length = string.length;
      position = position === undefined
        ? length
        : baseClamp(toInteger(position), 0, length);

      var end = position;
      position -= target.length;
      return position >= 0 && string.slice(position, end) == target;
    }

    /**
     * Converts the characters "&", "<", ">", '"', and "'" in `string` to their
     * corresponding HTML entities.
     *
     * **Note:** No other characters are escaped. To escape additional
     * characters use a third-party library like [_he_](https://mths.be/he).
     *
     * Though the ">" character is escaped for symmetry, characters like
     * ">" and "/" don't need escaping in HTML and have no special meaning
     * unless they're part of a tag or unquoted attribute value. See
     * [Mathias Bynens's article](https://mathiasbynens.be/notes/ambiguous-ampersands)
     * (under "semi-related fun fact") for more details.
     *
     * When working with HTML you should always
     * [quote attribute values](http://wonko.com/post/html-escaping) to reduce
     * XSS vectors.
     *
     * @static
     * @since 0.1.0
     * @memberOf _
     * @category String
     * @param {string} [string=''] The string to escape.
     * @returns {string} Returns the escaped string.
     * @example
     *
     * _.escape('fred, barney, & pebbles');
     * // => 'fred, barney, &amp; pebbles'
     */
    function escape(string) {
      string = toString(string);
      return (string && reHasUnescapedHtml.test(string))
        ? string.replace(reUnescapedHtml, escapeHtmlChar)
        : string;
    }

    /**
     * Escapes the `RegExp` special characters "^", "$", "\", ".", "*", "+",
     * "?", "(", ")", "[", "]", "{", "}", and "|" in `string`.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category String
     * @param {string} [string=''] The string to escape.
     * @returns {string} Returns the escaped string.
     * @example
     *
     * _.escapeRegExp('[lodash](https://lodash.com/)');
     * // => '\[lodash\]\(https://lodash\.com/\)'
     */
    function escapeRegExp(string) {
      string = toString(string);
      return (string && reHasRegExpChar.test(string))
        ? string.replace(reRegExpChar, '\\$&')
        : string;
    }

    /**
     * Converts `string` to
     * [kebab case](https://en.wikipedia.org/wiki/Letter_case#Special_case_styles).
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category String
     * @param {string} [string=''] The string to convert.
     * @returns {string} Returns the kebab cased string.
     * @example
     *
     * _.kebabCase('Foo Bar');
     * // => 'foo-bar'
     *
     * _.kebabCase('fooBar');
     * // => 'foo-bar'
     *
     * _.kebabCase('__FOO_BAR__');
     * // => 'foo-bar'
     */
    var kebabCase = createCompounder(function(result, word, index) {
      return result + (index ? '-' : '') + word.toLowerCase();
    });

    /**
     * Converts `string`, as space separated words, to lower case.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category String
     * @param {string} [string=''] The string to convert.
     * @returns {string} Returns the lower cased string.
     * @example
     *
     * _.lowerCase('--Foo-Bar--');
     * // => 'foo bar'
     *
     * _.lowerCase('fooBar');
     * // => 'foo bar'
     *
     * _.lowerCase('__FOO_BAR__');
     * // => 'foo bar'
     */
    var lowerCase = createCompounder(function(result, word, index) {
      return result + (index ? ' ' : '') + word.toLowerCase();
    });

    /**
     * Converts the first character of `string` to lower case.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category String
     * @param {string} [string=''] The string to convert.
     * @returns {string} Returns the converted string.
     * @example
     *
     * _.lowerFirst('Fred');
     * // => 'fred'
     *
     * _.lowerFirst('FRED');
     * // => 'fRED'
     */
    var lowerFirst = createCaseFirst('toLowerCase');

    /**
     * Pads `string` on the left and right sides if it's shorter than `length`.
     * Padding characters are truncated if they can't be evenly divided by `length`.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category String
     * @param {string} [string=''] The string to pad.
     * @param {number} [length=0] The padding length.
     * @param {string} [chars=' '] The string used as padding.
     * @returns {string} Returns the padded string.
     * @example
     *
     * _.pad('abc', 8);
     * // => '  abc   '
     *
     * _.pad('abc', 8, '_-');
     * // => '_-abc_-_'
     *
     * _.pad('abc', 3);
     * // => 'abc'
     */
    function pad(string, length, chars) {
      string = toString(string);
      length = toInteger(length);

      var strLength = length ? stringSize(string) : 0;
      if (!length || strLength >= length) {
        return string;
      }
      var mid = (length - strLength) / 2;
      return (
        createPadding(nativeFloor(mid), chars) +
        string +
        createPadding(nativeCeil(mid), chars)
      );
    }

    /**
     * Pads `string` on the right side if it's shorter than `length`. Padding
     * characters are truncated if they exceed `length`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category String
     * @param {string} [string=''] The string to pad.
     * @param {number} [length=0] The padding length.
     * @param {string} [chars=' '] The string used as padding.
     * @returns {string} Returns the padded string.
     * @example
     *
     * _.padEnd('abc', 6);
     * // => 'abc   '
     *
     * _.padEnd('abc', 6, '_-');
     * // => 'abc_-_'
     *
     * _.padEnd('abc', 3);
     * // => 'abc'
     */
    function padEnd(string, length, chars) {
      string = toString(string);
      length = toInteger(length);

      var strLength = length ? stringSize(string) : 0;
      return (length && strLength < length)
        ? (string + createPadding(length - strLength, chars))
        : string;
    }

    /**
     * Pads `string` on the left side if it's shorter than `length`. Padding
     * characters are truncated if they exceed `length`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category String
     * @param {string} [string=''] The string to pad.
     * @param {number} [length=0] The padding length.
     * @param {string} [chars=' '] The string used as padding.
     * @returns {string} Returns the padded string.
     * @example
     *
     * _.padStart('abc', 6);
     * // => '   abc'
     *
     * _.padStart('abc', 6, '_-');
     * // => '_-_abc'
     *
     * _.padStart('abc', 3);
     * // => 'abc'
     */
    function padStart(string, length, chars) {
      string = toString(string);
      length = toInteger(length);

      var strLength = length ? stringSize(string) : 0;
      return (length && strLength < length)
        ? (createPadding(length - strLength, chars) + string)
        : string;
    }

    /**
     * Converts `string` to an integer of the specified radix. If `radix` is
     * `undefined` or `0`, a `radix` of `10` is used unless `value` is a
     * hexadecimal, in which case a `radix` of `16` is used.
     *
     * **Note:** This method aligns with the
     * [ES5 implementation](https://es5.github.io/#x15.1.2.2) of `parseInt`.
     *
     * @static
     * @memberOf _
     * @since 1.1.0
     * @category String
     * @param {string} string The string to convert.
     * @param {number} [radix=10] The radix to interpret `value` by.
     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
     * @returns {number} Returns the converted integer.
     * @example
     *
     * _.parseInt('08');
     * // => 8
     *
     * _.map(['6', '08', '10'], _.parseInt);
     * // => [6, 8, 10]
     */
    function parseInt(string, radix, guard) {
      if (guard || radix == null) {
        radix = 0;
      } else if (radix) {
        radix = +radix;
      }
      return nativeParseInt(toString(string).replace(reTrimStart, ''), radix || 0);
    }

    /**
     * Repeats the given string `n` times.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category String
     * @param {string} [string=''] The string to repeat.
     * @param {number} [n=1] The number of times to repeat the string.
     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
     * @returns {string} Returns the repeated string.
     * @example
     *
     * _.repeat('*', 3);
     * // => '***'
     *
     * _.repeat('abc', 2);
     * // => 'abcabc'
     *
     * _.repeat('abc', 0);
     * // => ''
     */
    function repeat(string, n, guard) {
      if ((guard ? isIterateeCall(string, n, guard) : n === undefined)) {
        n = 1;
      } else {
        n = toInteger(n);
      }
      return baseRepeat(toString(string), n);
    }

    /**
     * Replaces matches for `pattern` in `string` with `replacement`.
     *
     * **Note:** This method is based on
     * [`String#replace`](https://mdn.io/String/replace).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category String
     * @param {string} [string=''] The string to modify.
     * @param {RegExp|string} pattern The pattern to replace.
     * @param {Function|string} replacement The match replacement.
     * @returns {string} Returns the modified string.
     * @example
     *
     * _.replace('Hi Fred', 'Fred', 'Barney');
     * // => 'Hi Barney'
     */
    function replace() {
      var args = arguments,
          string = toString(args[0]);

      return args.length < 3 ? string : string.replace(args[1], args[2]);
    }

    /**
     * Converts `string` to
     * [snake case](https://en.wikipedia.org/wiki/Snake_case).
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category String
     * @param {string} [string=''] The string to convert.
     * @returns {string} Returns the snake cased string.
     * @example
     *
     * _.snakeCase('Foo Bar');
     * // => 'foo_bar'
     *
     * _.snakeCase('fooBar');
     * // => 'foo_bar'
     *
     * _.snakeCase('--FOO-BAR--');
     * // => 'foo_bar'
     */
    var snakeCase = createCompounder(function(result, word, index) {
      return result + (index ? '_' : '') + word.toLowerCase();
    });

    /**
     * Splits `string` by `separator`.
     *
     * **Note:** This method is based on
     * [`String#split`](https://mdn.io/String/split).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category String
     * @param {string} [string=''] The string to split.
     * @param {RegExp|string} separator The separator pattern to split by.
     * @param {number} [limit] The length to truncate results to.
     * @returns {Array} Returns the string segments.
     * @example
     *
     * _.split('a-b-c', '-', 2);
     * // => ['a', 'b']
     */
    function split(string, separator, limit) {
      if (limit && typeof limit != 'number' && isIterateeCall(string, separator, limit)) {
        separator = limit = undefined;
      }
      limit = limit === undefined ? MAX_ARRAY_LENGTH : limit >>> 0;
      if (!limit) {
        return [];
      }
      string = toString(string);
      if (string && (
            typeof separator == 'string' ||
            (separator != null && !isRegExp(separator))
          )) {
        separator = baseToString(separator);
        if (!separator && hasUnicode(string)) {
          return castSlice(stringToArray(string), 0, limit);
        }
      }
      return string.split(separator, limit);
    }

    /**
     * Converts `string` to
     * [start case](https://en.wikipedia.org/wiki/Letter_case#Stylistic_or_specialised_usage).
     *
     * @static
     * @memberOf _
     * @since 3.1.0
     * @category String
     * @param {string} [string=''] The string to convert.
     * @returns {string} Returns the start cased string.
     * @example
     *
     * _.startCase('--foo-bar--');
     * // => 'Foo Bar'
     *
     * _.startCase('fooBar');
     * // => 'Foo Bar'
     *
     * _.startCase('__FOO_BAR__');
     * // => 'FOO BAR'
     */
    var startCase = createCompounder(function(result, word, index) {
      return result + (index ? ' ' : '') + upperFirst(word);
    });

    /**
     * Checks if `string` starts with the given target string.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category String
     * @param {string} [string=''] The string to inspect.
     * @param {string} [target] The string to search for.
     * @param {number} [position=0] The position to search from.
     * @returns {boolean} Returns `true` if `string` starts with `target`,
     *  else `false`.
     * @example
     *
     * _.startsWith('abc', 'a');
     * // => true
     *
     * _.startsWith('abc', 'b');
     * // => false
     *
     * _.startsWith('abc', 'b', 1);
     * // => true
     */
    function startsWith(string, target, position) {
      string = toString(string);
      position = position == null
        ? 0
        : baseClamp(toInteger(position), 0, string.length);

      target = baseToString(target);
      return string.slice(position, position + target.length) == target;
    }

    /**
     * Creates a compiled template function that can interpolate data properties
     * in "interpolate" delimiters, HTML-escape interpolated data properties in
     * "escape" delimiters, and execute JavaScript in "evaluate" delimiters. Data
     * properties may be accessed as free variables in the template. If a setting
     * object is given, it takes precedence over `_.templateSettings` values.
     *
     * **Note:** In the development build `_.template` utilizes
     * [sourceURLs](http://www.html5rocks.com/en/tutorials/developertools/sourcemaps/#toc-sourceurl)
     * for easier debugging.
     *
     * For more information on precompiling templates see
     * [lodash's custom builds documentation](https://lodash.com/custom-builds).
     *
     * For more information on Chrome extension sandboxes see
     * [Chrome's extensions documentation](https://developer.chrome.com/extensions/sandboxingEval).
     *
     * @static
     * @since 0.1.0
     * @memberOf _
     * @category String
     * @param {string} [string=''] The template string.
     * @param {Object} [options={}] The options object.
     * @param {RegExp} [options.escape=_.templateSettings.escape]
     *  The HTML "escape" delimiter.
     * @param {RegExp} [options.evaluate=_.templateSettings.evaluate]
     *  The "evaluate" delimiter.
     * @param {Object} [options.imports=_.templateSettings.imports]
     *  An object to import into the template as free variables.
     * @param {RegExp} [options.interpolate=_.templateSettings.interpolate]
     *  The "interpolate" delimiter.
     * @param {string} [options.sourceURL='lodash.templateSources[n]']
     *  The sourceURL of the compiled template.
     * @param {string} [options.variable='obj']
     *  The data object variable name.
     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
     * @returns {Function} Returns the compiled template function.
     * @example
     *
     * // Use the "interpolate" delimiter to create a compiled template.
     * var compiled = _.template('hello <%= user %>!');
     * compiled({ 'user': 'fred' });
     * // => 'hello fred!'
     *
     * // Use the HTML "escape" delimiter to escape data property values.
     * var compiled = _.template('<b><%- value %></b>');
     * compiled({ 'value': '<script>' });
     * // => '<b>&lt;script&gt;</b>'
     *
     * // Use the "evaluate" delimiter to execute JavaScript and generate HTML.
     * var compiled = _.template('<% _.forEach(users, function(user) { %><li><%- user %></li><% }); %>');
     * compiled({ 'users': ['fred', 'barney'] });
     * // => '<li>fred</li><li>barney</li>'
     *
     * // Use the internal `print` function in "evaluate" delimiters.
     * var compiled = _.template('<% print("hello " + user); %>!');
     * compiled({ 'user': 'barney' });
     * // => 'hello barney!'
     *
     * // Use the ES template literal delimiter as an "interpolate" delimiter.
     * // Disable support by replacing the "interpolate" delimiter.
     * var compiled = _.template('hello ${ user }!');
     * compiled({ 'user': 'pebbles' });
     * // => 'hello pebbles!'
     *
     * // Use backslashes to treat delimiters as plain text.
     * var compiled = _.template('<%= "\\<%- value %\\>" %>');
     * compiled({ 'value': 'ignored' });
     * // => '<%- value %>'
     *
     * // Use the `imports` option to import `jQuery` as `jq`.
     * var text = '<% jq.each(users, function(user) { %><li><%- user %></li><% }); %>';
     * var compiled = _.template(text, { 'imports': { 'jq': jQuery } });
     * compiled({ 'users': ['fred', 'barney'] });
     * // => '<li>fred</li><li>barney</li>'
     *
     * // Use the `sourceURL` option to specify a custom sourceURL for the template.
     * var compiled = _.template('hello <%= user %>!', { 'sourceURL': '/basic/greeting.jst' });
     * compiled(data);
     * // => Find the source of "greeting.jst" under the Sources tab or Resources panel of the web inspector.
     *
     * // Use the `variable` option to ensure a with-statement isn't used in the compiled template.
     * var compiled = _.template('hi <%= data.user %>!', { 'variable': 'data' });
     * compiled.source;
     * // => function(data) {
     * //   var __t, __p = '';
     * //   __p += 'hi ' + ((__t = ( data.user )) == null ? '' : __t) + '!';
     * //   return __p;
     * // }
     *
     * // Use custom template delimiters.
     * _.templateSettings.interpolate = /{{([\s\S]+?)}}/g;
     * var compiled = _.template('hello {{ user }}!');
     * compiled({ 'user': 'mustache' });
     * // => 'hello mustache!'
     *
     * // Use the `source` property to inline compiled templates for meaningful
     * // line numbers in error messages and stack traces.
     * fs.writeFileSync(path.join(process.cwd(), 'jst.js'), '\
     *   var JST = {\
     *     "main": ' + _.template(mainText).source + '\
     *   };\
     * ');
     */
    function template(string, options, guard) {
      // Based on John Resig's `tmpl` implementation
      // (http://ejohn.org/blog/javascript-micro-templating/)
      // and Laura Doktorova's doT.js (https://github.com/olado/doT).
      var settings = lodash.templateSettings;

      if (guard && isIterateeCall(string, options, guard)) {
        options = undefined;
      }
      string = toString(string);
      options = assignInWith({}, options, settings, customDefaultsAssignIn);

      var imports = assignInWith({}, options.imports, settings.imports, customDefaultsAssignIn),
          importsKeys = keys(imports),
          importsValues = baseValues(imports, importsKeys);

      var isEscaping,
          isEvaluating,
          index = 0,
          interpolate = options.interpolate || reNoMatch,
          source = "__p += '";

      // Compile the regexp to match each delimiter.
      var reDelimiters = RegExp(
        (options.escape || reNoMatch).source + '|' +
        interpolate.source + '|' +
        (interpolate === reInterpolate ? reEsTemplate : reNoMatch).source + '|' +
        (options.evaluate || reNoMatch).source + '|$'
      , 'g');

      // Use a sourceURL for easier debugging.
      // The sourceURL gets injected into the source that's eval-ed, so be careful
      // to normalize all kinds of whitespace, so e.g. newlines (and unicode versions of it) can't sneak in
      // and escape the comment, thus injecting code that gets evaled.
      var sourceURL = '//# sourceURL=' +
        (hasOwnProperty.call(options, 'sourceURL')
          ? (options.sourceURL + '').replace(/\s/g, ' ')
          : ('lodash.templateSources[' + (++templateCounter) + ']')
        ) + '\n';

      string.replace(reDelimiters, function(match, escapeValue, interpolateValue, esTemplateValue, evaluateValue, offset) {
        interpolateValue || (interpolateValue = esTemplateValue);

        // Escape characters that can't be included in string literals.
        source += string.slice(index, offset).replace(reUnescapedString, escapeStringChar);

        // Replace delimiters with snippets.
        if (escapeValue) {
          isEscaping = true;
          source += "' +\n__e(" + escapeValue + ") +\n'";
        }
        if (evaluateValue) {
          isEvaluating = true;
          source += "';\n" + evaluateValue + ";\n__p += '";
        }
        if (interpolateValue) {
          source += "' +\n((__t = (" + interpolateValue + ")) == null ? '' : __t) +\n'";
        }
        index = offset + match.length;

        // The JS engine embedded in Adobe products needs `match` returned in
        // order to produce the correct `offset` value.
        return match;
      });

      source += "';\n";

      // If `variable` is not specified wrap a with-statement around the generated
      // code to add the data object to the top of the scope chain.
      var variable = hasOwnProperty.call(options, 'variable') && options.variable;
      if (!variable) {
        source = 'with (obj) {\n' + source + '\n}\n';
      }
      // Throw an error if a forbidden character was found in `variable`, to prevent
      // potential command injection attacks.
      else if (reForbiddenIdentifierChars.test(variable)) {
        throw new Error(INVALID_TEMPL_VAR_ERROR_TEXT);
      }

      // Cleanup code by stripping empty strings.
      source = (isEvaluating ? source.replace(reEmptyStringLeading, '') : source)
        .replace(reEmptyStringMiddle, '$1')
        .replace(reEmptyStringTrailing, '$1;');

      // Frame code as the function body.
      source = 'function(' + (variable || 'obj') + ') {\n' +
        (variable
          ? ''
          : 'obj || (obj = {});\n'
        ) +
        "var __t, __p = ''" +
        (isEscaping
           ? ', __e = _.escape'
           : ''
        ) +
        (isEvaluating
          ? ', __j = Array.prototype.join;\n' +
            "function print() { __p += __j.call(arguments, '') }\n"
          : ';\n'
        ) +
        source +
        'return __p\n}';

      var result = attempt(function() {
        return Function(importsKeys, sourceURL + 'return ' + source)
          .apply(undefined, importsValues);
      });

      // Provide the compiled function's source by its `toString` method or
      // the `source` property as a convenience for inlining compiled templates.
      result.source = source;
      if (isError(result)) {
        throw result;
      }
      return result;
    }

    /**
     * Converts `string`, as a whole, to lower case just like
     * [String#toLowerCase](https://mdn.io/toLowerCase).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category String
     * @param {string} [string=''] The string to convert.
     * @returns {string} Returns the lower cased string.
     * @example
     *
     * _.toLower('--Foo-Bar--');
     * // => '--foo-bar--'
     *
     * _.toLower('fooBar');
     * // => 'foobar'
     *
     * _.toLower('__FOO_BAR__');
     * // => '__foo_bar__'
     */
    function toLower(value) {
      return toString(value).toLowerCase();
    }

    /**
     * Converts `string`, as a whole, to upper case just like
     * [String#toUpperCase](https://mdn.io/toUpperCase).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category String
     * @param {string} [string=''] The string to convert.
     * @returns {string} Returns the upper cased string.
     * @example
     *
     * _.toUpper('--foo-bar--');
     * // => '--FOO-BAR--'
     *
     * _.toUpper('fooBar');
     * // => 'FOOBAR'
     *
     * _.toUpper('__foo_bar__');
     * // => '__FOO_BAR__'
     */
    function toUpper(value) {
      return toString(value).toUpperCase();
    }

    /**
     * Removes leading and trailing whitespace or specified characters from `string`.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category String
     * @param {string} [string=''] The string to trim.
     * @param {string} [chars=whitespace] The characters to trim.
     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
     * @returns {string} Returns the trimmed string.
     * @example
     *
     * _.trim('  abc  ');
     * // => 'abc'
     *
     * _.trim('-_-abc-_-', '_-');
     * // => 'abc'
     *
     * _.map(['  foo  ', '  bar  '], _.trim);
     * // => ['foo', 'bar']
     */
    function trim(string, chars, guard) {
      string = toString(string);
      if (string && (guard || chars === undefined)) {
        return baseTrim(string);
      }
      if (!string || !(chars = baseToString(chars))) {
        return string;
      }
      var strSymbols = stringToArray(string),
          chrSymbols = stringToArray(chars),
          start = charsStartIndex(strSymbols, chrSymbols),
          end = charsEndIndex(strSymbols, chrSymbols) + 1;

      return castSlice(strSymbols, start, end).join('');
    }

    /**
     * Removes trailing whitespace or specified characters from `string`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category String
     * @param {string} [string=''] The string to trim.
     * @param {string} [chars=whitespace] The characters to trim.
     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
     * @returns {string} Returns the trimmed string.
     * @example
     *
     * _.trimEnd('  abc  ');
     * // => '  abc'
     *
     * _.trimEnd('-_-abc-_-', '_-');
     * // => '-_-abc'
     */
    function trimEnd(string, chars, guard) {
      string = toString(string);
      if (string && (guard || chars === undefined)) {
        return string.slice(0, trimmedEndIndex(string) + 1);
      }
      if (!string || !(chars = baseToString(chars))) {
        return string;
      }
      var strSymbols = stringToArray(string),
          end = charsEndIndex(strSymbols, stringToArray(chars)) + 1;

      return castSlice(strSymbols, 0, end).join('');
    }

    /**
     * Removes leading whitespace or specified characters from `string`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category String
     * @param {string} [string=''] The string to trim.
     * @param {string} [chars=whitespace] The characters to trim.
     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
     * @returns {string} Returns the trimmed string.
     * @example
     *
     * _.trimStart('  abc  ');
     * // => 'abc  '
     *
     * _.trimStart('-_-abc-_-', '_-');
     * // => 'abc-_-'
     */
    function trimStart(string, chars, guard) {
      string = toString(string);
      if (string && (guard || chars === undefined)) {
        return string.replace(reTrimStart, '');
      }
      if (!string || !(chars = baseToString(chars))) {
        return string;
      }
      var strSymbols = stringToArray(string),
          start = charsStartIndex(strSymbols, stringToArray(chars));

      return castSlice(strSymbols, start).join('');
    }

    /**
     * Truncates `string` if it's longer than the given maximum string length.
     * The last characters of the truncated string are replaced with the omission
     * string which defaults to "...".
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category String
     * @param {string} [string=''] The string to truncate.
     * @param {Object} [options={}] The options object.
     * @param {number} [options.length=30] The maximum string length.
     * @param {string} [options.omission='...'] The string to indicate text is omitted.
     * @param {RegExp|string} [options.separator] The separator pattern to truncate to.
     * @returns {string} Returns the truncated string.
     * @example
     *
     * _.truncate('hi-diddly-ho there, neighborino');
     * // => 'hi-diddly-ho there, neighbo...'
     *
     * _.truncate('hi-diddly-ho there, neighborino', {
     *   'length': 24,
     *   'separator': ' '
     * });
     * // => 'hi-diddly-ho there,...'
     *
     * _.truncate('hi-diddly-ho there, neighborino', {
     *   'length': 24,
     *   'separator': /,? +/
     * });
     * // => 'hi-diddly-ho there...'
     *
     * _.truncate('hi-diddly-ho there, neighborino', {
     *   'omission': ' [...]'
     * });
     * // => 'hi-diddly-ho there, neig [...]'
     */
    function truncate(string, options) {
      var length = DEFAULT_TRUNC_LENGTH,
          omission = DEFAULT_TRUNC_OMISSION;

      if (isObject(options)) {
        var separator = 'separator' in options ? options.separator : separator;
        length = 'length' in options ? toInteger(options.length) : length;
        omission = 'omission' in options ? baseToString(options.omission) : omission;
      }
      string = toString(string);

      var strLength = string.length;
      if (hasUnicode(string)) {
        var strSymbols = stringToArray(string);
        strLength = strSymbols.length;
      }
      if (length >= strLength) {
        return string;
      }
      var end = length - stringSize(omission);
      if (end < 1) {
        return omission;
      }
      var result = strSymbols
        ? castSlice(strSymbols, 0, end).join('')
        : string.slice(0, end);

      if (separator === undefined) {
        return result + omission;
      }
      if (strSymbols) {
        end += (result.length - end);
      }
      if (isRegExp(separator)) {
        if (string.slice(end).search(separator)) {
          var match,
              substring = result;

          if (!separator.global) {
            separator = RegExp(separator.source, toString(reFlags.exec(separator)) + 'g');
          }
          separator.lastIndex = 0;
          while ((match = separator.exec(substring))) {
            var newEnd = match.index;
          }
          result = result.slice(0, newEnd === undefined ? end : newEnd);
        }
      } else if (string.indexOf(baseToString(separator), end) != end) {
        var index = result.lastIndexOf(separator);
        if (index > -1) {
          result = result.slice(0, index);
        }
      }
      return result + omission;
    }

    /**
     * The inverse of `_.escape`; this method converts the HTML entities
     * `&amp;`, `&lt;`, `&gt;`, `&quot;`, and `&#39;` in `string` to
     * their corresponding characters.
     *
     * **Note:** No other HTML entities are unescaped. To unescape additional
     * HTML entities use a third-party library like [_he_](https://mths.be/he).
     *
     * @static
     * @memberOf _
     * @since 0.6.0
     * @category String
     * @param {string} [string=''] The string to unescape.
     * @returns {string} Returns the unescaped string.
     * @example
     *
     * _.unescape('fred, barney, &amp; pebbles');
     * // => 'fred, barney, & pebbles'
     */
    function unescape(string) {
      string = toString(string);
      return (string && reHasEscapedHtml.test(string))
        ? string.replace(reEscapedHtml, unescapeHtmlChar)
        : string;
    }

    /**
     * Converts `string`, as space separated words, to upper case.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category String
     * @param {string} [string=''] The string to convert.
     * @returns {string} Returns the upper cased string.
     * @example
     *
     * _.upperCase('--foo-bar');
     * // => 'FOO BAR'
     *
     * _.upperCase('fooBar');
     * // => 'FOO BAR'
     *
     * _.upperCase('__foo_bar__');
     * // => 'FOO BAR'
     */
    var upperCase = createCompounder(function(result, word, index) {
      return result + (index ? ' ' : '') + word.toUpperCase();
    });

    /**
     * Converts the first character of `string` to upper case.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category String
     * @param {string} [string=''] The string to convert.
     * @returns {string} Returns the converted string.
     * @example
     *
     * _.upperFirst('fred');
     * // => 'Fred'
     *
     * _.upperFirst('FRED');
     * // => 'FRED'
     */
    var upperFirst = createCaseFirst('toUpperCase');

    /**
     * Splits `string` into an array of its words.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category String
     * @param {string} [string=''] The string to inspect.
     * @param {RegExp|string} [pattern] The pattern to match words.
     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
     * @returns {Array} Returns the words of `string`.
     * @example
     *
     * _.words('fred, barney, & pebbles');
     * // => ['fred', 'barney', 'pebbles']
     *
     * _.words('fred, barney, & pebbles', /[^, ]+/g);
     * // => ['fred', 'barney', '&', 'pebbles']
     */
    function words(string, pattern, guard) {
      string = toString(string);
      pattern = guard ? undefined : pattern;

      if (pattern === undefined) {
        return hasUnicodeWord(string) ? unicodeWords(string) : asciiWords(string);
      }
      return string.match(pattern) || [];
    }

    /*------------------------------------------------------------------------*/

    /**
     * Attempts to invoke `func`, returning either the result or the caught error
     * object. Any additional arguments are provided to `func` when it's invoked.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Util
     * @param {Function} func The function to attempt.
     * @param {...*} [args] The arguments to invoke `func` with.
     * @returns {*} Returns the `func` result or error object.
     * @example
     *
     * // Avoid throwing errors for invalid selectors.
     * var elements = _.attempt(function(selector) {
     *   return document.querySelectorAll(selector);
     * }, '>_>');
     *
     * if (_.isError(elements)) {
     *   elements = [];
     * }
     */
    var attempt = baseRest(function(func, args) {
      try {
        return apply(func, undefined, args);
      } catch (e) {
        return isError(e) ? e : new Error(e);
      }
    });

    /**
     * Binds methods of an object to the object itself, overwriting the existing
     * method.
     *
     * **Note:** This method doesn't set the "length" property of bound functions.
     *
     * @static
     * @since 0.1.0
     * @memberOf _
     * @category Util
     * @param {Object} object The object to bind and assign the bound methods to.
     * @param {...(string|string[])} methodNames The object method names to bind.
     * @returns {Object} Returns `object`.
     * @example
     *
     * var view = {
     *   'label': 'docs',
     *   'click': function() {
     *     console.log('clicked ' + this.label);
     *   }
     * };
     *
     * _.bindAll(view, ['click']);
     * jQuery(element).on('click', view.click);
     * // => Logs 'clicked docs' when clicked.
     */
    var bindAll = flatRest(function(object, methodNames) {
      arrayEach(methodNames, function(key) {
        key = toKey(key);
        baseAssignValue(object, key, bind(object[key], object));
      });
      return object;
    });

    /**
     * Creates a function that iterates over `pairs` and invokes the corresponding
     * function of the first predicate to return truthy. The predicate-function
     * pairs are invoked with the `this` binding and arguments of the created
     * function.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Util
     * @param {Array} pairs The predicate-function pairs.
     * @returns {Function} Returns the new composite function.
     * @example
     *
     * var func = _.cond([
     *   [_.matches({ 'a': 1 }),           _.constant('matches A')],
     *   [_.conforms({ 'b': _.isNumber }), _.constant('matches B')],
     *   [_.stubTrue,                      _.constant('no match')]
     * ]);
     *
     * func({ 'a': 1, 'b': 2 });
     * // => 'matches A'
     *
     * func({ 'a': 0, 'b': 1 });
     * // => 'matches B'
     *
     * func({ 'a': '1', 'b': '2' });
     * // => 'no match'
     */
    function cond(pairs) {
      var length = pairs == null ? 0 : pairs.length,
          toIteratee = getIteratee();

      pairs = !length ? [] : arrayMap(pairs, function(pair) {
        if (typeof pair[1] != 'function') {
          throw new TypeError(FUNC_ERROR_TEXT);
        }
        return [toIteratee(pair[0]), pair[1]];
      });

      return baseRest(function(args) {
        var index = -1;
        while (++index < length) {
          var pair = pairs[index];
          if (apply(pair[0], this, args)) {
            return apply(pair[1], this, args);
          }
        }
      });
    }

    /**
     * Creates a function that invokes the predicate properties of `source` with
     * the corresponding property values of a given object, returning `true` if
     * all predicates return truthy, else `false`.
     *
     * **Note:** The created function is equivalent to `_.conformsTo` with
     * `source` partially applied.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Util
     * @param {Object} source The object of property predicates to conform to.
     * @returns {Function} Returns the new spec function.
     * @example
     *
     * var objects = [
     *   { 'a': 2, 'b': 1 },
     *   { 'a': 1, 'b': 2 }
     * ];
     *
     * _.filter(objects, _.conforms({ 'b': function(n) { return n > 1; } }));
     * // => [{ 'a': 1, 'b': 2 }]
     */
    function conforms(source) {
      return baseConforms(baseClone(source, CLONE_DEEP_FLAG));
    }

    /**
     * Creates a function that returns `value`.
     *
     * @static
     * @memberOf _
     * @since 2.4.0
     * @category Util
     * @param {*} value The value to return from the new function.
     * @returns {Function} Returns the new constant function.
     * @example
     *
     * var objects = _.times(2, _.constant({ 'a': 1 }));
     *
     * console.log(objects);
     * // => [{ 'a': 1 }, { 'a': 1 }]
     *
     * console.log(objects[0] === objects[1]);
     * // => true
     */
    function constant(value) {
      return function() {
        return value;
      };
    }

    /**
     * Checks `value` to determine whether a default value should be returned in
     * its place. The `defaultValue` is returned if `value` is `NaN`, `null`,
     * or `undefined`.
     *
     * @static
     * @memberOf _
     * @since 4.14.0
     * @category Util
     * @param {*} value The value to check.
     * @param {*} defaultValue The default value.
     * @returns {*} Returns the resolved value.
     * @example
     *
     * _.defaultTo(1, 10);
     * // => 1
     *
     * _.defaultTo(undefined, 10);
     * // => 10
     */
    function defaultTo(value, defaultValue) {
      return (value == null || value !== value) ? defaultValue : value;
    }

    /**
     * Creates a function that returns the result of invoking the given functions
     * with the `this` binding of the created function, where each successive
     * invocation is supplied the return value of the previous.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Util
     * @param {...(Function|Function[])} [funcs] The functions to invoke.
     * @returns {Function} Returns the new composite function.
     * @see _.flowRight
     * @example
     *
     * function square(n) {
     *   return n * n;
     * }
     *
     * var addSquare = _.flow([_.add, square]);
     * addSquare(1, 2);
     * // => 9
     */
    var flow = createFlow();

    /**
     * This method is like `_.flow` except that it creates a function that
     * invokes the given functions from right to left.
     *
     * @static
     * @since 3.0.0
     * @memberOf _
     * @category Util
     * @param {...(Function|Function[])} [funcs] The functions to invoke.
     * @returns {Function} Returns the new composite function.
     * @see _.flow
     * @example
     *
     * function square(n) {
     *   return n * n;
     * }
     *
     * var addSquare = _.flowRight([square, _.add]);
     * addSquare(1, 2);
     * // => 9
     */
    var flowRight = createFlow(true);

    /**
     * This method returns the first argument it receives.
     *
     * @static
     * @since 0.1.0
     * @memberOf _
     * @category Util
     * @param {*} value Any value.
     * @returns {*} Returns `value`.
     * @example
     *
     * var object = { 'a': 1 };
     *
     * console.log(_.identity(object) === object);
     * // => true
     */
    function identity(value) {
      return value;
    }

    /**
     * Creates a function that invokes `func` with the arguments of the created
     * function. If `func` is a property name, the created function returns the
     * property value for a given element. If `func` is an array or object, the
     * created function returns `true` for elements that contain the equivalent
     * source properties, otherwise it returns `false`.
     *
     * @static
     * @since 4.0.0
     * @memberOf _
     * @category Util
     * @param {*} [func=_.identity] The value to convert to a callback.
     * @returns {Function} Returns the callback.
     * @example
     *
     * var users = [
     *   { 'user': 'barney', 'age': 36, 'active': true },
     *   { 'user': 'fred',   'age': 40, 'active': false }
     * ];
     *
     * // The `_.matches` iteratee shorthand.
     * _.filter(users, _.iteratee({ 'user': 'barney', 'active': true }));
     * // => [{ 'user': 'barney', 'age': 36, 'active': true }]
     *
     * // The `_.matchesProperty` iteratee shorthand.
     * _.filter(users, _.iteratee(['user', 'fred']));
     * // => [{ 'user': 'fred', 'age': 40 }]
     *
     * // The `_.property` iteratee shorthand.
     * _.map(users, _.iteratee('user'));
     * // => ['barney', 'fred']
     *
     * // Create custom iteratee shorthands.
     * _.iteratee = _.wrap(_.iteratee, function(iteratee, func) {
     *   return !_.isRegExp(func) ? iteratee(func) : function(string) {
     *     return func.test(string);
     *   };
     * });
     *
     * _.filter(['abc', 'def'], /ef/);
     * // => ['def']
     */
    function iteratee(func) {
      return baseIteratee(typeof func == 'function' ? func : baseClone(func, CLONE_DEEP_FLAG));
    }

    /**
     * Creates a function that performs a partial deep comparison between a given
     * object and `source`, returning `true` if the given object has equivalent
     * property values, else `false`.
     *
     * **Note:** The created function is equivalent to `_.isMatch` with `source`
     * partially applied.
     *
     * Partial comparisons will match empty array and empty object `source`
     * values against any array or object value, respectively. See `_.isEqual`
     * for a list of supported value comparisons.
     *
     * **Note:** Multiple values can be checked by combining several matchers
     * using `_.overSome`
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Util
     * @param {Object} source The object of property values to match.
     * @returns {Function} Returns the new spec function.
     * @example
     *
     * var objects = [
     *   { 'a': 1, 'b': 2, 'c': 3 },
     *   { 'a': 4, 'b': 5, 'c': 6 }
     * ];
     *
     * _.filter(objects, _.matches({ 'a': 4, 'c': 6 }));
     * // => [{ 'a': 4, 'b': 5, 'c': 6 }]
     *
     * // Checking for several possible values
     * _.filter(objects, _.overSome([_.matches({ 'a': 1 }), _.matches({ 'a': 4 })]));
     * // => [{ 'a': 1, 'b': 2, 'c': 3 }, { 'a': 4, 'b': 5, 'c': 6 }]
     */
    function matches(source) {
      return baseMatches(baseClone(source, CLONE_DEEP_FLAG));
    }

    /**
     * Creates a function that performs a partial deep comparison between the
     * value at `path` of a given object to `srcValue`, returning `true` if the
     * object value is equivalent, else `false`.
     *
     * **Note:** Partial comparisons will match empty array and empty object
     * `srcValue` values against any array or object value, respectively. See
     * `_.isEqual` for a list of supported value comparisons.
     *
     * **Note:** Multiple values can be checked by combining several matchers
     * using `_.overSome`
     *
     * @static
     * @memberOf _
     * @since 3.2.0
     * @category Util
     * @param {Array|string} path The path of the property to get.
     * @param {*} srcValue The value to match.
     * @returns {Function} Returns the new spec function.
     * @example
     *
     * var objects = [
     *   { 'a': 1, 'b': 2, 'c': 3 },
     *   { 'a': 4, 'b': 5, 'c': 6 }
     * ];
     *
     * _.find(objects, _.matchesProperty('a', 4));
     * // => { 'a': 4, 'b': 5, 'c': 6 }
     *
     * // Checking for several possible values
     * _.filter(objects, _.overSome([_.matchesProperty('a', 1), _.matchesProperty('a', 4)]));
     * // => [{ 'a': 1, 'b': 2, 'c': 3 }, { 'a': 4, 'b': 5, 'c': 6 }]
     */
    function matchesProperty(path, srcValue) {
      return baseMatchesProperty(path, baseClone(srcValue, CLONE_DEEP_FLAG));
    }

    /**
     * Creates a function that invokes the method at `path` of a given object.
     * Any additional arguments are provided to the invoked method.
     *
     * @static
     * @memberOf _
     * @since 3.7.0
     * @category Util
     * @param {Array|string} path The path of the method to invoke.
     * @param {...*} [args] The arguments to invoke the method with.
     * @returns {Function} Returns the new invoker function.
     * @example
     *
     * var objects = [
     *   { 'a': { 'b': _.constant(2) } },
     *   { 'a': { 'b': _.constant(1) } }
     * ];
     *
     * _.map(objects, _.method('a.b'));
     * // => [2, 1]
     *
     * _.map(objects, _.method(['a', 'b']));
     * // => [2, 1]
     */
    var method = baseRest(function(path, args) {
      return function(object) {
        return baseInvoke(object, path, args);
      };
    });

    /**
     * The opposite of `_.method`; this method creates a function that invokes
     * the method at a given path of `object`. Any additional arguments are
     * provided to the invoked method.
     *
     * @static
     * @memberOf _
     * @since 3.7.0
     * @category Util
     * @param {Object} object The object to query.
     * @param {...*} [args] The arguments to invoke the method with.
     * @returns {Function} Returns the new invoker function.
     * @example
     *
     * var array = _.times(3, _.constant),
     *     object = { 'a': array, 'b': array, 'c': array };
     *
     * _.map(['a[2]', 'c[0]'], _.methodOf(object));
     * // => [2, 0]
     *
     * _.map([['a', '2'], ['c', '0']], _.methodOf(object));
     * // => [2, 0]
     */
    var methodOf = baseRest(function(object, args) {
      return function(path) {
        return baseInvoke(object, path, args);
      };
    });

    /**
     * Adds all own enumerable string keyed function properties of a source
     * object to the destination object. If `object` is a function, then methods
     * are added to its prototype as well.
     *
     * **Note:** Use `_.runInContext` to create a pristine `lodash` function to
     * avoid conflicts caused by modifying the original.
     *
     * @static
     * @since 0.1.0
     * @memberOf _
     * @category Util
     * @param {Function|Object} [object=lodash] The destination object.
     * @param {Object} source The object of functions to add.
     * @param {Object} [options={}] The options object.
     * @param {boolean} [options.chain=true] Specify whether mixins are chainable.
     * @returns {Function|Object} Returns `object`.
     * @example
     *
     * function vowels(string) {
     *   return _.filter(string, function(v) {
     *     return /[aeiou]/i.test(v);
     *   });
     * }
     *
     * _.mixin({ 'vowels': vowels });
     * _.vowels('fred');
     * // => ['e']
     *
     * _('fred').vowels().value();
     * // => ['e']
     *
     * _.mixin({ 'vowels': vowels }, { 'chain': false });
     * _('fred').vowels();
     * // => ['e']
     */
    function mixin(object, source, options) {
      var props = keys(source),
          methodNames = baseFunctions(source, props);

      if (options == null &&
          !(isObject(source) && (methodNames.length || !props.length))) {
        options = source;
        source = object;
        object = this;
        methodNames = baseFunctions(source, keys(source));
      }
      var chain = !(isObject(options) && 'chain' in options) || !!options.chain,
          isFunc = isFunction(object);

      arrayEach(methodNames, function(methodName) {
        var func = source[methodName];
        object[methodName] = func;
        if (isFunc) {
          object.prototype[methodName] = function() {
            var chainAll = this.__chain__;
            if (chain || chainAll) {
              var result = object(this.__wrapped__),
                  actions = result.__actions__ = copyArray(this.__actions__);

              actions.push({ 'func': func, 'args': arguments, 'thisArg': object });
              result.__chain__ = chainAll;
              return result;
            }
            return func.apply(object, arrayPush([this.value()], arguments));
          };
        }
      });

      return object;
    }

    /**
     * Reverts the `_` variable to its previous value and returns a reference to
     * the `lodash` function.
     *
     * @static
     * @since 0.1.0
     * @memberOf _
     * @category Util
     * @returns {Function} Returns the `lodash` function.
     * @example
     *
     * var lodash = _.noConflict();
     */
    function noConflict() {
      if (root._ === this) {
        root._ = oldDash;
      }
      return this;
    }

    /**
     * This method returns `undefined`.
     *
     * @static
     * @memberOf _
     * @since 2.3.0
     * @category Util
     * @example
     *
     * _.times(2, _.noop);
     * // => [undefined, undefined]
     */
    function noop() {
      // No operation performed.
    }

    /**
     * Creates a function that gets the argument at index `n`. If `n` is negative,
     * the nth argument from the end is returned.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Util
     * @param {number} [n=0] The index of the argument to return.
     * @returns {Function} Returns the new pass-thru function.
     * @example
     *
     * var func = _.nthArg(1);
     * func('a', 'b', 'c', 'd');
     * // => 'b'
     *
     * var func = _.nthArg(-2);
     * func('a', 'b', 'c', 'd');
     * // => 'c'
     */
    function nthArg(n) {
      n = toInteger(n);
      return baseRest(function(args) {
        return baseNth(args, n);
      });
    }

    /**
     * Creates a function that invokes `iteratees` with the arguments it receives
     * and returns their results.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Util
     * @param {...(Function|Function[])} [iteratees=[_.identity]]
     *  The iteratees to invoke.
     * @returns {Function} Returns the new function.
     * @example
     *
     * var func = _.over([Math.max, Math.min]);
     *
     * func(1, 2, 3, 4);
     * // => [4, 1]
     */
    var over = createOver(arrayMap);

    /**
     * Creates a function that checks if **all** of the `predicates` return
     * truthy when invoked with the arguments it receives.
     *
     * Following shorthands are possible for providing predicates.
     * Pass an `Object` and it will be used as an parameter for `_.matches` to create the predicate.
     * Pass an `Array` of parameters for `_.matchesProperty` and the predicate will be created using them.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Util
     * @param {...(Function|Function[])} [predicates=[_.identity]]
     *  The predicates to check.
     * @returns {Function} Returns the new function.
     * @example
     *
     * var func = _.overEvery([Boolean, isFinite]);
     *
     * func('1');
     * // => true
     *
     * func(null);
     * // => false
     *
     * func(NaN);
     * // => false
     */
    var overEvery = createOver(arrayEvery);

    /**
     * Creates a function that checks if **any** of the `predicates` return
     * truthy when invoked with the arguments it receives.
     *
     * Following shorthands are possible for providing predicates.
     * Pass an `Object` and it will be used as an parameter for `_.matches` to create the predicate.
     * Pass an `Array` of parameters for `_.matchesProperty` and the predicate will be created using them.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Util
     * @param {...(Function|Function[])} [predicates=[_.identity]]
     *  The predicates to check.
     * @returns {Function} Returns the new function.
     * @example
     *
     * var func = _.overSome([Boolean, isFinite]);
     *
     * func('1');
     * // => true
     *
     * func(null);
     * // => true
     *
     * func(NaN);
     * // => false
     *
     * var matchesFunc = _.overSome([{ 'a': 1 }, { 'a': 2 }])
     * var matchesPropertyFunc = _.overSome([['a', 1], ['a', 2]])
     */
    var overSome = createOver(arraySome);

    /**
     * Creates a function that returns the value at `path` of a given object.
     *
     * @static
     * @memberOf _
     * @since 2.4.0
     * @category Util
     * @param {Array|string} path The path of the property to get.
     * @returns {Function} Returns the new accessor function.
     * @example
     *
     * var objects = [
     *   { 'a': { 'b': 2 } },
     *   { 'a': { 'b': 1 } }
     * ];
     *
     * _.map(objects, _.property('a.b'));
     * // => [2, 1]
     *
     * _.map(_.sortBy(objects, _.property(['a', 'b'])), 'a.b');
     * // => [1, 2]
     */
    function property(path) {
      return isKey(path) ? baseProperty(toKey(path)) : basePropertyDeep(path);
    }

    /**
     * The opposite of `_.property`; this method creates a function that returns
     * the value at a given path of `object`.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Util
     * @param {Object} object The object to query.
     * @returns {Function} Returns the new accessor function.
     * @example
     *
     * var array = [0, 1, 2],
     *     object = { 'a': array, 'b': array, 'c': array };
     *
     * _.map(['a[2]', 'c[0]'], _.propertyOf(object));
     * // => [2, 0]
     *
     * _.map([['a', '2'], ['c', '0']], _.propertyOf(object));
     * // => [2, 0]
     */
    function propertyOf(object) {
      return function(path) {
        return object == null ? undefined : baseGet(object, path);
      };
    }

    /**
     * Creates an array of numbers (positive and/or negative) progressing from
     * `start` up to, but not including, `end`. A step of `-1` is used if a negative
     * `start` is specified without an `end` or `step`. If `end` is not specified,
     * it's set to `start` with `start` then set to `0`.
     *
     * **Note:** JavaScript follows the IEEE-754 standard for resolving
     * floating-point values which can produce unexpected results.
     *
     * @static
     * @since 0.1.0
     * @memberOf _
     * @category Util
     * @param {number} [start=0] The start of the range.
     * @param {number} end The end of the range.
     * @param {number} [step=1] The value to increment or decrement by.
     * @returns {Array} Returns the range of numbers.
     * @see _.inRange, _.rangeRight
     * @example
     *
     * _.range(4);
     * // => [0, 1, 2, 3]
     *
     * _.range(-4);
     * // => [0, -1, -2, -3]
     *
     * _.range(1, 5);
     * // => [1, 2, 3, 4]
     *
     * _.range(0, 20, 5);
     * // => [0, 5, 10, 15]
     *
     * _.range(0, -4, -1);
     * // => [0, -1, -2, -3]
     *
     * _.range(1, 4, 0);
     * // => [1, 1, 1]
     *
     * _.range(0);
     * // => []
     */
    var range = createRange();

    /**
     * This method is like `_.range` except that it populates values in
     * descending order.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Util
     * @param {number} [start=0] The start of the range.
     * @param {number} end The end of the range.
     * @param {number} [step=1] The value to increment or decrement by.
     * @returns {Array} Returns the range of numbers.
     * @see _.inRange, _.range
     * @example
     *
     * _.rangeRight(4);
     * // => [3, 2, 1, 0]
     *
     * _.rangeRight(-4);
     * // => [-3, -2, -1, 0]
     *
     * _.rangeRight(1, 5);
     * // => [4, 3, 2, 1]
     *
     * _.rangeRight(0, 20, 5);
     * // => [15, 10, 5, 0]
     *
     * _.rangeRight(0, -4, -1);
     * // => [-3, -2, -1, 0]
     *
     * _.rangeRight(1, 4, 0);
     * // => [1, 1, 1]
     *
     * _.rangeRight(0);
     * // => []
     */
    var rangeRight = createRange(true);

    /**
     * This method returns a new empty array.
     *
     * @static
     * @memberOf _
     * @since 4.13.0
     * @category Util
     * @returns {Array} Returns the new empty array.
     * @example
     *
     * var arrays = _.times(2, _.stubArray);
     *
     * console.log(arrays);
     * // => [[], []]
     *
     * console.log(arrays[0] === arrays[1]);
     * // => false
     */
    function stubArray() {
      return [];
    }

    /**
     * This method returns `false`.
     *
     * @static
     * @memberOf _
     * @since 4.13.0
     * @category Util
     * @returns {boolean} Returns `false`.
     * @example
     *
     * _.times(2, _.stubFalse);
     * // => [false, false]
     */
    function stubFalse() {
      return false;
    }

    /**
     * This method returns a new empty object.
     *
     * @static
     * @memberOf _
     * @since 4.13.0
     * @category Util
     * @returns {Object} Returns the new empty object.
     * @example
     *
     * var objects = _.times(2, _.stubObject);
     *
     * console.log(objects);
     * // => [{}, {}]
     *
     * console.log(objects[0] === objects[1]);
     * // => false
     */
    function stubObject() {
      return {};
    }

    /**
     * This method returns an empty string.
     *
     * @static
     * @memberOf _
     * @since 4.13.0
     * @category Util
     * @returns {string} Returns the empty string.
     * @example
     *
     * _.times(2, _.stubString);
     * // => ['', '']
     */
    function stubString() {
      return '';
    }

    /**
     * This method returns `true`.
     *
     * @static
     * @memberOf _
     * @since 4.13.0
     * @category Util
     * @returns {boolean} Returns `true`.
     * @example
     *
     * _.times(2, _.stubTrue);
     * // => [true, true]
     */
    function stubTrue() {
      return true;
    }

    /**
     * Invokes the iteratee `n` times, returning an array of the results of
     * each invocation. The iteratee is invoked with one argument; (index).
     *
     * @static
     * @since 0.1.0
     * @memberOf _
     * @category Util
     * @param {number} n The number of times to invoke `iteratee`.
     * @param {Function} [iteratee=_.identity] The function invoked per iteration.
     * @returns {Array} Returns the array of results.
     * @example
     *
     * _.times(3, String);
     * // => ['0', '1', '2']
     *
     *  _.times(4, _.constant(0));
     * // => [0, 0, 0, 0]
     */
    function times(n, iteratee) {
      n = toInteger(n);
      if (n < 1 || n > MAX_SAFE_INTEGER) {
        return [];
      }
      var index = MAX_ARRAY_LENGTH,
          length = nativeMin(n, MAX_ARRAY_LENGTH);

      iteratee = getIteratee(iteratee);
      n -= MAX_ARRAY_LENGTH;

      var result = baseTimes(length, iteratee);
      while (++index < n) {
        iteratee(index);
      }
      return result;
    }

    /**
     * Converts `value` to a property path array.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Util
     * @param {*} value The value to convert.
     * @returns {Array} Returns the new property path array.
     * @example
     *
     * _.toPath('a.b.c');
     * // => ['a', 'b', 'c']
     *
     * _.toPath('a[0].b.c');
     * // => ['a', '0', 'b', 'c']
     */
    function toPath(value) {
      if (isArray(value)) {
        return arrayMap(value, toKey);
      }
      return isSymbol(value) ? [value] : copyArray(stringToPath(toString(value)));
    }

    /**
     * Generates a unique ID. If `prefix` is given, the ID is appended to it.
     *
     * @static
     * @since 0.1.0
     * @memberOf _
     * @category Util
     * @param {string} [prefix=''] The value to prefix the ID with.
     * @returns {string} Returns the unique ID.
     * @example
     *
     * _.uniqueId('contact_');
     * // => 'contact_104'
     *
     * _.uniqueId();
     * // => '105'
     */
    function uniqueId(prefix) {
      var id = ++idCounter;
      return toString(prefix) + id;
    }

    /*------------------------------------------------------------------------*/

    /**
     * Adds two numbers.
     *
     * @static
     * @memberOf _
     * @since 3.4.0
     * @category Math
     * @param {number} augend The first number in an addition.
     * @param {number} addend The second number in an addition.
     * @returns {number} Returns the total.
     * @example
     *
     * _.add(6, 4);
     * // => 10
     */
    var add = createMathOperation(function(augend, addend) {
      return augend + addend;
    }, 0);

    /**
     * Computes `number` rounded up to `precision`.
     *
     * @static
     * @memberOf _
     * @since 3.10.0
     * @category Math
     * @param {number} number The number to round up.
     * @param {number} [precision=0] The precision to round up to.
     * @returns {number} Returns the rounded up number.
     * @example
     *
     * _.ceil(4.006);
     * // => 5
     *
     * _.ceil(6.004, 2);
     * // => 6.01
     *
     * _.ceil(6040, -2);
     * // => 6100
     */
    var ceil = createRound('ceil');

    /**
     * Divide two numbers.
     *
     * @static
     * @memberOf _
     * @since 4.7.0
     * @category Math
     * @param {number} dividend The first number in a division.
     * @param {number} divisor The second number in a division.
     * @returns {number} Returns the quotient.
     * @example
     *
     * _.divide(6, 4);
     * // => 1.5
     */
    var divide = createMathOperation(function(dividend, divisor) {
      return dividend / divisor;
    }, 1);

    /**
     * Computes `number` rounded down to `precision`.
     *
     * @static
     * @memberOf _
     * @since 3.10.0
     * @category Math
     * @param {number} number The number to round down.
     * @param {number} [precision=0] The precision to round down to.
     * @returns {number} Returns the rounded down number.
     * @example
     *
     * _.floor(4.006);
     * // => 4
     *
     * _.floor(0.046, 2);
     * // => 0.04
     *
     * _.floor(4060, -2);
     * // => 4000
     */
    var floor = createRound('floor');

    /**
     * Computes the maximum value of `array`. If `array` is empty or falsey,
     * `undefined` is returned.
     *
     * @static
     * @since 0.1.0
     * @memberOf _
     * @category Math
     * @param {Array} array The array to iterate over.
     * @returns {*} Returns the maximum value.
     * @example
     *
     * _.max([4, 2, 8, 6]);
     * // => 8
     *
     * _.max([]);
     * // => undefined
     */
    function max(array) {
      return (array && array.length)
        ? baseExtremum(array, identity, baseGt)
        : undefined;
    }

    /**
     * This method is like `_.max` except that it accepts `iteratee` which is
     * invoked for each element in `array` to generate the criterion by which
     * the value is ranked. The iteratee is invoked with one argument: (value).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Math
     * @param {Array} array The array to iterate over.
     * @param {Function} [iteratee=_.identity] The iteratee invoked per element.
     * @returns {*} Returns the maximum value.
     * @example
     *
     * var objects = [{ 'n': 1 }, { 'n': 2 }];
     *
     * _.maxBy(objects, function(o) { return o.n; });
     * // => { 'n': 2 }
     *
     * // The `_.property` iteratee shorthand.
     * _.maxBy(objects, 'n');
     * // => { 'n': 2 }
     */
    function maxBy(array, iteratee) {
      return (array && array.length)
        ? baseExtremum(array, getIteratee(iteratee, 2), baseGt)
        : undefined;
    }

    /**
     * Computes the mean of the values in `array`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Math
     * @param {Array} array The array to iterate over.
     * @returns {number} Returns the mean.
     * @example
     *
     * _.mean([4, 2, 8, 6]);
     * // => 5
     */
    function mean(array) {
      return baseMean(array, identity);
    }

    /**
     * This method is like `_.mean` except that it accepts `iteratee` which is
     * invoked for each element in `array` to generate the value to be averaged.
     * The iteratee is invoked with one argument: (value).
     *
     * @static
     * @memberOf _
     * @since 4.7.0
     * @category Math
     * @param {Array} array The array to iterate over.
     * @param {Function} [iteratee=_.identity] The iteratee invoked per element.
     * @returns {number} Returns the mean.
     * @example
     *
     * var objects = [{ 'n': 4 }, { 'n': 2 }, { 'n': 8 }, { 'n': 6 }];
     *
     * _.meanBy(objects, function(o) { return o.n; });
     * // => 5
     *
     * // The `_.property` iteratee shorthand.
     * _.meanBy(objects, 'n');
     * // => 5
     */
    function meanBy(array, iteratee) {
      return baseMean(array, getIteratee(iteratee, 2));
    }

    /**
     * Computes the minimum value of `array`. If `array` is empty or falsey,
     * `undefined` is returned.
     *
     * @static
     * @since 0.1.0
     * @memberOf _
     * @category Math
     * @param {Array} array The array to iterate over.
     * @returns {*} Returns the minimum value.
     * @example
     *
     * _.min([4, 2, 8, 6]);
     * // => 2
     *
     * _.min([]);
     * // => undefined
     */
    function min(array) {
      return (array && array.length)
        ? baseExtremum(array, identity, baseLt)
        : undefined;
    }

    /**
     * This method is like `_.min` except that it accepts `iteratee` which is
     * invoked for each element in `array` to generate the criterion by which
     * the value is ranked. The iteratee is invoked with one argument: (value).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Math
     * @param {Array} array The array to iterate over.
     * @param {Function} [iteratee=_.identity] The iteratee invoked per element.
     * @returns {*} Returns the minimum value.
     * @example
     *
     * var objects = [{ 'n': 1 }, { 'n': 2 }];
     *
     * _.minBy(objects, function(o) { return o.n; });
     * // => { 'n': 1 }
     *
     * // The `_.property` iteratee shorthand.
     * _.minBy(objects, 'n');
     * // => { 'n': 1 }
     */
    function minBy(array, iteratee) {
      return (array && array.length)
        ? baseExtremum(array, getIteratee(iteratee, 2), baseLt)
        : undefined;
    }

    /**
     * Multiply two numbers.
     *
     * @static
     * @memberOf _
     * @since 4.7.0
     * @category Math
     * @param {number} multiplier The first number in a multiplication.
     * @param {number} multiplicand The second number in a multiplication.
     * @returns {number} Returns the product.
     * @example
     *
     * _.multiply(6, 4);
     * // => 24
     */
    var multiply = createMathOperation(function(multiplier, multiplicand) {
      return multiplier * multiplicand;
    }, 1);

    /**
     * Computes `number` rounded to `precision`.
     *
     * @static
     * @memberOf _
     * @since 3.10.0
     * @category Math
     * @param {number} number The number to round.
     * @param {number} [precision=0] The precision to round to.
     * @returns {number} Returns the rounded number.
     * @example
     *
     * _.round(4.006);
     * // => 4
     *
     * _.round(4.006, 2);
     * // => 4.01
     *
     * _.round(4060, -2);
     * // => 4100
     */
    var round = createRound('round');

    /**
     * Subtract two numbers.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Math
     * @param {number} minuend The first number in a subtraction.
     * @param {number} subtrahend The second number in a subtraction.
     * @returns {number} Returns the difference.
     * @example
     *
     * _.subtract(6, 4);
     * // => 2
     */
    var subtract = createMathOperation(function(minuend, subtrahend) {
      return minuend - subtrahend;
    }, 0);

    /**
     * Computes the sum of the values in `array`.
     *
     * @static
     * @memberOf _
     * @since 3.4.0
     * @category Math
     * @param {Array} array The array to iterate over.
     * @returns {number} Returns the sum.
     * @example
     *
     * _.sum([4, 2, 8, 6]);
     * // => 20
     */
    function sum(array) {
      return (array && array.length)
        ? baseSum(array, identity)
        : 0;
    }

    /**
     * This method is like `_.sum` except that it accepts `iteratee` which is
     * invoked for each element in `array` to generate the value to be summed.
     * The iteratee is invoked with one argument: (value).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Math
     * @param {Array} array The array to iterate over.
     * @param {Function} [iteratee=_.identity] The iteratee invoked per element.
     * @returns {number} Returns the sum.
     * @example
     *
     * var objects = [{ 'n': 4 }, { 'n': 2 }, { 'n': 8 }, { 'n': 6 }];
     *
     * _.sumBy(objects, function(o) { return o.n; });
     * // => 20
     *
     * // The `_.property` iteratee shorthand.
     * _.sumBy(objects, 'n');
     * // => 20
     */
    function sumBy(array, iteratee) {
      return (array && array.length)
        ? baseSum(array, getIteratee(iteratee, 2))
        : 0;
    }

    /*------------------------------------------------------------------------*/

    // Add methods that return wrapped values in chain sequences.
    lodash.after = after;
    lodash.ary = ary;
    lodash.assign = assign;
    lodash.assignIn = assignIn;
    lodash.assignInWith = assignInWith;
    lodash.assignWith = assignWith;
    lodash.at = at;
    lodash.before = before;
    lodash.bind = bind;
    lodash.bindAll = bindAll;
    lodash.bindKey = bindKey;
    lodash.castArray = castArray;
    lodash.chain = chain;
    lodash.chunk = chunk;
    lodash.compact = compact;
    lodash.concat = concat;
    lodash.cond = cond;
    lodash.conforms = conforms;
    lodash.constant = constant;
    lodash.countBy = countBy;
    lodash.create = create;
    lodash.curry = curry;
    lodash.curryRight = curryRight;
    lodash.debounce = debounce;
    lodash.defaults = defaults;
    lodash.defaultsDeep = defaultsDeep;
    lodash.defer = defer;
    lodash.delay = delay;
    lodash.difference = difference;
    lodash.differenceBy = differenceBy;
    lodash.differenceWith = differenceWith;
    lodash.drop = drop;
    lodash.dropRight = dropRight;
    lodash.dropRightWhile = dropRightWhile;
    lodash.dropWhile = dropWhile;
    lodash.fill = fill;
    lodash.filter = filter;
    lodash.flatMap = flatMap;
    lodash.flatMapDeep = flatMapDeep;
    lodash.flatMapDepth = flatMapDepth;
    lodash.flatten = flatten;
    lodash.flattenDeep = flattenDeep;
    lodash.flattenDepth = flattenDepth;
    lodash.flip = flip;
    lodash.flow = flow;
    lodash.flowRight = flowRight;
    lodash.fromPairs = fromPairs;
    lodash.functions = functions;
    lodash.functionsIn = functionsIn;
    lodash.groupBy = groupBy;
    lodash.initial = initial;
    lodash.intersection = intersection;
    lodash.intersectionBy = intersectionBy;
    lodash.intersectionWith = intersectionWith;
    lodash.invert = invert;
    lodash.invertBy = invertBy;
    lodash.invokeMap = invokeMap;
    lodash.iteratee = iteratee;
    lodash.keyBy = keyBy;
    lodash.keys = keys;
    lodash.keysIn = keysIn;
    lodash.map = map;
    lodash.mapKeys = mapKeys;
    lodash.mapValues = mapValues;
    lodash.matches = matches;
    lodash.matchesProperty = matchesProperty;
    lodash.memoize = memoize;
    lodash.merge = merge;
    lodash.mergeWith = mergeWith;
    lodash.method = method;
    lodash.methodOf = methodOf;
    lodash.mixin = mixin;
    lodash.negate = negate;
    lodash.nthArg = nthArg;
    lodash.omit = omit;
    lodash.omitBy = omitBy;
    lodash.once = once;
    lodash.orderBy = orderBy;
    lodash.over = over;
    lodash.overArgs = overArgs;
    lodash.overEvery = overEvery;
    lodash.overSome = overSome;
    lodash.partial = partial;
    lodash.partialRight = partialRight;
    lodash.partition = partition;
    lodash.pick = pick;
    lodash.pickBy = pickBy;
    lodash.property = property;
    lodash.propertyOf = propertyOf;
    lodash.pull = pull;
    lodash.pullAll = pullAll;
    lodash.pullAllBy = pullAllBy;
    lodash.pullAllWith = pullAllWith;
    lodash.pullAt = pullAt;
    lodash.range = range;
    lodash.rangeRight = rangeRight;
    lodash.rearg = rearg;
    lodash.reject = reject;
    lodash.remove = remove;
    lodash.rest = rest;
    lodash.reverse = reverse;
    lodash.sampleSize = sampleSize;
    lodash.set = set;
    lodash.setWith = setWith;
    lodash.shuffle = shuffle;
    lodash.slice = slice;
    lodash.sortBy = sortBy;
    lodash.sortedUniq = sortedUniq;
    lodash.sortedUniqBy = sortedUniqBy;
    lodash.split = split;
    lodash.spread = spread;
    lodash.tail = tail;
    lodash.take = take;
    lodash.takeRight = takeRight;
    lodash.takeRightWhile = takeRightWhile;
    lodash.takeWhile = takeWhile;
    lodash.tap = tap;
    lodash.throttle = throttle;
    lodash.thru = thru;
    lodash.toArray = toArray;
    lodash.toPairs = toPairs;
    lodash.toPairsIn = toPairsIn;
    lodash.toPath = toPath;
    lodash.toPlainObject = toPlainObject;
    lodash.transform = transform;
    lodash.unary = unary;
    lodash.union = union;
    lodash.unionBy = unionBy;
    lodash.unionWith = unionWith;
    lodash.uniq = uniq;
    lodash.uniqBy = uniqBy;
    lodash.uniqWith = uniqWith;
    lodash.unset = unset;
    lodash.unzip = unzip;
    lodash.unzipWith = unzipWith;
    lodash.update = update;
    lodash.updateWith = updateWith;
    lodash.values = values;
    lodash.valuesIn = valuesIn;
    lodash.without = without;
    lodash.words = words;
    lodash.wrap = wrap;
    lodash.xor = xor;
    lodash.xorBy = xorBy;
    lodash.xorWith = xorWith;
    lodash.zip = zip;
    lodash.zipObject = zipObject;
    lodash.zipObjectDeep = zipObjectDeep;
    lodash.zipWith = zipWith;

    // Add aliases.
    lodash.entries = toPairs;
    lodash.entriesIn = toPairsIn;
    lodash.extend = assignIn;
    lodash.extendWith = assignInWith;

    // Add methods to `lodash.prototype`.
    mixin(lodash, lodash);

    /*------------------------------------------------------------------------*/

    // Add methods that return unwrapped values in chain sequences.
    lodash.add = add;
    lodash.attempt = attempt;
    lodash.camelCase = camelCase;
    lodash.capitalize = capitalize;
    lodash.ceil = ceil;
    lodash.clamp = clamp;
    lodash.clone = clone;
    lodash.cloneDeep = cloneDeep;
    lodash.cloneDeepWith = cloneDeepWith;
    lodash.cloneWith = cloneWith;
    lodash.conformsTo = conformsTo;
    lodash.deburr = deburr;
    lodash.defaultTo = defaultTo;
    lodash.divide = divide;
    lodash.endsWith = endsWith;
    lodash.eq = eq;
    lodash.escape = escape;
    lodash.escapeRegExp = escapeRegExp;
    lodash.every = every;
    lodash.find = find;
    lodash.findIndex = findIndex;
    lodash.findKey = findKey;
    lodash.findLast = findLast;
    lodash.findLastIndex = findLastIndex;
    lodash.findLastKey = findLastKey;
    lodash.floor = floor;
    lodash.forEach = forEach;
    lodash.forEachRight = forEachRight;
    lodash.forIn = forIn;
    lodash.forInRight = forInRight;
    lodash.forOwn = forOwn;
    lodash.forOwnRight = forOwnRight;
    lodash.get = get;
    lodash.gt = gt;
    lodash.gte = gte;
    lodash.has = has;
    lodash.hasIn = hasIn;
    lodash.head = head;
    lodash.identity = identity;
    lodash.includes = includes;
    lodash.indexOf = indexOf;
    lodash.inRange = inRange;
    lodash.invoke = invoke;
    lodash.isArguments = isArguments;
    lodash.isArray = isArray;
    lodash.isArrayBuffer = isArrayBuffer;
    lodash.isArrayLike = isArrayLike;
    lodash.isArrayLikeObject = isArrayLikeObject;
    lodash.isBoolean = isBoolean;
    lodash.isBuffer = isBuffer;
    lodash.isDate = isDate;
    lodash.isElement = isElement;
    lodash.isEmpty = isEmpty;
    lodash.isEqual = isEqual;
    lodash.isEqualWith = isEqualWith;
    lodash.isError = isError;
    lodash.isFinite = isFinite;
    lodash.isFunction = isFunction;
    lodash.isInteger = isInteger;
    lodash.isLength = isLength;
    lodash.isMap = isMap;
    lodash.isMatch = isMatch;
    lodash.isMatchWith = isMatchWith;
    lodash.isNaN = isNaN;
    lodash.isNative = isNative;
    lodash.isNil = isNil;
    lodash.isNull = isNull;
    lodash.isNumber = isNumber;
    lodash.isObject = isObject;
    lodash.isObjectLike = isObjectLike;
    lodash.isPlainObject = isPlainObject;
    lodash.isRegExp = isRegExp;
    lodash.isSafeInteger = isSafeInteger;
    lodash.isSet = isSet;
    lodash.isString = isString;
    lodash.isSymbol = isSymbol;
    lodash.isTypedArray = isTypedArray;
    lodash.isUndefined = isUndefined;
    lodash.isWeakMap = isWeakMap;
    lodash.isWeakSet = isWeakSet;
    lodash.join = join;
    lodash.kebabCase = kebabCase;
    lodash.last = last;
    lodash.lastIndexOf = lastIndexOf;
    lodash.lowerCase = lowerCase;
    lodash.lowerFirst = lowerFirst;
    lodash.lt = lt;
    lodash.lte = lte;
    lodash.max = max;
    lodash.maxBy = maxBy;
    lodash.mean = mean;
    lodash.meanBy = meanBy;
    lodash.min = min;
    lodash.minBy = minBy;
    lodash.stubArray = stubArray;
    lodash.stubFalse = stubFalse;
    lodash.stubObject = stubObject;
    lodash.stubString = stubString;
    lodash.stubTrue = stubTrue;
    lodash.multiply = multiply;
    lodash.nth = nth;
    lodash.noConflict = noConflict;
    lodash.noop = noop;
    lodash.now = now;
    lodash.pad = pad;
    lodash.padEnd = padEnd;
    lodash.padStart = padStart;
    lodash.parseInt = parseInt;
    lodash.random = random;
    lodash.reduce = reduce;
    lodash.reduceRight = reduceRight;
    lodash.repeat = repeat;
    lodash.replace = replace;
    lodash.result = result;
    lodash.round = round;
    lodash.runInContext = runInContext;
    lodash.sample = sample;
    lodash.size = size;
    lodash.snakeCase = snakeCase;
    lodash.some = some;
    lodash.sortedIndex = sortedIndex;
    lodash.sortedIndexBy = sortedIndexBy;
    lodash.sortedIndexOf = sortedIndexOf;
    lodash.sortedLastIndex = sortedLastIndex;
    lodash.sortedLastIndexBy = sortedLastIndexBy;
    lodash.sortedLastIndexOf = sortedLastIndexOf;
    lodash.startCase = startCase;
    lodash.startsWith = startsWith;
    lodash.subtract = subtract;
    lodash.sum = sum;
    lodash.sumBy = sumBy;
    lodash.template = template;
    lodash.times = times;
    lodash.toFinite = toFinite;
    lodash.toInteger = toInteger;
    lodash.toLength = toLength;
    lodash.toLower = toLower;
    lodash.toNumber = toNumber;
    lodash.toSafeInteger = toSafeInteger;
    lodash.toString = toString;
    lodash.toUpper = toUpper;
    lodash.trim = trim;
    lodash.trimEnd = trimEnd;
    lodash.trimStart = trimStart;
    lodash.truncate = truncate;
    lodash.unescape = unescape;
    lodash.uniqueId = uniqueId;
    lodash.upperCase = upperCase;
    lodash.upperFirst = upperFirst;

    // Add aliases.
    lodash.each = forEach;
    lodash.eachRight = forEachRight;
    lodash.first = head;

    mixin(lodash, (function() {
      var source = {};
      baseForOwn(lodash, function(func, methodName) {
        if (!hasOwnProperty.call(lodash.prototype, methodName)) {
          source[methodName] = func;
        }
      });
      return source;
    }()), { 'chain': false });

    /*------------------------------------------------------------------------*/

    /**
     * The semantic version number.
     *
     * @static
     * @memberOf _
     * @type {string}
     */
    lodash.VERSION = VERSION;

    // Assign default placeholders.
    arrayEach(['bind', 'bindKey', 'curry', 'curryRight', 'partial', 'partialRight'], function(methodName) {
      lodash[methodName].placeholder = lodash;
    });

    // Add `LazyWrapper` methods for `_.drop` and `_.take` variants.
    arrayEach(['drop', 'take'], function(methodName, index) {
      LazyWrapper.prototype[methodName] = function(n) {
        n = n === undefined ? 1 : nativeMax(toInteger(n), 0);

        var result = (this.__filtered__ && !index)
          ? new LazyWrapper(this)
          : this.clone();

        if (result.__filtered__) {
          result.__takeCount__ = nativeMin(n, result.__takeCount__);
        } else {
          result.__views__.push({
            'size': nativeMin(n, MAX_ARRAY_LENGTH),
            'type': methodName + (result.__dir__ < 0 ? 'Right' : '')
          });
        }
        return result;
      };

      LazyWrapper.prototype[methodName + 'Right'] = function(n) {
        return this.reverse()[methodName](n).reverse();
      };
    });

    // Add `LazyWrapper` methods that accept an `iteratee` value.
    arrayEach(['filter', 'map', 'takeWhile'], function(methodName, index) {
      var type = index + 1,
          isFilter = type == LAZY_FILTER_FLAG || type == LAZY_WHILE_FLAG;

      LazyWrapper.prototype[methodName] = function(iteratee) {
        var result = this.clone();
        result.__iteratees__.push({
          'iteratee': getIteratee(iteratee, 3),
          'type': type
        });
        result.__filtered__ = result.__filtered__ || isFilter;
        return result;
      };
    });

    // Add `LazyWrapper` methods for `_.head` and `_.last`.
    arrayEach(['head', 'last'], function(methodName, index) {
      var takeName = 'take' + (index ? 'Right' : '');

      LazyWrapper.prototype[methodName] = function() {
        return this[takeName](1).value()[0];
      };
    });

    // Add `LazyWrapper` methods for `_.initial` and `_.tail`.
    arrayEach(['initial', 'tail'], function(methodName, index) {
      var dropName = 'drop' + (index ? '' : 'Right');

      LazyWrapper.prototype[methodName] = function() {
        return this.__filtered__ ? new LazyWrapper(this) : this[dropName](1);
      };
    });

    LazyWrapper.prototype.compact = function() {
      return this.filter(identity);
    };

    LazyWrapper.prototype.find = function(predicate) {
      return this.filter(predicate).head();
    };

    LazyWrapper.prototype.findLast = function(predicate) {
      return this.reverse().find(predicate);
    };

    LazyWrapper.prototype.invokeMap = baseRest(function(path, args) {
      if (typeof path == 'function') {
        return new LazyWrapper(this);
      }
      return this.map(function(value) {
        return baseInvoke(value, path, args);
      });
    });

    LazyWrapper.prototype.reject = function(predicate) {
      return this.filter(negate(getIteratee(predicate)));
    };

    LazyWrapper.prototype.slice = function(start, end) {
      start = toInteger(start);

      var result = this;
      if (result.__filtered__ && (start > 0 || end < 0)) {
        return new LazyWrapper(result);
      }
      if (start < 0) {
        result = result.takeRight(-start);
      } else if (start) {
        result = result.drop(start);
      }
      if (end !== undefined) {
        end = toInteger(end);
        result = end < 0 ? result.dropRight(-end) : result.take(end - start);
      }
      return result;
    };

    LazyWrapper.prototype.takeRightWhile = function(predicate) {
      return this.reverse().takeWhile(predicate).reverse();
    };

    LazyWrapper.prototype.toArray = function() {
      return this.take(MAX_ARRAY_LENGTH);
    };

    // Add `LazyWrapper` methods to `lodash.prototype`.
    baseForOwn(LazyWrapper.prototype, function(func, methodName) {
      var checkIteratee = /^(?:filter|find|map|reject)|While$/.test(methodName),
          isTaker = /^(?:head|last)$/.test(methodName),
          lodashFunc = lodash[isTaker ? ('take' + (methodName == 'last' ? 'Right' : '')) : methodName],
          retUnwrapped = isTaker || /^find/.test(methodName);

      if (!lodashFunc) {
        return;
      }
      lodash.prototype[methodName] = function() {
        var value = this.__wrapped__,
            args = isTaker ? [1] : arguments,
            isLazy = value instanceof LazyWrapper,
            iteratee = args[0],
            useLazy = isLazy || isArray(value);

        var interceptor = function(value) {
          var result = lodashFunc.apply(lodash, arrayPush([value], args));
          return (isTaker && chainAll) ? result[0] : result;
        };

        if (useLazy && checkIteratee && typeof iteratee == 'function' && iteratee.length != 1) {
          // Avoid lazy use if the iteratee has a "length" value other than `1`.
          isLazy = useLazy = false;
        }
        var chainAll = this.__chain__,
            isHybrid = !!this.__actions__.length,
            isUnwrapped = retUnwrapped && !chainAll,
            onlyLazy = isLazy && !isHybrid;

        if (!retUnwrapped && useLazy) {
          value = onlyLazy ? value : new LazyWrapper(this);
          var result = func.apply(value, args);
          result.__actions__.push({ 'func': thru, 'args': [interceptor], 'thisArg': undefined });
          return new LodashWrapper(result, chainAll);
        }
        if (isUnwrapped && onlyLazy) {
          return func.apply(this, args);
        }
        result = this.thru(interceptor);
        return isUnwrapped ? (isTaker ? result.value()[0] : result.value()) : result;
      };
    });

    // Add `Array` methods to `lodash.prototype`.
    arrayEach(['pop', 'push', 'shift', 'sort', 'splice', 'unshift'], function(methodName) {
      var func = arrayProto[methodName],
          chainName = /^(?:push|sort|unshift)$/.test(methodName) ? 'tap' : 'thru',
          retUnwrapped = /^(?:pop|shift)$/.test(methodName);

      lodash.prototype[methodName] = function() {
        var args = arguments;
        if (retUnwrapped && !this.__chain__) {
          var value = this.value();
          return func.apply(isArray(value) ? value : [], args);
        }
        return this[chainName](function(value) {
          return func.apply(isArray(value) ? value : [], args);
        });
      };
    });

    // Map minified method names to their real names.
    baseForOwn(LazyWrapper.prototype, function(func, methodName) {
      var lodashFunc = lodash[methodName];
      if (lodashFunc) {
        var key = lodashFunc.name + '';
        if (!hasOwnProperty.call(realNames, key)) {
          realNames[key] = [];
        }
        realNames[key].push({ 'name': methodName, 'func': lodashFunc });
      }
    });

    realNames[createHybrid(undefined, WRAP_BIND_KEY_FLAG).name] = [{
      'name': 'wrapper',
      'func': undefined
    }];

    // Add methods to `LazyWrapper`.
    LazyWrapper.prototype.clone = lazyClone;
    LazyWrapper.prototype.reverse = lazyReverse;
    LazyWrapper.prototype.value = lazyValue;

    // Add chain sequence methods to the `lodash` wrapper.
    lodash.prototype.at = wrapperAt;
    lodash.prototype.chain = wrapperChain;
    lodash.prototype.commit = wrapperCommit;
    lodash.prototype.next = wrapperNext;
    lodash.prototype.plant = wrapperPlant;
    lodash.prototype.reverse = wrapperReverse;
    lodash.prototype.toJSON = lodash.prototype.valueOf = lodash.prototype.value = wrapperValue;

    // Add lazy aliases.
    lodash.prototype.first = lodash.prototype.head;

    if (symIterator) {
      lodash.prototype[symIterator] = wrapperToIterator;
    }
    return lodash;
  });

  /*--------------------------------------------------------------------------*/

  // Export lodash.
  var _ = runInContext();

  // Some AMD build optimizers, like r.js, check for condition patterns like:
  if (typeof define == 'function' && typeof define.amd == 'object' && define.amd) {
    // Expose Lodash on the global object to prevent errors when Lodash is
    // loaded by a script tag in the presence of an AMD loader.
    // See http://requirejs.org/docs/errors.html#mismatch for more details.
    // Use `_.noConflict` to remove Lodash from the global object.
    root._ = _;

    // Define as an anonymous module so, through path mapping, it can be
    // referenced as the "underscore" module.
    define('lodash',[],function() {
      return _;
    });
  }
  // Check for `exports` after `define` in case a build optimizer adds it.
  else if (freeModule) {
    // Export for Node.js.
    (freeModule.exports = _)._ = _;
    // Export for CommonJS support.
    freeExports._ = _;
  }
  else {
    // Export to the global object.
    root._ = _;
  }
}.call(this));

/**
 * @license RequireJS text 2.0.14 Copyright (c) 2010-2014, The Dojo Foundation All Rights Reserved.
 * Available via the MIT or new BSD license.
 * see: http://github.com/requirejs/text for details
 */
/*jslint regexp: true */
/*global require, XMLHttpRequest, ActiveXObject,
  define, window, process, Packages,
  java, location, Components, FileUtils */

define('text',['module'], function (module) {
    'use strict';

    var text, fs, Cc, Ci, xpcIsWindows,
        progIds = ['Msxml2.XMLHTTP', 'Microsoft.XMLHTTP', 'Msxml2.XMLHTTP.4.0'],
        xmlRegExp = /^\s*<\?xml(\s)+version=[\'\"](\d)*.(\d)*[\'\"](\s)*\?>/im,
        bodyRegExp = /<body[^>]*>\s*([\s\S]+)\s*<\/body>/im,
        hasLocation = typeof location !== 'undefined' && location.href,
        defaultProtocol = hasLocation && location.protocol && location.protocol.replace(/\:/, ''),
        defaultHostName = hasLocation && location.hostname,
        defaultPort = hasLocation && (location.port || undefined),
        buildMap = {},
        masterConfig = (module.config && module.config()) || {};

    text = {
        version: '2.0.14',

        strip: function (content) {
            //Strips <?xml ...?> declarations so that external SVG and XML
            //documents can be added to a document without worry. Also, if the string
            //is an HTML document, only the part inside the body tag is returned.
            if (content) {
                content = content.replace(xmlRegExp, "");
                var matches = content.match(bodyRegExp);
                if (matches) {
                    content = matches[1];
                }
            } else {
                content = "";
            }
            return content;
        },

        jsEscape: function (content) {
            return content.replace(/(['\\])/g, '\\$1')
                .replace(/[\f]/g, "\\f")
                .replace(/[\b]/g, "\\b")
                .replace(/[\n]/g, "\\n")
                .replace(/[\t]/g, "\\t")
                .replace(/[\r]/g, "\\r")
                .replace(/[\u2028]/g, "\\u2028")
                .replace(/[\u2029]/g, "\\u2029");
        },

        createXhr: masterConfig.createXhr || function () {
            //Would love to dump the ActiveX crap in here. Need IE 6 to die first.
            var xhr, i, progId;
            if (typeof XMLHttpRequest !== "undefined") {
                return new XMLHttpRequest();
            } else if (typeof ActiveXObject !== "undefined") {
                for (i = 0; i < 3; i += 1) {
                    progId = progIds[i];
                    try {
                        xhr = new ActiveXObject(progId);
                    } catch (e) {}

                    if (xhr) {
                        progIds = [progId];  // so faster next time
                        break;
                    }
                }
            }

            return xhr;
        },

        /**
         * Parses a resource name into its component parts. Resource names
         * look like: module/name.ext!strip, where the !strip part is
         * optional.
         * @param {String} name the resource name
         * @returns {Object} with properties "moduleName", "ext" and "strip"
         * where strip is a boolean.
         */
        parseName: function (name) {
            var modName, ext, temp,
                strip = false,
                index = name.lastIndexOf("."),
                isRelative = name.indexOf('./') === 0 ||
                             name.indexOf('../') === 0;

            if (index !== -1 && (!isRelative || index > 1)) {
                modName = name.substring(0, index);
                ext = name.substring(index + 1);
            } else {
                modName = name;
            }

            temp = ext || modName;
            index = temp.indexOf("!");
            if (index !== -1) {
                //Pull off the strip arg.
                strip = temp.substring(index + 1) === "strip";
                temp = temp.substring(0, index);
                if (ext) {
                    ext = temp;
                } else {
                    modName = temp;
                }
            }

            return {
                moduleName: modName,
                ext: ext,
                strip: strip
            };
        },

        xdRegExp: /^((\w+)\:)?\/\/([^\/\\]+)/,

        /**
         * Is an URL on another domain. Only works for browser use, returns
         * false in non-browser environments. Only used to know if an
         * optimized .js version of a text resource should be loaded
         * instead.
         * @param {String} url
         * @returns Boolean
         */
        useXhr: function (url, protocol, hostname, port) {
            var uProtocol, uHostName, uPort,
                match = text.xdRegExp.exec(url);
            if (!match) {
                return true;
            }
            uProtocol = match[2];
            uHostName = match[3];

            uHostName = uHostName.split(':');
            uPort = uHostName[1];
            uHostName = uHostName[0];

            return (!uProtocol || uProtocol === protocol) &&
                   (!uHostName || uHostName.toLowerCase() === hostname.toLowerCase()) &&
                   ((!uPort && !uHostName) || uPort === port);
        },

        finishLoad: function (name, strip, content, onLoad) {
            content = strip ? text.strip(content) : content;
            if (masterConfig.isBuild) {
                buildMap[name] = content;
            }
            onLoad(content);
        },

        load: function (name, req, onLoad, config) {
            //Name has format: some.module.filext!strip
            //The strip part is optional.
            //if strip is present, then that means only get the string contents
            //inside a body tag in an HTML string. For XML/SVG content it means
            //removing the <?xml ...?> declarations so the content can be inserted
            //into the current doc without problems.

            // Do not bother with the work if a build and text will
            // not be inlined.
            if (config && config.isBuild && !config.inlineText) {
                onLoad();
                return;
            }

            masterConfig.isBuild = config && config.isBuild;

            var parsed = text.parseName(name),
                nonStripName = parsed.moduleName +
                    (parsed.ext ? '.' + parsed.ext : ''),
                url = req.toUrl(nonStripName),
                useXhr = (masterConfig.useXhr) ||
                         text.useXhr;

            // Do not load if it is an empty: url
            if (url.indexOf('empty:') === 0) {
                onLoad();
                return;
            }

            //Load the text. Use XHR if possible and in a browser.
            if (!hasLocation || useXhr(url, defaultProtocol, defaultHostName, defaultPort)) {
                text.get(url, function (content) {
                    text.finishLoad(name, parsed.strip, content, onLoad);
                }, function (err) {
                    if (onLoad.error) {
                        onLoad.error(err);
                    }
                });
            } else {
                //Need to fetch the resource across domains. Assume
                //the resource has been optimized into a JS module. Fetch
                //by the module name + extension, but do not include the
                //!strip part to avoid file system issues.
                req([nonStripName], function (content) {
                    text.finishLoad(parsed.moduleName + '.' + parsed.ext,
                                    parsed.strip, content, onLoad);
                });
            }
        },

        write: function (pluginName, moduleName, write, config) {
            if (buildMap.hasOwnProperty(moduleName)) {
                var content = text.jsEscape(buildMap[moduleName]);
                write.asModule(pluginName + "!" + moduleName,
                               "define(function () { return '" +
                                   content +
                               "';});\n");
            }
        },

        writeFile: function (pluginName, moduleName, req, write, config) {
            var parsed = text.parseName(moduleName),
                extPart = parsed.ext ? '.' + parsed.ext : '',
                nonStripName = parsed.moduleName + extPart,
                //Use a '.js' file name so that it indicates it is a
                //script that can be loaded across domains.
                fileName = req.toUrl(parsed.moduleName + extPart) + '.js';

            //Leverage own load() method to load plugin value, but only
            //write out values that do not have the strip argument,
            //to avoid any potential issues with ! in file names.
            text.load(nonStripName, req, function (value) {
                //Use own write() method to construct full module value.
                //But need to create shell that translates writeFile's
                //write() to the right interface.
                var textWrite = function (contents) {
                    return write(fileName, contents);
                };
                textWrite.asModule = function (moduleName, contents) {
                    return write.asModule(moduleName, fileName, contents);
                };

                text.write(pluginName, nonStripName, textWrite, config);
            }, config);
        }
    };

    if (masterConfig.env === 'node' || (!masterConfig.env &&
            typeof process !== "undefined" &&
            process.versions &&
            !!process.versions.node &&
            !process.versions['node-webkit'] &&
            !process.versions['atom-shell'])) {
        //Using special require.nodeRequire, something added by r.js.
        fs = require.nodeRequire('fs');

        text.get = function (url, callback, errback) {
            try {
                var file = fs.readFileSync(url, 'utf8');
                //Remove BOM (Byte Mark Order) from utf8 files if it is there.
                if (file[0] === '\uFEFF') {
                    file = file.substring(1);
                }
                callback(file);
            } catch (e) {
                if (errback) {
                    errback(e);
                }
            }
        };
    } else if (masterConfig.env === 'xhr' || (!masterConfig.env &&
            text.createXhr())) {
        text.get = function (url, callback, errback, headers) {
            var xhr = text.createXhr(), header;
            xhr.open('GET', url, true);

            //Allow plugins direct access to xhr headers
            if (headers) {
                for (header in headers) {
                    if (headers.hasOwnProperty(header)) {
                        xhr.setRequestHeader(header.toLowerCase(), headers[header]);
                    }
                }
            }

            //Allow overrides specified in config
            if (masterConfig.onXhr) {
                masterConfig.onXhr(xhr, url);
            }

            xhr.onreadystatechange = function (evt) {
                var status, err;
                //Do not explicitly handle errors, those should be
                //visible via console output in the browser.
                if (xhr.readyState === 4) {
                    status = xhr.status || 0;
                    if (status > 399 && status < 600) {
                        //An http 4xx or 5xx error. Signal an error.
                        err = new Error(url + ' HTTP status: ' + status);
                        err.xhr = xhr;
                        if (errback) {
                            errback(err);
                        }
                    } else {
                        callback(xhr.responseText);
                    }

                    if (masterConfig.onXhrComplete) {
                        masterConfig.onXhrComplete(xhr, url);
                    }
                }
            };
            xhr.send(null);
        };
    } else if (masterConfig.env === 'rhino' || (!masterConfig.env &&
            typeof Packages !== 'undefined' && typeof java !== 'undefined')) {
        //Why Java, why is this so awkward?
        text.get = function (url, callback) {
            var stringBuffer, line,
                encoding = "utf-8",
                file = new java.io.File(url),
                lineSeparator = java.lang.System.getProperty("line.separator"),
                input = new java.io.BufferedReader(new java.io.InputStreamReader(new java.io.FileInputStream(file), encoding)),
                content = '';
            try {
                stringBuffer = new java.lang.StringBuffer();
                line = input.readLine();

                // Byte Order Mark (BOM) - The Unicode Standard, version 3.0, page 324
                // http://www.unicode.org/faq/utf_bom.html

                // Note that when we use utf-8, the BOM should appear as "EF BB BF", but it doesn't due to this bug in the JDK:
                // http://bugs.sun.com/bugdatabase/view_bug.do?bug_id=4508058
                if (line && line.length() && line.charAt(0) === 0xfeff) {
                    // Eat the BOM, since we've already found the encoding on this file,
                    // and we plan to concatenating this buffer with others; the BOM should
                    // only appear at the top of a file.
                    line = line.substring(1);
                }

                if (line !== null) {
                    stringBuffer.append(line);
                }

                while ((line = input.readLine()) !== null) {
                    stringBuffer.append(lineSeparator);
                    stringBuffer.append(line);
                }
                //Make sure we return a JavaScript string and not a Java string.
                content = String(stringBuffer.toString()); //String
            } finally {
                input.close();
            }
            callback(content);
        };
    } else if (masterConfig.env === 'xpconnect' || (!masterConfig.env &&
            typeof Components !== 'undefined' && Components.classes &&
            Components.interfaces)) {
        //Avert your gaze!
        Cc = Components.classes;
        Ci = Components.interfaces;
        Components.utils['import']('resource://gre/modules/FileUtils.jsm');
        xpcIsWindows = ('@mozilla.org/windows-registry-key;1' in Cc);

        text.get = function (url, callback) {
            var inStream, convertStream, fileObj,
                readData = {};

            if (xpcIsWindows) {
                url = url.replace(/\//g, '\\');
            }

            fileObj = new FileUtils.File(url);

            //XPCOM, you so crazy
            try {
                inStream = Cc['@mozilla.org/network/file-input-stream;1']
                           .createInstance(Ci.nsIFileInputStream);
                inStream.init(fileObj, 1, 0, false);

                convertStream = Cc['@mozilla.org/intl/converter-input-stream;1']
                                .createInstance(Ci.nsIConverterInputStream);
                convertStream.init(inStream, "utf-8", inStream.available(),
                Ci.nsIConverterInputStream.DEFAULT_REPLACEMENT_CHARACTER);

                convertStream.readString(inStream.available(), readData);
                convertStream.close();
                inStream.close();
                callback(readData.value);
            } catch (e) {
                throw new Error((fileObj && fileObj.path || '') + ': ' + e);
            }
        };
    }
    return text;
});

/** @license
 * RequireJS plugin for loading JSON files
 * - depends on Text plugin and it was HEAVILY "inspired" by it as well.
 * Author: Miller Medeiros
 * Version: 0.4.0 (2014/04/10)
 * Released under the MIT license
 */
define('json',['text'], function(text){

    var CACHE_BUST_QUERY_PARAM = 'bust',
        CACHE_BUST_FLAG = '!bust',
        jsonParse = (typeof JSON !== 'undefined' && typeof JSON.parse === 'function')? JSON.parse : function(val){
            return eval('('+ val +')'); //quick and dirty
        },
        buildMap = {};

    function cacheBust(url){
        url = url.replace(CACHE_BUST_FLAG, '');
        url += (url.indexOf('?') < 0)? '?' : '&';
        return url + CACHE_BUST_QUERY_PARAM +'='+ Math.round(2147483647 * Math.random());
    }

    //API
    return {

        load : function(name, req, onLoad, config) {
            if (( config.isBuild && (config.inlineJSON === false || name.indexOf(CACHE_BUST_QUERY_PARAM +'=') !== -1)) || (req.toUrl(name).indexOf('empty:') === 0)) {
                //avoid inlining cache busted JSON or if inlineJSON:false
                //and don't inline files marked as empty!
                onLoad(null);
            } else {
                text.get(req.toUrl(name), function(data){
                    if (config.isBuild) {
                        buildMap[name] = data;
                        onLoad(data);
                    } else {
                        onLoad(jsonParse(data));
                    }
                },
                    onLoad.error, {
                        accept: 'application/json'
                    }
                );
            }
        },

        normalize : function (name, normalize) {
            // used normalize to avoid caching references to a "cache busted" request
            if (name.indexOf(CACHE_BUST_FLAG) !== -1) {
                name = cacheBust(name);
            }
            // resolve any relative paths
            return normalize(name);
        },

        //write method based on RequireJS official text plugin by James Burke
        //https://github.com/jrburke/requirejs/blob/master/text.js
        write : function(pluginName, moduleName, write){
            if(moduleName in buildMap){
                var content = buildMap[moduleName];
                write('define("'+ pluginName +'!'+ moduleName +'", function(){ return '+ content +';});\n');
            }
        }

    };
});


define("json!etc/modules.json", function(){ return {
	"Gigaset_Theme": {
		"package": "rebrush",
		"active": true
	},

	"Gigaset_CSP": {
		"package": "gigaset_csp",
		"active": true
	},

	"Gigaset_StockNotification": {
		"package": "gigaset_stocknotification",
		"active": true
	},

	"Cartware_Checkout": {
		"package": "cartware_checkout",
		"active": true
	},

	"Gigaset_ContentBridge": {
		"package": "gigaset_contentbridge",
		"active": true
	},

	"Gigaset_ShoppingCodes": {
		"package": "gigaset_shoppingcodes",
		"active": true
	}
}
;});

/*!
 * @overview es6-promise - a tiny implementation of Promises/A+.
 * @copyright Copyright (c) 2014 Yehuda Katz, Tom Dale, Stefan Penner and contributors (Conversion to ES6 API by Jake Archibald)
 * @license   Licensed under MIT license
 *            See https://raw.githubusercontent.com/jakearchibald/es6-promise/master/LICENSE
 * @version   2.1.1
 */

(function() {
    "use strict";
    function lib$es6$promise$utils$$objectOrFunction(x) {
      return typeof x === 'function' || (typeof x === 'object' && x !== null);
    }

    function lib$es6$promise$utils$$isFunction(x) {
      return typeof x === 'function';
    }

    function lib$es6$promise$utils$$isMaybeThenable(x) {
      return typeof x === 'object' && x !== null;
    }

    var lib$es6$promise$utils$$_isArray;
    if (!Array.isArray) {
      lib$es6$promise$utils$$_isArray = function (x) {
        return Object.prototype.toString.call(x) === '[object Array]';
      };
    } else {
      lib$es6$promise$utils$$_isArray = Array.isArray;
    }

    var lib$es6$promise$utils$$isArray = lib$es6$promise$utils$$_isArray;
    var lib$es6$promise$asap$$len = 0;
    var lib$es6$promise$asap$$toString = {}.toString;
    var lib$es6$promise$asap$$vertxNext;
    function lib$es6$promise$asap$$asap(callback, arg) {
      lib$es6$promise$asap$$queue[lib$es6$promise$asap$$len] = callback;
      lib$es6$promise$asap$$queue[lib$es6$promise$asap$$len + 1] = arg;
      lib$es6$promise$asap$$len += 2;
      if (lib$es6$promise$asap$$len === 2) {
        // If len is 2, that means that we need to schedule an async flush.
        // If additional callbacks are queued before the queue is flushed, they
        // will be processed by this flush that we are scheduling.
        lib$es6$promise$asap$$scheduleFlush();
      }
    }

    var lib$es6$promise$asap$$default = lib$es6$promise$asap$$asap;

    var lib$es6$promise$asap$$browserWindow = (typeof window !== 'undefined') ? window : undefined;
    var lib$es6$promise$asap$$browserGlobal = lib$es6$promise$asap$$browserWindow || {};
    var lib$es6$promise$asap$$BrowserMutationObserver = lib$es6$promise$asap$$browserGlobal.MutationObserver || lib$es6$promise$asap$$browserGlobal.WebKitMutationObserver;
    var lib$es6$promise$asap$$isNode = typeof process !== 'undefined' && {}.toString.call(process) === '[object process]';

    // test for web worker but not in IE10
    var lib$es6$promise$asap$$isWorker = typeof Uint8ClampedArray !== 'undefined' &&
      typeof importScripts !== 'undefined' &&
      typeof MessageChannel !== 'undefined';

    // node
    function lib$es6$promise$asap$$useNextTick() {
      var nextTick = process.nextTick;
      // node version 0.10.x displays a deprecation warning when nextTick is used recursively
      // setImmediate should be used instead instead
      var version = process.versions.node.match(/^(?:(\d+)\.)?(?:(\d+)\.)?(\*|\d+)$/);
      if (Array.isArray(version) && version[1] === '0' && version[2] === '10') {
        nextTick = setImmediate;
      }
      return function() {
        nextTick(lib$es6$promise$asap$$flush);
      };
    }

    // vertx
    function lib$es6$promise$asap$$useVertxTimer() {
      return function() {
        lib$es6$promise$asap$$vertxNext(lib$es6$promise$asap$$flush);
      };
    }

    function lib$es6$promise$asap$$useMutationObserver() {
      var iterations = 0;
      var observer = new lib$es6$promise$asap$$BrowserMutationObserver(lib$es6$promise$asap$$flush);
      var node = document.createTextNode('');
      observer.observe(node, { characterData: true });

      return function() {
        node.data = (iterations = ++iterations % 2);
      };
    }

    // web worker
    function lib$es6$promise$asap$$useMessageChannel() {
      var channel = new MessageChannel();
      channel.port1.onmessage = lib$es6$promise$asap$$flush;
      return function () {
        channel.port2.postMessage(0);
      };
    }

    function lib$es6$promise$asap$$useSetTimeout() {
      return function() {
        setTimeout(lib$es6$promise$asap$$flush, 1);
      };
    }

    var lib$es6$promise$asap$$queue = new Array(1000);
    function lib$es6$promise$asap$$flush() {
      for (var i = 0; i < lib$es6$promise$asap$$len; i+=2) {
        var callback = lib$es6$promise$asap$$queue[i];
        var arg = lib$es6$promise$asap$$queue[i+1];

        callback(arg);

        lib$es6$promise$asap$$queue[i] = undefined;
        lib$es6$promise$asap$$queue[i+1] = undefined;
      }

      lib$es6$promise$asap$$len = 0;
    }

    function lib$es6$promise$asap$$attemptVertex() {
      try {
        var r = require;
        var vertx = r('vertx');
        lib$es6$promise$asap$$vertxNext = vertx.runOnLoop || vertx.runOnContext;
        return lib$es6$promise$asap$$useVertxTimer();
      } catch(e) {
        return lib$es6$promise$asap$$useSetTimeout();
      }
    }

    var lib$es6$promise$asap$$scheduleFlush;
    // Decide what async method to use to triggering processing of queued callbacks:
    if (lib$es6$promise$asap$$isNode) {
      lib$es6$promise$asap$$scheduleFlush = lib$es6$promise$asap$$useNextTick();
    } else if (lib$es6$promise$asap$$BrowserMutationObserver) {
      lib$es6$promise$asap$$scheduleFlush = lib$es6$promise$asap$$useMutationObserver();
    } else if (lib$es6$promise$asap$$isWorker) {
      lib$es6$promise$asap$$scheduleFlush = lib$es6$promise$asap$$useMessageChannel();
    } else if (lib$es6$promise$asap$$browserWindow === undefined && typeof require === 'function') {
      lib$es6$promise$asap$$scheduleFlush = lib$es6$promise$asap$$attemptVertex();
    } else {
      lib$es6$promise$asap$$scheduleFlush = lib$es6$promise$asap$$useSetTimeout();
    }

    function lib$es6$promise$$internal$$noop() {}

    var lib$es6$promise$$internal$$PENDING   = void 0;
    var lib$es6$promise$$internal$$FULFILLED = 1;
    var lib$es6$promise$$internal$$REJECTED  = 2;

    var lib$es6$promise$$internal$$GET_THEN_ERROR = new lib$es6$promise$$internal$$ErrorObject();

    function lib$es6$promise$$internal$$selfFullfillment() {
      return new TypeError("You cannot resolve a promise with itself");
    }

    function lib$es6$promise$$internal$$cannotReturnOwn() {
      return new TypeError('A promises callback cannot return that same promise.');
    }

    function lib$es6$promise$$internal$$getThen(promise) {
      try {
        return promise.then;
      } catch(error) {
        lib$es6$promise$$internal$$GET_THEN_ERROR.error = error;
        return lib$es6$promise$$internal$$GET_THEN_ERROR;
      }
    }

    function lib$es6$promise$$internal$$tryThen(then, value, fulfillmentHandler, rejectionHandler) {
      try {
        then.call(value, fulfillmentHandler, rejectionHandler);
      } catch(e) {
        return e;
      }
    }

    function lib$es6$promise$$internal$$handleForeignThenable(promise, thenable, then) {
       lib$es6$promise$asap$$default(function(promise) {
        var sealed = false;
        var error = lib$es6$promise$$internal$$tryThen(then, thenable, function(value) {
          if (sealed) { return; }
          sealed = true;
          if (thenable !== value) {
            lib$es6$promise$$internal$$resolve(promise, value);
          } else {
            lib$es6$promise$$internal$$fulfill(promise, value);
          }
        }, function(reason) {
          if (sealed) { return; }
          sealed = true;

          lib$es6$promise$$internal$$reject(promise, reason);
        }, 'Settle: ' + (promise._label || ' unknown promise'));

        if (!sealed && error) {
          sealed = true;
          lib$es6$promise$$internal$$reject(promise, error);
        }
      }, promise);
    }

    function lib$es6$promise$$internal$$handleOwnThenable(promise, thenable) {
      if (thenable._state === lib$es6$promise$$internal$$FULFILLED) {
        lib$es6$promise$$internal$$fulfill(promise, thenable._result);
      } else if (thenable._state === lib$es6$promise$$internal$$REJECTED) {
        lib$es6$promise$$internal$$reject(promise, thenable._result);
      } else {
        lib$es6$promise$$internal$$subscribe(thenable, undefined, function(value) {
          lib$es6$promise$$internal$$resolve(promise, value);
        }, function(reason) {
          lib$es6$promise$$internal$$reject(promise, reason);
        });
      }
    }

    function lib$es6$promise$$internal$$handleMaybeThenable(promise, maybeThenable) {
      if (maybeThenable.constructor === promise.constructor) {
        lib$es6$promise$$internal$$handleOwnThenable(promise, maybeThenable);
      } else {
        var then = lib$es6$promise$$internal$$getThen(maybeThenable);

        if (then === lib$es6$promise$$internal$$GET_THEN_ERROR) {
          lib$es6$promise$$internal$$reject(promise, lib$es6$promise$$internal$$GET_THEN_ERROR.error);
        } else if (then === undefined) {
          lib$es6$promise$$internal$$fulfill(promise, maybeThenable);
        } else if (lib$es6$promise$utils$$isFunction(then)) {
          lib$es6$promise$$internal$$handleForeignThenable(promise, maybeThenable, then);
        } else {
          lib$es6$promise$$internal$$fulfill(promise, maybeThenable);
        }
      }
    }

    function lib$es6$promise$$internal$$resolve(promise, value) {
      if (promise === value) {
        lib$es6$promise$$internal$$reject(promise, lib$es6$promise$$internal$$selfFullfillment());
      } else if (lib$es6$promise$utils$$objectOrFunction(value)) {
        lib$es6$promise$$internal$$handleMaybeThenable(promise, value);
      } else {
        lib$es6$promise$$internal$$fulfill(promise, value);
      }
    }

    function lib$es6$promise$$internal$$publishRejection(promise) {
      if (promise._onerror) {
        promise._onerror(promise._result);
      }

      lib$es6$promise$$internal$$publish(promise);
    }

    function lib$es6$promise$$internal$$fulfill(promise, value) {
      if (promise._state !== lib$es6$promise$$internal$$PENDING) { return; }

      promise._result = value;
      promise._state = lib$es6$promise$$internal$$FULFILLED;

      if (promise._subscribers.length !== 0) {
        lib$es6$promise$asap$$default(lib$es6$promise$$internal$$publish, promise);
      }
    }

    function lib$es6$promise$$internal$$reject(promise, reason) {
      if (promise._state !== lib$es6$promise$$internal$$PENDING) { return; }
      promise._state = lib$es6$promise$$internal$$REJECTED;
      promise._result = reason;

      lib$es6$promise$asap$$default(lib$es6$promise$$internal$$publishRejection, promise);
    }

    function lib$es6$promise$$internal$$subscribe(parent, child, onFulfillment, onRejection) {
      var subscribers = parent._subscribers;
      var length = subscribers.length;

      parent._onerror = null;

      subscribers[length] = child;
      subscribers[length + lib$es6$promise$$internal$$FULFILLED] = onFulfillment;
      subscribers[length + lib$es6$promise$$internal$$REJECTED]  = onRejection;

      if (length === 0 && parent._state) {
        lib$es6$promise$asap$$default(lib$es6$promise$$internal$$publish, parent);
      }
    }

    function lib$es6$promise$$internal$$publish(promise) {
      var subscribers = promise._subscribers;
      var settled = promise._state;

      if (subscribers.length === 0) { return; }

      var child, callback, detail = promise._result;

      for (var i = 0; i < subscribers.length; i += 3) {
        child = subscribers[i];
        callback = subscribers[i + settled];

        if (child) {
          lib$es6$promise$$internal$$invokeCallback(settled, child, callback, detail);
        } else {
          callback(detail);
        }
      }

      promise._subscribers.length = 0;
    }

    function lib$es6$promise$$internal$$ErrorObject() {
      this.error = null;
    }

    var lib$es6$promise$$internal$$TRY_CATCH_ERROR = new lib$es6$promise$$internal$$ErrorObject();

    function lib$es6$promise$$internal$$tryCatch(callback, detail) {
      try {
        return callback(detail);
      } catch(e) {
        lib$es6$promise$$internal$$TRY_CATCH_ERROR.error = e;
        return lib$es6$promise$$internal$$TRY_CATCH_ERROR;
      }
    }

    function lib$es6$promise$$internal$$invokeCallback(settled, promise, callback, detail) {
      var hasCallback = lib$es6$promise$utils$$isFunction(callback),
          value, error, succeeded, failed;

      if (hasCallback) {
        value = lib$es6$promise$$internal$$tryCatch(callback, detail);

        if (value === lib$es6$promise$$internal$$TRY_CATCH_ERROR) {
          failed = true;
          error = value.error;
          value = null;
        } else {
          succeeded = true;
        }

        if (promise === value) {
          lib$es6$promise$$internal$$reject(promise, lib$es6$promise$$internal$$cannotReturnOwn());
          return;
        }

      } else {
        value = detail;
        succeeded = true;
      }

      if (promise._state !== lib$es6$promise$$internal$$PENDING) {
        // noop
      } else if (hasCallback && succeeded) {
        lib$es6$promise$$internal$$resolve(promise, value);
      } else if (failed) {
        lib$es6$promise$$internal$$reject(promise, error);
      } else if (settled === lib$es6$promise$$internal$$FULFILLED) {
        lib$es6$promise$$internal$$fulfill(promise, value);
      } else if (settled === lib$es6$promise$$internal$$REJECTED) {
        lib$es6$promise$$internal$$reject(promise, value);
      }
    }

    function lib$es6$promise$$internal$$initializePromise(promise, resolver) {
      try {
        resolver(function resolvePromise(value){
          lib$es6$promise$$internal$$resolve(promise, value);
        }, function rejectPromise(reason) {
          lib$es6$promise$$internal$$reject(promise, reason);
        });
      } catch(e) {
        lib$es6$promise$$internal$$reject(promise, e);
      }
    }

    function lib$es6$promise$enumerator$$Enumerator(Constructor, input) {
      var enumerator = this;

      enumerator._instanceConstructor = Constructor;
      enumerator.promise = new Constructor(lib$es6$promise$$internal$$noop);

      if (enumerator._validateInput(input)) {
        enumerator._input     = input;
        enumerator.length     = input.length;
        enumerator._remaining = input.length;

        enumerator._init();

        if (enumerator.length === 0) {
          lib$es6$promise$$internal$$fulfill(enumerator.promise, enumerator._result);
        } else {
          enumerator.length = enumerator.length || 0;
          enumerator._enumerate();
          if (enumerator._remaining === 0) {
            lib$es6$promise$$internal$$fulfill(enumerator.promise, enumerator._result);
          }
        }
      } else {
        lib$es6$promise$$internal$$reject(enumerator.promise, enumerator._validationError());
      }
    }

    lib$es6$promise$enumerator$$Enumerator.prototype._validateInput = function(input) {
      return lib$es6$promise$utils$$isArray(input);
    };

    lib$es6$promise$enumerator$$Enumerator.prototype._validationError = function() {
      return new Error('Array Methods must be provided an Array');
    };

    lib$es6$promise$enumerator$$Enumerator.prototype._init = function() {
      this._result = new Array(this.length);
    };

    var lib$es6$promise$enumerator$$default = lib$es6$promise$enumerator$$Enumerator;

    lib$es6$promise$enumerator$$Enumerator.prototype._enumerate = function() {
      var enumerator = this;

      var length  = enumerator.length;
      var promise = enumerator.promise;
      var input   = enumerator._input;

      for (var i = 0; promise._state === lib$es6$promise$$internal$$PENDING && i < length; i++) {
        enumerator._eachEntry(input[i], i);
      }
    };

    lib$es6$promise$enumerator$$Enumerator.prototype._eachEntry = function(entry, i) {
      var enumerator = this;
      var c = enumerator._instanceConstructor;

      if (lib$es6$promise$utils$$isMaybeThenable(entry)) {
        if (entry.constructor === c && entry._state !== lib$es6$promise$$internal$$PENDING) {
          entry._onerror = null;
          enumerator._settledAt(entry._state, i, entry._result);
        } else {
          enumerator._willSettleAt(c.resolve(entry), i);
        }
      } else {
        enumerator._remaining--;
        enumerator._result[i] = entry;
      }
    };

    lib$es6$promise$enumerator$$Enumerator.prototype._settledAt = function(state, i, value) {
      var enumerator = this;
      var promise = enumerator.promise;

      if (promise._state === lib$es6$promise$$internal$$PENDING) {
        enumerator._remaining--;

        if (state === lib$es6$promise$$internal$$REJECTED) {
          lib$es6$promise$$internal$$reject(promise, value);
        } else {
          enumerator._result[i] = value;
        }
      }

      if (enumerator._remaining === 0) {
        lib$es6$promise$$internal$$fulfill(promise, enumerator._result);
      }
    };

    lib$es6$promise$enumerator$$Enumerator.prototype._willSettleAt = function(promise, i) {
      var enumerator = this;

      lib$es6$promise$$internal$$subscribe(promise, undefined, function(value) {
        enumerator._settledAt(lib$es6$promise$$internal$$FULFILLED, i, value);
      }, function(reason) {
        enumerator._settledAt(lib$es6$promise$$internal$$REJECTED, i, reason);
      });
    };
    function lib$es6$promise$promise$all$$all(entries) {
      return new lib$es6$promise$enumerator$$default(this, entries).promise;
    }
    var lib$es6$promise$promise$all$$default = lib$es6$promise$promise$all$$all;
    function lib$es6$promise$promise$race$$race(entries) {
      /*jshint validthis:true */
      var Constructor = this;

      var promise = new Constructor(lib$es6$promise$$internal$$noop);

      if (!lib$es6$promise$utils$$isArray(entries)) {
        lib$es6$promise$$internal$$reject(promise, new TypeError('You must pass an array to race.'));
        return promise;
      }

      var length = entries.length;

      function onFulfillment(value) {
        lib$es6$promise$$internal$$resolve(promise, value);
      }

      function onRejection(reason) {
        lib$es6$promise$$internal$$reject(promise, reason);
      }

      for (var i = 0; promise._state === lib$es6$promise$$internal$$PENDING && i < length; i++) {
        lib$es6$promise$$internal$$subscribe(Constructor.resolve(entries[i]), undefined, onFulfillment, onRejection);
      }

      return promise;
    }
    var lib$es6$promise$promise$race$$default = lib$es6$promise$promise$race$$race;
    function lib$es6$promise$promise$resolve$$resolve(object) {
      /*jshint validthis:true */
      var Constructor = this;

      if (object && typeof object === 'object' && object.constructor === Constructor) {
        return object;
      }

      var promise = new Constructor(lib$es6$promise$$internal$$noop);
      lib$es6$promise$$internal$$resolve(promise, object);
      return promise;
    }
    var lib$es6$promise$promise$resolve$$default = lib$es6$promise$promise$resolve$$resolve;
    function lib$es6$promise$promise$reject$$reject(reason) {
      /*jshint validthis:true */
      var Constructor = this;
      var promise = new Constructor(lib$es6$promise$$internal$$noop);
      lib$es6$promise$$internal$$reject(promise, reason);
      return promise;
    }
    var lib$es6$promise$promise$reject$$default = lib$es6$promise$promise$reject$$reject;

    var lib$es6$promise$promise$$counter = 0;

    function lib$es6$promise$promise$$needsResolver() {
      throw new TypeError('You must pass a resolver function as the first argument to the promise constructor');
    }

    function lib$es6$promise$promise$$needsNew() {
      throw new TypeError("Failed to construct 'Promise': Please use the 'new' operator, this object constructor cannot be called as a function.");
    }

    var lib$es6$promise$promise$$default = lib$es6$promise$promise$$Promise;
    /**
      Promise objects represent the eventual result of an asynchronous operation. The
      primary way of interacting with a promise is through its `then` method, which
      registers callbacks to receive either a promise’s eventual value or the reason
      why the promise cannot be fulfilled.

      Terminology
      -----------

      - `promise` is an object or function with a `then` method whose behavior conforms to this specification.
      - `thenable` is an object or function that defines a `then` method.
      - `value` is any legal JavaScript value (including undefined, a thenable, or a promise).
      - `exception` is a value that is thrown using the throw statement.
      - `reason` is a value that indicates why a promise was rejected.
      - `settled` the final resting state of a promise, fulfilled or rejected.

      A promise can be in one of three states: pending, fulfilled, or rejected.

      Promises that are fulfilled have a fulfillment value and are in the fulfilled
      state.  Promises that are rejected have a rejection reason and are in the
      rejected state.  A fulfillment value is never a thenable.

      Promises can also be said to *resolve* a value.  If this value is also a
      promise, then the original promise's settled state will match the value's
      settled state.  So a promise that *resolves* a promise that rejects will
      itself reject, and a promise that *resolves* a promise that fulfills will
      itself fulfill.


      Basic Usage:
      ------------

      ```js
      var promise = new Promise(function(resolve, reject) {
        // on success
        resolve(value);

        // on failure
        reject(reason);
      });

      promise.then(function(value) {
        // on fulfillment
      }, function(reason) {
        // on rejection
      });
      ```

      Advanced Usage:
      ---------------

      Promises shine when abstracting away asynchronous interactions such as
      `XMLHttpRequest`s.

      ```js
      function getJSON(url) {
        return new Promise(function(resolve, reject){
          var xhr = new XMLHttpRequest();

          xhr.open('GET', url);
          xhr.onreadystatechange = handler;
          xhr.responseType = 'json';
          xhr.setRequestHeader('Accept', 'application/json');
          xhr.send();

          function handler() {
            if (this.readyState === this.DONE) {
              if (this.status === 200) {
                resolve(this.response);
              } else {
                reject(new Error('getJSON: `' + url + '` failed with status: [' + this.status + ']'));
              }
            }
          };
        });
      }

      getJSON('/posts.json').then(function(json) {
        // on fulfillment
      }, function(reason) {
        // on rejection
      });
      ```

      Unlike callbacks, promises are great composable primitives.

      ```js
      Promise.all([
        getJSON('/posts'),
        getJSON('/comments')
      ]).then(function(values){
        values[0] // => postsJSON
        values[1] // => commentsJSON

        return values;
      });
      ```

      @class Promise
      @param {function} resolver
      Useful for tooling.
      @constructor
    */
    function lib$es6$promise$promise$$Promise(resolver) {
      this._id = lib$es6$promise$promise$$counter++;
      this._state = undefined;
      this._result = undefined;
      this._subscribers = [];

      if (lib$es6$promise$$internal$$noop !== resolver) {
        if (!lib$es6$promise$utils$$isFunction(resolver)) {
          lib$es6$promise$promise$$needsResolver();
        }

        if (!(this instanceof lib$es6$promise$promise$$Promise)) {
          lib$es6$promise$promise$$needsNew();
        }

        lib$es6$promise$$internal$$initializePromise(this, resolver);
      }
    }

    lib$es6$promise$promise$$Promise.all = lib$es6$promise$promise$all$$default;
    lib$es6$promise$promise$$Promise.race = lib$es6$promise$promise$race$$default;
    lib$es6$promise$promise$$Promise.resolve = lib$es6$promise$promise$resolve$$default;
    lib$es6$promise$promise$$Promise.reject = lib$es6$promise$promise$reject$$default;

    lib$es6$promise$promise$$Promise.prototype = {
      constructor: lib$es6$promise$promise$$Promise,

    /**
      The primary way of interacting with a promise is through its `then` method,
      which registers callbacks to receive either a promise's eventual value or the
      reason why the promise cannot be fulfilled.

      ```js
      findUser().then(function(user){
        // user is available
      }, function(reason){
        // user is unavailable, and you are given the reason why
      });
      ```

      Chaining
      --------

      The return value of `then` is itself a promise.  This second, 'downstream'
      promise is resolved with the return value of the first promise's fulfillment
      or rejection handler, or rejected if the handler throws an exception.

      ```js
      findUser().then(function (user) {
        return user.name;
      }, function (reason) {
        return 'default name';
      }).then(function (userName) {
        // If `findUser` fulfilled, `userName` will be the user's name, otherwise it
        // will be `'default name'`
      });

      findUser().then(function (user) {
        throw new Error('Found user, but still unhappy');
      }, function (reason) {
        throw new Error('`findUser` rejected and we're unhappy');
      }).then(function (value) {
        // never reached
      }, function (reason) {
        // if `findUser` fulfilled, `reason` will be 'Found user, but still unhappy'.
        // If `findUser` rejected, `reason` will be '`findUser` rejected and we're unhappy'.
      });
      ```
      If the downstream promise does not specify a rejection handler, rejection reasons will be propagated further downstream.

      ```js
      findUser().then(function (user) {
        throw new PedagogicalException('Upstream error');
      }).then(function (value) {
        // never reached
      }).then(function (value) {
        // never reached
      }, function (reason) {
        // The `PedgagocialException` is propagated all the way down to here
      });
      ```

      Assimilation
      ------------

      Sometimes the value you want to propagate to a downstream promise can only be
      retrieved asynchronously. This can be achieved by returning a promise in the
      fulfillment or rejection handler. The downstream promise will then be pending
      until the returned promise is settled. This is called *assimilation*.

      ```js
      findUser().then(function (user) {
        return findCommentsByAuthor(user);
      }).then(function (comments) {
        // The user's comments are now available
      });
      ```

      If the assimliated promise rejects, then the downstream promise will also reject.

      ```js
      findUser().then(function (user) {
        return findCommentsByAuthor(user);
      }).then(function (comments) {
        // If `findCommentsByAuthor` fulfills, we'll have the value here
      }, function (reason) {
        // If `findCommentsByAuthor` rejects, we'll have the reason here
      });
      ```

      Simple Example
      --------------

      Synchronous Example

      ```javascript
      var result;

      try {
        result = findResult();
        // success
      } catch(reason) {
        // failure
      }
      ```

      Errback Example

      ```js
      findResult(function(result, err){
        if (err) {
          // failure
        } else {
          // success
        }
      });
      ```

      Promise Example;

      ```javascript
      findResult().then(function(result){
        // success
      }, function(reason){
        // failure
      });
      ```

      Advanced Example
      --------------

      Synchronous Example

      ```javascript
      var author, books;

      try {
        author = findAuthor();
        books  = findBooksByAuthor(author);
        // success
      } catch(reason) {
        // failure
      }
      ```

      Errback Example

      ```js

      function foundBooks(books) {

      }

      function failure(reason) {

      }

      findAuthor(function(author, err){
        if (err) {
          failure(err);
          // failure
        } else {
          try {
            findBoooksByAuthor(author, function(books, err) {
              if (err) {
                failure(err);
              } else {
                try {
                  foundBooks(books);
                } catch(reason) {
                  failure(reason);
                }
              }
            });
          } catch(error) {
            failure(err);
          }
          // success
        }
      });
      ```

      Promise Example;

      ```javascript
      findAuthor().
        then(findBooksByAuthor).
        then(function(books){
          // found books
      }).catch(function(reason){
        // something went wrong
      });
      ```

      @method then
      @param {Function} onFulfilled
      @param {Function} onRejected
      Useful for tooling.
      @return {Promise}
    */
      then: function(onFulfillment, onRejection) {
        var parent = this;
        var state = parent._state;

        if (state === lib$es6$promise$$internal$$FULFILLED && !onFulfillment || state === lib$es6$promise$$internal$$REJECTED && !onRejection) {
          return this;
        }

        var child = new this.constructor(lib$es6$promise$$internal$$noop);
        var result = parent._result;

        if (state) {
          var callback = arguments[state - 1];
          lib$es6$promise$asap$$default(function(){
            lib$es6$promise$$internal$$invokeCallback(state, child, callback, result);
          });
        } else {
          lib$es6$promise$$internal$$subscribe(parent, child, onFulfillment, onRejection);
        }

        return child;
      },

    /**
      `catch` is simply sugar for `then(undefined, onRejection)` which makes it the same
      as the catch block of a try/catch statement.

      ```js
      function findAuthor(){
        throw new Error('couldn't find that author');
      }

      // synchronous
      try {
        findAuthor();
      } catch(reason) {
        // something went wrong
      }

      // async with promises
      findAuthor().catch(function(reason){
        // something went wrong
      });
      ```

      @method catch
      @param {Function} onRejection
      Useful for tooling.
      @return {Promise}
    */
      'catch': function(onRejection) {
        return this.then(null, onRejection);
      }
    };
    function lib$es6$promise$polyfill$$polyfill() {
      var local;

      if (typeof global !== 'undefined') {
          local = global;
      } else if (typeof self !== 'undefined') {
          local = self;
      } else {
          try {
              local = Function('return this')();
          } catch (e) {
              throw new Error('polyfill failed because global object is unavailable in this environment');
          }
      }

      var P = local.Promise;

      if (P && Object.prototype.toString.call(P.resolve()) === '[object Promise]' && !P.cast) {
        return;
      }

      local.Promise = lib$es6$promise$promise$$default;
    }
    var lib$es6$promise$polyfill$$default = lib$es6$promise$polyfill$$polyfill;

    var lib$es6$promise$umd$$ES6Promise = {
      'Promise': lib$es6$promise$promise$$default,
      'polyfill': lib$es6$promise$polyfill$$default
    };

    /* global define:true module:true window: true */
    if (typeof define === 'function' && define['amd']) {
      define('es6-promise',[],function() { return lib$es6$promise$umd$$ES6Promise; });
    } else if (typeof module !== 'undefined' && module['exports']) {
      module['exports'] = lib$es6$promise$umd$$ES6Promise;
    } else if (typeof this !== 'undefined') {
      this['ES6Promise'] = lib$es6$promise$umd$$ES6Promise;
    }

    lib$es6$promise$polyfill$$default();
}).call(this);


/*!
 * Modernizr v2.8.3
 * www.modernizr.com
 *
 * Copyright (c) Faruk Ates, Paul Irish, Alex Sexton
 * Available under the BSD and MIT licenses: www.modernizr.com/license/
 */

/*
 * Modernizr tests which native CSS3 and HTML5 features are available in
 * the current UA and makes the results available to you in two ways:
 * as properties on a global Modernizr object, and as classes on the
 * <html> element. This information allows you to progressively enhance
 * your pages with a granular level of control over the experience.
 *
 * Modernizr has an optional (not included) conditional resource loader
 * called Modernizr.load(), based on Yepnope.js (yepnopejs.com).
 * To get a build that includes Modernizr.load(), as well as choosing
 * which tests to include, go to www.modernizr.com/download/
 *
 * Authors        Faruk Ates, Paul Irish, Alex Sexton
 * Contributors   Ryan Seddon, Ben Alman
 */

window.Modernizr = (function( window, document, undefined ) {

    var version = '2.8.3',

    Modernizr = {},

    /*>>cssclasses*/
    // option for enabling the HTML classes to be added
    enableClasses = true,
    /*>>cssclasses*/

    docElement = document.documentElement,

    /**
     * Create our "modernizr" element that we do most feature tests on.
     */
    mod = 'modernizr',
    modElem = document.createElement(mod),
    mStyle = modElem.style,

    /**
     * Create the input element for various Web Forms feature tests.
     */
    inputElem /*>>inputelem*/ = document.createElement('input') /*>>inputelem*/ ,

    /*>>smile*/
    smile = ':)',
    /*>>smile*/

    toString = {}.toString,

    // TODO :: make the prefixes more granular
    /*>>prefixes*/
    // List of property values to set for css tests. See ticket #21
    prefixes = ' -webkit- -moz- -o- -ms- '.split(' '),
    /*>>prefixes*/

    /*>>domprefixes*/
    // Following spec is to expose vendor-specific style properties as:
    //   elem.style.WebkitBorderRadius
    // and the following would be incorrect:
    //   elem.style.webkitBorderRadius

    // Webkit ghosts their properties in lowercase but Opera & Moz do not.
    // Microsoft uses a lowercase `ms` instead of the correct `Ms` in IE8+
    //   erik.eae.net/archives/2008/03/10/21.48.10/

    // More here: github.com/Modernizr/Modernizr/issues/issue/21
    omPrefixes = 'Webkit Moz O ms',

    cssomPrefixes = omPrefixes.split(' '),

    domPrefixes = omPrefixes.toLowerCase().split(' '),
    /*>>domprefixes*/

    /*>>ns*/
    ns = {'svg': 'http://www.w3.org/2000/svg'},
    /*>>ns*/

    tests = {},
    inputs = {},
    attrs = {},

    classes = [],

    slice = classes.slice,

    featureName, // used in testing loop


    /*>>teststyles*/
    // Inject element with style element and some CSS rules
    injectElementWithStyles = function( rule, callback, nodes, testnames ) {

      var style, ret, node, docOverflow,
          div = document.createElement('div'),
          // After page load injecting a fake body doesn't work so check if body exists
          body = document.body,
          // IE6 and 7 won't return offsetWidth or offsetHeight unless it's in the body element, so we fake it.
          fakeBody = body || document.createElement('body');

      if ( parseInt(nodes, 10) ) {
          // In order not to give false positives we create a node for each test
          // This also allows the method to scale for unspecified uses
          while ( nodes-- ) {
              node = document.createElement('div');
              node.id = testnames ? testnames[nodes] : mod + (nodes + 1);
              div.appendChild(node);
          }
      }

      // <style> elements in IE6-9 are considered 'NoScope' elements and therefore will be removed
      // when injected with innerHTML. To get around this you need to prepend the 'NoScope' element
      // with a 'scoped' element, in our case the soft-hyphen entity as it won't mess with our measurements.
      // msdn.microsoft.com/en-us/library/ms533897%28VS.85%29.aspx
      // Documents served as xml will throw if using &shy; so use xml friendly encoded version. See issue #277
      style = ['&#173;','<style id="s', mod, '">', rule, '</style>'].join('');
      div.id = mod;
      // IE6 will false positive on some tests due to the style element inside the test div somehow interfering offsetHeight, so insert it into body or fakebody.
      // Opera will act all quirky when injecting elements in documentElement when page is served as xml, needs fakebody too. #270
      (body ? div : fakeBody).innerHTML += style;
      fakeBody.appendChild(div);
      if ( !body ) {
          //avoid crashing IE8, if background image is used
          fakeBody.style.background = '';
          //Safari 5.13/5.1.4 OSX stops loading if ::-webkit-scrollbar is used and scrollbars are visible
          fakeBody.style.overflow = 'hidden';
          docOverflow = docElement.style.overflow;
          docElement.style.overflow = 'hidden';
          docElement.appendChild(fakeBody);
      }

      ret = callback(div, rule);
      // If this is done after page load we don't want to remove the body so check if body exists
      if ( !body ) {
          fakeBody.parentNode.removeChild(fakeBody);
          docElement.style.overflow = docOverflow;
      } else {
          div.parentNode.removeChild(div);
      }

      return !!ret;

    },
    /*>>teststyles*/

    /*>>mq*/
    // adapted from matchMedia polyfill
    // by Scott Jehl and Paul Irish
    // gist.github.com/786768
    testMediaQuery = function( mq ) {

      var matchMedia = window.matchMedia || window.msMatchMedia;
      if ( matchMedia ) {
        return matchMedia(mq) && matchMedia(mq).matches || false;
      }

      var bool;

      injectElementWithStyles('@media ' + mq + ' { #' + mod + ' { position: absolute; } }', function( node ) {
        bool = (window.getComputedStyle ?
                  getComputedStyle(node, null) :
                  node.currentStyle)['position'] == 'absolute';
      });

      return bool;

     },
     /*>>mq*/


    /*>>hasevent*/
    //
    // isEventSupported determines if a given element supports the given event
    // kangax.github.com/iseventsupported/
    //
    // The following results are known incorrects:
    //   Modernizr.hasEvent("webkitTransitionEnd", elem) // false negative
    //   Modernizr.hasEvent("textInput") // in Webkit. github.com/Modernizr/Modernizr/issues/333
    //   ...
    isEventSupported = (function() {

      var TAGNAMES = {
        'select': 'input', 'change': 'input',
        'submit': 'form', 'reset': 'form',
        'error': 'img', 'load': 'img', 'abort': 'img'
      };

      function isEventSupported( eventName, element ) {

        element = element || document.createElement(TAGNAMES[eventName] || 'div');
        eventName = 'on' + eventName;

        // When using `setAttribute`, IE skips "unload", WebKit skips "unload" and "resize", whereas `in` "catches" those
        var isSupported = eventName in element;

        if ( !isSupported ) {
          // If it has no `setAttribute` (i.e. doesn't implement Node interface), try generic element
          if ( !element.setAttribute ) {
            element = document.createElement('div');
          }
          if ( element.setAttribute && element.removeAttribute ) {
            element.setAttribute(eventName, '');
            isSupported = is(element[eventName], 'function');

            // If property was created, "remove it" (by setting value to `undefined`)
            if ( !is(element[eventName], 'undefined') ) {
              element[eventName] = undefined;
            }
            element.removeAttribute(eventName);
          }
        }

        element = null;
        return isSupported;
      }
      return isEventSupported;
    })(),
    /*>>hasevent*/

    // TODO :: Add flag for hasownprop ? didn't last time

    // hasOwnProperty shim by kangax needed for Safari 2.0 support
    _hasOwnProperty = ({}).hasOwnProperty, hasOwnProp;

    if ( !is(_hasOwnProperty, 'undefined') && !is(_hasOwnProperty.call, 'undefined') ) {
      hasOwnProp = function (object, property) {
        return _hasOwnProperty.call(object, property);
      };
    }
    else {
      hasOwnProp = function (object, property) { /* yes, this can give false positives/negatives, but most of the time we don't care about those */
        return ((property in object) && is(object.constructor.prototype[property], 'undefined'));
      };
    }

    // Adapted from ES5-shim https://github.com/kriskowal/es5-shim/blob/master/es5-shim.js
    // es5.github.com/#x15.3.4.5

    if (!Function.prototype.bind) {
      Function.prototype.bind = function bind(that) {

        var target = this;

        if (typeof target != "function") {
            throw new TypeError();
        }

        var args = slice.call(arguments, 1),
            bound = function () {

            if (this instanceof bound) {

              var F = function(){};
              F.prototype = target.prototype;
              var self = new F();

              var result = target.apply(
                  self,
                  args.concat(slice.call(arguments))
              );
              if (Object(result) === result) {
                  return result;
              }
              return self;

            } else {

              return target.apply(
                  that,
                  args.concat(slice.call(arguments))
              );

            }

        };

        return bound;
      };
    }

    /**
     * setCss applies given styles to the Modernizr DOM node.
     */
    function setCss( str ) {
        mStyle.cssText = str;
    }

    /**
     * setCssAll extrapolates all vendor-specific css strings.
     */
    function setCssAll( str1, str2 ) {
        return setCss(prefixes.join(str1 + ';') + ( str2 || '' ));
    }

    /**
     * is returns a boolean for if typeof obj is exactly type.
     */
    function is( obj, type ) {
        return typeof obj === type;
    }

    /**
     * contains returns a boolean for if substr is found within str.
     */
    function contains( str, substr ) {
        return !!~('' + str).indexOf(substr);
    }

    /*>>testprop*/

    // testProps is a generic CSS / DOM property test.

    // In testing support for a given CSS property, it's legit to test:
    //    `elem.style[styleName] !== undefined`
    // If the property is supported it will return an empty string,
    // if unsupported it will return undefined.

    // We'll take advantage of this quick test and skip setting a style
    // on our modernizr element, but instead just testing undefined vs
    // empty string.

    // Because the testing of the CSS property names (with "-", as
    // opposed to the camelCase DOM properties) is non-portable and
    // non-standard but works in WebKit and IE (but not Gecko or Opera),
    // we explicitly reject properties with dashes so that authors
    // developing in WebKit or IE first don't end up with
    // browser-specific content by accident.

    function testProps( props, prefixed ) {
        for ( var i in props ) {
            var prop = props[i];
            if ( !contains(prop, "-") && mStyle[prop] !== undefined ) {
                return prefixed == 'pfx' ? prop : true;
            }
        }
        return false;
    }
    /*>>testprop*/

    // TODO :: add testDOMProps
    /**
     * testDOMProps is a generic DOM property test; if a browser supports
     *   a certain property, it won't return undefined for it.
     */
    function testDOMProps( props, obj, elem ) {
        for ( var i in props ) {
            var item = obj[props[i]];
            if ( item !== undefined) {

                // return the property name as a string
                if (elem === false) return props[i];

                // let's bind a function
                if (is(item, 'function')){
                  // default to autobind unless override
                  return item.bind(elem || obj);
                }

                // return the unbound function or obj or value
                return item;
            }
        }
        return false;
    }

    /*>>testallprops*/
    /**
     * testPropsAll tests a list of DOM properties we want to check against.
     *   We specify literally ALL possible (known and/or likely) properties on
     *   the element including the non-vendor prefixed one, for forward-
     *   compatibility.
     */
    function testPropsAll( prop, prefixed, elem ) {

        var ucProp  = prop.charAt(0).toUpperCase() + prop.slice(1),
            props   = (prop + ' ' + cssomPrefixes.join(ucProp + ' ') + ucProp).split(' ');

        // did they call .prefixed('boxSizing') or are we just testing a prop?
        if(is(prefixed, "string") || is(prefixed, "undefined")) {
          return testProps(props, prefixed);

        // otherwise, they called .prefixed('requestAnimationFrame', window[, elem])
        } else {
          props = (prop + ' ' + (domPrefixes).join(ucProp + ' ') + ucProp).split(' ');
          return testDOMProps(props, prefixed, elem);
        }
    }
    /*>>testallprops*/


    /**
     * Tests
     * -----
     */

    // The *new* flexbox
    // dev.w3.org/csswg/css3-flexbox

    tests['flexbox'] = function() {
      return testPropsAll('flexWrap');
    };

    // The *old* flexbox
    // www.w3.org/TR/2009/WD-css3-flexbox-20090723/

    tests['flexboxlegacy'] = function() {
        return testPropsAll('boxDirection');
    };

    // On the S60 and BB Storm, getContext exists, but always returns undefined
    // so we actually have to call getContext() to verify
    // github.com/Modernizr/Modernizr/issues/issue/97/

    tests['canvas'] = function() {
        var elem = document.createElement('canvas');
        return !!(elem.getContext && elem.getContext('2d'));
    };

    tests['canvastext'] = function() {
        return !!(Modernizr['canvas'] && is(document.createElement('canvas').getContext('2d').fillText, 'function'));
    };

    // webk.it/70117 is tracking a legit WebGL feature detect proposal

    // We do a soft detect which may false positive in order to avoid
    // an expensive context creation: bugzil.la/732441

    tests['webgl'] = function() {
        return !!window.WebGLRenderingContext;
    };

    /*
     * The Modernizr.touch test only indicates if the browser supports
     *    touch events, which does not necessarily reflect a touchscreen
     *    device, as evidenced by tablets running Windows 7 or, alas,
     *    the Palm Pre / WebOS (touch) phones.
     *
     * Additionally, Chrome (desktop) used to lie about its support on this,
     *    but that has since been rectified: crbug.com/36415
     *
     * We also test for Firefox 4 Multitouch Support.
     *
     * For more info, see: modernizr.github.com/Modernizr/touch.html
     */

    tests['touch'] = function() {
        var bool;

        if(('ontouchstart' in window) || window.DocumentTouch && document instanceof DocumentTouch) {
          bool = true;
        } else {
          injectElementWithStyles(['@media (',prefixes.join('touch-enabled),('),mod,')','{#modernizr{top:9px;position:absolute}}'].join(''), function( node ) {
            bool = node.offsetTop === 9;
          });
        }

        return bool;
    };


    // geolocation is often considered a trivial feature detect...
    // Turns out, it's quite tricky to get right:
    //
    // Using !!navigator.geolocation does two things we don't want. It:
    //   1. Leaks memory in IE9: github.com/Modernizr/Modernizr/issues/513
    //   2. Disables page caching in WebKit: webk.it/43956
    //
    // Meanwhile, in Firefox < 8, an about:config setting could expose
    // a false positive that would throw an exception: bugzil.la/688158

    tests['geolocation'] = function() {
        return 'geolocation' in navigator;
    };


    tests['postmessage'] = function() {
      return !!window.postMessage;
    };


    // Chrome incognito mode used to throw an exception when using openDatabase
    // It doesn't anymore.
    tests['websqldatabase'] = function() {
      return !!window.openDatabase;
    };

    // Vendors had inconsistent prefixing with the experimental Indexed DB:
    // - Webkit's implementation is accessible through webkitIndexedDB
    // - Firefox shipped moz_indexedDB before FF4b9, but since then has been mozIndexedDB
    // For speed, we don't test the legacy (and beta-only) indexedDB
    tests['indexedDB'] = function() {
      return !!testPropsAll("indexedDB", window);
    };

    // documentMode logic from YUI to filter out IE8 Compat Mode
    //   which false positives.
    tests['hashchange'] = function() {
      return isEventSupported('hashchange', window) && (document.documentMode === undefined || document.documentMode > 7);
    };

    // Per 1.6:
    // This used to be Modernizr.historymanagement but the longer
    // name has been deprecated in favor of a shorter and property-matching one.
    // The old API is still available in 1.6, but as of 2.0 will throw a warning,
    // and in the first release thereafter disappear entirely.
    tests['history'] = function() {
      return !!(window.history && history.pushState);
    };

    tests['draganddrop'] = function() {
        var div = document.createElement('div');
        return ('draggable' in div) || ('ondragstart' in div && 'ondrop' in div);
    };

    // FF3.6 was EOL'ed on 4/24/12, but the ESR version of FF10
    // will be supported until FF19 (2/12/13), at which time, ESR becomes FF17.
    // FF10 still uses prefixes, so check for it until then.
    // for more ESR info, see: mozilla.org/en-US/firefox/organizations/faq/
    tests['websockets'] = function() {
        return 'WebSocket' in window || 'MozWebSocket' in window;
    };


    // css-tricks.com/rgba-browser-support/
    tests['rgba'] = function() {
        // Set an rgba() color and check the returned value

        setCss('background-color:rgba(150,255,150,.5)');

        return contains(mStyle.backgroundColor, 'rgba');
    };

    tests['hsla'] = function() {
        // Same as rgba(), in fact, browsers re-map hsla() to rgba() internally,
        //   except IE9 who retains it as hsla

        setCss('background-color:hsla(120,40%,100%,.5)');

        return contains(mStyle.backgroundColor, 'rgba') || contains(mStyle.backgroundColor, 'hsla');
    };

    tests['multiplebgs'] = function() {
        // Setting multiple images AND a color on the background shorthand property
        //  and then querying the style.background property value for the number of
        //  occurrences of "url(" is a reliable method for detecting ACTUAL support for this!

        setCss('background:url(https://),url(https://),red url(https://)');

        // If the UA supports multiple backgrounds, there should be three occurrences
        //   of the string "url(" in the return value for elemStyle.background

        return (/(url\s*\(.*?){3}/).test(mStyle.background);
    };



    // this will false positive in Opera Mini
    //   github.com/Modernizr/Modernizr/issues/396

    tests['backgroundsize'] = function() {
        return testPropsAll('backgroundSize');
    };

    tests['borderimage'] = function() {
        return testPropsAll('borderImage');
    };


    // Super comprehensive table about all the unique implementations of
    // border-radius: muddledramblings.com/table-of-css3-border-radius-compliance

    tests['borderradius'] = function() {
        return testPropsAll('borderRadius');
    };

    // WebOS unfortunately false positives on this test.
    tests['boxshadow'] = function() {
        return testPropsAll('boxShadow');
    };

    // FF3.0 will false positive on this test
    tests['textshadow'] = function() {
        return document.createElement('div').style.textShadow === '';
    };


    tests['opacity'] = function() {
        // Browsers that actually have CSS Opacity implemented have done so
        //  according to spec, which means their return values are within the
        //  range of [0.0,1.0] - including the leading zero.

        setCssAll('opacity:.55');

        // The non-literal . in this regex is intentional:
        //   German Chrome returns this value as 0,55
        // github.com/Modernizr/Modernizr/issues/#issue/59/comment/516632
        return (/^0.55$/).test(mStyle.opacity);
    };


    // Note, Android < 4 will pass this test, but can only animate
    //   a single property at a time
    //   goo.gl/v3V4Gp
    tests['cssanimations'] = function() {
        return testPropsAll('animationName');
    };


    tests['csscolumns'] = function() {
        return testPropsAll('columnCount');
    };


    tests['cssgradients'] = function() {
        /**
         * For CSS Gradients syntax, please see:
         * webkit.org/blog/175/introducing-css-gradients/
         * developer.mozilla.org/en/CSS/-moz-linear-gradient
         * developer.mozilla.org/en/CSS/-moz-radial-gradient
         * dev.w3.org/csswg/css3-images/#gradients-
         */

        var str1 = 'background-image:',
            str2 = 'gradient(linear,left top,right bottom,from(#9f9),to(white));',
            str3 = 'linear-gradient(left top,#9f9, white);';

        setCss(
             // legacy webkit syntax (FIXME: remove when syntax not in use anymore)
              (str1 + '-webkit- '.split(' ').join(str2 + str1) +
             // standard syntax             // trailing 'background-image:'
              prefixes.join(str3 + str1)).slice(0, -str1.length)
        );

        return contains(mStyle.backgroundImage, 'gradient');
    };


    tests['cssreflections'] = function() {
        return testPropsAll('boxReflect');
    };


    tests['csstransforms'] = function() {
        return !!testPropsAll('transform');
    };


    tests['csstransforms3d'] = function() {

        var ret = !!testPropsAll('perspective');

        // Webkit's 3D transforms are passed off to the browser's own graphics renderer.
        //   It works fine in Safari on Leopard and Snow Leopard, but not in Chrome in
        //   some conditions. As a result, Webkit typically recognizes the syntax but
        //   will sometimes throw a false positive, thus we must do a more thorough check:
        if ( ret && 'webkitPerspective' in docElement.style ) {

          // Webkit allows this media query to succeed only if the feature is enabled.
          // `@media (transform-3d),(-webkit-transform-3d){ ... }`
          injectElementWithStyles('@media (transform-3d),(-webkit-transform-3d){#modernizr{left:9px;position:absolute;height:3px;}}', function( node, rule ) {
            ret = node.offsetLeft === 9 && node.offsetHeight === 3;
          });
        }
        return ret;
    };


    tests['csstransitions'] = function() {
        return testPropsAll('transition');
    };


    /*>>fontface*/
    // @font-face detection routine by Diego Perini
    // javascript.nwbox.com/CSSSupport/

    // false positives:
    //   WebOS github.com/Modernizr/Modernizr/issues/342
    //   WP7   github.com/Modernizr/Modernizr/issues/538
    tests['fontface'] = function() {
        var bool;

        injectElementWithStyles('@font-face {font-family:"font";src:url("https://")}', function( node, rule ) {
          var style = document.getElementById('smodernizr'),
              sheet = style.sheet || style.styleSheet,
              cssText = sheet ? (sheet.cssRules && sheet.cssRules[0] ? sheet.cssRules[0].cssText : sheet.cssText || '') : '';

          bool = /src/i.test(cssText) && cssText.indexOf(rule.split(' ')[0]) === 0;
        });

        return bool;
    };
    /*>>fontface*/

    // CSS generated content detection
    tests['generatedcontent'] = function() {
        var bool;

        injectElementWithStyles(['#',mod,'{font:0/0 a}#',mod,':after{content:"',smile,'";visibility:hidden;font:3px/1 a}'].join(''), function( node ) {
          bool = node.offsetHeight >= 3;
        });

        return bool;
    };



    // These tests evaluate support of the video/audio elements, as well as
    // testing what types of content they support.
    //
    // We're using the Boolean constructor here, so that we can extend the value
    // e.g.  Modernizr.video     // true
    //       Modernizr.video.ogg // 'probably'
    //
    // Codec values from : github.com/NielsLeenheer/html5test/blob/9106a8/index.html#L845
    //                     thx to NielsLeenheer and zcorpan

    // Note: in some older browsers, "no" was a return value instead of empty string.
    //   It was live in FF3.5.0 and 3.5.1, but fixed in 3.5.2
    //   It was also live in Safari 4.0.0 - 4.0.4, but fixed in 4.0.5

    tests['video'] = function() {
        var elem = document.createElement('video'),
            bool = false;

        // IE9 Running on Windows Server SKU can cause an exception to be thrown, bug #224
        try {
            if ( bool = !!elem.canPlayType ) {
                bool      = new Boolean(bool);
                bool.ogg  = elem.canPlayType('video/ogg; codecs="theora"')      .replace(/^no$/,'');

                // Without QuickTime, this value will be `undefined`. github.com/Modernizr/Modernizr/issues/546
                bool.h264 = elem.canPlayType('video/mp4; codecs="avc1.42E01E"') .replace(/^no$/,'');

                bool.webm = elem.canPlayType('video/webm; codecs="vp8, vorbis"').replace(/^no$/,'');
            }

        } catch(e) { }

        return bool;
    };

    tests['audio'] = function() {
        var elem = document.createElement('audio'),
            bool = false;

        try {
            if ( bool = !!elem.canPlayType ) {
                bool      = new Boolean(bool);
                bool.ogg  = elem.canPlayType('audio/ogg; codecs="vorbis"').replace(/^no$/,'');
                bool.mp3  = elem.canPlayType('audio/mpeg;')               .replace(/^no$/,'');

                // Mimetypes accepted:
                //   developer.mozilla.org/En/Media_formats_supported_by_the_audio_and_video_elements
                //   bit.ly/iphoneoscodecs
                bool.wav  = elem.canPlayType('audio/wav; codecs="1"')     .replace(/^no$/,'');
                bool.m4a  = ( elem.canPlayType('audio/x-m4a;')            ||
                              elem.canPlayType('audio/aac;'))             .replace(/^no$/,'');
            }
        } catch(e) { }

        return bool;
    };


    // In FF4, if disabled, window.localStorage should === null.

    // Normally, we could not test that directly and need to do a
    //   `('localStorage' in window) && ` test first because otherwise Firefox will
    //   throw bugzil.la/365772 if cookies are disabled

    // Also in iOS5 Private Browsing mode, attempting to use localStorage.setItem
    // will throw the exception:
    //   QUOTA_EXCEEDED_ERRROR DOM Exception 22.
    // Peculiarly, getItem and removeItem calls do not throw.

    // Because we are forced to try/catch this, we'll go aggressive.

    // Just FWIW: IE8 Compat mode supports these features completely:
    //   www.quirksmode.org/dom/html5.html
    // But IE8 doesn't support either with local files

    tests['localstorage'] = function() {
        try {
            localStorage.setItem(mod, mod);
            localStorage.removeItem(mod);
            return true;
        } catch(e) {
            return false;
        }
    };

    tests['sessionstorage'] = function() {
        try {
            sessionStorage.setItem(mod, mod);
            sessionStorage.removeItem(mod);
            return true;
        } catch(e) {
            return false;
        }
    };


    tests['webworkers'] = function() {
        return !!window.Worker;
    };


    tests['applicationcache'] = function() {
        return !!window.applicationCache;
    };


    // Thanks to Erik Dahlstrom
    tests['svg'] = function() {
        return !!document.createElementNS && !!document.createElementNS(ns.svg, 'svg').createSVGRect;
    };

    // specifically for SVG inline in HTML, not within XHTML
    // test page: paulirish.com/demo/inline-svg
    tests['inlinesvg'] = function() {
      var div = document.createElement('div');
      div.innerHTML = '<svg/>';
      return (div.firstChild && div.firstChild.namespaceURI) == ns.svg;
    };

    // SVG SMIL animation
    tests['smil'] = function() {
        return !!document.createElementNS && /SVGAnimate/.test(toString.call(document.createElementNS(ns.svg, 'animate')));
    };

    // This test is only for clip paths in SVG proper, not clip paths on HTML content
    // demo: srufaculty.sru.edu/david.dailey/svg/newstuff/clipPath4.svg

    // However read the comments to dig into applying SVG clippaths to HTML content here:
    //   github.com/Modernizr/Modernizr/issues/213#issuecomment-1149491
    tests['svgclippaths'] = function() {
        return !!document.createElementNS && /SVGClipPath/.test(toString.call(document.createElementNS(ns.svg, 'clipPath')));
    };

    /*>>webforms*/
    // input features and input types go directly onto the ret object, bypassing the tests loop.
    // Hold this guy to execute in a moment.
    function webforms() {
        /*>>input*/
        // Run through HTML5's new input attributes to see if the UA understands any.
        // We're using f which is the <input> element created early on
        // Mike Taylr has created a comprehensive resource for testing these attributes
        //   when applied to all input types:
        //   miketaylr.com/code/input-type-attr.html
        // spec: www.whatwg.org/specs/web-apps/current-work/multipage/the-input-element.html#input-type-attr-summary

        // Only input placeholder is tested while textarea's placeholder is not.
        // Currently Safari 4 and Opera 11 have support only for the input placeholder
        // Both tests are available in feature-detects/forms-placeholder.js
        Modernizr['input'] = (function( props ) {
            for ( var i = 0, len = props.length; i < len; i++ ) {
                attrs[ props[i] ] = !!(props[i] in inputElem);
            }
            if (attrs.list){
              // safari false positive's on datalist: webk.it/74252
              // see also github.com/Modernizr/Modernizr/issues/146
              attrs.list = !!(document.createElement('datalist') && window.HTMLDataListElement);
            }
            return attrs;
        })('autocomplete autofocus list placeholder max min multiple pattern required step'.split(' '));
        /*>>input*/

        /*>>inputtypes*/
        // Run through HTML5's new input types to see if the UA understands any.
        //   This is put behind the tests runloop because it doesn't return a
        //   true/false like all the other tests; instead, it returns an object
        //   containing each input type with its corresponding true/false value

        // Big thanks to @miketaylr for the html5 forms expertise. miketaylr.com/
        Modernizr['inputtypes'] = (function(props) {

            for ( var i = 0, bool, inputElemType, defaultView, len = props.length; i < len; i++ ) {

                inputElem.setAttribute('type', inputElemType = props[i]);
                bool = inputElem.type !== 'text';

                // We first check to see if the type we give it sticks..
                // If the type does, we feed it a textual value, which shouldn't be valid.
                // If the value doesn't stick, we know there's input sanitization which infers a custom UI
                if ( bool ) {

                    inputElem.value         = smile;
                    inputElem.style.cssText = 'position:absolute;visibility:hidden;';

                    if ( /^range$/.test(inputElemType) && inputElem.style.WebkitAppearance !== undefined ) {

                      docElement.appendChild(inputElem);
                      defaultView = document.defaultView;

                      // Safari 2-4 allows the smiley as a value, despite making a slider
                      bool =  defaultView.getComputedStyle &&
                              defaultView.getComputedStyle(inputElem, null).WebkitAppearance !== 'textfield' &&
                              // Mobile android web browser has false positive, so must
                              // check the height to see if the widget is actually there.
                              (inputElem.offsetHeight !== 0);

                      docElement.removeChild(inputElem);

                    } else if ( /^(search|tel)$/.test(inputElemType) ){
                      // Spec doesn't define any special parsing or detectable UI
                      //   behaviors so we pass these through as true

                      // Interestingly, opera fails the earlier test, so it doesn't
                      //  even make it here.

                    } else if ( /^(url|email)$/.test(inputElemType) ) {
                      // Real url and email support comes with prebaked validation.
                      bool = inputElem.checkValidity && inputElem.checkValidity() === false;

                    } else {
                      // If the upgraded input compontent rejects the :) text, we got a winner
                      bool = inputElem.value != smile;
                    }
                }

                inputs[ props[i] ] = !!bool;
            }
            return inputs;
        })('search tel url email datetime date month week time datetime-local number range color'.split(' '));
        /*>>inputtypes*/
    }
    /*>>webforms*/


    // End of test definitions
    // -----------------------



    // Run through all tests and detect their support in the current UA.
    // todo: hypothetically we could be doing an array of tests and use a basic loop here.
    for ( var feature in tests ) {
        if ( hasOwnProp(tests, feature) ) {
            // run the test, throw the return value into the Modernizr,
            //   then based on that boolean, define an appropriate className
            //   and push it into an array of classes we'll join later.
            featureName  = feature.toLowerCase();
            Modernizr[featureName] = tests[feature]();

            classes.push((Modernizr[featureName] ? '' : 'no-') + featureName);
        }
    }

    /*>>webforms*/
    // input tests need to run.
    Modernizr.input || webforms();
    /*>>webforms*/


    /**
     * addTest allows the user to define their own feature tests
     * the result will be added onto the Modernizr object,
     * as well as an appropriate className set on the html element
     *
     * @param feature - String naming the feature
     * @param test - Function returning true if feature is supported, false if not
     */
     Modernizr.addTest = function ( feature, test ) {
       if ( typeof feature == 'object' ) {
         for ( var key in feature ) {
           if ( hasOwnProp( feature, key ) ) {
             Modernizr.addTest( key, feature[ key ] );
           }
         }
       } else {

         feature = feature.toLowerCase();

         if ( Modernizr[feature] !== undefined ) {
           // we're going to quit if you're trying to overwrite an existing test
           // if we were to allow it, we'd do this:
           //   var re = new RegExp("\\b(no-)?" + feature + "\\b");
           //   docElement.className = docElement.className.replace( re, '' );
           // but, no rly, stuff 'em.
           return Modernizr;
         }

         test = typeof test == 'function' ? test() : test;

         if (typeof enableClasses !== "undefined" && enableClasses) {
           docElement.className += ' ' + (test ? '' : 'no-') + feature;
         }
         Modernizr[feature] = test;

       }

       return Modernizr; // allow chaining.
     };


    // Reset modElem.cssText to nothing to reduce memory footprint.
    setCss('');
    modElem = inputElem = null;

    /*>>shiv*/
    /**
     * @preserve HTML5 Shiv prev3.7.1 | @afarkas @jdalton @jon_neal @rem | MIT/GPL2 Licensed
     */
    ;(function(window, document) {
        /*jshint evil:true */
        /** version */
        var version = '3.7.0';

        /** Preset options */
        var options = window.html5 || {};

        /** Used to skip problem elements */
        var reSkip = /^<|^(?:button|map|select|textarea|object|iframe|option|optgroup)$/i;

        /** Not all elements can be cloned in IE **/
        var saveClones = /^(?:a|b|code|div|fieldset|h1|h2|h3|h4|h5|h6|i|label|li|ol|p|q|span|strong|style|table|tbody|td|th|tr|ul)$/i;

        /** Detect whether the browser supports default html5 styles */
        var supportsHtml5Styles;

        /** Name of the expando, to work with multiple documents or to re-shiv one document */
        var expando = '_html5shiv';

        /** The id for the the documents expando */
        var expanID = 0;

        /** Cached data for each document */
        var expandoData = {};

        /** Detect whether the browser supports unknown elements */
        var supportsUnknownElements;

        (function() {
          try {
            var a = document.createElement('a');
            a.innerHTML = '<xyz></xyz>';
            //if the hidden property is implemented we can assume, that the browser supports basic HTML5 Styles
            supportsHtml5Styles = ('hidden' in a);

            supportsUnknownElements = a.childNodes.length == 1 || (function() {
              // assign a false positive if unable to shiv
              (document.createElement)('a');
              var frag = document.createDocumentFragment();
              return (
                typeof frag.cloneNode == 'undefined' ||
                typeof frag.createDocumentFragment == 'undefined' ||
                typeof frag.createElement == 'undefined'
              );
            }());
          } catch(e) {
            // assign a false positive if detection fails => unable to shiv
            supportsHtml5Styles = true;
            supportsUnknownElements = true;
          }

        }());

        /*--------------------------------------------------------------------------*/

        /**
         * Creates a style sheet with the given CSS text and adds it to the document.
         * @private
         * @param {Document} ownerDocument The document.
         * @param {String} cssText The CSS text.
         * @returns {StyleSheet} The style element.
         */
        function addStyleSheet(ownerDocument, cssText) {
          var p = ownerDocument.createElement('p'),
          parent = ownerDocument.getElementsByTagName('head')[0] || ownerDocument.documentElement;

          p.innerHTML = 'x<style>' + cssText + '</style>';
          return parent.insertBefore(p.lastChild, parent.firstChild);
        }

        /**
         * Returns the value of `html5.elements` as an array.
         * @private
         * @returns {Array} An array of shived element node names.
         */
        function getElements() {
          var elements = html5.elements;
          return typeof elements == 'string' ? elements.split(' ') : elements;
        }

        /**
         * Returns the data associated to the given document
         * @private
         * @param {Document} ownerDocument The document.
         * @returns {Object} An object of data.
         */
        function getExpandoData(ownerDocument) {
          var data = expandoData[ownerDocument[expando]];
          if (!data) {
            data = {};
            expanID++;
            ownerDocument[expando] = expanID;
            expandoData[expanID] = data;
          }
          return data;
        }

        /**
         * returns a shived element for the given nodeName and document
         * @memberOf html5
         * @param {String} nodeName name of the element
         * @param {Document} ownerDocument The context document.
         * @returns {Object} The shived element.
         */
        function createElement(nodeName, ownerDocument, data){
          if (!ownerDocument) {
            ownerDocument = document;
          }
          if(supportsUnknownElements){
            return ownerDocument.createElement(nodeName);
          }
          if (!data) {
            data = getExpandoData(ownerDocument);
          }
          var node;

          if (data.cache[nodeName]) {
            node = data.cache[nodeName].cloneNode();
          } else if (saveClones.test(nodeName)) {
            node = (data.cache[nodeName] = data.createElem(nodeName)).cloneNode();
          } else {
            node = data.createElem(nodeName);
          }

          // Avoid adding some elements to fragments in IE < 9 because
          // * Attributes like `name` or `type` cannot be set/changed once an element
          //   is inserted into a document/fragment
          // * Link elements with `src` attributes that are inaccessible, as with
          //   a 403 response, will cause the tab/window to crash
          // * Script elements appended to fragments will execute when their `src`
          //   or `text` property is set
          return node.canHaveChildren && !reSkip.test(nodeName) && !node.tagUrn ? data.frag.appendChild(node) : node;
        }

        /**
         * returns a shived DocumentFragment for the given document
         * @memberOf html5
         * @param {Document} ownerDocument The context document.
         * @returns {Object} The shived DocumentFragment.
         */
        function createDocumentFragment(ownerDocument, data){
          if (!ownerDocument) {
            ownerDocument = document;
          }
          if(supportsUnknownElements){
            return ownerDocument.createDocumentFragment();
          }
          data = data || getExpandoData(ownerDocument);
          var clone = data.frag.cloneNode(),
          i = 0,
          elems = getElements(),
          l = elems.length;
          for(;i<l;i++){
            clone.createElement(elems[i]);
          }
          return clone;
        }

        /**
         * Shivs the `createElement` and `createDocumentFragment` methods of the document.
         * @private
         * @param {Document|DocumentFragment} ownerDocument The document.
         * @param {Object} data of the document.
         */
        function shivMethods(ownerDocument, data) {
          if (!data.cache) {
            data.cache = {};
            data.createElem = ownerDocument.createElement;
            data.createFrag = ownerDocument.createDocumentFragment;
            data.frag = data.createFrag();
          }


          ownerDocument.createElement = function(nodeName) {
            //abort shiv
            if (!html5.shivMethods) {
              return data.createElem(nodeName);
            }
            return createElement(nodeName, ownerDocument, data);
          };

          ownerDocument.createDocumentFragment = Function('h,f', 'return function(){' +
                                                          'var n=f.cloneNode(),c=n.createElement;' +
                                                          'h.shivMethods&&(' +
                                                          // unroll the `createElement` calls
                                                          getElements().join().replace(/[\w\-]+/g, function(nodeName) {
            data.createElem(nodeName);
            data.frag.createElement(nodeName);
            return 'c("' + nodeName + '")';
          }) +
            ');return n}'
                                                         )(html5, data.frag);
        }

        /*--------------------------------------------------------------------------*/

        /**
         * Shivs the given document.
         * @memberOf html5
         * @param {Document} ownerDocument The document to shiv.
         * @returns {Document} The shived document.
         */
        function shivDocument(ownerDocument) {
          if (!ownerDocument) {
            ownerDocument = document;
          }
          var data = getExpandoData(ownerDocument);

          if (html5.shivCSS && !supportsHtml5Styles && !data.hasCSS) {
            data.hasCSS = !!addStyleSheet(ownerDocument,
                                          // corrects block display not defined in IE6/7/8/9
                                          'article,aside,dialog,figcaption,figure,footer,header,hgroup,main,nav,section{display:block}' +
                                            // adds styling not present in IE6/7/8/9
                                            'mark{background:#FF0;color:#000}' +
                                            // hides non-rendered elements
                                            'template{display:none}'
                                         );
          }
          if (!supportsUnknownElements) {
            shivMethods(ownerDocument, data);
          }
          return ownerDocument;
        }

        /*--------------------------------------------------------------------------*/

        /**
         * The `html5` object is exposed so that more elements can be shived and
         * existing shiving can be detected on iframes.
         * @type Object
         * @example
         *
         * // options can be changed before the script is included
         * html5 = { 'elements': 'mark section', 'shivCSS': false, 'shivMethods': false };
         */
        var html5 = {

          /**
           * An array or space separated string of node names of the elements to shiv.
           * @memberOf html5
           * @type Array|String
           */
          'elements': options.elements || 'abbr article aside audio bdi canvas data datalist details dialog figcaption figure footer header hgroup main mark meter nav output progress section summary template time video',

          /**
           * current version of html5shiv
           */
          'version': version,

          /**
           * A flag to indicate that the HTML5 style sheet should be inserted.
           * @memberOf html5
           * @type Boolean
           */
          'shivCSS': (options.shivCSS !== false),

          /**
           * Is equal to true if a browser supports creating unknown/HTML5 elements
           * @memberOf html5
           * @type boolean
           */
          'supportsUnknownElements': supportsUnknownElements,

          /**
           * A flag to indicate that the document's `createElement` and `createDocumentFragment`
           * methods should be overwritten.
           * @memberOf html5
           * @type Boolean
           */
          'shivMethods': (options.shivMethods !== false),

          /**
           * A string to describe the type of `html5` object ("default" or "default print").
           * @memberOf html5
           * @type String
           */
          'type': 'default',

          // shivs the document according to the specified `html5` object options
          'shivDocument': shivDocument,

          //creates a shived element
          createElement: createElement,

          //creates a shived documentFragment
          createDocumentFragment: createDocumentFragment
        };

        /*--------------------------------------------------------------------------*/

        // expose html5
        window.html5 = html5;

        // shiv the document
        shivDocument(document);

    }(this, document));
    /*>>shiv*/

    // Assign private properties to the return object with prefix
    Modernizr._version      = version;

    // expose these for the plugin API. Look in the source for how to join() them against your input
    /*>>prefixes*/
    Modernizr._prefixes     = prefixes;
    /*>>prefixes*/
    /*>>domprefixes*/
    Modernizr._domPrefixes  = domPrefixes;
    Modernizr._cssomPrefixes  = cssomPrefixes;
    /*>>domprefixes*/

    /*>>mq*/
    // Modernizr.mq tests a given media query, live against the current state of the window
    // A few important notes:
    //   * If a browser does not support media queries at all (eg. oldIE) the mq() will always return false
    //   * A max-width or orientation query will be evaluated against the current state, which may change later.
    //   * You must specify values. Eg. If you are testing support for the min-width media query use:
    //       Modernizr.mq('(min-width:0)')
    // usage:
    // Modernizr.mq('only screen and (max-width:768)')
    Modernizr.mq            = testMediaQuery;
    /*>>mq*/

    /*>>hasevent*/
    // Modernizr.hasEvent() detects support for a given event, with an optional element to test on
    // Modernizr.hasEvent('gesturestart', elem)
    Modernizr.hasEvent      = isEventSupported;
    /*>>hasevent*/

    /*>>testprop*/
    // Modernizr.testProp() investigates whether a given style property is recognized
    // Note that the property names must be provided in the camelCase variant.
    // Modernizr.testProp('pointerEvents')
    Modernizr.testProp      = function(prop){
        return testProps([prop]);
    };
    /*>>testprop*/

    /*>>testallprops*/
    // Modernizr.testAllProps() investigates whether a given style property,
    //   or any of its vendor-prefixed variants, is recognized
    // Note that the property names must be provided in the camelCase variant.
    // Modernizr.testAllProps('boxSizing')
    Modernizr.testAllProps  = testPropsAll;
    /*>>testallprops*/


    /*>>teststyles*/
    // Modernizr.testStyles() allows you to add custom styles to the document and test an element afterwards
    // Modernizr.testStyles('#modernizr { position:absolute }', function(elem, rule){ ... })
    Modernizr.testStyles    = injectElementWithStyles;
    /*>>teststyles*/


    /*>>prefixed*/
    // Modernizr.prefixed() returns the prefixed or nonprefixed property name variant of your input
    // Modernizr.prefixed('boxSizing') // 'MozBoxSizing'

    // Properties must be passed as dom-style camelcase, rather than `box-sizing` hypentated style.
    // Return values will also be the camelCase variant, if you need to translate that to hypenated style use:
    //
    //     str.replace(/([A-Z])/g, function(str,m1){ return '-' + m1.toLowerCase(); }).replace(/^ms-/,'-ms-');

    // If you're trying to ascertain which transition end event to bind to, you might do something like...
    //
    //     var transEndEventNames = {
    //       'WebkitTransition' : 'webkitTransitionEnd',
    //       'MozTransition'    : 'transitionend',
    //       'OTransition'      : 'oTransitionEnd',
    //       'msTransition'     : 'MSTransitionEnd',
    //       'transition'       : 'transitionend'
    //     },
    //     transEndEventName = transEndEventNames[ Modernizr.prefixed('transition') ];

    Modernizr.prefixed      = function(prop, obj, elem){
      if(!obj) {
        return testPropsAll(prop, 'pfx');
      } else {
        // Testing DOM property e.g. Modernizr.prefixed('requestAnimationFrame', window) // 'mozRequestAnimationFrame'
        return testPropsAll(prop, obj, elem);
      }
    };
    /*>>prefixed*/


    /*>>cssclasses*/
    // Remove "no-js" class from <html> element, if it exists:
    docElement.className = docElement.className.replace(/(^|\s)no-js(\s|$)/, '$1$2') +

                            // Add the new classes to the <html> element.
                            (enableClasses ? ' js ' + classes.join(' ') : '');
    /*>>cssclasses*/

    return Modernizr;

})(this, this.document);

define("modernizr", function(){});

require([
	'lodash',
	'json!etc/modules.json',
	'es6-promise',
	'modernizr'
], function(
	_,
	Modules,
	ES6Promise
) {
	ES6Promise.polyfill();

	function App() {}
	App.prototype = {
		bootstrap: function() {
			this.bootstrapModules();
		},

		bootstrapModules: function() {
			var self = this;
			var activePackages = _.map(_.filter(Modules, function(module) {
				return !!module.active;
			}), 'package');

			require(activePackages, function() {
				var packages = Array.prototype.slice.call(arguments, 0);
				_.forEach(packages, function(modulePackage) {
					if ('bootstrap' in modulePackage) {
						modulePackage.bootstrap();
					}
				});
			});
		}
	};

	var app = new App();
	app.bootstrap();
});
define("skin/frontend/gigaset/rebrush/js/gigaset.js", function(){});

window.jQueryNoConflictBackup = window.$;

define("jquery-noconflict-backup", function(){});

/*!
 * jQuery JavaScript Library v3.7.1
 * https://jquery.com/
 *
 * Copyright OpenJS Foundation and other contributors
 * Released under the MIT license
 * https://jquery.org/license
 *
 * Date: 2023-08-28T13:37Z
 */
( function( global, factory ) {

	"use strict";

	if ( typeof module === "object" && typeof module.exports === "object" ) {

		// For CommonJS and CommonJS-like environments where a proper `window`
		// is present, execute the factory and get jQuery.
		// For environments that do not have a `window` with a `document`
		// (such as Node.js), expose a factory as module.exports.
		// This accentuates the need for the creation of a real `window`.
		// e.g. var jQuery = require("jquery")(window);
		// See ticket trac-14549 for more info.
		module.exports = global.document ?
			factory( global, true ) :
			function( w ) {
				if ( !w.document ) {
					throw new Error( "jQuery requires a window with a document" );
				}
				return factory( w );
			};
	} else {
		factory( global );
	}

// Pass this if window is not defined yet
} )( typeof window !== "undefined" ? window : this, function( window, noGlobal ) {

// Edge <= 12 - 13+, Firefox <=18 - 45+, IE 10 - 11, Safari 5.1 - 9+, iOS 6 - 9.1
// throw exceptions when non-strict code (e.g., ASP.NET 4.5) accesses strict mode
// arguments.callee.caller (trac-13335). But as of jQuery 3.0 (2016), strict mode should be common
// enough that all such attempts are guarded in a try block.


var arr = [];

var getProto = Object.getPrototypeOf;

var slice = arr.slice;

var flat = arr.flat ? function( array ) {
	return arr.flat.call( array );
} : function( array ) {
	return arr.concat.apply( [], array );
};


var push = arr.push;

var indexOf = arr.indexOf;

var class2type = {};

var toString = class2type.toString;

var hasOwn = class2type.hasOwnProperty;

var fnToString = hasOwn.toString;

var ObjectFunctionString = fnToString.call( Object );

var support = {};

var isFunction = function isFunction( obj ) {

		// Support: Chrome <=57, Firefox <=52
		// In some browsers, typeof returns "function" for HTML <object> elements
		// (i.e., `typeof document.createElement( "object" ) === "function"`).
		// We don't want to classify *any* DOM node as a function.
		// Support: QtWeb <=3.8.5, WebKit <=534.34, wkhtmltopdf tool <=0.12.5
		// Plus for old WebKit, typeof returns "function" for HTML collections
		// (e.g., `typeof document.getElementsByTagName("div") === "function"`). (gh-4756)
		return typeof obj === "function" && typeof obj.nodeType !== "number" &&
			typeof obj.item !== "function";
	};


var isWindow = function isWindow( obj ) {
		return obj != null && obj === obj.window;
	};


var document = window.document;



	var preservedScriptAttributes = {
		type: true,
		src: true,
		nonce: true,
		noModule: true
	};

	function DOMEval( code, node, doc ) {
		doc = doc || document;

		var i, val,
			script = doc.createElement( "script" );

		script.text = code;
		if ( node ) {
			for ( i in preservedScriptAttributes ) {

				// Support: Firefox 64+, Edge 18+
				// Some browsers don't support the "nonce" property on scripts.
				// On the other hand, just using `getAttribute` is not enough as
				// the `nonce` attribute is reset to an empty string whenever it
				// becomes browsing-context connected.
				// See https://github.com/whatwg/html/issues/2369
				// See https://html.spec.whatwg.org/#nonce-attributes
				// The `node.getAttribute` check was added for the sake of
				// `jQuery.globalEval` so that it can fake a nonce-containing node
				// via an object.
				val = node[ i ] || node.getAttribute && node.getAttribute( i );
				if ( val ) {
					script.setAttribute( i, val );
				}
			}
		}
		doc.head.appendChild( script ).parentNode.removeChild( script );
	}


function toType( obj ) {
	if ( obj == null ) {
		return obj + "";
	}

	// Support: Android <=2.3 only (functionish RegExp)
	return typeof obj === "object" || typeof obj === "function" ?
		class2type[ toString.call( obj ) ] || "object" :
		typeof obj;
}
/* global Symbol */
// Defining this global in .eslintrc.json would create a danger of using the global
// unguarded in another place, it seems safer to define global only for this module



var version = "3.7.1",

	rhtmlSuffix = /HTML$/i,

	// Define a local copy of jQuery
	jQuery = function( selector, context ) {

		// The jQuery object is actually just the init constructor 'enhanced'
		// Need init if jQuery is called (just allow error to be thrown if not included)
		return new jQuery.fn.init( selector, context );
	};

jQuery.fn = jQuery.prototype = {

	// The current version of jQuery being used
	jquery: version,

	constructor: jQuery,

	// The default length of a jQuery object is 0
	length: 0,

	toArray: function() {
		return slice.call( this );
	},

	// Get the Nth element in the matched element set OR
	// Get the whole matched element set as a clean array
	get: function( num ) {

		// Return all the elements in a clean array
		if ( num == null ) {
			return slice.call( this );
		}

		// Return just the one element from the set
		return num < 0 ? this[ num + this.length ] : this[ num ];
	},

	// Take an array of elements and push it onto the stack
	// (returning the new matched element set)
	pushStack: function( elems ) {

		// Build a new jQuery matched element set
		var ret = jQuery.merge( this.constructor(), elems );

		// Add the old object onto the stack (as a reference)
		ret.prevObject = this;

		// Return the newly-formed element set
		return ret;
	},

	// Execute a callback for every element in the matched set.
	each: function( callback ) {
		return jQuery.each( this, callback );
	},

	map: function( callback ) {
		return this.pushStack( jQuery.map( this, function( elem, i ) {
			return callback.call( elem, i, elem );
		} ) );
	},

	slice: function() {
		return this.pushStack( slice.apply( this, arguments ) );
	},

	first: function() {
		return this.eq( 0 );
	},

	last: function() {
		return this.eq( -1 );
	},

	even: function() {
		return this.pushStack( jQuery.grep( this, function( _elem, i ) {
			return ( i + 1 ) % 2;
		} ) );
	},

	odd: function() {
		return this.pushStack( jQuery.grep( this, function( _elem, i ) {
			return i % 2;
		} ) );
	},

	eq: function( i ) {
		var len = this.length,
			j = +i + ( i < 0 ? len : 0 );
		return this.pushStack( j >= 0 && j < len ? [ this[ j ] ] : [] );
	},

	end: function() {
		return this.prevObject || this.constructor();
	},

	// For internal use only.
	// Behaves like an Array's method, not like a jQuery method.
	push: push,
	sort: arr.sort,
	splice: arr.splice
};

jQuery.extend = jQuery.fn.extend = function() {
	var options, name, src, copy, copyIsArray, clone,
		target = arguments[ 0 ] || {},
		i = 1,
		length = arguments.length,
		deep = false;

	// Handle a deep copy situation
	if ( typeof target === "boolean" ) {
		deep = target;

		// Skip the boolean and the target
		target = arguments[ i ] || {};
		i++;
	}

	// Handle case when target is a string or something (possible in deep copy)
	if ( typeof target !== "object" && !isFunction( target ) ) {
		target = {};
	}

	// Extend jQuery itself if only one argument is passed
	if ( i === length ) {
		target = this;
		i--;
	}

	for ( ; i < length; i++ ) {

		// Only deal with non-null/undefined values
		if ( ( options = arguments[ i ] ) != null ) {

			// Extend the base object
			for ( name in options ) {
				copy = options[ name ];

				// Prevent Object.prototype pollution
				// Prevent never-ending loop
				if ( name === "__proto__" || target === copy ) {
					continue;
				}

				// Recurse if we're merging plain objects or arrays
				if ( deep && copy && ( jQuery.isPlainObject( copy ) ||
					( copyIsArray = Array.isArray( copy ) ) ) ) {
					src = target[ name ];

					// Ensure proper type for the source value
					if ( copyIsArray && !Array.isArray( src ) ) {
						clone = [];
					} else if ( !copyIsArray && !jQuery.isPlainObject( src ) ) {
						clone = {};
					} else {
						clone = src;
					}
					copyIsArray = false;

					// Never move original objects, clone them
					target[ name ] = jQuery.extend( deep, clone, copy );

				// Don't bring in undefined values
				} else if ( copy !== undefined ) {
					target[ name ] = copy;
				}
			}
		}
	}

	// Return the modified object
	return target;
};

jQuery.extend( {

	// Unique for each copy of jQuery on the page
	expando: "jQuery" + ( version + Math.random() ).replace( /\D/g, "" ),

	// Assume jQuery is ready without the ready module
	isReady: true,

	error: function( msg ) {
		throw new Error( msg );
	},

	noop: function() {},

	isPlainObject: function( obj ) {
		var proto, Ctor;

		// Detect obvious negatives
		// Use toString instead of jQuery.type to catch host objects
		if ( !obj || toString.call( obj ) !== "[object Object]" ) {
			return false;
		}

		proto = getProto( obj );

		// Objects with no prototype (e.g., `Object.create( null )`) are plain
		if ( !proto ) {
			return true;
		}

		// Objects with prototype are plain iff they were constructed by a global Object function
		Ctor = hasOwn.call( proto, "constructor" ) && proto.constructor;
		return typeof Ctor === "function" && fnToString.call( Ctor ) === ObjectFunctionString;
	},

	isEmptyObject: function( obj ) {
		var name;

		for ( name in obj ) {
			return false;
		}
		return true;
	},

	// Evaluates a script in a provided context; falls back to the global one
	// if not specified.
	globalEval: function( code, options, doc ) {
		DOMEval( code, { nonce: options && options.nonce }, doc );
	},

	each: function( obj, callback ) {
		var length, i = 0;

		if ( isArrayLike( obj ) ) {
			length = obj.length;
			for ( ; i < length; i++ ) {
				if ( callback.call( obj[ i ], i, obj[ i ] ) === false ) {
					break;
				}
			}
		} else {
			for ( i in obj ) {
				if ( callback.call( obj[ i ], i, obj[ i ] ) === false ) {
					break;
				}
			}
		}

		return obj;
	},


	// Retrieve the text value of an array of DOM nodes
	text: function( elem ) {
		var node,
			ret = "",
			i = 0,
			nodeType = elem.nodeType;

		if ( !nodeType ) {

			// If no nodeType, this is expected to be an array
			while ( ( node = elem[ i++ ] ) ) {

				// Do not traverse comment nodes
				ret += jQuery.text( node );
			}
		}
		if ( nodeType === 1 || nodeType === 11 ) {
			return elem.textContent;
		}
		if ( nodeType === 9 ) {
			return elem.documentElement.textContent;
		}
		if ( nodeType === 3 || nodeType === 4 ) {
			return elem.nodeValue;
		}

		// Do not include comment or processing instruction nodes

		return ret;
	},

	// results is for internal usage only
	makeArray: function( arr, results ) {
		var ret = results || [];

		if ( arr != null ) {
			if ( isArrayLike( Object( arr ) ) ) {
				jQuery.merge( ret,
					typeof arr === "string" ?
						[ arr ] : arr
				);
			} else {
				push.call( ret, arr );
			}
		}

		return ret;
	},

	inArray: function( elem, arr, i ) {
		return arr == null ? -1 : indexOf.call( arr, elem, i );
	},

	isXMLDoc: function( elem ) {
		var namespace = elem && elem.namespaceURI,
			docElem = elem && ( elem.ownerDocument || elem ).documentElement;

		// Assume HTML when documentElement doesn't yet exist, such as inside
		// document fragments.
		return !rhtmlSuffix.test( namespace || docElem && docElem.nodeName || "HTML" );
	},

	// Support: Android <=4.0 only, PhantomJS 1 only
	// push.apply(_, arraylike) throws on ancient WebKit
	merge: function( first, second ) {
		var len = +second.length,
			j = 0,
			i = first.length;

		for ( ; j < len; j++ ) {
			first[ i++ ] = second[ j ];
		}

		first.length = i;

		return first;
	},

	grep: function( elems, callback, invert ) {
		var callbackInverse,
			matches = [],
			i = 0,
			length = elems.length,
			callbackExpect = !invert;

		// Go through the array, only saving the items
		// that pass the validator function
		for ( ; i < length; i++ ) {
			callbackInverse = !callback( elems[ i ], i );
			if ( callbackInverse !== callbackExpect ) {
				matches.push( elems[ i ] );
			}
		}

		return matches;
	},

	// arg is for internal usage only
	map: function( elems, callback, arg ) {
		var length, value,
			i = 0,
			ret = [];

		// Go through the array, translating each of the items to their new values
		if ( isArrayLike( elems ) ) {
			length = elems.length;
			for ( ; i < length; i++ ) {
				value = callback( elems[ i ], i, arg );

				if ( value != null ) {
					ret.push( value );
				}
			}

		// Go through every key on the object,
		} else {
			for ( i in elems ) {
				value = callback( elems[ i ], i, arg );

				if ( value != null ) {
					ret.push( value );
				}
			}
		}

		// Flatten any nested arrays
		return flat( ret );
	},

	// A global GUID counter for objects
	guid: 1,

	// jQuery.support is not used in Core but other projects attach their
	// properties to it so it needs to exist.
	support: support
} );

if ( typeof Symbol === "function" ) {
	jQuery.fn[ Symbol.iterator ] = arr[ Symbol.iterator ];
}

// Populate the class2type map
jQuery.each( "Boolean Number String Function Array Date RegExp Object Error Symbol".split( " " ),
	function( _i, name ) {
		class2type[ "[object " + name + "]" ] = name.toLowerCase();
	} );

function isArrayLike( obj ) {

	// Support: real iOS 8.2 only (not reproducible in simulator)
	// `in` check used to prevent JIT error (gh-2145)
	// hasOwn isn't used here due to false negatives
	// regarding Nodelist length in IE
	var length = !!obj && "length" in obj && obj.length,
		type = toType( obj );

	if ( isFunction( obj ) || isWindow( obj ) ) {
		return false;
	}

	return type === "array" || length === 0 ||
		typeof length === "number" && length > 0 && ( length - 1 ) in obj;
}


function nodeName( elem, name ) {

	return elem.nodeName && elem.nodeName.toLowerCase() === name.toLowerCase();

}
var pop = arr.pop;


var sort = arr.sort;


var splice = arr.splice;


var whitespace = "[\\x20\\t\\r\\n\\f]";


var rtrimCSS = new RegExp(
	"^" + whitespace + "+|((?:^|[^\\\\])(?:\\\\.)*)" + whitespace + "+$",
	"g"
);




// Note: an element does not contain itself
jQuery.contains = function( a, b ) {
	var bup = b && b.parentNode;

	return a === bup || !!( bup && bup.nodeType === 1 && (

		// Support: IE 9 - 11+
		// IE doesn't have `contains` on SVG.
		a.contains ?
			a.contains( bup ) :
			a.compareDocumentPosition && a.compareDocumentPosition( bup ) & 16
	) );
};




// CSS string/identifier serialization
// https://drafts.csswg.org/cssom/#common-serializing-idioms
var rcssescape = /([\0-\x1f\x7f]|^-?\d)|^-$|[^\x80-\uFFFF\w-]/g;

function fcssescape( ch, asCodePoint ) {
	if ( asCodePoint ) {

		// U+0000 NULL becomes U+FFFD REPLACEMENT CHARACTER
		if ( ch === "\0" ) {
			return "\uFFFD";
		}

		// Control characters and (dependent upon position) numbers get escaped as code points
		return ch.slice( 0, -1 ) + "\\" + ch.charCodeAt( ch.length - 1 ).toString( 16 ) + " ";
	}

	// Other potentially-special ASCII characters get backslash-escaped
	return "\\" + ch;
}

jQuery.escapeSelector = function( sel ) {
	return ( sel + "" ).replace( rcssescape, fcssescape );
};




var preferredDoc = document,
	pushNative = push;

( function() {

var i,
	Expr,
	outermostContext,
	sortInput,
	hasDuplicate,
	push = pushNative,

	// Local document vars
	document,
	documentElement,
	documentIsHTML,
	rbuggyQSA,
	matches,

	// Instance-specific data
	expando = jQuery.expando,
	dirruns = 0,
	done = 0,
	classCache = createCache(),
	tokenCache = createCache(),
	compilerCache = createCache(),
	nonnativeSelectorCache = createCache(),
	sortOrder = function( a, b ) {
		if ( a === b ) {
			hasDuplicate = true;
		}
		return 0;
	},

	booleans = "checked|selected|async|autofocus|autoplay|controls|defer|disabled|hidden|ismap|" +
		"loop|multiple|open|readonly|required|scoped",

	// Regular expressions

	// https://www.w3.org/TR/css-syntax-3/#ident-token-diagram
	identifier = "(?:\\\\[\\da-fA-F]{1,6}" + whitespace +
		"?|\\\\[^\\r\\n\\f]|[\\w-]|[^\0-\\x7f])+",

	// Attribute selectors: https://www.w3.org/TR/selectors/#attribute-selectors
	attributes = "\\[" + whitespace + "*(" + identifier + ")(?:" + whitespace +

		// Operator (capture 2)
		"*([*^$|!~]?=)" + whitespace +

		// "Attribute values must be CSS identifiers [capture 5] or strings [capture 3 or capture 4]"
		"*(?:'((?:\\\\.|[^\\\\'])*)'|\"((?:\\\\.|[^\\\\\"])*)\"|(" + identifier + "))|)" +
		whitespace + "*\\]",

	pseudos = ":(" + identifier + ")(?:\\((" +

		// To reduce the number of selectors needing tokenize in the preFilter, prefer arguments:
		// 1. quoted (capture 3; capture 4 or capture 5)
		"('((?:\\\\.|[^\\\\'])*)'|\"((?:\\\\.|[^\\\\\"])*)\")|" +

		// 2. simple (capture 6)
		"((?:\\\\.|[^\\\\()[\\]]|" + attributes + ")*)|" +

		// 3. anything else (capture 2)
		".*" +
		")\\)|)",

	// Leading and non-escaped trailing whitespace, capturing some non-whitespace characters preceding the latter
	rwhitespace = new RegExp( whitespace + "+", "g" ),

	rcomma = new RegExp( "^" + whitespace + "*," + whitespace + "*" ),
	rleadingCombinator = new RegExp( "^" + whitespace + "*([>+~]|" + whitespace + ")" +
		whitespace + "*" ),
	rdescend = new RegExp( whitespace + "|>" ),

	rpseudo = new RegExp( pseudos ),
	ridentifier = new RegExp( "^" + identifier + "$" ),

	matchExpr = {
		ID: new RegExp( "^#(" + identifier + ")" ),
		CLASS: new RegExp( "^\\.(" + identifier + ")" ),
		TAG: new RegExp( "^(" + identifier + "|[*])" ),
		ATTR: new RegExp( "^" + attributes ),
		PSEUDO: new RegExp( "^" + pseudos ),
		CHILD: new RegExp(
			"^:(only|first|last|nth|nth-last)-(child|of-type)(?:\\(" +
				whitespace + "*(even|odd|(([+-]|)(\\d*)n|)" + whitespace + "*(?:([+-]|)" +
				whitespace + "*(\\d+)|))" + whitespace + "*\\)|)", "i" ),
		bool: new RegExp( "^(?:" + booleans + ")$", "i" ),

		// For use in libraries implementing .is()
		// We use this for POS matching in `select`
		needsContext: new RegExp( "^" + whitespace +
			"*[>+~]|:(even|odd|eq|gt|lt|nth|first|last)(?:\\(" + whitespace +
			"*((?:-\\d)?\\d*)" + whitespace + "*\\)|)(?=[^-]|$)", "i" )
	},

	rinputs = /^(?:input|select|textarea|button)$/i,
	rheader = /^h\d$/i,

	// Easily-parseable/retrievable ID or TAG or CLASS selectors
	rquickExpr = /^(?:#([\w-]+)|(\w+)|\.([\w-]+))$/,

	rsibling = /[+~]/,

	// CSS escapes
	// https://www.w3.org/TR/CSS21/syndata.html#escaped-characters
	runescape = new RegExp( "\\\\[\\da-fA-F]{1,6}" + whitespace +
		"?|\\\\([^\\r\\n\\f])", "g" ),
	funescape = function( escape, nonHex ) {
		var high = "0x" + escape.slice( 1 ) - 0x10000;

		if ( nonHex ) {

			// Strip the backslash prefix from a non-hex escape sequence
			return nonHex;
		}

		// Replace a hexadecimal escape sequence with the encoded Unicode code point
		// Support: IE <=11+
		// For values outside the Basic Multilingual Plane (BMP), manually construct a
		// surrogate pair
		return high < 0 ?
			String.fromCharCode( high + 0x10000 ) :
			String.fromCharCode( high >> 10 | 0xD800, high & 0x3FF | 0xDC00 );
	},

	// Used for iframes; see `setDocument`.
	// Support: IE 9 - 11+, Edge 12 - 18+
	// Removing the function wrapper causes a "Permission Denied"
	// error in IE/Edge.
	unloadHandler = function() {
		setDocument();
	},

	inDisabledFieldset = addCombinator(
		function( elem ) {
			return elem.disabled === true && nodeName( elem, "fieldset" );
		},
		{ dir: "parentNode", next: "legend" }
	);

// Support: IE <=9 only
// Accessing document.activeElement can throw unexpectedly
// https://bugs.jquery.com/ticket/13393
function safeActiveElement() {
	try {
		return document.activeElement;
	} catch ( err ) { }
}

// Optimize for push.apply( _, NodeList )
try {
	push.apply(
		( arr = slice.call( preferredDoc.childNodes ) ),
		preferredDoc.childNodes
	);

	// Support: Android <=4.0
	// Detect silently failing push.apply
	// eslint-disable-next-line no-unused-expressions
	arr[ preferredDoc.childNodes.length ].nodeType;
} catch ( e ) {
	push = {
		apply: function( target, els ) {
			pushNative.apply( target, slice.call( els ) );
		},
		call: function( target ) {
			pushNative.apply( target, slice.call( arguments, 1 ) );
		}
	};
}

function find( selector, context, results, seed ) {
	var m, i, elem, nid, match, groups, newSelector,
		newContext = context && context.ownerDocument,

		// nodeType defaults to 9, since context defaults to document
		nodeType = context ? context.nodeType : 9;

	results = results || [];

	// Return early from calls with invalid selector or context
	if ( typeof selector !== "string" || !selector ||
		nodeType !== 1 && nodeType !== 9 && nodeType !== 11 ) {

		return results;
	}

	// Try to shortcut find operations (as opposed to filters) in HTML documents
	if ( !seed ) {
		setDocument( context );
		context = context || document;

		if ( documentIsHTML ) {

			// If the selector is sufficiently simple, try using a "get*By*" DOM method
			// (excepting DocumentFragment context, where the methods don't exist)
			if ( nodeType !== 11 && ( match = rquickExpr.exec( selector ) ) ) {

				// ID selector
				if ( ( m = match[ 1 ] ) ) {

					// Document context
					if ( nodeType === 9 ) {
						if ( ( elem = context.getElementById( m ) ) ) {

							// Support: IE 9 only
							// getElementById can match elements by name instead of ID
							if ( elem.id === m ) {
								push.call( results, elem );
								return results;
							}
						} else {
							return results;
						}

					// Element context
					} else {

						// Support: IE 9 only
						// getElementById can match elements by name instead of ID
						if ( newContext && ( elem = newContext.getElementById( m ) ) &&
							find.contains( context, elem ) &&
							elem.id === m ) {

							push.call( results, elem );
							return results;
						}
					}

				// Type selector
				} else if ( match[ 2 ] ) {
					push.apply( results, context.getElementsByTagName( selector ) );
					return results;

				// Class selector
				} else if ( ( m = match[ 3 ] ) && context.getElementsByClassName ) {
					push.apply( results, context.getElementsByClassName( m ) );
					return results;
				}
			}

			// Take advantage of querySelectorAll
			if ( !nonnativeSelectorCache[ selector + " " ] &&
				( !rbuggyQSA || !rbuggyQSA.test( selector ) ) ) {

				newSelector = selector;
				newContext = context;

				// qSA considers elements outside a scoping root when evaluating child or
				// descendant combinators, which is not what we want.
				// In such cases, we work around the behavior by prefixing every selector in the
				// list with an ID selector referencing the scope context.
				// The technique has to be used as well when a leading combinator is used
				// as such selectors are not recognized by querySelectorAll.
				// Thanks to Andrew Dupont for this technique.
				if ( nodeType === 1 &&
					( rdescend.test( selector ) || rleadingCombinator.test( selector ) ) ) {

					// Expand context for sibling selectors
					newContext = rsibling.test( selector ) && testContext( context.parentNode ) ||
						context;

					// We can use :scope instead of the ID hack if the browser
					// supports it & if we're not changing the context.
					// Support: IE 11+, Edge 17 - 18+
					// IE/Edge sometimes throw a "Permission denied" error when
					// strict-comparing two documents; shallow comparisons work.
					// eslint-disable-next-line eqeqeq
					if ( newContext != context || !support.scope ) {

						// Capture the context ID, setting it first if necessary
						if ( ( nid = context.getAttribute( "id" ) ) ) {
							nid = jQuery.escapeSelector( nid );
						} else {
							context.setAttribute( "id", ( nid = expando ) );
						}
					}

					// Prefix every selector in the list
					groups = tokenize( selector );
					i = groups.length;
					while ( i-- ) {
						groups[ i ] = ( nid ? "#" + nid : ":scope" ) + " " +
							toSelector( groups[ i ] );
					}
					newSelector = groups.join( "," );
				}

				try {
					push.apply( results,
						newContext.querySelectorAll( newSelector )
					);
					return results;
				} catch ( qsaError ) {
					nonnativeSelectorCache( selector, true );
				} finally {
					if ( nid === expando ) {
						context.removeAttribute( "id" );
					}
				}
			}
		}
	}

	// All others
	return select( selector.replace( rtrimCSS, "$1" ), context, results, seed );
}

/**
 * Create key-value caches of limited size
 * @returns {function(string, object)} Returns the Object data after storing it on itself with
 *	property name the (space-suffixed) string and (if the cache is larger than Expr.cacheLength)
 *	deleting the oldest entry
 */
function createCache() {
	var keys = [];

	function cache( key, value ) {

		// Use (key + " ") to avoid collision with native prototype properties
		// (see https://github.com/jquery/sizzle/issues/157)
		if ( keys.push( key + " " ) > Expr.cacheLength ) {

			// Only keep the most recent entries
			delete cache[ keys.shift() ];
		}
		return ( cache[ key + " " ] = value );
	}
	return cache;
}

/**
 * Mark a function for special use by jQuery selector module
 * @param {Function} fn The function to mark
 */
function markFunction( fn ) {
	fn[ expando ] = true;
	return fn;
}

/**
 * Support testing using an element
 * @param {Function} fn Passed the created element and returns a boolean result
 */
function assert( fn ) {
	var el = document.createElement( "fieldset" );

	try {
		return !!fn( el );
	} catch ( e ) {
		return false;
	} finally {

		// Remove from its parent by default
		if ( el.parentNode ) {
			el.parentNode.removeChild( el );
		}

		// release memory in IE
		el = null;
	}
}

/**
 * Returns a function to use in pseudos for input types
 * @param {String} type
 */
function createInputPseudo( type ) {
	return function( elem ) {
		return nodeName( elem, "input" ) && elem.type === type;
	};
}

/**
 * Returns a function to use in pseudos for buttons
 * @param {String} type
 */
function createButtonPseudo( type ) {
	return function( elem ) {
		return ( nodeName( elem, "input" ) || nodeName( elem, "button" ) ) &&
			elem.type === type;
	};
}

/**
 * Returns a function to use in pseudos for :enabled/:disabled
 * @param {Boolean} disabled true for :disabled; false for :enabled
 */
function createDisabledPseudo( disabled ) {

	// Known :disabled false positives: fieldset[disabled] > legend:nth-of-type(n+2) :can-disable
	return function( elem ) {

		// Only certain elements can match :enabled or :disabled
		// https://html.spec.whatwg.org/multipage/scripting.html#selector-enabled
		// https://html.spec.whatwg.org/multipage/scripting.html#selector-disabled
		if ( "form" in elem ) {

			// Check for inherited disabledness on relevant non-disabled elements:
			// * listed form-associated elements in a disabled fieldset
			//   https://html.spec.whatwg.org/multipage/forms.html#category-listed
			//   https://html.spec.whatwg.org/multipage/forms.html#concept-fe-disabled
			// * option elements in a disabled optgroup
			//   https://html.spec.whatwg.org/multipage/forms.html#concept-option-disabled
			// All such elements have a "form" property.
			if ( elem.parentNode && elem.disabled === false ) {

				// Option elements defer to a parent optgroup if present
				if ( "label" in elem ) {
					if ( "label" in elem.parentNode ) {
						return elem.parentNode.disabled === disabled;
					} else {
						return elem.disabled === disabled;
					}
				}

				// Support: IE 6 - 11+
				// Use the isDisabled shortcut property to check for disabled fieldset ancestors
				return elem.isDisabled === disabled ||

					// Where there is no isDisabled, check manually
					elem.isDisabled !== !disabled &&
						inDisabledFieldset( elem ) === disabled;
			}

			return elem.disabled === disabled;

		// Try to winnow out elements that can't be disabled before trusting the disabled property.
		// Some victims get caught in our net (label, legend, menu, track), but it shouldn't
		// even exist on them, let alone have a boolean value.
		} else if ( "label" in elem ) {
			return elem.disabled === disabled;
		}

		// Remaining elements are neither :enabled nor :disabled
		return false;
	};
}

/**
 * Returns a function to use in pseudos for positionals
 * @param {Function} fn
 */
function createPositionalPseudo( fn ) {
	return markFunction( function( argument ) {
		argument = +argument;
		return markFunction( function( seed, matches ) {
			var j,
				matchIndexes = fn( [], seed.length, argument ),
				i = matchIndexes.length;

			// Match elements found at the specified indexes
			while ( i-- ) {
				if ( seed[ ( j = matchIndexes[ i ] ) ] ) {
					seed[ j ] = !( matches[ j ] = seed[ j ] );
				}
			}
		} );
	} );
}

/**
 * Checks a node for validity as a jQuery selector context
 * @param {Element|Object=} context
 * @returns {Element|Object|Boolean} The input node if acceptable, otherwise a falsy value
 */
function testContext( context ) {
	return context && typeof context.getElementsByTagName !== "undefined" && context;
}

/**
 * Sets document-related variables once based on the current document
 * @param {Element|Object} [node] An element or document object to use to set the document
 * @returns {Object} Returns the current document
 */
function setDocument( node ) {
	var subWindow,
		doc = node ? node.ownerDocument || node : preferredDoc;

	// Return early if doc is invalid or already selected
	// Support: IE 11+, Edge 17 - 18+
	// IE/Edge sometimes throw a "Permission denied" error when strict-comparing
	// two documents; shallow comparisons work.
	// eslint-disable-next-line eqeqeq
	if ( doc == document || doc.nodeType !== 9 || !doc.documentElement ) {
		return document;
	}

	// Update global variables
	document = doc;
	documentElement = document.documentElement;
	documentIsHTML = !jQuery.isXMLDoc( document );

	// Support: iOS 7 only, IE 9 - 11+
	// Older browsers didn't support unprefixed `matches`.
	matches = documentElement.matches ||
		documentElement.webkitMatchesSelector ||
		documentElement.msMatchesSelector;

	// Support: IE 9 - 11+, Edge 12 - 18+
	// Accessing iframe documents after unload throws "permission denied" errors
	// (see trac-13936).
	// Limit the fix to IE & Edge Legacy; despite Edge 15+ implementing `matches`,
	// all IE 9+ and Edge Legacy versions implement `msMatchesSelector` as well.
	if ( documentElement.msMatchesSelector &&

		// Support: IE 11+, Edge 17 - 18+
		// IE/Edge sometimes throw a "Permission denied" error when strict-comparing
		// two documents; shallow comparisons work.
		// eslint-disable-next-line eqeqeq
		preferredDoc != document &&
		( subWindow = document.defaultView ) && subWindow.top !== subWindow ) {

		// Support: IE 9 - 11+, Edge 12 - 18+
		subWindow.addEventListener( "unload", unloadHandler );
	}

	// Support: IE <10
	// Check if getElementById returns elements by name
	// The broken getElementById methods don't pick up programmatically-set names,
	// so use a roundabout getElementsByName test
	support.getById = assert( function( el ) {
		documentElement.appendChild( el ).id = jQuery.expando;
		return !document.getElementsByName ||
			!document.getElementsByName( jQuery.expando ).length;
	} );

	// Support: IE 9 only
	// Check to see if it's possible to do matchesSelector
	// on a disconnected node.
	support.disconnectedMatch = assert( function( el ) {
		return matches.call( el, "*" );
	} );

	// Support: IE 9 - 11+, Edge 12 - 18+
	// IE/Edge don't support the :scope pseudo-class.
	support.scope = assert( function() {
		return document.querySelectorAll( ":scope" );
	} );

	// Support: Chrome 105 - 111 only, Safari 15.4 - 16.3 only
	// Make sure the `:has()` argument is parsed unforgivingly.
	// We include `*` in the test to detect buggy implementations that are
	// _selectively_ forgiving (specifically when the list includes at least
	// one valid selector).
	// Note that we treat complete lack of support for `:has()` as if it were
	// spec-compliant support, which is fine because use of `:has()` in such
	// environments will fail in the qSA path and fall back to jQuery traversal
	// anyway.
	support.cssHas = assert( function() {
		try {
			document.querySelector( ":has(*,:jqfake)" );
			return false;
		} catch ( e ) {
			return true;
		}
	} );

	// ID filter and find
	if ( support.getById ) {
		Expr.filter.ID = function( id ) {
			var attrId = id.replace( runescape, funescape );
			return function( elem ) {
				return elem.getAttribute( "id" ) === attrId;
			};
		};
		Expr.find.ID = function( id, context ) {
			if ( typeof context.getElementById !== "undefined" && documentIsHTML ) {
				var elem = context.getElementById( id );
				return elem ? [ elem ] : [];
			}
		};
	} else {
		Expr.filter.ID =  function( id ) {
			var attrId = id.replace( runescape, funescape );
			return function( elem ) {
				var node = typeof elem.getAttributeNode !== "undefined" &&
					elem.getAttributeNode( "id" );
				return node && node.value === attrId;
			};
		};

		// Support: IE 6 - 7 only
		// getElementById is not reliable as a find shortcut
		Expr.find.ID = function( id, context ) {
			if ( typeof context.getElementById !== "undefined" && documentIsHTML ) {
				var node, i, elems,
					elem = context.getElementById( id );

				if ( elem ) {

					// Verify the id attribute
					node = elem.getAttributeNode( "id" );
					if ( node && node.value === id ) {
						return [ elem ];
					}

					// Fall back on getElementsByName
					elems = context.getElementsByName( id );
					i = 0;
					while ( ( elem = elems[ i++ ] ) ) {
						node = elem.getAttributeNode( "id" );
						if ( node && node.value === id ) {
							return [ elem ];
						}
					}
				}

				return [];
			}
		};
	}

	// Tag
	Expr.find.TAG = function( tag, context ) {
		if ( typeof context.getElementsByTagName !== "undefined" ) {
			return context.getElementsByTagName( tag );

		// DocumentFragment nodes don't have gEBTN
		} else {
			return context.querySelectorAll( tag );
		}
	};

	// Class
	Expr.find.CLASS = function( className, context ) {
		if ( typeof context.getElementsByClassName !== "undefined" && documentIsHTML ) {
			return context.getElementsByClassName( className );
		}
	};

	/* QSA/matchesSelector
	---------------------------------------------------------------------- */

	// QSA and matchesSelector support

	rbuggyQSA = [];

	// Build QSA regex
	// Regex strategy adopted from Diego Perini
	assert( function( el ) {

		var input;

		documentElement.appendChild( el ).innerHTML =
			"<a id='" + expando + "' href='' disabled='disabled'></a>" +
			"<select id='" + expando + "-\r\\' disabled='disabled'>" +
			"<option selected=''></option></select>";

		// Support: iOS <=7 - 8 only
		// Boolean attributes and "value" are not treated correctly in some XML documents
		if ( !el.querySelectorAll( "[selected]" ).length ) {
			rbuggyQSA.push( "\\[" + whitespace + "*(?:value|" + booleans + ")" );
		}

		// Support: iOS <=7 - 8 only
		if ( !el.querySelectorAll( "[id~=" + expando + "-]" ).length ) {
			rbuggyQSA.push( "~=" );
		}

		// Support: iOS 8 only
		// https://bugs.webkit.org/show_bug.cgi?id=136851
		// In-page `selector#id sibling-combinator selector` fails
		if ( !el.querySelectorAll( "a#" + expando + "+*" ).length ) {
			rbuggyQSA.push( ".#.+[+~]" );
		}

		// Support: Chrome <=105+, Firefox <=104+, Safari <=15.4+
		// In some of the document kinds, these selectors wouldn't work natively.
		// This is probably OK but for backwards compatibility we want to maintain
		// handling them through jQuery traversal in jQuery 3.x.
		if ( !el.querySelectorAll( ":checked" ).length ) {
			rbuggyQSA.push( ":checked" );
		}

		// Support: Windows 8 Native Apps
		// The type and name attributes are restricted during .innerHTML assignment
		input = document.createElement( "input" );
		input.setAttribute( "type", "hidden" );
		el.appendChild( input ).setAttribute( "name", "D" );

		// Support: IE 9 - 11+
		// IE's :disabled selector does not pick up the children of disabled fieldsets
		// Support: Chrome <=105+, Firefox <=104+, Safari <=15.4+
		// In some of the document kinds, these selectors wouldn't work natively.
		// This is probably OK but for backwards compatibility we want to maintain
		// handling them through jQuery traversal in jQuery 3.x.
		documentElement.appendChild( el ).disabled = true;
		if ( el.querySelectorAll( ":disabled" ).length !== 2 ) {
			rbuggyQSA.push( ":enabled", ":disabled" );
		}

		// Support: IE 11+, Edge 15 - 18+
		// IE 11/Edge don't find elements on a `[name='']` query in some cases.
		// Adding a temporary attribute to the document before the selection works
		// around the issue.
		// Interestingly, IE 10 & older don't seem to have the issue.
		input = document.createElement( "input" );
		input.setAttribute( "name", "" );
		el.appendChild( input );
		if ( !el.querySelectorAll( "[name='']" ).length ) {
			rbuggyQSA.push( "\\[" + whitespace + "*name" + whitespace + "*=" +
				whitespace + "*(?:''|\"\")" );
		}
	} );

	if ( !support.cssHas ) {

		// Support: Chrome 105 - 110+, Safari 15.4 - 16.3+
		// Our regular `try-catch` mechanism fails to detect natively-unsupported
		// pseudo-classes inside `:has()` (such as `:has(:contains("Foo"))`)
		// in browsers that parse the `:has()` argument as a forgiving selector list.
		// https://drafts.csswg.org/selectors/#relational now requires the argument
		// to be parsed unforgivingly, but browsers have not yet fully adjusted.
		rbuggyQSA.push( ":has" );
	}

	rbuggyQSA = rbuggyQSA.length && new RegExp( rbuggyQSA.join( "|" ) );

	/* Sorting
	---------------------------------------------------------------------- */

	// Document order sorting
	sortOrder = function( a, b ) {

		// Flag for duplicate removal
		if ( a === b ) {
			hasDuplicate = true;
			return 0;
		}

		// Sort on method existence if only one input has compareDocumentPosition
		var compare = !a.compareDocumentPosition - !b.compareDocumentPosition;
		if ( compare ) {
			return compare;
		}

		// Calculate position if both inputs belong to the same document
		// Support: IE 11+, Edge 17 - 18+
		// IE/Edge sometimes throw a "Permission denied" error when strict-comparing
		// two documents; shallow comparisons work.
		// eslint-disable-next-line eqeqeq
		compare = ( a.ownerDocument || a ) == ( b.ownerDocument || b ) ?
			a.compareDocumentPosition( b ) :

			// Otherwise we know they are disconnected
			1;

		// Disconnected nodes
		if ( compare & 1 ||
			( !support.sortDetached && b.compareDocumentPosition( a ) === compare ) ) {

			// Choose the first element that is related to our preferred document
			// Support: IE 11+, Edge 17 - 18+
			// IE/Edge sometimes throw a "Permission denied" error when strict-comparing
			// two documents; shallow comparisons work.
			// eslint-disable-next-line eqeqeq
			if ( a === document || a.ownerDocument == preferredDoc &&
				find.contains( preferredDoc, a ) ) {
				return -1;
			}

			// Support: IE 11+, Edge 17 - 18+
			// IE/Edge sometimes throw a "Permission denied" error when strict-comparing
			// two documents; shallow comparisons work.
			// eslint-disable-next-line eqeqeq
			if ( b === document || b.ownerDocument == preferredDoc &&
				find.contains( preferredDoc, b ) ) {
				return 1;
			}

			// Maintain original order
			return sortInput ?
				( indexOf.call( sortInput, a ) - indexOf.call( sortInput, b ) ) :
				0;
		}

		return compare & 4 ? -1 : 1;
	};

	return document;
}

find.matches = function( expr, elements ) {
	return find( expr, null, null, elements );
};

find.matchesSelector = function( elem, expr ) {
	setDocument( elem );

	if ( documentIsHTML &&
		!nonnativeSelectorCache[ expr + " " ] &&
		( !rbuggyQSA || !rbuggyQSA.test( expr ) ) ) {

		try {
			var ret = matches.call( elem, expr );

			// IE 9's matchesSelector returns false on disconnected nodes
			if ( ret || support.disconnectedMatch ||

					// As well, disconnected nodes are said to be in a document
					// fragment in IE 9
					elem.document && elem.document.nodeType !== 11 ) {
				return ret;
			}
		} catch ( e ) {
			nonnativeSelectorCache( expr, true );
		}
	}

	return find( expr, document, null, [ elem ] ).length > 0;
};

find.contains = function( context, elem ) {

	// Set document vars if needed
	// Support: IE 11+, Edge 17 - 18+
	// IE/Edge sometimes throw a "Permission denied" error when strict-comparing
	// two documents; shallow comparisons work.
	// eslint-disable-next-line eqeqeq
	if ( ( context.ownerDocument || context ) != document ) {
		setDocument( context );
	}
	return jQuery.contains( context, elem );
};


find.attr = function( elem, name ) {

	// Set document vars if needed
	// Support: IE 11+, Edge 17 - 18+
	// IE/Edge sometimes throw a "Permission denied" error when strict-comparing
	// two documents; shallow comparisons work.
	// eslint-disable-next-line eqeqeq
	if ( ( elem.ownerDocument || elem ) != document ) {
		setDocument( elem );
	}

	var fn = Expr.attrHandle[ name.toLowerCase() ],

		// Don't get fooled by Object.prototype properties (see trac-13807)
		val = fn && hasOwn.call( Expr.attrHandle, name.toLowerCase() ) ?
			fn( elem, name, !documentIsHTML ) :
			undefined;

	if ( val !== undefined ) {
		return val;
	}

	return elem.getAttribute( name );
};

find.error = function( msg ) {
	throw new Error( "Syntax error, unrecognized expression: " + msg );
};

/**
 * Document sorting and removing duplicates
 * @param {ArrayLike} results
 */
jQuery.uniqueSort = function( results ) {
	var elem,
		duplicates = [],
		j = 0,
		i = 0;

	// Unless we *know* we can detect duplicates, assume their presence
	//
	// Support: Android <=4.0+
	// Testing for detecting duplicates is unpredictable so instead assume we can't
	// depend on duplicate detection in all browsers without a stable sort.
	hasDuplicate = !support.sortStable;
	sortInput = !support.sortStable && slice.call( results, 0 );
	sort.call( results, sortOrder );

	if ( hasDuplicate ) {
		while ( ( elem = results[ i++ ] ) ) {
			if ( elem === results[ i ] ) {
				j = duplicates.push( i );
			}
		}
		while ( j-- ) {
			splice.call( results, duplicates[ j ], 1 );
		}
	}

	// Clear input after sorting to release objects
	// See https://github.com/jquery/sizzle/pull/225
	sortInput = null;

	return results;
};

jQuery.fn.uniqueSort = function() {
	return this.pushStack( jQuery.uniqueSort( slice.apply( this ) ) );
};

Expr = jQuery.expr = {

	// Can be adjusted by the user
	cacheLength: 50,

	createPseudo: markFunction,

	match: matchExpr,

	attrHandle: {},

	find: {},

	relative: {
		">": { dir: "parentNode", first: true },
		" ": { dir: "parentNode" },
		"+": { dir: "previousSibling", first: true },
		"~": { dir: "previousSibling" }
	},

	preFilter: {
		ATTR: function( match ) {
			match[ 1 ] = match[ 1 ].replace( runescape, funescape );

			// Move the given value to match[3] whether quoted or unquoted
			match[ 3 ] = ( match[ 3 ] || match[ 4 ] || match[ 5 ] || "" )
				.replace( runescape, funescape );

			if ( match[ 2 ] === "~=" ) {
				match[ 3 ] = " " + match[ 3 ] + " ";
			}

			return match.slice( 0, 4 );
		},

		CHILD: function( match ) {

			/* matches from matchExpr["CHILD"]
				1 type (only|nth|...)
				2 what (child|of-type)
				3 argument (even|odd|\d*|\d*n([+-]\d+)?|...)
				4 xn-component of xn+y argument ([+-]?\d*n|)
				5 sign of xn-component
				6 x of xn-component
				7 sign of y-component
				8 y of y-component
			*/
			match[ 1 ] = match[ 1 ].toLowerCase();

			if ( match[ 1 ].slice( 0, 3 ) === "nth" ) {

				// nth-* requires argument
				if ( !match[ 3 ] ) {
					find.error( match[ 0 ] );
				}

				// numeric x and y parameters for Expr.filter.CHILD
				// remember that false/true cast respectively to 0/1
				match[ 4 ] = +( match[ 4 ] ?
					match[ 5 ] + ( match[ 6 ] || 1 ) :
					2 * ( match[ 3 ] === "even" || match[ 3 ] === "odd" )
				);
				match[ 5 ] = +( ( match[ 7 ] + match[ 8 ] ) || match[ 3 ] === "odd" );

			// other types prohibit arguments
			} else if ( match[ 3 ] ) {
				find.error( match[ 0 ] );
			}

			return match;
		},

		PSEUDO: function( match ) {
			var excess,
				unquoted = !match[ 6 ] && match[ 2 ];

			if ( matchExpr.CHILD.test( match[ 0 ] ) ) {
				return null;
			}

			// Accept quoted arguments as-is
			if ( match[ 3 ] ) {
				match[ 2 ] = match[ 4 ] || match[ 5 ] || "";

			// Strip excess characters from unquoted arguments
			} else if ( unquoted && rpseudo.test( unquoted ) &&

				// Get excess from tokenize (recursively)
				( excess = tokenize( unquoted, true ) ) &&

				// advance to the next closing parenthesis
				( excess = unquoted.indexOf( ")", unquoted.length - excess ) - unquoted.length ) ) {

				// excess is a negative index
				match[ 0 ] = match[ 0 ].slice( 0, excess );
				match[ 2 ] = unquoted.slice( 0, excess );
			}

			// Return only captures needed by the pseudo filter method (type and argument)
			return match.slice( 0, 3 );
		}
	},

	filter: {

		TAG: function( nodeNameSelector ) {
			var expectedNodeName = nodeNameSelector.replace( runescape, funescape ).toLowerCase();
			return nodeNameSelector === "*" ?
				function() {
					return true;
				} :
				function( elem ) {
					return nodeName( elem, expectedNodeName );
				};
		},

		CLASS: function( className ) {
			var pattern = classCache[ className + " " ];

			return pattern ||
				( pattern = new RegExp( "(^|" + whitespace + ")" + className +
					"(" + whitespace + "|$)" ) ) &&
				classCache( className, function( elem ) {
					return pattern.test(
						typeof elem.className === "string" && elem.className ||
							typeof elem.getAttribute !== "undefined" &&
								elem.getAttribute( "class" ) ||
							""
					);
				} );
		},

		ATTR: function( name, operator, check ) {
			return function( elem ) {
				var result = find.attr( elem, name );

				if ( result == null ) {
					return operator === "!=";
				}
				if ( !operator ) {
					return true;
				}

				result += "";

				if ( operator === "=" ) {
					return result === check;
				}
				if ( operator === "!=" ) {
					return result !== check;
				}
				if ( operator === "^=" ) {
					return check && result.indexOf( check ) === 0;
				}
				if ( operator === "*=" ) {
					return check && result.indexOf( check ) > -1;
				}
				if ( operator === "$=" ) {
					return check && result.slice( -check.length ) === check;
				}
				if ( operator === "~=" ) {
					return ( " " + result.replace( rwhitespace, " " ) + " " )
						.indexOf( check ) > -1;
				}
				if ( operator === "|=" ) {
					return result === check || result.slice( 0, check.length + 1 ) === check + "-";
				}

				return false;
			};
		},

		CHILD: function( type, what, _argument, first, last ) {
			var simple = type.slice( 0, 3 ) !== "nth",
				forward = type.slice( -4 ) !== "last",
				ofType = what === "of-type";

			return first === 1 && last === 0 ?

				// Shortcut for :nth-*(n)
				function( elem ) {
					return !!elem.parentNode;
				} :

				function( elem, _context, xml ) {
					var cache, outerCache, node, nodeIndex, start,
						dir = simple !== forward ? "nextSibling" : "previousSibling",
						parent = elem.parentNode,
						name = ofType && elem.nodeName.toLowerCase(),
						useCache = !xml && !ofType,
						diff = false;

					if ( parent ) {

						// :(first|last|only)-(child|of-type)
						if ( simple ) {
							while ( dir ) {
								node = elem;
								while ( ( node = node[ dir ] ) ) {
									if ( ofType ?
										nodeName( node, name ) :
										node.nodeType === 1 ) {

										return false;
									}
								}

								// Reverse direction for :only-* (if we haven't yet done so)
								start = dir = type === "only" && !start && "nextSibling";
							}
							return true;
						}

						start = [ forward ? parent.firstChild : parent.lastChild ];

						// non-xml :nth-child(...) stores cache data on `parent`
						if ( forward && useCache ) {

							// Seek `elem` from a previously-cached index
							outerCache = parent[ expando ] || ( parent[ expando ] = {} );
							cache = outerCache[ type ] || [];
							nodeIndex = cache[ 0 ] === dirruns && cache[ 1 ];
							diff = nodeIndex && cache[ 2 ];
							node = nodeIndex && parent.childNodes[ nodeIndex ];

							while ( ( node = ++nodeIndex && node && node[ dir ] ||

								// Fallback to seeking `elem` from the start
								( diff = nodeIndex = 0 ) || start.pop() ) ) {

								// When found, cache indexes on `parent` and break
								if ( node.nodeType === 1 && ++diff && node === elem ) {
									outerCache[ type ] = [ dirruns, nodeIndex, diff ];
									break;
								}
							}

						} else {

							// Use previously-cached element index if available
							if ( useCache ) {
								outerCache = elem[ expando ] || ( elem[ expando ] = {} );
								cache = outerCache[ type ] || [];
								nodeIndex = cache[ 0 ] === dirruns && cache[ 1 ];
								diff = nodeIndex;
							}

							// xml :nth-child(...)
							// or :nth-last-child(...) or :nth(-last)?-of-type(...)
							if ( diff === false ) {

								// Use the same loop as above to seek `elem` from the start
								while ( ( node = ++nodeIndex && node && node[ dir ] ||
									( diff = nodeIndex = 0 ) || start.pop() ) ) {

									if ( ( ofType ?
										nodeName( node, name ) :
										node.nodeType === 1 ) &&
										++diff ) {

										// Cache the index of each encountered element
										if ( useCache ) {
											outerCache = node[ expando ] ||
												( node[ expando ] = {} );
											outerCache[ type ] = [ dirruns, diff ];
										}

										if ( node === elem ) {
											break;
										}
									}
								}
							}
						}

						// Incorporate the offset, then check against cycle size
						diff -= last;
						return diff === first || ( diff % first === 0 && diff / first >= 0 );
					}
				};
		},

		PSEUDO: function( pseudo, argument ) {

			// pseudo-class names are case-insensitive
			// https://www.w3.org/TR/selectors/#pseudo-classes
			// Prioritize by case sensitivity in case custom pseudos are added with uppercase letters
			// Remember that setFilters inherits from pseudos
			var args,
				fn = Expr.pseudos[ pseudo ] || Expr.setFilters[ pseudo.toLowerCase() ] ||
					find.error( "unsupported pseudo: " + pseudo );

			// The user may use createPseudo to indicate that
			// arguments are needed to create the filter function
			// just as jQuery does
			if ( fn[ expando ] ) {
				return fn( argument );
			}

			// But maintain support for old signatures
			if ( fn.length > 1 ) {
				args = [ pseudo, pseudo, "", argument ];
				return Expr.setFilters.hasOwnProperty( pseudo.toLowerCase() ) ?
					markFunction( function( seed, matches ) {
						var idx,
							matched = fn( seed, argument ),
							i = matched.length;
						while ( i-- ) {
							idx = indexOf.call( seed, matched[ i ] );
							seed[ idx ] = !( matches[ idx ] = matched[ i ] );
						}
					} ) :
					function( elem ) {
						return fn( elem, 0, args );
					};
			}

			return fn;
		}
	},

	pseudos: {

		// Potentially complex pseudos
		not: markFunction( function( selector ) {

			// Trim the selector passed to compile
			// to avoid treating leading and trailing
			// spaces as combinators
			var input = [],
				results = [],
				matcher = compile( selector.replace( rtrimCSS, "$1" ) );

			return matcher[ expando ] ?
				markFunction( function( seed, matches, _context, xml ) {
					var elem,
						unmatched = matcher( seed, null, xml, [] ),
						i = seed.length;

					// Match elements unmatched by `matcher`
					while ( i-- ) {
						if ( ( elem = unmatched[ i ] ) ) {
							seed[ i ] = !( matches[ i ] = elem );
						}
					}
				} ) :
				function( elem, _context, xml ) {
					input[ 0 ] = elem;
					matcher( input, null, xml, results );

					// Don't keep the element
					// (see https://github.com/jquery/sizzle/issues/299)
					input[ 0 ] = null;
					return !results.pop();
				};
		} ),

		has: markFunction( function( selector ) {
			return function( elem ) {
				return find( selector, elem ).length > 0;
			};
		} ),

		contains: markFunction( function( text ) {
			text = text.replace( runescape, funescape );
			return function( elem ) {
				return ( elem.textContent || jQuery.text( elem ) ).indexOf( text ) > -1;
			};
		} ),

		// "Whether an element is represented by a :lang() selector
		// is based solely on the element's language value
		// being equal to the identifier C,
		// or beginning with the identifier C immediately followed by "-".
		// The matching of C against the element's language value is performed case-insensitively.
		// The identifier C does not have to be a valid language name."
		// https://www.w3.org/TR/selectors/#lang-pseudo
		lang: markFunction( function( lang ) {

			// lang value must be a valid identifier
			if ( !ridentifier.test( lang || "" ) ) {
				find.error( "unsupported lang: " + lang );
			}
			lang = lang.replace( runescape, funescape ).toLowerCase();
			return function( elem ) {
				var elemLang;
				do {
					if ( ( elemLang = documentIsHTML ?
						elem.lang :
						elem.getAttribute( "xml:lang" ) || elem.getAttribute( "lang" ) ) ) {

						elemLang = elemLang.toLowerCase();
						return elemLang === lang || elemLang.indexOf( lang + "-" ) === 0;
					}
				} while ( ( elem = elem.parentNode ) && elem.nodeType === 1 );
				return false;
			};
		} ),

		// Miscellaneous
		target: function( elem ) {
			var hash = window.location && window.location.hash;
			return hash && hash.slice( 1 ) === elem.id;
		},

		root: function( elem ) {
			return elem === documentElement;
		},

		focus: function( elem ) {
			return elem === safeActiveElement() &&
				document.hasFocus() &&
				!!( elem.type || elem.href || ~elem.tabIndex );
		},

		// Boolean properties
		enabled: createDisabledPseudo( false ),
		disabled: createDisabledPseudo( true ),

		checked: function( elem ) {

			// In CSS3, :checked should return both checked and selected elements
			// https://www.w3.org/TR/2011/REC-css3-selectors-20110929/#checked
			return ( nodeName( elem, "input" ) && !!elem.checked ) ||
				( nodeName( elem, "option" ) && !!elem.selected );
		},

		selected: function( elem ) {

			// Support: IE <=11+
			// Accessing the selectedIndex property
			// forces the browser to treat the default option as
			// selected when in an optgroup.
			if ( elem.parentNode ) {
				// eslint-disable-next-line no-unused-expressions
				elem.parentNode.selectedIndex;
			}

			return elem.selected === true;
		},

		// Contents
		empty: function( elem ) {

			// https://www.w3.org/TR/selectors/#empty-pseudo
			// :empty is negated by element (1) or content nodes (text: 3; cdata: 4; entity ref: 5),
			//   but not by others (comment: 8; processing instruction: 7; etc.)
			// nodeType < 6 works because attributes (2) do not appear as children
			for ( elem = elem.firstChild; elem; elem = elem.nextSibling ) {
				if ( elem.nodeType < 6 ) {
					return false;
				}
			}
			return true;
		},

		parent: function( elem ) {
			return !Expr.pseudos.empty( elem );
		},

		// Element/input types
		header: function( elem ) {
			return rheader.test( elem.nodeName );
		},

		input: function( elem ) {
			return rinputs.test( elem.nodeName );
		},

		button: function( elem ) {
			return nodeName( elem, "input" ) && elem.type === "button" ||
				nodeName( elem, "button" );
		},

		text: function( elem ) {
			var attr;
			return nodeName( elem, "input" ) && elem.type === "text" &&

				// Support: IE <10 only
				// New HTML5 attribute values (e.g., "search") appear
				// with elem.type === "text"
				( ( attr = elem.getAttribute( "type" ) ) == null ||
					attr.toLowerCase() === "text" );
		},

		// Position-in-collection
		first: createPositionalPseudo( function() {
			return [ 0 ];
		} ),

		last: createPositionalPseudo( function( _matchIndexes, length ) {
			return [ length - 1 ];
		} ),

		eq: createPositionalPseudo( function( _matchIndexes, length, argument ) {
			return [ argument < 0 ? argument + length : argument ];
		} ),

		even: createPositionalPseudo( function( matchIndexes, length ) {
			var i = 0;
			for ( ; i < length; i += 2 ) {
				matchIndexes.push( i );
			}
			return matchIndexes;
		} ),

		odd: createPositionalPseudo( function( matchIndexes, length ) {
			var i = 1;
			for ( ; i < length; i += 2 ) {
				matchIndexes.push( i );
			}
			return matchIndexes;
		} ),

		lt: createPositionalPseudo( function( matchIndexes, length, argument ) {
			var i;

			if ( argument < 0 ) {
				i = argument + length;
			} else if ( argument > length ) {
				i = length;
			} else {
				i = argument;
			}

			for ( ; --i >= 0; ) {
				matchIndexes.push( i );
			}
			return matchIndexes;
		} ),

		gt: createPositionalPseudo( function( matchIndexes, length, argument ) {
			var i = argument < 0 ? argument + length : argument;
			for ( ; ++i < length; ) {
				matchIndexes.push( i );
			}
			return matchIndexes;
		} )
	}
};

Expr.pseudos.nth = Expr.pseudos.eq;

// Add button/input type pseudos
for ( i in { radio: true, checkbox: true, file: true, password: true, image: true } ) {
	Expr.pseudos[ i ] = createInputPseudo( i );
}
for ( i in { submit: true, reset: true } ) {
	Expr.pseudos[ i ] = createButtonPseudo( i );
}

// Easy API for creating new setFilters
function setFilters() {}
setFilters.prototype = Expr.filters = Expr.pseudos;
Expr.setFilters = new setFilters();

function tokenize( selector, parseOnly ) {
	var matched, match, tokens, type,
		soFar, groups, preFilters,
		cached = tokenCache[ selector + " " ];

	if ( cached ) {
		return parseOnly ? 0 : cached.slice( 0 );
	}

	soFar = selector;
	groups = [];
	preFilters = Expr.preFilter;

	while ( soFar ) {

		// Comma and first run
		if ( !matched || ( match = rcomma.exec( soFar ) ) ) {
			if ( match ) {

				// Don't consume trailing commas as valid
				soFar = soFar.slice( match[ 0 ].length ) || soFar;
			}
			groups.push( ( tokens = [] ) );
		}

		matched = false;

		// Combinators
		if ( ( match = rleadingCombinator.exec( soFar ) ) ) {
			matched = match.shift();
			tokens.push( {
				value: matched,

				// Cast descendant combinators to space
				type: match[ 0 ].replace( rtrimCSS, " " )
			} );
			soFar = soFar.slice( matched.length );
		}

		// Filters
		for ( type in Expr.filter ) {
			if ( ( match = matchExpr[ type ].exec( soFar ) ) && ( !preFilters[ type ] ||
				( match = preFilters[ type ]( match ) ) ) ) {
				matched = match.shift();
				tokens.push( {
					value: matched,
					type: type,
					matches: match
				} );
				soFar = soFar.slice( matched.length );
			}
		}

		if ( !matched ) {
			break;
		}
	}

	// Return the length of the invalid excess
	// if we're just parsing
	// Otherwise, throw an error or return tokens
	if ( parseOnly ) {
		return soFar.length;
	}

	return soFar ?
		find.error( selector ) :

		// Cache the tokens
		tokenCache( selector, groups ).slice( 0 );
}

function toSelector( tokens ) {
	var i = 0,
		len = tokens.length,
		selector = "";
	for ( ; i < len; i++ ) {
		selector += tokens[ i ].value;
	}
	return selector;
}

function addCombinator( matcher, combinator, base ) {
	var dir = combinator.dir,
		skip = combinator.next,
		key = skip || dir,
		checkNonElements = base && key === "parentNode",
		doneName = done++;

	return combinator.first ?

		// Check against closest ancestor/preceding element
		function( elem, context, xml ) {
			while ( ( elem = elem[ dir ] ) ) {
				if ( elem.nodeType === 1 || checkNonElements ) {
					return matcher( elem, context, xml );
				}
			}
			return false;
		} :

		// Check against all ancestor/preceding elements
		function( elem, context, xml ) {
			var oldCache, outerCache,
				newCache = [ dirruns, doneName ];

			// We can't set arbitrary data on XML nodes, so they don't benefit from combinator caching
			if ( xml ) {
				while ( ( elem = elem[ dir ] ) ) {
					if ( elem.nodeType === 1 || checkNonElements ) {
						if ( matcher( elem, context, xml ) ) {
							return true;
						}
					}
				}
			} else {
				while ( ( elem = elem[ dir ] ) ) {
					if ( elem.nodeType === 1 || checkNonElements ) {
						outerCache = elem[ expando ] || ( elem[ expando ] = {} );

						if ( skip && nodeName( elem, skip ) ) {
							elem = elem[ dir ] || elem;
						} else if ( ( oldCache = outerCache[ key ] ) &&
							oldCache[ 0 ] === dirruns && oldCache[ 1 ] === doneName ) {

							// Assign to newCache so results back-propagate to previous elements
							return ( newCache[ 2 ] = oldCache[ 2 ] );
						} else {

							// Reuse newcache so results back-propagate to previous elements
							outerCache[ key ] = newCache;

							// A match means we're done; a fail means we have to keep checking
							if ( ( newCache[ 2 ] = matcher( elem, context, xml ) ) ) {
								return true;
							}
						}
					}
				}
			}
			return false;
		};
}

function elementMatcher( matchers ) {
	return matchers.length > 1 ?
		function( elem, context, xml ) {
			var i = matchers.length;
			while ( i-- ) {
				if ( !matchers[ i ]( elem, context, xml ) ) {
					return false;
				}
			}
			return true;
		} :
		matchers[ 0 ];
}

function multipleContexts( selector, contexts, results ) {
	var i = 0,
		len = contexts.length;
	for ( ; i < len; i++ ) {
		find( selector, contexts[ i ], results );
	}
	return results;
}

function condense( unmatched, map, filter, context, xml ) {
	var elem,
		newUnmatched = [],
		i = 0,
		len = unmatched.length,
		mapped = map != null;

	for ( ; i < len; i++ ) {
		if ( ( elem = unmatched[ i ] ) ) {
			if ( !filter || filter( elem, context, xml ) ) {
				newUnmatched.push( elem );
				if ( mapped ) {
					map.push( i );
				}
			}
		}
	}

	return newUnmatched;
}

function setMatcher( preFilter, selector, matcher, postFilter, postFinder, postSelector ) {
	if ( postFilter && !postFilter[ expando ] ) {
		postFilter = setMatcher( postFilter );
	}
	if ( postFinder && !postFinder[ expando ] ) {
		postFinder = setMatcher( postFinder, postSelector );
	}
	return markFunction( function( seed, results, context, xml ) {
		var temp, i, elem, matcherOut,
			preMap = [],
			postMap = [],
			preexisting = results.length,

			// Get initial elements from seed or context
			elems = seed ||
				multipleContexts( selector || "*",
					context.nodeType ? [ context ] : context, [] ),

			// Prefilter to get matcher input, preserving a map for seed-results synchronization
			matcherIn = preFilter && ( seed || !selector ) ?
				condense( elems, preMap, preFilter, context, xml ) :
				elems;

		if ( matcher ) {

			// If we have a postFinder, or filtered seed, or non-seed postFilter
			// or preexisting results,
			matcherOut = postFinder || ( seed ? preFilter : preexisting || postFilter ) ?

				// ...intermediate processing is necessary
				[] :

				// ...otherwise use results directly
				results;

			// Find primary matches
			matcher( matcherIn, matcherOut, context, xml );
		} else {
			matcherOut = matcherIn;
		}

		// Apply postFilter
		if ( postFilter ) {
			temp = condense( matcherOut, postMap );
			postFilter( temp, [], context, xml );

			// Un-match failing elements by moving them back to matcherIn
			i = temp.length;
			while ( i-- ) {
				if ( ( elem = temp[ i ] ) ) {
					matcherOut[ postMap[ i ] ] = !( matcherIn[ postMap[ i ] ] = elem );
				}
			}
		}

		if ( seed ) {
			if ( postFinder || preFilter ) {
				if ( postFinder ) {

					// Get the final matcherOut by condensing this intermediate into postFinder contexts
					temp = [];
					i = matcherOut.length;
					while ( i-- ) {
						if ( ( elem = matcherOut[ i ] ) ) {

							// Restore matcherIn since elem is not yet a final match
							temp.push( ( matcherIn[ i ] = elem ) );
						}
					}
					postFinder( null, ( matcherOut = [] ), temp, xml );
				}

				// Move matched elements from seed to results to keep them synchronized
				i = matcherOut.length;
				while ( i-- ) {
					if ( ( elem = matcherOut[ i ] ) &&
						( temp = postFinder ? indexOf.call( seed, elem ) : preMap[ i ] ) > -1 ) {

						seed[ temp ] = !( results[ temp ] = elem );
					}
				}
			}

		// Add elements to results, through postFinder if defined
		} else {
			matcherOut = condense(
				matcherOut === results ?
					matcherOut.splice( preexisting, matcherOut.length ) :
					matcherOut
			);
			if ( postFinder ) {
				postFinder( null, results, matcherOut, xml );
			} else {
				push.apply( results, matcherOut );
			}
		}
	} );
}

function matcherFromTokens( tokens ) {
	var checkContext, matcher, j,
		len = tokens.length,
		leadingRelative = Expr.relative[ tokens[ 0 ].type ],
		implicitRelative = leadingRelative || Expr.relative[ " " ],
		i = leadingRelative ? 1 : 0,

		// The foundational matcher ensures that elements are reachable from top-level context(s)
		matchContext = addCombinator( function( elem ) {
			return elem === checkContext;
		}, implicitRelative, true ),
		matchAnyContext = addCombinator( function( elem ) {
			return indexOf.call( checkContext, elem ) > -1;
		}, implicitRelative, true ),
		matchers = [ function( elem, context, xml ) {

			// Support: IE 11+, Edge 17 - 18+
			// IE/Edge sometimes throw a "Permission denied" error when strict-comparing
			// two documents; shallow comparisons work.
			// eslint-disable-next-line eqeqeq
			var ret = ( !leadingRelative && ( xml || context != outermostContext ) ) || (
				( checkContext = context ).nodeType ?
					matchContext( elem, context, xml ) :
					matchAnyContext( elem, context, xml ) );

			// Avoid hanging onto element
			// (see https://github.com/jquery/sizzle/issues/299)
			checkContext = null;
			return ret;
		} ];

	for ( ; i < len; i++ ) {
		if ( ( matcher = Expr.relative[ tokens[ i ].type ] ) ) {
			matchers = [ addCombinator( elementMatcher( matchers ), matcher ) ];
		} else {
			matcher = Expr.filter[ tokens[ i ].type ].apply( null, tokens[ i ].matches );

			// Return special upon seeing a positional matcher
			if ( matcher[ expando ] ) {

				// Find the next relative operator (if any) for proper handling
				j = ++i;
				for ( ; j < len; j++ ) {
					if ( Expr.relative[ tokens[ j ].type ] ) {
						break;
					}
				}
				return setMatcher(
					i > 1 && elementMatcher( matchers ),
					i > 1 && toSelector(

						// If the preceding token was a descendant combinator, insert an implicit any-element `*`
						tokens.slice( 0, i - 1 )
							.concat( { value: tokens[ i - 2 ].type === " " ? "*" : "" } )
					).replace( rtrimCSS, "$1" ),
					matcher,
					i < j && matcherFromTokens( tokens.slice( i, j ) ),
					j < len && matcherFromTokens( ( tokens = tokens.slice( j ) ) ),
					j < len && toSelector( tokens )
				);
			}
			matchers.push( matcher );
		}
	}

	return elementMatcher( matchers );
}

function matcherFromGroupMatchers( elementMatchers, setMatchers ) {
	var bySet = setMatchers.length > 0,
		byElement = elementMatchers.length > 0,
		superMatcher = function( seed, context, xml, results, outermost ) {
			var elem, j, matcher,
				matchedCount = 0,
				i = "0",
				unmatched = seed && [],
				setMatched = [],
				contextBackup = outermostContext,

				// We must always have either seed elements or outermost context
				elems = seed || byElement && Expr.find.TAG( "*", outermost ),

				// Use integer dirruns iff this is the outermost matcher
				dirrunsUnique = ( dirruns += contextBackup == null ? 1 : Math.random() || 0.1 ),
				len = elems.length;

			if ( outermost ) {

				// Support: IE 11+, Edge 17 - 18+
				// IE/Edge sometimes throw a "Permission denied" error when strict-comparing
				// two documents; shallow comparisons work.
				// eslint-disable-next-line eqeqeq
				outermostContext = context == document || context || outermost;
			}

			// Add elements passing elementMatchers directly to results
			// Support: iOS <=7 - 9 only
			// Tolerate NodeList properties (IE: "length"; Safari: <number>) matching
			// elements by id. (see trac-14142)
			for ( ; i !== len && ( elem = elems[ i ] ) != null; i++ ) {
				if ( byElement && elem ) {
					j = 0;

					// Support: IE 11+, Edge 17 - 18+
					// IE/Edge sometimes throw a "Permission denied" error when strict-comparing
					// two documents; shallow comparisons work.
					// eslint-disable-next-line eqeqeq
					if ( !context && elem.ownerDocument != document ) {
						setDocument( elem );
						xml = !documentIsHTML;
					}
					while ( ( matcher = elementMatchers[ j++ ] ) ) {
						if ( matcher( elem, context || document, xml ) ) {
							push.call( results, elem );
							break;
						}
					}
					if ( outermost ) {
						dirruns = dirrunsUnique;
					}
				}

				// Track unmatched elements for set filters
				if ( bySet ) {

					// They will have gone through all possible matchers
					if ( ( elem = !matcher && elem ) ) {
						matchedCount--;
					}

					// Lengthen the array for every element, matched or not
					if ( seed ) {
						unmatched.push( elem );
					}
				}
			}

			// `i` is now the count of elements visited above, and adding it to `matchedCount`
			// makes the latter nonnegative.
			matchedCount += i;

			// Apply set filters to unmatched elements
			// NOTE: This can be skipped if there are no unmatched elements (i.e., `matchedCount`
			// equals `i`), unless we didn't visit _any_ elements in the above loop because we have
			// no element matchers and no seed.
			// Incrementing an initially-string "0" `i` allows `i` to remain a string only in that
			// case, which will result in a "00" `matchedCount` that differs from `i` but is also
			// numerically zero.
			if ( bySet && i !== matchedCount ) {
				j = 0;
				while ( ( matcher = setMatchers[ j++ ] ) ) {
					matcher( unmatched, setMatched, context, xml );
				}

				if ( seed ) {

					// Reintegrate element matches to eliminate the need for sorting
					if ( matchedCount > 0 ) {
						while ( i-- ) {
							if ( !( unmatched[ i ] || setMatched[ i ] ) ) {
								setMatched[ i ] = pop.call( results );
							}
						}
					}

					// Discard index placeholder values to get only actual matches
					setMatched = condense( setMatched );
				}

				// Add matches to results
				push.apply( results, setMatched );

				// Seedless set matches succeeding multiple successful matchers stipulate sorting
				if ( outermost && !seed && setMatched.length > 0 &&
					( matchedCount + setMatchers.length ) > 1 ) {

					jQuery.uniqueSort( results );
				}
			}

			// Override manipulation of globals by nested matchers
			if ( outermost ) {
				dirruns = dirrunsUnique;
				outermostContext = contextBackup;
			}

			return unmatched;
		};

	return bySet ?
		markFunction( superMatcher ) :
		superMatcher;
}

function compile( selector, match /* Internal Use Only */ ) {
	var i,
		setMatchers = [],
		elementMatchers = [],
		cached = compilerCache[ selector + " " ];

	if ( !cached ) {

		// Generate a function of recursive functions that can be used to check each element
		if ( !match ) {
			match = tokenize( selector );
		}
		i = match.length;
		while ( i-- ) {
			cached = matcherFromTokens( match[ i ] );
			if ( cached[ expando ] ) {
				setMatchers.push( cached );
			} else {
				elementMatchers.push( cached );
			}
		}

		// Cache the compiled function
		cached = compilerCache( selector,
			matcherFromGroupMatchers( elementMatchers, setMatchers ) );

		// Save selector and tokenization
		cached.selector = selector;
	}
	return cached;
}

/**
 * A low-level selection function that works with jQuery's compiled
 *  selector functions
 * @param {String|Function} selector A selector or a pre-compiled
 *  selector function built with jQuery selector compile
 * @param {Element} context
 * @param {Array} [results]
 * @param {Array} [seed] A set of elements to match against
 */
function select( selector, context, results, seed ) {
	var i, tokens, token, type, find,
		compiled = typeof selector === "function" && selector,
		match = !seed && tokenize( ( selector = compiled.selector || selector ) );

	results = results || [];

	// Try to minimize operations if there is only one selector in the list and no seed
	// (the latter of which guarantees us context)
	if ( match.length === 1 ) {

		// Reduce context if the leading compound selector is an ID
		tokens = match[ 0 ] = match[ 0 ].slice( 0 );
		if ( tokens.length > 2 && ( token = tokens[ 0 ] ).type === "ID" &&
				context.nodeType === 9 && documentIsHTML && Expr.relative[ tokens[ 1 ].type ] ) {

			context = ( Expr.find.ID(
				token.matches[ 0 ].replace( runescape, funescape ),
				context
			) || [] )[ 0 ];
			if ( !context ) {
				return results;

			// Precompiled matchers will still verify ancestry, so step up a level
			} else if ( compiled ) {
				context = context.parentNode;
			}

			selector = selector.slice( tokens.shift().value.length );
		}

		// Fetch a seed set for right-to-left matching
		i = matchExpr.needsContext.test( selector ) ? 0 : tokens.length;
		while ( i-- ) {
			token = tokens[ i ];

			// Abort if we hit a combinator
			if ( Expr.relative[ ( type = token.type ) ] ) {
				break;
			}
			if ( ( find = Expr.find[ type ] ) ) {

				// Search, expanding context for leading sibling combinators
				if ( ( seed = find(
					token.matches[ 0 ].replace( runescape, funescape ),
					rsibling.test( tokens[ 0 ].type ) &&
						testContext( context.parentNode ) || context
				) ) ) {

					// If seed is empty or no tokens remain, we can return early
					tokens.splice( i, 1 );
					selector = seed.length && toSelector( tokens );
					if ( !selector ) {
						push.apply( results, seed );
						return results;
					}

					break;
				}
			}
		}
	}

	// Compile and execute a filtering function if one is not provided
	// Provide `match` to avoid retokenization if we modified the selector above
	( compiled || compile( selector, match ) )(
		seed,
		context,
		!documentIsHTML,
		results,
		!context || rsibling.test( selector ) && testContext( context.parentNode ) || context
	);
	return results;
}

// One-time assignments

// Support: Android <=4.0 - 4.1+
// Sort stability
support.sortStable = expando.split( "" ).sort( sortOrder ).join( "" ) === expando;

// Initialize against the default document
setDocument();

// Support: Android <=4.0 - 4.1+
// Detached nodes confoundingly follow *each other*
support.sortDetached = assert( function( el ) {

	// Should return 1, but returns 4 (following)
	return el.compareDocumentPosition( document.createElement( "fieldset" ) ) & 1;
} );

jQuery.find = find;

// Deprecated
jQuery.expr[ ":" ] = jQuery.expr.pseudos;
jQuery.unique = jQuery.uniqueSort;

// These have always been private, but they used to be documented as part of
// Sizzle so let's maintain them for now for backwards compatibility purposes.
find.compile = compile;
find.select = select;
find.setDocument = setDocument;
find.tokenize = tokenize;

find.escape = jQuery.escapeSelector;
find.getText = jQuery.text;
find.isXML = jQuery.isXMLDoc;
find.selectors = jQuery.expr;
find.support = jQuery.support;
find.uniqueSort = jQuery.uniqueSort;

	/* eslint-enable */

} )();


var dir = function( elem, dir, until ) {
	var matched = [],
		truncate = until !== undefined;

	while ( ( elem = elem[ dir ] ) && elem.nodeType !== 9 ) {
		if ( elem.nodeType === 1 ) {
			if ( truncate && jQuery( elem ).is( until ) ) {
				break;
			}
			matched.push( elem );
		}
	}
	return matched;
};


var siblings = function( n, elem ) {
	var matched = [];

	for ( ; n; n = n.nextSibling ) {
		if ( n.nodeType === 1 && n !== elem ) {
			matched.push( n );
		}
	}

	return matched;
};


var rneedsContext = jQuery.expr.match.needsContext;

var rsingleTag = ( /^<([a-z][^\/\0>:\x20\t\r\n\f]*)[\x20\t\r\n\f]*\/?>(?:<\/\1>|)$/i );



// Implement the identical functionality for filter and not
function winnow( elements, qualifier, not ) {
	if ( isFunction( qualifier ) ) {
		return jQuery.grep( elements, function( elem, i ) {
			return !!qualifier.call( elem, i, elem ) !== not;
		} );
	}

	// Single element
	if ( qualifier.nodeType ) {
		return jQuery.grep( elements, function( elem ) {
			return ( elem === qualifier ) !== not;
		} );
	}

	// Arraylike of elements (jQuery, arguments, Array)
	if ( typeof qualifier !== "string" ) {
		return jQuery.grep( elements, function( elem ) {
			return ( indexOf.call( qualifier, elem ) > -1 ) !== not;
		} );
	}

	// Filtered directly for both simple and complex selectors
	return jQuery.filter( qualifier, elements, not );
}

jQuery.filter = function( expr, elems, not ) {
	var elem = elems[ 0 ];

	if ( not ) {
		expr = ":not(" + expr + ")";
	}

	if ( elems.length === 1 && elem.nodeType === 1 ) {
		return jQuery.find.matchesSelector( elem, expr ) ? [ elem ] : [];
	}

	return jQuery.find.matches( expr, jQuery.grep( elems, function( elem ) {
		return elem.nodeType === 1;
	} ) );
};

jQuery.fn.extend( {
	find: function( selector ) {
		var i, ret,
			len = this.length,
			self = this;

		if ( typeof selector !== "string" ) {
			return this.pushStack( jQuery( selector ).filter( function() {
				for ( i = 0; i < len; i++ ) {
					if ( jQuery.contains( self[ i ], this ) ) {
						return true;
					}
				}
			} ) );
		}

		ret = this.pushStack( [] );

		for ( i = 0; i < len; i++ ) {
			jQuery.find( selector, self[ i ], ret );
		}

		return len > 1 ? jQuery.uniqueSort( ret ) : ret;
	},
	filter: function( selector ) {
		return this.pushStack( winnow( this, selector || [], false ) );
	},
	not: function( selector ) {
		return this.pushStack( winnow( this, selector || [], true ) );
	},
	is: function( selector ) {
		return !!winnow(
			this,

			// If this is a positional/relative selector, check membership in the returned set
			// so $("p:first").is("p:last") won't return true for a doc with two "p".
			typeof selector === "string" && rneedsContext.test( selector ) ?
				jQuery( selector ) :
				selector || [],
			false
		).length;
	}
} );


// Initialize a jQuery object


// A central reference to the root jQuery(document)
var rootjQuery,

	// A simple way to check for HTML strings
	// Prioritize #id over <tag> to avoid XSS via location.hash (trac-9521)
	// Strict HTML recognition (trac-11290: must start with <)
	// Shortcut simple #id case for speed
	rquickExpr = /^(?:\s*(<[\w\W]+>)[^>]*|#([\w-]+))$/,

	init = jQuery.fn.init = function( selector, context, root ) {
		var match, elem;

		// HANDLE: $(""), $(null), $(undefined), $(false)
		if ( !selector ) {
			return this;
		}

		// Method init() accepts an alternate rootjQuery
		// so migrate can support jQuery.sub (gh-2101)
		root = root || rootjQuery;

		// Handle HTML strings
		if ( typeof selector === "string" ) {
			if ( selector[ 0 ] === "<" &&
				selector[ selector.length - 1 ] === ">" &&
				selector.length >= 3 ) {

				// Assume that strings that start and end with <> are HTML and skip the regex check
				match = [ null, selector, null ];

			} else {
				match = rquickExpr.exec( selector );
			}

			// Match html or make sure no context is specified for #id
			if ( match && ( match[ 1 ] || !context ) ) {

				// HANDLE: $(html) -> $(array)
				if ( match[ 1 ] ) {
					context = context instanceof jQuery ? context[ 0 ] : context;

					// Option to run scripts is true for back-compat
					// Intentionally let the error be thrown if parseHTML is not present
					jQuery.merge( this, jQuery.parseHTML(
						match[ 1 ],
						context && context.nodeType ? context.ownerDocument || context : document,
						true
					) );

					// HANDLE: $(html, props)
					if ( rsingleTag.test( match[ 1 ] ) && jQuery.isPlainObject( context ) ) {
						for ( match in context ) {

							// Properties of context are called as methods if possible
							if ( isFunction( this[ match ] ) ) {
								this[ match ]( context[ match ] );

							// ...and otherwise set as attributes
							} else {
								this.attr( match, context[ match ] );
							}
						}
					}

					return this;

				// HANDLE: $(#id)
				} else {
					elem = document.getElementById( match[ 2 ] );

					if ( elem ) {

						// Inject the element directly into the jQuery object
						this[ 0 ] = elem;
						this.length = 1;
					}
					return this;
				}

			// HANDLE: $(expr, $(...))
			} else if ( !context || context.jquery ) {
				return ( context || root ).find( selector );

			// HANDLE: $(expr, context)
			// (which is just equivalent to: $(context).find(expr)
			} else {
				return this.constructor( context ).find( selector );
			}

		// HANDLE: $(DOMElement)
		} else if ( selector.nodeType ) {
			this[ 0 ] = selector;
			this.length = 1;
			return this;

		// HANDLE: $(function)
		// Shortcut for document ready
		} else if ( isFunction( selector ) ) {
			return root.ready !== undefined ?
				root.ready( selector ) :

				// Execute immediately if ready is not present
				selector( jQuery );
		}

		return jQuery.makeArray( selector, this );
	};

// Give the init function the jQuery prototype for later instantiation
init.prototype = jQuery.fn;

// Initialize central reference
rootjQuery = jQuery( document );


var rparentsprev = /^(?:parents|prev(?:Until|All))/,

	// Methods guaranteed to produce a unique set when starting from a unique set
	guaranteedUnique = {
		children: true,
		contents: true,
		next: true,
		prev: true
	};

jQuery.fn.extend( {
	has: function( target ) {
		var targets = jQuery( target, this ),
			l = targets.length;

		return this.filter( function() {
			var i = 0;
			for ( ; i < l; i++ ) {
				if ( jQuery.contains( this, targets[ i ] ) ) {
					return true;
				}
			}
		} );
	},

	closest: function( selectors, context ) {
		var cur,
			i = 0,
			l = this.length,
			matched = [],
			targets = typeof selectors !== "string" && jQuery( selectors );

		// Positional selectors never match, since there's no _selection_ context
		if ( !rneedsContext.test( selectors ) ) {
			for ( ; i < l; i++ ) {
				for ( cur = this[ i ]; cur && cur !== context; cur = cur.parentNode ) {

					// Always skip document fragments
					if ( cur.nodeType < 11 && ( targets ?
						targets.index( cur ) > -1 :

						// Don't pass non-elements to jQuery#find
						cur.nodeType === 1 &&
							jQuery.find.matchesSelector( cur, selectors ) ) ) {

						matched.push( cur );
						break;
					}
				}
			}
		}

		return this.pushStack( matched.length > 1 ? jQuery.uniqueSort( matched ) : matched );
	},

	// Determine the position of an element within the set
	index: function( elem ) {

		// No argument, return index in parent
		if ( !elem ) {
			return ( this[ 0 ] && this[ 0 ].parentNode ) ? this.first().prevAll().length : -1;
		}

		// Index in selector
		if ( typeof elem === "string" ) {
			return indexOf.call( jQuery( elem ), this[ 0 ] );
		}

		// Locate the position of the desired element
		return indexOf.call( this,

			// If it receives a jQuery object, the first element is used
			elem.jquery ? elem[ 0 ] : elem
		);
	},

	add: function( selector, context ) {
		return this.pushStack(
			jQuery.uniqueSort(
				jQuery.merge( this.get(), jQuery( selector, context ) )
			)
		);
	},

	addBack: function( selector ) {
		return this.add( selector == null ?
			this.prevObject : this.prevObject.filter( selector )
		);
	}
} );

function sibling( cur, dir ) {
	while ( ( cur = cur[ dir ] ) && cur.nodeType !== 1 ) {}
	return cur;
}

jQuery.each( {
	parent: function( elem ) {
		var parent = elem.parentNode;
		return parent && parent.nodeType !== 11 ? parent : null;
	},
	parents: function( elem ) {
		return dir( elem, "parentNode" );
	},
	parentsUntil: function( elem, _i, until ) {
		return dir( elem, "parentNode", until );
	},
	next: function( elem ) {
		return sibling( elem, "nextSibling" );
	},
	prev: function( elem ) {
		return sibling( elem, "previousSibling" );
	},
	nextAll: function( elem ) {
		return dir( elem, "nextSibling" );
	},
	prevAll: function( elem ) {
		return dir( elem, "previousSibling" );
	},
	nextUntil: function( elem, _i, until ) {
		return dir( elem, "nextSibling", until );
	},
	prevUntil: function( elem, _i, until ) {
		return dir( elem, "previousSibling", until );
	},
	siblings: function( elem ) {
		return siblings( ( elem.parentNode || {} ).firstChild, elem );
	},
	children: function( elem ) {
		return siblings( elem.firstChild );
	},
	contents: function( elem ) {
		if ( elem.contentDocument != null &&

			// Support: IE 11+
			// <object> elements with no `data` attribute has an object
			// `contentDocument` with a `null` prototype.
			getProto( elem.contentDocument ) ) {

			return elem.contentDocument;
		}

		// Support: IE 9 - 11 only, iOS 7 only, Android Browser <=4.3 only
		// Treat the template element as a regular one in browsers that
		// don't support it.
		if ( nodeName( elem, "template" ) ) {
			elem = elem.content || elem;
		}

		return jQuery.merge( [], elem.childNodes );
	}
}, function( name, fn ) {
	jQuery.fn[ name ] = function( until, selector ) {
		var matched = jQuery.map( this, fn, until );

		if ( name.slice( -5 ) !== "Until" ) {
			selector = until;
		}

		if ( selector && typeof selector === "string" ) {
			matched = jQuery.filter( selector, matched );
		}

		if ( this.length > 1 ) {

			// Remove duplicates
			if ( !guaranteedUnique[ name ] ) {
				jQuery.uniqueSort( matched );
			}

			// Reverse order for parents* and prev-derivatives
			if ( rparentsprev.test( name ) ) {
				matched.reverse();
			}
		}

		return this.pushStack( matched );
	};
} );
var rnothtmlwhite = ( /[^\x20\t\r\n\f]+/g );



// Convert String-formatted options into Object-formatted ones
function createOptions( options ) {
	var object = {};
	jQuery.each( options.match( rnothtmlwhite ) || [], function( _, flag ) {
		object[ flag ] = true;
	} );
	return object;
}

/*
 * Create a callback list using the following parameters:
 *
 *	options: an optional list of space-separated options that will change how
 *			the callback list behaves or a more traditional option object
 *
 * By default a callback list will act like an event callback list and can be
 * "fired" multiple times.
 *
 * Possible options:
 *
 *	once:			will ensure the callback list can only be fired once (like a Deferred)
 *
 *	memory:			will keep track of previous values and will call any callback added
 *					after the list has been fired right away with the latest "memorized"
 *					values (like a Deferred)
 *
 *	unique:			will ensure a callback can only be added once (no duplicate in the list)
 *
 *	stopOnFalse:	interrupt callings when a callback returns false
 *
 */
jQuery.Callbacks = function( options ) {

	// Convert options from String-formatted to Object-formatted if needed
	// (we check in cache first)
	options = typeof options === "string" ?
		createOptions( options ) :
		jQuery.extend( {}, options );

	var // Flag to know if list is currently firing
		firing,

		// Last fire value for non-forgettable lists
		memory,

		// Flag to know if list was already fired
		fired,

		// Flag to prevent firing
		locked,

		// Actual callback list
		list = [],

		// Queue of execution data for repeatable lists
		queue = [],

		// Index of currently firing callback (modified by add/remove as needed)
		firingIndex = -1,

		// Fire callbacks
		fire = function() {

			// Enforce single-firing
			locked = locked || options.once;

			// Execute callbacks for all pending executions,
			// respecting firingIndex overrides and runtime changes
			fired = firing = true;
			for ( ; queue.length; firingIndex = -1 ) {
				memory = queue.shift();
				while ( ++firingIndex < list.length ) {

					// Run callback and check for early termination
					if ( list[ firingIndex ].apply( memory[ 0 ], memory[ 1 ] ) === false &&
						options.stopOnFalse ) {

						// Jump to end and forget the data so .add doesn't re-fire
						firingIndex = list.length;
						memory = false;
					}
				}
			}

			// Forget the data if we're done with it
			if ( !options.memory ) {
				memory = false;
			}

			firing = false;

			// Clean up if we're done firing for good
			if ( locked ) {

				// Keep an empty list if we have data for future add calls
				if ( memory ) {
					list = [];

				// Otherwise, this object is spent
				} else {
					list = "";
				}
			}
		},

		// Actual Callbacks object
		self = {

			// Add a callback or a collection of callbacks to the list
			add: function() {
				if ( list ) {

					// If we have memory from a past run, we should fire after adding
					if ( memory && !firing ) {
						firingIndex = list.length - 1;
						queue.push( memory );
					}

					( function add( args ) {
						jQuery.each( args, function( _, arg ) {
							if ( isFunction( arg ) ) {
								if ( !options.unique || !self.has( arg ) ) {
									list.push( arg );
								}
							} else if ( arg && arg.length && toType( arg ) !== "string" ) {

								// Inspect recursively
								add( arg );
							}
						} );
					} )( arguments );

					if ( memory && !firing ) {
						fire();
					}
				}
				return this;
			},

			// Remove a callback from the list
			remove: function() {
				jQuery.each( arguments, function( _, arg ) {
					var index;
					while ( ( index = jQuery.inArray( arg, list, index ) ) > -1 ) {
						list.splice( index, 1 );

						// Handle firing indexes
						if ( index <= firingIndex ) {
							firingIndex--;
						}
					}
				} );
				return this;
			},

			// Check if a given callback is in the list.
			// If no argument is given, return whether or not list has callbacks attached.
			has: function( fn ) {
				return fn ?
					jQuery.inArray( fn, list ) > -1 :
					list.length > 0;
			},

			// Remove all callbacks from the list
			empty: function() {
				if ( list ) {
					list = [];
				}
				return this;
			},

			// Disable .fire and .add
			// Abort any current/pending executions
			// Clear all callbacks and values
			disable: function() {
				locked = queue = [];
				list = memory = "";
				return this;
			},
			disabled: function() {
				return !list;
			},

			// Disable .fire
			// Also disable .add unless we have memory (since it would have no effect)
			// Abort any pending executions
			lock: function() {
				locked = queue = [];
				if ( !memory && !firing ) {
					list = memory = "";
				}
				return this;
			},
			locked: function() {
				return !!locked;
			},

			// Call all callbacks with the given context and arguments
			fireWith: function( context, args ) {
				if ( !locked ) {
					args = args || [];
					args = [ context, args.slice ? args.slice() : args ];
					queue.push( args );
					if ( !firing ) {
						fire();
					}
				}
				return this;
			},

			// Call all the callbacks with the given arguments
			fire: function() {
				self.fireWith( this, arguments );
				return this;
			},

			// To know if the callbacks have already been called at least once
			fired: function() {
				return !!fired;
			}
		};

	return self;
};


function Identity( v ) {
	return v;
}
function Thrower( ex ) {
	throw ex;
}

function adoptValue( value, resolve, reject, noValue ) {
	var method;

	try {

		// Check for promise aspect first to privilege synchronous behavior
		if ( value && isFunction( ( method = value.promise ) ) ) {
			method.call( value ).done( resolve ).fail( reject );

		// Other thenables
		} else if ( value && isFunction( ( method = value.then ) ) ) {
			method.call( value, resolve, reject );

		// Other non-thenables
		} else {

			// Control `resolve` arguments by letting Array#slice cast boolean `noValue` to integer:
			// * false: [ value ].slice( 0 ) => resolve( value )
			// * true: [ value ].slice( 1 ) => resolve()
			resolve.apply( undefined, [ value ].slice( noValue ) );
		}

	// For Promises/A+, convert exceptions into rejections
	// Since jQuery.when doesn't unwrap thenables, we can skip the extra checks appearing in
	// Deferred#then to conditionally suppress rejection.
	} catch ( value ) {

		// Support: Android 4.0 only
		// Strict mode functions invoked without .call/.apply get global-object context
		reject.apply( undefined, [ value ] );
	}
}

jQuery.extend( {

	Deferred: function( func ) {
		var tuples = [

				// action, add listener, callbacks,
				// ... .then handlers, argument index, [final state]
				[ "notify", "progress", jQuery.Callbacks( "memory" ),
					jQuery.Callbacks( "memory" ), 2 ],
				[ "resolve", "done", jQuery.Callbacks( "once memory" ),
					jQuery.Callbacks( "once memory" ), 0, "resolved" ],
				[ "reject", "fail", jQuery.Callbacks( "once memory" ),
					jQuery.Callbacks( "once memory" ), 1, "rejected" ]
			],
			state = "pending",
			promise = {
				state: function() {
					return state;
				},
				always: function() {
					deferred.done( arguments ).fail( arguments );
					return this;
				},
				"catch": function( fn ) {
					return promise.then( null, fn );
				},

				// Keep pipe for back-compat
				pipe: function( /* fnDone, fnFail, fnProgress */ ) {
					var fns = arguments;

					return jQuery.Deferred( function( newDefer ) {
						jQuery.each( tuples, function( _i, tuple ) {

							// Map tuples (progress, done, fail) to arguments (done, fail, progress)
							var fn = isFunction( fns[ tuple[ 4 ] ] ) && fns[ tuple[ 4 ] ];

							// deferred.progress(function() { bind to newDefer or newDefer.notify })
							// deferred.done(function() { bind to newDefer or newDefer.resolve })
							// deferred.fail(function() { bind to newDefer or newDefer.reject })
							deferred[ tuple[ 1 ] ]( function() {
								var returned = fn && fn.apply( this, arguments );
								if ( returned && isFunction( returned.promise ) ) {
									returned.promise()
										.progress( newDefer.notify )
										.done( newDefer.resolve )
										.fail( newDefer.reject );
								} else {
									newDefer[ tuple[ 0 ] + "With" ](
										this,
										fn ? [ returned ] : arguments
									);
								}
							} );
						} );
						fns = null;
					} ).promise();
				},
				then: function( onFulfilled, onRejected, onProgress ) {
					var maxDepth = 0;
					function resolve( depth, deferred, handler, special ) {
						return function() {
							var that = this,
								args = arguments,
								mightThrow = function() {
									var returned, then;

									// Support: Promises/A+ section 2.3.3.3.3
									// https://promisesaplus.com/#point-59
									// Ignore double-resolution attempts
									if ( depth < maxDepth ) {
										return;
									}

									returned = handler.apply( that, args );

									// Support: Promises/A+ section 2.3.1
									// https://promisesaplus.com/#point-48
									if ( returned === deferred.promise() ) {
										throw new TypeError( "Thenable self-resolution" );
									}

									// Support: Promises/A+ sections 2.3.3.1, 3.5
									// https://promisesaplus.com/#point-54
									// https://promisesaplus.com/#point-75
									// Retrieve `then` only once
									then = returned &&

										// Support: Promises/A+ section 2.3.4
										// https://promisesaplus.com/#point-64
										// Only check objects and functions for thenability
										( typeof returned === "object" ||
											typeof returned === "function" ) &&
										returned.then;

									// Handle a returned thenable
									if ( isFunction( then ) ) {

										// Special processors (notify) just wait for resolution
										if ( special ) {
											then.call(
												returned,
												resolve( maxDepth, deferred, Identity, special ),
												resolve( maxDepth, deferred, Thrower, special )
											);

										// Normal processors (resolve) also hook into progress
										} else {

											// ...and disregard older resolution values
											maxDepth++;

											then.call(
												returned,
												resolve( maxDepth, deferred, Identity, special ),
												resolve( maxDepth, deferred, Thrower, special ),
												resolve( maxDepth, deferred, Identity,
													deferred.notifyWith )
											);
										}

									// Handle all other returned values
									} else {

										// Only substitute handlers pass on context
										// and multiple values (non-spec behavior)
										if ( handler !== Identity ) {
											that = undefined;
											args = [ returned ];
										}

										// Process the value(s)
										// Default process is resolve
										( special || deferred.resolveWith )( that, args );
									}
								},

								// Only normal processors (resolve) catch and reject exceptions
								process = special ?
									mightThrow :
									function() {
										try {
											mightThrow();
										} catch ( e ) {

											if ( jQuery.Deferred.exceptionHook ) {
												jQuery.Deferred.exceptionHook( e,
													process.error );
											}

											// Support: Promises/A+ section 2.3.3.3.4.1
											// https://promisesaplus.com/#point-61
											// Ignore post-resolution exceptions
											if ( depth + 1 >= maxDepth ) {

												// Only substitute handlers pass on context
												// and multiple values (non-spec behavior)
												if ( handler !== Thrower ) {
													that = undefined;
													args = [ e ];
												}

												deferred.rejectWith( that, args );
											}
										}
									};

							// Support: Promises/A+ section 2.3.3.3.1
							// https://promisesaplus.com/#point-57
							// Re-resolve promises immediately to dodge false rejection from
							// subsequent errors
							if ( depth ) {
								process();
							} else {

								// Call an optional hook to record the error, in case of exception
								// since it's otherwise lost when execution goes async
								if ( jQuery.Deferred.getErrorHook ) {
									process.error = jQuery.Deferred.getErrorHook();

								// The deprecated alias of the above. While the name suggests
								// returning the stack, not an error instance, jQuery just passes
								// it directly to `console.warn` so both will work; an instance
								// just better cooperates with source maps.
								} else if ( jQuery.Deferred.getStackHook ) {
									process.error = jQuery.Deferred.getStackHook();
								}
								window.setTimeout( process );
							}
						};
					}

					return jQuery.Deferred( function( newDefer ) {

						// progress_handlers.add( ... )
						tuples[ 0 ][ 3 ].add(
							resolve(
								0,
								newDefer,
								isFunction( onProgress ) ?
									onProgress :
									Identity,
								newDefer.notifyWith
							)
						);

						// fulfilled_handlers.add( ... )
						tuples[ 1 ][ 3 ].add(
							resolve(
								0,
								newDefer,
								isFunction( onFulfilled ) ?
									onFulfilled :
									Identity
							)
						);

						// rejected_handlers.add( ... )
						tuples[ 2 ][ 3 ].add(
							resolve(
								0,
								newDefer,
								isFunction( onRejected ) ?
									onRejected :
									Thrower
							)
						);
					} ).promise();
				},

				// Get a promise for this deferred
				// If obj is provided, the promise aspect is added to the object
				promise: function( obj ) {
					return obj != null ? jQuery.extend( obj, promise ) : promise;
				}
			},
			deferred = {};

		// Add list-specific methods
		jQuery.each( tuples, function( i, tuple ) {
			var list = tuple[ 2 ],
				stateString = tuple[ 5 ];

			// promise.progress = list.add
			// promise.done = list.add
			// promise.fail = list.add
			promise[ tuple[ 1 ] ] = list.add;

			// Handle state
			if ( stateString ) {
				list.add(
					function() {

						// state = "resolved" (i.e., fulfilled)
						// state = "rejected"
						state = stateString;
					},

					// rejected_callbacks.disable
					// fulfilled_callbacks.disable
					tuples[ 3 - i ][ 2 ].disable,

					// rejected_handlers.disable
					// fulfilled_handlers.disable
					tuples[ 3 - i ][ 3 ].disable,

					// progress_callbacks.lock
					tuples[ 0 ][ 2 ].lock,

					// progress_handlers.lock
					tuples[ 0 ][ 3 ].lock
				);
			}

			// progress_handlers.fire
			// fulfilled_handlers.fire
			// rejected_handlers.fire
			list.add( tuple[ 3 ].fire );

			// deferred.notify = function() { deferred.notifyWith(...) }
			// deferred.resolve = function() { deferred.resolveWith(...) }
			// deferred.reject = function() { deferred.rejectWith(...) }
			deferred[ tuple[ 0 ] ] = function() {
				deferred[ tuple[ 0 ] + "With" ]( this === deferred ? undefined : this, arguments );
				return this;
			};

			// deferred.notifyWith = list.fireWith
			// deferred.resolveWith = list.fireWith
			// deferred.rejectWith = list.fireWith
			deferred[ tuple[ 0 ] + "With" ] = list.fireWith;
		} );

		// Make the deferred a promise
		promise.promise( deferred );

		// Call given func if any
		if ( func ) {
			func.call( deferred, deferred );
		}

		// All done!
		return deferred;
	},

	// Deferred helper
	when: function( singleValue ) {
		var

			// count of uncompleted subordinates
			remaining = arguments.length,

			// count of unprocessed arguments
			i = remaining,

			// subordinate fulfillment data
			resolveContexts = Array( i ),
			resolveValues = slice.call( arguments ),

			// the primary Deferred
			primary = jQuery.Deferred(),

			// subordinate callback factory
			updateFunc = function( i ) {
				return function( value ) {
					resolveContexts[ i ] = this;
					resolveValues[ i ] = arguments.length > 1 ? slice.call( arguments ) : value;
					if ( !( --remaining ) ) {
						primary.resolveWith( resolveContexts, resolveValues );
					}
				};
			};

		// Single- and empty arguments are adopted like Promise.resolve
		if ( remaining <= 1 ) {
			adoptValue( singleValue, primary.done( updateFunc( i ) ).resolve, primary.reject,
				!remaining );

			// Use .then() to unwrap secondary thenables (cf. gh-3000)
			if ( primary.state() === "pending" ||
				isFunction( resolveValues[ i ] && resolveValues[ i ].then ) ) {

				return primary.then();
			}
		}

		// Multiple arguments are aggregated like Promise.all array elements
		while ( i-- ) {
			adoptValue( resolveValues[ i ], updateFunc( i ), primary.reject );
		}

		return primary.promise();
	}
} );


// These usually indicate a programmer mistake during development,
// warn about them ASAP rather than swallowing them by default.
var rerrorNames = /^(Eval|Internal|Range|Reference|Syntax|Type|URI)Error$/;

// If `jQuery.Deferred.getErrorHook` is defined, `asyncError` is an error
// captured before the async barrier to get the original error cause
// which may otherwise be hidden.
jQuery.Deferred.exceptionHook = function( error, asyncError ) {

	// Support: IE 8 - 9 only
	// Console exists when dev tools are open, which can happen at any time
	if ( window.console && window.console.warn && error && rerrorNames.test( error.name ) ) {
		window.console.warn( "jQuery.Deferred exception: " + error.message,
			error.stack, asyncError );
	}
};




jQuery.readyException = function( error ) {
	window.setTimeout( function() {
		throw error;
	} );
};




// The deferred used on DOM ready
var readyList = jQuery.Deferred();

jQuery.fn.ready = function( fn ) {

	readyList
		.then( fn )

		// Wrap jQuery.readyException in a function so that the lookup
		// happens at the time of error handling instead of callback
		// registration.
		.catch( function( error ) {
			jQuery.readyException( error );
		} );

	return this;
};

jQuery.extend( {

	// Is the DOM ready to be used? Set to true once it occurs.
	isReady: false,

	// A counter to track how many items to wait for before
	// the ready event fires. See trac-6781
	readyWait: 1,

	// Handle when the DOM is ready
	ready: function( wait ) {

		// Abort if there are pending holds or we're already ready
		if ( wait === true ? --jQuery.readyWait : jQuery.isReady ) {
			return;
		}

		// Remember that the DOM is ready
		jQuery.isReady = true;

		// If a normal DOM Ready event fired, decrement, and wait if need be
		if ( wait !== true && --jQuery.readyWait > 0 ) {
			return;
		}

		// If there are functions bound, to execute
		readyList.resolveWith( document, [ jQuery ] );
	}
} );

jQuery.ready.then = readyList.then;

// The ready event handler and self cleanup method
function completed() {
	document.removeEventListener( "DOMContentLoaded", completed );
	window.removeEventListener( "load", completed );
	jQuery.ready();
}

// Catch cases where $(document).ready() is called
// after the browser event has already occurred.
// Support: IE <=9 - 10 only
// Older IE sometimes signals "interactive" too soon
if ( document.readyState === "complete" ||
	( document.readyState !== "loading" && !document.documentElement.doScroll ) ) {

	// Handle it asynchronously to allow scripts the opportunity to delay ready
	window.setTimeout( jQuery.ready );

} else {

	// Use the handy event callback
	document.addEventListener( "DOMContentLoaded", completed );

	// A fallback to window.onload, that will always work
	window.addEventListener( "load", completed );
}




// Multifunctional method to get and set values of a collection
// The value/s can optionally be executed if it's a function
var access = function( elems, fn, key, value, chainable, emptyGet, raw ) {
	var i = 0,
		len = elems.length,
		bulk = key == null;

	// Sets many values
	if ( toType( key ) === "object" ) {
		chainable = true;
		for ( i in key ) {
			access( elems, fn, i, key[ i ], true, emptyGet, raw );
		}

	// Sets one value
	} else if ( value !== undefined ) {
		chainable = true;

		if ( !isFunction( value ) ) {
			raw = true;
		}

		if ( bulk ) {

			// Bulk operations run against the entire set
			if ( raw ) {
				fn.call( elems, value );
				fn = null;

			// ...except when executing function values
			} else {
				bulk = fn;
				fn = function( elem, _key, value ) {
					return bulk.call( jQuery( elem ), value );
				};
			}
		}

		if ( fn ) {
			for ( ; i < len; i++ ) {
				fn(
					elems[ i ], key, raw ?
						value :
						value.call( elems[ i ], i, fn( elems[ i ], key ) )
				);
			}
		}
	}

	if ( chainable ) {
		return elems;
	}

	// Gets
	if ( bulk ) {
		return fn.call( elems );
	}

	return len ? fn( elems[ 0 ], key ) : emptyGet;
};


// Matches dashed string for camelizing
var rmsPrefix = /^-ms-/,
	rdashAlpha = /-([a-z])/g;

// Used by camelCase as callback to replace()
function fcamelCase( _all, letter ) {
	return letter.toUpperCase();
}

// Convert dashed to camelCase; used by the css and data modules
// Support: IE <=9 - 11, Edge 12 - 15
// Microsoft forgot to hump their vendor prefix (trac-9572)
function camelCase( string ) {
	return string.replace( rmsPrefix, "ms-" ).replace( rdashAlpha, fcamelCase );
}
var acceptData = function( owner ) {

	// Accepts only:
	//  - Node
	//    - Node.ELEMENT_NODE
	//    - Node.DOCUMENT_NODE
	//  - Object
	//    - Any
	return owner.nodeType === 1 || owner.nodeType === 9 || !( +owner.nodeType );
};




function Data() {
	this.expando = jQuery.expando + Data.uid++;
}

Data.uid = 1;

Data.prototype = {

	cache: function( owner ) {

		// Check if the owner object already has a cache
		var value = owner[ this.expando ];

		// If not, create one
		if ( !value ) {
			value = {};

			// We can accept data for non-element nodes in modern browsers,
			// but we should not, see trac-8335.
			// Always return an empty object.
			if ( acceptData( owner ) ) {

				// If it is a node unlikely to be stringify-ed or looped over
				// use plain assignment
				if ( owner.nodeType ) {
					owner[ this.expando ] = value;

				// Otherwise secure it in a non-enumerable property
				// configurable must be true to allow the property to be
				// deleted when data is removed
				} else {
					Object.defineProperty( owner, this.expando, {
						value: value,
						configurable: true
					} );
				}
			}
		}

		return value;
	},
	set: function( owner, data, value ) {
		var prop,
			cache = this.cache( owner );

		// Handle: [ owner, key, value ] args
		// Always use camelCase key (gh-2257)
		if ( typeof data === "string" ) {
			cache[ camelCase( data ) ] = value;

		// Handle: [ owner, { properties } ] args
		} else {

			// Copy the properties one-by-one to the cache object
			for ( prop in data ) {
				cache[ camelCase( prop ) ] = data[ prop ];
			}
		}
		return cache;
	},
	get: function( owner, key ) {
		return key === undefined ?
			this.cache( owner ) :

			// Always use camelCase key (gh-2257)
			owner[ this.expando ] && owner[ this.expando ][ camelCase( key ) ];
	},
	access: function( owner, key, value ) {

		// In cases where either:
		//
		//   1. No key was specified
		//   2. A string key was specified, but no value provided
		//
		// Take the "read" path and allow the get method to determine
		// which value to return, respectively either:
		//
		//   1. The entire cache object
		//   2. The data stored at the key
		//
		if ( key === undefined ||
				( ( key && typeof key === "string" ) && value === undefined ) ) {

			return this.get( owner, key );
		}

		// When the key is not a string, or both a key and value
		// are specified, set or extend (existing objects) with either:
		//
		//   1. An object of properties
		//   2. A key and value
		//
		this.set( owner, key, value );

		// Since the "set" path can have two possible entry points
		// return the expected data based on which path was taken[*]
		return value !== undefined ? value : key;
	},
	remove: function( owner, key ) {
		var i,
			cache = owner[ this.expando ];

		if ( cache === undefined ) {
			return;
		}

		if ( key !== undefined ) {

			// Support array or space separated string of keys
			if ( Array.isArray( key ) ) {

				// If key is an array of keys...
				// We always set camelCase keys, so remove that.
				key = key.map( camelCase );
			} else {
				key = camelCase( key );

				// If a key with the spaces exists, use it.
				// Otherwise, create an array by matching non-whitespace
				key = key in cache ?
					[ key ] :
					( key.match( rnothtmlwhite ) || [] );
			}

			i = key.length;

			while ( i-- ) {
				delete cache[ key[ i ] ];
			}
		}

		// Remove the expando if there's no more data
		if ( key === undefined || jQuery.isEmptyObject( cache ) ) {

			// Support: Chrome <=35 - 45
			// Webkit & Blink performance suffers when deleting properties
			// from DOM nodes, so set to undefined instead
			// https://bugs.chromium.org/p/chromium/issues/detail?id=378607 (bug restricted)
			if ( owner.nodeType ) {
				owner[ this.expando ] = undefined;
			} else {
				delete owner[ this.expando ];
			}
		}
	},
	hasData: function( owner ) {
		var cache = owner[ this.expando ];
		return cache !== undefined && !jQuery.isEmptyObject( cache );
	}
};
var dataPriv = new Data();

var dataUser = new Data();



//	Implementation Summary
//
//	1. Enforce API surface and semantic compatibility with 1.9.x branch
//	2. Improve the module's maintainability by reducing the storage
//		paths to a single mechanism.
//	3. Use the same single mechanism to support "private" and "user" data.
//	4. _Never_ expose "private" data to user code (TODO: Drop _data, _removeData)
//	5. Avoid exposing implementation details on user objects (eg. expando properties)
//	6. Provide a clear path for implementation upgrade to WeakMap in 2014

var rbrace = /^(?:\{[\w\W]*\}|\[[\w\W]*\])$/,
	rmultiDash = /[A-Z]/g;

function getData( data ) {
	if ( data === "true" ) {
		return true;
	}

	if ( data === "false" ) {
		return false;
	}

	if ( data === "null" ) {
		return null;
	}

	// Only convert to a number if it doesn't change the string
	if ( data === +data + "" ) {
		return +data;
	}

	if ( rbrace.test( data ) ) {
		return JSON.parse( data );
	}

	return data;
}

function dataAttr( elem, key, data ) {
	var name;

	// If nothing was found internally, try to fetch any
	// data from the HTML5 data-* attribute
	if ( data === undefined && elem.nodeType === 1 ) {
		name = "data-" + key.replace( rmultiDash, "-$&" ).toLowerCase();
		data = elem.getAttribute( name );

		if ( typeof data === "string" ) {
			try {
				data = getData( data );
			} catch ( e ) {}

			// Make sure we set the data so it isn't changed later
			dataUser.set( elem, key, data );
		} else {
			data = undefined;
		}
	}
	return data;
}

jQuery.extend( {
	hasData: function( elem ) {
		return dataUser.hasData( elem ) || dataPriv.hasData( elem );
	},

	data: function( elem, name, data ) {
		return dataUser.access( elem, name, data );
	},

	removeData: function( elem, name ) {
		dataUser.remove( elem, name );
	},

	// TODO: Now that all calls to _data and _removeData have been replaced
	// with direct calls to dataPriv methods, these can be deprecated.
	_data: function( elem, name, data ) {
		return dataPriv.access( elem, name, data );
	},

	_removeData: function( elem, name ) {
		dataPriv.remove( elem, name );
	}
} );

jQuery.fn.extend( {
	data: function( key, value ) {
		var i, name, data,
			elem = this[ 0 ],
			attrs = elem && elem.attributes;

		// Gets all values
		if ( key === undefined ) {
			if ( this.length ) {
				data = dataUser.get( elem );

				if ( elem.nodeType === 1 && !dataPriv.get( elem, "hasDataAttrs" ) ) {
					i = attrs.length;
					while ( i-- ) {

						// Support: IE 11 only
						// The attrs elements can be null (trac-14894)
						if ( attrs[ i ] ) {
							name = attrs[ i ].name;
							if ( name.indexOf( "data-" ) === 0 ) {
								name = camelCase( name.slice( 5 ) );
								dataAttr( elem, name, data[ name ] );
							}
						}
					}
					dataPriv.set( elem, "hasDataAttrs", true );
				}
			}

			return data;
		}

		// Sets multiple values
		if ( typeof key === "object" ) {
			return this.each( function() {
				dataUser.set( this, key );
			} );
		}

		return access( this, function( value ) {
			var data;

			// The calling jQuery object (element matches) is not empty
			// (and therefore has an element appears at this[ 0 ]) and the
			// `value` parameter was not undefined. An empty jQuery object
			// will result in `undefined` for elem = this[ 0 ] which will
			// throw an exception if an attempt to read a data cache is made.
			if ( elem && value === undefined ) {

				// Attempt to get data from the cache
				// The key will always be camelCased in Data
				data = dataUser.get( elem, key );
				if ( data !== undefined ) {
					return data;
				}

				// Attempt to "discover" the data in
				// HTML5 custom data-* attrs
				data = dataAttr( elem, key );
				if ( data !== undefined ) {
					return data;
				}

				// We tried really hard, but the data doesn't exist.
				return;
			}

			// Set the data...
			this.each( function() {

				// We always store the camelCased key
				dataUser.set( this, key, value );
			} );
		}, null, value, arguments.length > 1, null, true );
	},

	removeData: function( key ) {
		return this.each( function() {
			dataUser.remove( this, key );
		} );
	}
} );


jQuery.extend( {
	queue: function( elem, type, data ) {
		var queue;

		if ( elem ) {
			type = ( type || "fx" ) + "queue";
			queue = dataPriv.get( elem, type );

			// Speed up dequeue by getting out quickly if this is just a lookup
			if ( data ) {
				if ( !queue || Array.isArray( data ) ) {
					queue = dataPriv.access( elem, type, jQuery.makeArray( data ) );
				} else {
					queue.push( data );
				}
			}
			return queue || [];
		}
	},

	dequeue: function( elem, type ) {
		type = type || "fx";

		var queue = jQuery.queue( elem, type ),
			startLength = queue.length,
			fn = queue.shift(),
			hooks = jQuery._queueHooks( elem, type ),
			next = function() {
				jQuery.dequeue( elem, type );
			};

		// If the fx queue is dequeued, always remove the progress sentinel
		if ( fn === "inprogress" ) {
			fn = queue.shift();
			startLength--;
		}

		if ( fn ) {

			// Add a progress sentinel to prevent the fx queue from being
			// automatically dequeued
			if ( type === "fx" ) {
				queue.unshift( "inprogress" );
			}

			// Clear up the last queue stop function
			delete hooks.stop;
			fn.call( elem, next, hooks );
		}

		if ( !startLength && hooks ) {
			hooks.empty.fire();
		}
	},

	// Not public - generate a queueHooks object, or return the current one
	_queueHooks: function( elem, type ) {
		var key = type + "queueHooks";
		return dataPriv.get( elem, key ) || dataPriv.access( elem, key, {
			empty: jQuery.Callbacks( "once memory" ).add( function() {
				dataPriv.remove( elem, [ type + "queue", key ] );
			} )
		} );
	}
} );

jQuery.fn.extend( {
	queue: function( type, data ) {
		var setter = 2;

		if ( typeof type !== "string" ) {
			data = type;
			type = "fx";
			setter--;
		}

		if ( arguments.length < setter ) {
			return jQuery.queue( this[ 0 ], type );
		}

		return data === undefined ?
			this :
			this.each( function() {
				var queue = jQuery.queue( this, type, data );

				// Ensure a hooks for this queue
				jQuery._queueHooks( this, type );

				if ( type === "fx" && queue[ 0 ] !== "inprogress" ) {
					jQuery.dequeue( this, type );
				}
			} );
	},
	dequeue: function( type ) {
		return this.each( function() {
			jQuery.dequeue( this, type );
		} );
	},
	clearQueue: function( type ) {
		return this.queue( type || "fx", [] );
	},

	// Get a promise resolved when queues of a certain type
	// are emptied (fx is the type by default)
	promise: function( type, obj ) {
		var tmp,
			count = 1,
			defer = jQuery.Deferred(),
			elements = this,
			i = this.length,
			resolve = function() {
				if ( !( --count ) ) {
					defer.resolveWith( elements, [ elements ] );
				}
			};

		if ( typeof type !== "string" ) {
			obj = type;
			type = undefined;
		}
		type = type || "fx";

		while ( i-- ) {
			tmp = dataPriv.get( elements[ i ], type + "queueHooks" );
			if ( tmp && tmp.empty ) {
				count++;
				tmp.empty.add( resolve );
			}
		}
		resolve();
		return defer.promise( obj );
	}
} );
var pnum = ( /[+-]?(?:\d*\.|)\d+(?:[eE][+-]?\d+|)/ ).source;

var rcssNum = new RegExp( "^(?:([+-])=|)(" + pnum + ")([a-z%]*)$", "i" );


var cssExpand = [ "Top", "Right", "Bottom", "Left" ];

var documentElement = document.documentElement;



	var isAttached = function( elem ) {
			return jQuery.contains( elem.ownerDocument, elem );
		},
		composed = { composed: true };

	// Support: IE 9 - 11+, Edge 12 - 18+, iOS 10.0 - 10.2 only
	// Check attachment across shadow DOM boundaries when possible (gh-3504)
	// Support: iOS 10.0-10.2 only
	// Early iOS 10 versions support `attachShadow` but not `getRootNode`,
	// leading to errors. We need to check for `getRootNode`.
	if ( documentElement.getRootNode ) {
		isAttached = function( elem ) {
			return jQuery.contains( elem.ownerDocument, elem ) ||
				elem.getRootNode( composed ) === elem.ownerDocument;
		};
	}
var isHiddenWithinTree = function( elem, el ) {

		// isHiddenWithinTree might be called from jQuery#filter function;
		// in that case, element will be second argument
		elem = el || elem;

		// Inline style trumps all
		return elem.style.display === "none" ||
			elem.style.display === "" &&

			// Otherwise, check computed style
			// Support: Firefox <=43 - 45
			// Disconnected elements can have computed display: none, so first confirm that elem is
			// in the document.
			isAttached( elem ) &&

			jQuery.css( elem, "display" ) === "none";
	};



function adjustCSS( elem, prop, valueParts, tween ) {
	var adjusted, scale,
		maxIterations = 20,
		currentValue = tween ?
			function() {
				return tween.cur();
			} :
			function() {
				return jQuery.css( elem, prop, "" );
			},
		initial = currentValue(),
		unit = valueParts && valueParts[ 3 ] || ( jQuery.cssNumber[ prop ] ? "" : "px" ),

		// Starting value computation is required for potential unit mismatches
		initialInUnit = elem.nodeType &&
			( jQuery.cssNumber[ prop ] || unit !== "px" && +initial ) &&
			rcssNum.exec( jQuery.css( elem, prop ) );

	if ( initialInUnit && initialInUnit[ 3 ] !== unit ) {

		// Support: Firefox <=54
		// Halve the iteration target value to prevent interference from CSS upper bounds (gh-2144)
		initial = initial / 2;

		// Trust units reported by jQuery.css
		unit = unit || initialInUnit[ 3 ];

		// Iteratively approximate from a nonzero starting point
		initialInUnit = +initial || 1;

		while ( maxIterations-- ) {

			// Evaluate and update our best guess (doubling guesses that zero out).
			// Finish if the scale equals or crosses 1 (making the old*new product non-positive).
			jQuery.style( elem, prop, initialInUnit + unit );
			if ( ( 1 - scale ) * ( 1 - ( scale = currentValue() / initial || 0.5 ) ) <= 0 ) {
				maxIterations = 0;
			}
			initialInUnit = initialInUnit / scale;

		}

		initialInUnit = initialInUnit * 2;
		jQuery.style( elem, prop, initialInUnit + unit );

		// Make sure we update the tween properties later on
		valueParts = valueParts || [];
	}

	if ( valueParts ) {
		initialInUnit = +initialInUnit || +initial || 0;

		// Apply relative offset (+=/-=) if specified
		adjusted = valueParts[ 1 ] ?
			initialInUnit + ( valueParts[ 1 ] + 1 ) * valueParts[ 2 ] :
			+valueParts[ 2 ];
		if ( tween ) {
			tween.unit = unit;
			tween.start = initialInUnit;
			tween.end = adjusted;
		}
	}
	return adjusted;
}


var defaultDisplayMap = {};

function getDefaultDisplay( elem ) {
	var temp,
		doc = elem.ownerDocument,
		nodeName = elem.nodeName,
		display = defaultDisplayMap[ nodeName ];

	if ( display ) {
		return display;
	}

	temp = doc.body.appendChild( doc.createElement( nodeName ) );
	display = jQuery.css( temp, "display" );

	temp.parentNode.removeChild( temp );

	if ( display === "none" ) {
		display = "block";
	}
	defaultDisplayMap[ nodeName ] = display;

	return display;
}

function showHide( elements, show ) {
	var display, elem,
		values = [],
		index = 0,
		length = elements.length;

	// Determine new display value for elements that need to change
	for ( ; index < length; index++ ) {
		elem = elements[ index ];
		if ( !elem.style ) {
			continue;
		}

		display = elem.style.display;
		if ( show ) {

			// Since we force visibility upon cascade-hidden elements, an immediate (and slow)
			// check is required in this first loop unless we have a nonempty display value (either
			// inline or about-to-be-restored)
			if ( display === "none" ) {
				values[ index ] = dataPriv.get( elem, "display" ) || null;
				if ( !values[ index ] ) {
					elem.style.display = "";
				}
			}
			if ( elem.style.display === "" && isHiddenWithinTree( elem ) ) {
				values[ index ] = getDefaultDisplay( elem );
			}
		} else {
			if ( display !== "none" ) {
				values[ index ] = "none";

				// Remember what we're overwriting
				dataPriv.set( elem, "display", display );
			}
		}
	}

	// Set the display of the elements in a second loop to avoid constant reflow
	for ( index = 0; index < length; index++ ) {
		if ( values[ index ] != null ) {
			elements[ index ].style.display = values[ index ];
		}
	}

	return elements;
}

jQuery.fn.extend( {
	show: function() {
		return showHide( this, true );
	},
	hide: function() {
		return showHide( this );
	},
	toggle: function( state ) {
		if ( typeof state === "boolean" ) {
			return state ? this.show() : this.hide();
		}

		return this.each( function() {
			if ( isHiddenWithinTree( this ) ) {
				jQuery( this ).show();
			} else {
				jQuery( this ).hide();
			}
		} );
	}
} );
var rcheckableType = ( /^(?:checkbox|radio)$/i );

var rtagName = ( /<([a-z][^\/\0>\x20\t\r\n\f]*)/i );

var rscriptType = ( /^$|^module$|\/(?:java|ecma)script/i );



( function() {
	var fragment = document.createDocumentFragment(),
		div = fragment.appendChild( document.createElement( "div" ) ),
		input = document.createElement( "input" );

	// Support: Android 4.0 - 4.3 only
	// Check state lost if the name is set (trac-11217)
	// Support: Windows Web Apps (WWA)
	// `name` and `type` must use .setAttribute for WWA (trac-14901)
	input.setAttribute( "type", "radio" );
	input.setAttribute( "checked", "checked" );
	input.setAttribute( "name", "t" );

	div.appendChild( input );

	// Support: Android <=4.1 only
	// Older WebKit doesn't clone checked state correctly in fragments
	support.checkClone = div.cloneNode( true ).cloneNode( true ).lastChild.checked;

	// Support: IE <=11 only
	// Make sure textarea (and checkbox) defaultValue is properly cloned
	div.innerHTML = "<textarea>x</textarea>";
	support.noCloneChecked = !!div.cloneNode( true ).lastChild.defaultValue;

	// Support: IE <=9 only
	// IE <=9 replaces <option> tags with their contents when inserted outside of
	// the select element.
	div.innerHTML = "<option></option>";
	support.option = !!div.lastChild;
} )();


// We have to close these tags to support XHTML (trac-13200)
var wrapMap = {

	// XHTML parsers do not magically insert elements in the
	// same way that tag soup parsers do. So we cannot shorten
	// this by omitting <tbody> or other required elements.
	thead: [ 1, "<table>", "</table>" ],
	col: [ 2, "<table><colgroup>", "</colgroup></table>" ],
	tr: [ 2, "<table><tbody>", "</tbody></table>" ],
	td: [ 3, "<table><tbody><tr>", "</tr></tbody></table>" ],

	_default: [ 0, "", "" ]
};

wrapMap.tbody = wrapMap.tfoot = wrapMap.colgroup = wrapMap.caption = wrapMap.thead;
wrapMap.th = wrapMap.td;

// Support: IE <=9 only
if ( !support.option ) {
	wrapMap.optgroup = wrapMap.option = [ 1, "<select multiple='multiple'>", "</select>" ];
}


function getAll( context, tag ) {

	// Support: IE <=9 - 11 only
	// Use typeof to avoid zero-argument method invocation on host objects (trac-15151)
	var ret;

	if ( typeof context.getElementsByTagName !== "undefined" ) {
		ret = context.getElementsByTagName( tag || "*" );

	} else if ( typeof context.querySelectorAll !== "undefined" ) {
		ret = context.querySelectorAll( tag || "*" );

	} else {
		ret = [];
	}

	if ( tag === undefined || tag && nodeName( context, tag ) ) {
		return jQuery.merge( [ context ], ret );
	}

	return ret;
}


// Mark scripts as having already been evaluated
function setGlobalEval( elems, refElements ) {
	var i = 0,
		l = elems.length;

	for ( ; i < l; i++ ) {
		dataPriv.set(
			elems[ i ],
			"globalEval",
			!refElements || dataPriv.get( refElements[ i ], "globalEval" )
		);
	}
}


var rhtml = /<|&#?\w+;/;

function buildFragment( elems, context, scripts, selection, ignored ) {
	var elem, tmp, tag, wrap, attached, j,
		fragment = context.createDocumentFragment(),
		nodes = [],
		i = 0,
		l = elems.length;

	for ( ; i < l; i++ ) {
		elem = elems[ i ];

		if ( elem || elem === 0 ) {

			// Add nodes directly
			if ( toType( elem ) === "object" ) {

				// Support: Android <=4.0 only, PhantomJS 1 only
				// push.apply(_, arraylike) throws on ancient WebKit
				jQuery.merge( nodes, elem.nodeType ? [ elem ] : elem );

			// Convert non-html into a text node
			} else if ( !rhtml.test( elem ) ) {
				nodes.push( context.createTextNode( elem ) );

			// Convert html into DOM nodes
			} else {
				tmp = tmp || fragment.appendChild( context.createElement( "div" ) );

				// Deserialize a standard representation
				tag = ( rtagName.exec( elem ) || [ "", "" ] )[ 1 ].toLowerCase();
				wrap = wrapMap[ tag ] || wrapMap._default;
				tmp.innerHTML = wrap[ 1 ] + jQuery.htmlPrefilter( elem ) + wrap[ 2 ];

				// Descend through wrappers to the right content
				j = wrap[ 0 ];
				while ( j-- ) {
					tmp = tmp.lastChild;
				}

				// Support: Android <=4.0 only, PhantomJS 1 only
				// push.apply(_, arraylike) throws on ancient WebKit
				jQuery.merge( nodes, tmp.childNodes );

				// Remember the top-level container
				tmp = fragment.firstChild;

				// Ensure the created nodes are orphaned (trac-12392)
				tmp.textContent = "";
			}
		}
	}

	// Remove wrapper from fragment
	fragment.textContent = "";

	i = 0;
	while ( ( elem = nodes[ i++ ] ) ) {

		// Skip elements already in the context collection (trac-4087)
		if ( selection && jQuery.inArray( elem, selection ) > -1 ) {
			if ( ignored ) {
				ignored.push( elem );
			}
			continue;
		}

		attached = isAttached( elem );

		// Append to fragment
		tmp = getAll( fragment.appendChild( elem ), "script" );

		// Preserve script evaluation history
		if ( attached ) {
			setGlobalEval( tmp );
		}

		// Capture executables
		if ( scripts ) {
			j = 0;
			while ( ( elem = tmp[ j++ ] ) ) {
				if ( rscriptType.test( elem.type || "" ) ) {
					scripts.push( elem );
				}
			}
		}
	}

	return fragment;
}


var rtypenamespace = /^([^.]*)(?:\.(.+)|)/;

function returnTrue() {
	return true;
}

function returnFalse() {
	return false;
}

function on( elem, types, selector, data, fn, one ) {
	var origFn, type;

	// Types can be a map of types/handlers
	if ( typeof types === "object" ) {

		// ( types-Object, selector, data )
		if ( typeof selector !== "string" ) {

			// ( types-Object, data )
			data = data || selector;
			selector = undefined;
		}
		for ( type in types ) {
			on( elem, type, selector, data, types[ type ], one );
		}
		return elem;
	}

	if ( data == null && fn == null ) {

		// ( types, fn )
		fn = selector;
		data = selector = undefined;
	} else if ( fn == null ) {
		if ( typeof selector === "string" ) {

			// ( types, selector, fn )
			fn = data;
			data = undefined;
		} else {

			// ( types, data, fn )
			fn = data;
			data = selector;
			selector = undefined;
		}
	}
	if ( fn === false ) {
		fn = returnFalse;
	} else if ( !fn ) {
		return elem;
	}

	if ( one === 1 ) {
		origFn = fn;
		fn = function( event ) {

			// Can use an empty set, since event contains the info
			jQuery().off( event );
			return origFn.apply( this, arguments );
		};

		// Use same guid so caller can remove using origFn
		fn.guid = origFn.guid || ( origFn.guid = jQuery.guid++ );
	}
	return elem.each( function() {
		jQuery.event.add( this, types, fn, data, selector );
	} );
}

/*
 * Helper functions for managing events -- not part of the public interface.
 * Props to Dean Edwards' addEvent library for many of the ideas.
 */
jQuery.event = {

	global: {},

	add: function( elem, types, handler, data, selector ) {

		var handleObjIn, eventHandle, tmp,
			events, t, handleObj,
			special, handlers, type, namespaces, origType,
			elemData = dataPriv.get( elem );

		// Only attach events to objects that accept data
		if ( !acceptData( elem ) ) {
			return;
		}

		// Caller can pass in an object of custom data in lieu of the handler
		if ( handler.handler ) {
			handleObjIn = handler;
			handler = handleObjIn.handler;
			selector = handleObjIn.selector;
		}

		// Ensure that invalid selectors throw exceptions at attach time
		// Evaluate against documentElement in case elem is a non-element node (e.g., document)
		if ( selector ) {
			jQuery.find.matchesSelector( documentElement, selector );
		}

		// Make sure that the handler has a unique ID, used to find/remove it later
		if ( !handler.guid ) {
			handler.guid = jQuery.guid++;
		}

		// Init the element's event structure and main handler, if this is the first
		if ( !( events = elemData.events ) ) {
			events = elemData.events = Object.create( null );
		}
		if ( !( eventHandle = elemData.handle ) ) {
			eventHandle = elemData.handle = function( e ) {

				// Discard the second event of a jQuery.event.trigger() and
				// when an event is called after a page has unloaded
				return typeof jQuery !== "undefined" && jQuery.event.triggered !== e.type ?
					jQuery.event.dispatch.apply( elem, arguments ) : undefined;
			};
		}

		// Handle multiple events separated by a space
		types = ( types || "" ).match( rnothtmlwhite ) || [ "" ];
		t = types.length;
		while ( t-- ) {
			tmp = rtypenamespace.exec( types[ t ] ) || [];
			type = origType = tmp[ 1 ];
			namespaces = ( tmp[ 2 ] || "" ).split( "." ).sort();

			// There *must* be a type, no attaching namespace-only handlers
			if ( !type ) {
				continue;
			}

			// If event changes its type, use the special event handlers for the changed type
			special = jQuery.event.special[ type ] || {};

			// If selector defined, determine special event api type, otherwise given type
			type = ( selector ? special.delegateType : special.bindType ) || type;

			// Update special based on newly reset type
			special = jQuery.event.special[ type ] || {};

			// handleObj is passed to all event handlers
			handleObj = jQuery.extend( {
				type: type,
				origType: origType,
				data: data,
				handler: handler,
				guid: handler.guid,
				selector: selector,
				needsContext: selector && jQuery.expr.match.needsContext.test( selector ),
				namespace: namespaces.join( "." )
			}, handleObjIn );

			// Init the event handler queue if we're the first
			if ( !( handlers = events[ type ] ) ) {
				handlers = events[ type ] = [];
				handlers.delegateCount = 0;

				// Only use addEventListener if the special events handler returns false
				if ( !special.setup ||
					special.setup.call( elem, data, namespaces, eventHandle ) === false ) {

					if ( elem.addEventListener ) {
						elem.addEventListener( type, eventHandle );
					}
				}
			}

			if ( special.add ) {
				special.add.call( elem, handleObj );

				if ( !handleObj.handler.guid ) {
					handleObj.handler.guid = handler.guid;
				}
			}

			// Add to the element's handler list, delegates in front
			if ( selector ) {
				handlers.splice( handlers.delegateCount++, 0, handleObj );
			} else {
				handlers.push( handleObj );
			}

			// Keep track of which events have ever been used, for event optimization
			jQuery.event.global[ type ] = true;
		}

	},

	// Detach an event or set of events from an element
	remove: function( elem, types, handler, selector, mappedTypes ) {

		var j, origCount, tmp,
			events, t, handleObj,
			special, handlers, type, namespaces, origType,
			elemData = dataPriv.hasData( elem ) && dataPriv.get( elem );

		if ( !elemData || !( events = elemData.events ) ) {
			return;
		}

		// Once for each type.namespace in types; type may be omitted
		types = ( types || "" ).match( rnothtmlwhite ) || [ "" ];
		t = types.length;
		while ( t-- ) {
			tmp = rtypenamespace.exec( types[ t ] ) || [];
			type = origType = tmp[ 1 ];
			namespaces = ( tmp[ 2 ] || "" ).split( "." ).sort();

			// Unbind all events (on this namespace, if provided) for the element
			if ( !type ) {
				for ( type in events ) {
					jQuery.event.remove( elem, type + types[ t ], handler, selector, true );
				}
				continue;
			}

			special = jQuery.event.special[ type ] || {};
			type = ( selector ? special.delegateType : special.bindType ) || type;
			handlers = events[ type ] || [];
			tmp = tmp[ 2 ] &&
				new RegExp( "(^|\\.)" + namespaces.join( "\\.(?:.*\\.|)" ) + "(\\.|$)" );

			// Remove matching events
			origCount = j = handlers.length;
			while ( j-- ) {
				handleObj = handlers[ j ];

				if ( ( mappedTypes || origType === handleObj.origType ) &&
					( !handler || handler.guid === handleObj.guid ) &&
					( !tmp || tmp.test( handleObj.namespace ) ) &&
					( !selector || selector === handleObj.selector ||
						selector === "**" && handleObj.selector ) ) {
					handlers.splice( j, 1 );

					if ( handleObj.selector ) {
						handlers.delegateCount--;
					}
					if ( special.remove ) {
						special.remove.call( elem, handleObj );
					}
				}
			}

			// Remove generic event handler if we removed something and no more handlers exist
			// (avoids potential for endless recursion during removal of special event handlers)
			if ( origCount && !handlers.length ) {
				if ( !special.teardown ||
					special.teardown.call( elem, namespaces, elemData.handle ) === false ) {

					jQuery.removeEvent( elem, type, elemData.handle );
				}

				delete events[ type ];
			}
		}

		// Remove data and the expando if it's no longer used
		if ( jQuery.isEmptyObject( events ) ) {
			dataPriv.remove( elem, "handle events" );
		}
	},

	dispatch: function( nativeEvent ) {

		var i, j, ret, matched, handleObj, handlerQueue,
			args = new Array( arguments.length ),

			// Make a writable jQuery.Event from the native event object
			event = jQuery.event.fix( nativeEvent ),

			handlers = (
				dataPriv.get( this, "events" ) || Object.create( null )
			)[ event.type ] || [],
			special = jQuery.event.special[ event.type ] || {};

		// Use the fix-ed jQuery.Event rather than the (read-only) native event
		args[ 0 ] = event;

		for ( i = 1; i < arguments.length; i++ ) {
			args[ i ] = arguments[ i ];
		}

		event.delegateTarget = this;

		// Call the preDispatch hook for the mapped type, and let it bail if desired
		if ( special.preDispatch && special.preDispatch.call( this, event ) === false ) {
			return;
		}

		// Determine handlers
		handlerQueue = jQuery.event.handlers.call( this, event, handlers );

		// Run delegates first; they may want to stop propagation beneath us
		i = 0;
		while ( ( matched = handlerQueue[ i++ ] ) && !event.isPropagationStopped() ) {
			event.currentTarget = matched.elem;

			j = 0;
			while ( ( handleObj = matched.handlers[ j++ ] ) &&
				!event.isImmediatePropagationStopped() ) {

				// If the event is namespaced, then each handler is only invoked if it is
				// specially universal or its namespaces are a superset of the event's.
				if ( !event.rnamespace || handleObj.namespace === false ||
					event.rnamespace.test( handleObj.namespace ) ) {

					event.handleObj = handleObj;
					event.data = handleObj.data;

					ret = ( ( jQuery.event.special[ handleObj.origType ] || {} ).handle ||
						handleObj.handler ).apply( matched.elem, args );

					if ( ret !== undefined ) {
						if ( ( event.result = ret ) === false ) {
							event.preventDefault();
							event.stopPropagation();
						}
					}
				}
			}
		}

		// Call the postDispatch hook for the mapped type
		if ( special.postDispatch ) {
			special.postDispatch.call( this, event );
		}

		return event.result;
	},

	handlers: function( event, handlers ) {
		var i, handleObj, sel, matchedHandlers, matchedSelectors,
			handlerQueue = [],
			delegateCount = handlers.delegateCount,
			cur = event.target;

		// Find delegate handlers
		if ( delegateCount &&

			// Support: IE <=9
			// Black-hole SVG <use> instance trees (trac-13180)
			cur.nodeType &&

			// Support: Firefox <=42
			// Suppress spec-violating clicks indicating a non-primary pointer button (trac-3861)
			// https://www.w3.org/TR/DOM-Level-3-Events/#event-type-click
			// Support: IE 11 only
			// ...but not arrow key "clicks" of radio inputs, which can have `button` -1 (gh-2343)
			!( event.type === "click" && event.button >= 1 ) ) {

			for ( ; cur !== this; cur = cur.parentNode || this ) {

				// Don't check non-elements (trac-13208)
				// Don't process clicks on disabled elements (trac-6911, trac-8165, trac-11382, trac-11764)
				if ( cur.nodeType === 1 && !( event.type === "click" && cur.disabled === true ) ) {
					matchedHandlers = [];
					matchedSelectors = {};
					for ( i = 0; i < delegateCount; i++ ) {
						handleObj = handlers[ i ];

						// Don't conflict with Object.prototype properties (trac-13203)
						sel = handleObj.selector + " ";

						if ( matchedSelectors[ sel ] === undefined ) {
							matchedSelectors[ sel ] = handleObj.needsContext ?
								jQuery( sel, this ).index( cur ) > -1 :
								jQuery.find( sel, this, null, [ cur ] ).length;
						}
						if ( matchedSelectors[ sel ] ) {
							matchedHandlers.push( handleObj );
						}
					}
					if ( matchedHandlers.length ) {
						handlerQueue.push( { elem: cur, handlers: matchedHandlers } );
					}
				}
			}
		}

		// Add the remaining (directly-bound) handlers
		cur = this;
		if ( delegateCount < handlers.length ) {
			handlerQueue.push( { elem: cur, handlers: handlers.slice( delegateCount ) } );
		}

		return handlerQueue;
	},

	addProp: function( name, hook ) {
		Object.defineProperty( jQuery.Event.prototype, name, {
			enumerable: true,
			configurable: true,

			get: isFunction( hook ) ?
				function() {
					if ( this.originalEvent ) {
						return hook( this.originalEvent );
					}
				} :
				function() {
					if ( this.originalEvent ) {
						return this.originalEvent[ name ];
					}
				},

			set: function( value ) {
				Object.defineProperty( this, name, {
					enumerable: true,
					configurable: true,
					writable: true,
					value: value
				} );
			}
		} );
	},

	fix: function( originalEvent ) {
		return originalEvent[ jQuery.expando ] ?
			originalEvent :
			new jQuery.Event( originalEvent );
	},

	special: {
		load: {

			// Prevent triggered image.load events from bubbling to window.load
			noBubble: true
		},
		click: {

			// Utilize native event to ensure correct state for checkable inputs
			setup: function( data ) {

				// For mutual compressibility with _default, replace `this` access with a local var.
				// `|| data` is dead code meant only to preserve the variable through minification.
				var el = this || data;

				// Claim the first handler
				if ( rcheckableType.test( el.type ) &&
					el.click && nodeName( el, "input" ) ) {

					// dataPriv.set( el, "click", ... )
					leverageNative( el, "click", true );
				}

				// Return false to allow normal processing in the caller
				return false;
			},
			trigger: function( data ) {

				// For mutual compressibility with _default, replace `this` access with a local var.
				// `|| data` is dead code meant only to preserve the variable through minification.
				var el = this || data;

				// Force setup before triggering a click
				if ( rcheckableType.test( el.type ) &&
					el.click && nodeName( el, "input" ) ) {

					leverageNative( el, "click" );
				}

				// Return non-false to allow normal event-path propagation
				return true;
			},

			// For cross-browser consistency, suppress native .click() on links
			// Also prevent it if we're currently inside a leveraged native-event stack
			_default: function( event ) {
				var target = event.target;
				return rcheckableType.test( target.type ) &&
					target.click && nodeName( target, "input" ) &&
					dataPriv.get( target, "click" ) ||
					nodeName( target, "a" );
			}
		},

		beforeunload: {
			postDispatch: function( event ) {

				// Support: Firefox 20+
				// Firefox doesn't alert if the returnValue field is not set.
				if ( event.result !== undefined && event.originalEvent ) {
					event.originalEvent.returnValue = event.result;
				}
			}
		}
	}
};

// Ensure the presence of an event listener that handles manually-triggered
// synthetic events by interrupting progress until reinvoked in response to
// *native* events that it fires directly, ensuring that state changes have
// already occurred before other listeners are invoked.
function leverageNative( el, type, isSetup ) {

	// Missing `isSetup` indicates a trigger call, which must force setup through jQuery.event.add
	if ( !isSetup ) {
		if ( dataPriv.get( el, type ) === undefined ) {
			jQuery.event.add( el, type, returnTrue );
		}
		return;
	}

	// Register the controller as a special universal handler for all event namespaces
	dataPriv.set( el, type, false );
	jQuery.event.add( el, type, {
		namespace: false,
		handler: function( event ) {
			var result,
				saved = dataPriv.get( this, type );

			if ( ( event.isTrigger & 1 ) && this[ type ] ) {

				// Interrupt processing of the outer synthetic .trigger()ed event
				if ( !saved ) {

					// Store arguments for use when handling the inner native event
					// There will always be at least one argument (an event object), so this array
					// will not be confused with a leftover capture object.
					saved = slice.call( arguments );
					dataPriv.set( this, type, saved );

					// Trigger the native event and capture its result
					this[ type ]();
					result = dataPriv.get( this, type );
					dataPriv.set( this, type, false );

					if ( saved !== result ) {

						// Cancel the outer synthetic event
						event.stopImmediatePropagation();
						event.preventDefault();

						return result;
					}

				// If this is an inner synthetic event for an event with a bubbling surrogate
				// (focus or blur), assume that the surrogate already propagated from triggering
				// the native event and prevent that from happening again here.
				// This technically gets the ordering wrong w.r.t. to `.trigger()` (in which the
				// bubbling surrogate propagates *after* the non-bubbling base), but that seems
				// less bad than duplication.
				} else if ( ( jQuery.event.special[ type ] || {} ).delegateType ) {
					event.stopPropagation();
				}

			// If this is a native event triggered above, everything is now in order
			// Fire an inner synthetic event with the original arguments
			} else if ( saved ) {

				// ...and capture the result
				dataPriv.set( this, type, jQuery.event.trigger(
					saved[ 0 ],
					saved.slice( 1 ),
					this
				) );

				// Abort handling of the native event by all jQuery handlers while allowing
				// native handlers on the same element to run. On target, this is achieved
				// by stopping immediate propagation just on the jQuery event. However,
				// the native event is re-wrapped by a jQuery one on each level of the
				// propagation so the only way to stop it for jQuery is to stop it for
				// everyone via native `stopPropagation()`. This is not a problem for
				// focus/blur which don't bubble, but it does also stop click on checkboxes
				// and radios. We accept this limitation.
				event.stopPropagation();
				event.isImmediatePropagationStopped = returnTrue;
			}
		}
	} );
}

jQuery.removeEvent = function( elem, type, handle ) {

	// This "if" is needed for plain objects
	if ( elem.removeEventListener ) {
		elem.removeEventListener( type, handle );
	}
};

jQuery.Event = function( src, props ) {

	// Allow instantiation without the 'new' keyword
	if ( !( this instanceof jQuery.Event ) ) {
		return new jQuery.Event( src, props );
	}

	// Event object
	if ( src && src.type ) {
		this.originalEvent = src;
		this.type = src.type;

		// Events bubbling up the document may have been marked as prevented
		// by a handler lower down the tree; reflect the correct value.
		this.isDefaultPrevented = src.defaultPrevented ||
				src.defaultPrevented === undefined &&

				// Support: Android <=2.3 only
				src.returnValue === false ?
			returnTrue :
			returnFalse;

		// Create target properties
		// Support: Safari <=6 - 7 only
		// Target should not be a text node (trac-504, trac-13143)
		this.target = ( src.target && src.target.nodeType === 3 ) ?
			src.target.parentNode :
			src.target;

		this.currentTarget = src.currentTarget;
		this.relatedTarget = src.relatedTarget;

	// Event type
	} else {
		this.type = src;
	}

	// Put explicitly provided properties onto the event object
	if ( props ) {
		jQuery.extend( this, props );
	}

	// Create a timestamp if incoming event doesn't have one
	this.timeStamp = src && src.timeStamp || Date.now();

	// Mark it as fixed
	this[ jQuery.expando ] = true;
};

// jQuery.Event is based on DOM3 Events as specified by the ECMAScript Language Binding
// https://www.w3.org/TR/2003/WD-DOM-Level-3-Events-20030331/ecma-script-binding.html
jQuery.Event.prototype = {
	constructor: jQuery.Event,
	isDefaultPrevented: returnFalse,
	isPropagationStopped: returnFalse,
	isImmediatePropagationStopped: returnFalse,
	isSimulated: false,

	preventDefault: function() {
		var e = this.originalEvent;

		this.isDefaultPrevented = returnTrue;

		if ( e && !this.isSimulated ) {
			e.preventDefault();
		}
	},
	stopPropagation: function() {
		var e = this.originalEvent;

		this.isPropagationStopped = returnTrue;

		if ( e && !this.isSimulated ) {
			e.stopPropagation();
		}
	},
	stopImmediatePropagation: function() {
		var e = this.originalEvent;

		this.isImmediatePropagationStopped = returnTrue;

		if ( e && !this.isSimulated ) {
			e.stopImmediatePropagation();
		}

		this.stopPropagation();
	}
};

// Includes all common event props including KeyEvent and MouseEvent specific props
jQuery.each( {
	altKey: true,
	bubbles: true,
	cancelable: true,
	changedTouches: true,
	ctrlKey: true,
	detail: true,
	eventPhase: true,
	metaKey: true,
	pageX: true,
	pageY: true,
	shiftKey: true,
	view: true,
	"char": true,
	code: true,
	charCode: true,
	key: true,
	keyCode: true,
	button: true,
	buttons: true,
	clientX: true,
	clientY: true,
	offsetX: true,
	offsetY: true,
	pointerId: true,
	pointerType: true,
	screenX: true,
	screenY: true,
	targetTouches: true,
	toElement: true,
	touches: true,
	which: true
}, jQuery.event.addProp );

jQuery.each( { focus: "focusin", blur: "focusout" }, function( type, delegateType ) {

	function focusMappedHandler( nativeEvent ) {
		if ( document.documentMode ) {

			// Support: IE 11+
			// Attach a single focusin/focusout handler on the document while someone wants
			// focus/blur. This is because the former are synchronous in IE while the latter
			// are async. In other browsers, all those handlers are invoked synchronously.

			// `handle` from private data would already wrap the event, but we need
			// to change the `type` here.
			var handle = dataPriv.get( this, "handle" ),
				event = jQuery.event.fix( nativeEvent );
			event.type = nativeEvent.type === "focusin" ? "focus" : "blur";
			event.isSimulated = true;

			// First, handle focusin/focusout
			handle( nativeEvent );

			// ...then, handle focus/blur
			//
			// focus/blur don't bubble while focusin/focusout do; simulate the former by only
			// invoking the handler at the lower level.
			if ( event.target === event.currentTarget ) {

				// The setup part calls `leverageNative`, which, in turn, calls
				// `jQuery.event.add`, so event handle will already have been set
				// by this point.
				handle( event );
			}
		} else {

			// For non-IE browsers, attach a single capturing handler on the document
			// while someone wants focusin/focusout.
			jQuery.event.simulate( delegateType, nativeEvent.target,
				jQuery.event.fix( nativeEvent ) );
		}
	}

	jQuery.event.special[ type ] = {

		// Utilize native event if possible so blur/focus sequence is correct
		setup: function() {

			var attaches;

			// Claim the first handler
			// dataPriv.set( this, "focus", ... )
			// dataPriv.set( this, "blur", ... )
			leverageNative( this, type, true );

			if ( document.documentMode ) {

				// Support: IE 9 - 11+
				// We use the same native handler for focusin & focus (and focusout & blur)
				// so we need to coordinate setup & teardown parts between those events.
				// Use `delegateType` as the key as `type` is already used by `leverageNative`.
				attaches = dataPriv.get( this, delegateType );
				if ( !attaches ) {
					this.addEventListener( delegateType, focusMappedHandler );
				}
				dataPriv.set( this, delegateType, ( attaches || 0 ) + 1 );
			} else {

				// Return false to allow normal processing in the caller
				return false;
			}
		},
		trigger: function() {

			// Force setup before trigger
			leverageNative( this, type );

			// Return non-false to allow normal event-path propagation
			return true;
		},

		teardown: function() {
			var attaches;

			if ( document.documentMode ) {
				attaches = dataPriv.get( this, delegateType ) - 1;
				if ( !attaches ) {
					this.removeEventListener( delegateType, focusMappedHandler );
					dataPriv.remove( this, delegateType );
				} else {
					dataPriv.set( this, delegateType, attaches );
				}
			} else {

				// Return false to indicate standard teardown should be applied
				return false;
			}
		},

		// Suppress native focus or blur if we're currently inside
		// a leveraged native-event stack
		_default: function( event ) {
			return dataPriv.get( event.target, type );
		},

		delegateType: delegateType
	};

	// Support: Firefox <=44
	// Firefox doesn't have focus(in | out) events
	// Related ticket - https://bugzilla.mozilla.org/show_bug.cgi?id=687787
	//
	// Support: Chrome <=48 - 49, Safari <=9.0 - 9.1
	// focus(in | out) events fire after focus & blur events,
	// which is spec violation - http://www.w3.org/TR/DOM-Level-3-Events/#events-focusevent-event-order
	// Related ticket - https://bugs.chromium.org/p/chromium/issues/detail?id=449857
	//
	// Support: IE 9 - 11+
	// To preserve relative focusin/focus & focusout/blur event order guaranteed on the 3.x branch,
	// attach a single handler for both events in IE.
	jQuery.event.special[ delegateType ] = {
		setup: function() {

			// Handle: regular nodes (via `this.ownerDocument`), window
			// (via `this.document`) & document (via `this`).
			var doc = this.ownerDocument || this.document || this,
				dataHolder = document.documentMode ? this : doc,
				attaches = dataPriv.get( dataHolder, delegateType );

			// Support: IE 9 - 11+
			// We use the same native handler for focusin & focus (and focusout & blur)
			// so we need to coordinate setup & teardown parts between those events.
			// Use `delegateType` as the key as `type` is already used by `leverageNative`.
			if ( !attaches ) {
				if ( document.documentMode ) {
					this.addEventListener( delegateType, focusMappedHandler );
				} else {
					doc.addEventListener( type, focusMappedHandler, true );
				}
			}
			dataPriv.set( dataHolder, delegateType, ( attaches || 0 ) + 1 );
		},
		teardown: function() {
			var doc = this.ownerDocument || this.document || this,
				dataHolder = document.documentMode ? this : doc,
				attaches = dataPriv.get( dataHolder, delegateType ) - 1;

			if ( !attaches ) {
				if ( document.documentMode ) {
					this.removeEventListener( delegateType, focusMappedHandler );
				} else {
					doc.removeEventListener( type, focusMappedHandler, true );
				}
				dataPriv.remove( dataHolder, delegateType );
			} else {
				dataPriv.set( dataHolder, delegateType, attaches );
			}
		}
	};
} );

// Create mouseenter/leave events using mouseover/out and event-time checks
// so that event delegation works in jQuery.
// Do the same for pointerenter/pointerleave and pointerover/pointerout
//
// Support: Safari 7 only
// Safari sends mouseenter too often; see:
// https://bugs.chromium.org/p/chromium/issues/detail?id=470258
// for the description of the bug (it existed in older Chrome versions as well).
jQuery.each( {
	mouseenter: "mouseover",
	mouseleave: "mouseout",
	pointerenter: "pointerover",
	pointerleave: "pointerout"
}, function( orig, fix ) {
	jQuery.event.special[ orig ] = {
		delegateType: fix,
		bindType: fix,

		handle: function( event ) {
			var ret,
				target = this,
				related = event.relatedTarget,
				handleObj = event.handleObj;

			// For mouseenter/leave call the handler if related is outside the target.
			// NB: No relatedTarget if the mouse left/entered the browser window
			if ( !related || ( related !== target && !jQuery.contains( target, related ) ) ) {
				event.type = handleObj.origType;
				ret = handleObj.handler.apply( this, arguments );
				event.type = fix;
			}
			return ret;
		}
	};
} );

jQuery.fn.extend( {

	on: function( types, selector, data, fn ) {
		return on( this, types, selector, data, fn );
	},
	one: function( types, selector, data, fn ) {
		return on( this, types, selector, data, fn, 1 );
	},
	off: function( types, selector, fn ) {
		var handleObj, type;
		if ( types && types.preventDefault && types.handleObj ) {

			// ( event )  dispatched jQuery.Event
			handleObj = types.handleObj;
			jQuery( types.delegateTarget ).off(
				handleObj.namespace ?
					handleObj.origType + "." + handleObj.namespace :
					handleObj.origType,
				handleObj.selector,
				handleObj.handler
			);
			return this;
		}
		if ( typeof types === "object" ) {

			// ( types-object [, selector] )
			for ( type in types ) {
				this.off( type, selector, types[ type ] );
			}
			return this;
		}
		if ( selector === false || typeof selector === "function" ) {

			// ( types [, fn] )
			fn = selector;
			selector = undefined;
		}
		if ( fn === false ) {
			fn = returnFalse;
		}
		return this.each( function() {
			jQuery.event.remove( this, types, fn, selector );
		} );
	}
} );


var

	// Support: IE <=10 - 11, Edge 12 - 13 only
	// In IE/Edge using regex groups here causes severe slowdowns.
	// See https://connect.microsoft.com/IE/feedback/details/1736512/
	rnoInnerhtml = /<script|<style|<link/i,

	// checked="checked" or checked
	rchecked = /checked\s*(?:[^=]|=\s*.checked.)/i,

	rcleanScript = /^\s*<!\[CDATA\[|\]\]>\s*$/g;

// Prefer a tbody over its parent table for containing new rows
function manipulationTarget( elem, content ) {
	if ( nodeName( elem, "table" ) &&
		nodeName( content.nodeType !== 11 ? content : content.firstChild, "tr" ) ) {

		return jQuery( elem ).children( "tbody" )[ 0 ] || elem;
	}

	return elem;
}

// Replace/restore the type attribute of script elements for safe DOM manipulation
function disableScript( elem ) {
	elem.type = ( elem.getAttribute( "type" ) !== null ) + "/" + elem.type;
	return elem;
}
function restoreScript( elem ) {
	if ( ( elem.type || "" ).slice( 0, 5 ) === "true/" ) {
		elem.type = elem.type.slice( 5 );
	} else {
		elem.removeAttribute( "type" );
	}

	return elem;
}

function cloneCopyEvent( src, dest ) {
	var i, l, type, pdataOld, udataOld, udataCur, events;

	if ( dest.nodeType !== 1 ) {
		return;
	}

	// 1. Copy private data: events, handlers, etc.
	if ( dataPriv.hasData( src ) ) {
		pdataOld = dataPriv.get( src );
		events = pdataOld.events;

		if ( events ) {
			dataPriv.remove( dest, "handle events" );

			for ( type in events ) {
				for ( i = 0, l = events[ type ].length; i < l; i++ ) {
					jQuery.event.add( dest, type, events[ type ][ i ] );
				}
			}
		}
	}

	// 2. Copy user data
	if ( dataUser.hasData( src ) ) {
		udataOld = dataUser.access( src );
		udataCur = jQuery.extend( {}, udataOld );

		dataUser.set( dest, udataCur );
	}
}

// Fix IE bugs, see support tests
function fixInput( src, dest ) {
	var nodeName = dest.nodeName.toLowerCase();

	// Fails to persist the checked state of a cloned checkbox or radio button.
	if ( nodeName === "input" && rcheckableType.test( src.type ) ) {
		dest.checked = src.checked;

	// Fails to return the selected option to the default selected state when cloning options
	} else if ( nodeName === "input" || nodeName === "textarea" ) {
		dest.defaultValue = src.defaultValue;
	}
}

function domManip( collection, args, callback, ignored ) {

	// Flatten any nested arrays
	args = flat( args );

	var fragment, first, scripts, hasScripts, node, doc,
		i = 0,
		l = collection.length,
		iNoClone = l - 1,
		value = args[ 0 ],
		valueIsFunction = isFunction( value );

	// We can't cloneNode fragments that contain checked, in WebKit
	if ( valueIsFunction ||
			( l > 1 && typeof value === "string" &&
				!support.checkClone && rchecked.test( value ) ) ) {
		return collection.each( function( index ) {
			var self = collection.eq( index );
			if ( valueIsFunction ) {
				args[ 0 ] = value.call( this, index, self.html() );
			}
			domManip( self, args, callback, ignored );
		} );
	}

	if ( l ) {
		fragment = buildFragment( args, collection[ 0 ].ownerDocument, false, collection, ignored );
		first = fragment.firstChild;

		if ( fragment.childNodes.length === 1 ) {
			fragment = first;
		}

		// Require either new content or an interest in ignored elements to invoke the callback
		if ( first || ignored ) {
			scripts = jQuery.map( getAll( fragment, "script" ), disableScript );
			hasScripts = scripts.length;

			// Use the original fragment for the last item
			// instead of the first because it can end up
			// being emptied incorrectly in certain situations (trac-8070).
			for ( ; i < l; i++ ) {
				node = fragment;

				if ( i !== iNoClone ) {
					node = jQuery.clone( node, true, true );

					// Keep references to cloned scripts for later restoration
					if ( hasScripts ) {

						// Support: Android <=4.0 only, PhantomJS 1 only
						// push.apply(_, arraylike) throws on ancient WebKit
						jQuery.merge( scripts, getAll( node, "script" ) );
					}
				}

				callback.call( collection[ i ], node, i );
			}

			if ( hasScripts ) {
				doc = scripts[ scripts.length - 1 ].ownerDocument;

				// Re-enable scripts
				jQuery.map( scripts, restoreScript );

				// Evaluate executable scripts on first document insertion
				for ( i = 0; i < hasScripts; i++ ) {
					node = scripts[ i ];
					if ( rscriptType.test( node.type || "" ) &&
						!dataPriv.access( node, "globalEval" ) &&
						jQuery.contains( doc, node ) ) {

						if ( node.src && ( node.type || "" ).toLowerCase()  !== "module" ) {

							// Optional AJAX dependency, but won't run scripts if not present
							if ( jQuery._evalUrl && !node.noModule ) {
								jQuery._evalUrl( node.src, {
									nonce: node.nonce || node.getAttribute( "nonce" )
								}, doc );
							}
						} else {

							// Unwrap a CDATA section containing script contents. This shouldn't be
							// needed as in XML documents they're already not visible when
							// inspecting element contents and in HTML documents they have no
							// meaning but we're preserving that logic for backwards compatibility.
							// This will be removed completely in 4.0. See gh-4904.
							DOMEval( node.textContent.replace( rcleanScript, "" ), node, doc );
						}
					}
				}
			}
		}
	}

	return collection;
}

function remove( elem, selector, keepData ) {
	var node,
		nodes = selector ? jQuery.filter( selector, elem ) : elem,
		i = 0;

	for ( ; ( node = nodes[ i ] ) != null; i++ ) {
		if ( !keepData && node.nodeType === 1 ) {
			jQuery.cleanData( getAll( node ) );
		}

		if ( node.parentNode ) {
			if ( keepData && isAttached( node ) ) {
				setGlobalEval( getAll( node, "script" ) );
			}
			node.parentNode.removeChild( node );
		}
	}

	return elem;
}

jQuery.extend( {
	htmlPrefilter: function( html ) {
		return html;
	},

	clone: function( elem, dataAndEvents, deepDataAndEvents ) {
		var i, l, srcElements, destElements,
			clone = elem.cloneNode( true ),
			inPage = isAttached( elem );

		// Fix IE cloning issues
		if ( !support.noCloneChecked && ( elem.nodeType === 1 || elem.nodeType === 11 ) &&
				!jQuery.isXMLDoc( elem ) ) {

			// We eschew jQuery#find here for performance reasons:
			// https://jsperf.com/getall-vs-sizzle/2
			destElements = getAll( clone );
			srcElements = getAll( elem );

			for ( i = 0, l = srcElements.length; i < l; i++ ) {
				fixInput( srcElements[ i ], destElements[ i ] );
			}
		}

		// Copy the events from the original to the clone
		if ( dataAndEvents ) {
			if ( deepDataAndEvents ) {
				srcElements = srcElements || getAll( elem );
				destElements = destElements || getAll( clone );

				for ( i = 0, l = srcElements.length; i < l; i++ ) {
					cloneCopyEvent( srcElements[ i ], destElements[ i ] );
				}
			} else {
				cloneCopyEvent( elem, clone );
			}
		}

		// Preserve script evaluation history
		destElements = getAll( clone, "script" );
		if ( destElements.length > 0 ) {
			setGlobalEval( destElements, !inPage && getAll( elem, "script" ) );
		}

		// Return the cloned set
		return clone;
	},

	cleanData: function( elems ) {
		var data, elem, type,
			special = jQuery.event.special,
			i = 0;

		for ( ; ( elem = elems[ i ] ) !== undefined; i++ ) {
			if ( acceptData( elem ) ) {
				if ( ( data = elem[ dataPriv.expando ] ) ) {
					if ( data.events ) {
						for ( type in data.events ) {
							if ( special[ type ] ) {
								jQuery.event.remove( elem, type );

							// This is a shortcut to avoid jQuery.event.remove's overhead
							} else {
								jQuery.removeEvent( elem, type, data.handle );
							}
						}
					}

					// Support: Chrome <=35 - 45+
					// Assign undefined instead of using delete, see Data#remove
					elem[ dataPriv.expando ] = undefined;
				}
				if ( elem[ dataUser.expando ] ) {

					// Support: Chrome <=35 - 45+
					// Assign undefined instead of using delete, see Data#remove
					elem[ dataUser.expando ] = undefined;
				}
			}
		}
	}
} );

jQuery.fn.extend( {
	detach: function( selector ) {
		return remove( this, selector, true );
	},

	remove: function( selector ) {
		return remove( this, selector );
	},

	text: function( value ) {
		return access( this, function( value ) {
			return value === undefined ?
				jQuery.text( this ) :
				this.empty().each( function() {
					if ( this.nodeType === 1 || this.nodeType === 11 || this.nodeType === 9 ) {
						this.textContent = value;
					}
				} );
		}, null, value, arguments.length );
	},

	append: function() {
		return domManip( this, arguments, function( elem ) {
			if ( this.nodeType === 1 || this.nodeType === 11 || this.nodeType === 9 ) {
				var target = manipulationTarget( this, elem );
				target.appendChild( elem );
			}
		} );
	},

	prepend: function() {
		return domManip( this, arguments, function( elem ) {
			if ( this.nodeType === 1 || this.nodeType === 11 || this.nodeType === 9 ) {
				var target = manipulationTarget( this, elem );
				target.insertBefore( elem, target.firstChild );
			}
		} );
	},

	before: function() {
		return domManip( this, arguments, function( elem ) {
			if ( this.parentNode ) {
				this.parentNode.insertBefore( elem, this );
			}
		} );
	},

	after: function() {
		return domManip( this, arguments, function( elem ) {
			if ( this.parentNode ) {
				this.parentNode.insertBefore( elem, this.nextSibling );
			}
		} );
	},

	empty: function() {
		var elem,
			i = 0;

		for ( ; ( elem = this[ i ] ) != null; i++ ) {
			if ( elem.nodeType === 1 ) {

				// Prevent memory leaks
				jQuery.cleanData( getAll( elem, false ) );

				// Remove any remaining nodes
				elem.textContent = "";
			}
		}

		return this;
	},

	clone: function( dataAndEvents, deepDataAndEvents ) {
		dataAndEvents = dataAndEvents == null ? false : dataAndEvents;
		deepDataAndEvents = deepDataAndEvents == null ? dataAndEvents : deepDataAndEvents;

		return this.map( function() {
			return jQuery.clone( this, dataAndEvents, deepDataAndEvents );
		} );
	},

	html: function( value ) {
		return access( this, function( value ) {
			var elem = this[ 0 ] || {},
				i = 0,
				l = this.length;

			if ( value === undefined && elem.nodeType === 1 ) {
				return elem.innerHTML;
			}

			// See if we can take a shortcut and just use innerHTML
			if ( typeof value === "string" && !rnoInnerhtml.test( value ) &&
				!wrapMap[ ( rtagName.exec( value ) || [ "", "" ] )[ 1 ].toLowerCase() ] ) {

				value = jQuery.htmlPrefilter( value );

				try {
					for ( ; i < l; i++ ) {
						elem = this[ i ] || {};

						// Remove element nodes and prevent memory leaks
						if ( elem.nodeType === 1 ) {
							jQuery.cleanData( getAll( elem, false ) );
							elem.innerHTML = value;
						}
					}

					elem = 0;

				// If using innerHTML throws an exception, use the fallback method
				} catch ( e ) {}
			}

			if ( elem ) {
				this.empty().append( value );
			}
		}, null, value, arguments.length );
	},

	replaceWith: function() {
		var ignored = [];

		// Make the changes, replacing each non-ignored context element with the new content
		return domManip( this, arguments, function( elem ) {
			var parent = this.parentNode;

			if ( jQuery.inArray( this, ignored ) < 0 ) {
				jQuery.cleanData( getAll( this ) );
				if ( parent ) {
					parent.replaceChild( elem, this );
				}
			}

		// Force callback invocation
		}, ignored );
	}
} );

jQuery.each( {
	appendTo: "append",
	prependTo: "prepend",
	insertBefore: "before",
	insertAfter: "after",
	replaceAll: "replaceWith"
}, function( name, original ) {
	jQuery.fn[ name ] = function( selector ) {
		var elems,
			ret = [],
			insert = jQuery( selector ),
			last = insert.length - 1,
			i = 0;

		for ( ; i <= last; i++ ) {
			elems = i === last ? this : this.clone( true );
			jQuery( insert[ i ] )[ original ]( elems );

			// Support: Android <=4.0 only, PhantomJS 1 only
			// .get() because push.apply(_, arraylike) throws on ancient WebKit
			push.apply( ret, elems.get() );
		}

		return this.pushStack( ret );
	};
} );
var rnumnonpx = new RegExp( "^(" + pnum + ")(?!px)[a-z%]+$", "i" );

var rcustomProp = /^--/;


var getStyles = function( elem ) {

		// Support: IE <=11 only, Firefox <=30 (trac-15098, trac-14150)
		// IE throws on elements created in popups
		// FF meanwhile throws on frame elements through "defaultView.getComputedStyle"
		var view = elem.ownerDocument.defaultView;

		if ( !view || !view.opener ) {
			view = window;
		}

		return view.getComputedStyle( elem );
	};

var swap = function( elem, options, callback ) {
	var ret, name,
		old = {};

	// Remember the old values, and insert the new ones
	for ( name in options ) {
		old[ name ] = elem.style[ name ];
		elem.style[ name ] = options[ name ];
	}

	ret = callback.call( elem );

	// Revert the old values
	for ( name in options ) {
		elem.style[ name ] = old[ name ];
	}

	return ret;
};


var rboxStyle = new RegExp( cssExpand.join( "|" ), "i" );



( function() {

	// Executing both pixelPosition & boxSizingReliable tests require only one layout
	// so they're executed at the same time to save the second computation.
	function computeStyleTests() {

		// This is a singleton, we need to execute it only once
		if ( !div ) {
			return;
		}

		container.style.cssText = "position:absolute;left:-11111px;width:60px;" +
			"margin-top:1px;padding:0;border:0";
		div.style.cssText =
			"position:relative;display:block;box-sizing:border-box;overflow:scroll;" +
			"margin:auto;border:1px;padding:1px;" +
			"width:60%;top:1%";
		documentElement.appendChild( container ).appendChild( div );

		var divStyle = window.getComputedStyle( div );
		pixelPositionVal = divStyle.top !== "1%";

		// Support: Android 4.0 - 4.3 only, Firefox <=3 - 44
		reliableMarginLeftVal = roundPixelMeasures( divStyle.marginLeft ) === 12;

		// Support: Android 4.0 - 4.3 only, Safari <=9.1 - 10.1, iOS <=7.0 - 9.3
		// Some styles come back with percentage values, even though they shouldn't
		div.style.right = "60%";
		pixelBoxStylesVal = roundPixelMeasures( divStyle.right ) === 36;

		// Support: IE 9 - 11 only
		// Detect misreporting of content dimensions for box-sizing:border-box elements
		boxSizingReliableVal = roundPixelMeasures( divStyle.width ) === 36;

		// Support: IE 9 only
		// Detect overflow:scroll screwiness (gh-3699)
		// Support: Chrome <=64
		// Don't get tricked when zoom affects offsetWidth (gh-4029)
		div.style.position = "absolute";
		scrollboxSizeVal = roundPixelMeasures( div.offsetWidth / 3 ) === 12;

		documentElement.removeChild( container );

		// Nullify the div so it wouldn't be stored in the memory and
		// it will also be a sign that checks already performed
		div = null;
	}

	function roundPixelMeasures( measure ) {
		return Math.round( parseFloat( measure ) );
	}

	var pixelPositionVal, boxSizingReliableVal, scrollboxSizeVal, pixelBoxStylesVal,
		reliableTrDimensionsVal, reliableMarginLeftVal,
		container = document.createElement( "div" ),
		div = document.createElement( "div" );

	// Finish early in limited (non-browser) environments
	if ( !div.style ) {
		return;
	}

	// Support: IE <=9 - 11 only
	// Style of cloned element affects source element cloned (trac-8908)
	div.style.backgroundClip = "content-box";
	div.cloneNode( true ).style.backgroundClip = "";
	support.clearCloneStyle = div.style.backgroundClip === "content-box";

	jQuery.extend( support, {
		boxSizingReliable: function() {
			computeStyleTests();
			return boxSizingReliableVal;
		},
		pixelBoxStyles: function() {
			computeStyleTests();
			return pixelBoxStylesVal;
		},
		pixelPosition: function() {
			computeStyleTests();
			return pixelPositionVal;
		},
		reliableMarginLeft: function() {
			computeStyleTests();
			return reliableMarginLeftVal;
		},
		scrollboxSize: function() {
			computeStyleTests();
			return scrollboxSizeVal;
		},

		// Support: IE 9 - 11+, Edge 15 - 18+
		// IE/Edge misreport `getComputedStyle` of table rows with width/height
		// set in CSS while `offset*` properties report correct values.
		// Behavior in IE 9 is more subtle than in newer versions & it passes
		// some versions of this test; make sure not to make it pass there!
		//
		// Support: Firefox 70+
		// Only Firefox includes border widths
		// in computed dimensions. (gh-4529)
		reliableTrDimensions: function() {
			var table, tr, trChild, trStyle;
			if ( reliableTrDimensionsVal == null ) {
				table = document.createElement( "table" );
				tr = document.createElement( "tr" );
				trChild = document.createElement( "div" );

				table.style.cssText = "position:absolute;left:-11111px;border-collapse:separate";
				tr.style.cssText = "box-sizing:content-box;border:1px solid";

				// Support: Chrome 86+
				// Height set through cssText does not get applied.
				// Computed height then comes back as 0.
				tr.style.height = "1px";
				trChild.style.height = "9px";

				// Support: Android 8 Chrome 86+
				// In our bodyBackground.html iframe,
				// display for all div elements is set to "inline",
				// which causes a problem only in Android 8 Chrome 86.
				// Ensuring the div is `display: block`
				// gets around this issue.
				trChild.style.display = "block";

				documentElement
					.appendChild( table )
					.appendChild( tr )
					.appendChild( trChild );

				trStyle = window.getComputedStyle( tr );
				reliableTrDimensionsVal = ( parseInt( trStyle.height, 10 ) +
					parseInt( trStyle.borderTopWidth, 10 ) +
					parseInt( trStyle.borderBottomWidth, 10 ) ) === tr.offsetHeight;

				documentElement.removeChild( table );
			}
			return reliableTrDimensionsVal;
		}
	} );
} )();


function curCSS( elem, name, computed ) {
	var width, minWidth, maxWidth, ret,
		isCustomProp = rcustomProp.test( name ),

		// Support: Firefox 51+
		// Retrieving style before computed somehow
		// fixes an issue with getting wrong values
		// on detached elements
		style = elem.style;

	computed = computed || getStyles( elem );

	// getPropertyValue is needed for:
	//   .css('filter') (IE 9 only, trac-12537)
	//   .css('--customProperty) (gh-3144)
	if ( computed ) {

		// Support: IE <=9 - 11+
		// IE only supports `"float"` in `getPropertyValue`; in computed styles
		// it's only available as `"cssFloat"`. We no longer modify properties
		// sent to `.css()` apart from camelCasing, so we need to check both.
		// Normally, this would create difference in behavior: if
		// `getPropertyValue` returns an empty string, the value returned
		// by `.css()` would be `undefined`. This is usually the case for
		// disconnected elements. However, in IE even disconnected elements
		// with no styles return `"none"` for `getPropertyValue( "float" )`
		ret = computed.getPropertyValue( name ) || computed[ name ];

		if ( isCustomProp && ret ) {

			// Support: Firefox 105+, Chrome <=105+
			// Spec requires trimming whitespace for custom properties (gh-4926).
			// Firefox only trims leading whitespace. Chrome just collapses
			// both leading & trailing whitespace to a single space.
			//
			// Fall back to `undefined` if empty string returned.
			// This collapses a missing definition with property defined
			// and set to an empty string but there's no standard API
			// allowing us to differentiate them without a performance penalty
			// and returning `undefined` aligns with older jQuery.
			//
			// rtrimCSS treats U+000D CARRIAGE RETURN and U+000C FORM FEED
			// as whitespace while CSS does not, but this is not a problem
			// because CSS preprocessing replaces them with U+000A LINE FEED
			// (which *is* CSS whitespace)
			// https://www.w3.org/TR/css-syntax-3/#input-preprocessing
			ret = ret.replace( rtrimCSS, "$1" ) || undefined;
		}

		if ( ret === "" && !isAttached( elem ) ) {
			ret = jQuery.style( elem, name );
		}

		// A tribute to the "awesome hack by Dean Edwards"
		// Android Browser returns percentage for some values,
		// but width seems to be reliably pixels.
		// This is against the CSSOM draft spec:
		// https://drafts.csswg.org/cssom/#resolved-values
		if ( !support.pixelBoxStyles() && rnumnonpx.test( ret ) && rboxStyle.test( name ) ) {

			// Remember the original values
			width = style.width;
			minWidth = style.minWidth;
			maxWidth = style.maxWidth;

			// Put in the new values to get a computed value out
			style.minWidth = style.maxWidth = style.width = ret;
			ret = computed.width;

			// Revert the changed values
			style.width = width;
			style.minWidth = minWidth;
			style.maxWidth = maxWidth;
		}
	}

	return ret !== undefined ?

		// Support: IE <=9 - 11 only
		// IE returns zIndex value as an integer.
		ret + "" :
		ret;
}


function addGetHookIf( conditionFn, hookFn ) {

	// Define the hook, we'll check on the first run if it's really needed.
	return {
		get: function() {
			if ( conditionFn() ) {

				// Hook not needed (or it's not possible to use it due
				// to missing dependency), remove it.
				delete this.get;
				return;
			}

			// Hook needed; redefine it so that the support test is not executed again.
			return ( this.get = hookFn ).apply( this, arguments );
		}
	};
}


var cssPrefixes = [ "Webkit", "Moz", "ms" ],
	emptyStyle = document.createElement( "div" ).style,
	vendorProps = {};

// Return a vendor-prefixed property or undefined
function vendorPropName( name ) {

	// Check for vendor prefixed names
	var capName = name[ 0 ].toUpperCase() + name.slice( 1 ),
		i = cssPrefixes.length;

	while ( i-- ) {
		name = cssPrefixes[ i ] + capName;
		if ( name in emptyStyle ) {
			return name;
		}
	}
}

// Return a potentially-mapped jQuery.cssProps or vendor prefixed property
function finalPropName( name ) {
	var final = jQuery.cssProps[ name ] || vendorProps[ name ];

	if ( final ) {
		return final;
	}
	if ( name in emptyStyle ) {
		return name;
	}
	return vendorProps[ name ] = vendorPropName( name ) || name;
}


var

	// Swappable if display is none or starts with table
	// except "table", "table-cell", or "table-caption"
	// See here for display values: https://developer.mozilla.org/en-US/docs/CSS/display
	rdisplayswap = /^(none|table(?!-c[ea]).+)/,
	cssShow = { position: "absolute", visibility: "hidden", display: "block" },
	cssNormalTransform = {
		letterSpacing: "0",
		fontWeight: "400"
	};

function setPositiveNumber( _elem, value, subtract ) {

	// Any relative (+/-) values have already been
	// normalized at this point
	var matches = rcssNum.exec( value );
	return matches ?

		// Guard against undefined "subtract", e.g., when used as in cssHooks
		Math.max( 0, matches[ 2 ] - ( subtract || 0 ) ) + ( matches[ 3 ] || "px" ) :
		value;
}

function boxModelAdjustment( elem, dimension, box, isBorderBox, styles, computedVal ) {
	var i = dimension === "width" ? 1 : 0,
		extra = 0,
		delta = 0,
		marginDelta = 0;

	// Adjustment may not be necessary
	if ( box === ( isBorderBox ? "border" : "content" ) ) {
		return 0;
	}

	for ( ; i < 4; i += 2 ) {

		// Both box models exclude margin
		// Count margin delta separately to only add it after scroll gutter adjustment.
		// This is needed to make negative margins work with `outerHeight( true )` (gh-3982).
		if ( box === "margin" ) {
			marginDelta += jQuery.css( elem, box + cssExpand[ i ], true, styles );
		}

		// If we get here with a content-box, we're seeking "padding" or "border" or "margin"
		if ( !isBorderBox ) {

			// Add padding
			delta += jQuery.css( elem, "padding" + cssExpand[ i ], true, styles );

			// For "border" or "margin", add border
			if ( box !== "padding" ) {
				delta += jQuery.css( elem, "border" + cssExpand[ i ] + "Width", true, styles );

			// But still keep track of it otherwise
			} else {
				extra += jQuery.css( elem, "border" + cssExpand[ i ] + "Width", true, styles );
			}

		// If we get here with a border-box (content + padding + border), we're seeking "content" or
		// "padding" or "margin"
		} else {

			// For "content", subtract padding
			if ( box === "content" ) {
				delta -= jQuery.css( elem, "padding" + cssExpand[ i ], true, styles );
			}

			// For "content" or "padding", subtract border
			if ( box !== "margin" ) {
				delta -= jQuery.css( elem, "border" + cssExpand[ i ] + "Width", true, styles );
			}
		}
	}

	// Account for positive content-box scroll gutter when requested by providing computedVal
	if ( !isBorderBox && computedVal >= 0 ) {

		// offsetWidth/offsetHeight is a rounded sum of content, padding, scroll gutter, and border
		// Assuming integer scroll gutter, subtract the rest and round down
		delta += Math.max( 0, Math.ceil(
			elem[ "offset" + dimension[ 0 ].toUpperCase() + dimension.slice( 1 ) ] -
			computedVal -
			delta -
			extra -
			0.5

		// If offsetWidth/offsetHeight is unknown, then we can't determine content-box scroll gutter
		// Use an explicit zero to avoid NaN (gh-3964)
		) ) || 0;
	}

	return delta + marginDelta;
}

function getWidthOrHeight( elem, dimension, extra ) {

	// Start with computed style
	var styles = getStyles( elem ),

		// To avoid forcing a reflow, only fetch boxSizing if we need it (gh-4322).
		// Fake content-box until we know it's needed to know the true value.
		boxSizingNeeded = !support.boxSizingReliable() || extra,
		isBorderBox = boxSizingNeeded &&
			jQuery.css( elem, "boxSizing", false, styles ) === "border-box",
		valueIsBorderBox = isBorderBox,

		val = curCSS( elem, dimension, styles ),
		offsetProp = "offset" + dimension[ 0 ].toUpperCase() + dimension.slice( 1 );

	// Support: Firefox <=54
	// Return a confounding non-pixel value or feign ignorance, as appropriate.
	if ( rnumnonpx.test( val ) ) {
		if ( !extra ) {
			return val;
		}
		val = "auto";
	}


	// Support: IE 9 - 11 only
	// Use offsetWidth/offsetHeight for when box sizing is unreliable.
	// In those cases, the computed value can be trusted to be border-box.
	if ( ( !support.boxSizingReliable() && isBorderBox ||

		// Support: IE 10 - 11+, Edge 15 - 18+
		// IE/Edge misreport `getComputedStyle` of table rows with width/height
		// set in CSS while `offset*` properties report correct values.
		// Interestingly, in some cases IE 9 doesn't suffer from this issue.
		!support.reliableTrDimensions() && nodeName( elem, "tr" ) ||

		// Fall back to offsetWidth/offsetHeight when value is "auto"
		// This happens for inline elements with no explicit setting (gh-3571)
		val === "auto" ||

		// Support: Android <=4.1 - 4.3 only
		// Also use offsetWidth/offsetHeight for misreported inline dimensions (gh-3602)
		!parseFloat( val ) && jQuery.css( elem, "display", false, styles ) === "inline" ) &&

		// Make sure the element is visible & connected
		elem.getClientRects().length ) {

		isBorderBox = jQuery.css( elem, "boxSizing", false, styles ) === "border-box";

		// Where available, offsetWidth/offsetHeight approximate border box dimensions.
		// Where not available (e.g., SVG), assume unreliable box-sizing and interpret the
		// retrieved value as a content box dimension.
		valueIsBorderBox = offsetProp in elem;
		if ( valueIsBorderBox ) {
			val = elem[ offsetProp ];
		}
	}

	// Normalize "" and auto
	val = parseFloat( val ) || 0;

	// Adjust for the element's box model
	return ( val +
		boxModelAdjustment(
			elem,
			dimension,
			extra || ( isBorderBox ? "border" : "content" ),
			valueIsBorderBox,
			styles,

			// Provide the current computed size to request scroll gutter calculation (gh-3589)
			val
		)
	) + "px";
}

jQuery.extend( {

	// Add in style property hooks for overriding the default
	// behavior of getting and setting a style property
	cssHooks: {
		opacity: {
			get: function( elem, computed ) {
				if ( computed ) {

					// We should always get a number back from opacity
					var ret = curCSS( elem, "opacity" );
					return ret === "" ? "1" : ret;
				}
			}
		}
	},

	// Don't automatically add "px" to these possibly-unitless properties
	cssNumber: {
		animationIterationCount: true,
		aspectRatio: true,
		borderImageSlice: true,
		columnCount: true,
		flexGrow: true,
		flexShrink: true,
		fontWeight: true,
		gridArea: true,
		gridColumn: true,
		gridColumnEnd: true,
		gridColumnStart: true,
		gridRow: true,
		gridRowEnd: true,
		gridRowStart: true,
		lineHeight: true,
		opacity: true,
		order: true,
		orphans: true,
		scale: true,
		widows: true,
		zIndex: true,
		zoom: true,

		// SVG-related
		fillOpacity: true,
		floodOpacity: true,
		stopOpacity: true,
		strokeMiterlimit: true,
		strokeOpacity: true
	},

	// Add in properties whose names you wish to fix before
	// setting or getting the value
	cssProps: {},

	// Get and set the style property on a DOM Node
	style: function( elem, name, value, extra ) {

		// Don't set styles on text and comment nodes
		if ( !elem || elem.nodeType === 3 || elem.nodeType === 8 || !elem.style ) {
			return;
		}

		// Make sure that we're working with the right name
		var ret, type, hooks,
			origName = camelCase( name ),
			isCustomProp = rcustomProp.test( name ),
			style = elem.style;

		// Make sure that we're working with the right name. We don't
		// want to query the value if it is a CSS custom property
		// since they are user-defined.
		if ( !isCustomProp ) {
			name = finalPropName( origName );
		}

		// Gets hook for the prefixed version, then unprefixed version
		hooks = jQuery.cssHooks[ name ] || jQuery.cssHooks[ origName ];

		// Check if we're setting a value
		if ( value !== undefined ) {
			type = typeof value;

			// Convert "+=" or "-=" to relative numbers (trac-7345)
			if ( type === "string" && ( ret = rcssNum.exec( value ) ) && ret[ 1 ] ) {
				value = adjustCSS( elem, name, ret );

				// Fixes bug trac-9237
				type = "number";
			}

			// Make sure that null and NaN values aren't set (trac-7116)
			if ( value == null || value !== value ) {
				return;
			}

			// If a number was passed in, add the unit (except for certain CSS properties)
			// The isCustomProp check can be removed in jQuery 4.0 when we only auto-append
			// "px" to a few hardcoded values.
			if ( type === "number" && !isCustomProp ) {
				value += ret && ret[ 3 ] || ( jQuery.cssNumber[ origName ] ? "" : "px" );
			}

			// background-* props affect original clone's values
			if ( !support.clearCloneStyle && value === "" && name.indexOf( "background" ) === 0 ) {
				style[ name ] = "inherit";
			}

			// If a hook was provided, use that value, otherwise just set the specified value
			if ( !hooks || !( "set" in hooks ) ||
				( value = hooks.set( elem, value, extra ) ) !== undefined ) {

				if ( isCustomProp ) {
					style.setProperty( name, value );
				} else {
					style[ name ] = value;
				}
			}

		} else {

			// If a hook was provided get the non-computed value from there
			if ( hooks && "get" in hooks &&
				( ret = hooks.get( elem, false, extra ) ) !== undefined ) {

				return ret;
			}

			// Otherwise just get the value from the style object
			return style[ name ];
		}
	},

	css: function( elem, name, extra, styles ) {
		var val, num, hooks,
			origName = camelCase( name ),
			isCustomProp = rcustomProp.test( name );

		// Make sure that we're working with the right name. We don't
		// want to modify the value if it is a CSS custom property
		// since they are user-defined.
		if ( !isCustomProp ) {
			name = finalPropName( origName );
		}

		// Try prefixed name followed by the unprefixed name
		hooks = jQuery.cssHooks[ name ] || jQuery.cssHooks[ origName ];

		// If a hook was provided get the computed value from there
		if ( hooks && "get" in hooks ) {
			val = hooks.get( elem, true, extra );
		}

		// Otherwise, if a way to get the computed value exists, use that
		if ( val === undefined ) {
			val = curCSS( elem, name, styles );
		}

		// Convert "normal" to computed value
		if ( val === "normal" && name in cssNormalTransform ) {
			val = cssNormalTransform[ name ];
		}

		// Make numeric if forced or a qualifier was provided and val looks numeric
		if ( extra === "" || extra ) {
			num = parseFloat( val );
			return extra === true || isFinite( num ) ? num || 0 : val;
		}

		return val;
	}
} );

jQuery.each( [ "height", "width" ], function( _i, dimension ) {
	jQuery.cssHooks[ dimension ] = {
		get: function( elem, computed, extra ) {
			if ( computed ) {

				// Certain elements can have dimension info if we invisibly show them
				// but it must have a current display style that would benefit
				return rdisplayswap.test( jQuery.css( elem, "display" ) ) &&

					// Support: Safari 8+
					// Table columns in Safari have non-zero offsetWidth & zero
					// getBoundingClientRect().width unless display is changed.
					// Support: IE <=11 only
					// Running getBoundingClientRect on a disconnected node
					// in IE throws an error.
					( !elem.getClientRects().length || !elem.getBoundingClientRect().width ) ?
					swap( elem, cssShow, function() {
						return getWidthOrHeight( elem, dimension, extra );
					} ) :
					getWidthOrHeight( elem, dimension, extra );
			}
		},

		set: function( elem, value, extra ) {
			var matches,
				styles = getStyles( elem ),

				// Only read styles.position if the test has a chance to fail
				// to avoid forcing a reflow.
				scrollboxSizeBuggy = !support.scrollboxSize() &&
					styles.position === "absolute",

				// To avoid forcing a reflow, only fetch boxSizing if we need it (gh-3991)
				boxSizingNeeded = scrollboxSizeBuggy || extra,
				isBorderBox = boxSizingNeeded &&
					jQuery.css( elem, "boxSizing", false, styles ) === "border-box",
				subtract = extra ?
					boxModelAdjustment(
						elem,
						dimension,
						extra,
						isBorderBox,
						styles
					) :
					0;

			// Account for unreliable border-box dimensions by comparing offset* to computed and
			// faking a content-box to get border and padding (gh-3699)
			if ( isBorderBox && scrollboxSizeBuggy ) {
				subtract -= Math.ceil(
					elem[ "offset" + dimension[ 0 ].toUpperCase() + dimension.slice( 1 ) ] -
					parseFloat( styles[ dimension ] ) -
					boxModelAdjustment( elem, dimension, "border", false, styles ) -
					0.5
				);
			}

			// Convert to pixels if value adjustment is needed
			if ( subtract && ( matches = rcssNum.exec( value ) ) &&
				( matches[ 3 ] || "px" ) !== "px" ) {

				elem.style[ dimension ] = value;
				value = jQuery.css( elem, dimension );
			}

			return setPositiveNumber( elem, value, subtract );
		}
	};
} );

jQuery.cssHooks.marginLeft = addGetHookIf( support.reliableMarginLeft,
	function( elem, computed ) {
		if ( computed ) {
			return ( parseFloat( curCSS( elem, "marginLeft" ) ) ||
				elem.getBoundingClientRect().left -
					swap( elem, { marginLeft: 0 }, function() {
						return elem.getBoundingClientRect().left;
					} )
			) + "px";
		}
	}
);

// These hooks are used by animate to expand properties
jQuery.each( {
	margin: "",
	padding: "",
	border: "Width"
}, function( prefix, suffix ) {
	jQuery.cssHooks[ prefix + suffix ] = {
		expand: function( value ) {
			var i = 0,
				expanded = {},

				// Assumes a single number if not a string
				parts = typeof value === "string" ? value.split( " " ) : [ value ];

			for ( ; i < 4; i++ ) {
				expanded[ prefix + cssExpand[ i ] + suffix ] =
					parts[ i ] || parts[ i - 2 ] || parts[ 0 ];
			}

			return expanded;
		}
	};

	if ( prefix !== "margin" ) {
		jQuery.cssHooks[ prefix + suffix ].set = setPositiveNumber;
	}
} );

jQuery.fn.extend( {
	css: function( name, value ) {
		return access( this, function( elem, name, value ) {
			var styles, len,
				map = {},
				i = 0;

			if ( Array.isArray( name ) ) {
				styles = getStyles( elem );
				len = name.length;

				for ( ; i < len; i++ ) {
					map[ name[ i ] ] = jQuery.css( elem, name[ i ], false, styles );
				}

				return map;
			}

			return value !== undefined ?
				jQuery.style( elem, name, value ) :
				jQuery.css( elem, name );
		}, name, value, arguments.length > 1 );
	}
} );


function Tween( elem, options, prop, end, easing ) {
	return new Tween.prototype.init( elem, options, prop, end, easing );
}
jQuery.Tween = Tween;

Tween.prototype = {
	constructor: Tween,
	init: function( elem, options, prop, end, easing, unit ) {
		this.elem = elem;
		this.prop = prop;
		this.easing = easing || jQuery.easing._default;
		this.options = options;
		this.start = this.now = this.cur();
		this.end = end;
		this.unit = unit || ( jQuery.cssNumber[ prop ] ? "" : "px" );
	},
	cur: function() {
		var hooks = Tween.propHooks[ this.prop ];

		return hooks && hooks.get ?
			hooks.get( this ) :
			Tween.propHooks._default.get( this );
	},
	run: function( percent ) {
		var eased,
			hooks = Tween.propHooks[ this.prop ];

		if ( this.options.duration ) {
			this.pos = eased = jQuery.easing[ this.easing ](
				percent, this.options.duration * percent, 0, 1, this.options.duration
			);
		} else {
			this.pos = eased = percent;
		}
		this.now = ( this.end - this.start ) * eased + this.start;

		if ( this.options.step ) {
			this.options.step.call( this.elem, this.now, this );
		}

		if ( hooks && hooks.set ) {
			hooks.set( this );
		} else {
			Tween.propHooks._default.set( this );
		}
		return this;
	}
};

Tween.prototype.init.prototype = Tween.prototype;

Tween.propHooks = {
	_default: {
		get: function( tween ) {
			var result;

			// Use a property on the element directly when it is not a DOM element,
			// or when there is no matching style property that exists.
			if ( tween.elem.nodeType !== 1 ||
				tween.elem[ tween.prop ] != null && tween.elem.style[ tween.prop ] == null ) {
				return tween.elem[ tween.prop ];
			}

			// Passing an empty string as a 3rd parameter to .css will automatically
			// attempt a parseFloat and fallback to a string if the parse fails.
			// Simple values such as "10px" are parsed to Float;
			// complex values such as "rotate(1rad)" are returned as-is.
			result = jQuery.css( tween.elem, tween.prop, "" );

			// Empty strings, null, undefined and "auto" are converted to 0.
			return !result || result === "auto" ? 0 : result;
		},
		set: function( tween ) {

			// Use step hook for back compat.
			// Use cssHook if its there.
			// Use .style if available and use plain properties where available.
			if ( jQuery.fx.step[ tween.prop ] ) {
				jQuery.fx.step[ tween.prop ]( tween );
			} else if ( tween.elem.nodeType === 1 && (
				jQuery.cssHooks[ tween.prop ] ||
					tween.elem.style[ finalPropName( tween.prop ) ] != null ) ) {
				jQuery.style( tween.elem, tween.prop, tween.now + tween.unit );
			} else {
				tween.elem[ tween.prop ] = tween.now;
			}
		}
	}
};

// Support: IE <=9 only
// Panic based approach to setting things on disconnected nodes
Tween.propHooks.scrollTop = Tween.propHooks.scrollLeft = {
	set: function( tween ) {
		if ( tween.elem.nodeType && tween.elem.parentNode ) {
			tween.elem[ tween.prop ] = tween.now;
		}
	}
};

jQuery.easing = {
	linear: function( p ) {
		return p;
	},
	swing: function( p ) {
		return 0.5 - Math.cos( p * Math.PI ) / 2;
	},
	_default: "swing"
};

jQuery.fx = Tween.prototype.init;

// Back compat <1.8 extension point
jQuery.fx.step = {};




var
	fxNow, inProgress,
	rfxtypes = /^(?:toggle|show|hide)$/,
	rrun = /queueHooks$/;

function schedule() {
	if ( inProgress ) {
		if ( document.hidden === false && window.requestAnimationFrame ) {
			window.requestAnimationFrame( schedule );
		} else {
			window.setTimeout( schedule, jQuery.fx.interval );
		}

		jQuery.fx.tick();
	}
}

// Animations created synchronously will run synchronously
function createFxNow() {
	window.setTimeout( function() {
		fxNow = undefined;
	} );
	return ( fxNow = Date.now() );
}

// Generate parameters to create a standard animation
function genFx( type, includeWidth ) {
	var which,
		i = 0,
		attrs = { height: type };

	// If we include width, step value is 1 to do all cssExpand values,
	// otherwise step value is 2 to skip over Left and Right
	includeWidth = includeWidth ? 1 : 0;
	for ( ; i < 4; i += 2 - includeWidth ) {
		which = cssExpand[ i ];
		attrs[ "margin" + which ] = attrs[ "padding" + which ] = type;
	}

	if ( includeWidth ) {
		attrs.opacity = attrs.width = type;
	}

	return attrs;
}

function createTween( value, prop, animation ) {
	var tween,
		collection = ( Animation.tweeners[ prop ] || [] ).concat( Animation.tweeners[ "*" ] ),
		index = 0,
		length = collection.length;
	for ( ; index < length; index++ ) {
		if ( ( tween = collection[ index ].call( animation, prop, value ) ) ) {

			// We're done with this property
			return tween;
		}
	}
}

function defaultPrefilter( elem, props, opts ) {
	var prop, value, toggle, hooks, oldfire, propTween, restoreDisplay, display,
		isBox = "width" in props || "height" in props,
		anim = this,
		orig = {},
		style = elem.style,
		hidden = elem.nodeType && isHiddenWithinTree( elem ),
		dataShow = dataPriv.get( elem, "fxshow" );

	// Queue-skipping animations hijack the fx hooks
	if ( !opts.queue ) {
		hooks = jQuery._queueHooks( elem, "fx" );
		if ( hooks.unqueued == null ) {
			hooks.unqueued = 0;
			oldfire = hooks.empty.fire;
			hooks.empty.fire = function() {
				if ( !hooks.unqueued ) {
					oldfire();
				}
			};
		}
		hooks.unqueued++;

		anim.always( function() {

			// Ensure the complete handler is called before this completes
			anim.always( function() {
				hooks.unqueued--;
				if ( !jQuery.queue( elem, "fx" ).length ) {
					hooks.empty.fire();
				}
			} );
		} );
	}

	// Detect show/hide animations
	for ( prop in props ) {
		value = props[ prop ];
		if ( rfxtypes.test( value ) ) {
			delete props[ prop ];
			toggle = toggle || value === "toggle";
			if ( value === ( hidden ? "hide" : "show" ) ) {

				// Pretend to be hidden if this is a "show" and
				// there is still data from a stopped show/hide
				if ( value === "show" && dataShow && dataShow[ prop ] !== undefined ) {
					hidden = true;

				// Ignore all other no-op show/hide data
				} else {
					continue;
				}
			}
			orig[ prop ] = dataShow && dataShow[ prop ] || jQuery.style( elem, prop );
		}
	}

	// Bail out if this is a no-op like .hide().hide()
	propTween = !jQuery.isEmptyObject( props );
	if ( !propTween && jQuery.isEmptyObject( orig ) ) {
		return;
	}

	// Restrict "overflow" and "display" styles during box animations
	if ( isBox && elem.nodeType === 1 ) {

		// Support: IE <=9 - 11, Edge 12 - 15
		// Record all 3 overflow attributes because IE does not infer the shorthand
		// from identically-valued overflowX and overflowY and Edge just mirrors
		// the overflowX value there.
		opts.overflow = [ style.overflow, style.overflowX, style.overflowY ];

		// Identify a display type, preferring old show/hide data over the CSS cascade
		restoreDisplay = dataShow && dataShow.display;
		if ( restoreDisplay == null ) {
			restoreDisplay = dataPriv.get( elem, "display" );
		}
		display = jQuery.css( elem, "display" );
		if ( display === "none" ) {
			if ( restoreDisplay ) {
				display = restoreDisplay;
			} else {

				// Get nonempty value(s) by temporarily forcing visibility
				showHide( [ elem ], true );
				restoreDisplay = elem.style.display || restoreDisplay;
				display = jQuery.css( elem, "display" );
				showHide( [ elem ] );
			}
		}

		// Animate inline elements as inline-block
		if ( display === "inline" || display === "inline-block" && restoreDisplay != null ) {
			if ( jQuery.css( elem, "float" ) === "none" ) {

				// Restore the original display value at the end of pure show/hide animations
				if ( !propTween ) {
					anim.done( function() {
						style.display = restoreDisplay;
					} );
					if ( restoreDisplay == null ) {
						display = style.display;
						restoreDisplay = display === "none" ? "" : display;
					}
				}
				style.display = "inline-block";
			}
		}
	}

	if ( opts.overflow ) {
		style.overflow = "hidden";
		anim.always( function() {
			style.overflow = opts.overflow[ 0 ];
			style.overflowX = opts.overflow[ 1 ];
			style.overflowY = opts.overflow[ 2 ];
		} );
	}

	// Implement show/hide animations
	propTween = false;
	for ( prop in orig ) {

		// General show/hide setup for this element animation
		if ( !propTween ) {
			if ( dataShow ) {
				if ( "hidden" in dataShow ) {
					hidden = dataShow.hidden;
				}
			} else {
				dataShow = dataPriv.access( elem, "fxshow", { display: restoreDisplay } );
			}

			// Store hidden/visible for toggle so `.stop().toggle()` "reverses"
			if ( toggle ) {
				dataShow.hidden = !hidden;
			}

			// Show elements before animating them
			if ( hidden ) {
				showHide( [ elem ], true );
			}

			/* eslint-disable no-loop-func */

			anim.done( function() {

				/* eslint-enable no-loop-func */

				// The final step of a "hide" animation is actually hiding the element
				if ( !hidden ) {
					showHide( [ elem ] );
				}
				dataPriv.remove( elem, "fxshow" );
				for ( prop in orig ) {
					jQuery.style( elem, prop, orig[ prop ] );
				}
			} );
		}

		// Per-property setup
		propTween = createTween( hidden ? dataShow[ prop ] : 0, prop, anim );
		if ( !( prop in dataShow ) ) {
			dataShow[ prop ] = propTween.start;
			if ( hidden ) {
				propTween.end = propTween.start;
				propTween.start = 0;
			}
		}
	}
}

function propFilter( props, specialEasing ) {
	var index, name, easing, value, hooks;

	// camelCase, specialEasing and expand cssHook pass
	for ( index in props ) {
		name = camelCase( index );
		easing = specialEasing[ name ];
		value = props[ index ];
		if ( Array.isArray( value ) ) {
			easing = value[ 1 ];
			value = props[ index ] = value[ 0 ];
		}

		if ( index !== name ) {
			props[ name ] = value;
			delete props[ index ];
		}

		hooks = jQuery.cssHooks[ name ];
		if ( hooks && "expand" in hooks ) {
			value = hooks.expand( value );
			delete props[ name ];

			// Not quite $.extend, this won't overwrite existing keys.
			// Reusing 'index' because we have the correct "name"
			for ( index in value ) {
				if ( !( index in props ) ) {
					props[ index ] = value[ index ];
					specialEasing[ index ] = easing;
				}
			}
		} else {
			specialEasing[ name ] = easing;
		}
	}
}

function Animation( elem, properties, options ) {
	var result,
		stopped,
		index = 0,
		length = Animation.prefilters.length,
		deferred = jQuery.Deferred().always( function() {

			// Don't match elem in the :animated selector
			delete tick.elem;
		} ),
		tick = function() {
			if ( stopped ) {
				return false;
			}
			var currentTime = fxNow || createFxNow(),
				remaining = Math.max( 0, animation.startTime + animation.duration - currentTime ),

				// Support: Android 2.3 only
				// Archaic crash bug won't allow us to use `1 - ( 0.5 || 0 )` (trac-12497)
				temp = remaining / animation.duration || 0,
				percent = 1 - temp,
				index = 0,
				length = animation.tweens.length;

			for ( ; index < length; index++ ) {
				animation.tweens[ index ].run( percent );
			}

			deferred.notifyWith( elem, [ animation, percent, remaining ] );

			// If there's more to do, yield
			if ( percent < 1 && length ) {
				return remaining;
			}

			// If this was an empty animation, synthesize a final progress notification
			if ( !length ) {
				deferred.notifyWith( elem, [ animation, 1, 0 ] );
			}

			// Resolve the animation and report its conclusion
			deferred.resolveWith( elem, [ animation ] );
			return false;
		},
		animation = deferred.promise( {
			elem: elem,
			props: jQuery.extend( {}, properties ),
			opts: jQuery.extend( true, {
				specialEasing: {},
				easing: jQuery.easing._default
			}, options ),
			originalProperties: properties,
			originalOptions: options,
			startTime: fxNow || createFxNow(),
			duration: options.duration,
			tweens: [],
			createTween: function( prop, end ) {
				var tween = jQuery.Tween( elem, animation.opts, prop, end,
					animation.opts.specialEasing[ prop ] || animation.opts.easing );
				animation.tweens.push( tween );
				return tween;
			},
			stop: function( gotoEnd ) {
				var index = 0,

					// If we are going to the end, we want to run all the tweens
					// otherwise we skip this part
					length = gotoEnd ? animation.tweens.length : 0;
				if ( stopped ) {
					return this;
				}
				stopped = true;
				for ( ; index < length; index++ ) {
					animation.tweens[ index ].run( 1 );
				}

				// Resolve when we played the last frame; otherwise, reject
				if ( gotoEnd ) {
					deferred.notifyWith( elem, [ animation, 1, 0 ] );
					deferred.resolveWith( elem, [ animation, gotoEnd ] );
				} else {
					deferred.rejectWith( elem, [ animation, gotoEnd ] );
				}
				return this;
			}
		} ),
		props = animation.props;

	propFilter( props, animation.opts.specialEasing );

	for ( ; index < length; index++ ) {
		result = Animation.prefilters[ index ].call( animation, elem, props, animation.opts );
		if ( result ) {
			if ( isFunction( result.stop ) ) {
				jQuery._queueHooks( animation.elem, animation.opts.queue ).stop =
					result.stop.bind( result );
			}
			return result;
		}
	}

	jQuery.map( props, createTween, animation );

	if ( isFunction( animation.opts.start ) ) {
		animation.opts.start.call( elem, animation );
	}

	// Attach callbacks from options
	animation
		.progress( animation.opts.progress )
		.done( animation.opts.done, animation.opts.complete )
		.fail( animation.opts.fail )
		.always( animation.opts.always );

	jQuery.fx.timer(
		jQuery.extend( tick, {
			elem: elem,
			anim: animation,
			queue: animation.opts.queue
		} )
	);

	return animation;
}

jQuery.Animation = jQuery.extend( Animation, {

	tweeners: {
		"*": [ function( prop, value ) {
			var tween = this.createTween( prop, value );
			adjustCSS( tween.elem, prop, rcssNum.exec( value ), tween );
			return tween;
		} ]
	},

	tweener: function( props, callback ) {
		if ( isFunction( props ) ) {
			callback = props;
			props = [ "*" ];
		} else {
			props = props.match( rnothtmlwhite );
		}

		var prop,
			index = 0,
			length = props.length;

		for ( ; index < length; index++ ) {
			prop = props[ index ];
			Animation.tweeners[ prop ] = Animation.tweeners[ prop ] || [];
			Animation.tweeners[ prop ].unshift( callback );
		}
	},

	prefilters: [ defaultPrefilter ],

	prefilter: function( callback, prepend ) {
		if ( prepend ) {
			Animation.prefilters.unshift( callback );
		} else {
			Animation.prefilters.push( callback );
		}
	}
} );

jQuery.speed = function( speed, easing, fn ) {
	var opt = speed && typeof speed === "object" ? jQuery.extend( {}, speed ) : {
		complete: fn || !fn && easing ||
			isFunction( speed ) && speed,
		duration: speed,
		easing: fn && easing || easing && !isFunction( easing ) && easing
	};

	// Go to the end state if fx are off
	if ( jQuery.fx.off ) {
		opt.duration = 0;

	} else {
		if ( typeof opt.duration !== "number" ) {
			if ( opt.duration in jQuery.fx.speeds ) {
				opt.duration = jQuery.fx.speeds[ opt.duration ];

			} else {
				opt.duration = jQuery.fx.speeds._default;
			}
		}
	}

	// Normalize opt.queue - true/undefined/null -> "fx"
	if ( opt.queue == null || opt.queue === true ) {
		opt.queue = "fx";
	}

	// Queueing
	opt.old = opt.complete;

	opt.complete = function() {
		if ( isFunction( opt.old ) ) {
			opt.old.call( this );
		}

		if ( opt.queue ) {
			jQuery.dequeue( this, opt.queue );
		}
	};

	return opt;
};

jQuery.fn.extend( {
	fadeTo: function( speed, to, easing, callback ) {

		// Show any hidden elements after setting opacity to 0
		return this.filter( isHiddenWithinTree ).css( "opacity", 0 ).show()

			// Animate to the value specified
			.end().animate( { opacity: to }, speed, easing, callback );
	},
	animate: function( prop, speed, easing, callback ) {
		var empty = jQuery.isEmptyObject( prop ),
			optall = jQuery.speed( speed, easing, callback ),
			doAnimation = function() {

				// Operate on a copy of prop so per-property easing won't be lost
				var anim = Animation( this, jQuery.extend( {}, prop ), optall );

				// Empty animations, or finishing resolves immediately
				if ( empty || dataPriv.get( this, "finish" ) ) {
					anim.stop( true );
				}
			};

		doAnimation.finish = doAnimation;

		return empty || optall.queue === false ?
			this.each( doAnimation ) :
			this.queue( optall.queue, doAnimation );
	},
	stop: function( type, clearQueue, gotoEnd ) {
		var stopQueue = function( hooks ) {
			var stop = hooks.stop;
			delete hooks.stop;
			stop( gotoEnd );
		};

		if ( typeof type !== "string" ) {
			gotoEnd = clearQueue;
			clearQueue = type;
			type = undefined;
		}
		if ( clearQueue ) {
			this.queue( type || "fx", [] );
		}

		return this.each( function() {
			var dequeue = true,
				index = type != null && type + "queueHooks",
				timers = jQuery.timers,
				data = dataPriv.get( this );

			if ( index ) {
				if ( data[ index ] && data[ index ].stop ) {
					stopQueue( data[ index ] );
				}
			} else {
				for ( index in data ) {
					if ( data[ index ] && data[ index ].stop && rrun.test( index ) ) {
						stopQueue( data[ index ] );
					}
				}
			}

			for ( index = timers.length; index--; ) {
				if ( timers[ index ].elem === this &&
					( type == null || timers[ index ].queue === type ) ) {

					timers[ index ].anim.stop( gotoEnd );
					dequeue = false;
					timers.splice( index, 1 );
				}
			}

			// Start the next in the queue if the last step wasn't forced.
			// Timers currently will call their complete callbacks, which
			// will dequeue but only if they were gotoEnd.
			if ( dequeue || !gotoEnd ) {
				jQuery.dequeue( this, type );
			}
		} );
	},
	finish: function( type ) {
		if ( type !== false ) {
			type = type || "fx";
		}
		return this.each( function() {
			var index,
				data = dataPriv.get( this ),
				queue = data[ type + "queue" ],
				hooks = data[ type + "queueHooks" ],
				timers = jQuery.timers,
				length = queue ? queue.length : 0;

			// Enable finishing flag on private data
			data.finish = true;

			// Empty the queue first
			jQuery.queue( this, type, [] );

			if ( hooks && hooks.stop ) {
				hooks.stop.call( this, true );
			}

			// Look for any active animations, and finish them
			for ( index = timers.length; index--; ) {
				if ( timers[ index ].elem === this && timers[ index ].queue === type ) {
					timers[ index ].anim.stop( true );
					timers.splice( index, 1 );
				}
			}

			// Look for any animations in the old queue and finish them
			for ( index = 0; index < length; index++ ) {
				if ( queue[ index ] && queue[ index ].finish ) {
					queue[ index ].finish.call( this );
				}
			}

			// Turn off finishing flag
			delete data.finish;
		} );
	}
} );

jQuery.each( [ "toggle", "show", "hide" ], function( _i, name ) {
	var cssFn = jQuery.fn[ name ];
	jQuery.fn[ name ] = function( speed, easing, callback ) {
		return speed == null || typeof speed === "boolean" ?
			cssFn.apply( this, arguments ) :
			this.animate( genFx( name, true ), speed, easing, callback );
	};
} );

// Generate shortcuts for custom animations
jQuery.each( {
	slideDown: genFx( "show" ),
	slideUp: genFx( "hide" ),
	slideToggle: genFx( "toggle" ),
	fadeIn: { opacity: "show" },
	fadeOut: { opacity: "hide" },
	fadeToggle: { opacity: "toggle" }
}, function( name, props ) {
	jQuery.fn[ name ] = function( speed, easing, callback ) {
		return this.animate( props, speed, easing, callback );
	};
} );

jQuery.timers = [];
jQuery.fx.tick = function() {
	var timer,
		i = 0,
		timers = jQuery.timers;

	fxNow = Date.now();

	for ( ; i < timers.length; i++ ) {
		timer = timers[ i ];

		// Run the timer and safely remove it when done (allowing for external removal)
		if ( !timer() && timers[ i ] === timer ) {
			timers.splice( i--, 1 );
		}
	}

	if ( !timers.length ) {
		jQuery.fx.stop();
	}
	fxNow = undefined;
};

jQuery.fx.timer = function( timer ) {
	jQuery.timers.push( timer );
	jQuery.fx.start();
};

jQuery.fx.interval = 13;
jQuery.fx.start = function() {
	if ( inProgress ) {
		return;
	}

	inProgress = true;
	schedule();
};

jQuery.fx.stop = function() {
	inProgress = null;
};

jQuery.fx.speeds = {
	slow: 600,
	fast: 200,

	// Default speed
	_default: 400
};


// Based off of the plugin by Clint Helfers, with permission.
jQuery.fn.delay = function( time, type ) {
	time = jQuery.fx ? jQuery.fx.speeds[ time ] || time : time;
	type = type || "fx";

	return this.queue( type, function( next, hooks ) {
		var timeout = window.setTimeout( next, time );
		hooks.stop = function() {
			window.clearTimeout( timeout );
		};
	} );
};


( function() {
	var input = document.createElement( "input" ),
		select = document.createElement( "select" ),
		opt = select.appendChild( document.createElement( "option" ) );

	input.type = "checkbox";

	// Support: Android <=4.3 only
	// Default value for a checkbox should be "on"
	support.checkOn = input.value !== "";

	// Support: IE <=11 only
	// Must access selectedIndex to make default options select
	support.optSelected = opt.selected;

	// Support: IE <=11 only
	// An input loses its value after becoming a radio
	input = document.createElement( "input" );
	input.value = "t";
	input.type = "radio";
	support.radioValue = input.value === "t";
} )();


var boolHook,
	attrHandle = jQuery.expr.attrHandle;

jQuery.fn.extend( {
	attr: function( name, value ) {
		return access( this, jQuery.attr, name, value, arguments.length > 1 );
	},

	removeAttr: function( name ) {
		return this.each( function() {
			jQuery.removeAttr( this, name );
		} );
	}
} );

jQuery.extend( {
	attr: function( elem, name, value ) {
		var ret, hooks,
			nType = elem.nodeType;

		// Don't get/set attributes on text, comment and attribute nodes
		if ( nType === 3 || nType === 8 || nType === 2 ) {
			return;
		}

		// Fallback to prop when attributes are not supported
		if ( typeof elem.getAttribute === "undefined" ) {
			return jQuery.prop( elem, name, value );
		}

		// Attribute hooks are determined by the lowercase version
		// Grab necessary hook if one is defined
		if ( nType !== 1 || !jQuery.isXMLDoc( elem ) ) {
			hooks = jQuery.attrHooks[ name.toLowerCase() ] ||
				( jQuery.expr.match.bool.test( name ) ? boolHook : undefined );
		}

		if ( value !== undefined ) {
			if ( value === null ) {
				jQuery.removeAttr( elem, name );
				return;
			}

			if ( hooks && "set" in hooks &&
				( ret = hooks.set( elem, value, name ) ) !== undefined ) {
				return ret;
			}

			elem.setAttribute( name, value + "" );
			return value;
		}

		if ( hooks && "get" in hooks && ( ret = hooks.get( elem, name ) ) !== null ) {
			return ret;
		}

		ret = jQuery.find.attr( elem, name );

		// Non-existent attributes return null, we normalize to undefined
		return ret == null ? undefined : ret;
	},

	attrHooks: {
		type: {
			set: function( elem, value ) {
				if ( !support.radioValue && value === "radio" &&
					nodeName( elem, "input" ) ) {
					var val = elem.value;
					elem.setAttribute( "type", value );
					if ( val ) {
						elem.value = val;
					}
					return value;
				}
			}
		}
	},

	removeAttr: function( elem, value ) {
		var name,
			i = 0,

			// Attribute names can contain non-HTML whitespace characters
			// https://html.spec.whatwg.org/multipage/syntax.html#attributes-2
			attrNames = value && value.match( rnothtmlwhite );

		if ( attrNames && elem.nodeType === 1 ) {
			while ( ( name = attrNames[ i++ ] ) ) {
				elem.removeAttribute( name );
			}
		}
	}
} );

// Hooks for boolean attributes
boolHook = {
	set: function( elem, value, name ) {
		if ( value === false ) {

			// Remove boolean attributes when set to false
			jQuery.removeAttr( elem, name );
		} else {
			elem.setAttribute( name, name );
		}
		return name;
	}
};

jQuery.each( jQuery.expr.match.bool.source.match( /\w+/g ), function( _i, name ) {
	var getter = attrHandle[ name ] || jQuery.find.attr;

	attrHandle[ name ] = function( elem, name, isXML ) {
		var ret, handle,
			lowercaseName = name.toLowerCase();

		if ( !isXML ) {

			// Avoid an infinite loop by temporarily removing this function from the getter
			handle = attrHandle[ lowercaseName ];
			attrHandle[ lowercaseName ] = ret;
			ret = getter( elem, name, isXML ) != null ?
				lowercaseName :
				null;
			attrHandle[ lowercaseName ] = handle;
		}
		return ret;
	};
} );




var rfocusable = /^(?:input|select|textarea|button)$/i,
	rclickable = /^(?:a|area)$/i;

jQuery.fn.extend( {
	prop: function( name, value ) {
		return access( this, jQuery.prop, name, value, arguments.length > 1 );
	},

	removeProp: function( name ) {
		return this.each( function() {
			delete this[ jQuery.propFix[ name ] || name ];
		} );
	}
} );

jQuery.extend( {
	prop: function( elem, name, value ) {
		var ret, hooks,
			nType = elem.nodeType;

		// Don't get/set properties on text, comment and attribute nodes
		if ( nType === 3 || nType === 8 || nType === 2 ) {
			return;
		}

		if ( nType !== 1 || !jQuery.isXMLDoc( elem ) ) {

			// Fix name and attach hooks
			name = jQuery.propFix[ name ] || name;
			hooks = jQuery.propHooks[ name ];
		}

		if ( value !== undefined ) {
			if ( hooks && "set" in hooks &&
				( ret = hooks.set( elem, value, name ) ) !== undefined ) {
				return ret;
			}

			return ( elem[ name ] = value );
		}

		if ( hooks && "get" in hooks && ( ret = hooks.get( elem, name ) ) !== null ) {
			return ret;
		}

		return elem[ name ];
	},

	propHooks: {
		tabIndex: {
			get: function( elem ) {

				// Support: IE <=9 - 11 only
				// elem.tabIndex doesn't always return the
				// correct value when it hasn't been explicitly set
				// Use proper attribute retrieval (trac-12072)
				var tabindex = jQuery.find.attr( elem, "tabindex" );

				if ( tabindex ) {
					return parseInt( tabindex, 10 );
				}

				if (
					rfocusable.test( elem.nodeName ) ||
					rclickable.test( elem.nodeName ) &&
					elem.href
				) {
					return 0;
				}

				return -1;
			}
		}
	},

	propFix: {
		"for": "htmlFor",
		"class": "className"
	}
} );

// Support: IE <=11 only
// Accessing the selectedIndex property
// forces the browser to respect setting selected
// on the option
// The getter ensures a default option is selected
// when in an optgroup
// eslint rule "no-unused-expressions" is disabled for this code
// since it considers such accessions noop
if ( !support.optSelected ) {
	jQuery.propHooks.selected = {
		get: function( elem ) {

			/* eslint no-unused-expressions: "off" */

			var parent = elem.parentNode;
			if ( parent && parent.parentNode ) {
				parent.parentNode.selectedIndex;
			}
			return null;
		},
		set: function( elem ) {

			/* eslint no-unused-expressions: "off" */

			var parent = elem.parentNode;
			if ( parent ) {
				parent.selectedIndex;

				if ( parent.parentNode ) {
					parent.parentNode.selectedIndex;
				}
			}
		}
	};
}

jQuery.each( [
	"tabIndex",
	"readOnly",
	"maxLength",
	"cellSpacing",
	"cellPadding",
	"rowSpan",
	"colSpan",
	"useMap",
	"frameBorder",
	"contentEditable"
], function() {
	jQuery.propFix[ this.toLowerCase() ] = this;
} );




	// Strip and collapse whitespace according to HTML spec
	// https://infra.spec.whatwg.org/#strip-and-collapse-ascii-whitespace
	function stripAndCollapse( value ) {
		var tokens = value.match( rnothtmlwhite ) || [];
		return tokens.join( " " );
	}


function getClass( elem ) {
	return elem.getAttribute && elem.getAttribute( "class" ) || "";
}

function classesToArray( value ) {
	if ( Array.isArray( value ) ) {
		return value;
	}
	if ( typeof value === "string" ) {
		return value.match( rnothtmlwhite ) || [];
	}
	return [];
}

jQuery.fn.extend( {
	addClass: function( value ) {
		var classNames, cur, curValue, className, i, finalValue;

		if ( isFunction( value ) ) {
			return this.each( function( j ) {
				jQuery( this ).addClass( value.call( this, j, getClass( this ) ) );
			} );
		}

		classNames = classesToArray( value );

		if ( classNames.length ) {
			return this.each( function() {
				curValue = getClass( this );
				cur = this.nodeType === 1 && ( " " + stripAndCollapse( curValue ) + " " );

				if ( cur ) {
					for ( i = 0; i < classNames.length; i++ ) {
						className = classNames[ i ];
						if ( cur.indexOf( " " + className + " " ) < 0 ) {
							cur += className + " ";
						}
					}

					// Only assign if different to avoid unneeded rendering.
					finalValue = stripAndCollapse( cur );
					if ( curValue !== finalValue ) {
						this.setAttribute( "class", finalValue );
					}
				}
			} );
		}

		return this;
	},

	removeClass: function( value ) {
		var classNames, cur, curValue, className, i, finalValue;

		if ( isFunction( value ) ) {
			return this.each( function( j ) {
				jQuery( this ).removeClass( value.call( this, j, getClass( this ) ) );
			} );
		}

		if ( !arguments.length ) {
			return this.attr( "class", "" );
		}

		classNames = classesToArray( value );

		if ( classNames.length ) {
			return this.each( function() {
				curValue = getClass( this );

				// This expression is here for better compressibility (see addClass)
				cur = this.nodeType === 1 && ( " " + stripAndCollapse( curValue ) + " " );

				if ( cur ) {
					for ( i = 0; i < classNames.length; i++ ) {
						className = classNames[ i ];

						// Remove *all* instances
						while ( cur.indexOf( " " + className + " " ) > -1 ) {
							cur = cur.replace( " " + className + " ", " " );
						}
					}

					// Only assign if different to avoid unneeded rendering.
					finalValue = stripAndCollapse( cur );
					if ( curValue !== finalValue ) {
						this.setAttribute( "class", finalValue );
					}
				}
			} );
		}

		return this;
	},

	toggleClass: function( value, stateVal ) {
		var classNames, className, i, self,
			type = typeof value,
			isValidValue = type === "string" || Array.isArray( value );

		if ( isFunction( value ) ) {
			return this.each( function( i ) {
				jQuery( this ).toggleClass(
					value.call( this, i, getClass( this ), stateVal ),
					stateVal
				);
			} );
		}

		if ( typeof stateVal === "boolean" && isValidValue ) {
			return stateVal ? this.addClass( value ) : this.removeClass( value );
		}

		classNames = classesToArray( value );

		return this.each( function() {
			if ( isValidValue ) {

				// Toggle individual class names
				self = jQuery( this );

				for ( i = 0; i < classNames.length; i++ ) {
					className = classNames[ i ];

					// Check each className given, space separated list
					if ( self.hasClass( className ) ) {
						self.removeClass( className );
					} else {
						self.addClass( className );
					}
				}

			// Toggle whole class name
			} else if ( value === undefined || type === "boolean" ) {
				className = getClass( this );
				if ( className ) {

					// Store className if set
					dataPriv.set( this, "__className__", className );
				}

				// If the element has a class name or if we're passed `false`,
				// then remove the whole classname (if there was one, the above saved it).
				// Otherwise bring back whatever was previously saved (if anything),
				// falling back to the empty string if nothing was stored.
				if ( this.setAttribute ) {
					this.setAttribute( "class",
						className || value === false ?
							"" :
							dataPriv.get( this, "__className__" ) || ""
					);
				}
			}
		} );
	},

	hasClass: function( selector ) {
		var className, elem,
			i = 0;

		className = " " + selector + " ";
		while ( ( elem = this[ i++ ] ) ) {
			if ( elem.nodeType === 1 &&
				( " " + stripAndCollapse( getClass( elem ) ) + " " ).indexOf( className ) > -1 ) {
				return true;
			}
		}

		return false;
	}
} );




var rreturn = /\r/g;

jQuery.fn.extend( {
	val: function( value ) {
		var hooks, ret, valueIsFunction,
			elem = this[ 0 ];

		if ( !arguments.length ) {
			if ( elem ) {
				hooks = jQuery.valHooks[ elem.type ] ||
					jQuery.valHooks[ elem.nodeName.toLowerCase() ];

				if ( hooks &&
					"get" in hooks &&
					( ret = hooks.get( elem, "value" ) ) !== undefined
				) {
					return ret;
				}

				ret = elem.value;

				// Handle most common string cases
				if ( typeof ret === "string" ) {
					return ret.replace( rreturn, "" );
				}

				// Handle cases where value is null/undef or number
				return ret == null ? "" : ret;
			}

			return;
		}

		valueIsFunction = isFunction( value );

		return this.each( function( i ) {
			var val;

			if ( this.nodeType !== 1 ) {
				return;
			}

			if ( valueIsFunction ) {
				val = value.call( this, i, jQuery( this ).val() );
			} else {
				val = value;
			}

			// Treat null/undefined as ""; convert numbers to string
			if ( val == null ) {
				val = "";

			} else if ( typeof val === "number" ) {
				val += "";

			} else if ( Array.isArray( val ) ) {
				val = jQuery.map( val, function( value ) {
					return value == null ? "" : value + "";
				} );
			}

			hooks = jQuery.valHooks[ this.type ] || jQuery.valHooks[ this.nodeName.toLowerCase() ];

			// If set returns undefined, fall back to normal setting
			if ( !hooks || !( "set" in hooks ) || hooks.set( this, val, "value" ) === undefined ) {
				this.value = val;
			}
		} );
	}
} );

jQuery.extend( {
	valHooks: {
		option: {
			get: function( elem ) {

				var val = jQuery.find.attr( elem, "value" );
				return val != null ?
					val :

					// Support: IE <=10 - 11 only
					// option.text throws exceptions (trac-14686, trac-14858)
					// Strip and collapse whitespace
					// https://html.spec.whatwg.org/#strip-and-collapse-whitespace
					stripAndCollapse( jQuery.text( elem ) );
			}
		},
		select: {
			get: function( elem ) {
				var value, option, i,
					options = elem.options,
					index = elem.selectedIndex,
					one = elem.type === "select-one",
					values = one ? null : [],
					max = one ? index + 1 : options.length;

				if ( index < 0 ) {
					i = max;

				} else {
					i = one ? index : 0;
				}

				// Loop through all the selected options
				for ( ; i < max; i++ ) {
					option = options[ i ];

					// Support: IE <=9 only
					// IE8-9 doesn't update selected after form reset (trac-2551)
					if ( ( option.selected || i === index ) &&

							// Don't return options that are disabled or in a disabled optgroup
							!option.disabled &&
							( !option.parentNode.disabled ||
								!nodeName( option.parentNode, "optgroup" ) ) ) {

						// Get the specific value for the option
						value = jQuery( option ).val();

						// We don't need an array for one selects
						if ( one ) {
							return value;
						}

						// Multi-Selects return an array
						values.push( value );
					}
				}

				return values;
			},

			set: function( elem, value ) {
				var optionSet, option,
					options = elem.options,
					values = jQuery.makeArray( value ),
					i = options.length;

				while ( i-- ) {
					option = options[ i ];

					/* eslint-disable no-cond-assign */

					if ( option.selected =
						jQuery.inArray( jQuery.valHooks.option.get( option ), values ) > -1
					) {
						optionSet = true;
					}

					/* eslint-enable no-cond-assign */
				}

				// Force browsers to behave consistently when non-matching value is set
				if ( !optionSet ) {
					elem.selectedIndex = -1;
				}
				return values;
			}
		}
	}
} );

// Radios and checkboxes getter/setter
jQuery.each( [ "radio", "checkbox" ], function() {
	jQuery.valHooks[ this ] = {
		set: function( elem, value ) {
			if ( Array.isArray( value ) ) {
				return ( elem.checked = jQuery.inArray( jQuery( elem ).val(), value ) > -1 );
			}
		}
	};
	if ( !support.checkOn ) {
		jQuery.valHooks[ this ].get = function( elem ) {
			return elem.getAttribute( "value" ) === null ? "on" : elem.value;
		};
	}
} );




// Return jQuery for attributes-only inclusion
var location = window.location;

var nonce = { guid: Date.now() };

var rquery = ( /\?/ );



// Cross-browser xml parsing
jQuery.parseXML = function( data ) {
	var xml, parserErrorElem;
	if ( !data || typeof data !== "string" ) {
		return null;
	}

	// Support: IE 9 - 11 only
	// IE throws on parseFromString with invalid input.
	try {
		xml = ( new window.DOMParser() ).parseFromString( data, "text/xml" );
	} catch ( e ) {}

	parserErrorElem = xml && xml.getElementsByTagName( "parsererror" )[ 0 ];
	if ( !xml || parserErrorElem ) {
		jQuery.error( "Invalid XML: " + (
			parserErrorElem ?
				jQuery.map( parserErrorElem.childNodes, function( el ) {
					return el.textContent;
				} ).join( "\n" ) :
				data
		) );
	}
	return xml;
};


var rfocusMorph = /^(?:focusinfocus|focusoutblur)$/,
	stopPropagationCallback = function( e ) {
		e.stopPropagation();
	};

jQuery.extend( jQuery.event, {

	trigger: function( event, data, elem, onlyHandlers ) {

		var i, cur, tmp, bubbleType, ontype, handle, special, lastElement,
			eventPath = [ elem || document ],
			type = hasOwn.call( event, "type" ) ? event.type : event,
			namespaces = hasOwn.call( event, "namespace" ) ? event.namespace.split( "." ) : [];

		cur = lastElement = tmp = elem = elem || document;

		// Don't do events on text and comment nodes
		if ( elem.nodeType === 3 || elem.nodeType === 8 ) {
			return;
		}

		// focus/blur morphs to focusin/out; ensure we're not firing them right now
		if ( rfocusMorph.test( type + jQuery.event.triggered ) ) {
			return;
		}

		if ( type.indexOf( "." ) > -1 ) {

			// Namespaced trigger; create a regexp to match event type in handle()
			namespaces = type.split( "." );
			type = namespaces.shift();
			namespaces.sort();
		}
		ontype = type.indexOf( ":" ) < 0 && "on" + type;

		// Caller can pass in a jQuery.Event object, Object, or just an event type string
		event = event[ jQuery.expando ] ?
			event :
			new jQuery.Event( type, typeof event === "object" && event );

		// Trigger bitmask: & 1 for native handlers; & 2 for jQuery (always true)
		event.isTrigger = onlyHandlers ? 2 : 3;
		event.namespace = namespaces.join( "." );
		event.rnamespace = event.namespace ?
			new RegExp( "(^|\\.)" + namespaces.join( "\\.(?:.*\\.|)" ) + "(\\.|$)" ) :
			null;

		// Clean up the event in case it is being reused
		event.result = undefined;
		if ( !event.target ) {
			event.target = elem;
		}

		// Clone any incoming data and prepend the event, creating the handler arg list
		data = data == null ?
			[ event ] :
			jQuery.makeArray( data, [ event ] );

		// Allow special events to draw outside the lines
		special = jQuery.event.special[ type ] || {};
		if ( !onlyHandlers && special.trigger && special.trigger.apply( elem, data ) === false ) {
			return;
		}

		// Determine event propagation path in advance, per W3C events spec (trac-9951)
		// Bubble up to document, then to window; watch for a global ownerDocument var (trac-9724)
		if ( !onlyHandlers && !special.noBubble && !isWindow( elem ) ) {

			bubbleType = special.delegateType || type;
			if ( !rfocusMorph.test( bubbleType + type ) ) {
				cur = cur.parentNode;
			}
			for ( ; cur; cur = cur.parentNode ) {
				eventPath.push( cur );
				tmp = cur;
			}

			// Only add window if we got to document (e.g., not plain obj or detached DOM)
			if ( tmp === ( elem.ownerDocument || document ) ) {
				eventPath.push( tmp.defaultView || tmp.parentWindow || window );
			}
		}

		// Fire handlers on the event path
		i = 0;
		while ( ( cur = eventPath[ i++ ] ) && !event.isPropagationStopped() ) {
			lastElement = cur;
			event.type = i > 1 ?
				bubbleType :
				special.bindType || type;

			// jQuery handler
			handle = ( dataPriv.get( cur, "events" ) || Object.create( null ) )[ event.type ] &&
				dataPriv.get( cur, "handle" );
			if ( handle ) {
				handle.apply( cur, data );
			}

			// Native handler
			handle = ontype && cur[ ontype ];
			if ( handle && handle.apply && acceptData( cur ) ) {
				event.result = handle.apply( cur, data );
				if ( event.result === false ) {
					event.preventDefault();
				}
			}
		}
		event.type = type;

		// If nobody prevented the default action, do it now
		if ( !onlyHandlers && !event.isDefaultPrevented() ) {

			if ( ( !special._default ||
				special._default.apply( eventPath.pop(), data ) === false ) &&
				acceptData( elem ) ) {

				// Call a native DOM method on the target with the same name as the event.
				// Don't do default actions on window, that's where global variables be (trac-6170)
				if ( ontype && isFunction( elem[ type ] ) && !isWindow( elem ) ) {

					// Don't re-trigger an onFOO event when we call its FOO() method
					tmp = elem[ ontype ];

					if ( tmp ) {
						elem[ ontype ] = null;
					}

					// Prevent re-triggering of the same event, since we already bubbled it above
					jQuery.event.triggered = type;

					if ( event.isPropagationStopped() ) {
						lastElement.addEventListener( type, stopPropagationCallback );
					}

					elem[ type ]();

					if ( event.isPropagationStopped() ) {
						lastElement.removeEventListener( type, stopPropagationCallback );
					}

					jQuery.event.triggered = undefined;

					if ( tmp ) {
						elem[ ontype ] = tmp;
					}
				}
			}
		}

		return event.result;
	},

	// Piggyback on a donor event to simulate a different one
	// Used only for `focus(in | out)` events
	simulate: function( type, elem, event ) {
		var e = jQuery.extend(
			new jQuery.Event(),
			event,
			{
				type: type,
				isSimulated: true
			}
		);

		jQuery.event.trigger( e, null, elem );
	}

} );

jQuery.fn.extend( {

	trigger: function( type, data ) {
		return this.each( function() {
			jQuery.event.trigger( type, data, this );
		} );
	},
	triggerHandler: function( type, data ) {
		var elem = this[ 0 ];
		if ( elem ) {
			return jQuery.event.trigger( type, data, elem, true );
		}
	}
} );


var
	rbracket = /\[\]$/,
	rCRLF = /\r?\n/g,
	rsubmitterTypes = /^(?:submit|button|image|reset|file)$/i,
	rsubmittable = /^(?:input|select|textarea|keygen)/i;

function buildParams( prefix, obj, traditional, add ) {
	var name;

	if ( Array.isArray( obj ) ) {

		// Serialize array item.
		jQuery.each( obj, function( i, v ) {
			if ( traditional || rbracket.test( prefix ) ) {

				// Treat each array item as a scalar.
				add( prefix, v );

			} else {

				// Item is non-scalar (array or object), encode its numeric index.
				buildParams(
					prefix + "[" + ( typeof v === "object" && v != null ? i : "" ) + "]",
					v,
					traditional,
					add
				);
			}
		} );

	} else if ( !traditional && toType( obj ) === "object" ) {

		// Serialize object item.
		for ( name in obj ) {
			buildParams( prefix + "[" + name + "]", obj[ name ], traditional, add );
		}

	} else {

		// Serialize scalar item.
		add( prefix, obj );
	}
}

// Serialize an array of form elements or a set of
// key/values into a query string
jQuery.param = function( a, traditional ) {
	var prefix,
		s = [],
		add = function( key, valueOrFunction ) {

			// If value is a function, invoke it and use its return value
			var value = isFunction( valueOrFunction ) ?
				valueOrFunction() :
				valueOrFunction;

			s[ s.length ] = encodeURIComponent( key ) + "=" +
				encodeURIComponent( value == null ? "" : value );
		};

	if ( a == null ) {
		return "";
	}

	// If an array was passed in, assume that it is an array of form elements.
	if ( Array.isArray( a ) || ( a.jquery && !jQuery.isPlainObject( a ) ) ) {

		// Serialize the form elements
		jQuery.each( a, function() {
			add( this.name, this.value );
		} );

	} else {

		// If traditional, encode the "old" way (the way 1.3.2 or older
		// did it), otherwise encode params recursively.
		for ( prefix in a ) {
			buildParams( prefix, a[ prefix ], traditional, add );
		}
	}

	// Return the resulting serialization
	return s.join( "&" );
};

jQuery.fn.extend( {
	serialize: function() {
		return jQuery.param( this.serializeArray() );
	},
	serializeArray: function() {
		return this.map( function() {

			// Can add propHook for "elements" to filter or add form elements
			var elements = jQuery.prop( this, "elements" );
			return elements ? jQuery.makeArray( elements ) : this;
		} ).filter( function() {
			var type = this.type;

			// Use .is( ":disabled" ) so that fieldset[disabled] works
			return this.name && !jQuery( this ).is( ":disabled" ) &&
				rsubmittable.test( this.nodeName ) && !rsubmitterTypes.test( type ) &&
				( this.checked || !rcheckableType.test( type ) );
		} ).map( function( _i, elem ) {
			var val = jQuery( this ).val();

			if ( val == null ) {
				return null;
			}

			if ( Array.isArray( val ) ) {
				return jQuery.map( val, function( val ) {
					return { name: elem.name, value: val.replace( rCRLF, "\r\n" ) };
				} );
			}

			return { name: elem.name, value: val.replace( rCRLF, "\r\n" ) };
		} ).get();
	}
} );


var
	r20 = /%20/g,
	rhash = /#.*$/,
	rantiCache = /([?&])_=[^&]*/,
	rheaders = /^(.*?):[ \t]*([^\r\n]*)$/mg,

	// trac-7653, trac-8125, trac-8152: local protocol detection
	rlocalProtocol = /^(?:about|app|app-storage|.+-extension|file|res|widget):$/,
	rnoContent = /^(?:GET|HEAD)$/,
	rprotocol = /^\/\//,

	/* Prefilters
	 * 1) They are useful to introduce custom dataTypes (see ajax/jsonp.js for an example)
	 * 2) These are called:
	 *    - BEFORE asking for a transport
	 *    - AFTER param serialization (s.data is a string if s.processData is true)
	 * 3) key is the dataType
	 * 4) the catchall symbol "*" can be used
	 * 5) execution will start with transport dataType and THEN continue down to "*" if needed
	 */
	prefilters = {},

	/* Transports bindings
	 * 1) key is the dataType
	 * 2) the catchall symbol "*" can be used
	 * 3) selection will start with transport dataType and THEN go to "*" if needed
	 */
	transports = {},

	// Avoid comment-prolog char sequence (trac-10098); must appease lint and evade compression
	allTypes = "*/".concat( "*" ),

	// Anchor tag for parsing the document origin
	originAnchor = document.createElement( "a" );

originAnchor.href = location.href;

// Base "constructor" for jQuery.ajaxPrefilter and jQuery.ajaxTransport
function addToPrefiltersOrTransports( structure ) {

	// dataTypeExpression is optional and defaults to "*"
	return function( dataTypeExpression, func ) {

		if ( typeof dataTypeExpression !== "string" ) {
			func = dataTypeExpression;
			dataTypeExpression = "*";
		}

		var dataType,
			i = 0,
			dataTypes = dataTypeExpression.toLowerCase().match( rnothtmlwhite ) || [];

		if ( isFunction( func ) ) {

			// For each dataType in the dataTypeExpression
			while ( ( dataType = dataTypes[ i++ ] ) ) {

				// Prepend if requested
				if ( dataType[ 0 ] === "+" ) {
					dataType = dataType.slice( 1 ) || "*";
					( structure[ dataType ] = structure[ dataType ] || [] ).unshift( func );

				// Otherwise append
				} else {
					( structure[ dataType ] = structure[ dataType ] || [] ).push( func );
				}
			}
		}
	};
}

// Base inspection function for prefilters and transports
function inspectPrefiltersOrTransports( structure, options, originalOptions, jqXHR ) {

	var inspected = {},
		seekingTransport = ( structure === transports );

	function inspect( dataType ) {
		var selected;
		inspected[ dataType ] = true;
		jQuery.each( structure[ dataType ] || [], function( _, prefilterOrFactory ) {
			var dataTypeOrTransport = prefilterOrFactory( options, originalOptions, jqXHR );
			if ( typeof dataTypeOrTransport === "string" &&
				!seekingTransport && !inspected[ dataTypeOrTransport ] ) {

				options.dataTypes.unshift( dataTypeOrTransport );
				inspect( dataTypeOrTransport );
				return false;
			} else if ( seekingTransport ) {
				return !( selected = dataTypeOrTransport );
			}
		} );
		return selected;
	}

	return inspect( options.dataTypes[ 0 ] ) || !inspected[ "*" ] && inspect( "*" );
}

// A special extend for ajax options
// that takes "flat" options (not to be deep extended)
// Fixes trac-9887
function ajaxExtend( target, src ) {
	var key, deep,
		flatOptions = jQuery.ajaxSettings.flatOptions || {};

	for ( key in src ) {
		if ( src[ key ] !== undefined ) {
			( flatOptions[ key ] ? target : ( deep || ( deep = {} ) ) )[ key ] = src[ key ];
		}
	}
	if ( deep ) {
		jQuery.extend( true, target, deep );
	}

	return target;
}

/* Handles responses to an ajax request:
 * - finds the right dataType (mediates between content-type and expected dataType)
 * - returns the corresponding response
 */
function ajaxHandleResponses( s, jqXHR, responses ) {

	var ct, type, finalDataType, firstDataType,
		contents = s.contents,
		dataTypes = s.dataTypes;

	// Remove auto dataType and get content-type in the process
	while ( dataTypes[ 0 ] === "*" ) {
		dataTypes.shift();
		if ( ct === undefined ) {
			ct = s.mimeType || jqXHR.getResponseHeader( "Content-Type" );
		}
	}

	// Check if we're dealing with a known content-type
	if ( ct ) {
		for ( type in contents ) {
			if ( contents[ type ] && contents[ type ].test( ct ) ) {
				dataTypes.unshift( type );
				break;
			}
		}
	}

	// Check to see if we have a response for the expected dataType
	if ( dataTypes[ 0 ] in responses ) {
		finalDataType = dataTypes[ 0 ];
	} else {

		// Try convertible dataTypes
		for ( type in responses ) {
			if ( !dataTypes[ 0 ] || s.converters[ type + " " + dataTypes[ 0 ] ] ) {
				finalDataType = type;
				break;
			}
			if ( !firstDataType ) {
				firstDataType = type;
			}
		}

		// Or just use first one
		finalDataType = finalDataType || firstDataType;
	}

	// If we found a dataType
	// We add the dataType to the list if needed
	// and return the corresponding response
	if ( finalDataType ) {
		if ( finalDataType !== dataTypes[ 0 ] ) {
			dataTypes.unshift( finalDataType );
		}
		return responses[ finalDataType ];
	}
}

/* Chain conversions given the request and the original response
 * Also sets the responseXXX fields on the jqXHR instance
 */
function ajaxConvert( s, response, jqXHR, isSuccess ) {
	var conv2, current, conv, tmp, prev,
		converters = {},

		// Work with a copy of dataTypes in case we need to modify it for conversion
		dataTypes = s.dataTypes.slice();

	// Create converters map with lowercased keys
	if ( dataTypes[ 1 ] ) {
		for ( conv in s.converters ) {
			converters[ conv.toLowerCase() ] = s.converters[ conv ];
		}
	}

	current = dataTypes.shift();

	// Convert to each sequential dataType
	while ( current ) {

		if ( s.responseFields[ current ] ) {
			jqXHR[ s.responseFields[ current ] ] = response;
		}

		// Apply the dataFilter if provided
		if ( !prev && isSuccess && s.dataFilter ) {
			response = s.dataFilter( response, s.dataType );
		}

		prev = current;
		current = dataTypes.shift();

		if ( current ) {

			// There's only work to do if current dataType is non-auto
			if ( current === "*" ) {

				current = prev;

			// Convert response if prev dataType is non-auto and differs from current
			} else if ( prev !== "*" && prev !== current ) {

				// Seek a direct converter
				conv = converters[ prev + " " + current ] || converters[ "* " + current ];

				// If none found, seek a pair
				if ( !conv ) {
					for ( conv2 in converters ) {

						// If conv2 outputs current
						tmp = conv2.split( " " );
						if ( tmp[ 1 ] === current ) {

							// If prev can be converted to accepted input
							conv = converters[ prev + " " + tmp[ 0 ] ] ||
								converters[ "* " + tmp[ 0 ] ];
							if ( conv ) {

								// Condense equivalence converters
								if ( conv === true ) {
									conv = converters[ conv2 ];

								// Otherwise, insert the intermediate dataType
								} else if ( converters[ conv2 ] !== true ) {
									current = tmp[ 0 ];
									dataTypes.unshift( tmp[ 1 ] );
								}
								break;
							}
						}
					}
				}

				// Apply converter (if not an equivalence)
				if ( conv !== true ) {

					// Unless errors are allowed to bubble, catch and return them
					if ( conv && s.throws ) {
						response = conv( response );
					} else {
						try {
							response = conv( response );
						} catch ( e ) {
							return {
								state: "parsererror",
								error: conv ? e : "No conversion from " + prev + " to " + current
							};
						}
					}
				}
			}
		}
	}

	return { state: "success", data: response };
}

jQuery.extend( {

	// Counter for holding the number of active queries
	active: 0,

	// Last-Modified header cache for next request
	lastModified: {},
	etag: {},

	ajaxSettings: {
		url: location.href,
		type: "GET",
		isLocal: rlocalProtocol.test( location.protocol ),
		global: true,
		processData: true,
		async: true,
		contentType: "application/x-www-form-urlencoded; charset=UTF-8",

		/*
		timeout: 0,
		data: null,
		dataType: null,
		username: null,
		password: null,
		cache: null,
		throws: false,
		traditional: false,
		headers: {},
		*/

		accepts: {
			"*": allTypes,
			text: "text/plain",
			html: "text/html",
			xml: "application/xml, text/xml",
			json: "application/json, text/javascript"
		},

		contents: {
			xml: /\bxml\b/,
			html: /\bhtml/,
			json: /\bjson\b/
		},

		responseFields: {
			xml: "responseXML",
			text: "responseText",
			json: "responseJSON"
		},

		// Data converters
		// Keys separate source (or catchall "*") and destination types with a single space
		converters: {

			// Convert anything to text
			"* text": String,

			// Text to html (true = no transformation)
			"text html": true,

			// Evaluate text as a json expression
			"text json": JSON.parse,

			// Parse text as xml
			"text xml": jQuery.parseXML
		},

		// For options that shouldn't be deep extended:
		// you can add your own custom options here if
		// and when you create one that shouldn't be
		// deep extended (see ajaxExtend)
		flatOptions: {
			url: true,
			context: true
		}
	},

	// Creates a full fledged settings object into target
	// with both ajaxSettings and settings fields.
	// If target is omitted, writes into ajaxSettings.
	ajaxSetup: function( target, settings ) {
		return settings ?

			// Building a settings object
			ajaxExtend( ajaxExtend( target, jQuery.ajaxSettings ), settings ) :

			// Extending ajaxSettings
			ajaxExtend( jQuery.ajaxSettings, target );
	},

	ajaxPrefilter: addToPrefiltersOrTransports( prefilters ),
	ajaxTransport: addToPrefiltersOrTransports( transports ),

	// Main method
	ajax: function( url, options ) {

		// If url is an object, simulate pre-1.5 signature
		if ( typeof url === "object" ) {
			options = url;
			url = undefined;
		}

		// Force options to be an object
		options = options || {};

		var transport,

			// URL without anti-cache param
			cacheURL,

			// Response headers
			responseHeadersString,
			responseHeaders,

			// timeout handle
			timeoutTimer,

			// Url cleanup var
			urlAnchor,

			// Request state (becomes false upon send and true upon completion)
			completed,

			// To know if global events are to be dispatched
			fireGlobals,

			// Loop variable
			i,

			// uncached part of the url
			uncached,

			// Create the final options object
			s = jQuery.ajaxSetup( {}, options ),

			// Callbacks context
			callbackContext = s.context || s,

			// Context for global events is callbackContext if it is a DOM node or jQuery collection
			globalEventContext = s.context &&
				( callbackContext.nodeType || callbackContext.jquery ) ?
				jQuery( callbackContext ) :
				jQuery.event,

			// Deferreds
			deferred = jQuery.Deferred(),
			completeDeferred = jQuery.Callbacks( "once memory" ),

			// Status-dependent callbacks
			statusCode = s.statusCode || {},

			// Headers (they are sent all at once)
			requestHeaders = {},
			requestHeadersNames = {},

			// Default abort message
			strAbort = "canceled",

			// Fake xhr
			jqXHR = {
				readyState: 0,

				// Builds headers hashtable if needed
				getResponseHeader: function( key ) {
					var match;
					if ( completed ) {
						if ( !responseHeaders ) {
							responseHeaders = {};
							while ( ( match = rheaders.exec( responseHeadersString ) ) ) {
								responseHeaders[ match[ 1 ].toLowerCase() + " " ] =
									( responseHeaders[ match[ 1 ].toLowerCase() + " " ] || [] )
										.concat( match[ 2 ] );
							}
						}
						match = responseHeaders[ key.toLowerCase() + " " ];
					}
					return match == null ? null : match.join( ", " );
				},

				// Raw string
				getAllResponseHeaders: function() {
					return completed ? responseHeadersString : null;
				},

				// Caches the header
				setRequestHeader: function( name, value ) {
					if ( completed == null ) {
						name = requestHeadersNames[ name.toLowerCase() ] =
							requestHeadersNames[ name.toLowerCase() ] || name;
						requestHeaders[ name ] = value;
					}
					return this;
				},

				// Overrides response content-type header
				overrideMimeType: function( type ) {
					if ( completed == null ) {
						s.mimeType = type;
					}
					return this;
				},

				// Status-dependent callbacks
				statusCode: function( map ) {
					var code;
					if ( map ) {
						if ( completed ) {

							// Execute the appropriate callbacks
							jqXHR.always( map[ jqXHR.status ] );
						} else {

							// Lazy-add the new callbacks in a way that preserves old ones
							for ( code in map ) {
								statusCode[ code ] = [ statusCode[ code ], map[ code ] ];
							}
						}
					}
					return this;
				},

				// Cancel the request
				abort: function( statusText ) {
					var finalText = statusText || strAbort;
					if ( transport ) {
						transport.abort( finalText );
					}
					done( 0, finalText );
					return this;
				}
			};

		// Attach deferreds
		deferred.promise( jqXHR );

		// Add protocol if not provided (prefilters might expect it)
		// Handle falsy url in the settings object (trac-10093: consistency with old signature)
		// We also use the url parameter if available
		s.url = ( ( url || s.url || location.href ) + "" )
			.replace( rprotocol, location.protocol + "//" );

		// Alias method option to type as per ticket trac-12004
		s.type = options.method || options.type || s.method || s.type;

		// Extract dataTypes list
		s.dataTypes = ( s.dataType || "*" ).toLowerCase().match( rnothtmlwhite ) || [ "" ];

		// A cross-domain request is in order when the origin doesn't match the current origin.
		if ( s.crossDomain == null ) {
			urlAnchor = document.createElement( "a" );

			// Support: IE <=8 - 11, Edge 12 - 15
			// IE throws exception on accessing the href property if url is malformed,
			// e.g. http://example.com:80x/
			try {
				urlAnchor.href = s.url;

				// Support: IE <=8 - 11 only
				// Anchor's host property isn't correctly set when s.url is relative
				urlAnchor.href = urlAnchor.href;
				s.crossDomain = originAnchor.protocol + "//" + originAnchor.host !==
					urlAnchor.protocol + "//" + urlAnchor.host;
			} catch ( e ) {

				// If there is an error parsing the URL, assume it is crossDomain,
				// it can be rejected by the transport if it is invalid
				s.crossDomain = true;
			}
		}

		// Convert data if not already a string
		if ( s.data && s.processData && typeof s.data !== "string" ) {
			s.data = jQuery.param( s.data, s.traditional );
		}

		// Apply prefilters
		inspectPrefiltersOrTransports( prefilters, s, options, jqXHR );

		// If request was aborted inside a prefilter, stop there
		if ( completed ) {
			return jqXHR;
		}

		// We can fire global events as of now if asked to
		// Don't fire events if jQuery.event is undefined in an AMD-usage scenario (trac-15118)
		fireGlobals = jQuery.event && s.global;

		// Watch for a new set of requests
		if ( fireGlobals && jQuery.active++ === 0 ) {
			jQuery.event.trigger( "ajaxStart" );
		}

		// Uppercase the type
		s.type = s.type.toUpperCase();

		// Determine if request has content
		s.hasContent = !rnoContent.test( s.type );

		// Save the URL in case we're toying with the If-Modified-Since
		// and/or If-None-Match header later on
		// Remove hash to simplify url manipulation
		cacheURL = s.url.replace( rhash, "" );

		// More options handling for requests with no content
		if ( !s.hasContent ) {

			// Remember the hash so we can put it back
			uncached = s.url.slice( cacheURL.length );

			// If data is available and should be processed, append data to url
			if ( s.data && ( s.processData || typeof s.data === "string" ) ) {
				cacheURL += ( rquery.test( cacheURL ) ? "&" : "?" ) + s.data;

				// trac-9682: remove data so that it's not used in an eventual retry
				delete s.data;
			}

			// Add or update anti-cache param if needed
			if ( s.cache === false ) {
				cacheURL = cacheURL.replace( rantiCache, "$1" );
				uncached = ( rquery.test( cacheURL ) ? "&" : "?" ) + "_=" + ( nonce.guid++ ) +
					uncached;
			}

			// Put hash and anti-cache on the URL that will be requested (gh-1732)
			s.url = cacheURL + uncached;

		// Change '%20' to '+' if this is encoded form body content (gh-2658)
		} else if ( s.data && s.processData &&
			( s.contentType || "" ).indexOf( "application/x-www-form-urlencoded" ) === 0 ) {
			s.data = s.data.replace( r20, "+" );
		}

		// Set the If-Modified-Since and/or If-None-Match header, if in ifModified mode.
		if ( s.ifModified ) {
			if ( jQuery.lastModified[ cacheURL ] ) {
				jqXHR.setRequestHeader( "If-Modified-Since", jQuery.lastModified[ cacheURL ] );
			}
			if ( jQuery.etag[ cacheURL ] ) {
				jqXHR.setRequestHeader( "If-None-Match", jQuery.etag[ cacheURL ] );
			}
		}

		// Set the correct header, if data is being sent
		if ( s.data && s.hasContent && s.contentType !== false || options.contentType ) {
			jqXHR.setRequestHeader( "Content-Type", s.contentType );
		}

		// Set the Accepts header for the server, depending on the dataType
		jqXHR.setRequestHeader(
			"Accept",
			s.dataTypes[ 0 ] && s.accepts[ s.dataTypes[ 0 ] ] ?
				s.accepts[ s.dataTypes[ 0 ] ] +
					( s.dataTypes[ 0 ] !== "*" ? ", " + allTypes + "; q=0.01" : "" ) :
				s.accepts[ "*" ]
		);

		// Check for headers option
		for ( i in s.headers ) {
			jqXHR.setRequestHeader( i, s.headers[ i ] );
		}

		// Allow custom headers/mimetypes and early abort
		if ( s.beforeSend &&
			( s.beforeSend.call( callbackContext, jqXHR, s ) === false || completed ) ) {

			// Abort if not done already and return
			return jqXHR.abort();
		}

		// Aborting is no longer a cancellation
		strAbort = "abort";

		// Install callbacks on deferreds
		completeDeferred.add( s.complete );
		jqXHR.done( s.success );
		jqXHR.fail( s.error );

		// Get transport
		transport = inspectPrefiltersOrTransports( transports, s, options, jqXHR );

		// If no transport, we auto-abort
		if ( !transport ) {
			done( -1, "No Transport" );
		} else {
			jqXHR.readyState = 1;

			// Send global event
			if ( fireGlobals ) {
				globalEventContext.trigger( "ajaxSend", [ jqXHR, s ] );
			}

			// If request was aborted inside ajaxSend, stop there
			if ( completed ) {
				return jqXHR;
			}

			// Timeout
			if ( s.async && s.timeout > 0 ) {
				timeoutTimer = window.setTimeout( function() {
					jqXHR.abort( "timeout" );
				}, s.timeout );
			}

			try {
				completed = false;
				transport.send( requestHeaders, done );
			} catch ( e ) {

				// Rethrow post-completion exceptions
				if ( completed ) {
					throw e;
				}

				// Propagate others as results
				done( -1, e );
			}
		}

		// Callback for when everything is done
		function done( status, nativeStatusText, responses, headers ) {
			var isSuccess, success, error, response, modified,
				statusText = nativeStatusText;

			// Ignore repeat invocations
			if ( completed ) {
				return;
			}

			completed = true;

			// Clear timeout if it exists
			if ( timeoutTimer ) {
				window.clearTimeout( timeoutTimer );
			}

			// Dereference transport for early garbage collection
			// (no matter how long the jqXHR object will be used)
			transport = undefined;

			// Cache response headers
			responseHeadersString = headers || "";

			// Set readyState
			jqXHR.readyState = status > 0 ? 4 : 0;

			// Determine if successful
			isSuccess = status >= 200 && status < 300 || status === 304;

			// Get response data
			if ( responses ) {
				response = ajaxHandleResponses( s, jqXHR, responses );
			}

			// Use a noop converter for missing script but not if jsonp
			if ( !isSuccess &&
				jQuery.inArray( "script", s.dataTypes ) > -1 &&
				jQuery.inArray( "json", s.dataTypes ) < 0 ) {
				s.converters[ "text script" ] = function() {};
			}

			// Convert no matter what (that way responseXXX fields are always set)
			response = ajaxConvert( s, response, jqXHR, isSuccess );

			// If successful, handle type chaining
			if ( isSuccess ) {

				// Set the If-Modified-Since and/or If-None-Match header, if in ifModified mode.
				if ( s.ifModified ) {
					modified = jqXHR.getResponseHeader( "Last-Modified" );
					if ( modified ) {
						jQuery.lastModified[ cacheURL ] = modified;
					}
					modified = jqXHR.getResponseHeader( "etag" );
					if ( modified ) {
						jQuery.etag[ cacheURL ] = modified;
					}
				}

				// if no content
				if ( status === 204 || s.type === "HEAD" ) {
					statusText = "nocontent";

				// if not modified
				} else if ( status === 304 ) {
					statusText = "notmodified";

				// If we have data, let's convert it
				} else {
					statusText = response.state;
					success = response.data;
					error = response.error;
					isSuccess = !error;
				}
			} else {

				// Extract error from statusText and normalize for non-aborts
				error = statusText;
				if ( status || !statusText ) {
					statusText = "error";
					if ( status < 0 ) {
						status = 0;
					}
				}
			}

			// Set data for the fake xhr object
			jqXHR.status = status;
			jqXHR.statusText = ( nativeStatusText || statusText ) + "";

			// Success/Error
			if ( isSuccess ) {
				deferred.resolveWith( callbackContext, [ success, statusText, jqXHR ] );
			} else {
				deferred.rejectWith( callbackContext, [ jqXHR, statusText, error ] );
			}

			// Status-dependent callbacks
			jqXHR.statusCode( statusCode );
			statusCode = undefined;

			if ( fireGlobals ) {
				globalEventContext.trigger( isSuccess ? "ajaxSuccess" : "ajaxError",
					[ jqXHR, s, isSuccess ? success : error ] );
			}

			// Complete
			completeDeferred.fireWith( callbackContext, [ jqXHR, statusText ] );

			if ( fireGlobals ) {
				globalEventContext.trigger( "ajaxComplete", [ jqXHR, s ] );

				// Handle the global AJAX counter
				if ( !( --jQuery.active ) ) {
					jQuery.event.trigger( "ajaxStop" );
				}
			}
		}

		return jqXHR;
	},

	getJSON: function( url, data, callback ) {
		return jQuery.get( url, data, callback, "json" );
	},

	getScript: function( url, callback ) {
		return jQuery.get( url, undefined, callback, "script" );
	}
} );

jQuery.each( [ "get", "post" ], function( _i, method ) {
	jQuery[ method ] = function( url, data, callback, type ) {

		// Shift arguments if data argument was omitted
		if ( isFunction( data ) ) {
			type = type || callback;
			callback = data;
			data = undefined;
		}

		// The url can be an options object (which then must have .url)
		return jQuery.ajax( jQuery.extend( {
			url: url,
			type: method,
			dataType: type,
			data: data,
			success: callback
		}, jQuery.isPlainObject( url ) && url ) );
	};
} );

jQuery.ajaxPrefilter( function( s ) {
	var i;
	for ( i in s.headers ) {
		if ( i.toLowerCase() === "content-type" ) {
			s.contentType = s.headers[ i ] || "";
		}
	}
} );


jQuery._evalUrl = function( url, options, doc ) {
	return jQuery.ajax( {
		url: url,

		// Make this explicit, since user can override this through ajaxSetup (trac-11264)
		type: "GET",
		dataType: "script",
		cache: true,
		async: false,
		global: false,

		// Only evaluate the response if it is successful (gh-4126)
		// dataFilter is not invoked for failure responses, so using it instead
		// of the default converter is kludgy but it works.
		converters: {
			"text script": function() {}
		},
		dataFilter: function( response ) {
			jQuery.globalEval( response, options, doc );
		}
	} );
};


jQuery.fn.extend( {
	wrapAll: function( html ) {
		var wrap;

		if ( this[ 0 ] ) {
			if ( isFunction( html ) ) {
				html = html.call( this[ 0 ] );
			}

			// The elements to wrap the target around
			wrap = jQuery( html, this[ 0 ].ownerDocument ).eq( 0 ).clone( true );

			if ( this[ 0 ].parentNode ) {
				wrap.insertBefore( this[ 0 ] );
			}

			wrap.map( function() {
				var elem = this;

				while ( elem.firstElementChild ) {
					elem = elem.firstElementChild;
				}

				return elem;
			} ).append( this );
		}

		return this;
	},

	wrapInner: function( html ) {
		if ( isFunction( html ) ) {
			return this.each( function( i ) {
				jQuery( this ).wrapInner( html.call( this, i ) );
			} );
		}

		return this.each( function() {
			var self = jQuery( this ),
				contents = self.contents();

			if ( contents.length ) {
				contents.wrapAll( html );

			} else {
				self.append( html );
			}
		} );
	},

	wrap: function( html ) {
		var htmlIsFunction = isFunction( html );

		return this.each( function( i ) {
			jQuery( this ).wrapAll( htmlIsFunction ? html.call( this, i ) : html );
		} );
	},

	unwrap: function( selector ) {
		this.parent( selector ).not( "body" ).each( function() {
			jQuery( this ).replaceWith( this.childNodes );
		} );
		return this;
	}
} );


jQuery.expr.pseudos.hidden = function( elem ) {
	return !jQuery.expr.pseudos.visible( elem );
};
jQuery.expr.pseudos.visible = function( elem ) {
	return !!( elem.offsetWidth || elem.offsetHeight || elem.getClientRects().length );
};




jQuery.ajaxSettings.xhr = function() {
	try {
		return new window.XMLHttpRequest();
	} catch ( e ) {}
};

var xhrSuccessStatus = {

		// File protocol always yields status code 0, assume 200
		0: 200,

		// Support: IE <=9 only
		// trac-1450: sometimes IE returns 1223 when it should be 204
		1223: 204
	},
	xhrSupported = jQuery.ajaxSettings.xhr();

support.cors = !!xhrSupported && ( "withCredentials" in xhrSupported );
support.ajax = xhrSupported = !!xhrSupported;

jQuery.ajaxTransport( function( options ) {
	var callback, errorCallback;

	// Cross domain only allowed if supported through XMLHttpRequest
	if ( support.cors || xhrSupported && !options.crossDomain ) {
		return {
			send: function( headers, complete ) {
				var i,
					xhr = options.xhr();

				xhr.open(
					options.type,
					options.url,
					options.async,
					options.username,
					options.password
				);

				// Apply custom fields if provided
				if ( options.xhrFields ) {
					for ( i in options.xhrFields ) {
						xhr[ i ] = options.xhrFields[ i ];
					}
				}

				// Override mime type if needed
				if ( options.mimeType && xhr.overrideMimeType ) {
					xhr.overrideMimeType( options.mimeType );
				}

				// X-Requested-With header
				// For cross-domain requests, seeing as conditions for a preflight are
				// akin to a jigsaw puzzle, we simply never set it to be sure.
				// (it can always be set on a per-request basis or even using ajaxSetup)
				// For same-domain requests, won't change header if already provided.
				if ( !options.crossDomain && !headers[ "X-Requested-With" ] ) {
					headers[ "X-Requested-With" ] = "XMLHttpRequest";
				}

				// Set headers
				for ( i in headers ) {
					xhr.setRequestHeader( i, headers[ i ] );
				}

				// Callback
				callback = function( type ) {
					return function() {
						if ( callback ) {
							callback = errorCallback = xhr.onload =
								xhr.onerror = xhr.onabort = xhr.ontimeout =
									xhr.onreadystatechange = null;

							if ( type === "abort" ) {
								xhr.abort();
							} else if ( type === "error" ) {

								// Support: IE <=9 only
								// On a manual native abort, IE9 throws
								// errors on any property access that is not readyState
								if ( typeof xhr.status !== "number" ) {
									complete( 0, "error" );
								} else {
									complete(

										// File: protocol always yields status 0; see trac-8605, trac-14207
										xhr.status,
										xhr.statusText
									);
								}
							} else {
								complete(
									xhrSuccessStatus[ xhr.status ] || xhr.status,
									xhr.statusText,

									// Support: IE <=9 only
									// IE9 has no XHR2 but throws on binary (trac-11426)
									// For XHR2 non-text, let the caller handle it (gh-2498)
									( xhr.responseType || "text" ) !== "text"  ||
									typeof xhr.responseText !== "string" ?
										{ binary: xhr.response } :
										{ text: xhr.responseText },
									xhr.getAllResponseHeaders()
								);
							}
						}
					};
				};

				// Listen to events
				xhr.onload = callback();
				errorCallback = xhr.onerror = xhr.ontimeout = callback( "error" );

				// Support: IE 9 only
				// Use onreadystatechange to replace onabort
				// to handle uncaught aborts
				if ( xhr.onabort !== undefined ) {
					xhr.onabort = errorCallback;
				} else {
					xhr.onreadystatechange = function() {

						// Check readyState before timeout as it changes
						if ( xhr.readyState === 4 ) {

							// Allow onerror to be called first,
							// but that will not handle a native abort
							// Also, save errorCallback to a variable
							// as xhr.onerror cannot be accessed
							window.setTimeout( function() {
								if ( callback ) {
									errorCallback();
								}
							} );
						}
					};
				}

				// Create the abort callback
				callback = callback( "abort" );

				try {

					// Do send the request (this may raise an exception)
					xhr.send( options.hasContent && options.data || null );
				} catch ( e ) {

					// trac-14683: Only rethrow if this hasn't been notified as an error yet
					if ( callback ) {
						throw e;
					}
				}
			},

			abort: function() {
				if ( callback ) {
					callback();
				}
			}
		};
	}
} );




// Prevent auto-execution of scripts when no explicit dataType was provided (See gh-2432)
jQuery.ajaxPrefilter( function( s ) {
	if ( s.crossDomain ) {
		s.contents.script = false;
	}
} );

// Install script dataType
jQuery.ajaxSetup( {
	accepts: {
		script: "text/javascript, application/javascript, " +
			"application/ecmascript, application/x-ecmascript"
	},
	contents: {
		script: /\b(?:java|ecma)script\b/
	},
	converters: {
		"text script": function( text ) {
			jQuery.globalEval( text );
			return text;
		}
	}
} );

// Handle cache's special case and crossDomain
jQuery.ajaxPrefilter( "script", function( s ) {
	if ( s.cache === undefined ) {
		s.cache = false;
	}
	if ( s.crossDomain ) {
		s.type = "GET";
	}
} );

// Bind script tag hack transport
jQuery.ajaxTransport( "script", function( s ) {

	// This transport only deals with cross domain or forced-by-attrs requests
	if ( s.crossDomain || s.scriptAttrs ) {
		var script, callback;
		return {
			send: function( _, complete ) {
				script = jQuery( "<script>" )
					.attr( s.scriptAttrs || {} )
					.prop( { charset: s.scriptCharset, src: s.url } )
					.on( "load error", callback = function( evt ) {
						script.remove();
						callback = null;
						if ( evt ) {
							complete( evt.type === "error" ? 404 : 200, evt.type );
						}
					} );

				// Use native DOM manipulation to avoid our domManip AJAX trickery
				document.head.appendChild( script[ 0 ] );
			},
			abort: function() {
				if ( callback ) {
					callback();
				}
			}
		};
	}
} );




var oldCallbacks = [],
	rjsonp = /(=)\?(?=&|$)|\?\?/;

// Default jsonp settings
jQuery.ajaxSetup( {
	jsonp: "callback",
	jsonpCallback: function() {
		var callback = oldCallbacks.pop() || ( jQuery.expando + "_" + ( nonce.guid++ ) );
		this[ callback ] = true;
		return callback;
	}
} );

// Detect, normalize options and install callbacks for jsonp requests
jQuery.ajaxPrefilter( "json jsonp", function( s, originalSettings, jqXHR ) {

	var callbackName, overwritten, responseContainer,
		jsonProp = s.jsonp !== false && ( rjsonp.test( s.url ) ?
			"url" :
			typeof s.data === "string" &&
				( s.contentType || "" )
					.indexOf( "application/x-www-form-urlencoded" ) === 0 &&
				rjsonp.test( s.data ) && "data"
		);

	// Handle iff the expected data type is "jsonp" or we have a parameter to set
	if ( jsonProp || s.dataTypes[ 0 ] === "jsonp" ) {

		// Get callback name, remembering preexisting value associated with it
		callbackName = s.jsonpCallback = isFunction( s.jsonpCallback ) ?
			s.jsonpCallback() :
			s.jsonpCallback;

		// Insert callback into url or form data
		if ( jsonProp ) {
			s[ jsonProp ] = s[ jsonProp ].replace( rjsonp, "$1" + callbackName );
		} else if ( s.jsonp !== false ) {
			s.url += ( rquery.test( s.url ) ? "&" : "?" ) + s.jsonp + "=" + callbackName;
		}

		// Use data converter to retrieve json after script execution
		s.converters[ "script json" ] = function() {
			if ( !responseContainer ) {
				jQuery.error( callbackName + " was not called" );
			}
			return responseContainer[ 0 ];
		};

		// Force json dataType
		s.dataTypes[ 0 ] = "json";

		// Install callback
		overwritten = window[ callbackName ];
		window[ callbackName ] = function() {
			responseContainer = arguments;
		};

		// Clean-up function (fires after converters)
		jqXHR.always( function() {

			// If previous value didn't exist - remove it
			if ( overwritten === undefined ) {
				jQuery( window ).removeProp( callbackName );

			// Otherwise restore preexisting value
			} else {
				window[ callbackName ] = overwritten;
			}

			// Save back as free
			if ( s[ callbackName ] ) {

				// Make sure that re-using the options doesn't screw things around
				s.jsonpCallback = originalSettings.jsonpCallback;

				// Save the callback name for future use
				oldCallbacks.push( callbackName );
			}

			// Call if it was a function and we have a response
			if ( responseContainer && isFunction( overwritten ) ) {
				overwritten( responseContainer[ 0 ] );
			}

			responseContainer = overwritten = undefined;
		} );

		// Delegate to script
		return "script";
	}
} );




// Support: Safari 8 only
// In Safari 8 documents created via document.implementation.createHTMLDocument
// collapse sibling forms: the second one becomes a child of the first one.
// Because of that, this security measure has to be disabled in Safari 8.
// https://bugs.webkit.org/show_bug.cgi?id=137337
support.createHTMLDocument = ( function() {
	var body = document.implementation.createHTMLDocument( "" ).body;
	body.innerHTML = "<form></form><form></form>";
	return body.childNodes.length === 2;
} )();


// Argument "data" should be string of html
// context (optional): If specified, the fragment will be created in this context,
// defaults to document
// keepScripts (optional): If true, will include scripts passed in the html string
jQuery.parseHTML = function( data, context, keepScripts ) {
	if ( typeof data !== "string" ) {
		return [];
	}
	if ( typeof context === "boolean" ) {
		keepScripts = context;
		context = false;
	}

	var base, parsed, scripts;

	if ( !context ) {

		// Stop scripts or inline event handlers from being executed immediately
		// by using document.implementation
		if ( support.createHTMLDocument ) {
			context = document.implementation.createHTMLDocument( "" );

			// Set the base href for the created document
			// so any parsed elements with URLs
			// are based on the document's URL (gh-2965)
			base = context.createElement( "base" );
			base.href = document.location.href;
			context.head.appendChild( base );
		} else {
			context = document;
		}
	}

	parsed = rsingleTag.exec( data );
	scripts = !keepScripts && [];

	// Single tag
	if ( parsed ) {
		return [ context.createElement( parsed[ 1 ] ) ];
	}

	parsed = buildFragment( [ data ], context, scripts );

	if ( scripts && scripts.length ) {
		jQuery( scripts ).remove();
	}

	return jQuery.merge( [], parsed.childNodes );
};


/**
 * Load a url into a page
 */
jQuery.fn.load = function( url, params, callback ) {
	var selector, type, response,
		self = this,
		off = url.indexOf( " " );

	if ( off > -1 ) {
		selector = stripAndCollapse( url.slice( off ) );
		url = url.slice( 0, off );
	}

	// If it's a function
	if ( isFunction( params ) ) {

		// We assume that it's the callback
		callback = params;
		params = undefined;

	// Otherwise, build a param string
	} else if ( params && typeof params === "object" ) {
		type = "POST";
	}

	// If we have elements to modify, make the request
	if ( self.length > 0 ) {
		jQuery.ajax( {
			url: url,

			// If "type" variable is undefined, then "GET" method will be used.
			// Make value of this field explicit since
			// user can override it through ajaxSetup method
			type: type || "GET",
			dataType: "html",
			data: params
		} ).done( function( responseText ) {

			// Save response for use in complete callback
			response = arguments;

			self.html( selector ?

				// If a selector was specified, locate the right elements in a dummy div
				// Exclude scripts to avoid IE 'Permission Denied' errors
				jQuery( "<div>" ).append( jQuery.parseHTML( responseText ) ).find( selector ) :

				// Otherwise use the full result
				responseText );

		// If the request succeeds, this function gets "data", "status", "jqXHR"
		// but they are ignored because response was set above.
		// If it fails, this function gets "jqXHR", "status", "error"
		} ).always( callback && function( jqXHR, status ) {
			self.each( function() {
				callback.apply( this, response || [ jqXHR.responseText, status, jqXHR ] );
			} );
		} );
	}

	return this;
};




jQuery.expr.pseudos.animated = function( elem ) {
	return jQuery.grep( jQuery.timers, function( fn ) {
		return elem === fn.elem;
	} ).length;
};




jQuery.offset = {
	setOffset: function( elem, options, i ) {
		var curPosition, curLeft, curCSSTop, curTop, curOffset, curCSSLeft, calculatePosition,
			position = jQuery.css( elem, "position" ),
			curElem = jQuery( elem ),
			props = {};

		// Set position first, in-case top/left are set even on static elem
		if ( position === "static" ) {
			elem.style.position = "relative";
		}

		curOffset = curElem.offset();
		curCSSTop = jQuery.css( elem, "top" );
		curCSSLeft = jQuery.css( elem, "left" );
		calculatePosition = ( position === "absolute" || position === "fixed" ) &&
			( curCSSTop + curCSSLeft ).indexOf( "auto" ) > -1;

		// Need to be able to calculate position if either
		// top or left is auto and position is either absolute or fixed
		if ( calculatePosition ) {
			curPosition = curElem.position();
			curTop = curPosition.top;
			curLeft = curPosition.left;

		} else {
			curTop = parseFloat( curCSSTop ) || 0;
			curLeft = parseFloat( curCSSLeft ) || 0;
		}

		if ( isFunction( options ) ) {

			// Use jQuery.extend here to allow modification of coordinates argument (gh-1848)
			options = options.call( elem, i, jQuery.extend( {}, curOffset ) );
		}

		if ( options.top != null ) {
			props.top = ( options.top - curOffset.top ) + curTop;
		}
		if ( options.left != null ) {
			props.left = ( options.left - curOffset.left ) + curLeft;
		}

		if ( "using" in options ) {
			options.using.call( elem, props );

		} else {
			curElem.css( props );
		}
	}
};

jQuery.fn.extend( {

	// offset() relates an element's border box to the document origin
	offset: function( options ) {

		// Preserve chaining for setter
		if ( arguments.length ) {
			return options === undefined ?
				this :
				this.each( function( i ) {
					jQuery.offset.setOffset( this, options, i );
				} );
		}

		var rect, win,
			elem = this[ 0 ];

		if ( !elem ) {
			return;
		}

		// Return zeros for disconnected and hidden (display: none) elements (gh-2310)
		// Support: IE <=11 only
		// Running getBoundingClientRect on a
		// disconnected node in IE throws an error
		if ( !elem.getClientRects().length ) {
			return { top: 0, left: 0 };
		}

		// Get document-relative position by adding viewport scroll to viewport-relative gBCR
		rect = elem.getBoundingClientRect();
		win = elem.ownerDocument.defaultView;
		return {
			top: rect.top + win.pageYOffset,
			left: rect.left + win.pageXOffset
		};
	},

	// position() relates an element's margin box to its offset parent's padding box
	// This corresponds to the behavior of CSS absolute positioning
	position: function() {
		if ( !this[ 0 ] ) {
			return;
		}

		var offsetParent, offset, doc,
			elem = this[ 0 ],
			parentOffset = { top: 0, left: 0 };

		// position:fixed elements are offset from the viewport, which itself always has zero offset
		if ( jQuery.css( elem, "position" ) === "fixed" ) {

			// Assume position:fixed implies availability of getBoundingClientRect
			offset = elem.getBoundingClientRect();

		} else {
			offset = this.offset();

			// Account for the *real* offset parent, which can be the document or its root element
			// when a statically positioned element is identified
			doc = elem.ownerDocument;
			offsetParent = elem.offsetParent || doc.documentElement;
			while ( offsetParent &&
				( offsetParent === doc.body || offsetParent === doc.documentElement ) &&
				jQuery.css( offsetParent, "position" ) === "static" ) {

				offsetParent = offsetParent.parentNode;
			}
			if ( offsetParent && offsetParent !== elem && offsetParent.nodeType === 1 ) {

				// Incorporate borders into its offset, since they are outside its content origin
				parentOffset = jQuery( offsetParent ).offset();
				parentOffset.top += jQuery.css( offsetParent, "borderTopWidth", true );
				parentOffset.left += jQuery.css( offsetParent, "borderLeftWidth", true );
			}
		}

		// Subtract parent offsets and element margins
		return {
			top: offset.top - parentOffset.top - jQuery.css( elem, "marginTop", true ),
			left: offset.left - parentOffset.left - jQuery.css( elem, "marginLeft", true )
		};
	},

	// This method will return documentElement in the following cases:
	// 1) For the element inside the iframe without offsetParent, this method will return
	//    documentElement of the parent window
	// 2) For the hidden or detached element
	// 3) For body or html element, i.e. in case of the html node - it will return itself
	//
	// but those exceptions were never presented as a real life use-cases
	// and might be considered as more preferable results.
	//
	// This logic, however, is not guaranteed and can change at any point in the future
	offsetParent: function() {
		return this.map( function() {
			var offsetParent = this.offsetParent;

			while ( offsetParent && jQuery.css( offsetParent, "position" ) === "static" ) {
				offsetParent = offsetParent.offsetParent;
			}

			return offsetParent || documentElement;
		} );
	}
} );

// Create scrollLeft and scrollTop methods
jQuery.each( { scrollLeft: "pageXOffset", scrollTop: "pageYOffset" }, function( method, prop ) {
	var top = "pageYOffset" === prop;

	jQuery.fn[ method ] = function( val ) {
		return access( this, function( elem, method, val ) {

			// Coalesce documents and windows
			var win;
			if ( isWindow( elem ) ) {
				win = elem;
			} else if ( elem.nodeType === 9 ) {
				win = elem.defaultView;
			}

			if ( val === undefined ) {
				return win ? win[ prop ] : elem[ method ];
			}

			if ( win ) {
				win.scrollTo(
					!top ? val : win.pageXOffset,
					top ? val : win.pageYOffset
				);

			} else {
				elem[ method ] = val;
			}
		}, method, val, arguments.length );
	};
} );

// Support: Safari <=7 - 9.1, Chrome <=37 - 49
// Add the top/left cssHooks using jQuery.fn.position
// Webkit bug: https://bugs.webkit.org/show_bug.cgi?id=29084
// Blink bug: https://bugs.chromium.org/p/chromium/issues/detail?id=589347
// getComputedStyle returns percent when specified for top/left/bottom/right;
// rather than make the css module depend on the offset module, just check for it here
jQuery.each( [ "top", "left" ], function( _i, prop ) {
	jQuery.cssHooks[ prop ] = addGetHookIf( support.pixelPosition,
		function( elem, computed ) {
			if ( computed ) {
				computed = curCSS( elem, prop );

				// If curCSS returns percentage, fallback to offset
				return rnumnonpx.test( computed ) ?
					jQuery( elem ).position()[ prop ] + "px" :
					computed;
			}
		}
	);
} );


// Create innerHeight, innerWidth, height, width, outerHeight and outerWidth methods
jQuery.each( { Height: "height", Width: "width" }, function( name, type ) {
	jQuery.each( {
		padding: "inner" + name,
		content: type,
		"": "outer" + name
	}, function( defaultExtra, funcName ) {

		// Margin is only for outerHeight, outerWidth
		jQuery.fn[ funcName ] = function( margin, value ) {
			var chainable = arguments.length && ( defaultExtra || typeof margin !== "boolean" ),
				extra = defaultExtra || ( margin === true || value === true ? "margin" : "border" );

			return access( this, function( elem, type, value ) {
				var doc;

				if ( isWindow( elem ) ) {

					// $( window ).outerWidth/Height return w/h including scrollbars (gh-1729)
					return funcName.indexOf( "outer" ) === 0 ?
						elem[ "inner" + name ] :
						elem.document.documentElement[ "client" + name ];
				}

				// Get document width or height
				if ( elem.nodeType === 9 ) {
					doc = elem.documentElement;

					// Either scroll[Width/Height] or offset[Width/Height] or client[Width/Height],
					// whichever is greatest
					return Math.max(
						elem.body[ "scroll" + name ], doc[ "scroll" + name ],
						elem.body[ "offset" + name ], doc[ "offset" + name ],
						doc[ "client" + name ]
					);
				}

				return value === undefined ?

					// Get width or height on the element, requesting but not forcing parseFloat
					jQuery.css( elem, type, extra ) :

					// Set width or height on the element
					jQuery.style( elem, type, value, extra );
			}, type, chainable ? margin : undefined, chainable );
		};
	} );
} );


jQuery.each( [
	"ajaxStart",
	"ajaxStop",
	"ajaxComplete",
	"ajaxError",
	"ajaxSuccess",
	"ajaxSend"
], function( _i, type ) {
	jQuery.fn[ type ] = function( fn ) {
		return this.on( type, fn );
	};
} );




jQuery.fn.extend( {

	bind: function( types, data, fn ) {
		return this.on( types, null, data, fn );
	},
	unbind: function( types, fn ) {
		return this.off( types, null, fn );
	},

	delegate: function( selector, types, data, fn ) {
		return this.on( types, selector, data, fn );
	},
	undelegate: function( selector, types, fn ) {

		// ( namespace ) or ( selector, types [, fn] )
		return arguments.length === 1 ?
			this.off( selector, "**" ) :
			this.off( types, selector || "**", fn );
	},

	hover: function( fnOver, fnOut ) {
		return this
			.on( "mouseenter", fnOver )
			.on( "mouseleave", fnOut || fnOver );
	}
} );

jQuery.each(
	( "blur focus focusin focusout resize scroll click dblclick " +
	"mousedown mouseup mousemove mouseover mouseout mouseenter mouseleave " +
	"change select submit keydown keypress keyup contextmenu" ).split( " " ),
	function( _i, name ) {

		// Handle event binding
		jQuery.fn[ name ] = function( data, fn ) {
			return arguments.length > 0 ?
				this.on( name, null, data, fn ) :
				this.trigger( name );
		};
	}
);




// Support: Android <=4.0 only
// Make sure we trim BOM and NBSP
// Require that the "whitespace run" starts from a non-whitespace
// to avoid O(N^2) behavior when the engine would try matching "\s+$" at each space position.
var rtrim = /^[\s\uFEFF\xA0]+|([^\s\uFEFF\xA0])[\s\uFEFF\xA0]+$/g;

// Bind a function to a context, optionally partially applying any
// arguments.
// jQuery.proxy is deprecated to promote standards (specifically Function#bind)
// However, it is not slated for removal any time soon
jQuery.proxy = function( fn, context ) {
	var tmp, args, proxy;

	if ( typeof context === "string" ) {
		tmp = fn[ context ];
		context = fn;
		fn = tmp;
	}

	// Quick check to determine if target is callable, in the spec
	// this throws a TypeError, but we will just return undefined.
	if ( !isFunction( fn ) ) {
		return undefined;
	}

	// Simulated bind
	args = slice.call( arguments, 2 );
	proxy = function() {
		return fn.apply( context || this, args.concat( slice.call( arguments ) ) );
	};

	// Set the guid of unique handler to the same of original handler, so it can be removed
	proxy.guid = fn.guid = fn.guid || jQuery.guid++;

	return proxy;
};

jQuery.holdReady = function( hold ) {
	if ( hold ) {
		jQuery.readyWait++;
	} else {
		jQuery.ready( true );
	}
};
jQuery.isArray = Array.isArray;
jQuery.parseJSON = JSON.parse;
jQuery.nodeName = nodeName;
jQuery.isFunction = isFunction;
jQuery.isWindow = isWindow;
jQuery.camelCase = camelCase;
jQuery.type = toType;

jQuery.now = Date.now;

jQuery.isNumeric = function( obj ) {

	// As of jQuery 3.0, isNumeric is limited to
	// strings and numbers (primitives or objects)
	// that can be coerced to finite numbers (gh-2662)
	var type = jQuery.type( obj );
	return ( type === "number" || type === "string" ) &&

		// parseFloat NaNs numeric-cast false positives ("")
		// ...but misinterprets leading-number strings, particularly hex literals ("0x...")
		// subtraction forces infinities to NaN
		!isNaN( obj - parseFloat( obj ) );
};

jQuery.trim = function( text ) {
	return text == null ?
		"" :
		( text + "" ).replace( rtrim, "$1" );
};



// Register as a named AMD module, since jQuery can be concatenated with other
// files that may use define, but not via a proper concatenation script that
// understands anonymous AMD modules. A named AMD is safest and most robust
// way to register. Lowercase jquery is used because AMD module names are
// derived from file names, and jQuery is normally delivered in a lowercase
// file name. Do this after creating the global so that if an AMD module wants
// to call noConflict to hide this version of jQuery, it will work.

// Note that for maximum portability, libraries that are not jQuery should
// declare themselves as anonymous modules, and avoid setting a global if an
// AMD loader is present. jQuery is a special case. For more information, see
// https://github.com/jrburke/requirejs/wiki/Updating-existing-libraries#wiki-anon

if ( typeof define === "function" && define.amd ) {
	define( "jquery", [], function() {
		return jQuery;
	} );
}




var

	// Map over jQuery in case of overwrite
	_jQuery = window.jQuery,

	// Map over the $ in case of overwrite
	_$ = window.$;

jQuery.noConflict = function( deep ) {
	if ( window.$ === jQuery ) {
		window.$ = _$;
	}

	if ( deep && window.jQuery === jQuery ) {
		window.jQuery = _jQuery;
	}

	return jQuery;
};

// Expose jQuery and $ identifiers, even in AMD
// (trac-7102#comment:10, https://github.com/jquery/jquery/pull/557)
// and CommonJS for browser emulators (trac-13566)
if ( typeof noGlobal === "undefined" ) {
	window.jQuery = window.$ = jQuery;
}




return jQuery;
} );

window.$ = window.jQueryNoConflictBackup;
define('jquery-private',['jquery'], function ($) {
	$.noConflict();
	return $;
});

define('cartware/components/Config',[
	"lodash"
], function(
	_
) {
	function Config() {
		this.configObject = {};
	}

	Config.prototype = {
		get: function(key, defaultValue) {
			return _.get(this.configObject, key, defaultValue);
		},

		populate: function(obj) {
			this.configObject = obj;
			return this;
		}
	}

	return (new Config);
});
(function(f){if(typeof exports==="object"&&typeof module!=="undefined"){module.exports=f()}else if(typeof define==="function"&&define.amd){define('lucid',[],f)}else{var g;if(typeof window!=="undefined"){g=window}else if(typeof global!=="undefined"){g=global}else if(typeof self!=="undefined"){g=self}else{g=this}g.lucidJS = f()}})(function(){var define,module,exports;return (function e(t,n,r){function s(o,u){if(!n[o]){if(!t[o]){var a=typeof require=="function"&&require;if(!u&&a)return a(o,!0);if(i)return i(o,!0);var f=new Error("Cannot find module '"+o+"'");throw f.code="MODULE_NOT_FOUND",f}var l=n[o]={exports:{}};t[o][0].call(l.exports,function(e){var n=t[o][1][e];return s(n?n:e)},l,l.exports,e,t,n,r)}return n[o].exports}var i=typeof require=="function"&&require;for(var o=0;o<r.length;o++)s(r[o]);return s})({1:[function(require,module,exports){
exports.EventEmitter=require("./lib/event-emitter");
},{"./lib/event-emitter":2}],2:[function(require,module,exports){
function EventEmitter(){this._listeners={},this._pipedEmitters=[],this._flags={},this.source=null,this.event=null,this.cancelBubble=!1}EventEmitter.prototype._expandEvent=function(t){for(var e=t.split("."),i=[];e.length>0;)i.push(e.join(".")),e.pop();return i},EventEmitter.prototype.emit=function(t){if(null!==t&&"object"==typeof t&&"number"==typeof t.length){for(var e=0;e<t.length;e+=1)switch(arguments.length){case 1:this.emit(t[e]);break;case 2:this.emit(t[e],arguments[1]);break;case 3:this.emit(t[e],arguments[1],arguments[2]);break;default:var i=arguments.slice?arguments.slice(1):Array.prototype.slice.call(arguments,1);i.unshift(t[e]),this.emit.apply(this,i)}return this}var s=this.source;if(this.source=this._remoteSource||this,this._remoteSource&&(this._remoteSource=null),"emitter"!==t.slice(0,7)){var r=this.event;switch(this.event=t,arguments.length){case 1:this.emit("emitter.emit",t);break;case 2:this.emit("emitter.emit",t,arguments[1]);break;case 3:this.emit("emitter.emit",t,arguments[1],arguments[2]);break;default:var i=arguments.slice?arguments.slice(1):Array.prototype.slice.call(arguments,1);i.unshift("emitter.emit",t),this.emit.apply(this,i)}}var n=this.cancelBubble;this.cancelBubble=!1;for(var h=this._expandEvent(t),e=0;e<h.length;e+=1){var l=h[e];if(this._listeners[l]&&this._listeners[l].length)for(var o,p=this._listeners[l].slice(0),a=0;a<p.length;a+=1){switch(arguments.length){case 1:o=p[a].call(this);break;case 2:o=p[a].call(this,arguments[1]);break;case 3:o=p[a].call(this,arguments[1],arguments[2]);break;default:var i=arguments.slice?arguments.slice(1):Array.prototype.slice.call(arguments,1);o=p[a].apply(this,i)}o===!1&&(this.cancelBubble=!1)}else if("error"===t)throw arguments[1]||new Error("Unknown emitter error")}if("emitter"!==t.slice(0,7)&&(this.event=r),this.source=s,!this.cancelBubble){for(var e=0;e<this._pipedEmitters.length;e+=1){var u=this._pipedEmitters[e];u._remoteSource=this,u.emit.apply(u,arguments)}for(var e=0;e<h.length;e+=1){var l=h[e],m=this._pipedEmitters[l];if(m)for(var a=0;a<m.length;a+=1)u=m[e],u&&(u._remoteSource=this,u.emit.apply(u,arguments))}}return this.cancelBubble=n,this},EventEmitter.prototype.trigger=EventEmitter.prototype.emit,EventEmitter.prototype.bind=function(t,e){if(null!==t&&"object"==typeof t&&"number"==typeof t.length){for(var i=0;i<t.length;i+=1)this.bind(t[i],e);return this}var s=this.event,r=this.source;if(this.event=t,this.source=this,this.emit("emitter.bind",e),this.event=s,this.source=r,this._listeners[t]||(this._listeners[t]=[]),"function"==typeof e)this._listeners[t].push(e);else if(null!==e&&"object"==typeof e&&"number"==typeof e.length)for(var i=0;i<e.length;i+=1)this._listeners[t].push(e[i]);this._executeFlag(t,e);for(var i=0;i<this._pipedEmitters.length;i+=1){var n=this._pipedEmitters[i];n._executeFlag(t,e)}if(this._pipedEmitters[t])for(var i=0;i<this._pipedEmitters[t].length;i+=1){var n=this._pipedEmitters[t][i];n._executeFlag(t,e)}return this},EventEmitter.prototype.addListener=EventEmitter.prototype.bind,EventEmitter.prototype.on=EventEmitter.prototype.bind,EventEmitter.prototype._executeFlag=function(t,e){if(this._flags[t]){var i=this.source,s=this.event;if(this.source=this,this.event=t,"function"==typeof e)e.apply(this,this._flags[t]);else if(null!==e&&"object"==typeof e&&"number"==typeof e.length)for(var r=0;r<e.length;r+=1)e[r].apply(this,this._flags[t]);this.source=i,this.event=s}},EventEmitter.prototype.weakBind=function(t,e){if(null!==t&&"object"==typeof t&&"number"==typeof t.length){for(var i=0;i<t.length;i+=1)this.weakBind(t[i],e);return this}if(this._listeners[t]||(this._listeners[t]=[]),"function"==typeof e)this._listeners[t].push(e);else if(null!==e&&"object"==typeof e&&"number"==typeof e.length)for(var i=0;i<e.length;i+=1)this._listeners[t].push(e[i]);this._listeners[t].push(function r(){if("function"==typeof e){var i=this._listeners[t].indexOf(e);i>-1&&this._listeners[t].splice(i,1)}else if(null!==e&&"object"==typeof e&&"number"==typeof e.length)for(var i=0;i<e.length;i+=1){var s=this._listeners[t].indexOf(e[i]);s>-1&&(this._listeners[t].splice(s,1),i-=1)}var i=this._listeners[t].indexOf(r);i>-1&&this._listeners[t].splice(i,1)}),this._executeFlag(t,e);for(var i=0;i<this._pipedEmitters.length;i+=1){var s=this._pipedEmitters[i];s._executeFlag(t,e)}if(this._pipedEmitters[t])for(var i=0;i<this._pipedEmitters[t].length;i+=1){var s=this._pipedEmitters[i];s._executeFlag(t,e)}return this},EventEmitter.prototype.once=EventEmitter.prototype.weakBind,EventEmitter.prototype.unbind=function(t,e){if(null!==t&&"object"==typeof t&&"number"==typeof t.length){for(var i=0;i<t.length;i+=1)this.unbind(t[i],e);return this}if(this._listeners[t]){var s=this.event,r=this.source;this.event=t,this.source=this;var n=this._listeners[t];if(null!==e&&"object"==typeof e&&"number"==typeof e.length)for(var i=0;i<e.length;i+=1){var h=n.indexOf(e[i]);h>-1&&(this.emit("emitter.unbind",e[i]),n.splice(h,1),i-=1)}else if("function"==typeof e){var h=n.indexOf(e);h>-1&&(this.emit("emitter.unbind",e),n.splice(h,1))}this.event=s,this.source=r}return this},EventEmitter.prototype.removeListener=EventEmitter.prototype.unbind,EventEmitter.prototype.off=EventEmitter.prototype.unbind,EventEmitter.prototype.unbindAll=function(t){if(null!==t&&"object"==typeof t&&"number"==typeof t.length){for(var e=0;e<t.length;e+=1)this.unbindAll(t[e]);return this}if("string"==typeof t&&this._listeners[t]){for(var e=0;e<this._listeners[t].length;e+=1)this.unbind(t,this._listeners[t][e]);return this}for(var t in this._listeners)for(var e=0;e<this._listeners[t].length;e+=1)this.unbind(t,this._listeners[t][e]);return this},EventEmitter.prototype.removeAllListeners=EventEmitter.prototype.unbindAll,EventEmitter.prototype.flag=function(t){if(null!==t&&"object"==typeof t&&"number"==typeof t.length){for(var e=0;e<t.length;e+=1)switch(arguments.length){case 1:this.flag(t[e]);break;case 2:this.flag(t[e],arguments[1]);break;case 3:this.flag(t[e],arguments[1],arguments[2]);break;default:var i=arguments.slice?arguments.slice(1):Array.prototype.slice.call(arguments,1);i.unshift(t[e]),this.flag.apply(this,i)}return this}var s=this.event,r=this.source;switch(this.event=t,this.source=this,arguments.length){case 1:this.emit("emitter.flag",t);break;case 2:this.emit("emitter.flag",t,arguments[1]);break;case 3:this.emit("emitter.flag",t,arguments[1],arguments[2]);break;default:var i=arguments.slice?arguments.slice(1):Array.prototype.slice.call(arguments,1);i.unshift("emitter.flag",t),this.emit.apply(this,i)}if(this.event=s,this.source=r,this._flags[t]=arguments.slice?arguments.slice(1):Array.prototype.slice.call(arguments,1),this._listeners[t])for(var n=this._listeners[t].splice(0,this._listeners[t].length),e=0;e<n.length;e+=1)this._executeFlag(t,n[e]);return this},EventEmitter.prototype.unflag=function(t){if(null!==t&&"object"==typeof t&&"number"==typeof t.length){for(var e=0;e<t.length;e+=1)this.unflag(t[e]);return this}if(this._flags[t]){var i=this.event,s=this.source;this.event=t,this.source=this,this.emit("emitter.unflag",t),this.event=i,this.source=s,delete this._flags[t]}return this},EventEmitter.prototype.pipe=function(t,e){if(null!==t&&"object"==typeof t&&void 0===e&&(e=t,t=null),null!==t&&"object"==typeof t&&"number"==typeof t.length){for(var i=0;i<t.length;i+=1)this.pipe(t[i],e);return this}var s=this.source;this.source=this;var r=this.event;return this.event=t,this.emit("emitter.pipe",e,t),this.event=r,this.source=s,null===t?this._pipedEmitters.push(e):(this._pipedEmitters[t]||(this._pipedEmitters[t]=[]),this._pipedEmitters[t].push(e)),this},EventEmitter.prototype.unpipe=function(t,e){if(null!==t&&"object"==typeof t&&void 0===e&&(e=t,t=null),null!==t&&"object"==typeof t&&"number"==typeof t.length){for(var i=0;i<t.length;i+=1)this.unpipe(t[i],e);return this}var s=this.source;this.source=this;var r=this.event;if(this.event=t,null===t){var n=this._pipedEmitters.indexOf(e);n>-1&&(this.emit("emitter.unpipe",this._pipedEmitters[n]),this._pipedEmitters.splice(n,1))}else{if(!this._pipedEmitters[t])return this;var n=this._pipedEmitters[t].indexOf(e);n>-1&&(this.emit("emitter.unpipe",this._pipedEmitters[t][n],t),this._pipedEmitters[t].splice(n,1))}return this.event=r,this.source=s,this},EventEmitter.prototype.listeners=function(t){return t?this._listeners[t]:this._listeners},module.exports=EventEmitter;
},{}]},{},[1])(1)
});
define('cartware/components/Registry',[
	"lodash",
	"lucid"
], function(
	_,
	lucidJS
) {

	var _instance = null;

	var getInstance = function() {
		if (_instance === null) {
			_instance = new Registry();
		}

		return _instance;
	};

	function Registry() {
		if (!(this instanceof Registry)) {
			return getInstance();
		}

		lucidJS.EventEmitter.call(this);

		this._register = {};
	}

	Registry.getInstance = getInstance;

	Registry.prototype = _.create(new lucidJS.EventEmitter, {
		"constructor": Registry,

		_register: null,

		register: function(key, value, graceful) {
			if (key in this._register) {
				if (graceful) return;

				throw "Registry key '" + key + "' has already been set.";
			}

			this._register[key] = value;
			this.flag("available." + key, value);

			return this;
		},

		unregister: function(key) {
			if (key in this._register) {
				delete this._register[key];
			}

			this.unflag("available." + key);

			return this;
		},

		retrieve: function(key, callback) {
			this.bind("available." + key, callback);
		}
	});

	return Registry;
});
define('cartware/components/Utilities',[
	"lodash"
], function(
	_
) {
	return {
		getPath: function getPath (obj, ks) {
			if (typeof ks == "string") ks = ks.split(".");

			// If we have reached an undefined property
			// then stop executing and return undefined
			if (obj === undefined) return void 0;

			// If the path array has no more elements, we've reached
			// the intended property and return its value
			if (ks.length === 0) return obj;

			// If we still have elements in the path array and the current
			// value is null, stop executing and return undefined
			if (obj === null) return void 0;

			return getPath(obj[_.head(ks)], _.tail(ks));
		}
	}
});
define('cartware/components/FeatureFlagged',[
	"./Utilities"
], function(
	Utils
) {

	function FeatureFlaggedComponent(component, flags) {
		if (!(this instanceof FeatureFlaggedComponent)) {
			return new FeatureFlaggedComponent(component, flags);
		}

		this.component = component;
		this.flags = flags;
	}

	FeatureFlaggedComponent.prototype = {
		flags: null,

		getFlags: function() {
			return this.flags;
		},

		getComponent: function() {
			return this.component;
		}
	}

	return FeatureFlaggedComponent;
});
define('cartware/components/Dispatcher',[
	"./Utilities",
	"./FeatureFlagged"
], function(
	Utils,
	FeatureFlagged
) {
	var _sharedAppliedComponents = [],
		_allAppliedComponents = {
			components: [],
			instances: []
		};

	function Dispatcher(options) {
		this._appliedComponents	= ("useSharedComponentPool" in options) ? _sharedAppliedComponents : [];
		this._handles			= ("handles" in options) ? options.handles : [];
		this._featureFlags		= ("featureFlags" in options) ? options.featureFlags : {};

		if ("apply" in options) {
			this.applyHandles(options.apply);
		}
	}

	Dispatcher.prototype = {
		_appliedComponents:	null,
		_handles:			null,

		getHandles: function() {
			return this._handles;
		},

		setHandles: function(handles) {
			this.handles = handles;
			return this;
		},

		applyHandles: function(handlesMap, callback) {
			var self = this;
			var handle, components, innerKey, componentsQueue = [], componentsDispatched = 0;

			for (handle in handlesMap) {
				if (handlesMap.hasOwnProperty(handle)) {
					components = handlesMap[handle];
					if (self._handles.indexOf(handle) >= 0) {
						for (innerKey in components) {
							if (components.hasOwnProperty(innerKey)) {
								// Feature flagged component
								if (components[innerKey] instanceof FeatureFlagged) {
									if (!_.every(components[innerKey].getFlags(), function(flag) {
										return (flag in self._featureFlags && self._featureFlags[flag]);
									})) {
										return;
									}

									componentsQueue.push(components[innerKey].getComponent());
								}
								// Normal component
								else {
									componentsQueue.push(components[innerKey]);
								}
							}
						}
					}
				}
			}

			_.forEach(componentsQueue, function(component) {
				self.initializeComponent(component, callback ? function(componentInstance) {
					var isLast = componentsQueue.length === ++componentsDispatched;
					callback(componentInstance, isLast);
				} : undefined);
			});
		},

		initializeComponent: function(component, callback) {
			var self = this;

			if ("string" === typeof component) {
				require([component], function(c) { self.initializeComponent(c, callback); });
				return;
			}

			if (self._appliedComponents.indexOf(component) < 0) {
				var allComponentsIndex = _allAppliedComponents.components.indexOf(component);

				if (allComponentsIndex >= 0) {
					var oldInstance = _allAppliedComponents.instances[allComponentsIndex];

					if ("function" === typeof oldInstance.destructor) {
						oldInstance.destructor();
					}

					Array.prototype.splice.call(_allAppliedComponents.components, allComponentsIndex, 1);
					Array.prototype.splice.call(_allAppliedComponents.instances, allComponentsIndex, 1);
				}

				var c = new component();

				if ("undefined" !== typeof c.init) {
					c.init();
				}

				if ("undefined" !== typeof c.$init) {
					require(["jquery"], function($) {
						$($.proxy(c.$init, c));
					});
				}

				self._appliedComponents.push(component);

				_allAppliedComponents.components.push(component);
				_allAppliedComponents.instances.push(c);
			}

			if (callback) {
				callback.apply(null, [
					_allAppliedComponents.instances[_allAppliedComponents.components.indexOf(component)]
				]);
			}
		}
	};

	return Dispatcher;
});
define('cartware/components/FeatureFlags',[
	"./Utilities"
], function(
	Utils
) {

	function FeatureFlags(flags) {
		this.flags = flags;
	}

	FeatureFlags.prototype = {
		flags: null,

		isEnabled: function(flag) {
			return !!Utils.getPath(this.flags, flag)
		},

		getFlags: function() {
			return this.flags;
		}
	}

	return FeatureFlags;
});
/*
* loglevel - https://github.com/pimterry/loglevel
*
* Copyright (c) 2013 Tim Perry
* Licensed under the MIT license.
*/
(function (root, definition) {
    "use strict";
    if (typeof define === 'function' && define.amd) {
        define('loglevel',definition);
    } else if (typeof module === 'object' && module.exports) {
        module.exports = definition();
    } else {
        root.log = definition();
    }
}(this, function () {
    "use strict";

    // Slightly dubious tricks to cut down minimized file size
    var noop = function() {};
    var undefinedType = "undefined";

    var logMethods = [
        "trace",
        "debug",
        "info",
        "warn",
        "error"
    ];

    // Cross-browser bind equivalent that works at least back to IE6
    function bindMethod(obj, methodName) {
        var method = obj[methodName];
        if (typeof method.bind === 'function') {
            return method.bind(obj);
        } else {
            try {
                return Function.prototype.bind.call(method, obj);
            } catch (e) {
                // Missing bind shim or IE8 + Modernizr, fallback to wrapping
                return function() {
                    return Function.prototype.apply.apply(method, [obj, arguments]);
                };
            }
        }
    }

    // Build the best logging method possible for this env
    // Wherever possible we want to bind, not wrap, to preserve stack traces
    function realMethod(methodName) {
        if (methodName === 'debug') {
            methodName = 'log';
        }

        if (typeof console === undefinedType) {
            return false; // No method possible, for now - fixed later by enableLoggingWhenConsoleArrives
        } else if (console[methodName] !== undefined) {
            return bindMethod(console, methodName);
        } else if (console.log !== undefined) {
            return bindMethod(console, 'log');
        } else {
            return noop;
        }
    }

    // These private functions always need `this` to be set properly

    function replaceLoggingMethods(level, loggerName) {
        /*jshint validthis:true */
        for (var i = 0; i < logMethods.length; i++) {
            var methodName = logMethods[i];
            this[methodName] = (i < level) ?
                noop :
                this.methodFactory(methodName, level, loggerName);
        }

        // Define log.log as an alias for log.debug
        this.log = this.debug;
    }

    // In old IE versions, the console isn't present until you first open it.
    // We build realMethod() replacements here that regenerate logging methods
    function enableLoggingWhenConsoleArrives(methodName, level, loggerName) {
        return function () {
            if (typeof console !== undefinedType) {
                replaceLoggingMethods.call(this, level, loggerName);
                this[methodName].apply(this, arguments);
            }
        };
    }

    // By default, we use closely bound real methods wherever possible, and
    // otherwise we wait for a console to appear, and then try again.
    function defaultMethodFactory(methodName, level, loggerName) {
        /*jshint validthis:true */
        return realMethod(methodName) ||
               enableLoggingWhenConsoleArrives.apply(this, arguments);
    }

    function Logger(name, defaultLevel, factory) {
      var self = this;
      var currentLevel;
      var storageKey = "loglevel";
      if (name) {
        storageKey += ":" + name;
      }

      function persistLevelIfPossible(levelNum) {
          var levelName = (logMethods[levelNum] || 'silent').toUpperCase();

          if (typeof window === undefinedType) return;

          // Use localStorage if available
          try {
              window.localStorage[storageKey] = levelName;
              return;
          } catch (ignore) {}

          // Use session cookie as fallback
          try {
              window.document.cookie =
                encodeURIComponent(storageKey) + "=" + levelName + ";";
          } catch (ignore) {}
      }

      function getPersistedLevel() {
          var storedLevel;

          if (typeof window === undefinedType) return;

          try {
              storedLevel = window.localStorage[storageKey];
          } catch (ignore) {}

          // Fallback to cookies if local storage gives us nothing
          if (typeof storedLevel === undefinedType) {
              try {
                  var cookie = window.document.cookie;
                  var location = cookie.indexOf(
                      encodeURIComponent(storageKey) + "=");
                  if (location !== -1) {
                      storedLevel = /^([^;]+)/.exec(cookie.slice(location))[1];
                  }
              } catch (ignore) {}
          }

          // If the stored level is not valid, treat it as if nothing was stored.
          if (self.levels[storedLevel] === undefined) {
              storedLevel = undefined;
          }

          return storedLevel;
      }

      /*
       *
       * Public logger API - see https://github.com/pimterry/loglevel for details
       *
       */

      self.name = name;

      self.levels = { "TRACE": 0, "DEBUG": 1, "INFO": 2, "WARN": 3,
          "ERROR": 4, "SILENT": 5};

      self.methodFactory = factory || defaultMethodFactory;

      self.getLevel = function () {
          return currentLevel;
      };

      self.setLevel = function (level, persist) {
          if (typeof level === "string" && self.levels[level.toUpperCase()] !== undefined) {
              level = self.levels[level.toUpperCase()];
          }
          if (typeof level === "number" && level >= 0 && level <= self.levels.SILENT) {
              currentLevel = level;
              if (persist !== false) {  // defaults to true
                  persistLevelIfPossible(level);
              }
              replaceLoggingMethods.call(self, level, name);
              if (typeof console === undefinedType && level < self.levels.SILENT) {
                  return "No console available for logging";
              }
          } else {
              throw "log.setLevel() called with invalid level: " + level;
          }
      };

      self.setDefaultLevel = function (level) {
          if (!getPersistedLevel()) {
              self.setLevel(level, false);
          }
      };

      self.enableAll = function(persist) {
          self.setLevel(self.levels.TRACE, persist);
      };

      self.disableAll = function(persist) {
          self.setLevel(self.levels.SILENT, persist);
      };

      // Initialize with the right level
      var initialLevel = getPersistedLevel();
      if (initialLevel == null) {
          initialLevel = defaultLevel == null ? "WARN" : defaultLevel;
      }
      self.setLevel(initialLevel, false);
    }

    /*
     *
     * Top-level API
     *
     */

    var defaultLogger = new Logger();

    var _loggersByName = {};
    defaultLogger.getLogger = function getLogger(name) {
        if (typeof name !== "string" || name === "") {
          throw new TypeError("You must supply a name when creating a logger.");
        }

        var logger = _loggersByName[name];
        if (!logger) {
          logger = _loggersByName[name] = new Logger(
            name, defaultLogger.getLevel(), defaultLogger.methodFactory);
        }
        return logger;
    };

    // Grab the current global log variable in case of overwrite
    var _log = (typeof window !== undefinedType) ? window.log : undefined;
    defaultLogger.noConflict = function() {
        if (typeof window !== undefinedType &&
               window.log === defaultLogger) {
            window.log = _log;
        }

        return defaultLogger;
    };

    defaultLogger.getLoggers = function getLoggers() {
        return _loggersByName;
    };

    return defaultLogger;
}));

define('cartware/components/Logger',[
	"loglevel"
], function(
	log
) {
	return log;
});
define('cartware/components/PubSubHub',[
	"lodash",
	"lucid"
], function(
	_,
	lucidJS
) {

	var _instance = null;

	var getInstance = function() {
		if (_instance === null) {
			_instance = new PubSubHub();
		}

		return _instance;
	};

	function PubSubHub() {
		if (!(this instanceof PubSubHub)) {
			return getInstance();
		}

		lucidJS.EventEmitter.call(this);

		this._register = {};
	}

	PubSubHub.getInstance = getInstance;

	PubSubHub.prototype = _.create(new lucidJS.EventEmitter, {
		"constructor": PubSubHub
	});

	return PubSubHub.getInstance();
});

define("json!rebrush/handles/default.json", function(){ return {
	"default": [
        "rebrush/components/plugin/civik-cookie-control",
		"rebrush/components/review/bazaarvoice-analytics",
		"shoptheme/components/layout/default",
		"rebrush/components/layout/page/switch/storeview",
	  	"shoptheme/components/layout/page/sidebar-related",
		"shoptheme/components/layout/lazyload",
		"shoptheme/components/layout/messages",
		"rebrush/components/layout/navigation",
		"shoptheme/components/layout/trigger",
		"rebrush/components/layout/search",
	  	"shoptheme/components/layout/story",
		"shoptheme/components/layout/equal-heights",
		"shoptheme/components/ui/accordion",
		"shoptheme/components/ui/tabs",
		"shoptheme/components/ui/selects",
		"rebrush/components/ui/selects/native",
		"rebrush/components/ui/tooltips",
	  	"shoptheme/components/ui/affix",
		"shoptheme/components/ui/affix-generic",
		"shoptheme/components/ui/toggle-visibility",
		"shoptheme/components/ui/scrollspy",
	  	"shoptheme/components/ui/scrolltop",
		"rebrush/components/ui/generated-menu",
		"shoptheme/components/ui/sections",
		"rebrush/components/cart/ajax",
		"rebrush/components/cart/etracker",
		"shoptheme/components/product/wishlist",
		"rebrush/components/cartware/slider",
		"rebrush/components/cartware/video-loader",
		"rebrush/components/compare/compare",
		"shoptheme/components/plugin/universal-analytics",
		"shoptheme/components/blocks/fce/elements/usps",
	  	"shoptheme/components/blocks/fce/elements/image-map",
	    "rebrush/components/blocks/hero-slider",
	  	"shoptheme/components/blocks/fce/newsletter-subscribe-landingpage",
	  	"shoptheme/components/blocks/csp/detail/firmware-accordion",
		"shoptheme/components/blocks/captcha",
		"rebrush/components/plugin/turnstile-lazy",
		"shoptheme/components/blocks/privacy/opt",
	  	"shoptheme/components/widgets/band",
		"shoptheme/components/tracking",
		"shoptheme/components/contentbridge/cms-search-results",
		"rebrush/components/blocks/raster-grid",
		"rebrush/components/blocks/raster-grid/tiles/image",
		"rebrush/components/blocks/raster-grid-v2",
		"rebrush/components/blocks/raster-grid-v2/tiles/image",
		"rebrush/components/layout/newsletter-subscribe",
		"cartware_checkout/components/agreements",
		"rebrush/components/blocks/cliplister/3dgallery",
		"rebrush/components/blocks/catalogsearch/autocomplete/recommendations",
		"rebrush/components/layout/skip-links"
	],

	"default_shop": [
		"rebrush/components/blocks/varnish/esi"
	],

	"gigaset_theme_home_index": [
		"shoptheme/components/layout/home"
	],

	"catalog_product_view": [
		"rebrush/components/product/view",
		"rebrush/components/product/view/media",
		"rebrush/components/product/view/specifications",
		"rebrush/components/product/view/description",
		"rebrush/components/product/view/review",
		"rebrush/components/product/view/related-link-in-description",
		"rebrush/components/product/view/addtocart/related",
		"rebrush/components/product/options",
		"rebrush/components/product/options/engraving",
		"rebrush/components/product/options/protection",
		"shoptheme/components/review/form",
		"rebrush/components/review/list",
		"shoptheme/components/product/view/store-ship",
		"shoptheme/components/plugin/multiline-placeholder",
		"rebrush/components/ui/sections",
		"rebrush/components/csp/product/download/item",
		"rebrush/components/category/layered",
		"rebrush/components/category/layered/filter",
		"rebrush/components/category/layered/slider",
		"rebrush/components/category/tree",
		"shoptheme/components/ui/sidebar",
        "rebrush/components/plugin/touch-controller",
		"rebrush/components/product/view/configurable-options-help",
		"rebrush/components/product/options/group",
		"rebrush/components/gigaset_customprint/legal",
		"rebrush/components/gigaset_presale/confirm",
		"rebrush/components/product/view/tracking",
		"rebrush/components/product/view/bazaarvoice",
		"rebrush/components/review/bv-lazy",
        "rebrush/components/review/bv-ready"
    ],

	"PRODUCT_TYPE_configurable": [
		"cartware/components/magento/product/type/configurable",
		"rebrush/components/product/type/configurable",
        "rebrush/components/product/type/configurable/addtocartbutton"
	],

	"catalog_category_layered": [
		"rebrush/components/category/layered",
		"rebrush/components/category/layered/filter",
		"rebrush/components/category/layered/slider",
	  	"rebrush/components/category/tree",
		"shoptheme/components/ui/sidebar",
        "rebrush/components/plugin/touch-controller",
        "rebrush/components/review/bv-ready"
	],

	"newsletter_manage_index": [
		"shoptheme/components/customer/newsletter"
	],

	"customer_account_create": [
		"shoptheme/components/customer/account/create"
	],

	"customer_account_edit": [
		"shoptheme/components/customer/account/edit"
	],

    "customer_address_form": [
      "rebrush/components/checkout/packstation-validation",
	  "rebrush/components/checkout/postcode-validator",
	  "rebrush/components/customer/address/address-packstation"
    ],

	"gigaset_theme_policy_index": [
		"shoptheme/components/customer/account/policy"
	],

	"gigaset_contentbridge_cms_page": [
		"rebrush/components/contentbridge/tabs",
		"shoptheme/components/contentbridge/accordeon",
        "rebrush/components/review/bv-ready"
	],

	"gigaset_theme_category_style_elements_1": [
		"shoptheme/components/blocks/elements/product/item/pane"
	],

	"gigaset_csp_support_detail_downloads": [
		"rebrush/components/csp/product/download/item"
	],

	"gigaset_csp_support_downloads": [
		"rebrush/components/csp/product/download/item"
	],

	"checkout_onepage_index": [
		"shoptheme/components/cartware/checkout",
		"rebrush/components/checkout/advises",
		"rebrush/components/checkout/password/onpage-validator",
		"rebrush/components/checkout/closing-loop",
		"rebrush/components/checkout/address/qaddress",
		"rebrush/components/checkout/reepay/checkout",
		"rebrush/components/checkout/packstation",
		"rebrush/components/checkout/postcode-validator"
	],

	"gigaset_contentbridge_cms_page_col2-layout-article": [
		"rebrush/components/ui/anchor-navigation"
	],

	"gigaset_block_anchor_navigation": [
		"rebrush/components/ui/anchor-navigation"
	],

	"gigaset_hr_list": [
		"shoptheme/components/ui/datatables"
	],

  	"gigaset_hr_detail": [
		"shoptheme/components/ui/maps"
	],

	"b2e_agreements_popup": [
		"shoptheme/components/customer/b2e/popup"
	],

	"payone_core_pexpress_review": [
		"shoptheme/components/paypal/express/review"
	],
	"image_comparer": [
		"rebrush/components/blocks/image-comparer"
	],

	"lp_router_video": [
		"rebrush/components/blocks/landingpages/router/video"
	],

	"lp_router_app": [
		"rebrush/components/blocks/landingpages/router/app"
	],

	"gigaset_wheretobuy_product_view": [
		"rebrush/components/product/view/wheretobuy"
	],

	"catalog_product_customoption_print": [
		"rebrush/components/gigaset_customprint/view"
	],

	"checkout_cart_index": [
		"rebrush/components/blocks/discount-code",
		"rebrush/components/checkout/closing-loop"
	],

	"cartware_cart_popup_open": [
		"rebrush/components/product/view/description",
		"rebrush/components/product/view/addtocart/related"
	],

	"gigaset_tradein_index_index": [
		"rebrush/components/tradein/tradein"
	],

	"customer_referral_dashboard_index": [
		"rebrush/components/referral/progressbar"
	],

	"reepay_standard_redirect": [
		"rebrush/components/checkout/reepay/checkout"
	],

	"reepay_standard_success": [
		"rebrush/components/checkout/reepay/checkout"
	],

	"checkout_multishipping_shipping": [
		"rebrush/components/checkout/reepay/checkout"
	],

	"onepagecheckout_index_index": [
		"rebrush/components/checkout/reepay/checkout"
	],

	"opc_index_index": [
		"rebrush/components/checkout/reepay/checkout"
	],

	"onestepcheckout_index_index": [
		"rebrush/components/checkout/reepay/checkout"
	],

	"gigaset_shipping_return_index": [
		"rebrush/components/blocks/gigaset/shipping/sales/order/shipment/return"
	],

	"sales_order_history": [
		"rebrush/components/customer/account/order/return/popup"
	]
}
;});

define('rebrush/system/validation',[],function() {
    window.Validation.addAllThese([
        // ['validate-select', 'Please select an option.', function(v) {
        //     return ((v != "none") && (v != null) && (v.length != 0));
        // }],
            ['required-entry', 'This is a required field.', function(v, element) {
            if ('SELECT' === element.nodeName.toUpperCase()) {
                this.error = Translator.translate('Please select an option.');
                return window.Validation.get('validate-select').test(v, element);
            }

            this.error = Translator.translate('This is a required field.');
            return !window.Validation.get('IsEmpty').test(v);
        }]
    ]);

    // fixes silent validation exception for addToCartForm when in engraving legal popup
    const originalValidationIsVisible = window.Validation.isVisible;

    window.Validation.isVisible = function(elm) {
        if (document.querySelector('.print-legal-popup-open')) {
            return false;
        }
        return originalValidationIsVisible(elm);
    };
});

define('gigaset/spinner',[
	"jquery"
], function(
	$
) {

	function Spinner () {
		this.selector = '.spinner-container';
		this.template = $('<div class="spinner-container"><div class="spinner-bounce">'
			+ '<div class="double-bounce1"></div>'
			+ '<div class="double-bounce2"></div>'
			+ '</div></div>');
		this.transitionTime = 300;
		this.defaultContainer = 'body';
	}

	Spinner.prototype = {

		template: null,
		selector: null,
		transitionTime: null,
		defaultContainer: null,

		add: function(container) {
			container = container || this.defaultContainer;
			$(container).prepend((this.template).hide().fadeIn(this.transitionTime));
		},
		remove: function(container) {
			container = container || this.defaultContainer;
			$(this.selector, container).fadeOut(this.transitionTime, function() {
				$(this).remove();
			} );
		}
	};

	return new Spinner;

});
/*!
 * jQuery Transit - CSS3 transitions and transformations
 * (c) 2011-2014 Rico Sta. Cruz
 * MIT Licensed.
 *
 * http://ricostacruz.com/jquery.transit
 * http://github.com/rstacruz/jquery.transit
 */

/* jshint expr: true */

;(function (root, factory) {

  if (typeof define === 'function' && define.amd) {
    define('jquery-transit',['jquery'], factory);
  } else if (typeof exports === 'object') {
    module.exports = factory(require('jquery'));
  } else {
    factory(root.jQuery);
  }

}(this, function($) {

  $.transit = {
    version: "0.9.12",

    // Map of $.css() keys to values for 'transitionProperty'.
    // See https://developer.mozilla.org/en/CSS/CSS_transitions#Properties_that_can_be_animated
    propertyMap: {
      marginLeft    : 'margin',
      marginRight   : 'margin',
      marginBottom  : 'margin',
      marginTop     : 'margin',
      paddingLeft   : 'padding',
      paddingRight  : 'padding',
      paddingBottom : 'padding',
      paddingTop    : 'padding'
    },

    // Will simply transition "instantly" if false
    enabled: true,

    // Set this to false if you don't want to use the transition end property.
    useTransitionEnd: false
  };

  var div = document.createElement('div');
  var support = {};

  // Helper function to get the proper vendor property name.
  // (`transition` => `WebkitTransition`)
  function getVendorPropertyName(prop) {
    // Handle unprefixed versions (FF16+, for example)
    if (prop in div.style) return prop;

    var prefixes = ['Moz', 'Webkit', 'O', 'ms'];
    var prop_ = prop.charAt(0).toUpperCase() + prop.substr(1);

    for (var i=0; i<prefixes.length; ++i) {
      var vendorProp = prefixes[i] + prop_;
      if (vendorProp in div.style) { return vendorProp; }
    }
  }

  // Helper function to check if transform3D is supported.
  // Should return true for Webkits and Firefox 10+.
  function checkTransform3dSupport() {
    div.style[support.transform] = '';
    div.style[support.transform] = 'rotateY(90deg)';
    return div.style[support.transform] !== '';
  }

  var isChrome = navigator.userAgent.toLowerCase().indexOf('chrome') > -1;

  // Check for the browser's transitions support.
  support.transition      = getVendorPropertyName('transition');
  support.transitionDelay = getVendorPropertyName('transitionDelay');
  support.transform       = getVendorPropertyName('transform');
  support.transformOrigin = getVendorPropertyName('transformOrigin');
  support.filter          = getVendorPropertyName('Filter');
  support.transform3d     = checkTransform3dSupport();

  var eventNames = {
    'transition':       'transitionend',
    'MozTransition':    'transitionend',
    'OTransition':      'oTransitionEnd',
    'WebkitTransition': 'webkitTransitionEnd',
    'msTransition':     'MSTransitionEnd'
  };

  // Detect the 'transitionend' event needed.
  var transitionEnd = support.transitionEnd = eventNames[support.transition] || null;

  // Populate jQuery's `$.support` with the vendor prefixes we know.
  // As per [jQuery's cssHooks documentation](http://api.jquery.com/jQuery.cssHooks/),
  // we set $.support.transition to a string of the actual property name used.
  for (var key in support) {
    if (support.hasOwnProperty(key) && typeof $.support[key] === 'undefined') {
      $.support[key] = support[key];
    }
  }

  // Avoid memory leak in IE.
  div = null;

  // ## $.cssEase
  // List of easing aliases that you can use with `$.fn.transition`.
  $.cssEase = {
    '_default':       'ease',
    'in':             'ease-in',
    'out':            'ease-out',
    'in-out':         'ease-in-out',
    'snap':           'cubic-bezier(0,1,.5,1)',
    // Penner equations
    'easeInCubic':    'cubic-bezier(.550,.055,.675,.190)',
    'easeOutCubic':   'cubic-bezier(.215,.61,.355,1)',
    'easeInOutCubic': 'cubic-bezier(.645,.045,.355,1)',
    'easeInCirc':     'cubic-bezier(.6,.04,.98,.335)',
    'easeOutCirc':    'cubic-bezier(.075,.82,.165,1)',
    'easeInOutCirc':  'cubic-bezier(.785,.135,.15,.86)',
    'easeInExpo':     'cubic-bezier(.95,.05,.795,.035)',
    'easeOutExpo':    'cubic-bezier(.19,1,.22,1)',
    'easeInOutExpo':  'cubic-bezier(1,0,0,1)',
    'easeInQuad':     'cubic-bezier(.55,.085,.68,.53)',
    'easeOutQuad':    'cubic-bezier(.25,.46,.45,.94)',
    'easeInOutQuad':  'cubic-bezier(.455,.03,.515,.955)',
    'easeInQuart':    'cubic-bezier(.895,.03,.685,.22)',
    'easeOutQuart':   'cubic-bezier(.165,.84,.44,1)',
    'easeInOutQuart': 'cubic-bezier(.77,0,.175,1)',
    'easeInQuint':    'cubic-bezier(.755,.05,.855,.06)',
    'easeOutQuint':   'cubic-bezier(.23,1,.32,1)',
    'easeInOutQuint': 'cubic-bezier(.86,0,.07,1)',
    'easeInSine':     'cubic-bezier(.47,0,.745,.715)',
    'easeOutSine':    'cubic-bezier(.39,.575,.565,1)',
    'easeInOutSine':  'cubic-bezier(.445,.05,.55,.95)',
    'easeInBack':     'cubic-bezier(.6,-.28,.735,.045)',
    'easeOutBack':    'cubic-bezier(.175, .885,.32,1.275)',
    'easeInOutBack':  'cubic-bezier(.68,-.55,.265,1.55)'
  };

  // ## 'transform' CSS hook
  // Allows you to use the `transform` property in CSS.
  //
  //     $("#hello").css({ transform: "rotate(90deg)" });
  //
  //     $("#hello").css('transform');
  //     //=> { rotate: '90deg' }
  //
  $.cssHooks['transit:transform'] = {
    // The getter returns a `Transform` object.
    get: function(elem) {
      return $(elem).data('transform') || new Transform();
    },

    // The setter accepts a `Transform` object or a string.
    set: function(elem, v) {
      var value = v;

      if (!(value instanceof Transform)) {
        value = new Transform(value);
      }

      // We've seen the 3D version of Scale() not work in Chrome when the
      // element being scaled extends outside of the viewport.  Thus, we're
      // forcing Chrome to not use the 3d transforms as well.  Not sure if
      // translate is affectede, but not risking it.  Detection code from
      // http://davidwalsh.name/detecting-google-chrome-javascript
      if (support.transform === 'WebkitTransform' && !isChrome) {
        elem.style[support.transform] = value.toString(true);
      } else {
        elem.style[support.transform] = value.toString();
      }

      $(elem).data('transform', value);
    }
  };

  // Add a CSS hook for `.css({ transform: '...' })`.
  // In jQuery 1.8+, this will intentionally override the default `transform`
  // CSS hook so it'll play well with Transit. (see issue #62)
  $.cssHooks.transform = {
    set: $.cssHooks['transit:transform'].set
  };

  // ## 'filter' CSS hook
  // Allows you to use the `filter` property in CSS.
  //
  //     $("#hello").css({ filter: 'blur(10px)' });
  //
  $.cssHooks.filter = {
    get: function(elem) {
      return elem.style[support.filter];
    },
    set: function(elem, value) {
      elem.style[support.filter] = value;
    }
  };

  // jQuery 1.8+ supports prefix-free transitions, so these polyfills will not
  // be necessary.
  if ($.fn.jquery < "1.8") {
    // ## 'transformOrigin' CSS hook
    // Allows the use for `transformOrigin` to define where scaling and rotation
    // is pivoted.
    //
    //     $("#hello").css({ transformOrigin: '0 0' });
    //
    $.cssHooks.transformOrigin = {
      get: function(elem) {
        return elem.style[support.transformOrigin];
      },
      set: function(elem, value) {
        elem.style[support.transformOrigin] = value;
      }
    };

    // ## 'transition' CSS hook
    // Allows you to use the `transition` property in CSS.
    //
    //     $("#hello").css({ transition: 'all 0 ease 0' });
    //
    $.cssHooks.transition = {
      get: function(elem) {
        return elem.style[support.transition];
      },
      set: function(elem, value) {
        elem.style[support.transition] = value;
      }
    };
  }

  // ## Other CSS hooks
  // Allows you to rotate, scale and translate.
  registerCssHook('scale');
  registerCssHook('scaleX');
  registerCssHook('scaleY');
  registerCssHook('translate');
  registerCssHook('rotate');
  registerCssHook('rotateX');
  registerCssHook('rotateY');
  registerCssHook('rotate3d');
  registerCssHook('perspective');
  registerCssHook('skewX');
  registerCssHook('skewY');
  registerCssHook('x', true);
  registerCssHook('y', true);

  // ## Transform class
  // This is the main class of a transformation property that powers
  // `$.fn.css({ transform: '...' })`.
  //
  // This is, in essence, a dictionary object with key/values as `-transform`
  // properties.
  //
  //     var t = new Transform("rotate(90) scale(4)");
  //
  //     t.rotate             //=> "90deg"
  //     t.scale              //=> "4,4"
  //
  // Setters are accounted for.
  //
  //     t.set('rotate', 4)
  //     t.rotate             //=> "4deg"
  //
  // Convert it to a CSS string using the `toString()` and `toString(true)` (for WebKit)
  // functions.
  //
  //     t.toString()         //=> "rotate(90deg) scale(4,4)"
  //     t.toString(true)     //=> "rotate(90deg) scale3d(4,4,0)" (WebKit version)
  //
  function Transform(str) {
    if (typeof str === 'string') { this.parse(str); }
    return this;
  }

  Transform.prototype = {
    // ### setFromString()
    // Sets a property from a string.
    //
    //     t.setFromString('scale', '2,4');
    //     // Same as set('scale', '2', '4');
    //
    setFromString: function(prop, val) {
      var args =
        (typeof val === 'string')  ? val.split(',') :
        (val.constructor === Array) ? val :
        [ val ];

      args.unshift(prop);

      Transform.prototype.set.apply(this, args);
    },

    // ### set()
    // Sets a property.
    //
    //     t.set('scale', 2, 4);
    //
    set: function(prop) {
      var args = Array.prototype.slice.apply(arguments, [1]);
      if (this.setter[prop]) {
        this.setter[prop].apply(this, args);
      } else {
        this[prop] = args.join(',');
      }
    },

    get: function(prop) {
      if (this.getter[prop]) {
        return this.getter[prop].apply(this);
      } else {
        return this[prop] || 0;
      }
    },

    setter: {
      // ### rotate
      //
      //     .css({ rotate: 30 })
      //     .css({ rotate: "30" })
      //     .css({ rotate: "30deg" })
      //     .css({ rotate: "30deg" })
      //
      rotate: function(theta) {
        this.rotate = unit(theta, 'deg');
      },

      rotateX: function(theta) {
        this.rotateX = unit(theta, 'deg');
      },

      rotateY: function(theta) {
        this.rotateY = unit(theta, 'deg');
      },

      // ### scale
      //
      //     .css({ scale: 9 })      //=> "scale(9,9)"
      //     .css({ scale: '3,2' })  //=> "scale(3,2)"
      //
      scale: function(x, y) {
        if (y === undefined) { y = x; }
        this.scale = x + "," + y;
      },

      // ### skewX + skewY
      skewX: function(x) {
        this.skewX = unit(x, 'deg');
      },

      skewY: function(y) {
        this.skewY = unit(y, 'deg');
      },

      // ### perspectvie
      perspective: function(dist) {
        this.perspective = unit(dist, 'px');
      },

      // ### x / y
      // Translations. Notice how this keeps the other value.
      //
      //     .css({ x: 4 })       //=> "translate(4px, 0)"
      //     .css({ y: 10 })      //=> "translate(4px, 10px)"
      //
      x: function(x) {
        this.set('translate', x, null);
      },

      y: function(y) {
        this.set('translate', null, y);
      },

      // ### translate
      // Notice how this keeps the other value.
      //
      //     .css({ translate: '2, 5' })    //=> "translate(2px, 5px)"
      //
      translate: function(x, y) {
        if (this._translateX === undefined) { this._translateX = 0; }
        if (this._translateY === undefined) { this._translateY = 0; }

        if (x !== null && x !== undefined) { this._translateX = unit(x, 'px'); }
        if (y !== null && y !== undefined) { this._translateY = unit(y, 'px'); }

        this.translate = this._translateX + "," + this._translateY;
      }
    },

    getter: {
      x: function() {
        return this._translateX || 0;
      },

      y: function() {
        return this._translateY || 0;
      },

      scale: function() {
        var s = (this.scale || "1,1").split(',');
        if (s[0]) { s[0] = parseFloat(s[0]); }
        if (s[1]) { s[1] = parseFloat(s[1]); }

        // "2.5,2.5" => 2.5
        // "2.5,1" => [2.5,1]
        return (s[0] === s[1]) ? s[0] : s;
      },

      rotate3d: function() {
        var s = (this.rotate3d || "0,0,0,0deg").split(',');
        for (var i=0; i<=3; ++i) {
          if (s[i]) { s[i] = parseFloat(s[i]); }
        }
        if (s[3]) { s[3] = unit(s[3], 'deg'); }

        return s;
      }
    },

    // ### parse()
    // Parses from a string. Called on constructor.
    parse: function(str) {
      var self = this;
      str.replace(/([a-zA-Z0-9]+)\((.*?)\)/g, function(x, prop, val) {
        self.setFromString(prop, val);
      });
    },

    // ### toString()
    // Converts to a `transition` CSS property string. If `use3d` is given,
    // it converts to a `-webkit-transition` CSS property string instead.
    toString: function(use3d) {
      var re = [];

      for (var i in this) {
        if (this.hasOwnProperty(i)) {
          // Don't use 3D transformations if the browser can't support it.
          if ((!support.transform3d) && (
            (i === 'rotateX') ||
            (i === 'rotateY') ||
            (i === 'perspective') ||
            (i === 'transformOrigin'))) { continue; }

          if (i[0] !== '_') {
            if (use3d && (i === 'scale')) {
              re.push(i + "3d(" + this[i] + ",1)");
            } else if (use3d && (i === 'translate')) {
              re.push(i + "3d(" + this[i] + ",0)");
            } else {
              re.push(i + "(" + this[i] + ")");
            }
          }
        }
      }

      return re.join(" ");
    }
  };

  function callOrQueue(self, queue, fn) {
    if (queue === true) {
      self.queue(fn);
    } else if (queue) {
      self.queue(queue, fn);
    } else {
      self.each(function () {
                fn.call(this);
            });
    }
  }

  // ### getProperties(dict)
  // Returns properties (for `transition-property`) for dictionary `props`. The
  // value of `props` is what you would expect in `$.css(...)`.
  function getProperties(props) {
    var re = [];

    $.each(props, function(key) {
      key = $.camelCase(key); // Convert "text-align" => "textAlign"
      key = $.transit.propertyMap[key] || $.cssProps[key] || key;
      key = uncamel(key); // Convert back to dasherized

      // Get vendor specify propertie
      if (support[key])
        key = uncamel(support[key]);

      if ($.inArray(key, re) === -1) { re.push(key); }
    });

    return re;
  }

  // ### getTransition()
  // Returns the transition string to be used for the `transition` CSS property.
  //
  // Example:
  //
  //     getTransition({ opacity: 1, rotate: 30 }, 500, 'ease');
  //     //=> 'opacity 500ms ease, -webkit-transform 500ms ease'
  //
  function getTransition(properties, duration, easing, delay) {
    // Get the CSS properties needed.
    var props = getProperties(properties);

    // Account for aliases (`in` => `ease-in`).
    if ($.cssEase[easing]) { easing = $.cssEase[easing]; }

    // Build the duration/easing/delay attributes for it.
    var attribs = '' + toMS(duration) + ' ' + easing;
    if (parseInt(delay, 10) > 0) { attribs += ' ' + toMS(delay); }

    // For more properties, add them this way:
    // "margin 200ms ease, padding 200ms ease, ..."
    var transitions = [];
    $.each(props, function(i, name) {
      transitions.push(name + ' ' + attribs);
    });

    return transitions.join(', ');
  }

  // ## $.fn.transition
  // Works like $.fn.animate(), but uses CSS transitions.
  //
  //     $("...").transition({ opacity: 0.1, scale: 0.3 });
  //
  //     // Specific duration
  //     $("...").transition({ opacity: 0.1, scale: 0.3 }, 500);
  //
  //     // With duration and easing
  //     $("...").transition({ opacity: 0.1, scale: 0.3 }, 500, 'in');
  //
  //     // With callback
  //     $("...").transition({ opacity: 0.1, scale: 0.3 }, function() { ... });
  //
  //     // With everything
  //     $("...").transition({ opacity: 0.1, scale: 0.3 }, 500, 'in', function() { ... });
  //
  //     // Alternate syntax
  //     $("...").transition({
  //       opacity: 0.1,
  //       duration: 200,
  //       delay: 40,
  //       easing: 'in',
  //       complete: function() { /* ... */ }
  //      });
  //
  $.fn.transition = $.fn.transit = function(properties, duration, easing, callback) {
    var self  = this;
    var delay = 0;
    var queue = true;

    var theseProperties = $.extend(true, {}, properties);

    // Account for `.transition(properties, callback)`.
    if (typeof duration === 'function') {
      callback = duration;
      duration = undefined;
    }

    // Account for `.transition(properties, options)`.
    if (typeof duration === 'object') {
      easing = duration.easing;
      delay = duration.delay || 0;
      queue = typeof duration.queue === "undefined" ? true : duration.queue;
      callback = duration.complete;
      duration = duration.duration;
    }

    // Account for `.transition(properties, duration, callback)`.
    if (typeof easing === 'function') {
      callback = easing;
      easing = undefined;
    }

    // Alternate syntax.
    if (typeof theseProperties.easing !== 'undefined') {
      easing = theseProperties.easing;
      delete theseProperties.easing;
    }

    if (typeof theseProperties.duration !== 'undefined') {
      duration = theseProperties.duration;
      delete theseProperties.duration;
    }

    if (typeof theseProperties.complete !== 'undefined') {
      callback = theseProperties.complete;
      delete theseProperties.complete;
    }

    if (typeof theseProperties.queue !== 'undefined') {
      queue = theseProperties.queue;
      delete theseProperties.queue;
    }

    if (typeof theseProperties.delay !== 'undefined') {
      delay = theseProperties.delay;
      delete theseProperties.delay;
    }

    // Set defaults. (`400` duration, `ease` easing)
    if (typeof duration === 'undefined') { duration = $.fx.speeds._default; }
    if (typeof easing === 'undefined')   { easing = $.cssEase._default; }

    duration = toMS(duration);

    // Build the `transition` property.
    var transitionValue = getTransition(theseProperties, duration, easing, delay);

    // Compute delay until callback.
    // If this becomes 0, don't bother setting the transition property.
    var work = $.transit.enabled && support.transition;
    var i = work ? (parseInt(duration, 10) + parseInt(delay, 10)) : 0;

    // If there's nothing to do...
    if (i === 0) {
      var fn = function(next) {
        self.css(theseProperties);
        if (callback) { callback.apply(self); }
        if (next) { next(); }
      };

      callOrQueue(self, queue, fn);
      return self;
    }

    // Save the old transitions of each element so we can restore it later.
    var oldTransitions = {};

    var run = function(nextCall) {
      var bound = false;

      // Prepare the callback.
      var cb = function() {
        if (bound) { self.unbind(transitionEnd, cb); }

        if (i > 0) {
          self.each(function() {
            this.style[support.transition] = (oldTransitions[this] || null);
          });
        }

        if (typeof callback === 'function') { callback.apply(self); }
        if (typeof nextCall === 'function') { nextCall(); }
      };

      if ((i > 0) && (transitionEnd) && ($.transit.useTransitionEnd)) {
        // Use the 'transitionend' event if it's available.
        bound = true;
        self.bind(transitionEnd, cb);
      } else {
        // Fallback to timers if the 'transitionend' event isn't supported.
        window.setTimeout(cb, i);
      }

      // Apply transitions.
      self.each(function() {
        if (i > 0) {
          this.style[support.transition] = transitionValue;
        }
        $(this).css(theseProperties);
      });
    };

    // Defer running. This allows the browser to paint any pending CSS it hasn't
    // painted yet before doing the transitions.
    var deferredRun = function(next) {
        this.offsetWidth; // force a repaint
        run(next);
    };

    // Use jQuery's fx queue.
    callOrQueue(self, queue, deferredRun);

    // Chainability.
    return this;
  };

  function registerCssHook(prop, isPixels) {
    // For certain properties, the 'px' should not be implied.
    if (!isPixels) { $.cssNumber[prop] = true; }

    $.transit.propertyMap[prop] = support.transform;

    $.cssHooks[prop] = {
      get: function(elem) {
        var t = $(elem).css('transit:transform');
        return t.get(prop);
      },

      set: function(elem, value) {
        var t = $(elem).css('transit:transform');
        t.setFromString(prop, value);

        $(elem).css({ 'transit:transform': t });
      }
    };

  }

  // ### uncamel(str)
  // Converts a camelcase string to a dasherized string.
  // (`marginLeft` => `margin-left`)
  function uncamel(str) {
    return str.replace(/([A-Z])/g, function(letter) { return '-' + letter.toLowerCase(); });
  }

  // ### unit(number, unit)
  // Ensures that number `number` has a unit. If no unit is found, assume the
  // default is `unit`.
  //
  //     unit(2, 'px')          //=> "2px"
  //     unit("30deg", 'rad')   //=> "30deg"
  //
  function unit(i, units) {
    if ((typeof i === "string") && (!i.match(/^[\-0-9\.]+$/))) {
      return i;
    } else {
      return "" + i + units;
    }
  }

  // ### toMS(duration)
  // Converts given `duration` to a millisecond string.
  //
  // toMS('fast') => $.fx.speeds[i] => "200ms"
  // toMS('normal') //=> $.fx.speeds._default => "400ms"
  // toMS(10) //=> '10ms'
  // toMS('100ms') //=> '100ms'  
  //
  function toMS(duration) {
    var i = duration;

    // Allow string durations like 'fast' and 'slow', without overriding numeric values.
    if (typeof i === 'string' && (!i.match(/^[\-0-9\.]+/))) { i = $.fx.speeds[i] || $.fx.speeds._default; }

    return unit(i, 'ms');
  }

  // Export some functions for testable-ness.
  $.transit.getTransitionValue = getTransition;

  return $;
}));

(function(root) {
define("jquery-throttle-debounce", ["jquery"], function() {
  return (function() {
/*!
 * jQuery throttle / debounce - v1.1 - 3/7/2010
 * http://benalman.com/projects/jquery-throttle-debounce-plugin/
 * 
 * Copyright (c) 2010 "Cowboy" Ben Alman
 * Dual licensed under the MIT and GPL licenses.
 * http://benalman.com/about/license/
 */

// Script: jQuery throttle / debounce: Sometimes, less is more!
//
// *Version: 1.1, Last updated: 3/7/2010*
// 
// Project Home - http://benalman.com/projects/jquery-throttle-debounce-plugin/
// GitHub       - http://github.com/cowboy/jquery-throttle-debounce/
// Source       - http://github.com/cowboy/jquery-throttle-debounce/raw/master/jquery.ba-throttle-debounce.js
// (Minified)   - http://github.com/cowboy/jquery-throttle-debounce/raw/master/jquery.ba-throttle-debounce.min.js (0.7kb)
// 
// About: License
// 
// Copyright (c) 2010 "Cowboy" Ben Alman,
// Dual licensed under the MIT and GPL licenses.
// http://benalman.com/about/license/
// 
// About: Examples
// 
// These working examples, complete with fully commented code, illustrate a few
// ways in which this plugin can be used.
// 
// Throttle - http://benalman.com/code/projects/jquery-throttle-debounce/examples/throttle/
// Debounce - http://benalman.com/code/projects/jquery-throttle-debounce/examples/debounce/
// 
// About: Support and Testing
// 
// Information about what version or versions of jQuery this plugin has been
// tested with, what browsers it has been tested in, and where the unit tests
// reside (so you can test it yourself).
// 
// jQuery Versions - none, 1.3.2, 1.4.2
// Browsers Tested - Internet Explorer 6-8, Firefox 2-3.6, Safari 3-4, Chrome 4-5, Opera 9.6-10.1.
// Unit Tests      - http://benalman.com/code/projects/jquery-throttle-debounce/unit/
// 
// About: Release History
// 
// 1.1 - (3/7/2010) Fixed a bug in <jQuery.throttle> where trailing callbacks
//       executed later than they should. Reworked a fair amount of internal
//       logic as well.
// 1.0 - (3/6/2010) Initial release as a stand-alone project. Migrated over
//       from jquery-misc repo v0.4 to jquery-throttle repo v1.0, added the
//       no_trailing throttle parameter and debounce functionality.
// 
// Topic: Note for non-jQuery users
// 
// jQuery isn't actually required for this plugin, because nothing internal
// uses any jQuery methods or properties. jQuery is just used as a namespace
// under which these methods can exist.
// 
// Since jQuery isn't actually required for this plugin, if jQuery doesn't exist
// when this plugin is loaded, the method described below will be created in
// the `Cowboy` namespace. Usage will be exactly the same, but instead of
// $.method() or jQuery.method(), you'll need to use Cowboy.method().

(function(window,undefined){
  '$:nomunge'; // Used by YUI compressor.
  
  // Since jQuery really isn't required for this plugin, use `jQuery` as the
  // namespace only if it already exists, otherwise use the `Cowboy` namespace,
  // creating it if necessary.
  var $ = window.jQuery || window.Cowboy || ( window.Cowboy = {} ),
    
    // Internal method reference.
    jq_throttle;
  
  // Method: jQuery.throttle
  // 
  // Throttle execution of a function. Especially useful for rate limiting
  // execution of handlers on events like resize and scroll. If you want to
  // rate-limit execution of a function to a single time, see the
  // <jQuery.debounce> method.
  // 
  // In this visualization, | is a throttled-function call and X is the actual
  // callback execution:
  // 
  // > Throttled with `no_trailing` specified as false or unspecified:
  // > ||||||||||||||||||||||||| (pause) |||||||||||||||||||||||||
  // > X    X    X    X    X    X        X    X    X    X    X    X
  // > 
  // > Throttled with `no_trailing` specified as true:
  // > ||||||||||||||||||||||||| (pause) |||||||||||||||||||||||||
  // > X    X    X    X    X             X    X    X    X    X
  // 
  // Usage:
  // 
  // > var throttled = jQuery.throttle( delay, [ no_trailing, ] callback );
  // > 
  // > jQuery('selector').bind( 'someevent', throttled );
  // > jQuery('selector').unbind( 'someevent', throttled );
  // 
  // This also works in jQuery 1.4+:
  // 
  // > jQuery('selector').bind( 'someevent', jQuery.throttle( delay, [ no_trailing, ] callback ) );
  // > jQuery('selector').unbind( 'someevent', callback );
  // 
  // Arguments:
  // 
  //  delay - (Number) A zero-or-greater delay in milliseconds. For event
  //    callbacks, values around 100 or 250 (or even higher) are most useful.
  //  no_trailing - (Boolean) Optional, defaults to false. If no_trailing is
  //    true, callback will only execute every `delay` milliseconds while the
  //    throttled-function is being called. If no_trailing is false or
  //    unspecified, callback will be executed one final time after the last
  //    throttled-function call. (After the throttled-function has not been
  //    called for `delay` milliseconds, the internal counter is reset)
  //  callback - (Function) A function to be executed after delay milliseconds.
  //    The `this` context and all arguments are passed through, as-is, to
  //    `callback` when the throttled-function is executed.
  // 
  // Returns:
  // 
  //  (Function) A new, throttled, function.
  
  $.throttle = jq_throttle = function( delay, no_trailing, callback, debounce_mode ) {
    // After wrapper has stopped being called, this timeout ensures that
    // `callback` is executed at the proper times in `throttle` and `end`
    // debounce modes.
    var timeout_id,
      
      // Keep track of the last time `callback` was executed.
      last_exec = 0;
    
    // `no_trailing` defaults to falsy.
    if ( typeof no_trailing !== 'boolean' ) {
      debounce_mode = callback;
      callback = no_trailing;
      no_trailing = undefined;
    }
    
    // The `wrapper` function encapsulates all of the throttling / debouncing
    // functionality and when executed will limit the rate at which `callback`
    // is executed.
    function wrapper() {
      var that = this,
        elapsed = +new Date() - last_exec,
        args = arguments;
      
      // Execute `callback` and update the `last_exec` timestamp.
      function exec() {
        last_exec = +new Date();
        callback.apply( that, args );
      };
      
      // If `debounce_mode` is true (at_begin) this is used to clear the flag
      // to allow future `callback` executions.
      function clear() {
        timeout_id = undefined;
      };
      
      if ( debounce_mode && !timeout_id ) {
        // Since `wrapper` is being called for the first time and
        // `debounce_mode` is true (at_begin), execute `callback`.
        exec();
      }
      
      // Clear any existing timeout.
      timeout_id && clearTimeout( timeout_id );
      
      if ( debounce_mode === undefined && elapsed > delay ) {
        // In throttle mode, if `delay` time has been exceeded, execute
        // `callback`.
        exec();
        
      } else if ( no_trailing !== true ) {
        // In trailing throttle mode, since `delay` time has not been
        // exceeded, schedule `callback` to execute `delay` ms after most
        // recent execution.
        // 
        // If `debounce_mode` is true (at_begin), schedule `clear` to execute
        // after `delay` ms.
        // 
        // If `debounce_mode` is false (at end), schedule `callback` to
        // execute after `delay` ms.
        timeout_id = setTimeout( debounce_mode ? clear : exec, debounce_mode === undefined ? delay - elapsed : delay );
      }
    };
    
    // Set the guid of `wrapper` function to the same of original callback, so
    // it can be removed in jQuery 1.4+ .unbind or .die by using the original
    // callback as a reference.
    if ( $.guid ) {
      wrapper.guid = callback.guid = callback.guid || $.guid++;
    }
    
    // Return the wrapper function.
    return wrapper;
  };
  
  // Method: jQuery.debounce
  // 
  // Debounce execution of a function. Debouncing, unlike throttling,
  // guarantees that a function is only executed a single time, either at the
  // very beginning of a series of calls, or at the very end. If you want to
  // simply rate-limit execution of a function, see the <jQuery.throttle>
  // method.
  // 
  // In this visualization, | is a debounced-function call and X is the actual
  // callback execution:
  // 
  // > Debounced with `at_begin` specified as false or unspecified:
  // > ||||||||||||||||||||||||| (pause) |||||||||||||||||||||||||
  // >                          X                                 X
  // > 
  // > Debounced with `at_begin` specified as true:
  // > ||||||||||||||||||||||||| (pause) |||||||||||||||||||||||||
  // > X                                 X
  // 
  // Usage:
  // 
  // > var debounced = jQuery.debounce( delay, [ at_begin, ] callback );
  // > 
  // > jQuery('selector').bind( 'someevent', debounced );
  // > jQuery('selector').unbind( 'someevent', debounced );
  // 
  // This also works in jQuery 1.4+:
  // 
  // > jQuery('selector').bind( 'someevent', jQuery.debounce( delay, [ at_begin, ] callback ) );
  // > jQuery('selector').unbind( 'someevent', callback );
  // 
  // Arguments:
  // 
  //  delay - (Number) A zero-or-greater delay in milliseconds. For event
  //    callbacks, values around 100 or 250 (or even higher) are most useful.
  //  at_begin - (Boolean) Optional, defaults to false. If at_begin is false or
  //    unspecified, callback will only be executed `delay` milliseconds after
  //    the last debounced-function call. If at_begin is true, callback will be
  //    executed only at the first debounced-function call. (After the
  //    throttled-function has not been called for `delay` milliseconds, the
  //    internal counter is reset)
  //  callback - (Function) A function to be executed after delay milliseconds.
  //    The `this` context and all arguments are passed through, as-is, to
  //    `callback` when the debounced-function is executed.
  // 
  // Returns:
  // 
  //  (Function) A new, debounced, function.
  
  $.debounce = function( delay, at_begin, callback ) {
    return callback === undefined
      ? jq_throttle( delay, at_begin, false )
      : jq_throttle( delay, callback, at_begin !== false );
  };
  
})(this);


  }).apply(root, arguments);
});
}(this));

(function(root) {
define("cartware-slider", ["jquery","jquery-transit","jquery-throttle-debounce"], function() {
  return (function() {
!(function(factory) {
	if (typeof define === 'function' && define.amd) {
		require([
			'jquery'
		], factory);
	} else {
		factory(jQuery);
	}
})(function($) {
	if ($ === undefined) {
		throw new Error('jQuery is required to run this plug-in');
	}

	var animateAttributes = {
		left: 'left'
	};

	if ('transit' in $ && 'undefined' !== typeof Modernizr && Modernizr.csstransitions) {
		$.extend(animateAttributes, {
			left: 'x'
		});
	}

	function throttle (callback, limit, fireOnEnd) {
		fireOnEnd = ('undefined' === typeof fireOnEnd) ? true : !!fireOnEnd;

		var wait = false;

		return function () {
			if (!wait) {
				callback.call();
				wait = true;
				setTimeout(function () {
					wait = false;

					if (fireOnEnd) {
						callback.call();
					}
				}, limit);
			}
		};
	}

	function _t(string) {
		if ('undefined' !== typeof Translator && 'function' === typeof Translator.translate) {
			return Translator.translate(string);
		}

		return string;
	}

	var plugins = {

		autoResize: {
			construct: function() {
				this.settings.slidesToShow = 1;
				//
				// var _fitColsInWrap = this._fitColsInWrap;
				//
				// this._fitColsInWrap = function() {
				// 	var originalSlidesToShow = this.settings.slidesToShow;
				// 	this.settings.slidesToShow = 1;
				// 	var result = _fitColsInWrap.apply(this, arguments);
				// 	this.settings.slidesToShow = originalSlidesToShow;
				//
				// 	return result;
				// }
			},

			initialized: function() {
				// var slideshow = this;
				//
				// if ('slidesToShow' in slideshow.settings && slideshow.settings.slidesToShow > 1) {
				// 	if ('console' in window && 'function' === typeof console.warn) {
				// 		console.warn('Cartware slider: Using plugin \'autoResize\' although option \'slidesToShow\' is set. Skipping plugin.');
				// 	}
				//
				// 	return;
				// }
				//
				// /**
				//  * Set image width to current viewport width
				//  */
				// function stretchContent() {
				// 	var viewportWidth = slideshow.wrapper.width();
				//
				// 	slideshow.items.each(function() {
				// 		var slide = $(this);
				//
				// 		slide.width(viewportWidth);
				// 		$('img[data-resize]', slide).width(viewportWidth);
				// 	});
				//
				// 	slideshow.reflow();
				// }
				//
				// $(window).resize(('throttle' in $) ? $.throttle(150, stretchContent) : stretchContent);
				//
				// stretchContent();
			}
		},

		productMediaGallery: function(slideshow) {
			var productImage = $('#product-image');

			if (!productImage.length) {
				return;
			}

			slideshow.on('slide_click', function(e, slide) {
				var dataNode = $(slide).find('[data-detail-src]'),
					detailSrc = dataNode.data('detail-src'),
					img = new Image(),
					promise = $.Deferred();

				productImage.animate({
					opacity: 0
				}, {
					complete: function() {
						promise.resolve();
					}
				});

				img.onload = function() {
					promise.done(function() {
						productImage.attr('src', detailSrc);
						productImage.animate({
							opacity: 1
						});
					});
				};

				img.src = detailSrc;
			});
		},

		thumbnails: function(slideshow) {
			var itemTmpl = [
				'<li class="cartware-slider-item-wrap">',
				'<div class="cartware-slider-slide-item cartware-slider-slide-item-image row-1" data-slide-id="{slideId}">',
				'<div class="cartware-slider-slide-item-innerwrap">',

				'</div>',
				'</div>',
				'</li>'
			].join('');

			var items = [];

			$.each(slideshow.items, function(i) {
				var origItem = $(this),
					slideId = origItem.data('slide-id'),
					thumbnail = origItem.data('thumbnail') ?
						$('<img src="' + origItem.data('thumbnail') + '" alt="" />') :
						origItem.find('img:first').clone(),
					item = $(itemTmpl.replace('{slideId}', slideId));

				if (i === 0) {
					item.addClass('first');
				} else if (i === slideshow.items.length - 1) {
					item.addClass('last');
				}

				item.find('.cartware-slider-slide-item-innerwrap').append(thumbnail);
				items.push(item);
			});


			var thumbnailContainer = $([
					'<div class="cartware-slider thumbnails">',
					'<div class="cartware-slider-wrap">',
					'<div class="cartware-slider-stage">',
					'<ul class="cartware-slider-list cartware-slider-list-rows-1">',

					'</ul>',
					'</div>',
					'</div>',
					'</div>'
				].join('')),
				thumbnailSlider,
				list = thumbnailContainer.find('ul');

			$.each(items, function() {
				list.append(this);
			});

			thumbnailContainer.appendTo(slideshow.container);
			thumbnailContainer.cartwareSlider();
			thumbnailSlider = thumbnailContainer.data('cartware_slider/instance');

			slideshow.on('slide_after ready', function() {
				var activeItem = slideshow.activeItem,
					slideId = activeItem.find('[data-slide-id]').data('slide-id'),
					targetThumbnail = thumbnailContainer.find('[data-slide-id="' + slideId + '"]'),
					thumbApi = thumbnailContainer.data('cartware_slider');

				thumbApi.slideTo(targetThumbnail);
			});

			thumbnailSlider.on('slide_after', function() {
				thumbnailSlider.items.removeClass('active');
				thumbnailSlider.activeItem.addClass('active');
			});

			thumbnailSlider.items.on('click', function() {
				var item = $(this),
					slideId = item.data('slide-id'),
					targetSlide = slideshow.items.filter(function() {
						return $(this).data('slide-id') == slideId
					}),
					targetCol = targetSlide.parents('.cartware-slider-item-wrap:first');

				slideshow.slideTo(targetCol);
			});
		},

		swipe: function(slideshow) {
			var container = slideshow.container,
				wrapper = slideshow.wrapper,
				stage = slideshow.stage,
				$window = $(window),
				originalMode = slideshow.settings.mode,
				originalCarousel = slideshow.settings.carousel,
				isScroll = false,
				state = {
					isSwiping: false
				};

			var batches = slideshow.getBatches();

			slideshow.on('reflow', function() {
				batches = slideshow.getBatches();
			});

			container.data('cartware_slider/plugin/swipe/state', state);

			function setSwiping(flag) {
				state.isSwiping = flag;
			}

			function getCoordinates(event) {
				var originalEvent = event.originalEvent;

				if ('touches' in originalEvent) {
					event = originalEvent.touches[0];
				}

				return {
					x: event.pageX,
					y: event.pageY
				};
			}

			setSwiping(false);

			stage.on('click', function onClick(e) {
				if (state.isSwiping) {
					e.preventDefault();
					setSwiping(false);
				}
			});

			stage.on('dragstart', function(e) {
				if (batches.length < 2) {
					return;
				}

				e.preventDefault();
			});

			stage.on('touchstart mousedown', function(mouseDownEvent) {
				if (batches.length < 2) {
					return;
				}

				// Skip multi-touch gestures
				if ('touches' in mouseDownEvent.originalEvent && mouseDownEvent.originalEvent.touches.length > 1) {
					return;
				}

				var coordinates = getCoordinates(mouseDownEvent),
					originX = coordinates.x,
					originY = coordinates.y,
					deltaX = 0,
					deltaY = 0,
					originLeft = parseInt(stage.css(animateAttributes.left)),
					startTime = new Date().getTime();

				setSwiping(false);
				isScroll = false;

				function onMouseMove(mouseMoveEvent) {
					// Skip multi-touch gestures
					if ('touches' in mouseMoveEvent.originalEvent && mouseMoveEvent.originalEvent.touches.length > 1) {
						return;
					}

					var moveCoordinates = getCoordinates(mouseMoveEvent);

					deltaX = moveCoordinates.x - originX;
					deltaY = moveCoordinates.y - originY;

					if (!isScroll && !state.isSwiping && Math.abs(deltaX) > 10) {
						setSwiping(true);
					}

					if (!state.isSwiping && !isScroll && mouseMoveEvent.type === 'touchmove' && Math.abs(deltaY) > 20) {
						isScroll = true;
					}

					if (state.isSwiping && !isScroll) {
						stage.stop();
						stage.css(animateAttributes.left, originLeft + deltaX);
						mouseDownEvent.preventDefault();
						mouseMoveEvent.preventDefault();
					}
				}

				$window.on('touchmove mousemove', onMouseMove);

				$window.on('touchend mouseup', function onMouseUp() {
					$window.off('touchmove mousemove', onMouseMove);
					$window.off('touchend mouseup', onMouseUp);

					if (!state.isSwiping || isScroll) {
						return;
					}

					var currentTime = new Date().getTime(),
						deltaTime = currentTime - startTime,
						velocity = Math.abs(deltaX / deltaTime) || 0,
						isGesture = velocity <= 0.7;

					// slideshow.settings.mode = "columns";
					slideshow.settings.carousel = false;

					if (deltaX > 0) {
						slideshow.previous();
					} else if (deltaX < 0) {
						slideshow.next();
					}

					slideshow.settings.carousel = originalCarousel;
					// slideshow.settings.mode = originalMode;
				});
			});
		},

		columnOpacity: function(slideshow) {
			var container	= slideshow.container,
				wrapper		= slideshow.wrapper,
				stage		= slideshow.stage,
				cols		= slideshow.cols,
				items		= slideshow.items,
				minimumOpacity = 20;

			function fadeColumns() {
				var center = wrapper.width() / 2;

				cols.each(function() {
					var col = $(this),
						colItems = col.find('.cartware-slider-slide-item'),
						colCenter = col.position().left + col.width() / 2,
						opacity = Math.max(0, 1 - Math.abs(center - parseInt(stage.css('left'), 10) - colCenter) / center);

					colItems.animate({
						opacity: opacity + (minimumOpacity / 100 * (1 - opacity))
					});
				});
			}

			items.each(function() {
				var item = $(this),
					currentOpacity = null
					;

				item.hover(function() {
					currentOpacity = item.css('opacity');
					item.css({
						opacity: 1
					});
				}, function() {
					if (currentOpacity !== null) {
						item.css({
							opacity: currentOpacity
						});
					}
				});
			});

			slideshow.on('slide_after', fadeColumns);
			slideshow.on('slide_init', fadeColumns);
		},

		urlAnchor: function (slideshow) {
			if (!document.location.hash) {
				return;
			}

			//slideshow.on('ready', function() {
			var hash = document.location.hash.substr(1),
				marker = 'slide-',
				slideId = hash.substr(hash.indexOf(marker) + marker.length);

			slideshow.items.each(function() {
				var item = $(this), col;
				if (item.data('slide-id') == slideId) {
					col = item.parent();
					slideshow.slideTo(slideshow.cols.index(col), true);
					return false;
				}
			});
			//});
		}
	};

	var defaults = {
		settings: {
			autoslide: false,
			autoslide_interval: 5500,
			timeout: 1500,
			mode: 'columns', // options: columns, batches
			easing: ('easeOutBack' in $.easing) && ('ontouchstart' in document.documentElement) ? 'easeOutBack' : 'swing',
			speed: 650,
			carousel: false,
			shuffle: false,
			animateLayout: false,
			display: {
				nonFilledArrangement: 'center'
			},
			modesettings: {
				columns: {
					slidesPerMove: 1
				},

				batches: {}
			},
			controls: false,
			controlsettings: {
				handles: false,
				handleposition: 'bottom-center'
			},
			arrows: {
				position: 'middle'
			},
			thumbs: {
				display: false
			},
			plugins: ['swipe'],
			respondTo: 'window',
			labels: {
				controls: {
					previous: _t('Previous'),
					next: _t('Next')
				}
			}
		}
	};

	function ImagePool() {
		this._warm();
	}

	ImagePool.prototype = {
		_used: [],
		_free: [],
		_gcInterval: null,

		_warm: function() {
			var img;
			for (var i = 0; i < 10; i++) {
				img = new Image();
				img._recycleCount = 0;
				img.src = '';

				this._free.push(img);
			}

			if (!this._gcInterval) {
				this._initGc();
			}
		},

		_initGc: function() {
			var self = this,
				callback = function() {
					self._gc.call(self);
				};

			if (!this._gcInterval) {
				this._gcInterval = setInterval(callback, 3000);
			}
		},

		_pauseGc: function() {
			if (this._gcInterval) {
				clearTimeout(this._gcInterval);
				this._gcInterval = null;
			}
		},

		_gc: function() {
			for (var i = 0, l = this._free.length; i < l; i++) {
				this._resetObject(this._free[i]);
			}

			this._free = [];

			if (!this._used.length) {
				this._pauseGc();
			}
		},

		_resetObject: function(img) {
			img.onload = null;
			img.onerror = null;
			img.src = '';

			if ('TRANSPORT' in img) {
				img.TRANSPORT = null;
			}

			img._recycleCount++;

			return img;
		},

		alloc: function() {
			var img;

			if (!this._free.length) {
				this._warm();
			}

			img = this._free.pop();
			this._used.push(img);

			return img;
		},

		release: function(img) {
			var index = this._used.indexOf(this._resetObject(img));
			if (index >= 0) {
				this._free.push(this._used.splice(index, 1)[0]);
			}
		}
	};

	var GLOBAL_IMAGE_POOL = new ImagePool();

	function Slider(container, settings) {
		this._imagePool			= GLOBAL_IMAGE_POOL;
		this.container			= container;
		this.wrapper			= this.container.find('.cartware-slider-wrap');
		this.stage				= this.wrapper.find('.cartware-slider-stage');
		this.list				= this.stage.find('.cartware-slider-list');
		this.cols				= this.list.find('> .cartware-slider-item-wrap');
		this.items				= this.cols.find('.cartware-slider-slide-item');
		this.images				= this.items.find('img').not('.mobile-slide-image');
		this.thumbsContainer	= null;
		this.thumbsWrap			= null;
		this.activeItem			= this.cols.first();
		this.slideInterval		= null;
		this.slideIntervalMs    = parseInt(settings.autoslide_interval || this.slideIntervalMs);
		this.autoslidePaused    = false;
		this.settings			= $.extend({}, defaults.settings, settings);
		this.originalSettings	= $.extend({}, this.settings);
		this.body				= $('body');
		this.reflowTimeout		= null;
		this.initialized		= false;
		this.events				= {
			ready: []
		};
		this.currentBreakpoint	= null;
		this.defaultResponsive	= null;
		this.mobileFirst		= false;
		this.lastWrapperWidth	= null;
		this.lazyloadAttribute	= 'lazy';

		this.lazyloadPendingClass	= 'pending';
		this.lazyloadLoadedClass	= 'loaded';

		if (this.settings.plugins.indexOf('swipe') === -1) {
			this.settings.plugins.push('swipe');
		}

		this.settings.modesettings.columns.slidesPerMove = Math.max(1, parseInt(this.settings.modesettings.columns.slidesPerMove, 10));
		this.container.data('cartware_slider/settings', this.settings);


		// Export API
		var api = {
			slideTo:			$.proxy(this.slideTo, this),
			next:				$.proxy(this.next, this),
			previous:			$.proxy(this.previous, this),
			calibrate:			$.proxy(this.reflow, this),
			getVisibleItems:	$.proxy(this.getVisibleItems, this),
			on:					$.proxy(this.on, this),
			one:				$.proxy(this.one, this),
			off:				$.proxy(this.off, this),
			trigger:			$.proxy(this.trigger, this)
		};

		this.container.data('cartware_slider', api);

		this.callPluginMethod('construct');

		this._init();
	}

	Slider.prototype = {
		MODE_COLUMNS: 'columns',
		MODE_BATCHES: 'batches',
		MODE_CENTER: 'center',

		_init: function() {
			// Preload images
			var self = this,
				imagesToLoad = self.images.length,
				imagesLoaded = 0;

			if (0 === imagesToLoad) {
				return this.init();
			}

			this.container.addClass('loading');

			function iterate() {
				if (++imagesLoaded === imagesToLoad) {
					self.init();
				}
			}

			function onload() {
				self._imagePool.release(this);
				iterate();
			}

			function onerror() {
				$(this).remove();
				onload.call(this);
			}

			this.images.each(function() {
				var tmpImage,
					img = $(this);

				if (this.width > 1 && this.height > 1 && this.width !== this.height) {
					return iterate();
				}

				tmpImage = self._imagePool.alloc();
				tmpImage.onload = onload;
				tmpImage.onerror = onerror;
				tmpImage.src = img.data('original') || img.attr('src');
			});
		},

		init: function() {
			var self = this,
				preloadSlides = 1;

			if ('undefined' !== typeof this.settings.slidesToShow) {
				preloadSlides = this.settings.slidesToShow;
			}

			var lazyImages = this.items.slice(0, preloadSlides)
				.find('img')
				.filter(function() {
					return $(this).data(self.lazyloadAttribute);
				});

			var init = $.proxy(function() {
				this.initializeResponsive();
				this.initializeLayout();

				/*
				 stage.fadeOut(0);
				 stage.fadeIn();
				 */

				this.initializeEvents();

				if (this.settings.controls)	{
					this.initializeControls();
				}
				if (this.settings.controlsettings.handles) {
					this.initializeHandles();
				}
				if (this.settings.autoslide) {
					this.initializeInterval();
				}

				this.initializePlugins();

				this.container.trigger('cartware_slider/slide_init');
				this.container.removeClass('loading');

				this.initialized = true;
				this.trigger('ready');
			}, this);

			if (lazyImages.length) {
				this._lazyLoadImages(lazyImages, init);
			} else {
				init();
			}
		},

		on: function(eventString, cb) {
			var eventSplit = eventString.split(' '), i, j, event;

			for (i = 0, j = eventSplit.length; i < j; i++) {
				event = eventSplit[i];

				if (event === 'ready' && this.initialized) {
					cb.apply(this, [$.Event('ready')]);
					continue;
				}

				if (!(event in this.events)) {
					this.events[event] = [];
				}
				this.events[event].push(cb);
			}

			return this;
		},

		one: function(event, cb) {
			var self = this;

			this.on(event, function _onceCallback() {
				cb.apply(this, arguments);
				self.off(event, _onceCallback);
			});

			return this;
		},

		off: function(event, cb) {
			if ('undefined' === this.events[event]) {
				return;
			}

			if (cb) {
				this.events[event].splice(this.events[event].indexOf(cb), 1);
			} else {
				this.events[event] = [];
			}

			return this;
		},

		trigger: function(event, args) {
			var self = this;

			args = (args || []);
			args.unshift($.Event(event));

			if (!(event in this.events)) {
				return;
			}

			$.each(this.events[event], function() {
				this.apply(self, args);
			});

			return this;
		},

		_getNonFilledLeftValue: function(stageWidth) {
			var value = 0;

			switch (this.settings.display.nonFilledArrangement) {
				case 'center':
					value = (this.wrapper.outerWidth(true) - stageWidth) / 2;
					break;
				case 'right':
					value = (this.wrapper.outerWidth(true) - stageWidth);
					break;
			}

			return value;
		},

		_fitColsInWrap: function() {
			if ('undefined' === typeof this.settings.slidesToShow || !this.settings.slidesToShow) {
				return this;
			}

			var wrapperWidth = this.wrapper.width(),
				slidesToShow = this.settings.slidesToShow,
				targetWidth = parseInt(wrapperWidth / slidesToShow, 10);

			this.cols.each(function() {
				var item = $(this);

				item.css('width', targetWidth);
			});

			this.container.attr('class', this.container.attr('class').replace(/cartware-slider--slides-to-show-\d+/, '') + ' ' + 'cartware-slider--slides-to-show-' + slidesToShow);

			return this;
		},

		_renderControls: function() {
			if (this.settings.controls) {
				if (!this.container.find('.control-previous').length) {
					$('<div class="control control-previous">' + this.settings.labels.controls.previous + '</div>').appendTo(this.container);
				}

				if (!this.container.find('.control-next').length) {
					$('<div class="control control-next">' + this.settings.labels.controls.next + '</div>').appendTo(this.container);
				}
			}

			this.controls = {
				previous:	this.container.find('.control-previous'),
				next:		this.container.find('.control-next'),
				thumbs:		[]
			};
		},

		_lazyload: function (cb) {
			cb = cb || $.noop;

			var self = this;
			var images = this
				.getVisibleItems()
				.find('img')
				.filter(function() {
					return !!$(this).data(self.lazyloadAttribute);
				});

			this._lazyLoadImages(images, function() {
				// self.reflow();
				cb();
			});

			return this;
		},

		_lazyLoadImages: function(images, cb) {
			images = $(images);

			var self = this,
				imagesToLoad = images.length,
				imagesLoaded = 0;

			if (0 === imagesToLoad) {
				cb();
				return this;
			}

			function iterate() {
				if (++imagesLoaded === imagesToLoad) {
					cb();
				}
			}

			function onload() {
				var image = this.TRANSPORT;

				image.attr('src', this.src);

				setTimeout(function() {
					image.removeClass(self.lazyloadPendingClass)
						.addClass(self.lazyloadLoadedClass);
				}, 10);

				self._imagePool.release(this);
				iterate();
			}

			function onerror() {
				$(this).remove();
				onload.call(this);
			}

			images.each(function() {
				var tmpImage,
					img = $(this),
					src = img.data(self.lazyloadAttribute);

				tmpImage = self._imagePool.alloc();
				tmpImage.onload = onload;
				tmpImage.onerror = onerror;
				tmpImage.TRANSPORT = img;
				tmpImage.src = src;
			});
		},

		initializeLayout: function () {
			var self = this,
				stageWidth = 0,
				stageHeight = 0;
			//roundingInaccuracy = Math.round(this.body.width() / 2 - parseInt(this.body.width() / 2, 10));

			this._fitColsInWrap();
			this._renderControls();

			this.cols.each(function() {
				var item = $(this),
					rect = this.getBoundingClientRect ? this.getBoundingClientRect() : null,
					width = rect ?
						(rect.width || rect.right - rect.left) :
						item.outerWidth(false),
					height = rect ?
						(rect.height || rect.bottom - rect.top) :
						item.outerHeight(false);

				width += parseInt(item.css('margin-left'), 10) + parseInt(item.css('margin-right'), 10);
				height += parseInt(item.css('margin-top'), 10) + parseInt(item.css('margin-bottom'), 10);

				stageWidth += width;
				stageHeight = Math.max(stageHeight, height);
			});

			stageWidth = Math.ceil(stageWidth);
			stageHeight = Math.ceil(stageHeight);

			if (this.settings.shuffle) {
				this.shuffleColumns();
			}

			// TODO: Find out why elements wont float correctly without this
			// stageWidth += 1;

			if (this.settings.animateLayout) {
				this.stage.css('opacity', 0);
				this.stage.css('width', stageWidth);

				this.stage.animate({
					height: stageHeight,
					opacity: 1
				});
			} else {
				this.stage.css({
					width: stageWidth,
					height: stageHeight
				});
			}

			if (stageWidth < this.wrapper.width()) {
				this.stage.css(animateAttributes.left, this._getNonFilledLeftValue(stageWidth));
				if (this.settings.controls && !this.settings.carousel) {
					$.each(this.controls, function() {
						$(this).css('display', 'none');
					});
				}
			}

			if (self.settings.arrows.position === 'middle') {
				$.each([this.controls.previous, this.controls.next], function () {
					var control = $(this);

					control.css({
						visibility: 'hidden'
					});
				});
			}

			var wrapperHeight = stageHeight +
				parseInt(this.stage.css('padding-top'), 10) +
				parseInt(this.stage.css('padding-bottom'), 10) +
				parseInt(this.stage.css('margin-top'), 10) +
				parseInt(this.stage.css('margin-bottom'), 10);

			var onWrapperLayout = function() {
				if (self.settings.arrows.position === 'middle') {
					$.each([self.controls.previous, self.controls.next], function() {
						var control = $(this);

						control.css({
							top: self.wrapper.innerHeight() / 2 - control.innerHeight() / 2,
							visibility: 'visible'
						});
					});
				}
			};

			if (this.settings.animateLayout) {
				this.wrapper.animate({
					height: wrapperHeight
				}, {
					complete: onWrapperLayout
				});
			} else {
				this.wrapper.css({
					height: wrapperHeight
				});

				onWrapperLayout();
			}

			this.checkControls();
		},

		initializeEvents: function() {
			var container = this.container,
				self = this;

			container.hover(function() {
				container.trigger('cartware_slider/slide_mouseover');
				self.trigger('slide_mouseover');
			}, function() {
				container.trigger('cartware_slider/slide_mouseleave');
				self.trigger('slide_mouseleave');
			});

			this.items.hover(function() {
				$(this).addClass('hover');
			}, function() {
				$(this).removeClass('hover');
			});

			this.wrapper.on('click', '.cartware-slider-slide-item', function() {
				self.trigger('slide_click', [this]);
			});
		},

		initializeResponsive: function() {
			if (!$.isArray(this.settings.responsive)) {
				return;
			}

			var mobileFirst = this.settings.mobileFirst;
			var sortFlag = mobileFirst ? -1 : 1;

			this.settings.responsive.sort(function(a, b) {
				if (!a.breakpoint || !a.settings) {
					return -sortFlag;
				}

				if (!b.breakpoint || !b.settings) {
					return sortFlag;
				}

				return parseInt(a.breakpoint, 10) > parseInt(b.breakpoint, 10) ?
					sortFlag :
					(parseInt(a.breakpoint, 10) === parseInt(b.breakpoint, 10) ?
						0 :
						-sortFlag);
			});

			this.defaultResponsive = {
				breakpoint: this.settings.mobileFirst ? 0 : Infinity,
				settings: this.originalSettings
			};

			$(window).on('resize', throttle($.proxy(this._checkBreakpoint, this), 100));
			this._checkBreakpoint();
		},

		_checkBreakpoint: function() {
			var sliderWidth = this.wrapper.width();
			var windowWidth = window.innerWidth || $(window).width();
			var respondToWidth = windowWidth;

			if (this.settings.respondTo === 'slider') {
				respondToWidth = sliderWidth;
			} else if (this.settings.respondTo === 'min') {
				respondToWidth = Math.min(windowWidth, sliderWidth);
			}

			var mobileFirst = this.settings.mobileFirst;
			var targetEntry = null;
			var wrapperWidth = this.wrapper.width();

			$.each(this.settings.responsive, function(i, responsiveEntry) {
				var breakpoint = parseInt(responsiveEntry.breakpoint, 10) || null;
				var settings = responsiveEntry.settings || null;
				var innerTargetEntry = null;

				if (!breakpoint || !settings) {
					return;
				}

				if (mobileFirst) {
					if (respondToWidth > breakpoint) {
						innerTargetEntry = responsiveEntry;
					}
				} else {
					if (respondToWidth < breakpoint) {
						innerTargetEntry = responsiveEntry;
					}
				}

				if (!innerTargetEntry) {
					return;
				}

				if (!targetEntry) {
					targetEntry = innerTargetEntry;
				} else {
					if (mobileFirst) {
						if (parseInt(innerTargetEntry.breakpoint, 10) >= parseInt(targetEntry.breakpoint, 10)) {
							targetEntry = innerTargetEntry;
						}
					} else {
						if (parseInt(innerTargetEntry.breakpoint, 10) <= parseInt(targetEntry.breakpoint, 10)) {
							targetEntry = innerTargetEntry;
						}
					}
				}
			});

			if (!targetEntry)  {
				targetEntry = this.defaultResponsive;
			}

			var mustReflow = false;

			if (this.currentBreakpoint !== null && this.lastWrapperWidth !== wrapperWidth) {
				mustReflow = true;
			}

			if (targetEntry.breakpoint != this.currentBreakpoint) {
				this.settings = $.extend({}, this.originalSettings, targetEntry.settings);
				this.currentBreakpoint = targetEntry.breakpoint;
			}

			if (mustReflow) {
				this.reflowImmediately();
			}

			this.lastWrapperWidth = wrapperWidth;
		},

		initializeControls: function() {
			this.controls.next.bind('click', $.proxy(function() { this.next(); }, this));
			this.controls.next.hover(function() {
				$(this).addClass('hover');
			}, function() {
				$(this).removeClass('hover');
			});

			this.controls.previous.bind('click', $.proxy(function() { this.previous(); }, this));
			this.controls.previous.hover(function() {
				$(this).addClass('hover');
			}, function() {
				$(this).removeClass('hover');
			});

			this.container.bind('cartware_slider/slide_after', $.proxy(this.checkControls, this));
			this.container.addClass('cartware-slider--has-controls');
		},

		initializeHandles: function() {
			var handleposition = this.settings.controlsettings.handleposition;

			this.thumbsContainer = this.container.find('.thumbs-container');

			if (!this.thumbsContainer.length) {
				this.thumbsContainer = $('<div class="thumbs-container thumbs-container-' + handleposition + '">').appendTo(this.container);
			}

			if (!this.thumbsWrap) {
				this.thumbsWrap = $('<div class="thumbs-inner-wrap">').appendTo(this.thumbsContainer);
			}

			this.container.bind('cartware_slider/slide_after', $.proxy(this.determineActiveThumb, this));
			this.container.bind('cartware_slider/slide_init', $.proxy(this.determineActiveThumb, this));

			this.renderHandles();
			this.container.addClass('cartware-slider--has-handles');
		},

		renderHandles: function() {
			var thumbsList = $('<ul />');

			this.controls.thumbs = [];
			this.thumbsWrap.empty().append(thumbsList);

			if (this.settings.mode !== Slider.prototype.MODE_BATCHES) {
				// TODO: This seems obsolete, but the slideType=video logic is restricting refactor
				this.cols.each($.proxy(function(i) {
					var slide = $(this.cols[i]);
					var index = i;
					var slideType = 'default';
					if (slide.hasClass('cartware-slider-item-wrap--video')) {
						slideType = 'video';
					}
					var thumb = $('<a href="#" class="thumb thumb-' + slideType + ' thumb-' + index + '" />');

					thumb.html(i);
					thumbsList.append($('<li class="thumb-li-' + slideType + '" />').append(thumb));

					//thumb.appendTo(this.thumbsWrap);
					thumb.bind('click', $.proxy(function(e) {
						e.preventDefault();
						e.stopPropagation();

						this.slideTo(index);
					}, this));

					this.controls.thumbs.push(thumb);
				}, this));
			} else {
				var batches = this.getBatches();

				$.each(batches, $.proxy(function(i, batch) {
					var thumb = $('<a href="#" class="thumb thumb-default thumb-' + i + '" />'),
						item = batch[0];

					thumb.html(i);
					thumbsList.append($('<li class="thumb-li-default" />').append(thumb));

					//thumb.appendTo(this.thumbsWrap);
					thumb.bind('click', $.proxy(function(e) {
						e.preventDefault();
						e.stopPropagation();

						this.slideTo(item);
					}, this));

					this.controls.thumbs.push(thumb);
				}, this));

				if (batches.length < 2) {
					this.container.addClass('handles-disabled');
				} else {
					this.container.removeClass('handles-disabled');
				}
			}

			this.determineActiveThumb();
		},

		initializeInterval: function() {
			var self = this;

			this.container.bind('cartware_slider/slide_mouseover', function() {
				if (self.slideInterval) {
					clearInterval(self.slideInterval);
					self.slideInterval = null;
				}
			});

			this.container.bind('cartware_slider/slide_mouseleave', function() {
				if (!self.slideInterval && !self.autoslidePaused) {
					self.setupInterval();
				}
			});

			self.setupInterval();
		},

		setupInterval: function() {
			var self = this;
			self.slideInterval = setInterval(function() {
				self.next();
			}, self.slideIntervalMs);
		},

		initializePlugins: function() {
			var self = this;

			// Backwards compatibility
			$.each(this.settings.plugins, function() {
				var plugin = this;

				if (typeof plugin !== 'function' && !$.isPlainObject(plugin)) {
					if (!(plugin in plugins)) {
						return;
					}
					plugin = plugins[plugin];
				}

				if (typeof plugin === 'function') {
					plugin.call(self, self);
				}
			});

			this.callPluginMethod('initialized');
		},

		callPluginMethod: function(method) {
			var self = this,
				args = Array.prototype.slice.call(arguments, 1);

			$.each(this.settings.plugins, function() {
				var plugin = this;

				if (!$.isPlainObject(plugin)) {
					if (!(plugin in plugins)) {
						return;
					}
					plugin = plugins[plugin];
				}

				if (
					$.isPlainObject(plugin) &&
					'initialized' in plugin &&
					(typeof plugin[method] === 'function')
				) {
					plugin[method].apply(self, args);
				}
			});
		},

		getBatches: function() {
			var wrapperWidth = this.wrapper.width(),
				roundingInaccuracy = this.getRoundingInaccuracy(),
				rightConstraint = null,
				batches = [],
				self = this;

			this.cols.each(function() {
				var item = $(this),
					position = self.calculateItemPosition(item),
					batch = batches.length ? batches[batches.length - 1] : [];

				if (null === rightConstraint || position.left >= rightConstraint) {
					batch = [item];

					if (null === rightConstraint) {
						rightConstraint = wrapperWidth + roundingInaccuracy;
					} else {
						rightConstraint = position.left + wrapperWidth;
					}

					batches.push(batch);
					return;
				}

				batch.push(item);
			});

			return batches;
		},

		calculateItemPosition: function(item) {
			var position = item.position();
			position.right = position.left + item.width();
			position.bottom = position.top + item.height();
			return position;
		},

		pauseAutoslide: function(value) {
			var self = this;
			if (self.settings.autoslide === false) {
				return;
			}
			if (value === true) {
				self.autoslidePaused = true;
				if (self.slideInterval) {
					clearInterval(self.slideInterval);
					self.slideInterval = null;
				}
			} else {
				self.autoslidePaused = false;
				clearInterval(self.slideInterval);
				self.slideInterval = null;
				self.setupInterval();
			}
		},

		shuffleColumns: function() {
			if (this.cols.length < 2) {
				return;
			}

			var classes = this.cols.map(function() { return $(this).prop('class'); });

			this.cols.detach()
				.sort(function() { return 0.5 - Math.random(); })
				.each(function(i) {
					$(this).prop('class', classes[i]);
				})
				.appendTo(this.list);

			this.items				= this.cols.find('.cartware-slider-slide-item');
			this.images				= this.items.find('img').not('.mobile-slide-image');

			this.trigger('shuffle');
		},

		determineActiveThumb: function() {
			var index = 0;

			if (this.activeItem) {
				index = this.cols.index(this.activeItem);
			}

			if (this.settings.mode === Slider.prototype.MODE_BATCHES) {
				var batchNumber = 0;

				var batches = this.getBatches();

				$.each(batches, $.proxy(function(i, batch) {
					var firstBatchItem = $(batch[0]);

					if ((-parseInt(this.stage.css(animateAttributes.left), 10)) === 0) {
						return false;
					} else {
						if (firstBatchItem.position().left >= (-parseInt(this.stage.css(animateAttributes.left), 10))) {
							batchNumber = i;
							return false;
						}
					}

					batchNumber = i;
				}, this));

				index = batchNumber;
			}

			$.each(this.controls.thumbs, function(i) {
				$(this)[(i === index) ? 'addClass' : 'removeClass']('active');
			});
		},

		checkControls: function() {
			var stageWidth = this.stage.width(),
				currentLeft = parseInt(this.stage.css(animateAttributes.left), 10),
				wrapperWidth = this.wrapper.width(),
				colCount = this.cols.length,
				allDisabled = true;

			$.each([this.controls.previous, this.controls.next], function(i) {
				var control = $(this);

				if (colCount === 1) {
					control.css('display', 'none');
					return;
				}

				if (stageWidth <= wrapperWidth) {
					control.addClass('disabled');
				} else {
					control.removeClass('disabled');

					if (currentLeft < 0) {
						if (i === 0) {
							control.removeClass('disabled');
						} else if (i === 1) {
							// TODO: remove 1px tolerance
							if (stageWidth + currentLeft - 1 <= wrapperWidth) {
								control.addClass('disabled');
							} else {
								control.removeClass('disabled');
							}
						}
					} else {
						if (i === 1) {
							control.removeClass('disabled');
						} else if (i === 0) {
							control.addClass('disabled');
						}
					}
				}
			});

			$.each([this.controls.previous, this.controls.next], function() {
				var control = $(this);
				allDisabled = allDisabled && control.hasClass('disabled');
				if (!allDisabled) {
					return false;
				}
			});

			if (allDisabled) {
				this.container.addClass('controls-disabled');
			} else {
				this.container.removeClass('controls-disabled');
			}
		},

		getRoundingInaccuracy: function() { // TODO
			return 0;
			// return Math.round(this.body.width() / 2 - parseInt(this.body.width() / 2, 10));
		},

		slideTo: function(index, noFx, mode) {
			var target = $.isNumeric(index) ? $(this.cols[index]) : index;
			this.next(target, noFx, mode);
		},

		next: function(nextItem, noFx, mode) {
			var totalLeft = this.wrapper.width() - parseInt(this.stage.css(animateAttributes.left), 10) ,
				lastFullVisible = this.cols.first(),
				lastCenterItem = null,
				targetLeft = 0,
				center = totalLeft - this.wrapper.width() / 2,
				roundingInaccuracy = this.getRoundingInaccuracy();

			noFx = noFx || false;
			mode = mode || this.settings.mode;

			nextItem = nextItem || null;

			if (nextItem === null) {
				this.cols.each(function() {
					var item = $(this);

					if (item.width() + item.position().left <= totalLeft + roundingInaccuracy) {
						lastFullVisible = item;
					}

					if (parseInt(item.position().left + item.width() / 2) <= center || lastCenterItem === null) {
						lastCenterItem = item;
					}
				});

				if (mode === Slider.prototype.MODE_BATCHES) {
					nextItem = lastFullVisible.next();

					if (!nextItem.length) {
						if (!this.settings.carousel) {
							nextItem = lastFullVisible;
						} else {
							nextItem = this.items.first().parent();
						}
					}
				} else {
					nextItem = lastCenterItem.next();

					$.proxy(function() { // Skip slides if set up multiple slides per move
						var i = this.settings.modesettings.columns.slidesPerMove;

						if (nextItem.length || !this.settings.carousel) {
							while(--i && (nextItem = nextItem.next())) { /* noop */ };
						} else {
							nextItem = this.items.first().parent();
						}

					}, this)();
				}
			}

//			if ((!nextItem || !nextItem.length) && !this.settings.carousel) {
//				return;
//			}

			if ((!nextItem || !nextItem.length)) {
				nextItem = this.cols.last();
			}

			if (nextItem.length) {
				switch (mode) {
					case Slider.prototype.MODE_BATCHES:
						targetLeft = - Math.min(nextItem.position().left, this.stage.outerWidth(true) - this.wrapper.outerWidth(true));
						break;

					case Slider.prototype.MODE_CENTER:
						targetLeft = - (nextItem.position().left + nextItem.outerWidth(true) / 2 - this.wrapper.width() / 2);
						break;

					case Slider.prototype.MODE_COLUMNS:
					default:
						targetLeft = - (nextItem.position().left + nextItem.outerWidth(true) / 2 - this.wrapper.width() / 2);
						//if (targetLeft < - (this.stage.width() - this.wrapper.width())) targetLeft = 0;

						// Exceed
						if (targetLeft < - (this.stage.width() - this.wrapper.width())) {
							// Limit to right side
							targetLeft = - (this.stage.width() - this.wrapper.width());

							if (this.settings.carousel) {
								// Return to beginning
								if (targetLeft === parseInt(this.stage.css(animateAttributes.left), 10)) {
									targetLeft = 0;
									nextItem = this.items.get(0);
								}
							}
						}

						break;
				}

				if (this.stage.width() < this.wrapper.width()) {
					targetLeft = this._getNonFilledLeftValue(this.stage.width());
				}
			}

			this.container.trigger('cartware_slider/slide_before');
			this.trigger('slide_before');


			var onComplete = $.proxy(function() {
				this._lazyload($.proxy(function() {
					this.activeItem = nextItem.length ? nextItem : null;
					this.container.trigger('cartware_slider/slide_after');
					this.trigger('slide_after');
				}, this));
			}, this);

			var targetStyles = {};
			targetStyles[animateAttributes.left] = targetLeft;

			if (noFx) {
				this.stage.css(targetStyles);

				onComplete();
			} else {
				this.stage.animate(targetStyles, {
					easing: this.settings.easing,
					duration: this.speed,
					complete: onComplete
				});
			}
		},

		previous: function(noFx) {
			var totalLeft = - parseInt(this.stage.css(animateAttributes.left), 10),
				previousItem,
				targetLeft = - (this.stage.width() - this.wrapper.width());

			if (this.settings.mode === Slider.prototype.MODE_BATCHES) {
				totalLeft -= this.wrapper.width();
				totalLeft = Math.max(1, totalLeft);
			}

			this.cols.each(function() {
				var item = $(this);
				if (item.position().left < totalLeft) {
					previousItem = item;
				}
			});

			if (parseInt(this.stage.css(animateAttributes.left), 10) === 0) {
				if (!this.settings.carousel) {
					return;
				}
				previousItem = this.cols.last();
			}

			if (this.settings.mode !== Slider.prototype.MODE_BATCHES) {
				$.proxy(function() { // Skip slides if set up multiple slides per move
					var i = this.settings.modesettings.columns.slidesPerMove;
					while(--i && (previousItem = previousItem.prev())) { /* noop */ };
				}, this)();

				if (!previousItem || !previousItem.length) {
					previousItem = this.cols.first();
				}
			}

			if (previousItem && previousItem.length) {
				targetLeft = - Math.min(previousItem.position().left, this.stage.width() - this.wrapper.width());
				if (this.stage.width() < this.wrapper.width()) {
					targetLeft = this._getNonFilledLeftValue(this.stage.width());
				}
			}

			this.container.trigger('cartware_slider/slide_before');
			this.trigger('slide_before');

			var onComplete = $.proxy(function() {
				this._lazyload($.proxy(function() {
					this.activeItem = previousItem.length ? previousItem : null;
					this.container.trigger('cartware_slider/slide_after');
					this.trigger('slide_after');
				}, this));
			}, this);

			var targetStyles = {};
			targetStyles[animateAttributes.left] = targetLeft;

			if (noFx) {
				this.stage.css(targetStyles);

				onComplete();
			} else {
				this.stage.animate(targetStyles, {
					easing: this.settings.easing,
					duration: this.speed,
					complete: onComplete
				});
			}
		},

		getVisibleItems: function() {
			var currentLeft = parseInt(this.stage.css(animateAttributes.left), 10);
			var span = [- currentLeft, - currentLeft + this.wrapper.width()];
			var collection = [];

			this.items.each(function() {
				var item = $(this),
					itemLeft = item.position().left;

				if (itemLeft > span[1]) {
					return false; // exceed
				}

				if (itemLeft >= span[0] && itemLeft < span[1]) {
					collection.push(this);
					return true;
				}
			});

			return $(collection);
		},

			reflow: function() {
			if (this.reflowTimeout) {
				clearTimeout(this.reflowTimeout);
			}

			this.reflowTimeout = setTimeout($.proxy(this.reflowImmediately, this), 10);
		},

		reflowImmediately: function() {
			var self = this,
				stageWidth = 0,
				stageHeight = 0,
				targetCol = this.activeItem,//.parents('.cartware-slider-item-wrap'),
				roundingInaccuracy = this.getRoundingInaccuracy();

			this._fitColsInWrap();

			this.cols.each(function() {
				var item = $(this),
					rect = this.getBoundingClientRect ? this.getBoundingClientRect() : null,
					width = rect ?
						(rect.width || rect.right - rect.left) :
						item.outerWidth(false),
					height = rect ?
						(rect.height || rect.bottom - rect.top) :
						item.outerHeight(false);

				width += parseInt(item.css('margin-left'), 10) + parseInt(item.css('margin-right'), 10);
				height += parseInt(item.css('margin-top'), 10) + parseInt(item.css('margin-bottom'), 10);

				stageWidth += width;
				stageHeight = Math.max(stageHeight, height);
			});

			this.stage.css({
				width: stageWidth + roundingInaccuracy,
				height: stageHeight
			});

			/*
			 cols.each(function() {
			 var item = $(this);
			 stageHeight = Math.max(stageHeight, item.outerHeight(true));
			 });

			 stage.css({
			 width: stageWidth
			 });

			 cols.each(function() {
			 var item = $(this);
			 stageHeight = Math.max(stageHeight, item.outerHeight(true));
			 });

			 stage.css({
			 height: stageHeight
			 });
			 */

			this.wrapper.css({
				height: stageHeight +
					parseInt(this.stage.css('padding-top'), 10) +
					parseInt(this.stage.css('padding-bottom'), 10) +
					parseInt(this.stage.css('margin-top'), 10) +
					parseInt(this.stage.css('margin-bottom'), 10)
			});

			if (this.settings.arrows.position === 'middle') {
				$.each([this.controls.previous, this.controls.next], function() {
					var control = $(this);

					control.css({
						top: self.wrapper.innerHeight() / 2 - control.innerHeight() / 2
					});
				});
			}

			if (this.settings.mode === Slider.prototype.MODE_BATCHES) {
				this.cols.each(function() {
					var col = $(this);
					if (col.position().left + roundingInaccuracy >= - (self.stage.css(animateAttributes.left)|0)) {
						targetCol = col;
						return false;
					}
				});

				// var wrapperWidth = this.wrapper.width();
				//
				// this.cols.each(function() {
				// 	var item = $(this);
				//
				// 	if (item.width() + item.position().left <= wrapperWidth + roundingInaccuracy) {
				// 		targetCol = item;
				// 	}
				// });
			}


			var targetStyles = {};
			targetStyles[animateAttributes.left] = - Math.min(targetCol ? targetCol.position().left : 0, this.stage.outerWidth(true) - this.wrapper.outerWidth(true));

			if (this.stage.width() < this.wrapper.width()) {
				targetStyles[animateAttributes.left] = this._getNonFilledLeftValue(this.stage.width());
			}

			if (this.settings.controls) {
				if (this.stage.width() < this.wrapper.width()) {
					if (!this.settings.carousel) {
						$.each(this.controls, function() {
							$(this).css('display', 'none');
						});
					}
				} else {
					$.each(this.controls, function() {
						$(this).css('display', 'block');
					});
				}
			}

			this.stage.css(targetStyles);
			this.checkControls();
			this.renderHandles();

			this.container.trigger('cartware_slider/reflow');
			this.trigger('reflow');
		}
	};

	$.fn.cartwareSlider = function() {
		return this.each(function() {
			var el = $(this),
				options = el.data('config'),
				inst = el.data('cartware_slider/instance');

			if (!inst) {
				el.data('cartware_slider/instance', new Slider(el, options || {}));
			} else if (inst && options) {
				$.extend(inst.options, options);
			}
		});
	};

	function setup() {
		$('.cartware-slider').cartwareSlider();
	}

	setup();
	$(document).ready(setup);
	$(document).ajaxComplete(setup);
});


  }).apply(root, arguments);
});
}(this));

/*! grunt-grunticon Stylesheet Loader - v2.1.6 | https://github.com/filamentgroup/grunticon | (c) 2015 Scott Jehl, Filament Group, Inc. | MIT license. */

!function(){function e(e,n){function t(){!a&&n&&(a=!0,n.call(e))}var a;e.addEventListener&&e.addEventListener("load",t),e.attachEvent&&e.attachEvent("onload",t),"isApplicationInstalled"in navigator&&"onloadcssdefined"in e&&e.onloadcssdefined(t)}!function(e){"use strict";var n=function(n,t,a){function o(e){if(d.body)return e();setTimeout(function(){o(e)})}function i(){l.addEventListener&&l.removeEventListener("load",i),l.media=a||"all"}var r,d=e.document,l=d.createElement("link");if(t)r=t;else{var s=(d.body||d.getElementsByTagName("head")[0]).childNodes;r=s[s.length-1]}var c=d.styleSheets;l.rel="stylesheet",l.href=n,l.media="only x",o(function(){r.parentNode.insertBefore(l,t?r:r.nextSibling)});var f=function(e){for(var n=l.href,t=c.length;t--;)if(c[t].href===n)return e();setTimeout(function(){f(e)})};return l.addEventListener&&l.addEventListener("load",i),l.onloadcssdefined=f,f(i),l};"undefined"!=typeof exports?exports.loadCSS=n:e.loadCSS=n}("undefined"!=typeof global?global:this),function(n){var t=function(a,o){"use strict";if(a&&3===a.length){var i=n.navigator,r=n.document,d=n.Image,l=!(!r.createElementNS||!r.createElementNS("http://www.w3.org/2000/svg","svg").createSVGRect||!r.implementation.hasFeature("http://www.w3.org/TR/SVG11/feature#Image","1.1")||n.opera&&-1===i.userAgent.indexOf("Chrome")||-1!==i.userAgent.indexOf("Series40")),s=new d;s.onerror=function(){t.method="png",t.href=a[2],loadCSS(a[2])},s.onload=function(){var n=1===s.width&&1===s.height,i=a[n&&l?0:n?1:2];t.method=n&&l?"svg":n?"datapng":"png",t.href=i,e(loadCSS(i),o)},s.src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==",r.documentElement.className+=" grunticon"}};t.loadCSS=loadCSS,t.onloadCSS=e,n.grunticon=t}(this)}();
define("grunticon-loader", function(){});

/*! Magnific Popup - v1.0.0 - 2014-12-12
* http://dimsemenov.com/plugins/magnific-popup/
* Copyright (c) 2014 Dmitry Semenov; */
;(function (factory) { 
if (typeof define === 'function' && define.amd) { 
 // AMD. Register as an anonymous module. 
 define('magnific-popup',['jquery'], factory); 
 } else if (typeof exports === 'object') { 
 // Node/CommonJS 
 factory(require('jquery')); 
 } else { 
 // Browser globals 
 factory(window.jQuery || window.Zepto); 
 } 
 }(function($) { 

/*>>core*/
/**
 * 
 * Magnific Popup Core JS file
 * 
 */


/**
 * Private static constants
 */
var CLOSE_EVENT = 'Close',
	BEFORE_CLOSE_EVENT = 'BeforeClose',
	AFTER_CLOSE_EVENT = 'AfterClose',
	BEFORE_APPEND_EVENT = 'BeforeAppend',
	MARKUP_PARSE_EVENT = 'MarkupParse',
	OPEN_EVENT = 'Open',
	CHANGE_EVENT = 'Change',
	NS = 'mfp',
	EVENT_NS = '.' + NS,
	READY_CLASS = 'mfp-ready',
	REMOVING_CLASS = 'mfp-removing',
	PREVENT_CLOSE_CLASS = 'mfp-prevent-close';


/**
 * Private vars 
 */
var mfp, // As we have only one instance of MagnificPopup object, we define it locally to not to use 'this'
	MagnificPopup = function(){},
	_isJQ = !!(window.jQuery),
	_prevStatus,
	_window = $(window),
	_body,
	_document,
	_prevContentType,
	_wrapClasses,
	_currPopupType;


/**
 * Private functions
 */
var _mfpOn = function(name, f) {
		mfp.ev.on(NS + name + EVENT_NS, f);
	},
	_getEl = function(className, appendTo, html, raw) {
		var el = document.createElement('div');
		el.className = 'mfp-'+className;
		if(html) {
			el.innerHTML = html;
		}
		if(!raw) {
			el = $(el);
			if(appendTo) {
				el.appendTo(appendTo);
			}
		} else if(appendTo) {
			appendTo.appendChild(el);
		}
		return el;
	},
	_mfpTrigger = function(e, data) {
		mfp.ev.triggerHandler(NS + e, data);

		if(mfp.st.callbacks) {
			// converts "mfpEventName" to "eventName" callback and triggers it if it's present
			e = e.charAt(0).toLowerCase() + e.slice(1);
			if(mfp.st.callbacks[e]) {
				mfp.st.callbacks[e].apply(mfp, $.isArray(data) ? data : [data]);
			}
		}
	},
	_getCloseBtn = function(type) {
		if(type !== _currPopupType || !mfp.currTemplate.closeBtn) {
			mfp.currTemplate.closeBtn = $( mfp.st.closeMarkup.replace('%title%', mfp.st.tClose ) );
			_currPopupType = type;
		}
		return mfp.currTemplate.closeBtn;
	},
	// Initialize Magnific Popup only when called at least once
	_checkInstance = function() {
		if(!$.magnificPopup.instance) {
			mfp = new MagnificPopup();
			mfp.init();
			$.magnificPopup.instance = mfp;
		}
	},
	// CSS transition detection, http://stackoverflow.com/questions/7264899/detect-css-transitions-using-javascript-and-without-modernizr
	supportsTransitions = function() {
		var s = document.createElement('p').style, // 's' for style. better to create an element if body yet to exist
			v = ['ms','O','Moz','Webkit']; // 'v' for vendor

		if( s['transition'] !== undefined ) {
			return true; 
		}
			
		while( v.length ) {
			if( v.pop() + 'Transition' in s ) {
				return true;
			}
		}
				
		return false;
	};



/**
 * Public functions
 */
MagnificPopup.prototype = {

	constructor: MagnificPopup,

	/**
	 * Initializes Magnific Popup plugin. 
	 * This function is triggered only once when $.fn.magnificPopup or $.magnificPopup is executed
	 */
	init: function() {
		var appVersion = navigator.appVersion;
		mfp.isIE7 = appVersion.indexOf("MSIE 7.") !== -1; 
		mfp.isIE8 = appVersion.indexOf("MSIE 8.") !== -1;
		mfp.isLowIE = mfp.isIE7 || mfp.isIE8;
		mfp.isAndroid = (/android/gi).test(appVersion);
		mfp.isIOS = (/iphone|ipad|ipod/gi).test(appVersion);
		mfp.supportsTransition = supportsTransitions();

		// We disable fixed positioned lightbox on devices that don't handle it nicely.
		// If you know a better way of detecting this - let me know.
		mfp.probablyMobile = (mfp.isAndroid || mfp.isIOS || /(Opera Mini)|Kindle|webOS|BlackBerry|(Opera Mobi)|(Windows Phone)|IEMobile/i.test(navigator.userAgent) );
		_document = $(document);

		mfp.popupsCache = {};
	},

	/**
	 * Opens popup
	 * @param  data [description]
	 */
	open: function(data) {

		if(!_body) {
			_body = $(document.body);
		}

		var i;

		if(data.isObj === false) { 
			// convert jQuery collection to array to avoid conflicts later
			mfp.items = data.items.toArray();

			mfp.index = 0;
			var items = data.items,
				item;
			for(i = 0; i < items.length; i++) {
				item = items[i];
				if(item.parsed) {
					item = item.el[0];
				}
				if(item === data.el[0]) {
					mfp.index = i;
					break;
				}
			}
		} else {
			mfp.items = $.isArray(data.items) ? data.items : [data.items];
			mfp.index = data.index || 0;
		}

		// if popup is already opened - we just update the content
		if(mfp.isOpen) {
			mfp.updateItemHTML();
			return;
		}
		
		mfp.types = []; 
		_wrapClasses = '';
		if(data.mainEl && data.mainEl.length) {
			mfp.ev = data.mainEl.eq(0);
		} else {
			mfp.ev = _document;
		}

		if(data.key) {
			if(!mfp.popupsCache[data.key]) {
				mfp.popupsCache[data.key] = {};
			}
			mfp.currTemplate = mfp.popupsCache[data.key];
		} else {
			mfp.currTemplate = {};
		}



		mfp.st = $.extend(true, {}, $.magnificPopup.defaults, data ); 
		mfp.fixedContentPos = mfp.st.fixedContentPos === 'auto' ? !mfp.probablyMobile : mfp.st.fixedContentPos;

		if(mfp.st.modal) {
			mfp.st.closeOnContentClick = false;
			mfp.st.closeOnBgClick = false;
			mfp.st.showCloseBtn = false;
			mfp.st.enableEscapeKey = false;
		}
		

		// Building markup
		// main containers are created only once
		if(!mfp.bgOverlay) {

			// Dark overlay
			mfp.bgOverlay = _getEl('bg').on('click'+EVENT_NS, function() {
				mfp.close();
			});

			mfp.wrap = _getEl('wrap').attr('tabindex', -1).on('click'+EVENT_NS, function(e) {
				if(mfp._checkIfClose(e.target)) {
					mfp.close();
				}
			});

			mfp.container = _getEl('container', mfp.wrap);
		}

		mfp.contentContainer = _getEl('content');
		if(mfp.st.preloader) {
			mfp.preloader = _getEl('preloader', mfp.container, mfp.st.tLoading);
		}


		// Initializing modules
		var modules = $.magnificPopup.modules;
		for(i = 0; i < modules.length; i++) {
			var n = modules[i];
			n = n.charAt(0).toUpperCase() + n.slice(1);
			mfp['init'+n].call(mfp);
		}
		_mfpTrigger('BeforeOpen');


		if(mfp.st.showCloseBtn) {
			// Close button
			if(!mfp.st.closeBtnInside) {
				mfp.wrap.append( _getCloseBtn() );
			} else {
				_mfpOn(MARKUP_PARSE_EVENT, function(e, template, values, item) {
					values.close_replaceWith = _getCloseBtn(item.type);
				});
				_wrapClasses += ' mfp-close-btn-in';
			}
		}

		if(mfp.st.alignTop) {
			_wrapClasses += ' mfp-align-top';
		}

	

		if(mfp.fixedContentPos) {
			mfp.wrap.css({
				overflow: mfp.st.overflowY,
				overflowX: 'hidden',
				overflowY: mfp.st.overflowY
			});
		} else {
			mfp.wrap.css({ 
				top: _window.scrollTop(),
				position: 'absolute'
			});
		}
		if( mfp.st.fixedBgPos === false || (mfp.st.fixedBgPos === 'auto' && !mfp.fixedContentPos) ) {
			mfp.bgOverlay.css({
				height: _document.height(),
				position: 'absolute'
			});
		}

		

		if(mfp.st.enableEscapeKey) {
			// Close on ESC key
			_document.on('keyup' + EVENT_NS, function(e) {
				if(e.keyCode === 27) {
					mfp.close();
				}
			});
		}

		_window.on('resize' + EVENT_NS, function() {
			mfp.updateSize();
		});


		if(!mfp.st.closeOnContentClick) {
			_wrapClasses += ' mfp-auto-cursor';
		}
		
		if(_wrapClasses)
			mfp.wrap.addClass(_wrapClasses);


		// this triggers recalculation of layout, so we get it once to not to trigger twice
		var windowHeight = mfp.wH = _window.height();

		
		var windowStyles = {};

		if( mfp.fixedContentPos ) {
            if(mfp._hasScrollBar(windowHeight)){
                var s = mfp._getScrollbarSize();
                if(s) {
                    windowStyles.marginRight = s;
                }
            }
        }

		if(mfp.fixedContentPos) {
			if(!mfp.isIE7) {
				windowStyles.overflow = 'hidden';
			} else {
				// ie7 double-scroll bug
				$('body, html').css('overflow', 'hidden');
			}
		}

		
		
		var classesToadd = mfp.st.mainClass;
		if(mfp.isIE7) {
			classesToadd += ' mfp-ie7';
		}
		if(classesToadd) {
			mfp._addClassToMFP( classesToadd );
		}

		// add content
		mfp.updateItemHTML();

		_mfpTrigger('BuildControls');

		// remove scrollbar, add margin e.t.c
		$('html').css(windowStyles);
		
		// add everything to DOM
		mfp.bgOverlay.add(mfp.wrap).prependTo( mfp.st.prependTo || _body );

		// Save last focused element
		mfp._lastFocusedEl = document.activeElement;
		
		// Wait for next cycle to allow CSS transition
		setTimeout(function() {
			
			if(mfp.content) {
				mfp._addClassToMFP(READY_CLASS);
				mfp._setFocus();
			} else {
				// if content is not defined (not loaded e.t.c) we add class only for BG
				mfp.bgOverlay.addClass(READY_CLASS);
			}
			
			// Trap the focus in popup
			_document.on('focusin' + EVENT_NS, mfp._onFocusIn);

		}, 16);

		mfp.isOpen = true;
		mfp.updateSize(windowHeight);
		_mfpTrigger(OPEN_EVENT);

		return data;
	},

	/**
	 * Closes the popup
	 */
	close: function() {
		if(!mfp.isOpen) return;
		_mfpTrigger(BEFORE_CLOSE_EVENT);

		mfp.isOpen = false;
		// for CSS3 animation
		if(mfp.st.removalDelay && !mfp.isLowIE && mfp.supportsTransition )  {
			mfp._addClassToMFP(REMOVING_CLASS);
			setTimeout(function() {
				mfp._close();
			}, mfp.st.removalDelay);
		} else {
			mfp._close();
		}
	},

	/**
	 * Helper for close() function
	 */
	_close: function() {
		_mfpTrigger(CLOSE_EVENT);

		var classesToRemove = REMOVING_CLASS + ' ' + READY_CLASS + ' ';

		mfp.bgOverlay.detach();
		mfp.wrap.detach();
		mfp.container.empty();

		if(mfp.st.mainClass) {
			classesToRemove += mfp.st.mainClass + ' ';
		}

		mfp._removeClassFromMFP(classesToRemove);

		if(mfp.fixedContentPos) {
			var windowStyles = {marginRight: ''};
			if(mfp.isIE7) {
				$('body, html').css('overflow', '');
			} else {
				windowStyles.overflow = '';
			}
			$('html').css(windowStyles);
		}
		
		_document.off('keyup' + EVENT_NS + ' focusin' + EVENT_NS);
		mfp.ev.off(EVENT_NS);

		// clean up DOM elements that aren't removed
		mfp.wrap.attr('class', 'mfp-wrap').removeAttr('style');
		mfp.bgOverlay.attr('class', 'mfp-bg');
		mfp.container.attr('class', 'mfp-container');

		// remove close button from target element
		if(mfp.st.showCloseBtn &&
		(!mfp.st.closeBtnInside || mfp.currTemplate[mfp.currItem.type] === true)) {
			if(mfp.currTemplate.closeBtn)
				mfp.currTemplate.closeBtn.detach();
		}


		if(mfp._lastFocusedEl) {
			$(mfp._lastFocusedEl).focus(); // put tab focus back
		}
		mfp.currItem = null;	
		mfp.content = null;
		mfp.currTemplate = null;
		mfp.prevHeight = 0;

		_mfpTrigger(AFTER_CLOSE_EVENT);
	},
	
	updateSize: function(winHeight) {

		if(mfp.isIOS) {
			// fixes iOS nav bars https://github.com/dimsemenov/Magnific-Popup/issues/2
			var zoomLevel = document.documentElement.clientWidth / window.innerWidth;
			var height = window.innerHeight * zoomLevel;
			mfp.wrap.css('height', height);
			mfp.wH = height;
		} else {
			mfp.wH = winHeight || _window.height();
		}
		// Fixes #84: popup incorrectly positioned with position:relative on body
		if(!mfp.fixedContentPos) {
			mfp.wrap.css('height', mfp.wH);
		}

		_mfpTrigger('Resize');

	},

	/**
	 * Set content of popup based on current index
	 */
	updateItemHTML: function() {
		var item = mfp.items[mfp.index];

		// Detach and perform modifications
		mfp.contentContainer.detach();

		if(mfp.content)
			mfp.content.detach();

		if(!item.parsed) {
			item = mfp.parseEl( mfp.index );
		}

		var type = item.type;	

		_mfpTrigger('BeforeChange', [mfp.currItem ? mfp.currItem.type : '', type]);
		// BeforeChange event works like so:
		// _mfpOn('BeforeChange', function(e, prevType, newType) { });
		
		mfp.currItem = item;

		

		

		if(!mfp.currTemplate[type]) {
			var markup = mfp.st[type] ? mfp.st[type].markup : false;

			// allows to modify markup
			_mfpTrigger('FirstMarkupParse', markup);

			if(markup) {
				mfp.currTemplate[type] = $(markup);
			} else {
				// if there is no markup found we just define that template is parsed
				mfp.currTemplate[type] = true;
			}
		}

		if(_prevContentType && _prevContentType !== item.type) {
			mfp.container.removeClass('mfp-'+_prevContentType+'-holder');
		}
		
		var newContent = mfp['get' + type.charAt(0).toUpperCase() + type.slice(1)](item, mfp.currTemplate[type]);
		mfp.appendContent(newContent, type);

		item.preloaded = true;

		_mfpTrigger(CHANGE_EVENT, item);
		_prevContentType = item.type;
		
		// Append container back after its content changed
		mfp.container.prepend(mfp.contentContainer);

		_mfpTrigger('AfterChange');
	},


	/**
	 * Set HTML content of popup
	 */
	appendContent: function(newContent, type) {
		mfp.content = newContent;
		
		if(newContent) {
			if(mfp.st.showCloseBtn && mfp.st.closeBtnInside &&
				mfp.currTemplate[type] === true) {
				// if there is no markup, we just append close button element inside
				if(!mfp.content.find('.mfp-close').length) {
					mfp.content.append(_getCloseBtn());
				}
			} else {
				mfp.content = newContent;
			}
		} else {
			mfp.content = '';
		}

		_mfpTrigger(BEFORE_APPEND_EVENT);
		mfp.container.addClass('mfp-'+type+'-holder');

		mfp.contentContainer.append(mfp.content);
	},



	
	/**
	 * Creates Magnific Popup data object based on given data
	 * @param  {int} index Index of item to parse
	 */
	parseEl: function(index) {
		var item = mfp.items[index],
			type;

		if(item.tagName) {
			item = { el: $(item) };
		} else {
			type = item.type;
			item = { data: item, src: item.src };
		}

		if(item.el) {
			var types = mfp.types;

			// check for 'mfp-TYPE' class
			for(var i = 0; i < types.length; i++) {
				if( item.el.hasClass('mfp-'+types[i]) ) {
					type = types[i];
					break;
				}
			}

			item.src = item.el.attr('data-mfp-src');
			if(!item.src) {
				item.src = item.el.attr('href');
			}
		}

		item.type = type || mfp.st.type || 'inline';
		item.index = index;
		item.parsed = true;
		mfp.items[index] = item;
		_mfpTrigger('ElementParse', item);

		return mfp.items[index];
	},


	/**
	 * Initializes single popup or a group of popups
	 */
	addGroup: function(el, options) {
		var eHandler = function(e) {
			e.mfpEl = this;
			mfp._openClick(e, el, options);
		};

		if(!options) {
			options = {};
		} 

		var eName = 'click.magnificPopup';
		options.mainEl = el;
		
		if(options.items) {
			options.isObj = true;
			el.off(eName).on(eName, eHandler);
		} else {
			options.isObj = false;
			if(options.delegate) {
				el.off(eName).on(eName, options.delegate , eHandler);
			} else {
				options.items = el;
				el.off(eName).on(eName, eHandler);
			}
		}
	},
	_openClick: function(e, el, options) {
		var midClick = options.midClick !== undefined ? options.midClick : $.magnificPopup.defaults.midClick;


		if(!midClick && ( e.which === 2 || e.ctrlKey || e.metaKey ) ) {
			return;
		}

		var disableOn = options.disableOn !== undefined ? options.disableOn : $.magnificPopup.defaults.disableOn;

		if(disableOn) {
			if($.isFunction(disableOn)) {
				if( !disableOn.call(mfp) ) {
					return true;
				}
			} else { // else it's number
				if( _window.width() < disableOn ) {
					return true;
				}
			}
		}
		
		if(e.type) {
			e.preventDefault();

			// This will prevent popup from closing if element is inside and popup is already opened
			if(mfp.isOpen) {
				e.stopPropagation();
			}
		}
			

		options.el = $(e.mfpEl);
		if(options.delegate) {
			options.items = el.find(options.delegate);
		}
		mfp.open(options);
	},


	/**
	 * Updates text on preloader
	 */
	updateStatus: function(status, text) {

		if(mfp.preloader) {
			if(_prevStatus !== status) {
				mfp.container.removeClass('mfp-s-'+_prevStatus);
			}

			if(!text && status === 'loading') {
				text = mfp.st.tLoading;
			}

			var data = {
				status: status,
				text: text
			};
			// allows to modify status
			_mfpTrigger('UpdateStatus', data);

			status = data.status;
			text = data.text;

			mfp.preloader.html(text);

			mfp.preloader.find('a').on('click', function(e) {
				e.stopImmediatePropagation();
			});

			mfp.container.addClass('mfp-s-'+status);
			_prevStatus = status;
		}
	},


	/*
		"Private" helpers that aren't private at all
	 */
	// Check to close popup or not
	// "target" is an element that was clicked
	_checkIfClose: function(target) {

		if($(target).hasClass(PREVENT_CLOSE_CLASS)) {
			return;
		}

		var closeOnContent = mfp.st.closeOnContentClick;
		var closeOnBg = mfp.st.closeOnBgClick;

		if(closeOnContent && closeOnBg) {
			return true;
		} else {

			// We close the popup if click is on close button or on preloader. Or if there is no content.
			if(!mfp.content || $(target).hasClass('mfp-close') || (mfp.preloader && target === mfp.preloader[0]) ) {
				return true;
			}

			// if click is outside the content
			if(  (target !== mfp.content[0] && !$.contains(mfp.content[0], target))  ) {
				if(closeOnBg) {
					// last check, if the clicked element is in DOM, (in case it's removed onclick)
					if( $.contains(document, target) ) {
						return true;
					}
				}
			} else if(closeOnContent) {
				return true;
			}

		}
		return false;
	},
	_addClassToMFP: function(cName) {
		mfp.bgOverlay.addClass(cName);
		mfp.wrap.addClass(cName);
	},
	_removeClassFromMFP: function(cName) {
		this.bgOverlay.removeClass(cName);
		mfp.wrap.removeClass(cName);
	},
	_hasScrollBar: function(winHeight) {
		return (  (mfp.isIE7 ? _document.height() : document.body.scrollHeight) > (winHeight || _window.height()) );
	},
	_setFocus: function() {
		(mfp.st.focus ? mfp.content.find(mfp.st.focus).eq(0) : mfp.wrap).focus();
	},
	_onFocusIn: function(e) {
		if( e.target !== mfp.wrap[0] && !$.contains(mfp.wrap[0], e.target) ) {
			mfp._setFocus();
			return false;
		}
	},
	_parseMarkup: function(template, values, item) {
		var arr;
		if(item.data) {
			values = $.extend(item.data, values);
		}
		_mfpTrigger(MARKUP_PARSE_EVENT, [template, values, item] );

		$.each(values, function(key, value) {
			if(value === undefined || value === false) {
				return true;
			}
			arr = key.split('_');
			if(arr.length > 1) {
				var el = template.find(EVENT_NS + '-'+arr[0]);

				if(el.length > 0) {
					var attr = arr[1];
					if(attr === 'replaceWith') {
						if(el[0] !== value[0]) {
							el.replaceWith(value);
						}
					} else if(attr === 'img') {
						if(el.is('img')) {
							el.attr('src', value);
						} else {
							el.replaceWith( '<img src="'+value+'" class="' + el.attr('class') + '" />' );
						}
					} else {
						el.attr(arr[1], value);
					}
				}

			} else {
				template.find(EVENT_NS + '-'+key).html(value);
			}
		});
	},

	_getScrollbarSize: function() {
		// thx David
		if(mfp.scrollbarSize === undefined) {
			var scrollDiv = document.createElement("div");
			scrollDiv.style.cssText = 'width: 99px; height: 99px; overflow: scroll; position: absolute; top: -9999px;';
			document.body.appendChild(scrollDiv);
			mfp.scrollbarSize = scrollDiv.offsetWidth - scrollDiv.clientWidth;
			document.body.removeChild(scrollDiv);
		}
		return mfp.scrollbarSize;
	}

}; /* MagnificPopup core prototype end */




/**
 * Public static functions
 */
$.magnificPopup = {
	instance: null,
	proto: MagnificPopup.prototype,
	modules: [],

	open: function(options, index) {
		_checkInstance();	

		if(!options) {
			options = {};
		} else {
			options = $.extend(true, {}, options);
		}
			

		options.isObj = true;
		options.index = index || 0;
		return this.instance.open(options);
	},

	close: function() {
		return $.magnificPopup.instance && $.magnificPopup.instance.close();
	},

	registerModule: function(name, module) {
		if(module.options) {
			$.magnificPopup.defaults[name] = module.options;
		}
		$.extend(this.proto, module.proto);			
		this.modules.push(name);
	},

	defaults: {   

		// Info about options is in docs:
		// http://dimsemenov.com/plugins/magnific-popup/documentation.html#options
		
		disableOn: 0,	

		key: null,

		midClick: false,

		mainClass: '',

		preloader: true,

		focus: '', // CSS selector of input to focus after popup is opened
		
		closeOnContentClick: false,

		closeOnBgClick: true,

		closeBtnInside: true, 

		showCloseBtn: true,

		enableEscapeKey: true,

		modal: false,

		alignTop: false,
	
		removalDelay: 0,

		prependTo: null,
		
		fixedContentPos: 'auto', 
	
		fixedBgPos: 'auto',

		overflowY: 'auto',

		closeMarkup: '<button title="%title%" type="button" class="mfp-close">&times;</button>',

		tClose: 'Close (Esc)',

		tLoading: 'Loading...'

	}
};



$.fn.magnificPopup = function(options) {
	_checkInstance();

	var jqEl = $(this);

	// We call some API method of first param is a string
	if (typeof options === "string" ) {

		if(options === 'open') {
			var items,
				itemOpts = _isJQ ? jqEl.data('magnificPopup') : jqEl[0].magnificPopup,
				index = parseInt(arguments[1], 10) || 0;

			if(itemOpts.items) {
				items = itemOpts.items[index];
			} else {
				items = jqEl;
				if(itemOpts.delegate) {
					items = items.find(itemOpts.delegate);
				}
				items = items.eq( index );
			}
			mfp._openClick({mfpEl:items}, jqEl, itemOpts);
		} else {
			if(mfp.isOpen)
				mfp[options].apply(mfp, Array.prototype.slice.call(arguments, 1));
		}

	} else {
		// clone options obj
		options = $.extend(true, {}, options);
		
		/*
		 * As Zepto doesn't support .data() method for objects 
		 * and it works only in normal browsers
		 * we assign "options" object directly to the DOM element. FTW!
		 */
		if(_isJQ) {
			jqEl.data('magnificPopup', options);
		} else {
			jqEl[0].magnificPopup = options;
		}

		mfp.addGroup(jqEl, options);

	}
	return jqEl;
};


//Quick benchmark
/*
var start = performance.now(),
	i,
	rounds = 1000;

for(i = 0; i < rounds; i++) {

}
console.log('Test #1:', performance.now() - start);

start = performance.now();
for(i = 0; i < rounds; i++) {

}
console.log('Test #2:', performance.now() - start);
*/


/*>>core*/

/*>>inline*/

var INLINE_NS = 'inline',
	_hiddenClass,
	_inlinePlaceholder, 
	_lastInlineElement,
	_putInlineElementsBack = function() {
		if(_lastInlineElement) {
			_inlinePlaceholder.after( _lastInlineElement.addClass(_hiddenClass) ).detach();
			_lastInlineElement = null;
		}
	};

$.magnificPopup.registerModule(INLINE_NS, {
	options: {
		hiddenClass: 'hide', // will be appended with `mfp-` prefix
		markup: '',
		tNotFound: 'Content not found'
	},
	proto: {

		initInline: function() {
			mfp.types.push(INLINE_NS);

			_mfpOn(CLOSE_EVENT+'.'+INLINE_NS, function() {
				_putInlineElementsBack();
			});
		},

		getInline: function(item, template) {

			_putInlineElementsBack();

			if(item.src) {
				var inlineSt = mfp.st.inline,
					el = $(item.src);

				if(el.length) {

					// If target element has parent - we replace it with placeholder and put it back after popup is closed
					var parent = el[0].parentNode;
					if(parent && parent.tagName) {
						if(!_inlinePlaceholder) {
							_hiddenClass = inlineSt.hiddenClass;
							_inlinePlaceholder = _getEl(_hiddenClass);
							_hiddenClass = 'mfp-'+_hiddenClass;
						}
						// replace target inline element with placeholder
						_lastInlineElement = el.after(_inlinePlaceholder).detach().removeClass(_hiddenClass);
					}

					mfp.updateStatus('ready');
				} else {
					mfp.updateStatus('error', inlineSt.tNotFound);
					el = $('<div>');
				}

				item.inlineElement = el;
				return el;
			}

			mfp.updateStatus('ready');
			mfp._parseMarkup(template, {}, item);
			return template;
		}
	}
});

/*>>inline*/

/*>>ajax*/
var AJAX_NS = 'ajax',
	_ajaxCur,
	_removeAjaxCursor = function() {
		if(_ajaxCur) {
			_body.removeClass(_ajaxCur);
		}
	},
	_destroyAjaxRequest = function() {
		_removeAjaxCursor();
		if(mfp.req) {
			mfp.req.abort();
		}
	};

$.magnificPopup.registerModule(AJAX_NS, {

	options: {
		settings: null,
		cursor: 'mfp-ajax-cur',
		tError: '<a href="%url%">The content</a> could not be loaded.'
	},

	proto: {
		initAjax: function() {
			mfp.types.push(AJAX_NS);
			_ajaxCur = mfp.st.ajax.cursor;

			_mfpOn(CLOSE_EVENT+'.'+AJAX_NS, _destroyAjaxRequest);
			_mfpOn('BeforeChange.' + AJAX_NS, _destroyAjaxRequest);
		},
		getAjax: function(item) {

			if(_ajaxCur)
				_body.addClass(_ajaxCur);

			mfp.updateStatus('loading');

			var opts = $.extend({
				url: item.src,
				success: function(data, textStatus, jqXHR) {
					var temp = {
						data:data,
						xhr:jqXHR
					};

					_mfpTrigger('ParseAjax', temp);

					mfp.appendContent( $(temp.data), AJAX_NS );

					item.finished = true;

					_removeAjaxCursor();

					mfp._setFocus();

					setTimeout(function() {
						mfp.wrap.addClass(READY_CLASS);
					}, 16);

					mfp.updateStatus('ready');

					_mfpTrigger('AjaxContentAdded');
				},
				error: function() {
					_removeAjaxCursor();
					item.finished = item.loadError = true;
					mfp.updateStatus('error', mfp.st.ajax.tError.replace('%url%', item.src));
				}
			}, mfp.st.ajax.settings);

			mfp.req = $.ajax(opts);

			return '';
		}
	}
});





	

/*>>ajax*/

/*>>image*/
var _imgInterval,
	_getTitle = function(item) {
		if(item.data && item.data.title !== undefined) 
			return item.data.title;

		var src = mfp.st.image.titleSrc;

		if(src) {
			if($.isFunction(src)) {
				return src.call(mfp, item);
			} else if(item.el) {
				return item.el.attr(src) || '';
			}
		}
		return '';
	};

$.magnificPopup.registerModule('image', {

	options: {
		markup: '<div class="mfp-figure">'+
					'<div class="mfp-close"></div>'+
					'<figure>'+
						'<div class="mfp-img"></div>'+
						'<figcaption>'+
							'<div class="mfp-bottom-bar">'+
								'<div class="mfp-title"></div>'+
								'<div class="mfp-counter"></div>'+
							'</div>'+
						'</figcaption>'+
					'</figure>'+
				'</div>',
		cursor: 'mfp-zoom-out-cur',
		titleSrc: 'title', 
		verticalFit: true,
		tError: '<a href="%url%">The image</a> could not be loaded.'
	},

	proto: {
		initImage: function() {
			var imgSt = mfp.st.image,
				ns = '.image';

			mfp.types.push('image');

			_mfpOn(OPEN_EVENT+ns, function() {
				if(mfp.currItem.type === 'image' && imgSt.cursor) {
					_body.addClass(imgSt.cursor);
				}
			});

			_mfpOn(CLOSE_EVENT+ns, function() {
				if(imgSt.cursor) {
					_body.removeClass(imgSt.cursor);
				}
				_window.off('resize' + EVENT_NS);
			});

			_mfpOn('Resize'+ns, mfp.resizeImage);
			if(mfp.isLowIE) {
				_mfpOn('AfterChange', mfp.resizeImage);
			}
		},
		resizeImage: function() {
			var item = mfp.currItem;
			if(!item || !item.img) return;

			if(mfp.st.image.verticalFit) {
				var decr = 0;
				// fix box-sizing in ie7/8
				if(mfp.isLowIE) {
					decr = parseInt(item.img.css('padding-top'), 10) + parseInt(item.img.css('padding-bottom'),10);
				}
				item.img.css('max-height', mfp.wH-decr);
			}
		},
		_onImageHasSize: function(item) {
			if(item.img) {
				
				item.hasSize = true;

				if(_imgInterval) {
					clearInterval(_imgInterval);
				}
				
				item.isCheckingImgSize = false;

				_mfpTrigger('ImageHasSize', item);

				if(item.imgHidden) {
					if(mfp.content)
						mfp.content.removeClass('mfp-loading');
					
					item.imgHidden = false;
				}

			}
		},

		/**
		 * Function that loops until the image has size to display elements that rely on it asap
		 */
		findImageSize: function(item) {

			var counter = 0,
				img = item.img[0],
				mfpSetInterval = function(delay) {

					if(_imgInterval) {
						clearInterval(_imgInterval);
					}
					// decelerating interval that checks for size of an image
					_imgInterval = setInterval(function() {
						if(img.naturalWidth > 0) {
							mfp._onImageHasSize(item);
							return;
						}

						if(counter > 200) {
							clearInterval(_imgInterval);
						}

						counter++;
						if(counter === 3) {
							mfpSetInterval(10);
						} else if(counter === 40) {
							mfpSetInterval(50);
						} else if(counter === 100) {
							mfpSetInterval(500);
						}
					}, delay);
				};

			mfpSetInterval(1);
		},

		getImage: function(item, template) {

			var guard = 0,

				// image load complete handler
				onLoadComplete = function() {
					if(item) {
						if (item.img[0].complete) {
							item.img.off('.mfploader');
							
							if(item === mfp.currItem){
								mfp._onImageHasSize(item);

								mfp.updateStatus('ready');
							}

							item.hasSize = true;
							item.loaded = true;

							_mfpTrigger('ImageLoadComplete');
							
						}
						else {
							// if image complete check fails 200 times (20 sec), we assume that there was an error.
							guard++;
							if(guard < 200) {
								setTimeout(onLoadComplete,100);
							} else {
								onLoadError();
							}
						}
					}
				},

				// image error handler
				onLoadError = function() {
					if(item) {
						item.img.off('.mfploader');
						if(item === mfp.currItem){
							mfp._onImageHasSize(item);
							mfp.updateStatus('error', imgSt.tError.replace('%url%', item.src) );
						}

						item.hasSize = true;
						item.loaded = true;
						item.loadError = true;
					}
				},
				imgSt = mfp.st.image;


			var el = template.find('.mfp-img');
			if(el.length) {
				var img = document.createElement('img');
				img.className = 'mfp-img';
				if(item.el && item.el.find('img').length) {
					img.alt = item.el.find('img').attr('alt');
				}
				item.img = $(img).on('load.mfploader', onLoadComplete).on('error.mfploader', onLoadError);
				img.src = item.src;

				// without clone() "error" event is not firing when IMG is replaced by new IMG
				// TODO: find a way to avoid such cloning
				if(el.is('img')) {
					item.img = item.img.clone();
				}

				img = item.img[0];
				if(img.naturalWidth > 0) {
					item.hasSize = true;
				} else if(!img.width) {										
					item.hasSize = false;
				}
			}

			mfp._parseMarkup(template, {
				title: _getTitle(item),
				img_replaceWith: item.img
			}, item);

			mfp.resizeImage();

			if(item.hasSize) {
				if(_imgInterval) clearInterval(_imgInterval);

				if(item.loadError) {
					template.addClass('mfp-loading');
					mfp.updateStatus('error', imgSt.tError.replace('%url%', item.src) );
				} else {
					template.removeClass('mfp-loading');
					mfp.updateStatus('ready');
				}
				return template;
			}

			mfp.updateStatus('loading');
			item.loading = true;

			if(!item.hasSize) {
				item.imgHidden = true;
				template.addClass('mfp-loading');
				mfp.findImageSize(item);
			} 

			return template;
		}
	}
});



/*>>image*/

/*>>zoom*/
var hasMozTransform,
	getHasMozTransform = function() {
		if(hasMozTransform === undefined) {
			hasMozTransform = document.createElement('p').style.MozTransform !== undefined;
		}
		return hasMozTransform;		
	};

$.magnificPopup.registerModule('zoom', {

	options: {
		enabled: false,
		easing: 'ease-in-out',
		duration: 300,
		opener: function(element) {
			return element.is('img') ? element : element.find('img');
		}
	},

	proto: {

		initZoom: function() {
			var zoomSt = mfp.st.zoom,
				ns = '.zoom',
				image;
				
			if(!zoomSt.enabled || !mfp.supportsTransition) {
				return;
			}

			var duration = zoomSt.duration,
				getElToAnimate = function(image) {
					var newImg = image.clone().removeAttr('style').removeAttr('class').addClass('mfp-animated-image'),
						transition = 'all '+(zoomSt.duration/1000)+'s ' + zoomSt.easing,
						cssObj = {
							position: 'fixed',
							zIndex: 9999,
							left: 0,
							top: 0,
							'-webkit-backface-visibility': 'hidden'
						},
						t = 'transition';

					cssObj['-webkit-'+t] = cssObj['-moz-'+t] = cssObj['-o-'+t] = cssObj[t] = transition;

					newImg.css(cssObj);
					return newImg;
				},
				showMainContent = function() {
					mfp.content.css('visibility', 'visible');
				},
				openTimeout,
				animatedImg;

			_mfpOn('BuildControls'+ns, function() {
				if(mfp._allowZoom()) {

					clearTimeout(openTimeout);
					mfp.content.css('visibility', 'hidden');

					// Basically, all code below does is clones existing image, puts in on top of the current one and animated it
					
					image = mfp._getItemToZoom();

					if(!image) {
						showMainContent();
						return;
					}

					animatedImg = getElToAnimate(image); 
					
					animatedImg.css( mfp._getOffset() );

					mfp.wrap.append(animatedImg);

					openTimeout = setTimeout(function() {
						animatedImg.css( mfp._getOffset( true ) );
						openTimeout = setTimeout(function() {

							showMainContent();

							setTimeout(function() {
								animatedImg.remove();
								image = animatedImg = null;
								_mfpTrigger('ZoomAnimationEnded');
							}, 16); // avoid blink when switching images 

						}, duration); // this timeout equals animation duration

					}, 16); // by adding this timeout we avoid short glitch at the beginning of animation


					// Lots of timeouts...
				}
			});
			_mfpOn(BEFORE_CLOSE_EVENT+ns, function() {
				if(mfp._allowZoom()) {

					clearTimeout(openTimeout);

					mfp.st.removalDelay = duration;

					if(!image) {
						image = mfp._getItemToZoom();
						if(!image) {
							return;
						}
						animatedImg = getElToAnimate(image);
					}
					
					
					animatedImg.css( mfp._getOffset(true) );
					mfp.wrap.append(animatedImg);
					mfp.content.css('visibility', 'hidden');
					
					setTimeout(function() {
						animatedImg.css( mfp._getOffset() );
					}, 16);
				}

			});

			_mfpOn(CLOSE_EVENT+ns, function() {
				if(mfp._allowZoom()) {
					showMainContent();
					if(animatedImg) {
						animatedImg.remove();
					}
					image = null;
				}	
			});
		},

		_allowZoom: function() {
			return mfp.currItem.type === 'image';
		},

		_getItemToZoom: function() {
			if(mfp.currItem.hasSize) {
				return mfp.currItem.img;
			} else {
				return false;
			}
		},

		// Get element postion relative to viewport
		_getOffset: function(isLarge) {
			var el;
			if(isLarge) {
				el = mfp.currItem.img;
			} else {
				el = mfp.st.zoom.opener(mfp.currItem.el || mfp.currItem);
			}

			var offset = el.offset();
			var paddingTop = parseInt(el.css('padding-top'),10);
			var paddingBottom = parseInt(el.css('padding-bottom'),10);
			offset.top -= ( $(window).scrollTop() - paddingTop );


			/*
			
			Animating left + top + width/height looks glitchy in Firefox, but perfect in Chrome. And vice-versa.

			 */
			var obj = {
				width: el.width(),
				// fix Zepto height+padding issue
				height: (_isJQ ? el.innerHeight() : el[0].offsetHeight) - paddingBottom - paddingTop
			};

			// I hate to do this, but there is no another option
			if( getHasMozTransform() ) {
				obj['-moz-transform'] = obj['transform'] = 'translate(' + offset.left + 'px,' + offset.top + 'px)';
			} else {
				obj.left = offset.left;
				obj.top = offset.top;
			}
			return obj;
		}

	}
});



/*>>zoom*/

/*>>iframe*/

var IFRAME_NS = 'iframe',
	_emptyPage = '//about:blank',
	
	_fixIframeBugs = function(isShowing) {
		if(mfp.currTemplate[IFRAME_NS]) {
			var el = mfp.currTemplate[IFRAME_NS].find('iframe');
			if(el.length) { 
				// reset src after the popup is closed to avoid "video keeps playing after popup is closed" bug
				if(!isShowing) {
					el[0].src = _emptyPage;
				}

				// IE8 black screen bug fix
				if(mfp.isIE8) {
					el.css('display', isShowing ? 'block' : 'none');
				}
			}
		}
	};

$.magnificPopup.registerModule(IFRAME_NS, {

	options: {
		markup: '<div class="mfp-iframe-scaler">'+
					'<div class="mfp-close"></div>'+
					'<iframe class="mfp-iframe" src="//about:blank" frameborder="0" allowfullscreen></iframe>'+
				'</div>',

		srcAction: 'iframe_src',

		// we don't care and support only one default type of URL by default
		patterns: {
			youtube: {
				index: 'youtube.com', 
				id: 'v=', 
				src: '//www.youtube.com/embed/%id%?autoplay=1'
			},
			vimeo: {
				index: 'vimeo.com/',
				id: '/',
				src: '//player.vimeo.com/video/%id%?autoplay=1'
			},
			gmaps: {
				index: '//maps.google.',
				src: '%id%&output=embed'
			}
		}
	},

	proto: {
		initIframe: function() {
			mfp.types.push(IFRAME_NS);

			_mfpOn('BeforeChange', function(e, prevType, newType) {
				if(prevType !== newType) {
					if(prevType === IFRAME_NS) {
						_fixIframeBugs(); // iframe if removed
					} else if(newType === IFRAME_NS) {
						_fixIframeBugs(true); // iframe is showing
					} 
				}// else {
					// iframe source is switched, don't do anything
				//}
			});

			_mfpOn(CLOSE_EVENT + '.' + IFRAME_NS, function() {
				_fixIframeBugs();
			});
		},

		getIframe: function(item, template) {
			var embedSrc = item.src;
			var iframeSt = mfp.st.iframe;
				
			$.each(iframeSt.patterns, function() {
				if(embedSrc.indexOf( this.index ) > -1) {
					if(this.id) {
						if(typeof this.id === 'string') {
							embedSrc = embedSrc.substr(embedSrc.lastIndexOf(this.id)+this.id.length, embedSrc.length);
						} else {
							embedSrc = this.id.call( this, embedSrc );
						}
					}
					embedSrc = this.src.replace('%id%', embedSrc );
					return false; // break;
				}
			});
			
			var dataObj = {};
			if(iframeSt.srcAction) {
				dataObj[iframeSt.srcAction] = embedSrc;
			}
			mfp._parseMarkup(template, dataObj, item);

			mfp.updateStatus('ready');

			return template;
		}
	}
});



/*>>iframe*/

/*>>gallery*/
/**
 * Get looped index depending on number of slides
 */
var _getLoopedId = function(index) {
		var numSlides = mfp.items.length;
		if(index > numSlides - 1) {
			return index - numSlides;
		} else  if(index < 0) {
			return numSlides + index;
		}
		return index;
	},
	_replaceCurrTotal = function(text, curr, total) {
		return text.replace(/%curr%/gi, curr + 1).replace(/%total%/gi, total);
	};

$.magnificPopup.registerModule('gallery', {

	options: {
		enabled: false,
		arrowMarkup: '<button title="%title%" type="button" class="mfp-arrow mfp-arrow-%dir%"></button>',
		preload: [0,2],
		navigateByImgClick: true,
		arrows: true,

		tPrev: 'Previous (Left arrow key)',
		tNext: 'Next (Right arrow key)',
		tCounter: '%curr% of %total%'
	},

	proto: {
		initGallery: function() {

			var gSt = mfp.st.gallery,
				ns = '.mfp-gallery',
				supportsFastClick = Boolean($.fn.mfpFastClick);

			mfp.direction = true; // true - next, false - prev
			
			if(!gSt || !gSt.enabled ) return false;

			_wrapClasses += ' mfp-gallery';

			_mfpOn(OPEN_EVENT+ns, function() {

				if(gSt.navigateByImgClick) {
					mfp.wrap.on('click'+ns, '.mfp-img', function() {
						if(mfp.items.length > 1) {
							mfp.next();
							return false;
						}
					});
				}

				_document.on('keydown'+ns, function(e) {
					if (e.keyCode === 37) {
						mfp.prev();
					} else if (e.keyCode === 39) {
						mfp.next();
					}
				});
			});

			_mfpOn('UpdateStatus'+ns, function(e, data) {
				if(data.text) {
					data.text = _replaceCurrTotal(data.text, mfp.currItem.index, mfp.items.length);
				}
			});

			_mfpOn(MARKUP_PARSE_EVENT+ns, function(e, element, values, item) {
				var l = mfp.items.length;
				values.counter = l > 1 ? _replaceCurrTotal(gSt.tCounter, item.index, l) : '';
			});

			_mfpOn('BuildControls' + ns, function() {
				if(mfp.items.length > 1 && gSt.arrows && !mfp.arrowLeft) {
					var markup = gSt.arrowMarkup,
						arrowLeft = mfp.arrowLeft = $( markup.replace(/%title%/gi, gSt.tPrev).replace(/%dir%/gi, 'left') ).addClass(PREVENT_CLOSE_CLASS),			
						arrowRight = mfp.arrowRight = $( markup.replace(/%title%/gi, gSt.tNext).replace(/%dir%/gi, 'right') ).addClass(PREVENT_CLOSE_CLASS);

					var eName = supportsFastClick ? 'mfpFastClick' : 'click';
					arrowLeft[eName](function() {
						mfp.prev();
					});			
					arrowRight[eName](function() {
						mfp.next();
					});	

					// Polyfill for :before and :after (adds elements with classes mfp-a and mfp-b)
					if(mfp.isIE7) {
						_getEl('b', arrowLeft[0], false, true);
						_getEl('a', arrowLeft[0], false, true);
						_getEl('b', arrowRight[0], false, true);
						_getEl('a', arrowRight[0], false, true);
					}

					mfp.container.append(arrowLeft.add(arrowRight));
				}
			});

			_mfpOn(CHANGE_EVENT+ns, function() {
				if(mfp._preloadTimeout) clearTimeout(mfp._preloadTimeout);

				mfp._preloadTimeout = setTimeout(function() {
					mfp.preloadNearbyImages();
					mfp._preloadTimeout = null;
				}, 16);		
			});


			_mfpOn(CLOSE_EVENT+ns, function() {
				_document.off(ns);
				mfp.wrap.off('click'+ns);
			
				if(mfp.arrowLeft && supportsFastClick) {
					mfp.arrowLeft.add(mfp.arrowRight).destroyMfpFastClick();
				}
				mfp.arrowRight = mfp.arrowLeft = null;
			});

		}, 
		next: function() {
			mfp.direction = true;
			mfp.index = _getLoopedId(mfp.index + 1);
			mfp.updateItemHTML();
		},
		prev: function() {
			mfp.direction = false;
			mfp.index = _getLoopedId(mfp.index - 1);
			mfp.updateItemHTML();
		},
		goTo: function(newIndex) {
			mfp.direction = (newIndex >= mfp.index);
			mfp.index = newIndex;
			mfp.updateItemHTML();
		},
		preloadNearbyImages: function() {
			var p = mfp.st.gallery.preload,
				preloadBefore = Math.min(p[0], mfp.items.length),
				preloadAfter = Math.min(p[1], mfp.items.length),
				i;

			for(i = 1; i <= (mfp.direction ? preloadAfter : preloadBefore); i++) {
				mfp._preloadItem(mfp.index+i);
			}
			for(i = 1; i <= (mfp.direction ? preloadBefore : preloadAfter); i++) {
				mfp._preloadItem(mfp.index-i);
			}
		},
		_preloadItem: function(index) {
			index = _getLoopedId(index);

			if(mfp.items[index].preloaded) {
				return;
			}

			var item = mfp.items[index];
			if(!item.parsed) {
				item = mfp.parseEl( index );
			}

			_mfpTrigger('LazyLoad', item);

			if(item.type === 'image') {
				item.img = $('<img class="mfp-img" />').on('load.mfploader', function() {
					item.hasSize = true;
				}).on('error.mfploader', function() {
					item.hasSize = true;
					item.loadError = true;
					_mfpTrigger('LazyLoadError', item);
				}).attr('src', item.src);
			}


			item.preloaded = true;
		}
	}
});

/*
Touch Support that might be implemented some day

addSwipeGesture: function() {
	var startX,
		moved,
		multipleTouches;

		return;

	var namespace = '.mfp',
		addEventNames = function(pref, down, move, up, cancel) {
			mfp._tStart = pref + down + namespace;
			mfp._tMove = pref + move + namespace;
			mfp._tEnd = pref + up + namespace;
			mfp._tCancel = pref + cancel + namespace;
		};

	if(window.navigator.msPointerEnabled) {
		addEventNames('MSPointer', 'Down', 'Move', 'Up', 'Cancel');
	} else if('ontouchstart' in window) {
		addEventNames('touch', 'start', 'move', 'end', 'cancel');
	} else {
		return;
	}
	_window.on(mfp._tStart, function(e) {
		var oE = e.originalEvent;
		multipleTouches = moved = false;
		startX = oE.pageX || oE.changedTouches[0].pageX;
	}).on(mfp._tMove, function(e) {
		if(e.originalEvent.touches.length > 1) {
			multipleTouches = e.originalEvent.touches.length;
		} else {
			//e.preventDefault();
			moved = true;
		}
	}).on(mfp._tEnd + ' ' + mfp._tCancel, function(e) {
		if(moved && !multipleTouches) {
			var oE = e.originalEvent,
				diff = startX - (oE.pageX || oE.changedTouches[0].pageX);

			if(diff > 20) {
				mfp.next();
			} else if(diff < -20) {
				mfp.prev();
			}
		}
	});
},
*/


/*>>gallery*/

/*>>retina*/

var RETINA_NS = 'retina';

$.magnificPopup.registerModule(RETINA_NS, {
	options: {
		replaceSrc: function(item) {
			return item.src.replace(/\.\w+$/, function(m) { return '@2x' + m; });
		},
		ratio: 1 // Function or number.  Set to 1 to disable.
	},
	proto: {
		initRetina: function() {
			if(window.devicePixelRatio > 1) {

				var st = mfp.st.retina,
					ratio = st.ratio;

				ratio = !isNaN(ratio) ? ratio : ratio();

				if(ratio > 1) {
					_mfpOn('ImageHasSize' + '.' + RETINA_NS, function(e, item) {
						item.img.css({
							'max-width': item.img[0].naturalWidth / ratio,
							'width': '100%'
						});
					});
					_mfpOn('ElementParse' + '.' + RETINA_NS, function(e, item) {
						item.src = st.replaceSrc(item, ratio);
					});
				}
			}

		}
	}
});

/*>>retina*/

/*>>fastclick*/
/**
 * FastClick event implementation. (removes 300ms delay on touch devices)
 * Based on https://developers.google.com/mobile/articles/fast_buttons
 *
 * You may use it outside the Magnific Popup by calling just:
 *
 * $('.your-el').mfpFastClick(function() {
 *     console.log('Clicked!');
 * });
 *
 * To unbind:
 * $('.your-el').destroyMfpFastClick();
 * 
 * 
 * Note that it's a very basic and simple implementation, it blocks ghost click on the same element where it was bound.
 * If you need something more advanced, use plugin by FT Labs https://github.com/ftlabs/fastclick
 * 
 */

(function() {
	var ghostClickDelay = 1000,
		supportsTouch = 'ontouchstart' in window,
		unbindTouchMove = function() {
			_window.off('touchmove'+ns+' touchend'+ns);
		},
		eName = 'mfpFastClick',
		ns = '.'+eName;


	// As Zepto.js doesn't have an easy way to add custom events (like jQuery), so we implement it in this way
	$.fn.mfpFastClick = function(callback) {

		return $(this).each(function() {

			var elem = $(this),
				lock;

			if( supportsTouch ) {

				var timeout,
					startX,
					startY,
					pointerMoved,
					point,
					numPointers;

				elem.on('touchstart' + ns, function(e) {
					pointerMoved = false;
					numPointers = 1;

					point = e.originalEvent ? e.originalEvent.touches[0] : e.touches[0];
					startX = point.clientX;
					startY = point.clientY;

					_window.on('touchmove'+ns, function(e) {
						point = e.originalEvent ? e.originalEvent.touches : e.touches;
						numPointers = point.length;
						point = point[0];
						if (Math.abs(point.clientX - startX) > 10 ||
							Math.abs(point.clientY - startY) > 10) {
							pointerMoved = true;
							unbindTouchMove();
						}
					}).on('touchend'+ns, function(e) {
						unbindTouchMove();
						if(pointerMoved || numPointers > 1) {
							return;
						}
						lock = true;
						e.preventDefault();
						clearTimeout(timeout);
						timeout = setTimeout(function() {
							lock = false;
						}, ghostClickDelay);
						callback();
					});
				});

			}

			elem.on('click' + ns, function() {
				if(!lock) {
					callback();
				}
			});
		});
	};

	$.fn.destroyMfpFastClick = function() {
		$(this).off('touchstart' + ns + ' click' + ns);
		if(supportsTouch) _window.off('touchmove'+ns+' touchend'+ns);
	};
})();

/*>>fastclick*/
 _checkInstance(); }));
/*! iFrame Resizer (iframeSizer.min.js ) - v2.6.2 - 2014-10-11
 *  Desc: Force cross domain iframes to size to content.
 *  Requires: iframeResizer.contentWindow.min.js to be loaded into the target frame.
 *  Copyright: (c) 2014 David J. Bradshaw - dave@bradshaw.net
 *  License: MIT
 *	Requires: iframeResizer_child.js on host page.
 */

!function(){"use strict";function a(a,b,c){"addEventListener"in window?a.addEventListener(b,c,!1):"attachEvent"in window&&a.attachEvent("on"+b,c)}function b(){var a,b=["moz","webkit","o","ms"];for(a=0;a<b.length&&!w;a+=1)w=window[b[a]+"RequestAnimationFrame"];w||c(" RequestAnimationFrame not supported")}function c(a){y.log&&"object"==typeof console&&console.log(s+"[Host page"+u+"]"+a)}function d(a){function b(){function a(){h(z),f(),y.resizedCallback(z)}i(a,z,"resetPage")}function d(a){var b=a.id;c(" Removing iFrame: "+b),a.parentNode.removeChild(a),y.closedCallback(b),c(" --")}function e(){var a=x.substr(t).split(":");return{iframe:document.getElementById(a[0]),id:a[0],height:a[1],width:a[2],type:a[3]}}function j(a){var b=Number(y["max"+a]),d=Number(y["min"+a]),e=a.toLowerCase(),f=Number(z[e]);if(d>b)throw new Error("Value for min"+a+" can not be greater than max"+a);c(" Checking "+e+" is in range "+d+"-"+b),d>f&&(f=d,c(" Set "+e+" to min value")),f>b&&(f=b,c(" Set "+e+" to max value")),z[e]=""+f}function k(){var b=a.origin,d=z.iframe.src.split("/").slice(0,3).join("/");if(y.checkOrigin&&(c(" Checking connection is from: "+d),""+b!="null"&&b!==d))throw new Error("Unexpected message received from: "+b+" for "+z.iframe.id+". Message was: "+a.data+". This error can be disabled by adding the checkOrigin: false option.");return!0}function l(){return s===(""+x).substr(0,t)}function m(){var a=z.type in{"true":1,"false":1};return a&&c(" Ignoring init message from meta parent page"),a}function n(){var a=x.substr(x.indexOf(":")+r+6);c(" MessageCallback passed: {iframe: "+z.iframe.id+", message: "+a+"}"),y.messageCallback({iframe:z.iframe,message:a}),c(" --")}function o(){if(null===z.iframe)throw new Error("iFrame ("+z.id+") does not exist on "+u);return!0}function q(){c(" Reposition requested from iFrame"),v={x:z.width,y:z.height},f()}function w(){switch(z.type){case"close":d(z.iframe),y.resizedCallback(z);break;case"message":n();break;case"scrollTo":q();break;case"reset":g(z);break;case"init":b(),y.initCallback(z.iframe);break;default:b()}}var x=a.data,z={};l()&&(c(" Received: "+x),z=e(),j("Height"),j("Width"),!m()&&o()&&k()&&(w(),p=!1))}function e(){null===v&&(v={x:void 0!==window.pageXOffset?window.pageXOffset:document.documentElement.scrollLeft,y:void 0!==window.pageYOffset?window.pageYOffset:document.documentElement.scrollTop},c(" Get position: "+v.x+","+v.y))}function f(){null!==v&&(window.scrollTo(v.x,v.y),c(" Set position: "+v.x+","+v.y),v=null)}function g(a){function b(){h(a),j("reset","reset",a.iframe)}c(" Size reset requested by "+("init"===a.type?"host page":"iFrame")),e(),i(b,a,"init")}function h(a){function b(b){a.iframe.style[b]=a[b]+"px",c(" IFrame ("+a.iframe.id+") "+b+" set to "+a[b]+"px")}y.sizeHeight&&b("height"),y.sizeWidth&&b("width")}function i(a,b,d){d!==b.type&&w?(c(" Requesting animation frame"),w(a)):a()}function j(a,b,d){c("["+a+"] Sending msg to iframe ("+b+")"),d.contentWindow.postMessage(s+b,"*")}function k(){function b(){function a(a){1/0!==y[a]&&0!==y[a]&&(k.style[a]=y[a]+"px",c(" Set "+a+" = "+y[a]+"px"))}a("maxHeight"),a("minHeight"),a("maxWidth"),a("minWidth")}function d(a){return""===a&&(k.id=a="iFrameResizer"+o++,c(" Added missing iframe ID: "+a+" ("+k.src+")")),a}function e(){c(" IFrame scrolling "+(y.scrolling?"enabled":"disabled")+" for "+l),k.style.overflow=!1===y.scrolling?"hidden":"auto",k.scrolling=!1===y.scrolling?"no":"yes"}function f(){("number"==typeof y.bodyMargin||"0"===y.bodyMargin)&&(y.bodyMarginV1=y.bodyMargin,y.bodyMargin=""+y.bodyMargin+"px")}function h(){return l+":"+y.bodyMarginV1+":"+y.sizeWidth+":"+y.log+":"+y.interval+":"+y.enablePublicMethods+":"+y.autoResize+":"+y.bodyMargin+":"+y.heightCalculationMethod+":"+y.bodyBackground+":"+y.bodyPadding+":"+y.tolerance}function i(b){a(k,"load",function(){var a=p;j("iFrame.onload",b,k),!a&&y.heightCalculationMethod in x&&g({iframe:k,height:0,width:0,type:"init"})}),j("init",b,k)}var k=this,l=d(k.id);e(),b(),f(),i(h())}function l(a){if("object"!=typeof a)throw new TypeError("Options is not an object.")}function m(){function a(a){if("IFRAME"!==a.tagName.toUpperCase())throw new TypeError("Expected <IFRAME> tag, found <"+a.tagName+">.");k.call(a)}function b(a){a=a||{},l(a);for(var b in z)z.hasOwnProperty(b)&&(y[b]=a.hasOwnProperty(b)?a[b]:z[b])}return function(c,d){b(c),Array.prototype.forEach.call(document.querySelectorAll(d||"iframe"),a)}}function n(a){a.fn.iFrameResize=function(b){return b=b||{},l(b),y=a.extend({},z,b),this.filter("iframe").each(k).end()}}var o=0,p=!0,q="message",r=q.length,s="[iFrameSizer]",t=s.length,u="",v=null,w=window.requestAnimationFrame,x={max:1,scroll:1,bodyScroll:1,documentElementScroll:1},y={},z={autoResize:!0,bodyBackground:null,bodyMargin:null,bodyMarginV1:8,bodyPadding:null,checkOrigin:!0,enablePublicMethods:!1,heightCalculationMethod:"offset",interval:32,log:!1,maxHeight:1/0,maxWidth:1/0,minHeight:0,minWidth:0,scrolling:!1,sizeHeight:!0,sizeWidth:!1,tolerance:0,closedCallback:function(){},initCallback:function(){},messageCallback:function(){},resizedCallback:function(){}};b(),a(window,"message",d),"jQuery"in window&&n(jQuery),"function"==typeof define&&define.amd?define('frame-manager',[],function(){return m()}):window.iFrameResize=m()}();

if (!Array.prototype.forEach){
	Array.prototype.forEach = function(fun /*, thisArg */){
		"use strict";
		if (this === void 0 || this === null || typeof fun !== "function") throw new TypeError();

		var
			t = Object(this),
			len = t.length >>> 0,
			thisArg = arguments.length >= 2 ? arguments[1] : void 0;

		for (var i = 0; i < len; i++)
			if (i in t)
				fun.call(thisArg, t[i], i, t);
	};
}

+(function($) {
	$(document).ready(function()
	{
		$('iframe.responsive').iFrameResize({log:true, checkOrigin:false});
	});

	/** Changes for keeping popup in the centre for fundamental tool **/

	var __iframe;
	$(document).ready(function() {
		sendDimensions();
		if (__iframe !== undefined) {
			if (__iframe.addEventListener) {
				__iframe.addEventListener("load", sendDimensions, false);
			} else {
				__iframe.attachEvent("onload", sendDimensions);
			}
		}
	});
	$(window).scroll(function() {
		sendDimensions();
	});

	$(window).resize(function() {
		sendDimensions();
	});

	function sendDimensions() {

		fundamentalIndex = -1;
		$("iframe").each(function(index){
			tmpSrc = $(this).attr("src") || '';
			if(tmpSrc.indexOf("fundamental=true") > 0)
			{
				fundamentalIndex = index;
			}
		});
		if(fundamentalIndex>=0)
		{
			__iframe = document.querySelectorAll("iframe.responsive")[fundamentalIndex];
			var iFrameWindow = __iframe.contentWindow;
			var iframeSrcHost = getToolHostName(__iframe.src);
			iFrameWindow.postMessage("[Fundamentals_PageViewport]:" + $(window).height() + ":" + $(window).scrollTop() + ":" + $(__iframe).offset().top + ":" + $(__iframe).height(), iframeSrcHost);
		}
	}

	function getToolHostName(url) {
		var urlParser = document.createElement("a");
		urlParser.href = url;
		return urlParser.protocol + "//" + urlParser.host;
	}
})(jQuery);
(function(root) {
define("mgt_developertoolbar", ["jquery"], function() {
  return (function() {
var Cookie = {
    all: function() {
        var pairs = document.cookie.split(';');
        var cookies = {};
        pairs.each(function(item, index) {
            var pair = item.strip().split('=');
            cookies[unescape(pair[0])] = unescape(pair[1]);
        });

        return cookies;
    },
    read: function(cookieName) {
        var cookies = this.all();
        if(cookies[cookieName]) {
            return cookies[cookieName];
        }
        return null;
    },
    write: function(cookieName, cookieValue, cookieLifeTime) {
        var expires = '';
        if (cookieLifeTime) {
            var date = new Date();
            date.setTime(date.getTime()+(cookieLifeTime*1000));
            expires = '; expires='+date.toGMTString();
        }
        var urlPath = '/';
        document.cookie = escape(cookieName) + "=" + escape(cookieValue) + expires + "; path=" + urlPath;
    },
    clear: function(cookieName) {
        this.write(cookieName, '', -1);
    }
};

jQuery.noConflict();

jQuery(document).ready(function(){
  if (Cookie.read("mgt-developertoolbar") == 0)    {
    jQuery("#mgt-developer-toolbar").hide();  
    jQuery("#mgt-developer-toolbar-powered-by").hide();  
  }

  jQuery("#mgt-developer-toolbar-container img:first").click(function() {
    jQuery(".mgt-developer-toolbar-details").hide();
    jQuery("#mgt-developer-toolbar").toggle();
    jQuery("#mgt-developer-toolbar-powered-by").toggle();
    var display = jQuery("#mgt-developer-toolbar").attr("style");
    var toolbarHiddenExpression = /(none)/;
    if (toolbarHiddenExpression.exec(display)) {
      Cookie.write("mgt-developertoolbar", 0);
    } else {
      Cookie.write("mgt-developertoolbar", 1);    
    }
  });    
  
  jQuery("#mgt-developer-toolbar li").click(function() {
    var id = jQuery(this).attr("id");
    jQuery("#mgt-developer-toolbar li").removeClass("active");
    jQuery(this).addClass("active"); 
    jQuery(".mgt-developer-toolbar-details").each(function(e) {
      if (jQuery(this).attr("id") != id+"-details") {
        jQuery(this).hide();
      }
    });
    jQuery("#"+id+"-details").toggle();
  });
  
  jQuery("ul.mgt-developer-toolbar-tab-container li").click(function() {
    var id = jQuery(this).attr("id");
    jQuery("ul.mgt-developer-toolbar-tab-container li").removeClass("active"); 
    jQuery(this).addClass("active"); 
    jQuery(".mgt-developer-toolbar-tab-content").hide();
    jQuery("#"+id+"-content").show();
  });

  jQuery("#mgt-developer-toolbar-tab-blocks-content a.mgt-developer-toolbar-toggle-block-properties").click(function() {
    jQuery(this).next("ul.mgt-developer-toolbar-block-properties").toggle(); 
  });
  
  jQuery("#mgt-developer-toolbar-tab-blocks-content a.mgt-developer-toolbar-toggle-block-properties").click(function() {
     jQuery(this).next("ul.mgt-developer-toolbar-event-properties").toggle(); 
  });
  
  jQuery("#mgt-developer-toolbar-tab-events-content a.mgt-developer-toolbar-toggle-block-properties").click(function() {
    jQuery(this).next("ul.mgt-developer-toolbar-events").toggle(); 
});
  
});



  }).apply(root, arguments);
});
}(this));

define('rebrush/main',[
	'require',
	'lodash',
	'jquery',
	'cartware/components/Config',
	'cartware/components/Registry',
	'cartware/components/Dispatcher',
	'cartware/components/FeatureFlags',
	'cartware/components/Utilities',
	'cartware/components/Logger',
	'cartware/components/PubSubHub',
	'json!./handles/default.json',
	'rebrush/system/validation',
	'gigaset/spinner',
	'cartware-slider',
	'grunticon-loader',
	'magnific-popup',
	'frame-manager',
], function(
	require,
	_,
	$,
	Config,
	Registry,
	ComponentDispatcher,
	FeatureFlags,
	Utils,
	Logger,
	PubSubHub,
	Handles,
	Spinner
) {
	var gruntIconLastChange = 1792425600;

	grunticon([
		require.toUrl('skin/frontend/gigaset/rebrush/icons/dist/icons.data.svg.css?t=' + gruntIconLastChange),
		require.toUrl('skin/frontend/gigaset/rebrush/icons/dist/icons.data.png.css?t=' + gruntIconLastChange),
		require.toUrl('skin/frontend/gigaset/rebrush/icons/dist/icons.fallback.css?t=' + gruntIconLastChange)
	]);

	// Magnific Popup default values
	$.extend(true, $.magnificPopup.defaults, {
		fixedContentPos: false,
		fixedBgPos: true,
		overflowY: 'auto',
		closeBtnInside: true,
		preloader: false,
		midClick: true,
		removalDelay: 300,
		mainClass: 'gigaset-mfp-slide-bottom'	// slide in fx; alternative is gigaset-mfp-zoom-in
	});

	return {
		dispatcher: null,
		featureFlags: null,

		bootstrap: function() {
			Config.populate(window.gigaset || {});

			this.dispatcher = new ComponentDispatcher({
				handles: Utils.getPath(window, 'gigaset.handles') || []
			});

			this.bootstrapDispatcher();

			this.featureFlags = new FeatureFlags(Utils.getPath(window, 'gigaset.featureflags') || {});

			var developerMode = this.featureFlags.isEnabled('developer_mode');

			if (developerMode) {
				require(['mgt_developertoolbar']);
			}

			Logger.setDefaultLevel(developerMode ? Logger.levels.DEBUG : Logger.levels.ERROR);

			_.set(window, 'gigaset.debug.logger', Logger);

			Registry().register('featureFlags', this.featureFlags);
			Registry().register('system.dispatcher', this.dispatcher);
		},

		bootstrapDispatcher: function() {
			var self = this;

			Registry().register('component.dispatcher', this.dispatcher);

			_.forEach(Handles, function(components, handle) {
				require(components, function() {
					var handleMapApply = {};
					handleMapApply[handle] = Array.prototype.slice.call(arguments, 0);

					self.dispatcher.applyHandles(handleMapApply);
				});
			});
		}
	}
});

define('rebrush', ['rebrush/main'], function (main) { return main; });


define("json!gigaset_csp/handles/gigaset_csp.json", function(){ return {
	"gigaset_csp_service_product_register": [
		"./components/product/register"
	],

	"gigaset_csp_service_repair_register_plugin_product": [
		"./components/order/repair/form"
	],

	"gigaset_csp_service_repair_register_plugin_description": [
		"./components/order/repair/form/description"
	],

	"gigaset_csp_service_repair_register_plugin_address": [
		"./components/order/repair/form/address"
	],

	"gigaset_csp_service_repair_register_plugin_reference": [
		"./components/order/repair/form/reference"
	],

	"gigaset_csp_customer_request_index": [
		"./components/customer/request"
	],

	"gigaset_csp_handsetbase_registration" : [
		"./components/handsetbase/registration"
	],

	"gigaset_csp_compatibility_byproducts": [
		"./components/compatibility/byproducts"
	],

	"gigaset_csp_compatibility_byfeatures": [
		"./components/compatibility/byfeatures"
	],

	"gigaset_csp_support_detail": [
		"./components/support/detail/intro"
	],

	"gigaset_csp_support_detail_faq": [
		"./components/support/detail/faq",
		"./components/support/detail/faq/categories"
	],

	"gigaset_csp_search_field": [
		"./components/support/detail/faq",
		"./components/search/autocomplete"
	],

	"catalog_product_view": [
		"./components/product/support",
		"./components/product/downloads"
	],

	"gigaset_csp_support_search_results": [
		"./components/support/detail/faq"
	],

	"gigaset_csp_support_search_results_single": [
		"./components/support/detail/faq"
	],

	"catalogsearch_result_index": [
		"./components/support/detail/faq"
	],

	"gigaset_csp_support_downloads": [
		"./components/downloads/list"
	]
};});

define('gigaset_csp/main',[
	"require",
	"lodash",
	"cartware/components/Registry",
	"json!./handles/gigaset_csp.json"
], function(
	require,
	_,
	Registry,
	Handles
) {
	return {
		bootstrap: function() {
			this.bootstrapDispatcher();
		},

		bootstrapDispatcher: function() {
			Registry().retrieve("component.dispatcher", function(dispatcher) {
				_.forEach(Handles, function(components, handle) {
					require(components, function() {
						var handleMapApply = {};
						handleMapApply[handle] = Array.prototype.slice.call(arguments, 0);

						dispatcher.applyHandles(handleMapApply);
					});
				});
			});
		}
	};
});
define('gigaset_csp', ['gigaset_csp/main'], function (main) { return main; });


define("json!gigaset_stocknotification/handles/gigaset_stocknotification.json", function(){ return {
	"catalog_product_view": [
		"gigaset_stocknotification/components/product/view"
	],
	"catalog_category_view": [
		"gigaset_stocknotification/components/product/list"
	],
	"gigaset_contentbridge_cms_page": [
		"gigaset_stocknotification/components/product/list"
	]
};});

define('gigaset_stocknotification/main',[
	"require",
	"lodash",
	"cartware/components/Dispatcher",
	"cartware/components/Registry",
	"json!./handles/gigaset_stocknotification.json"
], function(
	require,
	_,
	ComponentDispatcher,
	Registry,
	Handles
) {
	return {
		bootstrap: function() {
			if (_.get(window, 'gigaset.featureflags.gigaset_stocknotification')) {
				this.bootstrapDispatcher();
			}
		},

		bootstrapDispatcher: function() {
			Registry().retrieve("component.dispatcher", function(dispatcher) {
				_.forEach(Handles, function(components, handle) {
					require(components, function() {
						var handleMapApply = {};
						handleMapApply[handle] = Array.prototype.slice.call(arguments, 0);

						dispatcher.applyHandles(handleMapApply);
					});
				});
			});
		}
	};
});
define('gigaset_stocknotification', ['gigaset_stocknotification/main'], function (main) { return main; });


define("json!cartware_checkout/handles/cartware_checkout.json", function(){ return {
	"checkout_onepage_index": [
		"./components/checkout",
		"./components/login",
		"./components/agreements",
	    "./components/acceptance",
		"./components/coupon",
		"./components/selects",
		"./components/address/selector",
	  	"./components/address/restriction",
		"./components/ops/payment"
	]
};});

define('cartware_checkout/main',[
	"require",
	"lodash",
	"lucid",
	"cartware/components/Registry",
	"json!./handles/cartware_checkout.json"
], function(
	require,
	_,
	lucidJS,
	Registry,
	Handles
) {
	var _instance = null;

	var getInstance = function() {
		if (_instance === null) {
			_instance = new Checkout();
		}

		return _instance;
	};

	function Checkout() {
		if (!(this instanceof Checkout)) {
			return getInstance();
		}

		lucidJS.EventEmitter.call(this);
		this._register = {};
	}

	Checkout.getInstance = getInstance;

	Checkout.ready = function(cb) {
		return getInstance().ready(cb);
	};

	Checkout.bootstrap = function() {
		return getInstance().bootstrap();
	};

	Checkout.prototype = _.create(new lucidJS.EventEmitter, {
		"constructor": Checkout,
		_register: null,

		bootstrap: function() {
			this.bootstrapDispatcher();
		},

		bootstrapDispatcher: function() {
			Registry().retrieve("component.dispatcher", function(dispatcher) {
				_.forEach(Handles, function(components, handle) {
					require(components, function() {
						var handleMapApply = {};
						handleMapApply[handle] = Array.prototype.slice.call(arguments, 0);

						dispatcher.applyHandles(handleMapApply);
					});
				});
			});
		},

		ready: function(cb) {
			this.bind("ready", cb);
		},

		updateProgress: function(json) {
			("progress-detail" in json.blocks) && $('col-right-opcheckout').update(json.blocks['progress-detail']);
			("progress-bar" in json.blocks) && $('opcheckout-progress-bar').update(json.blocks['progress-bar']);
		},

		register: function(key, value, graceful) {
			if (key in this._register) {
				if (graceful) return;

				throw "Registry key '" + key + "' has already been set.";
			}

			this._register[key] = value;
			this.flag("available." + key, value);


			return this;
		},

		unregister: function(key) {
			if (key in this._register) {
				delete this._register[key];
			}

			this.unflag("available." + key);

			return this;
		},

		retrieve: function(key, callback) {
			this.bind("available." + key, callback);
		}
	});

	return Checkout;
});
define('cartware_checkout', ['cartware_checkout/main'], function (main) { return main; });


define("json!gigaset_contentbridge/handles/gigaset_contentbridge.json", function(){ return {
	"gigaset_contentbridge_cms_page": [
		"./components/faq",
		"./components/customer-service",
		"./components/contact-form",
		"./components/news",
		"./components/manuals",
        "./components/slider",
		"./components/gallery"
	]
};});

define('gigaset_contentbridge/main',[
	"require",
	"lodash",
	"cartware/components/Registry",
	"json!./handles/gigaset_contentbridge.json"
], function(
	require,
	_,
	Registry,
	Handles
) {
	return {
		bootstrap: function() {
			this.bootstrapDispatcher();
		},

		bootstrapDispatcher: function() {
			Registry().retrieve("component.dispatcher", function(dispatcher) {
				_.forEach(Handles, function(components, handle) {
					require(components, function() {
						var handleMapApply = {};
						handleMapApply[handle] = Array.prototype.slice.call(arguments, 0);

						dispatcher.applyHandles(handleMapApply);
					});
				});
			});
		}
	};
});
define('gigaset_contentbridge', ['gigaset_contentbridge/main'], function (main) { return main; });


define("json!gigaset_shoppingcodes/handles/gigaset_shoppingcodes.json", function(){ return {
	"default": [
		"gigaset_shoppingcodes/components/form"
	]
};});

define('gigaset_shoppingcodes/main',[
	"require",
	"lodash",
	"cartware/components/Registry",
	"json!./handles/gigaset_shoppingcodes.json"
], function(
	require,
	_,
	Registry,
	Handles
) {
	return {
		bootstrap: function() {
			this.bootstrapDispatcher();
		},

		bootstrapDispatcher: function() {
			Registry().retrieve("component.dispatcher", function(dispatcher) {
				_.forEach(Handles, function(components, handle) {
					require(components, function() {
						var handleMapApply = {};
						handleMapApply[handle] = Array.prototype.slice.call(arguments, 0);

						dispatcher.applyHandles(handleMapApply);
					});
				});
			});
		}
	};
});
define('gigaset_shoppingcodes', ['gigaset_shoppingcodes/main'], function (main) { return main; });

define('cartware/components/_base',[
	"cartware/components/Logger",
	"cartware/components/PubSubHub"
], function(
	Logger,
	PubSubHub
) {

	return {
		create: function(properties) {
			var constructor = null;

			if ("constructor" in properties) {
				constructor = properties.constructor;
				delete properties.constructor;
			}

			function Component() {
				if (null !== constructor) {
					constructor.apply(this, arguments);
				}
			}

			if (!("logger" in properties)) {
				properties.logger = Logger;
			}

			if (!("hub" in properties)) {
				properties.hub = PubSubHub;
			}

			Component.prototype = properties;

			return Component;
		}
	}

});
define('cartware/dom/event/trigger',[],function() {
	var eventPrefix = "on";

	return function triggerEvent(element, type, options) {
		if ("undefined" !== typeof jQuery && element instanceof jQuery) {
			return element.each(function() {
				triggerEvent(this, type, options);
			});
		}

		if (element === undefined) {
			throw new Error("Parameter cannot be undefined");
		}

		if (!options) {
			options = {};
		}

		var o = {
			type: type,
			view: options.view || element.ownerDocument.defaultView,
			detail: options.detail || 1,
			screenX: options.screenX || 0,
			screenY: options.screenY || 0,
			clientX: options.clientX || 0,
			clientY: options.clientY || 0,
			button: options.button || 0,
			ctrlKey: options.ctrlKey || false,
			altKey: options.altKey || false,
			shiftKey: options.shiftKey || false,
			metaKey: options.metaKey || false,
			bubbles: options.bubbles || true,
			cancelable: options.cancelable || true
		};

		var props = {
			which: options.which || null
		};

		var eventObjects = {
			'HTMLEvents': /^(?:load|unload|abort|error|select|change|submit|reset|focus|blur|resize|scroll)$/,
			'MouseEvents': /^(?:click|dblclick|mouse(?:down|up|over|move|out))$/
		};
		var eventClass = 'Event';
		var event;

		for (var name in eventObjects) {
			if (!eventObjects.hasOwnProperty(name)) continue;
			if (eventObjects[name].test(type)) { eventClass = name; break; }
		}

		if (document.createEvent) {
			event = element.ownerDocument.createEvent(eventClass);
			if (eventClass == 'MouseEvents') {
				event.initMouseEvent( o.type, o.bubbles, o.cancelable, o.view,
					o.detail, o.screenX, o.screenY, o.clientX, o.clientY, o.ctrlKey,
					o.altKey, o.shiftKey, o.metaKey, 0, null);
			} else {
				event.initEvent(type, o.bubbles, o.cancelable);
			}
			for (var key in props) {
				if (!props.hasOwnProperty(key)) continue;
				event[key] = props[key];
			}
			element.dispatchEvent(event);
		} else if (document.createEventObject) {
			o.clientX = o.pointerX;
			o.clientY = o.pointerY;
			event = document.createEventObject();
			for (var key in o) {
				if (!o.hasOwnProperty(key)) continue;
				event[key] = o[key];
			}
			element.fireEvent(eventPrefix + type, event);
		}

		return event;
	}
});
define('cartware/jquery/bem',[
	"jquery"
], function(
	$
) {

	var options = {
		prefix: 'b_',
		elementSeparator: '__',
		modifierSeparator: '--'
	};

	function getClasses(node) {
		var result = {
				block: "",
				element: "",
				modifier: []
			},
			klass;

		node = $(node);

		klass = node.attr("class");

		if (!klass) {
			return result;
		}

		$.each(klass.split(" "), function() {
			if (this.substr(0, options.prefix.length) === options.prefix) {
				var sanitizedString = $.trim(this.substr(options.prefix.length));
				var classSplit = sanitizedString.split(options.elementSeparator);
				var blockString = classSplit[0];

				var blockSplit = blockString.split(options.modifierSeparator);

				result["block"] = blockSplit[0];

				if (blockSplit.length === 2) {
					result["modifier"].push(blockSplit[1]);
					return true;
				}

				if (classSplit.length > 1) {
					elementSplit = classSplit[1].split(options.modifierSeparator);

					result["element"] = elementSplit[0];

					if (elementSplit.length === 2) {
						result["modifier"].push(elementSplit[1]);
					}
				}
			}
		});

		return result;
	}

	function getModifierClass(baseClass, modifier) {
		return baseClass + options.modifierSeparator + modifier;
	}

	var elementMethods = {

	};

	function bemElementQuery(method, elements) {
		var info = getClasses(this),
			elementClass = options.prefix + info["block"] + options.elementSeparator + elements,
			query = "." + elementClass,
			result = this[method](query);

		$.extend(result, elementMethods, {
			__bem_element_class: elementClass
		});

		return result;
	}

	$.fn.bemAddModifier = function(modifier) {
		var info = getClasses(this);

		if (info.element) {
			this.addClass([options.prefix, info.block, options.elementSeparator, info.element, options.modifierSeparator, modifier].join(""));
		} else {
			this.addClass([options.prefix, info.block, options.modifierSeparator, modifier].join(""));
		}

		return this;
	};

	$.fn.bemHasModifier = function(modifier) {
		var info = getClasses(this),
			className = info.element
				? [options.prefix, info.block, options.elementSeparator, info.element, options.modifierSeparator, modifier].join("")
				: [options.prefix, info.block, options.modifierSeparator, modifier].join("");

		return this.hasClass(className);
	};

	$.fn.bemRemoveModifier = function(modifier) {
		var info = getClasses(this);

		if (info.element) {
			this.removeClass([options.prefix, info.block, options.elementSeparator, info.element, options.modifierSeparator, modifier].join(""))
		} else {
			this.removeClass([options.prefix, info.block, options.modifierSeparator, modifier].join(""))
		}

		return this;
	};

	$.fn.bemParents = function(elements) {
		return bemElementQuery.call(this, "parents", elements);
	};

	$.fn.bemSiblings = function(elements) {
		return bemElementQuery.call(this, "siblings", elements);
	};

	$.fn.bemGetElements = function(elements) {
		var info = getClasses(this),
			elementClass = options.prefix + info["block"] + options.elementSeparator + elements,
			query = "." + elementClass,
			result = this.find(query);

		$.extend(result, elementMethods, {
			__bem_element_class: elementClass
		});

		return result;
	};

	$.fn.findBlock = function(blockQuery) {
		return BEM.block(blockQuery, this);
	};

	var blockMethods = {
		getElements: function(element) {
			var elementClass = this.__bem_block_class + options.elementSeparator + element,
				query = "." + elementClass,
				result = this.find(query);

			$.extend(result, elementMethods, {
				__bem_element_class: elementClass
			});

			return result;
		},

		hasModifier: function(modifier) {
			var modifierClass = getModifierClass(this.__bem_block_class, modifier);
			return this.hasClass(modifierClass);
		},

		addModifier: function(modifier) {
			var modifierClass = getModifierClass(this.__bem_block_class, modifier);
			return this.addClass(modifierClass);
		},

		removeModifier: function(modifier) {
			var modifierClass = getModifierClass(this.__bem_block_class, modifier);
			return this.removeClass(modifierClass);
		}
	};

	var BEM = {
		block: function(blockQuery, parent) {
			var blockClass, query, result, info;

			if ("string" === typeof blockQuery) {
				blockClass = options.prefix + blockQuery;
				query = "." + blockClass;
				result = parent ? $(parent).find(query) : $(query);
			} else {
				info = getClasses(blockQuery);
				result = $(blockQuery);
				blockClass = options.prefix + info.block;
			}

			$.extend(result, blockMethods, {
				__bem_block_class: blockClass
			});

			return result;
		},

		buildBlockClassname: function(block, modifier) {
			var classname = options.prefix + block;

			if ("undefined" !== typeof modifier) {
				classname += options.prefix + options.modifierSeparator;
			}

			return classname;
		},

		buildElementClassname: function(block, element, modifier) {
			var classname = this.buildBlockClassname(block) + options.elementSeparator + element;

			if ("undefined" !== typeof modifier) {
				classname += options.modifierSeparator + modifier;
			}

			return classname;
		}
	};

	$.extend($.expr[":"], {
		modifier: function(element, index, details, collection) {
			return getClasses(element).modifier.indexOf(details[3]) >= 0;
		}
	});

	return BEM;
});

define('cartware_checkout/classes/accordeon',[
	'jquery',
	'lodash',
	'cartware/jquery/bem',
	'cartware_checkout'
], function(
	$,
	_,
	BEM,
	CartwareCheckout
) {
	var animateAttributes = {
		left: 'left'
	};

	if ('transit' in $ && 'undefined' != typeof Modernizr && Modernizr.csstransitions) {
		$.extend(animateAttributes, {
			left: 'x'
		});
	}

	var AccordionReplacement = function(container, options) {
		this.container = container;
		this.stage = this.container.bemGetElements('stage');
		this.stepsContainer = this.stage.bemGetElements('step-list');
		this.steps = this.stepsContainer.bemGetElements('section');
		this.currentStep = this.steps.first();
		this.sidebar = BEM.block('opcheckout-progress-sidebar');

		this.sections = this.steps.toArray();
		this.body = $(document.body);

		this._layout();
		this._initializeEvents();
		this._adviseValidation();

		$(window).on('resize', _.debounce(_.bind(function() {
			this._layout();
			this._position();
		}, this), 100));
	};

	AccordionReplacement.prototype = {
		_layout: function() {
			var containerWidth = this.container.width();

			this.stage.css({
				width: containerWidth * this.steps.length,
				height: Math.max(this.currentStep.outerHeight(true), this.sidebar.is(':visible') ? this.sidebar.outerHeight(true) : 0)
			});

			this.steps.css({
				'width': containerWidth
			});
		},

		_position: function() {
			var targetStyles = {
				height: this.currentStep.outerHeight(true)
			};

			targetStyles[animateAttributes.left] = - this._getTargetLeft(this.currentStep);

			this.stage.stop().animate(targetStyles, {duration: 0, queue: false});
		},

		_getTargetLeft: function(sectionNode) {
			var targetKey = this.steps.index(sectionNode),
				targetLeft = 0;

			this.steps.each(function(i) {
				if (i === targetKey) {
					return false;
				}
				targetLeft += $(this).outerWidth(true);
			});

			return targetLeft;
		},

		_initializeEvents: function() {
			var container = this.container,
				theBody = this.body,
				self = this;

			// no resize observer for IE
			if (!window.document.documentMode) {
				const resizeObserver = new ResizeObserver((entries) => {
					for (const entry of entries) {
						self._layout();
					}
				});

				this.steps.each(function(i, el) {
					resizeObserver.observe(el);
				});
			}

			container.on('click', function() {
				_.delay(function() {
					self._layout();
				}, 10);
			});
		},

		_adviseValidation: function() {
			var self = this,
				_validate = Validation.validate;

			Validation.validate = function() {
				var result = _validate.apply(this, arguments);

				setTimeout(function() {
					self._layout();
				}, 100);

				return result;
			};
		},

		openSection: function(section, options) {
			options = options || {};
			var sectionNode = $('#' + section),
				targetLeft = this._getTargetLeft(sectionNode),
				self = this;

			function scrollToTop(cb) {
				if (options.immediately) {
					$('html, body').scrollTop(0);
					return cb();
				}

				$('html, body').stop(true, false).animate({
					scrollTop: 0
				}, {
					duration: 450,
					easing: 'easeOutSine' in $.easing ? 'easeOutSine' : 'swing',
					complete: function() {
						cb();
					}
				});
			}

			function scrollToSection() {
				self.currentStep = sectionNode;

				setTimeout(function() {
					var targetStyles = {
						height: Math.max(sectionNode.outerHeight(true), self.sidebar.is(':visible') ? self.sidebar.outerHeight(true) : 0)
					};

					var mfpWrap = $('.mfp-wrap ');
					var $document = $(document);

					function onStep() {
						if (mfpWrap.length) {
							mfpWrap.css('top', $document.scrollTop());
						}
					}

					function onComplete() {
						self.currentStep = sectionNode;
					}

					targetStyles[animateAttributes.left] = - targetLeft;

					if (options.immediately) {
						self.stage.css(targetStyles);
						onStep();
						onComplete();
						return;
					}

					self.stage.stop(true, false).animate(targetStyles, {
						queue: false,
						duration: 650,
						easing: 'easeOutSine' in $.easing ? 'easeOutSine' : 'swing',
						complete: onComplete,
						step: onStep
					});
				}, 50);
			}

			scrollToTop(scrollToSection);
		},

		openPrevSection: function() {
			var previousSection = this.currentStep.prev('.section');
			if (previousSection.length) {
				this.openSection(previousSection.attr('id'));
			}
		}
	};

	return AccordionReplacement;
});
define('cartware_checkout/components/advises/login',[
	"jquery",
	"cartware/components/_base",
	"cartware_checkout"
], function(
	$,
	Component,
	CartwareCheckout
) {
	return Component.create({
		init: function() {
			var _setMethod = Checkout.prototype.setMethod;

			Checkout.prototype.setMethod = function(method) {
				// var allowedMethods = ['guest', 'register'];
				// if (!$.inArray) {
				// 	alert(Translator.translate('Please choose to register or to checkout as a guest').stripTags());
				// 	return false;
				// }
				this.method = method;
				new Ajax.Request(
					this.saveMethodUrl,
					{
						method: 'post',
						parameters: {method: method},
						onFailure: this.ajaxFailure.bind(this),
						onSuccess: function (transport) {
							var response = transport.responseJSON || transport.responseText.evalJSON(true) || {};

							if (response.blocks && ('register' in response.blocks)) {
								$('#customer-account-registration').replaceWith(response.blocks['register']);
							}

							if (response.error){
								if ((typeof response.message) == 'string') {
									alert(response.message);
								} else {
									alert(response.message.join("\n"));
								}

								return false;
							}

							this.gotoSection('shipping', true);
						}.bind(this),
						onComplete: function () {
							this.setLoadWaiting(false);
						}.bind(this),
					}
				);

				checkout.setLoadWaiting('shipping');

				document.body.fire('login:setMethod', {method : this.method});
			};
		}
	});
});
define('cartware_checkout/components/advises/billing',[
	"cartware/components/_base",
	"cartware/dom/event/trigger",
	"cartware_checkout"
], function(
	Component,
	triggerEvent,
	CartwareCheckout
) {
	var register;

	CartwareCheckout().retrieve("register", function(reg) {
		register = reg;
	});

	return Component.create({
		init: function() {
			function validationAdvise() {
				var validator = this.validator || new Validation(this.form);

				if ("undefined" != typeof this.validate) {
					if (!this.validate()) {
						return false;
					}
				}

				return validator.validate();
			}

			var _initialize = Billing.prototype.initialize;
			Billing.prototype.initialize = function () {
				_initialize.apply(this, arguments);

				var _onComplete = this.onComplete,
					self = this;

				this.onCompleteQueue = [];

				this.onComplete = function() {
					_onComplete.apply(self, arguments);

					self.onCompleteQueue.clone().each(function(cb) {
						cb.call(self);
						self.onCompleteQueue.splice(self.onCompleteQueue.indexOf(cb), 1);
					});
				}
			};

			Billing.prototype.save = function() {
				if (checkout.loadWaiting!=false) return;

				var validator = new Validation(this.form);

				var params = Form.serialize(this.form);

				if (register && register.isActive()) {
					if (!register.validate()) return;

					params += '&' + Form.serialize(register.getForm());
				}

				if (validator.validate()) {
					checkout.setLoadWaiting('billing');

					var request = new Ajax.Request(
						this.saveUrl,
						{
							method: 'post',
							onComplete: this.onComplete,
							onSuccess: this.onSave,
							onFailure: checkout.ajaxFailure.bind(checkout),
							parameters: params
						}
					);
				}
			};

			var _nextStep = Billing.prototype.nextStep;
			Billing.prototype.nextStep = function(billingTransport) {
				var _setStepResponse = checkout.setStepResponse,
					e = null;

				var billingResponse = {};
				if (billingTransport && billingTransport.responseText) {
					try {
						billingResponse = eval('(' + billingTransport.responseText + ')');
					} catch (e) {
						billingResponse = {};
					}
				}

				try {
					_nextStep.apply(this, arguments);
				} catch (e) {}

				checkout.setStepResponse = _setStepResponse;

				if (e !== null) {
					throw e;
				}

				if ("error" in billingResponse && billingResponse.error) {
					return;
				}

				this.onCompleteQueue.push(function() {
					var _paymentOnSave = payment.onSave;
					var _paymentOnComplete = payment.onComplete;

					payment.onSave = function(paymentTransport) {
						//console.log("payment.onSave");

						var paymentResponse;
						if (paymentTransport && paymentTransport.responseText) {
							try {
								paymentResponse = eval('(' + paymentTransport.responseText + ')');
							} catch (e) {
								paymentResponse = {};
							}
						}

						payment.onSave = _paymentOnSave;
						_paymentOnSave.apply(this, arguments);

						if (!paymentResponse.error) {
							setTimeout(function() {
								//console.log("checkout.setStepResponse(transport)");
								//checkout.loadWaiting = false;
								checkout.setStepResponse(billingResponse);
								checkout.setLoadWaiting(false);
								window.accordion._layout();
							}, 10);
						} else {
							payment.resetLoadWaiting();
							if ("applyStashedCcData" in payment) payment.applyStashedCcData();
						}
					};

					payment.onComplete = function() {
						//console.log("payment.onComplete");
						payment.onComplete = _paymentOnComplete;
					};

					//console.log("payment.save()");
					payment.save();
				});
			};

			Billing.prototype.originalSave = function() {
				_save.apply(this, arguments);
			};

			Billing.prototype.setDifferentFromShipping = function(flag) {
				$('billing:different_from_shipping').checked = flag;
				if (!flag) {
					this.syncWithShipping();
				}
			};

			Billing.prototype.syncWithShipping = function () {
				$('shipping-address-select') && this.newAddress(!$('shipping-address-select').value);
				$('billing:different_from_shipping').checked = false;
				if (!$('shipping-address-select') || !$('shipping-address-select').value) {
					arrElements = Form.getElements(this.form);
					for (var elemIndex in arrElements) {
						if (arrElements[elemIndex].id) {
							var sourceField = $(arrElements[elemIndex].id.replace(/^billing:/, 'shipping:'));
							if (sourceField){
								arrElements[elemIndex].value = sourceField.value;
							}
						}
					}

					if ($('billing:email') && $('customer:email')) {
						$('billing:email').value = $('customer:email').value;
					}

					$('billing:country_id').value = $('shipping:country_id').value;
					//billingRegionUpdater.update();
					triggerEvent($('billing:country_id'), 'change');

					$('billing:region_id').value = $('shipping:region_id').value;
					$('billing:region').value = $('shipping:region').value;
					triggerEvent($('billing:region_id'), 'change');

					//billingForm.elementChildLoad($('billing:country_id'), this.setRegionValue.bind(this));
				} else {
					$('billing-address-select').value = $('shipping-address-select').value;
				}
			};

			var _newAddress = Billing.prototype.newAddress;
			Billing.prototype.newAddress = function(flag) {
				_newAddress.apply(this, arguments);

				if ("accordion" in window) {
					window.accordion._layout();
				}
			};
		}
	});
});
define('cartware_checkout/classes/loader',[
	"jquery",
	"lodash",
	"gigaset/spinner"
], function(
	$,
	_,
	Spinner
) {
	function Loader() {}

	Loader.prototype = {
		show: function(selectors) {
			Spinner.add(selectors);
		},

		hide: function() {
			Spinner.remove();
		}
	}

	return Loader;
});
(function(root) {
define("bootstrap-prototype-fix", ["jquery"], function() {
  return (function() {
!(function($, window, undefined) {
	var affectedElements = [];

	$(document.body).on(
		"hide.bs.dropdown hide.bs.collapse hide.bs.modal hide.bs.tooltip hide.bs.popover show.bs.dropdown show.bs.collapse show.bs.modal show.bs.tooltip show.bs.popover",
		"*",
		function(e) {
			if (affectedElements.indexOf(e.target) < 0) {
				affectedElements.push(e.target);
			}
		}
	);

	var originalHide = Element.hide,
		originalShow = Element.show;

	Element.addMethods({
		hide: function(element) {
			var index = affectedElements.indexOf(element);

			if (index >= 0) {
				affectedElements.splice(index, 1);
				return element;
			}

			return originalHide(element);
		},

		show: function(element) {
			var index = affectedElements.indexOf(element);

			if (index >= 0) {
				affectedElements.splice(index, 1);
				return element;
			}

			return originalShow(element);
		}
	});
})(jQuery, this);

  }).apply(root, arguments);
});
}(this));

(function(root) {
define("bootstrap-transition", ["jquery","bootstrap-prototype-fix"], function() {
  return (function() {
/* ========================================================================
 * Bootstrap: transition.js v3.3.6
 * http://getbootstrap.com/javascript/#transitions
 * ========================================================================
 * Copyright 2011-2015 Twitter, Inc.
 * Licensed under MIT (https://github.com/twbs/bootstrap/blob/master/LICENSE)
 * ======================================================================== */


+function ($) {
  'use strict';

  // CSS TRANSITION SUPPORT (Shoutout: http://www.modernizr.com/)
  // ============================================================

  function transitionEnd() {
    var el = document.createElement('bootstrap')

    var transEndEventNames = {
      WebkitTransition : 'webkitTransitionEnd',
      MozTransition    : 'transitionend',
      OTransition      : 'oTransitionEnd otransitionend',
      transition       : 'transitionend'
    }

    for (var name in transEndEventNames) {
      if (el.style[name] !== undefined) {
        return { end: transEndEventNames[name] }
      }
    }

    return false // explicit for ie8 (  ._.)
  }

  // http://blog.alexmaccaw.com/css-transitions
  $.fn.emulateTransitionEnd = function (duration) {
    var called = false
    var $el = this
    $(this).one('bsTransitionEnd', function () { called = true })
    var callback = function () { if (!called) $($el).trigger($.support.transition.end) }
    setTimeout(callback, duration)
    return this
  }

  $(function () {
    $.support.transition = transitionEnd()

    if (!$.support.transition) return

    $.event.special.bsTransitionEnd = {
      bindType: $.support.transition.end,
      delegateType: $.support.transition.end,
      handle: function (e) {
        if ($(e.target).is(this)) return e.handleObj.handler.apply(this, arguments)
      }
    }
  })

}(jQuery);


  }).apply(root, arguments);
});
}(this));

(function(root) {
define("bootstrap-button", ["jquery","bootstrap-transition","bootstrap-prototype-fix"], function() {
  return (function() {
/* ========================================================================
 * Bootstrap: button.js v3.3.6
 * http://getbootstrap.com/javascript/#buttons
 * ========================================================================
 * Copyright 2011-2015 Twitter, Inc.
 * Licensed under MIT (https://github.com/twbs/bootstrap/blob/master/LICENSE)
 * ======================================================================== */


+function ($) {
  'use strict';

  // BUTTON PUBLIC CLASS DEFINITION
  // ==============================

  var Button = function (element, options) {
    this.$element  = $(element)
    this.options   = $.extend({}, Button.DEFAULTS, options)
    this.isLoading = false
  }

  Button.VERSION  = '3.3.6'

  Button.DEFAULTS = {
    loadingText: 'loading...'
  }

  Button.prototype.setState = function (state) {
    var d    = 'disabled'
    var $el  = this.$element
    var val  = $el.is('input') ? 'val' : 'html'
    var data = $el.data()

    state += 'Text'

    if (data.resetText == null) $el.data('resetText', $el[val]())

    // push to event loop to allow forms to submit
    setTimeout($.proxy(function () {
      $el[val](data[state] == null ? this.options[state] : data[state])

      if (state == 'loadingText') {
        this.isLoading = true
        $el.addClass(d).attr(d, d)
      } else if (this.isLoading) {
        this.isLoading = false
        $el.removeClass(d).removeAttr(d)
      }
    }, this), 0)
  }

  Button.prototype.toggle = function () {
    var changed = true
    var $parent = this.$element.closest('[data-toggle="buttons"]')

    if ($parent.length) {
      var $input = this.$element.find('input')
      if ($input.prop('type') == 'radio') {
        if ($input.prop('checked')) changed = false
        $parent.find('.active').removeClass('active')
        this.$element.addClass('active')
      } else if ($input.prop('type') == 'checkbox') {
        if (($input.prop('checked')) !== this.$element.hasClass('active')) changed = false
        this.$element.toggleClass('active')
      }
      $input.prop('checked', this.$element.hasClass('active'))
      if (changed) $input.trigger('change')
    } else {
      this.$element.attr('aria-pressed', !this.$element.hasClass('active'))
      this.$element.toggleClass('active')
    }
  }


  // BUTTON PLUGIN DEFINITION
  // ========================

  function Plugin(option) {
    return this.each(function () {
      var $this   = $(this)
      var data    = $this.data('bs.button')
      var options = typeof option == 'object' && option

      if (!data) $this.data('bs.button', (data = new Button(this, options)))

      if (option == 'toggle') data.toggle()
      else if (option) data.setState(option)
    })
  }

  var old = $.fn.button

  $.fn.button             = Plugin
  $.fn.button.Constructor = Button


  // BUTTON NO CONFLICT
  // ==================

  $.fn.button.noConflict = function () {
    $.fn.button = old
    return this
  }


  // BUTTON DATA-API
  // ===============

  $(document)
    .on('click.bs.button.data-api', '[data-toggle^="button"]', function (e) {
      var $btn = $(e.target)
      if (!$btn.hasClass('btn')) $btn = $btn.closest('.btn')
      Plugin.call($btn, 'toggle')
      if (!($(e.target).is('input[type="radio"]') || $(e.target).is('input[type="checkbox"]'))) e.preventDefault()
    })
    .on('focus.bs.button.data-api blur.bs.button.data-api', '[data-toggle^="button"]', function (e) {
      $(e.target).closest('.btn').toggleClass('focus', /^focus(in)?$/.test(e.type))
    })

}(jQuery);


  }).apply(root, arguments);
});
}(this));

define('cartware_checkout/components/advises/checkout',[
	"jquery",
	"cartware_checkout",
	"cartware/components/_base",
	"cartware/jquery/bem",
	"cartware_checkout/classes/loader",
	"bootstrap-button"
], function(
	jQuery,
	CartwareCheckout,
	Component,
	BEM,
	Loader
) {
	return Component.create({
		loader:		null,

		init: function() {
			var self = this;

			this.loader = new Loader();

			var _initialize = Checkout.prototype.initialize;
			Checkout.prototype.initialize = function() {
				_initialize.apply(this, arguments);

				this.method = 'guest';
				this.steps = ["shipping", "billing", "review"];
			};

			Checkout.prototype.reloadProgressBlock = function (toStep) {
				var updater = new Ajax.Request(this.progressUrl, {
					method: 'get',
					onFailure: this.ajaxFailure.bind(this),
					onSuccess: function(response) {
						var json = response.responseText.evalJSON();

						if (!json) return;

						CartwareCheckout().updateProgress(json);
					},
					parameters: toStep ? {toStep: toStep} : null
				});
			};

			var _setLoadWaiting = Checkout.prototype.setLoadWaiting;
			Checkout.prototype.setLoadWaiting = function(step, keepDisabled) {
				if (step) {
					self.loader.show();
				} else {
					self.loader.hide();
				}

				if (step == 'payment') step = 'billing';
				else if (step == 'shipping-method' || step === true) return;

				//_setLoadWaiting.apply(this, [step, keepDisabled]);

				if (step) {
					if (this.loadWaiting) {
						this.setLoadWaiting(false);
					}
					var container = $(step+'-buttons-container');
					container.addClassName('disabled');
					container.setStyle({opacity:.5});
					this._disableEnableAll(container, true);

					jQuery(container).bemGetElements("button").filter(":modifier(next)").button("loading");
				} else {
					if (this.loadWaiting) {
						var container = $(this.loadWaiting+'-buttons-container');
						var isDisabled = (keepDisabled ? true : false);
						if (!isDisabled) {
							container.removeClassName('disabled');
							container.setStyle({opacity:1});
							jQuery(container).find("[data-loading-text]").button("reset");
						}
						this._disableEnableAll(container, isDisabled);
					}
				}
				this.loadWaiting = step;
			};

			var _gotoSection = Checkout.prototype.gotoSection;
			Checkout.prototype.gotoSection = function(section, forceSection) {
				if (section == "") return;
				if (section == 'review') return; // skip intermediate step
				//if (section == "review") section = "summary";
				//if (section == "login") section = "shipping";

				try {
					if (section == 'payment') section = 'billing';
					else if (section == 'shipping-method') section = 'summary';

					try {
						if ("undefined" !== typeof _gaq) {
							_gaq.push(["_trackEvent", "3-Step Checkout", "gotoSection", section]);
						} else if ("undefined" !== typeof window.ga) {
							window.ga('send', 'pageview', location.pathname.replace(/\/+$/, '') + "/" + section);
						}
					} catch (e) {}


					if (section === "billing") {
						if (!$('billing:different_from_shipping').checked) {
							billing.syncWithShipping();
						}
					}

					if (forceSection) {
						this.currentStep = section;
					}

					if (section == "summary") {
						this.currentStep = "review";
					}

					_gotoSection.apply(this, [section, true]);

					if (section == 'billing') {
						billing.setDifferentFromShipping($('billing:different_from_shipping').checked);
					}
				} catch (e) {
					if ("undefined" != typeof console.log) {
						throw e;
					}
				}
			};

			var _setStepResponse = Checkout.prototype.setStepResponse;
			Checkout.prototype.setStepResponse = function(response) {
				if (response.blocks) {
					if (response.blocks.review) {
						$('checkout-review-wrapper').replace(response.blocks.review);
					}

					if ("shipping-methods" in response.blocks) {
						$('co-shipping-method-form').replace(response.blocks["shipping-methods"]);
					}

					if ("payment-methods" in response.blocks) {
						$('co-payment-block').replace(response.blocks["payment-methods"]);
					}
				}

				_setStepResponse.apply(this, arguments);
			};
		}
	});
});
define('cartware_checkout/components/advises/payment',[
	"jquery",
	"cartware/components/_base",
	"cartware/dom/event/trigger"
], function(
	jQuery,
	Component,
	triggerEvent
) {
	return Component.create({

		billingSection: null,

		init: function() {
			//		var _nextStep = Payment.prototype.nextStep;
//		Payment.prototype.nextStep = function(transport) {
//			/*
//			this.resetLoadWaiting();
//			billing.originalSave();
//			*/
//			_nextStep.apply(this, arguments);
//		}
			this.billingSection = jQuery('#billing-section');

			var self = this;

			var _changeVisible = Payment.prototype.changeVisible;
			Payment.prototype.changeVisible = function(method, mode) {
				_changeVisible.apply(this, arguments);

				var block = 'payment_form_' + method;
				[block + '_before', block, block + '_after'].each(function(el) {
					element = jQuery(el);
					if (element) {
						element.select('input', 'select', 'textarea', 'button').each(function(field) {
							jQuery(field).data('uniformed') && $(field).data('uniformed').update();
							triggerEvent(field, 'change');
						});
					}
				});
			};

			var _switchMethod = Payment.prototype.switchMethod;
			Payment.prototype.switchMethod = function() {
				checkout.setLoadWaiting(true);

				_switchMethod.apply(this, arguments);
				if ("accordion" in window) {
					window.accordion._layout();
				}

				new Ajax.Request(
					this.checkAllowDivergentAddressUrl,
					{
						method: 'post',
						parameters: {payment_method: this.currentMethod},
						onFailure: self._setAllowDivergentAddress,
						onSuccess: function (transport) {
							var response = transport.responseJSON || transport.responseText.evalJSON(true) || {};
							self._setAllowDivergentAddress(response.result);
						},
						onComplete: function () {
							checkout.setLoadWaiting(false);
						}.bind(checkout),
					}
				);
			}
		},

		_setAllowDivergentAddress: function(result) {
			if (result) {
				this.billingSection.show();
			} else {
				this.billingSection.hide();
				billing.setDifferentFromShipping(false);
			}

			window.accordion._layout();
		}

	});
});
define('cartware_checkout/components/advises/regionUpdater',[
	"jquery",
	"lodash",
	"cartware/components/_base",
	"cartware/dom/event/trigger"
], function(
	$,
	_,
	Component,
	triggerEvent
) {
	return Component.create({
		$init: function() {
			window.shippingRegionUpdater.update();
			window.billingRegionUpdater.update();
		},

		init: function() {
			var _update = RegionUpdater.prototype.update;
			RegionUpdater.prototype.update = function() {
				_update.apply(this, arguments);

				triggerEvent(this.regionSelectEl, "change");
			};

			RegionUpdater.prototype.setMarkDisplay = _.wrap(RegionUpdater.prototype.setMarkDisplay, function(parent, select, display) {
				parent(select, display);

				var select2 = $(select).data("select2");
				if (select2) {
					select2.$container.css("display", display ? "" : "none");
				}
			});

			RegionUpdater.prototype.update = _.wrap(RegionUpdater.prototype.update, function(parent) {
				var select = this.regionSelectEl,
					$select = $(select),
					select2 = $select.data("select2");

				parent.call(this);

				if (select2) {
					select2.$container.css("display", select.style.display === "none" ? "none" : "");
				}
			});
		}
	});
});
define('cartware_checkout/components/advises/review',[
	"cartware/components/_base",
	"cartware_checkout"
], function(
	Component,
	Checkout
) {
	return Component.create({
		init: function() {
			//var _save = Review.prototype.save;

			function _save() {
				if (checkout.loadWaiting != false) return;

				var params = Form.serialize(payment.form);
				if (this.agreementsForm) {
					params += '&'+Form.serialize(this.agreementsForm);
				}

				checkout.setLoadWaiting('review');

				params.save = true;
				var request = new Ajax.Request(
					this.saveUrl,
					{
						method:'post',
						parameters:params,
						onComplete: this.onComplete,
						onSuccess: this.onSave,
						onFailure: checkout.ajaxFailure.bind(checkout)
					}
				);
			}

			Review.prototype.save = function() {
				var self = this
					, args = arguments;

				if (!shippingMethod.isSaved) {
					var _shippingMethodOnSave = shippingMethod.onSave;
					shippingMethod.onSave = function() {
						_shippingMethodOnSave.apply(this, arguments);

						if (shippingMethod.isSaved) {
							setTimeout(function() {
								// _save.apply(self, args); // Can't use "self" here. For some reason IE keeps an old reference.
								_save.apply(review, args);
								if ("accordion" in window) {
									window.accordion._layout();
								}
							}, 10);
						}

						shippingMethod.onSave = _shippingMethodOnSave;
					};

					shippingMethod.save();
				} else {
					_save.apply(this, arguments);
					if ("accordion" in window) {
						window.accordion._layout();
					}
				}
			}
		}
	});
});
define('cartware_checkout/components/advises/shipping',[
	"jquery",
	"cartware/components/_base",
	"cartware_checkout",
	"cartware/jquery/bem",
], function(
	$,
	Component,
	CartwareCheckout,
	BEM
) {
	var register;

	CartwareCheckout().retrieve("register", function(reg) {
		register = reg;
	});

	return Component.create({
		init: function() {
			Shipping.prototype.save = function() {
				if (checkout.loadWaiting !== false) {
					return;
				}

				var validator = new Validation(this.form);

				var params = Form.serialize(this.form);

				if (register && register.isActive()) {
					if (!register.validate()) return;

					params += '&' + Form.serialize(register.getForm());
				}

				if (validator.validate()) {
					checkout.setLoadWaiting('shipping');
					var request = new Ajax.Request(
						this.saveUrl,
						{
							method:'post',
							onComplete: this.onComplete,
							onSuccess: this.onSave,
							onFailure: checkout.ajaxFailure.bind(checkout),
							parameters: params
						}
					);
				}
			};

			Shipping.prototype.resetTurnstileWidget = function() {
				try {
					if ('turnstile' in window) {
						const className = BEM.buildElementClassname('cartware-checkout', 'captcha-wrapper', 'cloudflare');
						const cloudflareWrapper = $(`.${className}`);
						const widgetIds = [];

						if (cloudflareWrapper) {
							_.forEach(cloudflareWrapper, function(wrapper, key) {
								var input = $(wrapper).find('input[type="hidden"]');
								if (input) {
									var id = input.attr('id');
									if (id) {
										id = id.replace('_response', '');
										widgetIds.push(id);
									}
								}
							});
						}

						if (widgetIds.length > 0) {
							_.forEach(widgetIds, function(id, key) {
								window.turnstile.reset(id);
								window.turnstile.execute(id);
							});
						}
					}
				} catch (e) {
					console.error("Error resetting Turnstile:", e);
				}
			};

			Shipping.prototype.nextStep = function(transport) {
				var response = transport.responseJSON || transport.responseText.evalJSON(true) || {};

				if (response.blocks && ('register' in response.blocks)) {
					$('#customer-account-registration').replaceWith(response.blocks['register']);
				}

				if (response.error){
					Shipping.prototype.resetTurnstileWidget();

					if ((typeof response.message) == 'string') {
						alert(response.message);
					} else {
						if (window.shippingRegionUpdater) {
							shippingRegionUpdater.update();
						}
						alert(response.message.join("\n"));
					}

					return false;
				}

				//checkout.setStepResponse(response);
				checkout.gotoSection('billing', true);
			};

			Shipping.prototype.setSameAsBilling = function(flag) {

			};

			var _newAddress = Shipping.prototype.newAddress;
			Shipping.prototype.newAddress = function(flag) {
				_newAddress.apply(this, arguments);

				if ("accordion" in window) {
					window.accordion._layout();
				}
			};
		}
	});
});

define('cartware_checkout/components/advises/shippingMethod',[
	"cartware/components/_base",
	"cartware_checkout"
], function(
	Component,
	CartwareCheckout
) {
	return Component.create({
		init: function() {
			var _initialize = ShippingMethod.prototype.initialize,
				_save = ShippingMethod.prototype.save;

			ShippingMethod.prototype.initialize = function() {
				_initialize.apply(this, arguments);

				this.isSaved = false;
			};

			//ShippingMethod.prototype.save = function() {
			//	if (checkout.loadWaiting!=false) return;
			//
			//	var params = Form.serialize(this.form);
			//
			//	if (register && register.isActive()) {
			//		if (!register.validate()) return;
			//
			//		params += '&' + Form.serialize(register.getForm());
			//	}
			//
			//	if (this.validate()) {
			//		checkout.setLoadWaiting('shipping-method');
			//		var request = new Ajax.Request(
			//			this.saveUrl,
			//			{
			//				method:'post',
			//				onComplete: this.onComplete,
			//				onSuccess: this.onSave,
			//				onFailure: checkout.ajaxFailure.bind(checkout),
			//				parameters: params
			//			}
			//		);
			//	}
			//}

			ShippingMethod.prototype.nextStep = function(transport) {
				var response;

				if (transport && transport.responseText){
					try{
						response = eval('(' + transport.responseText + ')');
					}
					catch (e) {
						response = {};
					}
				}

				if (response.error) {
					alert(response.message);
					checkout.setStepResponse(response);
					return false;
				}

				if (response.blocks) {
					if (response.blocks.review) {
						$('checkout-review-wrapper').replace(response.blocks.review);
					}

					CartwareCheckout().updateProgress(response);

					//$('checkout-'+response.update_section.name+'-load').update(response.update_section.html);
				}

				checkout.setStepResponse(response);

				//payment.initWhatIsCvvListeners();

				//checkout.setShippingMethod();

				this.isSaved = true;

				if ("accordion" in window) {
					setTimeout(function() {
						window.accordion._layout();
					}, 100);
				}
			}
		}
	});
});
(function(root) {
define("garlicjs", ["jquery"], function() {
  return (function() {
/*
  Garlic.js allows you to automatically persist your forms' text field values locally,
  until the form is submitted. This way, your users don't lose any precious data if they
  accidentally close their tab or browser.

  author: Guillaume Potier - @guillaumepotier
*/

!function ($) {

  "use strict";
  /*global localStorage */
  /*global document */

  /* STORAGE PUBLIC CLASS DEFINITION
   * =============================== */
  var Storage = function ( options ) {
    this.defined = 'undefined' !== typeof localStorage;
  }

  Storage.prototype = {

    constructor: Storage

    , get: function ( key, placeholder ) {
      return localStorage.getItem( key ) ? localStorage.getItem( key ) : 'undefined' !== typeof placeholder ? placeholder : null;
    }

    , has: function ( key ) {
      return localStorage.getItem( key ) ? true : false;
    }

    , set: function ( key, value, fn ) {
      if ( 'string' === typeof value ) {

        // if value is null, remove storage if exists
        if ( '' === value ) {
          this.destroy( key );
        } else {
          localStorage.setItem( key , value );
        }
      }

      return 'function' === typeof fn ? fn() : true;
    }

    , destroy: function ( key, fn ) {
      localStorage.removeItem( key );
      return 'function' === typeof fn ? fn() : true;
    }

    , clean: function ( fn ) {
      for ( var i = localStorage.length - 1; i >= 0; i-- ) {
        if ( 'undefined' === typeof Array.indexOf && -1 !== localStorage.key(i).indexOf( 'garlic:' ) ) {
          localStorage.removeItem( localStorage.key(i) );
        }
      }

      return 'function' === typeof fn ? fn() : true;
    }

    , clear: function ( fn ) {
      localStorage.clear();
      return 'function' === typeof fn ? fn() : true;
    }
  }

 /* GARLIC PUBLIC CLASS DEFINITION
  * =============================== */

  var Garlic = function ( element, storage, options ) {
    this.init( 'garlic', element, storage, options );
  }

  Garlic.prototype = {

    constructor: Garlic

    /* init data, bind jQuery on() actions */
    , init: function ( type, element, storage, options ) {
      this.type = type;
      this.$element = $( element );
      this.options = this.getOptions( options );
      this.storage = storage;
      this.path = this.options.getPath( this.$element ) || this.getPath();
      this.parentForm = this.$element.closest( 'form' );
      this.$element.addClass('garlic-auto-save');
      this.expiresFlag = !this.options.expires ? false : ( this.$element.data( 'expires' ) ? this.path : this.getPath( this.parentForm ) ) + '_flag' ;

      // bind garlic events
      this.$element.on( this.options.events.join( '.' + this.type + ' ') , false, $.proxy( this.persist, this ) );

      if ( this.options.destroy ) {
        $( this.parentForm ).on( 'submit reset' , false, $.proxy( this.destroy, this ) );
      }

      // retrieve garlic persisted data
      this.retrieve();
    }

    , getOptions: function ( options ) {
      return $.extend( {}, $.fn[this.type].defaults, options, this.$element.data() );
    }

    /* temporary store data / state in localStorage */
    , persist: function () {

      // some binded events are redundant (change & paste for example), persist only once by field val
      if ( this.val === this.getVal() ) {
        return;
      }

      this.val = this.getVal();

      // if auto-expires is enabled, set the expiration date for future auto-deletion
      if ( this.options.expires ) {
        this.storage.set( this.expiresFlag , ( new Date().getTime() + this.options.expires * 1000 ).toString() );
      }

      this.storage.set( this.path , this.getVal() );

      this.options.onPersist(this.$element, this.getVal());
    }

    , getVal: function () {
      return !this.$element.is( 'input[type=checkbox]' ) ? this.$element.val() : ( this.$element.prop( 'checked' ) ? 'checked' : 'unchecked' );
    }

    /* retrieve localStorage data / state and update elem accordingly */
    , retrieve: function () {
      if ( this.storage.has( this.path ) ) {

        // if data expired, destroy it!
        if ( this.options.expires ) {
          var date = new Date().getTime();
          if ( this.storage.get( this.expiresFlag ) < date.toString() ) {
            this.storage.destroy( this.path );
            return;
          } else {
            this.$element.attr( 'expires-in',  Math.floor( ( parseInt( this.storage.get( this.expiresFlag ) ) - date ) / 1000 ) );
          }
        }

        var storedValue = this.storage.get( this.path );

        // if conflictManager enabled, manage fields with already provided data, different from the one stored
        if ( this.options.conflictManager.enabled && this.detectConflict() ) {
          return this.conflictManager();
        }

        // input[type=checkbox] and input[type=radio] have a special checked / unchecked behavior
        if ( this.$element.is( 'input[type=radio], input[type=checkbox]' ) ) {

          // for checkboxes and radios
          if ( 'checked' === storedValue || this.$element.val() === storedValue ) {
            this.$element.attr( 'checked', true );

          // only needed for checkboxes
          } else if ( 'unchecked' === storedValue ) {
            this.$element.attr( 'checked', false );
          }

			this.options.onRetrieve( this.$element, storedValue );

          return;
        }

        // for input[type=text], select and textarea, just set val()
        this.$element.val( storedValue );

        // trigger an input event given the value has been changed
        this.$element.trigger( 'input' );

        // trigger custom user function when data is retrieved
        this.options.onRetrieve( this.$element, storedValue );

        return;
      }
    }

    /* there is a conflict when initial data / state differs from persisted data / state */
    , detectConflict: function() {
      var self = this;

      // radio buttons and checkboxes are yet not supported
      if ( this.$element.is( 'input[type=checkbox], input[type=radio]' ) ) {
        return false;
      }

      // there is a default not null value and we have a different one stored
      if ( this.$element.val() && this.storage.get( this.path ) !== this.$element.val() ) {

        // for select elements, we need to check if there is a default checked value
        if ( this.$element.is( 'select' ) ) {
          var selectConflictDetected = false;

          // foreach each options except first one, always considered as selected, seeking for a default selected one
          this.$element.find( 'option' ).each( function () {
            if ( $( this ).index() !== 0 && $( this ).attr( 'selected' ) && $( this ).val() !== self.storage.get( this.path ) ) {
              selectConflictDetected = true;
              return;
            }
          });

          return selectConflictDetected;
        }

        return true;
      }

      return false;
    }

    /* manage here the conflict, show default value depending on options.garlicPriority value */
    , conflictManager: function () {

      // user can define here a custom function that could stop Garlic default behavior, if returns false
      if ( 'function' === typeof this.options.conflictManager.onConflictDetected
        && !this.options.conflictManager.onConflictDetected( this.$element, this.storage.get( this.path ) ) ) {
        return false;
      }

      if ( this.options.conflictManager.garlicPriority ) {
        this.$element.data( 'swap-data', this.$element.val() );
        this.$element.data( 'swap-state', 'garlic' );
        this.$element.val( this.storage.get( this.path ) );
      } else {
        this.$element.data( 'swap-data', this.storage.get( this.path ) );
        this.$element.data( 'swap-state', 'default' );
      }

      this.swapHandler();
      this.$element.addClass( 'garlic-conflict-detected' );
      this.$element.closest( 'input[type=submit]' ).attr( 'disabled', true );
    }

    /* manage swap user interface */
    , swapHandler: function () {
      var swapChoiceElem = $( this.options.conflictManager.template );
      this.$element.after( swapChoiceElem.text( this.options.conflictManager.message ) );
      swapChoiceElem.on( 'click', false, $.proxy( this.swap, this ) );
    }

    /* swap data / states for conflicted elements */
    , swap: function () {
      var val = this.$element.data( 'swap-data' );
      this.$element.data( 'swap-state', 'garlic' === this.$element.data( 'swap-state' ) ? 'default' : 'garlic' );
      this.$element.data( 'swap-data', this.$element.val());
      $( this.$element ).val( val );
    }

    /* delete localStorage persistance only */
    , destroy: function () {
      this.storage.destroy( this.path );
    }

    /* remove data / reset state AND delete localStorage */
    , remove: function () {
      this.remove();

      if ( this.$element.is( 'input[type=radio], input[type=checkbox]' ) ) {
        $( this.$element ).prop( 'checked', false );
        return;
      }

      this.$element.val( '' );
    }

    /* retuns an unique identifier for form elements, depending on their behaviors:
       * radio buttons: domain > pathname > form.<attr.name>[:eq(x)] > input.<attr.name>
          no eq(); must be all stored under the same field name inside the same form

       * checkbokes: domain > pathname > form.<attr.name>[:eq(x)] > [fieldset, div, span..] > input.<attr.name>[:eq(y)]
          cuz' they have the same name, must detect their exact position in the form. detect the exact hierarchy in DOM elements

       * other inputs: domain > pathname > form.<attr.name>[:eq(x)] > input.<attr.name>[:eq(y)]
          we just need the element name / eq() inside a given form
    */
    , getPath: function ( elem ) {

      if ( 'undefined' === typeof elem ) {
        elem = this.$element;
      }

      if ( this.options.getPath( elem ) ) {
        return this.options.getPath( elem );
      }

      // Requires one element.
      if ( elem.length != 1 ) {
        return false;
      }

      var path = ''
        , fullPath = elem.is( 'input[type=checkbox]' )
        , node = elem;

      while ( node.length ) {
        var realNode = node[0]
          , name = realNode.nodeName;

        if ( !name ) {
          break;
        }

        name = name.toLowerCase();

        var parent = node.parent()
          , siblings = parent.children( name );

        // don't need to pollute path with select, fieldsets, divs and other noisy elements,
        // exept for checkboxes that need exact path, cuz have same name and sometimes same eq()!
        if ( !$( realNode ).is( 'form, input, select, textarea' ) && !fullPath ) {
          node = parent;
          continue;
        }

        // set input type as name + name attr if exists
        name += $( realNode ).attr( 'name' ) ? '.' + $( realNode ).attr( 'name' ) : '';

        // if has sibilings, get eq(), exept for radio buttons
        if ( siblings.length > 1 && !$( realNode ).is( 'input[type=radio]' ) ) {
          name += ':eq(' + siblings.index( realNode ) + ')';
        }

        path = name + ( path ? '>' + path : '' );

        // break once we came up to form:eq(x), no need to go further
        if ( 'form' == realNode.nodeName.toLowerCase() ) {
          break;
        }

        node = parent;
      }

      return 'garlic:' + document.domain + ( this.options.domain ? '*' : window.location.pathname ) + '>' + path;
    }

    , getStorage: function () {
      return this.storage;
    }
  }

  /* GARLIC PLUGIN DEFINITION
  * ========================= */

  $.fn.garlic = function ( option, fn ) {
    var options = $.extend(true, {}, $.fn.garlic.defaults, option, this.data() )
      , storage = new Storage()
      , returnValue = false;

    // this plugin heavily rely on local Storage. If there is no localStorage or data-storage=false, no need to go further
    if ( !storage.defined ) {
      return false;
    }

    function bind ( self ) {
      var $this = $( self )
        , data = $this.data( 'garlic' )
        , fieldOptions = $.extend( {}, options, $this.data() );

      // don't bind an elem with data-storage=false
      if ( 'undefined' !== typeof fieldOptions.storage && !fieldOptions.storage ) {
        return;
      }

      // don't bind a password type field
      if ( 'password' === $( self ).attr( 'type' ) ) {
        return;
      }

      // if data never binded, bind it right now!
      if ( !data ) {
        $this.data( 'garlic', ( data = new Garlic( self, storage, fieldOptions ) ) );
      }

      // here is our garlic public function accessor, currently does not support args
      if ( 'string' === typeof option && 'function' === typeof data[option] ) {
        return data[option]();
      }
    }

    // loop through every elemt we want to garlic
    this.each(function () {

      // if a form elem is given, bind all its input children
      if ( $( this ).is( 'form' ) ) {
        $( this ).find( options.inputs ).each( function () {
          if ( $( this ).is( options.excluded ) ) { return; }
          returnValue = bind( $( this ) );
        });

      // if it is a Garlic supported single element, bind it too
      // add here a return instance, cuz' we could call public methods on single elems with data[option]() above
      } else if ( $( this ).is( options.inputs ) ) {
        if ( $( this ).is( options.excluded ) ) { return; }
        returnValue = bind( $( this ) );
      }
    });

    return 'function' === typeof fn ? fn() : returnValue;
  }

  /* GARLIC CONFIGS & OPTIONS
  * ========================= */
  $.fn.garlic.Constructor = Garlic;

  $.fn.garlic.defaults = {
      destroy: true                                                                                         // Remove or not localstorage on submit & clear
    , inputs: 'input, textarea, select'                                                                     // Default supported inputs.
    , excluded: 'input[type="file"], input[type="hidden"], input[type="submit"]'                            // Default ignored inputs.
    , events: [ 'DOMAttrModified', 'textInput', 'input', 'change', 'click', 'keypress', 'paste', 'focus' ]  // Events list that trigger a localStorage
    , domain: false                                                                                         // Store et retrieve forms data accross all domain, not just on
    , expires: false                                                                                        // false for no expiration, otherwise (int) in seconds for auto-expiration
    , conflictManager: {
        enabled: false                                                                                      // Manage default data and persisted data. If false, persisted data will always replace default ones
      , garlicPriority: true                                                                                // If form have default data, garlic persisted data will be shown first
      , template: '<span class="garlic-swap"></span>'                                                       // Template used to swap between values if conflict detected
      , message: 'This is your saved data. Click here to see default one'                                   // Default message for swapping data / state
      , onConflictDetected: function ( $item, storedVal ) { return true; }                                  // This function will be triggered if a conflict is detected on an item. Return true if you want Garlic behavior, return false if you want to override it
    }
   , getPath: function ( $item ) {}                                                                         // Set your own key-storing strategy per field
   , onRetrieve: function ( $item, storedVal ) {}                                                           // This function will be triggered each time Garlic find an retrieve a local stored data for a field
   , onPersist: function ( $item, storedVal ) {}                                                            // This function will be triggered each time Garlic stores a field to local storage
  }


  /* GARLIC DATA-API
  * =============== */
  $( window ).on( 'load', function () {
    $( '[data-persist="garlic"]' ).each( function () {
      $(this).garlic();
    })
  });

// This plugin works with jQuery or Zepto (with data extension builded for Zepto. See changelog 0.0.6)
}(window.jQuery || window.Zepto);


  }).apply(root, arguments);
});
}(this));

(function(root) {
define("bootstrap-tooltip", ["jquery","bootstrap-transition","bootstrap-prototype-fix"], function() {
  return (function() {
/* ========================================================================
 * Bootstrap: tooltip.js v3.3.6
 * http://getbootstrap.com/javascript/#tooltip
 * Inspired by the original jQuery.tipsy by Jason Frame
 * ========================================================================
 * Copyright 2011-2015 Twitter, Inc.
 * Licensed under MIT (https://github.com/twbs/bootstrap/blob/master/LICENSE)
 * ======================================================================== */


+function ($) {
  'use strict';

  // TOOLTIP PUBLIC CLASS DEFINITION
  // ===============================

  var Tooltip = function (element, options) {
    this.type       = null
    this.options    = null
    this.enabled    = null
    this.timeout    = null
    this.hoverState = null
    this.$element   = null
    this.inState    = null

    this.init('tooltip', element, options)
  }

  Tooltip.VERSION  = '3.3.6'

  Tooltip.TRANSITION_DURATION = 150

  Tooltip.DEFAULTS = {
    animation: true,
    placement: 'top',
    selector: false,
    template: '<div class="tooltip" role="tooltip"><div class="tooltip-arrow"></div><div class="tooltip-inner"></div></div>',
    trigger: 'hover focus',
    title: '',
    delay: 0,
    html: false,
    container: false,
    viewport: {
      selector: 'body',
      padding: 0
    }
  }

  Tooltip.prototype.init = function (type, element, options) {
    this.enabled   = true
    this.type      = type
    this.$element  = $(element)
    this.options   = this.getOptions(options)
    this.$viewport = this.options.viewport && $($.isFunction(this.options.viewport) ? this.options.viewport.call(this, this.$element) : (this.options.viewport.selector || this.options.viewport))
    this.inState   = { click: false, hover: false, focus: false }

    if (this.$element[0] instanceof document.constructor && !this.options.selector) {
      throw new Error('`selector` option must be specified when initializing ' + this.type + ' on the window.document object!')
    }

    var triggers = this.options.trigger.split(' ')

    for (var i = triggers.length; i--;) {
      var trigger = triggers[i]

      if (trigger == 'click') {
        this.$element.on('click.' + this.type, this.options.selector, $.proxy(this.toggle, this))
      } else if (trigger != 'manual') {
        var eventIn  = trigger == 'hover' ? 'mouseenter' : 'focusin'
        var eventOut = trigger == 'hover' ? 'mouseleave' : 'focusout'

        this.$element.on(eventIn  + '.' + this.type, this.options.selector, $.proxy(this.enter, this))
        this.$element.on(eventOut + '.' + this.type, this.options.selector, $.proxy(this.leave, this))
      }
    }

    this.options.selector ?
      (this._options = $.extend({}, this.options, { trigger: 'manual', selector: '' })) :
      this.fixTitle()
  }

  Tooltip.prototype.getDefaults = function () {
    return Tooltip.DEFAULTS
  }

  Tooltip.prototype.getOptions = function (options) {
    options = $.extend({}, this.getDefaults(), this.$element.data(), options)

    if (options.delay && typeof options.delay == 'number') {
      options.delay = {
        show: options.delay,
        hide: options.delay
      }
    }

    return options
  }

  Tooltip.prototype.getDelegateOptions = function () {
    var options  = {}
    var defaults = this.getDefaults()

    this._options && $.each(this._options, function (key, value) {
      if (defaults[key] != value) options[key] = value
    })

    return options
  }

  Tooltip.prototype.enter = function (obj) {
    var self = obj instanceof this.constructor ?
      obj : $(obj.currentTarget).data('bs.' + this.type)

    if (!self) {
      self = new this.constructor(obj.currentTarget, this.getDelegateOptions())
      $(obj.currentTarget).data('bs.' + this.type, self)
    }

    if (obj instanceof $.Event) {
      self.inState[obj.type == 'focusin' ? 'focus' : 'hover'] = true
    }

    if (self.tip().hasClass('in') || self.hoverState == 'in') {
      self.hoverState = 'in'
      return
    }

    clearTimeout(self.timeout)

    self.hoverState = 'in'

    if (!self.options.delay || !self.options.delay.show) return self.show()

    self.timeout = setTimeout(function () {
      if (self.hoverState == 'in') self.show()
    }, self.options.delay.show)
  }

  Tooltip.prototype.isInStateTrue = function () {
    for (var key in this.inState) {
      if (this.inState[key]) return true
    }

    return false
  }

  Tooltip.prototype.leave = function (obj) {
    var self = obj instanceof this.constructor ?
      obj : $(obj.currentTarget).data('bs.' + this.type)

    if (!self) {
      self = new this.constructor(obj.currentTarget, this.getDelegateOptions())
      $(obj.currentTarget).data('bs.' + this.type, self)
    }

    if (obj instanceof $.Event) {
      self.inState[obj.type == 'focusout' ? 'focus' : 'hover'] = false
    }

    if (self.isInStateTrue()) return

    clearTimeout(self.timeout)

    self.hoverState = 'out'

    if (!self.options.delay || !self.options.delay.hide) return self.hide()

    self.timeout = setTimeout(function () {
      if (self.hoverState == 'out') self.hide()
    }, self.options.delay.hide)
  }

  Tooltip.prototype.show = function () {
    var e = $.Event('show.bs.' + this.type)

    if (this.hasContent() && this.enabled) {
      this.$element.trigger(e)

      var inDom = $.contains(this.$element[0].ownerDocument.documentElement, this.$element[0])
      if (e.isDefaultPrevented() || !inDom) return
      var that = this

      var $tip = this.tip()

      var tipId = this.getUID(this.type)

      this.setContent()
      $tip.attr('id', tipId)
      this.$element.attr('aria-describedby', tipId)

      if (this.options.animation) $tip.addClass('fade')

      var placement = typeof this.options.placement == 'function' ?
        this.options.placement.call(this, $tip[0], this.$element[0]) :
        this.options.placement

      var autoToken = /\s?auto?\s?/i
      var autoPlace = autoToken.test(placement)
      if (autoPlace) placement = placement.replace(autoToken, '') || 'top'

      $tip
        .detach()
        .css({ top: 0, left: 0, display: 'block' })
        .addClass(placement)
        .data('bs.' + this.type, this)

      this.options.container ? $tip.appendTo(this.options.container) : $tip.insertAfter(this.$element)
      this.$element.trigger('inserted.bs.' + this.type)

      var pos          = this.getPosition()
      var actualWidth  = $tip[0].offsetWidth
      var actualHeight = $tip[0].offsetHeight

      if (autoPlace) {
        var orgPlacement = placement
        var viewportDim = this.getPosition(this.$viewport)

        placement = placement == 'bottom' && pos.bottom + actualHeight > viewportDim.bottom ? 'top'    :
                    placement == 'top'    && pos.top    - actualHeight < viewportDim.top    ? 'bottom' :
                    placement == 'right'  && pos.right  + actualWidth  > viewportDim.width  ? 'left'   :
                    placement == 'left'   && pos.left   - actualWidth  < viewportDim.left   ? 'right'  :
                    placement

        $tip
          .removeClass(orgPlacement)
          .addClass(placement)
      }

      var calculatedOffset = this.getCalculatedOffset(placement, pos, actualWidth, actualHeight)

      this.applyPlacement(calculatedOffset, placement)

      var complete = function () {
        var prevHoverState = that.hoverState
        that.$element.trigger('shown.bs.' + that.type)
        that.hoverState = null

        if (prevHoverState == 'out') that.leave(that)
      }

      $.support.transition && this.$tip.hasClass('fade') ?
        $tip
          .one('bsTransitionEnd', complete)
          .emulateTransitionEnd(Tooltip.TRANSITION_DURATION) :
        complete()
    }
  }

  Tooltip.prototype.applyPlacement = function (offset, placement) {
    var $tip   = this.tip()
    var width  = $tip[0].offsetWidth
    var height = $tip[0].offsetHeight

    // manually read margins because getBoundingClientRect includes difference
    var marginTop = parseInt($tip.css('margin-top'), 10)
    var marginLeft = parseInt($tip.css('margin-left'), 10)

    // we must check for NaN for ie 8/9
    if (isNaN(marginTop))  marginTop  = 0
    if (isNaN(marginLeft)) marginLeft = 0

    offset.top  += marginTop
    offset.left += marginLeft

    // $.fn.offset doesn't round pixel values
    // so we use setOffset directly with our own function B-0
    $.offset.setOffset($tip[0], $.extend({
      using: function (props) {
        $tip.css({
          top: Math.round(props.top),
          left: Math.round(props.left)
        })
      }
    }, offset), 0)

    $tip.addClass('in')

    // check to see if placing tip in new offset caused the tip to resize itself
    var actualWidth  = $tip[0].offsetWidth
    var actualHeight = $tip[0].offsetHeight

    if (placement == 'top' && actualHeight != height) {
      offset.top = offset.top + height - actualHeight
    }

    var delta = this.getViewportAdjustedDelta(placement, offset, actualWidth, actualHeight)

    if (delta.left) offset.left += delta.left
    else offset.top += delta.top

    var isVertical          = /top|bottom/.test(placement)
    var arrowDelta          = isVertical ? delta.left * 2 - width + actualWidth : delta.top * 2 - height + actualHeight
    var arrowOffsetPosition = isVertical ? 'offsetWidth' : 'offsetHeight'

    $tip.offset(offset)
    this.replaceArrow(arrowDelta, $tip[0][arrowOffsetPosition], isVertical)
  }

  Tooltip.prototype.replaceArrow = function (delta, dimension, isVertical) {
    this.arrow()
      .css(isVertical ? 'left' : 'top', 50 * (1 - delta / dimension) + '%')
      .css(isVertical ? 'top' : 'left', '')
  }

  Tooltip.prototype.setContent = function () {
    var $tip  = this.tip()
    var title = this.getTitle()

    $tip.find('.tooltip-inner')[this.options.html ? 'html' : 'text'](title)
    $tip.removeClass('fade in top bottom left right')
  }

  Tooltip.prototype.hide = function (callback) {
    var that = this
    var $tip = $(this.$tip)
    var e    = $.Event('hide.bs.' + this.type)

    function complete() {
      if (that.hoverState != 'in') $tip.detach()
      that.$element
        .removeAttr('aria-describedby')
        .trigger('hidden.bs.' + that.type)
      callback && callback()
    }

    this.$element.trigger(e)

    if (e.isDefaultPrevented()) return

    $tip.removeClass('in')

    $.support.transition && $tip.hasClass('fade') ?
      $tip
        .one('bsTransitionEnd', complete)
        .emulateTransitionEnd(Tooltip.TRANSITION_DURATION) :
      complete()

    this.hoverState = null

    return this
  }

  Tooltip.prototype.fixTitle = function () {
    var $e = this.$element
    if ($e.attr('title') || typeof $e.attr('data-original-title') != 'string') {
      $e.attr('data-original-title', $e.attr('title') || '').attr('title', '')
    }
  }

  Tooltip.prototype.hasContent = function () {
    return this.getTitle()
  }

  Tooltip.prototype.getPosition = function ($element) {
    $element   = $element || this.$element

    var el     = $element[0]
    var isBody = el.tagName == 'BODY'

    var elRect    = el.getBoundingClientRect()
    if (elRect.width == null) {
      // width and height are missing in IE8, so compute them manually; see https://github.com/twbs/bootstrap/issues/14093
      elRect = $.extend({}, elRect, { width: elRect.right - elRect.left, height: elRect.bottom - elRect.top })
    }
    var elOffset  = isBody ? { top: 0, left: 0 } : $element.offset()
    var scroll    = { scroll: isBody ? document.documentElement.scrollTop || document.body.scrollTop : $element.scrollTop() }
    var outerDims = isBody ? { width: $(window).width(), height: $(window).height() } : null

    return $.extend({}, elRect, scroll, outerDims, elOffset)
  }

  Tooltip.prototype.getCalculatedOffset = function (placement, pos, actualWidth, actualHeight) {
    return placement == 'bottom' ? { top: pos.top + pos.height,   left: pos.left + pos.width / 2 - actualWidth / 2 } :
           placement == 'top'    ? { top: pos.top - actualHeight, left: pos.left + pos.width / 2 - actualWidth / 2 } :
           placement == 'left'   ? { top: pos.top + pos.height / 2 - actualHeight / 2, left: pos.left - actualWidth } :
        /* placement == 'right' */ { top: pos.top + pos.height / 2 - actualHeight / 2, left: pos.left + pos.width }

  }

  Tooltip.prototype.getViewportAdjustedDelta = function (placement, pos, actualWidth, actualHeight) {
    var delta = { top: 0, left: 0 }
    if (!this.$viewport) return delta

    var viewportPadding = this.options.viewport && this.options.viewport.padding || 0
    var viewportDimensions = this.getPosition(this.$viewport)

    if (/right|left/.test(placement)) {
      var topEdgeOffset    = pos.top - viewportPadding - viewportDimensions.scroll
      var bottomEdgeOffset = pos.top + viewportPadding - viewportDimensions.scroll + actualHeight
      if (topEdgeOffset < viewportDimensions.top) { // top overflow
        delta.top = viewportDimensions.top - topEdgeOffset
      } else if (bottomEdgeOffset > viewportDimensions.top + viewportDimensions.height) { // bottom overflow
        delta.top = viewportDimensions.top + viewportDimensions.height - bottomEdgeOffset
      }
    } else {
      var leftEdgeOffset  = pos.left - viewportPadding
      var rightEdgeOffset = pos.left + viewportPadding + actualWidth
      if (leftEdgeOffset < viewportDimensions.left) { // left overflow
        delta.left = viewportDimensions.left - leftEdgeOffset
      } else if (rightEdgeOffset > viewportDimensions.right) { // right overflow
        delta.left = viewportDimensions.left + viewportDimensions.width - rightEdgeOffset
      }
    }

    return delta
  }

  Tooltip.prototype.getTitle = function () {
    var title
    var $e = this.$element
    var o  = this.options

    title = $e.attr('data-original-title')
      || (typeof o.title == 'function' ? o.title.call($e[0]) :  o.title)

    return title
  }

  Tooltip.prototype.getUID = function (prefix) {
    do prefix += ~~(Math.random() * 1000000)
    while (document.getElementById(prefix))
    return prefix
  }

  Tooltip.prototype.tip = function () {
    if (!this.$tip) {
      this.$tip = $(this.options.template)
      if (this.$tip.length != 1) {
        throw new Error(this.type + ' `template` option must consist of exactly 1 top-level element!')
      }
    }
    return this.$tip
  }

  Tooltip.prototype.arrow = function () {
    return (this.$arrow = this.$arrow || this.tip().find('.tooltip-arrow'))
  }

  Tooltip.prototype.enable = function () {
    this.enabled = true
  }

  Tooltip.prototype.disable = function () {
    this.enabled = false
  }

  Tooltip.prototype.toggleEnabled = function () {
    this.enabled = !this.enabled
  }

  Tooltip.prototype.toggle = function (e) {
    var self = this
    if (e) {
      self = $(e.currentTarget).data('bs.' + this.type)
      if (!self) {
        self = new this.constructor(e.currentTarget, this.getDelegateOptions())
        $(e.currentTarget).data('bs.' + this.type, self)
      }
    }

    if (e) {
      self.inState.click = !self.inState.click
      if (self.isInStateTrue()) self.enter(self)
      else self.leave(self)
    } else {
      self.tip().hasClass('in') ? self.leave(self) : self.enter(self)
    }
  }

  Tooltip.prototype.destroy = function () {
    var that = this
    clearTimeout(this.timeout)
    this.hide(function () {
      that.$element.off('.' + that.type).removeData('bs.' + that.type)
      if (that.$tip) {
        that.$tip.detach()
      }
      that.$tip = null
      that.$arrow = null
      that.$viewport = null
    })
  }


  // TOOLTIP PLUGIN DEFINITION
  // =========================

  function Plugin(option) {
    return this.each(function () {
      var $this   = $(this)
      var data    = $this.data('bs.tooltip')
      var options = typeof option == 'object' && option

      if (!data && /destroy|hide/.test(option)) return
      if (!data) $this.data('bs.tooltip', (data = new Tooltip(this, options)))
      if (typeof option == 'string') data[option]()
    })
  }

  var old = $.fn.tooltip

  $.fn.tooltip             = Plugin
  $.fn.tooltip.Constructor = Tooltip


  // TOOLTIP NO CONFLICT
  // ===================

  $.fn.tooltip.noConflict = function () {
    $.fn.tooltip = old
    return this
  }

}(jQuery);


  }).apply(root, arguments);
});
}(this));

(function(root) {
define("bootstrap-popover", ["jquery","bootstrap-transition","bootstrap-tooltip","bootstrap-prototype-fix"], function() {
  return (function() {
/* ========================================================================
 * Bootstrap: popover.js v3.3.6
 * http://getbootstrap.com/javascript/#popovers
 * ========================================================================
 * Copyright 2011-2015 Twitter, Inc.
 * Licensed under MIT (https://github.com/twbs/bootstrap/blob/master/LICENSE)
 * ======================================================================== */


+function ($) {
  'use strict';

  // POPOVER PUBLIC CLASS DEFINITION
  // ===============================

  var Popover = function (element, options) {
    this.init('popover', element, options)
  }

  if (!$.fn.tooltip) throw new Error('Popover requires tooltip.js')

  Popover.VERSION  = '3.3.6'

  Popover.DEFAULTS = $.extend({}, $.fn.tooltip.Constructor.DEFAULTS, {
    placement: 'right',
    trigger: 'click',
    content: '',
    template: '<div class="popover" role="tooltip"><div class="arrow"></div><h3 class="popover-title"></h3><div class="popover-content"></div></div>'
  })


  // NOTE: POPOVER EXTENDS tooltip.js
  // ================================

  Popover.prototype = $.extend({}, $.fn.tooltip.Constructor.prototype)

  Popover.prototype.constructor = Popover

  Popover.prototype.getDefaults = function () {
    return Popover.DEFAULTS
  }

  Popover.prototype.setContent = function () {
    var $tip    = this.tip()
    var title   = this.getTitle()
    var content = this.getContent()

    $tip.find('.popover-title')[this.options.html ? 'html' : 'text'](title)
    $tip.find('.popover-content').children().detach().end()[ // we use append for html objects to maintain js events
      this.options.html ? (typeof content == 'string' ? 'html' : 'append') : 'text'
    ](content)

    $tip.removeClass('fade top bottom left right in')

    // IE8 doesn't accept hiding via the `:empty` pseudo selector, we have to do
    // this manually by checking the contents.
    if (!$tip.find('.popover-title').html()) $tip.find('.popover-title').hide()
  }

  Popover.prototype.hasContent = function () {
    return this.getTitle() || this.getContent()
  }

  Popover.prototype.getContent = function () {
    var $e = this.$element
    var o  = this.options

    return $e.attr('data-content')
      || (typeof o.content == 'function' ?
            o.content.call($e[0]) :
            o.content)
  }

  Popover.prototype.arrow = function () {
    return (this.$arrow = this.$arrow || this.tip().find('.arrow'))
  }


  // POPOVER PLUGIN DEFINITION
  // =========================

  function Plugin(option) {
    return this.each(function () {
      var $this   = $(this)
      var data    = $this.data('bs.popover')
      var options = typeof option == 'object' && option

      if (!data && /destroy|hide/.test(option)) return
      if (!data) $this.data('bs.popover', (data = new Popover(this, options)))
      if (typeof option == 'string') data[option]()
    })
  }

  var old = $.fn.popover

  $.fn.popover             = Plugin
  $.fn.popover.Constructor = Popover


  // POPOVER NO CONFLICT
  // ===================

  $.fn.popover.noConflict = function () {
    $.fn.popover = old
    return this
  }

}(jQuery);


  }).apply(root, arguments);
});
}(this));

define('cartware_checkout/components/checkout',[
	// dependencies
	"jquery",
	"lodash",
	"cartware/components/_base",
	"cartware/dom/event/trigger",
	"cartware/jquery/bem",
	"cartware_checkout",
	"cartware_checkout/classes/accordeon",

	// advises
	"./advises/login",
	"./advises/billing",
	"./advises/checkout",
	"./advises/payment",
	"./advises/regionUpdater",
	"./advises/review",
	"./advises/shipping",
	"./advises/shippingMethod",

	// shims
	"garlicjs",
	"bootstrap-tooltip",
	"bootstrap-popover",
	"bootstrap-button"
], function(
	jQuery,
	_,
	Component,
	triggerEvent,
	BEM,
	CartwareCheckout,
	AccordionReplacement,
	LoginAdvise,
	BillingAdvise,
	CheckoutAdvise,
	PaymentAdvise,
	RegionUpdaterAdvise,
	ReviewAdvise,
	ShippingAdvise,
	ShippingMethodAdvise
) {
	var $ = jQuery;
	var features = {
		garlic: true
	};

	// Add ECMA262-5 Array methods if not supported natively
	if (!('indexOf' in Array.prototype)) {
		Array.prototype.indexOf= function(find, i /*opt*/) {
			if (i===undefined) i= 0;
			if (i<0) i+= this.length;
			if (i<0) i= 0;
			for (var n= this.length; i<n; i++)
				if (i in this && this[i]===find)
					return i;
			return -1;
		};
	}

	return Component.create({
		block:		null,
		accordion:	null,
		checkout:	null,

		init: function() {
			var advises = [
				LoginAdvise,
				BillingAdvise,
				CheckoutAdvise,
				PaymentAdvise,
				RegionUpdaterAdvise,
				ReviewAdvise,
				ShippingAdvise,
				ShippingMethodAdvise
			];

			_.forEach(advises, function(Advise) {
				(new Advise).init();
			});
		},

		$init: function() {
			var self = this;

			this.block = BEM.block("cartware-checkout");
			this.accordion = new AccordionReplacement(this.block);

			this.checkout = new Checkout(this.accordion, this.block.data("checkout-config"));

			// Expose globally
			window.accordion = this.accordion;
			window.checkout = this.checkout;

			this.initializeBilling();

			CartwareCheckout().flag("ready");

			CartwareCheckout().retrieve("shipping", function(shipping) {
				CartwareCheckout().retrieve("billing", function(billing) {
					if (features.garlic) {
						self.initializeGarlic();
					}
				});
			});
		},

		initializeBilling: function() {
			var billingAddressBlock = $('#billing-address'),
				checkbox = $('input[name="billing[different_from_shipping]"]');

			function onChange() {
				var checked = checkbox.prop('checked');
				billingAddressBlock.css('display', checked ? 'block' : 'none');
			}

			checkbox.on('change garlic.retrieve', onChange);
			onChange();
		},

		initializeGarlic: function() {
			var filledTextArrays = [];

			this.block.find('#co-shipping-form, #co-billing-form').garlic({
				expires: 3600,
				onRetrieve: function (elem, value) {
					var node = elem.get(0),
						nodeName = node.nodeName.toLowerCase(),
						formName = elem.prop('name');

					switch (nodeName) {
						case 'select':
							triggerEvent(node, 'change');

							if (formName.match(/\[region_id\]/)) {
								setTimeout(function() {
									elem.prop('value', value);
									triggerEvent(node, 'change');
								}, 10);
							}
							break;

						case 'input':
							switch (node.type.toLowerCase()) {
								case 'text':
									if (formName.substr(formName.length - 2) === "[]") {
										if (filledTextArrays.indexOf(formName) >= 0) {
											elem.prop('value', '');
										} else {
											filledTextArrays.push(formName);
										}
									}
									break;

								case 'radio':
								case 'checkbox':
									//$('*[name="' + elem.prop('name') + '"]').each(function () {
									//	var uniform = $(this).data('uniformed');
									//	uniform && uniform.update();
									//});
									if (node.checked && node.onclick) {
										triggerEvent(node, 'click');
									}
									break;
							}
							break;

						//default:
						//	var uniform = elem.data('uniformed');
						//	uniform && uniform.update();
						//	break;
					}

					elem.trigger('garlic.retrieve');
				}
			});
		}
	});
});

define('text!shoptheme/templates/magnific-popup/default.html',[],function () { return '<div class="popup-wrap<% if ("undefined" != typeof modifier && modifier != "") { %> <%- modifier %><% } %>">\r\n\t<% if ("undefined" !== typeof displayCloseButton && displayCloseButton) { %>\r\n\t\t<button title="Close" type="button" class="mfp-close">&times;</button>\r\n\t<% } %>\r\n\t<% if ("undefined" != typeof title && title != "") { %>\r\n\t\t<div class="popup-header">\r\n\t\t\t<h4 class="popup-title">\r\n\t\t\t\t<%= title %>\r\n\t\t\t</h4>\r\n\t\t</div>\r\n\t<% } %>\r\n\t<div class="popup-body">\r\n\t\t<%= body %>\r\n\t</div>\r\n\t<% if ("undefined" != typeof footer) { %>\r\n\t\t<div class="popup-footer">\r\n\t\t\t<%= footer %>\r\n\t\t</div>\r\n\t<% } %>\r\n</div>';});

define('cartware_checkout/components/login',[
	"jquery",
	"lodash",
	"cartware/components/_base",
	"cartware/jquery/bem",
	"text!shoptheme/templates/magnific-popup/default.html",
	"magnific-popup"
], function(
	$,
	_,
	Component,
	BEM,
	PopupHtml
) {
	var _popupTemplate = null;

	return Component.create({
		block:		null,
		hintLink:	null,
		loginBlock:	null,

		$init: function() {
			var self = this;

			this.block = BEM.block("cartware-checkout");
			this.hintLink = this.block.getElements("login-hint");
			this.loginBlock = BEM.block("cartware-checkout-login");

			this.hintLink.on("click", function(e) {
				e.preventDefault();

				$.magnificPopup.open({
					items: {
						src: self._renderPopup({
							title: self.loginBlock.getElements('title').html(),
							body: self.loginBlock.getElements('content').html()
						}),
						type: 'inline'
					}
				});
			});
		},

		_renderPopup: function(data) {
			if (null === _popupTemplate) {
				_popupTemplate = _.template(PopupHtml);
			}

			return _popupTemplate(data);
		}
	});
});
define('cartware_checkout/components/agreements',[
	"jquery",
	"lodash",
	"cartware/components/_base",
	"cartware/jquery/bem",
	"text!shoptheme/templates/magnific-popup/default.html",
	"magnific-popup"
], function(
	$,
	_,
	Component,
	BEM,
	PopupHtml
) {
	var _popupTemplate = null;

	return Component.create({
		$init: function() {
			var self = this;
			var selector = BEM.buildElementClassname("checkout-agreements", "popup-link");

			$(document).on("click", "." + selector, function(e) {
				var link = $(e.currentTarget);

				e.preventDefault();

				link.magnificPopup({
					type: 'ajax',
					callbacks: {
						parseAjax: function(mfpResponse) {
							var html = mfpResponse.data
								.match(/<body[^>]*>([\s\S]*)<\/body>/)[1]
								.replace(/<script[^>]*>([\s\S]*?)<\/script>/gi, '');

							mfpResponse.data = self._renderPopup({
								body: html
							});
						}
					}
				}).magnificPopup('open');
			});
		},

		_renderPopup: function(data) {
			if (null === _popupTemplate) {
				_popupTemplate = _.template(PopupHtml);
			}

			return _popupTemplate(data);
		}
	});
});
define('cartware_checkout/components/acceptance',[
	"jquery",
	"lodash",
	"cartware/components/_base",
	"cartware/jquery/bem",
	"text!shoptheme/templates/magnific-popup/default.html",
	"magnific-popup"
], function(
	$,
	_,
	Component,
	BEM,
	PopupHtml
) {
	var _acceptancePopup = null;

	return Component.create({
		$init: function() {

			var overlay = $('#payolution_overlay');

			function displayPayolutionOverlay()
			{
				var wrap = [
					'<div class="popup-wrap">',
					'<div class="popup-body">',
					overlay.html(),
					'</div>',
					'</div>'
				];

				$.magnificPopup.open({ items: {type: "inline", src: wrap.join("")} });
			}

			function removePayolutionOverlay()
			{
				$.magnificPopup.close();
			}

			window.displayPayolutionOverlay = displayPayolutionOverlay;
			window.removePayolutionOverlay = removePayolutionOverlay;
			
		},

		_renderPopup: function(data) {
			if (null === _acceptancePopup) {
				_acceptancePopup = _.template(PopupHtml);
			}

			return _acceptancePopup(data);
		}
	});
});
define('cartware_checkout/components/coupon',[
	"jquery",
	"cartware/components/_base",
	"gigaset/spinner",
	"bootstrap-button"
], function(
	$,
	Component,
	Spinner
) {
	return Component.create({
		$init: function() {
			var self = this;

			$(document).on("submit", "#discount-coupon-form", function(e) {
				var form = $(e.target);

				e.preventDefault();
				e.stopPropagation();

				Spinner.add();

				self.submit(form);
			});

			$(document).on("click", "#discount-coupon-form button", function(e) {
				var button = $(e.target),
					action = button.data("action"),
					form = button.parents("#discount-coupon-form");

				self.submit(form, action);
			});
		},

		submit: function(form, action) {
			var wrap = form.parents("#cartware-checkout-coupon-wrap:first"),
				vForm = new VarienForm(form.get(0)),
				couponCode = form.find("[name=\"coupon_code\"]:first"),
				removeInput = form.find("[name=\"remove\"]:first"),
				messageContainer = wrap.find(".message:first"),
				loadingMessage = messageContainer.find(".loading:first");

			switch (action) {
				case "cancel":
					couponCode.removeClass("required-entry");
					removeInput.val(1);
					break;

				case "apply":
				default:
					couponCode.addClass("required-entry");
					removeInput.val(0);
					break;
			}

			if (vForm.validator.validate()) {
				wrap
					.find("button")
					.attr("disabled", "disabled")
					.animate({
						opacity: 0.4
					});

				loadingMessage.detach();
				messageContainer.empty();
				loadingMessage.appendTo(messageContainer);
				loadingMessage.css("display", "block");

				try { window.accordion._layout(); } catch (e) {}

				Spinner.add();

				$.ajax(form.attr("action"), {
					method: "POST",
					data: form.serialize(),
					dataType: "json"
				}).then(function(res) {
					if ("message" in res) {
						alert(res.message);
					}

					$.each(res.blocks, function(blockName, blockHtml) {
						switch (blockName) {
							case "checkout.onepage.review.coupon":
								$("#cartware-checkout-coupon-wrap").replaceWith(blockHtml);
								break;

							case "review_info":
								$("#checkout-review-wrapper").replaceWith(blockHtml);
								break;
						}

						try { window.accordion._layout(); } catch (e) {}
					});

					Spinner.remove();
				});
			}

			try { window.accordion._layout(); } catch (e) {}
		}
	});
});
/*!
 * SelectWoo 1.0.9
 * https://github.com/woocommerce/selectWoo
 *
 * Released under the MIT license
 * https://github.com/woocommerce/selectWoo/blob/master/LICENSE.md
 */
(function (factory) {
  if (typeof define === 'function' && define.amd) {
    // AMD. Register as an anonymous module.
    define('select2',['jquery'], factory);
  } else if (typeof module === 'object' && module.exports) {
    // Node/CommonJS
    module.exports = function (root, jQuery) {
      if (jQuery === undefined) {
        // require('jQuery') returns a factory that requires window to
        // build a jQuery instance, we normalize how we use modules
        // that require this pattern but the window provided is a noop
        // if it's defined (how jquery works)
        if (typeof window !== 'undefined') {
          jQuery = require('jquery');
        }
        else {
          jQuery = require('jquery')(root);
        }
      }
      factory(jQuery);
      return jQuery;
    };
  } else {
    // Browser globals
    factory(jQuery);
  }
} (function (jQuery) {
  // This is needed so we can catch the AMD loader configuration and use it
  // The inner file should be wrapped (by `banner.start.js`) in a function that
  // returns the AMD loader references.
  var S2 =(function () {
  // Restore the Select2 AMD loader so it can be used
  // Needed mostly in the language files, where the loader is not inserted
  if (jQuery && jQuery.fn && jQuery.fn.select2 && jQuery.fn.select2.amd) {
    var S2 = jQuery.fn.select2.amd;
  }
var S2;(function () { if (!S2 || !S2.requirejs) {
if (!S2) { S2 = {}; } else { require = S2; }
/**
 * @license almond 0.3.3 Copyright jQuery Foundation and other contributors.
 * Released under MIT license, http://github.com/requirejs/almond/LICENSE
 */
//Going sloppy to avoid 'use strict' string cost, but strict practices should
//be followed.
/*global setTimeout: false */

var requirejs, require, define;
(function (undef) {
    var main, req, makeMap, handlers,
        defined = {},
        waiting = {},
        config = {},
        defining = {},
        hasOwn = Object.prototype.hasOwnProperty,
        aps = [].slice,
        jsSuffixRegExp = /\.js$/;

    function hasProp(obj, prop) {
        return hasOwn.call(obj, prop);
    }

    /**
     * Given a relative module name, like ./something, normalize it to
     * a real name that can be mapped to a path.
     * @param {String} name the relative name
     * @param {String} baseName a real name that the name arg is relative
     * to.
     * @returns {String} normalized name
     */
    function normalize(name, baseName) {
        var nameParts, nameSegment, mapValue, foundMap, lastIndex,
            foundI, foundStarMap, starI, i, j, part, normalizedBaseParts,
            baseParts = baseName && baseName.split("/"),
            map = config.map,
            starMap = (map && map['*']) || {};

        //Adjust any relative paths.
        if (name) {
            name = name.split('/');
            lastIndex = name.length - 1;

            // If wanting node ID compatibility, strip .js from end
            // of IDs. Have to do this here, and not in nameToUrl
            // because node allows either .js or non .js to map
            // to same file.
            if (config.nodeIdCompat && jsSuffixRegExp.test(name[lastIndex])) {
                name[lastIndex] = name[lastIndex].replace(jsSuffixRegExp, '');
            }

            // Starts with a '.' so need the baseName
            if (name[0].charAt(0) === '.' && baseParts) {
                //Convert baseName to array, and lop off the last part,
                //so that . matches that 'directory' and not name of the baseName's
                //module. For instance, baseName of 'one/two/three', maps to
                //'one/two/three.js', but we want the directory, 'one/two' for
                //this normalization.
                normalizedBaseParts = baseParts.slice(0, baseParts.length - 1);
                name = normalizedBaseParts.concat(name);
            }

            //start trimDots
            for (i = 0; i < name.length; i++) {
                part = name[i];
                if (part === '.') {
                    name.splice(i, 1);
                    i -= 1;
                } else if (part === '..') {
                    // If at the start, or previous value is still ..,
                    // keep them so that when converted to a path it may
                    // still work when converted to a path, even though
                    // as an ID it is less than ideal. In larger point
                    // releases, may be better to just kick out an error.
                    if (i === 0 || (i === 1 && name[2] === '..') || name[i - 1] === '..') {
                        continue;
                    } else if (i > 0) {
                        name.splice(i - 1, 2);
                        i -= 2;
                    }
                }
            }
            //end trimDots

            name = name.join('/');
        }

        //Apply map config if available.
        if ((baseParts || starMap) && map) {
            nameParts = name.split('/');

            for (i = nameParts.length; i > 0; i -= 1) {
                nameSegment = nameParts.slice(0, i).join("/");

                if (baseParts) {
                    //Find the longest baseName segment match in the config.
                    //So, do joins on the biggest to smallest lengths of baseParts.
                    for (j = baseParts.length; j > 0; j -= 1) {
                        mapValue = map[baseParts.slice(0, j).join('/')];

                        //baseName segment has  config, find if it has one for
                        //this name.
                        if (mapValue) {
                            mapValue = mapValue[nameSegment];
                            if (mapValue) {
                                //Match, update name to the new value.
                                foundMap = mapValue;
                                foundI = i;
                                break;
                            }
                        }
                    }
                }

                if (foundMap) {
                    break;
                }

                //Check for a star map match, but just hold on to it,
                //if there is a shorter segment match later in a matching
                //config, then favor over this star map.
                if (!foundStarMap && starMap && starMap[nameSegment]) {
                    foundStarMap = starMap[nameSegment];
                    starI = i;
                }
            }

            if (!foundMap && foundStarMap) {
                foundMap = foundStarMap;
                foundI = starI;
            }

            if (foundMap) {
                nameParts.splice(0, foundI, foundMap);
                name = nameParts.join('/');
            }
        }

        return name;
    }

    function makeRequire(relName, forceSync) {
        return function () {
            //A version of a require function that passes a moduleName
            //value for items that may need to
            //look up paths relative to the moduleName
            var args = aps.call(arguments, 0);

            //If first arg is not require('string'), and there is only
            //one arg, it is the array form without a callback. Insert
            //a null so that the following concat is correct.
            if (typeof args[0] !== 'string' && args.length === 1) {
                args.push(null);
            }
            return req.apply(undef, args.concat([relName, forceSync]));
        };
    }

    function makeNormalize(relName) {
        return function (name) {
            return normalize(name, relName);
        };
    }

    function makeLoad(depName) {
        return function (value) {
            defined[depName] = value;
        };
    }

    function callDep(name) {
        if (hasProp(waiting, name)) {
            var args = waiting[name];
            delete waiting[name];
            defining[name] = true;
            main.apply(undef, args);
        }

        if (!hasProp(defined, name) && !hasProp(defining, name)) {
            throw new Error('No ' + name);
        }
        return defined[name];
    }

    //Turns a plugin!resource to [plugin, resource]
    //with the plugin being undefined if the name
    //did not have a plugin prefix.
    function splitPrefix(name) {
        var prefix,
            index = name ? name.indexOf('!') : -1;
        if (index > -1) {
            prefix = name.substring(0, index);
            name = name.substring(index + 1, name.length);
        }
        return [prefix, name];
    }

    //Creates a parts array for a relName where first part is plugin ID,
    //second part is resource ID. Assumes relName has already been normalized.
    function makeRelParts(relName) {
        return relName ? splitPrefix(relName) : [];
    }

    /**
     * Makes a name map, normalizing the name, and using a plugin
     * for normalization if necessary. Grabs a ref to plugin
     * too, as an optimization.
     */
    makeMap = function (name, relParts) {
        var plugin,
            parts = splitPrefix(name),
            prefix = parts[0],
            relResourceName = relParts[1];

        name = parts[1];

        if (prefix) {
            prefix = normalize(prefix, relResourceName);
            plugin = callDep(prefix);
        }

        //Normalize according
        if (prefix) {
            if (plugin && plugin.normalize) {
                name = plugin.normalize(name, makeNormalize(relResourceName));
            } else {
                name = normalize(name, relResourceName);
            }
        } else {
            name = normalize(name, relResourceName);
            parts = splitPrefix(name);
            prefix = parts[0];
            name = parts[1];
            if (prefix) {
                plugin = callDep(prefix);
            }
        }

        //Using ridiculous property names for space reasons
        return {
            f: prefix ? prefix + '!' + name : name, //fullName
            n: name,
            pr: prefix,
            p: plugin
        };
    };

    function makeConfig(name) {
        return function () {
            return (config && config.config && config.config[name]) || {};
        };
    }

    handlers = {
        require: function (name) {
            return makeRequire(name);
        },
        exports: function (name) {
            var e = defined[name];
            if (typeof e !== 'undefined') {
                return e;
            } else {
                return (defined[name] = {});
            }
        },
        module: function (name) {
            return {
                id: name,
                uri: '',
                exports: defined[name],
                config: makeConfig(name)
            };
        }
    };

    main = function (name, deps, callback, relName) {
        var cjsModule, depName, ret, map, i, relParts,
            args = [],
            callbackType = typeof callback,
            usingExports;

        //Use name if no relName
        relName = relName || name;
        relParts = makeRelParts(relName);

        //Call the callback to define the module, if necessary.
        if (callbackType === 'undefined' || callbackType === 'function') {
            //Pull out the defined dependencies and pass the ordered
            //values to the callback.
            //Default to [require, exports, module] if no deps
            deps = !deps.length && callback.length ? ['require', 'exports', 'module'] : deps;
            for (i = 0; i < deps.length; i += 1) {
                map = makeMap(deps[i], relParts);
                depName = map.f;

                //Fast path CommonJS standard dependencies.
                if (depName === "require") {
                    args[i] = handlers.require(name);
                } else if (depName === "exports") {
                    //CommonJS module spec 1.1
                    args[i] = handlers.exports(name);
                    usingExports = true;
                } else if (depName === "module") {
                    //CommonJS module spec 1.1
                    cjsModule = args[i] = handlers.module(name);
                } else if (hasProp(defined, depName) ||
                           hasProp(waiting, depName) ||
                           hasProp(defining, depName)) {
                    args[i] = callDep(depName);
                } else if (map.p) {
                    map.p.load(map.n, makeRequire(relName, true), makeLoad(depName), {});
                    args[i] = defined[depName];
                } else {
                    throw new Error(name + ' missing ' + depName);
                }
            }

            ret = callback ? callback.apply(defined[name], args) : undefined;

            if (name) {
                //If setting exports via "module" is in play,
                //favor that over return value and exports. After that,
                //favor a non-undefined return value over exports use.
                if (cjsModule && cjsModule.exports !== undef &&
                        cjsModule.exports !== defined[name]) {
                    defined[name] = cjsModule.exports;
                } else if (ret !== undef || !usingExports) {
                    //Use the return value from the function.
                    defined[name] = ret;
                }
            }
        } else if (name) {
            //May just be an object definition for the module. Only
            //worry about defining if have a module name.
            defined[name] = callback;
        }
    };

    requirejs = require = req = function (deps, callback, relName, forceSync, alt) {
        if (typeof deps === "string") {
            if (handlers[deps]) {
                //callback in this case is really relName
                return handlers[deps](callback);
            }
            //Just return the module wanted. In this scenario, the
            //deps arg is the module name, and second arg (if passed)
            //is just the relName.
            //Normalize module name, if it contains . or ..
            return callDep(makeMap(deps, makeRelParts(callback)).f);
        } else if (!deps.splice) {
            //deps is a config object, not an array.
            config = deps;
            if (config.deps) {
                req(config.deps, config.callback);
            }
            if (!callback) {
                return;
            }

            if (callback.splice) {
                //callback is an array, which means it is a dependency list.
                //Adjust args if there are dependencies
                deps = callback;
                callback = relName;
                relName = null;
            } else {
                deps = undef;
            }
        }

        //Support require(['a'])
        callback = callback || function () {};

        //If relName is a function, it is an errback handler,
        //so remove it.
        if (typeof relName === 'function') {
            relName = forceSync;
            forceSync = alt;
        }

        //Simulate async callback;
        if (forceSync) {
            main(undef, deps, callback, relName);
        } else {
            //Using a non-zero value because of concern for what old browsers
            //do, and latest browsers "upgrade" to 4 if lower value is used:
            //http://www.whatwg.org/specs/web-apps/current-work/multipage/timers.html#dom-windowtimers-settimeout:
            //If want a value immediately, use require('id') instead -- something
            //that works in almond on the global level, but not guaranteed and
            //unlikely to work in other AMD implementations.
            setTimeout(function () {
                main(undef, deps, callback, relName);
            }, 4);
        }

        return req;
    };

    /**
     * Just drops the config on the floor, but returns req in case
     * the config return value is used.
     */
    req.config = function (cfg) {
        return req(cfg);
    };

    /**
     * Expose module registry for debugging and tooling
     */
    requirejs._defined = defined;

    define = function (name, deps, callback) {
        if (typeof name !== 'string') {
            throw new Error('See almond README: incorrect module build, no module name');
        }

        //This module may not have dependencies
        if (!deps.splice) {
            //deps is not an array, so probably means
            //an object literal or factory function for
            //the value. Adjust args.
            callback = deps;
            deps = [];
        }

        if (!hasProp(defined, name) && !hasProp(waiting, name)) {
            waiting[name] = [name, deps, callback];
        }
    };

    define.amd = {
        jQuery: true
    };
}());

S2.requirejs = requirejs;S2.require = require;S2.define = define;
}
}());
S2.define("almond", function(){});

/* global jQuery:false, $:false */
S2.define('jquery',[],function () {
  var _$ = jQuery || $;

  if (_$ == null && console && console.error) {
    console.error(
      'Select2: An instance of jQuery or a jQuery-compatible library was not ' +
      'found. Make sure that you are including jQuery before Select2 on your ' +
      'web page.'
    );
  }

  return _$;
});

S2.define('select2/utils',[
  'jquery'
], function ($) {
  var Utils = {};

  Utils.Extend = function (ChildClass, SuperClass) {
    var __hasProp = {}.hasOwnProperty;

    function BaseConstructor () {
      this.constructor = ChildClass;
    }

    for (var key in SuperClass) {
      if (__hasProp.call(SuperClass, key)) {
        ChildClass[key] = SuperClass[key];
      }
    }

    BaseConstructor.prototype = SuperClass.prototype;
    ChildClass.prototype = new BaseConstructor();
    ChildClass.__super__ = SuperClass.prototype;

    return ChildClass;
  };

  function getMethods (theClass) {
    var proto = theClass.prototype;

    var methods = [];

    for (var methodName in proto) {
      var m = proto[methodName];

      if (typeof m !== 'function') {
        continue;
      }

      if (methodName === 'constructor') {
        continue;
      }

      methods.push(methodName);
    }

    return methods;
  }

  Utils.Decorate = function (SuperClass, DecoratorClass) {
    var decoratedMethods = getMethods(DecoratorClass);
    var superMethods = getMethods(SuperClass);

    function DecoratedClass () {
      var unshift = Array.prototype.unshift;

      var argCount = DecoratorClass.prototype.constructor.length;

      var calledConstructor = SuperClass.prototype.constructor;

      if (argCount > 0) {
        unshift.call(arguments, SuperClass.prototype.constructor);

        calledConstructor = DecoratorClass.prototype.constructor;
      }

      calledConstructor.apply(this, arguments);
    }

    DecoratorClass.displayName = SuperClass.displayName;

    function ctr () {
      this.constructor = DecoratedClass;
    }

    DecoratedClass.prototype = new ctr();

    for (var m = 0; m < superMethods.length; m++) {
        var superMethod = superMethods[m];

        DecoratedClass.prototype[superMethod] =
          SuperClass.prototype[superMethod];
    }

    var calledMethod = function (methodName) {
      // Stub out the original method if it's not decorating an actual method
      var originalMethod = function () {};

      if (methodName in DecoratedClass.prototype) {
        originalMethod = DecoratedClass.prototype[methodName];
      }

      var decoratedMethod = DecoratorClass.prototype[methodName];

      return function () {
        var unshift = Array.prototype.unshift;

        unshift.call(arguments, originalMethod);

        return decoratedMethod.apply(this, arguments);
      };
    };

    for (var d = 0; d < decoratedMethods.length; d++) {
      var decoratedMethod = decoratedMethods[d];

      DecoratedClass.prototype[decoratedMethod] = calledMethod(decoratedMethod);
    }

    return DecoratedClass;
  };

  var Observable = function () {
    this.listeners = {};
  };

  Observable.prototype.on = function (event, callback) {
    this.listeners = this.listeners || {};

    if (event in this.listeners) {
      this.listeners[event].push(callback);
    } else {
      this.listeners[event] = [callback];
    }
  };

  Observable.prototype.trigger = function (event) {
    var slice = Array.prototype.slice;
    var params = slice.call(arguments, 1);

    this.listeners = this.listeners || {};

    // Params should always come in as an array
    if (params == null) {
      params = [];
    }

    // If there are no arguments to the event, use a temporary object
    if (params.length === 0) {
      params.push({});
    }

    // Set the `_type` of the first object to the event
    params[0]._type = event;

    if (event in this.listeners) {
      this.invoke(this.listeners[event], slice.call(arguments, 1));
    }

    if ('*' in this.listeners) {
      this.invoke(this.listeners['*'], arguments);
    }
  };

  Observable.prototype.invoke = function (listeners, params) {
    for (var i = 0, len = listeners.length; i < len; i++) {
      listeners[i].apply(this, params);
    }
  };

  Utils.Observable = Observable;

  Utils.generateChars = function (length) {
    var chars = '';

    for (var i = 0; i < length; i++) {
      var randomChar = Math.floor(Math.random() * 36);
      chars += randomChar.toString(36);
    }

    return chars;
  };

  Utils.bind = function (func, context) {
    return function () {
      func.apply(context, arguments);
    };
  };

  Utils._convertData = function (data) {
    for (var originalKey in data) {
      var keys = originalKey.split('-');

      var dataLevel = data;

      if (keys.length === 1) {
        continue;
      }

      for (var k = 0; k < keys.length; k++) {
        var key = keys[k];

        // Lowercase the first letter
        // By default, dash-separated becomes camelCase
        key = key.substring(0, 1).toLowerCase() + key.substring(1);

        if (!(key in dataLevel)) {
          dataLevel[key] = {};
        }

        if (k == keys.length - 1) {
          dataLevel[key] = data[originalKey];
        }

        dataLevel = dataLevel[key];
      }

      delete data[originalKey];
    }

    return data;
  };

  Utils.hasScroll = function (index, el) {
    // Adapted from the function created by @ShadowScripter
    // and adapted by @BillBarry on the Stack Exchange Code Review website.
    // The original code can be found at
    // http://codereview.stackexchange.com/q/13338
    // and was designed to be used with the Sizzle selector engine.

    var $el = $(el);
    var overflowX = el.style.overflowX;
    var overflowY = el.style.overflowY;

    //Check both x and y declarations
    if (overflowX === overflowY &&
        (overflowY === 'hidden' || overflowY === 'visible')) {
      return false;
    }

    if (overflowX === 'scroll' || overflowY === 'scroll') {
      return true;
    }

    return ($el.innerHeight() < el.scrollHeight ||
      $el.innerWidth() < el.scrollWidth);
  };

  Utils.escapeMarkup = function (markup) {
    var replaceMap = {
      '\\': '&#92;',
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      '\'': '&#39;',
      '/': '&#47;'
    };

    // Do not try to escape the markup if it's not a string
    if (typeof markup !== 'string') {
      return markup;
    }

    return String(markup).replace(/[&<>"'\/\\]/g, function (match) {
      return replaceMap[match];
    });
  };

  Utils.entityDecode = function (html) {
    var txt = document.createElement('textarea');
    txt.innerHTML = html;
    return txt.value;
  }

  // Append an array of jQuery nodes to a given element.
  Utils.appendMany = function ($element, $nodes) {
    // jQuery 1.7.x does not support $.fn.append() with an array
    // Fall back to a jQuery object collection using $.fn.add()
    if ($.fn.jquery.substr(0, 3) === '1.7') {
      var $jqNodes = $();

      $.map($nodes, function (node) {
        $jqNodes = $jqNodes.add(node);
      });

      $nodes = $jqNodes;
    }

    $element.append($nodes);
  };

  // Determine whether the browser is on a touchscreen device.
  Utils.isTouchscreen = function() {
    if ('undefined' === typeof Utils._isTouchscreenCache) {
      Utils._isTouchscreenCache = 'ontouchstart' in document.documentElement;
    }
    return Utils._isTouchscreenCache;
  }

  return Utils;
});

S2.define('select2/results',[
  'jquery',
  './utils'
], function ($, Utils) {
  function Results ($element, options, dataAdapter) {
    this.$element = $element;
    this.data = dataAdapter;
    this.options = options;

    Results.__super__.constructor.call(this);
  }

  Utils.Extend(Results, Utils.Observable);

  Results.prototype.render = function () {
    var $results = $(
      '<ul class="select2-results__options" role="listbox" tabindex="-1"></ul>'
    );

    if (this.options.get('multiple')) {
      $results.attr('aria-multiselectable', 'true');
    }

    this.$results = $results;

    return $results;
  };

  Results.prototype.clear = function () {
    this.$results.empty();
  };

  Results.prototype.displayMessage = function (params) {
    var escapeMarkup = this.options.get('escapeMarkup');

    this.clear();
    this.hideLoading();

    var $message = $(
      '<li role="alert" aria-live="assertive"' +
      ' class="select2-results__option"></li>'
    );

    var message = this.options.get('translations').get(params.message);

    $message.append(
      escapeMarkup(
        message(params.args)
      )
    );

    $message[0].className += ' select2-results__message';

    this.$results.append($message);
  };

  Results.prototype.hideMessages = function () {
    this.$results.find('.select2-results__message').remove();
  };

  Results.prototype.append = function (data) {
    this.hideLoading();

    var $options = [];

    if (data.results == null || data.results.length === 0) {
      if (this.$results.children().length === 0) {
        this.trigger('results:message', {
          message: 'noResults'
        });
      }

      return;
    }

    data.results = this.sort(data.results);

    for (var d = 0; d < data.results.length; d++) {
      var item = data.results[d];

      var $option = this.option(item);

      $options.push($option);
    }

    this.$results.append($options);
  };

  Results.prototype.position = function ($results, $dropdown) {
    var $resultsContainer = $dropdown.find('.select2-results');
    $resultsContainer.append($results);
  };

  Results.prototype.sort = function (data) {
    var sorter = this.options.get('sorter');

    return sorter(data);
  };

  Results.prototype.highlightFirstItem = function () {
    var $options = this.$results
      .find('.select2-results__option[data-selected]');

    var $selected = $options.filter('[data-selected=true]');

    // Check if there are any selected options
    if ($selected.length > 0) {
      // If there are selected options, highlight the first
      $selected.first().trigger('mouseenter');
    } else {
      // If there are no selected options, highlight the first option
      // in the dropdown
      $options.first().trigger('mouseenter');
    }

    this.ensureHighlightVisible();
  };

  Results.prototype.setClasses = function () {
    var self = this;

    this.data.current(function (selected) {
      var selectedIds = $.map(selected, function (s) {
        return s.id.toString();
      });

      var $options = self.$results
        .find('.select2-results__option[data-selected]');

      $options.each(function () {
        var $option = $(this);

        var item = $.data(this, 'data');

        // id needs to be converted to a string when comparing
        var id = '' + item.id;

        if ((item.element != null && item.element.selected) ||
            (item.element == null && $.inArray(id, selectedIds) > -1)) {
          $option.attr('data-selected', 'true');
        } else {
          $option.attr('data-selected', 'false');
        }
      });

    });
  };

  Results.prototype.showLoading = function (params) {
    this.hideLoading();

    var loadingMore = this.options.get('translations').get('searching');

    var loading = {
      disabled: true,
      loading: true,
      text: loadingMore(params)
    };
    var $loading = this.option(loading);
    $loading.className += ' loading-results';

    this.$results.prepend($loading);
  };

  Results.prototype.hideLoading = function () {
    this.$results.find('.loading-results').remove();
  };

  Results.prototype.option = function (data) {
    var option = document.createElement('li');
    option.className = 'select2-results__option';

    var attrs = {
      'role': 'option',
      'data-selected': 'false',
      'tabindex': -1
    };

    if (data.disabled) {
      delete attrs['data-selected'];
      attrs['aria-disabled'] = 'true';
    }

    if (data.id == null) {
      delete attrs['data-selected'];
    }

    if (data._resultId != null) {
      option.id = data._resultId;
    }

    if (data.title) {
      option.title = data.title;
    }

    if (data.children) {
      attrs['aria-label'] = data.text;
      delete attrs['data-selected'];
    }

    for (var attr in attrs) {
      var val = attrs[attr];

      option.setAttribute(attr, val);
    }

    if (data.children) {
      var $option = $(option);

      var label = document.createElement('strong');
      label.className = 'select2-results__group';

      var $label = $(label);
      this.template(data, label);
      $label.attr('role', 'presentation');

      var $children = [];

      for (var c = 0; c < data.children.length; c++) {
        var child = data.children[c];

        var $child = this.option(child);

        $children.push($child);
      }

      var $childrenContainer = $('<ul></ul>', {
        'class': 'select2-results__options select2-results__options--nested',
        'role': 'listbox'
      });
      $childrenContainer.append($children);
      $option.attr('role', 'list');

      $option.append(label);
      $option.append($childrenContainer);
    } else {
      this.template(data, option);
    }

    $.data(option, 'data', data);

    return option;
  };

  Results.prototype.bind = function (container, $container) {
    var self = this;

    var id = container.id + '-results';

    this.$results.attr('id', id);

    container.on('results:all', function (params) {
      self.clear();
      self.append(params.data);

      if (container.isOpen()) {
        self.setClasses();
        self.highlightFirstItem();
      }
    });

    container.on('results:append', function (params) {
      self.append(params.data);

      if (container.isOpen()) {
        self.setClasses();
      }
    });

    container.on('query', function (params) {
      self.hideMessages();
      self.showLoading(params);
    });

    container.on('select', function () {
      if (!container.isOpen()) {
        return;
      }

      self.setClasses();
      self.highlightFirstItem();
    });

    container.on('unselect', function () {
      if (!container.isOpen()) {
        return;
      }

      self.setClasses();
      self.highlightFirstItem();
    });

    container.on('open', function () {
      // When the dropdown is open, aria-expended="true"
      self.$results.attr('aria-expanded', 'true');
      self.$results.attr('aria-hidden', 'false');

      self.setClasses();
      self.ensureHighlightVisible();
    });

    container.on('close', function () {
      // When the dropdown is closed, aria-expended="false"
      self.$results.attr('aria-expanded', 'false');
      self.$results.attr('aria-hidden', 'true');
      self.$results.removeAttr('aria-activedescendant');
    });

    container.on('results:toggle', function () {
      var $highlighted = self.getHighlightedResults();

      if ($highlighted.length === 0) {
        return;
      }

      $highlighted.trigger('mouseup');
    });

    container.on('results:select', function () {
      var $highlighted = self.getHighlightedResults();

      if ($highlighted.length === 0) {
        return;
      }

      var data = $highlighted.data('data');

      if ($highlighted.attr('data-selected') == 'true') {
        self.trigger('close', {});
      } else {
        self.trigger('select', {
          data: data
        });
      }
    });

    container.on('results:previous', function () {
      var $highlighted = self.getHighlightedResults();

      var $options = self.$results.find('[data-selected]');

      var currentIndex = $options.index($highlighted);

      // If we are already at te top, don't move further
      if (currentIndex === 0) {
        return;
      }

      var nextIndex = currentIndex - 1;

      // If none are highlighted, highlight the first
      if ($highlighted.length === 0) {
        nextIndex = 0;
      }

      var $next = $options.eq(nextIndex);

      $next.trigger('mouseenter');

      var currentOffset = self.$results.offset().top;
      var nextTop = $next.offset().top;
      var nextOffset = self.$results.scrollTop() + (nextTop - currentOffset);

      if (nextIndex === 0) {
        self.$results.scrollTop(0);
      } else if (nextTop - currentOffset < 0) {
        self.$results.scrollTop(nextOffset);
      }
    });

    container.on('results:next', function () {
      var $highlighted = self.getHighlightedResults();

      var $options = self.$results.find('[data-selected]');

      var currentIndex = $options.index($highlighted);

      var nextIndex = currentIndex + 1;

      // If we are at the last option, stay there
      if (nextIndex >= $options.length) {
        return;
      }

      var $next = $options.eq(nextIndex);

      $next.trigger('mouseenter');

      var currentOffset = self.$results.offset().top +
        self.$results.outerHeight(false);
      var nextBottom = $next.offset().top + $next.outerHeight(false);
      var nextOffset = self.$results.scrollTop() + nextBottom - currentOffset;

      if (nextIndex === 0) {
        self.$results.scrollTop(0);
      } else if (nextBottom > currentOffset) {
        self.$results.scrollTop(nextOffset);
      }
    });

    container.on('results:focus', function (params) {
      params.element.addClass('select2-results__option--highlighted').attr('aria-selected', 'true');
      self.$results.attr('aria-activedescendant', params.element.attr('id'));
    });

    container.on('results:message', function (params) {
      self.displayMessage(params);
    });

    if ($.fn.mousewheel) {
      this.$results.on('mousewheel', function (e) {
        var top = self.$results.scrollTop();

        var bottom = self.$results.get(0).scrollHeight - top + e.deltaY;

        var isAtTop = e.deltaY > 0 && top - e.deltaY <= 0;
        var isAtBottom = e.deltaY < 0 && bottom <= self.$results.height();

        if (isAtTop) {
          self.$results.scrollTop(0);

          e.preventDefault();
          e.stopPropagation();
        } else if (isAtBottom) {
          self.$results.scrollTop(
            self.$results.get(0).scrollHeight - self.$results.height()
          );

          e.preventDefault();
          e.stopPropagation();
        }
      });
    }

    this.$results.on('mouseup', '.select2-results__option[data-selected]',
      function (evt) {
      var $this = $(this);

      var data = $this.data('data');

      if ($this.attr('data-selected') === 'true') {
        if (self.options.get('multiple')) {
          self.trigger('unselect', {
            originalEvent: evt,
            data: data
          });
        } else {
          self.trigger('close', {});
        }

        return;
      }

      self.trigger('select', {
        originalEvent: evt,
        data: data
      });
    });

    this.$results.on('mouseenter', '.select2-results__option[data-selected]',
      function (evt) {
      var data = $(this).data('data');

      self.getHighlightedResults()
          .removeClass('select2-results__option--highlighted')
          .attr('aria-selected', 'false');

      self.trigger('results:focus', {
        data: data,
        element: $(this)
      });
    });
  };

  Results.prototype.getHighlightedResults = function () {
    var $highlighted = this.$results
    .find('.select2-results__option--highlighted');

    return $highlighted;
  };

  Results.prototype.destroy = function () {
    this.$results.remove();
  };

  Results.prototype.ensureHighlightVisible = function () {
    var $highlighted = this.getHighlightedResults();

    if ($highlighted.length === 0) {
      return;
    }

    var $options = this.$results.find('[data-selected]');

    var currentIndex = $options.index($highlighted);

    var currentOffset = this.$results.offset().top;
    var nextTop = $highlighted.offset().top;
    var nextOffset = this.$results.scrollTop() + (nextTop - currentOffset);

    var offsetDelta = nextTop - currentOffset;
    nextOffset -= $highlighted.outerHeight(false) * 2;

    if (currentIndex <= 2) {
      this.$results.scrollTop(0);
    } else if (offsetDelta > this.$results.outerHeight() || offsetDelta < 0) {
      this.$results.scrollTop(nextOffset);
    }
  };

  Results.prototype.template = function (result, container) {
    var template = this.options.get('templateResult');
    var escapeMarkup = this.options.get('escapeMarkup');

    var content = template(result, container);

    if (content == null) {
      container.style.display = 'none';
    } else if (typeof content === 'string') {
      container.innerHTML = escapeMarkup(content);
    } else {
      $(container).append(content);
    }
  };

  return Results;
});

S2.define('select2/keys',[

], function () {
  var KEYS = {
    BACKSPACE: 8,
    TAB: 9,
    ENTER: 13,
    SHIFT: 16,
    CTRL: 17,
    ALT: 18,
    ESC: 27,
    SPACE: 32,
    PAGE_UP: 33,
    PAGE_DOWN: 34,
    END: 35,
    HOME: 36,
    LEFT: 37,
    UP: 38,
    RIGHT: 39,
    DOWN: 40,
    DELETE: 46
  };

  return KEYS;
});

S2.define('select2/selection/base',[
  'jquery',
  '../utils',
  '../keys'
], function ($, Utils, KEYS) {
  function BaseSelection ($element, options) {
    this.$element = $element;
    this.options = options;

    BaseSelection.__super__.constructor.call(this);
  }

  Utils.Extend(BaseSelection, Utils.Observable);

  BaseSelection.prototype.render = function () {
    var $selection = $(
      '<span class="select2-selection" ' +
      ' aria-haspopup="true" aria-expanded="false">' +
      '</span>'
    );

    this._tabindex = 0;

    if (this.$element.data('old-tabindex') != null) {
      this._tabindex = this.$element.data('old-tabindex');
    } else if (this.$element.attr('tabindex') != null) {
      this._tabindex = this.$element.attr('tabindex');
    }

    $selection.attr('title', this.$element.attr('title'));
    $selection.attr('tabindex', this._tabindex);

    this.$selection = $selection;

    return $selection;
  };

  BaseSelection.prototype.bind = function (container, $container) {
    var self = this;

    var id = container.id + '-container';
    var resultsId = container.id + '-results';
    var searchHidden = this.options.get('minimumResultsForSearch') === Infinity;

    this.container = container;

    this.$selection.on('focus', function (evt) {
      self.trigger('focus', evt);
    });

    this.$selection.on('blur', function (evt) {
      self._handleBlur(evt);
    });

    this.$selection.on('keydown', function (evt) {
      self.trigger('keypress', evt);

      if (evt.which === KEYS.SPACE) {
        evt.preventDefault();
      }
    });

    container.on('results:focus', function (params) {
      self.$selection.attr('aria-activedescendant', params.data._resultId);
    });

    container.on('selection:update', function (params) {
      self.update(params.data);
    });

    container.on('open', function () {
      // When the dropdown is open, aria-expanded="true"
      self.$selection.attr('aria-expanded', 'true');
      self.$selection.attr('aria-owns', resultsId);

      self._attachCloseHandler(container);
    });

    container.on('close', function () {
      // When the dropdown is closed, aria-expanded="false"
      self.$selection.attr('aria-expanded', 'false');
      self.$selection.removeAttr('aria-activedescendant');
      self.$selection.removeAttr('aria-owns');

      // This needs to be delayed as the active element is the body when the
      // key is pressed.
      window.setTimeout(function () {
        self.$selection.focus();
      }, 1);

      self._detachCloseHandler(container);
    });

    container.on('enable', function () {
      self.$selection.attr('tabindex', self._tabindex);
    });

    container.on('disable', function () {
      self.$selection.attr('tabindex', '-1');
    });
  };

  BaseSelection.prototype._handleBlur = function (evt) {
    var self = this;

    // This needs to be delayed as the active element is the body when the tab
    // key is pressed, possibly along with others.
    window.setTimeout(function () {
      // Don't trigger `blur` if the focus is still in the selection
      if (
        (document.activeElement == self.$selection[0]) ||
        ($.contains(self.$selection[0], document.activeElement))
      ) {
        return;
      }

      self.trigger('blur', evt);
    }, 1);
  };

  BaseSelection.prototype._attachCloseHandler = function (container) {
    var self = this;

    $(document.body).on('mousedown.select2.' + container.id, function (e) {
      var $target = $(e.target);

      var $select = $target.closest('.select2');

      var $all = $('.select2.select2-container--open');

      $all.each(function () {
        var $this = $(this);

        if (this == $select[0]) {
          return;
        }

        var $element = $this.data('element');
        $element.select2('close');

        // Remove any focus when dropdown is closed by clicking outside the select area.
        // Timeout of 1 required for close to finish wrapping up.
        setTimeout(function(){
         $this.find('*:focus').blur();
         $target.focus();
        }, 1);
      });
    });
  };

  BaseSelection.prototype._detachCloseHandler = function (container) {
    $(document.body).off('mousedown.select2.' + container.id);
  };

  BaseSelection.prototype.position = function ($selection, $container) {
    var $selectionContainer = $container.find('.selection');
    $selectionContainer.append($selection);
  };

  BaseSelection.prototype.destroy = function () {
    this._detachCloseHandler(this.container);
  };

  BaseSelection.prototype.update = function (data) {
    throw new Error('The `update` method must be defined in child classes.');
  };

  return BaseSelection;
});

S2.define('select2/selection/single',[
  'jquery',
  './base',
  '../utils',
  '../keys'
], function ($, BaseSelection, Utils, KEYS) {
  function SingleSelection () {
    SingleSelection.__super__.constructor.apply(this, arguments);
  }

  Utils.Extend(SingleSelection, BaseSelection);

  SingleSelection.prototype.render = function () {
    var $selection = SingleSelection.__super__.render.call(this);

    $selection.addClass('select2-selection--single');

    $selection.html(
      '<span class="select2-selection__rendered"></span>' +
      '<span class="select2-selection__arrow" role="presentation">' +
        '<b role="presentation"></b>' +
      '</span>'
    );

    return $selection;
  };

  SingleSelection.prototype.bind = function (container, $container) {
    var self = this;

    SingleSelection.__super__.bind.apply(this, arguments);

    var id = container.id + '-container';

    this.$selection.find('.select2-selection__rendered')
      .attr('id', id)
      .attr('role', 'textbox')
      .attr('aria-readonly', 'true');

    var label = this.options.get( 'label' );

    if ( typeof( label ) === 'string' ) {
      this.$selection.attr( 'aria-label', label );
    } else {
      this.$selection.attr( 'aria-labelledby', id );
    }

    // This makes single non-search selects work in screen readers. If it causes problems elsewhere, remove.
    this.$selection.attr('role', 'combobox');

    this.$selection.on('mousedown', function (evt) {
      // Only respond to left clicks
      if (evt.which !== 1) {
        return;
      }

      self.trigger('toggle', {
        originalEvent: evt
      });
    });

    this.$selection.on('focus', function (evt) {
      // User focuses on the container
    });

    this.$selection.on('keydown', function (evt) {
      // If user starts typing an alphanumeric key on the keyboard, open if not opened.
      if (!container.isOpen() && evt.which >= 48 && evt.which <= 90) {
        container.open();
      }
    });

    this.$selection.on('blur', function (evt) {
      // User exits the container
    });

    container.on('focus', function (evt) {
      if (!container.isOpen()) {
        self.$selection.focus();
      }
    });

    container.on('selection:update', function (params) {
      self.update(params.data);
    });
  };

  SingleSelection.prototype.clear = function () {
    this.$selection.find('.select2-selection__rendered').empty();
  };

  SingleSelection.prototype.display = function (data, container) {
    var template = this.options.get('templateSelection');
    var escapeMarkup = this.options.get('escapeMarkup');

    return escapeMarkup(template(data, container));
  };

  SingleSelection.prototype.selectionContainer = function () {
    return $('<span></span>');
  };

  SingleSelection.prototype.update = function (data) {
    if (data.length === 0) {
      this.clear();
      return;
    }

    var selection = data[0];

    var $rendered = this.$selection.find('.select2-selection__rendered');
    var formatted = Utils.entityDecode(this.display(selection, $rendered));

    $rendered.empty().text(formatted);
    $rendered.prop('title', selection.title || selection.text);
  };

  return SingleSelection;
});

S2.define('select2/selection/multiple',[
  'jquery',
  './base',
  '../utils'
], function ($, BaseSelection, Utils) {
  function MultipleSelection ($element, options) {
    MultipleSelection.__super__.constructor.apply(this, arguments);
  }

  Utils.Extend(MultipleSelection, BaseSelection);

  MultipleSelection.prototype.render = function () {
    var $selection = MultipleSelection.__super__.render.call(this);

    $selection.addClass('select2-selection--multiple');

    $selection.html(
      '<ul class="select2-selection__rendered" aria-live="polite" aria-relevant="additions removals" aria-atomic="true"></ul>'
    );

    return $selection;
  };

  MultipleSelection.prototype.bind = function (container, $container) {
    var self = this;

    MultipleSelection.__super__.bind.apply(this, arguments);

    this.$selection.on('click', function (evt) {
      self.trigger('toggle', {
        originalEvent: evt
      });
    });

    this.$selection.on(
      'click',
      '.select2-selection__choice__remove',
      function (evt) {
        // Ignore the event if it is disabled
        if (self.options.get('disabled')) {
          return;
        }

        var $remove = $(this);
        var $selection = $remove.parent();

        var data = $selection.data('data');

        self.trigger('unselect', {
          originalEvent: evt,
          data: data
        });
      }
    );

    this.$selection.on('keydown', function (evt) {
      // If user starts typing an alphanumeric key on the keyboard, open if not opened.
      if (!container.isOpen() && evt.which >= 48 && evt.which <= 90) {
        container.open();
      }
    });

    // Focus on the search field when the container is focused instead of the main container.
    container.on( 'focus', function(){
      self.focusOnSearch();
    });
  };

  MultipleSelection.prototype.clear = function () {
    this.$selection.find('.select2-selection__rendered').empty();
  };

  MultipleSelection.prototype.display = function (data, container) {
    var template = this.options.get('templateSelection');
    var escapeMarkup = this.options.get('escapeMarkup');

    return escapeMarkup(template(data, container));
  };

  MultipleSelection.prototype.selectionContainer = function () {
    var $container = $(
      '<li class="select2-selection__choice">' +
        '<span class="select2-selection__choice__remove" role="presentation" aria-hidden="true">' +
          '&times;' +
        '</span>' +
      '</li>'
    );

    return $container;
  };

  /**
   * Focus on the search field instead of the main multiselect container.
   */
  MultipleSelection.prototype.focusOnSearch = function() {
    var self = this;

    if ('undefined' !== typeof self.$search) {
      // Needs 1 ms delay because of other 1 ms setTimeouts when rendering.
      setTimeout(function(){
        // Prevent the dropdown opening again when focused from this.
        // This gets reset automatically when focus is triggered.
        self._keyUpPrevented = true;

        self.$search.focus();
      }, 1);
    }
  }

  MultipleSelection.prototype.update = function (data) {
    this.clear();

    if (data.length === 0) {
      return;
    }

    var $selections = [];

    for (var d = 0; d < data.length; d++) {
      var selection = data[d];

      var $selection = this.selectionContainer();
      var removeItemTag = $selection.html();
      var formatted = this.display(selection, $selection);
      if ('string' === typeof formatted) {
        formatted = Utils.entityDecode(formatted.trim());
      }

      $selection.text(formatted);
      $selection.prepend(removeItemTag);
      $selection.prop('title', selection.title || selection.text);

      $selection.data('data', selection);

      $selections.push($selection);
    }

    var $rendered = this.$selection.find('.select2-selection__rendered');

    Utils.appendMany($rendered, $selections);
  };

  return MultipleSelection;
});

S2.define('select2/selection/placeholder',[
  '../utils'
], function (Utils) {
  function Placeholder (decorated, $element, options) {
    this.placeholder = this.normalizePlaceholder(options.get('placeholder'));

    decorated.call(this, $element, options);
  }

  Placeholder.prototype.normalizePlaceholder = function (_, placeholder) {
    if (typeof placeholder === 'string') {
      placeholder = {
        id: '',
        text: placeholder
      };
    }

    return placeholder;
  };

  Placeholder.prototype.createPlaceholder = function (decorated, placeholder) {
    var $placeholder = this.selectionContainer();

    $placeholder.text(Utils.entityDecode(this.display(placeholder)));
    $placeholder.addClass('select2-selection__placeholder')
                .removeClass('select2-selection__choice');

    return $placeholder;
  };

  Placeholder.prototype.update = function (decorated, data) {
    var singlePlaceholder = (
      data.length == 1 && data[0].id != this.placeholder.id
    );
    var multipleSelections = data.length > 1;

    if (multipleSelections || singlePlaceholder) {
      return decorated.call(this, data);
    }

    this.clear();

    var $placeholder = this.createPlaceholder(this.placeholder);

    this.$selection.find('.select2-selection__rendered').append($placeholder);
  };

  return Placeholder;
});

S2.define('select2/selection/allowClear',[
  'jquery',
  '../keys'
], function ($, KEYS) {
  function AllowClear () { }

  AllowClear.prototype.bind = function (decorated, container, $container) {
    var self = this;

    decorated.call(this, container, $container);

    if (this.placeholder == null) {
      if (this.options.get('debug') && window.console && console.error) {
        console.error(
          'Select2: The `allowClear` option should be used in combination ' +
          'with the `placeholder` option.'
        );
      }
    }

    this.$selection.on('mousedown', '.select2-selection__clear',
      function (evt) {
        self._handleClear(evt);
    });

    container.on('keypress', function (evt) {
      self._handleKeyboardClear(evt, container);
    });
  };

  AllowClear.prototype._handleClear = function (_, evt) {
    // Ignore the event if it is disabled
    if (this.options.get('disabled')) {
      return;
    }

    var $clear = this.$selection.find('.select2-selection__clear');

    // Ignore the event if nothing has been selected
    if ($clear.length === 0) {
      return;
    }

    evt.stopPropagation();

    var data = $clear.data('data');

    for (var d = 0; d < data.length; d++) {
      var unselectData = {
        data: data[d]
      };

      // Trigger the `unselect` event, so people can prevent it from being
      // cleared.
      this.trigger('unselect', unselectData);

      // If the event was prevented, don't clear it out.
      if (unselectData.prevented) {
        return;
      }
    }

    this.$element.val(this.placeholder.id).trigger('change');

    this.trigger('toggle', {});
  };

  AllowClear.prototype._handleKeyboardClear = function (_, evt, container) {
    if (container.isOpen()) {
      return;
    }

    if (evt.which == KEYS.DELETE || evt.which == KEYS.BACKSPACE) {
      this._handleClear(evt);
    }
  };

  AllowClear.prototype.update = function (decorated, data) {
    decorated.call(this, data);

    if (this.$selection.find('.select2-selection__placeholder').length > 0 ||
        data.length === 0) {
      return;
    }

    var $remove = $(
      '<span class="select2-selection__clear">' +
        '&times;' +
      '</span>'
    );
    $remove.data('data', data);

    this.$selection.find('.select2-selection__rendered').prepend($remove);
  };

  return AllowClear;
});

S2.define('select2/selection/search',[
  'jquery',
  '../utils',
  '../keys'
], function ($, Utils, KEYS) {
  function Search (decorated, $element, options) {
    decorated.call(this, $element, options);
  }

  Search.prototype.render = function (decorated) {
    var $search = $(
      '<li class="select2-search select2-search--inline">' +
        '<input class="select2-search__field" type="text" tabindex="-1"' +
        ' autocomplete="off" autocorrect="off" autocapitalize="none"' +
        ' spellcheck="false" role="textbox" aria-autocomplete="list" />' +
      '</li>'
    );

    this.$searchContainer = $search;
    this.$search = $search.find('input');

    var $rendered = decorated.call(this);

    this._transferTabIndex();

    return $rendered;
  };

  Search.prototype.bind = function (decorated, container, $container) {
    var self = this;
    var resultsId = container.id + '-results';

    decorated.call(this, container, $container);

    container.on('open', function () {
      self.$search.attr('aria-owns', resultsId);
      self.$search.trigger('focus');
    });

    container.on('close', function () {
      self.$search.val('');
      self.$search.removeAttr('aria-activedescendant');
      self.$search.removeAttr('aria-owns');
      self.$search.trigger('focus');
    });

    container.on('enable', function () {
      self.$search.prop('disabled', false);

      self._transferTabIndex();
    });

    container.on('disable', function () {
      self.$search.prop('disabled', true);
    });

    container.on('focus', function (evt) {
      self.$search.trigger('focus');
    });

    container.on('results:focus', function (params) {
      self.$search.attr('aria-activedescendant', params.data._resultId);
    });

    this.$selection.on('focusin', '.select2-search--inline', function (evt) {
      self.trigger('focus', evt);
    });

    this.$selection.on('focusout', '.select2-search--inline', function (evt) {
      self._handleBlur(evt);
    });

    this.$selection.on('keydown', '.select2-search--inline', function (evt) {
      evt.stopPropagation();

      self.trigger('keypress', evt);

      self._keyUpPrevented = evt.isDefaultPrevented();

      var key = evt.which;

      if (key === KEYS.BACKSPACE && self.$search.val() === '') {
        var $previousChoice = self.$searchContainer
          .prev('.select2-selection__choice');

        if ($previousChoice.length > 0) {
          var item = $previousChoice.data('data');

          self.searchRemoveChoice(item);

          evt.preventDefault();
        }
      } else if (evt.which === KEYS.ENTER) {
        container.open();
        evt.preventDefault();
      }
    });

    // Try to detect the IE version should the `documentMode` property that
    // is stored on the document. This is only implemented in IE and is
    // slightly cleaner than doing a user agent check.
    // This property is not available in Edge, but Edge also doesn't have
    // this bug.
    var msie = document.documentMode;
    var disableInputEvents = msie && msie <= 11;

    // Workaround for browsers which do not support the `input` event
    // This will prevent double-triggering of events for browsers which support
    // both the `keyup` and `input` events.
    this.$selection.on(
      'input.searchcheck',
      '.select2-search--inline',
      function (evt) {
        // IE will trigger the `input` event when a placeholder is used on a
        // search box. To get around this issue, we are forced to ignore all
        // `input` events in IE and keep using `keyup`.
        if (disableInputEvents) {
          self.$selection.off('input.search input.searchcheck');
          return;
        }

        // Unbind the duplicated `keyup` event
        self.$selection.off('keyup.search');
      }
    );

    this.$selection.on(
      'keyup.search input.search',
      '.select2-search--inline',
      function (evt) {
        // IE will trigger the `input` event when a placeholder is used on a
        // search box. To get around this issue, we are forced to ignore all
        // `input` events in IE and keep using `keyup`.
        if (disableInputEvents && evt.type === 'input') {
          self.$selection.off('input.search input.searchcheck');
          return;
        }

        var key = evt.which;

        // We can freely ignore events from modifier keys
        if (key == KEYS.SHIFT || key == KEYS.CTRL || key == KEYS.ALT) {
          return;
        }

        // Tabbing will be handled during the `keydown` phase
        if (key == KEYS.TAB) {
          return;
        }

        self.handleSearch(evt);
      }
    );
  };

  /**
   * This method will transfer the tabindex attribute from the rendered
   * selection to the search box. This allows for the search box to be used as
   * the primary focus instead of the selection container.
   *
   * @private
   */
  Search.prototype._transferTabIndex = function (decorated) {
    this.$search.attr('tabindex', this.$selection.attr('tabindex'));
    this.$selection.attr('tabindex', '-1');
  };

  Search.prototype.createPlaceholder = function (decorated, placeholder) {
    this.$search.attr('placeholder', placeholder.text);
  };

  Search.prototype.update = function (decorated, data) {
    var searchHadFocus = this.$search[0] == document.activeElement;

    this.$search.attr('placeholder', '');

    decorated.call(this, data);

    this.$selection.find('.select2-selection__rendered')
                   .append(this.$searchContainer);

    this.resizeSearch();
    if (searchHadFocus) {
      this.$search.focus();
    }
  };

  Search.prototype.handleSearch = function () {
    this.resizeSearch();

    if (!this._keyUpPrevented) {
      var input = this.$search.val();

      this.trigger('query', {
        term: input
      });
    }

    this._keyUpPrevented = false;
  };

  Search.prototype.searchRemoveChoice = function (decorated, item) {
    this.trigger('unselect', {
      data: item
    });

    this.$search.val(item.text);
    this.handleSearch();
  };

  Search.prototype.resizeSearch = function () {
    this.$search.css('width', '25px');

    var width = '';

    if (this.$search.attr('placeholder') !== '') {
      width = this.$selection.find('.select2-selection__rendered').innerWidth();
    } else {
      var minimumWidth = this.$search.val().length + 1;

      width = (minimumWidth * 0.75) + 'em';
    }

    this.$search.css('width', width);
  };

  return Search;
});

S2.define('select2/selection/eventRelay',[
  'jquery'
], function ($) {
  function EventRelay () { }

  EventRelay.prototype.bind = function (decorated, container, $container) {
    var self = this;
    var relayEvents = [
      'open', 'opening',
      'close', 'closing',
      'select', 'selecting',
      'unselect', 'unselecting'
    ];

    var preventableEvents = ['opening', 'closing', 'selecting', 'unselecting'];

    decorated.call(this, container, $container);

    container.on('*', function (name, params) {
      // Ignore events that should not be relayed
      if ($.inArray(name, relayEvents) === -1) {
        return;
      }

      // The parameters should always be an object
      params = params || {};

      // Generate the jQuery event for the Select2 event
      var evt = $.Event('select2:' + name, {
        params: params
      });

      self.$element.trigger(evt);

      // Only handle preventable events if it was one
      if ($.inArray(name, preventableEvents) === -1) {
        return;
      }

      params.prevented = evt.isDefaultPrevented();
    });
  };

  return EventRelay;
});

S2.define('select2/translation',[
  'jquery',
  'require'
], function ($, require) {
  function Translation (dict) {
    this.dict = dict || {};
  }

  Translation.prototype.all = function () {
    return this.dict;
  };

  Translation.prototype.get = function (key) {
    return this.dict[key];
  };

  Translation.prototype.extend = function (translation) {
    this.dict = $.extend({}, translation.all(), this.dict);
  };

  // Static functions

  Translation._cache = {};

  Translation.loadPath = function (path) {
    if (!(path in Translation._cache)) {
      var translations = require(path);

      Translation._cache[path] = translations;
    }

    return new Translation(Translation._cache[path]);
  };

  return Translation;
});

S2.define('select2/diacritics',[

], function () {
  var diacritics = {
    '\u24B6': 'A',
    '\uFF21': 'A',
    '\u00C0': 'A',
    '\u00C1': 'A',
    '\u00C2': 'A',
    '\u1EA6': 'A',
    '\u1EA4': 'A',
    '\u1EAA': 'A',
    '\u1EA8': 'A',
    '\u00C3': 'A',
    '\u0100': 'A',
    '\u0102': 'A',
    '\u1EB0': 'A',
    '\u1EAE': 'A',
    '\u1EB4': 'A',
    '\u1EB2': 'A',
    '\u0226': 'A',
    '\u01E0': 'A',
    '\u00C4': 'A',
    '\u01DE': 'A',
    '\u1EA2': 'A',
    '\u00C5': 'A',
    '\u01FA': 'A',
    '\u01CD': 'A',
    '\u0200': 'A',
    '\u0202': 'A',
    '\u1EA0': 'A',
    '\u1EAC': 'A',
    '\u1EB6': 'A',
    '\u1E00': 'A',
    '\u0104': 'A',
    '\u023A': 'A',
    '\u2C6F': 'A',
    '\uA732': 'AA',
    '\u00C6': 'AE',
    '\u01FC': 'AE',
    '\u01E2': 'AE',
    '\uA734': 'AO',
    '\uA736': 'AU',
    '\uA738': 'AV',
    '\uA73A': 'AV',
    '\uA73C': 'AY',
    '\u24B7': 'B',
    '\uFF22': 'B',
    '\u1E02': 'B',
    '\u1E04': 'B',
    '\u1E06': 'B',
    '\u0243': 'B',
    '\u0182': 'B',
    '\u0181': 'B',
    '\u24B8': 'C',
    '\uFF23': 'C',
    '\u0106': 'C',
    '\u0108': 'C',
    '\u010A': 'C',
    '\u010C': 'C',
    '\u00C7': 'C',
    '\u1E08': 'C',
    '\u0187': 'C',
    '\u023B': 'C',
    '\uA73E': 'C',
    '\u24B9': 'D',
    '\uFF24': 'D',
    '\u1E0A': 'D',
    '\u010E': 'D',
    '\u1E0C': 'D',
    '\u1E10': 'D',
    '\u1E12': 'D',
    '\u1E0E': 'D',
    '\u0110': 'D',
    '\u018B': 'D',
    '\u018A': 'D',
    '\u0189': 'D',
    '\uA779': 'D',
    '\u01F1': 'DZ',
    '\u01C4': 'DZ',
    '\u01F2': 'Dz',
    '\u01C5': 'Dz',
    '\u24BA': 'E',
    '\uFF25': 'E',
    '\u00C8': 'E',
    '\u00C9': 'E',
    '\u00CA': 'E',
    '\u1EC0': 'E',
    '\u1EBE': 'E',
    '\u1EC4': 'E',
    '\u1EC2': 'E',
    '\u1EBC': 'E',
    '\u0112': 'E',
    '\u1E14': 'E',
    '\u1E16': 'E',
    '\u0114': 'E',
    '\u0116': 'E',
    '\u00CB': 'E',
    '\u1EBA': 'E',
    '\u011A': 'E',
    '\u0204': 'E',
    '\u0206': 'E',
    '\u1EB8': 'E',
    '\u1EC6': 'E',
    '\u0228': 'E',
    '\u1E1C': 'E',
    '\u0118': 'E',
    '\u1E18': 'E',
    '\u1E1A': 'E',
    '\u0190': 'E',
    '\u018E': 'E',
    '\u24BB': 'F',
    '\uFF26': 'F',
    '\u1E1E': 'F',
    '\u0191': 'F',
    '\uA77B': 'F',
    '\u24BC': 'G',
    '\uFF27': 'G',
    '\u01F4': 'G',
    '\u011C': 'G',
    '\u1E20': 'G',
    '\u011E': 'G',
    '\u0120': 'G',
    '\u01E6': 'G',
    '\u0122': 'G',
    '\u01E4': 'G',
    '\u0193': 'G',
    '\uA7A0': 'G',
    '\uA77D': 'G',
    '\uA77E': 'G',
    '\u24BD': 'H',
    '\uFF28': 'H',
    '\u0124': 'H',
    '\u1E22': 'H',
    '\u1E26': 'H',
    '\u021E': 'H',
    '\u1E24': 'H',
    '\u1E28': 'H',
    '\u1E2A': 'H',
    '\u0126': 'H',
    '\u2C67': 'H',
    '\u2C75': 'H',
    '\uA78D': 'H',
    '\u24BE': 'I',
    '\uFF29': 'I',
    '\u00CC': 'I',
    '\u00CD': 'I',
    '\u00CE': 'I',
    '\u0128': 'I',
    '\u012A': 'I',
    '\u012C': 'I',
    '\u0130': 'I',
    '\u00CF': 'I',
    '\u1E2E': 'I',
    '\u1EC8': 'I',
    '\u01CF': 'I',
    '\u0208': 'I',
    '\u020A': 'I',
    '\u1ECA': 'I',
    '\u012E': 'I',
    '\u1E2C': 'I',
    '\u0197': 'I',
    '\u24BF': 'J',
    '\uFF2A': 'J',
    '\u0134': 'J',
    '\u0248': 'J',
    '\u24C0': 'K',
    '\uFF2B': 'K',
    '\u1E30': 'K',
    '\u01E8': 'K',
    '\u1E32': 'K',
    '\u0136': 'K',
    '\u1E34': 'K',
    '\u0198': 'K',
    '\u2C69': 'K',
    '\uA740': 'K',
    '\uA742': 'K',
    '\uA744': 'K',
    '\uA7A2': 'K',
    '\u24C1': 'L',
    '\uFF2C': 'L',
    '\u013F': 'L',
    '\u0139': 'L',
    '\u013D': 'L',
    '\u1E36': 'L',
    '\u1E38': 'L',
    '\u013B': 'L',
    '\u1E3C': 'L',
    '\u1E3A': 'L',
    '\u0141': 'L',
    '\u023D': 'L',
    '\u2C62': 'L',
    '\u2C60': 'L',
    '\uA748': 'L',
    '\uA746': 'L',
    '\uA780': 'L',
    '\u01C7': 'LJ',
    '\u01C8': 'Lj',
    '\u24C2': 'M',
    '\uFF2D': 'M',
    '\u1E3E': 'M',
    '\u1E40': 'M',
    '\u1E42': 'M',
    '\u2C6E': 'M',
    '\u019C': 'M',
    '\u24C3': 'N',
    '\uFF2E': 'N',
    '\u01F8': 'N',
    '\u0143': 'N',
    '\u00D1': 'N',
    '\u1E44': 'N',
    '\u0147': 'N',
    '\u1E46': 'N',
    '\u0145': 'N',
    '\u1E4A': 'N',
    '\u1E48': 'N',
    '\u0220': 'N',
    '\u019D': 'N',
    '\uA790': 'N',
    '\uA7A4': 'N',
    '\u01CA': 'NJ',
    '\u01CB': 'Nj',
    '\u24C4': 'O',
    '\uFF2F': 'O',
    '\u00D2': 'O',
    '\u00D3': 'O',
    '\u00D4': 'O',
    '\u1ED2': 'O',
    '\u1ED0': 'O',
    '\u1ED6': 'O',
    '\u1ED4': 'O',
    '\u00D5': 'O',
    '\u1E4C': 'O',
    '\u022C': 'O',
    '\u1E4E': 'O',
    '\u014C': 'O',
    '\u1E50': 'O',
    '\u1E52': 'O',
    '\u014E': 'O',
    '\u022E': 'O',
    '\u0230': 'O',
    '\u00D6': 'O',
    '\u022A': 'O',
    '\u1ECE': 'O',
    '\u0150': 'O',
    '\u01D1': 'O',
    '\u020C': 'O',
    '\u020E': 'O',
    '\u01A0': 'O',
    '\u1EDC': 'O',
    '\u1EDA': 'O',
    '\u1EE0': 'O',
    '\u1EDE': 'O',
    '\u1EE2': 'O',
    '\u1ECC': 'O',
    '\u1ED8': 'O',
    '\u01EA': 'O',
    '\u01EC': 'O',
    '\u00D8': 'O',
    '\u01FE': 'O',
    '\u0186': 'O',
    '\u019F': 'O',
    '\uA74A': 'O',
    '\uA74C': 'O',
    '\u01A2': 'OI',
    '\uA74E': 'OO',
    '\u0222': 'OU',
    '\u24C5': 'P',
    '\uFF30': 'P',
    '\u1E54': 'P',
    '\u1E56': 'P',
    '\u01A4': 'P',
    '\u2C63': 'P',
    '\uA750': 'P',
    '\uA752': 'P',
    '\uA754': 'P',
    '\u24C6': 'Q',
    '\uFF31': 'Q',
    '\uA756': 'Q',
    '\uA758': 'Q',
    '\u024A': 'Q',
    '\u24C7': 'R',
    '\uFF32': 'R',
    '\u0154': 'R',
    '\u1E58': 'R',
    '\u0158': 'R',
    '\u0210': 'R',
    '\u0212': 'R',
    '\u1E5A': 'R',
    '\u1E5C': 'R',
    '\u0156': 'R',
    '\u1E5E': 'R',
    '\u024C': 'R',
    '\u2C64': 'R',
    '\uA75A': 'R',
    '\uA7A6': 'R',
    '\uA782': 'R',
    '\u24C8': 'S',
    '\uFF33': 'S',
    '\u1E9E': 'S',
    '\u015A': 'S',
    '\u1E64': 'S',
    '\u015C': 'S',
    '\u1E60': 'S',
    '\u0160': 'S',
    '\u1E66': 'S',
    '\u1E62': 'S',
    '\u1E68': 'S',
    '\u0218': 'S',
    '\u015E': 'S',
    '\u2C7E': 'S',
    '\uA7A8': 'S',
    '\uA784': 'S',
    '\u24C9': 'T',
    '\uFF34': 'T',
    '\u1E6A': 'T',
    '\u0164': 'T',
    '\u1E6C': 'T',
    '\u021A': 'T',
    '\u0162': 'T',
    '\u1E70': 'T',
    '\u1E6E': 'T',
    '\u0166': 'T',
    '\u01AC': 'T',
    '\u01AE': 'T',
    '\u023E': 'T',
    '\uA786': 'T',
    '\uA728': 'TZ',
    '\u24CA': 'U',
    '\uFF35': 'U',
    '\u00D9': 'U',
    '\u00DA': 'U',
    '\u00DB': 'U',
    '\u0168': 'U',
    '\u1E78': 'U',
    '\u016A': 'U',
    '\u1E7A': 'U',
    '\u016C': 'U',
    '\u00DC': 'U',
    '\u01DB': 'U',
    '\u01D7': 'U',
    '\u01D5': 'U',
    '\u01D9': 'U',
    '\u1EE6': 'U',
    '\u016E': 'U',
    '\u0170': 'U',
    '\u01D3': 'U',
    '\u0214': 'U',
    '\u0216': 'U',
    '\u01AF': 'U',
    '\u1EEA': 'U',
    '\u1EE8': 'U',
    '\u1EEE': 'U',
    '\u1EEC': 'U',
    '\u1EF0': 'U',
    '\u1EE4': 'U',
    '\u1E72': 'U',
    '\u0172': 'U',
    '\u1E76': 'U',
    '\u1E74': 'U',
    '\u0244': 'U',
    '\u24CB': 'V',
    '\uFF36': 'V',
    '\u1E7C': 'V',
    '\u1E7E': 'V',
    '\u01B2': 'V',
    '\uA75E': 'V',
    '\u0245': 'V',
    '\uA760': 'VY',
    '\u24CC': 'W',
    '\uFF37': 'W',
    '\u1E80': 'W',
    '\u1E82': 'W',
    '\u0174': 'W',
    '\u1E86': 'W',
    '\u1E84': 'W',
    '\u1E88': 'W',
    '\u2C72': 'W',
    '\u24CD': 'X',
    '\uFF38': 'X',
    '\u1E8A': 'X',
    '\u1E8C': 'X',
    '\u24CE': 'Y',
    '\uFF39': 'Y',
    '\u1EF2': 'Y',
    '\u00DD': 'Y',
    '\u0176': 'Y',
    '\u1EF8': 'Y',
    '\u0232': 'Y',
    '\u1E8E': 'Y',
    '\u0178': 'Y',
    '\u1EF6': 'Y',
    '\u1EF4': 'Y',
    '\u01B3': 'Y',
    '\u024E': 'Y',
    '\u1EFE': 'Y',
    '\u24CF': 'Z',
    '\uFF3A': 'Z',
    '\u0179': 'Z',
    '\u1E90': 'Z',
    '\u017B': 'Z',
    '\u017D': 'Z',
    '\u1E92': 'Z',
    '\u1E94': 'Z',
    '\u01B5': 'Z',
    '\u0224': 'Z',
    '\u2C7F': 'Z',
    '\u2C6B': 'Z',
    '\uA762': 'Z',
    '\u24D0': 'a',
    '\uFF41': 'a',
    '\u1E9A': 'a',
    '\u00E0': 'a',
    '\u00E1': 'a',
    '\u00E2': 'a',
    '\u1EA7': 'a',
    '\u1EA5': 'a',
    '\u1EAB': 'a',
    '\u1EA9': 'a',
    '\u00E3': 'a',
    '\u0101': 'a',
    '\u0103': 'a',
    '\u1EB1': 'a',
    '\u1EAF': 'a',
    '\u1EB5': 'a',
    '\u1EB3': 'a',
    '\u0227': 'a',
    '\u01E1': 'a',
    '\u00E4': 'a',
    '\u01DF': 'a',
    '\u1EA3': 'a',
    '\u00E5': 'a',
    '\u01FB': 'a',
    '\u01CE': 'a',
    '\u0201': 'a',
    '\u0203': 'a',
    '\u1EA1': 'a',
    '\u1EAD': 'a',
    '\u1EB7': 'a',
    '\u1E01': 'a',
    '\u0105': 'a',
    '\u2C65': 'a',
    '\u0250': 'a',
    '\uA733': 'aa',
    '\u00E6': 'ae',
    '\u01FD': 'ae',
    '\u01E3': 'ae',
    '\uA735': 'ao',
    '\uA737': 'au',
    '\uA739': 'av',
    '\uA73B': 'av',
    '\uA73D': 'ay',
    '\u24D1': 'b',
    '\uFF42': 'b',
    '\u1E03': 'b',
    '\u1E05': 'b',
    '\u1E07': 'b',
    '\u0180': 'b',
    '\u0183': 'b',
    '\u0253': 'b',
    '\u24D2': 'c',
    '\uFF43': 'c',
    '\u0107': 'c',
    '\u0109': 'c',
    '\u010B': 'c',
    '\u010D': 'c',
    '\u00E7': 'c',
    '\u1E09': 'c',
    '\u0188': 'c',
    '\u023C': 'c',
    '\uA73F': 'c',
    '\u2184': 'c',
    '\u24D3': 'd',
    '\uFF44': 'd',
    '\u1E0B': 'd',
    '\u010F': 'd',
    '\u1E0D': 'd',
    '\u1E11': 'd',
    '\u1E13': 'd',
    '\u1E0F': 'd',
    '\u0111': 'd',
    '\u018C': 'd',
    '\u0256': 'd',
    '\u0257': 'd',
    '\uA77A': 'd',
    '\u01F3': 'dz',
    '\u01C6': 'dz',
    '\u24D4': 'e',
    '\uFF45': 'e',
    '\u00E8': 'e',
    '\u00E9': 'e',
    '\u00EA': 'e',
    '\u1EC1': 'e',
    '\u1EBF': 'e',
    '\u1EC5': 'e',
    '\u1EC3': 'e',
    '\u1EBD': 'e',
    '\u0113': 'e',
    '\u1E15': 'e',
    '\u1E17': 'e',
    '\u0115': 'e',
    '\u0117': 'e',
    '\u00EB': 'e',
    '\u1EBB': 'e',
    '\u011B': 'e',
    '\u0205': 'e',
    '\u0207': 'e',
    '\u1EB9': 'e',
    '\u1EC7': 'e',
    '\u0229': 'e',
    '\u1E1D': 'e',
    '\u0119': 'e',
    '\u1E19': 'e',
    '\u1E1B': 'e',
    '\u0247': 'e',
    '\u025B': 'e',
    '\u01DD': 'e',
    '\u24D5': 'f',
    '\uFF46': 'f',
    '\u1E1F': 'f',
    '\u0192': 'f',
    '\uA77C': 'f',
    '\u24D6': 'g',
    '\uFF47': 'g',
    '\u01F5': 'g',
    '\u011D': 'g',
    '\u1E21': 'g',
    '\u011F': 'g',
    '\u0121': 'g',
    '\u01E7': 'g',
    '\u0123': 'g',
    '\u01E5': 'g',
    '\u0260': 'g',
    '\uA7A1': 'g',
    '\u1D79': 'g',
    '\uA77F': 'g',
    '\u24D7': 'h',
    '\uFF48': 'h',
    '\u0125': 'h',
    '\u1E23': 'h',
    '\u1E27': 'h',
    '\u021F': 'h',
    '\u1E25': 'h',
    '\u1E29': 'h',
    '\u1E2B': 'h',
    '\u1E96': 'h',
    '\u0127': 'h',
    '\u2C68': 'h',
    '\u2C76': 'h',
    '\u0265': 'h',
    '\u0195': 'hv',
    '\u24D8': 'i',
    '\uFF49': 'i',
    '\u00EC': 'i',
    '\u00ED': 'i',
    '\u00EE': 'i',
    '\u0129': 'i',
    '\u012B': 'i',
    '\u012D': 'i',
    '\u00EF': 'i',
    '\u1E2F': 'i',
    '\u1EC9': 'i',
    '\u01D0': 'i',
    '\u0209': 'i',
    '\u020B': 'i',
    '\u1ECB': 'i',
    '\u012F': 'i',
    '\u1E2D': 'i',
    '\u0268': 'i',
    '\u0131': 'i',
    '\u24D9': 'j',
    '\uFF4A': 'j',
    '\u0135': 'j',
    '\u01F0': 'j',
    '\u0249': 'j',
    '\u24DA': 'k',
    '\uFF4B': 'k',
    '\u1E31': 'k',
    '\u01E9': 'k',
    '\u1E33': 'k',
    '\u0137': 'k',
    '\u1E35': 'k',
    '\u0199': 'k',
    '\u2C6A': 'k',
    '\uA741': 'k',
    '\uA743': 'k',
    '\uA745': 'k',
    '\uA7A3': 'k',
    '\u24DB': 'l',
    '\uFF4C': 'l',
    '\u0140': 'l',
    '\u013A': 'l',
    '\u013E': 'l',
    '\u1E37': 'l',
    '\u1E39': 'l',
    '\u013C': 'l',
    '\u1E3D': 'l',
    '\u1E3B': 'l',
    '\u017F': 'l',
    '\u0142': 'l',
    '\u019A': 'l',
    '\u026B': 'l',
    '\u2C61': 'l',
    '\uA749': 'l',
    '\uA781': 'l',
    '\uA747': 'l',
    '\u01C9': 'lj',
    '\u24DC': 'm',
    '\uFF4D': 'm',
    '\u1E3F': 'm',
    '\u1E41': 'm',
    '\u1E43': 'm',
    '\u0271': 'm',
    '\u026F': 'm',
    '\u24DD': 'n',
    '\uFF4E': 'n',
    '\u01F9': 'n',
    '\u0144': 'n',
    '\u00F1': 'n',
    '\u1E45': 'n',
    '\u0148': 'n',
    '\u1E47': 'n',
    '\u0146': 'n',
    '\u1E4B': 'n',
    '\u1E49': 'n',
    '\u019E': 'n',
    '\u0272': 'n',
    '\u0149': 'n',
    '\uA791': 'n',
    '\uA7A5': 'n',
    '\u01CC': 'nj',
    '\u24DE': 'o',
    '\uFF4F': 'o',
    '\u00F2': 'o',
    '\u00F3': 'o',
    '\u00F4': 'o',
    '\u1ED3': 'o',
    '\u1ED1': 'o',
    '\u1ED7': 'o',
    '\u1ED5': 'o',
    '\u00F5': 'o',
    '\u1E4D': 'o',
    '\u022D': 'o',
    '\u1E4F': 'o',
    '\u014D': 'o',
    '\u1E51': 'o',
    '\u1E53': 'o',
    '\u014F': 'o',
    '\u022F': 'o',
    '\u0231': 'o',
    '\u00F6': 'o',
    '\u022B': 'o',
    '\u1ECF': 'o',
    '\u0151': 'o',
    '\u01D2': 'o',
    '\u020D': 'o',
    '\u020F': 'o',
    '\u01A1': 'o',
    '\u1EDD': 'o',
    '\u1EDB': 'o',
    '\u1EE1': 'o',
    '\u1EDF': 'o',
    '\u1EE3': 'o',
    '\u1ECD': 'o',
    '\u1ED9': 'o',
    '\u01EB': 'o',
    '\u01ED': 'o',
    '\u00F8': 'o',
    '\u01FF': 'o',
    '\u0254': 'o',
    '\uA74B': 'o',
    '\uA74D': 'o',
    '\u0275': 'o',
    '\u01A3': 'oi',
    '\u0223': 'ou',
    '\uA74F': 'oo',
    '\u24DF': 'p',
    '\uFF50': 'p',
    '\u1E55': 'p',
    '\u1E57': 'p',
    '\u01A5': 'p',
    '\u1D7D': 'p',
    '\uA751': 'p',
    '\uA753': 'p',
    '\uA755': 'p',
    '\u24E0': 'q',
    '\uFF51': 'q',
    '\u024B': 'q',
    '\uA757': 'q',
    '\uA759': 'q',
    '\u24E1': 'r',
    '\uFF52': 'r',
    '\u0155': 'r',
    '\u1E59': 'r',
    '\u0159': 'r',
    '\u0211': 'r',
    '\u0213': 'r',
    '\u1E5B': 'r',
    '\u1E5D': 'r',
    '\u0157': 'r',
    '\u1E5F': 'r',
    '\u024D': 'r',
    '\u027D': 'r',
    '\uA75B': 'r',
    '\uA7A7': 'r',
    '\uA783': 'r',
    '\u24E2': 's',
    '\uFF53': 's',
    '\u00DF': 's',
    '\u015B': 's',
    '\u1E65': 's',
    '\u015D': 's',
    '\u1E61': 's',
    '\u0161': 's',
    '\u1E67': 's',
    '\u1E63': 's',
    '\u1E69': 's',
    '\u0219': 's',
    '\u015F': 's',
    '\u023F': 's',
    '\uA7A9': 's',
    '\uA785': 's',
    '\u1E9B': 's',
    '\u24E3': 't',
    '\uFF54': 't',
    '\u1E6B': 't',
    '\u1E97': 't',
    '\u0165': 't',
    '\u1E6D': 't',
    '\u021B': 't',
    '\u0163': 't',
    '\u1E71': 't',
    '\u1E6F': 't',
    '\u0167': 't',
    '\u01AD': 't',
    '\u0288': 't',
    '\u2C66': 't',
    '\uA787': 't',
    '\uA729': 'tz',
    '\u24E4': 'u',
    '\uFF55': 'u',
    '\u00F9': 'u',
    '\u00FA': 'u',
    '\u00FB': 'u',
    '\u0169': 'u',
    '\u1E79': 'u',
    '\u016B': 'u',
    '\u1E7B': 'u',
    '\u016D': 'u',
    '\u00FC': 'u',
    '\u01DC': 'u',
    '\u01D8': 'u',
    '\u01D6': 'u',
    '\u01DA': 'u',
    '\u1EE7': 'u',
    '\u016F': 'u',
    '\u0171': 'u',
    '\u01D4': 'u',
    '\u0215': 'u',
    '\u0217': 'u',
    '\u01B0': 'u',
    '\u1EEB': 'u',
    '\u1EE9': 'u',
    '\u1EEF': 'u',
    '\u1EED': 'u',
    '\u1EF1': 'u',
    '\u1EE5': 'u',
    '\u1E73': 'u',
    '\u0173': 'u',
    '\u1E77': 'u',
    '\u1E75': 'u',
    '\u0289': 'u',
    '\u24E5': 'v',
    '\uFF56': 'v',
    '\u1E7D': 'v',
    '\u1E7F': 'v',
    '\u028B': 'v',
    '\uA75F': 'v',
    '\u028C': 'v',
    '\uA761': 'vy',
    '\u24E6': 'w',
    '\uFF57': 'w',
    '\u1E81': 'w',
    '\u1E83': 'w',
    '\u0175': 'w',
    '\u1E87': 'w',
    '\u1E85': 'w',
    '\u1E98': 'w',
    '\u1E89': 'w',
    '\u2C73': 'w',
    '\u24E7': 'x',
    '\uFF58': 'x',
    '\u1E8B': 'x',
    '\u1E8D': 'x',
    '\u24E8': 'y',
    '\uFF59': 'y',
    '\u1EF3': 'y',
    '\u00FD': 'y',
    '\u0177': 'y',
    '\u1EF9': 'y',
    '\u0233': 'y',
    '\u1E8F': 'y',
    '\u00FF': 'y',
    '\u1EF7': 'y',
    '\u1E99': 'y',
    '\u1EF5': 'y',
    '\u01B4': 'y',
    '\u024F': 'y',
    '\u1EFF': 'y',
    '\u24E9': 'z',
    '\uFF5A': 'z',
    '\u017A': 'z',
    '\u1E91': 'z',
    '\u017C': 'z',
    '\u017E': 'z',
    '\u1E93': 'z',
    '\u1E95': 'z',
    '\u01B6': 'z',
    '\u0225': 'z',
    '\u0240': 'z',
    '\u2C6C': 'z',
    '\uA763': 'z',
    '\u0386': '\u0391',
    '\u0388': '\u0395',
    '\u0389': '\u0397',
    '\u038A': '\u0399',
    '\u03AA': '\u0399',
    '\u038C': '\u039F',
    '\u038E': '\u03A5',
    '\u03AB': '\u03A5',
    '\u038F': '\u03A9',
    '\u03AC': '\u03B1',
    '\u03AD': '\u03B5',
    '\u03AE': '\u03B7',
    '\u03AF': '\u03B9',
    '\u03CA': '\u03B9',
    '\u0390': '\u03B9',
    '\u03CC': '\u03BF',
    '\u03CD': '\u03C5',
    '\u03CB': '\u03C5',
    '\u03B0': '\u03C5',
    '\u03C9': '\u03C9',
    '\u03C2': '\u03C3'
  };

  return diacritics;
});

S2.define('select2/data/base',[
  '../utils'
], function (Utils) {
  function BaseAdapter ($element, options) {
    BaseAdapter.__super__.constructor.call(this);
  }

  Utils.Extend(BaseAdapter, Utils.Observable);

  BaseAdapter.prototype.current = function (callback) {
    throw new Error('The `current` method must be defined in child classes.');
  };

  BaseAdapter.prototype.query = function (params, callback) {
    throw new Error('The `query` method must be defined in child classes.');
  };

  BaseAdapter.prototype.bind = function (container, $container) {
    // Can be implemented in subclasses
  };

  BaseAdapter.prototype.destroy = function () {
    // Can be implemented in subclasses
  };

  BaseAdapter.prototype.generateResultId = function (container, data) {
    var id = '';

    if (container != null) {
      id += container.id
    } else {
      id += Utils.generateChars(4);
    }

    id += '-result-';
    id += Utils.generateChars(4);

    if (data.id != null) {
      id += '-' + data.id.toString();
    } else {
      id += '-' + Utils.generateChars(4);
    }
    return id;
  };

  return BaseAdapter;
});

S2.define('select2/data/select',[
  './base',
  '../utils',
  'jquery'
], function (BaseAdapter, Utils, $) {
  function SelectAdapter ($element, options) {
    this.$element = $element;
    this.options = options;

    SelectAdapter.__super__.constructor.call(this);
  }

  Utils.Extend(SelectAdapter, BaseAdapter);

  SelectAdapter.prototype.current = function (callback) {
    var data = [];
    var self = this;

    this.$element.find(':selected').each(function () {
      var $option = $(this);

      var option = self.item($option);

      data.push(option);
    });

    callback(data);
  };

  SelectAdapter.prototype.select = function (data) {
    var self = this;

    data.selected = true;

    // If data.element is a DOM node, use it instead
    if ($(data.element).is('option')) {
      data.element.selected = true;

      this.$element.trigger('change');

      return;
    }

    if (this.$element.prop('multiple')) {
      this.current(function (currentData) {
        var val = [];

        data = [data];
        data.push.apply(data, currentData);

        for (var d = 0; d < data.length; d++) {
          var id = data[d].id;

          if ($.inArray(id, val) === -1) {
            val.push(id);
          }
        }

        self.$element.val(val);
        self.$element.trigger('change');
      });
    } else {
      var val = data.id;

      this.$element.val(val);
      this.$element.trigger('change');
    }
  };

  SelectAdapter.prototype.unselect = function (data) {
    var self = this;

    if (!this.$element.prop('multiple')) {
      return;
    }

    data.selected = false;

    if ($(data.element).is('option')) {
      data.element.selected = false;

      this.$element.trigger('change');

      return;
    }

    this.current(function (currentData) {
      var val = [];

      for (var d = 0; d < currentData.length; d++) {
        var id = currentData[d].id;

        if (id !== data.id && $.inArray(id, val) === -1) {
          val.push(id);
        }
      }

      self.$element.val(val);

      self.$element.trigger('change');
    });
  };

  SelectAdapter.prototype.bind = function (container, $container) {
    var self = this;

    this.container = container;

    container.on('select', function (params) {
      self.select(params.data);
    });

    container.on('unselect', function (params) {
      self.unselect(params.data);
    });
  };

  SelectAdapter.prototype.destroy = function () {
    // Remove anything added to child elements
    this.$element.find('*').each(function () {
      // Remove any custom data set by Select2
      $.removeData(this, 'data');
    });
  };

  SelectAdapter.prototype.query = function (params, callback) {
    var data = [];
    var self = this;

    var $options = this.$element.children();

    $options.each(function () {
      var $option = $(this);

      if (!$option.is('option') && !$option.is('optgroup')) {
        return;
      }

      var option = self.item($option);

      var matches = self.matches(params, option);

      if (matches !== null) {
        data.push(matches);
      }
    });

    callback({
      results: data
    });
  };

  SelectAdapter.prototype.addOptions = function ($options) {
    Utils.appendMany(this.$element, $options);
  };

  SelectAdapter.prototype.option = function (data) {
    var option;

    if (data.children) {
      option = document.createElement('optgroup');
      option.label = data.text;
    } else {
      option = document.createElement('option');

      if (option.textContent !== undefined) {
        option.textContent = data.text;
      } else {
        option.innerText = data.text;
      }
    }

    if (data.id !== undefined) {
      option.value = data.id;
    }

    if (data.disabled) {
      option.disabled = true;
    }

    if (data.selected) {
      option.selected = true;
    }

    if (data.title) {
      option.title = data.title;
    }

    var $option = $(option);

    var normalizedData = this._normalizeItem(data);
    normalizedData.element = option;

    // Override the option's data with the combined data
    $.data(option, 'data', normalizedData);

    return $option;
  };

  SelectAdapter.prototype.item = function ($option) {
    var data = {};

    data = $.data($option[0], 'data');

    if (data != null) {
      return data;
    }

    if ($option.is('option')) {
      data = {
        id: $option.val(),
        text: $option.text(),
        disabled: $option.prop('disabled'),
        selected: $option.prop('selected'),
        title: $option.prop('title')
      };
    } else if ($option.is('optgroup')) {
      data = {
        text: $option.prop('label'),
        children: [],
        title: $option.prop('title')
      };

      var $children = $option.children('option');
      var children = [];

      for (var c = 0; c < $children.length; c++) {
        var $child = $($children[c]);

        var child = this.item($child);

        children.push(child);
      }

      data.children = children;
    }

    data = this._normalizeItem(data);
    data.element = $option[0];

    $.data($option[0], 'data', data);

    return data;
  };

  SelectAdapter.prototype._normalizeItem = function (item) {
    if (!$.isPlainObject(item)) {
      item = {
        id: item,
        text: item
      };
    }

    item = $.extend({}, {
      text: ''
    }, item);

    var defaults = {
      selected: false,
      disabled: false
    };

    if (item.id != null) {
      item.id = item.id.toString();
    }

    if (item.text != null) {
      item.text = item.text.toString();
    }

    if (item._resultId == null && item.id) {
      item._resultId = this.generateResultId(this.container, item);
    }

    return $.extend({}, defaults, item);
  };

  SelectAdapter.prototype.matches = function (params, data) {
    var matcher = this.options.get('matcher');

    return matcher(params, data);
  };

  return SelectAdapter;
});

S2.define('select2/data/array',[
  './select',
  '../utils',
  'jquery'
], function (SelectAdapter, Utils, $) {
  function ArrayAdapter ($element, options) {
    var data = options.get('data') || [];

    ArrayAdapter.__super__.constructor.call(this, $element, options);

    this.addOptions(this.convertToOptions(data));
  }

  Utils.Extend(ArrayAdapter, SelectAdapter);

  ArrayAdapter.prototype.select = function (data) {
    var $option = this.$element.find('option').filter(function (i, elm) {
      return elm.value == data.id.toString();
    });

    if ($option.length === 0) {
      $option = this.option(data);

      this.addOptions($option);
    }

    ArrayAdapter.__super__.select.call(this, data);
  };

  ArrayAdapter.prototype.convertToOptions = function (data) {
    var self = this;

    var $existing = this.$element.find('option');
    var existingIds = $existing.map(function () {
      return self.item($(this)).id;
    }).get();

    var $options = [];

    // Filter out all items except for the one passed in the argument
    function onlyItem (item) {
      return function () {
        return $(this).val() == item.id;
      };
    }

    for (var d = 0; d < data.length; d++) {
      var item = this._normalizeItem(data[d]);

      // Skip items which were pre-loaded, only merge the data
      if ($.inArray(item.id, existingIds) >= 0) {
        var $existingOption = $existing.filter(onlyItem(item));

        var existingData = this.item($existingOption);
        var newData = $.extend(true, {}, item, existingData);

        var $newOption = this.option(newData);

        $existingOption.replaceWith($newOption);

        continue;
      }

      var $option = this.option(item);

      if (item.children) {
        var $children = this.convertToOptions(item.children);

        Utils.appendMany($option, $children);
      }

      $options.push($option);
    }

    return $options;
  };

  return ArrayAdapter;
});

S2.define('select2/data/ajax',[
  './array',
  '../utils',
  'jquery'
], function (ArrayAdapter, Utils, $) {
  function AjaxAdapter ($element, options) {
    this.ajaxOptions = this._applyDefaults(options.get('ajax'));

    if (this.ajaxOptions.processResults != null) {
      this.processResults = this.ajaxOptions.processResults;
    }

    AjaxAdapter.__super__.constructor.call(this, $element, options);
  }

  Utils.Extend(AjaxAdapter, ArrayAdapter);

  AjaxAdapter.prototype._applyDefaults = function (options) {
    var defaults = {
      data: function (params) {
        return $.extend({}, params, {
          q: params.term
        });
      },
      transport: function (params, success, failure) {
        var $request = $.ajax(params);

        $request.then(success);
        $request.fail(failure);

        return $request;
      }
    };

    return $.extend({}, defaults, options, true);
  };

  AjaxAdapter.prototype.processResults = function (results) {
    return results;
  };

  AjaxAdapter.prototype.query = function (params, callback) {
    var matches = [];
    var self = this;

    if (this._request != null) {
      // JSONP requests cannot always be aborted
      if ($.isFunction(this._request.abort)) {
        this._request.abort();
      }

      this._request = null;
    }

    var options = $.extend({
      type: 'GET'
    }, this.ajaxOptions);

    if (typeof options.url === 'function') {
      options.url = options.url.call(this.$element, params);
    }

    if (typeof options.data === 'function') {
      options.data = options.data.call(this.$element, params);
    }

    function request () {
      var $request = options.transport(options, function (data) {
        var results = self.processResults(data, params);

        if (self.options.get('debug') && window.console && console.error) {
          // Check to make sure that the response included a `results` key.
          if (!results || !results.results || !$.isArray(results.results)) {
            console.error(
              'Select2: The AJAX results did not return an array in the ' +
              '`results` key of the response.'
            );
          }
        }

        callback(results);
        self.container.focusOnActiveElement();
      }, function () {
        // Attempt to detect if a request was aborted
        // Only works if the transport exposes a status property
        if ($request.status && $request.status === '0') {
          return;
        }

        self.trigger('results:message', {
          message: 'errorLoading'
        });
      });

      self._request = $request;
    }

    if (this.ajaxOptions.delay && params.term != null) {
      if (this._queryTimeout) {
        window.clearTimeout(this._queryTimeout);
      }

      this._queryTimeout = window.setTimeout(request, this.ajaxOptions.delay);
    } else {
      request();
    }
  };

  return AjaxAdapter;
});

S2.define('select2/data/tags',[
  'jquery'
], function ($) {
  function Tags (decorated, $element, options) {
    var tags = options.get('tags');

    var createTag = options.get('createTag');

    if (createTag !== undefined) {
      this.createTag = createTag;
    }

    var insertTag = options.get('insertTag');

    if (insertTag !== undefined) {
        this.insertTag = insertTag;
    }

    decorated.call(this, $element, options);

    if ($.isArray(tags)) {
      for (var t = 0; t < tags.length; t++) {
        var tag = tags[t];
        var item = this._normalizeItem(tag);

        var $option = this.option(item);

        this.$element.append($option);
      }
    }
  }

  Tags.prototype.query = function (decorated, params, callback) {
    var self = this;

    this._removeOldTags();

    if (params.term == null || params.page != null) {
      decorated.call(this, params, callback);
      return;
    }

    function wrapper (obj, child) {
      var data = obj.results;

      for (var i = 0; i < data.length; i++) {
        var option = data[i];

        var checkChildren = (
          option.children != null &&
          !wrapper({
            results: option.children
          }, true)
        );

        var optionText = (option.text || '').toUpperCase();
        var paramsTerm = (params.term || '').toUpperCase();

        var checkText = optionText === paramsTerm;

        if (checkText || checkChildren) {
          if (child) {
            return false;
          }

          obj.data = data;
          callback(obj);

          return;
        }
      }

      if (child) {
        return true;
      }

      var tag = self.createTag(params);

      if (tag != null) {
        var $option = self.option(tag);
        $option.attr('data-select2-tag', true);

        self.addOptions([$option]);

        self.insertTag(data, tag);
      }

      obj.results = data;

      callback(obj);
    }

    decorated.call(this, params, wrapper);
  };

  Tags.prototype.createTag = function (decorated, params) {
    var term = $.trim(params.term);

    if (term === '') {
      return null;
    }

    return {
      id: term,
      text: term
    };
  };

  Tags.prototype.insertTag = function (_, data, tag) {
    data.unshift(tag);
  };

  Tags.prototype._removeOldTags = function (_) {
    var tag = this._lastTag;

    var $options = this.$element.find('option[data-select2-tag]');

    $options.each(function () {
      if (this.selected) {
        return;
      }

      $(this).remove();
    });
  };

  return Tags;
});

S2.define('select2/data/tokenizer',[
  'jquery'
], function ($) {
  function Tokenizer (decorated, $element, options) {
    var tokenizer = options.get('tokenizer');

    if (tokenizer !== undefined) {
      this.tokenizer = tokenizer;
    }

    decorated.call(this, $element, options);
  }

  Tokenizer.prototype.bind = function (decorated, container, $container) {
    decorated.call(this, container, $container);

    this.$search =  container.dropdown.$search || container.selection.$search ||
      $container.find('.select2-search__field');
  };

  Tokenizer.prototype.query = function (decorated, params, callback) {
    var self = this;

    function createAndSelect (data) {
      // Normalize the data object so we can use it for checks
      var item = self._normalizeItem(data);

      // Check if the data object already exists as a tag
      // Select it if it doesn't
      var $existingOptions = self.$element.find('option').filter(function () {
        return $(this).val() === item.id;
      });

      // If an existing option wasn't found for it, create the option
      if (!$existingOptions.length) {
        var $option = self.option(item);
        $option.attr('data-select2-tag', true);

        self._removeOldTags();
        self.addOptions([$option]);
      }

      // Select the item, now that we know there is an option for it
      select(item);
    }

    function select (data) {
      self.trigger('select', {
        data: data
      });
    }

    params.term = params.term || '';

    var tokenData = this.tokenizer(params, this.options, createAndSelect);

    if (tokenData.term !== params.term) {
      // Replace the search term if we have the search box
      if (this.$search.length) {
        this.$search.val(tokenData.term);
        this.$search.focus();
      }

      params.term = tokenData.term;
    }

    decorated.call(this, params, callback);
  };

  Tokenizer.prototype.tokenizer = function (_, params, options, callback) {
    var separators = options.get('tokenSeparators') || [];
    var term = params.term;
    var i = 0;

    var createTag = this.createTag || function (params) {
      return {
        id: params.term,
        text: params.term
      };
    };

    while (i < term.length) {
      var termChar = term[i];

      if ($.inArray(termChar, separators) === -1) {
        i++;

        continue;
      }

      var part = term.substr(0, i);
      var partParams = $.extend({}, params, {
        term: part
      });

      var data = createTag(partParams);

      if (data == null) {
        i++;
        continue;
      }

      callback(data);

      // Reset the term to not include the tokenized portion
      term = term.substr(i + 1) || '';
      i = 0;
    }

    return {
      term: term
    };
  };

  return Tokenizer;
});

S2.define('select2/data/minimumInputLength',[

], function () {
  function MinimumInputLength (decorated, $e, options) {
    this.minimumInputLength = options.get('minimumInputLength');

    decorated.call(this, $e, options);
  }

  MinimumInputLength.prototype.query = function (decorated, params, callback) {
    params.term = params.term || '';

    if (params.term.length < this.minimumInputLength) {
      this.trigger('results:message', {
        message: 'inputTooShort',
        args: {
          minimum: this.minimumInputLength,
          input: params.term,
          params: params
        }
      });

      return;
    }

    decorated.call(this, params, callback);
  };

  return MinimumInputLength;
});

S2.define('select2/data/maximumInputLength',[

], function () {
  function MaximumInputLength (decorated, $e, options) {
    this.maximumInputLength = options.get('maximumInputLength');

    decorated.call(this, $e, options);
  }

  MaximumInputLength.prototype.query = function (decorated, params, callback) {
    params.term = params.term || '';

    if (this.maximumInputLength > 0 &&
        params.term.length > this.maximumInputLength) {
      this.trigger('results:message', {
        message: 'inputTooLong',
        args: {
          maximum: this.maximumInputLength,
          input: params.term,
          params: params
        }
      });

      return;
    }

    decorated.call(this, params, callback);
  };

  return MaximumInputLength;
});

S2.define('select2/data/maximumSelectionLength',[

], function (){
  function MaximumSelectionLength (decorated, $e, options) {
    this.maximumSelectionLength = options.get('maximumSelectionLength');

    decorated.call(this, $e, options);
  }

  MaximumSelectionLength.prototype.query =
    function (decorated, params, callback) {
      var self = this;

      this.current(function (currentData) {
        var count = currentData != null ? currentData.length : 0;
        if (self.maximumSelectionLength > 0 &&
          count >= self.maximumSelectionLength) {
          self.trigger('results:message', {
            message: 'maximumSelected',
            args: {
              maximum: self.maximumSelectionLength
            }
          });
          return;
        }
        decorated.call(self, params, callback);
      });
  };

  return MaximumSelectionLength;
});

S2.define('select2/dropdown',[
  'jquery',
  './utils'
], function ($, Utils) {
  function Dropdown ($element, options) {
    this.$element = $element;
    this.options = options;

    Dropdown.__super__.constructor.call(this);
  }

  Utils.Extend(Dropdown, Utils.Observable);

  Dropdown.prototype.render = function () {
    var $dropdown = $(
      '<span class="select2-dropdown">' +
        '<span class="select2-results"></span>' +
      '</span>'
    );

    $dropdown.attr('dir', this.options.get('dir'));

    this.$dropdown = $dropdown;

    return $dropdown;
  };

  Dropdown.prototype.bind = function () {
    // Should be implemented in subclasses
  };

  Dropdown.prototype.position = function ($dropdown, $container) {
    // Should be implmented in subclasses
  };

  Dropdown.prototype.destroy = function () {
    // Remove the dropdown from the DOM
    this.$dropdown.remove();
  };

  return Dropdown;
});

S2.define('select2/dropdown/search',[
  'jquery',
  '../utils'
], function ($, Utils) {
  function Search () { }

  Search.prototype.render = function (decorated) {
    var $rendered = decorated.call(this);

    var $search = $(
      '<span class="select2-search select2-search--dropdown">' +
        '<input class="select2-search__field" type="text" tabindex="-1"' +
        ' autocomplete="off" autocorrect="off" autocapitalize="none"' +
        ' spellcheck="false" role="combobox" aria-autocomplete="list" aria-expanded="true" />' +
      '</span>'
    );

    this.$searchContainer = $search;
    this.$search = $search.find('input');

    $rendered.prepend($search);

    return $rendered;
  };

  Search.prototype.bind = function (decorated, container, $container) {
    var self = this;
    var resultsId = container.id + '-results';

    decorated.call(this, container, $container);

    this.$search.on('keydown', function (evt) {
      self.trigger('keypress', evt);

      self._keyUpPrevented = evt.isDefaultPrevented();
    });

    // Workaround for browsers which do not support the `input` event
    // This will prevent double-triggering of events for browsers which support
    // both the `keyup` and `input` events.
    this.$search.on('input', function (evt) {
      // Unbind the duplicated `keyup` event
      $(this).off('keyup');
    });

    this.$search.on('keyup input', function (evt) {
      self.handleSearch(evt);
    });

    container.on('open', function () {
      self.$search.attr('tabindex', 0);
      self.$search.attr('aria-owns', resultsId);
      self.$search.focus();

      window.setTimeout(function () {
        self.$search.focus();
      }, 0);
    });

    container.on('close', function () {
      self.$search.attr('tabindex', -1);
      self.$search.removeAttr('aria-activedescendant');
      self.$search.removeAttr('aria-owns');
      self.$search.val('');
    });

    container.on('focus', function () {
      if (!container.isOpen()) {
        self.$search.focus();
      }
    });

    container.on('results:all', function (params) {
      if (params.query.term == null || params.query.term === '') {
        var showSearch = self.showSearch(params);

        if (showSearch) {
          self.$searchContainer.removeClass('select2-search--hide');
        } else {
          self.$searchContainer.addClass('select2-search--hide');
        }
      }
    });

    container.on('results:focus', function (params) {
      self.$search.attr('aria-activedescendant', params.data._resultId);
    });
  };

  Search.prototype.handleSearch = function (evt) {
    if (!this._keyUpPrevented) {
      var input = this.$search.val();

      this.trigger('query', {
        term: input
      });
    }

    this._keyUpPrevented = false;
  };

  Search.prototype.showSearch = function (_, params) {
    return true;
  };

  return Search;
});

S2.define('select2/dropdown/hidePlaceholder',[

], function () {
  function HidePlaceholder (decorated, $element, options, dataAdapter) {
    this.placeholder = this.normalizePlaceholder(options.get('placeholder'));

    decorated.call(this, $element, options, dataAdapter);
  }

  HidePlaceholder.prototype.append = function (decorated, data) {
    data.results = this.removePlaceholder(data.results);

    decorated.call(this, data);
  };

  HidePlaceholder.prototype.normalizePlaceholder = function (_, placeholder) {
    if (typeof placeholder === 'string') {
      placeholder = {
        id: '',
        text: placeholder
      };
    }

    return placeholder;
  };

  HidePlaceholder.prototype.removePlaceholder = function (_, data) {
    var modifiedData = data.slice(0);

    for (var d = data.length - 1; d >= 0; d--) {
      var item = data[d];

      if (this.placeholder.id === item.id) {
        modifiedData.splice(d, 1);
      }
    }

    return modifiedData;
  };

  return HidePlaceholder;
});

S2.define('select2/dropdown/infiniteScroll',[
  'jquery'
], function ($) {
  function InfiniteScroll (decorated, $element, options, dataAdapter) {
    this.lastParams = {};

    decorated.call(this, $element, options, dataAdapter);

    this.$loadingMore = this.createLoadingMore();
    this.loading = false;
  }

  InfiniteScroll.prototype.append = function (decorated, data) {
    this.$loadingMore.remove();
    this.loading = false;

    decorated.call(this, data);

    if (this.showLoadingMore(data)) {
      this.$results.append(this.$loadingMore);
    }
  };

  InfiniteScroll.prototype.bind = function (decorated, container, $container) {
    var self = this;

    decorated.call(this, container, $container);

    container.on('query', function (params) {
      self.lastParams = params;
      self.loading = true;
    });

    container.on('query:append', function (params) {
      self.lastParams = params;
      self.loading = true;
    });

    this.$results.on('scroll', function () {
      var isLoadMoreVisible = $.contains(
        document.documentElement,
        self.$loadingMore[0]
      );

      if (self.loading || !isLoadMoreVisible) {
        return;
      }

      var currentOffset = self.$results.offset().top +
        self.$results.outerHeight(false);
      var loadingMoreOffset = self.$loadingMore.offset().top +
        self.$loadingMore.outerHeight(false);

      if (currentOffset + 50 >= loadingMoreOffset) {
        self.loadMore();
      }
    });
  };

  InfiniteScroll.prototype.loadMore = function () {
    this.loading = true;

    var params = $.extend({}, {page: 1}, this.lastParams);

    params.page++;

    this.trigger('query:append', params);
  };

  InfiniteScroll.prototype.showLoadingMore = function (_, data) {
    return data.pagination && data.pagination.more;
  };

  InfiniteScroll.prototype.createLoadingMore = function () {
    var $option = $(
      '<li ' +
      'class="select2-results__option select2-results__option--load-more"' +
      'role="option" aria-disabled="true"></li>'
    );

    var message = this.options.get('translations').get('loadingMore');

    $option.html(message(this.lastParams));

    return $option;
  };

  return InfiniteScroll;
});

S2.define('select2/dropdown/attachBody',[
  'jquery',
  '../utils'
], function ($, Utils) {
  function AttachBody (decorated, $element, options) {
    this.$dropdownParent = options.get('dropdownParent') || $(document.body);

    decorated.call(this, $element, options);
  }

  AttachBody.prototype.bind = function (decorated, container, $container) {
    var self = this;

    var setupResultsEvents = false;

    decorated.call(this, container, $container);

    container.on('open', function () {
      self._showDropdown();
      self._attachPositioningHandler(container);

      if (!setupResultsEvents) {
        setupResultsEvents = true;

        container.on('results:all', function () {
          self._positionDropdown();
          self._resizeDropdown();
        });

        container.on('results:append', function () {
          self._positionDropdown();
          self._resizeDropdown();
        });
      }
    });

    container.on('close', function () {
      self._hideDropdown();
      self._detachPositioningHandler(container);
    });

    this.$dropdownContainer.on('mousedown', function (evt) {
      evt.stopPropagation();
    });
  };

  AttachBody.prototype.destroy = function (decorated) {
    decorated.call(this);

    this.$dropdownContainer.remove();
  };

  AttachBody.prototype.position = function (decorated, $dropdown, $container) {
    // Clone all of the container classes
    $dropdown.attr('class', $container.attr('class'));

    $dropdown.removeClass('select2');
    $dropdown.addClass('select2-container--open');

    $dropdown.css({
      position: 'absolute',
      top: -999999
    });

    this.$container = $container;
  };

  AttachBody.prototype.render = function (decorated) {
    var $container = $('<span></span>');

    var $dropdown = decorated.call(this);
    $container.append($dropdown);

    this.$dropdownContainer = $container;

    return $container;
  };

  AttachBody.prototype._hideDropdown = function (decorated) {
    this.$dropdownContainer.detach();
  };

  AttachBody.prototype._attachPositioningHandler =
      function (decorated, container) {
    var self = this;

    var scrollEvent = 'scroll.select2.' + container.id;
    var resizeEvent = 'resize.select2.' + container.id;
    var orientationEvent = 'orientationchange.select2.' + container.id;

    var $watchers = this.$container.parents().filter(Utils.hasScroll);
    $watchers.each(function () {
      $(this).data('select2-scroll-position', {
        x: $(this).scrollLeft(),
        y: $(this).scrollTop()
      });
    });

    $watchers.on(scrollEvent, function (ev) {
      var position = $(this).data('select2-scroll-position');
      $(this).scrollTop(position.y);
    });

    $(window).on(scrollEvent + ' ' + resizeEvent + ' ' + orientationEvent,
      function (e) {
      self._positionDropdown();
      self._resizeDropdown();
    });
  };

  AttachBody.prototype._detachPositioningHandler =
      function (decorated, container) {
    var scrollEvent = 'scroll.select2.' + container.id;
    var resizeEvent = 'resize.select2.' + container.id;
    var orientationEvent = 'orientationchange.select2.' + container.id;

    var $watchers = this.$container.parents().filter(Utils.hasScroll);
    $watchers.off(scrollEvent);

    $(window).off(scrollEvent + ' ' + resizeEvent + ' ' + orientationEvent);
  };

  AttachBody.prototype._positionDropdown = function () {
    var $window = $(window);

    var isCurrentlyAbove = this.$dropdown.hasClass('select2-dropdown--above');
    var isCurrentlyBelow = this.$dropdown.hasClass('select2-dropdown--below');

    var newDirection = null;

    var offset = this.$container.offset();

    offset.bottom = offset.top + this.$container.outerHeight(false);

    var container = {
      height: this.$container.outerHeight(false)
    };

    container.top = offset.top;
    container.bottom = offset.top + container.height;

    var dropdown = {
      height: this.$dropdown.outerHeight(false)
    };

    var viewport = {
      top: $window.scrollTop(),
      bottom: $window.scrollTop() + $window.height()
    };

    var enoughRoomAbove = viewport.top < (offset.top - dropdown.height);
    var enoughRoomBelow = viewport.bottom > (offset.bottom + dropdown.height);

    var css = {
      left: offset.left,
      top: container.bottom
    };

    // Determine what the parent element is to use for calciulating the offset
    var $offsetParent = this.$dropdownParent;

    // For statically positoned elements, we need to get the element
    // that is determining the offset
    if ($offsetParent.css('position') === 'static') {
      $offsetParent = $offsetParent.offsetParent();
    }

    var parentOffset = $offsetParent.offset();

    css.top -= parentOffset.top;
    css.left -= parentOffset.left;

    if (!isCurrentlyAbove && !isCurrentlyBelow) {
      newDirection = 'below';
    }

    if (!enoughRoomBelow && enoughRoomAbove && !isCurrentlyAbove) {
      newDirection = 'above';
    } else if (!enoughRoomAbove && enoughRoomBelow && isCurrentlyAbove) {
      newDirection = 'below';
    }

    if (newDirection == 'above' ||
      (isCurrentlyAbove && newDirection !== 'below')) {
      css.top = container.top - parentOffset.top - dropdown.height;
    }

    if (newDirection != null) {
      this.$dropdown
        .removeClass('select2-dropdown--below select2-dropdown--above')
        .addClass('select2-dropdown--' + newDirection);
      this.$container
        .removeClass('select2-container--below select2-container--above')
        .addClass('select2-container--' + newDirection);
    }

    this.$dropdownContainer.css(css);
  };

  AttachBody.prototype._resizeDropdown = function () {
    var css = {
      width: this.$container.outerWidth(false) + 'px'
    };

    if (this.options.get('dropdownAutoWidth')) {
      css.minWidth = css.width;
      css.position = 'relative';
      css.width = 'auto';
    }

    this.$dropdown.css(css);
  };

  AttachBody.prototype._showDropdown = function (decorated) {
    this.$dropdownContainer.appendTo(this.$dropdownParent);

    this._positionDropdown();
    this._resizeDropdown();
  };

  return AttachBody;
});

S2.define('select2/dropdown/minimumResultsForSearch',[

], function () {
  function countResults (data) {
    var count = 0;

    for (var d = 0; d < data.length; d++) {
      var item = data[d];

      if (item.children) {
        count += countResults(item.children);
      } else {
        count++;
      }
    }

    return count;
  }

  function MinimumResultsForSearch (decorated, $element, options, dataAdapter) {
    this.minimumResultsForSearch = options.get('minimumResultsForSearch');

    if (this.minimumResultsForSearch < 0) {
      this.minimumResultsForSearch = Infinity;
    }

    decorated.call(this, $element, options, dataAdapter);
  }

  MinimumResultsForSearch.prototype.showSearch = function (decorated, params) {
    if (countResults(params.data.results) < this.minimumResultsForSearch) {
      return false;
    }

    return decorated.call(this, params);
  };

  return MinimumResultsForSearch;
});

S2.define('select2/dropdown/selectOnClose',[

], function () {
  function SelectOnClose () { }

  SelectOnClose.prototype.bind = function (decorated, container, $container) {
    var self = this;

    decorated.call(this, container, $container);

    container.on('close', function (params) {
      self._handleSelectOnClose(params);
    });
  };

  SelectOnClose.prototype._handleSelectOnClose = function (_, params) {
    if (params && params.originalSelect2Event != null) {
      var event = params.originalSelect2Event;

      // Don't select an item if the close event was triggered from a select or
      // unselect event
      if (event._type === 'select' || event._type === 'unselect') {
        return;
      }
    }

    var $highlightedResults = this.getHighlightedResults();

    // Only select highlighted results
    if ($highlightedResults.length < 1) {
      return;
    }

    var data = $highlightedResults.data('data');

    // Don't re-select already selected resulte
    if (
      (data.element != null && data.element.selected) ||
      (data.element == null && data.selected)
    ) {
      return;
    }

    this.trigger('select', {
        data: data
    });
  };

  return SelectOnClose;
});

S2.define('select2/dropdown/closeOnSelect',[

], function () {
  function CloseOnSelect () { }

  CloseOnSelect.prototype.bind = function (decorated, container, $container) {
    var self = this;

    decorated.call(this, container, $container);

    container.on('select', function (evt) {
      self._selectTriggered(evt);
    });

    container.on('unselect', function (evt) {
      self._selectTriggered(evt);
    });
  };

  CloseOnSelect.prototype._selectTriggered = function (_, evt) {
    var originalEvent = evt.originalEvent;

    // Don't close if the control key is being held
    if (originalEvent && originalEvent.ctrlKey) {
      return;
    }

    this.trigger('close', {
      originalEvent: originalEvent,
      originalSelect2Event: evt
    });
  };

  return CloseOnSelect;
});

S2.define('select2/i18n/en',[],function () {
  // English
  return {
    errorLoading: function () {
      return 'The results could not be loaded.';
    },
    inputTooLong: function (args) {
      var overChars = args.input.length - args.maximum;

      var message = 'Please delete ' + overChars + ' character';

      if (overChars != 1) {
        message += 's';
      }

      return message;
    },
    inputTooShort: function (args) {
      var remainingChars = args.minimum - args.input.length;

      var message = 'Please enter ' + remainingChars + ' or more characters';

      return message;
    },
    loadingMore: function () {
      return 'Loading more results…';
    },
    maximumSelected: function (args) {
      var message = 'You can only select ' + args.maximum + ' item';

      if (args.maximum != 1) {
        message += 's';
      }

      return message;
    },
    noResults: function () {
      return 'No results found';
    },
    searching: function () {
      return 'Searching…';
    }
  };
});

S2.define('select2/defaults',[
  'jquery',
  'require',

  './results',

  './selection/single',
  './selection/multiple',
  './selection/placeholder',
  './selection/allowClear',
  './selection/search',
  './selection/eventRelay',

  './utils',
  './translation',
  './diacritics',

  './data/select',
  './data/array',
  './data/ajax',
  './data/tags',
  './data/tokenizer',
  './data/minimumInputLength',
  './data/maximumInputLength',
  './data/maximumSelectionLength',

  './dropdown',
  './dropdown/search',
  './dropdown/hidePlaceholder',
  './dropdown/infiniteScroll',
  './dropdown/attachBody',
  './dropdown/minimumResultsForSearch',
  './dropdown/selectOnClose',
  './dropdown/closeOnSelect',

  './i18n/en'
], function ($, require,

             ResultsList,

             SingleSelection, MultipleSelection, Placeholder, AllowClear,
             SelectionSearch, EventRelay,

             Utils, Translation, DIACRITICS,

             SelectData, ArrayData, AjaxData, Tags, Tokenizer,
             MinimumInputLength, MaximumInputLength, MaximumSelectionLength,

             Dropdown, DropdownSearch, HidePlaceholder, InfiniteScroll,
             AttachBody, MinimumResultsForSearch, SelectOnClose, CloseOnSelect,

             EnglishTranslation) {
  function Defaults () {
    this.reset();
  }

  Defaults.prototype.apply = function (options) {
    options = $.extend(true, {}, this.defaults, options);

    if (options.dataAdapter == null) {
      if (options.ajax != null) {
        options.dataAdapter = AjaxData;
      } else if (options.data != null) {
        options.dataAdapter = ArrayData;
      } else {
        options.dataAdapter = SelectData;
      }

      if (options.minimumInputLength > 0) {
        options.dataAdapter = Utils.Decorate(
          options.dataAdapter,
          MinimumInputLength
        );
      }

      if (options.maximumInputLength > 0) {
        options.dataAdapter = Utils.Decorate(
          options.dataAdapter,
          MaximumInputLength
        );
      }

      if (options.maximumSelectionLength > 0) {
        options.dataAdapter = Utils.Decorate(
          options.dataAdapter,
          MaximumSelectionLength
        );
      }

      if (options.tags) {
        options.dataAdapter = Utils.Decorate(options.dataAdapter, Tags);
      }

      if (options.tokenSeparators != null || options.tokenizer != null) {
        options.dataAdapter = Utils.Decorate(
          options.dataAdapter,
          Tokenizer
        );
      }

      if (options.query != null) {
        var Query = require(options.amdBase + 'compat/query');

        options.dataAdapter = Utils.Decorate(
          options.dataAdapter,
          Query
        );
      }

      if (options.initSelection != null) {
        var InitSelection = require(options.amdBase + 'compat/initSelection');

        options.dataAdapter = Utils.Decorate(
          options.dataAdapter,
          InitSelection
        );
      }
    }

    if (options.resultsAdapter == null) {
      options.resultsAdapter = ResultsList;

      if (options.ajax != null) {
        options.resultsAdapter = Utils.Decorate(
          options.resultsAdapter,
          InfiniteScroll
        );
      }

      if (options.placeholder != null) {
        options.resultsAdapter = Utils.Decorate(
          options.resultsAdapter,
          HidePlaceholder
        );
      }

      if (options.selectOnClose) {
        options.resultsAdapter = Utils.Decorate(
          options.resultsAdapter,
          SelectOnClose
        );
      }
    }

    if (options.dropdownAdapter == null) {
      if (options.multiple) {
        options.dropdownAdapter = Dropdown;
      } else {
        var SearchableDropdown = Utils.Decorate(Dropdown, DropdownSearch);

        options.dropdownAdapter = SearchableDropdown;
      }

      if (options.minimumResultsForSearch !== 0) {
        options.dropdownAdapter = Utils.Decorate(
          options.dropdownAdapter,
          MinimumResultsForSearch
        );
      }

      if (options.closeOnSelect) {
        options.dropdownAdapter = Utils.Decorate(
          options.dropdownAdapter,
          CloseOnSelect
        );
      }

      if (
        options.dropdownCssClass != null ||
        options.dropdownCss != null ||
        options.adaptDropdownCssClass != null
      ) {
        var DropdownCSS = require(options.amdBase + 'compat/dropdownCss');

        options.dropdownAdapter = Utils.Decorate(
          options.dropdownAdapter,
          DropdownCSS
        );
      }

      options.dropdownAdapter = Utils.Decorate(
        options.dropdownAdapter,
        AttachBody
      );
    }

    if (options.selectionAdapter == null) {
      if (options.multiple) {
        options.selectionAdapter = MultipleSelection;
      } else {
        options.selectionAdapter = SingleSelection;
      }

      // Add the placeholder mixin if a placeholder was specified
      if (options.placeholder != null) {
        options.selectionAdapter = Utils.Decorate(
          options.selectionAdapter,
          Placeholder
        );
      }

      if (options.allowClear) {
        options.selectionAdapter = Utils.Decorate(
          options.selectionAdapter,
          AllowClear
        );
      }

      if (options.multiple) {
        options.selectionAdapter = Utils.Decorate(
          options.selectionAdapter,
          SelectionSearch
        );
      }

      if (
        options.containerCssClass != null ||
        options.containerCss != null ||
        options.adaptContainerCssClass != null
      ) {
        var ContainerCSS = require(options.amdBase + 'compat/containerCss');

        options.selectionAdapter = Utils.Decorate(
          options.selectionAdapter,
          ContainerCSS
        );
      }

      options.selectionAdapter = Utils.Decorate(
        options.selectionAdapter,
        EventRelay
      );
    }

    if (typeof options.language === 'string') {
      // Check if the language is specified with a region
      if (options.language.indexOf('-') > 0) {
        // Extract the region information if it is included
        var languageParts = options.language.split('-');
        var baseLanguage = languageParts[0];

        options.language = [options.language, baseLanguage];
      } else {
        options.language = [options.language];
      }
    }

    if ($.isArray(options.language)) {
      var languages = new Translation();
      options.language.push('en');

      var languageNames = options.language;

      for (var l = 0; l < languageNames.length; l++) {
        var name = languageNames[l];
        var language = {};

        try {
          // Try to load it with the original name
          language = Translation.loadPath(name);
        } catch (e) {
          try {
            // If we couldn't load it, check if it wasn't the full path
            name = this.defaults.amdLanguageBase + name;
            language = Translation.loadPath(name);
          } catch (ex) {
            // The translation could not be loaded at all. Sometimes this is
            // because of a configuration problem, other times this can be
            // because of how Select2 helps load all possible translation files.
            if (options.debug && window.console && console.warn) {
              console.warn(
                'Select2: The language file for "' + name + '" could not be ' +
                'automatically loaded. A fallback will be used instead.'
              );
            }

            continue;
          }
        }

        languages.extend(language);
      }

      options.translations = languages;
    } else {
      var baseTranslation = Translation.loadPath(
        this.defaults.amdLanguageBase + 'en'
      );
      var customTranslation = new Translation(options.language);

      customTranslation.extend(baseTranslation);

      options.translations = customTranslation;
    }

    return options;
  };

  Defaults.prototype.reset = function () {
    function stripDiacritics (text) {
      // Used 'uni range + named function' from http://jsperf.com/diacritics/18
      function match(a) {
        return DIACRITICS[a] || a;
      }

      return text.replace(/[^\u0000-\u007E]/g, match);
    }

    function matcher (params, data) {
      // Always return the object if there is nothing to compare
      if ($.trim(params.term) === '') {
        return data;
      }

      // Do a recursive check for options with children
      if (data.children && data.children.length > 0) {
        // Clone the data object if there are children
        // This is required as we modify the object to remove any non-matches
        var match = $.extend(true, {}, data);

        // Check each child of the option
        for (var c = data.children.length - 1; c >= 0; c--) {
          var child = data.children[c];

          var matches = matcher(params, child);

          // If there wasn't a match, remove the object in the array
          if (matches == null) {
            match.children.splice(c, 1);
          }
        }

        // If any children matched, return the new object
        if (match.children.length > 0) {
          return match;
        }

        // If there were no matching children, check just the plain object
        return matcher(params, match);
      }

      var original = stripDiacritics(data.text).toUpperCase();
      var term = stripDiacritics(params.term).toUpperCase();

      // Check if the text contains the term
      if (original.indexOf(term) > -1) {
        return data;
      }

      // If it doesn't contain the term, don't return anything
      return null;
    }

    this.defaults = {
      amdBase: './',
      amdLanguageBase: './i18n/',
      closeOnSelect: true,
      debug: false,
      dropdownAutoWidth: false,
      escapeMarkup: Utils.escapeMarkup,
      language: EnglishTranslation,
      matcher: matcher,
      minimumInputLength: 0,
      maximumInputLength: 0,
      maximumSelectionLength: 0,
      minimumResultsForSearch: 0,
      selectOnClose: false,
      sorter: function (data) {
        return data;
      },
      templateResult: function (result) {
        return result.text;
      },
      templateSelection: function (selection) {
        return selection.text;
      },
      theme: 'default',
      width: 'resolve'
    };
  };

  Defaults.prototype.set = function (key, value) {
    var camelKey = $.camelCase(key);

    var data = {};
    data[camelKey] = value;

    var convertedData = Utils._convertData(data);

    $.extend(this.defaults, convertedData);
  };

  var defaults = new Defaults();

  return defaults;
});

S2.define('select2/options',[
  'require',
  'jquery',
  './defaults',
  './utils'
], function (require, $, Defaults, Utils) {
  function Options (options, $element) {
    this.options = options;

    if ($element != null) {
      this.fromElement($element);
    }

    this.options = Defaults.apply(this.options);

    if ($element && $element.is('input')) {
      var InputCompat = require(this.get('amdBase') + 'compat/inputData');

      this.options.dataAdapter = Utils.Decorate(
        this.options.dataAdapter,
        InputCompat
      );
    }
  }

  Options.prototype.fromElement = function ($e) {
    var excludedData = ['select2'];

    if (this.options.multiple == null) {
      this.options.multiple = $e.prop('multiple');
    }

    if (this.options.disabled == null) {
      this.options.disabled = $e.prop('disabled');
    }

    if (this.options.language == null) {
      if ($e.prop('lang')) {
        this.options.language = $e.prop('lang').toLowerCase();
      } else if ($e.closest('[lang]').prop('lang')) {
        this.options.language = $e.closest('[lang]').prop('lang');
      }
    }

    if (this.options.dir == null) {
      if ($e.prop('dir')) {
        this.options.dir = $e.prop('dir');
      } else if ($e.closest('[dir]').prop('dir')) {
        this.options.dir = $e.closest('[dir]').prop('dir');
      } else {
        this.options.dir = 'ltr';
      }
    }

    $e.prop('disabled', this.options.disabled);
    $e.prop('multiple', this.options.multiple);

    if ($e.data('select2Tags')) {
      if (this.options.debug && window.console && console.warn) {
        console.warn(
          'Select2: The `data-select2-tags` attribute has been changed to ' +
          'use the `data-data` and `data-tags="true"` attributes and will be ' +
          'removed in future versions of Select2.'
        );
      }

      $e.data('data', $e.data('select2Tags'));
      $e.data('tags', true);
    }

    if ($e.data('ajaxUrl')) {
      if (this.options.debug && window.console && console.warn) {
        console.warn(
          'Select2: The `data-ajax-url` attribute has been changed to ' +
          '`data-ajax--url` and support for the old attribute will be removed' +
          ' in future versions of Select2.'
        );
      }

      $e.attr('ajax--url', $e.data('ajaxUrl'));
      $e.data('ajax--url', $e.data('ajaxUrl'));
    }

    var dataset = {};

    // Prefer the element's `dataset` attribute if it exists
    // jQuery 1.x does not correctly handle data attributes with multiple dashes
    if ($.fn.jquery && $.fn.jquery.substr(0, 2) == '1.' && $e[0].dataset) {
      dataset = $.extend(true, {}, $e[0].dataset, $e.data());
    } else {
      dataset = $e.data();
    }

    var data = $.extend(true, {}, dataset);

    data = Utils._convertData(data);

    for (var key in data) {
      if ($.inArray(key, excludedData) > -1) {
        continue;
      }

      if ($.isPlainObject(this.options[key])) {
        $.extend(this.options[key], data[key]);
      } else {
        this.options[key] = data[key];
      }
    }

    return this;
  };

  Options.prototype.get = function (key) {
    return this.options[key];
  };

  Options.prototype.set = function (key, val) {
    this.options[key] = val;
  };

  return Options;
});

S2.define('select2/core',[
  'jquery',
  './options',
  './utils',
  './keys'
], function ($, Options, Utils, KEYS) {
  var Select2 = function ($element, options) {
    if ($element.data('select2') != null) {
      $element.data('select2').destroy();
    }

    this.$element = $element;

    this.id = this._generateId($element);

    options = options || {};

    this.options = new Options(options, $element);

    Select2.__super__.constructor.call(this);

    // Set up the tabindex

    var tabindex = $element.attr('tabindex') || 0;
    $element.data('old-tabindex', tabindex);
    $element.attr('tabindex', '-1');

    // Set up containers and adapters

    var DataAdapter = this.options.get('dataAdapter');
    this.dataAdapter = new DataAdapter($element, this.options);

    var $container = this.render();

    this._placeContainer($container);

    var SelectionAdapter = this.options.get('selectionAdapter');
    this.selection = new SelectionAdapter($element, this.options);
    this.$selection = this.selection.render();

    this.selection.position(this.$selection, $container);

    var DropdownAdapter = this.options.get('dropdownAdapter');
    this.dropdown = new DropdownAdapter($element, this.options);
    this.$dropdown = this.dropdown.render();

    this.dropdown.position(this.$dropdown, $container);

    var ResultsAdapter = this.options.get('resultsAdapter');
    this.results = new ResultsAdapter($element, this.options, this.dataAdapter);
    this.$results = this.results.render();

    this.results.position(this.$results, this.$dropdown);

    // Bind events

    var self = this;

    // Bind the container to all of the adapters
    this._bindAdapters();

    // Register any DOM event handlers
    this._registerDomEvents();

    // Register any internal event handlers
    this._registerDataEvents();
    this._registerSelectionEvents();
    this._registerDropdownEvents();
    this._registerResultsEvents();
    this._registerEvents();

    // Set the initial state
    this.dataAdapter.current(function (initialData) {
      self.trigger('selection:update', {
        data: initialData
      });
    });

    // Hide the original select
    $element.addClass('select2-hidden-accessible');
    $element.attr('aria-hidden', 'true');

    // Synchronize any monitored attributes
    this._syncAttributes();

    $element.data('select2', this);
  };

  Utils.Extend(Select2, Utils.Observable);

  Select2.prototype._generateId = function ($element) {
    var id = '';

    if ($element.attr('id') != null) {
      id = $element.attr('id');
    } else if ($element.attr('name') != null) {
      id = $element.attr('name') + '-' + Utils.generateChars(2);
    } else {
      id = Utils.generateChars(4);
    }

    id = id.replace(/(:|\.|\[|\]|,)/g, '');
    id = 'select2-' + id;

    return id;
  };

  Select2.prototype._placeContainer = function ($container) {
    $container.insertAfter(this.$element);

    var width = this._resolveWidth(this.$element, this.options.get('width'));

    if (width != null) {
      $container.css('width', width);
    }
  };

  Select2.prototype._resolveWidth = function ($element, method) {
    var WIDTH = /^width:(([-+]?([0-9]*\.)?[0-9]+)(px|em|ex|%|in|cm|mm|pt|pc))/i;

    if (method == 'resolve') {
      var styleWidth = this._resolveWidth($element, 'style');

      if (styleWidth != null) {
        return styleWidth;
      }

      return this._resolveWidth($element, 'element');
    }

    if (method == 'element') {
      var elementWidth = $element.outerWidth(false);

      if (elementWidth <= 0) {
        return 'auto';
      }

      return elementWidth + 'px';
    }

    if (method == 'style') {
      var style = $element.attr('style');

      if (typeof(style) !== 'string') {
        return null;
      }

      var attrs = style.split(';');

      for (var i = 0, l = attrs.length; i < l; i = i + 1) {
        var attr = attrs[i].replace(/\s/g, '');
        var matches = attr.match(WIDTH);

        if (matches !== null && matches.length >= 1) {
          return matches[1];
        }
      }

      return null;
    }

    return method;
  };

  Select2.prototype._bindAdapters = function () {
    this.dataAdapter.bind(this, this.$container);
    this.selection.bind(this, this.$container);

    this.dropdown.bind(this, this.$container);
    this.results.bind(this, this.$container);
  };

  Select2.prototype._registerDomEvents = function () {
    var self = this;

    this.$element.on('change.select2', function () {
      self.dataAdapter.current(function (data) {
        self.trigger('selection:update', {
          data: data
        });
      });
    });

    this.$element.on('focus.select2', function (evt) {
      self.trigger('focus', evt);
    });

    this._syncA = Utils.bind(this._syncAttributes, this);
    this._syncS = Utils.bind(this._syncSubtree, this);

    if (this.$element[0].attachEvent) {
      this.$element[0].attachEvent('onpropertychange', this._syncA);
    }

    var observer = window.MutationObserver ||
      window.WebKitMutationObserver ||
      window.MozMutationObserver
    ;

    if (observer != null) {
      this._observer = new observer(function (mutations) {
        $.each(mutations, self._syncA);
        $.each(mutations, self._syncS);
      });
      this._observer.observe(this.$element[0], {
        attributes: true,
        childList: true,
        subtree: false
      });
    } else if (this.$element[0].addEventListener) {
      this.$element[0].addEventListener(
        'DOMAttrModified',
        self._syncA,
        false
      );
      this.$element[0].addEventListener(
        'DOMNodeInserted',
        self._syncS,
        false
      );
      this.$element[0].addEventListener(
        'DOMNodeRemoved',
        self._syncS,
        false
      );
    }
  };

  Select2.prototype._registerDataEvents = function () {
    var self = this;

    this.dataAdapter.on('*', function (name, params) {
      self.trigger(name, params);
    });
  };

  Select2.prototype._registerSelectionEvents = function () {
    var self = this;
    var nonRelayEvents = ['toggle', 'focus'];

    this.selection.on('toggle', function () {
      self.toggleDropdown();
    });

    this.selection.on('focus', function (params) {
      self.focus(params);
    });

    this.selection.on('*', function (name, params) {
      if ($.inArray(name, nonRelayEvents) !== -1) {
        return;
      }

      self.trigger(name, params);
    });
  };

  Select2.prototype._registerDropdownEvents = function () {
    var self = this;

    this.dropdown.on('*', function (name, params) {
      self.trigger(name, params);
    });
  };

  Select2.prototype._registerResultsEvents = function () {
    var self = this;

    this.results.on('*', function (name, params) {
      self.trigger(name, params);
    });
  };

  Select2.prototype._registerEvents = function () {
    var self = this;

    this.on('open', function () {
      self.$container.addClass('select2-container--open');
    });

    this.on('close', function () {
      self.$container.removeClass('select2-container--open');
    });

    this.on('enable', function () {
      self.$container.removeClass('select2-container--disabled');
    });

    this.on('disable', function () {
      self.$container.addClass('select2-container--disabled');
    });

    this.on('blur', function () {
      self.$container.removeClass('select2-container--focus');
    });

    this.on('query', function (params) {
      if (!self.isOpen()) {
        self.trigger('open', {});
      }

      this.dataAdapter.query(params, function (data) {
        self.trigger('results:all', {
          data: data,
          query: params
        });
      });
    });

    this.on('query:append', function (params) {
      this.dataAdapter.query(params, function (data) {
        self.trigger('results:append', {
          data: data,
          query: params
        });
      });
    });

    this.on('open', function(){
      // Focus on the active element when opening dropdown.
      // Needs 1 ms delay because of other 1 ms setTimeouts when rendering.
      setTimeout(function(){
        self.focusOnActiveElement();
      }, 1);
    });

    $(document).on('keydown', function (evt) {
      var key = evt.which;
      if (self.isOpen()) {
        if (key === KEYS.ESC || (key === KEYS.UP && evt.altKey)) {
          self.close();

          evt.preventDefault();
        } else if (key === KEYS.ENTER || key === KEYS.TAB) {
          self.trigger('results:select', {});

          evt.preventDefault();
        } else if ((key === KEYS.SPACE && evt.ctrlKey)) {
          self.trigger('results:toggle', {});

          evt.preventDefault();
        } else if (key === KEYS.UP) {
          self.trigger('results:previous', {});

          evt.preventDefault();
        } else if (key === KEYS.DOWN) {
          self.trigger('results:next', {});

          evt.preventDefault();
        }

        var $searchField = self.$dropdown.find('.select2-search__field');
        if (! $searchField.length) {
          $searchField = self.$container.find('.select2-search__field');
        }

        // Move the focus to the selected element on keyboard navigation.
        // Required for screen readers to work properly.
        if (key === KEYS.DOWN || key === KEYS.UP) {
            self.focusOnActiveElement();
        } else {
          // Focus on the search if user starts typing.
          $searchField.focus();
          // Focus back to active selection when finished typing.
          // Small delay so typed character can be read by screen reader.
          setTimeout(function(){
              self.focusOnActiveElement();
          }, 1000);
        }
      } else if (self.hasFocus()) {
        if (key === KEYS.ENTER || key === KEYS.SPACE ||
            key === KEYS.DOWN) {
          self.open();
          evt.preventDefault();
        }
      }
    });
  };

  Select2.prototype.focusOnActiveElement = function () {
    // Don't mess with the focus on touchscreens because it causes havoc with on-screen keyboards.
    if (this.isOpen() && ! Utils.isTouchscreen()) {
      this.$results.find('li.select2-results__option--highlighted').focus();
    }
  };

  Select2.prototype._syncAttributes = function () {
    this.options.set('disabled', this.$element.prop('disabled'));

    if (this.options.get('disabled')) {
      if (this.isOpen()) {
        this.close();
      }

      this.trigger('disable', {});
    } else {
      this.trigger('enable', {});
    }
  };

  Select2.prototype._syncSubtree = function (evt, mutations) {
    var changed = false;
    var self = this;

    // Ignore any mutation events raised for elements that aren't options or
    // optgroups. This handles the case when the select element is destroyed
    if (
      evt && evt.target && (
        evt.target.nodeName !== 'OPTION' && evt.target.nodeName !== 'OPTGROUP'
      )
    ) {
      return;
    }

    if (!mutations) {
      // If mutation events aren't supported, then we can only assume that the
      // change affected the selections
      changed = true;
    } else if (mutations.addedNodes && mutations.addedNodes.length > 0) {
      for (var n = 0; n < mutations.addedNodes.length; n++) {
        var node = mutations.addedNodes[n];

        if (node.selected) {
          changed = true;
        }
      }
    } else if (mutations.removedNodes && mutations.removedNodes.length > 0) {
      changed = true;
    }

    // Only re-pull the data if we think there is a change
    if (changed) {
      this.dataAdapter.current(function (currentData) {
        self.trigger('selection:update', {
          data: currentData
        });
      });
    }
  };

  /**
   * Override the trigger method to automatically trigger pre-events when
   * there are events that can be prevented.
   */
  Select2.prototype.trigger = function (name, args) {
    var actualTrigger = Select2.__super__.trigger;
    var preTriggerMap = {
      'open': 'opening',
      'close': 'closing',
      'select': 'selecting',
      'unselect': 'unselecting'
    };

    if (args === undefined) {
      args = {};
    }

    if (name in preTriggerMap) {
      var preTriggerName = preTriggerMap[name];
      var preTriggerArgs = {
        prevented: false,
        name: name,
        args: args
      };

      actualTrigger.call(this, preTriggerName, preTriggerArgs);

      if (preTriggerArgs.prevented) {
        args.prevented = true;

        return;
      }
    }

    actualTrigger.call(this, name, args);
  };

  Select2.prototype.toggleDropdown = function () {
    if (this.options.get('disabled')) {
      return;
    }

    if (this.isOpen()) {
      this.close();
    } else {
      this.open();
    }
  };

  Select2.prototype.open = function () {
    if (this.isOpen()) {
      return;
    }

    this.trigger('query', {});
  };

  Select2.prototype.close = function () {
    if (!this.isOpen()) {
      return;
    }

    this.trigger('close', {});
  };

  Select2.prototype.isOpen = function () {
    return this.$container.hasClass('select2-container--open');
  };

  Select2.prototype.hasFocus = function () {
    return this.$container.hasClass('select2-container--focus');
  };

  Select2.prototype.focus = function (data) {
    // No need to re-trigger focus events if we are already focused
    if (this.hasFocus()) {
      return;
    }

    this.$container.addClass('select2-container--focus');
    this.trigger('focus', {});
  };

  Select2.prototype.enable = function (args) {
    if (this.options.get('debug') && window.console && console.warn) {
      console.warn(
        'Select2: The `select2("enable")` method has been deprecated and will' +
        ' be removed in later Select2 versions. Use $element.prop("disabled")' +
        ' instead.'
      );
    }

    if (args == null || args.length === 0) {
      args = [true];
    }

    var disabled = !args[0];

    this.$element.prop('disabled', disabled);
  };

  Select2.prototype.data = function () {
    if (this.options.get('debug') &&
        arguments.length > 0 && window.console && console.warn) {
      console.warn(
        'Select2: Data can no longer be set using `select2("data")`. You ' +
        'should consider setting the value instead using `$element.val()`.'
      );
    }

    var data = [];

    this.dataAdapter.current(function (currentData) {
      data = currentData;
    });

    return data;
  };

  Select2.prototype.val = function (args) {
    if (this.options.get('debug') && window.console && console.warn) {
      console.warn(
        'Select2: The `select2("val")` method has been deprecated and will be' +
        ' removed in later Select2 versions. Use $element.val() instead.'
      );
    }

    if (args == null || args.length === 0) {
      return this.$element.val();
    }

    var newVal = args[0];

    if ($.isArray(newVal)) {
      newVal = $.map(newVal, function (obj) {
        return obj.toString();
      });
    }

    this.$element.val(newVal).trigger('change');
  };

  Select2.prototype.destroy = function () {
    this.$container.remove();

    if (this.$element[0].detachEvent) {
      this.$element[0].detachEvent('onpropertychange', this._syncA);
    }

    if (this._observer != null) {
      this._observer.disconnect();
      this._observer = null;
    } else if (this.$element[0].removeEventListener) {
      this.$element[0]
        .removeEventListener('DOMAttrModified', this._syncA, false);
      this.$element[0]
        .removeEventListener('DOMNodeInserted', this._syncS, false);
      this.$element[0]
        .removeEventListener('DOMNodeRemoved', this._syncS, false);
    }

    this._syncA = null;
    this._syncS = null;

    this.$element.off('.select2');
    this.$element.attr('tabindex', this.$element.data('old-tabindex'));

    this.$element.removeClass('select2-hidden-accessible');
    this.$element.attr('aria-hidden', 'false');
    this.$element.removeData('select2');

    this.dataAdapter.destroy();
    this.selection.destroy();
    this.dropdown.destroy();
    this.results.destroy();

    this.dataAdapter = null;
    this.selection = null;
    this.dropdown = null;
    this.results = null;
  };

  Select2.prototype.render = function () {
    var $container = $(
      '<span class="select2 select2-container">' +
        '<span class="selection"></span>' +
        '<span class="dropdown-wrapper" aria-hidden="true"></span>' +
      '</span>'
    );

    $container.attr('dir', this.options.get('dir'));

    this.$container = $container;

    this.$container.addClass('select2-container--' + this.options.get('theme'));

    $container.data('element', this.$element);

    return $container;
  };

  return Select2;
});

S2.define('select2/compat/utils',[
  'jquery'
], function ($) {
  function syncCssClasses ($dest, $src, adapter) {
    var classes, replacements = [], adapted;

    classes = $.trim($dest.attr('class'));

    if (classes) {
      classes = '' + classes; // for IE which returns object

      $(classes.split(/\s+/)).each(function () {
        // Save all Select2 classes
        if (this.indexOf('select2-') === 0) {
          replacements.push(this);
        }
      });
    }

    classes = $.trim($src.attr('class'));

    if (classes) {
      classes = '' + classes; // for IE which returns object

      $(classes.split(/\s+/)).each(function () {
        // Only adapt non-Select2 classes
        if (this.indexOf('select2-') !== 0) {
          adapted = adapter(this);

          if (adapted != null) {
            replacements.push(adapted);
          }
        }
      });
    }

    $dest.attr('class', replacements.join(' '));
  }

  return {
    syncCssClasses: syncCssClasses
  };
});

S2.define('select2/compat/containerCss',[
  'jquery',
  './utils'
], function ($, CompatUtils) {
  // No-op CSS adapter that discards all classes by default
  function _containerAdapter (clazz) {
    return null;
  }

  function ContainerCSS () { }

  ContainerCSS.prototype.render = function (decorated) {
    var $container = decorated.call(this);

    var containerCssClass = this.options.get('containerCssClass') || '';

    if ($.isFunction(containerCssClass)) {
      containerCssClass = containerCssClass(this.$element);
    }

    var containerCssAdapter = this.options.get('adaptContainerCssClass');
    containerCssAdapter = containerCssAdapter || _containerAdapter;

    if (containerCssClass.indexOf(':all:') !== -1) {
      containerCssClass = containerCssClass.replace(':all:', '');

      var _cssAdapter = containerCssAdapter;

      containerCssAdapter = function (clazz) {
        var adapted = _cssAdapter(clazz);

        if (adapted != null) {
          // Append the old one along with the adapted one
          return adapted + ' ' + clazz;
        }

        return clazz;
      };
    }

    var containerCss = this.options.get('containerCss') || {};

    if ($.isFunction(containerCss)) {
      containerCss = containerCss(this.$element);
    }

    CompatUtils.syncCssClasses($container, this.$element, containerCssAdapter);

    $container.css(containerCss);
    $container.addClass(containerCssClass);

    return $container;
  };

  return ContainerCSS;
});

S2.define('select2/compat/dropdownCss',[
  'jquery',
  './utils'
], function ($, CompatUtils) {
  // No-op CSS adapter that discards all classes by default
  function _dropdownAdapter (clazz) {
    return null;
  }

  function DropdownCSS () { }

  DropdownCSS.prototype.render = function (decorated) {
    var $dropdown = decorated.call(this);

    var dropdownCssClass = this.options.get('dropdownCssClass') || '';

    if ($.isFunction(dropdownCssClass)) {
      dropdownCssClass = dropdownCssClass(this.$element);
    }

    var dropdownCssAdapter = this.options.get('adaptDropdownCssClass');
    dropdownCssAdapter = dropdownCssAdapter || _dropdownAdapter;

    if (dropdownCssClass.indexOf(':all:') !== -1) {
      dropdownCssClass = dropdownCssClass.replace(':all:', '');

      var _cssAdapter = dropdownCssAdapter;

      dropdownCssAdapter = function (clazz) {
        var adapted = _cssAdapter(clazz);

        if (adapted != null) {
          // Append the old one along with the adapted one
          return adapted + ' ' + clazz;
        }

        return clazz;
      };
    }

    var dropdownCss = this.options.get('dropdownCss') || {};

    if ($.isFunction(dropdownCss)) {
      dropdownCss = dropdownCss(this.$element);
    }

    CompatUtils.syncCssClasses($dropdown, this.$element, dropdownCssAdapter);

    $dropdown.css(dropdownCss);
    $dropdown.addClass(dropdownCssClass);

    return $dropdown;
  };

  return DropdownCSS;
});

S2.define('select2/compat/initSelection',[
  'jquery'
], function ($) {
  function InitSelection (decorated, $element, options) {
    if (options.get('debug') && window.console && console.warn) {
      console.warn(
        'Select2: The `initSelection` option has been deprecated in favor' +
        ' of a custom data adapter that overrides the `current` method. ' +
        'This method is now called multiple times instead of a single ' +
        'time when the instance is initialized. Support will be removed ' +
        'for the `initSelection` option in future versions of Select2'
      );
    }

    this.initSelection = options.get('initSelection');
    this._isInitialized = false;

    decorated.call(this, $element, options);
  }

  InitSelection.prototype.current = function (decorated, callback) {
    var self = this;

    if (this._isInitialized) {
      decorated.call(this, callback);

      return;
    }

    this.initSelection.call(null, this.$element, function (data) {
      self._isInitialized = true;

      if (!$.isArray(data)) {
        data = [data];
      }

      callback(data);
    });
  };

  return InitSelection;
});

S2.define('select2/compat/inputData',[
  'jquery'
], function ($) {
  function InputData (decorated, $element, options) {
    this._currentData = [];
    this._valueSeparator = options.get('valueSeparator') || ',';

    if ($element.prop('type') === 'hidden') {
      if (options.get('debug') && console && console.warn) {
        console.warn(
          'Select2: Using a hidden input with Select2 is no longer ' +
          'supported and may stop working in the future. It is recommended ' +
          'to use a `<select>` element instead.'
        );
      }
    }

    decorated.call(this, $element, options);
  }

  InputData.prototype.current = function (_, callback) {
    function getSelected (data, selectedIds) {
      var selected = [];

      if (data.selected || $.inArray(data.id, selectedIds) !== -1) {
        data.selected = true;
        selected.push(data);
      } else {
        data.selected = false;
      }

      if (data.children) {
        selected.push.apply(selected, getSelected(data.children, selectedIds));
      }

      return selected;
    }

    var selected = [];

    for (var d = 0; d < this._currentData.length; d++) {
      var data = this._currentData[d];

      selected.push.apply(
        selected,
        getSelected(
          data,
          this.$element.val().split(
            this._valueSeparator
          )
        )
      );
    }

    callback(selected);
  };

  InputData.prototype.select = function (_, data) {
    if (!this.options.get('multiple')) {
      this.current(function (allData) {
        $.map(allData, function (data) {
          data.selected = false;
        });
      });

      this.$element.val(data.id);
      this.$element.trigger('change');
    } else {
      var value = this.$element.val();
      value += this._valueSeparator + data.id;

      this.$element.val(value);
      this.$element.trigger('change');
    }
  };

  InputData.prototype.unselect = function (_, data) {
    var self = this;

    data.selected = false;

    this.current(function (allData) {
      var values = [];

      for (var d = 0; d < allData.length; d++) {
        var item = allData[d];

        if (data.id == item.id) {
          continue;
        }

        values.push(item.id);
      }

      self.$element.val(values.join(self._valueSeparator));
      self.$element.trigger('change');
    });
  };

  InputData.prototype.query = function (_, params, callback) {
    var results = [];

    for (var d = 0; d < this._currentData.length; d++) {
      var data = this._currentData[d];

      var matches = this.matches(params, data);

      if (matches !== null) {
        results.push(matches);
      }
    }

    callback({
      results: results
    });
  };

  InputData.prototype.addOptions = function (_, $options) {
    var options = $.map($options, function ($option) {
      return $.data($option[0], 'data');
    });

    this._currentData.push.apply(this._currentData, options);
  };

  return InputData;
});

S2.define('select2/compat/matcher',[
  'jquery'
], function ($) {
  function oldMatcher (matcher) {
    function wrappedMatcher (params, data) {
      var match = $.extend(true, {}, data);

      if (params.term == null || $.trim(params.term) === '') {
        return match;
      }

      if (data.children) {
        for (var c = data.children.length - 1; c >= 0; c--) {
          var child = data.children[c];

          // Check if the child object matches
          // The old matcher returned a boolean true or false
          var doesMatch = matcher(params.term, child.text, child);

          // If the child didn't match, pop it off
          if (!doesMatch) {
            match.children.splice(c, 1);
          }
        }

        if (match.children.length > 0) {
          return match;
        }
      }

      if (matcher(params.term, data.text, data)) {
        return match;
      }

      return null;
    }

    return wrappedMatcher;
  }

  return oldMatcher;
});

S2.define('select2/compat/query',[

], function () {
  function Query (decorated, $element, options) {
    if (options.get('debug') && window.console && console.warn) {
      console.warn(
        'Select2: The `query` option has been deprecated in favor of a ' +
        'custom data adapter that overrides the `query` method. Support ' +
        'will be removed for the `query` option in future versions of ' +
        'Select2.'
      );
    }

    decorated.call(this, $element, options);
  }

  Query.prototype.query = function (_, params, callback) {
    params.callback = callback;

    var query = this.options.get('query');

    query.call(null, params);
  };

  return Query;
});

S2.define('select2/dropdown/attachContainer',[

], function () {
  function AttachContainer (decorated, $element, options) {
    decorated.call(this, $element, options);
  }

  AttachContainer.prototype.position =
    function (decorated, $dropdown, $container) {
    var $dropdownContainer = $container.find('.dropdown-wrapper');
    $dropdownContainer.append($dropdown);

    $dropdown.addClass('select2-dropdown--below');
    $container.addClass('select2-container--below');
  };

  return AttachContainer;
});

S2.define('select2/dropdown/stopPropagation',[

], function () {
  function StopPropagation () { }

  StopPropagation.prototype.bind = function (decorated, container, $container) {
    decorated.call(this, container, $container);

    var stoppedEvents = [
    'blur',
    'change',
    'click',
    'dblclick',
    'focus',
    'focusin',
    'focusout',
    'input',
    'keydown',
    'keyup',
    'keypress',
    'mousedown',
    'mouseenter',
    'mouseleave',
    'mousemove',
    'mouseover',
    'mouseup',
    'search',
    'touchend',
    'touchstart'
    ];

    this.$dropdown.on(stoppedEvents.join(' '), function (evt) {
      evt.stopPropagation();
    });
  };

  return StopPropagation;
});

S2.define('select2/selection/stopPropagation',[

], function () {
  function StopPropagation () { }

  StopPropagation.prototype.bind = function (decorated, container, $container) {
    decorated.call(this, container, $container);

    var stoppedEvents = [
      'blur',
      'change',
      'click',
      'dblclick',
      'focus',
      'focusin',
      'focusout',
      'input',
      'keydown',
      'keyup',
      'keypress',
      'mousedown',
      'mouseenter',
      'mouseleave',
      'mousemove',
      'mouseover',
      'mouseup',
      'search',
      'touchend',
      'touchstart'
    ];

    this.$selection.on(stoppedEvents.join(' '), function (evt) {
      evt.stopPropagation();
    });
  };

  return StopPropagation;
});

/*!
 * jQuery Mousewheel 3.1.13
 *
 * Copyright jQuery Foundation and other contributors
 * Released under the MIT license
 * http://jquery.org/license
 */

(function (factory) {
    if ( typeof S2.define === 'function' && S2.define.amd ) {
        // AMD. Register as an anonymous module.
        S2.define('jquery-mousewheel',['jquery'], factory);
    } else if (typeof exports === 'object') {
        // Node/CommonJS style for Browserify
        module.exports = factory;
    } else {
        // Browser globals
        factory(jQuery);
    }
}(function ($) {

    var toFix  = ['wheel', 'mousewheel', 'DOMMouseScroll', 'MozMousePixelScroll'],
        toBind = ( 'onwheel' in document || document.documentMode >= 9 ) ?
                    ['wheel'] : ['mousewheel', 'DomMouseScroll', 'MozMousePixelScroll'],
        slice  = Array.prototype.slice,
        nullLowestDeltaTimeout, lowestDelta;

    if ( $.event.fixHooks ) {
        for ( var i = toFix.length; i; ) {
            $.event.fixHooks[ toFix[--i] ] = $.event.mouseHooks;
        }
    }

    var special = $.event.special.mousewheel = {
        version: '3.1.12',

        setup: function() {
            if ( this.addEventListener ) {
                for ( var i = toBind.length; i; ) {
                    this.addEventListener( toBind[--i], handler, false );
                }
            } else {
                this.onmousewheel = handler;
            }
            // Store the line height and page height for this particular element
            $.data(this, 'mousewheel-line-height', special.getLineHeight(this));
            $.data(this, 'mousewheel-page-height', special.getPageHeight(this));
        },

        teardown: function() {
            if ( this.removeEventListener ) {
                for ( var i = toBind.length; i; ) {
                    this.removeEventListener( toBind[--i], handler, false );
                }
            } else {
                this.onmousewheel = null;
            }
            // Clean up the data we added to the element
            $.removeData(this, 'mousewheel-line-height');
            $.removeData(this, 'mousewheel-page-height');
        },

        getLineHeight: function(elem) {
            var $elem = $(elem),
                $parent = $elem['offsetParent' in $.fn ? 'offsetParent' : 'parent']();
            if (!$parent.length) {
                $parent = $('body');
            }
            return parseInt($parent.css('fontSize'), 10) || parseInt($elem.css('fontSize'), 10) || 16;
        },

        getPageHeight: function(elem) {
            return $(elem).height();
        },

        settings: {
            adjustOldDeltas: true, // see shouldAdjustOldDeltas() below
            normalizeOffset: true  // calls getBoundingClientRect for each event
        }
    };

    $.fn.extend({
        mousewheel: function(fn) {
            return fn ? this.bind('mousewheel', fn) : this.trigger('mousewheel');
        },

        unmousewheel: function(fn) {
            return this.unbind('mousewheel', fn);
        }
    });


    function handler(event) {
        var orgEvent   = event || window.event,
            args       = slice.call(arguments, 1),
            delta      = 0,
            deltaX     = 0,
            deltaY     = 0,
            absDelta   = 0,
            offsetX    = 0,
            offsetY    = 0;
        event = $.event.fix(orgEvent);
        event.type = 'mousewheel';

        // Old school scrollwheel delta
        if ( 'detail'      in orgEvent ) { deltaY = orgEvent.detail * -1;      }
        if ( 'wheelDelta'  in orgEvent ) { deltaY = orgEvent.wheelDelta;       }
        if ( 'wheelDeltaY' in orgEvent ) { deltaY = orgEvent.wheelDeltaY;      }
        if ( 'wheelDeltaX' in orgEvent ) { deltaX = orgEvent.wheelDeltaX * -1; }

        // Firefox < 17 horizontal scrolling related to DOMMouseScroll event
        if ( 'axis' in orgEvent && orgEvent.axis === orgEvent.HORIZONTAL_AXIS ) {
            deltaX = deltaY * -1;
            deltaY = 0;
        }

        // Set delta to be deltaY or deltaX if deltaY is 0 for backwards compatabilitiy
        delta = deltaY === 0 ? deltaX : deltaY;

        // New school wheel delta (wheel event)
        if ( 'deltaY' in orgEvent ) {
            deltaY = orgEvent.deltaY * -1;
            delta  = deltaY;
        }
        if ( 'deltaX' in orgEvent ) {
            deltaX = orgEvent.deltaX;
            if ( deltaY === 0 ) { delta  = deltaX * -1; }
        }

        // No change actually happened, no reason to go any further
        if ( deltaY === 0 && deltaX === 0 ) { return; }

        // Need to convert lines and pages to pixels if we aren't already in pixels
        // There are three delta modes:
        //   * deltaMode 0 is by pixels, nothing to do
        //   * deltaMode 1 is by lines
        //   * deltaMode 2 is by pages
        if ( orgEvent.deltaMode === 1 ) {
            var lineHeight = $.data(this, 'mousewheel-line-height');
            delta  *= lineHeight;
            deltaY *= lineHeight;
            deltaX *= lineHeight;
        } else if ( orgEvent.deltaMode === 2 ) {
            var pageHeight = $.data(this, 'mousewheel-page-height');
            delta  *= pageHeight;
            deltaY *= pageHeight;
            deltaX *= pageHeight;
        }

        // Store lowest absolute delta to normalize the delta values
        absDelta = Math.max( Math.abs(deltaY), Math.abs(deltaX) );

        if ( !lowestDelta || absDelta < lowestDelta ) {
            lowestDelta = absDelta;

            // Adjust older deltas if necessary
            if ( shouldAdjustOldDeltas(orgEvent, absDelta) ) {
                lowestDelta /= 40;
            }
        }

        // Adjust older deltas if necessary
        if ( shouldAdjustOldDeltas(orgEvent, absDelta) ) {
            // Divide all the things by 40!
            delta  /= 40;
            deltaX /= 40;
            deltaY /= 40;
        }

        // Get a whole, normalized value for the deltas
        delta  = Math[ delta  >= 1 ? 'floor' : 'ceil' ](delta  / lowestDelta);
        deltaX = Math[ deltaX >= 1 ? 'floor' : 'ceil' ](deltaX / lowestDelta);
        deltaY = Math[ deltaY >= 1 ? 'floor' : 'ceil' ](deltaY / lowestDelta);

        // Normalise offsetX and offsetY properties
        if ( special.settings.normalizeOffset && this.getBoundingClientRect ) {
            var boundingRect = this.getBoundingClientRect();
            offsetX = event.clientX - boundingRect.left;
            offsetY = event.clientY - boundingRect.top;
        }

        // Add information to the event object
        event.deltaX = deltaX;
        event.deltaY = deltaY;
        event.deltaFactor = lowestDelta;
        event.offsetX = offsetX;
        event.offsetY = offsetY;
        // Go ahead and set deltaMode to 0 since we converted to pixels
        // Although this is a little odd since we overwrite the deltaX/Y
        // properties with normalized deltas.
        event.deltaMode = 0;

        // Add event and delta to the front of the arguments
        args.unshift(event, delta, deltaX, deltaY);

        // Clearout lowestDelta after sometime to better
        // handle multiple device types that give different
        // a different lowestDelta
        // Ex: trackpad = 3 and mouse wheel = 120
        if (nullLowestDeltaTimeout) { clearTimeout(nullLowestDeltaTimeout); }
        nullLowestDeltaTimeout = setTimeout(nullLowestDelta, 200);

        return ($.event.dispatch || $.event.handle).apply(this, args);
    }

    function nullLowestDelta() {
        lowestDelta = null;
    }

    function shouldAdjustOldDeltas(orgEvent, absDelta) {
        // If this is an older event and the delta is divisable by 120,
        // then we are assuming that the browser is treating this as an
        // older mouse wheel event and that we should divide the deltas
        // by 40 to try and get a more usable deltaFactor.
        // Side note, this actually impacts the reported scroll distance
        // in older browsers and can cause scrolling to be slower than native.
        // Turn this off by setting $.event.special.mousewheel.settings.adjustOldDeltas to false.
        return special.settings.adjustOldDeltas && orgEvent.type === 'mousewheel' && absDelta % 120 === 0;
    }

}));

S2.define('jquery.select2',[
  'jquery',
  'jquery-mousewheel',

  './select2/core',
  './select2/defaults'
], function ($, _, Select2, Defaults) {
  if ($.fn.selectWoo == null) {
    // All methods that should return the element
    var thisMethods = ['open', 'close', 'destroy'];

    $.fn.selectWoo = function (options) {
      options = options || {};

      if (typeof options === 'object') {
        this.each(function () {
          var instanceOptions = $.extend(true, {}, options);

          var instance = new Select2($(this), instanceOptions);
        });

        return this;
      } else if (typeof options === 'string') {
        var ret;
        var args = Array.prototype.slice.call(arguments, 1);

        this.each(function () {
          var instance = $(this).data('select2');

          if (instance == null && window.console && console.error) {
            console.error(
              'The select2(\'' + options + '\') method was called on an ' +
              'element that is not using Select2.'
            );
          }

          ret = instance[options].apply(instance, args);
        });

        // Check if we should be returning `this`
        if ($.inArray(options, thisMethods) > -1) {
          return this;
        }

        return ret;
      } else {
        throw new Error('Invalid arguments for Select2: ' + options);
      }
    };
  }

  if ($.fn.select2 != null && $.fn.select2.defaults != null) {
    $.fn.selectWoo.defaults = $.fn.select2.defaults;
  }

  if ($.fn.selectWoo.defaults == null) {
    $.fn.selectWoo.defaults = Defaults;
  }

  // Also register selectWoo under select2 if select2 is not already present.
  $.fn.select2 = $.fn.select2 || $.fn.selectWoo;

  return Select2;
});

  // Return the AMD loader configuration so it can be used outside of this file
  return {
    define: S2.define,
    require: S2.require
  };
}());

  // Autoload the jQuery bindings
  // We know that all of the modules exist above this, so we're safe
  var select2 = S2.require('jquery.select2');

  // Hold the AMD module references on the jQuery function that was just loaded
  // This allows Select2 to use the internal loader outside of this file, such
  // as in the language files.
  jQuery.fn.select2.amd = S2;
  jQuery.fn.selectWoo.amd = S2;

  // Return the Select2 instance for anyone who is importing it.
  return select2;
}));

define('select2-patched',[
	"jquery",
	"select2",
	"jquery-throttle-debounce"
], function(
	$,
	Select2
) {
	// Place container before element instead of after
	var _origPlaceContainer = Select2.prototype._placeContainer;
	Select2.prototype._placeContainer = function ($container) {
		var res = _origPlaceContainer.apply(this, arguments);
		$container.insertBefore(this.$element);

		//console.log("install");
		//$(window).on("resize", $.debounce(250, $.proxy(function() {
		//	var width = this._resolveWidth(this.$element, this.options.get('width'));
		//	if (width != null) {
		//		$container.css('width', width);
		//	}
		//}, this)));

		return res;
	};

	return Select2;
});
define('cartware_checkout/components/selects',[
	"jquery",
	"lodash",
	"cartware/components/_base",
	"cartware/jquery/bem",
	"cartware/dom/event/trigger",
	"select2"
], function(
	$,
	_,
	Component,
	BEM,
	triggerEvent,
	S2
) {
	return Component.create({
		block:		null,

		$init: function() {
			this.block = BEM.block("cartware-checkout");

			this.transformSelects();

			var debounceTransform = _.debounce(_.bind(this.transformSelects, this), 10);

			$(document).ajaxComplete(debounceTransform);
			Ajax.Responders.register({
				onComplete: debounceTransform
			});

			this.patchShowHide();
		},

		patchShowHide: function() {
			var originalHide = Element.hide,
				originalShow = Element.show;
			
			Element.addMethods({
				hideShow: function(element, callback) {
					var $this = $(element),
						displayUpdateHandler = $this.data('cartware_checkout/displayUpdateHandler');
					
					callback.apply(this, arguments);
					
					if (displayUpdateHandler && typeof displayUpdateHandler === 'function') {
						displayUpdateHandler();
					}
				},
				
				hide: function(element) {
					Element.hideShow(element, originalHide);
				},
				
				show: function(element) {
					Element.hideShow(element, originalShow);
				}
			});
			
		},

		transformSelects: function() {
			var self = this;
			$.fn.select2.amd.require(
				["select2/utils", "select2/dropdown", "select2/dropdown/attachContainer", "select2/dropdown/closeOnSelect", "select2/dropdown/search"],
				function (Utils, Dropdown, AttachContainer, CloseOnSelect, Search) {
					self.block.find("select").each(function() {
						var select = $(this),
							features = (''+select.data("ui-select-features")).split(" "),
							dropdownAdapter;

						if (select.data('select2')) return;

						dropdownAdapter = Utils.Decorate(Utils.Decorate(Dropdown, AttachContainer), CloseOnSelect);

						if (features.indexOf("search") >= 0) {
							dropdownAdapter = Utils.Decorate(dropdownAdapter, Search);
						}

						select.select2({
							dropdownAdapter: dropdownAdapter,
							width: null
						});

						var displayUpdateHandler = _.bind(function() {
							select.data("select2").$container.css("display", this.style.display === "none" ? "none" : "");
						}, this);

						displayUpdateHandler();

						select.data('cartware_checkout/displayUpdateHandler', displayUpdateHandler);

						select.on("change", function(e) {
							if ("undefined" === typeof e.originalEvent) {
								//if (e instanceof $.Event) {
								triggerEvent(this, "change");
							}
						});
					});
				}
			);
		}
	});
});
define('cartware_checkout/components/address/selector',[
	"jquery",
	"cartware/components/_base",
	"cartware/jquery/bem",
	"bootstrap-button"
], function(
	$,
	Component,
	BEM
) {
	function Section(block) {
		var self = this;

		this.block = block;
		this.addressSwitch = this.block.bemGetElements("address-switch");
		this.addressSelect = this.block.bemGetElements("address-select");

		this.block.on("change", "input", function(e) {
			e.preventDefault();

			var input = $(this),
				addressSwitch = self.addressSwitch,
				action = addressSwitch.data("checkout-address-action"),
				type = addressSwitch.data("checkout-address-type"),
				checked = addressSwitch.prop("checked"),
				button = addressSwitch.parent();

			if (this !== addressSwitch.get(0)) button[checked ? "addClass" : "removeClass"]("active");

			self.addressSelect.val(input.val());

			switch (action) {
				case "new":
					self[checked ? "showNewAddressBlock" : "hideNewAddressBlock"](type);
					break;
			}
		});
	}

	Section.prototype = {
		block:			null,
		addressSwitch:	null,
		addressSelect:	null,

		showNewAddressBlock: function(type) {
			var form = this._getNewAddressBlock(type),
				selector = this._getAddressSelector(type);

			form.css("display", "");
			selector.bemAddModifier("disabled");
		},

		hideNewAddressBlock: function(type) {
			var form = this._getNewAddressBlock(type),
				selector = this._getAddressSelector(type);

			form.css("display", "none");
			selector.bemRemoveModifier("disabled");
		},

		_getNewAddressBlock: function(type) {
			return $("#" + type + "-new-address-form");
		},

		_getAddressForm: function(type) {
			return $("#co-" + type + "-form");
		},

		_getAddressSelector: function(type) {
			var className = BEM.buildBlockClassname("customer-address-chooser"),
				parent = this._getAddressForm(type);

			return parent.find("." + className);
		}
	};

	return Component.create({
		$init: function() {
			this.block = BEM.block("checkout-address-selection");

			this.block.each(function() {
				new Section($(this));
			});
		}
	});
});
/*
    json2.js
    2015-02-25

    Public Domain.

    NO WARRANTY EXPRESSED OR IMPLIED. USE AT YOUR OWN RISK.

    See http://www.JSON.org/js.html


    This code should be minified before deployment.
    See http://javascript.crockford.com/jsmin.html

    USE YOUR OWN COPY. IT IS EXTREMELY UNWISE TO LOAD CODE FROM SERVERS YOU DO
    NOT CONTROL.


    This file creates a global JSON object containing two methods: stringify
    and parse.

        JSON.stringify(value, replacer, space)
            value       any JavaScript value, usually an object or array.

            replacer    an optional parameter that determines how object
                        values are stringified for objects. It can be a
                        function or an array of strings.

            space       an optional parameter that specifies the indentation
                        of nested structures. If it is omitted, the text will
                        be packed without extra whitespace. If it is a number,
                        it will specify the number of spaces to indent at each
                        level. If it is a string (such as '\t' or '&nbsp;'),
                        it contains the characters used to indent at each level.

            This method produces a JSON text from a JavaScript value.

            When an object value is found, if the object contains a toJSON
            method, its toJSON method will be called and the result will be
            stringified. A toJSON method does not serialize: it returns the
            value represented by the name/value pair that should be serialized,
            or undefined if nothing should be serialized. The toJSON method
            will be passed the key associated with the value, and this will be
            bound to the value

            For example, this would serialize Dates as ISO strings.

                Date.prototype.toJSON = function (key) {
                    function f(n) {
                        // Format integers to have at least two digits.
                        return n < 10 
                        ? '0' + n 
                        : n;
                    }

                    return this.getUTCFullYear()   + '-' +
                         f(this.getUTCMonth() + 1) + '-' +
                         f(this.getUTCDate())      + 'T' +
                         f(this.getUTCHours())     + ':' +
                         f(this.getUTCMinutes())   + ':' +
                         f(this.getUTCSeconds())   + 'Z';
                };

            You can provide an optional replacer method. It will be passed the
            key and value of each member, with this bound to the containing
            object. The value that is returned from your method will be
            serialized. If your method returns undefined, then the member will
            be excluded from the serialization.

            If the replacer parameter is an array of strings, then it will be
            used to select the members to be serialized. It filters the results
            such that only members with keys listed in the replacer array are
            stringified.

            Values that do not have JSON representations, such as undefined or
            functions, will not be serialized. Such values in objects will be
            dropped; in arrays they will be replaced with null. You can use
            a replacer function to replace those with JSON values.
            JSON.stringify(undefined) returns undefined.

            The optional space parameter produces a stringification of the
            value that is filled with line breaks and indentation to make it
            easier to read.

            If the space parameter is a non-empty string, then that string will
            be used for indentation. If the space parameter is a number, then
            the indentation will be that many spaces.

            Example:

            text = JSON.stringify(['e', {pluribus: 'unum'}]);
            // text is '["e",{"pluribus":"unum"}]'


            text = JSON.stringify(['e', {pluribus: 'unum'}], null, '\t');
            // text is '[\n\t"e",\n\t{\n\t\t"pluribus": "unum"\n\t}\n]'

            text = JSON.stringify([new Date()], function (key, value) {
                return this[key] instanceof Date ?
                    'Date(' + this[key] + ')' : value;
            });
            // text is '["Date(---current time---)"]'


        JSON.parse(text, reviver)
            This method parses a JSON text to produce an object or array.
            It can throw a SyntaxError exception.

            The optional reviver parameter is a function that can filter and
            transform the results. It receives each of the keys and values,
            and its return value is used instead of the original value.
            If it returns what it received, then the structure is not modified.
            If it returns undefined then the member is deleted.

            Example:

            // Parse the text. Values that look like ISO date strings will
            // be converted to Date objects.

            myData = JSON.parse(text, function (key, value) {
                var a;
                if (typeof value === 'string') {
                    a =
/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2}(?:\.\d*)?)Z$/.exec(value);
                    if (a) {
                        return new Date(Date.UTC(+a[1], +a[2] - 1, +a[3], +a[4],
                            +a[5], +a[6]));
                    }
                }
                return value;
            });

            myData = JSON.parse('["Date(09/09/2001)"]', function (key, value) {
                var d;
                if (typeof value === 'string' &&
                        value.slice(0, 5) === 'Date(' &&
                        value.slice(-1) === ')') {
                    d = new Date(value.slice(5, -1));
                    if (d) {
                        return d;
                    }
                }
                return value;
            });


    This is a reference implementation. You are free to copy, modify, or
    redistribute.
*/

/*jslint 
    eval, for, this 
*/

/*property
    JSON, apply, call, charCodeAt, getUTCDate, getUTCFullYear, getUTCHours,
    getUTCMinutes, getUTCMonth, getUTCSeconds, hasOwnProperty, join,
    lastIndex, length, parse, prototype, push, replace, slice, stringify,
    test, toJSON, toString, valueOf
*/


// Create a JSON object only if one does not already exist. We create the
// methods in a closure to avoid creating global variables.

if (typeof JSON !== 'object') {
    JSON = {};
}

(function () {
    'use strict';

    function f(n) {
        // Format integers to have at least two digits.
        return n < 10 
        ? '0' + n 
        : n;
    }
    
    function this_value() {
        return this.valueOf();
    }

    if (typeof Date.prototype.toJSON !== 'function') {

        Date.prototype.toJSON = function () {

            return isFinite(this.valueOf())
            ? this.getUTCFullYear() + '-' +
                    f(this.getUTCMonth() + 1) + '-' +
                    f(this.getUTCDate()) + 'T' +
                    f(this.getUTCHours()) + ':' +
                    f(this.getUTCMinutes()) + ':' +
                    f(this.getUTCSeconds()) + 'Z'
            : null;
        };

        Boolean.prototype.toJSON = this_value;
        Number.prototype.toJSON = this_value;
        String.prototype.toJSON = this_value;
    }

    var cx,
        escapable,
        gap,
        indent,
        meta,
        rep;


    function quote(string) {

// If the string contains no control characters, no quote characters, and no
// backslash characters, then we can safely slap some quotes around it.
// Otherwise we must also replace the offending characters with safe escape
// sequences.

        escapable.lastIndex = 0;
        return escapable.test(string) 
        ? '"' + string.replace(escapable, function (a) {
            var c = meta[a];
            return typeof c === 'string'
            ? c
            : '\\u' + ('0000' + a.charCodeAt(0).toString(16)).slice(-4);
        }) + '"' 
        : '"' + string + '"';
    }


    function str(key, holder) {

// Produce a string from holder[key].

        var i,          // The loop counter.
            k,          // The member key.
            v,          // The member value.
            length,
            mind = gap,
            partial,
            value = holder[key];

// If the value has a toJSON method, call it to obtain a replacement value.

        if (value && typeof value === 'object' &&
                typeof value.toJSON === 'function') {
            value = value.toJSON(key);
        }

// If we were called with a replacer function, then call the replacer to
// obtain a replacement value.

        if (typeof rep === 'function') {
            value = rep.call(holder, key, value);
        }

// What happens next depends on the value's type.

        switch (typeof value) {
        case 'string':
            return quote(value);

        case 'number':

// JSON numbers must be finite. Encode non-finite numbers as null.

            return isFinite(value) 
            ? String(value) 
            : 'null';

        case 'boolean':
        case 'null':

// If the value is a boolean or null, convert it to a string. Note:
// typeof null does not produce 'null'. The case is included here in
// the remote chance that this gets fixed someday.

            return String(value);

// If the type is 'object', we might be dealing with an object or an array or
// null.

        case 'object':

// Due to a specification blunder in ECMAScript, typeof null is 'object',
// so watch out for that case.

            if (!value) {
                return 'null';
            }

// Make an array to hold the partial results of stringifying this object value.

            gap += indent;
            partial = [];

// Is the value an array?

            if (Object.prototype.toString.apply(value) === '[object Array]') {

// The value is an array. Stringify every element. Use null as a placeholder
// for non-JSON values.

                length = value.length;
                for (i = 0; i < length; i += 1) {
                    partial[i] = str(i, value) || 'null';
                }

// Join all of the elements together, separated with commas, and wrap them in
// brackets.

                v = partial.length === 0
                ? '[]'
                : gap
                ? '[\n' + gap + partial.join(',\n' + gap) + '\n' + mind + ']'
                : '[' + partial.join(',') + ']';
                gap = mind;
                return v;
            }

// If the replacer is an array, use it to select the members to be stringified.

            if (rep && typeof rep === 'object') {
                length = rep.length;
                for (i = 0; i < length; i += 1) {
                    if (typeof rep[i] === 'string') {
                        k = rep[i];
                        v = str(k, value);
                        if (v) {
                            partial.push(quote(k) + (
                                gap 
                                ? ': ' 
                                : ':'
                            ) + v);
                        }
                    }
                }
            } else {

// Otherwise, iterate through all of the keys in the object.

                for (k in value) {
                    if (Object.prototype.hasOwnProperty.call(value, k)) {
                        v = str(k, value);
                        if (v) {
                            partial.push(quote(k) + (
                                gap 
                                ? ': ' 
                                : ':'
                            ) + v);
                        }
                    }
                }
            }

// Join all of the member texts together, separated with commas,
// and wrap them in braces.

            v = partial.length === 0
            ? '{}'
            : gap
            ? '{\n' + gap + partial.join(',\n' + gap) + '\n' + mind + '}'
            : '{' + partial.join(',') + '}';
            gap = mind;
            return v;
        }
    }

// If the JSON object does not yet have a stringify method, give it one.

    if (typeof JSON.stringify !== 'function') {
        escapable = /[\\\"\u0000-\u001f\u007f-\u009f\u00ad\u0600-\u0604\u070f\u17b4\u17b5\u200c-\u200f\u2028-\u202f\u2060-\u206f\ufeff\ufff0-\uffff]/g;
        meta = {    // table of character substitutions
            '\b': '\\b',
            '\t': '\\t',
            '\n': '\\n',
            '\f': '\\f',
            '\r': '\\r',
            '"': '\\"',
            '\\': '\\\\'
        };
        JSON.stringify = function (value, replacer, space) {

// The stringify method takes a value and an optional replacer, and an optional
// space parameter, and returns a JSON text. The replacer can be a function
// that can replace values, or an array of strings that will select the keys.
// A default replacer method can be provided. Use of the space parameter can
// produce text that is more easily readable.

            var i;
            gap = '';
            indent = '';

// If the space parameter is a number, make an indent string containing that
// many spaces.

            if (typeof space === 'number') {
                for (i = 0; i < space; i += 1) {
                    indent += ' ';
                }

// If the space parameter is a string, it will be used as the indent string.

            } else if (typeof space === 'string') {
                indent = space;
            }

// If there is a replacer, it must be a function or an array.
// Otherwise, throw an error.

            rep = replacer;
            if (replacer && typeof replacer !== 'function' &&
                    (typeof replacer !== 'object' ||
                    typeof replacer.length !== 'number')) {
                throw new Error('JSON.stringify');
            }

// Make a fake root object containing our value under the key of ''.
// Return the result of stringifying the value.

            return str('', {'': value});
        };
    }


// If the JSON object does not yet have a parse method, give it one.

    if (typeof JSON.parse !== 'function') {
        cx = /[\u0000\u00ad\u0600-\u0604\u070f\u17b4\u17b5\u200c-\u200f\u2028-\u202f\u2060-\u206f\ufeff\ufff0-\uffff]/g;
        JSON.parse = function (text, reviver) {

// The parse method takes a text and an optional reviver function, and returns
// a JavaScript value if the text is a valid JSON text.

            var j;

            function walk(holder, key) {

// The walk method is used to recursively walk the resulting structure so
// that modifications can be made.

                var k, v, value = holder[key];
                if (value && typeof value === 'object') {
                    for (k in value) {
                        if (Object.prototype.hasOwnProperty.call(value, k)) {
                            v = walk(value, k);
                            if (v !== undefined) {
                                value[k] = v;
                            } else {
                                delete value[k];
                            }
                        }
                    }
                }
                return reviver.call(holder, key, value);
            }


// Parsing happens in four stages. In the first stage, we replace certain
// Unicode characters with escape sequences. JavaScript handles many characters
// incorrectly, either silently deleting them, or treating them as line endings.

            text = String(text);
            cx.lastIndex = 0;
            if (cx.test(text)) {
                text = text.replace(cx, function (a) {
                    return '\\u' +
                            ('0000' + a.charCodeAt(0).toString(16)).slice(-4);
                });
            }

// In the second stage, we run the text against regular expressions that look
// for non-JSON patterns. We are especially concerned with '()' and 'new'
// because they can cause invocation, and '=' because it can cause mutation.
// But just to be safe, we want to reject all unexpected forms.

// We split the second stage into 4 regexp operations in order to work around
// crippling inefficiencies in IE's and Safari's regexp engines. First we
// replace the JSON backslash pairs with '@' (a non-JSON character). Second, we
// replace all simple value tokens with ']' characters. Third, we delete all
// open brackets that follow a colon or comma or that begin the text. Finally,
// we look to see that the remaining characters are only whitespace or ']' or
// ',' or ':' or '{' or '}'. If that is so, then the text is safe for eval.

            if (
                /^[\],:{}\s]*$/.test(
                    text.replace(/\\(?:["\\\/bfnrt]|u[0-9a-fA-F]{4})/g, '@')
                        .replace(/"[^"\\\n\r]*"|true|false|null|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?/g, ']')
                        .replace(/(?:^|:|,)(?:\s*\[)+/g, '')
                )
            ) {

// In the third stage we use the eval function to compile the text into a
// JavaScript structure. The '{' operator is subject to a syntactic ambiguity
// in JavaScript: it can begin a block or an object literal. We wrap the text
// in parens to eliminate the ambiguity.

                j = eval('(' + text + ')');

// In the optional fourth stage, we recursively walk the new structure, passing
// each name/value pair to a reviver function for possible transformation.

                return typeof reviver === 'function'
                ? walk({'': j}, '')
                : j;
            }

// If the text is not JSON parseable, then a SyntaxError is thrown.

            throw new SyntaxError('JSON.parse');
        };
    }
}());

define("json2", function(){});

define('cartware_checkout/components/address/restriction',[
	"jquery",
	"lodash",
	"cartware_checkout",
	"cartware/components/_base",
	"text!shoptheme/templates/magnific-popup/default.html",
	"json2",
	"magnific-popup"
], function(
	$,
	_,
	Checkout,
	Component,
	PopupHtml
) {
	var popupTemplate = null,
		messageTemplate = null;

	var messageHtml;

	return Component.create({
		$init: function() {
            messageHtml = '<div class="b_qaddress-validation-result">' +
                '<div class="b_qaddress-validation-result__message">' +
                '<%= message %>' +
                '</div>' +
                '<div class="b_qaddress-validation-result__buttons">' +
                '<button class="btn btn-primary b_qaddress-validation-result__button b_qaddress-validation-result__button--accept" data-mfp-close>' +
                    Translator.translate("OK") +
                '</button>' +
                '</div>' +
                '</div>';

			this._adviseShipping();
			this._adviseBilling();
		},

		_adviseShipping: function() {
			this._setupAdvise("shipping");
		},

		_adviseBilling: function() {
			this._setupAdvise("billing");
		},

		_setupAdvise: function(type) {
			var self = this;

			Checkout().retrieve(type, function(obj) {
				var onSave = obj.onSave;
				obj.onSave = function(transport) {
					var response = JSON.parse(transport.responseText);

					if (response.error && response.restricted) {
						$.magnificPopup.open({
							items: {
								src: self._renderPopup({
									title: Translator.translate('Address validation'),
									body: self._renderMessage({	
										message: response.message,
									})
								}),
								type: 'inline'
							},

							callbacks: {
								open: function() {
									var self = this;
									this.content.on("click", "[data-mfp-close]", function() {
										self.close();
									});
								},
							}
						});

						return;
					}

					return onSave.apply(this, arguments);
				}
			});
		},

		_renderPopup: function(data) {
			if (null === popupTemplate) {
				popupTemplate = _.template(PopupHtml);
			}

			return popupTemplate(data);
		},

		_renderMessage: function(data) {
			if (null === messageTemplate) {
				messageTemplate = _.template(messageHtml);
			}

			return messageTemplate(data);
		}
	});
});
define('cartware_checkout/components/ops/payment',[
	"require",
	"cartware/components/_base",
	"cartware_checkout"
], function(
	require,
	Component,
	Checkout
) {
	return Component.create({
		init: function() {
			var self = this;

			Checkout().retrieve("payment", function(payment) {
				self.initializeOPS();
			});
		},

		initializeOPS: function() {
			payment.opsNextStep = function(transport)
			{
				var response;
				var key, validationElement;
				var billingDifferentFromShipping = $("billing:different_from_shipping").checked;
				var forceStep = null;

				if (transport && transport.responseText){
					try{
						response = eval('(' + transport.responseText + ')');
					}
					catch (e) {
						response = {};
					}
				}
				if (!response.opsError) {
					return payment.nextStep(transport);
				}
				if (response.error) {
					opsValidationFields = payment.opsValidationFields.evalJSON(true);
					errorneousFields = response.fields;
					for (key in errorneousFields) {
						if (!errorneousFields.hasOwnProperty(key)) continue;

						validationElement = $(key);

						if (!billingDifferentFromShipping && key.indexOf("billing:") === 0) {
							// key = key.replace(/^billing:/, "shipping:");
							validationElement = $(key.replace(/^billing:/, "shipping:"));

							if (!forceStep && response.goto_section === "billing") {
								forceStep = "shipping";
							}
						}

						//if (!forceStep && key.indexOf("shipping:") === 0) {
						//	forceStep = "shipping";
						//}

						if (validationElement) {
							if (opsValidationFields[key]) {
								validationElement.removeClassName('validation-passed');
								validationElement.addClassName('validate-string-length');
								validationElement.addClassName('maximum-length-' + opsValidationFields[key]);
							}
							if(errorneousFields[key]) {
								Validation.ajaxError(validationElement, errorneousFields[key]);
							}

						}
					}

					if (forceStep) {
						response.goto_section = forceStep;
					}


					if (response.goto_section !== "billing") {
						checkout.gotoSection(response.goto_section, false);
					}

					return;
				}

				return payment.nextStep(transport);
			};

			payment.onSave = payment.opsNextStep.bindAsEventListener(this);

			payment.save = payment.save.wrap(function(originalSaveMethod) {
				payment.originalSaveMethod = originalSaveMethod;
				//this form element is always set in payment object this.form or payment.form no need to bind to specific
				var opsValidator = new Validation(payment.form);
				if (!opsValidator.validate()) {
					return;
				}
				if ('ops_directDebit' == payment.currentMethod) {
					payment.saveOpsDirectDebit();
					return; //return as you have another call chain here
				}
				if ('ops_cc' == payment.currentMethod) {
					payment.saveOpsCcBrand();
					return; //return as you have another call chain here
				}

				originalSaveMethod();
			});



			payment.saveOpsDirectDebit = function() {
				checkout.setLoadWaiting('payment');
				var countryId = $('ops_directdebit_country_id').value;
				var accountNo = $('ops_directdebit_account_no').value;
				var bankCode  = $('ops_directdebit_bank_code').value;
				var CN        = $('ops_directdebit_CN').value;
				var iban      = $('ops_directdebit_iban').value.replace(/\s+/g, '');
				var bic       = $('ops_directdebit_bic').value.replace(/\s+/g, '');
				new Ajax.Request(opsDirectDebitUrl, {
					method: 'post',
					parameters: { country : countryId, account : accountNo, bankcode : bankCode, CN : CN, iban : iban, bic : bic },
					onSuccess: function(transport) {
						checkout.setLoadWaiting(false);
						payment.originalSaveMethod();
					},
					onFailure: function(transport) {
						checkout.setLoadWaiting(false);
						if (transport.responseText && 0 < transport.responseText.length) {
							message = transport.responseText;
						} else {
							message = 'Payment failed. Please select another payment method.';
						}
						alert(Translator.translate(message));
						checkout.setLoadWaiting(false);
					}
				});
			};

			payment.saveOpsCcBrand = function() {
				checkout.setLoadWaiting('payment');
				var owner = $('OPS_CC_CN').value;
				new Ajax.Request(opsSaveCcBrandUrl, {
					method: 'post',
					parameters: { brand : $('OPS_CC_BRAND').value, cn: owner },
					onSuccess: function(transport) {
						if (-1 < opsCcBrandsForAliasInterface.indexOf($('OPS_CC_BRAND').value)) {
							payment.requestOpsCcAlias();
						} else {
							checkout.setLoadWaiting(false);
							//moved inside else otherwise called twice if previous condition is true
							payment.originalSaveMethod();
						}
					},
					onFailure: function(transport) {
						alert(Translator.translate('Payment failed. Please select another payment method.'));
						checkout.setLoadWaiting(false);
					}
				});
			};

			payment.requestOpsCcAlias = function() {
				checkout.setLoadWaiting('payment');
				var iframe = $('ops_iframe_' + payment.currentMethod);
				var doc = null;

				if(iframe.contentDocument) {
					doc = iframe.contentDocument;
				} else if(iframe.contentWindow) {
					doc = iframe.contentWindow.document;
				} else if(iframe.document) {
					doc = iframe.document;
				}

				doc.body.innerHTML="";
				iframe.alreadySet = false;
				if (payment.opsStoredAliasPresent == false) {
					if ('true' != iframe.alreadySet) {
						form = doc.createElement('form');
						form.id = 'ops_request_form';
						form.method = 'post';
						form.action = opsUrl;
						submit = doc.createElement('submit');
						form.appendChild(submit);

						var cardholder = doc.createElement('input');
						cardholder.id = 'CN';
						cardholder.name = 'CN';
						cardholder.value = $('OPS_CC_CN').value;

						var cardnumber = doc.createElement('input');
						cardnumber.id = 'CARDNO';
						cardnumber.name = 'CARDNO';
						cardnumber.value = $('OPS_CC_CARDNO').value;

						var verificationCode = doc.createElement('input');
						verificationCode.id = 'CVC';
						verificationCode.name = 'CVC';
						verificationCode.value = $('OPS_CC_CVC').value;

						var brandElement = doc.createElement('input');
						brandElement.id = 'BRAND';
						brandElement.name = 'BRAND';
						brandElement.value = $('OPS_CC_BRAND').value;

						var edElement = doc.createElement('input');
						edElement.id = 'ED';
						edElement.name = 'ED';
						edElement.value = $('OPS_CC_ECOM_CARDINFO_EXPDATE_MONTH').value + $('OPS_CC_ECOM_CARDINFO_EXPDATE_YEAR').value;

						var pspidElement = doc.createElement('input');
						pspidElement.id = 'PSPID';
						pspidElement.name = 'PSPID';
						pspidElement.value = opsPspid;

						var orderIdElement = doc.createElement('input');
						orderIdElement.name = 'ORDERID';
						orderIdElement.id = 'ORDERID';
						orderIdElement.value = opsOrderId;

						var acceptUrlElement = doc.createElement('input');
						acceptUrlElement.name = 'ACCEPTURL';
						acceptUrlElement.id = 'ACCEPTURL';
						acceptUrlElement.value = opsAcceptUrl;

						var exceptionUrlElement = doc.createElement('input');
						exceptionUrlElement.name = 'EXCEPTIONURL';
						exceptionUrlElement.id = 'EXCEPTIONURL';
						exceptionUrlElement.value = opsExceptionUrl;

						var paramplusElement = doc.createElement('input');
						paramplusElement.name = 'PARAMPLUS';
						paramplusElement.id = 'PARAMPLUS';
						paramplusElement.value = 'RESPONSEFORMAT=JSON';

						var aliasElement = doc.createElement('input');
						aliasElement.name = 'ALIAS';
						aliasElement.id = 'ALIAS';
						aliasElement.value = opsAlias;

						saveAliasData = 0;
						if ($('ops_alias_save') && $('ops_alias_save').checked) {
							saveAliasData = 1;
						}
						var aliasPersistedElement = doc.createElement('input');
						aliasPersistedElement.name = "ALIASPERSISTEDAFTERUSE";
						aliasPersistedElement.id = "ALIASPERSISTEDAFTERUSE";
						aliasPersistedElement.value = saveAliasData ? 'Y' : 'N';

						form.appendChild(pspidElement);
						form.appendChild(brandElement);
						form.appendChild(cardholder);
						form.appendChild(cardnumber);
						form.appendChild(verificationCode);
						form.appendChild(edElement);
						form.appendChild(acceptUrlElement);
						form.appendChild(exceptionUrlElement);
						form.appendChild(orderIdElement);
						form.appendChild(paramplusElement);
						form.appendChild(aliasElement);
						form.appendChild(aliasPersistedElement);

						var hash = doc.createElement('input');
						hash.id = 'SHASIGN';
						hash.name = 'SHASIGN';

						new Ajax.Request(opsHashUrl, {
							method: 'get',
							parameters: {
								brand: brandElement.value,
								orderid: opsOrderId,
								paramplus: paramplusElement.value,
								alias: aliasElement.value,
								saveAlias: saveAliasData,
								storedAlias: payment.opsStoredAlias
							},
							onSuccess: function(transport) {
								var data = transport.responseText.evalJSON();
								hash.value = data.hash;
								aliasElement.value = data.alias;
								form.appendChild(hash);
								doc.body.appendChild(form);
								iframe.alreadySet = 'true';

								form.submit();

								doc.body.innerHTML = '{ "result" : "waiting" }';
								setTimeout("payment.processOpsResponse(500)", 500);
							}
						});
					}
				} else {
					new Ajax.Request(opsAcceptUrl, {
						method: 'get',
						parameters: {
							Alias: payment.opsStoredAlias,
							CVC: $('OPS_CC_CVC').value,
							CN: $('OPS_CC_CN').value
						},
						onSuccess: function(transport) {
							doc.body.innerHTML = transport.responseText;
							setTimeout("payment.processOpsResponse(500)", 500);
						}
					});
				}
			};

			payment.processOpsResponse = function(timeOffset) {
				try {
					var responseIframe = $('ops_iframe_' + payment.currentMethod);
					var responseResult;

					/* payment fails after 30s without response */
					var maxOffset = 30000;

					if(responseIframe.contentDocument) {
						responseResult = responseIframe.contentDocument;
					} else if(responseIframe.contentWindow) {
						responseResult = responseIframe.contentWindow.document;
					} else if(responseIframe.document) {
						responseResult = responseIframe.document;
					}

					//Remove links in JSON response
					//can happen f.e. on iPad <a href="tel:0301125679">0301125679</a> if alias is interpreted as a phone number
					var htmlResponse = responseResult.body.innerHTML.replace(/<a\b[^>]*>/i, '');
					htmlResponse = htmlResponse.replace(/<\/a>/i, '');

					if ("undefined" == typeof(responseResult)) {
						currentStatus = '{ "result" : "waiting" }'.evalJSON();
					} else {
						var currentStatus = htmlResponse.evalJSON();
						if ("undefined" == typeof(currentStatus) || "undefined" == typeof(currentStatus.result)) {
							currentStatus = '{ "result" : "waiting" }'.evalJSON();
						}
					}
				} catch (e) {
					currentStatus = '{ "result" : "waiting" }'.evalJSON();
				}

				if ('waiting' == currentStatus.result && timeOffset <= maxOffset) {
					setTimeout("payment.processOpsResponse(" + (500+timeOffset) + ")", 500);
					return false;
				} else if ('success' == currentStatus.result) {
					new Ajax.Request(opsCcSaveAliasUrl, {
						method: 'post',
						parameters: { alias : currentStatus.alias,
							CVC : currentStatus.CVC,
							CN: $('OPS_CC_CN').value
						},
						onSuccess: function(transport) {
							var data = transport.responseText;
							checkout.setLoadWaiting(false);
							$('OPS_CC_CVC').value='';
							payment.stashCcData();
							payment.originalSaveMethod();

						},
						onFailure: function(transport) {
							payment.applyStashedCcData();
							//reset the buttons on failure
							checkout.setLoadWaiting(false);
						}
					});

					return true;
				}

				alert(Translator.translate('Payment failed. Please review your input or select another payment method.'));
				checkout.setLoadWaiting(false);
				return false;
			};

			payment.criticalOpsCcData = ['CN', 'CARDNO', 'CVC'];
			payment.stashedOpsCcData = new Array();

			payment.stashCcData = function() {
				payment.criticalOpsCcData.each(function(item) {
					if (!payment.stashedOpsCcData[item] || $('OPS_CC_' + item).value.length) {
						payment.stashedOpsCcData[item] = $('OPS_CC_' + item).value;
						$('OPS_CC_' + item).removeClassName('required-entry');
						$('OPS_CC_' + item).value = '';
						$('OPS_CC_' + item).disable();
					}
				});
			};

			payment.applyStashedCcData = function() {
				payment.criticalOpsCcData.each(function(item) {
					if ($('OPS_CC_' + item)) {
						if (payment.stashedOpsCcData[item] && 0 < payment.stashedOpsCcData[item].length) {
							$('OPS_CC_' + item).value = payment.stashedOpsCcData[item];
						}
						$('OPS_CC_' + item).addClassName('required-entry');
						$('OPS_CC_' + item).enable();
					}
				});
			};

			payment.toggleOpsDirectDebitInputs = function(country) {
				var bankcode = 'ops_directdebit_bank_code';
				var bic = 'ops_directdebit_bic';
				var iban = 'ops_directdebit_iban';
				var showInput = function(id) {
					$$('#' + id)[0].up().show();
					if (!$(id).hasClassName('required-entry') && id != 'ops_directdebit_bic' && $('ops_directdebit_iban').value == '') {
						$(id).addClassName('required-entry');
					}
				};
				var hideInput = function(id) {
					$$('#' + id)[0].up().hide();
					$(id).removeClassName('required-entry');
				};
				if ('NL' == country) {
					hideInput(bankcode);
					showInput(bic);
					showInput(iban);
				}
				if ('DE' == country || 'AT' == country) {
					showInput(bankcode);
					hideInput(bic);
					showInput(iban);
				}
				if ('AT' == country) {
					hideInput(iban)
				}
			};


			payment.toggleOpsCcInputs = function() {
				if (-1 < opsCcBrandsForAliasInterface.indexOf($('OPS_CC_BRAND').value)) {
					$('ops_cc_data').show();
				} else {
					$('ops_cc_data').hide();
				}
			};

			if(typeof accordion != 'undefined'){
				accordion.openSection = accordion.openSection.wrap(function(originalOpenSectionMethod, section) {

					if (section.id == 'opc-payment' || section == 'opc-payment') {
						payment.applyStashedCcData();
					}
					if ((section.id == 'opc-payment' || section == 'opc-payment') && 'ops_cc' == payment.currentMethod) {
						if ($('OPS_CC_CN') && $('OPS_CC_CN').hasAttribute('disabled')) {
							$('OPS_CC_CN').removeAttribute('disabled');
						}
						if ($('OPS_CC_CARDNO') && $('OPS_CC_CARDNO').hasAttribute('disabled')) {
							$('OPS_CC_CARDNO').removeAttribute('disabled');
						}
						if ($('OPS_CC_CVC') && $('OPS_CC_CVC').hasAttribute('disabled')) {
							$('OPS_CC_CVC').removeAttribute('disabled');
						}
					}
					originalOpenSectionMethod(section);
				});
			}

			payment.clearOpsCcInputs = function() {
				if (payment.opsStoredAliasPresent == true) {
					$('OPS_CC_CN').value = '';
					$('OPS_CC_CN').removeAttribute('readonly');
					$('OPS_CC_CN').removeClassName('readonly');
					$('OPS_CC_CN').readOnly = false;
					$('OPS_CC_CARDNO').value = '';
					$('OPS_CC_CARDNO').removeClassName('readonly');
					$('OPS_CC_CARDNO').addClassName('validate-cc-number');
					$('OPS_CC_CARDNO').addClassName('validate-cc-type');
					$('OPS_CC_CARDNO').removeAttribute('readonly');
					$('OPS_CC_CARDNO').readOnly = false;;
					$('OPS_CC_ECOM_CARDINFO_EXPDATE_MONTH').selectedIndex = 0;
					$('OPS_CC_ECOM_CARDINFO_EXPDATE_MONTH').removeClassName('readonly');
					$('OPS_CC_ECOM_CARDINFO_EXPDATE_MONTH').removeAttribute('readonly');
					$('OPS_CC_ECOM_CARDINFO_EXPDATE_MONTH').readOnly = false;
					$('OPS_CC_ECOM_CARDINFO_EXPDATE_YEAR').selectedIndex = 0;
					$('OPS_CC_ECOM_CARDINFO_EXPDATE_YEAR').removeClassName('readonly');
					$('OPS_CC_ECOM_CARDINFO_EXPDATE_YEAR').removeAttribute('readonly');
					$('OPS_CC_ECOM_CARDINFO_EXPDATE_YEAR').readOnly = false;
					$('ops_save_alias_li').show();
					payment.opsStoredAliasPresent = false;
				}
			};

			payment.jumpToLoginStep = function() {
				if(typeof accordion != 'undefined'){
					accordion.openSection('opc-login');
					$('login:register').checked = true;
				}
			};

			payment.setRequiredDirectDebitFields = function(element) {

				country = $('ops_directdebit_country_id').value;
				accountNo = 'ops_directdebit_account_no';
				blz = 'ops_directdebit_bank_code';
				iban = 'ops_directdebit_iban';
				bic = 'ops_directdebit_bic';

				if ($(iban).value == '' && $(bic).value == '' && $(accountNo).value == '' && $(blz).value == '') {
					$(iban).addClassName('required-entry');
					$(accountNo).addClassName('required-entry');
					$(blz).addClassName('required-entry');
					return;
				}

				if ($(iban).value == '' && $(bic).value == '' && $(accountNo).value == '' && $(blz).value == '') {
					$(iban).addClassName('required-entry');
					$(accountNo).addClassName('required-entry');
					$(blz).addClassName('required-entry');
					return;
				}

				accountNoClasses = new Array('required-entry');
				blzClasses = new Array('required-entry');
				if (country == 'AT' || (element.id == accountNo || element.id == blz)) {

					$(iban).removeClassName('required-entry');
					$(iban).removeClassName('validation-failed');
					if ($('advice-required-entry-ops_directdebit_iban')) {
						$('advice-required-entry-ops_directdebit_iban').remove();
					}
					accountNoClasses.each(function(accountNoClass) {
						if (!$(accountNo).hasClassName(accountNoClass)) {
							$(accountNo).addClassName(accountNoClass);
						}
					});

					if (country == 'DE' || country == 'AT') {
						blzClasses.each(function(blzClass) {
							if (!$(blz).hasClassName(blzClass)) {
								$(blz).addClassName(blzClass);
							}
						});
					}


					$(accountNo).removeClassName('validation-passed');
					$(blz).removeClassName('validation-passed');

					if (country == 'NL') {
						$(blz).removeClassName('required-entry');
						$(blz).removeClassName('validation-failed');
						if ($('advice-required-entry-ops_directdebit_bank_code')) {
							$('advice-required-entry-ops_directdebit_bank_code').remove();
						}
					}
				}
				if ((element.id == iban || element.id == bic)) {
					if (!$(iban).hasClassName('required-entry')) {
						$(iban).addClassName('required-entry')
					}
					if ($(iban).hasClassName('validation-passed')) {
						$(iban).removeClassName('validation-passed')
					}

					accountNoClasses.each(function(accountNoClass) {
						if ($(accountNo).hasClassName(accountNoClass)) {
							$(accountNo).removeClassName(accountNoClass);
						}
					});
					if ($('advice-required-entry-ops_directdebit_account_no')) {
						$('advice-required-entry-ops_directdebit_account_no').remove();
					}
					$(accountNo).removeClassName('validation-failed');

					$(blz).removeClassName('validation-failed');
					blzClasses.each(function(blzClass) {
						if ($(blz).hasClassName(blzClass)) {
							$(blz).removeClassName(blzClass);
						}
					});
					if ($('advice-required-entry-ops_directdebit_bank_code')) {
						$('advice-required-entry-ops_directdebit_bank_code').remove();
					}

				}
			}
		}
	});
});
(function(root) {
define("jquery-dotdotdot", ["jquery"], function() {
  return (function() {
/*
 *	jQuery dotdotdot 3.2.3
 *	@requires jQuery 1.7.0 or later
 *
 *	dotdotdot.frebsite.nl
 *
 *	Copyright (c) Fred Heusschen
 *	www.frebsite.nl
 *
 *	License: CC-BY-NC-4.0
 *	http://creativecommons.org/licenses/by-nc/4.0/
 */

(function( $ ) {
	'use strict';
	
	var _PLUGIN_    = 'dotdotdot';
	var _VERSION_   = '3.2.3';

	if ( $[ _PLUGIN_ ] && $[ _PLUGIN_ ].version > _VERSION_ )
	{
		return;
	}



	/*
		The class
	*/
	$[ _PLUGIN_ ] = function( $container, opts )
	{
		this.$dot 	= $container;
		this.api	= [ 'getInstance', 'truncate', 'restore', 'destroy', 'watch', 'unwatch' ];
		this.opts	= opts;

		var oldAPI = this.$dot.data( _PLUGIN_ );
		if ( oldAPI )
		{
			oldAPI.destroy();
		}

		this.init();
		this.truncate();

		if ( this.opts.watch )
		{
			this.watch();
		}

		return this;
	};

	$[ _PLUGIN_ ].version 	= _VERSION_;
	$[ _PLUGIN_ ].uniqueId 	= 0;

	$[ _PLUGIN_ ].defaults  = {
		ellipsis		: '\u2026 ',
		callback		: function( isTruncated ) {},
		truncate 		: 'word',
		tolerance		: 0,
		keep			: null,
		watch			: 'window',
		height 			: null
	};


	$[ _PLUGIN_ ].prototype = {

		init: function()
		{
			this.watchTimeout		= null;
			this.watchInterval		= null;
			this.uniqueId 			= $[ _PLUGIN_ ].uniqueId++;
			this.originalStyle		= this.$dot.attr( 'style' ) || '';
			this.originalContent 	= this._getOriginalContent();
			
			if ( this.$dot.css( 'word-wrap' ) !== 'break-word' )
			{
				this.$dot.css( 'word-wrap', 'break-word' );
			}
			if ( this.$dot.css( 'white-space' ) === 'nowrap' )
			{
				this.$dot.css( 'white-space', 'normal' );
			}

			if ( this.opts.height === null )
			{
				this.opts.height = this._getMaxHeight();
			}

			if ( typeof this.opts.ellipsis == 'string' )
			{
				this.opts.ellipsis = document.createTextNode( this.opts.ellipsis );
			}
		},

		getInstance: function()
		{
			return this;
		},

		truncate: function()
		{
			var that = this;


			//	Add inner node for measuring the height
			this.$inner = this.$dot
				.wrapInner( '<div />' )
				.children()
				.css({
					'display'	: 'block',
					'height'	: 'auto',
					'width'		: 'auto',
					'border'	: 'none',
					'padding'	: 0,
					'margin'	: 0
				});


			//	Set original content
			this.$inner
				.empty()
				.append( this.originalContent.clone( true ) );


			//	Get max height
			this.maxHeight = this._getMaxHeight();


			//	Truncate the text
			var isTruncated = false;
			if ( !this._fits() )
			{
				isTruncated = true;
				this._truncateToNode( this.$inner[ 0 ] );
			}

			this.$dot[ isTruncated ? 'addClass' : 'removeClass' ]( _c.truncated );


			//	Remove inner node
			this.$inner.replaceWith( this.$inner.contents() );
			this.$inner = null;


			//	 Callback
			this.opts.callback.call( this.$dot[ 0 ], isTruncated );

			return isTruncated;
		},

		restore: function()
		{
			this.unwatch();

			this.$dot
				.empty()
				.append( this.originalContent )
				.attr( 'style', this.originalStyle )
				.removeClass( _c.truncated );
		},

		destroy: function()
		{
			this.restore();
			this.$dot.data( _PLUGIN_, null );
		},

		watch: function()
		{
			var that = this;

			this.unwatch();

			var oldSizes = {};

			if ( this.opts.watch == 'window' )
			{
				$wndw.on(
					_e.resize + that.uniqueId,
					function( e )
					{
						if ( that.watchTimeout )
						{
							clearTimeout( that.watchTimeout );
						}
						that.watchTimeout = setTimeout(
							function() {

								oldSizes = that._watchSizes( oldSizes, $wndw, 'width', 'height' );

							}, 100
						);
					}
				);

			}
			else
			{
				this.watchInterval = setInterval(
					function()
					{
						oldSizes = that._watchSizes( oldSizes, that.$dot, 'innerWidth', 'innerHeight' );

					}, 500
				);
			}
		},

		unwatch: function()
		{
			$wndw.off( _e.resize + this.uniqueId );

			if ( this.watchInterval )
			{
				clearInterval( this.watchInterval );
			}

			if ( this.watchTimeout )
			{
				clearTimeout( this.watchTimeout );
			}
		},

		_api: function()
		{
			var that = this,
				api = {};

			$.each( this.api,
				function( i )
				{
					var fn = this;
					api[ fn ] = function()
					{
						var re = that[ fn ].apply( that, arguments );
						return ( typeof re == 'undefined' ) ? api : re;
					};
				}
			);
			return api;
		},

		_truncateToNode: function( _elem )
		{

			var that = this;

			var _coms = [],
				_elms = [];

			//	Empty the node 
			//		-> replace all contents with comments
			$(_elem)
				.contents()
				.each(
					function()
					{
						var $e = $(this);
						if ( !$e.hasClass( _c.keep ) )
						{
							var c = document.createComment( '' );
							$e.replaceWith( c );

							_elms.push( this );
							_coms.push( c );
						}
					}
				);

			if ( !_elms.length )
			{
				return;
			}

			//	Re-fill the node 
			//		-> replace comments with contents until it doesn't fit anymore
			for ( var e = 0; e < _elms.length; e++ )
			{

				$(_coms[ e ]).replaceWith( _elms[ e ] );

				$(_elms[ e ]).append( this.opts.ellipsis );
				var fits = this._fits();
				$(this.opts.ellipsis, _elms[ e ]).remove();

				if ( !fits )
				{
					if ( this.opts.truncate == 'node' && e > 1 )
					{
						$(_elms[ e - 2 ]).remove();
						return;
					}
					break;
				}
			}

			//	Remove left over comments
			for ( var c = e; c < _coms.length; c++ )
			{
				$(_coms[ c ]).remove();
			}

			//	Get last node 
			//		-> the node that overflows

			var _last = _elms[ Math.max( 0, Math.min( e, _elms.length - 1 ) ) ];

			//	Border case
			//		-> the last node with only an ellipsis in it...
			if ( _last.nodeType == 1 )
			{

				var $e = $('<' + _last.nodeName + ' />');
				$e.append( this.opts.ellipsis );

				$(_last).replaceWith( $e );

				//	... fits
				//		-> Restore the full last node
				if ( this._fits() )
				{
					$e.replaceWith( _last );
				}

				//	... doesn't fit
				//		-> remove it and go back one node
				else
				{
					$e.remove();
					_last = _elms[ Math.max( 0, e - 1 ) ];
				}
			}

			//	Proceed inside last node
			if ( _last.nodeType == 1 )
			{
				this._truncateToNode( _last );
			}
			else
			{
				this._truncateToWord( _last );
			}
		},

		_truncateToWord: function( _elem )
		{

			var e = _elem;

			var that = this;

			var txt = this.__getTextContent( e ),
				sep = ( txt.indexOf( ' ' ) !== -1 ) ? ' ' : '\u3000',
				arr = txt.split( sep ),
				str = '';

			for ( var a = arr.length; a >= 0; a-- )
			{
				str = arr.slice( 0, a ).join( sep );

				that.__setTextContent( e, that._addEllipsis( str ) );

				if ( that._fits() )
				{
					if ( that.opts.truncate == 'letter' )
					{
						that.__setTextContent( e, arr.slice( 0, a + 1 ).join( sep ) );
						that._truncateToLetter( e );
					}
					break;
				}
			}
		},

		_truncateToLetter: function( e )
		{
			var that = this;

			var txt = this.__getTextContent( e ),
				arr = txt.split( '' ),
				str = '';

			for ( var a = arr.length; a >= 0; a-- )
			{
				str = arr.slice( 0, a ).join( '' );

				if ( !str.length )
				{
					continue;
				}

				that.__setTextContent( e, that._addEllipsis( str ) );

				if ( that._fits() )
				{
					break;
				}
			}
		},

		_fits: function()
		{
			return ( this.$inner.innerHeight() <= this.maxHeight + this.opts.tolerance );
		},

		_addEllipsis: function( txt )
		{
			var remove = [' ', '\u3000', ',', ';', '.', '!', '?'];

			while ( $.inArray( txt.slice( -1 ), remove ) > -1 )
			{
				txt = txt.slice( 0, -1 );
			}
			txt += this.__getTextContent( this.opts.ellipsis );

			return txt;
		},

		_getOriginalContent: function()
		{
			var that = this;

			//	Add "keep" class to nodes to keep
			this.$dot
				.find( 'script, style' )
				.addClass( _c.keep );

			if ( this.opts.keep )
			{
				this.$dot
					.find( this.opts.keep )
					.addClass( _c.keep );
			}

			//	Filter out unneeded whitespace
			this.$dot
				.find( '*' )
				.not( '.' + _c.keep )
				.add( this.$dot )
				.contents()
				.each(
					function()
					{

						var e = this,
							$e = $(this);

						//	Text nodes
						if ( e.nodeType == 3 )
						{

							//	Remove whitespace where it does not take up space in the DOM
							if ( $.trim( that.__getTextContent( e ) ) == '' )
							{
								if ( $e.parent().is( 'table, thead, tbody, tfoot, tr, dl, ul, ol, video' ) )
								{
									$e.remove();
									return;
								}
								if ( $e.prev().is( 'div, p, table, td, td, dt, dd, li' ) )
								{
									$e.remove();
									return;
								}
								if ( $e.next().is( 'div, p, table, td, td, dt, dd, li' ) )
								{
									$e.remove();
									return;
								}
								if ( !$e.prev().length )
								{
									$e.remove();
									return;
								}
								if ( !$e.next().length )
								{
									$e.remove();
									return;
								}
							}
						}

						//	Comment nodes
						else if ( e.nodeType == 8 )
						{
							$e.remove();
						}

					}
				);

			return this.$dot.contents();
		},

		_getMaxHeight: function()
		{
			if ( typeof this.opts.height == 'number' )
			{
				return this.opts.height;
			}

			//	Find smallest CSS height
			var arr = [ 'maxHeight', 'height' ],
				hgh = 0;
 
			for ( var a = 0; a < arr.length; a++ )
			{
				hgh = window.getComputedStyle( this.$dot[ 0 ] )[ arr[ a ] ];
				if ( hgh.slice( -2 ) == 'px' )
				{
					hgh = parseFloat( hgh );
					break;
				}
			}

			//	Remove padding-top/bottom when needed.
			var arr = [];
			switch ( this.$dot.css( 'boxSizing' ) )
			{
				case 'border-box':
					arr.push( 'borderTopWidth' );
					arr.push( 'borderBottomWidth' );
					//	no break -> padding needs to be added too

				case 'padding-box':
					arr.push( 'paddingTop' );
					arr.push( 'paddingBottom' );
					break;
			}
			for ( var a = 0; a < arr.length; a++ )
			{
				var p = window.getComputedStyle( this.$dot[ 0 ] )[ arr[ a ] ];
				if ( p.slice( -2 ) == 'px' )
				{
					hgh -= parseFloat( p );
				}
			}

			//	Sanitize
			return Math.max( hgh, 0 );
		},

		_watchSizes: function( oldSizes, $elem, width, height )
		{
			if ( this.$dot.is( ':visible' ) )
			{
				var newSizes = {
					'width'		: $elem[ width  ](),
					'height'	: $elem[ height ]()
				};

				if ( oldSizes.width != newSizes.width || oldSizes.height != newSizes.height )
				{
					this.truncate();
				}

				return newSizes;
			}
			return oldSizes;
		},

		__getTextContent: function( elem )
		{
			var arr = [ 'nodeValue', 'textContent', 'innerText' ];
			for ( var a = 0; a < arr.length; a++ )
			{
				if ( typeof elem[ arr[ a ] ] == 'string' )
				{
					return elem[ arr[ a ] ];
				}
			}
			return '';
		},
		__setTextContent: function( elem, content )
		{
			var arr = [ 'nodeValue', 'textContent', 'innerText' ];
			for ( var a = 0; a < arr.length; a++ )
			{
				elem[ arr[ a ] ] = content;
			}
		}
	};



	/*
		The jQuery plugin
	*/
	$.fn[ _PLUGIN_ ] = function( opts )
	{
		initPlugin();

		opts = $.extend( true, {}, $[ _PLUGIN_ ].defaults, opts );

		return this.each(
			function()
			{
				$(this).data( _PLUGIN_, new $[ _PLUGIN_ ]( $(this), opts )._api() );
			}
		);
	};



	/*
		Global variables
	*/
	var _c, _d, _e, $wndw;

	function initPlugin()
	{
		$wndw = $(window);

		//	Classnames, Datanames, Eventnames
		_c = {};
		_d = {};
		_e = {};

		$.each( [ _c, _d, _e ],
			function( i, o )
			{
				o.add = function( a )
				{
					a = a.split( ' ' );
					for ( var b = 0, l = a.length; b < l; b++ )
					{
						o[ a[ b ] ] = o.ddd( a[ b ] );
					}
				};
			}
		);

		//	Classnames
		_c.ddd = function( c ) { return 'ddd-' + c; };
		_c.add( 'truncated keep' );

		//	Datanames
		_d.ddd = function( d ) { return 'ddd-' + d; };

		//	Eventnames
		_e.ddd = function( e ) { return e + '.ddd'; };
		_e.add( 'resize' );


		//	Only once
		initPlugin = function() {};

	}


})( jQuery );


  }).apply(root, arguments);
});
}(this));

(function(root) {
define("checked-polyfill", ["jquery"], function() {
  return (function() {
/*	Checked Polyfill 1.5
	Provides a .checked class that works like the :checked pseudo class on radio buttons and checkboxes but is available in older browsers such as IE7/8. 
	https://github.com/rdebeasi/checked-polyfill */
(function ($) {
	$.fn.checkedPolyfill = function (options) {
		function supportsChecked() {
			// Create a hidden input, style it, and then check to see whether the styles are applied.
			// Inspired by Modernizr's testStyles function
			var $style = $('<style type="text/css"> #checkedPolyfill-test:checked { margin-left: 123456px; display: none; } </style>}'),
				$checkbox = $('<input type="checkbox" checked id="checkedPolyfill-test" />'),
				result;
			$('head').append($style);
			$('body').append($checkbox);
			if ($checkbox.css('margin-left') === '123456px') {
				result = true;
			} else {
				result = false;
			}
			$style.remove();
			$checkbox.remove();
			return result;
		}

		if( supportsChecked() ) {
			// Browser natively supports :checked and doesn't need the polyfill.
			//return false;
		}

		function checkValue ($elem) {
			var $label = $('label[for="' + $elem.attr('id') + '"]'),
				$labelWrap = $label.parent(".label");
			// TODO: also find labels wrapped around the input
			if ($elem.prop('checked')) {
				$elem.addClass('checked');
				$label.addClass('checked');
				$labelWrap.addClass('checked');
			} else {
				$elem.removeClass('checked');
				$label.removeClass('checked');
				$labelWrap.removeClass('checked');
			}
			// We modify the label as well as the input because authors may want to style the labels based on the state of the chebkox, and IE7 and IE8 don't fully support sibling selectors.
			// For more info: http://www.quirksmode.org/css/selectors/#t11
			return $elem;
		}

		return this.each(function () {
			var $self = $(this);
			if ($self.prop('type') === 'radio') {
				$('input[name="' + $self.prop('name') + '"]').change(function() {
					checkValue($self);
				});
			} else if ($self.prop('type') === 'checkbox') {
				$self.change(function() {
					checkValue($self);
				});
			}
			checkValue($self); // Check value when plugin is first called, in case a value has already been set.
		});
	};
})(jQuery);

  }).apply(root, arguments);
});
}(this));

define('cartware/objectpool/image',[],function() {
	var instance = null;

	function ImagePool() {
		this._warm();
	}

	ImagePool.getInstance = function() {
		if (null === instance) {
			instance = new ImagePool();
		}

		return instance;
	}

	ImagePool.prototype = {
		_used: [],
		_free: [],
		_gcInterval: null,

		_warm: function() {
			var img;
			for (var i = 0; i < 10; i++) {
				img = new Image;
				img._recycleCount = 0;
				img.src = "";

				this._free.push(img);
			}

			if (!this._gcInterval) this._initGc();
		},

		_initGc: function() {
			var self = this
				, callback = function() {
					self._gc.call(self);
				};

			if (!this._gcInterval) {
				this._gcInterval = setInterval(callback, 3000);
			}
		},

		_pauseGc: function() {
			if (this._gcInterval) {
				clearTimeout(this._gcInterval);
				this._gcInterval = null;
			}
		},

		_gc: function() {
			for (var i = 0, l = this._free.length; i < l; i++) {
				this._resetObject(this._free[i]);
			}

			this._free = [];

			if (!this._used.length) this._pauseGc();
		},

		_resetObject: function(img) {
			img.onload = null;
			img.onerror = null;
			img.src = "";

			if ("TRANSPORT" in img) img.TRANSPORT = null;

			img._recycleCount++;

			return img;
		},

		alloc: function() {
			var img;

			if (!this._free.length) this._warm();

			img = this._free.pop();

			this._used.push(img);

			return img;
		},

		release: function(img) {
			var index = this._used.indexOf(this._resetObject(img));
			if (index >= 0) this._free.push(this._used.splice(index, 1)[0]);
		}
	};

	return ImagePool;
});
/*
 * Lazy Load - jQuery plugin for lazy loading images
 *
 * Copyright (c) 2007-2013 Mika Tuupola
 * Copyright (c) 2013-2015 Cartware GmbH
 *
 * Licensed under the MIT license:
 *   http://www.opensource.org/licenses/mit-license.php
 *
 * Project home:
 *   http://www.appelsiini.net/projects/lazyload
 *
 * Version:  1.9.5
 *
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * 	@Cartware:
 *
 *  - Added AMD support
 *	- Added support for image-pooling
 *	- Added custom event "lazyload"
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 */

define('cartware/jquery/lazyload',[
	"jquery",
	"cartware/objectpool/image"
], function(
	$,
	ImagePool,
	undefined
) {
	var $window = $(window);

	var IMAGE_POOL = new ImagePool;

	$.fn.lazyload = function(options) {
		var elements = this;

		var $container;
		var settings = {
			threshold       : 0,
			failure_limit   : 0,
			event           : "scroll",
			effect          : "show",
			container       : window,
			data_attribute  : "original",
			skip_invisible  : false,
			appear          : null,
			load            : null,
			placeholder     : "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAAJcEhZcwAADsQAAA7EAZUrDhsAAAANSURBVBhXYzh8+PB/AAffA0nNPuCLAAAAAElFTkSuQmCC"
		};

		function update() {
			var counter = 0;

			elements.each(function() {
				var $this = $(this);
				if (settings.skip_invisible && !$this.is(":visible")) {
					return;
				}
				if ($.abovethetop(this, settings) ||
					$.leftofbegin(this, settings)) {
					/* Nothing. */
				} else if (!$.belowthefold(this, settings) &&
					!$.rightoffold(this, settings)) {
					$this.trigger("lazyload");
					/* if we found an image we'll load, reset the counter */
					counter = 0;
				} else {
					if (++counter > settings.failure_limit) {
						return false;
					}
				}
			});

		}

		if(options) {
			/* Maintain BC for a couple of versions. */
			if (undefined !== options.failurelimit) {
				options.failure_limit = options.failurelimit;
				delete options.failurelimit;
			}
			if (undefined !== options.effectspeed) {
				options.effect_speed = options.effectspeed;
				delete options.effectspeed;
			}

			$.extend(settings, options);
		}

		/* Cache container as jQuery as object. */
		$container = (settings.container === undefined ||
		settings.container === window) ? $window : $(settings.container);

		/* Fire one scroll event per scroll. Not one scroll event per image. */
		if (0 === settings.event.indexOf("scroll")) {
			$container.bind(settings.event, (function() {
				if ("undefined" !== (typeof _) && "function" === (typeof _.throttle)) {
					return _.throttle(update, 100);
				}

				if ("throttle" in $) {
					return $.throttle(100, update);
				}

				return function() {
					return update();
				}
			})());
		}

		function _imageOnLoad() {
			var self = this.TRANSPORT
				, $self = $(self);

			var original = $self.attr("data-" + settings.data_attribute);
			$self.hide();
			if ($self.is("img")) {
				$self.attr("src", original);
			} else {
				$self.css("background-image", "url('" + original + "')");
			}
			$self[settings.effect](settings.effect_speed);

			self.loaded = true;

			/* Remove image from array so it is not looped next time. */
			var temp = $.grep(elements, function(element) {
				return !element.loaded;
			});

			elements = $(temp);

			if (settings.load) {
				var elements_left = elements.length;
				settings.load.call(self, elements_left, settings);
			}

			IMAGE_POOL.release(this);
		}

		this.each(function() {
			var self = this;
			var $self = $(self);

			self.loaded = false;

			/* If no src attribute given use data:uri. */
			if ($self.attr("src") === undefined || $self.attr("src") === false) {
				if ($self.is("img")) {
					$self.attr("src", settings.placeholder);
				}
			}

			/* When appear is triggered load original image. */
			$self.one("lazyload", function() {
				var src = $self.attr("data-" + settings.data_attribute);

				if ("undefined" == typeof src) return;

				if (!this.loaded) {
					if (settings.appear) {
						var elements_left = elements.length;
						settings.appear.call(self, elements_left, settings);
					}

					var image = IMAGE_POOL.alloc();
					image.onload = _imageOnLoad;
					image.TRANSPORT = self;
					image.src = src;
				}
			});

			/* When wanted event is triggered load original image */
			/* by triggering appear.                              */
			if (0 !== settings.event.indexOf("scroll")) {
				$self.bind(settings.event, function(event) {
					if (!self.loaded) {
						$self.trigger("lazyload");
					}
				});
			}
		});

		/* Check if something appears when window is resized. */
		$window.bind("resize", function() {
			update();
		});

		/* With IOS5 force loading images when navigating with back button. */
		/* Non optimal workaround. */
		if ((/(?:iphone|ipod|ipad).*os 5/gi).test(navigator.appVersion)) {
			$window.bind("pageshow", function(event) {
				if (event.originalEvent && event.originalEvent.persisted) {
					elements.each(function() {
						$(this).trigger("lazyload");
					});
				}
			});
		}

		/* Force initial check if images should appear. */
		$(document).ready(function() {
			update();
		});

		$(window).on('lazyload/update', function() {
			update();
		});

		return this;
	};

	/* Convenience methods in jQuery namespace.           */
	/* Use as  $.belowthefold(element, {threshold : 100, container : window}) */

	$.belowthefold = function(element, settings) {
		var fold;

		if (settings.container === undefined || settings.container === window) {
			fold = (window.innerHeight ? window.innerHeight : $window.height()) + $window.scrollTop();
		} else {
			fold = $(settings.container).offset().top + $(settings.container).height();
		}

		return fold <= $(element).offset().top - settings.threshold;
	};

	$.rightoffold = function(element, settings) {
		var fold;

		if (settings.container === undefined || settings.container === window) {
			fold = $window.width() + $window.scrollLeft();
		} else {
			fold = $(settings.container).offset().left + $(settings.container).width();
		}

		return fold <= $(element).offset().left - settings.threshold;
	};

	$.abovethetop = function(element, settings) {
		var fold;

		if (settings.container === undefined || settings.container === window) {
			fold = $window.scrollTop();
		} else {
			fold = $(settings.container).offset().top;
		}

		return fold >= $(element).offset().top + settings.threshold  + $(element).height();
	};

	$.leftofbegin = function(element, settings) {
		var fold;

		if (settings.container === undefined || settings.container === window) {
			fold = $window.scrollLeft();
		} else {
			fold = $(settings.container).offset().left;
		}

		return fold >= $(element).offset().left + settings.threshold + $(element).width();
	};

	$.inviewport = function(element, settings) {
		return !$.rightoffold(element, settings) && !$.leftofbegin(element, settings) &&
			!$.belowthefold(element, settings) && !$.abovethetop(element, settings);
	};

	/* Custom selectors for your convenience.   */
	/* Use as $("img:below-the-fold").something() or */
	/* $("img").filter(":below-the-fold").something() which is faster */

	$.extend($.expr[':'], {
		"below-the-fold" : function(a) { return $.belowthefold(a, {threshold : 0}); },
		"above-the-top"  : function(a) { return !$.belowthefold(a, {threshold : 0}); },
		"right-of-screen": function(a) { return $.rightoffold(a, {threshold : 0}); },
		"left-of-screen" : function(a) { return !$.rightoffold(a, {threshold : 0}); },
		"in-viewport"    : function(a) { return $.inviewport(a, {threshold : 0}); },
		/* Maintain BC for couple of versions. */
		"above-the-fold" : function(a) { return !$.belowthefold(a, {threshold : 0}); },
		"right-of-fold"  : function(a) { return $.rightoffold(a, {threshold : 0}); },
		"left-of-fold"   : function(a) { return !$.rightoffold(a, {threshold : 0}); }
	});

});
define('shoptheme/components/layout/default',[
	"cartware/components/_base",
	"lodash",
	"jquery",
	"jquery-dotdotdot",
	"checked-polyfill",
	"jquery-lazyload",
	"magnific-popup"
], function(
	Component,
	_,
	$
) {
	return Component.create({
		$init: function() {
			$('input[type="checkbox"], input[type="radio"]').checkedPolyfill();

			this.patchMagnificPopup();
			this.textOverflowEllipsis();
			this.focusValidation();
		},

		patchMagnificPopup: function() {
			var proto = $.magnificPopup.proto,
				open = proto.open,
				close = proto.close,
				$body = $(document.body);

			proto.open = function() {
				var res = open.apply(this, arguments);

				$body.addClass("modal-open");

				return res;
			};

			proto.close = function() {
				var res = close.apply(this, arguments);

				$body.removeClass("modal-open");

				return res;
			};
		},

		textOverflowEllipsis: function() {
			var ellipsisElements = $('.ellipsis');
			ellipsisElements.dotdotdot();
			$(window).resize($.throttle(10, function() { ellipsisElements.dotdotdot(); }));
		},

		focusValidation: function() {
			Validation.prototype.validate = _.wrap(Validation.prototype.validate, function(orig) {
				var result = orig.apply(this, arguments),
					form = this.form;
				
				if (!result) {
					_.delay(function() {
						var $window = $(window),
							windowHeight = $window.height(),
							scrollTop = $window.scrollTop(),
							advices = $(".validation-advice:visible", form),
							adviseVisible;

						adviseVisible = advices.length === 0 || _.some(advices.get(), function(el) {
							var $el = $(el),
								position = $el.offset();

							return position.top + $el.height() >= scrollTop && position.top <= scrollTop + windowHeight;
						});

						if (!adviseVisible) {
							$window.scrollTop(advices.offset().top - 100);
						}
					}, 10);
				}

				return result;
			});
		}
	});
});

define('text!shoptheme/templates/magnific-popup/storeswitch.html',[],function () { return '<div class="popup-wrap">\n\t<% if ("undefined" != typeof title && title != "") { %>\n\t<div class="popup-header">\n\t\t<h4 class="popup-title">\n\t\t\t<%= title %>\n\t\t</h4>\n\t</div>\n\t<% } %>\n\t<div class="popup-body">\n\t\t<p><%= body %></p>\n\t</div>\n\t<div class="popup-footer">\n\t\t<button class="storeswitch-accept btn btn-primary btn-next" onclick="window.location.href=\'<%= url %>\'"><%= proceed %></button>\n\t\t<button class="storeswitch-cancel btn btn-default" onclick="jQuery.magnificPopup.close()"><%= cancel %></button>\n\t</div>\n</div>';});

define('shoptheme/components/layout/page/switch',[
	"cartware/components/_base",
	"jquery",
	"cartware/jquery/bem",
	"text!shoptheme/templates/magnific-popup/storeswitch.html",
	"bootstrap-popover"
], function(
	Component,
	$,
	BEM,
	PopupHtml
) {
	return Component.create({
		block:				null,
		popoverTrigger:		null,
		popoverWrap:		null,
		popoverContainer:	null,
		storeGroupSelect:	null,
		currentGroup:		null,
		newGroup:			null,
		storeViewContainer:	null,
		storeViewSelects:	null,
		currentView:		null,
		currentViewIndex:	null,
		popup: 				null,
		popupAcceptBtn:	null,

		$init: function() {
			var self = this;
			var url = null;

			this.block = BEM.block("page-switch");
			this.popupAcceptBtn = $(".storeswitch-accept");

			this.popoverTrigger = this.block.getElements("popover-trigger");
			this.popoverWrap = this.block.getElements("popover");
			this.popoverContainer = this.block.find("." + BEM.buildBlockClassname("page-switch-popover"));

			this.storeGroupSelect = this.popoverContainer.bemGetElements("storegroup-select");
			this.storeViewContainer = this.popoverContainer.bemGetElements("storeview-container");
			this.storeViewSelects = this.popoverContainer.bemGetElements("storeview-select");
			this.currentGroup = this.storeGroupSelect.val();

			var storeGroup = this.currentGroup;

			this.popoverTrigger.popover({
				content: function() {
					return self.popoverContainer;
				},
				template: '<div class="popover popover-servicemenu" role="tooltip"><div class="arrow"></div><div class="popover-content"></div></div>',
				html: true,
				placement: "bottom"
			});

			this.storeGroupSelect.on("change", function() {
				var select = $(this);
				storeGroup = select.val();
				var	activeStoreViewContainer = self.storeViewContainer.filter('[data-store-group="' + storeGroup + '"]'),
					options = activeStoreViewContainer.find("option").filter(function() { return $(this).prop("value"); }),
					itemCount = $(".btn-mini-cart span").text();

				if (options.length === 1) {
					if (itemCount > 0) {
						$.magnificPopup.open({
							items: {
								src: self._renderPopup({
									url: options.prop("value"),
									title: Translator.translate("Notice"),
									body: Translator.translate("Changing the country will empty your shopping cart."),
									cancel: Translator.translate("Cancel"),
									proceed: Translator.translate("Proceed")
								})
							},
							callbacks: {
								open: function () {
									self.popoverTrigger.popover('hide');
									self.storeViewContainer.bemRemoveModifier("active");
								}
							}
						});
					} else {
						self.storeViewContainer.bemRemoveModifier("active");
						document.location.href = options.prop("value");
						return;
					}
				}

				self.storeViewContainer.bemRemoveModifier("active");
				activeStoreViewContainer.bemAddModifier("active");
			});

			this.storeViewSelects.on("change", function(e) {
				url = $(this).val();
				var itemCount = $(".btn-mini-cart span").text(),
					view = $(this);

				if (itemCount > 0 && storeGroup != self.currentGroup) {
					$.magnificPopup.open({
						items: {
							src: self._renderPopup({
								url: url,
								title: Translator.translate("Notice"),
								body: Translator.translate("Changing the country will empty your shopping cart."),
								cancel: Translator.translate("Cancel"),
								proceed: Translator.translate("Proceed")
							})
						},
						callbacks: {
							open: function () {
								self.popoverTrigger.popover('hide');
								view[0].selectedIndex = 0;

								$.magnificPopup.instance.close = function () {

									// Call the original close method to close the popup
									$.magnificPopup.proto.close.call(this);
								};
							}
						}
					});
				} else {
					document.location.href = url;
				}
			});
		},

		_renderPopup: function(data) {
			if (null === this.popup) {
				this.popup = _.template(PopupHtml);
			}

			return this.popup(data);
		}
	});
});
define('shoptheme/components/layout/page/sidebar-related',[
	"jquery",
	"lodash",
	"cartware/components/_base"
], function(
	$,
	_,
	Component
) {
	return Component.create({

		$init: function() {
			var self = this;

			this.container = $('.b_main-container__col2-right-col-right--sidebar');
			this.headlines = this.container.find('.b_main-container__sidebar-headline--container');

			this.headlines.on('click', function() {
				self.container.toggleClass('active');
			});
		}
	});

});
define('shoptheme/components/layout/lazyload',[
	"cartware/components/_base",
	"jquery",
	"jquery-lazyload",
	"cartware-slider"
], function(
	Component,
	$
) {
	var __slideshows = [];

	return Component.create({
		$init: function() {
			var self = this;

			this.initializeLazyload();
			this.initializeSlider();

			$(document).on('product/changeGallerySimple', _.debounce(function() {
				self.initializeSlider();
			}, 100));
		},

		initializeLazyload: function() {
			$("img").lazyload({
				load: function() {
					$(this).trigger("cartware/lazyload");
				}
			});
		},

		initializeSlider: function() {
			$(".cartware-slider").each(function() {
				if (__slideshows.indexOf(this) < 0) {
					var slideshow = $(this);

					slideshow.find("img").lazyload();

					__slideshows.push(this);
				}
			});
		}
	});
});
define('shoptheme/components/layout/messages',[
	"cartware/components/_base",
	"jquery"
], function(
	Component,
	$
) {
	return Component.create({
		$init: function() {
			var messages = $("ul.messages");
			if (messages.length > 0) {
				$(".notice-msg ul li", messages).prepend("<i class='icon-message-notice'></i>");
				$(".success-msg ul li", messages).prepend("<i class='icon-added-to-cart-check'></i>");
				$(".error-msg ul li", messages).prepend("<i class='icon-message-error'></i>");
			}
		}
	});
});

define("json!shoptheme/etc/breakpoints.json", function(){ return {
  	"mobile": 479,
  	"xs": 767,
	"sm": 991,
	"md": 1199,
	"lg": 1649
};});

(function(root) {
define("media-match", [], function() {
  return (function() {
/* MediaMatch v.2.0.2 - Testing css media queries in Javascript. Authors & copyright (c) 2013: WebLinc, David Knight. */

window.matchMedia || (window.matchMedia = function (win) {
    'use strict';

    // Internal globals
    var _doc        = win.document,
        _viewport   = _doc.documentElement,
        _queries    = [],
        _queryID    = 0,
        _type       = '',
        _features   = {},
                    // only screen
                    // only screen and
                    // not screen
                    // not screen and
                    // screen
                    // screen and
        _typeExpr   = /\s*(only|not)?\s*(screen|print|[a-z\-]+)\s*(and)?\s*/i,
                    // (-vendor-min-width: 300px)
                    // (min-width: 300px)
                    // (width: 300px)
                    // (width)
                    // (orientation: portrait|landscape)
        _mediaExpr  = /^\s*\(\s*(-[a-z]+-)?(min-|max-)?([a-z\-]+)\s*(:?\s*([0-9]+(\.[0-9]+)?|portrait|landscape)(px|em|dppx|dpcm|rem|%|in|cm|mm|ex|pt|pc|\/([0-9]+(\.[0-9]+)?))?)?\s*\)\s*$/,
        _timer      = 0,

        // Helper methods

        /*
            _matches
         */
        _matches = function (media) {
            // screen and (min-width: 400px), screen and (max-width: 500px)
            var mql         = (media.indexOf(',') !== -1 && media.split(',')) || [media],
                mqIndex     = mql.length - 1,
                mqLength    = mqIndex,
                mq          = null,

                // not screen, screen
                negateType      = null,
                negateTypeFound = '',
                negateTypeIndex = 0,
                negate          = false,
                type            = '',

                // (min-width: 400px), (min-width)
                exprListStr = '',
                exprList    = null,
                exprIndex   = 0,
                exprLength  = 0,
                expr        = null,

                prefix      = '',
                length      = '',
                unit        = '',
                value       = '',
                feature     = '',

                match       = false;

            if (media === '') {
                return true;
            }

            do {
                mq          = mql[mqLength - mqIndex];
                negate      = false;
                negateType  = mq.match(_typeExpr);

                if (negateType) {
                    negateTypeFound = negateType[0];
                    negateTypeIndex = negateType.index;
                }

                if (!negateType || ((mq.substring(0, negateTypeIndex).indexOf('(') === -1) && (negateTypeIndex || (!negateType[3] && negateTypeFound !== negateType.input)))) {
                    match = false;
                    continue;
                }

                exprListStr = mq;

                negate = negateType[1] === 'not';

                if (!negateTypeIndex) {
                    type        =  negateType[2];
                    exprListStr = mq.substring(negateTypeFound.length);
                }

                // Test media type
                // Test type against this device or if 'all' or empty ''
                match       = type === _type || type === 'all' || type === '';

                exprList    = (exprListStr.indexOf(' and ') !== -1 && exprListStr.split(' and ')) || [exprListStr];
                exprIndex   = exprList.length - 1;
                exprLength  = exprIndex;

                if (match && exprIndex >= 0 && exprListStr !== '') {
                    do {
                        expr = exprList[exprIndex].match(_mediaExpr);

                        if (!expr || !_features[expr[3]]) {
                            match = false;
                            break;
                        }

                        prefix  = expr[2];
                        length  = expr[5];
                        value   = length;
                        unit    = expr[7];
                        feature = _features[expr[3]];

                        // Convert unit types
                        if (unit) {
                            if (unit === 'px') {
                                // If unit is px
                                value = Number(length);
                            } else if (unit === 'em' || unit === 'rem') {
                                // Convert relative length unit to pixels
                                // Assumed base font size is 16px
                                value = 16 * length;
                            } else if (expr[8]) {
                                // Convert aspect ratio to decimal
                                value = (length / expr[8]).toFixed(2);
                            } else if (unit === 'dppx') {
                                // Convert resolution dppx unit to pixels
                                value = length * 96;
                            } else if (unit === 'dpcm') {
                                // Convert resolution dpcm unit to pixels
                                value = length * 0.3937;
                            } else {
                                // default
                                value = Number(length);
                            }
                        }

                        // Test for prefix min or max
                        // Test value against feature
                        if (prefix === 'min-' && value) {
                            match = feature >= value;
                        } else if (prefix === 'max-' && value) {
                            match = feature <= value;
                        } else if (value) {
                            match = feature === value;
                        } else {
                            match = !!feature;
                        }

                        // If 'match' is false, break loop
                        // Continue main loop through query list
                        if (!match) {
                            break;
                        }
                    } while (exprIndex--);
                }

                // If match is true, break loop
                // Once matched, no need to check other queries
                if (match) {
                    break;
                }
            } while (mqIndex--);

            return negate ? !match : match;
        },

        /*
            _setFeature
         */
        _setFeature = function () {
            // Sets properties of '_features' that change on resize and/or orientation.
            var w   = win.innerWidth || _viewport.clientWidth,
                h   = win.innerHeight || _viewport.clientHeight,
                dw  = win.screen.width,
                dh  = win.screen.height,
                c   = win.screen.colorDepth,
                x   = win.devicePixelRatio;

            _features.width                     = w;
            _features.height                    = h;
            _features['aspect-ratio']           = (w / h).toFixed(2);
            _features['device-width']           = dw;
            _features['device-height']          = dh;
            _features['device-aspect-ratio']    = (dw / dh).toFixed(2);
            _features.color                     = c;
            _features['color-index']            = Math.pow(2, c);
            _features.orientation               = (h >= w ? 'portrait' : 'landscape');
            _features.resolution                = (x && x * 96) || win.screen.deviceXDPI || 96;
            _features['device-pixel-ratio']     = x || 1;
        },

        /*
            _watch
         */
        _watch = function () {
            clearTimeout(_timer);

            _timer = setTimeout(function () {
                var query   = null,
                    qIndex  = _queryID - 1,
                    qLength = qIndex,
                    match   = false;

                if (qIndex >= 0) {
                    _setFeature();

                    do {
                        query = _queries[qLength - qIndex];

                        if (query) {
                            match = _matches(query.mql.media);

                            if ((match && !query.mql.matches) || (!match && query.mql.matches)) {
                                query.mql.matches = match;

                                if (query.listeners) {
                                    for (var i = 0, il = query.listeners.length; i < il; i++) {
                                        if (query.listeners[i]) {
                                            query.listeners[i].call(win, query.mql);
                                        }
                                    }
                                }
                            }
                        }
                    } while(qIndex--);
                }

                
            }, 10);
        },

        /*
            _init
         */
        _init = function () {
            var head        = _doc.getElementsByTagName('head')[0],
                style       = _doc.createElement('style'),
                info        = null,
                typeList    = ['screen', 'print', 'speech', 'projection', 'handheld', 'tv', 'braille', 'embossed', 'tty'],
                typeIndex   = 0,
                typeLength  = typeList.length,
                cssText     = '#mediamatchjs { position: relative; z-index: 0; }',
                eventPrefix = '',
                addEvent    = win.addEventListener || (eventPrefix = 'on') && win.attachEvent;

            style.type  = 'text/css';
            style.id    = 'mediamatchjs';

            head.appendChild(style);

            // Must be placed after style is inserted into the DOM for IE
            info = (win.getComputedStyle && win.getComputedStyle(style)) || style.currentStyle;

            // Create media blocks to test for media type
            for ( ; typeIndex < typeLength; typeIndex++) {
                cssText += '@media ' + typeList[typeIndex] + ' { #mediamatchjs { position: relative; z-index: ' + typeIndex + ' } }';
            }

            // Add rules to style element
            if (style.styleSheet) {
                style.styleSheet.cssText = cssText;
            } else {
                style.textContent = cssText;
            }

            // Get media type
            _type = typeList[(info.zIndex * 1) || 0];

            head.removeChild(style);

            _setFeature();

            // Set up listeners
            addEvent(eventPrefix + 'resize', _watch);
            addEvent(eventPrefix + 'orientationchange', _watch);
        };

    _init();

    /*
        A list of parsed media queries, ex. screen and (max-width: 400px), screen and (max-width: 800px)
    */
    return function (media) {
        var id  = _queryID,
            mql = {
                matches         : false,
                media           : media,
                addListener     : function addListener(listener) {
                    _queries[id].listeners || (_queries[id].listeners = []);
                    listener && _queries[id].listeners.push(listener);
                },
                removeListener  : function removeListener(listener) {
                    var query   = _queries[id],
                        i       = 0,
                        il      = 0;

                    if (!query) {
                        return;
                    }

                    il = query.listeners.length;

                    for ( ; i < il; i++) {
                        if (query.listeners[i] === listener) {
                            query.listeners.splice(i, 1);
                        }
                    }
                }
            };

        if (media === '') {
            mql.matches = true;
            return mql;
        }

        mql.matches = _matches(media);

        _queryID = _queries.push({
            mql         : mql,
            listeners   : null
        });

        return mql;
    };
}(window));

  }).apply(root, arguments);
});
}(this));

define('implementations',{
	"enquire": [{
		isAvailable: function() {
			return ("matchMedia" in window) && "undefined" !== (typeof Modernizr) && Modernizr.mq('only all');
		},

		implementation: 'enquire'
	}, {
		isAvailable: function() {
			return Modernizr.mq('only all');
		},

		implementation: 'depend!enquire[media-match]'
	}, {
		isAvailable: true,

		module: function() {
			function isFunction(target) {
				return typeof target === 'function';
			}

			function isArray(target) {
				return Object.prototype.toString.apply(target) === '[object Array]';
			}

			function each(collection, fn) {
				var i      = 0,
					length = collection.length,
					cont;

				for(i; i < length; i++) {
					cont = fn(collection[i], i);
					if(cont === false) {
						break; //allow early exit
					}
				}
			}

			function Shim () {}
			Shim.prototype = {
				register : function(q, options, shouldDegrade) {
					if (shouldDegrade) {
						//normalise to object in an array
						if (isFunction(options)) {
							options = { match : options };
						}
						if (!isArray(options)) {
							options = [options];
						}
						each(options, function(handler) {
							if (isFunction(handler)) {
								handler = { match : handler };
							}

							handler.match();
						});
					}

					return this;
				},

				unregister : function(q, handler) {
					return this
				}
			};

			return new Shim();
		}
	}]
});
/**
 * AMD-Feature - A loader plugin for AMD loaders.
 *
 * https://github.com/jensarps/AMD-feature
 *
 * @author Jens Arps - http://jensarps.de/
 * @license MIT or BSD - https://github.com/jensarps/AMD-feature/blob/master/LICENSE
 * @version 1.2.0
 */
define('feature',['implementations'], function (implementations) {

  // Check availability of implementation
  function isAvailable (impl, name) {
    var isFunction = typeof impl.isAvailable === 'function';
    return (isFunction && impl.isAvailable(name)) || (!isFunction && impl.isAvailable);
  }

  return {

    load: function (name, req, load, config) {

      var i, impl, m, toLoad,
          featureInfo = implementations[name],
          hasMultipleImpls = Object.prototype.toString.call(featureInfo) === '[object Array]';

      if (config.isBuild && hasMultipleImpls) {
        // In build context, we want all possible
        // implementations included.
        for (i = 0, m = featureInfo.length; i < m; i++) {
          impl = featureInfo[i].implementation;
          if (typeof impl === 'function') {
            impl = impl.call(featureInfo[i], name);
          }
          if (impl) {
            req([impl], load);
          }
        }

        // We're done here now.
        return;
      }

      if (hasMultipleImpls) {
        // We have different implementations available,
        // test for the one to use.
        for (i = 0, m = featureInfo.length; i < m; i++) {
          impl = featureInfo[i];
          if (isAvailable(impl, name)) {
            break;
          }
          impl = null;
        }
      } else {
        impl = featureInfo;
        // The only implementation can have isAvailable check
        if (impl && typeof impl === 'object' && ('isAvailable' in impl) && !isAvailable(impl, name)) {
          impl = null;
        }
      }

      if (impl) {
        if (typeof impl === 'string') {
          toLoad = impl;
        } else if (typeof impl === 'object' && ('module' in impl)) {
          load(typeof impl.module === 'function' ? impl.module(name) : impl.module);
          return;
        } else {
          toLoad = impl.implementation;
          if (typeof toLoad === 'function') {
            toLoad = impl.implementation(name);
          }
        }
        req([toLoad], load);
      } else {
        req([], load);
      }
    }
  };
});

/*!
 * enquire.js v2.1.2 - Awesome Media Queries in JavaScript
 * Copyright (c) 2014 Nick Williams - http://wicky.nillia.ms/enquire.js
 * License: MIT (http://www.opensource.org/licenses/mit-license.php)
 */

;(function (name, context, factory) {
	if (typeof module !== 'undefined' && module.exports) {
		module.exports = factory();
	}
	else if (typeof define === 'function' && define.amd) {
		define('enquire',[],function() {
			return (context[name] = factory());
		});
	}
	else {
		context[name] = factory();
	}
}('enquire', this, function () {

	'use strict';

	var matchMedia = window.matchMedia;

    /*jshint unused:false */
    /**
     * Helper function for iterating over a collection
     *
     * @param collection
     * @param fn
     */
    function each(collection, fn) {
        var i      = 0,
            length = collection.length,
            cont;

        for(i; i < length; i++) {
            cont = fn(collection[i], i);
            if(cont === false) {
                break; //allow early exit
            }
        }
    }

    /**
     * Helper function for determining whether target object is an array
     *
     * @param target the object under test
     * @return {Boolean} true if array, false otherwise
     */
    function isArray(target) {
        return Object.prototype.toString.apply(target) === '[object Array]';
    }

    /**
     * Helper function for determining whether target object is a function
     *
     * @param target the object under test
     * @return {Boolean} true if function, false otherwise
     */
    function isFunction(target) {
        return typeof target === 'function';
    }

    /**
     * Delegate to handle a media query being matched and unmatched.
     *
     * @param {object} options
     * @param {function} options.match callback for when the media query is matched
     * @param {function} [options.unmatch] callback for when the media query is unmatched
     * @param {function} [options.setup] one-time callback triggered the first time a query is matched
     * @param {boolean} [options.deferSetup=false] should the setup callback be run immediately, rather than first time query is matched?
     * @constructor
     */
    function QueryHandler(options) {
        this.options = options;
        !options.deferSetup && this.setup();
    }
    QueryHandler.prototype = {

        /**
         * coordinates setup of the handler
         *
         * @function
         */
        setup : function() {
            if(this.options.setup) {
                this.options.setup();
            }
            this.initialised = true;
        },

        /**
         * coordinates setup and triggering of the handler
         *
         * @function
         */
        on : function() {
            !this.initialised && this.setup();
            this.options.match && this.options.match();
        },

        /**
         * coordinates the unmatch event for the handler
         *
         * @function
         */
        off : function() {
            this.options.unmatch && this.options.unmatch();
        },

        /**
         * called when a handler is to be destroyed.
         * delegates to the destroy or unmatch callbacks, depending on availability.
         *
         * @function
         */
        destroy : function() {
            this.options.destroy ? this.options.destroy() : this.off();
        },

        /**
         * determines equality by reference.
         * if object is supplied compare options, if function, compare match callback
         *
         * @function
         * @param {object || function} [target] the target for comparison
         */
        equals : function(target) {
            return this.options === target || this.options.match === target;
        }

    };
    /**
     * Represents a single media query, manages it's state and registered handlers for this query
     *
     * @constructor
     * @param {string} query the media query string
     * @param {boolean} [isUnconditional=false] whether the media query should run regardless of whether the conditions are met. Primarily for helping older browsers deal with mobile-first design
     */
    function MediaQuery(query, isUnconditional) {
        this.query = query;
        this.isUnconditional = isUnconditional;
        this.handlers = [];
        this.mql = matchMedia(query);

        var self = this;
        this.listener = function(mql) {
            self.mql = mql;
            self.assess();
        };
        this.mql.addListener(this.listener);
    }
    MediaQuery.prototype = {

        /**
         * add a handler for this query, triggering if already active
         *
         * @param {object} handler
         * @param {function} handler.match callback for when query is activated
         * @param {function} [handler.unmatch] callback for when query is deactivated
         * @param {function} [handler.setup] callback for immediate execution when a query handler is registered
         * @param {boolean} [handler.deferSetup=false] should the setup callback be deferred until the first time the handler is matched?
         */
        addHandler : function(handler) {
            var qh = new QueryHandler(handler);
            this.handlers.push(qh);

            this.matches() && qh.on();
        },

        /**
         * removes the given handler from the collection, and calls it's destroy methods
         * 
         * @param {object || function} handler the handler to remove
         */
        removeHandler : function(handler) {
            var handlers = this.handlers;
            each(handlers, function(h, i) {
                if(h.equals(handler)) {
                    h.destroy();
                    return !handlers.splice(i,1); //remove from array and exit each early
                }
            });
        },

        /**
         * Determine whether the media query should be considered a match
         * 
         * @return {Boolean} true if media query can be considered a match, false otherwise
         */
        matches : function() {
            return this.mql.matches || this.isUnconditional;
        },

        /**
         * Clears all handlers and unbinds events
         */
        clear : function() {
            each(this.handlers, function(handler) {
                handler.destroy();
            });
            this.mql.removeListener(this.listener);
            this.handlers.length = 0; //clear array
        },

        /*
         * Assesses the query, turning on all handlers if it matches, turning them off if it doesn't match
         */
        assess : function() {
            var action = this.matches() ? 'on' : 'off';

            each(this.handlers, function(handler) {
                handler[action]();
            });
        }
    };
    /**
     * Allows for registration of query handlers.
     * Manages the query handler's state and is responsible for wiring up browser events
     *
     * @constructor
     */
    function MediaQueryDispatch () {
        if(!matchMedia) {
            throw new Error('matchMedia not present, legacy browsers require a polyfill');
        }

        this.queries = {};
        this.browserIsIncapable = !matchMedia('only all').matches;
    }

    MediaQueryDispatch.prototype = {

        /**
         * Registers a handler for the given media query
         *
         * @param {string} q the media query
         * @param {object || Array || Function} options either a single query handler object, a function, or an array of query handlers
         * @param {function} options.match fired when query matched
         * @param {function} [options.unmatch] fired when a query is no longer matched
         * @param {function} [options.setup] fired when handler first triggered
         * @param {boolean} [options.deferSetup=false] whether setup should be run immediately or deferred until query is first matched
         * @param {boolean} [shouldDegrade=false] whether this particular media query should always run on incapable browsers
         */
        register : function(q, options, shouldDegrade) {
            var queries         = this.queries,
                isUnconditional = shouldDegrade && this.browserIsIncapable;

            if(!queries[q]) {
                queries[q] = new MediaQuery(q, isUnconditional);
            }

            //normalise to object in an array
            if(isFunction(options)) {
                options = { match : options };
            }
            if(!isArray(options)) {
                options = [options];
            }
            each(options, function(handler) {
                if (isFunction(handler)) {
                    handler = { match : handler };
                }
                queries[q].addHandler(handler);
            });

            return this;
        },

        /**
         * unregisters a query and all it's handlers, or a specific handler for a query
         *
         * @param {string} q the media query to target
         * @param {object || function} [handler] specific handler to unregister
         */
        unregister : function(q, handler) {
            var query = this.queries[q];

            if(query) {
                if(handler) {
                    query.removeHandler(handler);
                }
                else {
                    query.clear();
                    delete this.queries[q];
                }
            }

            return this;
        }
    };

	return new MediaQueryDispatch();

}));

/** @license
 * Plugin to load JS files that have dependencies but aren't wrapped into
 * `define` calls.
 * Author: Miller Medeiros
 * Version: 0.1.0 (2011/12/13)
 * Released under the MIT license
 */
define('depend',[],function () {

    var rParts = /^(.*)\[([^\]]*)\]$/;

    return {

        //example: depend!bar[jquery,lib/foo]
        load : function(name, req, onLoad, config){
            var parts = rParts.exec(name);

            req(parts[2].split(','), function(){
                req([parts[1]], function(mod){
                    onLoad(mod);
                });
            });
        }

    };

});


define('enquire-patched',[
	"media-match",
	"feature!enquire"
], function(
	undefined1,
	enquire
) {
	return enquire;
});
/**
 * THIS IS A FORK IN ORDER TO PROVIDE A NPM PACKAGE OF IT!
 *
 * ORIGINAL:
 * Copyright © 2016 Ben Kamens | MIT license | https://github.com/kamens/jQuery-menu-aim
 *
 * menu-aim is a jQuery plugin for dropdown menus that can differentiate
 * between a user trying hover over a dropdown item vs trying to navigate into
 * a submenu's contents.
 *
 * menu-aim assumes that you have are using a menu with submenus that expand
 * to the menu's right. It will fire events when the user's mouse enters a new
 * dropdown item *and* when that item is being intentionally hovered over.
 *
 * __________________________
 * | Monkeys  >|   Gorilla  |
 * | Gorillas >|   Content  |
 * | Chimps   >|   Here     |
 * |___________|____________|
 *
 * In the above example, "Gorillas" is selected and its submenu content is
 * being shown on the right. Imagine that the user's cursor is hovering over
 * "Gorillas." When they move their mouse into the "Gorilla Content" area, they
 * may briefly hover over "Chimps." This shouldn't close the "Gorilla Content"
 * area.
 *
 * This problem is normally solved using timeouts and delays. menu-aim tries to
 * solve this by detecting the direction of the user's mouse movement. This can
 * make for quicker transitions when navigating up and down the menu. The
 * experience is hopefully similar to amazon.com/'s "Shop by Department"
 * dropdown.
 *
 * Use like so:
 *
 *      $("#menu").menuAim({
 *          activate: $.noop,  // fired on row activation
 *          deactivate: $.noop  // fired on row deactivation
 *      });
 *
 *  ...to receive events when a menu's row has been purposefully (de)activated.
 *
 * The following options can be passed to menuAim. All functions execute with
 * the relevant row's HTML element as the execution context ('this'):
 *
 *      .menuAim({
 *          // Function to call when a row is purposefully activated. Use this
 *          // to show a submenu's content for the activated row.
 *          activate: function() {},
 *
 *          // Function to call when a row is deactivated.
 *          deactivate: function() {},
 *
 *          // Function to call when mouse enters a menu row. Entering a row
 *          // does not mean the row has been activated, as the user may be
 *          // mousing over to a submenu.
 *          enter: function() {},
 *
 *          // Function to call when mouse exits a menu row.
 *          exit: function() {},
 *
 *          // Selector for identifying which elements in the menu are rows
 *          // that can trigger the above events. Defaults to "> li".
 *          rowSelector: "> li",
 *
 *          // You may have some menu rows that aren't submenus and therefore
 *          // shouldn't ever need to "activate." If so, filter submenu rows w/
 *          // this selector. Defaults to "*" (all elements).
 *          submenuSelector: "*",
 *
 *          // Direction the submenu opens relative to the main menu. Can be
 *          // left, right, above, or below. Defaults to "right".
 *          submenuDirection: "right"
 *      });
 *
*/
(function (factory) {
  if (typeof exports === 'object') {
      factory(require('jquery'));
  } else if (typeof define === 'function' && define.amd) {
      define('jquery-menu-aim',['jquery'], factory);
  } else {
      factory(jQuery);
  }
}(function ($) {
    $.fn.menuAim = function(opts) {
        // Initialize menu-aim for all elements in jQuery collection
        this.each(function() {
            init.call(this, opts);
        });

        return this;
    };

    function init(opts) {
        var $menu = $(this),
            activeRow = null,
            mouseLocs = [],
            lastDelayLoc = null,
            timeoutId = null,
            options = $.extend({
                rowSelector: "> li",
                submenuSelector: "*",
                submenuDirection: "right",
                tolerance: 75,  // bigger = more forgivey when entering submenu
                enter: $.noop,
                exit: $.noop,
                activate: $.noop,
                deactivate: $.noop,
                exitMenu: $.noop
            }, opts);

        var MOUSE_LOCS_TRACKED = 3,  // number of past mouse locations to track
            DELAY = 300;  // ms delay when user appears to be entering submenu

        /**
         * Keep track of the last few locations of the mouse.
         */
        var mousemoveDocument = function(e) {
                mouseLocs.push({x: e.pageX, y: e.pageY});

                if (mouseLocs.length > MOUSE_LOCS_TRACKED) {
                    mouseLocs.shift();
                }
            };

        /**
         * Cancel possible row activations when leaving the menu entirely
         */
        var mouseleaveMenu = function() {
                if (timeoutId) {
                    clearTimeout(timeoutId);
                }

                // If exitMenu is supplied and returns true, deactivate the
                // currently active row on menu exit.
                if (options.exitMenu(this)) {
                    if (activeRow) {
                        options.deactivate(activeRow);
                    }

                    activeRow = null;
                }
            };

        /**
         * Trigger a possible row activation whenever entering a new row.
         */
        var mouseenterRow = function() {
                if (timeoutId) {
                    // Cancel any previous activation delays
                    clearTimeout(timeoutId);
                }

                options.enter(this);
                possiblyActivate(this);
            },
            mouseleaveRow = function() {
                options.exit(this);
            };

        /*
         * Immediately activate a row if the user clicks on it.
         */
        var clickRow = function() {
                activate(this);
            };

        /**
         * Activate a menu row.
         */
        var activate = function(row) {
                if (row == activeRow) {
                    return;
                }

                if (activeRow) {
                    options.deactivate(activeRow);
                }

                options.activate(row);
                activeRow = row;
            };

        /**
         * Possibly activate a menu row. If mouse movement indicates that we
         * shouldn't activate yet because user may be trying to enter
         * a submenu's content, then delay and check again later.
         */
        var possiblyActivate = function(row) {
                var delay = activationDelay();

                if (delay) {
                    timeoutId = setTimeout(function() {
                        possiblyActivate(row);
                    }, delay);
                } else {
                    activate(row);
                }
            };

        /**
         * Return the amount of time that should be used as a delay before the
         * currently hovered row is activated.
         *
         * Returns 0 if the activation should happen immediately. Otherwise,
         * returns the number of milliseconds that should be delayed before
         * checking again to see if the row should be activated.
         */
        var activationDelay = function() {
                if (!activeRow || !$(activeRow).is(options.submenuSelector)) {
                    // If there is no other submenu row already active, then
                    // go ahead and activate immediately.
                    return 0;
                }

                var offset = $menu.offset(),
                    upperLeft = {
                        x: offset.left,
                        y: offset.top - options.tolerance
                    },
                    upperRight = {
                        x: offset.left + $menu.outerWidth(),
                        y: upperLeft.y
                    },
                    lowerLeft = {
                        x: offset.left,
                        y: offset.top + $menu.outerHeight() + options.tolerance
                    },
                    lowerRight = {
                        x: offset.left + $menu.outerWidth(),
                        y: lowerLeft.y
                    },
                    loc = mouseLocs[mouseLocs.length - 1],
                    prevLoc = mouseLocs[0];

                if (!loc) {
                    return 0;
                }

                if (!prevLoc) {
                    prevLoc = loc;
                }

                if (prevLoc.x < offset.left || prevLoc.x > lowerRight.x ||
                    prevLoc.y < offset.top || prevLoc.y > lowerRight.y) {
                    // If the previous mouse location was outside of the entire
                    // menu's bounds, immediately activate.
                    return 0;
                }

                if (lastDelayLoc &&
                        loc.x == lastDelayLoc.x && loc.y == lastDelayLoc.y) {
                    // If the mouse hasn't moved since the last time we checked
                    // for activation status, immediately activate.
                    return 0;
                }

                // Detect if the user is moving towards the currently activated
                // submenu.
                //
                // If the mouse is heading relatively clearly towards
                // the submenu's content, we should wait and give the user more
                // time before activating a new row. If the mouse is heading
                // elsewhere, we can immediately activate a new row.
                //
                // We detect this by calculating the slope formed between the
                // current mouse location and the upper/lower right points of
                // the menu. We do the same for the previous mouse location.
                // If the current mouse location's slopes are
                // increasing/decreasing appropriately compared to the
                // previous's, we know the user is moving toward the submenu.
                //
                // Note that since the y-axis increases as the cursor moves
                // down the screen, we are looking for the slope between the
                // cursor and the upper right corner to decrease over time, not
                // increase (somewhat counterintuitively).
                function slope(a, b) {
                    return (b.y - a.y) / (b.x - a.x);
                };

                var decreasingCorner = upperRight,
                    increasingCorner = lowerRight;

                // Our expectations for decreasing or increasing slope values
                // depends on which direction the submenu opens relative to the
                // main menu. By default, if the menu opens on the right, we
                // expect the slope between the cursor and the upper right
                // corner to decrease over time, as explained above. If the
                // submenu opens in a different direction, we change our slope
                // expectations.
                if (options.submenuDirection == "left") {
                    decreasingCorner = lowerLeft;
                    increasingCorner = upperLeft;
                } else if (options.submenuDirection == "below") {
                    decreasingCorner = lowerRight;
                    increasingCorner = lowerLeft;
                } else if (options.submenuDirection == "above") {
                    decreasingCorner = upperLeft;
                    increasingCorner = upperRight;
                }

                var decreasingSlope = slope(loc, decreasingCorner),
                    increasingSlope = slope(loc, increasingCorner),
                    prevDecreasingSlope = slope(prevLoc, decreasingCorner),
                    prevIncreasingSlope = slope(prevLoc, increasingCorner);

                if (decreasingSlope < prevDecreasingSlope &&
                        increasingSlope > prevIncreasingSlope) {
                    // Mouse is moving from previous location towards the
                    // currently activated submenu. Delay before activating a
                    // new menu row, because user may be moving into submenu.
                    lastDelayLoc = loc;
                    return DELAY;
                }

                lastDelayLoc = null;
                return 0;
            };

        /**
         * Hook up initial menu events
         */
        $menu
            .mouseleave(mouseleaveMenu)
            .find(options.rowSelector)
                .mouseenter(mouseenterRow)
                .mouseleave(mouseleaveRow)
                .click(clickRow);

        $(document).mousemove(mousemoveDocument);

    };
}));

(function(root) {
define("bootstrap-collapse", ["jquery","bootstrap-transition","bootstrap-prototype-fix"], function() {
  return (function() {
/* ========================================================================
 * Bootstrap: collapse.js v3.3.6
 * http://getbootstrap.com/javascript/#collapse
 * ========================================================================
 * Copyright 2011-2015 Twitter, Inc.
 * Licensed under MIT (https://github.com/twbs/bootstrap/blob/master/LICENSE)
 * ======================================================================== */


+function ($) {
  'use strict';

  // COLLAPSE PUBLIC CLASS DEFINITION
  // ================================

  var Collapse = function (element, options) {
    this.$element      = $(element)
    this.options       = $.extend({}, Collapse.DEFAULTS, options)
    this.$trigger      = $('[data-toggle="collapse"][href="#' + element.id + '"],' +
                           '[data-toggle="collapse"][data-target="#' + element.id + '"]')
    this.transitioning = null

    if (this.options.parent) {
      this.$parent = this.getParent()
    } else {
      this.addAriaAndCollapsedClass(this.$element, this.$trigger)
    }

    if (this.options.toggle) this.toggle()
  }

  Collapse.VERSION  = '3.3.6'

  Collapse.TRANSITION_DURATION = 350

  Collapse.DEFAULTS = {
    toggle: true
  }

  Collapse.prototype.dimension = function () {
    var hasWidth = this.$element.hasClass('width')
    return hasWidth ? 'width' : 'height'
  }

  Collapse.prototype.show = function () {
    if (this.transitioning || this.$element.hasClass('in')) return

    var activesData
    var actives = this.$parent && this.$parent.children('.panel').children('.in, .collapsing')

    if (actives && actives.length) {
      activesData = actives.data('bs.collapse')
      if (activesData && activesData.transitioning) return
    }

    var startEvent = $.Event('show.bs.collapse')
    this.$element.trigger(startEvent)
    if (startEvent.isDefaultPrevented()) return

    if (actives && actives.length) {
      Plugin.call(actives, 'hide')
      activesData || actives.data('bs.collapse', null)
    }

    var dimension = this.dimension()

    this.$element
      .removeClass('collapse')
      .addClass('collapsing')[dimension](0)
      .attr('aria-expanded', true)

    this.$trigger
      .removeClass('collapsed')
      .attr('aria-expanded', true)

    this.transitioning = 1

    var complete = function () {
      this.$element
        .removeClass('collapsing')
        .addClass('collapse in')[dimension]('')
      this.transitioning = 0
      this.$element
        .trigger('shown.bs.collapse')
    }

    if (!$.support.transition) return complete.call(this)

    var scrollSize = $.camelCase(['scroll', dimension].join('-'))

    this.$element
      .one('bsTransitionEnd', $.proxy(complete, this))
      .emulateTransitionEnd(Collapse.TRANSITION_DURATION)[dimension](this.$element[0][scrollSize])
  }

  Collapse.prototype.hide = function () {
    if (this.transitioning || !this.$element.hasClass('in')) return

    var startEvent = $.Event('hide.bs.collapse')
    this.$element.trigger(startEvent)
    if (startEvent.isDefaultPrevented()) return

    var dimension = this.dimension()

    this.$element[dimension](this.$element[dimension]())[0].offsetHeight

    this.$element
      .addClass('collapsing')
      .removeClass('collapse in')
      .attr('aria-expanded', false)

    this.$trigger
      .addClass('collapsed')
      .attr('aria-expanded', false)

    this.transitioning = 1

    var complete = function () {
      this.transitioning = 0
      this.$element
        .removeClass('collapsing')
        .addClass('collapse')
        .trigger('hidden.bs.collapse')
    }

    if (!$.support.transition) return complete.call(this)

    this.$element
      [dimension](0)
      .one('bsTransitionEnd', $.proxy(complete, this))
      .emulateTransitionEnd(Collapse.TRANSITION_DURATION)
  }

  Collapse.prototype.toggle = function () {
    this[this.$element.hasClass('in') ? 'hide' : 'show']()
  }

  Collapse.prototype.getParent = function () {
    return $(this.options.parent)
      .find('[data-toggle="collapse"][data-parent="' + this.options.parent + '"]')
      .each($.proxy(function (i, element) {
        var $element = $(element)
        this.addAriaAndCollapsedClass(getTargetFromTrigger($element), $element)
      }, this))
      .end()
  }

  Collapse.prototype.addAriaAndCollapsedClass = function ($element, $trigger) {
    var isOpen = $element.hasClass('in')

    $element.attr('aria-expanded', isOpen)
    $trigger
      .toggleClass('collapsed', !isOpen)
      .attr('aria-expanded', isOpen)
  }

  function getTargetFromTrigger($trigger) {
    var href
    var target = $trigger.attr('data-target')
      || (href = $trigger.attr('href')) && href.replace(/.*(?=#[^\s]+$)/, '') // strip for ie7

    return $(target)
  }


  // COLLAPSE PLUGIN DEFINITION
  // ==========================

  function Plugin(option) {
    return this.each(function () {
      var $this   = $(this)
      var data    = $this.data('bs.collapse')
      var options = $.extend({}, Collapse.DEFAULTS, $this.data(), typeof option == 'object' && option)

      if (!data && options.toggle && /show|hide/.test(option)) options.toggle = false
      if (!data) $this.data('bs.collapse', (data = new Collapse(this, options)))
      if (typeof option == 'string') data[option]()
    })
  }

  var old = $.fn.collapse

  $.fn.collapse             = Plugin
  $.fn.collapse.Constructor = Collapse


  // COLLAPSE NO CONFLICT
  // ====================

  $.fn.collapse.noConflict = function () {
    $.fn.collapse = old
    return this
  }


  // COLLAPSE DATA-API
  // =================

  $(document).on('click.bs.collapse.data-api', '[data-toggle="collapse"]', function (e) {
    var $this   = $(this)

    if (!$this.attr('data-target')) e.preventDefault()

    var $target = getTargetFromTrigger($this)
    var data    = $target.data('bs.collapse')
    var option  = data ? 'toggle' : $this.data()

    Plugin.call($target, option)
  })

}(jQuery);


  }).apply(root, arguments);
});
}(this));

(function(root) {
define("jquery-dlmenu", ["jquery","modernizr"], function() {
  return (function() {
/**
 * jquery.dlmenu.js v1.0.1
 * http://www.codrops.com
 *
 * Licensed under the MIT license.
 * http://www.opensource.org/licenses/mit-license.php
 *
 * Copyright 2013, Codrops
 * http://www.codrops.com
 */
;( function( $, window, undefined ) {

	'use strict';

	// global
	var Modernizr = window.Modernizr, $body = $( 'body' );

	$.DLMenu = function( options, element ) {
		this.$el = $( element );
		this._init( options );
	};

	// the options
	$.DLMenu.defaults = {
		// classes for the animation effects
		animationClasses : { classin : 'dl-animate-in-1', classout : 'dl-animate-out-1' },
		// callback: click a link that has a sub menu
		// el is the link element (li); name is the level name
		onLevelClick : function( el, name ) { return false; },
		// callback: click a link that does not have a sub menu
		// el is the link element (li); ev is the event obj
		onLinkClick : function( el, ev ) { return false; },
		backLabel: 'Back',
		// Change to "true" to use the active item as back link label.
		useActiveItemAsBackLabel: false,
		// Change to "true" to add a navigable link to the active item to its child
		// menu.
		useActiveItemAsLink: false,
		// On close reset the menu to root
		resetOnClose: true
	};

	$.DLMenu.prototype = {
		_init : function( options ) {

			// options
			this.options = $.extend( true, {}, $.DLMenu.defaults, options );
			// cache some elements and initialize some variables
			this._config();

			var animEndEventNames = {
					'WebkitAnimation' : 'webkitAnimationEnd',
					'OAnimation' : 'oAnimationEnd',
					'msAnimation' : 'MSAnimationEnd',
					'animation' : 'animationend'
				},
				transEndEventNames = {
					'WebkitTransition' : 'webkitTransitionEnd',
					'MozTransition' : 'transitionend',
					'OTransition' : 'oTransitionEnd',
					'msTransition' : 'MSTransitionEnd',
					'transition' : 'transitionend'
				};
			// animation end event name
			this.animEndEventName = animEndEventNames[ Modernizr.prefixed( 'animation' ) ] + '.dlmenu';
			// transition end event name
			this.transEndEventName = transEndEventNames[ Modernizr.prefixed( 'transition' ) ] + '.dlmenu';
			// support for css animations and css transitions
			this.supportAnimations = Modernizr.cssanimations;
			this.supportTransitions = Modernizr.csstransitions;

			this._initEvents();

		},
		_config : function() {
			this.open = false;
			this.$trigger = this.$el.children( '.dl-trigger' );
			this.$menu = this.$el.children( 'ul.dl-menu' );
			this.$menuitems = this.$menu.find( 'li:not(.dl-back)' );
			this.$el.find( 'ul.dl-submenu' ).prepend( '<li class="dl-back"><a href="#">' + this.options.backLabel + '</a></li>' );
			this.$back = this.$menu.find( 'li.dl-back' );

			// Set the label text for the back link.
			if (this.options.useActiveItemAsBackLabel) {
				this.$back.each(function() {
					var $this = $(this),
						parentLabel = $this.parents('li:first').find('a:first').text();

					$this.find('a').html(parentLabel);
				});
			}
			// If the active item should also be a clickable link, create one and put
			// it at the top of our menu.
			if (this.options.useActiveItemAsLink) {
				this.$el.find( 'ul.dl-submenu' ).prepend(function() {
					var parentli = $(this).parents('li:not(.dl-back):first').find('a:first');
					return '<li class="dl-parent"><a href="' + parentli.attr('href') + '">' + parentli.text() + '</a></li>';
				});
			}

		},
		_initEvents : function() {

			var self = this;

			this.$trigger.on( 'click.dlmenu', function() {
				if( self.open ) {
					self._closeMenu();
				}
				else {
					self._openMenu();
					// clicking somewhere else makes the menu close
					// $body.off( 'click' ).children().on( 'click.dlmenu', function() {
					// 	self._closeMenu() ;
					// } );

				}
				return false;
			} );

			this.$menuitems.on( 'click.dlmenu', function( event ) {

				event.stopPropagation();

				var $item = $(this),
					$submenu = $item.children( 'ul.dl-submenu' );

				// Only go to the next menu level if one exists AND the link isn't the
				// one we added specifically for navigating to parent item pages.
				if( ($submenu.length > 0) && !($(event.currentTarget).hasClass('dl-subviewopen'))) {

					var $flyin = $submenu.clone().css( 'opacity', 0 ).insertAfter( self.$menu ),
						onAnimationEndFn = function() {
							self.$menu.off( self.animEndEventName ).removeClass( self.options.animationClasses.classout ).addClass( 'dl-subview' );
							$item.addClass( 'dl-subviewopen' ).parents( '.dl-subviewopen:first' ).removeClass( 'dl-subviewopen' ).addClass( 'dl-subview' );
							$flyin.remove();
						};

					setTimeout( function() {
						$flyin.addClass( self.options.animationClasses.classin );
						self.$menu.addClass( self.options.animationClasses.classout );
						if( self.supportAnimations ) {
							self.$menu.on( self.animEndEventName, onAnimationEndFn );
						}
						else {
							onAnimationEndFn.call();
						}

						self.options.onLevelClick( $item, $item.children( 'a:first' ).text() );
					} );

					return false;

				}
				else {
					self.options.onLinkClick( $item, event );
				}

			} );

			this.$back.on( 'click.dlmenu', function( event ) {

				var $this = $( this ),
					$submenu = $this.parents( 'ul.dl-submenu:first' ),
					$item = $submenu.parent(),

					$flyin = $submenu.clone().insertAfter( self.$menu );

				var onAnimationEndFn = function() {
					self.$menu.off( self.animEndEventName ).removeClass( self.options.animationClasses.classin );
					$flyin.remove();
				};

				setTimeout( function() {
					$flyin.addClass( self.options.animationClasses.classout );
					self.$menu.addClass( self.options.animationClasses.classin );
					if( self.supportAnimations ) {
						self.$menu.on( self.animEndEventName, onAnimationEndFn );
					}
					else {
						onAnimationEndFn.call();
					}

					$item.removeClass( 'dl-subviewopen' );

					var $subview = $this.parents( '.dl-subview:first' );
					if( $subview.is( 'li' ) ) {
						$subview.addClass( 'dl-subviewopen' );
					}
					$subview.removeClass( 'dl-subview' );
				} );

				return false;

			} );

		},
		closeMenu : function() {
			if( this.open ) {
				this._closeMenu();
			}
		},
		_closeMenu : function() {
			var self = this,
				onTransitionEndFn = function() {
					self.$menu.off( self.transEndEventName );
					if( self.options.resetOnClose ){
						self._resetMenu();
					}
				};

			this.$menu.removeClass( 'dl-menuopen' );
			this.$menu.addClass( 'dl-menu-toggle' );
			this.$trigger.removeClass( 'dl-active' );

			if( this.supportTransitions ) {
				this.$menu.on( this.transEndEventName, onTransitionEndFn );
			}
			else {
				onTransitionEndFn.call();
			}

			this.open = false;
		},
		openMenu : function() {
			if( !this.open ) {
				this._openMenu();
			}
		},
		_openMenu : function() {
			var self = this;
			// clicking somewhere else makes the menu close
			// $body.off( 'click' ).on( 'click.dlmenu', function() {
			// 	self._closeMenu() ;
			// } );
			this.$menu.addClass( 'dl-menuopen dl-menu-toggle' ).on( this.transEndEventName, function() {
				$( this ).removeClass( 'dl-menu-toggle' );
			} );
			this.$trigger.addClass( 'dl-active' );
			this.open = true;
		},
		// resets the menu to its original state (first level of options)
		_resetMenu : function() {
			this.$menu.removeClass( 'dl-subview' );
			this.$menuitems.removeClass( 'dl-subview dl-subviewopen' );
		}
	};

	var logError = function( message ) {
		if ( window.console ) {
			window.console.error( message );
		}
	};

	$.fn.dlmenu = function( options ) {
		if ( typeof options === 'string' ) {
			var args = Array.prototype.slice.call( arguments, 1 );
			this.each(function() {
				var instance = $.data( this, 'dlmenu' );
				if ( !instance ) {
					logError( "cannot call methods on dlmenu prior to initialization; " +
					"attempted to call method '" + options + "'" );
					return;
				}
				if ( !$.isFunction( instance[options] ) || options.charAt(0) === "_" ) {
					logError( "no such method '" + options + "' for dlmenu instance" );
					return;
				}
				instance[ options ].apply( instance, args );
			});
		}
		else {
			this.each(function() {
				var instance = $.data( this, 'dlmenu' );
				if ( instance ) {
					instance._init();
				}
				else {
					instance = $.data( this, 'dlmenu', new $.DLMenu( options, this ) );
				}
			});
		}
		return this;
	};

} )( jQuery, window );

  }).apply(root, arguments);
});
}(this));

define('jquery-dlmenu-patched',[
	'lodash',
	'lucid',
	'jquery',
	'jquery-dlmenu'
], function(
	_,
	lucidJS,
	$
) {
	var Translator = window.Translator;

	$.DLMenu.defaults.overviewLabel = Translator ? Translator.translate("Overview") : "Overview";
	$.DLMenu.defaults.backLabel = Translator ? Translator.translate("Back") : "Back";

	$.DLMenu.prototype._config = function() {
		var self = this;

		this.open = false;
		this.$trigger = this.$el.children( '.dl-trigger' );
		this.$menu = this.$el.children( 'ul.dl-menu' );
		this.$menuitems = this.$menu.find( 'li:not(.dl-back)' );
		this.$el.find( 'ul.dl-submenu' ).prepend( '<li class="dl-back"><a href="#">' + this.options.backLabel + '</a></li>' );
		this.$back = this.$menu.find( 'li.dl-back' );

		// Set the label text for the back link.
		if (this.options.useActiveItemAsBackLabel) {
			this.$back.each(function() {
				var $this = $(this),
					parentLabel = $this.parents('li:first').find('a:first').text();

				$this.find('a').html(parentLabel);
			});
		}
		// If the active item should also be a clickable link, create one and put
		// it at the top of our menu.
		if (this.options.useActiveItemAsLink) {
			// this.$el.find( 'ul.dl-submenu' ).prepend(function() {
			this.$back.after(function() {
				var parentli = $(this).parents('li:not(.dl-back):first').find('a:first'),
					altMobileTitle = $(this).parents('li:not(.dl-back):first').data('alt-title-mobile');

				if (altMobileTitle) {
					return '<li class="dl-parent"><a href="' + parentli.attr('href') + '">' + altMobileTitle +  '</a></li>';
				}
				else {
					return '<li class="dl-parent"><a href="' + parentli.attr('href') + '">' + parentli.text() + '</a></li>';
				}
				

			});
		}
	};

	var _openMenu = $.DLMenu.prototype._openMenu;
	$.DLMenu.prototype._openMenu = function() {
		var trigger = !this.open;

		_openMenu.apply(this, arguments);
		trigger && this.trigger('open');
	};

	var _closeMenu = $.DLMenu.prototype._closeMenu;
	$.DLMenu.prototype._closeMenu = function() {
		var trigger = this.open;

		_closeMenu.apply(this, arguments);
		trigger && this.trigger('close');
	};

	$.DLMenu.prototype = _.create(new lucidJS.EventEmitter, _.assign($.DLMenu.prototype, {
		'constructor': $.DLMenu
	}));

});
define('shoptheme/components/layout/navigation',[
	"jquery",
	"lodash",
	"cartware/components/_base",
	"cartware/jquery/bem",
	"json!shoptheme/etc/breakpoints.json",
	"enquire",
	"jquery-menu-aim",
	"modernizr",
	"bootstrap-collapse",
	"jquery-dlmenu"
], function(
	$,
	_,
	Component,
	BEM,
	BreakPoints,
	enquire,
	menuAim
) {
	return Component.create({
		blocks:					null,
		path:					null,
		area:					null,
		additionalCategories:	null,
		topmenu:				null,
		desktopAimInitialized:	false,

		$init: function() {
			var menuItemClass = "." + BEM.buildElementClassname("nav", "menu-item", "level0");

			this.blocks = BEM.block("nav");

			this.path = _.get(window, "gigaset.category.current");
			this.area = _.get(window, "gigaset.area");
			this.additionalCategories = _.get(window, "gigaset.category.additional");
			this.topmenu = $("#topmenu");

			_.forEach(_.union(this.path, [this.area], this.additionalCategories), function(categoryId) {
				var nodeId = ((categoryId|0) == categoryId)
					? "category-node-" + categoryId
					: categoryId;

				this.blocks.find('[data-category-id="' + nodeId + '"]').bemAddModifier("active");
			}, this);

			$(document).on("mouseenter", menuItemClass, function(e) {
				$(e.currentTarget).addClass("hover");
			}).on("mouseleave", menuItemClass, function(e) {
				$(e.currentTarget).removeClass("hover");
			});

			this.topmenu.dlmenu({
				useActiveItemAsLink: true
			});

			this.initResponsive();

			this.prepareCustomerAccountNavigation();

			this.desktopMenuAim();

		},

		initResponsive: function() {
			enquire.register('(min-width: ' + (BreakPoints.sm + 1) + 'px)', {
				match: _.bind(this.layoutDesktop, this),
				unmatch: _.bind(this.layoutMobile, this)
			}, true);
		},

		layoutMobile: function() {

			var menus = this.blocks.getElements("menu");

			menus.addClass("dl-submenu");

			menus.filter(":modifier(level0)").each(function() {
				var menu = $(this);
				menu.bemParents("menu-item").bemGetElements("submenu-slot-mobile").before(menu);
			});

			this.topmenu.addClass("dl-menuwrapper");
		},

		layoutDesktop: function() {

			var menus = this.blocks.getElements("menu");

			menus.removeClass("dl-submenu");

			menus.filter(":modifier(level0)").each(function(i) {
				var menu = $(this);
				menu.bemParents("menu-item").bemGetElements("submenu-slot-desktop").append(menu);
			});

			this.topmenu.removeClass("dl-menuwrapper");
		},

		prepareCustomerAccountNavigation: function() {
			if ($('html').hasClass('ie8')) return;
			var customerAccountMobileNav = $('.b_account-mobile-nav');
			if (customerAccountMobileNav.length > 0) {
				customerAccountMobileNav.prependTo($('.b_main-container__col2-left-col-main'));
				$('select', customerAccountMobileNav).on('change', function() {
					window.location = $(this).val();
				})
			}
		},

		desktopMenuAim: function() {
			$(document.body).one("mouseenter", "." + BEM.buildElementClassname("nav", "menu-item"), _.bind(function() {

				var menus = this.blocks.getElements("menu");
				var link = BEM.buildElementClassname("nav", "menu-item-link", "level1")

				menus.menuAim({
					activate: activateSubmenu,
					deactivate: deactiveSubmenu,
					rowSelector: "> li." + BEM.buildElementClassname("nav", "menu-item", "level1"),
					exitMenu: deactiveSubmenu
				});

				function activateSubmenu(row) {
					var $row = $(row);

					if($row.hasClass('b_nav__menu-item--parent')) {
						$row.parent('ul').addClass('removeHover');
					}

					$submenu = $row.find('ul');
					$submenu.addClass('open');

					$menulink = $row.find('.' + link);
					$menulink.addClass('maintainHover');
				}

				function deactiveSubmenu(row) {
					var $row = $(row);

					$row.parent('ul').removeClass('removeHover');

					$submenu = $row.find('ul');
					$submenu.removeClass('open');

					$menulink = $row.find('.' + link);
					$menulink.removeClass('maintainHover');

					return true;
				}
			}, this));
		}
	});
});
define('shoptheme/components/layout/trigger',[
	"jquery",
	"lodash",
	"cartware/components/_base"
], function(
	$,
	_,
	Component
) {
	return Component.create({

		config:		null,

		constructor: function() {
			// Create config
			this.config = {
				'.b_product-item': {
					'trigger': '.trigger__product-overlay',
					'target': '.b_product-item__overlay',
					'event': 'click'
				}
			};
		},

		$init: function() {
			var $body = $(document.body);
			_.forEach(this.config, function(data, selector) {
				$body.on(data.event, selector + " " + data.trigger, function(e) {
					var trigger = $(e.currentTarget),
						target = trigger.parents(selector).find(data.target);

					e.preventDefault();

					if (trigger.hasClass('active')) {
						trigger.removeClass('active');
						target.fadeOut(300);
					} else {
						trigger.addClass('active');
						target.fadeIn(300);
					}
				});
			});
		}

	});
});
define('shoptheme/components/layout/search',[
	"jquery",
	"lodash",
	"cartware/components/_base",
	"cartware/jquery/bem",
	"json!shoptheme/etc/breakpoints.json"
], function(
	$,
	_,
	Component,
	BEM,
	BreakPoints
) {
	return Component.create({
		block:				null,
		formContainer:		null,
		button:				null,
		closeButton:		null,
		autoComplete:		null,
		input:				null,

		isMobile:			true,

		$init: function() {
			var self = this;
			this.block = BEM.block("search");
			this.formContainer = this.block.getElements("form-container");
			this.button = this.block.getElements("button");
			this.closeButton = this.block.getElements("close-button");
			this.autoComplete = this.block.getElements("autocomplete-container");
			this.input = this.block.getElements("input");

			this.closeButton.on("click", _.bind(function(e) {
				e.preventDefault();
				this.closeSearch();
			}, this));

			this.button.on("click", _.bind(function(e) {
				if (!this.isMobile) return;

				if (!this.formContainer.bemHasModifier("open")) {
					e.preventDefault();
					this.openSearch();
				}
			}, this));

			enquire.register('(min-width: ' + (BreakPoints.sm + 1) + 'px)', {
				match: _.bind(this.flagDesktop, this),
				unmatch: _.bind(this.flagMobile, this)
			}, true);

			$(window).on('resize', _.debounce(function() {
				var width = self.formContainer.width();
				self.autoComplete.css('width', width);
			}, 400));

		},

		openSearch: function() {
			this.formContainer.bemAddModifier("open");
			this.input.focus();
		},

		closeSearch: function() {
			this.formContainer.bemRemoveModifier("open");
			this.autoComplete.css("display", "none");
		},

		flagDesktop: function() {
			this.isMobile = false;
		},

		flagMobile: function() {
			this.isMobile = true;
		}
	});
});
define('shoptheme/components/layout/story',[
	"cartware/components/_base",
	"jquery",
	"cartware/jquery/bem",
	"enquire",
	"json!shoptheme/etc/breakpoints.json",
], function(
	Component,
	$,
	BEM,
	enquire,
	BreakPoints
) {
	return Component.create({
		//var
		init: function() {

			this.blocks = BEM.block('story-item');
			this.container = this.blocks.getElements('container');
			this.mobileImage = this.container.attr('data-mobile-image');
			this.desktopImage = this.container.attr('data-desktop-image');


			//this.story();
		},

		story: function() {

			var _self = this;

			enquire.register('(min-width: ' + (BreakPoints.mobile + 1) + 'px)', {
				match: function() {
					_self.container.css('background-image', 'url('+_self.desktopImage+')');
				},
				unmatch: function() {
					_self.container.css('background-image', 'url('+_self.mobileImage+')');
				}
			}, true);

		}
	});

});
define('shoptheme/components/layout/equal-heights',[
	"jquery",
	"lodash",
	"cartware/components/_base",
	"cartware/jquery/bem",
	"json!shoptheme/etc/breakpoints.json",
	"enquire"
], function(
	$,
	_,
	Component,
	BEM,
	BreakPoints,
	enquire
) {
	var COMPONENT_SELECTOR = '.js-equal-heights';
	var COMPONENT_CONFIG_KEY = 'equal-heights';
	var COMPONENT_INSTANCE_KEY = 'equalheights/instance';

	var DEFAULTS = {
		childSelector: '>*',
		heightContainer: null
	};

	var EqualHeights = function(container, config) {
		var self = this;

		this.container = $(container);
		this.containerWidth = this.container.width();
		this.config = $.extend({}, DEFAULTS, config);
		this.elements = [];

		this.container.find(this.config.childSelector).each(function() {
			var child = $(this);

			self.elements.push({
				child: child,
				heightContainer: self.config.heightContainer
					? child.find(self.config.heightContainer)
					: child
			});
		});

		this.container.parents("." + BEM.buildElementClassname("tabs", "tab-content")).on("tab-content-active", _.bind(this.layout, this));

		this.layout();

		$(window).on("resize", _.debounce(_.bind(function() {
			var containerWidth = this.container.width();
			if (containerWidth !== this.containerWidth) {
				this.containerWidth = containerWidth;
				this.layout();
			}
		}, this)));
	}

	EqualHeights.prototype = {
		layout: function() {
			var self = this;

			this.resetLayout();

			function row(elements) {
				var _row = [], remains = [];
				var lastTop = null, topLock = false, maxHeight = null;

				_.forEach(elements, function(element) {
					var top;

					if (!topLock) {
						top = element.child.offset().top;

						if (null === lastTop) { lastTop = top; }

						if (top > lastTop) {
							topLock = true;
							remains.push(element);
						} else {
							maxHeight = Math.max(maxHeight, element.child.height());
							_row.push(element);
						}
					} else {
						remains.push(element);
					}
				});

				function next() {
					if (remains.length) {
						row(remains);
					}
				};

				if (_row.length > 1) {
					self.layoutRow(_row, maxHeight, next)
				} else {
					next();
				}
			}

			row(this.elements);

			this.container.trigger("gigaset.content.change");
		},

		layoutRow: function(row, targetHeight, next) {
			_.forEach(row, function(element) {
				var height = element.child.height();
				var diff = targetHeight - height;

				if (!diff) return;

				element.heightContainer.height(element.heightContainer.height() + diff);
			});

			next();
		},

		resetLayout: function() {
			_.forEach(this.elements, function(element) {
				element.heightContainer.css("height", "");
			})
		}
	};


	return Component.create({
		$init: function() {
			$(COMPONENT_SELECTOR).each(function() {
				var container = $(this),
					instance = container.data(COMPONENT_INSTANCE_KEY);

				if (!instance) {
					instance = new EqualHeights(container, container.data(COMPONENT_CONFIG_KEY));
					container.data(COMPONENT_INSTANCE_KEY, instance);
				}
			});
		}
	});
});
define('shoptheme/components/ui/accordion',[
	"jquery",
	"lodash",
	"cartware/components/_base",
	"cartware/jquery/bem",
	"json!shoptheme/etc/breakpoints.json",
	"enquire"
], function(
	$,
	_,
	Component,
	BEM,
	BreakPoints,
	enquire
) {
	var BLOCK = 'accordion';

	var defaults = {};
	var instances = [];
	var isMobile = true;

	function refreshHeight() {
		_.forEach(instances, function(instance) {
			instance.refreshHeight();
		});
	}

	var debouncedRefreshHeight = _.debounce(refreshHeight, 100);

	var mobileActions = function() {
		_.forEach(instances, function(instance) {
			if (instance.block.is(":modifier(close-mobile)")) {
				instance.closeAll();
			}
		});

		debouncedRefreshHeight();
	};

	enquire.register('(min-width: ' + (BreakPoints.sm + 1) + 'px)', {
		match: function() {
			isMobile = false;

			_.forEach(instances, function(instance) {
				instance.resetCurrent();
			});

			debouncedRefreshHeight();
		},
		unmatch: function() {
			isMobile = true;

			mobileActions();
		}
	}, true);

	Validation.prototype.validate = _.wrap(Validation.prototype.validate, function(orig) {
		var result = orig.apply(this, arguments);

		if (!result) {
			setTimeout(refreshHeight, 1);
		}

		return result;
	});

	function Accordion(block, options) {
		this.block = $(block);
		this.options = $.extend({}, defaults, options);

		this.items = this.block.bemGetElements("item");
		this.current = this.items.filter(":modifier(active)");

		this.init();

		instances.push(this);
	}

	Accordion.prototype = {
		block:		null,
		options:	null,
		items:		null,
		current:	null,

		init: function() {
			this.items.on("click", "." + BEM.buildElementClassname(BLOCK, "title"), _.bind(this._toggleItem, this));
			this.items.on("gigaset.content.change", _.bind(this.refreshHeight, this));
			this.refreshHeight();
		},

		_toggleItem: function(e) {
			var item = $(e.delegateTarget),
				content = item.find("." + BEM.buildElementClassname(BLOCK, "content"));

			if (e.target.nodeName.toUpperCase() === "A"
				|| item.bemHasModifier("inactive")
			) {
				return true;
			}

			if (!isMobile && !this.current.is(item)) {
				this.current.bemRemoveModifier("active");
				this.current.trigger("accordion-item-inactive");
			}

			if (item.is(":modifier(active)")) {
				item.bemRemoveModifier("active");
			} else {
				item.bemAddModifier("active");
			}

			item.trigger("accordion-item-active");


			//this.contents.filter(":modifier(active)").bemRemoveModifier("active");
			//this.tabs.filter(":modifier(active)").bemRemoveModifier("active");


			this.current = item;

			item.trigger("gigaset.content.change");

			_.defer(_.bind(this.refreshHeight, this));
		},

		refreshHeight: function() {
			// var height = "auto";
			// if (!isMobile) {
			// 	height = this.list.outerHeight(true) + this.current.content.outerHeight(true);
			// }
			//
			// this.block.css("height", height);
		},

		resetCurrent: function() {
			this.closeAll();
			this.current.bemAddModifier("active");
		},

		closeAll: function() {
			this.items.bemRemoveModifier("active");
		}
	};

	return Component.create({
		blocks:			null,

		$init: function() {
			this.blocks = BEM.block(BLOCK);
			this.blocks.each(function() {
				var block = $(this);
				new Accordion(block, block.data("accordion"));
			});

			if (isMobile) {
				mobileActions();
			}
		}
	});
});
define('shoptheme/components/ui/tabs',[
	"jquery",
	"lodash",
	"cartware/components/_base",
	"cartware/jquery/bem",
	"json!shoptheme/etc/breakpoints.json",
	"enquire"
], function(
	$,
	_,
	Component,
	BEM,
	BreakPoints,
	enquire
) {
	var defaults = {};
	var instances = [];
	var isMobile = true;

	function refreshHeight() {
		_.forEach(instances, function(instance) {
			instance.refreshHeight();
		});
	}

	var debouncedRefreshHeight = _.debounce(refreshHeight, 100);

	var mobileActions = function() {
		_.forEach(instances, function(instance) {
			if (instance.block.is(":modifier(close-mobile)")) {
				instance.closeAll();
			}
		});

		debouncedRefreshHeight();
	};

	enquire.register('(min-width: ' + (BreakPoints.sm + 1) + 'px)', {
		match: function() {
			isMobile = false;

			_.forEach(instances, function(instance) {
				instance.resetCurrent();
			});

			debouncedRefreshHeight();
		},
		unmatch: function() {
			isMobile = true;

			mobileActions();
		}
	}, true);

	Validation.prototype.validate = _.wrap(Validation.prototype.validate, function(orig) {
		var result = orig.apply(this, arguments);

		if (!result) {
			setTimeout(refreshHeight, 1);
		}

		return result;
	});

	function Tabs(block, options) {
		this.block = $(block);
		this.options = $.extend({}, defaults, options);

		this.list = this.block.bemGetElements("list");
		this.tabs = this.block.bemGetElements("tab");
		this.contents = this.block.bemGetElements("tab-content");

		this.map = _.map(this.tabs, function(tab, i) {
			var tabElement = $(tab);

			return {
				tab: tabElement,
				content: tabElement.next("." + BEM.buildElementClassname("tabs", "tab-content"))
			};
		}, this);

		this.current = {
			tab: this.tabs.filter(":modifier(active)"),
			content: this.contents.filter(":modifier(active)")
		};

		this.init();

		instances.push(this);
	}

	Tabs.prototype = {
		block:		null,
		options:	null,
		list:		null,
		tabs:		null,
		contents:	null,
		current:	null,

		init: function() {
			this.tabs.on("click", _.bind(this._onTabClick, this));
			this.contents.on("gigaset.content.change", _.bind(this.refreshHeight, this));
			this.refreshHeight();
		},

		_onTabClick: function(e) {
			var tab = $(e.currentTarget),
				mapItem = _.find(this.map, function(item) {
					return item.tab.get(0) === tab.get(0);
				}),
				content = mapItem.content;

			if (e.target.nodeName.toUpperCase() === "A"
				|| tab.bemHasModifier("inactive")
			) {
				return true;
			}

			if (!isMobile) {
				this.current.tab.bemRemoveModifier("active");
				this.current.content.bemRemoveModifier("active");

				this.current.content.trigger("tab-content-inactive");
			}

			if (isMobile && tab.is(":modifier(active)")) {
				tab.bemRemoveModifier("active");
				content.bemRemoveModifier("active");
			} else {
				tab.bemAddModifier("active");
				content.bemAddModifier("active");
			}

			content.trigger("tab-content-active");


			//this.contents.filter(":modifier(active)").bemRemoveModifier("active");
			//this.tabs.filter(":modifier(active)").bemRemoveModifier("active");


			this.current = mapItem;

			_.defer(_.bind(this.refreshHeight, this));
		},

		refreshHeight: function() {
			var height = "auto";
			if (!isMobile) {
				height = this.list.outerHeight(true) + this.current.content.outerHeight(true);
			}

			this.block.css("height", height);
		},

		resetCurrent: function() {
			this.closeAll();

			this.current.tab.bemAddModifier("active");
			this.current.content.bemAddModifier("active");
		},

		closeAll: function() {
			this.tabs.bemRemoveModifier("active");
			this.contents.bemRemoveModifier("active");
		}
	};

	return Component.create({
		blocks:			null,

		$init: function() {
			this.blocks = BEM.block("tabs");
			this.blocks.each(function() {
				var block = $(this);
				new Tabs(block, block.data("tabs"));
			});

			$(window).on("resize", debouncedRefreshHeight);
			debouncedRefreshHeight();

			if (isMobile) {
				mobileActions();
			}
		},

		initAjax: function (block) {
			new Tabs(block, block.data('tabs'));
		}
	});
});
define('shoptheme/components/ui/selects',[
	"jquery",
	"lodash",
	"cartware/components/_base",
	"cartware/dom/event/trigger",
	"select2"
], function(
	$,
	_,
	Component,
	triggerEvent,
	S2
) {

	function MaxHeightDecorator () { }

	MaxHeightDecorator.prototype.bind = function (decorated, container, $container) {
		var self = this;

		decorated.call(this, container, $container);

		function layout() {
			var dropdown = container.$dropdown,
				offset = dropdown.offset(),
				scrollTop = $(window).scrollTop(),
				height = dropdown.get(0).scrollHeight,
				windowHeight = $(window).height();

			if (offset.top + height - scrollTop > windowHeight) {
				dropdown.css({
					'max-height': windowHeight - offset.top + scrollTop,
					'overflow': 'auto'
				});
			} else {
				dropdown.css({
					'max-height': '',
					'overflow': ''
				});
			}
		}

		container.on('open', function (evt) {
			$(window).on('resize scroll', layout);
			layout();
		});

		container.on('close', function (evt) {
			$(window).off('resize scroll', layout);
		});
	};

	return Component.create({
		$init: function() {
			this.transformSelects();

			var debounceTransform = _.debounce(this.transformSelects, 1);

			$(document).ajaxComplete(debounceTransform);
			Ajax.Responders.register({
				onComplete: debounceTransform
			});
		},

		transformSelects: function() {
			$.fn.select2.amd.require(
				[
					"select2/utils",
					"select2/dropdown",
					"select2/dropdown/attachContainer",
					"select2/dropdown/closeOnSelect",
					"select2/dropdown/search",
					"select2/selection/placeholder",
					"select2/data/ajax"
				],
				function (
					Utils,
					Dropdown,
					AttachContainer,
					CloseOnSelect,
					Search,
					Placeholder,
					AjaxData
				) {
					$("select").each(function() {
						var select = $(this),
							features = (''+select.data("ui-select-features")).split(" "),
							dropdownAdapter,
							options;

						if (select.data("ui-select-refuse") || select.data('select2')
							|| select.hasClass("bv-dropdown-select")) {
							return;
						}

						options = {
							dropdownAdapter: dropdownAdapter,
							width: null
						};

						dropdownAdapter = Utils.Decorate(Utils.Decorate(Dropdown, AttachContainer), CloseOnSelect);

						dropdownAdapter = Utils.Decorate(dropdownAdapter, MaxHeightDecorator);

						if (select.data("suggest-url")) {
							if (features.indexOf("search") < 0) {
								features.push("search");
							}
							options = $.extend(options, {
								dataAdapter: AjaxData,
								ajax: {
									url: select.data("suggest-url")
								}
							});
						}

						if (features.indexOf("search") >= 0) {
							dropdownAdapter = Utils.Decorate(dropdownAdapter, Search);
						}

						if (select.data("placeholder")) {
							dropdownAdapter = Utils.Decorate(dropdownAdapter, Placeholder);
						}

						options.dropdownAdapter = dropdownAdapter;
						// options.width = 'resolve';

						select.select2(options);

						//select.on("change", function(e) {
						//	//if ("undefined" === typeof e.originalEvent) {
						//	if (e instanceof $.Event) {
						//		triggerEvent(this, "change");
						//	}
						//});
					});
				}
			);
		}
	});
});

define('shoptheme/components/ui/tooltips',[
	"jquery",
	"cartware/components/_base",
	"bootstrap-tooltip"
], function(
	$,
	Component
) {
	return Component.create({
		$init: function() {
			$(document.body).on("mouseover", '[data-toggle="tooltip"]', function(e) {
				var $this   = $(this),
					data    = $this.data('bs.tooltip');

				if (!data) {
					$this.tooltip({
						html: true
					});
				}

				$this.tooltip("show");
			});
		}
	});
});
define('shoptheme/components/ui/affix',[
	"jquery",
	"cartware/components/_base",
	"cartware/jquery/bem"
], function(
	$,
	Component,
	BEM
) {

	return Component.create({

		blocks: null,

		$init: function() {
			var affixTop;

			this.blocks = BEM.block('category-view');
			this.blocks.each(function() {
				var block = BEM.block(this);
				var container = block.getElements('headline-affix-padding');
				var headline = block.getElements('headline');

				if (container.length > 0) {
					affixTop = container.offset().top;
					headline.attr('data-offset-top', affixTop);

					$(window).off('.affix')
					headline.removeData('affix').removeClass('affix affix-top affix-bottom')
					headline.affix({ offset: affixTop})
					container.css('height', container.outerHeight(true));

				}

			});

		}

	});

});
(function(root) {
define("bootstrap-affix", ["jquery","bootstrap-transition","bootstrap-prototype-fix"], function() {
  return (function() {
/* ========================================================================
 * Bootstrap: affix.js v3.3.6
 * http://getbootstrap.com/javascript/#affix
 * ========================================================================
 * Copyright 2011-2015 Twitter, Inc.
 * Licensed under MIT (https://github.com/twbs/bootstrap/blob/master/LICENSE)
 * ======================================================================== */


+function ($) {
  'use strict';

  // AFFIX CLASS DEFINITION
  // ======================

  var Affix = function (element, options) {
    this.options = $.extend({}, Affix.DEFAULTS, options)

    this.$target = $(this.options.target)
      .on('scroll.bs.affix.data-api', $.proxy(this.checkPosition, this))
      .on('click.bs.affix.data-api',  $.proxy(this.checkPositionWithEventLoop, this))

    this.$element     = $(element)
    this.affixed      = null
    this.unpin        = null
    this.pinnedOffset = null

    this.checkPosition()
  }

  Affix.VERSION  = '3.3.6'

  Affix.RESET    = 'affix affix-top affix-bottom'

  Affix.DEFAULTS = {
    offset: 0,
    target: window
  }

  Affix.prototype.getState = function (scrollHeight, height, offsetTop, offsetBottom) {
    var scrollTop    = this.$target.scrollTop()
    var position     = this.$element.offset()
    var targetHeight = this.$target.height()

    if (offsetTop != null && this.affixed == 'top') return scrollTop < offsetTop ? 'top' : false

    if (this.affixed == 'bottom') {
      if (offsetTop != null) return (scrollTop + this.unpin <= position.top) ? false : 'bottom'
      return (scrollTop + targetHeight <= scrollHeight - offsetBottom) ? false : 'bottom'
    }

    var initializing   = this.affixed == null
    var colliderTop    = initializing ? scrollTop : position.top
    var colliderHeight = initializing ? targetHeight : height

    if (offsetTop != null && scrollTop <= offsetTop) return 'top'
    if (offsetBottom != null && (colliderTop + colliderHeight >= scrollHeight - offsetBottom)) return 'bottom'

    return false
  }

  Affix.prototype.getPinnedOffset = function () {
    if (this.pinnedOffset) return this.pinnedOffset
    this.$element.removeClass(Affix.RESET).addClass('affix')
    var scrollTop = this.$target.scrollTop()
    var position  = this.$element.offset()
    return (this.pinnedOffset = position.top - scrollTop)
  }

  Affix.prototype.checkPositionWithEventLoop = function () {
    setTimeout($.proxy(this.checkPosition, this), 1)
  }

  Affix.prototype.checkPosition = function () {
    if (!this.$element.is(':visible')) return

    var height       = this.$element.height()
    var offset       = this.options.offset
    var offsetTop    = offset.top
    var offsetBottom = offset.bottom
    var scrollHeight = Math.max($(document).height(), $(document.body).height())

    if (typeof offset != 'object')         offsetBottom = offsetTop = offset
    if (typeof offsetTop == 'function')    offsetTop    = offset.top(this.$element)
    if (typeof offsetBottom == 'function') offsetBottom = offset.bottom(this.$element)

    var affix = this.getState(scrollHeight, height, offsetTop, offsetBottom)

    if (this.affixed != affix) {
      if (this.unpin != null) this.$element.css('top', '')

      var affixType = 'affix' + (affix ? '-' + affix : '')
      var e         = $.Event(affixType + '.bs.affix')

      this.$element.trigger(e)

      if (e.isDefaultPrevented()) return

      this.affixed = affix
      this.unpin = affix == 'bottom' ? this.getPinnedOffset() : null

      this.$element
        .removeClass(Affix.RESET)
        .addClass(affixType)
        .trigger(affixType.replace('affix', 'affixed') + '.bs.affix')
    }

    if (affix == 'bottom') {
      this.$element.offset({
        top: scrollHeight - height - offsetBottom
      })
    }
  }


  // AFFIX PLUGIN DEFINITION
  // =======================

  function Plugin(option) {
    return this.each(function () {
      var $this   = $(this)
      var data    = $this.data('bs.affix')
      var options = typeof option == 'object' && option

      if (!data) $this.data('bs.affix', (data = new Affix(this, options)))
      if (typeof option == 'string') data[option]()
    })
  }

  var old = $.fn.affix

  $.fn.affix             = Plugin
  $.fn.affix.Constructor = Affix


  // AFFIX NO CONFLICT
  // =================

  $.fn.affix.noConflict = function () {
    $.fn.affix = old
    return this
  }


  // AFFIX DATA-API
  // ==============

  $(window).on('load', function () {
    $('[data-spy="affix"]').each(function () {
      var $spy = $(this)
      var data = $spy.data()

      data.offset = data.offset || {}

      if (data.offsetBottom != null) data.offset.bottom = data.offsetBottom
      if (data.offsetTop    != null) data.offset.top    = data.offsetTop

      Plugin.call($spy, data)
    })
  })

}(jQuery);


  }).apply(root, arguments);
});
}(this));

define('shoptheme/components/ui/affix-generic',[
	"jquery",
	"cartware/components/_base",
	"cartware/jquery/bem",
	"bootstrap-affix"
], function(
	$,
	Component,
	BEM
) {

	return Component.create({

		blocks: null,

		$init: function() {
			var self = this;
			this.blocks = $('[data-affix-target]');
			this.blocks.each(function() {
				var block = $(this);
				self._init(block);
				$(window).on('cookieconsent.open cookieconsent.close', function(){
					$(window).off('.affix');
					block.removeData('bs.affix').removeClass('affix affix-top affix-bottom');
					self._init(block);
				});
			});
		},

		_init: function(block){
			var heightElement = $(block.data('affix-height-element')),
					top = block.offset().top,
					bottom = (heightElement.length) ? $(document).height() - heightElement.offset().top - heightElement.outerHeight(true) : 0;

				block.width(block.parent().width());

				block.css('position','relative');

				block.affix({
					offset: {
						top: top,
						bottom: bottom
					}
				});

				$(window).resize($.throttle(10, function() {block.width(block.parent().width());}));
		}
	});

});

define('shoptheme/components/ui/toggle-visibility',[
	"jquery",
	"cartware/components/_base",
	"cartware/jquery/bem",
	"bootstrap-affix"
], function(
	$,
	Component,
	BEM
) {

	return Component.create({

		blocks: null,

		$init: function() {
			this.blocks = $('[data-toggle-visibility]');
			this.blocks.each(function() {
				var state = 0,
					button = $(this),
					showMore = button.html(),
					showLess = button.data('toggle-less-text'),
					container = $(button.data('toggle-visibility')),
					targetElements = container.find(button.data('toggle-visibility-elements'));

				button.click( function(e){
					if (!state) {
						e.preventDefault();
						targetElements.show();
						button.html(showLess);
						state = 1;
					} else {
						e.preventDefault();
						targetElements.hide();
						button.html(showMore);
						state = 0;
					}
				});
			});

		}

	});

});

(function(root) {
define("bootstrap-scrollspy", ["jquery","bootstrap-transition","bootstrap-prototype-fix"], function() {
  return (function() {
/* ========================================================================
 * Bootstrap: scrollspy.js v3.3.6
 * http://getbootstrap.com/javascript/#scrollspy
 * ========================================================================
 * Copyright 2011-2015 Twitter, Inc.
 * Licensed under MIT (https://github.com/twbs/bootstrap/blob/master/LICENSE)
 * ======================================================================== */


+function ($) {
  'use strict';

  // SCROLLSPY CLASS DEFINITION
  // ==========================

  function ScrollSpy(element, options) {
    this.$body          = $(document.body)
    this.$scrollElement = $(element).is(document.body) ? $(window) : $(element)
    this.options        = $.extend({}, ScrollSpy.DEFAULTS, options)
    this.selector       = (this.options.target || '') + ' .nav li > a'
    this.offsets        = []
    this.targets        = []
    this.activeTarget   = null
    this.scrollHeight   = 0

    this.$scrollElement.on('scroll.bs.scrollspy', $.proxy(this.process, this))
    this.refresh()
    this.process()
  }

  ScrollSpy.VERSION  = '3.3.6'

  ScrollSpy.DEFAULTS = {
    offset: 10
  }

  ScrollSpy.prototype.getScrollHeight = function () {
    return this.$scrollElement[0].scrollHeight || Math.max(this.$body[0].scrollHeight, document.documentElement.scrollHeight)
  }

  ScrollSpy.prototype.refresh = function () {
    var that          = this
    var offsetMethod  = 'offset'
    var offsetBase    = 0

    this.offsets      = []
    this.targets      = []
    this.scrollHeight = this.getScrollHeight()

    if (!$.isWindow(this.$scrollElement[0])) {
      offsetMethod = 'position'
      offsetBase   = this.$scrollElement.scrollTop()
    }

    this.$body
      .find(this.selector)
      .map(function () {
        var $el   = $(this)
        var href  = $el.data('target') || $el.attr('href')
        var $href = /^#./.test(href) && $(href)

        return ($href
          && $href.length
          && $href.is(':visible')
          && [[$href[offsetMethod]().top + offsetBase, href]]) || null
      })
      .sort(function (a, b) { return a[0] - b[0] })
      .each(function () {
        that.offsets.push(this[0])
        that.targets.push(this[1])
      })
  }

  ScrollSpy.prototype.process = function () {
    var scrollTop    = this.$scrollElement.scrollTop() + this.options.offset
    var scrollHeight = this.getScrollHeight()
    var maxScroll    = this.options.offset + scrollHeight - this.$scrollElement.height()
    var offsets      = this.offsets
    var targets      = this.targets
    var activeTarget = this.activeTarget
    var i

    if (this.scrollHeight != scrollHeight) {
      this.refresh()
    }

    if (scrollTop >= maxScroll) {
      return activeTarget != (i = targets[targets.length - 1]) && this.activate(i)
    }

    if (activeTarget && scrollTop < offsets[0]) {
      this.activeTarget = null
      return this.clear()
    }

    for (i = offsets.length; i--;) {
      activeTarget != targets[i]
        && scrollTop >= offsets[i]
        && (offsets[i + 1] === undefined || scrollTop < offsets[i + 1])
        && this.activate(targets[i])
    }
  }

  ScrollSpy.prototype.activate = function (target) {
    this.activeTarget = target

    this.clear()

    var selector = this.selector +
      '[data-target="' + target + '"],' +
      this.selector + '[href="' + target + '"]'

    var active = $(selector)
      .parents('li')
      .addClass('active')

    if (active.parent('.dropdown-menu').length) {
      active = active
        .closest('li.dropdown')
        .addClass('active')
    }

    active.trigger('activate.bs.scrollspy')
  }

  ScrollSpy.prototype.clear = function () {
    $(this.selector)
      .parentsUntil(this.options.target, '.active')
      .removeClass('active')
  }


  // SCROLLSPY PLUGIN DEFINITION
  // ===========================

  function Plugin(option) {
    return this.each(function () {
      var $this   = $(this)
      var data    = $this.data('bs.scrollspy')
      var options = typeof option == 'object' && option

      if (!data) $this.data('bs.scrollspy', (data = new ScrollSpy(this, options)))
      if (typeof option == 'string') data[option]()
    })
  }

  var old = $.fn.scrollspy

  $.fn.scrollspy             = Plugin
  $.fn.scrollspy.Constructor = ScrollSpy


  // SCROLLSPY NO CONFLICT
  // =====================

  $.fn.scrollspy.noConflict = function () {
    $.fn.scrollspy = old
    return this
  }


  // SCROLLSPY DATA-API
  // ==================

  $(window).on('load.bs.scrollspy.data-api', function () {
    $('[data-spy="scroll"]').each(function () {
      var $spy = $(this)
      Plugin.call($spy, $spy.data())
    })
  })

}(jQuery);


  }).apply(root, arguments);
});
}(this));

define('shoptheme/components/ui/scrollspy',[
	"jquery",
	"cartware/components/_base",
	"cartware/jquery/bem",
	"bootstrap-scrollspy"
], function(
	$,
	Component,
	BEM
) {

	return Component.create({

		blocks: null,

		$init: function() {
			this.blocks = $('[data-scrollspy-target]');
			this.blocks.each(function() {
				var block = $(this);
				$('body').scrollspy({ target: block.data('scrollspy-target') });
			});

		}

	});

});

define('shoptheme/components/ui/scrolltop',[
	"jquery",
	"cartware/jquery/bem",
	"cartware/components/_base"
], function(
	$,
	BEM,
	Component
) {
	return Component.create({
		blocks:			null,

		$init: function() {
			this.blocks = BEM.block('scrolltopbutton');
			this.blocks.each(function() {
				var block = BEM.block(this);
				var link = block.getElements('link');

				link.click(function(e){
					e.preventDefault();
					$('body, html').animate({
						scrollTop: 0
					}, 200);
				});

				if( $(window).scrollTop() > 200 ) {
					block.addClass('active');
				}

				$(window).scroll(function () {
					if( $(window).scrollTop() > 200 ) {
						block.addClass('active');
					}

					if( $(window).scrollTop() < 200 ) {
						block.removeClass('active');
					}
				});
			});
		}
	});
});



define('shoptheme/components/ui/generated-menu',[
	"jquery",
	"cartware/components/_base",
	"cartware/jquery/bem"
], function(
	$,
	Component,
	BEM
) {

	return Component.create({

		blocks: null,

		$init: function() {
			this.blocks = $('[data-links-source-selector]');
			this.blocks.each(function() {
				var block = $(this),
					nav = block.find('ul.nav'),
					sourceSelector = block.data('links-source-selector'),
					sources = $(sourceSelector);

				sources.each(function(index, obj){
					var source = $(obj),
						generatedLi = $('<li>'),
						generatedLink = $(
							'<a>',
							{
								href: '#' + source.attr('id'),
								text: source.find('h3').text()
							}
						);
					nav.append(generatedLi.append(generatedLink));
				});

				$('body').scrollspy('refresh');
			});

		}

	});

});

define('shoptheme/components/ui/sections',[
	"jquery",
	"cartware/components/_base",
	"bootstrap-tooltip"
], function(
	$,
	Component
) {
	return Component.create({
		$init: function() {
			this.scrollToSection(document.location.href);

			var self = this;

			$('a[href*=\\#c]').on('click', function (event) {
				self.scrollToSection(event.currentTarget.href);
			});
		},

		scrollToSection: function (href) {
			var regex = /.*#c(.*)/,
				matches = regex.exec(href);

			if (matches) {
				var element = $('[data-section-id="' + matches[1] +'"]');

				if (element.length > 0) {
					var target = element[0];

					$([document.documentElement, document.body]).animate({
						scrollTop: $(target).offset().top
					}, 500);
				}
			}
		}
	});
});
define('gigaset/fx-engine',[
	"jquery"
], function(
	$
) {
	function FX() {}
	FX.prototype = {

		transitionBlend: function(from, to, ms, callback) {

			var fromContent = $( from ),
				toContent = $( to ),
				original = fromContent.wrapAll( '<div class="fx-blend-wrapper" style="position: absolute; left: 0; top: 0"></div>').parent(),
				replacement = toContent.wrapAll( '<div class="fx-blend-wrapper" style="position: absolute; left: 0; top: 0; display: none"></div>').parent();

			replacement.insertBefore( original );

			var wrapper = $( '.fx-blend-wrapper');
			wrapper.wrapAll( '<div class="fx-blend-main-wrapper" style="position: relative"></div>' );

			var fixedHeight = wrapper.outerHeight( true );
			$( '.fx-blend-main-wrapper' ).outerHeight( fixedHeight );

			original.fadeOut( ms, function() {
				$( this ).remove();
			} );
			replacement.fadeIn( ms, function() {
				toContent.unwrap().unwrap();
				if (typeof callback === 'function') callback();
			} );

		}

	};

	return new FX;

});

define("json!shoptheme/handles/default.json", function(){ return {
	"default": [
		"shoptheme/components/layout/default",
		"shoptheme/components/layout/page/switch",
	  	"shoptheme/components/layout/page/sidebar-related",
		"shoptheme/components/layout/lazyload",
		"shoptheme/components/layout/messages",
		"shoptheme/components/layout/navigation",
		"shoptheme/components/layout/trigger",
		"shoptheme/components/layout/search",
	  	"shoptheme/components/layout/story",
		"shoptheme/components/layout/equal-heights",
		"shoptheme/components/ui/accordion",
		"shoptheme/components/ui/tabs",
		"shoptheme/components/ui/selects",
		"shoptheme/components/ui/tooltips",
	  	"shoptheme/components/ui/affix",
		"shoptheme/components/ui/affix-generic",
		"shoptheme/components/ui/toggle-visibility",
		"shoptheme/components/ui/scrollspy",
	  	"shoptheme/components/ui/scrolltop",
		"shoptheme/components/ui/generated-menu",
		"shoptheme/components/ui/sections",
		"shoptheme/components/cart/ajax",
		"shoptheme/components/product/wishlist",
		"shoptheme/components/cartware/slider",
		"shoptheme/components/cartware/video",
		"shoptheme/components/plugin/universal-analytics",
		"shoptheme/components/blocks/fce/elements/usps",
	  	"shoptheme/components/blocks/fce/elements/image-map",
	    "shoptheme/components/blocks/fce/elements/hero-slider",
		"shoptheme/components/blocks/fce/elements/videoteaser",
	  	"shoptheme/components/blocks/fce/newsletter-subscribe-landingpage",
	  	"shoptheme/components/blocks/csp/detail/firmware-accordion",
		"shoptheme/components/blocks/captcha",
		"shoptheme/components/blocks/privacy/opt",
	  	"shoptheme/components/widgets/band",
		"shoptheme/components/tracking",
		"shoptheme/components/contentbridge/cms-search-results"
	],
	"gigaset_theme_home_index": [
		"shoptheme/components/layout/home"
	],

	"catalog_product_view": [
		"shoptheme/components/product/view",
		"shoptheme/components/product/view/media",
		"shoptheme/components/product/view/specifications",
		"shoptheme/components/product/options",
		"shoptheme/components/review/form",
		"shoptheme/components/product/view/store-ship",
		"shoptheme/components/plugin/shariff",
		"shoptheme/components/plugin/multiline-placeholder",
		"shoptheme/components/review/list"
	],

	"PRODUCT_TYPE_configurable": [
		"cartware/components/magento/product/type/configurable",
		"shoptheme/components/product/type/configurable"
	],

	"catalog_category_layered": [
		"shoptheme/components/category/layered",
		"shoptheme/components/ui/sidebar"
	],

	"newsletter_manage_index": [
		"shoptheme/components/customer/newsletter"
	],

	"customer_account_create": [
		"shoptheme/components/customer/account/create"
	],

	"customer_account_edit": [
		"shoptheme/components/customer/account/edit"
	],

	"gigaset_theme_policy_index": [
		"shoptheme/components/customer/account/policy"
	],

	"gigaset_contentbridge_cms_page": [
		"shoptheme/components/contentbridge/tabs",
		"shoptheme/components/contentbridge/accordeon"
	],

	"gigaset_theme_category_style_elements_1": [
		"shoptheme/components/blocks/elements/product/item/pane"
	],

	"gigaset_csp_support_detail_videotutorials": [
		"shoptheme/components/blocks/fce/elements/videotutorials"
	],

	"checkout_onepage_index": [
		"shoptheme/components/cartware/checkout"
	],

	"gigaset_contentbridge_cms_page_col2-layout-article": [
		"shoptheme/components/ui/anchor-navigation"
	],

	"gigaset_hr_list": [
		"shoptheme/components/ui/datatables"
	],

  	"gigaset_hr_detail": [
		"shoptheme/components/ui/maps"
	],

	"b2e_agreements_popup": [
		"shoptheme/components/customer/b2e/popup"
	]
}
;});

define('shoptheme/components/cart/ajax',[
	"jquery",
	"lodash",
	"cartware/components/_base",
	"cartware/jquery/bem",
	"cartware/components/Dispatcher",
	"gigaset/spinner",
	"gigaset/fx-engine",
	"json!shoptheme/handles/default.json",
	"text!shoptheme/templates/magnific-popup/default.html",
	"magnific-popup",
	"bootstrap-button"
], function(
	$,
	_,
	Component,
	BEM,
	ComponentDispatcher,
	Spinner,
	FX,
	Handles,
	PopupHtml
) {
	var popupTemplate = null;

	return Component.create({
		miniCart:		null,
		$init: function() {
			var self = this,
				$body = $(document.body);

			this.miniCart = BEM.block("mini-cart");

			this._prepareForm();

			// Bind add to cart buttons / links
			$body.on("click", "[data-ajax-add-to-cart]", _.bind(function(e) {
				e.preventDefault();
				var link = $(e.currentTarget),
					url = link.attr("href") || link.data("add-to-cart-url"),
					container = link.closest(".b_product-item__container"),
					data = link.data("ajax-add-to-cart"),
					hasRequiredOptions = _.get(data, "hasRequiredOptions");

				if (hasRequiredOptions && $body.hasClass("catalog-product-view")) {
					return true;
				}

				e.preventDefault();

				Spinner.add(container.length == 0 ? 'body' : container);
				self._sendRequest(url, { "isAjax": 1, "qty": link.data("order-qty") }, self._onAddSuccess);
				$.magnificPopup.close();
			}, this));

			// Bind cart move to wishlist links
			$body.on("click", ".cart-table .move-to-wishlist", _.bind(function(e) {
				e.preventDefault();
				var link = $(e.currentTarget),
					url = link.attr("href");

				Spinner.add("#shopping-cart-table");

				self._sendRequest(url, { "isAjax": 1 });

			}, this));

			// Bind cart view qty selects
			$body.on("change", ".cart-table select.qty", function(e) {
				var form = $(".cart > form"),
					select = $(e.target),
					url = form.attr("action"),
					data = form.serialize() + '&content=cart',
					xhr = self._sendRequest(url, data, self._onUpdateSuccess);

				Spinner.add("#shopping-cart-table");

				xhr.then(function(response) {
					if (_.isObject(response)) {
						if (response.error) {
							select.val(select.data("__original_value"));

							if (select.data("select2")) {
								select.trigger("change.select2");
							}
						}
					}
					self._rememberCartQty();
				});
			});

			// Bind cart view remove links
			$body.on("click", ".cart-table a.remove-from-cart", function(e) {
				e.preventDefault();
				var url = $(e.currentTarget).attr("href"),
					data = $('.cart input[name="form_key"]').serialize() + '&content=cart';

				Spinner.add("#shopping-cart-table");
				e.preventDefault();
				self._sendRequest(url, data, self._onRemoveSuccess);
			});

			this._rememberCartQty();
		},

		_rememberCartQty: function() {
			$(".cart-table select.qty").each(function() {
				var select = $(this);

				select.data("__original_value", select.val());
			});
		},

		/**
		 * Overwrite productAddToCartForm submit function
		 * @private
		 */
		_prepareForm: function() {
			if ($('body').hasClass('wishlist-index-configure') !== true) { // quick fix to prevent ajax add2cart on wishlist configure product view page
				var self = this,
					formObj = _.get(window, "productAddToCartFormOld", _.get(window, "productAddToCartForm"));

				if (!formObj) return;

				formObj.submit = function () {
					if (this.validator.validate()) {
						var form = $(this.form),
							url = form.attr("action");

						Spinner.add();
						self._sendRequest(url, form.serialize(), self._onAddSuccess);
					}
				};
			}
		},

		_renderPopup: function(data) {
			if (null === popupTemplate) {
				popupTemplate = _.template(PopupHtml);
			}

			return popupTemplate(data);
		},

		/**
		 * Send ajax request
		 *
		 * @param url
		 * @param data
		 * @param onSuccessCallback
		 * @private
		 */
		_sendRequest: function(url, data, onSuccessCallback) {
			var self = this;

			data = data || {};

			// add isAjax param to avoid caching problems
			if (url.indexOf("isAjax=1") === -1 && typeof data === 'string' && data.indexOf("isAjax=1") === -1) {
				url += url.indexOf("?") === -1 ? '?isAjax=1' : '&isAjax=1';
			}

			var xhr = $.ajax(url, {
				method: "POST",
				data: data
			}).always(function() {
				Spinner.remove();
			}).then(_.bind(function(response, status, xhr) {
				var responseHandles = $.parseJSON(xhr.getResponseHeader("X-Magento-Handles") || "[]");

				var _isResponseHtml = (typeof response === 'string');
				response = _isResponseHtml ? { body: response } : response;

				if (response.redirect) {
					window.location = response.redirect;
					return;
				}

				if (response.success === true && typeof onSuccessCallback === 'function') {
					onSuccessCallback(response);
				}
				if (response.mini_cart) {
					self.miniCart.replaceWith(response.mini_cart);
					self.miniCart = BEM.block("mini-cart");
				}
				if (response.cart) {
					FX.transitionBlend(".cart", response.cart, 300);
				}
				if (response.body || response.message) {
					$.magnificPopup.open({
						items: {
							src: this._renderPopup({
								body: response.body ? response.body : response.message
							}),
							type: 'inline'
						},
						callbacks: {
							open: function () {
								if (_isResponseHtml === true) {
									new ComponentDispatcher({
										handles: _.intersection(["catalog_product_view", "PRODUCT_TYPE_configurable"], responseHandles),
										apply: _.assign({}, Handles)
									});

									$(document).trigger("cartware_cart/modal/open", [response]);

									self._prepareForm();
								}

								$(document.body).scrollTop($(".mfp-content").offset().top - 60);
							}
						}
					});

					if ($.magnificPopup.instance.isOpen) {
						$(document.body).scrollTop($(".mfp-content").offset().top - 60);
					}
				}

				return response;
			}, this));

			return xhr;
		},
		_onAddSuccess: function(response) {
			$(document).trigger("cartware_cart/add", response);
		},
		_onRemoveSuccess: function(response) {
			$(document).trigger("cartware_cart/remove", response);
		},
		_onUpdateSuccess: function(response) {
			$(document).trigger("cartware_cart/update", response);
		}
	});
});

define('shoptheme/components/product/wishlist',[
	"require",
	"jquery",
	"lodash",
	"cartware/components/_base",
	"gigaset/spinner",
	"text!shoptheme/templates/magnific-popup/default.html",
	"magnific-popup"
], function(
	require,
	$,
	_,
	Component,
	Spinner,
	PopupHtml
) {
	var popupTemplate = null;

	return Component.create({
		itemCount:				0,
		productIds:				null,
		customerIsLoggedIn:		(gigaset.handles.indexOf('customer_logged_in') !== -1),
		isSecure:				('https:' == document.location.protocol),
		config:					{
			controller: 		'wishlist/index/',
			cookiePrefix:		'wish_list',
			cookieSeparator:	'__'
		},

		$init: function() {
			var self = this;

			if (this.customerIsLoggedIn === false) {
				$('.wishlist-trigger').remove();
				$('.wishlist-link').remove();
				$('.b_wishlist-link').remove();
				// return;
			}

			self._initData();

			$(document).on('click', '.wishlist-trigger', function(e) {
				var element = $(e.currentTarget),
					parent = element.closest('.b_product-item__container'),
					addUrl = element.data('wishlist-add-url'),
					removeUrl = element.data('wishlist-remove-url');

				Spinner.add(parent);

				if (element.hasClass('active')) {
					self._sendRequest(removeUrl);
					element.removeClass('active');
				} else {
					if (self.itemCount == 0) {
						element.addClass('active');
					}
					self._sendRequest(addUrl);
				}
			});

			$(document).on('click', '.wishlist-link', function(e) {
				e.preventDefault();
				if(productAddToCartForm.validator) {

					var element = $(e.currentTarget),
						parent = element.closest('.b_product-add-to-links'),
						url = element.attr('href'),
						data,
						nv = Validation.methods;

					delete Validation.methods['required-entry'];
					delete Validation.methods['validate-one-required'];
					delete Validation.methods['validate-one-required-by-name'];
					// Remove custom datetime validators
					for (var methodName in Validation.methods) {
						if (methodName.match(/^validate-datetime-.*/i)) {
							delete Validation.methods[methodName];
						}
					}

					if (productAddToCartForm.validator.validate()) {
						Spinner.add(parent);
						data = $('#product_addtocart_form').serialize();
						self._sendRequest(url, data);
					}

					Object.extend(Validation.methods, nv);
				}
			});
		},
		_initData: function() {
			this.itemCount = Mage.Cookies
				.get(this.config.cookiePrefix + this.config.cookieSeparator + 'item_count') || 0;
			this.productIds = Mage.Cookies
				.get(this.config.cookiePrefix + this.config.cookieSeparator + 'product_ids') || [];
			if (typeof this.productIds === 'string') {
				this.productIds = this.productIds.split(',');
			}
			$('.wishlist-trigger.active','.b_product-item[data-product-id]').removeClass('active');
			_.forEach($('.wishlist-link'), function(link) {
				link = $(link);
				link.attr('href', link.data('add-url')).text(link.data('add-text'));
			});
			_.forEach(this.productIds, function(productId) {
				$('.wishlist-trigger','.b_product-item[data-product-id="'+productId+'"]').addClass('active');
				var removeLink = $('.wishlist-link[data-product="'+productId+'"]');
				removeLink.attr('href', removeLink.data('remove-url')).text(removeLink.data('remove-text'));
			});

			$('.b_wishlist-link__count').text(this.itemCount);
		},
		/**
		 * Send ajax request
		 *
		 * @param url
		 * @param data
		 * @private
		 */
		_sendRequest: function(url, data, callback) {
			var self = this;
			data = data || {};

			// add isAjax param to avoid caching problems
			if (url.indexOf("isAjax=1") === -1 && typeof data === 'string' && data.indexOf("isAjax=1") === -1) {
				url += url.indexOf("?") === -1 ? '?isAjax=1' : '&isAjax=1';
			}

			$.ajax(url, {
				method: "POST",
				data: data
			}).then(_.bind(function(response) {
				if (typeof callback === 'function') callback();
				Spinner.remove();
				if ((response.error || response.success === false) && response.message) {

					self._showMessage(response.message);
				}
				self._initData();

			}, this));
		},
		_renderPopup: function(data) {
			if (null === popupTemplate) {
				popupTemplate = _.template(PopupHtml);
			}

			return popupTemplate(data);
		},
		_showMessage: function(message) {
			$.magnificPopup.open({
				items: {
					src: this._renderPopup({ body: message }),
					type: 'inline'
				}
			});
		}
	});
});
define('cartware/utils/image/preload',[
	"../../objectpool/image"
], function(
	ImagePool
) {
	var pool = ImagePool.getInstance();

	function preload(src, cb) {
		var image = pool.alloc();

		image.onload = function() {
			var info = { width: image.width, height: image.height};
			pool.release(image);
			cb(null, info);
		};

		image.onerror = function() {
			pool.release(image);
			cb(new Error("Image with source \"" + src + "\" could not be loaded."));
		};

		image.src = src;
	}

	return preload;
});
define('shoptheme/components/cartware/slider',[
	"cartware/components/_base",
	"cartware/jquery/bem",
	"cartware/utils/image/preload",
	"jquery",
	"lodash",
	"json!shoptheme/etc/breakpoints.json",
	"enquire",
	"cartware-slider"
], function(
	Component,
	BEM,
	preload,
	$,
	_,
	BreakPoints,
	enquire
) {
	var TEASER_MODE_COMBINED	= "combined",
		TEASER_MODE_STACKED		= "stacked";

	var BACKGROUND_MODE_STRETCH 	= "stretch",
		BACKGROUND_MODE_INFLATE 	= "inflate";

	return Component.create({
		teaserLayoutCallbacks:			null,
		teaserMode:						null,

		backgroundLayoutCallbacks:		null,
		backgroundMode:					null,

		slideshows:						null,

		constructor: function() {
			this.teaserLayoutCallbacks = [];
			this.teaserMode = TEASER_MODE_STACKED;

			this.backgroundLayoutCallbacks = [];
			this.backgroundMode = BACKGROUND_MODE_INFLATE;

			this.slideshows = [];
		},

		init: function() {
			enquire.register('(min-width: ' + (BreakPoints.xs + 1) + 'px)', {
				match: _.bind(this.layoutTeasersCombined, this),
				unmatch: _.bind(this.layoutTeasersStacked, this)
			}, true);

			enquire.register('(min-width: ' + (BreakPoints.sm + 1) + 'px)', {
				match: _.bind(this.layoutBackgroundStretch, this),
				unmatch: _.bind(this.layoutBackgroundInflate, this)
			}, true);
		},

		$init: function() {
			var self = this;

			var reflow = _.throttle(function() {
				_.forEach(self.slideshows, function(slideshow) {
					slideshow.reflow();
				});
			}, 150);
			BEM.block("cartware-slider-slide:modifier(teaser)").each(function() {
				var slide = BEM.block(this),
					wrapper = slide.getElements("wrapper"),
					teaser = slide.getElements("teaser"),
					vAlignMiddle = wrapper.is(":modifier(v-middle)"),
					slider = slide.parents(".cartware-slider:first"),
					slideshow = slider.data("cartware_slider/instance"),
					adjustMargin = slider.parents(".container").length === 0;

				if (self.slideshows.indexOf(slideshow) < 0) {
					self.slideshows.push(slideshow);
				}

				var layout = function() {
					var viewportWidth = $(window).width(),
						slideTextContentMargin;

					if (adjustMargin) {
						slideTextContentMargin = Math.round((viewportWidth - wrapper.outerWidth()) / 2);

						wrapper.css({
							marginLeft: slideTextContentMargin,
							marginRight: slideTextContentMargin
						});
					}

					if (vAlignMiddle && self.teaserMode == TEASER_MODE_COMBINED) {
						wrapper.css({
							top: Math.round((slide.innerHeight() - teaser.outerHeight()) / 2)
						});
					}

					teaser.css({
						width: self.teaserMode == TEASER_MODE_STACKED ? viewportWidth : ""
					});

					slide.css({
						minHeight: self.teaserMode == TEASER_MODE_STACKED
							? ""
							: wrapper.outerHeight() + wrapper.position().top
					});

					reflow();
				};

				$(window).resize(_.throttle(layout, 150));
				layout();

				self.teaserLayoutCallbacks.push(layout);
			});

			$(".cartware-slider-slide-background").each(function() {
				var backgroundContainer = $(this),
					backgroundImage = backgroundContainer.css("background-image").match(/^url\(["'](.*)["']\)$/)[1] || null,
					slide = backgroundContainer.closest(".cartware-slider-item-wrap"),
					slider = slide.closest(".cartware-slider"),
					slideshow = slider.data("cartware_slider/instance"),
					stage = slide.closest(".cartware-slider-stage"),
					inner = slide.find(".teaser-inner:first"),
					imageDimensions = null;

				if (!backgroundImage) return;

				if (self.slideshows.indexOf(slideshow) < 0) {
					self.slideshows.push(slideshow);
				}

				var layout = function() {
					backgroundContainer.css(
						"min-height",
						self.backgroundMode === BACKGROUND_MODE_STRETCH
							? imageDimensions.height
							: ""
					);

					slide.css(
						"min-height",
						self.backgroundMode === BACKGROUND_MODE_STRETCH
							? imageDimensions.height
							: ""
					);

					inner.css("min-height", "");

					slideshow.reflow();
				};

				slideshow.on("reflow", function() {
					inner.css(
						"min-height",
						self.backgroundMode === BACKGROUND_MODE_STRETCH
							? ""
							: stage.height()
					);
				});
                
				preload(backgroundImage, function(err, info) {
					if (err) {
						throw err;
					} else {
						imageDimensions = info;

						$(window).resize(_.throttle(layout, 150));
						layout();

						self.backgroundLayoutCallbacks.push(layout);
					}
				});

				//var img = imagePool.alloc();
				//
				//img.onload = function() {
				//	imageDimensions = {
				//		width: img.width,
				//		height: img.height
				//	};
				//
				//	$(window).resize(_.throttle(layout, 150));
				//	layout();
				//
				//	self.backgroundLayoutCallbacks.push(layout);
				//
				//	imagePool.release(img);
				//};
				//
				//img.onerror = function() {
				//	imagePool.release(img);
				//};
				//
				//img.src = backgroundImage;
			});
		},

		layoutTeasers: function() {
			_.forEach(this.teaserLayoutCallbacks, function(cb) {
				cb();
			});
		},

		layoutTeasersCombined: function() {
			this.teaserMode = TEASER_MODE_COMBINED;
			this.layoutTeasers();
		},

		layoutTeasersStacked: function() {
			this.teaserMode = TEASER_MODE_STACKED;
			this.layoutTeasers();
		},

		layoutBackgrounds: function() {
			_.forEach(this.backgroundLayoutCallbacks, function(cb) {
				cb();
			});
		},

		layoutBackgroundStretch: function() {
			this.backgroundMode = BACKGROUND_MODE_STRETCH;
			this.layoutBackgrounds();
		},

		layoutBackgroundInflate: function() {
			this.backgroundMode = BACKGROUND_MODE_INFLATE;
			this.layoutBackgrounds();
		}
	});
});

define("json!shoptheme/etc/consent.json", function(){ return {
  	"youtube": "BJz7qNsdj-7",
  	"vimeo": "HyEX5Nidi-m"
};});

/**
 * - Uses GDPR-compliant custom rewrites of Vimeo and Youtube video.js (videojs-vimeo-gdpr.js, videojs-youtube-gdpr.js)
 * - Manages Cookie consent flow for video players (listens to civik:cookie-consent-changed custom event)
 * - Controls 3rd party player lifecycles and disposal
 * - Restores HTML player functionality and cookie overlays for multiple deny and grant scenarios without reload requirement
 *
 *  Used by B2C stores as well as B2B (Pro) stores
 */
define('shoptheme/components/cartware/video',[
    "jquery",
    "lodash",
    "cartware/components/_base",
    "cartware/jquery/bem",
    "json!shoptheme/etc/consent.json",
    "videojs",
    "videojs-vimeo",
    "videojs-youtube"
], function(
    $,
    _,
    Component,
    BEM,
    Consent,
    videojs
) {
    return Component.create({
        elements: [],
        sliderSlideClass: "." + BEM.buildElementClassname("cartware-slider-slide"),
        videoWidgetClass: "." + BEM.buildBlockClassname("video-widget"),
        videoOverlayClass: "." + BEM.buildElementClassname("cartware-slider-slide", "loading-overlay"),
        consentIdentifierVideo: 'functional',
        localStorageConsentIdentifier: 'civik:cookie-control',
        consentEventIdentifier: 'civik:cookie-consent-changed',
        videoRegistry: {}, // tracks video elements and their state
        consentListenerAttached: false,

        $init: function() {
            var self = this;

            var cwSlider = $('.cartware-slider');
            var sliderInstance = cwSlider.data('cartware_slider/instance');

            // Set to true for debug mode
            this.isDebugMode = _.get(window, 'gigaset.featureflags.developer_mode', false);

            // Attach consent listener
            this.attachConsentListener();

            // Initialize videos
            this.initialize();

            if (cwSlider.length > 0 && sliderInstance !== undefined) {
                sliderInstance.on('ready', function (event) {
                    self.resizeVideoJS();
                    self.handleActiveVideoSlide(cwSlider);
                });

                cwSlider.on('cartware_slider/slide_after', function (event) {
                    self.handleActiveVideoSlide($(event.currentTarget));
                });
            }

            window.onresize = function() {
                self.resizeVideoJS();
            };

            $('.b_video_no_consent__link').on('click', function(e) {
                e.preventDefault();
                const link = $(this);
                const cookieControl = _.get(window, 'CookieControl');

                if (cookieControl) {
                    link.css('cursor', 'pointer');
                    cookieControl.open();
                }
            });
        },

        attachConsentListener: function() {
            // Prevent duplicate event listeners
            if (this.consentListenerAttached) {
                return;
            }
            this.consentListenerAttached = true;

            var self = this;

            window.addEventListener(self.consentEventIdentifier, function(event) {
                if (self.isDebugMode) {
                    console.log('[Video.js] Cookie consent changed event triggered', event);
                }

                try {
                    var payload = event.detail && event.detail.payload;
                    var hasConsent = payload && payload.accepted &&
                        payload.accepted.includes(self.consentIdentifierVideo);

                    if (hasConsent) {
                        self.unlockAllVideos();
                    } else {
                        self.lockAllVideos();
                    }
                } catch (error) {
                    if (self.isDebugMode) {
                        console.log('[Video.js] Error processing consent event:', error);
                    }
                }
            });

            if (this.isDebugMode) {
                console.log('[Video.js] Consent event listener attached');
            }
        },

        hasConsent: function() {
            // Check actual consent
            try {
                var stored = localStorage.getItem(this.localStorageConsentIdentifier);
                if (stored) {
                    var data = JSON.parse(stored);
                    return data.accepted && data.accepted.includes(this.consentIdentifierVideo);
                }
            } catch (e) {}
            return false;
        },

        grantConsentToVideoPlayers: function() {
            if (typeof Youtube !== 'undefined' && Youtube.grantConsent) {
                Youtube.grantConsent();
                if (this.isDebugMode) {
                    console.log('[Video.js] Granted consent to YouTube');
                }
            }

            if (typeof Vimeo !== 'undefined' && Vimeo.grantConsent) {
                Vimeo.grantConsent();
                if (this.isDebugMode) {
                    console.log('[Video.js] Granted consent to Vimeo');
                }
            }
        },

        revokeConsentFromVideoPlayers: function() {
            if (typeof Youtube !== 'undefined' && Youtube.revokeConsent) {
                Youtube.revokeConsent();
                if (this.isDebugMode) {
                    console.log('[Video.js] Revoked consent from YouTube');
                }
            }

            if (typeof Vimeo !== 'undefined' && Vimeo.revokeConsent) {
                Vimeo.revokeConsent();
                if (this.isDebugMode) {
                    console.log('[Video.js] Revoked consent from Vimeo');
                }
            }
        },

        registerVideo: function(element) {
            var $el = $(element);
            var id = $el.attr('id');

            if (!id) {
                id = 'video-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
                $el.attr('id', id);
            }

            // Register if not registered yet
            if (!this.videoRegistry[id]) {
                this.videoRegistry[id] = {
                    id: id,
                    originalHtml: element.outerHTML,
                    setup: $el.data('custom-setup'),
                    aspectRatio: $el.data('aspect-ratio'),
                    isSlide: $el.data('is-slide') == 1,
                    isInitialized: false,
                    player: null
                };

                if (this.isDebugMode) {
                    console.log('[Video.js] Registered video:', id);
                }
            }

            return this.videoRegistry[id];
        },

        initialize: function() {
            var elements = $("video[data-custom-setup]");
            var self = this;
            var consentGiven = this.hasConsent();

            if (this.isDebugMode) {
                console.log('[Video.js] Initialize called, consent:', consentGiven, 'videos found:', elements.length);
            }

            elements.each(function() {
                var video = $(this);
                var setup = video.data("custom-setup");

                if (consentGiven && setup && "autoInitialization" in setup && setup.autoInitialization === false) {
                    if (self.isDebugMode) {
                        console.log('[Video.js] Skipping video with autoInitialization :', video.attr('id'));
                    }
                    // Skip autoinitialize videos which show in a popup when consent is granted, for instance for videoteaser.js
                    return;
                }

                // Register video (stores original HTML)
                var registry = self.registerVideo(this);

                if (consentGiven) {
                    self.initVideoWithConsent(this, registry);
                } else {
                    self.showConsentOverlay(this, registry);
                }
            });
        },

        initVideoWithConsent: function(element, registry) {
            var self = this;
            var $video = $(element);

            // Grant consent to video players
            this.grantConsentToVideoPlayers();

            // Remove overlay
            this.toggleClassToParent(false, [element]);

            // Initialize video.js player
            var callback = function() {
                var player = this;

                if (element.hasAttribute("muted")) {
                    player.muted(true);
                }

                player.videoAspectRatio = registry.aspectRatio;

                // Store player reference
                registry.player = player;
                registry.isInitialized = true;

                // Add to elements array for resize handling
                if (self.elements.indexOf(player) === -1) {
                    self.elements.push(player);
                }

                self.resizeVideoJS();

                if (self.isDebugMode) {
                    console.log('[Video.js] Player initialized for:', registry.id);
                }
            };

            if (registry.isSlide) {
                this.initVideoSlideInternal(element, registry, callback);
            } else {
                videojs(element, registry.setup, callback);
            }
        },

        // Initialize slide video with consent
        initVideoSlideInternal: function(element, registry, callback) {
            var self = this;
            var videoSlide = $(element);
            var slideContainer = videoSlide.closest('.cartware-slider-item-wrap');
            var slider = videoSlide.closest('.cartware-slider');
            var instance = slider.data('cartware_slider/instance');

            if (instance) {
                if (slideContainer.css('display') === 'none') {
                    self.fixMobileActiveThumbSelection();
                    instance.pauseAutoslide(false);
                } else {
                    instance.pauseAutoslide(true);
                }
            }

            videojs(element, registry.setup, function() {
                var player = this;
                player.loadingSpinner.show();

                callback.call(this);

                slider.one('mouseenter', function() {
                    videoSlide.removeClass('hide-controls');
                });

                slider.trigger('resize');
            });
        },

        /**
         * Show consent overlay for a video
         */
        showConsentOverlay: function(element, registry) {
            this.toggleClassToParent(true, [element]);

            if (this.isDebugMode) {
                console.log('[Video.js] Showing consent overlay for:', registry.id);
            }
        },

        unlockAllVideos: function() {
            var self = this;

            if (this.isDebugMode) {
                console.log('[Video.js] Unlocking all videos');
            }

            // Grant consent
            this.grantConsentToVideoPlayers();

            // Process registered videos
            Object.keys(this.videoRegistry).forEach(function(id) {
                var registry = self.videoRegistry[id];

                // Find element (eventually restored HTML)
                var element = $('#' + id);

                if (element.length === 0) {
                    if (self.isDebugMode) {
                        console.log('[Video.js] Element not found for:', id);
                    }
                    return;
                }

                // If already initialized, just remove overlay
                if (registry.isInitialized && registry.player) {
                    self.toggleClassToParent(false, [element[0]]);
                    return;
                }

                // Initialize video with consent
                self.initVideoWithConsent(element[0], registry);
            });
        },

        lockAllVideos: function() {
            var self = this;

            if (this.isDebugMode) {
                console.log('[Video.js] Locking all videos');
            }

            // Revoke consent
            this.revokeConsentFromVideoPlayers();

            // Process registered videos
            Object.keys(this.videoRegistry).forEach(function(id) {
                var registry = self.videoRegistry[id];

                // Find container before dispose removes the child element
                var container = null;
                var parent = null;

                // Get original wrapper
                container = $('#' + id);

                if (container.length === 0) {
                    // Fallback as original video.js could have changed ID
                    container = $('[id^="' + id + '"]').first();

                    if (container.length > 0 && !container.hasClass('video-js')) {
                        container = container.closest('.video-js');
                    }
                }

                // Store parent reference BEFORE dispose removes the element
                if (container.length > 0) {
                    parent = container.parent();

                    if (self.isDebugMode) {
                        console.log('[Video.js] Found container for:', id, container[0]);
                    }
                }

                // Dispose player if exists
                if (registry.player) {
                    try {
                        // Remove from elements
                        var idx = self.elements.indexOf(registry.player);
                        if (idx > -1) {
                            self.elements.splice(idx, 1);
                        }

                        // dispose
                        registry.player.dispose();

                        if (self.isDebugMode) {
                            console.log('[Video.js] Player disposed for:', id);
                        }
                    } catch (e) {
                        if (self.isDebugMode) {
                            console.log('[Video.js] Error disposing player:', e);
                        }
                    }
                    registry.player = null;
                }

                registry.isInitialized = false;

                // Restore original HTML
                if (parent && parent.length > 0) {

                    var restore = $(registry.originalHtml);

                    // Restore to parent
                    parent.append(restore);

                    // Show cookie overlay
                    self.toggleClassToParent(true, [restore[0]]);

                    if (self.isDebugMode) {
                        console.log('[Video.js] HTML restored for:', id);
                    }
                } else {
                    if (self.isDebugMode) {
                        console.log('[Video.js] Could not find parent to restore:', id);
                    }
                }
            });
        },

        // Init Video
        initVideo: function(element) {
            var self = this;
            var video = $(element);
            var tabContentClass = "." + BEM.buildElementClassname("tabs", "tab-content");

            video.closest(tabContentClass).on("tab-content-active", function(e) {
                self.resizeVideoJS();
            });

            // Register and check consent
            var registry = this.registerVideo(element);

            if (this.hasConsent()) {
                this.initVideoWithConsent(element, registry);
            } else {
                this.showConsentOverlay(element, registry);
            }
        },

        // Init slider video slide
        initVideoSlide: function(element) {
            var registry = this.registerVideo(element);

            if (this.hasConsent()) {
                this.initVideoWithConsent(element, registry);
            } else {
                this.showConsentOverlay(element, registry);
            }
        },

        // Handle active slider video slide
        handleActiveVideoSlide: function(slider) {
            var self = this;
            var instance = slider.data('cartware_slider/instance');

            if (!instance) {
                return;
            }

            var video = instance.activeItem.find('.video-js');

            if ($(instance.activeItem).css('display') === 'none') {
                self.fixMobileActiveThumbSelection();
                instance.pauseAutoslide(false);
            } else {
                if (video.length > 0) {
                    var videoOverlay = instance.activeItem.find(self.videoOverlayClass);

                    // Get player from registry or video.js
                    var videoId = video.attr('id');
                    var registry = self.videoRegistry[videoId];
                    var player = registry && registry.player;

                    if (!player) {
                        try {
                            player = videojs(video[0]);
                        } catch (e) {
                            return;
                        }
                    }

                    if (player.ended()) {
                        player.currentTime(0);
                    }

                    player.play();
                    instance.pauseAutoslide(true);

                    if (!("__component_events_bound" in player)) {
                        player.on('timeupdate', function () {
                            if (!videoOverlay.hasClass('disabled') && this.currentTime() > 0) {
                                videoOverlay.addClass('disabled');
                            }
                        });
                        player.on('ended', function () {
                            if (instance.settings.autoslide == true) {
                                videoOverlay.removeClass('disabled');
                                instance.next();
                            }
                        });

                        player["__component_events_bound"] = true;
                    }
                } else {
                    instance.pauseAutoslide(false);
                }
            }
        },

        // Mobile thumbs
        fixMobileActiveThumbSelection: function() {
            var self = this;
            var activeThumb = $('.thumbs-container .thumb-video.active');
            if (activeThumb.css('display') === 'none') {
                var nextThumb = activeThumb.closest('li').next().find('.thumb');
                activeThumb.removeClass('active');
                nextThumb.addClass('active');
                self.fixMobileActiveThumbSelection();
            }
        },

        // Resize
        resizeVideoJS: function() {
            var windowHeight = $(window).height();

            $.each(this.elements, function(index, value) {
                if (!value || !value.id) return;

                var player = $(document.getElementById(value.id()));
                if (player.length === 0) return;

                var width = player.parent().width(),
                    height = width / value.videoAspectRatio,
                    maxHeight = Math.min(player.offsetParent().height() || windowHeight, windowHeight);

                if (player.closest(".b_cartware-slider-slide--video, .b_tabs__tab-content").length) {
                    maxHeight = Infinity;
                }

                if (height > maxHeight) {
                    width = width * maxHeight / height;
                    height = maxHeight;
                }

                player
                    .width(width|0)
                    .height(height|0);

                player.trigger("gigaset.content.change");
            });

            this.fixMobileActiveThumbSelection();
        },

        // Legacy method
        videojs: function () {
            var element = $(arguments[0]),
                setup = element.data('custom-setup');

            if (setup) {
                var registry = this.registerVideo(arguments[0]);

                if (this.hasConsent()) {
                    this.initVideoWithConsent(arguments[0], registry);
                } else {
                    this.showConsentOverlay(arguments[0], registry);
                }
            }
        },

        // Add / hide cookie denied class and therefore the cookie banner
        toggleClassToParent: function (addClass, args) {
            if (!args || !args[0]) {
                return;
            }

            var $element = $(args[0]);
            var $widget = $element.closest('.b_video-widget');

            if ($widget.length === 0) {
                // Fallback to finding any video widget
                $widget = $('.b_video-widget');
            }

            if (addClass) {
                $widget.addClass('cookie-denied');
                if (this.isDebugMode) {
                    console.log('[Video.js] Added cookie-denied class');
                }
            } else {
                $widget.removeClass('cookie-denied');
                if (this.isDebugMode) {
                    console.log('[Video.js] Removed cookie-denied class');
                }
            }
        }
    });
});

/*!
 * jQuery Cookie Plugin v1.4.1
 * https://github.com/carhartl/jquery-cookie
 *
 * Copyright 2013 Klaus Hartl
 * Released under the MIT license
 */
(function (factory) {
	if (typeof define === 'function' && define.amd) {
		// AMD
		define('jquery-cookie',['jquery'], factory);
	} else if (typeof exports === 'object') {
		// CommonJS
		factory(require('jquery'));
	} else {
		// Browser globals
		factory(jQuery);
	}
}(function ($) {

	var pluses = /\+/g;

	function encode(s) {
		return config.raw ? s : encodeURIComponent(s);
	}

	function decode(s) {
		return config.raw ? s : decodeURIComponent(s);
	}

	function stringifyCookieValue(value) {
		return encode(config.json ? JSON.stringify(value) : String(value));
	}

	function parseCookieValue(s) {
		if (s.indexOf('"') === 0) {
			// This is a quoted cookie as according to RFC2068, unescape...
			s = s.slice(1, -1).replace(/\\"/g, '"').replace(/\\\\/g, '\\');
		}

		try {
			// Replace server-side written pluses with spaces.
			// If we can't decode the cookie, ignore it, it's unusable.
			// If we can't parse the cookie, ignore it, it's unusable.
			s = decodeURIComponent(s.replace(pluses, ' '));
			return config.json ? JSON.parse(s) : s;
		} catch(e) {}
	}

	function read(s, converter) {
		var value = config.raw ? s : parseCookieValue(s);
		return $.isFunction(converter) ? converter(value) : value;
	}

	var config = $.cookie = function (key, value, options) {

		// Write

		if (value !== undefined && !$.isFunction(value)) {
			options = $.extend({}, config.defaults, options);

			if (typeof options.expires === 'number') {
				var days = options.expires, t = options.expires = new Date();
				t.setTime(+t + days * 864e+5);
			}

			return (document.cookie = [
				encode(key), '=', stringifyCookieValue(value),
				options.expires ? '; expires=' + options.expires.toUTCString() : '', // use expires attribute, max-age is not supported by IE
				options.path    ? '; path=' + options.path : '',
				options.domain  ? '; domain=' + options.domain : '',
				options.secure  ? '; secure' : ''
			].join(''));
		}

		// Read

		var result = key ? undefined : {};

		// To prevent the for loop in the first place assign an empty array
		// in case there are no cookies at all. Also prevents odd result when
		// calling $.cookie().
		var cookies = document.cookie ? document.cookie.split('; ') : [];

		for (var i = 0, l = cookies.length; i < l; i++) {
			var parts = cookies[i].split('=');
			var name = decode(parts.shift());
			var cookie = parts.join('=');

			if (key && key === name) {
				// If second argument (value) is a function it's a converter...
				result = read(cookie, value);
				break;
			}

			// Prevent storing a cookie that we couldn't decode.
			if (!key && (cookie = read(cookie)) !== undefined) {
				result[name] = cookie;
			}
		}

		return result;
	};

	config.defaults = {};

	$.removeCookie = function (key, options) {
		if ($.cookie(key) === undefined) {
			return false;
		}

		// Must not alter options, thus extending a fresh object...
		$.cookie(key, '', $.extend({}, options, { expires: -1 }));
		return !$.cookie(key);
	};

}));

define('shoptheme/components/plugin/universal-analytics',[
	"jquery",
	"cartware/components/_base",
    "jquery-cookie"
], function(
	$,
	Component
) {
	return Component.create({
		$init: function () {
            if (typeof manipulationOfCart === 'function') {
                // On product add
                $(document).on('cartware_cart/add', function(response) {
                    $.cookie.json = true;
                    var productToBasket = $.cookie("productToBasket"),
                        productlist = $.cookie("productlist");
                    if (productToBasket != undefined) {
                        manipulationOfCart(productToBasket, 'add', productlist);
                        $.removeCookie("productToBasket", { path: '/', domain: '.' + document.domain });
                    }
                });

                // On product remove
                $(document).on('cartware_cart/remove', function(response) {
                    $.cookie.json = true;
                    var productOutBasket = $.cookie("productOutBasket");
                    if (productOutBasket != undefined) {
                        manipulationOfCart(productOutBasket, 'remove', '');
                        $.removeCookie("productOutBasket", {path: '/', domain: '.' + document.domain});
                    }
                });
            }
		}
	});
});
/////    /////    /////    /////
/////    /////    /////    /////
/////    /////    /////    /////
/////    /////    /////    /////
/////             /////    /////
/////             /////    /////
/////    /////    /////    /////
/////    /////    /////    /////
         /////    /////
         /////    /////
/////    /////    /////    /////
/////    /////    /////    /////
/////    /////    /////    /////
/////    /////    /////    /////

/**
 * ScrollReveal
 * ------------
 * Version : 3.3.2
 * Website : scrollrevealjs.org
 * Repo    : github.com/jlmakes/scrollreveal.js
 * Author  : Julian Lloyd (@jlmakes)
 */

;(function () {
  'use strict'

  var sr
  var _requestAnimationFrame

  function ScrollReveal (config) {
    // Support instantiation without the `new` keyword.
    if (typeof this === 'undefined' || Object.getPrototypeOf(this) !== ScrollReveal.prototype) {
      return new ScrollReveal(config)
    }

    sr = this // Save reference to instance.
    sr.version = '3.3.2'
    sr.tools = new Tools() // *required utilities

    if (sr.isSupported()) {
      sr.tools.extend(sr.defaults, config || {})

      sr.defaults.container = _resolveContainer(sr.defaults)

      sr.store = {
        elements: {},
        containers: []
      }

      sr.sequences = {}
      sr.history = []
      sr.uid = 0
      sr.initialized = false
    } else if (typeof console !== 'undefined' && console !== null) {
      // Note: IE9 only supports console if devtools are open.
      console.log('ScrollReveal is not supported in this browser.')
    }

    return sr
  }

  /**
   * Configuration
   * -------------
   * This object signature can be passed directly to the ScrollReveal constructor,
   * or as the second argument of the `reveal()` method.
   */

  ScrollReveal.prototype.defaults = {
    // 'bottom', 'left', 'top', 'right'
    origin: 'bottom',

    // Can be any valid CSS distance, e.g. '5rem', '10%', '20vw', etc.
    distance: '20px',

    // Time in milliseconds.
    duration: 500,
    delay: 0,

    // Starting angles in degrees, will transition from these values to 0 in all axes.
    rotate: { x: 0, y: 0, z: 0 },

    // Starting opacity value, before transitioning to the computed opacity.
    opacity: 0,

    // Starting scale value, will transition from this value to 1
    scale: 0.9,

    // Accepts any valid CSS easing, e.g. 'ease', 'ease-in-out', 'linear', etc.
    easing: 'cubic-bezier(0.6, 0.2, 0.1, 1)',

    // `<html>` is the default reveal container. You can pass either:
    // DOM Node, e.g. document.querySelector('.fooContainer')
    // Selector, e.g. '.fooContainer'
    container: window.document.documentElement,

    // true/false to control reveal animations on mobile.
    mobile: true,

    // true:  reveals occur every time elements become visible
    // false: reveals occur once as elements become visible
    reset: false,

    // 'always' — delay for all reveal animations
    // 'once'   — delay only the first time reveals occur
    // 'onload' - delay only for animations triggered by first load
    useDelay: 'always',

    // Change when an element is considered in the viewport. The default value
    // of 0.20 means 20% of an element must be visible for its reveal to occur.
    viewFactor: 0.2,

    // Pixel values that alter the container boundaries.
    // e.g. Set `{ top: 48 }`, if you have a 48px tall fixed toolbar.
    // --
    // Visual Aid: https://scrollrevealjs.org/assets/viewoffset.png
    viewOffset: { top: 0, right: 0, bottom: 0, left: 0 },

    // Callbacks that fire for each triggered element reveal, and reset.
    beforeReveal: function (domEl) {},
    beforeReset: function (domEl) {},

    // Callbacks that fire for each completed element reveal, and reset.
    afterReveal: function (domEl) {},
    afterReset: function (domEl) {}
  }

  /**
   * Check if client supports CSS Transform and CSS Transition.
   * @return {boolean}
   */
  ScrollReveal.prototype.isSupported = function () {
    var style = document.documentElement.style
    return 'WebkitTransition' in style && 'WebkitTransform' in style ||
      'transition' in style && 'transform' in style
  }

  /**
   * Creates a reveal set, a group of elements that will animate when they
   * become visible. If [interval] is provided, a new sequence is created
   * that will ensure elements reveal in the order they appear in the DOM.
   *
   * @param {Node|NodeList|string} [target]   The node, node list or selector to use for animation.
   * @param {Object}               [config]   Override the defaults for this reveal set.
   * @param {number}               [interval] Time between sequenced element animations (milliseconds).
   * @param {boolean}              [sync]     Used internally when updating reveals for async content.
   *
   * @return {Object} The current ScrollReveal instance.
   */
  ScrollReveal.prototype.reveal = function (target, config, interval, sync) {
    var container
    var elements
    var elem
    var elemId
    var sequence
    var sequenceId

    // No custom configuration was passed, but a sequence interval instead.
    // let’s shuffle things around to make sure everything works.
    if (config !== undefined && typeof config === 'number') {
      interval = config
      config = {}
    } else if (config === undefined || config === null) {
      config = {}
    }

    container = _resolveContainer(config)
    elements = _getRevealElements(target, container)

    if (!elements.length) {
      console.log('ScrollReveal: reveal on "' + target + '" failed, no elements found.')
      return sr
    }

    // Prepare a new sequence if an interval is passed.
    if (interval && typeof interval === 'number') {
      sequenceId = _nextUid()

      sequence = sr.sequences[sequenceId] = {
        id: sequenceId,
        interval: interval,
        elemIds: [],
        active: false
      }
    }

    // Begin main loop to configure ScrollReveal elements.
    for (var i = 0; i < elements.length; i++) {
      // Check if the element has already been configured and grab it from the store.
      elemId = elements[i].getAttribute('data-sr-id')
      if (elemId) {
        elem = sr.store.elements[elemId]
      } else {
        // Otherwise, let’s do some basic setup.
        elem = {
          id: _nextUid(),
          domEl: elements[i],
          seen: false,
          revealing: false
        }
        elem.domEl.setAttribute('data-sr-id', elem.id)
      }

      // Sequence only setup
      if (sequence) {
        elem.sequence = {
          id: sequence.id,
          index: sequence.elemIds.length
        }

        sequence.elemIds.push(elem.id)
      }

      // New or existing element, it’s time to update its configuration, styles,
      // and send the updates to our store.
      _configure(elem, config, container)
      _style(elem)
      _updateStore(elem)

      // We need to make sure elements are set to visibility: visible, even when
      // on mobile and `config.mobile === false`, or if unsupported.
      if (sr.tools.isMobile() && !elem.config.mobile || !sr.isSupported()) {
        elem.domEl.setAttribute('style', elem.styles.inline)
        elem.disabled = true
      } else if (!elem.revealing) {
        // Otherwise, proceed normally.
        elem.domEl.setAttribute('style',
          elem.styles.inline +
          elem.styles.transform.initial
        )
      }
    }

    // Each `reveal()` is recorded so that when calling `sync()` while working
    // with asynchronously loaded content, it can re-trace your steps but with
    // all your new elements now in the DOM.

    // Since `reveal()` is called internally by `sync()`, we don’t want to
    // record or intiialize each reveal during syncing.
    if (!sync && sr.isSupported()) {
      _record(target, config, interval)

      // We push initialization to the event queue using setTimeout, so that we can
      // give ScrollReveal room to process all reveal calls before putting things into motion.
      // --
      // Philip Roberts - What the heck is the event loop anyway? (JSConf EU 2014)
      // https://www.youtube.com/watch?v=8aGhZQkoFbQ
      if (sr.initTimeout) {
        window.clearTimeout(sr.initTimeout)
      }
      sr.initTimeout = window.setTimeout(_init, 0)
    }

    return sr
  }

  /**
   * Re-runs `reveal()` for each record stored in history, effectively capturing
   * any content loaded asynchronously that matches existing reveal set targets.
   * @return {Object} The current ScrollReveal instance.
   */
  ScrollReveal.prototype.sync = function () {
    if (sr.history.length && sr.isSupported()) {
      for (var i = 0; i < sr.history.length; i++) {
        var record = sr.history[i]
        sr.reveal(record.target, record.config, record.interval, true)
      }
      _init()
    } else {
      console.log('ScrollReveal: sync failed, no reveals found.')
    }
    return sr
  }

  /**
   * Private Methods
   * ---------------
   */

  function _resolveContainer (config) {
    if (config && config.container) {
      if (typeof config.container === 'string') {
        return window.document.documentElement.querySelector(config.container)
      } else if (sr.tools.isNode(config.container)) {
        return config.container
      } else {
        console.log('ScrollReveal: invalid container "' + config.container + '" provided.')
        console.log('ScrollReveal: falling back to default container.')
      }
    }
    return sr.defaults.container
  }

  /**
   * check to see if a node or node list was passed in as the target,
   * otherwise query the container using target as a selector.
   *
   * @param {Node|NodeList|string} [target]    client input for reveal target.
   * @param {Node}                 [container] parent element for selector queries.
   *
   * @return {array} elements to be revealed.
   */
  function _getRevealElements (target, container) {
    if (typeof target === 'string') {
      return Array.prototype.slice.call(container.querySelectorAll(target))
    } else if (sr.tools.isNode(target)) {
      return [target]
    } else if (sr.tools.isNodeList(target)) {
      return Array.prototype.slice.call(target)
    }
    return []
  }

  /**
   * A consistent way of creating unique IDs.
   * @returns {number}
   */
  function _nextUid () {
    return ++sr.uid
  }

  function _configure (elem, config, container) {
    // If a container was passed as a part of the config object,
    // let’s overwrite it with the resolved container passed in.
    if (config.container) config.container = container
    // If the element hasn’t already been configured, let’s use a clone of the
    // defaults extended by the configuration passed as the second argument.
    if (!elem.config) {
      elem.config = sr.tools.extendClone(sr.defaults, config)
    } else {
      // Otherwise, let’s use a clone of the existing element configuration extended
      // by the configuration passed as the second argument.
      elem.config = sr.tools.extendClone(elem.config, config)
    }

    // Infer CSS Transform axis from origin string.
    if (elem.config.origin === 'top' || elem.config.origin === 'bottom') {
      elem.config.axis = 'Y'
    } else {
      elem.config.axis = 'X'
    }
  }

  function _style (elem) {
    var computed = window.getComputedStyle(elem.domEl)

    if (!elem.styles) {
      elem.styles = {
        transition: {},
        transform: {},
        computed: {}
      }

      // Capture any existing inline styles, and add our visibility override.
      // --
      // See section 4.2. in the Documentation:
      // https://github.com/jlmakes/scrollreveal.js#42-improve-user-experience
      elem.styles.inline = elem.domEl.getAttribute('style') || ''
      elem.styles.inline += '; visibility: visible; '

      // grab the elements existing opacity.
      elem.styles.computed.opacity = computed.opacity

      // grab the elements existing transitions.
      if (!computed.transition || computed.transition === 'all 0s ease 0s') {
        elem.styles.computed.transition = ''
      } else {
        elem.styles.computed.transition = computed.transition + ', '
      }
    }

    // Create transition styles
    elem.styles.transition.instant = _generateTransition(elem, 0)
    elem.styles.transition.delayed = _generateTransition(elem, elem.config.delay)

    // Generate transform styles, first with the webkit prefix.
    elem.styles.transform.initial = ' -webkit-transform:'
    elem.styles.transform.target = ' -webkit-transform:'
    _generateTransform(elem)

    // And again without any prefix.
    elem.styles.transform.initial += 'transform:'
    elem.styles.transform.target += 'transform:'
    _generateTransform(elem)
  }

  function _generateTransition (elem, delay) {
    var config = elem.config

    return '-webkit-transition: ' + elem.styles.computed.transition +
      '-webkit-transform ' + config.duration / 1000 + 's ' +
      config.easing + ' ' +
      delay / 1000 + 's, opacity ' +
      config.duration / 1000 + 's ' +
      config.easing + ' ' +
      delay / 1000 + 's; ' +

      'transition: ' + elem.styles.computed.transition +
      'transform ' + config.duration / 1000 + 's ' +
      config.easing + ' ' +
      delay / 1000 + 's, opacity ' +
      config.duration / 1000 + 's ' +
      config.easing + ' ' +
      delay / 1000 + 's; '
  }

  function _generateTransform (elem) {
    var config = elem.config
    var cssDistance
    var transform = elem.styles.transform

    // Let’s make sure our our pixel distances are negative for top and left.
    // e.g. origin = 'top' and distance = '25px' starts at `top: -25px` in CSS.
    if (config.origin === 'top' || config.origin === 'left') {
      cssDistance = /^-/.test(config.distance)
        ? config.distance.substr(1)
        : '-' + config.distance
    } else {
      cssDistance = config.distance
    }

    if (parseInt(config.distance)) {
      transform.initial += ' translate' + config.axis + '(' + cssDistance + ')'
      transform.target += ' translate' + config.axis + '(0)'
    }
    if (config.scale) {
      transform.initial += ' scale(' + config.scale + ')'
      transform.target += ' scale(1)'
    }
    if (config.rotate.x) {
      transform.initial += ' rotateX(' + config.rotate.x + 'deg)'
      transform.target += ' rotateX(0)'
    }
    if (config.rotate.y) {
      transform.initial += ' rotateY(' + config.rotate.y + 'deg)'
      transform.target += ' rotateY(0)'
    }
    if (config.rotate.z) {
      transform.initial += ' rotateZ(' + config.rotate.z + 'deg)'
      transform.target += ' rotateZ(0)'
    }
    transform.initial += '; opacity: ' + config.opacity + ';'
    transform.target += '; opacity: ' + elem.styles.computed.opacity + ';'
  }

  function _updateStore (elem) {
    var container = elem.config.container

    // If this element’s container isn’t already in the store, let’s add it.
    if (container && sr.store.containers.indexOf(container) === -1) {
      sr.store.containers.push(elem.config.container)
    }

    // Update the element stored with our new element.
    sr.store.elements[elem.id] = elem
  }

  function _record (target, config, interval) {
    // Save the `reveal()` arguments that triggered this `_record()` call, so we
    // can re-trace our steps when calling the `sync()` method.
    var record = {
      target: target,
      config: config,
      interval: interval
    }
    sr.history.push(record)
  }

  function _init () {
    if (sr.isSupported()) {
      // Initial animate call triggers valid reveal animations on first load.
      // Subsequent animate calls are made inside the event handler.
      _animate()

      // Then we loop through all container nodes in the store and bind event
      // listeners to each.
      for (var i = 0; i < sr.store.containers.length; i++) {
        sr.store.containers[i].addEventListener('scroll', _handler)
        sr.store.containers[i].addEventListener('resize', _handler)
      }

      // Let’s also do a one-time binding of window event listeners.
      if (!sr.initialized) {
        window.addEventListener('scroll', _handler)
        window.addEventListener('resize', _handler)
        sr.initialized = true
      }
    }
    return sr
  }

  function _handler () {
    _requestAnimationFrame(_animate)
  }

  function _setActiveSequences () {
    var active
    var elem
    var elemId
    var sequence

    // Loop through all sequences
    sr.tools.forOwn(sr.sequences, function (sequenceId) {
      sequence = sr.sequences[sequenceId]
      active = false

      // For each sequenced elemenet, let’s check visibility and if
      // any are visible, set it’s sequence to active.
      for (var i = 0; i < sequence.elemIds.length; i++) {
        elemId = sequence.elemIds[i]
        elem = sr.store.elements[elemId]
        if (_isElemVisible(elem) && !active) {
          active = true
        }
      }

      sequence.active = active
    })
  }

  function _animate () {
    var delayed
    var elem

    _setActiveSequences()

    // Loop through all elements in the store
    sr.tools.forOwn(sr.store.elements, function (elemId) {
      elem = sr.store.elements[elemId]
      delayed = _shouldUseDelay(elem)

      // Let’s see if we should revealand if so,
      // trigger the `beforeReveal` callback and
      // determine whether or not to use delay.
      if (_shouldReveal(elem)) {
        elem.config.beforeReveal(elem.domEl)
        if (delayed) {
          elem.domEl.setAttribute('style',
            elem.styles.inline +
            elem.styles.transform.target +
            elem.styles.transition.delayed
          )
        } else {
          elem.domEl.setAttribute('style',
            elem.styles.inline +
            elem.styles.transform.target +
            elem.styles.transition.instant
          )
        }

        // Let’s queue the `afterReveal` callback
        // and mark the element as seen and revealing.
        _queueCallback('reveal', elem, delayed)
        elem.revealing = true
        elem.seen = true

        if (elem.sequence) {
          _queueNextInSequence(elem, delayed)
        }
      } else if (_shouldReset(elem)) {
        //Otherwise reset our element and
        // trigger the `beforeReset` callback.
        elem.config.beforeReset(elem.domEl)
        elem.domEl.setAttribute('style',
          elem.styles.inline +
          elem.styles.transform.initial +
          elem.styles.transition.instant
        )
        // And queue the `afterReset` callback.
        _queueCallback('reset', elem)
        elem.revealing = false
      }
    })
  }

  function _queueNextInSequence (elem, delayed) {
    var elapsed = 0
    var delay = 0
    var sequence = sr.sequences[elem.sequence.id]

    // We’re processing a sequenced element, so let's block other elements in this sequence.
    sequence.blocked = true

    // Since we’re triggering animations a part of a sequence after animations on first load,
    // we need to check for that condition and explicitly add the delay to our timer.
    if (delayed && elem.config.useDelay === 'onload') {
      delay = elem.config.delay
    }

    // If a sequence timer is already running, capture the elapsed time and clear it.
    if (elem.sequence.timer) {
      elapsed = Math.abs(elem.sequence.timer.started - new Date())
      window.clearTimeout(elem.sequence.timer)
    }

    // Start a new timer.
    elem.sequence.timer = { started: new Date() }
    elem.sequence.timer.clock = window.setTimeout(function () {
      // Sequence interval has passed, so unblock the sequence and re-run the handler.
      sequence.blocked = false
      elem.sequence.timer = null
      _handler()
    }, Math.abs(sequence.interval) + delay - elapsed)
  }

  function _queueCallback (type, elem, delayed) {
    var elapsed = 0
    var duration = 0
    var callback = 'after'

    // Check which callback we’re working with.
    switch (type) {
      case 'reveal':
        duration = elem.config.duration
        if (delayed) {
          duration += elem.config.delay
        }
        callback += 'Reveal'
        break

      case 'reset':
        duration = elem.config.duration
        callback += 'Reset'
        break
    }

    // If a timer is already running, capture the elapsed time and clear it.
    if (elem.timer) {
      elapsed = Math.abs(elem.timer.started - new Date())
      window.clearTimeout(elem.timer.clock)
    }

    // Start a new timer.
    elem.timer = { started: new Date() }
    elem.timer.clock = window.setTimeout(function () {
      // The timer completed, so let’s fire the callback and null the timer.
      elem.config[callback](elem.domEl)
      elem.timer = null
    }, duration - elapsed)
  }

  function _shouldReveal (elem) {
    if (elem.sequence) {
      var sequence = sr.sequences[elem.sequence.id]
      return sequence.active &&
        !sequence.blocked &&
        !elem.revealing &&
        !elem.disabled
    }
    return _isElemVisible(elem) &&
      !elem.revealing &&
      !elem.disabled
  }

  function _shouldUseDelay (elem) {
    var config = elem.config.useDelay
    return config === 'always' ||
      (config === 'onload' && !sr.initialized) ||
      (config === 'once' && !elem.seen)
  }

  function _shouldReset (elem) {
    if (elem.sequence) {
      var sequence = sr.sequences[elem.sequence.id]
      return !sequence.active &&
        elem.config.reset &&
        elem.revealing &&
        !elem.disabled
    }
    return !_isElemVisible(elem) &&
      elem.config.reset &&
      elem.revealing &&
      !elem.disabled
  }

  function _getContainer (container) {
    return {
      width: container.clientWidth,
      height: container.clientHeight
    }
  }

  function _getScrolled (container) {
    // Return the container scroll values, plus the its offset.
    if (container && container !== window.document.documentElement) {
      var offset = _getOffset(container)
      return {
        x: container.scrollLeft + offset.left,
        y: container.scrollTop + offset.top
      }
    } else {
      // Otherwise, default to the window object’s scroll values.
      return {
        x: window.pageXOffset,
        y: window.pageYOffset
      }
    }
  }

  function _getOffset (domEl) {
    var offsetTop = 0
    var offsetLeft = 0

      // Grab the element’s dimensions.
    var offsetHeight = domEl.offsetHeight
    var offsetWidth = domEl.offsetWidth

    // Now calculate the distance between the element and its parent, then
    // again for the parent to its parent, and again etc... until we have the
    // total distance of the element to the document’s top and left origin.
    do {
      if (!isNaN(domEl.offsetTop)) {
        offsetTop += domEl.offsetTop
      }
      if (!isNaN(domEl.offsetLeft)) {
        offsetLeft += domEl.offsetLeft
      }
      domEl = domEl.offsetParent
    } while (domEl)

    return {
      top: offsetTop,
      left: offsetLeft,
      height: offsetHeight,
      width: offsetWidth
    }
  }

  function _isElemVisible (elem) {
    var offset = _getOffset(elem.domEl)
    var container = _getContainer(elem.config.container)
    var scrolled = _getScrolled(elem.config.container)
    var vF = elem.config.viewFactor

      // Define the element geometry.
    var elemHeight = offset.height
    var elemWidth = offset.width
    var elemTop = offset.top
    var elemLeft = offset.left
    var elemBottom = elemTop + elemHeight
    var elemRight = elemLeft + elemWidth

    return confirmBounds() || isPositionFixed()

    function confirmBounds () {
      // Define the element’s functional boundaries using its view factor.
      var top = elemTop + elemHeight * vF
      var left = elemLeft + elemWidth * vF
      var bottom = elemBottom - elemHeight * vF
      var right = elemRight - elemWidth * vF

      // Define the container functional boundaries using its view offset.
      var viewTop = scrolled.y + elem.config.viewOffset.top
      var viewLeft = scrolled.x + elem.config.viewOffset.left
      var viewBottom = scrolled.y - elem.config.viewOffset.bottom + container.height
      var viewRight = scrolled.x - elem.config.viewOffset.right + container.width

      return top < viewBottom &&
        bottom > viewTop &&
        left > viewLeft &&
        right < viewRight
    }

    function isPositionFixed () {
      return (window.getComputedStyle(elem.domEl).position === 'fixed')
    }
  }

  /**
   * Utilities
   * ---------
   */

  function Tools () {}

  Tools.prototype.isObject = function (object) {
    return object !== null && typeof object === 'object' && object.constructor === Object
  }

  Tools.prototype.isNode = function (object) {
    return typeof window.Node === 'object'
      ? object instanceof window.Node
      : object && typeof object === 'object' &&
        typeof object.nodeType === 'number' &&
        typeof object.nodeName === 'string'
  }

  Tools.prototype.isNodeList = function (object) {
    var prototypeToString = Object.prototype.toString.call(object)
    var regex = /^\[object (HTMLCollection|NodeList|Object)\]$/

    return typeof window.NodeList === 'object'
      ? object instanceof window.NodeList
      : object && typeof object === 'object' &&
        regex.test(prototypeToString) &&
        typeof object.length === 'number' &&
        (object.length === 0 || this.isNode(object[0]))
  }

  Tools.prototype.forOwn = function (object, callback) {
    if (!this.isObject(object)) {
      throw new TypeError('Expected "object", but received "' + typeof object + '".')
    } else {
      for (var property in object) {
        if (object.hasOwnProperty(property)) {
          callback(property)
        }
      }
    }
  }

  Tools.prototype.extend = function (target, source) {
    this.forOwn(source, function (property) {
      if (this.isObject(source[property])) {
        if (!target[property] || !this.isObject(target[property])) {
          target[property] = {}
        }
        this.extend(target[property], source[property])
      } else {
        target[property] = source[property]
      }
    }.bind(this))
    return target
  }

  Tools.prototype.extendClone = function (target, source) {
    return this.extend(this.extend({}, target), source)
  }

  Tools.prototype.isMobile = function () {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
  }

  /**
   * Polyfills
   * --------
   */

  _requestAnimationFrame = window.requestAnimationFrame ||
    window.webkitRequestAnimationFrame ||
    window.mozRequestAnimationFrame ||
    function (callback) {
      window.setTimeout(callback, 1000 / 60)
    }

  /**
   * Module Wrapper
   * --------------
   */
  if (typeof define === 'function' && typeof define.amd === 'object' && define.amd) {
    define('scrollreveal',[],function () {
      return ScrollReveal
    })
  } else if (typeof module !== 'undefined' && module.exports) {
    module.exports = ScrollReveal
  } else {
    window.ScrollReveal = ScrollReveal
  }
}())
;
define('shoptheme/components/blocks/fce/elements/usps',[
	"jquery",
	"cartware/components/_base",
	"cartware/jquery/bem",
	"scrollreveal"
], function(
	$,
	Component,
	BEM,
	ScrollReveal
) {
	return Component.create({
		blocks:			null,

		$init: function() {
			this.blocks = BEM.block("elements-usps");

			var items = this.blocks.getElements("list-item");
			var heroItem = this.blocks.getElements("hero-usp");
			var allItems = items.add(heroItem);

			if (!allItems.length) {
				return;
			}

			var reveal = ScrollReveal();

			reveal.reveal(allItems, {
				rotate: {x: 65},
				reset: true
			}, 100);
		}
	});
});
define('shoptheme/components/blocks/fce/elements/image-map',[
	"jquery",
	"cartware/components/_base",
	"cartware/jquery/bem",
	"scrollreveal"
], function(
	$,
	Component,
	BEM,
	ScrollReveal
) {
	return Component.create({

		blocks: null,

		$init: function() {


			this.blocks = BEM.block('image-map');
			this.blocks.each(function() {

				var block = BEM.block(this);
				var list = block.getElements("list");
				var items = block.getElements("list-item");
				var map = block.getElements("map");
				var icons = block.getElements("icon");
				var mobileList = block.getElements("mobile-list");
				var mobileListItem = block.getElements("mobile-list-item");
				var image = block.getElements("image");

				items.click(function(event){
					/*
					if(!$(event.target).is('a')){
						event.preventDefault();
					}
					else {
					}
					*/
					items.removeClass('active');
					$(this).addClass('active');

					var itemIndex = $(this).index();

					mobileListItem.removeClass('active');
					mobileListItem.eq(itemIndex).addClass('active');

				});

				mobileListItem.click(function(event){
					event.preventDefault();
					mobileListItem.removeClass('active');
					$(this).addClass('active');

					var mobileItemIndex = $(this).index();

					items.removeClass('active');
					items.eq(mobileItemIndex).addClass('active');

				});


				/*
				var allItems = list.add(items);
				var reveal = ScrollReveal();

				reveal.reveal(allItems, {
					rotate: {x: 65},
					reset: true,
					opacity: 0,
					mobile: false,
					beforeReset: function (items) {},
					afterReveal: function(items) {}
				}, 100);
				*/


				function mobileHeight() {
					var imageHeight = map.find('img').height();
					mobileList.css('height', imageHeight+'px');
					//console.log(image);
				}

				$(window).resize(function(){
					mobileHeight();
				});

				mobileHeight();

			});

		}

	});
});
define('shoptheme/components/blocks/fce/elements/hero-slider',[
	"jquery",
	"cartware/components/_base",
	"cartware/jquery/bem",
	"json!shoptheme/etc/breakpoints.json",
	"enquire"
], function(
	$,
	Component,
	BEM,
	BreakPoints,
	enquire
) {
	return Component.create({

		blocks: null,

		$init: function() {



			this.blocks = BEM.block('hero-slider');
			this.blocks.each(function() {

				var block = BEM.block(this);
				var headlines = block.getElements('headline');
				var sublines = block.getElements('subline');

				var maxSublineHeight = Math.max.apply(Math,sublines.map(function(){
					return $(this).height();
				}).get());

				var maxHeight = Math.max.apply(Math,headlines.map(function(){
					return $(this).height();
				}).get());

				enquire.register('(min-width: ' + (BreakPoints.sm + 1) + 'px)', {
					match: function() {
						headlines.css('height', maxHeight + 'px');
						sublines.css('height', maxSublineHeight + 'px');
					},
					unmatch: function() {
						headlines.css('height', 'auto');
						sublines.css('height', 'auto');

					}
				}, true);

				//headlines.css('height', maxHeight + 'px')

			});

		}

	});
});
/**
 * Video Teaser FCE (Video Player in a Popup) - can be vimeo, youtube player instances
 * Consent and overlay handled via video.js component
 * Used by B2C stores as well as B2B (Pro) stores
 */
define('shoptheme/components/blocks/fce/elements/videoteaser',[
	"jquery",
	"cartware/components/_base",
	"cartware/jquery/bem",
	"videojs",
	"cartware/components/Dispatcher",
	"videojs-vimeo",
	"videojs-youtube",
	"magnific-popup"
], function(
	$,
	Component,
	BEM,
	videojs,
	ComponentDispatcher
) {
	var BLOCK_NAME = "elements-video-teaser";

	return Component.create({
		blocks:			null,

		$init: function() {
			$(document.body).on('click', '.' + BEM.buildElementClassname(BLOCK_NAME, 'close'), function() {
				$.magnificPopup.close();
			});

			this.blocks = BEM.block(BLOCK_NAME);
			this.blocks.each(function() {
				var block = BEM.block(this);
				var video = block.getElements("video");
				var embed = block.getElements("video-embed");

				var triggerClasses = [
					'.' + BEM.buildElementClassname(BLOCK_NAME, "poster"),
					'.' + BEM.buildElementClassname(BLOCK_NAME, "trigger")
				];



				block.on("click", triggerClasses.join(", "), function(e) {
					$.magnificPopup.open({
						mainClass: "video-fullscreen",
						items: {
							src: embed.html() + '<span class="' + BEM.buildElementClassname(BLOCK_NAME, "close") + '"></span>',
							type: 'inline'
						}
					});

					var clonedVideo = $.magnificPopup.instance.container.find("video");

					var setup = clonedVideo.data("custom-setup");

					if (setup && "autoInitialization" in setup && setup.autoInitialization === false) {
						delete setup.autoInitialization;
					}

					// setup.fluid = true;
					setup.autoplay = true;

					clonedVideo.data("custom-setup", setup);

					// videojs(
					// 	clonedVideo.get(0),
					// 	clonedVideo.data("custom-setup")
					// ).play();

					new ComponentDispatcher({
						handles: ["default"],
						apply: {
							"default": ["shoptheme/components/cartware/video"]
						}
					});
				});

			});
		}
	});
});

define('shoptheme/components/blocks/fce/newsletter-subscribe-landingpage',[
	'jquery',
	'lodash',
	'cartware/components/_base',
	'cartware/jquery/bem',
	'text!shoptheme/templates/magnific-popup/default.html',
	'magnific-popup'
], function(
	$,
	_,
	Component,
	BEM,
	PopupHtml
) {

	var popupTemplate = null;

	function NewsletterSignupBlock(blockName) {
		this.blocks = BEM.block(blockName);

		var legalTextClassName = BEM.buildElementClassname(blockName, 'legal-text'),
			items = this.blocks.getElements('legal-link'),
			topicsContainer = this.blocks.getElements('topics'),
			topics = topicsContainer.find('input[type=checkbox]'),
			topicLabels = topicsContainer.find('label'),
			selectAll = this.blocks.getElements('select-all'),
			exampleImageContainer = this.blocks.getElements('example-images'),
			exampleImages = exampleImageContainer.find('img'),
			self = this;

		exampleImages.click(function(e) {
			e.preventDefault();
			var message = $(this).parent().prop('outerHTML');
			self._showMessage('', message);
		});

		selectAll.on('change', function() {
			if ($(this).prop('checked')) {
				topics.attr('checked', true);
				topicLabels.addClass('checked');
				topics.addClass('checked');
			} else {
				topics.attr('checked', false);
				topicLabels.removeClass('checked');
				topics.removeClass('checked');
			}
		});

		items.click(function(e) {
			e.preventDefault();
			// var title = $(this).html();
			var title = '';
			var message = $(this).parent().next('.'+legalTextClassName).html();
			self._showMessage(title, message);
		});
	}

	NewsletterSignupBlock.prototype = {
		_renderPopup: function(data) {
			if (null === popupTemplate) {
				popupTemplate = _.template(PopupHtml);
			}

			return popupTemplate(data);
		},

		_showMessage: function(title, message) {
			$.magnificPopup.open({
				items: {
					src: this._renderPopup({
						title: title,
						body: message
					}),
					type: 'inline'
				}
			});
		}
	};

	return Component.create({
		$init: function() {
			$.each(['newsletter-subscribe-landingpage', 'newsletter-subscribe-minimal'], function(_key, blockName) {
				new NewsletterSignupBlock(blockName);
			});
		}
	});
});

define('shoptheme/components/blocks/csp/detail/firmware-accordion',[
	"jquery",
	"cartware/components/_base",
	"cartware/jquery/bem",
], function(
	$,
	Component,
	BEM
) {
	return Component.create({

		blocks: null,

		$init: function() {


			this.blocks = BEM.block('csp-support-detail-firmware');
			this.blocks.each(function() {

				var block = BEM.block(this);
				var list = block.getElements("list");
				var items = block.getElements("list-item");
				var headlines = block.getElements("headline");

				headlines.click(function(event){
					event.preventDefault();

					//items.removeClass('active');

					if ($(this).parents('.b_csp-support-detail-firmware__list-item').hasClass('active')) {
						//already opened, then close
						$(this).parents('.b_csp-support-detail-firmware__list-item').removeClass('active');
					}
					else {
						$(this).parents('.b_csp-support-detail-firmware__list-item').addClass('active');
					}


					setHeight();

				});

				function setHeight() {
					/*
					var height = block.height();
					var tabHeight = block.parents('.b_tabs__tab-content.b_tabs__tab-content--active').outerHeight(true);
					var tabs = block.parents('.b_tabs');
					tabs.css('height', 'auto');
					tabs.css('min-height', tabHeight);
					*/

					block.trigger("gigaset.content.change");

				}

			});

		}

	});
});
define('shoptheme/components/blocks/captcha',[
	"jquery",
	"lodash",
	"cartware/components/_base",
	"cartware/jquery/bem",
], function(
	$,
	_,
	Component,
	BEM
) {

	function Captcha(block) {
		this.block = BEM.block(block);
		this.bindEvents();
	}

	Captcha.prototype = {
		block: null,

		bindEvents: function() {
			var self = this;

			this.block.on('click', '[data-action="refresh"]', function(e) {
				var trigger = $(this),
					isButton = trigger.is('button') || trigger.hasClass('btn');

				e.preventDefault();

				if (isButton) {
					trigger.button("loading");
					trigger.attr("disabled", true);
				}

				self.reload().always(function() {
					trigger.button("reset");
					trigger.attr("disabled", false);
				});
			});
		},

		reload: function() {
			var deferred = $.Deferred();
			var url = this.block.data('refresh-url');
			var self = this;

			if (!url) {
				deferred.reject();
			} else {
				$.post(url).done(function(data) {
					try {
						self._insertData(data);
					} catch (ex) {
						deferred.reject();
						return;
					}

					deferred.resolve(data);
				}).fail(function() {
					deferred.reject();
				});
			}

			return deferred.promise();
		},

		_insertData: function(data) {
			this.block.find('[name="captcha[id]"]').val(data.id);
			this.block.getElements('image').find('img').attr('src', ['data:image/png;base64,', data.image].join(""));
		}
	};


	return Component.create({
		blocks: null,

		$init: function() {
			this.blocks = BEM.block('captcha');
			this.blocks.each(function() {
				var block = BEM.block(this);
				new Captcha(block);
			});
		}
	});
});
define('shoptheme/components/blocks/privacy/opt',[
	"jquery",
	"cartware/components/_base",
	"cartware/jquery/bem"
], function(
	$,
	Component,
	BEM
) {

	return Component.create({
		supportsLocalStorage:	false,

		init: function() {
			this.supportsLocalStorage = (function() {
				var mod = 'test_ls_' + ~~(Math.random(1,100) * 100000);
				try {
					localStorage.setItem(mod, mod);
					localStorage.removeItem(mod);
					return true;
				} catch(e) {
					return false;
				}
			})();
		},

		$init: function() {
			var self = this;
			var checkboxSelector = '.' + BEM.buildElementClassname('privacy-opt', 'checkbox');

			function paintCheckboxes() {
				$(checkboxSelector).each(function() {
					var checkbox = $(this),
						flag = checkbox.data('flag'),
						value = self.getFlagValue(flag);

					checkbox.prop('checked', !!~~value);
				});
			}

			paintCheckboxes();

			if ("magnificPopup" in $) {
				(function() {
					var _appendContent = $.magnificPopup.proto.appendContent;

					$.magnificPopup.proto.appendContent = function(data) {
						_appendContent.call(this, data);
						paintCheckboxes();
					}
				})();
			}

			$(document.body).on("click", checkboxSelector, function(e) {
				var checkbox = $(e.currentTarget),
					flag = checkbox.data('flag'),
					value = checkbox.is(':checked');

				self.setFlagValue(flag, ~~!!value);
			});
		},

		getFlagValue: function(flag) {
			var value = false;

			if (flag && this.supportsLocalStorage) {
				value = localStorage.getItem(flag);
			}

			return value;
		},

		setFlagValue: function(flag, value) {
			if (flag && this.supportsLocalStorage) {
				localStorage.setItem(flag, value);
			}
		}
	});
});
define('shoptheme/components/widgets/band',[
	"jquery",
	"cartware/components/_base",
	"cartware/jquery/bem"
], function(
	$,
	Component,
	BEM
) {
	return Component.create({
		blocks:			null,

		$init: function() {
			this.blocks = BEM.block("band-widget");
			this.blocks.each(function() {
				var block = BEM.block(this);
				var link = block.getElements("link");
				var href = link.attr("href");

				if (/^#.*/.test(href)) {
					link.click(function(e) {
						e.preventDefault();

						$('html, body').animate({
							scrollTop: $(href).offset().top,
							ease: 'swing'
						}, 200);
					});
				}
			});
		}
	});
});
define('shoptheme/components/tracking',[
	"jquery",
	"cartware/components/_base",
	"cartware/components/Config",
	"cartware/components/PubSubHub",
	"cartware/components/Registry"
], function(
	$,
	Component,
	Config,
	PubSubHub,
	Registry
) {
	var LOGGING_ENABLED = false;
	var TIMEOUT_TRACKING = 250;

	function setAndTrackSubPage() {
		var args = Array.prototype.splice.call(arguments, 0);
		args.unshift(document.location.pathname);
		setAndTrackPage(createSubUrl.apply(this, args));
	}

	function setAndTrackPage(page) {
		if (LOGGING_ENABLED) {
			console.log("setAndTrackPage", page);
		}

		if ("undefined" !== typeof ga) {
			ga('set', 'page', page);
			ga('send', 'pageview', {'page': location.pathname + location.search + location.hash});
		}
	}

	// Use this method only for temporary, single pageviews. Otherwise prefer setAndTrackPage.
	// This method will not send the page and therefor not track further data
	// such as events etc. in context of the given page
	function trackPageView(page) {
		return callFunctionWithTimeout(function(hitCallback) {
			if (LOGGING_ENABLED) {
				console.log("trackPageView", page);
			}

			if ("undefined" !== typeof ga) {
				ga('send', 'pageview', page, {
					hitCallback: hitCallback
				});
			}
		});
	}

	function callFunctionWithTimeout(fn, timeout) {
		var deferred = $.Deferred(),
			resolved = false;

		function hitCallback() {
			if (!resolved) {
				resolved = true;
				deferred.resolve();
			}
		}

		fn(hitCallback);

		setTimeout(hitCallback, TIMEOUT_TRACKING);

		return deferred.promise();
	}

	function createSubUrl(pathName) {
		var subPathSegments = Array.prototype.slice.call(arguments, 0);
		var subPath = [];

		pathName = subPathSegments.shift();

		var pathSegments = pathName.split("/");

		for (var i = 0, j = subPathSegments.length; i < j; i++) {
			subPath.push(encodeURIComponent(subPathSegments[i]));
		}

		var suffix = pathName.substr(-1) === "/" ? "/" : "";

		if ("" === suffix) {
			var lastSegment = pathSegments.pop();
			var suffixMatches = /(.*?)(\/|\.[a-zA-Z0-9]+)$/.exec(lastSegment);

			if (suffixMatches) {
				suffix = suffixMatches[2];

				pathSegments.push(suffixMatches[1]);
			}
		}

		return pathSegments.join("/") + "/" + subPath.join("/") + suffix;
	}

	function absoluteToRelativeUrl(absoluteUrl) {
		var baseUrl = Config.get("system.base_url");
		var regex = new RegExp(baseUrl.replace(/https?:\/\//, "https?://").replace(/\/$/, "") + "/");

		return absoluteUrl.replace(regex, "/");
	}

	return Component.create({
		init: function() {
			var self = this;

			Registry().retrieve("featureFlags", function(featureFlags) {
				self.enableLogging(featureFlags.isEnabled("developer_mode") && ("undefined" !== typeof console.log));
			});

			PubSubHub.bind("csp.handsetbase.registration.view", function(data) {
				var basestation = data.basestation;
				var handset = data.handset;

				setAndTrackSubPage(basestation.name, handset.name);
			});

			PubSubHub.bind("csp.compatibility.byproducts.view", function(data) {
				var productNames = [data.productOne.name, data.productTwo.name].sort();

				setAndTrackSubPage(productNames[0], productNames[1]);
			});

			PubSubHub.bind("csp.compatibility.byfeatures.view", function(data) {
				var segments = data.functions;
				Array.prototype.unshift.call(segments, data.product);

				setAndTrackSubPage.apply(this, segments);
			});

			PubSubHub.bind("csp.support.detail.faq.view", function(data) {
				var relativeUrl = absoluteToRelativeUrl(data.faqUrl);
				trackPageView(
					data.product ? createSubUrl(relativeUrl, data.product) : relativeUrl
				).then(function() {
					data.deferred.resolve();
				});
			});
		},

		enableLogging: function(flag) {
			LOGGING_ENABLED = !!flag;
			return this;
		}
	});
});

define('shoptheme/components/contentbridge/cms-search-results',[
	"jquery",
	"cartware/components/_base",
	"cartware/jquery/bem",
	"json!shoptheme/etc/breakpoints.json",
	"enquire"
], function(
	$,
	Component,
	BEM,
	BreakPoints,
	enquire
) {
	return Component.create({

		blocks: null,

		$init: function() {

			this.textOverflowEllipsis();

			this.blocks = BEM.block('contentbridge-search-result__content');
			this.blocks.each(function() {

				var block = BEM.block(this),
					children = block.find('.wrapper');

				var childHeight = Math.max.apply(Math,children.map(function(){
					return $(this).outerHeight();
				}).get());

				enquire.register('(min-width: ' + (BreakPoints.sm + 1) + 'px)', {
					match: function() {
						children.css('height', childHeight + 'px');
					},
					unmatch: function() {
						children.css('height', 'auto');
					}
				}, true);
				
			});

		},

		textOverflowEllipsis: function() {
			var ellipsisElements = $('.ellipsis-readmore');
			$.each(ellipsisElements, function(index, element){
				var elem = $(element),
					wrapper = elem.find('.ellipsis-readmore-wrapper'),
					readMoreLink = elem.find('a.read-more');

				wrapper.dotdotdot();
				$(window).resize($.throttle(10, function() { wrapper.dotdotdot(); }));
				readMoreLink.on('click', function(e){
					e.preventDefault();
					var content = wrapper.triggerHandler("originalContent");
					elem.html(content);
				});
				wrapper.trigger("isTruncated", function( isTruncated ) {
					if ( isTruncated ) {
						readMoreLink.show();
					}
				});
			});
		}

	});
});
define('cartware/components/Holepunch',[
	"jquery",
	"./Utilities"
], function(
	$,
	Utils
) {
	function Holepunch() {
		this.url		= Utils.getPath(window, "gigaset.holepunch.url");
		this.handle		= Utils.getPath(window, "gigaset.request.action");
		this.product	= Utils.getPath(window, "gigaset.request.product");
		this.category	= Utils.getPath(window, "gigaset.request.category");
		this.excludedRefererList = JSON.parse(Utils.getPath(window, "gigaset.holepunch.utm.excludedRefererList") || '[]');
		this.utmParams  = this.getUtmParams();
		this.isHolepunchActive = Utils.getPath(window, "gigaset.holepunch.active");
		this.hasUtmParams = !$.isEmptyObject(this.utmParams);
		this.isUtmTrackingActive = Utils.getPath(window, "gigaset.holepunch.utm.active");
		this.deferred	= $.Deferred();

		if (this.isHolepunchActive ||
			(this.isUtmTrackingActive && this.hasUtmParams)
		) {
			$($.proxy(this.load, this));
		}
	}

	Holepunch.prototype = {
		url:		null,
		handle:		null,
		product:	null,
		category:	null,
		utmParams: null,

		deferred:	null,

		load: function() {
			// fresh holepunch
			if (this.deferred.state() !== "pending") {
				this.deferred = $.Deferred();
			}

			var xhr = $.ajax({
				url: this.url,
				type: "POST",
				dataType: "JSON",
				data: {
					handle:		this.handle,
					product:	this.product,
					category:	this.category,
					info:		Utils.getPath(window, "gigaset.holepunch.info") || {},
					utmParams:  this.utmParams,
					isHolepunchActive: this.isHolepunchActive
				}
			});

			xhr.done($.proxy(function(data) {
				this.deferred.resolveWith(null, [data]);
				this.fill(data);
			}, this));

			xhr.fail($.proxy(this._onError, this));

			return this;
		},

		ready: function(cb) {
			this.deferred.then(cb);
		},

		fill: function(data) {
			if ("blocks" in data) {
				$.each(data.blocks, function(index, value) {
					$('#' + index.replace(/(:|\.|\[|\])/g, "\\$1")).replaceWith(value);
				});
			}
		},

		_onError: function(jqXHR, textStatus, errorThrown) {
			if ("console" in window && "undefined" !== typeof console.error) {
				console.error(errorThrown);
				console.log(jqXHR);
			}
		},

		getUtmParams: function () {
			var urlParams = this.getAllParams(),
				utmParams = ['utm_campaign', 'utm_medium', 'utm_source', 'utm_term', 'utm_content'],
				utmParamsValues = {};

			$.each(utmParams, function (index, value) {
				var paramValue = Utils.getPath(urlParams, value);
				if (paramValue) {
					utmParamsValues[value] = paramValue;
				}
			});

			if (window.document.referrer &&
				!this._isInternalReferer() &&
				!this._isPaymentThirdPartyReferer() &&
				!this._isInCheckoutPage()
			) {
				utmParamsValues.referer = window.document.referrer;
			}

			return utmParamsValues;
		},

		_isInternalReferer: function() {
			return window.document.referrer.indexOf(location.protocol + '//' + location.host) === 0;
		},

		_isInCheckoutPage: function() {
			var checkoutLinks = ['checkout/cart', 'checkout/cart/index', 'checkout/onepage', 'checkout/onepage/index'],
				patternPrefix = '^' + location.protocol + '//' + location.host + '/[a-z]{2}_[a-z]{2}/',
				patternSuffix = '/*$',
				isInCheckoutPage = false;

			$.each(checkoutLinks, function (index, value) {
				if (new RegExp(patternPrefix + value + patternSuffix).match(window.location.href)) {
					isInCheckoutPage = true;
					return false;
				}
			});

			return isInCheckoutPage;
		},

		_isPaymentThirdPartyReferer: function() {
			var isExcludedReferer = false;
			$.each(this.excludedRefererList, function(index, value) {
				if (value !== '' && window.document.referrer.indexOf(value) === 0) {
					isExcludedReferer = true;
					return false;
				}
			});
			return isExcludedReferer;
		},

		getAllParams: function () {
			var params = {};
			window.location.search.slice(1).split('&').forEach(function(elm) {
				if (elm === '') return;
				var spl = elm.split('='),
					d = decodeURIComponent;
				params[d(spl[0])] = (spl.length >= 2 ? d(spl[1]) : true);
			});

			return params;
		}
	};

	return (new Holepunch());
});
define('shoptheme/components/layout/home',[
	"jquery",
	"cartware/components/_base",
	"cartware/components/Holepunch"
], function(
	$,
	Component,
	Holepunch
) {
	return Component.create({
		constructor: function() {
			Holepunch.ready(function(data) {
				//console.log("HOLEPUNCH", data);
			});
		},
		$init: function() {
			//console.log("DOM READY");
		}
	});
});
define('shoptheme/components/product/view',[
	"require",
	"jquery",
	"lodash",
	"cartware/components/_base",
	"cartware/jquery/bem",
	"cartware/components/Registry",
	"cartware/dom/event/trigger",
	"json!shoptheme/etc/breakpoints.json",
	"enquire",
	"modernizr",
	"bootstrap-affix"
], function(
	require,
	$,
	_,
	Component,
	BEM,
	Registry,
	triggerEvent,
	BreakPoints,
	enquire
) {
	var $window = $(window);

	function scrollToFragment(url) {
		var id, fragmentMatches, target, treshold;

		fragmentMatches = url.match(/[#&](tab-.*)$/);

		if (!fragmentMatches || fragmentMatches.length < 2) return;

		id = fragmentMatches[1];
		target = $('#' + id);

		if (!target.length) return;

		treshold = target.height() / 2;

		$('html:not(:animated),body:not(:animated)').animate({
			scrollTop: target.offset().top - treshold
		}, {
			duration: 600
		});
		
		if (('' + document.location).indexOf('#') < 0) {
			document.location = document.location + '#' + id;
		}

		setTimeout(function() {
			target.click();
		}, 10);
	}

	return Component.create({
		block:					null,
		purchaseBlock:			null,

		affixElement:			null,

		productOrder:			null,
		productOrderSlots:		null,

		productOther:			null,
		productOtherSlots:		null,

        productName:            null,
        originalProductName:    null,

		shortDescription:		null,
		originalShortDescription: null,

		//addToCartButton:		null,

		$init: function() {
			this.block = BEM.block("product-view");
			this.purchaseBlock = BEM.block("block:modifier(purchase)");

			this.affixElement = this.block.getElements('affix');

			this.productOrder = this.block.getElements("product-order");
			this.productOrderSlots = {
				mobile: this.block.getElements("product-order-mobile"),
				desktop: this.block.getElements("product-order-desktop")
			};

			this.productOther = this.block.getElements("product-other");
			this.productOtherSlots = {
				mobile: this.block.getElements("product-other-mobile"),
				desktop: this.block.getElements("product-other-desktop")
			};

            this.productName = this.block.getElements("name");
            this.originalProductName = this.productName.html();

			this.shortDescription = this.block.getElements("short-description");
			this.originalShortDescription = this.shortDescription.html();

			$(document).on('product/changeSimple', _.bind(this._onChangeSimple, this));
			$(document).on('product/changeGallerySimple', _.bind(this._onChangeSimple, this));

			$(document).on("click", 'a[href*="#tab-"]', function(e) {
				
				var $this = $(this);
				
				if ($this.hasClass('js-tab-reload') || $this.parents('.js-review-pagination').length) {
                    return ;
				}
                
                e.preventDefault();

				var href = $this.attr('href'),
					pattern = /^([a-zA-Z]*:\/\/)?([^\/]+).*(\/.*)$/,
					match = (''+document.location).replace(pattern, "$1$2$3").replace(/#.*/, '') === href.replace(pattern, "$1$2$3").replace(/#.*/, '');

				if (match) {
					scrollToFragment(href);
				}
			});
			
			/* scroll to fragment after loading complete of page */
			$(window).load(function() {
                if (('' + document.location).indexOf('#') != -1) {
                    scrollToFragment('' + document.location);
                }
            });

			this.initResponsive();
			this.initAffixPlaceholders();

			//var addToCartForm = $('#product_addtocart_form')
			//	, addToCartContainer = addToCartForm.find('.add-to-cart-button-wrap');
			//
			//this.addToCartButton = addToCartContainer.find('button');

			//this.adviseAddToCartForm();

			this.adviseRenderFunction();
		},

		initResponsive: function() {
			enquire.register('(min-width: ' + (BreakPoints.sm + 1) + 'px)', {
				match: _.bind(this.layoutDesktop, this),
				unmatch: _.bind(this.layoutMobile, this)
			}, true);
		},

		initAffixPlaceholders: function() {
			var element = this.affixElement,
				placeholder = $('<div class="affix-placeholder">');

			var layout = function() {
				placeholder.height(element.outerHeight(true));
			};

			element.after(placeholder);

			$window.on("resize click", _.debounce(layout, 150));
			Registry().retrieve("product.view.configurable.spConfig", _.debounce(layout, 150));
			element.on("affix.bs.affix", layout);
			layout();
		},

		layoutMobile: function() {
			this.productOrderSlots.mobile.append(this.productOrder);
		},

		layoutDesktop: function() {
			var $document = $(document),
				$header = BEM.block("header"),
				$bottomStopElement = this.block.getElements('col--main'),
				$sidebar = this.block.getElements('col--side'),
				lock = false;

			var self = this;

			this.productOrderSlots.desktop.append(this.productOrder);

			function calculateBottom() {
				return Math.max(
					$bottomStopElement.offset().top + $bottomStopElement.outerHeight(true),
					$sidebar.offset().top + $sidebar.outerHeight(true)
				);
			}

			this.affixElement.affix({
				offset: {
					top: $header.offset().top + $header.outerHeight(true),
					bottom: function() {
						if (lock) {
							return null;
						}

						var bottom = calculateBottom();
						return $document.height() - bottom;
					}
				}
			});

			this.affixElement.on("affix.bs.affix", _.bind(function(e) {
				var headerBottom = $header.offset().top + $header.height(),
					purchaseHeight = this.affixElement.outerHeight(true),
					purchaseOffset = this.affixElement.offset(),
					windowHeight = $window.height(),
					affix = this.affixElement.data("bs.affix");

				lock = false;

				if (windowHeight < purchaseHeight + purchaseOffset.top - headerBottom) {
					e.preventDefault();
					affix.affixed = null;
					lock = true;
				}

				var bottom = calculateBottom();
				if (bottom <= purchaseOffset.top + purchaseHeight) {
					e.preventDefault();
				}

			}, this));
		},

		adviseRenderFunction: function() {
			if ("function" === typeof render) {
				var _orig = render,
					self = this;

				render = function() {
					_orig.apply(this, arguments);

					var affix = self.affixElement.data('bs.affix');

					if (affix) {
						affix.checkPositionWithEventLoop();
					}
				}
			}
		},

		_onChangeSimple: function(e, simple) {

			var productNameHtml = simple ? (simple["product_name"] || this.originalProductName)
                    : this.originalProductName,
                shortDescriptionHtml = simple ? (simple["short_description"] || this.originalShortDescription)
				    : this.originalShortDescription;

			this.productName.html(productNameHtml);
            this.shortDescription.html(shortDescriptionHtml);
		},

		//adviseAddToCartForm: function() {
		//	var self = this;
		//
		//	if ("undefined" == typeof productAddToCartForm) return;
		//
		//	var _submit = productAddToCartForm.submit;
		//
		//	productAddToCartForm.submit = function() {
		//		if (this.validator.validate()) {
		//			$.fn.button && self.addToCartButton.button('loading');
		//		}
		//		_submit.apply(this, arguments);
		//	};
		//},

		destructor: function() {
			//this.addToCartButton = null;
		}
	});
});
(function(root) {
define("elevatezoom", ["jquery"], function() {
  return (function() {
/*
 *	jQuery elevateZoom 3.0.8
 *	Demo's and documentation:
 *	www.elevateweb.co.uk/image-zoom
 *
 *	Copyright (c) 2012 Andrew Eades
 *	www.elevateweb.co.uk
 *
 *	Dual licensed under the GPL and MIT licenses.
 *	http://en.wikipedia.org/wiki/MIT_License
 *	http://en.wikipedia.org/wiki/GNU_General_Public_License
 *

/*
 *	jQuery elevateZoom 3.0.3
 *	Demo's and documentation:
 *	www.elevateweb.co.uk/image-zoom
 *
 *	Copyright (c) 2012 Andrew Eades
 *	www.elevateweb.co.uk
 *
 *	Dual licensed under the GPL and MIT licenses.
 *	http://en.wikipedia.org/wiki/MIT_License
 *	http://en.wikipedia.org/wiki/GNU_General_Public_License
 */


if ( typeof Object.create !== 'function' ) {
	Object.create = function( obj ) {
		function F() {};
		F.prototype = obj;
		return new F();
	};
}

(function( $, window, document, undefined ) {
	var ElevateZoom = {
			init: function( options, elem ) {
				var self = this;

				self.elem = elem;
				self.$elem = $( elem );

				self.imageSrc = self.$elem.data("zoom-image") ? self.$elem.data("zoom-image") : self.$elem.attr("src");

				self.options = $.extend( {}, $.fn.elevateZoom.options, options );

				//TINT OVERRIDE SETTINGS
				if(self.options.tint) {
					self.options.lensColour = "none", //colour of the lens background
					self.options.lensOpacity =  "1" //opacity of the lens
				}
				//INNER OVERRIDE SETTINGS
				if(self.options.zoomType == "inner") {self.options.showLens = false;}


				//Remove alt on hover

				self.$elem.parent().removeAttr('title').removeAttr('alt');

				self.zoomImage = self.imageSrc;

				self.refresh( 1 );



				//Create the image swap from the gallery 
				$('#'+self.options.gallery + ' a').click( function(e) { 

					//Set a class on the currently active gallery image
					if(self.options.galleryActiveClass){
						$('#'+self.options.gallery + ' a').removeClass(self.options.galleryActiveClass);
						$(this).addClass(self.options.galleryActiveClass);
					}
					//stop any link on the a tag from working
					e.preventDefault();

					//call the swap image function            
					if($(this).data("zoom-image")){self.zoomImagePre = $(this).data("zoom-image")}
					else{self.zoomImagePre = $(this).data("image");}
					self.swaptheimage($(this).data("image"), self.zoomImagePre);
					return false;
				});

			},

			refresh: function( length ) {
				var self = this;

				setTimeout(function() {
					self.fetch(self.imageSrc);

				}, length || self.options.refresh );
			},

			fetch: function(imgsrc) {
				//get the image
				var self = this;
				var newImg = new Image();
				newImg.onload = function() {
					//set the large image dimensions - used to calculte ratio's
					self.largeWidth = newImg.width;
					self.largeHeight = newImg.height;
					//once image is loaded start the calls
					self.startZoom();
					self.currentImage = self.imageSrc;
					//let caller know image has been loaded
					self.options.onZoomedImageLoaded(self.$elem);
				}
				newImg.src = imgsrc; // this must be done AFTER setting onload

				return;

			},

			startZoom: function( ) {
				var self = this;
				//get dimensions of the non zoomed image
				self.nzWidth = self.$elem.width();
				self.nzHeight = self.$elem.height();

				//activated elements
				self.isWindowActive = false;
				self.isLensActive = false;
				self.isTintActive = false;
				self.overWindow = false;    

				//CrossFade Wrappe
				if(self.options.imageCrossfade){
					self.zoomWrap = self.$elem.wrap('<div style="height:'+self.nzHeight+'px;width:'+self.nzWidth+'px;" class="zoomWrapper" />');        
					self.$elem.css('position', 'absolute'); 
				}

				self.zoomLock = 1;
				self.scrollingLock = false;
				self.changeBgSize = false;
				self.currentZoomLevel = self.options.zoomLevel;


				//get offset of the non zoomed image
				self.nzOffset = self.$elem.offset();
				//calculate the width ratio of the large/small image
				self.widthRatio = (self.largeWidth/self.currentZoomLevel) / self.nzWidth;
				self.heightRatio = (self.largeHeight/self.currentZoomLevel) / self.nzHeight; 


				//if window zoom        
				if(self.options.zoomType == "window") {
					self.zoomWindowStyle = "overflow: hidden;"
						+ "background-position: 0px 0px;text-align:center;"  
						+ "background-color: " + String(self.options.zoomWindowBgColour)            
						+ ";width: " + String(self.options.zoomWindowWidth) + "px;"
						+ "height: " + String(self.options.zoomWindowHeight)
						+ "px;float: left;"
						+ "background-size: "+ self.largeWidth/self.currentZoomLevel+ "px " +self.largeHeight/self.currentZoomLevel + "px;"
						+ "display: none;z-index:100;"
						+ "border: " + String(self.options.borderSize) 
						+ "px solid " + self.options.borderColour 
						+ ";background-repeat: no-repeat;"
						+ "position: absolute;";
				}    


				//if inner  zoom    
				if(self.options.zoomType == "inner") {
					//has a border been put on the image? Lets cater for this

					var borderWidth = self.$elem.css("border-left-width");

					self.zoomWindowStyle = "overflow: hidden;"
						+ "margin-left: " + String(borderWidth) + ";" 
						+ "margin-top: " + String(borderWidth) + ";"         
						+ "background-position: 0px 0px;"
						+ "width: " + String(self.nzWidth) + "px;"
						+ "height: " + String(self.nzHeight)
						+ "px;float: left;"
						+ "display: none;"
						+ "cursor:"+(self.options.cursor)+";"
						+ "px solid " + self.options.borderColour 
						+ ";background-repeat: no-repeat;"
						+ "position: absolute;";
				}    



				//lens style for window zoom
				if(self.options.zoomType == "window") {


					// adjust images less than the window height

					if(self.nzHeight < self.options.zoomWindowWidth/self.widthRatio){
						lensHeight = self.nzHeight;              
					}
					else{
						lensHeight = String((self.options.zoomWindowHeight/self.heightRatio))
					}
					if(self.largeWidth < self.options.zoomWindowWidth){
						lensWidth = self.nzWidth;
					}       
					else{
						lensWidth =  (self.options.zoomWindowWidth/self.widthRatio);
					}


					self.lensStyle = "background-position: 0px 0px;width: " + String((self.options.zoomWindowWidth)/self.widthRatio) + "px;height: " + String((self.options.zoomWindowHeight)/self.heightRatio)
					+ "px;float: right;display: none;"
					+ "overflow: hidden;"
					+ "z-index: 999;"   
					+ "-webkit-transform: translateZ(0);"               
					+ "opacity:"+(self.options.lensOpacity)+";filter: alpha(opacity = "+(self.options.lensOpacity*100)+"); zoom:1;"
					+ "width:"+lensWidth+"px;"
					+ "height:"+lensHeight+"px;"
					+ "background-color:"+(self.options.lensColour)+";"					
					+ "cursor:"+(self.options.cursor)+";"
					+ "border: "+(self.options.lensBorderSize)+"px" +
					" solid "+(self.options.lensBorderColour)+";background-repeat: no-repeat;position: absolute;";
				} 


				//tint style
				self.tintStyle = "display: block;"
					+ "position: absolute;"
					+ "background-color: "+self.options.tintColour+";"	
					+ "filter:alpha(opacity=0);"		
					+ "opacity: 0;"	
					+ "width: " + self.nzWidth + "px;"
					+ "height: " + self.nzHeight + "px;"

					;

				//lens style for lens zoom with optional round for modern browsers
				self.lensRound = '';

				if(self.options.zoomType == "lens") {

					self.lensStyle = "background-position: 0px 0px;"
						+ "float: left;display: none;"
						+ "border: " + String(self.options.borderSize) + "px solid " + self.options.borderColour+";"
						+ "width:"+ String(self.options.lensSize) +"px;"
						+ "height:"+ String(self.options.lensSize)+"px;"
						+ "background-repeat: no-repeat;position: absolute;";


				}


				//does not round in all browsers
				if(self.options.lensShape == "round") {
					self.lensRound = "border-top-left-radius: " + String(self.options.lensSize / 2 + self.options.borderSize) + "px;"
					+ "border-top-right-radius: " + String(self.options.lensSize / 2 + self.options.borderSize) + "px;"
					+ "border-bottom-left-radius: " + String(self.options.lensSize / 2 + self.options.borderSize) + "px;"
					+ "border-bottom-right-radius: " + String(self.options.lensSize / 2 + self.options.borderSize) + "px;";

				}

				//create the div's                                                + ""
				//self.zoomContainer = $('<div/>').addClass('zoomContainer').css({"position":"relative", "height":self.nzHeight, "width":self.nzWidth});

				self.zoomContainer = $('<div class="zoomContainer" style="-webkit-transform: translateZ(0);position:absolute;left:'+self.nzOffset.left+'px;top:'+self.nzOffset.top+'px;height:'+self.nzHeight+'px;width:'+self.nzWidth+'px;"></div>');
				$('body').append(self.zoomContainer);	


				//this will add overflow hidden and contrain the lens on lens mode       
				if(self.options.containLensZoom && self.options.zoomType == "lens"){
					self.zoomContainer.css("overflow", "hidden");
				}
				if(self.options.zoomType != "inner") {
					self.zoomLens = $("<div class='zoomLens' style='" + self.lensStyle + self.lensRound +"'>&nbsp;</div>")
					.appendTo(self.zoomContainer)
					.click(function () {
						self.$elem.trigger('click');
					});


					if(self.options.tint) {
						self.tintContainer = $('<div/>').addClass('tintContainer');	
						self.zoomTint = $("<div class='zoomTint' style='"+self.tintStyle+"'></div>");


						self.zoomLens.wrap(self.tintContainer);


						self.zoomTintcss = self.zoomLens.after(self.zoomTint);	

						//if tint enabled - set an image to show over the tint

						self.zoomTintImage = $('<img style="position: absolute; left: 0px; top: 0px; max-width: none; width: '+self.nzWidth+'px; height: '+self.nzHeight+'px;" src="'+self.imageSrc+'">')
						.appendTo(self.zoomLens)
						.click(function () {

							self.$elem.trigger('click');
						});

					}          

				}







				//create zoom window 
				if(isNaN(self.options.zoomWindowPosition)){
					self.zoomWindow = $("<div style='z-index:999;left:"+(self.windowOffsetLeft)+"px;top:"+(self.windowOffsetTop)+"px;" + self.zoomWindowStyle + "' class='zoomWindow'>&nbsp;</div>")
					.appendTo('body')
					.click(function () {
						self.$elem.trigger('click');
					});
				}else{
					self.zoomWindow = $("<div style='z-index:999;left:"+(self.windowOffsetLeft)+"px;top:"+(self.windowOffsetTop)+"px;" + self.zoomWindowStyle + "' class='zoomWindow'>&nbsp;</div>")
					.appendTo(self.zoomContainer)
					.click(function () {
						self.$elem.trigger('click');
					});
				}              
				self.zoomWindowContainer = $('<div/>').addClass('zoomWindowContainer').css("width",self.options.zoomWindowWidth);
				self.zoomWindow.wrap(self.zoomWindowContainer);


				//  self.captionStyle = "text-align: left;background-color: black;color: white;font-weight: bold;padding: 10px;font-family: sans-serif;font-size: 11px";                                                                                                                                                                                                                                          
				// self.zoomCaption = $('<div class="elevatezoom-caption" style="'+self.captionStyle+'display: block; width: 280px;">INSERT ALT TAG</div>').appendTo(self.zoomWindow.parent());

				if(self.options.zoomType == "lens") {
					self.zoomLens.css({ backgroundImage: "url('" + self.imageSrc + "')" }); 
				}
				if(self.options.zoomType == "window") {
					self.zoomWindow.css({ backgroundImage: "url('" + self.imageSrc + "')" }); 
				}
				if(self.options.zoomType == "inner") {
					self.zoomWindow.css({ backgroundImage: "url('" + self.imageSrc + "')" }); 
				}
				/*-------------------END THE ZOOM WINDOW AND LENS----------------------------------*/
				//touch events
				self.$elem.bind('touchmove', function(e){    
					e.preventDefault();
					var touch = e.originalEvent.touches[0] || e.originalEvent.changedTouches[0];  
					self.setPosition(touch);

				});  
				self.zoomContainer.bind('touchmove', function(e){ 
					if(self.options.zoomType == "inner") {
						self.showHideWindow("show");

					}
					e.preventDefault();
					var touch = e.originalEvent.touches[0] || e.originalEvent.changedTouches[0];  
					self.setPosition(touch); 

				});  	
				self.zoomContainer.bind('touchend', function(e){ 
					self.showHideWindow("hide");
					if(self.options.showLens) {self.showHideLens("hide");}
					if(self.options.tint && self.options.zoomType != "inner") {self.showHideTint("hide");}
				});  	

				self.$elem.bind('touchend', function(e){ 
					self.showHideWindow("hide");
					if(self.options.showLens) {self.showHideLens("hide");}
					if(self.options.tint && self.options.zoomType != "inner") {self.showHideTint("hide");}
				});  	
				if(self.options.showLens) {
					self.zoomLens.bind('touchmove', function(e){ 

						e.preventDefault();
						var touch = e.originalEvent.touches[0] || e.originalEvent.changedTouches[0];  
						self.setPosition(touch); 
					});    


					self.zoomLens.bind('touchend', function(e){ 
						self.showHideWindow("hide");
						if(self.options.showLens) {self.showHideLens("hide");}
						if(self.options.tint && self.options.zoomType != "inner") {self.showHideTint("hide");}
					});  
				}
				//Needed to work in IE
				self.$elem.bind('mousemove', function(e){   
					if(self.overWindow == false){self.setElements("show");}
					//make sure on orientation change the setposition is not fired
					if(self.lastX !== e.clientX || self.lastY !== e.clientY){
						self.setPosition(e);
						self.currentLoc = e;
					}   
					self.lastX = e.clientX;
					self.lastY = e.clientY;    

				});  	

				self.zoomContainer.bind('mousemove', function(e){ 

					if(self.overWindow == false){self.setElements("show");} 

					//make sure on orientation change the setposition is not fired 
					if(self.lastX !== e.clientX || self.lastY !== e.clientY){
						self.setPosition(e);
						self.currentLoc = e;
					}   
					self.lastX = e.clientX;
					self.lastY = e.clientY;    
				});  	
				if(self.options.zoomType != "inner") {
					self.zoomLens.bind('mousemove', function(e){      
						//make sure on orientation change the setposition is not fired
						if(self.lastX !== e.clientX || self.lastY !== e.clientY){
							self.setPosition(e);
							self.currentLoc = e;
						}   
						self.lastX = e.clientX;
						self.lastY = e.clientY;    
					});
				}
				if(self.options.tint && self.options.zoomType != "inner") {
					self.zoomTint.bind('mousemove', function(e){ 
						//make sure on orientation change the setposition is not fired
						if(self.lastX !== e.clientX || self.lastY !== e.clientY){
							self.setPosition(e);
							self.currentLoc = e;
						}   
						self.lastX = e.clientX;
						self.lastY = e.clientY;    
					});

				}
				if(self.options.zoomType == "inner") {
					self.zoomWindow.bind('mousemove', function(e) {
						//self.overWindow = true;
						//make sure on orientation change the setposition is not fired
						if(self.lastX !== e.clientX || self.lastY !== e.clientY){
							self.setPosition(e);
							self.currentLoc = e;
						}   
						self.lastX = e.clientX;
						self.lastY = e.clientY;    
					});

				}


				//  lensFadeOut: 500,  zoomTintFadeIn
				self.zoomContainer.add(self.$elem).mouseenter(function(){

					if(self.overWindow == false){self.setElements("show");} 


				}).mouseleave(function(){
					if(!self.scrollLock){
						self.setElements("hide");
					}
				});
				//end ove image





				if(self.options.zoomType != "inner") {
					self.zoomWindow.mouseenter(function(){
						self.overWindow = true;   
						self.setElements("hide");                  
					}).mouseleave(function(){

						self.overWindow = false;
					});
				}
				//end ove image



//				var delta = parseInt(e.originalEvent.wheelDelta || -e.originalEvent.detail);

				//      $(this).empty();    
				//    return false;

				//fix for initial zoom setting
				if (self.options.zoomLevel != 1){
					//	self.changeZoomLevel(self.currentZoomLevel);
				}
				//set the min zoomlevel
				if(self.options.minZoomLevel){
					self.minZoomLevel = self.options.minZoomLevel;
				}
				else{
					self.minZoomLevel = self.options.scrollZoomIncrement * 2;
				}


				if(self.options.scrollZoom){


					self.zoomContainer.add(self.$elem).bind('mousewheel DOMMouseScroll MozMousePixelScroll', function(e){


//						in IE there is issue with firing of mouseleave - So check whether still scrolling
//						and on mouseleave check if scrolllock          
						self.scrollLock = true;
						clearTimeout($.data(this, 'timer'));
						$.data(this, 'timer', setTimeout(function() {
							self.scrollLock = false;
							//do something
						}, 250));

						var theEvent = e.originalEvent.wheelDelta || e.originalEvent.detail*-1


						//this.scrollTop += ( delta < 0 ? 1 : -1 ) * 30;
						//   e.preventDefault();


						e.stopImmediatePropagation();
						e.stopPropagation();
						e.preventDefault();


						if(theEvent /120 > 0) {
							//scrolling up
							if(self.currentZoomLevel >= self.minZoomLevel){ 
								self.changeZoomLevel(self.currentZoomLevel-self.options.scrollZoomIncrement);        
							}

						}
						else{
							//scrolling down


							if(self.options.maxZoomLevel){
								if(self.currentZoomLevel <= self.options.maxZoomLevel){           
									self.changeZoomLevel(parseFloat(self.currentZoomLevel)+self.options.scrollZoomIncrement);
								}
							}
							else{
								//andy 

								self.changeZoomLevel(parseFloat(self.currentZoomLevel)+self.options.scrollZoomIncrement);
							}

						}
						return false;
					});
				}


			},
			setElements: function(type) {
				var self = this;
        if(!self.options.zoomEnabled){return false;}
				if(type=="show"){
					if(self.isWindowSet){
						if(self.options.zoomType == "inner") {self.showHideWindow("show");}
						if(self.options.zoomType == "window") {self.showHideWindow("show");}
						if(self.options.showLens) {self.showHideLens("show");}
						if(self.options.tint && self.options.zoomType != "inner") {self.showHideTint("show");
						}
					}
				}

				if(type=="hide"){
					if(self.options.zoomType == "window") {self.showHideWindow("hide");}
					if(!self.options.tint) {self.showHideWindow("hide");}
					if(self.options.showLens) {self.showHideLens("hide");}
					if(self.options.tint) {	self.showHideTint("hide");}
				}   
			},
			setPosition: function(e) {
      
				var self = this;
        
        if(!self.options.zoomEnabled){return false;}

				//recaclc offset each time in case the image moves
				//this can be caused by other on page elements
				self.nzHeight = self.$elem.height();
				self.nzWidth = self.$elem.width();
				self.nzOffset = self.$elem.offset();

				if(self.options.tint && self.options.zoomType != "inner") {
					self.zoomTint.css({ top: 0});
					self.zoomTint.css({ left: 0});
				}
				//set responsive       
				//will checking if the image needs changing before running this code work faster?
				if(self.options.responsive && !self.options.scrollZoom){
					if(self.options.showLens){ 
						if(self.nzHeight < self.options.zoomWindowWidth/self.widthRatio){
							lensHeight = self.nzHeight;              
						}
						else{
							lensHeight = String((self.options.zoomWindowHeight/self.heightRatio))
						}
						if(self.largeWidth < self.options.zoomWindowWidth){
							lensWidth = self.nzWidth;
						}       
						else{
							lensWidth =  (self.options.zoomWindowWidth/self.widthRatio);
						}
						self.widthRatio = self.largeWidth / self.nzWidth;
						self.heightRatio = self.largeHeight / self.nzHeight;        
						if(self.options.zoomType != "lens") {


							//possibly dont need to keep recalcalculating
							//if the lens is heigher than the image, then set lens size to image size
							if(self.nzHeight < self.options.zoomWindowWidth/self.widthRatio){
								lensHeight = self.nzHeight;  

							}
							else{
								lensHeight = String((self.options.zoomWindowHeight/self.heightRatio))
							}

							if(self.options.zoomWindowWidth < self.options.zoomWindowWidth){
								lensWidth = self.nzWidth;
							}       
							else{
								lensWidth =  (self.options.zoomWindowWidth/self.widthRatio);
							}            

							self.zoomLens.css('width', lensWidth);    
							self.zoomLens.css('height', lensHeight); 

							if(self.options.tint){    
								self.zoomTintImage.css('width', self.nzWidth);    
								self.zoomTintImage.css('height', self.nzHeight); 
							}

						}                     
						if(self.options.zoomType == "lens") {  

							self.zoomLens.css({ width: String(self.options.lensSize) + 'px', height: String(self.options.lensSize) + 'px' })      


						}        
						//end responsive image change
					}
				}

				//container fix
				self.zoomContainer.css({ top: self.nzOffset.top});
				self.zoomContainer.css({ left: self.nzOffset.left});
				self.mouseLeft = parseInt(e.pageX - self.nzOffset.left);
				self.mouseTop = parseInt(e.pageY - self.nzOffset.top);
				//calculate the Location of the Lens

				//calculate the bound regions - but only if zoom window
				if(self.options.zoomType == "window") {
					self.Etoppos = (self.mouseTop < (self.zoomLens.height()/2));
					self.Eboppos = (self.mouseTop > self.nzHeight - (self.zoomLens.height()/2)-(self.options.lensBorderSize*2));
					self.Eloppos = (self.mouseLeft < 0+((self.zoomLens.width()/2))); 
					self.Eroppos = (self.mouseLeft > (self.nzWidth - (self.zoomLens.width()/2)-(self.options.lensBorderSize*2)));  
				}
				//calculate the bound regions - but only for inner zoom
				if(self.options.zoomType == "inner"){ 
					self.Etoppos = (self.mouseTop < ((self.nzHeight/2)/self.heightRatio) );
					self.Eboppos = (self.mouseTop > (self.nzHeight - ((self.nzHeight/2)/self.heightRatio)));
					self.Eloppos = (self.mouseLeft < 0+(((self.nzWidth/2)/self.widthRatio)));
					self.Eroppos = (self.mouseLeft > (self.nzWidth - (self.nzWidth/2)/self.widthRatio-(self.options.lensBorderSize*2)));  
				}

				// if the mouse position of the slider is one of the outerbounds, then hide  window and lens
				if (self.mouseLeft <= 0 || self.mouseTop < 0 || self.mouseLeft > self.nzWidth || self.mouseTop > self.nzHeight ) {				          
					self.setElements("hide");
					return;
				}
				//else continue with operations
				else {


					//lens options
					if(self.options.showLens) {
						//		self.showHideLens("show");
						//set background position of lens
						self.lensLeftPos = String(self.mouseLeft - self.zoomLens.width() / 2);
						self.lensTopPos = String(self.mouseTop - self.zoomLens.height() / 2);


					}
					//adjust the background position if the mouse is in one of the outer regions 

					//Top region
					if(self.Etoppos){
						self.lensTopPos = 0;
					}
					//Left Region
					if(self.Eloppos){
						self.windowLeftPos = 0;
						self.lensLeftPos = 0;
						self.tintpos=0;
					}     
					//Set bottom and right region for window mode
					if(self.options.zoomType == "window") {
						if(self.Eboppos){
							self.lensTopPos = Math.max( (self.nzHeight)-self.zoomLens.height()-(self.options.lensBorderSize*2), 0 );
						} 
						if(self.Eroppos){
							self.lensLeftPos = (self.nzWidth-(self.zoomLens.width())-(self.options.lensBorderSize*2));
						}  
					}  
					//Set bottom and right region for inner mode
					if(self.options.zoomType == "inner") {
						if(self.Eboppos){
							self.lensTopPos = Math.max( ((self.nzHeight)-(self.options.lensBorderSize*2)), 0 );
						} 
						if(self.Eroppos){
							self.lensLeftPos = (self.nzWidth-(self.nzWidth)-(self.options.lensBorderSize*2));
						}  

					}
					//if lens zoom
					if(self.options.zoomType == "lens") {  
						self.windowLeftPos = String(((e.pageX - self.nzOffset.left) * self.widthRatio - self.zoomLens.width() / 2) * (-1));   
						self.windowTopPos = String(((e.pageY - self.nzOffset.top) * self.heightRatio - self.zoomLens.height() / 2) * (-1));

						self.zoomLens.css({ backgroundPosition: self.windowLeftPos + 'px ' + self.windowTopPos + 'px' });

						if(self.changeBgSize){  

							if(self.nzHeight>self.nzWidth){  
								if(self.options.zoomType == "lens"){       
									self.zoomLens.css({ "background-size": self.largeWidth/self.newvalueheight + 'px ' + self.largeHeight/self.newvalueheight + 'px' });
								}   

								self.zoomWindow.css({ "background-size": self.largeWidth/self.newvalueheight + 'px ' + self.largeHeight/self.newvalueheight + 'px' });
							}
							else{     
								if(self.options.zoomType == "lens"){       
									self.zoomLens.css({ "background-size": self.largeWidth/self.newvaluewidth + 'px ' + self.largeHeight/self.newvaluewidth + 'px' });
								}   
								self.zoomWindow.css({ "background-size": self.largeWidth/self.newvaluewidth + 'px ' + self.largeHeight/self.newvaluewidth + 'px' });            
							}
							self.changeBgSize = false;
						}    

						self.setWindowPostition(e);  
					}
					//if tint zoom   
					if(self.options.tint && self.options.zoomType != "inner") {
						self.setTintPosition(e);

					}
					//set the css background position 
					if(self.options.zoomType == "window") {
						self.setWindowPostition(e);   
					}
					if(self.options.zoomType == "inner") {
						self.setWindowPostition(e);   
					}
					if(self.options.showLens) {

						if(self.fullwidth && self.options.zoomType != "lens"){
							self.lensLeftPos = 0;

						}
						self.zoomLens.css({ left: self.lensLeftPos + 'px', top: self.lensTopPos + 'px' })  
					}

				} //end else



			},
			showHideWindow: function(change) {
				var self = this;              
				if(change == "show"){      
					if(!self.isWindowActive){
						if(self.options.zoomWindowFadeIn){
							self.zoomWindow.stop(true, true, false).fadeIn(self.options.zoomWindowFadeIn);
						}
						else{self.zoomWindow.show();}
						self.isWindowActive = true;
					}            
				}
				if(change == "hide"){
					if(self.isWindowActive){
						if(self.options.zoomWindowFadeOut){
							self.zoomWindow.stop(true, true).fadeOut(self.options.zoomWindowFadeOut);
						}
						else{self.zoomWindow.hide();}
						self.isWindowActive = false;        
					}      
				}
			},
			showHideLens: function(change) {
				var self = this;              
				if(change == "show"){      
					if(!self.isLensActive){
						if(self.options.lensFadeIn){
							self.zoomLens.stop(true, true, false).fadeIn(self.options.lensFadeIn);
						}
						else{self.zoomLens.show();}
						self.isLensActive = true;
					}            
				}
				if(change == "hide"){
					if(self.isLensActive){
						if(self.options.lensFadeOut){
							self.zoomLens.stop(true, true).fadeOut(self.options.lensFadeOut);
						}
						else{self.zoomLens.hide();}
						self.isLensActive = false;        
					}      
				}
			},
			showHideTint: function(change) {
				var self = this;              
				if(change == "show"){      
					if(!self.isTintActive){

						if(self.options.zoomTintFadeIn){
							self.zoomTint.css({opacity:self.options.tintOpacity}).animate().stop(true, true).fadeIn("slow");
						}
						else{
							self.zoomTint.css({opacity:self.options.tintOpacity}).animate();
							self.zoomTint.show();


						}
						self.isTintActive = true;
					}            
				}
				if(change == "hide"){      
					if(self.isTintActive){ 

						if(self.options.zoomTintFadeOut){
							self.zoomTint.stop(true, true).fadeOut(self.options.zoomTintFadeOut);
						}
						else{self.zoomTint.hide();}
						self.isTintActive = false;        
					}      
				}
			},
			setLensPostition: function( e ) {


			},
			setWindowPostition: function( e ) {
				//return obj.slice( 0, count );
				var self = this;

				if(!isNaN(self.options.zoomWindowPosition)){

					switch (self.options.zoomWindowPosition) { 
					case 1: //done         
						self.windowOffsetTop = (self.options.zoomWindowOffety);//DONE - 1
						self.windowOffsetLeft =(+self.nzWidth); //DONE 1, 2, 3, 4, 16
						break;
					case 2:
						if(self.options.zoomWindowHeight > self.nzHeight){ //positive margin

							self.windowOffsetTop = ((self.options.zoomWindowHeight/2)-(self.nzHeight/2))*(-1);
							self.windowOffsetLeft =(self.nzWidth); //DONE 1, 2, 3, 4, 16
						}
						else{ //negative margin

						}
						break;
					case 3: //done        
						self.windowOffsetTop = (self.nzHeight - self.zoomWindow.height() - (self.options.borderSize*2)); //DONE 3,9
						self.windowOffsetLeft =(self.nzWidth); //DONE 1, 2, 3, 4, 16
						break;      
					case 4: //done  
						self.windowOffsetTop = (self.nzHeight); //DONE - 4,5,6,7,8
						self.windowOffsetLeft =(self.nzWidth); //DONE 1, 2, 3, 4, 16
						break;
					case 5: //done  
						self.windowOffsetTop = (self.nzHeight); //DONE - 4,5,6,7,8
						self.windowOffsetLeft =(self.nzWidth-self.zoomWindow.width()-(self.options.borderSize*2)); //DONE - 5,15
						break;
					case 6: 
						if(self.options.zoomWindowHeight > self.nzHeight){ //positive margin
							self.windowOffsetTop = (self.nzHeight);  //DONE - 4,5,6,7,8

							self.windowOffsetLeft =((self.options.zoomWindowWidth/2)-(self.nzWidth/2)+(self.options.borderSize*2))*(-1);  
						}
						else{ //negative margin

						}


						break;
					case 7: //done  
						self.windowOffsetTop = (self.nzHeight);  //DONE - 4,5,6,7,8
						self.windowOffsetLeft = 0; //DONE 7, 13
						break;
					case 8: //done  
						self.windowOffsetTop = (self.nzHeight); //DONE - 4,5,6,7,8
						self.windowOffsetLeft =(self.zoomWindow.width()+(self.options.borderSize*2) )* (-1);  //DONE 8,9,10,11,12
						break;
					case 9:  //done  
						self.windowOffsetTop = (self.nzHeight - self.zoomWindow.height() - (self.options.borderSize*2)); //DONE 3,9
						self.windowOffsetLeft =(self.zoomWindow.width()+(self.options.borderSize*2) )* (-1);  //DONE 8,9,10,11,12
						break;
					case 10: 
						if(self.options.zoomWindowHeight > self.nzHeight){ //positive margin

							self.windowOffsetTop = ((self.options.zoomWindowHeight/2)-(self.nzHeight/2))*(-1);
							self.windowOffsetLeft =(self.zoomWindow.width()+(self.options.borderSize*2) )* (-1);  //DONE 8,9,10,11,12
						}
						else{ //negative margin

						}
						break;
					case 11: 
						self.windowOffsetTop = (self.options.zoomWindowOffety);
						self.windowOffsetLeft =(self.zoomWindow.width()+(self.options.borderSize*2) )* (-1);  //DONE 8,9,10,11,12
						break;
					case 12: //done  
						self.windowOffsetTop = (self.zoomWindow.height()+(self.options.borderSize*2))*(-1); //DONE 12,13,14,15,16
						self.windowOffsetLeft =(self.zoomWindow.width()+(self.options.borderSize*2) )* (-1);  //DONE 8,9,10,11,12
						break;
					case 13: //done  
						self.windowOffsetTop = (self.zoomWindow.height()+(self.options.borderSize*2))*(-1); //DONE 12,13,14,15,16
						self.windowOffsetLeft =(0); //DONE 7, 13
						break;
					case 14: 
						if(self.options.zoomWindowHeight > self.nzHeight){ //positive margin
							self.windowOffsetTop = (self.zoomWindow.height()+(self.options.borderSize*2))*(-1); //DONE 12,13,14,15,16

							self.windowOffsetLeft =((self.options.zoomWindowWidth/2)-(self.nzWidth/2)+(self.options.borderSize*2))*(-1);  
						}
						else{ //negative margin

						}

						break;
					case 15://done   
						self.windowOffsetTop = (self.zoomWindow.height()+(self.options.borderSize*2))*(-1); //DONE 12,13,14,15,16
						self.windowOffsetLeft =(self.nzWidth-self.zoomWindow.width()-(self.options.borderSize*2)); //DONE - 5,15
						break;
					case 16:  //done  
						self.windowOffsetTop = (self.zoomWindow.height()+(self.options.borderSize*2))*(-1); //DONE 12,13,14,15,16
						self.windowOffsetLeft =(self.nzWidth); //DONE 1, 2, 3, 4, 16
						break;            
					default: //done  
						self.windowOffsetTop = (self.options.zoomWindowOffety);//DONE - 1
					self.windowOffsetLeft =(self.nzWidth); //DONE 1, 2, 3, 4, 16
					} 
				} //end isNAN
				else{
					//WE CAN POSITION IN A CLASS - ASSUME THAT ANY STRING PASSED IS
					self.externalContainer = $('#'+self.options.zoomWindowPosition);
					self.externalContainerWidth = self.externalContainer.width();
					self.externalContainerHeight = self.externalContainer.height();
					self.externalContainerOffset = self.externalContainer.offset();

					self.windowOffsetTop = self.externalContainerOffset.top;//DONE - 1
					self.windowOffsetLeft =self.externalContainerOffset.left; //DONE 1, 2, 3, 4, 16

				}
				self.isWindowSet = true;
				self.windowOffsetTop = self.windowOffsetTop + self.options.zoomWindowOffety;
				self.windowOffsetLeft = self.windowOffsetLeft + self.options.zoomWindowOffetx;

				self.zoomWindow.css({ top: self.windowOffsetTop});
				self.zoomWindow.css({ left: self.windowOffsetLeft});

				if(self.options.zoomType == "inner") {
					self.zoomWindow.css({ top: 0});
					self.zoomWindow.css({ left: 0});

				}   


				self.windowLeftPos = String(((e.pageX - self.nzOffset.left) * self.widthRatio - self.zoomWindow.width() / 2) * (-1));   
				self.windowTopPos = String(((e.pageY - self.nzOffset.top) * self.heightRatio - self.zoomWindow.height() / 2) * (-1));
				if(self.Etoppos){self.windowTopPos = 0;}
				if(self.Eloppos){self.windowLeftPos = 0;}     
				if(self.Eboppos){self.windowTopPos = (self.largeHeight/self.currentZoomLevel-self.zoomWindow.height())*(-1);  } 
				if(self.Eroppos){self.windowLeftPos = ((self.largeWidth/self.currentZoomLevel-self.zoomWindow.width())*(-1));}    

				//stops micro movements
				if(self.fullheight){
					self.windowTopPos = 0;

				}
				if(self.fullwidth){
					self.windowLeftPos = 0;

				}
				//set the css background position 


				if(self.options.zoomType == "window" || self.options.zoomType == "inner") {

					if(self.zoomLock == 1){
						//overrides for images not zoomable
						if(self.widthRatio <= 1){

							self.windowLeftPos = 0;
						}
						if(self.heightRatio <= 1){ 
							self.windowTopPos = 0;
						}
					}
					// adjust images less than the window height

					if(self.largeHeight < self.options.zoomWindowHeight){

						self.windowTopPos = 0;
					}
					if(self.largeWidth < self.options.zoomWindowWidth){
						self.windowLeftPos = 0;
					}       

					//set the zoomwindow background position
					if (self.options.easing){

						//     if(self.changeZoom){
						//           clearInterval(self.loop);
						//           self.changeZoom = false;
						//           self.loop = false;

						//            }
						//set the pos to 0 if not set
						if(!self.xp){self.xp = 0;}
						if(!self.yp){self.yp = 0;}  
						//if loop not already started, then run it 
						if (!self.loop){           
							self.loop = setInterval(function(){                
								//using zeno's paradox    

								self.xp += (self.windowLeftPos  - self.xp) / self.options.easingAmount; 
								self.yp += (self.windowTopPos  - self.yp) / self.options.easingAmount;
								if(self.scrollingLock){


									clearInterval(self.loop);
									self.xp = self.windowLeftPos;
									self.yp = self.windowTopPos            

									self.xp = ((e.pageX - self.nzOffset.left) * self.widthRatio - self.zoomWindow.width() / 2) * (-1);
									self.yp = (((e.pageY - self.nzOffset.top) * self.heightRatio - self.zoomWindow.height() / 2) * (-1));                         

									if(self.changeBgSize){    
										if(self.nzHeight>self.nzWidth){  
											if(self.options.zoomType == "lens"){      
												self.zoomLens.css({ "background-size": self.largeWidth/self.newvalueheight + 'px ' + self.largeHeight/self.newvalueheight + 'px' });
											}   
											self.zoomWindow.css({ "background-size": self.largeWidth/self.newvalueheight + 'px ' + self.largeHeight/self.newvalueheight + 'px' });
										}
										else{   
											if(self.options.zoomType != "lens"){      
												self.zoomLens.css({ "background-size": self.largeWidth/self.newvaluewidth + 'px ' + self.largeHeight/self.newvalueheight + 'px' });
											}            
											self.zoomWindow.css({ "background-size": self.largeWidth/self.newvaluewidth + 'px ' + self.largeHeight/self.newvaluewidth + 'px' });            

										}

										/*
             if(!self.bgxp){self.bgxp = self.largeWidth/self.newvalue;}
						if(!self.bgyp){self.bgyp = self.largeHeight/self.newvalue ;}  
                 if (!self.bgloop){   
                 	self.bgloop = setInterval(function(){   

                 self.bgxp += (self.largeWidth/self.newvalue  - self.bgxp) / self.options.easingAmount; 
								self.bgyp += (self.largeHeight/self.newvalue  - self.bgyp) / self.options.easingAmount;

           self.zoomWindow.css({ "background-size": self.bgxp + 'px ' + self.bgyp + 'px' });


                  }, 16);

                 }
										 */
										self.changeBgSize = false;
									}

									self.zoomWindow.css({ backgroundPosition: self.windowLeftPos + 'px ' + self.windowTopPos + 'px' });
									self.scrollingLock = false;
									self.loop = false;

								}
								else{
									if(self.changeBgSize){    
										if(self.nzHeight>self.nzWidth){ 
											if(self.options.zoomType == "lens"){      
												self.zoomLens.css({ "background-size": self.largeWidth/self.newvalueheight + 'px ' + self.largeHeight/self.newvalueheight + 'px' });
											}         
											self.zoomWindow.css({ "background-size": self.largeWidth/self.newvalueheight + 'px ' + self.largeHeight/self.newvalueheight + 'px' });
										}
										else{                 
											if(self.options.zoomType != "lens"){     
												self.zoomLens.css({ "background-size": self.largeWidth/self.newvaluewidth + 'px ' + self.largeHeight/self.newvaluewidth + 'px' });
											}      
											self.zoomWindow.css({ "background-size": self.largeWidth/self.newvaluewidth + 'px ' + self.largeHeight/self.newvaluewidth + 'px' });            
										}
										self.changeBgSize = false;
									}                   

									self.zoomWindow.css({ backgroundPosition: self.xp + 'px ' + self.yp + 'px' });
								}       
							}, 16);
						}
					}   
					else{    
						if(self.changeBgSize){  
							if(self.nzHeight>self.nzWidth){  
								if(self.options.zoomType == "lens"){      
									self.zoomLens.css({ "background-size": self.largeWidth/self.newvalueheight + 'px ' + self.largeHeight/self.newvalueheight + 'px' });
								} 

								self.zoomWindow.css({ "background-size": self.largeWidth/self.newvalueheight + 'px ' + self.largeHeight/self.newvalueheight + 'px' });
							}
							else{     
								if(self.options.zoomType == "lens"){      
									self.zoomLens.css({ "background-size": self.largeWidth/self.newvaluewidth + 'px ' + self.largeHeight/self.newvaluewidth + 'px' });
								} 
								if((self.largeHeight/self.newvaluewidth) < self.options.zoomWindowHeight){ 

									self.zoomWindow.css({ "background-size": self.largeWidth/self.newvaluewidth + 'px ' + self.largeHeight/self.newvaluewidth + 'px' });            
								}
								else{

									self.zoomWindow.css({ "background-size": self.largeWidth/self.newvalueheight + 'px ' + self.largeHeight/self.newvalueheight + 'px' });   
								}

							}
							self.changeBgSize = false;
						}     

						self.zoomWindow.css({ backgroundPosition: self.windowLeftPos + 'px ' + self.windowTopPos + 'px' });       
					}
				} 
			},
			setTintPosition: function(e){
				var self = this;
				self.nzOffset = self.$elem.offset();
				self.tintpos = String(((e.pageX - self.nzOffset.left)-(self.zoomLens.width() / 2)) * (-1)); 
				self.tintposy = String(((e.pageY - self.nzOffset.top) - self.zoomLens.height() / 2) * (-1));	
				if(self.Etoppos){
					self.tintposy = 0;
				}
				if(self.Eloppos){
					self.tintpos=0;
				}     
				if(self.Eboppos){
					self.tintposy = (self.nzHeight-self.zoomLens.height()-(self.options.lensBorderSize*2))*(-1);
				} 
				if(self.Eroppos){
					self.tintpos = ((self.nzWidth-self.zoomLens.width()-(self.options.lensBorderSize*2))*(-1));
				}    
				if(self.options.tint) {
					//stops micro movements
					if(self.fullheight){
						self.tintposy = 0;

					}
					if(self.fullwidth){ 
						self.tintpos = 0;

					}   
					self.zoomTintImage.css({'left': self.tintpos+'px'});
					self.zoomTintImage.css({'top': self.tintposy+'px'});
				}
			},

			swaptheimage: function(smallimage, largeimage){
				var self = this;
				var newImg = new Image(); 

				if(self.options.loadingIcon){
					self.spinner = $('<div style="background: url(\''+self.options.loadingIcon+'\') no-repeat center;height:'+self.nzHeight+'px;width:'+self.nzWidth+'px;z-index: 2000;position: absolute; background-position: center center;"></div>');
					self.$elem.after(self.spinner);
				}

				self.options.onImageSwap(self.$elem);

				newImg.onload = function() {
					self.largeWidth = newImg.width;
					self.largeHeight = newImg.height;
					self.zoomImage = largeimage;
					self.zoomWindow.css({ "background-size": self.largeWidth + 'px ' + self.largeHeight + 'px' });
					self.zoomWindow.css({ "background-size": self.largeWidth + 'px ' + self.largeHeight + 'px' });


					self.swapAction(smallimage, largeimage);
					return;              
				}          
				newImg.src = largeimage; // this must be done AFTER setting onload

			},
			swapAction: function(smallimage, largeimage){


				var self = this;    

				var newImg2 = new Image(); 
				newImg2.onload = function() {
					//re-calculate values
					self.nzHeight = newImg2.height;
					self.nzWidth = newImg2.width;
					self.options.onImageSwapComplete(self.$elem);

					self.doneCallback();  
					return;      
				}          
				newImg2.src = smallimage; 

				//reset the zoomlevel to that initially set in options
				self.currentZoomLevel = self.options.zoomLevel;
				self.options.maxZoomLevel = false;

				//swaps the main image
				//self.$elem.attr("src",smallimage);
				//swaps the zoom image     
				if(self.options.zoomType == "lens") {
					self.zoomLens.css({ backgroundImage: "url('" + largeimage + "')" }); 
				}
				if(self.options.zoomType == "window") {
					self.zoomWindow.css({ backgroundImage: "url('" + largeimage + "')" }); 
				}
				if(self.options.zoomType == "inner") {
					self.zoomWindow.css({ backgroundImage: "url('" + largeimage + "')" }); 
				} 



				self.currentImage = largeimage;

				if(self.options.imageCrossfade){
					var oldImg = self.$elem;
					var newImg = oldImg.clone();         
					self.$elem.attr("src",smallimage)
					self.$elem.after(newImg);
					newImg.stop(true).fadeOut(self.options.imageCrossfade, function() {
						$(this).remove();         
					});

					//       				if(self.options.zoomType == "inner"){
					//remove any attributes on the cloned image so we can resize later
					self.$elem.width("auto").removeAttr("width");
					self.$elem.height("auto").removeAttr("height");
					//   }

					oldImg.fadeIn(self.options.imageCrossfade);

					if(self.options.tint && self.options.zoomType != "inner") {

						var oldImgTint = self.zoomTintImage;
						var newImgTint = oldImgTint.clone();         
						self.zoomTintImage.attr("src",largeimage)
						self.zoomTintImage.after(newImgTint);
						newImgTint.stop(true).fadeOut(self.options.imageCrossfade, function() {
							$(this).remove();         
						});



						oldImgTint.fadeIn(self.options.imageCrossfade);


						//self.zoomTintImage.attr("width",elem.data("image"));

						//resize the tint window
						self.zoomTint.css({ height: self.$elem.height()});
						self.zoomTint.css({ width: self.$elem.width()});
					}    

					self.zoomContainer.css("height", self.$elem.height());
					self.zoomContainer.css("width", self.$elem.width());

					if(self.options.zoomType == "inner"){ 
						if(!self.options.constrainType){
							self.zoomWrap.parent().css("height", self.$elem.height());
							self.zoomWrap.parent().css("width", self.$elem.width());

							self.zoomWindow.css("height", self.$elem.height());
							self.zoomWindow.css("width", self.$elem.width());
						}
					} 

					if(self.options.imageCrossfade){  
						self.zoomWrap.css("height", self.$elem.height());
						self.zoomWrap.css("width", self.$elem.width());
					} 
				}
				else{
					self.$elem.attr("src",smallimage); 
					if(self.options.tint) {
						self.zoomTintImage.attr("src",largeimage);
						//self.zoomTintImage.attr("width",elem.data("image"));
						self.zoomTintImage.attr("height",self.$elem.height());
						//self.zoomTintImage.attr('src') = elem.data("image");
						self.zoomTintImage.css({ height: self.$elem.height()}); 
						self.zoomTint.css({ height: self.$elem.height()});

					}
					self.zoomContainer.css("height", self.$elem.height());
					self.zoomContainer.css("width", self.$elem.width());

					if(self.options.imageCrossfade){  
						self.zoomWrap.css("height", self.$elem.height());
						self.zoomWrap.css("width", self.$elem.width());
					} 
				}              
				if(self.options.constrainType){     

					//This will contrain the image proportions
					if(self.options.constrainType == "height"){ 

						self.zoomContainer.css("height", self.options.constrainSize);
						self.zoomContainer.css("width", "auto");

						if(self.options.imageCrossfade){  
							self.zoomWrap.css("height", self.options.constrainSize);
							self.zoomWrap.css("width", "auto"); 
							self.constwidth = self.zoomWrap.width();


						}
						else{                  
							self.$elem.css("height", self.options.constrainSize);
							self.$elem.css("width", "auto");
							self.constwidth = self.$elem.width();
						} 

						if(self.options.zoomType == "inner"){

							self.zoomWrap.parent().css("height", self.options.constrainSize);
							self.zoomWrap.parent().css("width", self.constwidth);   
							self.zoomWindow.css("height", self.options.constrainSize);
							self.zoomWindow.css("width", self.constwidth);    
						}        
						if(self.options.tint){
							self.tintContainer.css("height", self.options.constrainSize);
							self.tintContainer.css("width", self.constwidth);
							self.zoomTint.css("height", self.options.constrainSize);
							self.zoomTint.css("width", self.constwidth);
							self.zoomTintImage.css("height", self.options.constrainSize);
							self.zoomTintImage.css("width", self.constwidth); 
						} 

					}
					if(self.options.constrainType == "width"){       
						self.zoomContainer.css("height", "auto");
						self.zoomContainer.css("width", self.options.constrainSize);

						if(self.options.imageCrossfade){
							self.zoomWrap.css("height", "auto");
							self.zoomWrap.css("width", self.options.constrainSize);
							self.constheight = self.zoomWrap.height();
						}
						else{            
							self.$elem.css("height", "auto");
							self.$elem.css("width", self.options.constrainSize); 
							self.constheight = self.$elem.height();              
						} 
						if(self.options.zoomType == "inner"){
							self.zoomWrap.parent().css("height", self.constheight);
							self.zoomWrap.parent().css("width", self.options.constrainSize);   
							self.zoomWindow.css("height", self.constheight);
							self.zoomWindow.css("width", self.options.constrainSize);    
						} 
						if(self.options.tint){
							self.tintContainer.css("height", self.constheight);
							self.tintContainer.css("width", self.options.constrainSize);
							self.zoomTint.css("height", self.constheight);
							self.zoomTint.css("width", self.options.constrainSize);
							self.zoomTintImage.css("height", self.constheight);
							self.zoomTintImage.css("width", self.options.constrainSize); 
						}   

					}        


				}

			},
			doneCallback: function(){

				var self = this;
				if(self.options.loadingIcon){
					self.spinner.hide();     
				}   

				self.nzOffset = self.$elem.offset();
				self.nzWidth = self.$elem.width();
				self.nzHeight = self.$elem.height();

				// reset the zoomlevel back to default
				self.currentZoomLevel = self.options.zoomLevel;

				//ratio of the large to small image
				self.widthRatio = self.largeWidth / self.nzWidth;
				self.heightRatio = self.largeHeight / self.nzHeight; 

				//NEED TO ADD THE LENS SIZE FOR ROUND
				// adjust images less than the window height
				if(self.options.zoomType == "window") {

					if(self.nzHeight < self.options.zoomWindowWidth/self.widthRatio){
						lensHeight = self.nzHeight;  

					}
					else{
						lensHeight = String((self.options.zoomWindowHeight/self.heightRatio))
					}

					if(self.options.zoomWindowWidth < self.options.zoomWindowWidth){
						lensWidth = self.nzWidth;
					}       
					else{
						lensWidth =  (self.options.zoomWindowWidth/self.widthRatio);
					}


					if(self.zoomLens){

						self.zoomLens.css('width', lensWidth);    
						self.zoomLens.css('height', lensHeight); 


					}
				}
			},
			getCurrentImage: function(){
				var self = this;  
				return self.zoomImage; 
			}, 
			getGalleryList: function(){
				var self = this;   
				//loop through the gallery options and set them in list for fancybox
				self.gallerylist = [];
				if (self.options.gallery){ 


					$('#'+self.options.gallery + ' a').each(function() {

						var img_src = '';
						if($(this).data("zoom-image")){
							img_src = $(this).data("zoom-image");
						}
						else if($(this).data("image")){
							img_src = $(this).data("image");
						}			
						//put the current image at the start
						if(img_src == self.zoomImage){
							self.gallerylist.unshift({
								href: ''+img_src+'',
								title: $(this).find('img').attr("title")
							});	
						}
						else{
							self.gallerylist.push({
								href: ''+img_src+'',
								title: $(this).find('img').attr("title")
							});
						}


					});
				}                                                       
				//if no gallery - return current image
				else{
					self.gallerylist.push({
						href: ''+self.zoomImage+'',
						title: $(this).find('img').attr("title")
					}); 
				}
				return self.gallerylist;

			},
			changeZoomLevel: function(value){
				var self = this;   

				//flag a zoom, so can adjust the easing during setPosition     
				self.scrollingLock = true;   

				//round to two decimal places
				self.newvalue = parseFloat(value).toFixed(2);
				newvalue = parseFloat(value).toFixed(2);




				//maxwidth & Maxheight of the image
				maxheightnewvalue = self.largeHeight/((self.options.zoomWindowHeight / self.nzHeight) * self.nzHeight);     
				maxwidthtnewvalue = self.largeWidth/((self.options.zoomWindowWidth / self.nzWidth) * self.nzWidth);   	




				//calculate new heightratio
				if(self.options.zoomType != "inner")
				{
					if(maxheightnewvalue <= newvalue){
						self.heightRatio = (self.largeHeight/maxheightnewvalue) / self.nzHeight;
						self.newvalueheight = maxheightnewvalue;
						self.fullheight = true;

					}
					else{
						self.heightRatio = (self.largeHeight/newvalue) / self.nzHeight; 
						self.newvalueheight = newvalue;
						self.fullheight = false;

					}


//					calculate new width ratio

					if(maxwidthtnewvalue <= newvalue){
						self.widthRatio = (self.largeWidth/maxwidthtnewvalue) / self.nzWidth;
						self.newvaluewidth = maxwidthtnewvalue;
						self.fullwidth = true;

					}
					else{
						self.widthRatio = (self.largeWidth/newvalue) / self.nzWidth; 
						self.newvaluewidth = newvalue;
						self.fullwidth = false;

					}
					if(self.options.zoomType == "lens"){
						if(maxheightnewvalue <= newvalue){
							self.fullwidth = true;
							self.newvaluewidth = maxheightnewvalue;

						} else{
							self.widthRatio = (self.largeWidth/newvalue) / self.nzWidth; 
							self.newvaluewidth = newvalue;

							self.fullwidth = false;
						}}
				}



				if(self.options.zoomType == "inner")
				{
					maxheightnewvalue = parseFloat(self.largeHeight/self.nzHeight).toFixed(2);     
					maxwidthtnewvalue = parseFloat(self.largeWidth/self.nzWidth).toFixed(2);      
					if(newvalue > maxheightnewvalue){
						newvalue = maxheightnewvalue;
					}
					if(newvalue > maxwidthtnewvalue){
						newvalue = maxwidthtnewvalue;
					}      


					if(maxheightnewvalue <= newvalue){


						self.heightRatio = (self.largeHeight/newvalue) / self.nzHeight; 
						if(newvalue > maxheightnewvalue){
							self.newvalueheight = maxheightnewvalue;
						}else{
							self.newvalueheight = newvalue;
						}
						self.fullheight = true;


					}
					else{



						self.heightRatio = (self.largeHeight/newvalue) / self.nzHeight; 

						if(newvalue > maxheightnewvalue){

							self.newvalueheight = maxheightnewvalue;
						}else{
							self.newvalueheight = newvalue;
						}
						self.fullheight = false;
					}




					if(maxwidthtnewvalue <= newvalue){   

						self.widthRatio = (self.largeWidth/newvalue) / self.nzWidth; 
						if(newvalue > maxwidthtnewvalue){

							self.newvaluewidth = maxwidthtnewvalue;
						}else{
							self.newvaluewidth = newvalue;
						}

						self.fullwidth = true;


					}
					else{  

						self.widthRatio = (self.largeWidth/newvalue) / self.nzWidth; 
						self.newvaluewidth = newvalue;
						self.fullwidth = false;
					}        


				} //end inner
				scrcontinue = false;

				if(self.options.zoomType == "inner"){

					if(self.nzWidth >= self.nzHeight){
						if( self.newvaluewidth <= maxwidthtnewvalue){
							scrcontinue = true;
						}
						else{

							scrcontinue = false;
							self.fullheight = true;
							self.fullwidth = true;
						}
					}
					if(self.nzHeight > self.nzWidth){     
						if( self.newvaluewidth <= maxwidthtnewvalue){
							scrcontinue = true;
						}
						else{
							scrcontinue = false;  

							self.fullheight = true;
							self.fullwidth = true;
						}
					}
				}

				if(self.options.zoomType != "inner"){
					scrcontinue = true;
				}

				if(scrcontinue){



					self.zoomLock = 0;
					self.changeZoom = true;

					//if lens height is less than image height


					if(((self.options.zoomWindowHeight)/self.heightRatio) <= self.nzHeight){


						self.currentZoomLevel = self.newvalueheight; 
						if(self.options.zoomType != "lens" && self.options.zoomType != "inner") {
							self.changeBgSize = true;

							self.zoomLens.css({height: String((self.options.zoomWindowHeight)/self.heightRatio) + 'px' }) 
						}
						if(self.options.zoomType == "lens" || self.options.zoomType == "inner") {  
							self.changeBgSize = true;  
						}	


					} 




					if((self.options.zoomWindowWidth/self.widthRatio) <= self.nzWidth){



						if(self.options.zoomType != "inner"){
							if(self.newvaluewidth > self.newvalueheight)   {
								self.currentZoomLevel = self.newvaluewidth;                 

							}
						}

						if(self.options.zoomType != "lens" && self.options.zoomType != "inner") {
							self.changeBgSize = true;

							self.zoomLens.css({width: String((self.options.zoomWindowWidth)/self.widthRatio) + 'px' })
						}
						if(self.options.zoomType == "lens" || self.options.zoomType == "inner") {  
							self.changeBgSize = true;
						}	

					}
					if(self.options.zoomType == "inner"){
						self.changeBgSize = true;  

						if(self.nzWidth > self.nzHeight){
							self.currentZoomLevel = self.newvaluewidth;
						}
						if(self.nzHeight > self.nzWidth){
							self.currentZoomLevel = self.newvaluewidth;
						}
					}

				}      //under

				//sets the boundry change, called in setWindowPos
				self.setPosition(self.currentLoc);
				//
			},
			closeAll: function(){
				if(self.zoomWindow){self.zoomWindow.hide();}
				if(self.zoomLens){self.zoomLens.hide();}
				if(self.zoomTint){self.zoomTint.hide();}
			},
			changeState: function(value){
      	var self = this;
				if(value == 'enable'){self.options.zoomEnabled = true;}
				if(value == 'disable'){self.options.zoomEnabled = false;}

			}

	};




	$.fn.elevateZoom = function( options ) {
		return this.each(function() {
			var elevate = Object.create( ElevateZoom );

			elevate.init( options, this );

			$.data( this, 'elevateZoom', elevate );

		});
	};

	$.fn.elevateZoom.options = {
			zoomActivation: "hover", // Can also be click (PLACEHOLDER FOR NEXT VERSION)
      zoomEnabled: true, //false disables zoomwindow from showing
			preloading: 1, //by default, load all the images, if 0, then only load images after activated (PLACEHOLDER FOR NEXT VERSION)
			zoomLevel: 1, //default zoom level of image
			scrollZoom: false, //allow zoom on mousewheel, true to activate
			scrollZoomIncrement: 0.1,  //steps of the scrollzoom
			minZoomLevel: false,
			maxZoomLevel: false,
			easing: false,
			easingAmount: 12,
			lensSize: 200,
			zoomWindowWidth: 400,
			zoomWindowHeight: 400,
			zoomWindowOffetx: 0,
			zoomWindowOffety: 0,
			zoomWindowPosition: 1,
			zoomWindowBgColour: "#fff",
			lensFadeIn: false,
			lensFadeOut: false,
			debug: false,
			zoomWindowFadeIn: false,
			zoomWindowFadeOut: false,
			zoomWindowAlwaysShow: false,
			zoomTintFadeIn: false,
			zoomTintFadeOut: false,
			borderSize: 4,
			showLens: true,
			borderColour: "#888",
			lensBorderSize: 1,
			lensBorderColour: "#000",
			lensShape: "square", //can be "round"
			zoomType: "window", //window is default,  also "lens" available -
			containLensZoom: false,
			lensColour: "white", //colour of the lens background
			lensOpacity: 0.4, //opacity of the lens
			lenszoom: false,
			tint: false, //enable the tinting
			tintColour: "#333", //default tint color, can be anything, red, #ccc, rgb(0,0,0)
			tintOpacity: 0.4, //opacity of the tint
			gallery: false,
			galleryActiveClass: "zoomGalleryActive",
			imageCrossfade: false,
			constrainType: false,  //width or height
			constrainSize: false,  //in pixels the dimensions you want to constrain on
			loadingIcon: false, //http://www.example.com/spinner.gif
			cursor:"default", // user should set to what they want the cursor as, if they have set a click function
			responsive:true,
			onComplete: $.noop,
			onZoomedImageLoaded: function() {},
			onImageSwap: $.noop,
			onImageSwapComplete: $.noop
	};

})( jQuery, window, document );

  }).apply(root, arguments);
});
}(this));

define('shoptheme/components/product/view/media',[
	"jquery",
	"lodash",
	"cartware/components/_base",
	"cartware/jquery/bem",
	"modernizr",
	"elevatezoom",
	"cartware-slider",
	"magnific-popup"
], function(
	$,
	_,
	Component,
	BEM
) {
	var cache = {
		galleries: {}
	};

	return Component.create({
		block:			null,
		thumbs:			null,
		galleryImages:	null,
		galleryElement:	null,

		$init: function() {
			this.initializeComponent();
			this.initializeSimpleObservation();
			cache.galleries["__initial"] = this.block;
		},

		initializeComponent: function() {
			this.block = BEM.block("product-media");

			this.galleryImages = this.block.getElements("gallery-image");

			this.initializeThumbnails();
			this.createZoom(this.galleryImages.filter(":modifier(visible)"));
		},

		initializeThumbnails: function() {
			this.thumbs = this.block.getElements("more-views-thumb-link");

			this.thumbs.each(function(i) {
				$(this).data("image-index", i);
			});

			this.thumbs.on("click", _.bind(this._onThumbnailClick, this));
		},

		initializeSimpleObservation: function() {
			var currentSimple = ("undefined" !== typeof spConfig) ? _.get(spConfig, "__currentGallerySimple") : null;

			var _onChangeGallerySimple = _.bind(function(e, simple) {
				this.changeSimple(simple);
			}, this);

			$(document).on('product/changeGallerySimple', _onChangeGallerySimple);

			if (currentSimple) {
				_onChangeGallerySimple(null, currentSimple);
			}
		},

		changeSimple: function(simple) {
			var block,
				wrap = $("<div />").css("min-height", this.block.outerHeight(true)),
				self = this;

			if (!(simple.id in cache.galleries)) {
				cache.galleries[simple.id] = $(simple.mediaGallery);
			}

			block = cache.galleries[simple.id];

			$('.zoomContainer').remove();

			this.block.wrap(wrap);

			//this.block.replaceWith(block);
			block.replaceAll(this.block);
			this.initializeComponent();

			var sliderBlock = this.block.find(".cartware-slider");

			if (sliderBlock.length) {
				sliderBlock.cartwareSlider()
					.data('cartware_slider/instance')
					.on("ready", function() {
						self.block.unwrap();
					});
			}
		},

		_onThumbnailClick: function(e) {
			var thumb = $(e.currentTarget),
				image = thumb.data("media-gallery-image");

			if (!image) {
				image = $("#image-" + thumb.data("image-index"));
				thumb.data("media-gallery-image", image);
			}

			e.preventDefault();

			this.destroyZoom();

			this.galleryImages.filter(":modifier(visible)")
				.bemRemoveModifier("visible");

			image.bemAddModifier("visible");
			this.createZoom(image);
		},

		createZoom: function(image) {
			var self = this;

			if (!Modernizr.touch) {
				image.elevateZoom({
					zoomType: "inner",
					responsive: true
				});
			}

			image.on("click", function(e) {
				var items = [],
					index = 0,
					imageSrc = image.attr("src");

				if ($.magnificPopup.instance.isOpen) {
					return;
				}

				self.galleryImages.each(function(i) {
					var src = $(this).attr("src");

					if (i > 0) { // skip default image
						items.push({
							src: src
						});

						if (index === 0 && src == imageSrc) {
							index = i - 1;
						}
					}
				});

				$.magnificPopup.open({
					items: items,
					type: "image",
					gallery: {
						enabled: true
					}
				}, index);
			});
		},

		destroyZoom: function() {
			$('.zoomContainer').remove();
			this.galleryImages.filter(":modifier(visible)")
				.removeData('elevateZoom');
		}
	});
});
define('shoptheme/components/product/view/specifications',[
	"jquery",
	"lodash",
	"cartware/components/_base",
	"cartware/jquery/bem",
	"modernizr",
	"elevatezoom",
	"cartware-slider",
	"magnific-popup"
], function(
	$,
	_,
	Component,
	BEM
) {
	var cache = {
		blocks: {}
	};

	return Component.create({
		block:			null,
		thumbs:			null,
		galleryImages:	null,
		galleryElement:	null,

		$init: function() {
			this.initializeComponent();
			this.initializeSimpleObservation();
			cache.blocks["__initial"] = this.block;
		},

		initializeComponent: function() {
			this.block = BEM.block("product-detail-specifications");
		},

		initializeSimpleObservation: function() {
			var _onChange = _.bind(function(e, simple) {
				this.changeSimple(simple);
			}, this);

			$(document).on('product/changeSimple', _onChange);
			//$(document).on('product/changeGallerySimple', _onChange);
		},

		changeSimple: function(simple) {
			var block,
				self = this;

			if (!simple) {
				block = cache.blocks["__initial"];
			} else {
				if (!(simple.id in cache.blocks)) {
					cache.blocks[simple.id] = $(simple.specifications);
				}

				block = cache.blocks[simple.id];
			}

			//this.block.replaceWith(block);
			block.replaceAll(this.block);
			this.block = block;

			this.block.trigger("gigaset.content.change");
		}
	});
});

define('text!shoptheme/components/product/options/templates/color.html',[],function () { return '<div class="b_super-attribute-select">\r\n\t<ul class="b_super-attribute-select__list">\r\n\t\t<% _.forEach(options, function(option) { %>\r\n\t\t\t<li class="b_super-attribute-select__list-item<% if (\'qty\' in option && option.qty == 0) { %> b_super-attribute-select__list-item--disabled<% } %>" data-option="<%- option.value %>">\r\n\t\t\t\t<div class="b_super-attribute-select__color-swatch" style="background-color: <%= _.get(attributeExtension, [\'options\', option.value, \'color\'], "white") %>" title="<%= option.label %>">\r\n\t\t\t\t\t<% if (_.get(attributeExtension, ["options", option.value, "image"])) { %>\r\n\t\t\t\t\t\t<img src="<%= _.get(attributeExtension, [\'options\', option.value, \'image\']) %>" alt="<%= option.label %>" />\r\n\t\t\t\t\t<% } else { %>\r\n\t\t\t\t\t\t<span class="sr-only"><%= option.label %></span>\r\n\t\t\t\t\t<% } %>\r\n\t\t\t\t</div>\r\n\t\t\t</li>\r\n\t\t<% }); %>\r\n\t</ul>\r\n</div>';});


define('text!shoptheme/components/product/options/templates/badge.html',[],function () { return '<div class="b_super-attribute-select">\r\n\t<ul class="b_super-attribute-select__list">\r\n\t\t<% _.forEach(options, function(option) { %>\r\n\t\t\t<% console.log(option); %>\r\n\t\t\t<li class="b_super-attribute-select__list-item<% if (\'qty\' in option && option.qty == 0) { %> b_super-attribute-select__list-item--disabled<% } %>" data-option="<%= option.value %>">\r\n\t\t\t\t<div class="b_super-attribute-select__badge" title="<%= option.label %>">\r\n\t\t\t\t\t<% if ("undefined" !== option.tmpl && option.tmpl) { %>\r\n\t\t\t\t\t\t<%= option.tmpl %>\r\n\t\t\t\t\t<% } else { %>\r\n\t\t\t\t\t\t<span class="b_super-attribute-select__badge-label">\r\n\t\t\t\t\t\t\t<a href="daw" onclick="return false;" style="visibility: hidden;"><%= option.label %></a>\r\n\t\t\t\t\t\t</span>\r\n\t\t\t\t\t<% } %>\r\n\t\t\t\t</div>\r\n\t\t\t</li>\r\n\t\t<% }); %>\r\n\t</ul>\r\n</div>\r\n';});

define('shoptheme/components/product/options',[
	"require",
	"jquery",
	"lodash",
	"cartware/components/Registry",
	"cartware/components/_base",
	"cartware/jquery/bem",
	"cartware/dom/event/trigger",
	"select2",
	"text!./options/templates/color.html",
	"text!./options/templates/badge.html"
], function(
	require,
	$,
	_,
	Registry,
	Component,
	BEM,
	triggerEvent,
	S2,
	ColorOptionTemplate,
	BadgeOptionTemplate
) {
	var templates = {
		color: _.template(ColorOptionTemplate),
		badge: _.template(BadgeOptionTemplate)
	};

	return Component.create({
		block:			null,
		options:		null,

		$init: function() {
			this.block = BEM.block("product-options");

			this.options = this.block.getElements("attribute-option");

			Registry().retrieve("product.view.configurable.spConfig", _.bind(this.initializeRendering, this));
			
			this.initEngravingOption();
		},

		initializeRendering: function() {
			var self = this;
			this.options.each(function() { self.initializeOptionElement(this); });
		},

		initializeOptionElement: function(element) {
			var element = $(element),
				select = element.find("select"),
				value = select.val() << 0,
				attributeCode = element.data("attribute-code"),
				attributeData = _.find(spConfig.config.attributes, function(attribute, attributeId) {
					return attribute.code == attributeCode;
				}),
				attributeExtension = _.find(spConfig.config.attribute_extensions, function(attribute, attributeId) {
					return attribute.code == attributeCode;
				}),
				renderer = _.get(attributeExtension, "renderer", "default"),
				template = _.get(templates, renderer),
				displayNode = $('<div class="display" />').prependTo(element),
				options = [];

			var setActiveValue = function(value) {
				displayNode
					.findBlock("super-attribute-select")
					.getElements("list-item")
					.bemRemoveModifier("active")
					.filter('[data-option="' + value + '"]')
					.bemAddModifier("active");
			};

			if (!template) {
				select.on("attribute/fillSelect", function() {
					triggerEvent(this, "change");
				});

				select.on("change", function(e) {
					if ("undefined" === typeof e.originalEvent) {
						triggerEvent(this, "change");
					}
				});

				$.fn.select2.amd.require(
					["select2/utils", "select2/dropdown", "select2/dropdown/attachContainer", "select2/dropdown/closeOnSelect"],
					function (Utils, Dropdown, AttachContainer, CloseOnSelect) {
						select.select2({
							dropdownAdapter: Utils.Decorate(Utils.Decorate(Dropdown, AttachContainer), CloseOnSelect),
							width: "100%"
						});
					}
				);
			} else {
				element.on("click", "[data-option]", function() {
					var value = $(this).data("option");

					// todo: this seems to be called twice for no obvious reason

					//if ($(this).is(":modifier(disabled)")) return;

					select.val(value);
					triggerEvent(select, "change")
				});

				select.on("change", function() {
					var value = select.val();

					if (!value) return;

					setActiveValue(value);
				});

				function render() {
					select.find("option").each(function() {
						var node = $(this),
							select = node.parent(),
							attributeId = select.prop('id').replace(/[a-z]*/, ''),
							value = node.prop('value'),
							option = {
								value: value,
								label: node.html(),
								tmpl: node.data('tmpl')
							};

						if (!value) return;

						var configOptions = spConfig.config.attributes[attributeId].options;

						for (var i = 0, j = configOptions.length; i < j; i++) {
							if (configOptions[i].id == option.value) {
								if (configOptions[i].allowedProducts.length == 1
									&& _.get(spConfig, '_configurable.simples')
								) {
									for (var k in spConfig._configurable.simples) {
										if (k == configOptions[i].allowedProducts[0]) {
											option.qty = spConfig._configurable.simples[k].qty;
											break;
										}
									}
								}
								break;
							}
						}

						_.forEach(spConfig.dataProviders, function(provider) {
							if ("function" == typeof provider.provideSelectOption) {
								provider.provideSelectOption(option, select, attributeData.id);
							}
						});

						options.push(option);
					});

					function done() {
						element.bemAddModifier("rendered");
						displayNode
							.empty()
							.html(template({
								options: options,
								attribute: attributeData,
								attributeExtension: attributeExtension
							}));
					}

					if (spConfig.dataProviders.length) {
						+function() {
							var i = 0, j = spConfig.dataProviders.length;

							function next(result) {
								var provider;

								options = result;

								if (i >= j) {
									done();
									return;
								}

								provider = spConfig.dataProviders[i];
								i++;

								if ("function" == typeof provider.sortSelectOptions) {
									options = provider.sortSelectOptions(options, attributeId);

									if ("object" === typeof options && "then" in options) { // promise
										options.then(next);
									} else {
										next(options);
									}

									return;
								}

								next(options);
							}

							next(options);
						}();
					} else {
						done();
					}
				}

				render();
				select.on("attribute/fillSelect", function() {
					options = [];
					render();
				});
			}

			if (value) {
				setActiveValue(value);
			}
		},

		initEngravingOption: function() {
			var block = $('[data-engraving-block]');

			if (block.length) {
				var showLink = block.find('[data-show-engraving-field]'),
					priceNotice = block.parents('dl').find('.price-notice'),
					inputBox = block.find('.input-box'),
					input = inputBox.find('input[type="text"]'),
					maxCharactersBlock = block.find('[data-max-characters]'),
					previewBlock = block.find('[data-engraving-preview]'),
					saveEngravingButton = block.find('[data-save-engraving]'),
					engravingNoticeBlock = block.find('[data-engraving-notice]'),
					savedEngravingBlock = block.find('[data-saved-engraving]'),
					maxCharacters = maxCharactersBlock.data('max-characters'),
					closeButton = $('<a href="#">&times;</a>').hide();


				closeButton.on('click', function (e) {
					e.preventDefault();
					closeButton.hide();
					inputBox.hide();
					showLink.show();
					savedEngravingBlock.hide();
					savedEngravingBlock.html('');
					previewBlock.html('');
					input.val('');
					maxCharactersBlock.html(maxCharacters);
				});

				input.on('change keyup', function(e){
					var elem = $(this);
					previewBlock.html(elem.val());
					savedEngravingBlock.html(elem.val());
					maxCharactersBlock.html(parseInt(maxCharacters, 10) - elem.val().length)
				});

				priceNotice.append(closeButton);

				showLink.on('click', function(e){
					e.preventDefault();
					closeButton.show();
					inputBox.show();
					showLink.hide();
				});

				saveEngravingButton.on('click', function(e) {
					e.preventDefault();
					if (input.val().length) {
						inputBox.hide();
						closeButton.hide();
						savedEngravingBlock.show();
					}
				});

				savedEngravingBlock.on('click', function(e){
					e.preventDefault();
					inputBox.show();
					closeButton.show();
					savedEngravingBlock.hide();
				});
			}
		}
			
	});
});
(function(root) {
define("bootstrap-rating", ["jquery","bootstrap-tooltip"], function() {
  return (function() {
(function ($, undefined) {
    'use strict';

    var OFFSET = 5;

    function Rating(element, options) {
        this.$input = $(element);
        this.$rating = $('<span></span>').css({
            cursor: 'default'
        }).insertBefore(this.$input);
        // Merge data and parameter options.
        // Those provided as parameter prevail over the data ones.
        this.options = (function (opts) {
            // Sanitize start, stop, step, and fractions.
            // All of them start, stop, and step must be integers.
            opts.start = parseInt(opts.start, 10);
            opts.start = isNaN(opts.start) ? undefined : opts.start;
            // In case we don't have a valid stop rate try to get a reasonable
            // one based on the existence of a valid start rate.
            opts.stop = parseInt(opts.stop, 10);
            opts.stop = isNaN(opts.stop) ?
                    opts.start + OFFSET || undefined : opts.stop;
            // 0 step is ignored.
            opts.step = parseInt(opts.step, 10) || undefined;
            // Symbol fractions and scale (number of significant digits).
            // 0 is ignored and negative numbers are turned to positive.
            opts.fractions = Math.abs(parseInt(opts.fractions, 10)) || undefined;
            opts.scale = Math.abs(parseInt(opts.scale, 10)) || undefined;

            // Extend/Override the default options with those provided either as
            // data attributes or function parameters.
            opts = $.extend({}, $.fn.rating.defaults, opts);
            // Inherit default filled if none is defined for the selected symbol.
            opts.filledSelected = opts.filledSelected || opts.filled;
            return opts;
        }($.extend({}, this.$input.data(), options)));

        this._init();
    };

    Rating.prototype = {
        _init: function () {
            var rating = this,
                    $input = this.$input,
                    $rating = this.$rating;

            var ifEnabled = function (f) {
                return function (e) {
                    // According to the W3C attribute readonly is not allowed on input
                    // elements with type hidden.
                    // Keep readonly prop for legacy but its use should be deprecated.
                    if (!$input.prop('disabled') && !$input.prop('readonly') &&
                            $input.data('readonly') === undefined) {
                        f.call(this, e);
                    }
                }
            };

            // Build the rating control.
            for (var i = 1; i <= this._rateToIndex(this.options.stop); i++) {
                // Create the rating symbol container.
                var $symbol = $('<div class="rating-symbol"></div>').css({
                    display: 'inline-block',
                    position: 'relative'
                });
                // Add background symbol to the symbol container.
                $('<div class="rating-symbol-background ' + this.options.empty + '"></div>')
                        .appendTo($symbol);
                // Add foreground symbol to the symbol container.
                // The filled icon is wrapped with a div to allow fractional selection.
                $('<div class="rating-symbol-foreground"></div>')
                        .append('<span class="' + this.options.filled + '"></span>')
                        .css({
                            display: 'inline-block',
                            position: 'absolute',
                            overflow: 'hidden',
                            left: 0,
                            // Overspecify right and left to 0 and let the container direction
                            // decide which one is going to take precedence according to the
                            // ltr/rtl direction.
                            // (https://developer.mozilla.org/en-US/docs/Web/CSS/right)
                            // When both the right CSS property and the left CSS property are
                            // defined, the position of the element is overspecified. In that
                            // case, the left value has precedence when the container is
                            // left-to-right (that is that the right computed value is set to
                            // -left), and the right value has precedence when the container is
                            // right-to-left (that is that the left computed value is set to
                            // -right).
                            right: 0,
                            width: 0
                        }).appendTo($symbol);
                $rating.append($symbol);
                this.options.extendSymbol.call($symbol, this._indexToRate(i));
            }
            // Initialize the rating control with the associated input value rate.
            this._updateRate($input.val());

            // Keep rating control and its associated input in sync.
            $input
                    .on('change', function () {
                        rating._updateRate($(this).val());
                    });

            var fractionalIndex = function (e) {
                var $symbol = $(e.currentTarget);
                // Calculate the distance from the mouse pointer to the origin of the
                // symbol. We need to be careful with the CSS direction. If we are
                // right-to-left then the symbol starts at the right. So we have to add
                // the symbol width to the left offset to get the CSS rigth position.
                var x = Math.abs((e.pageX || e.originalEvent.touches[0].pageX) -
                        (($symbol.css('direction') === 'rtl' && $symbol.width()) +
                                $symbol.offset().left));

                // NOTE: When the mouse pointer is close to the left side of the symbol
                // a negative x is returned. Probably some precision error in the
                // calculation.
                // x should never be less than 0 because this would mean that we are in
                // the previous symbol.
                x = x > 0 ? x : rating.options.scale * 0.1;
                return $symbol.index() + x / $symbol.width();
            };
            // Keep the current highlighted index (fractional or not).
            var index;
            $rating
                    .on('mousedown touchstart', '.rating-symbol', ifEnabled(function (e) {
                        // Set input 'trigger' the change event.
                        $input.val(rating._indexToRate(fractionalIndex(e))).change();
                    }))
                    .on('mousemove touchmove', '.rating-symbol', ifEnabled(function (e) {
                        var current = rating._roundToFraction(fractionalIndex(e));
                        if (current !== index) {
                            // Trigger pseudo rate leave event if the mouse pointer is not
                            // leaving from another symbol (mouseleave).
                            if (index !== undefined) $(this).trigger('rating.rateleave');
                            // Update index and trigger rate enter event.
                            index = current;
                            $(this).trigger('rating.rateenter', [rating._indexToRate(index)]);
                        }
                        // Fill the symbols as fractions chunks.
                        rating._fillUntil(current);
                    }))
                    .on('mouseleave touchend', '.rating-symbol', ifEnabled(function () {
                        // When a symbol is left, reset index and trigger rate leave event.
                        index = undefined;
                        $(this).trigger('rating.rateleave');
                        // Restore on hover out.
                        rating._fillUntil(rating._rateToIndex(parseFloat($input.val())));
                    }));

        },
        // Fill rating symbols until index.
        _fillUntil: function (index) {
            var $rating = this.$rating;
            // Get the index of the last whole symbol.
            var i = Math.floor(index);
            // Hide completely hidden symbols background.
            $rating.find('.rating-symbol-background')
                    .css('visibility', 'visible')
                    .slice(0, i).css('visibility', 'hidden');
            var $rates = $rating.find('.rating-symbol-foreground');
            // Reset foreground
            $rates.width(0);
            // Fill all the foreground symbols up to the selected one.
            $rates.slice(0, i).width('auto')
                    .find('span').attr('class', this.options.filled);
            // Amend selected symbol.
            $rates.eq(index % 1 ? i : i - 1)
                    .find('span').attr('class', this.options.filledSelected);
            // Partially fill the fractional one.
            $rates.eq(i).width(index % 1 * 100 + '%');
        },
        // Calculate the rate of an index according the the start and step.
        _indexToRate: function (index) {
            return this.options.start + Math.floor(index) * this.options.step +
                    this.options.step * this._roundToFraction(index % 1);
        },
        // Calculate the corresponding index for a rate.
        _rateToIndex: function (rate) {
            return (rate - this.options.start) / this.options.step;
        },
        // Round index to the configured opts.fractions.
        _roundToFraction: function (index) {
            // Get the closest top fraction.
            var fraction = Math.ceil(index % 1 * this.options.fractions) / this.options.fractions;
            // Truncate decimal trying to avoid float precission issues.
            var p = Math.pow(10, this.options.scale);
            return Math.floor(index) + Math.floor(fraction * p) / p;
        },
        // Check the rate is in the proper range [start..stop].
        _contains: function (rate) {
            var start = this.options.step > 0 ? this.options.start : this.options.stop;
            var stop = this.options.step > 0 ? this.options.stop : this.options.start;
            return start <= rate && rate <= stop;
        },
        // Update empty and filled rating symbols according to a rate.
        _updateRate: function (rate) {
            var value = parseFloat(rate);
            if (this._contains(value)) {
                this._fillUntil(this._rateToIndex(value));
                this.$input.val(value);
            } else if (rate === '') {
                this._fillUntil(0);
                this.$input.val('');
            }
        },
        rate: function (value) {
            if (value === undefined) {
                return this.$input.val();
            }
            this._updateRate(value);
        }
    };

    $.fn.rating = function (options) {
        var args = Array.prototype.slice.call(arguments, 1),
                result;
        this.each(function () {
            var $input = $(this);
            var rating = $input.data('rating');
            if (!rating) {
                $input.data('rating', (rating = new Rating(this, options)));
            }
            // Underscore are used for private methods.
            if (typeof options === 'string' && options[0] !== '_') {
                result = rating[options].apply(rating, args);
            }
        });
        return result !== undefined ? result : this;
    };

    // Plugin defaults.
    $.fn.rating.defaults = {
        filled: 'glyphicon glyphicon-star',
        filledSelected: undefined,
        empty: 'glyphicon glyphicon-star-empty',
        start: 0,
        stop: OFFSET,
        step: 1,
        fractions: 1,
        scale: 3,
        extendSymbol: function (rate) {},
    };

    $(function () {
        $('input.rating').rating();
    });
}(jQuery));


  }).apply(root, arguments);
});
}(this));

define('shoptheme/components/review/form',[
	"jquery",
	"cartware/components/_base",
	"cartware/jquery/bem",
	"text!shoptheme/templates/magnific-popup/default.html",
	"magnific-popup",
	"bootstrap-rating"
], function(
	$,
	Component,
	BEM,
	PopupHtml
) {
	var _popupTemplate = null;

	return Component.create({
		block:			null,
		ratings:		null,
        submit:         null,

		$init: function() {
			this.block = BEM.block("review-form");
			this.ratings = this.block.getElements("rating");
			this.submit = this.block.getElements("submit");
			var agreementBlocks = this.block.getElements('review-agreement');

			var $tooltips = [Translator.translate('Bad'), Translator.translate('OK'), Translator.translate('Average'), Translator.translate('Good'), Translator.translate('Excellent')];
			var minValue = parseInt(this.ratings.data('min')),
                maxValue = parseInt(this.ratings.data('max'));
			this.ratings.rating({
				start: minValue - 1,
				stop:  maxValue,
                extendSymbol: function (rate) {
                    $(this).tooltip({
                        container: 'body',
                        placement: 'top',
                        title: $tooltips[rate-1]
                    });
                }
            });

			var self = this;

			$.each(agreementBlocks, function(){
				var block = BEM.block(this),
					link = block.getElements('agreement-link'),
					content = block.getElements('agreement-tooltip-inner');

				link.on('click', function(e) {
					e.preventDefault();
					e.stopPropagation();

					var title = '';
					var message = content.html();
					self._showMessage(title, message);
				});
			});
		},
		_renderPopup: function(data) {
			if (null === _popupTemplate) {
				_popupTemplate = _.template(PopupHtml);
			}
			return _popupTemplate(data);
		},
		_showMessage: function(title, message) {
			$.magnificPopup.open({
				items: {
					src: this._renderPopup({
						title: title,
						body: message
					}),
					type: 'inline'
				}
			});
		}
	});
});
window.brodos = window.brodos || {};
window.storeShip = window.storeShip || {};
brodos.storeShip = brodos.storeShip || {};
storeShip = storeShip || {};
var currentUrl = window.location.href;
var splitedUrl = currentUrl.split("/");
brodos.storeShip.path = splitedUrl[0]+'//www.storeship.com/storeship';
storeShip.path = splitedUrl[0]+'//www.storeship.com/storeship';
function render(id, content) {
    content = content.substring(1, content.length - 1);
    document.getElementById(id).innerHTML = content;
    var autoCompleteId = "_storeship_autocomplete_"+id;
    if(document.getElementById(autoCompleteId)){
        new google.maps.places.Autocomplete(document.getElementById(autoCompleteId));
    }
}
brodos.storeShip.appendJS = function(url,patternToCheck){
    if(patternToCheck){
        var scriptTags = document.getElementsByTagName('script');
        for(scriptTag in scriptTags){
            var scriptTagObj =scriptTags[scriptTag];		
            if(scriptTagObj.src!=undefined){
                if(patternToCheck.test(scriptTagObj.src)){
                    return;
                }
            }
        }
    }
    var head = document.getElementsByTagName("head")[0];
    var script = document.createElement("script");
    script.type = "text/javascript";
    script.src = url;    
    head.appendChild(script);
};
brodos.storeShip.order = function(options) {
    var query = brodos.storeShip.toQueryString(options); 
    brodos.storeShip.param = query;
    var custombutton_url = brodos.storeShip.path + '/custombutton/customwebshopbutton?' + query;
    brodos.storeShip.appendJS(custombutton_url,'');
    if(options.location!=undefined && options.location=="true"){
        var customlocation_url = brodos.storeShip.path+'/js/libraries/integration/customLocation.js';        
        var patterToCheck = /\/js\/libraries\/integration\/customLocation.js/;
        brodos.storeShip.appendJS(customlocation_url,patterToCheck);
    }    
};
brodos.storeShip.toQueryString = function(object) {
    var paramObjects = [], params = [], i, j, ln, paramObject, value;
    for (i in object) {
        if (object.hasOwnProperty(i)) {
            paramObjects = paramObjects.concat({name: i, value: object[i]});
        }
    }
    for (j = 0, ln = paramObjects.length; j < ln; j++) {
        paramObject = paramObjects[j];
        value = paramObject.value;
        params.push(encodeURIComponent(paramObject.name) + '=' + encodeURIComponent(String(value)));
    }
    for (j = 0, ln = object.length; j < ln; j++) {
        params.push(encodeURIComponent(object[j].name) + '=' + encodeURIComponent(String(value)));
    }
    params.push('ref=' + encodeURIComponent(location.href));
    return params.join('&');
};
storeShip.button = brodos.storeShip.order;
storeShip.toQueryString = brodos.storeShip.toQueryString;
define("store-ship", function(){});

define('shoptheme/components/product/view/store-ship',[
	"jquery",
	"lodash",
	"cartware/components/Registry",
	"cartware/components/_base",
	"cartware/jquery/bem",
	"store-ship"
], function(
	$,
	_,
	Registry,
	Component,
	BEM
) {
	var block = BEM.block('store-ship'),
		parentBlock = block.parent().parent(),
		productSku = block.data('product-sku'), // test data: '4250366837079'
		childSkuMap = null,
		webshop = block.data('webshop'),
		country = block.data('country'),
		language = $('html').prop('lang'),
		containerId = 'storeshipbtn',
		container = $('#'+containerId)[0],
		buttonLabel = block.data('button-label'),
		observer = null;

	return Component.create({
		$init: function() {
			if (this._isEnabled() === false) {
				parentBlock.remove();
				return;
			}

			Registry().retrieve("product.view.configurable.spConfig", _.bind(this._initializeConfigurableOptions, this));
			if ($('html').hasClass('ie8') === false) {
				this._observeButtonMutations();
			}
			this._initializeStoreShipButton(productSku);
			$(document).on('product/changeSimple', _.bind(this._onChangeSimple, this));
		},
		_initializeConfigurableOptions: function(options) {
			childSkuMap = options.config.id_to_sku;
		},
		_initializeStoreShipButton: function(sku) {
			storeShip.button({
				gtin: sku,
				webshop: webshop,
				template: 'storelocator',
				language: language,
				country: country,
				id: containerId,
				location: 'true'
			});
		},
		_observeButtonMutations: function() {
			var self = this;
			if (observer !== null) observer.disconnect();
			observer = new MutationObserver(function(mutations) {
					mutations.forEach(function(mutation) {
						var newNodes = mutation.addedNodes;
						if(newNodes !== null) {
							self._onChangeButton();
						}
					});
				});
			observer.observe(container, { childList: true });
		},
		_onChangeButton: function() {
			var button = $('input[type="submit"]', container);
			if (button.length > 0) {
				parentBlock.fadeIn(300);
				$('input[type="submit"]', container).val(buttonLabel);
			} else {
				parentBlock.fadeOut(300);
			}
		},
		_onChangeSimple: function(e, simple) {
			this._initializeStoreShipButton((simple !== null) ? childSkuMap[simple.id] : productSku);
		},
		_isEnabled: function() {
			if (block.length == 0) return false;
			return _.some(gigaset.handles, function(handle) {
				return _.startsWith(handle, 'STORE_de_');
			});
		},
		_ie8Repaint: function() {
			element.html(element.html().replace('>', '>'));
		},
		destructor: function() {
			if (observer !== null) {
				observer.disconnect();
			}
		}
	});
});
(function(root) {
define("shariff", ["jquery"], function() {
  return (function() {
/*!
 * shariff - v2.0.4 - Mon, 06 Nov 2017 13:42:53 GMT
 * https://github.com/heiseonline/shariff
 * Copyright (c) 2017 Ines Pauer, Philipp Busse, Sebastian Hilbig, Erich Kramer, Deniz Sesli, Alexander Buch
 * Licensed under the MIT license
 */
!function(e){function t(n){if(r[n])return r[n].exports;var a=r[n]={i:n,l:!1,exports:{}};return e[n].call(a.exports,a,a.exports,t),a.l=!0,a.exports}var r={};t.m=e,t.c=r,t.d=function(e,r,n){t.o(e,r)||Object.defineProperty(e,r,{configurable:!1,enumerable:!0,get:n})},t.n=function(e){var r=e&&e.__esModule?function(){return e.default}:function(){return e};return t.d(r,"a",r),r},t.o=function(e,t){return Object.prototype.hasOwnProperty.call(e,t)},t.p="",t(t.s=2)}([function(e,t,r){"use strict";function n(){this.protocol=null,this.slashes=null,this.auth=null,this.host=null,this.port=null,this.hostname=null,this.hash=null,this.search=null,this.query=null,this.pathname=null,this.path=null,this.href=null}function a(e,t,r){if(e&&h.isObject(e)&&e instanceof n)return e;var a=new n;return a.parse(e,t,r),a}function o(e){return h.isString(e)&&(e=a(e)),e instanceof n?e.format():n.prototype.format.call(e)}function i(e,t){return a(e,!1,!0).resolve(t)}function s(e,t){return e?a(e,!1,!0).resolveObject(t):t}var l=r(9),h=r(11);t.parse=a,t.resolve=i,t.resolveObject=s,t.format=o,t.Url=n;var p=/^([a-z0-9.+-]+:)/i,u=/:[0-9]*$/,c=/^(\/\/?(?!\/)[^\?\s]*)(\?[^\s]*)?$/,d=["<",">",'"',"`"," ","\r","\n","\t"],f=["{","}","|","\\","^","`"].concat(d),m=["'"].concat(f),g=["%","/","?",";","#"].concat(m),v=["/","?","#"],b=/^[+a-z0-9A-Z_-]{0,63}$/,k=/^([+a-z0-9A-Z_-]{0,63})(.*)$/,y={javascript:!0,"javascript:":!0},j={javascript:!0,"javascript:":!0},T={http:!0,https:!0,ftp:!0,gopher:!0,file:!0,"http:":!0,"https:":!0,"ftp:":!0,"gopher:":!0,"file:":!0},w=r(12);n.prototype.parse=function(e,t,r){if(!h.isString(e))throw new TypeError("Parameter 'url' must be a string, not "+typeof e);var n=e.indexOf("?"),a=-1!==n&&n<e.indexOf("#")?"?":"#",o=e.split(a),i=/\\/g;o[0]=o[0].replace(i,"/"),e=o.join(a);var s=e;if(s=s.trim(),!r&&1===e.split("#").length){var u=c.exec(s);if(u)return this.path=s,this.href=s,this.pathname=u[1],u[2]?(this.search=u[2],this.query=t?w.parse(this.search.substr(1)):this.search.substr(1)):t&&(this.search="",this.query={}),this}var d=p.exec(s);if(d){d=d[0];var f=d.toLowerCase();this.protocol=f,s=s.substr(d.length)}if(r||d||s.match(/^\/\/[^@\/]+@[^@\/]+/)){var z="//"===s.substr(0,2);!z||d&&j[d]||(s=s.substr(2),this.slashes=!0)}if(!j[d]&&(z||d&&!T[d])){for(var U=-1,x=0;x<v.length;x++){var C=s.indexOf(v[x]);-1!==C&&(-1===U||C<U)&&(U=C)}var I,R;R=-1===U?s.lastIndexOf("@"):s.lastIndexOf("@",U),-1!==R&&(I=s.slice(0,R),s=s.slice(R+1),this.auth=decodeURIComponent(I)),U=-1;for(var x=0;x<g.length;x++){var C=s.indexOf(g[x]);-1!==C&&(-1===U||C<U)&&(U=C)}-1===U&&(U=s.length),this.host=s.slice(0,U),s=s.slice(U),this.parseHost(),this.hostname=this.hostname||"";var P="["===this.hostname[0]&&"]"===this.hostname[this.hostname.length-1];if(!P)for(var L=this.hostname.split(/\./),x=0,O=L.length;x<O;x++){var A=L[x];if(A&&!A.match(b)){for(var D="",S=0,q=A.length;S<q;S++)A.charCodeAt(S)>127?D+="x":D+=A[S];if(!D.match(b)){var N=L.slice(0,x),F=L.slice(x+1),G=A.match(k);G&&(N.push(G[1]),F.unshift(G[2])),F.length&&(s="/"+F.join(".")+s),this.hostname=N.join(".");break}}}this.hostname.length>255?this.hostname="":this.hostname=this.hostname.toLowerCase(),P||(this.hostname=l.toASCII(this.hostname));var W=this.port?":"+this.port:"",_=this.hostname||"";this.host=_+W,this.href+=this.host,P&&(this.hostname=this.hostname.substr(1,this.hostname.length-2),"/"!==s[0]&&(s="/"+s))}if(!y[f])for(var x=0,O=m.length;x<O;x++){var M=m[x];if(-1!==s.indexOf(M)){var B=encodeURIComponent(M);B===M&&(B=escape(M)),s=s.split(M).join(B)}}var E=s.indexOf("#");-1!==E&&(this.hash=s.substr(E),s=s.slice(0,E));var J=s.indexOf("?");if(-1!==J?(this.search=s.substr(J),this.query=s.substr(J+1),t&&(this.query=w.parse(this.query)),s=s.slice(0,J)):t&&(this.search="",this.query={}),s&&(this.pathname=s),T[f]&&this.hostname&&!this.pathname&&(this.pathname="/"),this.pathname||this.search){var W=this.pathname||"",Z=this.search||"";this.path=W+Z}return this.href=this.format(),this},n.prototype.format=function(){var e=this.auth||"";e&&(e=encodeURIComponent(e),e=e.replace(/%3A/i,":"),e+="@");var t=this.protocol||"",r=this.pathname||"",n=this.hash||"",a=!1,o="";this.host?a=e+this.host:this.hostname&&(a=e+(-1===this.hostname.indexOf(":")?this.hostname:"["+this.hostname+"]"),this.port&&(a+=":"+this.port)),this.query&&h.isObject(this.query)&&Object.keys(this.query).length&&(o=w.stringify(this.query));var i=this.search||o&&"?"+o||"";return t&&":"!==t.substr(-1)&&(t+=":"),this.slashes||(!t||T[t])&&!1!==a?(a="//"+(a||""),r&&"/"!==r.charAt(0)&&(r="/"+r)):a||(a=""),n&&"#"!==n.charAt(0)&&(n="#"+n),i&&"?"!==i.charAt(0)&&(i="?"+i),r=r.replace(/[?#]/g,function(e){return encodeURIComponent(e)}),i=i.replace("#","%23"),t+a+r+i+n},n.prototype.resolve=function(e){return this.resolveObject(a(e,!1,!0)).format()},n.prototype.resolveObject=function(e){if(h.isString(e)){var t=new n;t.parse(e,!1,!0),e=t}for(var r=new n,a=Object.keys(this),o=0;o<a.length;o++){var i=a[o];r[i]=this[i]}if(r.hash=e.hash,""===e.href)return r.href=r.format(),r;if(e.slashes&&!e.protocol){for(var s=Object.keys(e),l=0;l<s.length;l++){var p=s[l];"protocol"!==p&&(r[p]=e[p])}return T[r.protocol]&&r.hostname&&!r.pathname&&(r.path=r.pathname="/"),r.href=r.format(),r}if(e.protocol&&e.protocol!==r.protocol){if(!T[e.protocol]){for(var u=Object.keys(e),c=0;c<u.length;c++){var d=u[c];r[d]=e[d]}return r.href=r.format(),r}if(r.protocol=e.protocol,e.host||j[e.protocol])r.pathname=e.pathname;else{for(var f=(e.pathname||"").split("/");f.length&&!(e.host=f.shift()););e.host||(e.host=""),e.hostname||(e.hostname=""),""!==f[0]&&f.unshift(""),f.length<2&&f.unshift(""),r.pathname=f.join("/")}if(r.search=e.search,r.query=e.query,r.host=e.host||"",r.auth=e.auth,r.hostname=e.hostname||e.host,r.port=e.port,r.pathname||r.search){var m=r.pathname||"",g=r.search||"";r.path=m+g}return r.slashes=r.slashes||e.slashes,r.href=r.format(),r}var v=r.pathname&&"/"===r.pathname.charAt(0),b=e.host||e.pathname&&"/"===e.pathname.charAt(0),k=b||v||r.host&&e.pathname,y=k,w=r.pathname&&r.pathname.split("/")||[],f=e.pathname&&e.pathname.split("/")||[],z=r.protocol&&!T[r.protocol];if(z&&(r.hostname="",r.port=null,r.host&&(""===w[0]?w[0]=r.host:w.unshift(r.host)),r.host="",e.protocol&&(e.hostname=null,e.port=null,e.host&&(""===f[0]?f[0]=e.host:f.unshift(e.host)),e.host=null),k=k&&(""===f[0]||""===w[0])),b)r.host=e.host||""===e.host?e.host:r.host,r.hostname=e.hostname||""===e.hostname?e.hostname:r.hostname,r.search=e.search,r.query=e.query,w=f;else if(f.length)w||(w=[]),w.pop(),w=w.concat(f),r.search=e.search,r.query=e.query;else if(!h.isNullOrUndefined(e.search)){if(z){r.hostname=r.host=w.shift();var U=!!(r.host&&r.host.indexOf("@")>0)&&r.host.split("@");U&&(r.auth=U.shift(),r.host=r.hostname=U.shift())}return r.search=e.search,r.query=e.query,h.isNull(r.pathname)&&h.isNull(r.search)||(r.path=(r.pathname?r.pathname:"")+(r.search?r.search:"")),r.href=r.format(),r}if(!w.length)return r.pathname=null,r.search?r.path="/"+r.search:r.path=null,r.href=r.format(),r;for(var x=w.slice(-1)[0],C=(r.host||e.host||w.length>1)&&("."===x||".."===x)||""===x,I=0,R=w.length;R>=0;R--)x=w[R],"."===x?w.splice(R,1):".."===x?(w.splice(R,1),I++):I&&(w.splice(R,1),I--);if(!k&&!y)for(;I--;I)w.unshift("..");!k||""===w[0]||w[0]&&"/"===w[0].charAt(0)||w.unshift(""),C&&"/"!==w.join("/").substr(-1)&&w.push("");var P=""===w[0]||w[0]&&"/"===w[0].charAt(0);if(z){r.hostname=r.host=P?"":w.length?w.shift():"";var U=!!(r.host&&r.host.indexOf("@")>0)&&r.host.split("@");U&&(r.auth=U.shift(),r.host=r.hostname=U.shift())}return k=k||r.host&&w.length,k&&!P&&w.unshift(""),w.length?r.pathname=w.join("/"):(r.pathname=null,r.path=null),h.isNull(r.pathname)&&h.isNull(r.search)||(r.path=(r.pathname?r.pathname:"")+(r.search?r.search:"")),r.auth=e.auth||r.auth,r.slashes=r.slashes||e.slashes,r.href=r.format(),r},n.prototype.parseHost=function(){var e=this.host,t=u.exec(e);t&&(t=t[0],":"!==t&&(this.port=t.substr(1)),e=e.substr(0,e.length-t.length)),e&&(this.hostname=e)}},function(e,t){var r;r=function(){return this}();try{r=r||Function("return this")()||(0,eval)("this")}catch(e){"object"==typeof window&&(r=window)}e.exports=r},function(e,t,r){"use strict";r(3),e.exports=r(4)},function(e,t){},function(e,t,r){"use strict";(function(t){function n(e,t){if(!(e instanceof t))throw new TypeError("Cannot call a class as a function")}var a="function"==typeof Symbol&&"symbol"==typeof Symbol.iterator?function(e){return typeof e}:function(e){return e&&"function"==typeof Symbol&&e.constructor===Symbol&&e!==Symbol.prototype?"symbol":typeof e},o=function(){function e(e,t){for(var r=0;r<t.length;r++){var n=t[r];n.enumerable=n.enumerable||!1,n.configurable=!0,"value"in n&&(n.writable=!0),Object.defineProperty(e,n.key,n)}}return function(t,r,n){return r&&e(t.prototype,r),n&&e(t,n),t}}(),i=r(5),s=r(6),l=r(0),h={theme:"color",backendUrl:null,infoUrl:"http://ct.de/-2467514",lang:"de",langFallback:"en",mailUrl:function(){var e=l.parse(this.getURL(),!0);return e.query.view="mail",delete e.search,l.format(e)},mailBody:function(){return this.getURL()},mediaUrl:null,orientation:"horizontal",referrerTrack:null,services:["twitter","facebook","googleplus","info"],title:function(){return i("head title").text()},twitterVia:null,flattrUser:null,flattrCategory:null,url:function(){var e=t.document.location.href,r=i("link[rel=canonical]").attr("href")||this.getMeta("og:url")||"";return r.length>0&&(r.indexOf("http")<0&&(r=t.document.location.protocol+"//"+t.document.location.host+r),e=r),e}},p=function(){function e(t,r){var a=this;n(this,e),this.element=t,i(t).empty(),this.options=i.extend({},h,r,i(t).data()),this.services=Object.keys(s).filter(function(e){return a.isEnabledService(e)}).sort(function(e,t){var r=a.options.services;return r.indexOf(e)-r.indexOf(t)}).map(function(e){return s[e](a)}),this._addButtonList(),null!==this.options.backendUrl&&this.getShares(this._updateCounts.bind(this))}return o(e,[{key:"isEnabledService",value:function(e){return this.options.services.indexOf(e)>-1}},{key:"$socialshareElement",value:function(){return i(this.element)}},{key:"getLocalized",value:function(e,t){if("object"===a(e[t])){if(void 0!==e.name){var r=e.name;if("object"==a(this.options.l10n)&&void 0!==this.options.l10n[r]&&void 0!==this.options.l10n[r][t])return this.options.l10n[r][t]}return void 0===e[t][this.options.lang]?e[t][this.options.langFallback]:e[t][this.options.lang]}if("string"==typeof e[t])return e[t]}},{key:"getMeta",value:function(e){return i('meta[name="'+e+'"],[property="'+e+'"]').attr("content")||""}},{key:"getInfoUrl",value:function(){return this.options.infoUrl}},{key:"getURL",value:function(){return this.getOption("url")}},{key:"getOption",value:function(e){var t=this.options[e];return"function"==typeof t?t.call(this):t}},{key:"getTitle",value:function(){var e=this.getOption("title")||this.getMeta("DC.title"),t=this.getMeta("DC.creator");return e&&t&&(e=e+" - "+t),e}},{key:"getReferrerTrack",value:function(){return this.options.referrerTrack||""}},{key:"getShares",value:function(e){var t=l.parse(this.options.backendUrl,!0);return t.query.url=this.getURL(),delete t.search,i.getJSON(l.format(t),e)}},{key:"_updateCounts",value:function(e,t,r){var n=this;e&&i.each(e,function(e,t){n.isEnabledService(e)&&(t>=1e3&&(t=Math.round(t/1e3)+"k"),i(n.element).find("."+e+" a").append(i("<span/>").addClass("share_count").text(t)))})}},{key:"_addButtonList",value:function(){var e=this,r=i("<ul/>").addClass(["theme-"+this.options.theme,"orientation-"+this.options.orientation,"col-"+this.options.services.length].join(" "));this.services.forEach(function(t){var n=i("<li/>").addClass("shariff-button "+t.name),a=i("<span/>").addClass("share_text").text(e.getLocalized(t,"shareText")),o=i("<a/>").attr("href",t.shareUrl).append(a);void 0!==t.faName&&o.prepend(i("<span/>").addClass("fa "+t.faName)),t.popup?o.attr("data-rel","popup"):t.blank&&o.attr("target","_blank"),o.attr("title",e.getLocalized(t,"title")),o.attr("role","button"),o.attr("aria-label",e.getLocalized(t,"title")),n.append(o),r.append(n)}),r.on("click",'[data-rel="popup"]',function(e){e.preventDefault();var r=i(this).attr("href");if(r.match(/twitter\.com\/intent\/(\w+)/)){var n=t.window;if(n.__twttr&&n.__twttr.widgets&&n.__twttr.widgets.loaded)return}t.window.open(r,"_blank","width=600,height=460")}),this.$socialshareElement().append(r)}}]),e}();e.exports=p,t.Shariff=p,i(function(){i(".shariff").each(function(){this.hasOwnProperty("shariff")||(this.shariff=new p(this))})})}).call(t,r(1))},function(e,t){e.exports=jQuery},function(e,t,r){"use strict";e.exports={addthis:r(7),diaspora:r(8),facebook:r(15),flattr:r(16),googleplus:r(17),info:r(18),linkedin:r(19),mail:r(20),pinterest:r(21),print:r(22),qzone:r(23),reddit:r(24),stumbleupon:r(25),tencent:r(26),threema:r(27),tumblr:r(28),twitter:r(29),weibo:r(30),whatsapp:r(31),xing:r(32)}},function(e,t,r){"use strict";e.exports=function(e){return{popup:!0,shareText:{bg:"cподеляне",da:"del",de:"teilen",en:"share",es:"compartir",fi:"Jaa",fr:"partager",hr:"podijelite",hu:"megosztás",it:"condividi",ja:"共有",ko:"공유하기",nl:"delen",no:"del",pl:"udostępnij",pt:"compartilhar",ro:"partajează",ru:"поделиться",sk:"zdieľať",sl:"deli",sr:"podeli",sv:"dela",tr:"paylaş",zh:"分享"},name:"addthis",faName:"fa-plus",title:{bg:"Сподели в AddThis",da:"Del på AddThis",de:"Bei AddThis teilen",en:"Share on AddThis",es:"Compartir en AddThis",fi:"Jaa AddThisissä",fr:"Partager sur AddThis",hr:"Podijelite na AddThis",hu:"Megosztás AddThisen",it:"Condividi su AddThis",ja:"AddThis上で共有",ko:"AddThis에서 공유하기",nl:"Delen op AddThis",no:"Del på AddThis",pl:"Udostępnij przez AddThis",pt:"Compartilhar no AddThis",ro:"Partajează pe AddThis",ru:"Поделиться на AddThis",sk:"Zdieľať na AddThis",sl:"Deli na AddThis",sr:"Podeli na AddThis",sv:"Dela på AddThis",tr:"AddThis'ta paylaş",zh:"在AddThis上分享"},shareUrl:"http://api.addthis.com/oexchange/0.8/offer?url="+encodeURIComponent(e.getURL())+e.getReferrerTrack()}}},function(e,t,r){"use strict";var n=r(0);e.exports=function(e){var t=n.parse("https://share.diasporafoundation.org/",!0);return t.query.url=e.getURL(),t.query.title=e.getTitle(),t.protocol="https",delete t.search,{popup:!0,shareText:{de:"teilen",en:"share",zh:"分享"},name:"diaspora",faName:"fa-asterisk",title:{de:"Bei Diaspora teilen",en:"Share on Diaspora",zh:"分享至Diaspora"},shareUrl:n.format(t)+e.getReferrerTrack()}}},function(e,t,r){(function(e,n){var a;!function(n){function o(e){throw new RangeError(P[e])}function i(e,t){for(var r=e.length,n=[];r--;)n[r]=t(e[r]);return n}function s(e,t){var r=e.split("@"),n="";return r.length>1&&(n=r[0]+"@",e=r[1]),e=e.replace(R,"."),n+i(e.split("."),t).join(".")}function l(e){for(var t,r,n=[],a=0,o=e.length;a<o;)t=e.charCodeAt(a++),t>=55296&&t<=56319&&a<o?(r=e.charCodeAt(a++),56320==(64512&r)?n.push(((1023&t)<<10)+(1023&r)+65536):(n.push(t),a--)):n.push(t);return n}function h(e){return i(e,function(e){var t="";return e>65535&&(e-=65536,t+=A(e>>>10&1023|55296),e=56320|1023&e),t+=A(e)}).join("")}function p(e){return e-48<10?e-22:e-65<26?e-65:e-97<26?e-97:k}function u(e,t){return e+22+75*(e<26)-((0!=t)<<5)}function c(e,t,r){var n=0;for(e=r?O(e/w):e>>1,e+=O(e/t);e>L*j>>1;n+=k)e=O(e/L);return O(n+(L+1)*e/(e+T))}function d(e){var t,r,n,a,i,s,l,u,d,f,m=[],g=e.length,v=0,T=U,w=z;for(r=e.lastIndexOf(x),r<0&&(r=0),n=0;n<r;++n)e.charCodeAt(n)>=128&&o("not-basic"),m.push(e.charCodeAt(n));for(a=r>0?r+1:0;a<g;){for(i=v,s=1,l=k;a>=g&&o("invalid-input"),u=p(e.charCodeAt(a++)),(u>=k||u>O((b-v)/s))&&o("overflow"),v+=u*s,d=l<=w?y:l>=w+j?j:l-w,!(u<d);l+=k)f=k-d,s>O(b/f)&&o("overflow"),s*=f;t=m.length+1,w=c(v-i,t,0==i),O(v/t)>b-T&&o("overflow"),T+=O(v/t),v%=t,m.splice(v++,0,T)}return h(m)}function f(e){var t,r,n,a,i,s,h,p,d,f,m,g,v,T,w,C=[];for(e=l(e),g=e.length,t=U,r=0,i=z,s=0;s<g;++s)(m=e[s])<128&&C.push(A(m));for(n=a=C.length,a&&C.push(x);n<g;){for(h=b,s=0;s<g;++s)(m=e[s])>=t&&m<h&&(h=m);for(v=n+1,h-t>O((b-r)/v)&&o("overflow"),r+=(h-t)*v,t=h,s=0;s<g;++s)if(m=e[s],m<t&&++r>b&&o("overflow"),m==t){for(p=r,d=k;f=d<=i?y:d>=i+j?j:d-i,!(p<f);d+=k)w=p-f,T=k-f,C.push(A(u(f+w%T,0))),p=O(w/T);C.push(A(u(p,0))),i=c(r,v,n==a),r=0,++n}++r,++t}return C.join("")}function m(e){return s(e,function(e){return C.test(e)?d(e.slice(4).toLowerCase()):e})}function g(e){return s(e,function(e){return I.test(e)?"xn--"+f(e):e})}var v,b=("object"==typeof t&&t&&t.nodeType,"object"==typeof e&&e&&e.nodeType,2147483647),k=36,y=1,j=26,T=38,w=700,z=72,U=128,x="-",C=/^xn--/,I=/[^\x20-\x7E]/,R=/[\x2E\u3002\uFF0E\uFF61]/g,P={overflow:"Overflow: input needs wider integers to process","not-basic":"Illegal input >= 0x80 (not a basic code point)","invalid-input":"Invalid input"},L=k-y,O=Math.floor,A=String.fromCharCode;v={version:"1.4.1",ucs2:{decode:l,encode:h},decode:d,encode:f,toASCII:g,toUnicode:m},void 0!==(a=function(){return v}.call(t,r,t,e))&&(e.exports=a)}()}).call(t,r(10)(e),r(1))},function(e,t){e.exports=function(e){return e.webpackPolyfill||(e.deprecate=function(){},e.paths=[],e.children||(e.children=[]),Object.defineProperty(e,"loaded",{enumerable:!0,get:function(){return e.l}}),Object.defineProperty(e,"id",{enumerable:!0,get:function(){return e.i}}),e.webpackPolyfill=1),e}},function(e,t,r){"use strict";e.exports={isString:function(e){return"string"==typeof e},isObject:function(e){return"object"==typeof e&&null!==e},isNull:function(e){return null===e},isNullOrUndefined:function(e){return null==e}}},function(e,t,r){"use strict";t.decode=t.parse=r(13),t.encode=t.stringify=r(14)},function(e,t,r){"use strict";function n(e,t){return Object.prototype.hasOwnProperty.call(e,t)}e.exports=function(e,t,r,o){t=t||"&",r=r||"=";var i={};if("string"!=typeof e||0===e.length)return i;var s=/\+/g;e=e.split(t);var l=1e3;o&&"number"==typeof o.maxKeys&&(l=o.maxKeys);var h=e.length;l>0&&h>l&&(h=l);for(var p=0;p<h;++p){var u,c,d,f,m=e[p].replace(s,"%20"),g=m.indexOf(r);g>=0?(u=m.substr(0,g),c=m.substr(g+1)):(u=m,c=""),d=decodeURIComponent(u),f=decodeURIComponent(c),n(i,d)?a(i[d])?i[d].push(f):i[d]=[i[d],f]:i[d]=f}return i};var a=Array.isArray||function(e){return"[object Array]"===Object.prototype.toString.call(e)}},function(e,t,r){"use strict";function n(e,t){if(e.map)return e.map(t);for(var r=[],n=0;n<e.length;n++)r.push(t(e[n],n));return r}var a=function(e){switch(typeof e){case"string":return e;case"boolean":return e?"true":"false";case"number":return isFinite(e)?e:"";default:return""}};e.exports=function(e,t,r,s){return t=t||"&",r=r||"=",null===e&&(e=void 0),"object"==typeof e?n(i(e),function(i){var s=encodeURIComponent(a(i))+r;return o(e[i])?n(e[i],function(e){return s+encodeURIComponent(a(e))}).join(t):s+encodeURIComponent(a(e[i]))}).join(t):s?encodeURIComponent(a(s))+r+encodeURIComponent(a(e)):""};var o=Array.isArray||function(e){return"[object Array]"===Object.prototype.toString.call(e)},i=Object.keys||function(e){var t=[];for(var r in e)Object.prototype.hasOwnProperty.call(e,r)&&t.push(r);return t}},function(e,t,r){"use strict";e.exports=function(e){return{popup:!0,shareText:{bg:"cподеляне",da:"del",de:"teilen",en:"share",es:"compartir",fi:"Jaa",fr:"partager",hr:"podijelite",hu:"megosztás",it:"condividi",ja:"共有",ko:"공유하기",nl:"delen",no:"del",pl:"udostępnij",pt:"compartilhar",ro:"partajează",ru:"поделиться",sk:"zdieľať",sl:"deli",sr:"podeli",sv:"dela",tr:"paylaş",zh:"分享"},name:"facebook",faName:"fa-facebook",title:{bg:"Сподели във Facebook",da:"Del på Facebook",de:"Bei Facebook teilen",en:"Share on Facebook",es:"Compartir en Facebook",fi:"Jaa Facebookissa",fr:"Partager sur Facebook",hr:"Podijelite na Facebooku",hu:"Megosztás Facebookon",it:"Condividi su Facebook",ja:"フェイスブック上で共有",ko:"페이스북에서 공유하기",nl:"Delen op Facebook",no:"Del på Facebook",pl:"Udostępnij na Facebooku",pt:"Compartilhar no Facebook",ro:"Partajează pe Facebook",ru:"Поделиться на Facebook",sk:"Zdieľať na Facebooku",sl:"Deli na Facebooku",sr:"Podeli na Facebook-u",sv:"Dela på Facebook",tr:"Facebook'ta paylaş",zh:"在Facebook上分享"},shareUrl:"https://www.facebook.com/sharer/sharer.php?u="+encodeURIComponent(e.getURL())+e.getReferrerTrack()}}},function(e,t,r){"use strict";e.exports=function(e){var t=encodeURIComponent(e.getURL()),r=e.getTitle(),n=e.getMeta("description");return{popup:!0,shareText:"Flattr",name:"flattr",faName:"fa-money",title:{de:"Artikel flattrn",en:"Flattr this"},shareUrl:"https://flattr.com/submit/auto?title="+encodeURIComponent(r)+"&description="+encodeURIComponent(n)+"&category="+encodeURIComponent(e.options.flattrCategory||"text")+"&user_id="+encodeURIComponent(e.options.flattrUser)+"&url="+t+e.getReferrerTrack()}}},function(e,t,r){"use strict";e.exports=function(e){return{popup:!0,shareText:{bg:"cподеляне",da:"del",de:"teilen",en:"share",es:"compartir",fi:"Jaa",fr:"partager",hr:"podijelite",hu:"megosztás",it:"condividi",ja:"共有",ko:"공유하기",nl:"delen",no:"del",pl:"udostępnij",pt:"compartilhar",ro:"partajează",ru:"поделиться",sk:"zdieľať",sl:"deli",sr:"podeli",sv:"dela",tr:"paylaş",zh:"分享"},name:"googleplus",faName:"fa-google-plus",title:{bg:"Сподели в Google+",da:"Del på Google+",de:"Bei Google+ teilen",en:"Share on Google+",es:"Compartir en Google+",fi:"Jaa Google+:ssa",fr:"Partager sur Goolge+",hr:"Podijelite na Google+",hu:"Megosztás Google+on",it:"Condividi su Google+",ja:"Google+上で共有",ko:"Google+에서 공유하기",nl:"Delen op Google+",no:"Del på Google+",pl:"Udostępnij na Google+",pt:"Compartilhar no Google+",ro:"Partajează pe Google+",ru:"Поделиться на Google+",sk:"Zdieľať na Google+",sl:"Deli na Google+",sr:"Podeli na Google+",sv:"Dela på Google+",tr:"Google+'da paylaş",zh:"在Google+上分享"},shareUrl:"https://plus.google.com/share?url="+encodeURIComponent(e.getURL())+e.getReferrerTrack()}}},function(e,t,r){"use strict";e.exports=function(e){return{blank:!0,popup:!1,shareText:"Info",name:"info",faName:"fa-info",title:{de:"weitere Informationen",en:"more information",es:"más informaciones",fr:"plus d'informations",it:"maggiori informazioni",da:"flere oplysninger",nl:"verdere informatie",zh:"更多信息"},shareUrl:e.getInfoUrl()}}},function(e,t,r){"use strict";e.exports=function(e){var t=encodeURIComponent(e.getURL()),r=encodeURIComponent(e.getTitle());return{popup:!0,shareText:{de:"mitteilen",en:"share",es:"compartir",fi:"Jaa",fr:"partager",hr:"podijelite",hu:"megosztás",it:"condividi",ja:"シェア",ko:"공유하기",nl:"delen",no:"del",pl:"udostępnij",pt:"compartilhar",ro:"distribuiți",ru:"поделиться",sk:"zdieľať",sl:"deli",sr:"podeli",sv:"dela",tr:"paylaş",zh:"分享"},name:"linkedin",faName:"fa-linkedin",title:{bg:"Сподели в LinkedIn",da:"Del på LinkedIn",de:"Bei LinkedIn teilen",en:"Share on LinkedIn",es:"Compartir en LinkedIn",fi:"Jaa LinkedInissä",fr:"Partager sur LinkedIn",hr:"Podijelite na LinkedIn",hu:"Megosztás LinkedInen",it:"Condividi su LinkedIn",ja:"LinkedIn上で共有",ko:"LinkedIn에서 공유하기",nl:"Delen op LinkedIn",no:"Del på LinkedIn",pl:"Udostępnij przez LinkedIn",pt:"Compartilhar no LinkedIn",ro:"Partajează pe LinkedIn",ru:"Поделиться на LinkedIn",sk:"Zdieľať na LinkedIn",sl:"Deli na LinkedIn",sr:"Podeli na LinkedIn-u",sv:"Dela på LinkedIn",tr:"LinkedIn'ta paylaş",zh:"在LinkedIn上分享"},shareUrl:"https://www.linkedin.com/shareArticle?mini=true&summary="+encodeURIComponent(e.getMeta("description"))+"&title="+r+"&url="+t}}},function(e,t,r){"use strict";e.exports=function(e){var t=e.getOption("mailUrl");return 0===t.indexOf("mailto:")&&(t+="?subject="+encodeURIComponent(e.getTitle()),t+="&body="+encodeURIComponent(e.getOption("mailBody").replace(/\{url\}/i,e.getURL()))),{blank:0===t.indexOf("http"),popup:!1,shareText:{en:"mail",zh:"分享"},name:"mail",faName:"fa-envelope",title:{bg:"Изпрати по имейл",da:"Sende via e-mail",de:"Per E-Mail versenden",en:"Send by email",es:"Enviar por email",fi:"Lähetä sähköpostitse",fr:"Envoyer par courriel",hr:"Pošaljite emailom",hu:"Elküldés e-mailben",it:"Inviare via email",ja:"電子メールで送信",ko:"이메일로 보내기",nl:"Sturen via e-mail",no:"Send via epost",pl:"Wyślij e-mailem",pt:"Enviar por e-mail",ro:"Trimite prin e-mail",ru:"Отправить по эл. почте",sk:"Poslať e-mailom",sl:"Pošlji po elektronski pošti",sr:"Pošalji putem email-a",sv:"Skicka via e-post",tr:"E-posta ile gönder",zh:"通过电子邮件传送"},shareUrl:t}}},function(e,t,r){"use strict";var n=r(0);e.exports=function(e){var t=e.getTitle(),r=e.getMeta("DC.creator");r.length>0&&(t+=" - "+r);var a=e.getOption("mediaUrl");(!a||a.length<=0)&&(a=e.getMeta("og:image"));var o=n.parse("https://www.pinterest.com/pin/create/link/",!0);return o.query.url=e.getURL(),o.query.media=a,o.query.description=t,delete o.search,{popup:!0,shareText:"pin it",name:"pinterest",faName:"fa-pinterest-p",title:{de:"Bei Pinterest pinnen",en:"Pin it on Pinterest",es:"Compartir en Pinterest",fr:"Partager sur Pinterest",it:"Condividi su Pinterest",da:"Del på Pinterest",nl:"Delen op Pinterest",zh:"分享至Pinterest"},shareUrl:n.format(o)+e.getReferrerTrack()}}},function(e,t,r){"use strict";e.exports=function(e){return{name:"print",faName:"fa-print",popup:!1,shareText:{bg:"",da:"",de:"drucken",en:"print",es:"",fi:"",fr:"",hr:"",hu:"",it:"",ja:"",ko:"",nl:"",no:"",pl:"",pt:"",ro:"",ru:"",sk:"",sl:"",sr:"",sv:"",tr:"",zh:""},title:{bg:"",da:"",de:"drucken",en:"print",es:"",fi:"",fr:"",hr:"",hu:"",it:"",ja:"",ko:"",nl:"",no:"",pl:"",pt:"",ro:"",ru:"",sk:"",sl:"",sr:"",sv:"",tr:"",zh:""},shareUrl:"javascript:window.print();"}}},function(e,t,r){"use strict";e.exports=function(e){return{popup:!0,shareText:{bg:"cподеляне",da:"del",de:"teilen",en:"share",es:"compartir",fi:"Jaa",fr:"partager",hr:"podijelite",hu:"megosztás",it:"condividi",ja:"共有",ko:"공유하기",nl:"delen",no:"del",pl:"udostępnij",pt:"compartilhar",ro:"partajează",ru:"поделиться",sk:"zdieľať",sl:"deli",sr:"podeli",sv:"dela",tr:"paylaş",zh:"分享"},name:"qzone",faName:"fa-qq",title:{de:"Bei Qzone teilen",en:"Share on Qzone",zh:"分享至QQ空间"},shareUrl:"http://sns.qzone.qq.com/cgi-bin/qzshare/cgi_qzshare_onekey?url="+encodeURIComponent(e.getURL())+"&title="+e.getTitle()+e.getReferrerTrack()}}},function(e,t,r){"use strict";e.exports=function(e){var t=encodeURIComponent(e.getURL()),r=encodeURIComponent(e.getTitle());return""!==r&&(r="&title="+r),{popup:!0,shareText:{de:"teilen",en:"share",zh:"分享"},name:"reddit",faName:"fa-reddit",title:{de:"Bei Reddit teilen",en:"Share on Reddit",zh:"分享至Reddit"},shareUrl:"https://reddit.com/submit?url="+t+r+e.getReferrerTrack()}}},function(e,t,r){"use strict";e.exports=function(e){var t=encodeURIComponent(e.getURL()),r=encodeURIComponent(e.getTitle());return""!==r&&(r="&title="+r),{popup:!0,shareText:{de:"teilen",en:"share",zh:"分享"},name:"stumbleupon",faName:"fa-stumbleupon",title:{de:"Bei Stumbleupon teilen",en:"Share on Stumbleupon",zh:"分享至Stumbleupon"},shareUrl:"https://www.stumbleupon.com/submit?url="+t+r+e.getReferrerTrack()}}},function(e,t,r){"use strict";e.exports=function(e){return{popup:!0,shareText:{bg:"cподеляне",da:"del",de:"teilen",en:"share",es:"compartir",fi:"Jaa",fr:"partager",hr:"podijelite",hu:"megosztás",it:"condividi",ja:"共有",ko:"공유하기",nl:"delen",no:"del",pl:"udostępnij",pt:"compartilhar",ro:"partajează",ru:"поделиться",sk:"zdieľať",sl:"deli",sr:"podeli",sv:"dela",tr:"paylaş",zh:"分享"},name:"tencent-weibo",faName:"fa-tencent-weibo",title:{de:"Bei tencent weibo teilen",en:"Share on tencent weibo",zh:"分享至腾讯微博"},shareUrl:"http://v.t.qq.com/share/share.php?url="+encodeURIComponent(e.getURL())+"&title="+e.getTitle()+e.getReferrerTrack()}}},function(e,t,r){"use strict";e.exports=function(e){var t=encodeURIComponent(e.getURL()),r=e.getTitle();return{popup:!1,shareText:{bg:"cподеляне",da:"del",de:"teilen",en:"share",es:"compartir",fi:"Jaa",fr:"partager",hr:"podijelite",hu:"megosztás",it:"condividi",ja:"共有",ko:"공유하기",nl:"delen",no:"del",pl:"udostępnij",pt:"compartilhar",ro:"partajează",ru:"поделиться",sk:"zdieľať",sl:"deli",sr:"podeli",sv:"dela",tr:"paylaş",zh:"分享"},name:"threema",faName:"fa-lock",title:{bg:"Сподели в Threema",da:"Del på Threema",de:"Bei Threema teilen",en:"Share on Threema",es:"Compartir en Threema",fi:"Jaa Threemaissä",fr:"Partager sur Threema",hr:"Podijelite na Threema",hu:"Megosztás Threemaen",it:"Condividi su Threema",ja:"Threema上で共有",ko:"Threema에서 공유하기",nl:"Delen op Threema",no:"Del på Threema",pl:"Udostępnij przez Threema",pt:"Compartilhar no Threema",ro:"Partajează pe Threema",ru:"Поделиться на Threema",sk:"Zdieľať na Threema",sl:"Deli na Threema",sr:"Podeli na Threema-u",sv:"Dela på Threema",tr:"Threema'ta paylaş",zh:"在Threema上分享"},shareUrl:"threema://compose?text="+encodeURIComponent(r)+"%20"+t+e.getReferrerTrack()}}},function(e,t,r){"use strict";e.exports=function(e){return{popup:!0,shareText:{bg:"cподеляне",da:"del",de:"teilen",en:"share",es:"compartir",fi:"Jaa",fr:"partager",hr:"podijelite",hu:"megosztás",it:"condividi",ja:"共有",ko:"공유하기",nl:"delen",no:"del",pl:"udostępnij",pt:"compartilhar",ro:"partajează",ru:"поделиться",sk:"zdieľať",sl:"deli",sr:"podeli",sv:"dela",tr:"paylaş",zh:"分享"},name:"tumblr",faName:"fa-tumblr",title:{bg:"Сподели в tumblr",da:"Del på tumblr",de:"Bei tumblr teilen",en:"Share on tumblr",es:"Compartir en tumblr",fi:"Jaa tumblrissä",fr:"Partager sur tumblr",hr:"Podijelite na tumblr",hu:"Megosztás tumblren",it:"Condividi su tumblr",ja:"tumblr上で共有",ko:"tumblr에서 공유하기",nl:"Delen op tumblr",no:"Del på tumblr",pl:"Udostępnij przez tumblr",pt:"Compartilhar no tumblr",ro:"Partajează pe tumblr",ru:"Поделиться на tumblr",sk:"Zdieľať na tumblr",sl:"Deli na tumblr",sr:"Podeli na tumblr-u",sv:"Dela på tumblr",tr:"tumblr'ta paylaş",zh:"在tumblr上分享"},shareUrl:"http://tumblr.com/widgets/share/tool?canonicalUrl="+encodeURIComponent(e.getURL())+e.getReferrerTrack()}}},function(e,t,r){"use strict";var n=r(0),a=function(e,t){var r=document.createElement("div");r.innerHTML=e;var n=r.textContent;if(n.length<=t)return e;var a=n.substring(0,t-1).lastIndexOf(" ");return n=n.substring(0,a)+"…"};e.exports=function(e){var t=n.parse("https://twitter.com/intent/tweet",!0),r=e.getTitle();return t.query.text=a(r,120),t.query.url=e.getURL(),null!==e.options.twitterVia&&(t.query.via=e.options.twitterVia),delete t.search,{popup:!0,shareText:{en:"tweet",zh:"分享"},name:"twitter",faName:"fa-twitter",title:{bg:"Сподели в Twitter",da:"Del på Twitter",de:"Bei Twitter teilen",en:"Share on Twitter",es:"Compartir en Twitter",fi:"Jaa Twitterissä",fr:"Partager sur Twitter",hr:"Podijelite na Twitteru",hu:"Megosztás Twitteren",it:"Condividi su Twitter",ja:"ツイッター上で共有",ko:"트위터에서 공유하기",nl:"Delen op Twitter",no:"Del på Twitter",pl:"Udostępnij na Twitterze",pt:"Compartilhar no Twitter",ro:"Partajează pe Twitter",ru:"Поделиться на Twitter",sk:"Zdieľať na Twitteri",sl:"Deli na Twitterju",sr:"Podeli na Twitter-u",sv:"Dela på Twitter",tr:"Twitter'da paylaş",zh:"在Twitter上分享"},shareUrl:n.format(t)+e.getReferrerTrack()}}},function(e,t,r){"use strict";e.exports=function(e){return{popup:!0,shareText:{bg:"cподеляне",da:"del",de:"teilen",en:"share",es:"compartir",fi:"Jaa",fr:"partager",hr:"podijelite",hu:"megosztás",it:"condividi",ja:"共有",ko:"공유하기",nl:"delen",no:"del",pl:"udostępnij",pt:"compartilhar",ro:"partajează",ru:"поделиться",sk:"zdieľať",sl:"deli",sr:"podeli",sv:"dela",tr:"paylaş",zh:"分享"},name:"weibo",faName:"fa-weibo",title:{de:"Bei weibo teilen",en:"Share on weibo",zh:"分享至新浪微博"},shareUrl:"http://service.weibo.com/share/share.php?url="+encodeURIComponent(e.getURL())+"&title="+e.getTitle()+e.getReferrerTrack()}}},function(e,t,r){"use strict";e.exports=function(e){var t=encodeURIComponent(e.getURL()),r=e.getTitle();return{popup:!1,shareText:{bg:"cподеляне",da:"del",de:"teilen",en:"share",es:"compartir",fi:"Jaa",fr:"partager",hr:"podijelite",hu:"megosztás",it:"condividi",ja:"共有",ko:"공유하기",nl:"delen",no:"del",pl:"udostępnij",pt:"compartilhar",ro:"partajează",ru:"поделиться",sk:"zdieľať",sl:"deli",sr:"podeli",sv:"dela",tr:"paylaş",zh:"分享"},name:"whatsapp",faName:"fa-whatsapp",title:{bg:"Сподели в Whatsapp",da:"Del på Whatsapp",de:"Bei Whatsapp teilen",en:"Share on Whatsapp",es:"Compartir en Whatsapp",fi:"Jaa WhatsAppissä",fr:"Partager sur Whatsapp",hr:"Podijelite na Whatsapp",hu:"Megosztás WhatsAppen",it:"Condividi su Whatsapp",ja:"Whatsapp上で共有",ko:"Whatsapp에서 공유하기",nl:"Delen op Whatsapp",no:"Del på Whatsapp",pl:"Udostępnij przez WhatsApp",pt:"Compartilhar no Whatsapp",ro:"Partajează pe Whatsapp",ru:"Поделиться на Whatsapp",sk:"Zdieľať na Whatsapp",sl:"Deli na Whatsapp",sr:"Podeli na WhatsApp-u",sv:"Dela på Whatsapp",tr:"Whatsapp'ta paylaş",zh:"在Whatsapp上分享"},shareUrl:"whatsapp://send?text="+encodeURIComponent(r)+"%20"+t+e.getReferrerTrack()}}},function(e,t,r){"use strict";e.exports=function(e){return{popup:!0,shareText:{de:"teilen",en:"share",es:"compartir",fr:"partager",it:"condividi",da:"del",nl:"delen",zh:"分享"},name:"xing",faName:"fa-xing",title:{de:"Bei XING teilen",en:"Share on XING",es:"Compartir en XING",fr:"Partager sur XING",it:"Condividi su XING",da:"Del på XING",nl:"Delen op XING",zh:"分享至XING"},shareUrl:"https://www.xing.com/social_plugins/share?url="+encodeURIComponent(e.getURL())+e.getReferrerTrack()}}}]);

  }).apply(root, arguments);
});
}(this));

define('shoptheme/components/plugin/shariff',[
	"jquery",
	"cartware/components/_base",
	"shariff"
], function(
	$,
	Component
) {

	return Component.create({
		$init: function () {
			// new Shariff($(".shariff"), {
			// 	orientation: 'horizontal',
			// 	services: ['facebook', 'twitter', 'googleplus'],
			// 	theme: 'grey',
			// 	col: 3
			// });
		}
	});
});
define('shoptheme/components/plugin/multiline-placeholder',[
    "jquery",
    "cartware/components/_base",
    "cartware/jquery/bem"
], function(
        $,
        Component,
        BEM
) {
    return Component.create({
        block:			null,
        textarea:			null,
        submit:		null,
        name:		null,
        summary:		null,

        $init: function () {
            this.block = BEM.block("review-form");
            this.textarea = this.block.getElements("text");
            this.name = this.block.getElements("name");
            this.summary = this.block.getElements("summary");

            var $textarea = this.textarea,
                    text  = $textarea.attr('placeholder'),
                    match = /\r|\n/.exec(text),
                    $fieldName = this.name,
                    $fieldSummary = this.summary;

            if (! match) {
                return;
            }

            $textarea.attr('placeholder', '')
                    .data('placeholder', text)
                    .addClass('active-placeholder')
                    .val(text);

            $textarea.on('focus', function() {
                var currentVal = $textarea.val();
                if ($textarea.data('placeholder') === currentVal) {
                    $textarea.data('placeholder', currentVal)
                            .val('')
                            .removeClass('active-placeholder');
                }
            });

            $textarea.on('blur', function() {
                if ($textarea.val() === '') {
                    $textarea.val($textarea.data('placeholder'))
                            .addClass('active-placeholder');
                }
            });

            //clear value of textarea before validation
            this.submit = this.block.getElements("submit");
            this.submit.click(function() {
               if ($fieldName.val() != '' && $fieldSummary.val() != '' && $textarea.val() == $textarea.data('placeholder')) {
                   $textarea.focus();
               }
            });
        }
    });
});
define('shoptheme/components/review/list',[
	"jquery",
	"cartware/components/_base",
	"cartware/jquery/bem",
	"gigaset/spinner"
], function(
	$,
	Component,
	BEM,
	Spinner
) {
	return Component.create({
		block:			null,
		pagerLinks:     null,
		ajaxControllerUrl:  null,
		productId:      null,

		$init: function() {
			var self = this;
			this.block = BEM.block("review-list");

			this.block.on('click', '.pager li:not(.active)>a', function(e) {
				currentUrl = $(this).prop('href');
				try {
					e.preventDefault();
					var dataBlock = self.block.find('[data-ajax-reviews-url]');
					self.ajaxControllerUrl = dataBlock.data('ajax-reviews-url');
					self.productId = dataBlock.data('ajax-reviews-product-id');

					var listWrapper = self.block.find('.' + BEM.buildElementClassname('review-list','list-wrapper')),
						listListSelector = '.' + BEM.buildElementClassname('review-list', 'list'),
						paginationSelector = '.js-review-pagination',
						ajaxUrl = self.ajaxControllerUrl + 'product/' + self.productId  + '/p/' + self.parseQueryString('p', $(this).prop('href'));

					Spinner["add"](listWrapper);
					$.ajax(ajaxUrl, {
						success: function(res) {
							if (typeof res.reviews !== "undefined") {
								var reviewsBlock = $(res.reviews);

								self.block.find(listListSelector).html(reviewsBlock.find(listListSelector).html());
								self.block.find(paginationSelector).html(reviewsBlock.find(paginationSelector).html());

								self.block.parents('.' + BEM.buildElementClassname('tabs','tab-content')).trigger('gigaset.content.change');
								self.scrollIntoViewIfNeeded(self.block.find(listListSelector));
								Spinner["remove"](listWrapper);
							} else {
								self.fallback(currentUrl);
							}
						},
						error: function() {
							self.fallback(currentUrl);
						},
						cache: true
					});
					return false;
				} catch (e) {
					console.log(e);
					self.fallback(currentUrl);
				}
			});
		},

		parseQueryString: function( name, url ) {
			if (!url) url = location.href;
			name = name.replace(/[\[]/,"\\\[").replace(/[\]]/,"\\\]");
			var regexS = "[\\?&]"+name+"=([^&#]*)";
			var regex = new RegExp( regexS );
			var results = regex.exec( url );
			return results == null ? null : results[1];
		},

		scrollIntoViewIfNeeded: function($target) {
			if ($target.offset()) {
				if ($target.offset().top < jQuery(window).scrollTop()){
					//scroll up
					$('html,body').animate({scrollTop: $target.offset().top});
				}
				else if ($target.offset().top + $target.outerHeight() >
					$(window).scrollTop() + (
						window.innerHeight || document.documentElement.clientHeight
					)) {
					//scroll down
					$('html,body').animate({scrollTop: $target.offset().top -
							(window.innerHeight || document.documentElement.clientHeight)
							+ $target.outerHeight()}
					);
				}
			}
		},

		fallback: function(url) {
			location.href = url;
		}
	});
});
define('cartware/components/magento/product/type/configurable',[
	'jquery',
	'lodash',
	'cartware/components/_base'
],
function(
	$,
	_,
	Component
) {
	return Component.create({
		init: function() {
			this.patchPrototype();
		},

		patchPrototype: function() {
			if (_.get(window, "Product.Config") == null) {
				return null;
			}

			var $document = $(document),
				PubSubHub = this.hub;

			if (!Product.Config.prototype._advises_installed) {
				var _initialize = Product.Config.prototype.initialize;

				var self = this;

				Product.Config.prototype.dataProviders = null;

				Product.Config.prototype.registerDataProvider = function(dataProvider) {
					this.dataProviders.push(dataProvider);
				};

				Product.Config.prototype.unregisterDataProvider = function(dataProvider) {
					var index = this.dataProviders.indexOf(dataProvider);

					if (index >= 0) {
						delete this.dataProviders[index];
					}
				};

				Product.Config.prototype.initialize = function(config) {
					var separatorIndex = window.location.href.indexOf('#'),
						i;

					this.dataProviders = [];

					if (separatorIndex == -1) {
						if (this._configurable && "initialCriteria" in this._configurable) {
							config.defaultValues = this._generateDefaultValues(config);
						}
					}

					_initialize.apply(this, [config]);

					//for (i in this.state) {
					//	if (this.state.hasOwnProperty(i) && this.state[i] == false) {
					//		self.addToCartButton.addClass('disabled');
					//		break;
					//	}
					//}
				};

				Product.Config.prototype._generateDefaultValues = function(config) {
					return $.extend({}, this._configurable ? this._configurable.initialCriteria : {}, (config.defaultValues || {}));
				};

				var _configureElement = Product.Config.prototype.configureElement;
				Product.Config.prototype.configureElement = function(element) {
					_configureElement.apply(this, arguments);

					// clean state
					_.forEach(this.settings, _.bind(function(el) {
						this.state[el.config.id] = (el.value && (isNaN(el.value) || el.value == ~~el.value)) ? el.value : false;
					}, this));

					var selectionComplete = _.every(this.settings, function(select) {
						return select.value;
					});

					$document.trigger('product/configure', [element, this, selectionComplete]);
					this.__onConfigure(element);
				};

				var _fillSelect = Product.Config.prototype.fillSelect;
				Product.Config.prototype.fillSelect = function(element) {
					//var attributeId = element.id.replace(/[a-z]*/, '');
					_fillSelect.apply(this, arguments);
					if (element.options.length > 1 && element.options[0].value === '') {
						element.options[0].remove();
					}
					$(element).trigger('attribute/fillSelect');
				};

				var _reloadOptionLabels = Product.Config.prototype.reloadOptionLabels;
				Product.Config.prototype.reloadOptionLabels = function(element) {
					_reloadOptionLabels.apply(this, arguments);
					$(element).trigger('attribute/reloadOptionLabels');
				};

				var _resetChildren = Product.Config.prototype.resetChildren;
				Product.Config.prototype.resetChildren = function(element) {
					_resetChildren.apply(this, arguments);
					if (element.childSettings) {
						for (var i = 0; i < element.childSettings.length; i++) {
							$(element.childSettings[i]).trigger('attribute/reset');
						}
					}
				};

				//var _configureForValues = Product.Config.prototype.configureForValues;
				var _configureForValues = function () {
					if (this.values) {
						this.settings.each(function(element){
							var attributeId = element.attributeId,
								valueUndefined = (typeof(this.values[attributeId]) == 'undefined');

							if (valueUndefined) {
								throw $break;
							}

							element.value = valueUndefined ? '' : this.values[attributeId];

							if (!element.value) {
								if ("undefined" !== console.error) {
									console.error("Given configurable options are invalid");
								}

								throw $break;
							}

							this.configureElement(element);
						}.bind(this));
					}
				};

				Product.Config.prototype.configureForValues = function() {
					var values = {}
						, count = 0;

					if (this.values) {
						this.settings.each(function(element) {
							var attributeId = element.attributeId;
							if (typeof(this.values[attributeId]) != 'undefined') {
								values[attributeId] = this.values[attributeId];
								++count;
							}
						}, this);

						if (!count) values = this._generateDefaultValues(this.config);

						this.values = values;
					}

					if (count > 0) _configureForValues.apply(this, arguments);
					$document.trigger('product/configureForValues', [this]);
				};

				Product.Config.prototype.__currentGallerySimple = null;
				Product.Config.prototype.__currentSimple = null;

				Product.Config.prototype.__onConfigure = function() {
					var self = this
						, simples = _.get(this._configurable, "simples", {})
						, gallerySimple = null
						, targetSimple = null
						, simpleConditions = _.assign.apply(
							null,
							_.filter(
								_.map(
									_.get(this._configurable, "super_media_attributes", []),
									function(attributeId) {
										var obj = {};

										if (!_.get(self.state, attributeId, false)) {
											return false;
										}

										obj[attributeId] = self.state[attributeId];
										return obj;
									}
								)
							)
						)
						, conditions = {}
						, selects = this.settings;

					_.forEach(selects, function(node) {
						var select = $(node)
							, value = select.val()
							, attributeId = select.prop('id').replace(/[a-z]*/, '');

						conditions[attributeId] = value;
					});

					// see also skin/frontend/gigaset/rebrush/js/rebrush/components/product/type/configurable.js (l:59)
					var simplesArray = _.sortBy(simples, function (o) {
						if (o.is_available) {
							return 0;
						}
						if (o.priority) {
							return parseInt(o.priority);
						}
						return 1000;
					});

					// Find simple which meets the configured conditions
					_.forEach(simplesArray, function(simple) {
						var galleryConditionsMet = true
							, conditionsMet = true;

						_.forEach(simpleConditions, function(value, attributeId) {
							if (_.get(simple.attributes, [attributeId, "value"], null) != value) {
								galleryConditionsMet = false;
								return false;
							}
						});

						if (galleryConditionsMet) {
							gallerySimple = simple;
						}

						_.forEach(conditions, function(value, attributeId) {
							if (_.get(simple.attributes, [attributeId, "value"], null) != value) {
								conditionsMet = false;
								return false;
							}
						});

						if (conditionsMet) {
							targetSimple = simple;
						}
					});

					if (null === this.__currentGallerySimple || gallerySimple !== this.__currentGallerySimple) {
						this.__currentGallerySimple = gallerySimple;
						//$document.trigger('product/changeGalleryImage', [this.__currentGallerySimple['3dpicture']]);
						$document.trigger('product/changeGallerySimple', [this.__currentGallerySimple]);
					}

					if (null === this.__currentSimple || targetSimple !== this.__currentSimple) {
						this.__currentSimple = targetSimple;

						PubSubHub.flag('product/changeSimple', this.__currentSimple);
						$document.trigger('product/changeSimple', [this.__currentSimple]);
					}
				};

				Product.Config.prototype._advises_installed = true;
			}
		}
	});
});
define('shoptheme/components/product/type/configurable',[
	"jquery",
	"lodash",
	"cartware/components/Registry",
	"cartware/components/_base",
	"cartware/jquery/bem",
	"bootstrap-popover",
	"bootstrap-button"
], function(
	$,
	_,
	Registry,
	Component,
	BEM
) {

	var $document = $(document);

	function triggerEvent(element, eventName) {
		if ("dispatchEvent" in element) {
			var evt = document.createEvent("HTMLEvents");
			evt.initEvent(eventName, false, true);
			element.dispatchEvent(evt);
		} else if ("fireEvent" in element) {
			element.fireEvent("on" + eventName);
		}
	}

	return Component.create({
		simpleItems:		null,
		stockBlock:			null,
		priceBlock:			null,

		$init: function() {
			var $document = $(document);

			$document.trigger('product/configurable/uninstall');

			$document.on('product/changeGallerySimple', function() {

			});

			this.simpleItems = $(".options-simples li");

			//this.installButtonPopover();

			this.stockBlock = BEM.block("product-detail-stock-status");
			this.priceBlock = BEM.block("product-detail-price");

			this.initializeEvents();
			this.initializeStock();
			this.initializePrice();

			/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
				Catalog :: Product :: Configurable :: Attribute options
			 ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */
			$('.input-box').on('click', '.attribute-option', function(e) {
				var item = $(this)
					, attributeId = item.data('attribute-id')
					, attributeValue = item.data('attribute-value')
					, select = $('#attribute' + attributeId);

				select.val(attributeValue);
				triggerEvent(select.get(0), 'change');
			});

			/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
				Catalog :: Product :: Configurable :: Multiple images
			 ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */
			;(function() {
				var template = $('#media-additional-template').html()
					, targetNode = $('#product-thumbnails');

				;(function() {
					function _onChangeGallerySimple(e, simple) {
						var images = [];

						for (var i = 0, j = simple.mediaImages.length; i < j; i++) {
							images.push(simple.mediaImages[i]);
						}

						images.reverse();

						var renderedTemplate = _.template(template, { images : images, simple : simple });
						targetNode.html(renderedTemplate);
					}

					$document.on('product/changeGallerySimple', _onChangeGallerySimple);
					$document.one('product/configurable/uninstall', function() {
						$document.off('product/changeGallerySimple', _onChangeGallerySimple);
					});
				})();
			})();

			/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
			 	Catalog :: Product :: Configurable :: Delivery times
			 ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */
			;(function() {
				var deliveryTimeWrap = $('.delivery-time-wrap')
					, defaultDeliveryTime = deliveryTimeWrap.find('.delivery-time');

				function _onSimpleChange(e, simple) {
					if (simple) {
						defaultDeliveryTime.detach();
						deliveryTimeWrap.html(simple.deliveryTime || '');
					} else {
						deliveryTimeWrap.empty();
						defaultDeliveryTime.appendTo(deliveryTimeWrap);
					}
				}

				$document.on('product/changeSimple', _onSimpleChange);
				$document.one('product/configurable/uninstall', function() {
					$document.off('product/changeSimple', _onSimpleChange);
				});
			})();

			/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
			 	Catalog :: Product :: Configurable :: Global exposure
			 ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */
			if ("spConfigData" in window) {
				window.spConfig = new Product.Config(spConfigData);
				Registry().register("product.view.configurable.spConfig", spConfig);
			}

			/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
			 	Catalog :: Product :: Configurable :: Options
			 ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */
			$('.super-attribute-select').each(function() {
				var select = $(this)
					, attributeId = select.prop('id').replace(/[a-z]*/, '')
					, templateNode = $('#attribute-template-' + attributeId)
					, template = templateNode.html()
					, displayNode = select.parent().find('.display');

				if (!template) return;

				select.on('attribute/reset', function(e) {
					displayNode.addClass('disabled');
					$document.trigger('attribute/reset', [this, displayNode]);
				});

				select.on('attribute/fillSelect', function(e) {
					var options = []
						, state = spConfig.state || {};

					select.find('option').each(function() {
						var node = $(this)
							, option = {
								value: node.prop('value'),
								label: node.html(),
								qty: null
							};

						var configOptions = spConfig.config.attributes[attributeId].options;

						for (var i = 0, j = configOptions.length; i < j; i++) {
							if (configOptions[i].id == option.value) {
								if (configOptions[i].allowedProducts.length == 1) {
									for (var k in spConfig._configurable.simples) {
										if (k == configOptions[i].allowedProducts[0]) {
											option.qty = spConfig._configurable.simples[k].qty;
											break;
										}
									}
								}
								break;
							}
						}

						_.forEach(spConfig.dataProviders, function(provider) {
							if ("function" == typeof provider.provideSelectOption) {
								provider.provideSelectOption(option, select, attributeId);
							}
						});

						options.push(option);
					});

					function done() {
						var renderedTemplate = _.template(template, { options: options, attributeId: attributeId, state: state });
						displayNode.html(renderedTemplate);
						displayNode.removeClass('disabled');
						$document.trigger('attribute/fillSelectAfter', [this, displayNode]);
					}

					if (spConfig.dataProviders.length) {
						+function() {
							var i = 0, j = spConfig.dataProviders.length;

							function next(result) {
								var provider;

								options = result;

								if (i >= j) {
									done();
									return;
								}

								provider = spConfig.dataProviders[i];
								i++;

								if ("function" == typeof provider.sortSelectOptions) {
									options = provider.sortSelectOptions(options, attributeId);

									if ("object" === typeof options && "then" in options) { // promise
										options.then(next);
									} else {
										next(options);
									}

									return;
								}

								next(options);
							}

							next(options);
						}();
					} else {
						done();
					}
				});

				select.trigger('attribute/fillSelect');
			});
		},

		initializeStock: function() {
			var defaultElement = this.stockBlock.getElements("default"),
				tmpl = _.template(this.stockBlock.find("[data-template]").html()),
				blockCache = {},
				self = this;

			function _onSimpleChange(e, simple) {
				var status,
					block;

				if (simple) {
					status = simple.stock_status;
					block = blockCache[status] || null;

					if (!block) {
						block = $(tmpl({
							stockStatus: status
						})).bemAddModifier(status);

						blockCache[status] = block;
					}

					self.stockBlock.empty();
					self.stockBlock.append(block);
				} else {
					self.stockBlock.empty();
					self.stockBlock.append(defaultElement);
				}
			}

			$document.on('product/changeSimple', _onSimpleChange);
			$document.one('product/configurable/uninstall', function() {
				$document.off('product/changeSimple', _onSimpleChange);
			});
		},

		initializePrice: function() {
			var fromLabel = this.priceBlock.findBlock("price").getElements("label-from");

			function _onSimpleChange(e, simple) {
				var status,
					block;

				if (simple) {
					fromLabel.hide();
				} else {
					fromLabel.show();
				}
			}

			$document.on('product/changeSimple', _onSimpleChange);
			$document.one('product/configurable/uninstall', function() {
				$document.off('product/changeSimple', _onSimpleChange);
			});
		},

		//installButtonPopover: function() {
		//	var self = this;
		//
		//	if (!this.addToCartButton.length) return;
		//
		//	var template = $('#add-to-cart-popover-template').html();
		//
		//	this.addToCartButton.get(0)['hide'] = $.noop;
		//	this.addToCartButton.get(0)['show'] = $.noop;
		//
		//	this.addToCartButton.on('mouseenter', function() {
		//		if (self.addToCartButton.hasClass('disabled')) {
		//			self.addToCartButton.popover('show');
		//		}
		//	});
		//
		//	this.addToCartButton.on('mouseleave', function() {
		//		self.addToCartButton.popover('hide');
		//	});
		//
		//	this.addToCartButton.popover({
		//		title: '',
		//		content: function() {
		//			var settings = spConfig.settings;
		//
		//			if (!settings.length) return '';
		//
		//			var emptySetting = null;
		//
		//			$.each(settings, function() {
		//				if (this.disabled || this.value === '') {
		//					emptySetting = this;
		//					return false;
		//				}
		//			});
		//
		//			if (!emptySetting) return '';
		//
		//
		//			var renderedTemplate = _.template(template, { conf : $(emptySetting).data('label') });
		//			return renderedTemplate;
		//		},
		//		trigger: 'manual',
		//		placement: function() {
		//			if ($(window).width() < 768) {
		//				return "left";
		//			}
		//
		//			return "bottom";
		//		}
		//	});
		//},

		initializeEvents: function() {
			this.simpleItems.on('click', function(e) {
				var listItem = $(this)
					, value = listItem.data('attribute-value')
					, attributeId = listItem.data('attribute-id')
					, select = $('#attribute' + attributeId);

				if (select.length === 0) return;

				select.val(value);
				triggerEvent(select.get(0), 'change');
			});
		},

		destructor: function() {
			$(document).trigger('product/configurable/uninstall');
			Registry().unregister("product.view.configurable.spConfig");
			this.simpleItems = null;
			window.spConfig = null;
			delete window.spConfig;
		}
	});
});
define('shoptheme/components/category/layered',[
	"jquery",
	"lodash",
	"cartware/components/_base",
	"cartware/jquery/bem",
	"bootstrap-collapse"
], function(
	$,
	_,
	Component,
	BEM
) {
	return Component.create({
		block:			null,
		categoryBlock:	null,
		filterTitles:	null,
		filterPanels:	null,
		state:			null,

		$init: function() {
			this.state = JSON.parse(Mage.Cookies.get("gigaset_filter_state") || "{}");
			this.block = BEM.block("layered-nav");
			this.categoryBlock = BEM.block("layer-category-tree");
			this.filterTitles = this.block.getElements("filter-title");
			this.filterPanels = this.block.getElements("filter-panel");

			this._initializeEvents();
			this._initializeActiveStatus();
			this.restoreState();
		},

		_initializeEvents: function() {
			var self = this;

			this.block.on("hide.bs.collapse show.bs.collapse", function(e) {
				var collapsible = $(e.target),
					panel = collapsible.parents(this.filterPanels).first(),
					attributeId = panel.data("attribute-id");

				if (attributeId) {
					self.state[attributeId] = (e.type === "show");
					self.persistState();
				}
			});

			this.block.on("click", "." + BEM.buildElementClassname("layer-category-tree", "item-opener"), function(e) {
				e.preventDefault();
			});
		},

		_initializeActiveStatus: function() {
			var path = _.get(window, "gigaset.category.current");
			_.forEach(path, function(categoryId) {
				var item = this.categoryBlock.find('[data-node-id="category-node-' + categoryId + '"]');

				item.bemAddModifier("active");
				item.find('[data-toggle="collapse"]').removeClass("collapsed");
				item.find('.collapse').addClass("in");
			}, this);
		},

		restoreState: function() {
			_.forEach(this.state, function(value, key) {
				var query = '[data-attribute-id="' + key + '"]';

				this.filterTitles.filter(query).find('[data-toggle="collapse"]')[value ? "removeClass" : "addClass"]("collapsed");
				this.filterPanels.filter(query).find(".collapse")[value ? "addClass" : "removeClass"]("in");
			}, this);
		},

		persistState: function() {
			Mage.Cookies.set("gigaset_filter_state", JSON.stringify(this.state));
		}
	});
});
define('shoptheme/components/ui/sidebar',[
	"jquery",
	"lodash",
	"cartware/components/_base",
	"cartware/jquery/bem",
	"json!shoptheme/etc/breakpoints.json",
	"enquire"
], function(
	$,
	_,
	Component,
	BEM,
	BreakPoints,
	enquire
) {
	var instances = [];
	var isMobile = true;

	var TRANSITION_DURATION = 250;

	enquire.register('(min-width: ' + (BreakPoints.sm + 1) + 'px)', {
		match: function() {
			isMobile = false;

			_.forEach(instances, function(instance) {
				instance.desktop();
			});
		},
		unmatch: function() {
			isMobile = true;

			_.forEach(instances, function(instance) {
				instance.mobile();
			});
		}
	}, true);

	return Component.create({
		block: null,
		triggerBlock: null,
		productList: null,
		isOpen: false,
		isBuilt: false,
		backdrop: null,
		closeBlock: null,

		$init: function() {
			this.block = $("." + BEM.buildElementClassname('main-container', 'col2-left-col-left'));
			this.productList = BEM.block("product-list");

			instances.push(this);

			if (isMobile) {
				this.mobile();
			}
		},

		desktop: function() {
			if (!this.isBuilt) return;
			this.triggerBlock.css("display", "none");
			this.close(true);
		},

		mobile: function() {
			this.mobile = this.buildMobile()
				? function() {
					this.triggerBlock.css("display", "");
				}
				: $.noop;
		},

		toggle: function() {
			this[this.isOpen ? 'close' : 'open']();
		},

		open: function() {
			this.block.bemAddModifier("open");
			this.backdrop.appendTo(document.body);

			if ($.support.transition) {
				this.block
					.one('bsTransitionEnd', _.bind(this.onOpened, this))
					.emulateTransitionEnd(TRANSITION_DURATION);

				$({foo:0}).animate({foo:50}, {
					step: _.bind(function(val) {
						this.backdrop.css("opacity", val / 100);
					}, this)
				}, TRANSITION_DURATION);
			} else {
				this.onOpened();
			}
		},

		onOpened: function() {
			this.isOpen = true;
		},

		close: function(immediately) {
			immediately = !!immediately;
			this.block.bemRemoveModifier("open");

			if (!immediately && $.support.transition) {
				this.block
					.one('bsTransitionEnd', _.bind(this.onClosed, this))
					.emulateTransitionEnd(TRANSITION_DURATION);

				$({foo: this.backdrop.css("opacity") * 100 }).animate({foo: 0}, {
					step: _.bind(function(val) {
						this.backdrop.css("opacity", val / 100);
					}, this)
				}, TRANSITION_DURATION);
			} else {
				this.onClosed();
			}
		},

		onClosed: function() {
			this.isOpen = false;
			this.backdrop.detach();
		},

		buildMobile: function() {
			if (!this.productList.length) return false;

			var layeredNav = BEM.block('layered-nav'),
				titles = layeredNav.getElements('filter-title'),
				filterCount = titles.length;

			if (!filterCount) return false;

			this.triggerBlock = BEM.block($('<div class="' + BEM.buildBlockClassname('sidebar-trigger') + '" />'));
			this.triggerBlock.on("click", _.bind(this.toggle, this));

			this.backdrop = $('<div class="modal-backdrop in" />');
			this.backdrop.on("click", _.bind(this.close, this));

			this.closeBlock = BEM.block($('<div class="' + BEM.buildBlockClassname('sidebar-close') + '" />'));
			this.closeBlock.on("click", _.bind(this.close, this));
			this.closeBlock.prependTo(this.block);

			$('<span class="' + BEM.buildElementClassname('sidebar-trigger', 'title') + '" />')
				.text(window.Translator.translate("Show filters"))
				.appendTo(this.triggerBlock);

			$('<span class="' + BEM.buildElementClassname('sidebar-trigger', 'count') + '" />')
				.text('(' + filterCount + ')')
				.appendTo(this.triggerBlock);

			this.productList.before(this.triggerBlock);

			this.isBuilt = true;

			return true;
		}
	});
});

define('shoptheme/components/customer/newsletter',[
	"jquery",
	"cartware/components/_base",
	"cartware/jquery/bem"
], function(
	$,
	Component,
	BEM
) {
	return Component.create({

		$init: function() {

			this.monitorCheckboxes();
			this.monitorControlCheckbox();

		},
		monitorCheckboxes: function() {
			$("[id*='newsletter-checkbox-Gigaset']").change(function(e, stopPropagation) {
				var allCheck = true,
					checkboxControl = $('#checkbox-control');

				if (stopPropagation) return false;

				$("[id*='newsletter-checkbox-']").each(function (){
					if ($(this).prop('checked')) {
						allCheck = allCheck && true;
					} else {
						allCheck = allCheck && false;
					}
				});

				if (allCheck){
					checkboxControl.prop('checked', 'checked');

				} else {
					checkboxControl.removeProp('checked');
				}

				checkboxControl.trigger("change", true);
			});
		},
		monitorControlCheckbox: function() {
			$('#checkbox-control').change(function (e, stopPropagation) {
				var isChecked = $(this).prop('checked');

				if (stopPropagation) return false;

				$("[id*='newsletter-checkbox-']").each(function (){
					var checkbox = $(this);

					if (isChecked) {
						checkbox.prop('checked', 'checked');
					} else {
						checkbox.removeProp('checked');
					}

					checkbox.trigger("change", true);
				});
			});
		}
	});
});



define('shoptheme/components/customer/account/create',[
	'jquery',
	'lodash',
	'cartware/components/_base',
	'cartware/jquery/bem',
	'text!shoptheme/templates/magnific-popup/default.html',
	'magnific-popup'
], function(
	$,
	_,
	Component,
	BEM,
	PopupHtml
) {
	var _popupTemplate = null;

	return Component.create({
		$init: function() {
			var self = this;
			var selector = '.' + BEM.buildElementClassname('account-create', 'agreements') + ' .checkbox a';

			$(document).on('click', selector, function(e) {
				var link = $(e.currentTarget),
					href = link.attr('href');

				e.preventDefault();

				href = href.replace(/.*?:/g, '');
				href += (href.indexOf('?') === -1 ? '?' : '&') + 'isAjax=1';

				link.attr('href', href);

				link.magnificPopup({
					type: 'ajax',
					callbacks: {
						parseAjax: function(mfpResponse) {
							var html = mfpResponse.data.replace(/<script[^>]*>([\s\S]*?)<\/script>/gi, '');

							mfpResponse.data = self._renderPopup({
								body: html
							});
						}
					}
				}).magnificPopup('open');
			});
		},

		_renderPopup: function(data) {
			if (null === _popupTemplate) {
				_popupTemplate = _.template(PopupHtml);
			}

			return _popupTemplate(data);
		}
	});
});
define('shoptheme/components/customer/account/edit',[
	"jquery",
	"lodash",
	"cartware/components/_base",
	"cartware/jquery/bem"
], function(
	$,
	_,
	Component,
	BEM
) {
	var BLOCK = 'customer-account-edit';

	return Component.create({
		blocks: null,

		$init: function() {
			this.blocks = BEM.block(BLOCK);
			this.blocks.each(function() {
				var block = BEM.block(this),
					form = block.getElements('form'),
					containerChangePassword = block.getElements('container-change-password'),
					containerCurrentPassword = block.getElements('container-current-password'),
					inputChangePassword = block.getElements('input-change-password'),
					inputCurrentPassword = block.getElements('input-current-password'),
					inputNewPassword = block.getElements('input-new-password'),
					inputNewPasswordConfirm = block.getElements('input-new-password-confirm'),
					inputEmail = block.getElements('input-email'),
					originalValues = _.map($.prop(form.get(0), 'elements'), function(node) { return {node: node, value: $(node).val()}; });

				function getOriginalValue(node) {
					return _.get(_.find(originalValues, {node: node}), 'value');
				}

				function showHideContainerCurrentPassword() {
					var show = inputChangePassword.is(':checked');
					show |= inputEmail.val() !== getOriginalValue(inputEmail.get(0));

					containerCurrentPassword[show ? 'show' : 'hide']();
					inputCurrentPassword[show ? 'addClass' : 'removeClass']('required-entry');
				}

				function showHideContainerChangePassword() {
					var show = inputChangePassword.is(':checked');
					containerChangePassword[show ? 'show' : 'hide']();
					inputNewPassword[show ? 'addClass' : 'removeClass']('required-entry');
					inputNewPasswordConfirm[show ? 'addClass' : 'removeClass']('required-entry');
				}

				inputChangePassword.on('change', showHideContainerChangePassword);
				inputChangePassword.on('change', showHideContainerCurrentPassword);
				inputEmail.on('keyup', showHideContainerCurrentPassword);

				showHideContainerCurrentPassword();
				showHideContainerChangePassword();
			});
		}
	});
});
define('shoptheme/components/customer/account/policy',[
	"jquery",
	"lodash",
	"cartware/components/_base",
	"cartware/jquery/bem",
	"text!shoptheme/templates/magnific-popup/default.html",
	"magnific-popup"
], function(
	$,
	_,
	Component,
	BEM,
	PopupHtml
) {
	var _popupTemplate = null;

	return Component.create({
		$init: function() {
			var self = this;
			var selector = '.' + BEM.buildElementClassname("privacy-optin", "agreements") + ' .checkbox a';

			$(document).on("click", selector, function(e) {
				var link = $(e.currentTarget);

				e.preventDefault();

				link.attr('href', link.attr('href').replace(/.*?:/g, ''));

				link.magnificPopup({
					type: 'ajax',
					callbacks: {
						parseAjax: function(mfpResponse) {
							var html = mfpResponse.data.replace(/<script[^>]*>([\s\S]*?)<\/script>/gi, '');

							mfpResponse.data = self._renderPopup({
								body: html
							});
						}
					}
				}).magnificPopup('open');
			});
		},

		_renderPopup: function(data) {
			if (null === _popupTemplate) {
				_popupTemplate = _.template(PopupHtml);
			}

			return _popupTemplate(data);
		}
	});
});
define('shoptheme/components/contentbridge/tabs',[
	"jquery",
	"cartware/components/_base"
], function(
	$,
	Component
) {
	return Component.create({
		blocks:			null,

		$init: function() {
			this.blocks = $(".b_contentbridge .tabs");
			this.blocks.each(function() {
				var block = $(this),
					container = $('<div class="b_tabs b_tabs--close-mobile">'),
					list = $('<dl class="b_tabs__list">').appendTo(container);

				block.find("> li").each(function(i) {
					var li = $(this),
						title = li.find(".tab-title").html() || (Translator.translate("Tab") + " " + (i + 1)),
						dt = $('<dt class="b_tabs__tab">').appendTo(list),
						titleSpan = $('<span>').html(title).appendTo(dt),
						dd = $('<dd class="b_tabs__tab-content">').appendTo(list).html(li.html());

					if (0 === i) {
						dt.addClass("b_tabs__tab--active");
						dd.addClass("b_tabs__tab-content--active");
					}
				});

				block.after(container);
				block.remove();
			});
		}
	});
});
define('shoptheme/components/contentbridge/accordeon',[
	"jquery",
	"cartware/components/_base"
], function(
	$,
	Component
) {
	return Component.create({
		blocks:			null,

		$init: function() {
			this.blocks = $(".b_contentbridge .accordion");
			this.blocks.each(function() {
				var items = $(this).find(".accordion-item");
				items.each(function(i) {
					var item = $(this),
						title = item.find(".accordion-title"),
						content = item.find(".accordion-content");

					if (0 === i) {
						content.slideDown(0);
					}

					title.on("click", function() {
						items.find(".accordion-content:visible").slideUp();
						content.slideToggle();
					});
				});
			});
		}
	});
});
define('shoptheme/components/blocks/elements/product/item/pane',[
	"jquery",
	"cartware/components/_base",
	"cartware/jquery/bem",
	"gigaset/spinner",
	"select2"
], function(
	$,
	Component,
	BEM,
	Spinner,
	s2
) {
	var BLOCK_NAME = "elements-product-item-pane";

	return Component.create({
		blocks:			null,

		$init: function() {
			var self = this;
			this.blocks = BEM.block(BLOCK_NAME);

			this.blocks.each(function() {
				var block = BEM.block(this),
					detailsWrap = block.getElements("details-wrap"),
					detailsWrapHelper = block.getElements("details-wrap-helper"),
					detailsWrapSlot = block.getElements("details-wrap-slot"),
					animationSpeedMs = 500,
					detailsWrapHeight = 0;

				self.initQtyBlock(block);

				block.on("click", "." + BEM.buildElementClassname(BLOCK_NAME, "cta-link", "js-load-details"), function(e) {
					var link = $(e.target),
						href = link.data("ajax-tabs-url");

					if (!href) return true;

					e.preventDefault();

					if (detailsWrap.bemHasModifier("empty")) {
						block.bemAddModifier("loading");

						Spinner.add(detailsWrapHelper);
						detailsWrapHeight = detailsWrapHelper.outerHeight();
						detailsWrap.bemRemoveModifier("empty");
						detailsWrap.bemAddModifier("visible");
						detailsWrap.css('height', detailsWrapHeight);

						var xhr = $.get(href);

						xhr.then(function() {
							Spinner.remove(detailsWrapHelper);
							block.bemRemoveModifier("loading");
						});

						xhr.done(function(response) {
							detailsWrapSlot.html("");

							var tabs = "tabs" in response ? response.tabs : {};
							var key, element;

							for (key in tabs) {
								if (tabs.hasOwnProperty(key)) {
									element = $('<div class="' + BEM.buildElementClassname(BLOCK_NAME, "details-content") + " " + BEM.buildElementClassname(BLOCK_NAME, "details-content", key) + '" />');
									element.html(tabs[key]);
									element.appendTo(detailsWrapSlot);
								}
							}

							detailsWrapHeight = detailsWrapHelper.outerHeight();
							detailsWrap.bemRemoveModifier("empty");
							detailsWrap.bemAddModifier("visible");
							detailsWrap.css('height', detailsWrapHeight);
							setTimeout(function(){detailsWrap.css('height', 'auto')}, animationSpeedMs);
						});
					} else {
						if (detailsWrap.bemHasModifier("visible")) {
							detailsWrapHeight = detailsWrapHelper.outerHeight();
							detailsWrap.css('height', detailsWrapHeight);
							setTimeout(function(){detailsWrap.css('height', 0)}, 10);
							setTimeout(function(){detailsWrap.bemRemoveModifier("visible")}, 500);
						} else {
							detailsWrapHeight = detailsWrapHelper.outerHeight();
							detailsWrap.bemAddModifier("visible");
							detailsWrap.css('height', detailsWrapHeight);
							setTimeout(function(){detailsWrap.css('height', 'auto')}, animationSpeedMs);
						}
					}
				});

				block.on("click", "." + BEM.buildElementClassname(BLOCK_NAME, "cta-link", "js-hide-details"), function(e) {
					e.preventDefault();
					detailsWrapHeight = detailsWrapHelper.outerHeight();
					detailsWrap.css('height', detailsWrapHeight);
					setTimeout(function(){detailsWrap.css('height', 0); jQuery('html,body').animate({ scrollTop: $('body').scrollTop() - detailsWrapHeight }, animationSpeedMs);}, 10);
					detailsWrap.bemRemoveModifier("visible");
				});
			});
			
		},
		
		initQtyBlock: function(block) {
			var qtyBlocks = BEM.block('product-qty', block);
			qtyBlocks.each(function(){
				var qtyBlock = BEM.block(this),
					lessLink = qtyBlock.getElements('less-link'),
					moreLink = qtyBlock.getElements('more-link'),
					qtySelect = qtyBlock.getElements('select');

				qtySelect.select2({	minimumResultsForSearch: Infinity });
				lessLink.click(function(e){
					e.preventDefault();
					if (qtySelect.find('option:selected').prev().val()) {
						qtySelect.select2().val(qtySelect.find('option:selected').prev().val()).trigger('change');
					}
				});
				moreLink.click(function(e){
					e.preventDefault();
					if (qtySelect.find('option:selected').next().val()) {
						qtySelect.select2().val(qtySelect.find('option:selected').next().val()).trigger('change');
					}
				});

				qtySelect.on('change', function(){
					block.find('[data-ajax-add-to-cart]').data('order-qty', this.value);
				});

			});
		}
	});
});
define('shoptheme/components/blocks/fce/elements/videotutorials',[
	"jquery",
	"cartware/components/_base",
	"cartware/jquery/bem",
	"videojs",
	"videojs",
	"cartware/components/Dispatcher",
	"videojs-vimeo",
	"videojs-youtube",
	"magnific-popup"
], function(
	$,
	Component,
	BEM,
	videojs,
	ComponentDispatcher
) {
	var BLOCK_NAME = "csp-support-detail-videotutorials";

	return Component.create({
		blocks:			null,
		$init: function() {
			var self = this;

			this.blocks = BEM.block(BLOCK_NAME);
			this.blocks.each(function() {
				var block = BEM.block(this),
					title = block.getElements("title"),
					reel =  block.getElements("sources-list"),
					firstElement = reel.find('a').first();

				reel.find("[data-video-sources]").click(function(e){
					e.preventDefault();
					elem = $(this);
					self.setVideo(elem, block, true);
					self.scrollToVideo(block);
				});

				self.setVideo(firstElement, block, false);

			});
		},
		
		setVideo: function(source, rootBlock, start){
			var sources = source.data('video-sources'),
				currentVideo = rootBlock.find('.b_video-widget');

			if (currentVideo.length) {
				if (currentVideo.find('.video-js').length) {
					videojs(currentVideo.find('.video-js').get(0)).dispose();
					currentVideo.html('');
				}
			}
			var newVideo = $('<video controls class="video-js vjs-gigaset-tutorials vjs-16-9"></video>').appendTo(rootBlock.find('.b_video-widget'));

			if (sources.type == "video/youtube") {
				videojs.options.techOrder = ['youtube'];
			} else {
				videojs.options.techOrder = ['html5'];
			}
			videojs(newVideo.get(0), {} , function() {
				var player = this;
				rootBlock.getElements("sources-list").find('a').removeClass('currently-playing');
				player.src(sources);
				rootBlock.getElements("video-title").html(source.attr('title'));
				rootBlock.getElements("video-description").html(source.data('video-description'));
				player.poster(source.find('img').attr('src'));
				source.addClass('currently-playing');
				if (start) {
					player.play();
				}
			});

		},

		scrollToVideo: function(block){
			var offset = block.offset().top - $(window).scrollTop();

			if(offset < 0){
				$('html,body').animate({scrollTop: block.offset().top}, 1000);
				return false;
			}
			return true;
		}
	});
});
define('shoptheme/components/cartware/checkout',[
	"cartware/components/_base",
	"jquery",
	"cartware/jquery/bem",
	"json!shoptheme/etc/breakpoints.json",
	"enquire",
	"text!shoptheme/templates/magnific-popup/default.html",
	"magnific-popup"
], function(
	Component,
	$,
	BEM,
	BreakPoints,
	enquire,
	PopupHtml
) {

	var _popupTemplate = null;

	return Component.create({
		init: function() {
			var self = this;
			var blocks = $('.' + BEM.buildBlockClassname('payment-tooltip'));
			var schippingCompany = $('#shipping\\:company');
			var countryTooltip = $('.b_opcheckout-progress-information');

			if (schippingCompany.length > 0 && countryTooltip.length > 0) {
				schippingCompany.after($('.b_opcheckout-progress-information'));
				schippingCompany.attr('autocomplete', 'nope');
				
				schippingCompany.on('focus', function () {
					countryTooltip.show();
				});

				schippingCompany.on('blur', function () {
					countryTooltip.hide();
				});

			}

			$.each(blocks, function(){
				var block = BEM.block(this),
					link = block.getElements('link'),
					content = block.getElements('tooltip-inner');

				link.on('click', function(e) {
					e.preventDefault();
					e.stopPropagation();

					var title = '';
					var message = content.html();
					self._showMessage(title, message);

				});
			});
		},
		_renderPopup: function(data) {
			if (null === _popupTemplate) {
				_popupTemplate = _.template(PopupHtml);
			}
			return _popupTemplate(data);
		},
		_showMessage: function(title, message) {
			$.magnificPopup.open({
				items: {
					src: this._renderPopup({
						title: title,
						body: message
					}),
					type: 'inline'
				}
			});
		}
	});
});
define('shoptheme/components/ui/anchor-navigation',[
	"jquery",
	"lodash",
	"cartware/components/_base",
	"cartware/jquery/bem",
	"json!shoptheme/etc/breakpoints.json",
	"enquire"
], function(
	$,
	_,
	Component,
	BEM,
	BreakPoints,
	enquire
) {

	var BLOCK_ANCHOR_NAVIGATION = "anchor-navigation";
	var BLOCK_HEADLINES = "heading-container";

	var SELECTOR_CONTAINER = ".b_main-container__col2-right-col-main";

	var instances = [];
	var isMobile = true;

	enquire.register('(min-width: ' + (BreakPoints.sm + 1) + 'px)', {
		match: function() {
			isMobile = false;

			_.forEach(instances, function(instance) {
				instance.place();
			});
		},
		unmatch: function() {
			isMobile = true;

			_.forEach(instances, function(instance) {
				instance.place();
			});
		}
	}, true);


	return Component.create({

		$init: function() {
			var self = this;

			this.container = $(SELECTOR_CONTAINER);
			this.headlines = this.container.find("h1, h2");

			this.mobileSpace = $('.b_main-container__col2-right-col-main');
			this.desktopSpace = $('.b_main-container__col2-right-col-right--sidebar');

			this.dom = this.buildDom();

			this.place();

			this.listBlock = BEM.block(BLOCK_ANCHOR_NAVIGATION);
			this.list = this.listBlock.getElements("list");
			this.links = this.listBlock.getElements("link");

			this.crawl();
			this.bindEvents();

			instances.push(this);
		},

		buildDom: function() {
			var domContainer 	= $('<div class="' + BEM.buildBlockClassname(BLOCK_ANCHOR_NAVIGATION) + '" />'),
				titleContainer 	= $('<div class="' + BEM.buildElementClassname(BLOCK_ANCHOR_NAVIGATION, 'title-container') + '" />'),
				listTitle 		= $('<strong class="' + BEM.buildElementClassname(BLOCK_ANCHOR_NAVIGATION, 'title') + '">' + Translator.translate("In this article:") + '</strong>'),
				listContainer 	= $('<ol class="' + BEM.buildElementClassname(BLOCK_ANCHOR_NAVIGATION, 'list') + '" />');

			return domContainer.append(titleContainer.append(listTitle)).append(listContainer);
		},

		place: function() {
			if (isMobile) {
				this.headlines.first().before(this.dom);
			} else {
				this.desktopSpace.prepend(this.dom);
			}
		},

		crawl: function() {
			var self = this;

			this.container.find(this.headlines).each(function(i) {
				self.addItem($(this), i);
			});
		},

		addItem: function(item, i) {
			var cleanRegExp = RegExp("^" + (i+1) + "\\.\\s*"),
				linkTitle = _.trim(item.text().replace(cleanRegExp, "")),
				listItem = $('<li class="' + BEM.buildElementClassname(BLOCK_ANCHOR_NAVIGATION, "list-item") + '" />'),
				//link = $('<a href="#" title="' + linkTitle + '" class="' +  BEM.buildElementClassname(BLOCK_ANCHOR_NAVIGATION, 'link') + '">' + linkTitle + '</a>');
				link = $('<a href="#" title="' + _.escape(linkTitle) + '" class="' +  BEM.buildElementClassname(BLOCK_ANCHOR_NAVIGATION, "link") + '">' + _.escape(linkTitle) + '</a>');

			link.data("content-item", item);

			this.list.append(listItem.append(link));
		},

		bindEvents: function() {
			var self = this;
			this.list.on("click", "." + BEM.buildElementClassname(BLOCK_ANCHOR_NAVIGATION, "link"), function(e) {
				e.preventDefault();
				self.onLinkClick(e.target);
			});
		},

		onLinkClick: function(link) {
			var item = $(link).data("content-item");
			var position = item.offset().top;

			$('html, body').animate({
				scrollTop: position - 15
			}, 500);
		}


	});

});
define('datatables-sort-de',[
	"jquery",
	"datatables"
], function(
	$
) {
	$.extend( $.fn.dataTableExt.oSort, {
		"de_datetime-asc": function ( a, b ) {
			var x, y;
			if ($.trim(a) !== '') {
				var deDatea = $.trim(a).split(' ');
				var deTimea = deDatea[1].split(':');
				var deDatea2 = deDatea[0].split('.');
				if(typeof deTimea[2] != 'undefined') {
					x = (deDatea2[2] + deDatea2[1] + deDatea2[0] + deTimea[0] + deTimea[1] + deTimea[2]) * 1;
				} else {
					x = (deDatea2[2] + deDatea2[1] + deDatea2[0] + deTimea[0] + deTimea[1]) * 1;
				}
			} else {
				x = -Infinity; // = l'an 1000 ...
			}

			if ($.trim(b) !== '') {
				var deDateb = $.trim(b).split(' ');
				var deTimeb = deDateb[1].split(':');
				deDateb = deDateb[0].split('.');
				if(typeof deTimeb[2] != 'undefined') {
					y = (deDateb[2] + deDateb[1] + deDateb[0] + deTimeb[0] + deTimeb[1] + deTimeb[2]) * 1;
				} else {
					y = (deDateb[2] + deDateb[1] + deDateb[0] + deTimeb[0] + deTimeb[1]) * 1;
				}
			} else {
				y = -Infinity;
			}
			var z = ((x < y) ? -1 : ((x > y) ? 1 : 0));
			return z;
		},

		"de_datetime-desc": function ( a, b ) {
			var x, y;
			if ($.trim(a) !== '') {
				var deDatea = $.trim(a).split(' ');
				var deTimea = deDatea[1].split(':');
				var deDatea2 = deDatea[0].split('.');
				if(typeof deTimea[2] != 'undefined') {
					x = (deDatea2[2] + deDatea2[1] + deDatea2[0] + deTimea[0] + deTimea[1] + deTimea[2]) * 1;
				} else {
					x = (deDatea2[2] + deDatea2[1] + deDatea2[0] + deTimea[0] + deTimea[1]) * 1;
				}
			} else {
				x = Infinity;
			}

			if ($.trim(b) !== '') {
				var deDateb = $.trim(b).split(' ');
				var deTimeb = deDateb[1].split(':');
				deDateb = deDateb[0].split('.');
				if(typeof deTimeb[2] != 'undefined') {
					y = (deDateb[2] + deDateb[1] + deDateb[0] + deTimeb[0] + deTimeb[1] + deTimeb[2]) * 1;
				} else {
					y = (deDateb[2] + deDateb[1] + deDateb[0] + deTimeb[0] + deTimeb[1]) * 1;
				}
			} else {
				y = -Infinity;
			}
			var z = ((x < y) ? 1 : ((x > y) ? -1 : 0));
			return z;
		},

		"de_date-asc": function ( a, b ) {
			var x, y;
			if ($.trim(a) !== '') {
				var deDatea = $.trim(a).split('.');
				x = (deDatea[2] + deDatea[1] + deDatea[0]) * 1;
			} else {
				x = Infinity; // = l'an 1000 ...
			}

			if ($.trim(b) !== '') {
				var deDateb = $.trim(b).split('.');
				y = (deDateb[2] + deDateb[1] + deDateb[0]) * 1;
			} else {
				y = -Infinity;
			}
			var z = ((x < y) ? -1 : ((x > y) ? 1 : 0));
			return z;
		},

		"de_date-desc": function ( a, b ) {
			var x, y;
			if ($.trim(a) !== '') {
				var deDatea = $.trim(a).split('.');
				x = (deDatea[2] + deDatea[1] + deDatea[0]) * 1;
			} else {
				x = -Infinity;
			}

			if ($.trim(b) !== '') {
				var deDateb = $.trim(b).split('.');
				y = (deDateb[2] + deDateb[1] + deDateb[0]) * 1;
			} else {
				y = Infinity;
			}
			var z = ((x < y) ? 1 : ((x > y) ? -1 : 0));
			return z;
		}
	} );
});

define('shoptheme/components/ui/datatables',[
	"jquery",
	"cartware/components/_base",
	"cartware/jquery/bem"
], function(
	$,
	Component,
	BEM
) {
	return Component.create({

		$init: function() {
			var blocks = BEM.block('sortable-table'),
				_self = this;

			if (!blocks.length) {
				return;
			}

			require(['datatables', 'datatables-sort-de'], function() {
				blocks.each(function(){
					var table = $($(this).find('table').get(0));

					// WCAG requirement - no roles for <tr> when role="grid" is present on the table element
					table.on('init.dt', function() {
						$(this).find('tr').removeAttr('role');
					});

					var itemCount = table.find('tr').length,
						itemMax = table.data('page-min-items');

					var paginate = false;

					if (itemCount-1 > itemMax-1) {
						paginate = true;
					}

					_self.dataTables(table, paginate);
				});
			});
		},

		dataTables: function(table, paginate) {
			table.DataTable({
				searching: false,
				lengthChange: false,
				responsive: true,
				'info' : false,
				'order': [
					[ 2, "desc" ]
				],
				'columnDefs': [
					{ "orderable": false, "targets": 0 },
					{ "type": "de_date", "targets": 2 },
					{ "orderable": false, "targets": 3 }
				],
				"paging": paginate,
				"info": false,
				'language': {
					'paginate': {
						'previous' : '&laquo;',
						'next'     : '&raquo;'
					}
				},
			});
		}
	});
});

define('shoptheme/components/ui/maps',[
	"jquery",
	"lodash",
	"cartware/components/_base",
	"cartware/jquery/bem",
	"cartware/components/Config"
], function(
	$,
	_,
	Component,
	BEM,
	Config
) {
	return Component.create({
		consentListenerAttached: false,
		consentIdentifierVideo: 'functional',
		googleMapsScriptsInjected: false,
		consentEventIdentifier: 'civik:cookie-consent-changed',
		localStorageConsentIdentifier: 'civik:cookie-control',

		$init: function() {
			// Debug mode
			this.isDebugMode = _.get(window, 'gigaset.featureflags.developer_mode', false);

			// BEM (b_map)
			this.mapBlocks = BEM.block('map');

			// Skip logic
			if (typeof this.mapBlocks === 'undefined' || !this.mapBlocks.length) {
				if (this.isDebugMode) {
					console.log('[Maps.js] No google maps found on this page. Skipping.');
				}
				return;
			}

			// Attach consent listener
			this.attachConsentListener();

			var self = this;

			if (this.isDebugMode) {
				console.log('[Maps.js] Initial check on pageload.');
			}

			// Initial check on pageload
			this.mapBlocks.each(function() {
				var map = BEM.block(this);

				if (!self.hasRequiredConsentLocalStorage()) {
					// Lock Map
					self.lockGoogleMap(map);
				} else {
					// Unlock Map
					self.initGoogleMap(map);
					self.unlockGoogleMap(map);
				}
			});
		},

		hasRequiredConsentLocalStorage: function() {
			// Check actual consent
			try {
				var stored = localStorage.getItem(this.localStorageConsentIdentifier);
				if (stored) {
					var data = JSON.parse(stored);
					return data.accepted && data.accepted.includes(this.consentIdentifierVideo);
				}
			} catch (e) {}

			return false;
		},

		unlockGoogleMap: function(map) {
			this.toggleCookieOverlay(false, map);

			if (this.isDebugMode) {
				console.log('[Maps.js] UNLOCKED Google Map.');
			}
		},

		lockGoogleMap: function(map) {
			this.modifyCookieOverlayText(map);
			this.toggleCookieOverlay(true, map);

			if (this.isDebugMode) {
				console.log('[Maps.js] LOCKED Google Map.');
			}
		},

		attachConsentListener: function() {
			// Prevent duplicate event listeners
			if (this.consentListenerAttached) {
				return;
			}

			this.consentListenerAttached = true;
			var self = this;

			// Consent listener (event dispatched via civik-cookie-control.js)
			window.addEventListener(self.consentEventIdentifier, function(event) {
				if (self.isDebugMode) {
					console.log('[Maps.js] Cookie consent changed event triggered', event);
				}

				// BEM (b_map)
				var mapBlocks = BEM.block('map');

				// Skip logic
				if (typeof mapBlocks === 'undefined' || !mapBlocks.length) {
					if (self.isDebugMode) {
						console.log('[Maps.js] No google maps found on this page. Skipping.');
					}
					return;
				}

				try {
					var payload = event.detail && event.detail.payload;
					var hasConsent = payload && payload.accepted &&
						payload.accepted.includes(self.consentIdentifierVideo);

					mapBlocks.each(function() {
						var map = BEM.block(this);

						if (!hasConsent) {
							// Lock Map
							self.lockGoogleMap(map);
						} else {
							// Unlock Map
							self.initGoogleMap(map);
							self.unlockGoogleMap(map);
						}
					});

				} catch (error) {
					if (self.isDebugMode) {
						console.log('[Maps.js] Error processing consent event:', error);
					}
				}
			});

			if (this.isDebugMode) {
				console.log('[Maps.js] Consent event listener attached');
			}
		},

		modifyCookieOverlayText: function (map) {
			const overlayTitle = map.getElements('cookie-overlay-title');
			const overlayContent = map.getElements('cookie-overlay-text');
			const overlayButton = map.getElements('cookie-overlay-button');

			if (overlayContent) {
				overlayContent.html(Translator.translate('Please accept functional cookies to view this map from Google Maps.').stripTags());
			}

			if (overlayButton) {

				overlayButton.html(Translator.translate('Cookie settings').stripTags());
			}

			if (this.isDebugMode) {
				console.log('[Maps.js] Translated cookie banner texts.');
			}
		},

		toggleCookieOverlay: function (lock, map) {

			if (lock !== false) {
				lock = true;
			}

			if (lock) {
				// add overlay
				if (!map.bemHasModifier('cookie-denied')) {
					map.bemAddModifier('cookie-denied');
				}
				if (this.isDebugMode) {
					console.log('[Maps.js] Add cookie-denied class');
				}
			} else {
				// remove Overlay
				map.bemRemoveModifier('cookie-denied');
				if (this.isDebugMode) {
					console.log('[Maps.js] Remove cookie-denied class');
				}
			}
		},

		initGoogleMap: function(block) {
			var self = this;

			if (!this.googleMapsScriptsInjected) {

				var apiKey = Config.get('google.maps.api.key');
				var apiUrl = 'https://maps.googleapis.com/maps/api/js?v=3&key=' + apiKey + '&sensor=false';
				var mapContainer = block.bemGetElements('map');
				var location = mapContainer.data('location-coords');

				// Inject Google maps scripts with async loading
				var script = document.createElement('script');
				script.src = apiUrl;
				script.async = true;
				script.defer = true;

				script.onload = function() {
					// Init map
					var map = new google.maps.Map(mapContainer.get(0), {
						center: location,
						zoom: 12
					});
					// Init marker
					var marker = new google.maps.Marker({
						position: location,
						map: map
					});
					if (self.isDebugMode) {
						console.log('[Maps.js] Google maps vendor scripts injected.');
					}
				};

				document.head.appendChild(script);
			}

			this.googleMapsScriptsInjected = true;
		}
	});
});

define('shoptheme/components/customer/b2e/popup',[
	"jquery",
	"cartware/components/_base",
	"cartware/jquery/bem",
	"text!shoptheme/templates/magnific-popup/default.html",
	"magnific-popup"
], function(
	$,
	Component,
	BEM,
	PopupHtml
) {

	var popupTemplate = null;

	return Component.create({
		blocks:	null,

		$init: function() {
			var self = this;
			this.block = BEM.block('b2e-customer-notice');
			this.form = window.b2eConsentForm;

			var legalTextClassName = BEM.buildElementClassname('b2e-customer-notice', 'wrapper'),
				legalBlock = this.block.find('.' + legalTextClassName);

			if (legalBlock.length) {
				this._showMessage('', legalBlock.html());
				legalBlock.remove();
			}
		},

		_renderPopup: function(data) {
			if (null === popupTemplate) {
				popupTemplate = _.template(PopupHtml);
			}

			return popupTemplate(data);
		},

		_showMessage: function(title, message) {
			var root = this;
			$.magnificPopup.open({
				items: {
					src: this._renderPopup({
						title: title,
						body: message
					}),
					type: 'inline',
				},
				callbacks: {
					open: function(a,b,c) {
						var self = this;
						var form = this.contentContainer.find('#b2e_consent_form');
						var b2eConsentForm = new VarienForm('b2e_consent_form');
						form.on('submit', function (e) {
							e.preventDefault();
							if (b2eConsentForm.validator.validate()) {
								root.setCookie('should_agree_b2e', 0);
								self.close();
							}
						})
					},
					close: function() {
					}
				},
				modal: true
			});
		},

		setCookie: function(name, value, days) {
			var expires = "";
			var domainName = window.location.hostname;
			if (days) {
				var date = new Date();
				date.setTime(date.getTime() + (days*24*60*60*1000));
				expires = "; expires=" + date.toUTCString();
			}
			document.cookie = name + "=" + (value || "")  + expires + "; path=/; domain=." + domainName + ";";
		}
	});
});
(function(){/*

 Copyright The Closure Library Authors.
 SPDX-License-Identifier: Apache-2.0
*/
/*
 jQuery v1.9.1 (c) 2005, 2012
 jQuery Foundation, Inc. jquery.org/license.
*/
var f=/\[object (Boolean|Number|String|Function|Array|Date|RegExp|Arguments)\]/;function g(a){return null==a?String(a):(a=f.exec(Object.prototype.toString.call(Object(a))))?a[1].toLowerCase():"object"}function m(a,b){return Object.prototype.hasOwnProperty.call(Object(a),b)}function n(a){if(!a||"object"!=g(a)||a.nodeType||a==a.window)return!1;try{if(a.constructor&&!m(a,"constructor")&&!m(a.constructor.prototype,"isPrototypeOf"))return!1}catch(c){return!1}for(var b in a);return void 0===b||m(a,b)};function p(a,b){var c={},d=c;a=a.split(".");for(var e=0;e<a.length-1;e++)d=d[a[e]]={};d[a[a.length-1]]=b;return c}function q(a,b){var c=!a._clear,d;for(d in a)if(m(a,d)){var e=a[d];"array"===g(e)&&c?("array"===g(b[d])||(b[d]=[]),q(e,b[d])):n(e)&&c?(n(b[d])||(b[d]={}),q(e,b[d])):b[d]=e}delete b._clear};/*
 Copyright 2012 Google Inc. All rights reserved. */
function r(a,b,c){b=void 0===b?{}:b;"function"===typeof b?b={listener:b,listenToPast:void 0===c?!1:c,processNow:!0,commandProcessors:{}}:b={listener:b.listener||function(){},listenToPast:b.listenToPast||!1,processNow:void 0===b.processNow?!0:b.processNow,commandProcessors:b.commandProcessors||{}};this.a=a;this.l=b.listener;this.j=b.listenToPast;this.g=this.i=!1;this.c={};this.f=[];this.b=b.commandProcessors;this.h=u(this);var d=this.a.push,e=this;this.a.push=function(){var k=[].slice.call(arguments,
0),l=d.apply(e.a,k);v(e,k);return l};b.processNow&&this.process()}r.prototype.process=function(){this.registerProcessor("set",function(){var c={};1===arguments.length&&"object"===g(arguments[0])?c=arguments[0]:2===arguments.length&&"string"===g(arguments[0])&&(c=p(arguments[0],arguments[1]));return c});this.i=!0;for(var a=this.a.length,b=0;b<a;b++)v(this,[this.a[b]],!this.j)};r.prototype.get=function(a){var b=this.c;a=a.split(".");for(var c=0;c<a.length;c++){if(void 0===b[a[c]])return;b=b[a[c]]}return b};
r.prototype.flatten=function(){this.a.splice(0,this.a.length);this.a[0]={};q(this.c,this.a[0])};r.prototype.registerProcessor=function(a,b){a in this.b||(this.b[a]=[]);this.b[a].push(b)};
function v(a,b,c){c=void 0===c?!1:c;if(a.i&&(a.f.push.apply(a.f,b),!a.g))for(;0<a.f.length;){b=a.f.shift();if("array"===g(b))a:{var d=a.c;g(b[0]);for(var e=b[0].split("."),k=e.pop(),l=b.slice(1),h=0;h<e.length;h++){if(void 0===d[e[h]])break a;d=d[e[h]]}try{d[k].apply(d,l)}catch(w){}}else if("arguments"===g(b)){e=a;k=[];l=b[0];if(e.b[l])for(d=e.b[l].length,h=0;h<d;h++)k.push(e.b[l][h].apply(e.h,[].slice.call(b,1)));a.f.push.apply(a.f,k)}else if("function"==typeof b)try{b.call(a.h)}catch(w){}else if(n(b))for(var t in b)q(p(t,
b[t]),a.c);else continue;c||(a.g=!0,a.l(a.c,b),a.g=!1)}}r.prototype.registerProcessor=r.prototype.registerProcessor;r.prototype.flatten=r.prototype.flatten;r.prototype.get=r.prototype.get;r.prototype.process=r.prototype.process;window.DataLayerHelper=r;function u(a){return{set:function(b,c){q(p(b,c),a.c)},get:function(b){return a.get(b)}}};})();

define("google-datalayer-helper", function(){});

define('rebrush/components/plugin/civik-cookie-control',[
    'jquery',
    'lodash',
    'cartware/components/_base',
    'cartware/components/Dispatcher',
    'google-datalayer-helper'
], function(
    $,
    _,
    Component,
    ComponentDispatcher
) {
    const cookieControlTriggerClass = 'civik-cookie-control-trigger';
    const cookieControlOverlayClass = 'ccc-overlay';
    const cookieBannerVendorTriggerIcon = 'ccc-icon'; // icon not in use
    const identifierStorageAndCookie = 'civik:cookie-control';
    const identifierCookieBannerPreviouslyAccepted = 'civik:cookie-control-banner-previously-interacted';
    var self;

    function modifyAjaxResponse(response) {

        // Varnish ESI Script replacement by given consent
        if (response.getHeader('content-type') && response.getHeader('content-type').match(/text\/html/gi)) {

            var container = $('<div />');
            container.html(response.responseText);

            if (self.isDebugMode) {
                console.log('[Civik Cookie Banner] Async Varnish ESI Script replacement started.');
            }

            self.replaceScriptsByConsent(container);

            if (self.isDebugMode) {
                console.log('[Civik Cookie Banner] Async Varnish ESI Script replacement finished.');
            }
        }
    }

    return Component.create({
        $init: function () {
            self = this;

            // Set to true for debug mode
            this.isDebugMode = _.get(window, 'gigaset.featureflags.developer_mode', false);

            // Init Event Listeners
            this.bindEvents();

            // GTM Datalayer Listeners
            this.bindConsentChangedListenerGTM();

            // Initial sync for page load
            this.syncScriptsByConsent().then(function() {
                if (self.isDebugMode) {
                    console.log('[Civik Cookie Banner] All scripts synced.');
                }
            });

            // Overwrite some vendor functions
            this.overwriteCivikCookieControlFunctions();

            // Track first time visitors
            this.initFirstTimeVisitCookieBanner();

            this.initHeadingLevelFix();
        },

        initHeadingLevelFix: function() {
            // Civic renders category headers as h3/h4 directly below the h1 title, which skips h2
            const selector = '#ccc h3.optional-cookie-header:not([aria-level]), #ccc h4.optional-cookie-header:not([aria-level])';

            const fixHeadingLevels = () => {
                document.querySelectorAll(selector).forEach(function(heading) {
                    heading.setAttribute('aria-level', heading.tagName === 'H3' ? '2' : '3');
                });
            };

            fixHeadingLevels();

            new MutationObserver(function() {
                if (document.querySelector(selector)) {
                    fixHeadingLevels();
                }
            }).observe(document.documentElement, { childList: true, subtree: true });
        },

        bindEvents: function() {
            var self = this;

            // Varnish ESI
            Ajax.Responders.register({
                onComplete: function(request, response) {
                    modifyAjaxResponse(response);
                }
            });

            // Varnish ESI
            $(document).ajaxComplete(function(event, xhr) {
                xhr.getHeader = xhr.getResponseHeader;
                modifyAjaxResponse(xhr);
            });

            // Show Cookie Banner on class trigger (for footer link triggers)
            $(document.body).on('click', '.' + cookieControlTriggerClass, function(e) {
                e.preventDefault();
                self.showCookieBanner();
            });

            // General listeners for civik banner buttons (more specific binding does not work cause of shadow DOM)
            $(document).on('click', '', function(e) {
                const target = e.target;
                if (target.classList.contains('civic-cookie-btn-acceptAll') || target.classList.contains('civic-cookie-btn-close')) {
                    self.dispatchGTMEventCookieConsentAccept(self);
                    self.setCookieBannerAcceptedFlag();
                }
            });

            // Click Listener for Cookie Control overlay accept method
            $(document.body).on('click', '#' + cookieControlOverlayClass, function(e) {
                e.preventDefault();
                self.hideCookieBanner();
                self.dispatchGTMEventCookieConsentAccept(self);
                self.setCookieBannerAcceptedFlag();
            });
        },

        dispatchGTMEventCookieConsentAccept: function(self) {
            const getIsFirstTimeVisitor = self.getIsFirstTimeVisitor();
            var GTMEvent;

            if (getIsFirstTimeVisitor) {
                GTMEvent = 'civic:cookie-consent-accept-first';
            } else {
                GTMEvent = 'civic:cookie-consent-accept';
            }

            // Push datalayer event
            window.dataLayer.push({event: GTMEvent});

            if (self.isDebugMode) {
                console.log('[Civik Cookie Banner] Dispatched GTM Event (', GTMEvent, ')');
            }
        },

        bindConsentChangedListenerGTM: function() {
            // GTM - Callback Consent Listener
            var self = this;

            function consentChangedListener(consent) {

                if (self.isDebugMode) {
                    console.log('[Civik Cookie Banner] Consent change received from GTM:', consent);
                }

                // 500ms delay to let Civic update the cookie payload
                setTimeout(() => {

                    // Sync scripts according to given consent
                    self.syncScriptsByConsent().then(() => {
                        // Entry point for other consumers to listen to
                        if (self.consentData) {
                            const event = new CustomEvent("civik:cookie-consent-changed", {
                                detail: {
                                    payload: self.consentData
                                }
                            });
                            if (self.isDebugMode) {
                                console.log('[Civik Cookie Banner] Dispatched custom JS Event (civik:cookie-consent-changed) for other consumers to hook into.');
                            }
                            window.dispatchEvent(event);
                        }
                    }).catch(err => {
                        if (self.isDebugMode) {
                            console.error('[Civik Cookie Banner] Sync on consent change failed:', err);
                        }
                    });

                }, 500);
            }

            window.__consentChangedListenerGTM = consentChangedListener;
        },

        overwriteCivikCookieControlFunctions: function() {
            const self = this;

            if (window.CookieControl) {

                // Overwrite open function
                if (typeof window.CookieControl.open === 'function') {

                    const originalOpen = window.CookieControl.open;

                    window.CookieControl.open = function(...args) {
                        // Apply original open
                        originalOpen.apply(this, args);
                        // Add observers for class manipulation as otherwise the span in buttons wouldn't trigger
                        self.initObservers();
                    };
                }
            }
        },

        initObservers: function() {
            // Only observe the document
            const root = document.documentElement;

            const observer = new MutationObserver((mutations) => {
                let needsCheck = false;

                // Quick pre-scan: only proceed if any added node could contain our targets
                for (const mutation of mutations) {
                    if (mutation.type === 'childList' && mutation.addedNodes.length) {
                        for (const node of mutation.addedNodes) {
                            if (node.nodeType === 1 && (
                                (node.matches && node.matches('#ccc-recommended-settings, #ccc-close')) ||
                                node.querySelector && node.querySelector('#ccc-recommended-settings, #ccc-close')
                            )) {
                                needsCheck = true;
                                break;
                            }
                        }
                        if (needsCheck) break;
                    }
                }

                if (!needsCheck) {
                    return;
                }

                // Only when one of our buttons has appeared
                this.applyCivicClasses();
            });

            // Apply classes
            let scheduled = false;
            this.applyCivicClasses = () => {
                if (scheduled) {
                    return;
                }
                scheduled = true;

                requestAnimationFrame(() => {
                    scheduled = false;

                    this.styleAcceptAllButton();
                    this.styleCloseButton();

                    // Disconnect observer if both buttons are styled
                    if (this.bothButtonsStyled()) {
                        observer.disconnect();
                    }
                });
            };

            this.styleAcceptAllButton = () => {
                const btn = document.getElementById('ccc-recommended-settings');
                if (btn && !btn._civicStyled) {
                    btn.classList.add('civic-cookie-btn-acceptAll');
                    const span = btn.querySelector('span');
                    if (span) {
                        span.classList.add('civic-cookie-btn-acceptAll');
                    }
                    btn._civicStyled = true;
                }
            };

            this.styleCloseButton = () => {
                const btn = document.getElementById('ccc-close');
                if (btn && !btn._civicStyled) {
                    btn.classList.add('civic-cookie-btn-close');
                    const svg = btn.querySelector('svg');
                    if (svg) {
                        svg.classList.add('civic-cookie-btn-close');
                    }
                    btn._civicStyled = true;
                }
            };

            this.bothButtonsStyled = () => {
                return document.getElementById('ccc-recommended-settings')
                    && document.getElementById('ccc-recommended-settings')._civicStyled
                    && document.getElementById('ccc-close')
                    && document.getElementById('ccc-close')._civicStyled;
            };

            // Initial check in case buttons already exist
            this.applyCivicClasses();

            // Start observing
            observer.observe(root, { childList: true, subtree: true });
        },

        initFirstTimeVisitCookieBanner: function() {
            if (this.getIsFirstTimeVisitor()) {
                // Trigger the banner automatically when cookies were not accepted yet
                this.showCookieBanner();
            }
        },

        getPreviousConsentStateExists: function() {
            const localStorage = window.localStorage;
            const storageConsent = localStorage.getItem(identifierStorageAndCookie);

            var previousConsentStateExists = false;

            if (storageConsent) {
                try {
                    const consentData = JSON.parse(storageConsent);

                    // Check if previous consent data is already available
                    previousConsentStateExists = consentData &&
                        typeof consentData === 'object' &&
                        'accepted' in consentData &&
                        'denied' in consentData;
                } catch (e) {
                    // Invalid JSON, no previous consent
                    previousConsentStateExists = false;
                }
            }

            return previousConsentStateExists;
        },

        waitForCookieControl: function(timeout = 15000, interval = 100) {
            return new Promise((resolve, reject) => {
                const startTime = Date.now();

                const checkForCookieControl = () => {
                    const cookieControl = _.get(window, 'CookieControl');

                    if (cookieControl && typeof cookieControl.open === 'function') {
                        try {
                            // Test if can actually call methods without error
                            if (typeof cookieControl.getAllCookies === 'function') {
                                resolve(cookieControl);
                                return;
                            }
                        } catch (e) {
                            // Not ready yet, continue polling
                        }
                    }

                    if (Date.now() - startTime >= timeout) {
                        reject(new Error('[Civik Cookie Banner] Error: CookieControl not available within timeout'));
                        return;
                    }

                    setTimeout(checkForCookieControl, interval);
                };

                checkForCookieControl();
            });
        },

        showCookieBanner: async function () {
            try {
                const cookieControl = await this.waitForCookieControl();
                if (this.isDebugMode) {
                    console.log('[Civik Cookie Banner] Triggered Cookie Banner open()');
                }
                cookieControl.open();
            } catch (error) {
                if (this.isDebugMode) {
                    console.error('[Civik Cookie Banner] Cannot show cookie banner:', error);
                }
            }
        },

        hideCookieBanner: async function () {
            try {
                const cookieControl = await this.waitForCookieControl();
                if (this.isDebugMode) {
                    console.log('[Civik Cookie Banner] Banner hidden()');
                }
                cookieControl.hide();
            } catch (error) {
                if (this.isDebugMode) {
                    console.error('Cannot hide cookie banner:', error);
                }
            }
        },

        getCookieControlData: async function () {
            try {
                const cookieControlObject = await this.waitForCookieControl();
                const cookieData = cookieControlObject.getAllCookies();

                if (!cookieData || !cookieData.CookieControl) {
                    return { categories: [], accepted: [], denied: [] };
                }

                var payload = cookieData.CookieControl;
                payload = JSON.parse(payload);

                if (!payload || !payload.optionalCookies) {
                    return { categories: [], accepted: [], denied: [] };
                }

                const optionalCookiesOriginal = payload.optionalCookies;
                const optionalCookies = Object.fromEntries(
                    Object.entries(optionalCookiesOriginal).map(([key, value]) => [key.toLowerCase(), value])
                );

                const categories = Object.keys(optionalCookies);

                const accepted = categories.filter(function(cat) {
                    return optionalCookies[cat] === 'accepted';
                });

                const denied = categories.filter(function(cat) {
                    return optionalCookies[cat] === 'revoked';
                });

                var consent = { categories, accepted, denied};

                // Save consent to local storage and document cookie
                this.saveCookieConsentLocalStorage(consent);
                this.saveCookieConsentDocumentCookie(consent);

                // Save to local var
                this.consentData = consent;

                if (this.isDebugMode) {
                    console.log('[Civik Cookie Banner] Cookie payload: ', consent);
                }

                return consent;

            } catch (error) {

                if (this.isDebugMode) {
                    console.error('[Civik Cookie Banner] Error getting cookie control data:', error);
                }

                return { categories: [], accepted: [], denied: [] };
            }
        },

        _activateCategoryScripts: function (category, isAccepted, searchContainer = $(document)) {
            const origSelectors = `script[data-civik-cookie-control="${category}"][type="text/plain"]`;
            const originals = searchContainer.find(origSelectors);

            const cloneSelectors = `script[data-civik-cookie-control="${category}"][type="text/javascript"][data-activated-clone="true"]`;
            const clones = $(document).find(cloneSelectors);

            var self = this;

            if (isAccepted) {
                // Grant: clone and activate if not done
                originals.each(function () {
                    const original = $(this);

                    if (!original.data('activated')) {

                        const newScript = $('<script></script>')
                            .attr('type', 'text/javascript')
                            .attr('data-activated-clone', true)
                            .attr('data-civik-cookie-control', category);

                        if (original.attr('src')) {
                            newScript.attr('src', original.attr('src'));
                        }

                        if (original.attr('async')) {
                            newScript.attr('async', original.attr('async'));
                        }

                        if (original.attr('id')) {
                            newScript.removeAttr('id'); // Avoid duplicate IDs
                        }

                        // Copy inline content if any
                        newScript.html(original.html());

                        $('body').append(newScript);
                        original.data('activated', true);
                    }
                });

                if (self.isDebugMode) {
                    console.log(`[Civik Cookie Banner] Activated ${originals.length} ${category} Scripts.`);
                }
            } else {
                // Revoke: nuke clones, reset flags
                clones.remove();

                if (self.isDebugMode) {
                    console.log(`[Civik Cookie Banner] Revoked ${clones.length} ${category} Scripts.`);
                }

                originals.each(function () {
                    $(this).data('[Civik Cookie Banner] activated', false);
                });
            }
        },

        replaceScriptsByConsent: async function (searchContainer) {
            const { accepted } = await this.getCookieControlData();
            if (!accepted.length) {
                return;
            }

            accepted.forEach(cat => {
                this._activateCategoryScripts(cat, true, searchContainer);
            });
        },

        syncScriptsByConsent: async function () {
            const { categories, accepted, denied } = await this.getCookieControlData();

            if (this.isDebugMode) {
                console.log('[Civik Cookie Banner] Start sync scripts by consent.');
            }

            categories.forEach(cat => {
                const isAccepted = accepted.includes(cat);
                this._activateCategoryScripts(cat, isAccepted);
            });
        },

        getIsFirstTimeVisitor: function () {
            // Checks if the at least once visitor fully interacted with the cookie banner (Close via X, Accept All Btn, Click on Overlay)
            const localStorage = window.localStorage;
            const isAcceptedFlagSet = localStorage.getItem('civik:cookie-control-banner-accepted-flag');

            if (isAcceptedFlagSet !== null && isAcceptedFlagSet.length > 0) {
                return false;
            }

            return !this.hasCivicDecision();
        },

        // Civic opens itself (initialState "open" in the GTM config), so its buttons often lack the classes the
        // accepted-flag listener waits for. Civic stores accepted/revoked per category in its own cookie either way;
        // interactedWith is no signal, Civic sets it as soon as the banner was shown.
        hasCivicDecision: function () {
            const match = document.cookie.match(/(?:^|; )CookieControl=([^;]*)/);

            if (!match) {
                return false;
            }

            try {
                return Object.keys(JSON.parse(decodeURIComponent(match[1])).optionalCookies || {}).length > 0;
            } catch (e) {
                return false;
            }
        },

        setCookieBannerAcceptedFlag: function () {
            const localStorage = window.localStorage;

            if (!localStorage.getItem('civik:cookie-control-banner-accepted-flag')) {
                localStorage.setItem('civik:cookie-control-banner-accepted-flag', '1');

                if (this.isDebugMode) {
                    console.log('[Civik Cookie Banner] Set local storage cookie banner interaction flag: "civik:cookie-control-banner-accepted-flag"');
                }
            }
        },

        saveCookieConsentLocalStorage: function (consent) {
            var storageKey = identifierStorageAndCookie;
            var localStorage =  window.localStorage;
            var existingStorageData = localStorage.getItem(storageKey);

            if (existingStorageData) {
                localStorage.removeItem(storageKey);
            }

            localStorage.setItem(storageKey, JSON.stringify(consent));

            if (this.isDebugMode) {
                console.log('[Civik Cookie Banner] Saved local Storage cookie consent data.');
            }
        },

        saveCookieConsentDocumentCookie: function (consent) {
            // Acts as a second alternative to the local storage for devs to be used potentially (could be used from GTM)
            this.setCookie(identifierStorageAndCookie, JSON.stringify(consent));

            if (this.isDebugMode) {
                console.log('[Civik Cookie Banner] Saved civik:cookie-control cookie.');
            }
        },

        setCookie: function (name, value, days = 30, encode = false) {
            const date = new Date();
            date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
            const expires = "; expires=" + date.toUTCString();

            document.cookie = name + "=" + (encode ? encodeURIComponent(value) : value) + expires + "; path=/";
        }
    });
});

define('rebrush/components/review/bazaarvoice-analytics',[
    "jquery",
    "lodash",
    "cartware/components/_base",
    "cartware/jquery/bem"
], function(
    $,
    _,
    Component,
    BEM
) {
    return Component.create({
        consentIdentifier: 'marketing',
        localStorageConsentIdentifier: 'civik:cookie-control',
        consentEventIdentifier: 'civik:cookie-consent-changed',
        consentListenerAttached: false,
        transactionDataSelector: '#bv-transaction-data',
        transactionPixelFired: false,
        pieZoneScriptInjected: false,
        bvCallbackInvocationCount: 0,

        $init: function() {
            this.isDebugMode = _.get(window, 'gigaset.featureflags.developer_mode', false);
            this.initialize();
            this.attachConsentListener();
        },

        attachConsentListener: function() {
            if (this.consentListenerAttached) {
                return;
            }
            this.consentListenerAttached = true;

            window.addEventListener(this.consentEventIdentifier, (event) => {
                if (this.isDebugMode) {
                    console.log('[bazaarvoice-analytics.js] Cookie consent changed event triggered', event);
                }

                try {
                    const payload = event.detail && event.detail.payload;
                    const hasConsent = payload && payload.accepted &&
                        payload.accepted.includes(this.consentIdentifier);

                    if (hasConsent) {
                        this.grantConsentBazaarvoiceAnalytics();
                    } else {
                        this.revokeConsentBazaarvoiceAnalytics();
                    }
                } catch (error) {
                    if (this.isDebugMode) {
                        console.log('[bazaarvoice-analytics.js] Error processing consent event:', error);
                    }
                }
            });

            if (this.isDebugMode) {
                console.log('[bazaarvoice-analytics.js] Consent event listener attached');
            }
        },

        initialize: function() {
            const consentGiven = this.hasConsent();

            if (this.isDebugMode) {
                console.log('[bazaarvoice-analytics.js] Initialize called, consent:', consentGiven);
            }

            if (consentGiven) {
                this.grantConsentBazaarvoiceAnalytics();
            } else {
                this.revokeConsentBazaarvoiceAnalytics();
            }
        },

        hasConsent: function() {
            try {
                const stored = localStorage.getItem(this.localStorageConsentIdentifier);
                if (stored) {
                    const data = JSON.parse(stored);
                    return data.accepted && data.accepted.includes(this.consentIdentifier);
                }
            } catch (e) {}
            return false;
        },

        grantConsentBazaarvoiceAnalytics: function() {
            if (this.isDebugMode) {
                console.log('[bazaarvoice-analytics.js] Granted consent to bazaarvoice analytics.');
            }
            this._setBazaarvoiceConsent(true);
        },

        revokeConsentBazaarvoiceAnalytics: function() {
            if (this.isDebugMode) {
                console.log('[bazaarvoice-analytics.js] Revoked consent for bazaarvoice analytics.');
            }
            this._setBazaarvoiceConsent(false);
        },

        _setBazaarvoiceConsent: async function(granted) {
            await this._onBvReady();

            if (window.BV && window.BV.cookieConsent) {
                window.BV.cookieConsent.setConsent({
                    bv_metrics: granted,
                    BVBRANDID: granted,
                    BVBRANDSID: granted
                });
            } else if (this.isDebugMode) {
                console.log('[bazaarvoice-analytics.js] window.BV.cookieConsent still not available.');
            }

            if (granted) {
                this._fireTransactionPixel();
            }
        },

        _fireTransactionPixel: async function() {
            if (this.transactionPixelFired) {
                return;
            }

            const dataEl = document.querySelector(this.transactionDataSelector);
            if (!dataEl) {
                if (this.isDebugMode) {
                    console.log('[bazaarvoice-analytics.js] No #bv-transaction-data element on this page, skipping pixel.');
                }
                return;
            }

            // Claimed before the first await: initialize() and each consent event from GTM run in parallel and would all
            // send the pixel. Kept when trackTransaction() throws, the beacons may already be out.
            this.transactionPixelFired = true;

            this._loadPieZoneScript(dataEl);

            const isReady = await this._pollUntilReady(
                () => !!(window.BV && window.BV.pixel) && this._hasBrandCookies()
            );

            if (!isReady) {
                this.transactionPixelFired = false;
                return;
            }

            try {
                const transactionData = JSON.parse(dataEl.textContent);
                window.BV.pixel.trackTransaction(transactionData);

                if (this.isDebugMode) {
                    console.log('[bazaarvoice-analytics.js] BV transaction pixel fired.', transactionData);
                }
            } catch (error) {
                if (this.isDebugMode) {
                    console.log('[bazaarvoice-analytics.js] Failed to send BV transaction pixel:', error);
                }
            }
        },

        _hasBrandCookies: function() {
            return /(?:^|; )BVBRANDID=/.test(document.cookie) &&
                /(?:^|; )BVBRANDSID=/.test(document.cookie);
        },

        _loadPieZoneScript: function(dataEl) {
            if (this.pieZoneScriptInjected) {
                return;
            }

            const src = dataEl.getAttribute('data-bv-loader-src');
            if (!src) {
                return;
            }

            this.pieZoneScriptInjected = true;

            const script = document.createElement('script');
            script.src = src;
            document.head.appendChild(script);

            if (this.isDebugMode) {
                console.log('[bazaarvoice-analytics.js] Injected pie zone bv.js after consent was set:', src);
            }
        },

        _pollUntilReady: function(conditionFn, timeout = 5000, interval = 250) {
            return new Promise((resolve) => {
                const startTime = Date.now();

                const check = () => {
                    if (conditionFn()) {
                        resolve(true);
                        return;
                    }

                    if (Date.now() - startTime >= timeout) {
                        if (this.isDebugMode) {
                            console.log(`[bazaarvoice-analytics.js] Gave up waiting for condition after ${timeout}ms.`);
                        }
                        resolve(false);
                        return;
                    }

                    setTimeout(check, interval);
                };

                check();
            });
        },

        // Any $BV call on the bv.js stub loads the legacy bvapi.js and bv-primary.js (255 KB). Only the transaction
        // page keeps waiting for it: the pixel needs the BVBRANDID cookies, which bv.js alone does not set reliably.
        _hasLegacyReady: function() {
            return !!document.querySelector(this.transactionDataSelector) &&
                typeof $BV !== 'undefined' && $BV && typeof $BV.ready === 'function';
        },

        _onBvReady: function() {
            return new Promise((resolve) => {
                if (this._hasLegacyReady()) {
                    $BV.ready(resolve);
                    return;
                }

                if (window.BV && window.BV.cookieConsent) {
                    resolve();
                    return;
                }

                const previousBvCallback = window.bvCallback;

                window.bvCallback = () => {
                    this.bvCallbackInvocationCount++;

                    if (this.isDebugMode) {
                        console.log('[bazaarvoice-analytics.js] window.bvCallback invoked, count:', this.bvCallbackInvocationCount);
                    }

                    if (typeof previousBvCallback === 'function') {
                        previousBvCallback();
                    }

                    if (this._hasLegacyReady()) {
                        $BV.ready(resolve);
                    } else {
                        resolve();
                    }
                };
            });
        },
    });
});

define('rebrush/system/layout/responsive-hub',[
    'lodash',
    'lucid',
    'json!shoptheme/etc/breakpoints.json',
    'enquire'
], function(
    _,
    lucidJS,
    BreakPoints,
    enquire
) {
    var _instance = null;
    var isMobile = true;

    function getInstance() {
        if (_instance === null) {
            _instance = new PubSubHub();
        }

        return _instance;
    };

    function PubSubHub() {
        if (!(this instanceof PubSubHub)) {
            return getInstance();
        }

        lucidJS.EventEmitter.call(this);

        this._register = {};
    }

    PubSubHub.getInstance = getInstance;

    PubSubHub.prototype = _.create(new lucidJS.EventEmitter, {
        'constructor': PubSubHub,
        'isMobile': function() {
            return isMobile;
        }
    });

    enquire.register('(min-width: ' + (BreakPoints.sm + 1) + 'px)', {
        match: function() {
            var instance = getInstance();
            isMobile = false;

            instance.unflag('change.mobile.enter');
            instance.flag('change.mobile.leave');
        },
        unmatch: function() {
            var instance = getInstance();
            isMobile = true;

            instance.unflag('change.mobile.leave');
            instance.flag('change.mobile.enter');
        }
    }, true);

    return PubSubHub.getInstance();
});
define('rebrush/components/layout/page/switch/storeview',[
	'cartware/components/_base',
	'jquery',
	'lodash',
	'cartware/jquery/bem',
	'rebrush/system/layout/responsive-hub',
	'text!shoptheme/templates/magnific-popup/storeswitch.html',
	'bootstrap-popover'
], function(
	Component,
	$,
	_,
	BEM,
	ResponsiveHub,
	PopupHtml
) {
	var isMobile = true;

	return Component.create({
		block:				null,
		popoverTrigger:		null,
		popoverWrap:		null,
		popoverContainer:	null,
		storeGroupSelect:	null,
		currentGroup:		null,
		newGroup:			null,
		storeViewContainer:	null,
		storeViewSelects:	null,
		currentView:		null,
		currentViewIndex:	null,
		popup: 				null,
		popupAcceptBtn:		null,
        isRebuildGroupStore:  false,
        currentStoreCode:   null,
        activeView:         null,
        activeViewSelectedVal:   null,

		$init: function() {
			var self = this,
				url = null,
				storeGroup = null;

			this.block = BEM.block('page-switch');
			this.popupAcceptBtn = $('.storeswitch-accept');

			this.popoverTrigger = this.block.getElements('popover-trigger');
			this.popoverWrap = this.block.getElements('popover');
			this.popoverContainer = this.block.find('.' + BEM.buildBlockClassname('page-switch-popover'));

			this.storeGroupSelect = this.popoverContainer.find('.' + BEM.buildElementClassname('select', 'storegroup-select'));
			this.storeViewContainer = this.popoverContainer.bemGetElements('storeview-container');
			this.storeViewSelects = this.popoverContainer.find('.' + BEM.buildElementClassname('select', 'storeview-select'));
			this.currentGroup = this.storeGroupSelect.val();
            this.currentStoreCode = this.popoverWrap.data('current-store-code');
            this.activeView = this.storeViewContainer.filter('.b_page-switch-popover__storeview-container--active');
            this.activeViewSelectedVal = this.activeView.find('select.b_select__storeview-select').find('option[aria-selected="true"]').val();

			storeGroup = this.currentGroup;

			ResponsiveHub
				.on('change.mobile.enter', function() { isMobile = true; })
				.on('change.mobile.leave', function() { isMobile = false; });

			// Initialize focusable elements for keyboard navigation
			this._initKeyboardNavigation();

			this.popoverTrigger.popover({
				content: function() {
					return self.popoverContainer;
				},
				template: '<div class="popover popover-servicemenu" role="tooltip"><div class="arrow"></div><div class="popover-content"></div></div>',
				html: true,
				placement: function() {
					return isMobile ? 'top' : 'bottom';
				}
			});

			// Handle popover show/hide for focus management, specific to languagePopoverSwitcher
			this.popoverTrigger.on('shown.bs.popover', function(e) {
				// Check if the popover is the store view switcher
				var popoverId = $(this).attr('data-bs-target');
				if (popoverId !== '#languagePopoverSwitcher') {
					return;
				}
				self._focusFirstElement();
				self.popoverTrigger.attr('aria-expanded', 'true');
			}).on('hidden.bs.popover', function(e) {
                // Check if the popover is the store view switcher
				var popoverId = $(this).attr('data-bs-target');
				if (popoverId !== '#languagePopoverSwitcher') {
					return;
				}
				self.popoverTrigger.attr('aria-expanded', 'false');
				self.popoverTrigger.focus();
			});

			this.storeGroupSelect.on('change', function() {
				var select = $(this);
				storeGroup = select.val();
				var activeStoreViewContainer = self.storeViewContainer.filter('[data-store-group="' + storeGroup + '"]'),
					options = activeStoreViewContainer.find('option').filter(function() { return $(this).prop('value'); }),
					itemCount = $('.btn-mini-cart span').text();

				if (options.length === 1) {
					if (itemCount > 0) {
						$.magnificPopup.open({
							items: {
								src: self._renderPopup({
									url: options.prop('value'),
									title: Translator.translate('Notice'),
									body: Translator.translate('Changing the country will empty your shopping cart.'),
									cancel: Translator.translate('Cancel'),
									proceed: Translator.translate('Proceed')
								})
							},
							callbacks: {
								open: function () {
									self.popoverTrigger.popover('hide');
									self.storeViewContainer.bemRemoveModifier('active');
								}
							}
						});
					} else {
						self.storeViewContainer.bemRemoveModifier('active');
                        if (!this.isRebuildGroupStore) {
                            let url = options.prop('value');
                            if (options.data('mode') === 'external_website') {
                                self._rebuildStoreSelects();
                                window.open(url, '_blank');
                            } else {
                                self._redirectToStoreUrl(url);
                            }
                        }
                        return;
					}
				}

				self.storeViewContainer.bemRemoveModifier('active');
				activeStoreViewContainer.bemAddModifier('active');

				self.storeViewSelects.each(function() {
					$(this).data('select2').val(-1);
				});

				// Focus the corresponding language select
				var languageSelect = activeStoreViewContainer.find('.' + BEM.buildElementClassname('select', 'storeview-select'));
				if (languageSelect.length) {
					languageSelect.focus();
				}
			});

			this.storeViewSelects.on('change', function(e) {
				var url = $(this).val();
				var itemCount = $('.btn-mini-cart span').text(),
					view = $(this);

				if (null === url || '' === url) {
					return false;
				}
                if ($(this).find('option:selected').data('mode') === 'external_website') {
                    if (!this.isRebuildGroupStore) {
                        window.open(url, '_blank');
                        self._rebuildStoreSelects();
                        return;
                    }
                }

				if (itemCount > 0 && storeGroup !== self.currentGroup) {
					$.magnificPopup.open({
						items: {
							src: self._renderPopup({
								url: url,
								title: Translator.translate('Notice'),
								body: Translator.translate('Changing the country will empty your shopping cart.'),
								cancel: Translator.translate('Cancel'),
								proceed: Translator.translate('Proceed')
							})
						},
						callbacks: {
							open: function () {
								self.popoverTrigger.popover('hide');
								view[0].selectedIndex = 0;

								$.magnificPopup.instance.close = function () {
									// Call the original close method to close the popup
									$.magnificPopup.proto.close.call(this);
								};
							}
						}
					});
				} else {
                    self._redirectToStoreUrl(url);
				}
			});
		},

		_initKeyboardNavigation: function() {
			var self = this;
			var focusableElements = 'select:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])';
			var firstFocusableElement = null;
			var lastFocusableElement = null;

			// Update focusable elements within popover
			function updateFocusableElements() {
				var focusable = self.popoverContainer.find(focusableElements);
				firstFocusableElement = focusable.first();
				lastFocusableElement = focusable.last();
			}

			// Trap focus within popover
			function trapFocus(e) {
				if (e.key === 'Tab') {
					if (e.shiftKey) {
						if (document.activeElement === firstFocusableElement[0]) {
							e.preventDefault();
							lastFocusableElement.focus();
						}
					} else {
						if (document.activeElement === lastFocusableElement[0]) {
							e.preventDefault();
							firstFocusableElement.focus();
						}
					}
				} else if (e.key === 'Escape') {
					self.popoverTrigger.popover('hide');
				}
			}

			// Initialize focusable elements when popover is shown
			this.popoverTrigger.on('shown.bs.popover', function(e) {
				// Check if the popover is the store view switcher
				var popoverId = $(this).attr('data-bs-target');
				if (popoverId !== '#languagePopoverSwitcher') {
					return;
				}
				updateFocusableElements();
				self.popoverContainer.on('keydown', trapFocus);
			});

			// Clean up event listeners when popover is hidden
			this.popoverTrigger.on('hidden.bs.popover', function(e) {
				// Check if the popover is the store view switcher
				var popoverId = $(this).attr('data-bs-target');
				if (popoverId !== '#languagePopoverSwitcher') {
					return;
				}
				self.popoverContainer.off('keydown', trapFocus);
			});

			// Expose focus method for shown.bs.popover handler
			this._focusFirstElement = function() {
				updateFocusableElements();
				if (firstFocusableElement.length) {
					firstFocusableElement.focus();
				}
			};
		},

		_renderPopup: function(data) {
			if (null === this.popup) {
				this.popup = _.template(PopupHtml);
			}

			return this.popup(data);
		},

        _getStoreCode: function (url) {
            let pathParts = url.split('/');
            return pathParts[3];
        },

        _redirectToStoreUrl: function (url) {
            const selectedStoreCode = this._getStoreCode(url) || this.popoverWrap.data('default-store-code') || '';
            if (this.currentStoreCode !== selectedStoreCode) {
                window.location.href = url;
            }
        },

        _rebuildStoreSelects: function() {
            let self = this;
            let selectedGroupId = this.storeGroupSelect.prop('value');
            let currentGroupId = this.popoverWrap.data('current-group-id');
            if (selectedGroupId !== currentGroupId) {
                this.isRebuildGroupStore = true;
                this.storeGroupSelect.val(currentGroupId).trigger('change');
                this.storeViewSelects.val(self.activeViewSelectedVal).trigger('change');
                this.isRebuildGroupStore = false;
                this.popoverTrigger.trigger('click');
            }
        },
	});
});

define('rebrush/components/layout/navigation',[
	'jquery',
	'lodash',
	'cartware/components/_base',
	'cartware/components/PubSubHub',
	'cartware/jquery/bem',
	'json!shoptheme/etc/breakpoints.json',
	'enquire',
	'jquery-menu-aim',
	'modernizr',
	'bootstrap-collapse',
	'jquery-dlmenu'
], function(
	$,
	_,
	Component,
	PubSubHub,
	BEM,
	BreakPoints,
	enquire,
	menuAim
) {
	var isMobile = true;
	var MENU_ANIMATION = '3';

	return Component.create({
		blocks:					null,
		path:					null,
		area:					null,
		additionalCategories:	null,
		topmenu:				null,
		desktopAimInitialized:	false,

		$init: function() {
			this.blocks = BEM.block('nav');
			this.path = _.get(window, 'gigaset.category.current');
			this.area = _.get(window, 'gigaset.area');
			this.additionalCategories = _.get(window, 'gigaset.category.additional');
			this.topmenu = $('#topmenu');
			this.headerMenuTriggerBlock = BEM.block('header-menu-trigger');
			this.headerMenuTriggerButton = this.headerMenuTriggerBlock.getElements('button');
			this.recommendationsBlock = BEM.block('search-autocomplete-recommendations');

			var self = this;
			_.forEach(_.union(this.path, [this.area], this.additionalCategories), _.bind(function(categoryId) {
				var nodeId = ((categoryId|0) == categoryId)
					? 'category-node-' + categoryId
					: categoryId;

				self.blocks.find('[data-category-id="' + nodeId + '"]').bemAddModifier('active');
			}, this));

			// jQuery('#topmenu').data("dlmenu").openMenu()
			this.topmenu.dlmenu({
				animationClasses : { classin : 'dl-animate-in-' + MENU_ANIMATION, classout : 'dl-animate-out-' + MENU_ANIMATION },
				useActiveItemAsLink: true
			});

			this.initResponsive();
			this.bindEvents();
			this.prepareCustomerAccountNavigation();
			this.desktopMenuAim();
			this.initAccessibility();
			this.bindAccessibilityEvents();
		},

		bindEvents: function() {
			var menuItemClass = '.' + BEM.buildElementClassname('nav', 'menu-item', 'level0'),
				dlmenu = this.topmenu.data('dlmenu'),
				button = this.headerMenuTriggerButton,
				$body = $(document.body),
				self = this;

			$(document).on('mouseenter', menuItemClass, function(e) {
				$(e.currentTarget).addClass('hover');
			}).on('mouseleave', menuItemClass, function(e) {
				$(e.currentTarget).removeClass('hover');
			});

			if (dlmenu) {
				button.on('click', _.bind(function (e) {
					e.preventDefault();
					e.stopPropagation();

					if (dlmenu.open) {
						dlmenu.closeMenu();
					}  else {
						dlmenu.openMenu();
					}
				}, this));

				dlmenu
					.on('open', function() {
						button.addClass('is-active');
						$body.addClass('topmenu-mobile-open');
						PubSubHub.trigger.apply(PubSubHub, ['layout.topmenu.mobile.open'].concat(Array.prototype.slice.call(arguments)));
						if (!self.recommendationsBlock.hasModifier('hidden')) {
							self.recommendationsBlock.addModifier('hidden');
						}
						self.toggleMobileTabIndexOuterElements(true);
					})
					.on('close', function() {
						button.removeClass('is-active');
						$body.removeClass('topmenu-mobile-open');
						PubSubHub.trigger.apply(PubSubHub, ['layout.topmenu.mobile.close'].concat(Array.prototype.slice.call(arguments)));
						if (self.recommendationsBlock.hasModifier('hidden')) {
							self.recommendationsBlock.removeModifier('hidden');
						}
						self.toggleMobileTabIndexOuterElements(false);
					});

				$(document.body).on('keyup', _.bind(function(e) {
					var isEscape = ('key' in e) ?
						(e.key === 'Escape' || e.key === 'Esc') :
						(e.keyCode === 27);

					if (isEscape && dlmenu.open) {
						e.preventDefault();
						dlmenu.closeMenu();
					}
				}, this));
			}

			// Optimization for touch devices on desktop breakpoints (e.g. iPad Pro) on level 0
			;(function() {
				var parentMenuItemSelector = '.' + BEM.buildElementClassname('nav', 'menu-item', 'parent');
				var parentMenuItemLinkSelector = parentMenuItemSelector + ' a';

				$(document.body).on('touchstart', parentMenuItemLinkSelector, function(e) {
					if (isMobile) {
						return;
					}

					var link = $(e.currentTarget),
						menuItem = link.parents(parentMenuItemSelector);

					if (!menuItem.hasClass('hover')) {
						link.one('click', function(ev) {
							ev.preventDefault();
						});
					}
				});
			})();

			// Optimization for touch devices on desktop breakpoints (e.g. iPad Pro) on level 1
			;(function() {
				var parentMenuItemSelector = '.' + BEM.buildElementClassname('nav', 'menu-item', 'parent');
				var parentMenuItemLinkSelector = parentMenuItemSelector + ' a';
				var subMenuSelector = '.' + BEM.buildElementClassname('nav', 'menu');

				$(document.body).on('touchstart', parentMenuItemLinkSelector, function(e) {
					if (isMobile) {
						return;
					}

					var link = $(e.currentTarget),
						subMenu = link.siblings(subMenuSelector);

					if (subMenu.length && !subMenu.hasClass('open')) {
						link.one('click', function(ev) {
							ev.preventDefault();
						});
					}
				});
			})();
		},

		bindAccessibilityEvents: function() {
			if (!isMobile) {
				this.blocks.find('.' + BEM.buildElementClassname('nav', 'submenu-trigger')).on('click', _.bind(this.toggleSubmenuAccessibility, this));
				this.blocks.find('.' + BEM.buildElementClassname('nav', 'submenu-trigger')).on('keydown', _.bind(this.handleSubmenuTriggerKeydownAccessibility, this));
				this.blocks.find('.' + BEM.buildElementClassname('nav', 'submenu-panel') + ' ul[role="menu"]').on('keydown', _.bind(this.handleSubmenuArrowNavigationAccessibility, this));
				this.blocks.find('.' + BEM.buildElementClassname('nav', 'menu-item-link', 'level0')).on('keydown', _.bind(this.handleParentArrowNavigationAccessibility, this));
				this.blocks.find('.' + BEM.buildElementClassname('nav', 'menu', 'level0') + ' li.' + BEM.buildElementClassname('nav','menu-item','first')).find('a').on('keydown', _.bind(this.handleFirstChildMenuItemKeydown, this));
			} else {
				this.blocks.find('.' + BEM.buildElementClassname('nav', 'menu-item-link', 'level0')).on('click', _.bind(this.toggleSubmenuAccessibility, this));
			}
		},

		initResponsive: function() {
			enquire.register('(min-width: ' + (BreakPoints.sm + 1) + 'px)', {
				match: _.bind(this.layoutDesktop, this),
				unmatch: _.bind(this.layoutMobile, this)
			}, true);
		},

		initAccessibility: function() {
			this.blocks.find('.' + BEM.buildElementClassname('nav', 'submenu-panel') + '[aria-hidden="true"] a').attr('tabindex', '-1');

			if (isMobile) {
				const parentsLevel0 = this.blocks.find('.' + BEM.buildElementClassname('nav', 'menu-item', 'level0'));

				if (parentsLevel0) {
					$.each(parentsLevel0, function (i, parent) {
						parent = $(parent);
						const menuItemLink = parent.find('.' + BEM.buildElementClassname('nav', 'menu-item-link', 'level0'));
						const submenuTrigger = parent.find('.' + BEM.buildElementClassname('nav', 'submenu-trigger'));

						menuItemLink.addClass(submenuTrigger.attr('class'));
						menuItemLink.addClass(BEM.buildElementClassname('nav', 'submenu-trigger', 'mobile'));
						if (menuItemLink && submenuTrigger) {
							if (submenuTrigger[0] && submenuTrigger[0].attributes) {
								$.each(submenuTrigger[0].attributes, function () {
									if (this.name !== 'id' && this.name !== 'class') {
										menuItemLink.attr(this.name, this.value);
									}
								});
							}
						}
						submenuTrigger.detach();
					});
				}
			}
		},

		toggleMobileTabIndexOuterElements: function(disable) {
			if (!isMobile) {
				return;
			}

			var blocks = [
				BEM.block('header-functions'),
				BEM.block('header-logo')
			];

			var selector = blocks.map(function(block) {
				return '.' + block.__bem_block_class + ' a, .' + block.__bem_block_class + ' button';
			}).join(', ');

			var elements = $(selector);

			elements.attr('tabindex', disable ? '-1' : '0');
		},

		toggleSubmenuAccessibility: function(event) {
			var trigger = $(event.currentTarget),
				submenu = $('#' + trigger.attr('aria-controls')),
				submenuPanel = $('#' + trigger.attr('aria-controls') + '_panel'),
				isExpanded = trigger.attr('aria-expanded') === 'true',
				parentNode = submenuPanel.parent('.' + BEM.buildElementClassname('nav', 'menu-item', 'level0')),
				submenuLinks = submenu.find('a[role="menuitem"]');

			submenuLinks.attr('tabindex', isExpanded ? '-1' : '0');
			trigger.attr('aria-expanded', !isExpanded);
			submenu.attr('aria-hidden', isExpanded);

			console.log(submenuPanel);

			if (!isExpanded) {
				submenuLinks.first().focus();
				if (!isMobile) {
					parentNode.addClass('hover');
					submenuPanel.bemAddModifier('open');
				}
			} else {
				if (!isMobile) {
					parentNode.removeClass('hover');
					submenuPanel.bemRemoveModifier('open');
				}
			}
		},

		handleFirstChildMenuItemKeydown: function(event) {
			if (event.key === 'ArrowUp') {
				event.preventDefault();
				var link = $(event.currentTarget),
					submenu = link.closest('.' + BEM.buildElementClassname('nav', 'submenu-panel')),
					triggerButton = submenu.prev('.' + BEM.buildElementClassname('nav','submenu-trigger')),
					parentLi = this.getParentMenuItem(link),
					parentLink = parentLi.find('.' + BEM.buildElementClassname('nav','menu-item-link','level0'));

				this.closeAllSubmenus();
				parentLink.focus();
			}
		},

		handleSubmenuTriggerKeydownAccessibility: function(event) {
			var button = $(event.currentTarget),
				submenu = $('#' + button.attr('aria-controls')),
				parentMenuItem = button.closest('.' + BEM.buildElementClassname('nav', 'menu-item', 'level0')),
				parentItems = this.blocks.find('.' + BEM.buildElementClassname('nav', 'menu-item', 'level0')),
				parentIndex = parentItems.index(parentMenuItem);

			if (event.key === 'Enter' || event.key === 'Space' || event.key === 'ArrowDown') {
				event.preventDefault();
				this.toggleSubmenuAccessibility(event);
			} else if (event.key === 'Escape' && button.attr('aria-expanded') === 'true') {
				event.preventDefault();
				button.attr('aria-expanded', 'false');
				submenu.attr('aria-hidden', 'true');
				submenu.find('a').attr('tabindex', '-1');
				button.focus();
			} else if (event.key === 'ArrowUp') {
				event.preventDefault();
				this.closeAllSubmenus();
				if (parentMenuItem.length) {
					var parentMenuLink = parentMenuItem.find('.' + BEM.buildElementClassname('nav','menu-item-link','level0'));
					parentMenuLink.focus();
				}
			} else if (event.key === 'ArrowRight') {
				event.preventDefault();
				if (parentIndex < parentItems.length - 1) {
					this.closeAllSubmenus();
					var nextParent = parentItems.eq(parentIndex + 1);
					nextParent.find('.' + BEM.buildElementClassname('nav', 'menu-item-link', 'level0')).first().focus();
				}
			} else if (event.key === 'ArrowLeft') {
				event.preventDefault();
				if (parentIndex > 0) {
					this.closeAllSubmenus();
					var prevParent = parentItems.eq(parentIndex - 1);
					prevParent.find('.' + BEM.buildElementClassname('nav', 'menu-item-link', 'level0')).first().focus();
				}
			}
		},

		handleSubmenuArrowNavigationAccessibility: function(event) {
			var menu = $(event.currentTarget),
				menuItems = menu.find('a[role="menuitem"]'),
				listItems = menu.find('.' + BEM.buildElementClassname('nav', 'menu-item', 'level1')),
				currentIndex = menuItems.index(document.activeElement),
				parentMenuItem = menu.closest('.' + BEM.buildElementClassname('nav', 'menu-item', 'level0')),
				parentItems = this.blocks.find('.' + BEM.buildElementClassname('nav', 'menu-item', 'level0')),
				parentIndex = parentItems.index(parentMenuItem);

			if (event.key === 'ArrowDown') {
				event.preventDefault();
				var nextIndex = (currentIndex + 1) % menuItems.length;
				menuItems.eq(nextIndex).focus();
			} else if (event.key === 'ArrowUp') {
				if (currentIndex > 0) {
					event.preventDefault();
					var prevIndex = (currentIndex - 1 + menuItems.length) % menuItems.length;
					menuItems.eq(prevIndex).focus();
				}
			} else if (event.key === 'ArrowRight') {
				event.preventDefault();
				if (parentIndex < parentItems.length - 1) {
					this.closeAllSubmenus();
					var nextParent = parentItems.eq(parentIndex + 1);
					nextParent.find('a').first().focus();
				}
			} else if (event.key === 'ArrowLeft') {
				event.preventDefault();
				if (parentIndex > 0) {
					this.closeAllSubmenus();
					var prevParent = parentItems.eq(parentIndex - 1);
					prevParent.find('a').first().focus();
				}
			}
		},

		handleParentArrowNavigationAccessibility: function(event) {
			var currentItem = $(event.currentTarget),
				currentParent = this.getParentMenuItem(currentItem),
				parentItems = this.blocks.find('.' + BEM.buildElementClassname('nav', 'menu-item', 'level0')),
				currentIndex = parentItems.index(currentParent);

			if (event.target.tagName !== 'A') {
				return;
			}

			if (event.key === 'ArrowRight') { // next parent
				event.preventDefault();
				if (currentIndex < parentItems.length - 1) {
					this.closeAllSubmenus();
					var nextItem = parentItems.eq(currentIndex + 1);
					nextItem.find('.' + BEM.buildElementClassname('nav', 'menu-item-link', 'level0')).first().focus();
				}
			} else if (event.key === 'ArrowLeft') { // previous parent
				event.preventDefault();
				if (currentIndex > 0) {
					this.closeAllSubmenus();
					var prevItem = parentItems.eq(currentIndex - 1);
					prevItem.find('.' + BEM.buildElementClassname('nav', 'menu-item-link')).first().focus();
				}
			} else if (event.key === 'ArrowDown') { // next menu item
				event.preventDefault();
				this.closeAllSubmenus();
				var parentItem = this.getParentMenuItem(currentItem);
				if (parentItem) {
					var submenuTrigger = parentItem.find('.' + BEM.buildElementClassname('nav', 'submenu-trigger'));
					if (submenuTrigger.length) {
						submenuTrigger.focus();
					}
				}
			}
		},

		getParentMenuItem: function(element) {
			var parentLi = $(element).closest('.' + BEM.buildElementClassname('nav', 'menu-item', 'level0'));

			if (!parentLi.length) {
				console.log('Parent menu item not found for element:', element);
				return false;
			}
			return parentLi;
		},

		closeAllSubmenus: function() {
			var submenus = this.blocks.find('.' + BEM.buildElementClassname('nav', 'submenu-panel'));

			submenus.each(function() {
				var submenu = $(this),
					trigger = submenu.prev('.' + BEM.buildElementClassname('nav', 'submenu-trigger')),
					parentNode = submenu.parent('.' + BEM.buildElementClassname('nav', 'menu-item', 'level0'));

				submenu.attr('aria-hidden', 'true');
				submenu.find('a').attr('tabindex', '-1');
				trigger.attr('aria-expanded', 'false');
				parentNode.removeClass('hover');
				trigger.bemRemoveModifier('open');
			});
		},

		layoutMobile: function() {
			isMobile = true;
			var menus = this.blocks.getElements('menu');
			menus.addClass('dl-submenu');
			menus.filter(':modifier(level0)').each(function() {
				var menu = $(this);
				menu.bemParents('menu-item').bemGetElements('submenu-slot-mobile').before(menu);
			});

			this.topmenu.addClass('dl-menuwrapper');
		},

		layoutDesktop: function() {
			isMobile = false;
			var menus = this.blocks.getElements('menu');
			menus.removeClass('dl-submenu');
			menus.filter(':modifier(level0)').each(function() {
				var menu = $(this);
				menu.bemParents('menu-item').bemGetElements('submenu-slot-desktop').append(menu);
			});

			this.topmenu.removeClass('dl-menuwrapper');
		},

		prepareCustomerAccountNavigation: function() {
			if ($('html').hasClass('ie8')) return;
			var customerAccountMobileNav = $('.b_account-mobile-nav');
			if (customerAccountMobileNav.length > 0) {
				customerAccountMobileNav.prependTo($('.b_main-container__col2-left-col-main'));
				$('select', customerAccountMobileNav).on('change', function() {
					window.location = $(this).val();
				});
			}
		},

		desktopMenuAim: function() {
			$(document.body).one('mouseenter', '.' + BEM.buildElementClassname('nav', 'menu-item'), _.bind(function() {
				var menus = this.blocks.getElements('menu'),
					link = BEM.buildElementClassname('nav', 'menu-item-link', 'level1');

				function activateSubmenu(row) {
					var $row = $(row),
						$submenu = $row.find('ul'),
						$menulink = $row.find('.' + link);

					if($row.hasClass('b_nav__menu-item--parent')) {
						$row.parent('ul').addClass('removeHover');
					}

					$submenu.addClass('open');
					$menulink.addClass('maintainHover');
				}

				function deactiveSubmenu(row) {
					var $row = $(row),
						$submenu = $row.find('ul'),
						$menulink = $row.find('.' + link);

					$row.parent('ul').removeClass('removeHover');
					$submenu.removeClass('open');
					$menulink.removeClass('maintainHover');

					return true;
				}

				menus.menuAim({
					activate: activateSubmenu,
					deactivate: deactiveSubmenu,
					rowSelector: '> li.' + BEM.buildElementClassname('nav', 'menu-item', 'level1'),
					exitMenu: deactiveSubmenu
				});
			}, this));
		}
	});
});

define('rebrush/system/animation',[
    'modernizr'
], function() {
    var animEndEventNames = {
            'WebkitAnimation' : 'webkitAnimationEnd',
            'OAnimation' : 'oAnimationEnd',
            'msAnimation' : 'MSAnimationEnd',
            'animation' : 'animationend'
        },
        transEndEventNames = {
            'WebkitTransition' : 'webkitTransitionEnd',
            'MozTransition' : 'transitionend',
            'OTransition' : 'oTransitionEnd',
            'msTransition' : 'MSTransitionEnd',
            'transition' : 'transitionend'
        };

    return {
        animationEndEventName: animEndEventNames[Modernizr.prefixed('animation')],
        transitionEndEventName: transEndEventNames[ Modernizr.prefixed('transition')],
        supportAnimations: Modernizr.cssanimations,
        supportTransitions: Modernizr.csstransitions
    };
});
define('rebrush/components/layout/search',[
	'jquery',
	'lodash',
	'cartware/components/_base',
	'cartware/jquery/bem',
	'rebrush/system/animation'
], function(
	$,
	_,
	Component,
	BEM,
	Animation
) {
	var endPrecedingAnimation = $.noop;

	return Component.create({
		$init: function() {
			this.docRoot = $('html');
			this.cube = BEM.block('header').getElements('cube');
			this.bindEvents();
		},

		bindEvents: function() {
			$(document.body).on('click', '.' + BEM.buildElementClassname('header-functions', 'search-button'), _.bind(function(e) {
				e.preventDefault();
				this.openSearch();
			}, this));

			$(document.body).on('click', '.' + BEM.buildElementClassname('header-search', 'button', 'close'), _.bind(function(e) {
				e.preventDefault();
				this.closeSearch();
			}, this));

			$(document.body).on('click', '.' + BEM.buildElementClassname('header', 'shadow'), _.bind(this.closeSearch, this));
			$(document.body).on('click', '.' + BEM.buildBlockClassname('page-shadow'), _.bind(this.closeSearch, this));

			$(document.body).on('keyup', _.bind(function(e) {
				var isEscape = ('key' in e) ?
					(e.key === 'Escape' || e.key === 'Esc') :
					(e.keyCode === 27);

				if (isEscape) {
					e.preventDefault();
					this.closeSearch();
				}
			}, this));
		},

		openSearch: function() {
			var onAnimationEnd = _.bind(function() {
				endPrecedingAnimation = $.noop;
				this.docRoot.removeClass('search-showing search-hiding');
				this.docRoot.addClass('search-shown');
			}, this);


			if (Animation.supportAnimations) {
				endPrecedingAnimation();
				endPrecedingAnimation = onAnimationEnd;

				this.cube.one(Animation.transitionEndEventName, onAnimationEnd);

				this.docRoot.addClass('search-in');
				this.cube.bemAddModifier('show-top');
				_.defer(_.bind(function() {
					this.docRoot.addClass('search-showing');
				}, this));
				$('.' + BEM.buildElementClassname('header-search', 'input')).focus();
			} else {
				this.cube.bemAddModifier('show-top');
				onAnimationEnd();
			}
		},

		closeSearch: function() {
			var onAnimationEnd = _.bind(function() {
				endPrecedingAnimation = $.noop;
				this.docRoot.removeClass('search-in search-hiding search-shown');
			}, this);

			if (Animation.supportAnimations) {
				endPrecedingAnimation();
				endPrecedingAnimation = onAnimationEnd;

				this.docRoot.removeClass('search-showing search-shown');
				this.docRoot.addClass('search-hiding');
				this.cube.bemRemoveModifier('show-top');
				this.cube.one(Animation.transitionEndEventName, onAnimationEnd);
			} else {
				this.cube.bemRemoveModifier('show-top');
				onAnimationEnd();
			}
		}
	});
});
define('rebrush/components/ui/selects/native',[
    'jquery',
    'lodash',
    'cartware/components/_base',
    'cartware/jquery/bem'
], function(
    $,
    _,
    Component,
    BEM
) {
    var SEMAPHORE_KEY               = 'component/ui/selects/native';
    var BLOCK_MODIFIER_JS_SUPPORT   = 'js-support';

    return Component.create({
        $init: function() {
            this.transformSelects();

            var debounceTransform = _.debounce(this.transformSelects, 1);

            $(document).ajaxComplete(debounceTransform);
            Ajax.Responders.register({
                onComplete: debounceTransform
            });
        },

        transformSelects: function() {
            BEM.block('select').filter(':modifier(native-mobile)').each(function() {
                var block = BEM.block(this);

                if (block.data(SEMAPHORE_KEY)) { return; }
                block.data(SEMAPHORE_KEY, true);

                function onChange(select) {
                    block.attr('data-selection', select.find('option:selected').text());
                }

                block.on('change', 'select', function(e) {
                    onChange($(e.currentTarget));
                });

                onChange(block.find('select'));

                block.addModifier(BLOCK_MODIFIER_JS_SUPPORT);
            });
        }
    });
});
define('rebrush/components/ui/tooltips',[
	"jquery",
	"cartware/components/_base",
	"bootstrap-tooltip"
], function(
	$,
	Component
) {
	return Component.create({
		$init: function() {
			$(document.body).on("mouseover", '[data-toggle="tooltip"]', function(e) {
				var $this   = $(this),
					data    = $this.data('bs.tooltip');

				if (!data) {
					$this.tooltip({
						html: true,
						container: 'body'
					});
				}

				$this.tooltip("show");
			});
		}
	});
});
define('rebrush/components/ui/generated-menu',[
	'jquery',
	'cartware/components/_base',
	'cartware/jquery/bem'
], function(
	$,
	Component,
	BEM
) {

	return Component.create({

		blocks: null,

		$init: function() {
			this.blocks = $('[data-links-source-selector]');
			this.blocks.each(function() {
				var block = $(this),
					nav = block.find('[data-output-target]'),
					sourceSelector = block.data('links-source-selector'),
					sources = $(sourceSelector);

				sources.each(function(index, obj){
					var source = $(obj),
						generatedLi = $('<li>'),
						generatedLink = $(
							'<a>',
							{
								href: '#' + source.attr('id'),
								text: source.find('h3').text()
							}
						);
					nav.append(generatedLi.append(generatedLink));
				});

				$('body').scrollspy('refresh');
			});

		}

	});

});

define('rebrush/components/cart/ajax',[
	'jquery',
	'lodash',
	'cartware/components/_base',
	'cartware/jquery/bem',
	'cartware/components/Dispatcher',
	'gigaset/spinner',
	'gigaset/fx-engine',
	'cartware/components/Utilities',
	'json!rebrush/handles/default.json',
	'text!shoptheme/templates/magnific-popup/default.html',
	'magnific-popup',
	'bootstrap-button'
], function(
	$,
	_,
	Component,
	BEM,
	ComponentDispatcher,
	Spinner,
	FX,
	Utils,
	Handles,
	PopupHtml
) {
	var popupTemplate = null;


	return Component.create({
		miniCart:		null,
		cart:			null,
		modalMainClasses: null,
		$init: function() {
			var self = this,
				$body = $(document.body);

			this.miniCart = BEM.block('mini-cart');
			this.cart = BEM.block('cart');

			this._prepareForm();

			// Bind add to cart buttons / links
			$body.on('click', '[data-ajax-add-to-cart]', _.bind(function(e) {
				e.preventDefault();

				var link = $(e.currentTarget);

				if (link.hasClass('btn-disabled')) {
					return false;
				}

				var url = link.attr('href') || link.data('add-to-cart-url'),
					container = link.closest('.b_product-item__container'),
					data = link.data('ajax-add-to-cart'),
					hasRequiredOptions = _.get(data, 'hasRequiredOptions');

				if (hasRequiredOptions && $body.hasClass('catalog-product-view')) {
					return true;
				}

				Spinner.add(container.length === 0 ? 'body' : container);
				self._sendRequest(
					url,
					{ 'isAjax': 1, 'qty': link.data('order-qty') },
					function(response) {
						if (response.success) {
							// Disable add to cart button in product minicard (category page only).
							var inTheCartText = link.attr('data-text-after-add-to-cart');
							if (inTheCartText) {
								link.addClass('btn-disabled');
								link.html('<span class="icon-rebrush-ui-check"></span>' + inTheCartText);
							}
						}
						self._onAddSuccess(response);
					}
				);
				$.magnificPopup.close();
			}, this));

			// Bind cart move to wishlist links
			$body.on('click', '.cart-table .move-to-wishlist', _.bind(function(e) {
				e.preventDefault();
				var link = $(e.currentTarget),
					url = link.attr('href');

				Spinner.add('#shopping-cart-table');
				self._sendRequest(url, { 'isAjax': 1 });
			}, this));

			// Bind cart view qty selects
			$body.on('change', '.cart-table select.qty', function(e) {
				var form = self.cart.find('form'),
					select = $(e.target),
					url = form.attr('action'),
					data = form.serialize() + '&content=cart',
					xhr = self._sendRequest(url, data, self._onUpdateSuccess);

				Spinner.add('#shopping-cart-table');

				xhr.then(function(response) {
					if (_.isObject(response)) {
						if (response.error) {
							select.val(select.data('__original_value'));

							if (select.data('select2')) {
								select.trigger('change.select2');
							}
						}
					}
					self._rememberCartQty();
				});
			});


			// Bind cart view remove links
			$body.on('click', '.cart-table a.remove-from-cart', function(e) {
				e.preventDefault();
				var url = $(e.currentTarget).attr('href'),
					data = $('.b_cart input[name="form_key"]').serialize() + '&content=cart';

				Spinner.add('#shopping-cart-table');
				e.preventDefault();
				self._sendRequest(url, data, self._onRemoveSuccess);
			});

			this._rememberCartQty();
		},

		_rememberCartQty: function() {
			$('.cart-table select.qty').each(function() {
				var select = $(this);

				select.data('__original_value', select.val());
			});
		},

		/**
		 * Overwrite productAddToCartForm submit function
		 * @private
		 */
		_prepareForm: function() {
			if ($(document.body).hasClass('wishlist-index-configure') !== true) { // quick fix to prevent ajax add2cart on wishlist configure product view page
				var self = this,
					formObj = _.get(window, 'productAddToCartFormOld', _.get(window, 'productAddToCartForm'));

				if (!formObj) {
					return;
				}

				formObj.submit = function () {
					if (this.validator.validate()) {
						var form = $(this.form),
							url = form.attr('action');

						Spinner.add();
						self._sendRequest(url, form.serialize(), self._onAddSuccess);
					}
				};
			}
		},

		_renderPopup: function(data) {
			if (null === popupTemplate) {
				popupTemplate = _.template(PopupHtml);
			}

			return popupTemplate(data);
		},

		/**
		 * Send ajax request
		 *
		 * @param url
		 * @param data
		 * @param onSuccessCallback
		 * @private
		 */
		_sendRequest: function(url, data, onSuccessCallback) {
			var self = this;

			data = data || {};

			// add isAjax param to avoid caching problems
			if (url.indexOf('isAjax=1') === -1 && typeof data === 'string' && data.indexOf('isAjax=1') === -1) {
				url += url.indexOf('?') === -1 ? '?isAjax=1' : '&isAjax=1';
			}

			var xhr = $.ajax(url, {
				method: 'POST',
				data: data
			}).always(function() {
				Spinner.remove();
			}).then(_.bind(function(response, status, xhr) {
				var responseHandles = $.parseJSON(xhr.getResponseHeader('X-Magento-Handles') || '[]');

				if (responseHandles.indexOf('gigaset_dealerpos_checkout_productview') > -1 &&
					responseHandles.indexOf('catalog_product_view') < 0
				) {
					responseHandles.push('catalog_product_view');
				}

				var pageHandles = Utils.getPath(window, 'gigaset.handles');

				if (responseHandles.indexOf('checkout_cart_index') < 0 &&
					pageHandles.indexOf('checkout_cart_index') > -1
				) {
					responseHandles.push('checkout_cart_index');
				}

				var _isResponseHtml = (typeof response === 'string');
				response = _isResponseHtml ? { body: response } : response;

				if (response.redirect) {
					window.location = response.redirect;
					return;
				}

				if (response.success === true && typeof onSuccessCallback === 'function') {
					onSuccessCallback(response);
				}
				if (response.mini_cart) {
					self.miniCart.replaceWith(response.mini_cart);
					self.miniCart = BEM.block('mini-cart');
				}
				if (response.cart) {
					self.cart.replaceWith(response.cart);
					self.cart = BEM.block('cart');
				}
				if (response.body || response.message) {
					var newModalMainClasses = (responseHandles.indexOf('catalog_product_view') > -1) ?
						['container-size', 'shadow', 'mfp-product-view'] :
						['shadow'];

					var onOpen = function () {

						var onDone = function() {
							$(document.body).scrollTop($('.mfp-content').offset().top - 60);
							self.hub.trigger('cartware_cart/add-after', response);
						};

						if (_isResponseHtml === true || 'body' in response) {
							var handles = _.intersection([
								'catalog_product_view',
								'PRODUCT_TYPE_configurable',
								'gigaset_dealerpos_checkout_productview',
								'catalog_product_customoption_print',
								'gigaset_wheretobuy_product_view'
							], responseHandles);


							handles.push('cartware_cart_popup_open');

							var dispatcher = new ComponentDispatcher({
								handles: handles
							});

							dispatcher.applyHandles(_.assign({}, Handles), function(_component, isLast) {
								if (isLast) {
									onDone();
								}
							});

							// TODO use `self.hub.trigger()` here instead for event and component dispatching
							$(document).trigger('cartware_cart/modal/open', [response]);

							self._prepareForm();
						} else {
							onDone();
						}
					};

					if ($.magnificPopup.instance.isOpen) {
						if (self.modalMainClasses) {
							_.forEach(self.modalMainClasses, function(className) {
								$.magnificPopup.instance.wrap.removeClass(className);
								$.magnificPopup.instance.bgOverlay.removeClass(className);
							});
						}

						_.forEach(newModalMainClasses, function(className) {
							$.magnificPopup.instance.wrap.addClass(className);
							$.magnificPopup.instance.bgOverlay.addClass(className);
						});

						onOpen();
					}

					self.modalMainClasses = newModalMainClasses;

					$.magnificPopup.open({
						mainClass: self.modalMainClasses.join(' '),
						fixedContentPos: true,
						items: {
							src: this._renderPopup({
								body: response.body ? response.body : response.message,
								displayCloseButton: true
							}),
							type: 'inline'
						},
						callbacks: {
							open: onOpen
						}
					});

					if ($.magnificPopup.instance.isOpen) {
						$(document.body).scrollTop($('.mfp-content').offset().top - 60);
					}
				} else {

					var handles = _.intersection([
						'checkout_cart_index'
					], responseHandles);

					var dispatcher = new ComponentDispatcher({
						handles: handles
					});

					dispatcher.applyHandles(_.assign({}, Handles));

					$(document).trigger('cartware_cart/add-after', response);
				}

				return response;
			}, this));

			return xhr;
		},
		_onAddSuccess: function(response) {
			if ('dataLayer' in window) {
				var dataLayer = window.dataLayer;
				var ecommerce = {};

				if (response.tracking) {
					ecommerce = JSON.parse(response.tracking);
				}

				// reset data layer add-to-cart item data
				dataLayer.push({
					'ecommerce': undefined
				});

				if (Object.keys(ecommerce).length !== 0) {
					dataLayer.push({'event':'add-to-cart', ecommerce});
				} else {
					dataLayer.push({'event':'add-to-cart'});
				}
			}
			$(document).trigger('cartware_cart/add', response);
		},
		_onRemoveSuccess: function(response) {
			$(document).trigger('cartware_cart/remove', response);
		},
		_onUpdateSuccess: function(response) {
			$(document).trigger('cartware_cart/update', response);
		},
	});
});

define('rebrush/components/cart/etracker',[
    'jquery',
    'lodash',
    'cartware/components/_base'
], function(
    $,
    _,
    Component
) {
    return Component.create({
        $init: function () {

            var self = this;

            if( typeof window._etrackerOnReady === 'undefined' ) {
                window._etrackerOnReady = [];
            }

            // insertToBasket Tracking
            $(document).on('cartware_cart/add', function(event, response) {

                if (response.etracker) {
                    var eventData = response.etracker;
                    var eTrackerEvent = eventData.event;
                    self.sendEtrackerEvent(eTrackerEvent, eventData);
                }
            });

            // removeFromBasket Tracking
            $(document).on('cartware_cart/remove', function(event, response) {

                if (response.etracker) {
                    var eventData = response.etracker;
                    var eTrackerEvent = eventData.event;
                    self.sendEtrackerEvent(eTrackerEvent, eventData);
                }
            });

            // updateCart Tracking
            $(document).on('cartware_cart/update', function(event, response) {

                if (response.etracker) {
                    var eventData = response.etracker;
                    var eTrackerEvent = eventData.event;
                    self.sendEtrackerEvent(eTrackerEvent, eventData);
                }
            });
        },
        sendEtrackerEvent: function(eTrackerEvent, eventData) {

            var etCommerceProduct = {
                id: eventData.id,
                name: eventData.name,
                price: eventData.price,
                currency: eventData.currency,
                category: eventData.category
            };

            var etCommerceQuantity = Number(eventData.qty);

            var etCommerceBasketId = eventData.basket;

            window._etrackerOnReady.push(function() {
                window.etCommerce.sendEvent(eTrackerEvent, etCommerceProduct, etCommerceQuantity, etCommerceBasketId);
            });
        }
    });
});
define('rebrush/components/cartware/slider',[
	'cartware/components/_base',
	'cartware/jquery/bem',
	'cartware/utils/image/preload',
	'jquery',
	'lodash',
	'json!shoptheme/etc/breakpoints.json',
	'enquire',
	'cartware-slider'
], function(
	Component,
	BEM,
	preload,
	$,
	_,
	BreakPoints,
	enquire
) {
	var TEASER_MODE_COMBINED	= 'combined',
		TEASER_MODE_STACKED		= 'stacked';

	var BACKGROUND_MODE_STRETCH 	= 'stretch',
		BACKGROUND_MODE_INFLATE 	= 'inflate';

	return Component.create({
		teaserLayoutCallbacks:			null,
		teaserMode:						null,

		backgroundLayoutCallbacks:		null,
		backgroundMode:					null,

		slideshows:						null,

		constructor: function() {
			this.teaserLayoutCallbacks = [];
			this.teaserMode = TEASER_MODE_STACKED;

			this.backgroundLayoutCallbacks = [];
			this.backgroundMode = BACKGROUND_MODE_INFLATE;

			this.slideshows = [];
		},

		init: function() {
			enquire.register('(min-width: ' + (BreakPoints.xs + 1) + 'px)', {
				match: _.bind(this.layoutTeasersCombined, this),
				unmatch: _.bind(this.layoutTeasersStacked, this)
			}, true);

			enquire.register('(min-width: ' + (BreakPoints.sm + 1) + 'px)', {
				match: _.bind(this.layoutBackgroundStretch, this),
				unmatch: _.bind(this.layoutBackgroundInflate, this)
			}, true);
		},

		$init: function() {
			var self = this;

			var reflow = _.throttle(function() {
				_.forEach(self.slideshows, function(slideshow) {
					slideshow.reflow();
				});
			}, 150);

			BEM.block('cartware-slider-slide:modifier(teaser)').each(function() {
				var slide = BEM.block(this),
					wrapper = slide.getElements('wrapper'),
					teaser = slide.getElements('teaser'),
					vAlignMiddle = wrapper.is(':modifier(v-middle)'),
					slider = slide.parents('.cartware-slider:first'),
					slideshow = slider.data('cartware_slider/instance');
					// adjustMargin = slider.parents('.container').length === 0;

				if (self.slideshows.indexOf(slideshow) < 0) {
					self.slideshows.push(slideshow);
				}

				var layout = function() {
					// var viewportWidth = slider.width(),
					// 	slideTextContentMargin;
					//
					// if (adjustMargin) {
					// 	slideTextContentMargin = Math.round((viewportWidth - wrapper.outerWidth()) / 2);
					//
					// 	wrapper.css({
					// 		marginLeft: slideTextContentMargin,
					// 		marginRight: slideTextContentMargin
					// 	});
					// }

					if (vAlignMiddle && self.teaserMode === TEASER_MODE_COMBINED) {
						wrapper.css({
							top: Math.round((slide.innerHeight() - teaser.outerHeight()) / 2)
						});
					}

					// teaser.css({
					// 	width: self.teaserMode === TEASER_MODE_STACKED ? viewportWidth : ''
					// });

					slide.css({
						minHeight: self.teaserMode === TEASER_MODE_STACKED ? '' : wrapper.outerHeight() + wrapper.position().top
					});

					reflow();
				};

				$(window).resize(_.throttle(layout, 150));
				layout();

				self.teaserLayoutCallbacks.push(layout);
			});

			$('.cartware-slider-slide-background').each(function() {
				var backgroundContainer = $(this),
					backgroundImage = backgroundContainer.css('background-image').match(/^url\(["'](.*)["']\)$/)[1] || null,
					slide = backgroundContainer.closest('.cartware-slider-item-wrap'),
					slider = slide.closest('.cartware-slider'),
					slideshow = slider.data('cartware_slider/instance'),
					stage = slide.closest('.cartware-slider-stage'),
					inner = slide.find('.teaser-inner:first'),
					imageDimensions = null;

				if (!backgroundImage) {
					return;
				}

				if (self.slideshows.indexOf(slideshow) < 0) {
					self.slideshows.push(slideshow);
				}

				var layout = function() {
					backgroundContainer.css(
						'min-height',
						self.backgroundMode === BACKGROUND_MODE_STRETCH ? imageDimensions.height : ''
					);

					slide.css(
						'min-height',
						self.backgroundMode === BACKGROUND_MODE_STRETCH ? imageDimensions.height : ''
					);

					inner.css('min-height', '');

					slideshow.reflow();
				};

				slideshow.on('reflow', function() {
					inner.css(
						'min-height',
						self.backgroundMode === BACKGROUND_MODE_STRETCH ? '' : stage.height()
					);
				});
                
				preload(backgroundImage, function(err, info) {
					if (err) {
						throw err;
					} else {
						imageDimensions = info;

						$(window).resize(_.throttle(layout, 150));
						layout();

						self.backgroundLayoutCallbacks.push(layout);
					}
				});

				//var img = imagePool.alloc();
				//
				//img.onload = function() {
				//	imageDimensions = {
				//		width: img.width,
				//		height: img.height
				//	};
				//
				//	$(window).resize(_.throttle(layout, 150));
				//	layout();
				//
				//	self.backgroundLayoutCallbacks.push(layout);
				//
				//	imagePool.release(img);
				//};
				//
				//img.onerror = function() {
				//	imagePool.release(img);
				//};
				//
				//img.src = backgroundImage;
			});
		},

		layoutTeasers: function() {
			_.forEach(this.teaserLayoutCallbacks, function(cb) {
				cb();
			});
		},

		layoutTeasersCombined: function() {
			this.teaserMode = TEASER_MODE_COMBINED;
			this.layoutTeasers();
		},

		layoutTeasersStacked: function() {
			this.teaserMode = TEASER_MODE_STACKED;
			this.layoutTeasers();
		},

		layoutBackgrounds: function() {
			_.forEach(this.backgroundLayoutCallbacks, function(cb) {
				cb();
			});
		},

		layoutBackgroundStretch: function() {
			this.backgroundMode = BACKGROUND_MODE_STRETCH;
			this.layoutBackgrounds();
		},

		layoutBackgroundInflate: function() {
			this.backgroundMode = BACKGROUND_MODE_INFLATE;
			this.layoutBackgrounds();
		}
	});
});
/**
 * - Uses GDPR-compliant custom rewrites of Vimeo and Youtube video.js (videojs-vimeo-gdpr.js, videojs-youtube-gdpr.js)
 * - Manages Cookie consent flow for video players (listens to civik:cookie-consent-changed custom event)
 * - Controls 3rd party player lifecycles and disposal
 * - Restores HTML player functionality and cookie overlays for multiple deny and grant scenarios without reload requirement
 *
 * Used by B2C stores as well as B2B (Pro) stores
 */
define('rebrush/components/cartware/video',[
    "jquery",
    "lodash",
    "cartware/components/_base",
    "cartware/jquery/bem",
    "json!shoptheme/etc/consent.json",
    "videojs",
    "videojs-vimeo",
    "videojs-youtube"
], function(
    $,
    _,
    Component,
    BEM,
    Consent,
    videojs
) {
    return Component.create({
        elements: [],
        sliderSlideClass: "." + BEM.buildElementClassname("cartware-slider-slide"),
        videoWidgetClass: "." + BEM.buildBlockClassname("video-widget"),
        videoOverlayClass: "." + BEM.buildElementClassname("cartware-slider-slide", "loading-overlay"),
        consentIdentifierVideo: 'functional',
        localStorageConsentIdentifier: 'civik:cookie-control',
        consentEventIdentifier: 'civik:cookie-consent-changed',
        videoRegistry: {}, // tracks video elements and their state
        consentListenerAttached: false,

        $init: function() {
            var self = this;

            var cwSlider = $('.cartware-slider');
            var sliderInstance = cwSlider.data('cartware_slider/instance');

            // Set to true for debug mode
            this.isDebugMode = _.get(window, 'gigaset.featureflags.developer_mode', false);

            // Attach consent listener
            this.attachConsentListener();

            // Initialize videos
            this.initialize();

            if (cwSlider.length > 0 && sliderInstance !== undefined) {
                sliderInstance.on('ready', function (event) {
                    self.resizeVideoJS();
                    self.handleActiveVideoSlide(cwSlider);
                });

                cwSlider.on('cartware_slider/slide_after', function (event) {
                    self.handleActiveVideoSlide($(event.currentTarget));
                });
            }

            window.onresize = function() {
                self.resizeVideoJS();
            };

            $('.b_video_no_consent__link').on('click', function(e) {
                e.preventDefault();
                const link = $(this);
                const cookieControl = _.get(window, 'CookieControl');

                if (cookieControl) {
                    link.css('cursor', 'pointer');
                    cookieControl.open();
                }
            });
        },

        attachConsentListener: function() {
            // Prevent duplicate event listeners
            if (this.consentListenerAttached) {
                return;
            }
            this.consentListenerAttached = true;

            var self = this;

            window.addEventListener(self.consentEventIdentifier, function(event) {
                if (self.isDebugMode) {
                    console.log('[Video.js] Cookie consent changed event triggered', event);
                }

                try {
                    var payload = event.detail && event.detail.payload;
                    var hasConsent = payload && payload.accepted &&
                        payload.accepted.includes(self.consentIdentifierVideo);

                    if (hasConsent) {
                        self.unlockAllVideos();
                    } else {
                        self.lockAllVideos();
                    }
                } catch (error) {
                    if (self.isDebugMode) {
                        console.log('[Video.js] Error processing consent event:', error);
                    }
                }
            });

            if (this.isDebugMode) {
                console.log('[Video.js] Consent event listener attached');
            }
        },

        hasConsent: function() {
            // Check actual consent
            try {
                var stored = localStorage.getItem(this.localStorageConsentIdentifier);
                if (stored) {
                    var data = JSON.parse(stored);
                    return data.accepted && data.accepted.includes(this.consentIdentifierVideo);
                }
            } catch (e) {}
            return false;
        },

        grantConsentToVideoPlayers: function() {
            if (typeof Youtube !== 'undefined' && Youtube.grantConsent) {
                Youtube.grantConsent();
                if (this.isDebugMode) {
                    console.log('[Video.js] Granted consent to YouTube');
                }
            }

            if (typeof Vimeo !== 'undefined' && Vimeo.grantConsent) {
                Vimeo.grantConsent();
                if (this.isDebugMode) {
                    console.log('[Video.js] Granted consent to Vimeo');
                }
            }
        },

        revokeConsentFromVideoPlayers: function() {
            if (typeof Youtube !== 'undefined' && Youtube.revokeConsent) {
                Youtube.revokeConsent();
                if (this.isDebugMode) {
                    console.log('[Video.js] Revoked consent from YouTube');
                }
            }

            if (typeof Vimeo !== 'undefined' && Vimeo.revokeConsent) {
                Vimeo.revokeConsent();
                if (this.isDebugMode) {
                    console.log('[Video.js] Revoked consent from Vimeo');
                }
            }
        },

        registerVideo: function(element) {
            var $el = $(element);
            var id = $el.attr('id');

            if (!id) {
                id = 'video-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
                $el.attr('id', id);
            }

            // Register if not registered yet
            if (!this.videoRegistry[id]) {
                this.videoRegistry[id] = {
                    id: id,
                    originalHtml: element.outerHTML,
                    setup: $el.data('custom-setup'),
                    aspectRatio: $el.data('aspect-ratio'),
                    isSlide: $el.data('is-slide') == 1,
                    isInitialized: false,
                    player: null
                };

                if (this.isDebugMode) {
                    console.log('[Video.js] Registered video:', id);
                }
            }

            return this.videoRegistry[id];
        },

        initialize: function() {
            var elements = $("video[data-custom-setup]");
            var self = this;
            var consentGiven = this.hasConsent();

            if (this.isDebugMode) {
                console.log('[Video.js] Initialize called, consent:', consentGiven, 'videos found:', elements.length);
            }

            elements.each(function() {
                var video = $(this);
                var setup = video.data("custom-setup");

                if (consentGiven && setup && "autoInitialization" in setup && setup.autoInitialization === false) {
                    if (self.isDebugMode) {
                        console.log('[Video.js] Skipping video with autoInitialization :', video.attr('id'));
                    }
                    // Skip autoinitialize videos which show in a popup when consent is granted, for instance for videoteaser.js
                    return;
                }

                // Register video (stores original HTML)
                var registry = self.registerVideo(this);

                if (consentGiven) {
                    self.initVideoWithConsent(this, registry);
                } else {
                    self.showConsentOverlay(this, registry);
                }
            });
        },

        initVideoWithConsent: function(element, registry) {
            var self = this;
            var $video = $(element);

            // Grant consent to video players
            this.grantConsentToVideoPlayers();

            // Remove overlay
            this.toggleClassToParent(false, [element]);

            // Initialize video.js player
            var callback = function() {
                var player = this;

                if (element.hasAttribute("muted")) {
                    player.muted(true);
                }

                player.videoAspectRatio = registry.aspectRatio;

                // Store player reference
                registry.player = player;
                registry.isInitialized = true;

                // Add to elements array for resize handling
                if (self.elements.indexOf(player) === -1) {
                    self.elements.push(player);
                }

                self.resizeVideoJS();

                if (self.isDebugMode) {
                    console.log('[Video.js] Player initialized for:', registry.id);
                }
            };

            if (registry.isSlide) {
                this.initVideoSlideInternal(element, registry, callback);
            } else {
                videojs(element, registry.setup, callback);
            }
        },

        // Initialize slide video with consent
        initVideoSlideInternal: function(element, registry, callback) {
            var self = this;
            var videoSlide = $(element);
            var slideContainer = videoSlide.closest('.cartware-slider-item-wrap');
            var slider = videoSlide.closest('.cartware-slider');
            var instance = slider.data('cartware_slider/instance');

            if (instance) {
                if (slideContainer.css('display') === 'none') {
                    self.fixMobileActiveThumbSelection();
                    instance.pauseAutoslide(false);
                } else {
                    instance.pauseAutoslide(true);
                }
            }

            videojs(element, registry.setup, function() {
                var player = this;
                player.loadingSpinner.show();

                callback.call(this);

                slider.one('mouseenter', function() {
                    videoSlide.removeClass('hide-controls');
                });

                slider.trigger('resize');
            });
        },

        /**
         * Show consent overlay for a video
         */
        showConsentOverlay: function(element, registry) {
            this.toggleClassToParent(true, [element]);

            if (this.isDebugMode) {
                console.log('[Video.js] Showing consent overlay for:', registry.id);
            }
        },

        unlockAllVideos: function() {
            var self = this;

            if (this.isDebugMode) {
                console.log('[Video.js] Unlocking all videos');
            }

            // Grant consent
            this.grantConsentToVideoPlayers();

            // Process registered videos
            Object.keys(this.videoRegistry).forEach(function(id) {
                var registry = self.videoRegistry[id];

                // Find element (eventually restored HTML)
                var element = $('#' + id);

                if (element.length === 0) {
                    if (self.isDebugMode) {
                        console.log('[Video.js] Element not found for:', id);
                    }
                    return;
                }

                // If already initialized, just remove overlay
                if (registry.isInitialized && registry.player) {
                    self.toggleClassToParent(false, [element[0]]);
                    return;
                }

                // Initialize video with consent
                self.initVideoWithConsent(element[0], registry);
            });
        },

        lockAllVideos: function() {
            var self = this;

            if (this.isDebugMode) {
                console.log('[Video.js] Locking all videos');
            }

            // Revoke consent
            this.revokeConsentFromVideoPlayers();

            // Process registered videos
            Object.keys(this.videoRegistry).forEach(function(id) {
                var registry = self.videoRegistry[id];

                // Find container before dispose removes the child element
                var container = null;
                var parent = null;

                // Get original wrapper
                container = $('#' + id);

                if (container.length === 0) {
                    // Fallback as original video.js could have changed ID
                    container = $('[id^="' + id + '"]').first();

                    if (container.length > 0 && !container.hasClass('video-js')) {
                        container = container.closest('.video-js');
                    }
                }

                // Store parent reference BEFORE dispose removes the element
                if (container.length > 0) {
                    parent = container.parent();

                    if (self.isDebugMode) {
                        console.log('[Video.js] Found container for:', id, container[0]);
                    }
                }

                // Dispose player if exists
                if (registry.player) {
                    try {
                        // Remove from elements
                        var idx = self.elements.indexOf(registry.player);
                        if (idx > -1) {
                            self.elements.splice(idx, 1);
                        }

                        // dispose
                        registry.player.dispose();

                        if (self.isDebugMode) {
                            console.log('[Video.js] Player disposed for:', id);
                        }
                    } catch (e) {
                        if (self.isDebugMode) {
                            console.log('[Video.js] Error disposing player:', e);
                        }
                    }
                    registry.player = null;
                }

                registry.isInitialized = false;

                // Restore original HTML
                if (parent && parent.length > 0) {

                    var restore = $(registry.originalHtml);

                    // Restore to parent
                    parent.append(restore);

                    // Show cookie overlay
                    self.toggleClassToParent(true, [restore[0]]);

                    if (self.isDebugMode) {
                        console.log('[Video.js] HTML restored for:', id);
                    }
                } else {
                    if (self.isDebugMode) {
                        console.log('[Video.js] Could not find parent to restore:', id);
                    }
                }
            });
        },

        // Init Video
        initVideo: function(element) {
            var self = this;
            var video = $(element);
            var tabContentClass = "." + BEM.buildElementClassname("tabs", "tab-content");

            video.closest(tabContentClass).on("tab-content-active", function(e) {
                self.resizeVideoJS();
            });

            // Register and check consent
            var registry = this.registerVideo(element);

            if (this.hasConsent()) {
                this.initVideoWithConsent(element, registry);
            } else {
                this.showConsentOverlay(element, registry);
            }
        },

        // Init slider video slide
        initVideoSlide: function(element) {
            var registry = this.registerVideo(element);

            if (this.hasConsent()) {
                this.initVideoWithConsent(element, registry);
            } else {
                this.showConsentOverlay(element, registry);
            }
        },

        // Handle active slider video slide
        handleActiveVideoSlide: function(slider) {
            var self = this;
            var instance = slider.data('cartware_slider/instance');

            if (!instance) {
                return;
            }

            var video = instance.activeItem.find('.video-js');

            if ($(instance.activeItem).css('display') === 'none') {
                self.fixMobileActiveThumbSelection();
                instance.pauseAutoslide(false);
            } else {
                if (video.length > 0) {
                    var videoOverlay = instance.activeItem.find(self.videoOverlayClass);

                    // Get player from registry or video.js
                    var videoId = video.attr('id');
                    var registry = self.videoRegistry[videoId];
                    var player = registry && registry.player;

                    if (!player) {
                        try {
                            player = videojs(video[0]);
                        } catch (e) {
                            return;
                        }
                    }

                    if (player.ended()) {
                        player.currentTime(0);
                    }

                    player.play();
                    instance.pauseAutoslide(true);

                    if (!("__component_events_bound" in player)) {
                        player.on('timeupdate', function () {
                            if (!videoOverlay.hasClass('disabled') && this.currentTime() > 0) {
                                videoOverlay.addClass('disabled');
                            }
                        });
                        player.on('ended', function () {
                            if (instance.settings.autoslide == true) {
                                videoOverlay.removeClass('disabled');
                                instance.next();
                            }
                        });

                        player["__component_events_bound"] = true;
                    }
                } else {
                    instance.pauseAutoslide(false);
                }
            }
        },

        // Mobile thumbs
        fixMobileActiveThumbSelection: function() {
            var self = this;
            var activeThumb = $('.thumbs-container .thumb-video.active');
            if (activeThumb.css('display') === 'none') {
                var nextThumb = activeThumb.closest('li').next().find('.thumb');
                activeThumb.removeClass('active');
                nextThumb.addClass('active');
                self.fixMobileActiveThumbSelection();
            }
        },

        // Resize
        resizeVideoJS: function() {
            var windowHeight = $(window).height();

            $.each(this.elements, function(index, value) {
                if (!value || !value.id) return;

                var player = $(document.getElementById(value.id()));
                if (player.length === 0) return;

                var width = player.parent().width(),
                    height = width / value.videoAspectRatio,
                    maxHeight = Math.min(player.offsetParent().height() || windowHeight, windowHeight);

                if (player.closest(".b_cartware-slider-slide--video, .b_tabs__tab-content").length) {
                    maxHeight = Infinity;
                }

                if (height > maxHeight) {
                    width = width * maxHeight / height;
                    height = maxHeight;
                }

                player
                    .width(width|0)
                    .height(height|0);

                player.trigger("gigaset.content.change");
            });

            this.fixMobileActiveThumbSelection();
        },

        // Legacy method
        videojs: function () {
            var element = $(arguments[0]),
                setup = element.data('custom-setup');

            if (setup) {
                var registry = this.registerVideo(arguments[0]);

                if (this.hasConsent()) {
                    this.initVideoWithConsent(arguments[0], registry);
                } else {
                    this.showConsentOverlay(arguments[0], registry);
                }
            }
        },

        // Add / hide cookie denied class and therefore the cookie banner
        toggleClassToParent: function (addClass, args) {
            if (!args || !args[0]) {
                return;
            }

            var element = $(args[0]);
            var widget = element.closest('.b_video-widget');

            if (widget.length === 0) {
                // Fallback to finding any video widget
                widget = $('.b_video-widget');
            }

            if (addClass) {
                widget.addClass('cookie-denied');
                if (this.isDebugMode) {
                    console.log('[Video.js] Added cookie-denied class');
                }
            } else {
                widget.removeClass('cookie-denied');
                if (this.isDebugMode) {
                    console.log('[Video.js] Removed cookie-denied class');
                }
            }
        }
    });
});

/**
 * Video Teaser FCE (Video Player in a Popup) - can be vimeo, youtube player instances
 * Consent and overlay handled via video.js component
 * Used by B2C stores as well as B2B (Pro) stores
 */
define('rebrush/components/blocks/fce/elements/videoteaser',[
    "jquery",
    "cartware/components/_base",
    "cartware/jquery/bem",
    "videojs",
    "cartware/components/Dispatcher",
    "videojs-vimeo",
    "videojs-youtube",
    "magnific-popup"
], function(
    $,
    Component,
    BEM,
    videojs,
    ComponentDispatcher
) {
    var BLOCK_NAME = "elements-video-teaser";

    return Component.create({
        blocks:			null,

        $init: function() {
            $(document.body).on('click', '.' + BEM.buildElementClassname(BLOCK_NAME, 'close'), function() {
                $.magnificPopup.close();
            });

            this.blocks = BEM.block(BLOCK_NAME);
            this.blocks.each(function() {
                var block = BEM.block(this);
                var video = block.getElements("video");
                var embed = block.getElements("video-embed");

                var triggerClasses = [
                    '.' + BEM.buildElementClassname(BLOCK_NAME, "poster"),
                    '.' + BEM.buildElementClassname(BLOCK_NAME, "trigger")
                ];



                block.on("click", triggerClasses.join(", "), function(e) {
                    $.magnificPopup.open({
                        mainClass: "video-fullscreen",
                        items: {
                            src: embed.html() + '<span class="' + BEM.buildElementClassname(BLOCK_NAME, "close") + '"></span>',
                            type: 'inline'
                        }
                    });

                    var clonedVideo = $.magnificPopup.instance.container.find("video");

                    var setup = clonedVideo.data("custom-setup");

                    if (setup && "autoInitialization" in setup && setup.autoInitialization === false) {
                        delete setup.autoInitialization;
                    }

                    // setup.fluid = true;
                    setup.autoplay = true;

                    clonedVideo.data("custom-setup", setup);

                    // videojs(
                    // 	clonedVideo.get(0),
                    // 	clonedVideo.data("custom-setup")
                    // ).play();

                    new ComponentDispatcher({
                        handles: ["default"],
                        apply: {
                            "default": ["rebrush/components/cartware/video"]
                        }
                    });
                });

            });
        }
    });
});

!(function(win) {

/**
 * FastDom
 *
 * Eliminates layout thrashing
 * by batching DOM read/write
 * interactions.
 *
 * @author Wilson Page <wilsonpage@me.com>
 * @author Kornel Lesinski <kornel.lesinski@ft.com>
 */



/**
 * Mini logger
 *
 * @return {Function}
 */
var debug = 0 ? console.log.bind(console, '[fastdom]') : function() {};

/**
 * Normalized rAF
 *
 * @type {Function}
 */
var raf = win.requestAnimationFrame
  || win.webkitRequestAnimationFrame
  || win.mozRequestAnimationFrame
  || win.msRequestAnimationFrame
  || function(cb) { return setTimeout(cb, 16); };

/**
 * Initialize a `FastDom`.
 *
 * @constructor
 */
function FastDom() {
  var self = this;
  self.reads = [];
  self.writes = [];
  self.raf = raf.bind(win); // test hook
  debug('initialized', self);
}

FastDom.prototype = {
  constructor: FastDom,

  /**
   * Adds a job to the read batch and
   * schedules a new frame if need be.
   *
   * @param  {Function} fn
   * @param  {Object} ctx the context to be bound to `fn` (optional).
   * @public
   */
  measure: function(fn, ctx) {
    debug('measure');
    var task = !ctx ? fn : fn.bind(ctx);
    this.reads.push(task);
    scheduleFlush(this);
    return task;
  },

  /**
   * Adds a job to the
   * write batch and schedules
   * a new frame if need be.
   *
   * @param  {Function} fn
   * @param  {Object} ctx the context to be bound to `fn` (optional).
   * @public
   */
  mutate: function(fn, ctx) {
    debug('mutate');
    var task = !ctx ? fn : fn.bind(ctx);
    this.writes.push(task);
    scheduleFlush(this);
    return task;
  },

  /**
   * Clears a scheduled 'read' or 'write' task.
   *
   * @param {Object} task
   * @return {Boolean} success
   * @public
   */
  clear: function(task) {
    debug('clear', task);
    return remove(this.reads, task) || remove(this.writes, task);
  },

  /**
   * Extend this FastDom with some
   * custom functionality.
   *
   * Because fastdom must *always* be a
   * singleton, we're actually extending
   * the fastdom instance. This means tasks
   * scheduled by an extension still enter
   * fastdom's global task queue.
   *
   * The 'super' instance can be accessed
   * from `this.fastdom`.
   *
   * @example
   *
   * var myFastdom = fastdom.extend({
   *   initialize: function() {
   *     // runs on creation
   *   },
   *
   *   // override a method
   *   measure: function(fn) {
   *     // do extra stuff ...
   *
   *     // then call the original
   *     return this.fastdom.measure(fn);
   *   },
   *
   *   ...
   * });
   *
   * @param  {Object} props  properties to mixin
   * @return {FastDom}
   */
  extend: function(props) {
    debug('extend', props);
    if (typeof props != 'object') throw new Error('expected object');

    var child = Object.create(this);
    mixin(child, props);
    child.fastdom = this;

    // run optional creation hook
    if (child.initialize) child.initialize();

    return child;
  },

  // override this with a function
  // to prevent Errors in console
  // when tasks throw
  catch: null
};

/**
 * Schedules a new read/write
 * batch if one isn't pending.
 *
 * @private
 */
function scheduleFlush(fastdom) {
  if (!fastdom.scheduled) {
    fastdom.scheduled = true;
    fastdom.raf(flush.bind(null, fastdom));
    debug('flush scheduled');
  }
}

/**
 * Runs queued `read` and `write` tasks.
 *
 * Errors are caught and thrown by default.
 * If a `.catch` function has been defined
 * it is called instead.
 *
 * @private
 */
function flush(fastdom) {
  debug('flush');

  var writes = fastdom.writes;
  var reads = fastdom.reads;
  var error;

  try {
    debug('flushing reads', reads.length);
    runTasks(reads);
    debug('flushing writes', writes.length);
    runTasks(writes);
  } catch (e) { error = e; }

  fastdom.scheduled = false;

  // If the batch errored we may still have tasks queued
  if (reads.length || writes.length) scheduleFlush(fastdom);

  if (error) {
    debug('task errored', error.message);
    if (fastdom.catch) fastdom.catch(error);
    else throw error;
  }
}

/**
 * We run this inside a try catch
 * so that if any jobs error, we
 * are able to recover and continue
 * to flush the batch until it's empty.
 *
 * @private
 */
function runTasks(tasks) {
  debug('run tasks');
  var task; while (task = tasks.shift()) task();
}

/**
 * Remove an item from an Array.
 *
 * @param  {Array} array
 * @param  {*} item
 * @return {Boolean}
 */
function remove(array, item) {
  var index = array.indexOf(item);
  return !!~index && !!array.splice(index, 1);
}

/**
 * Mixin own properties of source
 * object into the target.
 *
 * @param  {Object} target
 * @param  {Object} source
 */
function mixin(target, source) {
  for (var key in source) {
    if (source.hasOwnProperty(key)) target[key] = source[key];
  }
}

// There should never be more than
// one instance of `FastDom` in an app
var exports = win.fastdom = (win.fastdom || new FastDom()); // jshint ignore:line

// Expose to CJS & AMD
if ((typeof define) == 'function') define('fastdom',[],function() { return exports; });
else if ((typeof module) == 'object') module.exports = exports;

})( typeof window !== 'undefined' ? window : this);

define('rebrush/components/blocks/raster-grid/tiles/helpers/image/center',[
    'jquery',
    'cartware/jquery/bem',
    'lodash',
    'fastdom'
], function(
    $,
    BEM,
    _,
    fastdom
) {
    var sourcesCache = {};

    function Helper(block) {
        this.block = BEM.block(block);
        this.image = this.block.getElements('image');

        this.layout();
    }

    Helper.prototype = {
        layout: function() {


            return;


            setTimeout(_.bind(function() {
                var currentSrc = this.image.get(0).currentSrc || this.image.get(0).src;

                var tick = _.bind(function(dimensions) {
                    fastdom.measure(_.bind(function() {
                        var width = this.block.width(),
                            height = this.block.height();

                        // CSS assigns the image a width of 100 %
                        if (dimensions[0] !== width) {
                            var q = dimensions[0] / width;
                            dimensions = _.map(dimensions, function(val) {
                                return val / q;
                            });
                        }

                        var targetLeft = Math.floor((dimensions[0] - width) / -2),
                            targetTop = Math.floor((dimensions[1] - height) / -2);

                        fastdom.mutate(_.bind(function() {
                            this.image.css({
                                left: targetLeft,
                                top: targetTop
                            });
                        }, this));
                    }, this));
                }, this);

                console.log(this.image.get(0), this.image.get(0).currentSrc);

                if (currentSrc in sourcesCache) {
                    tick(sourcesCache[currentSrc]);
                } else {
                    var tmpImage = new Image();
                    tmpImage.onload = function() {
                        var dimensions = [tmpImage.width, tmpImage.height];
                        sourcesCache[currentSrc] = dimensions;
                        tick(dimensions);
                    };
                    tmpImage.src = currentSrc;
                }
            }, this), 2000);
        }
    };

    return Helper;
});
define('rebrush/components/blocks/raster-grid/tiles/video',[
    'jquery',
    'cartware/components/_base',
    'cartware/jquery/bem',
    'lodash',
    './helpers/image/center',
    'cartware/components/Dispatcher',
    'videojs',
    'videojs-vimeo',
    'videojs-youtube',
    'magnific-popup'
], function(
    $,
    Component,
    BEM,
    _,
    ImageCenterHelper,
    ComponentDispatcher,
    videojs
) {
    var BLOCK_NAME = 'raster-grid-item-video';

    function Tile(block) {
        this.block = BEM.block(block);
        this.embed = this.block.getElements('embed');
        this.bindEvents();
    }

    Tile.prototype = {
        bindEvents: function() {
            this.block.on('click', 'a', _.bind(function(e) {
                // var link = e.currentTarget;
                e.preventDefault();
                this.openVideo();
            }, this));
        },

        openVideo: function() {
            $.magnificPopup.open({
                mainClass: 'mfp-full',
                fixedContentPos: true,
                items: {
                    src: this.embed.html(),
                    type: 'inline'
                }
            });

            var video = $.magnificPopup.instance.container.find('video');
            var setup = video.data('custom-setup');

            $.magnificPopup.instance.container.find('.' + BEM.buildElementClassname('raster-grid-item-video-popup', 'close')).on('click', function() {
                video.remove();
                $.magnificPopup.instance.container.html('');
                $.magnificPopup.instance.close();
            });

            video.data('custom-setup', setup);
            video.addClass('vjs-16-9');

            videojs(video.get(0), video.data('custom-setup'), function() {
                var player = this;
                if (video[0].hasAttribute('muted')) {
                    player.muted(true);
                }
                player.videoAspectRatio = video.data('aspect-ratio');
            });
        }
    };

    return Component.create({
        blocks: null,
        imageCenterHelpers: null,

        constructor: function() {
            this.imageCenterHelpers = [];
            this.tiles = [];
        },

        $init: function() {
            var self = this;

            this.blocks = BEM.block(BLOCK_NAME);
            this.blocks.each(function() {
                self.imageCenterHelpers.push(new ImageCenterHelper(this));
                self.tiles.push(new Tile(this));
            });

            this.hub.on('ui.component.rendering.after', function() {
                _.forEach(self.imageCenterHelpers, function(helper) {
                    helper.layout();
                });
            });
        }
    });
});
define('rebrush/components/blocks/raster-grid-v2/tiles/video',[
    'jquery',
    'cartware/components/_base',
    'cartware/jquery/bem',
    'lodash',
    'cartware/components/Dispatcher',
    'videojs',
    'videojs-vimeo',
    'videojs-youtube',
    'magnific-popup'
], function(
    $,
    Component,
    BEM,
    _,
    ComponentDispatcher,
    videojs
) {
    var BLOCK_NAME = 'raster-grid-v2-item-video';

    function Tile(block) {
        this.block = BEM.block(block);
        this.embed = this.block.getElements('embed');
        this.bindEvents();
    }

    Tile.prototype = {
        bindEvents: function() {
            this.block.on('click', 'a', _.bind(function(e) {
                e.preventDefault();
                this.openVideo();
            }, this));
        },

        openVideo: function() {
            $.magnificPopup.open({
                mainClass: 'mfp-full',
                fixedContentPos: true,
                items: {
                    src: this.embed.html(),
                    type: 'inline'
                }
            });

            var video = $.magnificPopup.instance.container.find('video');
            var setup = video.data('custom-setup');

            $.magnificPopup.instance.container.find('.' + BEM.buildElementClassname('raster-grid-v2-item-video-popup', 'close')).on('click', function() {
                video.remove();
                $.magnificPopup.instance.container.html('');
                $.magnificPopup.instance.close();
            });

            video.data('custom-setup', setup);
            video.addClass('vjs-16-9');

            videojs(video.get(0), video.data('custom-setup'), function() {
                var player = this;
                if (video[0].hasAttribute('muted')) {
                    player.muted(true);
                }
                player.videoAspectRatio = video.data('aspect-ratio');
            });
        }
    };

    return Component.create({
        blocks: null,

        constructor: function() {
            this.tiles = [];
        },

        $init: function() {
            var self = this;

            this.blocks = BEM.block(BLOCK_NAME);
            this.blocks.each(function() {
                self.tiles.push(new Tile(this));
            });
        }
    });
});

/**
 * Requires the video components only on pages that contain a video, the video player only when it comes near the viewport.
 * Requiring video.js executes its object-keys shim, which reads window.sharedStorage
 * and triggers a Chrome deprecation warning (Lighthouse Best Practices) on every page.
 *
 * The require() calls must stay literal so r.js keeps the components in the bundle.
 */
define('rebrush/components/cartware/video-loader',[
    'jquery',
    'cartware/components/_base',
    'cartware/components/Dispatcher'
], function(
    $,
    Component,
    ComponentDispatcher
) {
    const ROOT_MARGIN = '600px 0px';

    return Component.create({
        $init: function() {
            var dispatcher = new ComponentDispatcher({});
            var initialize = function(component) {
                dispatcher.initializeComponent(component);
            };

            var videos = $('video[data-custom-setup], .b_video-widget');

            if (videos.length) {
                this.whenNearViewport(videos, function() {
                    require(['rebrush/components/cartware/video'], initialize);
                });
            }

            if ($('.b_elements-video-teaser').length) {
                require(['rebrush/components/blocks/fce/elements/videoteaser'], initialize);
            }

            if ($('.b_raster-grid-item-video').length) {
                require(['rebrush/components/blocks/raster-grid/tiles/video'], initialize);
            }

            if ($('.b_raster-grid-v2-item-video').length) {
                require(['rebrush/components/blocks/raster-grid-v2/tiles/video'], initialize);
            }
        },

        // Gallery slider videos need the slider ready event, which only fires once
        whenNearViewport: function(elements, callback) {
            if (elements.closest('.cartware-slider').length || typeof window.IntersectionObserver !== 'function') {
                callback();
                return;
            }

            var observer = new IntersectionObserver(function(entries) {
                if (entries.some(function(entry) { return entry.isIntersecting; })) {
                    observer.disconnect();
                    callback();
                }
            }, { rootMargin: ROOT_MARGIN });

            elements.each(function() {
                observer.observe(this);
            });
        }
    });
});

define('rebrush/components/compare/compare',[
    'jquery',
    'lodash',
    'cartware/components/_base',
    'cartware/jquery/bem',
    'cartware/components/Dispatcher',
    'json!rebrush/handles/default.json',
    'gigaset/spinner',
    'text!shoptheme/templates/magnific-popup/default.html',
    'magnific-popup'
], function (
    $,
    _,
    Component,
    BEM,
    ComponentDispatcher,
    Handles,
    Spinner,
    PopupHtml
) {
    var popupTemplate = null,
        $document = $(document.body),
        MODIFIER_ROTATE = 'rotate',
        STATE_ACTIVE = 'active',
        STATE_INACTIVE = 'inactive';

    return Component.create({
        overlay: null,
        compareList: null,
        compareListHeaderSidebar: null,
        compareListHeaderItem: null,
        compareListFooter: null,
        productChooser: null,
        modalMainClasses: null,
        isFilteredView: false,
        localStorage: window.localStorage,
        compareTriggerClass: BEM.buildElementClassname('product-item', 'compare-trigger'),
        isCheckoutPage: gigaset.handles && gigaset.handles.includes('checkout-onepage-index') || false,
        isCategoryPage: gigaset.handles && gigaset.handles.includes('catalog_category_view') || false,
        isProductPage: gigaset.handles && gigaset.handles.includes('catalog_product_view') || false,
        isComparisonPage: gigaset.handles && gigaset.handles.includes('catalog_product_compare_list') || false,
        storeCode: gigaset.store_code,
        stickyRowOffsetTop: null,
        config: {
            storage: {
                identifier: 'compare_data',
                key_whitelist: ['item_count', 'product_ids', 'attribute_set', 'attribute_set_name', 'filter_attributes', 'is_filtered_view']
            },
            maxItemCount: gigaset.cartware_compare && gigaset.cartware_compare.config && gigaset.cartware_compare.config.max_item_count || 8
        },

        $init: function () {
            this._initData();
            this.initializeEvents();
        },

        _initData: function () {
            var productIds = this.getLocalStorageData('product_ids') || null;

            if (this.isComparisonPage) {
                this.initializeCompareList();
            } else {
                if (productIds) {
                    this.triggerCompareTriggerState();
                    this.triggerCompareLinkState();
                }
                this.toggleOverlay();
            }
        },

        initializeEvents: function () {
            $document.on('click', '.compare-link', _.bind(this.onClickCompareLink, this));
            $(document).on('product/changeSimple', $.proxy(this.onConfigurableSimpleChange, this));
            $document.on('click', '.' + this.compareTriggerClass, _.bind(this.onClickCompareTrigger, this));
            $document.on('click', '.' + BEM.buildElementClassname('compare-overlay', 'reset-button'), _.bind(this.onClickResetButton, this));
        },

        initializeCompareList: function () {
            this.compareList = BEM.block('compare-list');
            this.compareListHeaderSidebar = BEM.block('compare-list-header-sidebar');
            this.compareListHeaderItem = BEM.block('compare-list-header-item');
            this.compareListFooter = BEM.block('compare-list-footer');

            $document.on('click', '.' + BEM.buildElementClassname('compare-list-header-sidebar', 'cta-product-chooser'), _.bind(this.onClickProductChooserButton, this));
            $document.on('click', '.' + BEM.buildElementClassname('compare-list-header-sidebar', 'cta-share'), _.bind(this.onClickShareButton, this));
            $document.on('click', '.' + BEM.buildElementClassname('compare-list-header-sidebar', 'cta-list-reset'), _.bind(this.onClickResetButton, this));
            $document.on('change', '.' + BEM.buildElementClassname('compare-list-header-sidebar', 'radio-view'), _.bind(this.onChangeRadioView, this));
            $document.on('click', '.' + BEM.buildElementClassname('compare-list-header-item', 'remove-button'), _.bind(this.onClickRemoveButton, this));
            $document.on('click', 'tr.' + BEM.buildElementClassname('compare-list', 'row-attributes-parent'), _.bind(this.onAttributeParentSectionTrigger, this));
            $document.on('click', '.' + BEM.buildElementClassname('compare-list', 'checkbox-filter'), _.bind(this.onClickCheckboxFilter, this));
            $document.on('click', '.' + BEM.buildElementClassname('compare-list', 'cta-close'), _.bind(this.onClickCloseButton, this));
            $document.on('click', '.' + BEM.buildElementClassname('compare-list-footer', 'cta-top'), _.bind(this.onOverlayBackToTop, this));

            this.initWrapperSideScrollEvents();
            this.initWrapperDownScrollEvents();

            this.initLocalStorage();
            this.restoreFilterStates();
        },

        initializeProductChooser: function () {
            this.productChooser = BEM.block('compare-product-chooser');
            this.itemCountBeforeChooser = this.getLocalStorageData('item_count');

            var self = this;

            $document.on('click', '.' + BEM.buildElementClassname('compare-product-chooser', 'cta-close'), _.bind(this.onClickCloseButton, this));
            $document.on('click', '.' + BEM.buildElementClassname('compare-product-chooser', 'cta-submit'), _.bind(this.onClickCloseButton, this));

            this.productChooser.getElements('cta-item-add').each(function (i, e) {
                e.on('click', _.bind(self.onClickProductChooserAddItem, self));
            });
        },

        onClickCompareTrigger: function (e) {
            var element = $(e.currentTarget),
                productType = element.data('product-type'),
                parent = element.closest('.b_product-item__container');

            e.preventDefault();
            Spinner.add(parent);

            if (productType !== 'configurable') {
                this.updateCompareList(element);
                Spinner.remove();
            } else {
                var ajaxCartUrl = element.data('ajax-cart-url');
                this.openCatalogProductViewLightbox(ajaxCartUrl);
            }
        },

        onClickCompareLink: function (e) {
            var element = $(e.currentTarget),
                parent = element.closest('.b_product-add-to-links'),
                productType = element.data('product-type'),
                notificationInput = $('#watchlist_email');

            e.preventDefault();

            if (notificationInput) {
                notificationInput.removeClass('required-entry');
                notificationInput.removeClass('validate-email');
            }

            if (this.validateProduct(productType)) {
                Spinner.add(parent);

                if (false !== this.updateCompareList(element)) {

                    if (this.isCategoryPage) {
                        this.triggerCompareTriggerState();
                        if (element.parents('.popup-body').length > 0) {
                            $.magnificPopup.close();
                        }
                    } else {
                        if (element.hasClass('active')) {
                            var msg = Translator.translate('%s added to the comparison list. Add more products ' +
                                'via the category overview or select more product variants for the comparison to compare products ' +
                                'with each other.').replace('%s', element.data('product-name'));

                            if (this.getLocalStorageData('item_count') > 1) {
                                var compareUrl = this.getCompareUrl();
                                msg += ' ' + '<a href="' + compareUrl + '" class="text-primary">' + Translator.translate('Go to comparison') + '</a>';
                            }

                            this.triggerSessionMessage(msg, 'success');
                        }
                    }
                }
                Spinner.remove();
            }

            if (notificationInput) {
                notificationInput.addClass('required-entry');
                notificationInput.addClass('validate-email');
            }
        },

        triggerCompareTriggerState: function () {
            var self = this,
                storageData = this.getLocalStorageData();

            $('.' + self.compareTriggerClass).each(function (index, item) {
                var e = $(item);
                if (storageData && storageData.product_ids) {
                    if (e.data('product-type') === 'configurable') {
                        var childIds = [];
                        var childProducts = e.attr('data-child-products');
                        if (childProducts) {
                            if (childProducts.includes(',')) {
                                childIds = childProducts.split(',');
                            } else {
                                childIds.push(childProducts);
                            }
                        }
                        if (childIds.some(id => storageData.product_ids.includes(Number(id)))) {
                            self.setElementState(e, STATE_ACTIVE);
                        } else {
                            self.setElementState(e, STATE_INACTIVE);
                        }
                    } else {
                        var productId = e.data('product');
                        if (productId && storageData.product_ids.includes(productId)) {
                            self.setElementState(e, STATE_ACTIVE);
                        } else {
                            self.setElementState(e, STATE_INACTIVE);
                        }
                    }
                } else {
                    self.setElementState(e, STATE_INACTIVE);
                }
            });
        },

        triggerCompareLinkState: function (simple) {
            var compareLink = $('.compare-link'),
                simpleId = simple && simple.id ? simple.id : null,
                storageData = this.getLocalStorageData();

            if (!simpleId) {
                if (compareLink.data('product-type') === 'configurable') {
                    var currentSimple = _.get(window, 'spConfig.__currentSimple');
                    if (currentSimple) {
                        simpleId = currentSimple.id;
                    }
                } else {
                    simpleId = compareLink.data('product');
                }
            }
            simpleId = Number(simpleId);
            if (storageData && storageData.product_ids && simpleId && storageData.product_ids.include(simpleId)) {
                this.setElementState(compareLink, STATE_ACTIVE);
            } else {
                this.setElementState(compareLink, STATE_INACTIVE);
            }
        },

        setElementState: function (e, state) {
            switch (state) {
                case STATE_ACTIVE:
                    if (!e.hasClass('active')) {
                        e.addClass('active');
                    }
                    if (e.hasClass('compare-link')) {
                        e.text(e.data('remove-text'));
                    }
                    break;
                case STATE_INACTIVE:
                    if (e.hasClass('active')) {
                        e.removeClass('active');
                    }
                    if (e.hasClass('compare-link')) {
                        e.text(e.data('add-text'));
                    }
                    break;
                default:
                    return;
            }
        },

        validateProduct: function (type) {
            if (type === 'configurable') {
                if (typeof productAddToCartForm === 'undefined' || !productAddToCartForm.validator.validate()) {
                    return false;
                }
            }
            return true;
        },

        updateCompareList: function (element) {
            let storageData = this.getLocalStorageData(),
                itemId = null;

            if (element.data('product-type') === 'configurable') {
                var configurableConfig = _.get(window, 'spConfig');
                itemId = Number(configurableConfig.__currentSimple.id);
            } else {
                itemId = Number(element.data('product'));
            }

            if (!storageData) {
                storageData = this.initCompareListStorageData(itemId, element.data('attribute-set'), element.data('attribute-set-name'));
                this.setElementState(element, STATE_ACTIVE);
            } else {
                if (storageData.product_ids.includes(itemId)) {
                    storageData = this.removeFromCompareList(itemId);
                    this.setElementState(element, STATE_INACTIVE);
                } else {
                    storageData = this.addToCompareList(itemId, element.data('attribute-set'));

                    if (!storageData) {
                        this.setElementState(element, STATE_INACTIVE);
                    } else {
                        this.setElementState(element, STATE_ACTIVE);
                    }
                }
            }

            this.toggleOverlay();

            return storageData;
        },

        addToCompareList: function (itemId, attributeSet) {
            var storageData = this.getLocalStorageData();

            if (!this.validateCompareData(storageData, attributeSet)) {
                return false;
            } else {
                storageData.item_count += 1;
                storageData.product_ids.push(itemId);
                this.updateLocalStorageData(storageData);
            }

            return storageData;
        },

        removeFromCompareList: function (itemId) {
            var storageData = this.getLocalStorageData();

            storageData.item_count -= 1;

            if (storageData.item_count <= 0) {
                this.resetLocalStorageData();
                this.resetCompareView();
                this.hideOverlay();
            } else {
                storageData.product_ids.splice(storageData.product_ids.indexOf(itemId), 1);
                this.updateLocalStorageData(storageData);
            }

            return storageData;
        },

        initCompareListStorageData: function (itemId, attributeSet, attributeSetName) {
            let configObject = {
                item_count: 1,
                product_ids: [],
                attribute_set: attributeSet,
                attribute_set_name: attributeSetName
            };
            configObject.product_ids.push(itemId);
            this.updateLocalStorageData(configObject);

            return configObject;
        },

        validateCompareData: function (storageData, attributeSet) {
            var errorMessage;

            if (storageData.attribute_set !== attributeSet) {
                errorMessage = Translator.translate('Chosen product is not matching the product type %s').replace('%s', storageData.attribute_set_name || '');
            }
            if (storageData.item_count + 1 > this.config.maxItemCount) {
                errorMessage = Translator.translate('Compare list is full (Max. %s items).').replace('%s', this.config.maxItemCount);
            }

            if (errorMessage) {
                if (this.isComparisonPage) {

                    var alertBoxClass = BEM.buildElementClassname('compare-product-chooser', 'alert-box');
                    var alertBox = $('.' + alertBoxClass);

                    alertBox.text(errorMessage).bemAddModifier('error');

                    setTimeout(function () {
                        alertBox.bemRemoveModifier('error');
                    }, 2500);
                } else {
                    this.renderMagnificPopup(errorMessage);
                }

                return false;
            }

            return true;
        },

        onConfigurableSimpleChange: function (e, simple) {
            this.triggerCompareLinkState(simple);
        },

        onClickResetButton: function (e) {
            var element = $(e.currentTarget),
                confirmation = element.data('confirmation'),
                allowClean = confirm(confirmation);

            e.preventDefault();

            if (allowClean === true) {
                this.resetLocalStorageData();
                this.resetCompareView();
                this.hideOverlay();
                if (this.isCategoryPage) {
                    this.triggerCompareTriggerState();
                }
                if (this.isProductPage) {
                    this.triggerCompareLinkState();
                }
            } else {
                e.stopPropagation();
                return false;
            }
        },

        exitLightbox: function () {
            $('.tooltip').hide();
            if ($.magnificPopup.instance.isOpen) {
                $.magnificPopup.close();
            }
            this.toggleOverlay();
            if (this.isProductPage) {
                this.triggerCompareLinkState();
            }
            if (this.isCategoryPage) {
                this.triggerCompareTriggerState();
            }
        },

        resetCompareView: function () {
            if (this.isComparisonPage) {
                BEM.block('compare-list-footer').remove();
                var compareList = BEM.block('compare-list');
                compareList.getElements('container').remove();
                compareList.getElements('no-items').bemRemoveModifier('hide');
            }
        },

        onClickRemoveButton: function (e) {
            var element = $(e.currentTarget),
                itemId = element.data('product');

            if (itemId) {
                $('.' + BEM.buildElementClassname('compare-list', 'table')).find('td[data-product="' + itemId + '"]').remove();
                this.removeFromCompareList(itemId);
                $('.tooltip').hide();
                var itemCount = this.getLocalStorageData('item_count');
                if (this.getLocalStorageData('item_count') === 0 || !itemCount) {
                    this.resetCompareView();
                }
            }

            this.refreshWindowUrl();
            this.fixStickyRowDimensions();
        },

        onClickShareButton: function (e) {
            var shareUrl = this.getCompareUrl(),
                successNotification = BEM.block('compare-list-header-sidebar').getElements('success-notification--cta-share');

            if (navigator.share) {
                navigator.share({
                    title: Translator.translate('Select your preferred share option.'),
                    url: shareUrl
                }).then(() => {
                    successNotification.fadeIn().delay(2000).fadeOut();
                });
            } else {
                navigator.permissions.query({name: 'clipboard-write'}).then((result) => {
                    if (result.state === 'granted' || result.state === 'prompt') {
                        navigator.clipboard.writeText(shareUrl).then(function () {
                            successNotification.fadeIn().delay(3000).fadeOut();
                        });
                    }
                });
            }
        },

        onClickProductChooserButton: function (e) {
          this.openProductChooserPopup();
        },

        onClickProductChooserAddItem: function (e) {
            const element = $(e.currentTarget),
                  self = this,
                  parentWrapper = element.closest('.' + BEM.buildElementClassname('compare-product-chooser', 'item-wrapper'));

            let productId = element.data('product-id'),
                success = false;

            if (element.data('product-type') === 'configurable') {
                const variantSelect = parentWrapper.find('.' + BEM.buildElementClassname('compare-product-chooser', 'item-variant-select'));
                productId = variantSelect.val();
            }

            if (self.getLocalStorageData('product_ids').includes(productId)) {
                success = self.removeFromCompareList(productId);
            } else {
                success = self.addToCompareList(productId, element.data('attribute-set-id'));
            }

            if (typeof success !== 'undefined') this.toggleProductChooserItemSelected(element);
        },

        toggleProductChooserItemSelected: function (element) {
            var icon = element.find('svg'),
                parentWrapper = element.closest('.' + BEM.buildElementClassname('compare-product-chooser', 'item-wrapper')),
                variantSelect = parentWrapper.find('.' + BEM.buildElementClassname('compare-product-chooser', 'item-variant-select'));

            if (icon) {
                icon.toggleClass('icon-rebrush-ui-check-white');
                icon.toggleClass('icon-rebrush-ui-plus-orange');
            }

            if (parentWrapper) {
                if (!parentWrapper.bemHasModifier('selected')) {
                    parentWrapper.bemAddModifier('selected');
                } else {
                    parentWrapper.bemRemoveModifier('selected');
                }
            }

            if (element) {
                if (!element.bemHasModifier('selected')) {
                    element.bemAddModifier('selected');
                } else {
                    element.bemRemoveModifier('selected');
                }
            }

            if(variantSelect) {
                if (!variantSelect.attr('disabled')) {
                    variantSelect.attr('disabled','disabled');
                } else {
                    variantSelect.removeAttr('disabled');
                }
            }
        },

        onChangeRadioView: function (e) {
            switch (e.target.value) {
                case 'view-all':
                    this.resetFilters();
                    this.isFilteredView = false;
                    break;
                case 'view-filtered':
                    this.applyFilters();
                    this.isFilteredView = true;
                    break;
                default:
                    return;
            }
        },

        resetFilters: function () {
            var compareList = BEM.block('compare-list'),
                attributeRows = compareList.getElements('row-attributes-child'),
                storageData = this.getLocalStorageData(),
                self = this;

            attributeRows.each(function (index, element) {
                self.showRow($(element));
            });

            $('.' + BEM.buildElementClassname('compare-list', 'section-trigger-icon')).each(function (index, element) {
                var triggerIcon = $(element);
                if (!triggerIcon.bemHasModifier(MODIFIER_ROTATE)) {
                    triggerIcon.bemAddModifier(MODIFIER_ROTATE);
                }
            });

            storageData.is_filtered_view = 0;
            this.updateLocalStorageData(storageData);
        },

        applyFilters: function () {
            var compareList = BEM.block('compare-list'),
                checkboxes = compareList.getElements('checkbox-filter'),
                sections = compareList.getElements('row-attributes-parent'),
                storageData = this.getLocalStorageData(),
                self = this;

            checkboxes.each(function (index, element) {
                var box = $(element);
                if (!box.is(':checked')) {
                    var row = box.closest('tr');
                    self.hideRow(row);
                }
            });

            sections.each(function (index, element) {
                var section = $(element);
                var childRows = $('tr[data-section-parent="' + section.data('section-identifier') + '"]');

                var allChildsHidden = true;
                childRows.each(function (index, row) {
                    var childRow = $(row);
                    if (childRow.css('display') !== 'none') {
                        allChildsHidden = false;
                        return false;
                    }
                });

                var triggerIcon = section.find('.' + BEM.buildElementClassname('compare-list', 'section-trigger-icon'));
                if (allChildsHidden) {
                    if (triggerIcon.bemHasModifier(MODIFIER_ROTATE)) {
                        triggerIcon.bemRemoveModifier(MODIFIER_ROTATE);
                    }
                } else {
                    if (!triggerIcon.bemHasModifier(MODIFIER_ROTATE)) {
                        triggerIcon.bemAddModifier(MODIFIER_ROTATE);
                    }
                }
            });

            storageData.is_filtered_view = 1;
            this.updateLocalStorageData(storageData);
        },

        restoreFilterStates: function () {
            var storageData = this.getLocalStorageData(),
                isFilteredView = storageData && storageData.is_filtered_view || null,
                filterAttributes = storageData && storageData.filter_attributes || null;

            if (filterAttributes) {
                $.each(filterAttributes, function (index, attribute) {
                    var checkbox = $('input[data-attribute="' + attribute + '"');
                    if (checkbox) {
                        checkbox.prop('checked', true);
                    }
                });
                if (isFilteredView) {
                    $('#radio-view-filtered').prop('checked', true).trigger('change');
                }
            }
        },

        onAttributeParentSectionTrigger: function (e) {
            if (this.isFilteredView) {
                return;
            }

            var section = $(e.currentTarget),
                childRows = $('tr[data-section-parent="' + section.data('section-identifier') + '"]'),
                self = this;

            childRows.each(function (index, element) {
                var row = $(element);
                if (row.css('display') !== 'none') {
                    self.hideRow(row);
                } else {
                    self.showRow(row);
                    section.css('box-shadow', '-16px 14px 12px -20px rgb(0 0 0)');
                }
            });

            var triggerIcon = section.find('i[data-section-parent="' + section.data('section-identifier') + '"]');

            if (triggerIcon.bemHasModifier(MODIFIER_ROTATE)) {
                triggerIcon.bemRemoveModifier(MODIFIER_ROTATE);
            } else {
                triggerIcon.bemAddModifier(MODIFIER_ROTATE);
            }
        },

        onClickCheckboxFilter: function (e) {
            var checkbox = $(e.currentTarget),
                storageData = this.getLocalStorageData(),
                attributeName = checkbox.data('attribute'),
                filters = storageData.filter_attributes || [];

            if (!filters.includes(attributeName)) {
                filters.push(attributeName);
            } else {
                filters.splice(filters.indexOf(attributeName), 1);
                if (this.isFilteredView) {
                    var row = checkbox.closest('tr');
                    this.hideRow(row);
                }
            }

            storageData.filter_attributes = filters;
            this.updateLocalStorageData(storageData);
        },

        onClickCloseButton: function (e) {
            this.exitLightbox();
        },

        productChooserCloseCallback: function () {
            if (this.itemCountBeforeChooser !== this.getLocalStorageData('item_count')) {
                this.refreshCompareTable();
            }
        },

        refreshCompareTable: function () {
            this.compareUrl = this.getCompareUrl();

            this._sendAjaxRequest(this.compareUrl, null, null, _.bind(function (response, status, xhr) {
                $('.' + BEM.buildElementClassname('compare-list', 'main-container')).replaceWith(response.result);
                this.refreshWindowUrl();
                this.initWrapperSideScrollEvents();
                this.initWrapperDownScrollEvents();
                this.restoreFilterStates();
                this.itemCountBeforeChooser = this.getLocalStorageData('item_count');
            }, this));
        },

        refreshWindowUrl: function() {
            var compareUrl = this.getCompareUrl();
            if (history.pushState) {
                window.history.pushState({ path: compareUrl }, '', compareUrl);
            }
        },

        onOverlayBackToTop: function (e) {
            e.preventDefault();
            $('#compare-list-overlay')[0].scrollIntoView();
        },

        openCatalogProductViewLightbox: function (detailViewUrl) {
            this._sendAjaxRequest(detailViewUrl, null, null, _.bind(function (response, status, xhr) {

                var responseHandles = $.parseJSON(xhr.getResponseHeader('X-Magento-Handles') || '[]');

                if (responseHandles.indexOf('gigaset_dealerpos_checkout_productview') > -1 &&
                    responseHandles.indexOf('catalog_product_view') < 0
                ) {
                    responseHandles.push('catalog_product_view');
                }

                if (responseHandles.indexOf('catalog_product_customoption_print') > -1) {
                    responseHandles.splice(responseHandles.indexOf('catalog_product_customoption_print'), 1);
                }

                this.renderMagnificPopup(response, _.bind(this.applyProductViewLightboxStyles, this), null,true, responseHandles);
            }, this));
        },

        openProductChooserPopup: function () {
            var productChooserCta = this.compareListHeaderSidebar.getElements('cta-product-chooser'),
                url = productChooserCta.data('product-chooser-url');

            let data = { attributeSet: this.getLocalStorageData('attribute_set'), productIds: this.getLocalStorageData('product_ids') };

            this._sendAjaxRequest(url, data, null, _.bind(function (response, status, xhr) {

                this.renderMagnificPopup(response, _.bind(this.initializeProductChooser, this), _.bind(this.productChooserCloseCallback, this), null, null);

            }, this));
        },

        /**
         * Send ajax request
         *
         * @param url
         * @param data
         * @param callback
         * @param onSuccessCallback
         * @private
         */
        _sendAjaxRequest: function (url, data, callback, onSuccessCallback) {
            var self = this;
            data = data || {};

            Spinner.add($document);

            // add isAjax param to avoid caching problems
            if (url.indexOf('isAjax=1') === -1) {
                let hasAjaxParam = false;

                if (typeof data === 'string') {
                    hasAjaxParam = data.indexOf('isAjax=1') !== -1;
                } else if (typeof data === 'object' && data !== null) {
                    hasAjaxParam = data.isAjax === '1' || data.isAjax === 1;
                }

                if (!hasAjaxParam) {
                    url += (url.indexOf('?') === -1 ? '?' : '&') + 'isAjax=1';
                }
            }

            url = (this.isSecure === true && url.indexOf('http://') === 0) ? url.replace('http://', 'https://') : url;

            $.ajax(url, {
                method: 'POST',
                data: data
            }).then(_.bind(function (response, status, xhr) {

                if (typeof callback === 'function') {
                    callback(response, status, xhr);
                }

                Spinner.remove();

                if ((response.error || response.success === false)) {
                    self.renderMagnificPopup(response.error);
                } else {
                    if (typeof onSuccessCallback === 'function') {
                        onSuccessCallback(response, status, xhr);
                    }
                }
            }, this));
        },

        renderMagnificPopup: function (message, onOpenCallback, onCloseCallback, configurableOptions, responseHandles) {
            configurableOptions = configurableOptions || false;
            responseHandles = responseHandles || [];

            var self = this;

            var newModalMainClasses = (responseHandles.indexOf('catalog_product_view') > -1) ?
                ['container-size', 'shadow', 'mfp-product-view', 'b_compare-popup-product-details'] :
                ['shadow'];

            if ($.magnificPopup.instance.isOpen) {
                if (self.modalMainClasses) {
                    _.forEach(self.modalMainClasses, function (className) {
                        $.magnificPopup.instance.wrap.removeClass(className);
                        $.magnificPopup.instance.bgOverlay.removeClass(className);
                    });
                }
                _.forEach(newModalMainClasses, function (className) {
                    $.magnificPopup.instance.wrap.addClass(className);
                    $.magnificPopup.instance.bgOverlay.addClass(className);
                });
            }

            self.modalMainClasses = newModalMainClasses;

            $.magnificPopup.open({
                mainClass: self.modalMainClasses.join(' '),
                items: {
                    src: this._renderPopup({
                        body: message,
                    }),
                    type: 'inline',
                },
                closeBtnInside: true,
                callbacks: {
                    open: function () {

                        if (typeof onOpenCallback === 'function') {
                            onOpenCallback();
                        }

                        if (configurableOptions) {
                            new ComponentDispatcher({
                                handles: ['catalog_product_view', 'PRODUCT_TYPE_configurable'],
                                apply: Handles
                            });
                        }

                        if (typeof onCloseCallback === 'function') {
                            $.magnificPopup.instance.close = function() {
                                onCloseCallback();
                                // Call the original close method to close the popup
                                $.magnificPopup.proto.close.call(this);
                            };
                        }
                    }
                }
            });
        },

        _renderPopup: function (data) {
            if (null === popupTemplate) {
                popupTemplate = _.template(PopupHtml);
            }

            return popupTemplate(data);
        },

        applyProductViewLightboxStyles: function () {
            var viewBlock = BEM.block('product-view');

            if (this.isCategoryPage && viewBlock.length) {
                var popup = $('.popup-body'),
                    compareLink = $('.compare-link', popup);

                $('.tooltip').hide();

                compareLink.insertAfter(viewBlock.getElements('column-top').first());
                compareLink.addClass('btn btn-outline');
                compareLink.wrap('<div class="compare-link-container"></div>');

                var closeBtn = '<div class="cta-close-container"><div class="cta-close btn" ' +
                    'data-toggle="tooltip" title="%s">×</div></div>'.replace('%s', Translator.translate('Close'));

                popup.prepend(closeBtn);
                $document.on('click', '.cta-close', _.bind(this.exitLightbox, this));
            }
        },

        updateLocalStorageData: function (data) {
            var storageData = this.localStorage.getItem(this.config.storage.identifier);
            if (data && Object.keys(data).length) {
                if (!storageData) {
                    storageData = {};
                } else {
                    storageData = JSON.parse(storageData);
                }
                storageData[this.storeCode] = data;
                this.localStorage.setItem(this.config.storage.identifier, JSON.stringify(storageData));
            }
        },

        getLocalStorageData: function (key) {
            var storageData = this.localStorage.getItem(this.config.storage.identifier);

            if (!storageData || storageData === 'undefined') {
                return null;
            }

            storageData = JSON.parse(storageData);

            if (!storageData[this.storeCode]) return null;

            storageData = storageData[this.storeCode];

            return key ? storageData[key] : storageData;
        },

        resetLocalStorageData: function () {
            var storageData = this.localStorage.getItem(this.config.storage.identifier);
            storageData = JSON.parse(storageData);

            if (storageData && storageData[this.storeCode]) {
                delete storageData[this.storeCode];
                this.localStorage.setItem(this.config.storage.identifier, JSON.stringify(storageData));
            }
        },

        initLocalStorage: function () {
            var keyWhitelist = this.config.storage.key_whitelist,
                data = $('.' + BEM.buildElementClassname('compare-list', 'container')).data('compare-list-init');

            var storageUpdate = {};

            $.each(data, function (key, value) {
                var result = null;
                if (keyWhitelist.includes(key)) {
                    if (key === 'product_ids' || key === 'filter_attributes') {
                        result = [];
                        if (key === 'product_ids') {
                            result = value.split(',').map(Number);
                        }
                        if (key === 'filter_attributes') {
                            result = value.split(',');
                        }
                    } else {
                        if (key === 'item_count' || key === 'attribute_set' || key === 'is_filtered_view') {
                            result = Number(value);
                        } else {
                            result = value;
                        }
                    }

                    storageUpdate[key] = result;
                }
            });

            if (Object.keys(storageUpdate).length) {
                this.updateLocalStorageData(storageUpdate);
            }
        },

        toggleOverlay: function () {
            var storageData = this.getLocalStorageData(),
                overlay = BEM.block('compare-overlay');

            if (overlay) {
                this.overlay = overlay;

                if (!storageData || !storageData.item_count || storageData.item_count < 1 || this.isCheckoutPage || this.isComparisonPage) {
                    this.hideOverlay();
                } else {
                    this.showOverlay();
                }

                $document.on('click', '.' + BEM.buildElementClassname('compare-overlay', 'compare-button'), _.bind(function (e) {
                    var button = $(e.currentTarget);
                    button.prop('href', this.getCompareUrl());
                    button.prop('target', '_blank');
                    button.prop('rel', 'noreferer noopener');
                }, this));
            }
        },

        hideOverlay: function () {
            if (this.overlay.length) {
                var compareButton = this.overlay.getElements('compare-button');

                if (this.overlay.hasClass('slideIn')) {
                    this.overlay.removeClass('slideIn').addClass('slideOut');
                }
                if (!compareButton.hasClass('disabled')) {
                    compareButton.addClass('disabled');
                }
            }
        },

        showOverlay: function () {
            var compareButton = this.overlay.getElements('compare-button');

            if (this.overlay.hasClass('slideOut')) {
                this.overlay.removeClass('slideOut').addClass('slideIn');
            }
            if (compareButton.hasClass('disabled')) {
                compareButton.removeClass('disabled');
            }
        },

        getCompareUrl: function () {
            var compareButton = BEM.block('compare-overlay').getElements('compare-button'),
                storageData = this.getLocalStorageData();

            if (storageData) {
                var searchParams = new URLSearchParams(this.getLocalStorageData()).toString();
                return new URL(compareButton.data('base-url')) + '?' + btoa(searchParams);
            } else {
                return compareButton.data('base-url');
            }
        },

        showRow: function (row) {
            if (row.bemHasModifier('hide')) {
                row.bemRemoveModifier('hide');
            }
            if (row.hasClass('hidden-print')) {
                row.removeClass('hidden-print');
            }
        },

        hideRow: function (row) {
            if (!row.hasClass('hidden-print')) {
                row.addClass('hidden-print');
            }
            if (!row.bemHasModifier('hide')) {
                row.bemAddModifier('hide');
            }
        },

        fixStickyRowDimensions: function () {
            var rowSticky = $('.' + BEM.buildElementClassname('compare-list', 'row-sticky')),
                cloneRow = $('.' + BEM.buildElementClassname('compare-list', 'row-attributes-child')).first();

            if (cloneRow.length) {
                rowSticky.css('width', cloneRow.css('width'));
                rowSticky.find('th').css('width', cloneRow.find('th').first().css('width'));
                rowSticky.find('td').css('width', cloneRow.find('td').first().css('width'));
            }
        },

        isMobileDevice: function () {
            return window.matchMedia('only screen and (max-width: 480px)').matches;
        },

        triggerSessionMessage: function (txt, type, fadeSeconds = null) {
            const html = '<ul class="messages"><li class="' + type + '-msg"><ul><li><span>' + txt + '</span></li></ul></li></ul>';

            BEM.block('product-view').getElements('container').first().prepend(html);

            const messageBlock = $('ul.messages:first');
            messageBlock.css('margin-top', '10px').css('margin-bottom', '30px');

            setTimeout(function () {
                messageBlock.fadeOut('slow').remove();
            }, fadeSeconds || 15000);
        },

        initWrapperDownScrollEvents: function () {
            this.fixStickyRowDimensions();

            $(window).on('scroll', function () {
                var rowSticky = $('.' + BEM.buildElementClassname('compare-list', 'row-sticky'));

                if (rowSticky.length) {
                    if (!self.stickyRowOffsetTop) {
                        self.stickyRowOffsetTop = rowSticky.offset().top;
                    }
                    var windowTop = $(window).scrollTop();
                    if (self.stickyRowOffsetTop < windowTop) {
                        if (!rowSticky.bemHasModifier('sticky')) {
                            rowSticky.bemAddModifier('sticky');
                        }
                    } else {
                        if (rowSticky.bemHasModifier('sticky')) {
                            rowSticky.bemRemoveModifier('sticky');
                        }
                    }
                }
            });
        },

        initWrapperSideScrollEvents: function () {
            var wrapper = $('.' + BEM.buildElementClassname('compare-list', 'wrapper')),
                wrapperHeader = $('.' + BEM.buildElementClassname('compare-list', 'wrapper--header')),
                wrapperContent = $('.' + BEM.buildElementClassname('compare-list', 'wrapper--content')),
                wrapperHeaderSticky = $('.' + BEM.buildElementClassname('compare-list', 'row-sticky')),
                running = false;

            wrapperHeader.on('scroll', function () {
                if (running) {
                    running = false;
                    return;
                }
                running = true;
                wrapperContent.scrollLeft(wrapperHeader.scrollLeft());
                wrapperHeaderSticky.css({marginLeft: -1 * wrapperHeader.scrollLeft()});
                // wrapperHeaderSticky.scrollLeft(wrapperHeader.scrollLeft());
            });
            wrapperContent.on('scroll', function () {
                if (running) {
                    running = false;
                    return;
                }
                running = true;
                wrapperHeader.scrollLeft(wrapperContent.scrollLeft());
                wrapperHeaderSticky.css({marginLeft: -1 * wrapperContent.scrollLeft()});
                // wrapperHeaderSticky.scrollLeft(wrapperContent.scrollLeft());
            });

            let pos = {top: 0, left: 0, x: 0, y: 0};
            var actualTarget = null;

            const compareWrapperMouseMoveHandler = function (e) {
                var target = $(actualTarget);
                const dx = e.clientX - pos.x;
                target.scrollLeft(pos.left - dx);
                // const dy = e.clientY - pos.y;
                // wrapper.scrollTop(pos.top - dy);
            };

            const compareWrapperMouseUpHandler = function () {
                var target = $(actualTarget);
                target.css('cursor', 'grab');
                target.removeProp('user-select');

                $document.off('mousemove', compareWrapperMouseMoveHandler);
                $document.off('mouseup', compareWrapperMouseUpHandler);
            };

            const compareWrapperMouseDownHandler = function (e) {
                var target = $(e.currentTarget);
                target.css('cursor', 'grabbing');
                target.css('userSelect', 'none');

                pos = {
                    left: target.scrollLeft(),
                    top: target.scrollTop(),
                    // Get the current mouse position
                    x: e.clientX,
                    y: e.clientY,
                };

                actualTarget = e.currentTarget;

                $document.on('mousemove', compareWrapperMouseMoveHandler);
                $document.on('mouseup', compareWrapperMouseUpHandler);
            };

            wrapper.on('mousedown', compareWrapperMouseDownHandler);
        }
    });
});

define('rebrush/components/blocks/hero-slider',[
	'jquery',
	'cartware/components/_base',
	'cartware/jquery/bem',
	'rebrush/system/layout/responsive-hub'
], function(
	$,
	Component,
	BEM,
	ResponsiveHub
) {
	return Component.create({
		blocks: null,

		$init: function() {
			this.blocks = BEM.block('hero-slider');
			this.blocks.each(function() {
				var block = BEM.block(this),
					headlines = block.getElements('headline'),
					sublines = block.getElements('subline'),
					maxSublineHeight = Math.max.apply(Math, sublines.map(function() {
						return $(this).outerHeight();
					}).get()),
					maxHeight = Math.max.apply(Math, headlines.map(function() {
						return $(this).outerHeight();
					}).get());

				ResponsiveHub
					.on('change.mobile.enter', function() {
						headlines.css('height', 'auto');
						sublines.css('height', 'auto');
					})
					.on('change.mobile.leave', function() {
						headlines.css('height', maxHeight + 'px');
						sublines.css('height', maxSublineHeight + 'px');
					});
			});
		}
	});
});
/**
 * Renders Turnstile placeholders from Gigaset_Theme_Helper_Turnstile::renderLazyWidget()
 * on the first focus inside their form.
 *
 * A submit before the token exists is held back and repeated from the Turnstile callback,
 * otherwise the server rejects the request (empty cf-turnstile-response).
 */
define('rebrush/components/plugin/turnstile-lazy',[
    'jquery',
    'cartware/components/_base'
], function(
    $,
    Component
) {
    const PLACEHOLDER_SELECTOR = '.gigaset-turnstile-lazy';
    const ACTIVE_CLASS = 'gigaset-turnstile-lazy--active';
    const RESPONSE_FIELD_SELECTOR = '[name="cf-turnstile-response"]';
    const MAX_ATTEMPTS = 200;
    const RETRY_INTERVAL = 50;

    return Component.create({
        $init: function() {
            var self = this;

            $(document).on('focusin', 'form', function() {
                $(this).find(PLACEHOLDER_SELECTOR).each(function() {
                    self.render(this, 0);
                });
            });

            // Capture phase, so this runs before VarienForm's submit handler on the form
            document.addEventListener('submit', function(event) {
                self.holdSubmitWithoutToken(event);
            }, true);
        },

        holdSubmitWithoutToken: function(event) {
            var form = event.target;
            var placeholder = form.querySelector ? form.querySelector(PLACEHOLDER_SELECTOR) : null;

            if (!placeholder) {
                return;
            }

            var responseField = placeholder.querySelector(RESPONSE_FIELD_SELECTOR);

            if (responseField && responseField.value) {
                return;
            }

            event.preventDefault();
            event.stopImmediatePropagation();

            $(placeholder).data('turnstile-submit-pending', true);
            this.render(placeholder, 0);
        },

        render: function(placeholder, attempts) {
            var self = this;
            var $placeholder = $(placeholder);

            if ($placeholder.data('turnstile-rendered')) {
                return;
            }

            // Reserved in the same task as the focus, so the shift counts as user-initiated and stays out of CLS
            $placeholder.addClass(ACTIVE_CLASS);

            if (typeof window.turnstile === 'undefined' || typeof window.turnstile.render !== 'function') {
                if (attempts < MAX_ATTEMPTS) {
                    setTimeout(function() {
                        self.render(placeholder, attempts + 1);
                    }, RETRY_INTERVAL);
                }
                return;
            }

            $placeholder.data('turnstile-rendered', true);

            var options = $.extend({}, $placeholder.data('turnstile-options'), {
                callback: function() {
                    self.resubmitPending(placeholder);
                }
            });

            try {
                window.turnstile.render(placeholder, options);
            } catch (e) {
                $placeholder.data('turnstile-rendered', false);
                console.error('[Turnstile] Lazy render failed:', e);
            }
        },

        resubmitPending: function(placeholder) {
            var $placeholder = $(placeholder);
            var form = $placeholder.closest('form').get(0);

            if (!form || !$placeholder.data('turnstile-submit-pending')) {
                return;
            }

            $placeholder.data('turnstile-submit-pending', false);

            if (typeof form.requestSubmit === 'function') {
                form.requestSubmit();
            } else {
                form.submit();
            }
        }
    });
});


define("json!rebrush/etc/breakpoints.json", function(){ return {
  	"mobile": 479,
  	"xs": 639,
	"sm": 1009,
	"md": 1229,
	"lg": 1439
};});

define('rebrush/components/blocks/raster-grid',[
    'jquery',
    'cartware/components/_base',
    'cartware/jquery/bem',
    'lodash',
    'lucid',
    'json!rebrush/etc/breakpoints.json',
    'enquire'
], function(
    $,
    Component,
    BEM,
    _,
    lucidJS,
    Breakpoints,
    enquire
) {
    var BLOCK_NAME = 'raster-grid';
    var BLOCK_DATA_ATTRIBUTE_MATRIX = 'grid-matrix';
    var TILE_DATA_ATTRIBUTE_CONFIG = 'grid-config';
    var MODE_MOBILE = 'mobile';
    var MODE_TABLET = 'tablet';
    var MODE_DESKTOP = 'desktop';

    // TODO remove
    var mockData = {
        tiles: [{
            mobile: {
                size: [1, 1],
                offset: [0, 0]
            },
            tablet: {
                size: [2, 1],
                offset: [0, 0]
            },
            desktop: {
                size: [2, 2],
                offset: [1, 0]
            }
        }, {
            mobile: {
                size: [1, 1],
                offset: [0, 1]
            },
            tablet: {
                size: [1, 1],
                offset: [0, 1]
            },
            desktop: {
                size: [1, 1],
                offset: [0, 0]
            }
        }, {
            mobile: {
                size: [1, 1],
                offset: [0, 2]
            },
            tablet: {
                size: [1, 1],
                offset: [1, 1]
            },
            desktop: {
                size: [1, 1],
                offset: [0, 1]
            }
        },



        {
            mobile: {
                size: [1, 1],
                offset: [0, 3]
            },
            tablet: {
                size: [1, 1],
                offset: [0, 2]
            },
            desktop: {
                size: [2, 1],
                offset: [0, 2]
            }
        }, {
            mobile: {
                size: [1, 1],
                offset: [0, 4]
            },
            tablet: {
                size: [1, 1],
                offset: [1, 2]
            },
            desktop: {
                size: [1, 1],
                offset: [1, 2]
            }
        }]
    };

    // TODO remove
    function generateMockTiles(parent) {
        parent = $(parent);

        _.forEach(mockData.tiles, function(tileData, i) {
            var element = $('<div class="' + BEM.buildElementClassname(BLOCK_NAME, 'tile') + '"></div>');
            // var inner = $('<div class="' + BEM.buildElementClassname(BLOCK_NAME, 'tile-inner') + '"></div>');
            // inner.appendTo(element);

            var dummyContent = $('<div class="b_raster-grid-item-image"><div class="b_raster-grid-item-image__title">Lorem ipsum</div></div>');
            dummyContent.appendTo(element);

            element.attr('data-tile-id', i);
            element.data(TILE_DATA_ATTRIBUTE_CONFIG, tileData);
            parent.append(element);
        });
    }

    // TODO remove
    function generateMockGridMatrix(block) {
        block = $(block);
        block.data(BLOCK_DATA_ATTRIBUTE_MATRIX, {
            mobile: {
                columns: 1
            },
            tablet: {
                columns: 2
            },
            desktop: {
                columns: 3
            }
        });
    }

    // TODO remove
    function generateMockBlock() {
        var block = $('<div class="' + BEM.buildBlockClassname(BLOCK_NAME) +  '"></div>');

        generateMockTiles(block);
        generateMockGridMatrix(block);

        $('.b_main-container__col1-col-main .container:first').append(block);
    }

    function Matrix() {
        this.rows = [];
    }
    Matrix.prototype = {
        addRow: function(row) {
            this.rows.push(row);
            return this;
        },

        getRows: function() {
            return this.rows;
        }
    };

    function Row() {
        this.cells = [];
    }
    Row.prototype = {
        addCell: function(cell) {
            this.cells.push(cell);
            return this;
        },

        getCells: function() {
            return this.cells;
        }
    };

    function Cell() {
        this.tiles = [];
    }
    Cell.prototype = {
        addTile: function(tile) {
            this.tiles.push(tile);
            return this;
        },

        getTiles: function() {
            return this.tiles;
        }
    };

    function Grid(el) {
        lucidJS.EventEmitter.call(this);

        this.block = BEM.block(el);
        this.tiles = this.block.getElements('tile');
        this.config = {
            matrix: this.block.data(BLOCK_DATA_ATTRIBUTE_MATRIX)
        };

        this.mode = MODE_MOBILE;

        // this.render('desktop');
        this.watchBreakpoints();
    }

    Grid.prototype = _.create(new lucidJS.EventEmitter(), {
        watchBreakpoints: function() {
            enquire.register('(max-width: ' + (Breakpoints.mobile) + 'px)', {
                match: _.bind(function() {
                    this.render(MODE_MOBILE);
                }, this)
            });
            enquire.register('(min-width: ' + (Breakpoints.mobile + 1) + 'px) and (max-width: ' + (Breakpoints.sm) + 'px)', {
                match: _.bind(function() {
                    this.render(MODE_TABLET);
                }, this)
            });

            enquire.register('(min-width: ' + (Breakpoints.sm + 1) + 'px)', {
                match: _.bind(function() {
                    this.render(MODE_DESKTOP);
                }, this)
            });
        },

        render: function(mode) {
            if (this.mode === mode) {
                return;
            }

            // Placement comes from the server markup, moving the tiles would repaint them late (LCP)
            if (this.block.hasClass(BEM.buildBlockClassname(BLOCK_NAME) + '--css-grid')) {
                this.mode = mode;
                this.trigger('render');
                this.block.bemAddModifier('rendered');
                return;
            }

// console.log('RENDER ' + mode);

            var matrix = this.calculateMatrix(mode);
            this.clear();

            _.forEach(matrix.getRows(), _.bind(function(row) {
                var rowElement = this.createRow();

                _.forEach(row.getCells(), _.bind(function(cell) {
                    var cellElement = this.createCell(),
                        tiles = cell.getTiles();

                    var tileData = this.getTileData(_.head(tiles), mode),
                        size = tileData.size;

                    cellElement.bemAddModifier('width-' + size[0]);

                    _.forEach(tiles, _.bind(function(tile) {
                        var tileData = this.getTileData(tile, mode),
                            size = tileData.size,
                            tileElementClassname = BEM.buildElementClassname(BLOCK_NAME, 'tile');

                        tile = $(tile);

                        tile.attr('class', _.filter(tile.attr('class').split(' '), function(c) {
                            return !(new RegExp('^' + tileElementClassname + '--size-\\d+-\\d+')).match(c);
                        }).join(' '));

                        tile.bemAddModifier('size-' + size[0] + '-' + size[1]);

                        tile.appendTo(cellElement);
                    }, this));

                    cellElement.appendTo(rowElement);
                }, this));

                rowElement.appendTo(this.block);
            }, this));

            this.mode = mode;

            this.trigger('render');

            this.block.bemAddModifier('rendered');
        },

        getTileData: function(tile, mode) {
            var tileData = $(tile).data(TILE_DATA_ATTRIBUTE_CONFIG),
                data = _.get(tileData, mode, _.get(tileData, 'mobile', null));

            return data;
        },

        calculateMatrix: function(mode) {
            var coordinates = _.reduce(this.tiles.get(), _.bind(function(result, tile, tileId) {
                var data = this.getTileData(tile, mode),
                    size = data.size,
                    offset = data.offset,
                    width = Math.max(size[0], 1),
                    height = Math.max(size[1], 1);

                _.times(width, function(i) {
                    var x = parseInt(offset[0], 10) + parseInt(i, 10);
                    _.times(height, function(j) {
                        var y = parseInt(offset[1], 10) + parseInt(j, 10);

                        if ('undefined' === typeof result[x]) {
                            result[x] = [];
                        }

                        result[x][y] = tile;
                    });
                });

                return result;
            }, this), []);

            var lineCoordinates = _.reduce(coordinates, function(result, col, x) {
                _.forEach(col, function(tile, y) {
                    if ('undefined' === typeof result[y]) {
                        result[y] = [];
                    }

                    if ('console' in window && 'undefined' !== typeof result[y][x]) {
                        console.warn('Raster grid tile collision detected');
                    }

                    result[y][x] = tile;
                });

                return result;
            }, []);


            var rows = [];
            var rowBuffer = [];
            _.forEach(lineCoordinates, function(line, y) {
                if (rowBuffer.length) {
                    var previousLine = _.last(rowBuffer);

                    if (_.intersection(previousLine, line).length === 0) {
                        rows.push(rowBuffer);
                        rowBuffer = [];
                    }
                }

                rowBuffer.push(line);
            });

            if (rowBuffer.length) {
                rows.push(rowBuffer);
            }



            var matrix = _.reduce(rows, function(m, lines) {
                var row = new Row();
                var placedTiles = [];
                var cells = [];

                _.forEach(lines, function(line, y) {
                    _.forEach(line, function(tile, x) {
                        if ('undefined' === typeof tile || 0 <= placedTiles.indexOf(tile)) {
                            return;
                        }

                        var cell = cells[x] || new Cell();

                        cell.addTile(tile);
                        cells[x] = cell;
                        placedTiles.push(tile);
                    });
                });

                _.forEach(cells, function(cell) {
                    if ('undefined' !== typeof cell) {
                        row.addCell(cell);
                    }
                });

                m.addRow(row);

                return m;
            }, new Matrix());

            return matrix;
        },

        clear: function() {
            this.tiles.detach();
            this.block.empty();
        },

        createRow: function() {
            return $('<div class="' + BEM.buildElementClassname(BLOCK_NAME, 'row') + '"></div>');
        },

        createCell: function() {
            return $('<div class="' + BEM.buildElementClassname(BLOCK_NAME, 'cell') + '"></div>');
        },

        createTile: function() {
            return $('<div class="' + BEM.buildElementClassname(BLOCK_NAME, 'tile') + '"></div>');
        }
    });


    return Component.create({
        blocks: null,

        $init: function() {
            var self = this;
            // generateMockBlock(); // TODO remove
            this.blocks = BEM.block(BLOCK_NAME);
            this.blocks.each(function() {
                var grid = new Grid(this);

                grid.on('render', function() {
                    self.hub.trigger('ui.component.rendering.after', {
                        component: self,
                        grid: grid
                    });
                });
            });
        }
    });
});
define('rebrush/components/blocks/raster-grid/tiles/image',[
    'jquery',
    'cartware/components/_base',
    'cartware/jquery/bem',
    'lodash',
    './helpers/image/center'
], function(
    $,
    Component,
    BEM,
    _,
    ImageCenterHelper
) {
    var BLOCK_NAME = 'raster-grid-item-image';

    return Component.create({
        blocks: null,
        imageCenterHelpers: null,

        constructor: function() {
            this.imageCenterHelpers = [];
        },

        $init: function() {
            var self = this;

            this.blocks = BEM.block(BLOCK_NAME);
            this.blocks.each(function() {
                self.imageCenterHelpers.push(new ImageCenterHelper(this));
            });

            this.hub.on('ui.component.rendering.after', function() {
                _.forEach(self.imageCenterHelpers, function(helper) {
                    helper.layout();
                });
            });
        }
    });
});
define('rebrush/components/blocks/raster-grid-v2',[
    'jquery',
    'cartware/components/_base',
    'cartware/jquery/bem',
    'lodash',
    'lucid',
    'json!rebrush/etc/breakpoints.json',
    'enquire'
], function(
    $,
    Component,
    BEM,
    _,
    lucidJS,
    Breakpoints,
    enquire
) {
    var BLOCK_NAME = 'raster-grid-v2';
    var BLOCK_DATA_ATTRIBUTE_MATRIX = 'grid-matrix';
    var TILE_DATA_ATTRIBUTE_CONFIG = 'grid-config';
    var MODE_MOBILE = 'mobile';
    var MODE_TABLET = 'tablet';
    var MODE_DESKTOP = 'desktop';

    function Matrix() {
        this.rows = [];
    }
    Matrix.prototype = {
        addRow: function(row) {
            this.rows.push(row);
            return this;
        },

        getRows: function() {
            return this.rows;
        }
    };

    function Row() {
        this.cells = [];
    }
    Row.prototype = {
        addCell: function(cell) {
            this.cells.push(cell);
            return this;
        },

        getCells: function() {
            return this.cells;
        }
    };

    function Cell() {
        this.tiles = [];
    }
    Cell.prototype = {
        addTile: function(tile) {
            this.tiles.push(tile);
            return this;
        },

        getTiles: function() {
            return this.tiles;
        }
    };

    function Grid(el) {
        lucidJS.EventEmitter.call(this);

        this.block = BEM.block(el);
        this.tiles = this.block.getElements('tile');
        this.config = {
            matrix: this.block.data(BLOCK_DATA_ATTRIBUTE_MATRIX)
        };

        this.mode = MODE_MOBILE;

        this.watchBreakpoints();
    }

    Grid.prototype = _.create(new lucidJS.EventEmitter(), {
        watchBreakpoints: function() {
            enquire.register('(max-width: ' + (Breakpoints.mobile) + 'px)', {
                match: _.bind(function() {
                    this.render(MODE_MOBILE);
                }, this)
            });
            enquire.register('(min-width: ' + (Breakpoints.mobile + 1) + 'px) and (max-width: ' + (Breakpoints.sm) + 'px)', {
                match: _.bind(function() {
                    this.render(MODE_TABLET);
                }, this)
            });

            enquire.register('(min-width: ' + (Breakpoints.sm + 1) + 'px)', {
                match: _.bind(function() {
                    this.render(MODE_DESKTOP);
                }, this)
            });
        },

        render: function(mode) {
            if (this.mode === mode) {
                return;
            }

            // Placement comes from the server markup, moving the tiles would repaint them late (LCP)
            if (this.block.hasClass(BEM.buildBlockClassname(BLOCK_NAME) + '--css-grid')) {
                this.mode = mode;
                this.trigger('render');
                this.block.bemAddModifier('rendered');
                return;
            }

            var matrix = this.calculateMatrix(mode);
            this.clear();

            _.forEach(matrix.getRows(), _.bind(function(row) {
                var rowElement = this.createRow();

                _.forEach(row.getCells(), _.bind(function(cell) {
                    var cellElement = this.createCell(),
                        tiles = cell.getTiles();

                    var tileData = this.getTileData(_.head(tiles), mode),
                        size = tileData.size;

                    cellElement.bemAddModifier('width-' + size[0]);

                    _.forEach(tiles, _.bind(function(tile) {
                        var tileData = this.getTileData(tile, mode),
                            size = tileData.size,
                            tileElementClassname = BEM.buildElementClassname(BLOCK_NAME, 'tile');

                        tile = $(tile);

                        tile.attr('class', _.filter(tile.attr('class').split(' '), function(c) {
                            return !(new RegExp('^' + tileElementClassname + '--size-\\d+-\\d+')).match(c);
                        }).join(' '));

                        tile.bemAddModifier('size-' + size[0] + '-' + size[1]);

                        tile.appendTo(cellElement);
                    }, this));

                    cellElement.appendTo(rowElement);
                }, this));

                rowElement.appendTo(this.block);
            }, this));

            this.mode = mode;

            this.trigger('render');

            this.block.bemAddModifier('rendered');
        },

        getTileData: function(tile, mode) {
            var tileData = $(tile).data(TILE_DATA_ATTRIBUTE_CONFIG),
                data = _.get(tileData, mode, _.get(tileData, 'mobile', null));

            return data;
        },

        calculateMatrix: function(mode) {
            var coordinates = _.reduce(this.tiles.get(), _.bind(function(result, tile, tileId) {
                var data = this.getTileData(tile, mode),
                    size = data.size,
                    offset = data.offset,
                    width = Math.max(size[0], 1),
                    height = Math.max(size[1], 1);

                _.times(width, function(i) {
                    var x = parseInt(offset[0], 10) + parseInt(i, 10);
                    _.times(height, function(j) {
                        var y = parseInt(offset[1], 10) + parseInt(j, 10);

                        if ('undefined' === typeof result[x]) {
                            result[x] = [];
                        }

                        result[x][y] = tile;
                    });
                });

                return result;
            }, this), []);

            var lineCoordinates = _.reduce(coordinates, function(result, col, x) {
                _.forEach(col, function(tile, y) {
                    if ('undefined' === typeof result[y]) {
                        result[y] = [];
                    }

                    if ('console' in window && 'undefined' !== typeof result[y][x]) {
                        console.warn('Raster grid v2 tile collision detected');
                    }

                    result[y][x] = tile;
                });

                return result;
            }, []);


            var rows = [];
            var rowBuffer = [];
            _.forEach(lineCoordinates, function(line, y) {
                if (rowBuffer.length) {
                    var previousLine = _.last(rowBuffer);

                    if (_.intersection(previousLine, line).length === 0) {
                        rows.push(rowBuffer);
                        rowBuffer = [];
                    }
                }

                rowBuffer.push(line);
            });

            if (rowBuffer.length) {
                rows.push(rowBuffer);
            }



            var matrix = _.reduce(rows, function(m, lines) {
                var row = new Row();
                var placedTiles = [];
                var cells = [];

                _.forEach(lines, function(line, y) {
                    _.forEach(line, function(tile, x) {
                        if ('undefined' === typeof tile || 0 <= placedTiles.indexOf(tile)) {
                            return;
                        }

                        var cell = cells[x] || new Cell();

                        cell.addTile(tile);
                        cells[x] = cell;
                        placedTiles.push(tile);
                    });
                });

                _.forEach(cells, function(cell) {
                    if ('undefined' !== typeof cell) {
                        row.addCell(cell);
                    }
                });

                m.addRow(row);

                return m;
            }, new Matrix());

            return matrix;
        },

        clear: function() {
            this.tiles.detach();
            this.block.empty();
        },

        createRow: function() {
            return $('<div class="' + BEM.buildElementClassname(BLOCK_NAME, 'row') + '"></div>');
        },

        createCell: function() {
            return $('<div class="' + BEM.buildElementClassname(BLOCK_NAME, 'cell') + '"></div>');
        },

        createTile: function() {
            return $('<div class="' + BEM.buildElementClassname(BLOCK_NAME, 'tile') + '"></div>');
        }
    });


    return Component.create({
        blocks: null,

        $init: function() {
            var self = this;
            this.blocks = BEM.block(BLOCK_NAME);
            this.blocks.each(function() {
                var grid = new Grid(this);

                grid.on('render', function() {
                    self.hub.trigger('ui.component.rendering.after', {
                        component: self,
                        grid: grid
                    });
                });
            });
        }
    });
});

define('rebrush/components/blocks/raster-grid-v2/tiles/helpers/image/center',[
    'jquery',
    'cartware/jquery/bem',
    'lodash',
    'fastdom'
], function(
    $,
    BEM,
    _,
    fastdom
) {
    var sourcesCache = {};

    function Helper(block) {
        this.block = BEM.block(block);
        this.image = this.block.getElements('image');

        this.layout();
    }

    Helper.prototype = {
        layout: function() {


            return;


            setTimeout(_.bind(function() {
                var currentSrc = this.image.get(0).currentSrc || this.image.get(0).src;

                var tick = _.bind(function(dimensions) {
                    fastdom.measure(_.bind(function() {
                        var width = this.block.width(),
                            height = this.block.height();

                        // CSS assigns the image a width of 100 %
                        if (dimensions[0] !== width) {
                            var q = dimensions[0] / width;
                            dimensions = _.map(dimensions, function(val) {
                                return val / q;
                            });
                        }

                        var targetLeft = Math.floor((dimensions[0] - width) / -2),
                            targetTop = Math.floor((dimensions[1] - height) / -2);

                        fastdom.mutate(_.bind(function() {
                            this.image.css({
                                left: targetLeft,
                                top: targetTop
                            });
                        }, this));
                    }, this));
                }, this);

                console.log(this.image.get(0), this.image.get(0).currentSrc);

                if (currentSrc in sourcesCache) {
                    tick(sourcesCache[currentSrc]);
                } else {
                    var tmpImage = new Image();
                    tmpImage.onload = function() {
                        var dimensions = [tmpImage.width, tmpImage.height];
                        sourcesCache[currentSrc] = dimensions;
                        tick(dimensions);
                    };
                    tmpImage.src = currentSrc;
                }
            }, this), 2000);
        }
    };

    return Helper;
});
define('rebrush/components/blocks/raster-grid-v2/tiles/image',[
    'jquery',
    'cartware/components/_base',
    'cartware/jquery/bem',
    'lodash',
    './helpers/image/center'
], function(
    $,
    Component,
    BEM,
    _,
    ImageCenterHelper
) {
    var BLOCK_NAME = 'raster-grid-v2-item-image';

    return Component.create({
        blocks: null,
        imageCenterHelpers: null,

        constructor: function() {
            this.imageCenterHelpers = [];
        },

        $init: function() {
            var self = this;

            this.blocks = BEM.block(BLOCK_NAME);
            this.blocks.each(function() {
                self.imageCenterHelpers.push(new ImageCenterHelper(this));
            });

            this.hub.on('ui.component.rendering.after', function() {
                _.forEach(self.imageCenterHelpers, function(helper) {
                    helper.layout();
                });
            });
        }
    });
});

define('rebrush/components/layout/newsletter-subscribe',[
	'jquery',
	'lodash',
	'cartware/components/_base',
	'cartware/jquery/bem',
	'text!shoptheme/templates/magnific-popup/default.html',
	'magnific-popup'
], function(
	$,
	_,
	Component,
	BEM,
	PopupHtml
) {

	var popupTemplate = null;

	function Plugin(block) {
		this.block = BEM.block(block);
		this.trigger = this.block.getElements('agreement-content-trigger');
		this.contentBlock = this.block.getElements('agreement-content');

		this.bindEvents();
	}

	Plugin.prototype = {
		bindEvents: function() {
			this.trigger.on('click', _.bind(this.showContent, this));
			this.trigger.on('keyup', _.bind(function(e) {
				if (e.key === 'Enter') {
					this.showContent(e);
				}
			}, this));
		},

		showContent: function(e) {
			e.preventDefault();
			e.stopPropagation();

			$.magnificPopup.open({
				// mainClass: 'mfp-full',
				fixedContentPos: true,
				items: {
					src: this._renderPopup({
						body: this.contentBlock.html(),
						displayCloseButton: true
					}),
					type: 'inline'
				},
			});
		},

		_renderPopup: function(data) {
			if (null === popupTemplate) {
				popupTemplate = _.template(PopupHtml);
			}

			return popupTemplate(data);
		},
	};

	return Component.create({
		$init: function() {
			new Plugin('newsletter-subscribe');
		}
	});
});
define('rebrush/components/blocks/cliplister/3dgallery',[
    'jquery',
    'lodash',
    'cartware/components/_base',
    'cartware/jquery/bem',
    'cartware/components/Config'
], function(
    $,
    _,
    Component,
    BEM,
    Config
) {
    const BLOCK_NAME = 'cliplister-3dgallery';

    let instanceCount = 0;

    const componentSymbol = Symbol();

    function Cliplister3DGallery(block) {
        this.instanceId = instanceCount++;
        this.block = BEM.block(block);
        this.block.attr('id', 'Cliplister3DGallery_' + this.instanceId);

        this.settings = this.block.data('cliplister-settings');

        this.scriptSrc = Config.get('cliplister.scriptSrc');
        this.customerId = Config.get('cliplister.customerId');
        this.init();
    }

    Cliplister3DGallery.prototype = {
        init: function() {
            const trigger = 'trigger' in this.settings ? this.settings.trigger : 'auto';
            const self = this;

            if (trigger === 'auto') {
                this.injectScript();
            } else if (trigger === 'observer') {
                let observer;
                const check = () => {
                    if (this.block.is(':visible')) {
                        this.injectScript();
                        observer && observer.disconnect();
                    }
                };

                observer = new MutationObserver(check);
                observer.observe(document.body, { attributes: true, childList: true, subtree: true });
            } else {
                (typeof console !== 'undefined') && console.warn('Trigger "' + trigger + '" not supported');
            }
        },

        injectScript: function() {
            var self = this;
            var callbackName = 'Cliplister3DGallery_callback_' + this.instanceId;
            window[callbackName] = function() {
                new Cliplister.Viewer({
                    //parentId: 'viewer',
                    parentId: 'Cliplister3DGallery_' + self.instanceId,
                    customer: self.customerId,
                    assettype: 'data',
                    assetkind: '3Dasset',
                    assets: 'assets' in self.settings ? self.settings.assets : [],
                    keyType: '30000',
                    backgroundColor: '#FFF',
                    stage: {
                        width: '100%',
                        aspectRatio: 'asset',
                        cameraControls: true,
                        autoRotate: true,
                        arEnabled: true,
                        arScale: 0.5,
                        environment: 'neutral'
                    },
                    plugins: {
                        Controls3D: {
                            layer: 1,
                            template: {
                                type: 'external',
                                source: 'https://mycliplister.com/static/viewer/assets/skins/default/controls-3d_2.html'
                            },
                            qrcode: true
                        },
                        QRCode: {
                            layer: 2,
                            id: 'qrcode'
                        }
                    }
                });
            };

            require(['qrious'], function(QR) {
                window['QRious'] = QR;

                var script = document.createElement('script');
                script.setAttribute('src', self.scriptSrc);
                script.setAttribute('type', 'module');
                script.setAttribute('onload', callbackName + '()');

                document.head.appendChild(script);
            });
        }
    };

    return Component.create({
        constructor: function() {
            this.onChange = _.bind(this.transform, this);
        },

        $init: function() {
            if (!Config.get('cliplister.active')) {
                return;
            }
            $(document).on('product/changeGallerySimple', this.onChange);
            $(document).on('product/changeGalleryPicture', this.onChange);
            this.transform();
        },

        destructor: function() {
            $(document).off('product/changeSimple', this.onChange);
            $(document).off('product/changeGallerySimple', this.onChange);
        },

        transform: function() {
            BEM.block(BLOCK_NAME).each(function() {
                if (!(componentSymbol in this)) {
                    this[componentSymbol] = new Cliplister3DGallery(this);
                }
            });
        }
    });
});
define('rebrush/components/blocks/catalogsearch/autocomplete/recommendations',[
    'jquery',
    'lodash',
    'cartware/components/_base',
    'cartware/jquery/bem'
], function(
    $,
    _,
    Component,
    BEM
) {
    return Component.create({

        searchBlock: null,
        recommendationsBlock: null,
        $document: $(document.body),
        minQueryLength: null,

        $init: function() {
            this.searchBlock = BEM.block('header-search');
            this.recommendationsBlock = BEM.block('search-autocomplete-recommendations');

            this.minQueryLength = _.get(window, 'gigaset.catalogsearch.config.min_query_length', 2);

            this.initializeEvents();
        },

        initializeEvents: function() {
            var searchInput = this.searchBlock.getElements('input').first();

            // this.searchBlock.getElements('input').first().on(['input', 'focus'], _.bind(this.onSearchInput, this));

            ['input','focus'].forEach(event =>
                searchInput.on(event, _.bind(this.onSearchInput, this))
            );
        },

        onSearchInput: function(e) {
            var input = e.currentTarget;

            if (input.value.length >= this.minQueryLength) {
                if (!this.recommendationsBlock.hasModifier('hidden')) {
                    this.recommendationsBlock.addModifier('hidden');
                }
            } else {
                if (this.recommendationsBlock.hasModifier('hidden')) {
                    this.recommendationsBlock.removeModifier('hidden');
                }
            }
        }

    });
});
define('rebrush/components/layout/skip-links',[
    'jquery',
    'lodash',
    'cartware/components/_base',
    'cartware/jquery/bem',
], function(
    $,
    _,
    Component,
    BEM
) {
    var BLOCK_IDENTIFIER_HEADER_FUNCTIONS = 'header-functions';
    var BLOCK_IDENTIFIER_SKIP_LINKS = 'page-accessibility-navigation-skiplinks';
    var BLOCK_IDENTIFIER_PAGE_SWITCH = 'page-switch';

    return Component.create({

        $init: function(){
            this.headerFunctionsBlock = BEM.block(BLOCK_IDENTIFIER_HEADER_FUNCTIONS);
            this.searchTrigger = this.headerFunctionsBlock.getElements('search-button');

            this.pageLanguageSwitchBlock = BEM.block(BLOCK_IDENTIFIER_PAGE_SWITCH);
            this.languageSwitchTrigger = this.pageLanguageSwitchBlock.getElements('popover-trigger');

            this.skipLinksBlock = BEM.block(BLOCK_IDENTIFIER_SKIP_LINKS);
            this.skipLinks = this.skipLinksBlock.find('li');
            this.skipLinkAnchors = this.skipLinks.find('a');

            this.isKeyboardFocus = false;

            this.bindEvents();
        },

        bindEvents: function() {
            $(document).on('keydown', this.handleKeyboardShortcuts.bind(this));

            this.skipLinkAnchors.on('keydown', this.handleChildLinkKeydown.bind(this));
            this.skipLinkAnchors.on('click', this.handleChildLinkClick.bind(this));
            this.skipLinkAnchors.on('focus', this.handleChildFocus.bind(this));

            this.skipLinksBlock.on('focus', this.handleContainerFocus.bind(this));
            this.skipLinksBlock.on('keydown', this.handleContainerKeydown.bind(this));
            this.skipLinksBlock.on('focusout', this.handleContainerFocusOut.bind(this));

            $(document).on('keydown', () => {
                this.isKeyboardFocus = true;
            }).on('mousedown', () => {
                this.isKeyboardFocus = false;
            });

        },

        handleChildLinkClick: function(e) {
            var link = $(e.target);

            if (!link.hasClass(BEM.buildElementClassname(BLOCK_IDENTIFIER_SKIP_LINKS, 'skip-link'))) {
                return;
            }

            e.preventDefault();

            if (link.bemHasModifier('search')) {
                this.triggerSearch();
            } else if (link.bemHasModifier('content')) {
                this.skipLinksBlock.removeModifier('open');
                this.triggerContent();
            } else if (link.bemHasModifier('language')) {
                this.triggerLanguageSwitch();
            } else if (link.bemHasModifier('cookie')) {
                this.showCookieBanner();
            }
        },

        handleKeyboardShortcuts: function(e) {
            const { altKey, shiftKey, key } = e;

            if (shiftKey && altKey) {
                if (key.toLowerCase() === 'c') { // Cart shortcut: Shift + Alt + C
                    e.preventDefault();
                    this.triggerCart();
                } else if (key.toLowerCase() === 'a') { // Customer Account shortcut: Shift + Alt + A
                    e.preventDefault();
                    this.triggerCustomerAccount();
                }
            }
        },

        handleChildLinkKeydown: function(e) {
            var link = $(e.target),
                index = this.skipLinkAnchors.index(link);

            if (e.key === 'ArrowUp') {
                e.preventDefault();
                this.skipLinkAnchors.eq(index - 1).focus();
            } else if (e.key === 'ArrowDown') {
                e.preventDefault();
                if (index + 1 > this.skipLinkAnchors.length || index + 1 === this.skipLinkAnchors.length) {
                    this.skipLinkAnchors.eq(0).focus();
                } else {
                    this.skipLinkAnchors.eq(index + 1).focus();
                }
            }
        },

        handleChildFocus: function(e) {
            const container = this.skipLinksBlock;

            // Show container if a child (e.g., <a>) receives focus via keyboard
            if (this.isKeyboardFocus && !container.hasModifier('open')) {
                container.addModifier('open');
            }
        },

        handleContainerFocus: function(e) {
            const container = this.skipLinksBlock;

            if ((container.is(':focus') || container.is(':focus-within')) && this.isKeyboardFocus) {
                e.preventDefault();
                container.addModifier('open');
                this.skipLinkAnchors.first().focus();
            }
        },

        handleContainerKeydown: function(e) {
            if (e.key === 'Escape' || e.key === 'Esc') {

                const container = this.skipLinksBlock,
                    serviceLinks = BEM.block('header-service-links') || null;

                e.preventDefault();
                container.removeModifier('open');

                if (serviceLinks.length) {
                    serviceLinks.find('a').first().focus();
                } else {
                    $(document.body).focus();
                }
            }
        },

        handleContainerFocusOut: function(e) {
            const container = this.skipLinksBlock;

            setTimeout(() => {
                if (!container.is(':focus') && !container.find(':focus').length || !container.is(':focus-within') && !container.find(':focus-within').length) {
                    container.removeModifier('open');
                }
            }, 0);
        },

        triggerSearch: function() {
            if (this.searchTrigger && this.searchTrigger.length) {
                this.searchTrigger.trigger('click');
                setTimeout(() => {
                    const searchInput = $('#topSearch_search') || null;
                    if (searchInput && searchInput.length) {
                        searchInput.focus();
                    }
                }, 0);
            }
        },

        triggerContent: function() {
            const mainContent = $('#content') || null;
            if (mainContent && mainContent.length) {
                mainContent[0].scrollIntoView({ behavior: 'smooth' });
                mainContent.focus();
            }
        },

        triggerLanguageSwitch: function() {
            if (this.languageSwitchTrigger && this.languageSwitchTrigger.length) {
                this.languageSwitchTrigger.trigger('click');
                var self = this;
                setTimeout(() => {
                    var input = self.languageSwitchTrigger.find('select');
                    if (input && input.length) {
                        input = input.first();
                        input.focus();
                    }
                }, 0);
            }
        },

        showCookieBanner: function() {
            var cookieBannerModal = $('#uc-center-container') || null;
            if (cookieBannerModal && cookieBannerModal.length) {
                cookieBannerModal.focus();
            } else {
                var cookieBannerTrigger = $('.uc-modal-trigger') || null;
                if (cookieBannerTrigger && cookieBannerTrigger.length) {
                    cookieBannerTrigger.trigger('click');
                    setTimeout(() => {
                        const centerContainer = $('#uc-center-container') || null;
                        if (centerContainer && centerContainer.length) {
                            centerContainer.focus();
                        }
                    }, 0);
                }
            }
        },

        triggerCart: function() {
            window.location.href = _.get(window, 'gigaset.system.base_url', '') + _.get(window, 'gigaset.store_code', '') + '/checkout/cart/';
        },

        triggerCustomerAccount: function() {
            window.location.href = _.get(window, 'gigaset.system.base_url', '') + _.get(window, 'gigaset.store_code', '') + '/customer/account/';
        },
    });
});

define('rebrush/components/blocks/varnish/esi',[
    'jquery',
    'lodash',
    'cartware/components/_base',
    'cartware/jquery/bem'
], function(
    $,
    _,
    Component,
    BEM
) {
    return Component.create({

        $init: function() {

            const esiUrl = _.get(window, 'gigaset.varnish.esi_url') || null;

            // The holepunch request on the same page creates the session as well
            if (!esiUrl || _.get(window, 'gigaset.holepunch.active')) {
                return;
            }

            // the whole purpose of this is to create the varnish cookie session
            new Ajax.Request(esiUrl, {
                onSuccess: function(response) {}
            });
        }
    });
});

define('rebrush/components/product/view',[
	'jquery',
	'lodash',
	'cartware/components/_base',
	'cartware/jquery/bem'
], function(
	$,
	_,
	Component,
	BEM
) {
	return Component.create({
		block:						null,

        productName:            	null,
        originalProductName:    	null,

		subheadline:				null,
		originalSubheadline:		null,

		shortDescription:			null,
		originalShortDescription:	null,

		onChangeCallback:			null,

		constructor: function() {
			this.onChangeCallback = _.bind(this._onChangeSimple, this);
		},

		$init: function() {
			this.block = BEM.block('product-view');

            this.productName = this.block.getElements('name');
            this.originalProductName = this.productName.html();

			this.shortDescription = this.block.getElements('short-description');
			this.originalShortDescription = this.shortDescription.html();

			this.subheadline = this.block.getElements('subheadline');
			this.originalSubheadline = this.subheadline.html();

			this.initializeEvents();
		},

		initializeEvents: function() {
			var currentSimple = _.get(window, 'spConfig.__currentSimple');

			$(document).on('product/changeSimple', this.onChangeCallback);
			$(document).on('product/changeGallerySimple', this.onChangeCallback);

			if (currentSimple) {
				this._onChangeSimple(null, currentSimple);
			}
		},

		_onChangeSimple: function(e, simple) {
			var productNameHtml = _.get(simple, 'product_name', this.originalProductName),
                shortDescriptionHtml = _.get(simple, 'short_description', this.originalShortDescription),
				subheadlineHtml = _.get(simple, 'subheadline', this.originalSubheadline);

			this.productName.html(productNameHtml);
            this.shortDescription.html(shortDescriptionHtml);
			this.subheadline.html(subheadlineHtml);
		},

		destructor: function() {
			$(document).off('product/changeSimple', this.onChangeCallback);
			$(document).off('product/changeGallerySimple', this.onChangeCallback);
		}
	});
});
define('rebrush/components/product/view/media',[
	'jquery',
	'lodash',
	'cartware/components/_base',
	'cartware/jquery/bem',
	'cartware/components/Registry',
	'modernizr',
	'elevatezoom',
	'cartware-slider',
	'magnific-popup'
], function(
	$,
	_,
	Component,
	BEM,
	Registry
) {
	var cache = {
		galleries: {}
	};

	return Component.create({
		block:				null,
		wrap:				null,
		thumbs:				null,
		badge:				null,
		galleryListItems:	null,
		galleryImages:		null,
		galleryElement:		null,
		spConfig:			null,

		$init: function() {
			this.initializeComponent();
			this.initializeWrap();
			this.initializeBadge();

			Registry().retrieve('product.view.configurable.spConfig', _.bind(function(spConfig) {
				this.spConfig = spConfig;
				this.initializeSimpleObservation();
			}, this));

			cache.galleries.__initial = this.block;
		},

		initializeComponent: function() {
			this.block = BEM.block('product-media');

			this.galleryListItems = this.block.getElements('gallery-list-item');
			this.galleryImages = this.block.getElements('gallery-image');

			this.initializeThumbnails();
			this.createZoom(this.galleryListItems.filter(':modifier(visible)').find(this.galleryImages));
		},

		initializeWrap: function() {
			this.block.wrap($('<div data-product-media-wrap />'));
			this.wrap = this.block.closest('[data-product-media-wrap]');
		},

		initializeBadge: function() {
			this.badge = this.block.getElements('badge-image-container');
		},

		initializeThumbnails: function() {
			this.thumbs = this.block.getElements('more-views-thumb-link');

			this.thumbs.each(function(i) {
				$(this).data('image-index', i);
			});

			this.thumbs.on('click', _.bind(this._onThumbnailClick, this));
			this.thumbs.first().click();
		},

		initializeSimpleObservation: function() {
			var currentSimple = _.get(this.spConfig, '__currentGallerySimple');
			var _onChangeGallerySimple = _.bind(function(e, simple) {
				this.changeSimple(simple);
			}, this);

			$(document).on('product/changeGallerySimple', _onChangeGallerySimple);

			if (currentSimple) {
				_onChangeGallerySimple(null, currentSimple);
			}
		},

		changeSimple: function(simple) {
			var block;

			if (!(simple.id in cache.galleries)) {
				cache.galleries[simple.id] = $(simple.mediaGallery);
			}

			block = cache.galleries[simple.id];

			$('.zoomContainer').remove();

			// this.block.wrap(wrap);

			//this.block.replaceWith(block);
			// block.replaceAll(this.block);

			this.wrap.empty().append(block);
			this.initializeComponent();

			// Append initial badge if simple doesn't carry one on its own
			if (this.badge.length && !this.block.getElements('badge-image-container').length) {
				this.block.getElements('gallery').append(this.badge);
			}

			var sliderBlock = this.block.find('.cartware-slider');

			if (sliderBlock.length) {
				sliderBlock.cartwareSlider()
					.data('cartware_slider/instance')
					.on('ready', function() {

					});
			}
		},

		_onThumbnailClick: function(e) {
			var thumb = $(e.currentTarget),
				itemWrap = thumb.data('media-gallery-item-wrap'),
				listItem = thumb.data('media-gallery-image'),
				galleryImage;

			if (!itemWrap) {
				itemWrap = thumb.closest('.cartware-slider-item-wrap');
				thumb.data('media-gallery-item-wrap', itemWrap);
			}

			if (!listItem) {
				listItem = this.galleryListItems.filter('[data-item="' + thumb.data('image-index') + '"]');
				thumb.data('media-gallery-image', listItem);
			}

			galleryImage = listItem.find(this.galleryImages);

			e.preventDefault();

			this.destroyZoom();

			this.galleryListItems.filter(':modifier(visible)')
				.bemRemoveModifier('visible');

			itemWrap.closest('.cartware-slider').find('.cartware-slider-item-wrap--active').removeClass('cartware-slider-item-wrap--active');
			itemWrap.addClass('cartware-slider-item-wrap--active');

			listItem.bemAddModifier('visible');

			if (!thumb.data('prevent-zoom')) {
				this.createZoom(galleryImage);
			}

			$(document).trigger('product/changeGalleryPicture');
		},

		createZoom: function(image) {
			var self = this;

			if (!Modernizr.touch) {
				image.elevateZoom({
					zoomType: 'inner',
					responsive: true
				});
			}

			image.on('click', function(e) {
				var items = [],
					index = 0,
					imageSrc = image.attr('src');

				if ($.magnificPopup.instance.isOpen) {
					return;
				}

				self.galleryImages.each(function(i) {
					var src = $(this).attr('src');

					if (i > 0 || self.galleryImages.length === 1) { // skip default image
						items.push({
							src: src
						});

						if (i > 0 && index === 0 && src === imageSrc) {
							index = i - 1;
						}
					}
				});

				$.magnificPopup.open({
					items: items,
					type: 'image',
					gallery: {
						enabled: true
					}
				}, index);
			});
		},

		destroyZoom: function() {
			$('.zoomContainer').remove();
			this.galleryListItems.filter(':modifier(visible)')
				.find('.' + BEM.buildElementClassname('product-media', 'gallery-image'))
				.removeData('elevateZoom');
		},

		destructor: function() {
			this.spConfig = null;
		}
	});
});
define('rebrush/components/product/view/specifications',[
	'jquery',
	'lodash',
	'cartware/components/_base',
	'cartware/jquery/bem',
	'cartware/components/Registry',
	'modernizr',
	'elevatezoom',
	'cartware-slider',
	'magnific-popup'
], function(
	$,
	_,
	Component,
	BEM,
	Registry
) {
	var cache = {
		blocks: {}
	};

	return Component.create({
		cache:			null,
		block:			null,
		thumbs:			null,
		galleryImages:	null,
		galleryElement:	null,

		onSimpleChangeCallback:	null,

		constructor: function() {
			this.onSimpleChangeCallback = _.bind(function(e, simple) {
				this.changeSimple(simple);
			}, this);
		},

		$init: function() {
			this.initializeComponent();

			this.cache = {
				blocks: {
					'__initial': this.block
				}
			};

			this.initializeSimpleObservation();
		},

		initializeComponent: function() {
			this.block = BEM.block('product-detail-specifications');
		},

		initializeSimpleObservation: function() {
			var currentSimple = _.get(window, 'spConfig.__currentSimple');

			$(document).on('product/changeSimple', this.onSimpleChangeCallback);

			if (currentSimple) {
				this.changeSimple(currentSimple);
			}
			//$(document).on('product/changeGallerySimple', _onChange);
		},

		changeSimple: function(simple) {
			var block;

			if (!simple) {
				block = this.cache.blocks.__initial;
			} else {
				if (!(simple.id in cache.blocks)) {
					cache.blocks[simple.id] = $('<div/>').append(simple.specifications);
				}

				block = cache.blocks[simple.id];
			}

			//this.block.replaceWith(block);
			block.replaceAll(this.block);
			this.block = block;

			this.block.trigger('gigaset.content.change');
		},

		destructor: function() {
			$(document).off('product/changeSimple', this.onSimpleChangeCallback);
		}
	});
});
define('rebrush/components/product/view/description',[
	'jquery',
	'lodash',
	'cartware/components/_base',
	'cartware/jquery/bem',
	'cartware/components/Registry',
	'modernizr',
	'elevatezoom',
	'cartware-slider',
	'magnific-popup'
], function(
	$,
	_,
	Component,
	BEM,
	Registry
) {
	var cache = {
		blocks: {}
	};

	return Component.create({
		cache:			null,
		block:			null,
		thumbs:			null,
		galleryImages:	null,
		galleryElement:	null,

		onSimpleChangeCallback:	null,
		destructorCallbacks: null,

		constructor: function() {
			this.destructorCallbacks = [];
			this.onSimpleChangeCallback = _.bind(function(e, simple) {
				this.changeSimple(simple);
			}, this);
		},

		init: function() {
			var self = this;
			var addToCartAfterCallback = _.bind(this.showHideProductDescriptionPopup, this);
			this.hub.on('cartware_cart/add-after', addToCartAfterCallback);
			this.destructorCallbacks.push(function() {
				self.hub.off('cartware_cart/add-after', addToCartAfterCallback);
			});
		},

		$init: function() {
			this.initializeComponent();

			this.cache = {
				blocks: {
					'__initial': this.block
				}
			};

			this.initializeSimpleObservation();
		},

		initializeComponent: function() {
			this.block = BEM.block('product-detail-description');
		},

		initializeSimpleObservation: function() {
			var currentSimple = _.get(window, 'spConfig.__currentSimple');

			$(document).on('product/changeSimple', this.onSimpleChangeCallback);

			if (currentSimple) {
				this.changeSimple(currentSimple);
			}
			//$(document).on('product/changeGallerySimple', _onChange);
		},

		changeSimple: function(simple) {
			var block;

			if (!simple || null === _.get(simple, 'description', null)) {
				block = this.cache.blocks.__initial;
			} else {
				if (!(simple.id in cache.blocks)) {
					cache.blocks[simple.id] = $('<div/>').append(simple.description);
				}

				block = cache.blocks[simple.id];
			}

			//this.block.replaceWith(block);
			block.replaceAll(this.block);
			this.block = block;

			this.block.trigger('gigaset.content.change');
		},

		showHideProductDescriptionPopup: function() {
			this.ajaxPopupBlock = BEM.block('cart-add-ajax-popup');
			this.ajaxPopupBlock.getElements('show-full-description-button').on('click', _.bind(function(event) {
				var productDescription = this.ajaxPopupBlock.getElements('product-description');
				var button = $(event.currentTarget);
				if (productDescription.bemHasModifier('open')) {
					productDescription.bemRemoveModifier('open');
					button.html(button.attr('data-text'));
				} else {
					productDescription.bemAddModifier('open');
					button.html(button.attr('data-text-open'));
				}
			}, this));
		},

		destructor: function() {
			$(document).off('product/changeSimple', this.onSimpleChangeCallback);
			_.forEach(this.destructorCallbacks, function(fn) { fn.call(this); }, this);
			this.destructorCallbacks = [];
		}

	});
});
define('rebrush/components/product/view/review',[
    'jquery',
    'lodash',
    'cartware/components/_base',
    'cartware/jquery/bem'
], function(
    $,
    _,
    Component,
    BEM
) {
    return Component.create({
        block:					null,
        linkSelector:           null,
        onClickCallback:		null,

        constructor: function() {
            this.onClickCallback = _.bind(this._onClick, this);
            this.linkSelector = '.' + BEM.buildBlockClassname('review-summary') + ' a';
        },

        $init: function() {
            this.block = BEM.block('product-view');
            this.initializeEvents();
        },

        initializeEvents: function() {
            this.block.on('click', this.linkSelector, this.onClickCallback);
        },

        _onClick: function(e) {
           var link = $(e.currentTarget),
               href = link.attr('href'),
                fragmentMatch = href.match(/#[^#]+$/),
                targetElement;

           if (fragmentMatch) {
               targetElement = $(fragmentMatch[0]);

               if (targetElement.length) {
                   e.preventDefault();

                   // mirror of `sections.js`
                   $('html, body').animate({
                       scrollTop: targetElement.offset().top + $.css(targetElement.get(0), 'padding-top', '') - BEM.block('sections-bar').outerHeight() + 1 // +1 in order to jump into the section
                   }, 300);

                   return false;
               }
           }
        },

        destructor: function() {
            if (this.block) {
                this.block.off('click', this.linkSelector, this.onClickCallback);
            }
        }
    });
});
define('rebrush/components/product/view/related-link-in-description',[
    'jquery',
    'lodash',
    'cartware/components/_base',
    'cartware/jquery/bem',
    'cartware/components/Holepunch'
], function(
    $,
    _,
    Component,
    BEM,
    holepunch
) {
    var PRODUCT_VIEW_BLOCK = 'product-view';

    return Component.create({
        constructor: function() {
        },

        $init: function() {
            $(document).on('product/changeSimple', $.proxy(_onSimpleChange, this));
            this.hub.on('product/changeSimple', $.proxy(_onSimpleChangeCallback, this));
            holepunch.ready($.proxy(function(payload) {
                _.forEach(payload.blocks || [], function (block, key) {
                    if ('product_info_related_holepunch' === key) {
                        var currentSimple = _.get(window, 'spConfig.__currentSimple');
                        addAccessoriesLink({}, currentSimple);
                    }
                });
            }, this));
            function _onSimpleChangeCallback(simple) {
                _onSimpleChange({}, simple);
            }
            function _onSimpleChange(e, simple) {
                addAccessoriesLink({}, simple);
            }
            function addAccessoriesLink(e, simple) {
                if ($('.' + BEM.buildBlockClassname('product-related')).length) {
                    if ($('.' + BEM.buildElementClassname(PRODUCT_VIEW_BLOCK, 'short-description > ul > li')).length) {
                        if (window._etracker && simple && simple.product_name != null) {
                            var $append = '<li><a onclick="_etracker.sendEvent(new et_ClickEvent(\'Beschreibung Zubehoer\',\'' + simple.product_name + '\')); ' +
                                'return false;" href="#product_info_related_holepunch">' + Translator.translate('To product accessories') + '</a></li>';
                        } else {
                            var $append = '<li><a href="#product_info_related_holepunch">' + Translator.translate('To product accessories') + '</a></li>';
                        }
                        $('.' + BEM.buildElementClassname(PRODUCT_VIEW_BLOCK, 'short-description > ul'))
                            .append($append);
                        return;
                    } else {
                        if (window._etracker && simple && simple.product_name != null) {
                            var $append = '<a onclick="_etracker.sendEvent(new et_ClickEvent(\'Beschreibung Zubehoer\',\'' + simple.product_name + '\')); ' +
                                'return false;" href="#related-products">' + payload.block_titles[key] + '</a>';
                        } else {
                            var $append = '<a href="#related-products">' + payload.block_titles[key] + '</a>';
                        }
                        $('.' + BEM.buildElementClassname(PRODUCT_VIEW_BLOCK, 'short-description'))
                            .after($append);
                        return;
                    }
                }
            }
        }
    });
});
define('rebrush/components/product/view/addtocart/related',[
    'jquery',
    'lodash',
    'cartware/components/_base',
    'cartware/jquery/bem',
    'gigaset/spinner',
], function(
    $,
    _,
    Component,
    BEM,
    Spinner
) {
    return Component.create({
        miniCart: null,
        addToCartPopup: null,
        destructorCallbacks: null,

        constructor: function() {
            // this.onClickCallback = _.bind(this._onClick, this);
            // this.linkSelector = '.' + BEM.buildBlockClassname('review-summary') + ' a';
            this.destructorCallbacks = [];
        },

        init: function() {
            var self = this;
            var addToCartAfterCallback = _.bind(this.onAddToCartSuccessAfter, this);
            this.hub.on('cartware_cart/add-after', addToCartAfterCallback);
            this.destructorCallbacks.push(function() {
                self.hub.off('cartware_cart/add-after', addToCartAfterCallback);
            });
        },

        onAddToCartSuccessAfter: function() {
            this.addToCartPopup = BEM.block('cart-add-ajax-popup');
            this.addToCartPopup.addModifier('has-related');
            this.miniCart = BEM.block('mini-cart');
            this.accessoriesBlock = BEM.block('product-list-related');
            this.accessoriesBlock.getElements('add-to-cart-button').on('click', _.bind(this.onAddToCartClick, this));

            // children of related configurables

            this.accessoriesBlock.getElements('show-options-button').on('click', _.bind(function(event) {
                var button = $(event.currentTarget);
                if (button.bemHasModifier('open')) {
                    button.bemRemoveModifier('open');
                } else {
                    button.bemAddModifier('open');
                }

                var list = this.accessoriesBlock
                    .getElements('list')
                    .filter(':modifier(level-2)')
                    .filter('[data-product-id=' + button.attr('data-product-id') + ']');

                if (list.bemHasModifier('open')) {
                    list.bemRemoveModifier('open');
                } else {
                    list.bemAddModifier('open');
                }
            }, this));
        },

        onAddToCartClick: function(event) {
            var self = this;
            var button = $(event.currentTarget);

            if (button.bemHasModifier('disabled')) {
                return;
            }

            var data = {
                product: button.data('product-id'),
                form_key: document.getElementsByName('form_key')[0] ? document.getElementsByName('form_key')[0].value : null
            };

            var url = button.data('url');

            /*
            if (url.indexOf('isAjax=1') === -1) {
                url += url.indexOf('?') === -1 ? '?isAjax=1' : '&isAjax=1';
            }
            */

            // Spinner.add(this.accessoriesBlock);

            button.bemAddModifier('loading');
            button.button('loading');

            $.ajax(url, {
                data: data,
                method: 'POST'
            }).always(function() {
                button.button('reset');
                // Spinner.remove();
            }).then(_.bind(function(response) {
                if (typeof response === 'string') {
                    alert(response);
                } else {
                    if (response.success === true) {
                        button.bemAddModifier('disabled');
                        if ('mini_cart' in response) {
                            self.miniCart.replaceWith(response.mini_cart);
                            self.miniCart = BEM.block('mini-cart');
                        }
                    } else {
                        if ('message' in response) {
                            alert(response.message);
                        }
                    }
                }
                button.bemRemoveModifier('loading');
            }), this);
        },

        destructor: function() {
            _.forEach(this.destructorCallbacks, function(fn) { fn.call(this); }, this);
            this.destructorCallbacks = [];
        }
    });
});

define('text!rebrush/components/product/options/templates/color.html',[],function () { return '<div class="b_super-attribute-select b_super-attribute-select--evenly-distributed">\n\t<ul class="b_super-attribute-select__list">\n\n\t\t<% _.forEach(options, function(option) { %>\n\t\t\t<li class="b_super-attribute-select__list-item b_super-attribute-select__list-item--color-swatch<% if ((\'qty\' in option && option.qty == 0) || option.isEol) { %> b_super-attribute-select__list-item--disabled<% } %>" data-option="<%- option.value %>">\n\n<!--\t\t\t\t<a class="b_super-attribute-select__color-swatch-link-wrapper" href="<%= option.preselectUrl %>" onclick="return false;">-->\n<!--\t\t\t\t\t<div class="b_super-attribute-select__color-swatch" style="background-color: <%= _.get(attributeExtension, [\'options\', option.value, \'color\'], \'white\') %>" title="<%= option.label %>">-->\n<!--\t\t\t\t\t\t<% if (_.get(attributeExtension, ["options", option.value, "image"])) { %>-->\n<!--\t\t\t\t\t\t\t<img src="<%= _.get(attributeExtension, [\'options\', option.value, \'image\']) %>"-->\n<!--\t\t\t\t\t\t\t\t alt="<%= option.label %>" title="<%= option.label %>"-->\n<!--\t\t\t\t\t\t\t\t class="b_super-attribute-select__color-swatch-image" />-->\n<!--\t\t\t\t\t\t<% } else { %>-->\n<!--\t\t\t\t\t\t\t<span class="b_super-attribute-select__color-swatch-label">-->\n<!--\t\t\t\t\t\t\t\t<%= option.label %>-->\n<!--\t\t\t\t\t\t\t</span>-->\n<!--\t\t\t\t\t\t<% } %>-->\n<!--\t\t\t\t\t</div>-->\n<!--\t\t\t\t</a>-->\n\n\t\t\t\t<div class="b_super-attribute-select__color-swatch" style="background-color: <%= _.get(attributeExtension, [\'options\', option.value, \'color\'], \'white\') %>" title="<%= option.label %>">\n\t\t\t\t\t<% if (_.get(attributeExtension, ["options", option.value, "image"])) { %>\n\t\t\t\t\t<img src="<%= _.get(attributeExtension, [\'options\', option.value, \'image\']) %>" alt="<%= option.label %>" title="<%= option.label %>" class="b_super-attribute-select__color-swatch-image" />\n\t\t\t\t\t<% } else { %>\n\t\t\t\t\t<span class="b_super-attribute-select__color-swatch-label"><%= option.label %></span>\n\t\t\t\t\t<% } %>\n\t\t\t\t</div>\n\n\t\t\t</li>\n\t\t<% }); %>\n\t</ul>\n</div>\n';});


define('text!rebrush/components/product/options/templates/badge.html',[],function () { return '<div class="b_super-attribute-select">\n\t<ul class="b_super-attribute-select__list">\n\n\t\t<% _.forEach(options, function(option) { %>\n\t\t\t<li class="b_super-attribute-select__list-item<% if ((\'qty\' in option && option.qty == 0) || option.isEol) { %> b_super-attribute-select__list-item--disabled<% } %>" data-option="<%= option.value %>">\n\t\t\t\t<a class="b_super-attribute-select__badge-link-wrapper" href="<%= option.preselectUrl %>" onclick="return false;">\n\t\t\t\t\t<div class="b_super-attribute-select__badge" title="<%= option.label %>">\n\t\t\t\t\t\t<% if ("undefined" !== option.tmpl && option.tmpl) { %>\n\t\t\t\t\t\t\t<%= option.tmpl %>\n\t\t\t\t\t\t<% } else { %>\n\t\t\t\t\t\t\t<span class="b_super-attribute-select__badge-label">\n\t\t\t\t\t\t\t\t<%= option.label %>\n\t\t\t\t\t\t\t</span>\n\t\t\t\t\t\t<% } %>\n\t\t\t\t\t</div>\n\t\t\t\t</a>\n\t\t\t</li>\n\t\t<% }); %>\n\t</ul>\n</div>\n';});

define('rebrush/components/product/options',[
	'require',
	'jquery',
	'lodash',
	'cartware/components/Registry',
	'cartware/components/_base',
	'cartware/jquery/bem',
	'cartware/dom/event/trigger',
	'select2',
	'text!./options/templates/color.html',
	'text!./options/templates/badge.html'
], function(
	require,
	$,
	_,
	Registry,
	Component,
	BEM,
	triggerEvent,
	S2,
	ColorOptionTemplate,
	BadgeOptionTemplate
) {
	var templates = {
		color: _.template(ColorOptionTemplate),
		badge: _.template(BadgeOptionTemplate)
	};

	return Component.create({
		topOptionsBlock:			null,
		masterOptionsBlock:			null,

		topAttributesContainer:		null,
		masterAttributesContainer:	null,
		functionsContainer:			null,
		attributeContainers:		null,

		options:					null,

		$init: function() {
			this.viewBlock = BEM.block('product-view');
			this.topAttributesContainer = this.viewBlock.getElements('top-attributes-container');
			this.masterAttributesContainer = this.viewBlock.getElements('attributes-container');
			this.functionsContainer = this.viewBlock.getElements('functions-container');
			this.attributeContainers = this.masterAttributesContainer.add(this.functionsContainer);

			var optionsBlocks = BEM.block('product-options'),
				attributeLists = optionsBlocks.getElements('attribute-list');

			this.masterOptionsBlock = BEM.block(this.attributeContainers.find(optionsBlocks));

			this.topOptionsBlock = BEM.block(this.topAttributesContainer.find(optionsBlocks));

			this.masterAttributeList = this.masterOptionsBlock.find(attributeLists);
			this.topAttributeList = this.topOptionsBlock.find(attributeLists);

			this.optionHeadlines = this.masterOptionsBlock.getElements('attribute-headline');
			this.options = this.masterOptionsBlock.getElements('attribute-option');

			Registry().retrieve('product.view.configurable.spConfig', _.bind(this.initializeRendering, this));
		},

		initializeRendering: function() {
			var self = this;

			this.options.each(function() { self.initializeOptionElement(this); });

			this.optionHeadlines.first().appendTo(this.topAttributeList);
			this.options.first().appendTo(this.topAttributeList);

			if (!this.masterAttributeList.find(this.options).length) {
				var parentOptionsBlock = this.masterAttributeList.closest(BEM.block('product-options'));
				this.masterAttributeList.remove();
				var text = ''+parentOptionsBlock.html();

				text = text.replace(/(<script[^>]*>[\s\S]*?<\/script>)/gi, '');
				text = text.replace(/(<!--[^]*?-->)/gi, '');

				if (_.trim(text) === '') {
					parentOptionsBlock.bemAddModifier('empty');
				} else if (_.trim(parentOptionsBlock.text()) === '') {
					parentOptionsBlock.remove();
				}
			}

			this.hub.trigger('ui.component.rendering.after', {
				component: this
			});

			this.viewBlock.bemAddModifier('options-initialized');
		},

		initializeOptionElement: function(element) {
			element = $(element);

			var spConfig = window.spConfig,
				select = element.find('select'),
				value = select.val(),
				attributeCode = element.data('attribute-code'),
				attributeData = _.find(spConfig.config.attributes, function(attribute, attributeId) {
					return attribute.code === attributeCode;
				}),
				attributeExtension = _.find(spConfig.config.attribute_extensions, function(attribute, attributeId) {
					return attribute.code === attributeCode;
				}),
				renderer = _.get(attributeExtension, 'renderer', 'default'),
				template = _.get(templates, renderer),
				displayNode = $('<div class="display" />').prependTo(element),
				options = [],
				pubSubHub = this.hub;

			var setActiveValue = function(value) {
				displayNode
					.findBlock('super-attribute-select')
					.getElements('list-item')
					.bemRemoveModifier('active')
					.filter('[data-option="' + value + '"]')
					.bemAddModifier('active');
			};

			if (!template) {
				select.on('attribute/fillSelect', function() {
					triggerEvent(this, 'change');
				});

				select.on('change', function(e) {
					if ('undefined' === typeof e.originalEvent) {
						triggerEvent(this, 'change');
					}
				});

				$.fn.select2.amd.require(
					['select2/utils', 'select2/dropdown', 'select2/dropdown/attachContainer', 'select2/dropdown/closeOnSelect'],
					function (Utils, Dropdown, AttachContainer, CloseOnSelect) {
						select.select2({
							dropdownAdapter: Utils.Decorate(Utils.Decorate(Dropdown, AttachContainer), CloseOnSelect),
							width: '100%'
						});
					}
				);
			} else {
				element.on('click', '[data-option]', function() {
					var value = $(this).data('option');

					// todo: this seems to be called twice for no obvious reason

					//if ($(this).is(':modifier(disabled)')) return;

					select.val(value);
					triggerEvent(select, 'change');
				});

				select.on('change', function() {
					var value = select.val();

					if (!value) {
						return;
					}

					setActiveValue(value);
				});

				function render() {
					select.find('option').each(function() {
						var node = $(this),
							select = node.parent(),
							attributeId = select.prop('id').replace(/[a-z]*/, ''),
							value = node.prop('value'),
							option = {
								value: value,
								label: node.html(),
								tmpl: node.data('tmpl'),
								isEol: false,
							};

						if (!value) {
							return;
						}

						// get configurable super attribute options
						var configOptions = spConfig.config.attributes[attributeId].options;

						for (var i = 0, j = configOptions.length; i < j; i++) {
							if (configOptions[i].id == option.value) {

								var allowedSimples = _.pick(spConfig._configurable.simples, configOptions[i].allowedProducts);
								option.isEol = _.every(allowedSimples, 'is_eol');
								option.isIndexable = _.every(allowedSimples, 'is_search_engine_indexable');

								// preselect url
								if (option.isIndexable) {
									const simpleId = _.pick(configOptions[i].allowedProducts, 0);
									if (simpleId[0] !== undefined) {
										const preselectUrl = getPreselectUrl(simpleId[0]);
										option.preselectUrl = preselectUrl.href;
									}
								}

								if (configOptions[i].allowedProducts.length == 1
									&& _.get(spConfig, '_configurable.simples')
								) {
									for (var k in spConfig._configurable.simples) {
										if (k == configOptions[i].allowedProducts[0]) {
											// add child qty
											option.qty = spConfig._configurable.simples[k].qty;
										}
									}
								}
								break;
							}
						}

						_.forEach(spConfig.dataProviders, _.bind(function(provider) {
							if ('function' === typeof provider.provideSelectOption) {
								provider.provideSelectOption(option, select, attributeData.id);
							}
						},this));

						options.push(option);
					});

					function done() {
						element.bemAddModifier('rendered');
						displayNode
							.empty()
							.html(template({
								options: options,
								attribute: attributeData,
								attributeExtension: attributeExtension
							}));

						setActiveValue(select.val());
						pubSubHub.trigger('ui.change');
					}

					function getPreselectUrl(productId) {
						// add preselect url
						var currentUrl = window.location.href;
						// Remove trailing slash if present
						if (currentUrl.endsWith('/')) {
							currentUrl = currentUrl.slice(0, -1);
						}
						var preselectUrl = new URL(currentUrl);
						// add preselect param
						preselectUrl.searchParams.set('preselect', productId);

						return preselectUrl;
					}

					if (spConfig.dataProviders.length) {
						(function() {
							var i = 0, j = spConfig.dataProviders.length;

							function next(result) {
								var provider;

								options = result;

								if (i >= j) {
									done();
									return;
								}

								provider = spConfig.dataProviders[i];
								i++;

								if ('function' === typeof provider.sortSelectOptions) {
									options = provider.sortSelectOptions(options, attributeId);

									if ('object' === typeof options && 'then' in options) { // promise
										options.then(next);
									} else {
										next(options);
									}

									return;
								}

								next(options);
							}

							next(options);
						})();
					} else {
						done();
					}
				}

				render();
				select.on('attribute/fillSelect attribute/reloadOptionLabels', function() {
					options = [];
					render();
				});
			}

			if (value) {
				setActiveValue(value);
			}
		}

	});
});

define('rebrush/components/product/options/engraving',[
    'jquery',
    'cartware/components/_base',
    'cartware/jquery/bem'
], function(
    $,
    Component,
    BEM
) {
    var INPUT_NORMALIZATION_PATTERN = /[^\x20-\x7E\u00C0-\u024F\u1E00-\u1EFF€£¥§°²³µ]/g;

    return Component.create({

        $init: function() {
            $('[data-engraving-block]').each(function() {
                var block               = $(this),
                    showLink            = block.find('[data-show-engraving-field]'),
                    removeLink          = block.find('[data-remove-engraving-field]'),
                    priceNotice         = block.parents('dd').prev('dt') .find('.price-notice'),
                    inputBox            = block.find('.input-box'),
                    input               = inputBox.find('input[type="text"]'),
                    maxCharactersBlock  = block.find('[data-max-characters]'),
                    previewBlock        = block.find('[data-engraving-preview]'),
                    saveEngravingButton = block.find('[data-save-engraving]'),
                    savedEngravingBlock = block.find('[data-saved-engraving]'),
                    maxCharacters       = maxCharactersBlock.data('max-characters'),
                    closeButton         = $('<a class="engraving-close" href="#">&times;</a>').hide(),
                    addToCartButton     = BEM.block('product-detail-add-to-cart').getElements('add-to-cart-button').first(),
                    inputValueNormalized = '';

                closeButton.on('click', function (e) {
                    e.preventDefault();
                    closeButton.hide();
                    inputBox.hide();
                    showLink.show();
                    removeLink.hide();
                    savedEngravingBlock.hide();
                    savedEngravingBlock.html('');
                    previewBlock.html('');
                    maxCharactersBlock.html(maxCharacters);

                    if (input.val().length) {
                        input.val('');
                        input.trigger('change');
                        opConfig.reloadPrice();
                    }
                });

                input.on('change keyup', function(){
                    var elem = $(this);
                    var normalized = elem.val().replace(INPUT_NORMALIZATION_PATTERN, '');

                    inputValueNormalized = normalized;
                    previewBlock.html(normalized);
                    savedEngravingBlock.html(normalized);
                    maxCharactersBlock.html(parseInt(maxCharacters, 10) - normalized.length);
                });


                input.on('change', function () {
                    if (input.val() === '') {
                        closeButton.trigger('click');
                        opConfig.reloadPrice();
                    }
                });

                priceNotice.append(closeButton);

                showLink.on('click', function(e){
                    e.preventDefault();
                    closeButton.show();
                    inputBox.show();
                    showLink.hide();
                });

                saveEngravingButton.on('click', function(e) {
                    e.preventDefault();
                    if (input.val().length && inputValueNormalized) {
                        input.val(inputValueNormalized); // set normalized value
                        inputBox.hide();
                        closeButton.hide();
                        addToCartButton.attr('data-customprint-added', 1);
                        savedEngravingBlock.attr('data-saved-engraving', '1');
                        savedEngravingBlock.show();
                        removeLink.show();
                    }
                });

                savedEngravingBlock.on('click', function(e){
                    e.preventDefault();
                    inputBox.show();
                    closeButton.show();
                    savedEngravingBlock.attr('data-saved-engraving', '0');
                    addToCartButton.attr('data-customprint-added', 0);
                    savedEngravingBlock.hide();
                });

                removeLink.on('click', function(e){
                    e.preventDefault();
                    $(this).hide();
                    showLink.show();
                    input.val('');
                    savedEngravingBlock.hide();
                    savedEngravingBlock.attr('data-saved-engraving', '0');
                    savedEngravingBlock.html('');
                    addToCartButton.attr('data-customprint-added', 0);
                    input.trigger('change');
                    opConfig.reloadPrice();
                });

                if (input.val().length) {
                    input.trigger('change');
                    savedEngravingBlock.html(input.val());
                    savedEngravingBlock.show();
                    opConfig.reloadPrice();
                    showLink.hide();
                    removeLink.show();
                }
            });
        }
    });
});

define('rebrush/components/product/options/protection',[
    'jquery',
    'cartware/components/_base'
], function(
    $,
    Component
) {

    return Component.create({
        $init: function() {
            $('[data-protection-block]').each(function() {
                var block               = $(this),
                    showLink            = block.find('[data-show-protection-field]'),
                    removeLink          = block.find('[data-remove-protection-field]'),
                    priceNotice         = block.parents('dd').prev('dt') .find('.price-notice'),
                    inputBox            = block.find('.input-box'),
                    input               = inputBox.find('input[type="checkbox"]'),
                    maxCharactersBlock  = block.find('[data-max-characters]'),
                    previewBlock        = block.find('[data-protection-preview]'),
                    saveProtectionButton = block.find('[data-save-protection]'),
                    maxCharacters       = maxCharactersBlock.data('max-characters'),
                    closeButton         = $('<a class="protection-close" href="#">&times;</a>').hide();

                closeButton.on('click', function (e) {
                    e.preventDefault();
                    closeButton.hide();
                    inputBox.hide();
                    showLink.show();
                    removeLink.hide();
                    previewBlock.html('');
                    maxCharactersBlock.html(maxCharacters);

                    if (input.is(':checked')) {
                        input.prop('checked', false);
                        input.trigger('change');
                        opConfig.reloadPrice();
                    }

                });

                priceNotice.append(closeButton);

                showLink.on('click', function(e){
                    e.preventDefault();
                    closeButton.show();
                    inputBox.show();
                    $(this).hide();
                });

                removeLink.on('click', function(e){
                    e.preventDefault();
                    $(this).hide();
                    showLink.show();
                    input.prop('checked', false);
                    input.trigger('change');
                    opConfig.reloadPrice();
                });

                saveProtectionButton.on('click', function(e) {
                    e.preventDefault();
                    inputBox.hide();
                    closeButton.hide();
                    input.prop('checked', true);
                    input.trigger('change');
                    opConfig.reloadPrice();
                    removeLink.show();
                });

                if (input.is(':checked')) {
                    input.trigger('change');
                    opConfig.reloadPrice();
                    showLink.hide();
                    removeLink.show();
                }

            });
        }
    });
});
define('rebrush/components/review/list',[
    'jquery',
    'cartware/components/_base',
    'cartware/jquery/bem',
    'gigaset/spinner'
], function(
    $,
    Component,
    BEM,
    Spinner
) {
    return Component.create({
        block:			    null,
        pagerLinks:         null,
        ajaxControllerUrl:  null,
        productId:          null,

        $init: function() {
            var self = this;
            this.block = BEM.block('review-list');

            this.block.on('click', '.b_pager li:not(.active)>a', function(e) {
                var currentUrl = $(this).prop('href');

                e.preventDefault();

                try {
                    var dataBlock = self.block.find('[data-ajax-reviews-url]');
                    self.ajaxControllerUrl = dataBlock.data('ajax-reviews-url');
                    self.productId = dataBlock.data('ajax-reviews-product-id');

                    var listWrapper = self.block.find('.' + BEM.buildElementClassname('review-list','list-wrapper')),
                        listListSelector = '.' + BEM.buildElementClassname('review-list', 'list'),
                        paginationSelector = '.js-review-pagination',
                        ajaxUrl = self.ajaxControllerUrl + 'product/' + self.productId  + '/p/' + self.parseQueryString('p', currentUrl);

                    Spinner['add'](listWrapper);
                    $.ajax(ajaxUrl, {
                        success: function(res) {
                            if (typeof res.reviews !== 'undefined') {
                                var reviewsBlock = $(res.reviews);

                                self.block.find(listListSelector).html(reviewsBlock.find(listListSelector).html());
                                self.block.find(paginationSelector).html(reviewsBlock.find(paginationSelector).html());

                                self.block.parents('.' + BEM.buildElementClassname('tabs','tab-content')).trigger('gigaset.content.change');
                                self.scrollIntoViewIfNeeded(self.block);
                                Spinner['remove'](listWrapper);
                            } else {
                                self.fallback(currentUrl);
                            }
                        },
                        error: function() {
                            self.fallback(currentUrl);
                        },
                        cache: true
                    });
                    return false;
                } catch (e) {
                    self.fallback(currentUrl);
                }
            });
        },

        parseQueryString: function( name, url ) {
            if (!url) url = location.href;
            name = name.replace(/[\[]/,'\\\[').replace(/[\]]/,'\\\]');
            var regexS = '[\\?&]'+name+'=([^&#]*)';
            var regex = new RegExp( regexS );
            var results = regex.exec( url );
            return results == null ? null : results[1];
        },

        scrollIntoViewIfNeeded: function($target) {
            if ($target.offset()) {
                if ($target.offset().top < $(window).scrollTop()){
                    //scroll up
                    $('html,body').animate({scrollTop: $target.offset().top});
                }
                else if ($target.offset().top + $target.outerHeight() >
                    $(window).scrollTop() + (
                        window.innerHeight || document.documentElement.clientHeight
                    )) {
                    //scroll down
                    $('html,body').animate({scrollTop: $target.offset().top -
                            (window.innerHeight || document.documentElement.clientHeight)
                            + $target.outerHeight()}
                    );
                }
            }
        },

        fallback: function(url) {
            location.href = url;
        }
    });
});

define('text!rebrush/components/ui/sections/templates/bar.html',[],function () { return '<div class="b_sections-bar">\n    <div class="b_sections-bar__container">\n        <div class="b_sections-bar__inner-container">\n            <% if (block.title) { %>\n                <div class="b_sections-bar__title"><%= block.title %></div>\n            <% } %>\n\n            <ul class="b_sections-bar__sections-list">\n                <% _.forEach(block.sectionTitles, function(sectionTitle, i) { %>\n                    <li class="b_sections-bar__section-item" data-section-id="<%= i %>">\n                        <%= $(sectionTitle).text() %>\n                    </li>\n                <% }); %>\n            </ul>\n\n            <div class="b_sections-bar__sections-select-container">\n                <select class="b_sections-bar__sections-select" data-ui-select-refuse="1">\n                    <% _.forEach(block.sectionTitles, function(sectionTitle, i) { %>\n                        <option value="<%= i %>">\n                            <%= $(sectionTitle).text() %>\n                        </option>\n                    <% }); %>\n                </select>\n            </div>\n        </div>\n    </div>\n</div>';});

define('rebrush/components/ui/sections',[
    'jquery',
    'lodash',
    'cartware/components/_base',
    'cartware/jquery/bem',
    'text!./sections/templates/bar.html',
    'select2',
    'cartware/components/Holepunch'
], function(
    $,
    _,
    Component,
    BEM,
    BarTemplate,
    S2,
    holepunch
) {
    var BLOCK = 'sections';
    var barTemplate = _.template(BarTemplate, { imports: {$:$} });

    function Sections(block, hub) {
        this.block = BEM.block(block);
        this.list = this.block.getElements('list');
        this.sectionTitles = this.block.getElements('section-title');
        this.barSlot = $('#' + this.block.data('bar-target'));
        this.title = this.block.data('bar-title');
        this.affixTarget = this.barSlot;
        this.hub = hub;
        this.affixSwitchCallbacks = [];
        this.affixMode = null;

        if (!this.barSlot.length) {
            return;
        }

        this.initializeRendering();
        this.initializeEvents();
        this.initializeBottomAffix();
        // this.initializeTopAffix();

        holepunch.ready($.proxy(function(payload) {
            _.forEach(payload.block_titles || [], function (title, key) {
                $('[data-name-in-layout="' + key + '"]')
                    .parents('.' + BEM.buildElementClassname(BLOCK, 'section-content'))
                    .before('<dt class="' + BEM.buildElementClassname(BLOCK, 'section-title') + '" id="' + key + '">'
                            + '<div class="' + BEM.buildElementClassname(BLOCK, 'container') + '">'
                                + '<span>'
                                    + title
                                + '</span>'
                            + '</div>'
                        + '</dt>');
            });
            this.initializeRendering();
        }, this));

        this.onScroll();
    }

    Sections.prototype = {
        bar:    null,
        affixInstance: null,
        currentSectionId: null,

        initializeRendering: function() {
            this.sectionTitles = this.block.getElements('section-title');
            if (this.bar) {
                this.bar.off();
                this.bar.remove();
            }

            this.bar = $('<div />').html(barTemplate({ block: this }));
            this.barSlot.append(this.bar);
            this.transformSelects();
            this.onScroll();
        },

        initializeEvents: function() {
            $(document.body).on('click', '.' + BEM.buildElementClassname('sections-bar', 'section-item'), _.bind(this.onSectionItemClick, this));
            $(document.body).on('change', '.' + BEM.buildElementClassname('sections-bar', 'sections-select'), _.bind(this.onSectionSelectChange, this));
            $(document.body).on('click', 'a', _.bind(this.onLinkClick, this))
            $(window).on('scroll', _.throttle(_.bind(this.onScroll, this)));
            $(window).on('resize', _.throttle(_.bind(this.onResize, this)));
            this.hub.on('ui', _.bind(this.onUiChange, this));
            this.hub.on('gigaset.content.change', _.bind(this.onUiChange, this));
        },

        initializeBottomAffix: function() {
            var self = this;
            var placeHolder = $('<div class="pl1" />');
            var wasTop = true;
            var affixInstance = this.getAffixInstance();
            var affixOptions = {
                offset: {
                    top: null,
                    bottom: _.bind(function() {
                        return $(document.body).height()
                            - (this.block.offset().top - $.css(this.block.get(0), 'margin-top', ''));
                    }, this)
                }
            };
            var monitorAffixSwitch = _.bind(function() {
                // TODO share with below "top"
                var top = _.bind(function() {
                    return (this.block.offset().top - $.css(this.block.get(0), 'margin-top', ''))
                        - (this.affixTarget.height() + $.css(this.affixTarget.get(0), 'margin-bottom', ''));
                }, this);

                var offsetTop = top(affixInstance.$element);
                var scrollTop    = affixInstance.$target.scrollTop();

                if (scrollTop > offsetTop) {
                    affixInstance.$target.off('scroll.bs.affix.data-api', monitorAffixSwitch);
                    _.defer(_.bind(self.initializeTopAffix, self));
                    // setTimeout(_.bind(self.initializeTopAffix, self), 5000); // Deferred testing
                }
            }, this);

            // Prepare
            this.onAffixSwitch();

            this.affixTarget.addClass('mode-bottom');

            this.affixTarget.css({
                position: 'relative'
            });

            // affixInstance.affixed = 'top';

            if ('top' === this.affixMode) {
                affixInstance.affixed = 'top';
            }

            function onAffix(e) {
                var target = $(e.currentTarget),
                    targetHeight;

                if (wasTop) {
                    targetHeight = target.outerHeight(true);
                    placeHolder.height(targetHeight);
                }

                target.after(placeHolder);

                wasTop = false;
                affixInstance.$target.off('scroll.bs.affix.data-api', monitorAffixSwitch);
            }

            function onAffixTop() {
                affixInstance.$target.off('scroll.bs.affix.data-api', monitorAffixSwitch);
            }

            function onAffixBottom() {
                placeHolder.detach();
                affixInstance.$target.on('scroll.bs.affix.data-api', monitorAffixSwitch);
            }

            // Cleanup
            this.affixSwitchCallbacks.push(function() {
                this.affixTarget
                    .off('affix.bs.affix', onAffix)
                    .off('affixed-top.bs.affix', onAffixTop)
                    .off('affixed-bottom.bs.affix', onAffixBottom);

                affixInstance.$target.off('scroll.bs.affix.data-api', monitorAffixSwitch);

                placeHolder.remove();

                this.affixTarget.removeClass('mode-bottom');
            });

            this.affixTarget
                .on('affix.bs.affix', onAffix)
                .on('affixed-top.bs.affix', onAffixTop)
                .on('affixed-bottom.bs.affix', onAffixBottom);

            // Initiate
            if (affixInstance) {
                $.extend(affixInstance.options, affixOptions);
                affixInstance.checkPosition();
            } else {
                this.affixTarget.affix(affixOptions);
            }

            // monitorAffixSwitch();
            this.affixMode = 'bottom';
        },

        initializeTopAffix: function() {
            var self = this;
            var placeHolder = $('<div class="pl2" />');
            var placeHolderBottom = $('<div class="pl3" />');
            var wasTop = true;
            var affixInstance = this.getAffixInstance();
            var affixOptions = {
                offset: {
                    top: _.bind(function() {
                        return (this.block.offset().top - $.css(this.block.get(0), 'margin-top', ''))
                            - (this.affixTarget.height() + $.css(this.affixTarget.get(0), 'margin-bottom', ''));
                    }, this),
                    bottom: _.bind(function() {
                        return $(document.body).height()
                            // + affixInstance.$target.height()
                            - (this.block.offset().top + this.block.outerHeight(true))
                            - (
                                affixInstance.affixed == null
                                    ? this.affixTarget.outerHeight(true)
                                    : placeHolderBottom.outerHeight(true)
                            );
                    }, this)
                }
            };
            var monitorAffixSwitch = _.bind(function() {
                // TODO share with above "bottom"
                var bottom = _.bind(function() {
                    return $(document.body).height()
                        - (this.block.offset().top - $.css(this.block.get(0), 'margin-top', ''));
                }, this);

                var offsetBottom = bottom(affixInstance.$element);
                var scrollHeight = Math.max($(document).height(), $(document.body).height());

                var scrollTop    = affixInstance.$target.scrollTop();
                var targetHeight = affixInstance.$target.height();

                if (scrollTop + targetHeight < scrollHeight - offsetBottom) {
                    affixInstance.$target.off('scroll.bs.affix.data-api', monitorAffixSwitch);
                    self.initializeBottomAffix();
                    // _.defer(_.bind(self.initializeBottomAffix, self));
                }

            }, this);

            // Prepare
            this.onAffixSwitch();

            this.affixTarget.addClass('mode-top');

            // Special case, when the page loads with the window scrolled into 'bottom' but not below it
            (function() {
                var height       = affixInstance.$element.height();
                var offset       = affixOptions.offset;
                var offsetTop    = offset.top();
                var offsetBottom = offset.bottom();
                var scrollHeight = Math.max($(document).height(), $(document.body).height());

                if ('bottom' == affixInstance.getState(scrollHeight, height, offsetTop, offsetBottom)) {
                    var scrollTop    = affixInstance.$target.scrollTop();

                    if (scrollHeight - scrollTop > offsetBottom) {
                        affixInstance.affixed = 'top';
                    }
                }
            })();

            function onAffix(e) {
                var target = $(e.currentTarget),
                    targetHeight;

                if (wasTop) {
                    targetHeight = target.outerHeight(true);
                    placeHolder.height(targetHeight);
                    placeHolderBottom.height(targetHeight);
                }

                target.after(placeHolder);
                self.block.after(placeHolderBottom);

                wasTop = false;

                affixInstance.$target.off('scroll.bs.affix.data-api', monitorAffixSwitch);
            }

            function onAffixTop() {
                wasTop = true;
                placeHolder.detach();
                // self.initializeBottomAffix();
                affixInstance.$target.on('scroll.bs.affix.data-api', monitorAffixSwitch);
            }

            function onAffixBottom() {
                placeHolder.detach();
                affixInstance.$target.off('scroll.bs.affix.data-api', monitorAffixSwitch);
            }

            // Cleanup
            this.affixSwitchCallbacks.push(function() {
                this.affixTarget.off('affix.bs.affix', onAffix);
                this.affixTarget.off('affixed-top.bs.affix', onAffixTop);
                this.affixTarget.off('affixed-bottom.bs.affix', onAffixBottom);

                affixInstance.$target.off('scroll.bs.affix.data-api', monitorAffixSwitch);

                placeHolder.remove();
                placeHolderBottom.remove();

                this.affixTarget.removeClass('mode-top');
            });

            this.affixTarget
                .on('affix.bs.affix', onAffix)
                .on('affixed-top.bs.affix', onAffixTop)
                .on('affixed-bottom.bs.affix', onAffixBottom);

            // Initiate
            if (affixInstance) {
                $.extend(affixInstance.options, affixOptions);
                affixInstance.checkPosition();
            } else {
                this.affixTarget.affix(affixOptions);
            }

            // monitorAffixSwitch();
            this.affixMode = 'top';
        },

        onAffixSwitch: function() {
            var affixInstance = this.getAffixInstance();

            this.affixTarget
                .removeClass('affix affix-top affix-bottom')
                .css({
                    top: '',
                    position: ''
                });

            if (affixInstance) {
                affixInstance.affixed      = null;
                affixInstance.unpin        = null;
                affixInstance.pinnedOffset = null;
            }

            _.forEach(this.affixSwitchCallbacks, _.bind(function(fn) {
                fn.call(this);
            }, this));

            this.affixSwitchCallbacks = [];
        },

        getAffixInstance: function() {
            if (!this.affixInstance) {
                this.affixTarget.affix();
                this.affixInstance = this.affixTarget.data('bs.affix');
            }

            return this.affixInstance;
        },

        onUiChange: function() {
            this.getAffixInstance().checkPositionWithEventLoop();
        },

        onResize: function() {
            this.getAffixInstance().checkPositionWithEventLoop();
        },

        onScroll: function() {
            var scrollHeight = $(window).scrollTop(),
                barHeight = this.bar.height(),
                targetSectionTitle = this.sectionTitles.first(),
                targetSectionId = 0;

            this.sectionTitles.each(function(i) {
                var sectionTitle = $(this),
                    offset = sectionTitle.offset();

                if ('none' === sectionTitle.css('display')) {
                    return;
                }

                if (offset.top < (scrollHeight + barHeight)) {
                    targetSectionTitle = sectionTitle;
                    targetSectionId = i;
                }
            });

            if (this.currentSectionId === targetSectionId) {
                return;
            }

            this.bar.find('.' + BEM.buildElementClassname('sections-bar', 'section-item')).each(function() {
                var item = $(this),
                    sectionId = item.data('section-id');

                // console.log(targetSectionId, sectionId, item);

                if (targetSectionId == sectionId) {
                    item.addClass('active');
                } else {
                    item.removeClass('active');
                }
            });

            var select = this.bar.find('.' + BEM.buildElementClassname('sections-bar', 'sections-select')),
                select2 = select.data('select2');

            select.val(targetSectionId);

            if (select2) {
                select2.dataAdapter.current(function (data) {
                    select2.trigger('selection:update', {
                        data: data
                    });
                });
            }

            this.currentSectionId = targetSectionId;
        },

        onLinkClick: function(e) {
            var link = $(e.currentTarget),
                href = link.attr('href');

            if (!href) {
                return;
            }

            var hrefMatch = href.match(/^#(.*)$/),
                section = (hrefMatch && hrefMatch[1] !== '') ? $(this.sectionTitles.filter(hrefMatch[0])) : null;

            if (section && section.length) {
                e.preventDefault();
                this.gotoSection(section);
            }
        },

        onSectionItemClick: function(e) {
            var sectionItem = $(e.currentTarget),
                sectionId = sectionItem.data('section-id'),
                section = $(this.sectionTitles.get(sectionId));

            this.gotoSection(section);
        },

        onSectionSelectChange: function(e) {
            var select = $(e.currentTarget),
                sectionId = select.val(),
                section = $(this.sectionTitles.get(sectionId));

            this.gotoSection(section);
        },

        gotoSection: function(section) {
            $('html, body').animate({
                scrollTop: section.offset().top + $.css(section.get(0), 'padding-top', '') - this.bar.outerHeight() + 1 // +1 in order to jump into the section
            }, 300);
        },

        transformSelects: function() {
            $.fn.select2.amd.require(
                ['select2/utils', 'select2/dropdown', 'select2/dropdown/attachBody', 'select2/dropdown/closeOnSelect'],
                _.bind(function (Utils, Dropdown, AttachBody, CloseOnSelect) {
                    this.bar.find('.' + BEM.buildElementClassname('sections-bar', 'sections-select')).each(function() {
                        var select = $(this),
                            dropdownAdapter;

                        if (select.data('select2')) {
                            return;
                        }

                        dropdownAdapter = Utils.Decorate(Utils.Decorate(Dropdown, AttachBody), CloseOnSelect);

                        select.select2({
                            dropdownAdapter: dropdownAdapter,
                            width: null,
                            theme: 'sections-selector'
                        });

                        //select.on("change", function(e) {
                        //	//if ("undefined" === typeof e.originalEvent) {
                        //	if (e instanceof $.Event) {
                        //		triggerEvent(this, "change");
                        //	}
                        //});
                    });
                }, this)
            );
        }
    };

    return Component.create({
        $init: function() {
            var self = this;
            this.block = BEM.block(BLOCK);
            this.block.each(function() {
                new Sections(this, self.hub);
            });
        }
    });
});

// http://paulirish.com/2011/requestanimationframe-for-smart-animating/
// http://my.opera.com/emoller/blog/2011/12/20/requestanimationframe-for-smart-er-animating

// requestAnimationFrame polyfill by Erik Möller. fixes from Paul Irish and Tino Zijdel

// MIT license

(function() {
    var lastTime = 0;
    var vendors = ['ms', 'moz', 'webkit', 'o'];
    for(var x = 0; x < vendors.length && !window.requestAnimationFrame; ++x) {
        window.requestAnimationFrame = window[vendors[x]+'RequestAnimationFrame'];
        window.cancelAnimationFrame = window[vendors[x]+'CancelAnimationFrame']
            || window[vendors[x]+'CancelRequestAnimationFrame'];
    }

    if (!window.requestAnimationFrame)
        window.requestAnimationFrame = function(callback, element) {
            var currTime = new Date().getTime();
            var timeToCall = Math.max(0, 16 - (currTime - lastTime));
            var id = window.setTimeout(function() { callback(currTime + timeToCall); },
                timeToCall);
            lastTime = currTime + timeToCall;
            return id;
        };

    if (!window.cancelAnimationFrame)
        window.cancelAnimationFrame = function(id) {
            clearTimeout(id);
        };
}());
define("cartware/polyfill/requestAnimationFrame", function(){});

define('rebrush/components/csp/product/download/item',[
    'jquery',
    'lodash',
    'cartware/components/_base',
    'cartware/jquery/bem',
    'rebrush/system/layout/responsive-hub',
    'rebrush/system/animation',
    'cartware/polyfill/requestAnimationFrame'
], function(
    $,
    _,
    Component,
    BEM,
    ResponsiveHub,
    Animation
) {
    var BLOCK_CLASSNAME         = BEM.buildBlockClassname('csp-product-download-item'),
        ELEMENT_TITLE_CLASSNAME = BEM.buildElementClassname('csp-product-download-item', 'title'),
        MODIFIER_OPENING        = 'opening',
        MODIFIER_OPEN           = 'open';

    var isMobile = true;
    var endPrecedingAnimation = $.noop;

    return Component.create({
        $init: function() {
            $(document.body).on('click', '.' + ELEMENT_TITLE_CLASSNAME, _.bind(this.onTitleClick, this));
            $('.' + ELEMENT_TITLE_CLASSNAME).on('click', _.bind(this.onTitleClick, this));
            ResponsiveHub
                .on('change.mobile.enter', function() { isMobile = true; })
                .on('change.mobile.leave', function() { isMobile = false; });
        },

        onTitleClick: function(e) {
            var title = $(e.currentTarget),
                block = BEM.block(title.closest('.' + BLOCK_CLASSNAME)),
                content = block.getElements('content');

            if (content.length) {
                e.preventDefault();

                if (block.bemHasModifier('open')) {
                    this.closeItem(block, content);
                } else {
                    this.openItem(block, content);
                }
            }
        },

        openItem: function(block, content) {
            var onFrame = function() {
                content.trigger('gigaset.content.change');
                requestAnimationFrame(onFrame);
            };

            var targetHeight;
            var onAnimationEnd = _.bind(function() {
                endPrecedingAnimation = $.noop;
                content.css('height', '');
                block.bemRemoveModifier(MODIFIER_OPENING);
                block.bemAddModifier(MODIFIER_OPEN);
                cancelAnimationFrame(onFrame);
            }, this);

            block.bemAddModifier(MODIFIER_OPENING);
            if (!isMobile && Animation.supportAnimations) {
                endPrecedingAnimation();
                endPrecedingAnimation = onAnimationEnd;
                content.css('height', 'auto');
                targetHeight = content.height();
                content.css('height', '');
                requestAnimationFrame(onFrame);
                _.defer(_.bind(function() {
                    content.one(Animation.transitionEndEventName, onAnimationEnd);
                    content.css('height', targetHeight);
                }, this));
            } else {
                onAnimationEnd();
            }
        },

        closeItem: function(block, content) {
            var onFrame = function() {
                content.trigger('gigaset.content.change');
                requestAnimationFrame(onFrame);
            };

            var onAnimationEnd = _.bind(function() {
                endPrecedingAnimation = $.noop;
                content.css('height', '');
                block.bemRemoveModifier(MODIFIER_OPEN);
                cancelAnimationFrame(onFrame);
            }, this);

            if (!isMobile && Animation.supportAnimations) {
                endPrecedingAnimation();
                endPrecedingAnimation = onAnimationEnd;
                content.css('height', content.height());
                requestAnimationFrame(onFrame);
                _.defer(_.bind(function() {
                    content.one(Animation.transitionEndEventName, onAnimationEnd);
                    content.css('height', 0);
                }, this));
            } else {
                onAnimationEnd();
            }
        }
    });
});
define('rebrush/components/category/layered',[
	'jquery',
	'lodash',
	'cartware/components/_base',
	'cartware/jquery/bem',
	'bootstrap-collapse'
], function(
	$,
	_,
	Component,
	BEM
) {
	return Component.create({
		block:			null,
		categoryBlock:	null,
		filterTitles:	null,
		filterPanels:	null,
		state:			null,

		$init: function() {
			this.state = JSON.parse(Mage.Cookies.get('gigaset_filter_state') || '{}');
			this.block = BEM.block('layered-nav');
			this.categoryBlock = BEM.block('layer-category-tree');
			this.filterTitles = this.block.getElements('filter-title');
			this.filterPanels = this.block.getElements('filter-panel');

			this._initializeEvents();
			this._initializeActiveStatus();
			this.restoreState();
		},

		_initializeEvents: function() {
			var self = this;

			this.block.on('hide.bs.collapse show.bs.collapse', function(e) {
				var collapsible = $(e.target),
					panel = collapsible.parents(this.filterPanels).first(),
					attributeId = panel.data('attribute-id');

				if (attributeId) {
					self.state[attributeId] = (e.type === 'show');
					self.persistState();
				}
			});

			this.block.on('click', '.' + BEM.buildElementClassname('layer-category-tree', 'item-opener'), function(e) {
				e.preventDefault();
			});
		},

		_initializeActiveStatus: function() {
			var path = _.get(window, 'gigaset.category.current');
			_.forEach(path, _.bind(function(categoryId) {
				var item = this.categoryBlock.find('[data-node-id="category-node-' + categoryId + '"]');

				item.bemAddModifier('active');
				item.find('[data-toggle="collapse"]').removeClass('collapsed');
				item.find('.collapse').addClass('in');
			}, this));
		},

		restoreState: function() {
			_.forEach(this.state, _.bind(function(value, key) {
				var query = '[data-attribute-id="' + key + '"]';

				this.filterTitles.filter(query).find('[data-toggle="collapse"]')[value ? 'removeClass' : 'addClass']('collapsed');
				this.filterPanels.filter(query).find('.collapse')[value ? 'addClass' : 'removeClass']('in');
			}, this));
		},

		persistState: function() {
			Mage.Cookies.set('gigaset_filter_state', JSON.stringify(this.state));
		}
	});
});
define('rebrush/components/category/layered/filter',[
    'jquery',
    'lodash',
    'cartware/components/_base',
    'cartware/jquery/bem',
    'rebrush/system/layout/responsive-hub',
    'rebrush/system/animation',
    'modernizr' // shim
], function(
    $,
    _,
    Component,
    BEM,
    ResponsiveHub,
    Animation
) {
    var isMobile = true;

    var MODIFIER_OPEN       = 'open',
        MODIFIER_OPENING    = 'opening',
        MODIFIER_CLOSED     = 'closed';

    var endPrecedingAnimation = $.noop;

    return Component.create({
        block:			    null,
        trigger:            null,
        flyout:             null,
        flyoutWrap:         null,
        closeTrigger:       null,
        currentFilterBar:   null,
        submitButton:       null,

        originalButtonLabel:    null,
        isFilterSelectedOnLoad:   false,

        $init: function() {
            this.block = BEM.block('layered-nav-filter');
            this.trigger = this.block.getElements('wrapper-trigger');
            this.flyoutWrap = this.block.getElements('flyout');
            this.closeTrigger = this.block.getElements('trigger-close');
            this.flyout = BEM.block('layered-nav-flyout');
            this.currentFilterBar = BEM.block('layered-nav-current');
            this.submitButton = this.flyout.getElements('submit');
            this.form = this.submitButton.closest('form');
            this.priceSlider = BEM.block(this.block.find(BEM.block('layer-slider')));
            this.iconOpener = this.block.getElements('trigger-bar-icon');

            this.priceInputs = {
                original: {
                    min: this.priceSlider.find('.price-min'),
                    max: this.priceSlider.find('.price-max')
                },
                value: {
                    min: this.priceSlider.find('.price-value-min'),
                    max: this.priceSlider.find('.price-value-max')
                }
            };

            this.originalButtonLabel = this.submitButton.text();
            this.isFilterSelectedOnLoad = this.isFilterSelected();

            this._bindEvents();
            this.onFormChange();
        },

        _bindEvents: function() {
            this.trigger.on('click', _.bind(this.toggle, this));
            this.closeTrigger.on('click', _.bind(this.close, this));
            this.currentFilterBar.on('click', '.' + BEM.buildElementClassname('layered-nav-current', 'list-item'), _.bind(this.onActiveFilterClick, this));
            this.form.on('change', 'input[type="checkbox"]', _.bind(this.onCheckboxChange, this));
            this.form.on('change', _.bind(this.onFormChange, this));
            this.submitButton.on('click', _.bind(this.onSubmitButtonClick, this));

            ResponsiveHub
                .on('change.mobile.enter', function() { isMobile = true; })
                .on('change.mobile.leave', function() { isMobile = false; });
        },

        onFormChange: function() {
            var exitLabel = this.isFilterSelectedOnLoad ? this.submitButton.data('label-reset') : this.submitButton.data('label-close');
            this.submitButton.text(this.isFilterSelected() ? this.originalButtonLabel : exitLabel);
        },

        onCheckboxChange: function(e) {
            var checkbox = $(e.currentTarget),
                filterNode = checkbox.closest('[data-filter-id]'),
                filterId = filterNode.data('filter-id'),
                isBoolean = filterNode.data('boolean'),
                self = this;

            if (isBoolean) { // if this is a boolean or pseudo-boolean (yes/no select) attribute, uncheck other options
                this.flyout.find('[data-filter-id="' + filterId + '"]').not(filterNode).find('input[type="checkbox"]').each(function() {
                    self.setCheckboxChecked(this, false);
                });
            }
        },

        onSubmitButtonClick: function(e) {
            var self = this;

            if (!this.isFilterSelected()) {
                if (!this.isFilterSelectedOnLoad) {
                    e.preventDefault();
                    this.close();
                    return;
                }
            }
        },

        setCheckboxChecked: function(checkbox, checked) {
            checkbox = $(checkbox);
            var label = $('label[for="' + checkbox.attr('id') + '"]');

            if (checked) {
                checkbox.prop('checked', 'checked').addClass('checked');
                label.addClass('checked');
            } else {
                checkbox.prop('checked', false).removeClass('checked');
                label.removeClass('checked');
            }
        },

        isFilterSelected: function() {
            var formValues = _.filter(this.form.serializeArray(), function (item) { return item.name !== 'url' && item.value !== ''; });
            if (formValues.length > 0) {
                return true;
            }

            if (this.priceInputs.original.min.val() !== this.priceInputs.value.min.val()
                || this.priceInputs.original.max.val() !== this.priceInputs.value.max.val()
            ) {
                return true;
            }

            return false;
        },

        onActiveFilterClick: function(e) {
            var node = $(e.currentTarget),
                filterId = node.data('filter-id'),
                attributeId = node.data('attribute-id');

            if (filterId) {
                this.removeFilter(filterId, attributeId);
            }
        },

        removeFilter: function(filterId, attributeId) {
            var filterItem = this.flyout.getElements('filter-item'),
                form = filterItem.closest('form'),
                self = this;

            if (attributeId) {
                filterItem = filterItem.filter('[data-attribute-id="' + attributeId + '"]');
            }

            if ('*' !== filterId) {
                filterItem = filterItem.filter('[data-filter-id="' + filterId + '"]');
            }

            if (!filterItem.length) {
                return;
            }

            var typeActions = {
                'attribute': function(filterItem) {
                    var checkbox = filterItem.find('input[type=checkbox]');
                    self.setCheckboxChecked(checkbox, false);
                },

                'price': function(filterItem) {
                    var input = filterItem.find('.' + BEM.buildElementClassname('layer-slider', 'form-input'));

                    input.val('');
                }
            };

            typeActions.default = typeActions.attribute;

            filterItem.each(function() {
                var item = $(this),
                    filterType = filterItem.data('filter-type');

                if ('*' !== filterId) {
                    typeActions[filterType in typeActions ? filterType : 'default'](item);
                } else {
                    $.each(typeActions, function(filterType, typeAction) {
                        typeAction(item);
                    });
                }
            });

            form.submit();
        },

        toggle: function() {
            if (this.block.bemHasModifier(MODIFIER_OPEN)) {
                this.close();
            } else {
                this.open();
            }
        },

        open: function() {
            var targetHeight;
            var onAnimationEnd = _.bind(function() {
                endPrecedingAnimation = $.noop;
                this.flyoutWrap.css('height', '');
                this.block.bemRemoveModifier(MODIFIER_OPENING);
                this.block.bemAddModifier(MODIFIER_OPEN);
            }, this);

            this.block.bemAddModifier(MODIFIER_OPENING);
            this.iconOpener.bemAddModifier(MODIFIER_OPEN);
            this.iconOpener.bemRemoveModifier(MODIFIER_CLOSED);

            if (!isMobile && Animation.supportAnimations) {
                endPrecedingAnimation();
                endPrecedingAnimation = onAnimationEnd;
                this.flyoutWrap.css('height', 'auto');
                targetHeight = this.flyoutWrap.height();
                this.flyoutWrap.css('height', '');
                _.defer(_.bind(function() {
                    this.flyoutWrap.one(Animation.transitionEndEventName, onAnimationEnd);
                    this.flyoutWrap.css('height', targetHeight);
                }, this));
            } else {
                onAnimationEnd();
            }
        },

        close: function() {
            var onAnimationEnd = _.bind(function() {
                endPrecedingAnimation = $.noop;
                this.flyoutWrap.css('height', '');
                this.block.bemRemoveModifier(MODIFIER_OPEN);
            }, this);

            this.iconOpener.bemAddModifier(MODIFIER_CLOSED);
            this.iconOpener.bemRemoveModifier(MODIFIER_OPEN);

            if (!isMobile && Animation.supportAnimations) {
                endPrecedingAnimation();
                endPrecedingAnimation = onAnimationEnd;
                this.flyoutWrap.css('height', this.flyoutWrap.height());
                _.defer(_.bind(function() {
                    this.flyoutWrap.one(Animation.transitionEndEventName, onAnimationEnd);
                    this.flyoutWrap.css('height', 0);
                }, this));
            } else {
                onAnimationEnd();
            }
        }
    });
});

define('rebrush/components/category/layered/slider',[
    'jquery',
    'lodash',
    'cartware/components/_base',
    'cartware/jquery/bem',
    'cartware/components/Config',
    'cartware/dom/event/trigger'
], function(
    $,
    _,
    Component,
    BEM,
    Config,
    triggerEvent
) {
    function LayerSlider(block) {
        this.block = BEM.block(block);
        this.handles = this.block.getElements('price-slider-handle');
        this.bar = this.block.getElements('price-slider');
        this.span = this.block.getElements('price-slider-span');

        this.requestVar = this.block.find('.request-var').val();
        this.priceMin = parseInt(this.block.find('.price-min').val());
        this.priceMax = parseInt(this.block.find('.price-max').val());
        this.priceValueMin = parseInt(this.block.find('.price-value-min').val());
        this.priceValueMax = parseInt(this.block.find('.price-value-max').val());

        this.formInput = this.block.getElements('form-input');

        this.initializeSlider();
        this._bindEvents();
    }

    LayerSlider.prototype = {
        activeHandle: null,
        activeHandleIdx: null,

        _bindEvents: function() {
            this.bar.on('mousemove', _.bind(function(event) {
                var posHandleL = this.handles.get(0).cumulativeOffset().left;
                var posHandleR = this.handles.get(1).cumulativeOffset().left;
                var posPointer = Event.pointerX(event);
                if (posPointer > posHandleR || posPointer > ((posHandleR + posHandleL) / 2)) {
                    this.activeHandle = this.handles.get(1);
                    this.activeHandleIdx = 1;
                } else {
                    this.activeHandle = this.handles.get(0);
                    this.activeHandleIdx = 0;
                }
                // BubblePriceSlider.updateStyles();
            }, this));
        },

        initializeSlider: function() {
            var self = this,
                formatCurrency = window.formatCurrency,
                priceFormat = Config.get('currency.format');

            priceFormat.requiredPrecision = 0;

            new Control.Slider(this.handles.toArray(), this.bar.get(0), {
                range: $R(this.priceMin, this.priceMax),
                sliderValue: [this.priceValueMin, this.priceValueMax],
                restricted: true,
                spans: this.span.toArray(),
                onSlide: function(v) {
                    if (!isNaN(v[0])) {
                        self.bar.find('.price-range span.price')[0].update(formatCurrency(v[0], priceFormat, false));
                    }
                    if (!isNaN(v[1])) {
                        self.bar.find('.price-range span.price')[1].update(formatCurrency(v[1], priceFormat, false));
                    }
                },
                onChange: function(v) {
                    var min = v[0].toFixed(),
                        max = v[1].toFixed();

                    if (min === 0 || isNaN(min) || min <= this.range.start) {
                        min = '';
                    }
                    if (max >= this.range.end || isNaN(max)) {
                        max = '';
                    }

                    self.setRange(min, max);
                }
            });
        },

        setRange: function(min, max) {
            this.formInput.val([min, max].join('-'));
            triggerEvent(this.formInput.get(0), 'change');
        }
    };

    return Component.create({
        $init: function() {
            this.block = BEM.block('layer-slider');
            this.block.each(function() {
                $(this).data('component/layer-slide', new LayerSlider(this));
            });
        }
    });
});
define('rebrush/components/category/tree',[
	'jquery',
	'cartware/components/_base',
	'cartware/jquery/bem'
], function(
	$,
	Component,
	BEM
) {
	function Tree(node) {
		this.block = BEM.block(node);
		this.headline = this.block.getElements('select-category-headline');

		this.bindEvents();
	}

	Tree.prototype = {
		bindEvents: function() {
			var self = this;
			this.block.on('change', '.' + BEM.buildElementClassname('select', 'select'), function (e) {
				var select = $(this),
					url = select.val();

				self.headline.text(select.find('option:selected').text());

				if (url) {
					window.location = url;
				}

				e.preventDefault();
				return false;
			});
		}
	};

	return Component.create({
		blocks:	null,

		$init: function() {
			this.blocks = BEM.block('layer-category-tree');
			this.blocks.each(function() {
				new Tree(this);
			});
		}
	});
});
define('rebrush/components/plugin/touch-controller',[
    'jquery',
    'cartware/components/_base'
], function(
        $,
        Component
) {
    
    function mapTouchEvents(event,simulatedType) {
        
        //Ignore any mapping if more than 1 fingers touching
        if (event.changedTouches.length > 1 || $(event.target).hasClass('select2-results__option')) { return; }
        
        var touch = event.changedTouches[0];
        
        //--https://developer.mozilla.org/en/DOM/document.createEvent--
        eventToSimulate = document.createEvent('MouseEvent');
        
        //--https://developer.mozilla.org/en-US/docs/Web/API/event.initMouseEvent--
        eventToSimulate.initMouseEvent(
                simulatedType,		//type
                true,				//bubbles
                true,				//cancelable
                window,				//view
                1,					//detail
                touch.screenX,		//screenX
                touch.screenY,		//screenY
                touch.clientX,		//clientX
                touch.clientY,		//clientY
                false,				//ctrlKey
                false,				//altKey
                false,				//shiftKey
                false,				//metaKey
                0,					//button
                null				//relatedTarget
        );
        
        touch.target.dispatchEvent(eventToSimulate);
        //This ignores the default scroll behavior
        // event.preventDefault();
    }
    
    function addTouchEvents() {
        document.addEventListener('touchstart',function(e){ mapTouchEvents(e,'mousedown'); },true);
        document.addEventListener('touchmove',function(e){ mapTouchEvents(e,'mousemove'); },true);
        document.addEventListener('touchend',function(e){ mapTouchEvents(e,'mouseup'); },true);
        document.addEventListener('touchcancel',function(e){ mapTouchEvents(e,'mouseup'); },true);
    }
    
    return Component.create({
        $init: function () {
            addTouchEvents();
        }
    });
    
});
define('rebrush/components/product/view/configurable-options-help',[
    'jquery',
    'lodash',
    'cartware/components/_base',
    'cartware/jquery/bem',
    'magnific-popup'
], function(
    $,
    _,
    Component,
    BEM
) {
    return Component.create({
        block:					null,

        $init: function() {
            this.block = BEM.block('product-view');
            this.block.getElements('configurable-options-help-link').magnificPopup({
                type:'inline',
                midClick: true
            });
        }
    });
});
define('rebrush/components/product/options/group',[
	'jquery',
	'lodash',
	'cartware/components/_base',
	'cartware/jquery/bem',
], function(
	$,
	_,
	Component,
	BEM
) {

    function Group(block) {
        this.block = block;
        this.select = block.find('.' + BEM.buildElementClassname('product-options','option-wrap', 'option-select') + ' select');
        this.options = block.find('.option-group');

        this.init();
    }

    Group.prototype = {
        block: null,
        select: null,
        options: null,
        id: null,

        init: function () {
            let self = this;

            this.toggleOption(this.select.val());

            this.select.on('change', function () {
                let select = $(this);

                self.options.hide();
                self.toggleOption(select.val());
            });
        },

        toggleOption: function (optionId) {
            this.options.each(function () {
                let option = $(this);

                if (option.data('option-id').toString() === optionId) {
                    option.show();
                } else {
                    let input = option.find('input[name="options\[' + option.data('option-id') +'\]"]');

                    input.val('');
                    input.trigger('change');
                }
            });
        }
    };

    return Component.create({
        $init: function () {
            var blocks = $('.' + BEM.buildElementClassname('product-options', 'custom-option-list', 'grouped'));

            blocks.each(function () {
                let block = $(this);

                if (!block.data('option-group-id')) {
                    return;
                }

                new Group(block);
            });
        }
    });

});
define('rebrush/components/gigaset_customprint/legal',[
    'jquery',
    'lodash',
    'cartware/components/_base',
    'cartware/jquery/bem',
    'gigaset/spinner',
    'text!shoptheme/templates/magnific-popup/default.html',
    'magnific-popup'
], function(
    $,
    _,
    Component,
    BEM,
    Spinner,
    PopupHtml
) {
    return Component.create({
        $init: function () {
            let productViewBlock = BEM.block('product-view'),
                productAddToCartBlock = BEM.block('product-detail-add-to-cart'),
                container = productViewBlock.getElements('column-technical'),
                addToCart = productAddToCartBlock.getElements('add-to-cart-button'),
                optionInputs = container.find('input[data-show-legal-popup="true"]'),
                legalInput = $(productAddToCartForm.form).find('input[name="legal_accepted"]'),
                popupTemplate = null;

            const legalPopupOpenClass = 'print-legal-popup-open';

            if (legalInput.length < 1) {
                return;
            }

            function _sendRequest(url, data, onSuccessCallback) {
                var data = data || {};

                // add isAjax param to avoid caching problems
                if (url.indexOf('isAjax=1') === -1 && typeof data === 'string' && data.indexOf('isAjax=1') === -1) {
                    url += url.indexOf('?') === -1 ? '?isAjax=1' : '&isAjax=1';
                }

                url = (this.isSecure === true && url.indexOf('http://') === 0) ? url.replace('http://', 'https://') : url;

                $.ajax(url, {
                    method: 'POST',
                    data: data
                }).then(_.bind(function (response, status, xhr) {
                    if (response.success) {
                        if (typeof onSuccessCallback === 'function') {
                            onSuccessCallback(response, status, xhr);
                        }
                    }
                }, this));
            }

            let defaultAddToCartFunc = addToCart.prop('onclick');
            addToCart.prop('onclick', null);

            function callbackLegalPopup() {
                $(document).on('click', '.' + BEM.buildElementClassname('print-legal-popup', 'add-to-cart-btn'), _.bind(function(e) {
                    let inlineValidateNotice = $('.' + BEM.buildElementClassname('print-legal-popup', 'print-validate-error')),
                        inlineTermsCheckbox = $('.' + BEM.buildElementClassname('print-legal-popup', 'print-terms-checkbox'));

                    legalInput.val(0);

                    inlineValidateNotice.hide();

                    if (!inlineTermsCheckbox.prop('checked')) {
                        e.preventDefault();
                        inlineValidateNotice.show();

                        return false;
                    }

                    legalInput.val(1);


                    $.magnificPopup.close();
                    defaultAddToCartFunc();
                }, this));
            }

            function _renderPopup(data) {
                if (null === popupTemplate) {
                    popupTemplate = _.template(PopupHtml);
                }

                return popupTemplate(data);
            }

            function _showPopup(message, openCallBack) {

                $.magnificPopup.open({
                    closeOnContentClick: false,
                    closeOnBgClick: false,
                    closeBtnInside: true,
                    items: [
                        {
                            src: _renderPopup({ body: message }),
                            type: 'inline'
                        }
                    ],
                    callbacks: {
                        open: function() {
                            if (typeof openCallBack === 'function') {
                                openCallBack();
                            }
                        }
                    }
                });
            }

            function initLegalPopup() {
                var legalPopupUrl = optionInputs.data('legal-popup-url');

                Spinner.add();

                _sendRequest(legalPopupUrl, null, _.bind(function(response, status, xhr) {

                    _showPopup(response.success, _.bind(callbackLegalPopup, this));

                    $.magnificPopup.instance.wrap.addClass(legalPopupOpenClass);
                    $.magnificPopup.instance.bgOverlay.addClass(legalPopupOpenClass);

                    Spinner.remove();
                }, this));
            }

            function waitForElm(selector) {
                return new Promise(resolve => {
                    if (document.querySelector(selector)) {
                        return resolve(document.querySelector(selector));
                    }

                    const observer = new MutationObserver(mutations => {
                        if (document.querySelector(selector)) {
                            resolve(document.querySelector(selector));
                            observer.disconnect();
                        }
                    });

                    observer.observe(document.body, {
                        childList: true,
                        subtree: true
                    });
                });
            }

            addToCart.on('click', function (e) {
                let showLegal = false;

                if (!optionInputs.length || e.currentTarget.getAttribute('data-customprint-added') !== '1') {
                    // skip if on engraving has been added
                    defaultAddToCartFunc();
                } else {
                    // category lightbox
                    if($.magnificPopup.instance.isOpen) {

                        initLegalPopup.call($.magnificPopup.instance);

                        var legalPopup = '.' + BEM.buildElementClassname('print-legal-popup', 'container');

                        waitForElm(legalPopup).then((elm) => {
                            callbackLegalPopup();
                        });
                    }

                    // product detail page
                    optionInputs.each(function () {
                        if ($(this).val()) {
                            initLegalPopup();
                            showLegal = true;
                            return false;
                        }
                    });

                    if (!showLegal) {
                        defaultAddToCartFunc();
                    }
                }
            });
        },
    });
});
define('rebrush/components/gigaset_presale/confirm',[
    'jquery',
    'cartware/components/_base',
    'cartware/jquery/bem',
    'magnific-popup'
], function(
    $,
    Component,
    BEM
) {
    return Component.create({
        $init: function() {
            var $popup = BEM.block('presale-confirm-popup');
            if (!$popup.length) {
                return;
            }

            if (typeof productAddToCartForm === 'undefined') {
                return;
            }

            var cancelSelector = '.' + BEM.buildElementClassname('presale-confirm-popup', 'cancel');
            var confirmSelector = '.' + BEM.buildElementClassname('presale-confirm-popup', 'confirm');

            var _confirmed = false;
            var _origSubmit = productAddToCartForm.submit;

            // Intercept at productAddToCartForm.submit level — catches any caller
            // (inline onclick, legal.js defaultAddToCartFunc, googletag handler, etc.)
            productAddToCartForm.submit = function(button) {
                if (_confirmed) {
                    _confirmed = false;
                    return _origSubmit.call(productAddToCartForm, button);
                }
                $.magnificPopup.open({
                    mainClass: 'gigaset-mfp-zoom-in',
                    closeOnContentClick: false,
                    closeOnBgClick: false,
                    closeBtnInside: true,
                    items: [{
                        src: $popup,
                        type: 'inline'
                    }],
                    callbacks: {
                        open: function() {
                            $popup.removeModifier('hidden');
                        },
                        close: function() {
                            $popup.addModifier('hidden');
                        }
                    }
                });
            };

            $(document).on('click', confirmSelector, function() {
                _confirmed = true;
                $.magnificPopup.close();
                productAddToCartForm.submit();
            });

            $(document).on('click', cancelSelector, function() {
                $.magnificPopup.close();
            });
        }
    });
});

define('rebrush/components/product/view/tracking',[
    'jquery',
    'lodash',
    'cartware/components/_base',
    'cartware/jquery/bem',
    'cartware/components/Registry'
], function (
    $,
    _,
    Component,
    BEM,
    Registry
) {
    return Component.create({
        spConfig: null,
        viewItemEvent: 'view_item',
        dataLayer: null,

        $init: function () {
            this.initComponent();
        },

        initComponent: function () {
            Registry().retrieve('product.view.configurable.spConfig', _.bind(function (spConfig) {
                this.spConfig = spConfig;
                this.bindEvents();
                this.trackItemView();
            }, this));
        },

        bindEvents: function () {
            $(document).on('product/changeSimple', $.proxy(this._onSimpleChange, this));
        },

        trackItemView: function () {
            var dataLayer = window.dataLayer || [];
            if (typeof window.dataLayer !== 'undefined' && typeof window.dataLayer.ecommerce !== 'undefined') {
                dataLayer.push({
                    'ecommerce': undefined
                });
            }
            var ecommerce = this.getItemTrackingJson();
            dataLayer.push({'event': this.viewItemEvent, ecommerce});

            if (typeof window.dataLayer === 'undefined' || !window.dataLayer) {
                window.dataLayer = dataLayer;
            }
        },

        getItemTrackingJson: function () {
            var ecommerce = {},
                currentSimple = this.spConfig.__currentSimple,
                discount = 0.00,
                originalPrice = 0.00,
                finalPrice = 0.00;

            const item = {
                'item_id': this.spConfig.config.id_to_sku[currentSimple.id],
                'item_name': currentSimple.product_name,
                'index': 0
            };

            if (currentSimple.attributes) {
                var attributes = Object.values(currentSimple.attributes);

                // sort by code desc
                attributes.sort((a, b) => {
                    const nameA = a.code.toUpperCase();
                    const nameB = b.code.toUpperCase();
                    return (nameA > nameB) ? -1 : (nameA < nameB) ? 1 : 0;
                });

                item.item_variant = attributes.map((attribute) => attribute.text_default).join(' / ').toLowerCase();
            }

            if (Product.Config.prototype.hasOwnProperty('_configurable')) {
                const configurable = Product.Config.prototype._configurable;
                if (configurable.gtm_categories) {
                    Object.assign(item, configurable.gtm_categories);
                }
            }

            if (optionsPrice) {
                if (optionsPrice.priceInclTax) {
                    finalPrice = optionsPrice.priceInclTax;
                }
                if (optionsPrice.productOldPrice) {
                    originalPrice = optionsPrice.productOldPrice;
                }
                if (optionsPrice.optionPrices.config.price) {
                    finalPrice += optionsPrice.optionPrices.config.price;
                    originalPrice += optionsPrice.optionPrices.config.price;
                }
                if (optionsPrice.productOldPrice && optionsPrice.productPrice && (optionsPrice.productOldPrice !== optionsPrice.productPrice)) {
                    discount = optionsPrice.productOldPrice - optionsPrice.productPrice;
                }
            }

            item.price = parseFloat(Number(originalPrice.toFixed(2)));
            item.discount = parseFloat(Number(discount.toFixed(2)));

            ecommerce.items = [item];
            ecommerce.currency = _.get(window, 'gigaset.currency.code');
            ecommerce.value = parseFloat(Number(finalPrice.toFixed(2)));

            return ecommerce;
        },

        _onSimpleChange: function () {
            if (!this.spConfig) {
                this.spConfig = _.get(window, 'spConfig');
            }
            this.trackItemView();
        },
    });
});

define('rebrush/components/product/view/bazaarvoice',[
    'jquery',
    'cartware/components/_base',
    'cartware/jquery/bem',
    'lodash'
], function(
    $,
    Component,
    BEM,
    _
) {
    var BLOCK_BAZAARVOICE_RATING_SUMMARY = 'bazaarvoice-summary';

    return Component.create({

        // only for product view pages
        $init: function() {

            var ratingSummary = BEM.block(BLOCK_BAZAARVOICE_RATING_SUMMARY);
            var targetDiv = $('div[data-bv-show="rating_summary"]');

            if (!targetDiv.length) {
                return;
            }

            var processRatingSummary = function() {

                // Manipulate Bazaarvoice recommend text
                const recommendContainer = ratingSummary.find('.bv_percentRecommend_component_container');
                const qaContainer = ratingSummary.find('.bv_questionsAndAnswers_component_container');

                if (recommendContainer && recommendContainer.length) {
                    _.each(recommendContainer, function(e, i) {
                        const container = $(e),
                            text = container.text();

                        // Match digits followed by % (for instance, "94%")
                        const match = text.match(/^\d+%/);
                        if (match) {
                            const percentage = match[0];
                            const remainingText = text.slice(percentage.length);
                            container.html(`<strong>${percentage}</strong> ${remainingText}`);
                        }
                    });
                }

                // Hide Bazaarvoice CTAs
                if ((recommendContainer && recommendContainer.length) || (qaContainer && qaContainer.length)) {
                    // Hide both Cta's
                    const target = 'bv_main_container_CTA_row';
                    ratingSummary.find('.' + target).addClass(target + '--hidden');
                } else {
                    // No review and questions yet => Only hide "ask a question" button
                    const target = 'bv_aaq_button';
                    ratingSummary.find('.' + target).addClass(target + '--hidden');
                }
            };

            // If the target containers already exist, process immediately
            if (targetDiv.find('.bv_percentRecommend_component_container, .bv_questionsAndAnswers_component_container').length > 0) {
                processRatingSummary();
                return;
            }

            // Set up MutationObserver to watch for specific divs
            var observer = new MutationObserver(function(mutations, obs) {
                // Check if the target containers exist in the DOM
                if (targetDiv.find('.bv_percentRecommend_component_container, .bv_questionsAndAnswers_component_container').length > 0) {
                    processRatingSummary();
                    obs.disconnect(); // Stop observing
                }
            });

            // Init observer
            observer.observe(targetDiv[0], {
                childList: true,
                subtree: true
            });
        }
    });
});

/**
 * Bazaarvoice renders every [data-bv-show] container as soon as bv.js runs, also those far below the fold.
 * Containers rendered with data-bv-show-lazy get their data-bv-show attribute when they come near the viewport,
 * bv.js picks them up from there.
 */
define('rebrush/components/review/bv-lazy',[
    'cartware/components/_base'
], function(
    Component
) {
    const LAZY_ATTRIBUTE = 'data-bv-show-lazy';
    const ROOT_MARGIN = '600px 0px';

    return Component.create({
        $init: function() {
            var self = this;
            var containers = document.querySelectorAll('[' + LAZY_ATTRIBUTE + ']');

            if (!containers.length) {
                return;
            }

            if (typeof window.IntersectionObserver !== 'function') {
                this.activateAll();
                return;
            }

            var observer = new IntersectionObserver(function(entries) {
                entries.forEach(function(entry) {
                    if (entry.isIntersecting) {
                        observer.unobserve(entry.target);
                        self.activate(entry.target);
                    }
                });
            }, { rootMargin: ROOT_MARGIN });

            containers.forEach(function(container) {
                observer.observe(container);
            });

            // The rating summary links jump to the reviews, which must exist by then.
            // Capture phase, because Bazaarvoice stops the propagation of its own clicks
            var onSummaryClick = function(event) {
                if (event.target.closest && event.target.closest('[data-bv-show="rating_summary"]')) {
                    document.removeEventListener('click', onSummaryClick, true);
                    self.activateAll();
                }
            };
            document.addEventListener('click', onSummaryClick, true);
        },

        activateAll: function() {
            document.querySelectorAll('[' + LAZY_ATTRIBUTE + ']').forEach(this.activate);
        },

        activate: function(container) {
            var type = container.getAttribute(LAZY_ATTRIBUTE);

            if (!type) {
                return;
            }

            container.removeAttribute(LAZY_ATTRIBUTE);
            container.setAttribute('data-bv-show', type);
        }
    });
});

/**
 * Bazaarvoice renders into its containers after the sliders around them have been laid out. A height change of a
 * container inside a slider recalculates that slider. Watching the containers instead of waiting for $BV.ready keeps
 * this independent of the legacy $BV API (bvapi.js). Only height counts, the relayout itself changes widths.
 */
define('rebrush/components/review/bv-ready',[
    'jquery',
    'cartware/components/_base',
    'cartware/components/Holepunch'
], function ($, Component, holepunch) {

    const BV_BLOCK_SELECTOR = '[data-bv-show], [data-bv-show-lazy]';
    const SLIDER_CONTAINER_SELECTOR = '.cartware-slider';

    return Component.create({

        $init: function () {
            var self = this;

            if (typeof window.ResizeObserver !== 'function') {
                return;
            }

            this.pendingSliders = [];
            this.heights = new WeakMap();

            this.observer = new ResizeObserver(function (entries) {
                entries.forEach(function (entry) {
                    var height = Math.round(entry.contentRect.height);

                    if (self.heights.get(entry.target) === height) {
                        return;
                    }

                    self.heights.set(entry.target, height);

                    var slider = $(entry.target).closest(SLIDER_CONTAINER_SELECTOR).get(0);

                    if (slider && self.pendingSliders.indexOf(slider) === -1) {
                        self.pendingSliders.push(slider);
                    }
                });

                window.requestAnimationFrame(function () {
                    self.pendingSliders.splice(0).forEach(self.relayout);
                });
            });

            this.observe();

            // Accessory and upsell sliders arrive with the holepunch, after this component started
            holepunch.ready(function () {
                self.observe();
            });
        },

        observe: function () {
            var observer = this.observer;

            $(SLIDER_CONTAINER_SELECTOR).find(BV_BLOCK_SELECTOR).each(function () {
                observer.observe(this);
            });
        },

        relayout: function (slider) {
            var instance = $(slider).data('cartware_slider/instance');

            if (!instance) {
                return;
            }

            if (typeof instance.initializeLayout === 'function') {
                instance.initializeLayout();
            }

            if (
                instance.settings &&
                instance.settings.controlsettings &&
                instance.settings.controlsettings.handles &&
                typeof instance.initializeHandles === 'function'
            ) {
                instance.initializeHandles();
            }
        }

    });

});


define('text!rebrush/templates/product/energy-label.html',[],function () { return '<div class="b_energy-label">\n\t<div class="b_energy-label__container b_energy-label__container--product-view">\n\t\t<div class="b_energy-label__classification-wrapper b_energy-label__classification-wrapper--product-view">\n\t\t\t<% if (typeof data_sheet_link !== \'undefined\' && data_sheet_link) { %>\n\t\t\t<a class="b_energy-label__link-datasheet b_energy-label__link-datasheet--product-view" rel="noopener noreferrer" href="<%- data_sheet_link %>" target="_blank">\n\t\t\t\t<span class="b_energy-label__datasheet-label b_energy-label__datasheet-label--product-view"><%- typeof data_sheet_label !== \'undefined\' ? data_sheet_label : \'\' %></span>\n\t\t\t</a>\n\t\t\t<% } %>\n\t\t\t<% if (typeof certificate_link !== \'undefined\' && certificate_link) { %>\n\t\t\t<a class="b_energy-label__link-certificate b_energy-label__link-certificate--product-view" rel="noopener noreferrer" href="<%- certificate_link %>" target="_blank" aria-label="<%- typeof certificate_label !== \'undefined\' ? certificate_label : \'\' %>">\n\t\t\t<% } %>\n\t\t\t\t<i class="b_energy-label__icon-classification b_energy-label__icon-classification--product-view <%- typeof classification_icon_class !== \'undefined\' ? classification_icon_class : \'\' %>"></i>\n\t\t\t<% if (typeof certificate_link !== \'undefined\' && certificate_link) { %>\n\t\t\t</a>\n\t\t\t<% } %>\n\t\t\t<% if (typeof psu_icon_product !== \'undefined\' && psu_icon_product) { %>\n\t\t\t<i class="b_energy-label__icon-psu b_energy-label__icon-psu--product-view icon-energy-label-psu"></i>\n\t\t\t<% } %>\n\t\t</div>\n\t</div>\n</div>\n';});

define('rebrush/components/product/type/configurable',[
	'jquery',
	'lodash',
	'cartware/components/Registry',
	'cartware/components/_base',
	'cartware/jquery/bem',
	'text!rebrush/templates/product/energy-label.html',
	'bootstrap-popover',
	'bootstrap-button'
], function(
	$,
	_,
	Registry,
	Component,
	BEM,
	EnergyLabelHtml
) {
	var $document = $(document);

	return Component.create({
		stockBlock:			null,
		priceBlock:			null,
		spConfig:			null,
		destructorCallbacks: null,

		constructor: function() {
			this.destructorCallbacks = [];
		},

		$init: function() {
			this.block = BEM.block('product-view');
			this.stockBlock = BEM.block('product-detail-stock-status');
			this.priceBlock = BEM.block('product-detail-price');
			this.stockWrap  = this.block.find(BEM.block('product-detail-stock-wrap'));
			this.eolStatusWrap = this.block.find(BEM.block('product-detail-eol-status-wrap'));
			this.eolStatusBlock = BEM.block('product-detail-eol-status');
			this.wtbExternalBlock = BEM.block(this.block.find(BEM.block('gigaset-wheretobuy-external')));
			this.wtbExternalDisplay = this.wtbExternalBlock.getElements('display');
			this.deliveryTimeBlock = BEM.block(this.block.find(BEM.block('product-delivery-time')));
			this.metaWrap = this.block.find(BEM.block('product-detail-seo-meta-wrap'));
			this.metaBlock = this.block.find(BEM.block('product-detail-seo-meta-json'));
			this.mediaBlock = BEM.block('product-media');
			// this.itemPropOffersBlock = BEM.block(this.block.find(BEM.block('product-detail-seo-itemprop-offers')));

			this.initializeDeliveryTime();
			this.initializeWtbExternal();
			this.initializeEol();
			this.initializeStock();
			this.initializePrice();
			this.initializeProductConfig();
			this.initializeMetaData();
			this.initializeEnergyLabel();
		},

		initializeProductConfig: function() {
			var spConfigData = _.get(window, 'spConfigData');

			if (spConfigData) {
				var urlMatches = /[#?](.+)/.exec(document.location),
					preselect = false;

				if (urlMatches) {
					$.each(urlMatches[1].split("&"), $.proxy(function(i, fragment) {
						var parts = /(.+?)=([^&=]+)/.exec(fragment);

						if (!parts) return;

						if (parts[1] === 'preselect') {
							preselect = parts[2];
						}
					}, this));
				}

				var simplesArray = _.get(Product.Config, 'prototype._configurable.simples', {});

				var sortedSimples = _.sortBy(simplesArray,function (o){
					if (preselect) {
						return parseInt(o.id) == preselect ? 0 : 1;
					}
					if (o.is_available) {
						return 0;
					}
					if (o.priority) {
						return parseInt(o.priority);
					}
					return 1000;
				});

				if (!_.isEmpty(sortedSimples)) {
					_.merge(spConfigData, {
						defaultValues: _.reduce(
							_.get(_.head(sortedSimples), 'attributes'),
							function(acc, value, key) {
								acc[key] = _.get(value, 'value');
								return acc;
							},
							{}
						)
					});
				}

				this.spConfig = new Product.Config(spConfigData);
				window.spConfig = this.spConfig;
				Registry().register('product.view.configurable.spConfig', this.spConfig);
				this.destructorCallbacks.push(function() {
					Registry().unregister('product.view.configurable.spConfig');
				});
			}
		},

		initializeWtbExternal: function() {
			var defaultElement = this.wtbExternalBlock.getElements('static'),
				tmpl = _.template(this.wtbExternalBlock.find('[data-template]').html()),
				blockCache = {};

			var _onSimpleChange = _.bind(function (e, simple) {
				var wtbText = simple.wtb.external_product_text || '',
					wtbCtaLabel = simple.wtb.external_product_link_label || '',
					wtbCtaUrl = simple.wtb.external_product_link || '',
					block;

				if (simple
					&& (
						wtbText
						|| (wtbCtaLabel && wtbCtaUrl)
					)
				) {
					block = blockCache[simple.id] || null;

					if (!block) {
						block = $(tmpl({
							text: wtbText,
							label: wtbCtaLabel,
							url: wtbCtaUrl
						}));

						blockCache[simple.id] = block;
					}

					this.wtbExternalDisplay.empty();
					this.wtbExternalDisplay.append(block);
					this.wtbExternalBlock.show();
				} else {
					this.wtbExternalDisplay.empty();
					this.wtbExternalDisplay.append(defaultElement);
					this.wtbExternalBlock.hide();
				}
			}, this);

			$document.on('product/changeSimple', _onSimpleChange);
			this.destructorCallbacks.push(function() {
				$document.off('product/changeSimple', _onSimpleChange);
			});
		},

		initializeDeliveryTime: function() {
			var _onSimpleChange = _.bind(function (e, simple) {
				var isEol = simple.is_eol || false;

				if (isEol) {
					this.deliveryTimeBlock.hide();
				} else {
					this.deliveryTimeBlock.show();
				}
			}, this);

			$document.on('product/changeSimple', _onSimpleChange);
			this.destructorCallbacks.push(function() {
				$document.off('product/changeSimple', _onSimpleChange);
			});
		},

		initializeEol: function() {
			var defaultElement = this.eolStatusBlock.getElements('default'),
				tmpl = _.template(this.eolStatusBlock.find('[data-template]').html()),
				blockCache = {};

			var _onSimpleChange = _.bind(function (e, simple) {
				var isEol = simple.is_eol || false,
					eolCtaLabel = simple.eol_cta_label || '',
					eolCtaUrl = simple.eol_cta_url || '',
					block;

				if (simple) {
					block = blockCache[simple.id] || null;

					if (!block && isEol) {
						block = $(tmpl({
							label: eolCtaLabel,
							url: eolCtaUrl
						}));

						blockCache[simple.id] = block;
					}

					this.eolStatusBlock.empty();
					this.eolStatusBlock.append(block);
					this.eolStatusWrap.show();
				} else {
					this.eolStatusBlock.empty();
					this.eolStatusBlock.append(defaultElement);
					this.eolStatusWrap.hide();
				}
			}, this);

			$document.on('product/changeSimple', _onSimpleChange);
			this.destructorCallbacks.push(function() {
				$document.off('product/changeSimple', _onSimpleChange);
			});
		},

		initializeStock: function() {
			var defaultElement = this.stockBlock.getElements('default'),
				tmpl = _.template(this.stockBlock.find('[data-template]').html()),
				blockCache = {};

			var _onSimpleChange = _.bind(function (e, simple) {
				var status,
					block,
					isEol;

				if (simple) {
					status = simple.stock_status;
					block = blockCache[status] || null;
					isEol = simple.is_eol || false;

					if (!block) {
						block = $(tmpl({
							stockStatus: status,
							isEol: isEol,
						})).bemAddModifier(status);

						blockCache[status] = block;
					}

					this.stockBlock.empty();
					this.stockBlock.append(block);
					this.stockWrap.show();
				} else {
					this.stockBlock.empty();
					this.stockBlock.append(defaultElement);
					this.stockWrap.hide();
				}
			}, this);

			$document.on('product/changeSimple', _onSimpleChange);
			this.destructorCallbacks.push(function() {
				$document.off('product/changeSimple', _onSimpleChange);
			});
		},

		initializePrice: function() {
			var fromLabel = this.priceBlock.findBlock('price').getElements('label-from');

			function _onSimpleChange(e, simple) {
				if (simple) {
					fromLabel.hide();
				} else {
					fromLabel.show();
				}
			}

			$document.on('product/changeSimple', _onSimpleChange);
			this.destructorCallbacks.push(function() {
				$document.off('product/changeSimple', _onSimpleChange);
			});
		},

		initializeMetaData: function() {
			// meta data
			var _onSimpleChange = _.bind(function (e, simple) {
				if (simple && typeof simple.meta !== "undefined") {
					var simpleMetaData = simple.meta;
					var self = this;

					// iterate simple meta data
					_.each(simpleMetaData, function (data, index) {
						// HEAD - Meta Tags
						self.setMetaTag(index, data);
					});
					// BODY - Json LD
					this.setMetaJsonLd(simple);
				}
			}, this);

			// initial
			var currentSimple = _.get(window, 'spConfig.__currentSimple');
			if (currentSimple) {
				_onSimpleChange(null, currentSimple);
			}

			$document.on('product/changeSimple', _onSimpleChange);
			this.destructorCallbacks.push(function() {
				$document.off('product/changeSimple', _onSimpleChange);
			});
		},

		setMetaTag: function (identifier, data) {
			if (identifier === 'keyword') {
				identifier = 'keywords';
			}

			var metaTag = $(`head meta[name="${identifier}"]`);

			if (typeof metaTag !== "undefined" && metaTag.length > 0) {
				metaTag.attr('content', data);
			} else {
				var head = $('head');
				head.append(`<meta name="${identifier}" content="${data}">`);
			}
		},

		setMetaJsonLd: function (simple) {
			var metaJson = this.metaBlock.find('#metaJsonTypeProduct') || null;
			var optionPrices = window.optionsPrice || null;

			if (metaJson && metaJson[0]) {
				try {
					var json = JSON.parse(metaJson[0].textContent.trim());

					// simple attributes
					if (simple.sku) {
						json.sku = simple.sku;
					}
					if (simple.product_name) {
						json.name = simple.product_name;
					}

					// Itemprop Offer
					if (json.offers) {
						if(simple.price && simple.price.currency_code) {
							json.offers.priceCurrency = simple.price.currency_code;
						}
						if (optionPrices && optionPrices.productPrice) {
							json.offers.price = optionPrices.productPrice + optionPrices.optionPrices.config.price;
						}
						if (simple.stock_status) {
							json.offers.availability = simple.stock_status === 'low' ? 'https://schema.org/OutOfStock' : 'https://schema.org/InStock';
						}
					}

					// gallery image
					var galleryItem = this.mediaBlock.getElements('gallery-list-item--visible');
					var primaryImage = galleryItem ? galleryItem.find('img') : null;
					if (primaryImage) {
						json.image = primaryImage.attr('src');
					}

					simpleJson = JSON.stringify(json);
					// replace Json LD content with simple json
					metaJson.attr('content', simpleJson);
					metaJson.text(JSON.stringify(json, null, 2));

				} catch (error) {
					console.error("Error parsing Meta JSON-LD:", error);
				}
			}
		},

		initializeEnergyLabel: function() {
			this._energyLabelContainer = this.block.getElements('functions-container');
			this._energyLabelTmpl = _.template(EnergyLabelHtml);

			var _onSimpleChange = _.bind(function (e, simple) {
				this.setEnergyLabel(simple.energy_label || null);
			}, this);

			var currentSimple = _.get(window, 'spConfig.__currentSimple');
			if (currentSimple) {
				_onSimpleChange(null, currentSimple);
			}

			$document.on('product/changeSimple', _onSimpleChange);
			this.destructorCallbacks.push(function() {
				$document.off('product/changeSimple', _onSimpleChange);
			});
		},

		setEnergyLabel: function(energyLabel) {
			const bemClass = BEM.buildBlockClassname('energy-label');
			var currentBlock = this._energyLabelContainer.find(`.${bemClass}`);

			if (energyLabel) {
				var newBlock = $(this._energyLabelTmpl(energyLabel));

				if (currentBlock.length > 0) {
					currentBlock.replaceWith(newBlock);
				} else {
					this._energyLabelContainer.prepend(newBlock);
				}
			}
		},

		destructor: function() {
			_.forEach(this.destructorCallbacks, function(fn) { fn.call(this); }, this);
			this.destructorCallbacks = [];
			this.spConfig = null;
			window.spConfig = null;
			delete window.spConfig;
		}
	});
});

define('rebrush/components/product/type/configurable/addtocartbutton',[
    'jquery',
    'lodash',
    'cartware/components/Registry',
    'cartware/components/_base',
    'cartware/jquery/bem',
    'bootstrap-popover',
    'bootstrap-button'
], function(
    $,
    _,
    Registry,
    Component,
    BEM
) {
    return Component.create({
        $init: function() {
            this.addToCartButtonBlock = BEM.block('product-detail-add-to-cart');
            this.popoverTemplate = _.template(this.addToCartButtonBlock.getElements('popover-template').html());
            Registry().retrieve('product.view.configurable.spConfig', _.bind(function(spConfig) {
                this.spConfig = spConfig;
                this.initializePopover();
                this.initializeEvents();
                this.updateButtonStatus();
            }, this));
        },

        initializeEvents: function() {
            var popoverShown = false;

            _.forEach(this.spConfig.settings, _.bind(function(select) {
                select.on('change', _.bind(this.updateButtonStatus, this));
            }, this));

            this.addToCartButtonBlock.on('mouseenter', _.bind(function() {
                if (!popoverShown && this.addToCartButtonBlock.bemHasModifier('disabled')) {
                    popoverShown = true;
                    this.addToCartButtonBlock.popover('show');
                }
            }, this));

            this.addToCartButtonBlock.on('mouseleave', _.bind(function() {
                this.addToCartButtonBlock.popover('hide');
                popoverShown = false;
            }, this));
        },

        initializePopover: function() {
            this.addToCartButtonBlock.popover({
                title: '',
                content: _.bind(function() {
                    var settings = this.spConfig.settings,
                        emptySetting = null;

                    if (!settings.length) {
                        return '';
                    }

                    $.each(settings, function() {
                        if (this.disabled || this.value === '') {
                            emptySetting = this;
                            return false;
                        }
                    });

                    if (!emptySetting) {
                        return '';
                    }

                    return this.popoverTemplate({ conf : $(emptySetting).data('label') });
                }, this),
                trigger: 'manual',
                placement: function() {
                    if ($(window).width() >= 768) {
                        return 'left';
                    }

                    return 'top';
                }
            });
        },

        updateButtonStatus: function() {
            if (this.isFullyConfigured()) {
                this.addToCartButtonBlock.bemRemoveModifier('disabled');
                this.addToCartButtonBlock.popover('hide');
            } else {
                this.addToCartButtonBlock.bemAddModifier('disabled');
            }
        },

        isFullyConfigured: function() {
            return !_.some(this.spConfig.state, function(value) { return value === false; });
        },

        destructor: function() {
            this.spConfig = null;
        }
    });
});
define('rebrush/components/checkout/packstation-validation',[
    'jquery',
    'cartware/components/_base'
], function ($) {
    'use strict';

    /** -------------------------------
     *  Constants
     * ------------------------------- */
    const VALIDATION_PREFIX = 'validate-packstation-';
    const DEFAULT_ERROR_MSG = 'Invalid input';
    const STREET_DHL_GROUP_SELECTOR = '.street-dhl';

    const SELECTORS = {
        packstationCustomerNumber: `${STREET_DHL_GROUP_SELECTOR} #street2`,
        packstationDhlNumber: `${STREET_DHL_GROUP_SELECTOR} #street1`
    };

    function PackstationValidation(options = {}) {
        const packstation = window.packstation || {};
        const isPackstation = packstation.isPackstation || false;
        const rules = packstation.rules || {};
        const country = (packstation.country || 'DE').toLowerCase();

        this.register(rules, country);
        this.selectors = Object.assign({}, SELECTORS, options);

        if (isPackstation) {
            this.toggleValidationClasses(true);
        }
    }

    PackstationValidation.prototype.toggleValidationClasses = function(show) {
        const $packstationCustomerNumber = $(this.selectors.packstationCustomerNumber);
        const $packstationDhlNumber = $(this.selectors.packstationDhlNumber);

        $packstationCustomerNumber.toggleClass(`${VALIDATION_PREFIX}post_number`, show);
        $packstationDhlNumber.toggleClass(`${VALIDATION_PREFIX}dhl_number`, show);
    };

    PackstationValidation.prototype.register = function (rules, countryCode) {
        if (!rules) return;
        const country = (countryCode || 'DE').toLowerCase();
        if (!rules[country]) return;

        Object.keys(rules[country]).forEach(ruleCode => {
            const { pattern, message } = rules[country][ruleCode];
            const regex = new RegExp(pattern, 'i');

            Validation.add(
                VALIDATION_PREFIX + ruleCode,
                message || DEFAULT_ERROR_MSG,
                function (value) {
                    if (Validation.get('IsEmpty').test(value)) return false;
                    return regex.test(value);
                }
            );
        });
    };

    return PackstationValidation;
});
define('rebrush/components/checkout/postcode-validator',[
    'lodash',
    'jquery',
    'cartware_checkout',
    'cartware/components/_base'
], function(
    _,
    $,
    CartwareCheckout,
    Component
) {
    // Constants
    const VALIDATOR_NAME = 'validate-zip-by-country';
    const DEFAULT_ERROR_MESSAGE = 'Please enter a valid zip code.';

    return Component.create({

        $init: function() {
            this.initZipValidator();
        },

        /**
         * Initialize postcode validator
         * Adds a custom Validation rule to validate zip code based on country
         */
        initZipValidator: function() {
            if (!window.Validation || !window.postcodeValidationConfig) {
                return;
            }

            Validation.add(
                VALIDATOR_NAME,
                DEFAULT_ERROR_MESSAGE,
                this._validateZipByCountry.bind(this)
            );
        },

        /**
         * Core validation function
         * @param {string} value - postcode value
         * @param {HTMLElement} element - input element
         * @returns {boolean} - true if valid, false otherwise
         */
        _validateZipByCountry: function(value, element) {
            try {
                if (!element || !element.id) return true;

                const countryRule = this._getCountryRule(element);
                if (!countryRule) return true;

                value = this._applyAutoSpace(value, element, countryRule);

                if (!this._validatePattern(value, countryRule)) {
                    this._setValidationError(element, countryRule);
                    return false;
                }

                return true;

            } catch (e) {
                return true;
            }
        },

        /**
         * Get country rule from config based on the input element
         *
         * @param {HTMLElement} element
         * @returns {Object|null}
         */
        _getCountryRule: function(element) {
            const $input = $(element);
            const form = $input.closest('form');

            if (!form.length) return null;

            const postcodeName = $input.attr('name');
            const countryName = postcodeName.replace(/(?:(\[)postcode(\])|^postcode$)/, '$1country_id$2');

            const $countryInput = form.find(`[name="${countryName.replace(/(")/g, '\\$1')}"]`);

            if (!$countryInput.length || !$countryInput.val()) return null;

            const countryCode = ($countryInput.val() || '').toLowerCase();
            return window.postcodeValidationConfig[countryCode] || null;
        },

        /**
         * Apply auto-insert space logic
         * @param {string} value
         * @param {HTMLElement} element
         * @param {Object} rule
         * @returns {string} modified value
         */
        _applyAutoSpace: function(value, element, rule) {
            if (rule.replace_space === "true" && rule.allowed_pattern) {
                const allowedRe = new RegExp(rule.allowed_pattern, 'i');
                if (allowedRe.test(value)) {
                    const delimiter = rule.delimiter || ' ';
                    const newValue = value.replace(allowedRe, '$1' + delimiter + '$2');
                    element.value = newValue;
                    return newValue;
                }
            }
            return value;
        },

        /**
         * Validate value against the regex pattern
         * @param {string} value
         * @param {Object} rule
         * @returns {boolean} true if valid
         */
        _validatePattern: function(value, rule) {
            if (!rule.pattern) return true;
            const pattern = new RegExp(rule.pattern, 'i');
            return pattern.test(value);
        },

        /**
         * Set the error message on the validator
         * @param {HTMLElement} element
         * @param {Object} rule
         */
        _setValidationError: function(element, rule) {
            const validator = Validation.get(VALIDATOR_NAME);
            if (!validator) return;

            if (rule.message) {
                const example = rule.example || '';
                validator.error = rule.message.replace('%s', example);
            } else {
                validator.error = DEFAULT_ERROR_MESSAGE;
            }
        }

    });
});

define('rebrush/components/customer/address/address-packstation',[
    'jquery',
    'cartware/components/_base',
    'cartware/jquery/bem',
    'rebrush/components/checkout/packstation-validation'
], function (
    $,
    Component,
    BEM,
    PackstationValidation
) {

    var PACKSTATION_TYPE = 'packingstation';

    var selectors = {
        block: 'customer-address-edit',
        addressType: 'input[name="address_type_ui"]',
        formNormal: '#address-form-normal',
        formPackstation: '#address-form-packstation',
        hiddenInput: 'input[name="ship_to_packstation"]',
        packstationCustomerNumber: '#street2',
        packstationDhlNumber: '#street1'
    };

    return Component.create({

        selectors: selectors,

        init: function () {
            this.block = BEM.block(this.selectors.block);

            this.addressType = this.block.find(this.selectors.addressType);
            this.formNormal = this.block.find(this.selectors.formNormal);
            this.formPackstation = this.block.find(this.selectors.formPackstation);
            this.hiddenInput = this.block.find(this.selectors.hiddenInput);

            this.initValidation();
            this.bindEvents();
            this.updateInitialState();
        },

        initValidation: function () {
            var packstationConfig = window.packstation || {};

            this.validation = new PackstationValidation({
                packstationCustomerNumber: this.selectors.packstationCustomerNumber,
                packstationDhlNumber: this.selectors.packstationDhlNumber
            });

            this.validation.register(
                packstationConfig.rules || {},
                (packstationConfig.country || 'DE').toLowerCase()
            );
        },

        bindEvents: function () {
            this.addressType.on('change', this.handleAddressTypeChange.bind(this));
        },

        updateInitialState: function () {
            var selectedValue = this.addressType.filter(':checked').val();
            this.toggleForms(selectedValue === PACKSTATION_TYPE);
        },

        handleAddressTypeChange: function (event) {
            var selected = $(event.currentTarget);
            var isPackstation = selected.val() === PACKSTATION_TYPE;

            this.addressType.prop('checked', false);
            selected.prop('checked', true);

            this.toggleForms(isPackstation);
        },

        toggleForms: function (showPackstation) {
            if (showPackstation) {
                this.formNormal
                    .addClass('hidden')
                    .find(':input')
                    .prop('disabled', true);

                this.formPackstation
                    .removeClass('hidden')
                    .find(':input')
                    .prop('disabled', false);

                this.hiddenInput.val(1);
            } else {
                this.formNormal
                    .removeClass('hidden')
                    .find(':input')
                    .prop('disabled', false);

                this.formPackstation
                    .addClass('hidden')
                    .find(':input')
                    .prop('disabled', true);

                this.hiddenInput.val(0);
            }

            if (this.validation) {
                this.validation.toggleValidationClasses(showPackstation);
            }
        }
    });
});

define('rebrush/components/contentbridge/tabs',[
	'jquery',
	'cartware/components/_base'
], function(
	$,
	Component
) {
	return Component.create({
		blocks:			null,

		$init: function() {
			this.blocks = $('ul.tabs');
			this.blocks.each(function() {
				var block = $(this),
					container = $('<div class="b_tabs b_tabs--close-mobile">'),
					list = $('<dl class="b_tabs__list">').appendTo(container);

				block.find("> li").each(function(i) {
					var li = $(this),
						title = li.find('.tab-title').html() || (Translator.translate('Tab') + ' ' + (i + 1)),
						dt = $('<dt class="b_tabs__tab">').appendTo(list),
						titleSpan = $('<span>').html(title).appendTo(dt),
						dd = $('<dd class="b_tabs__tab-content">').appendTo(list).html(li.html());

					if (0 === i) {
						dt.addClass('b_tabs__tab--active');
						dd.addClass('b_tabs__tab-content--active');
					}
				});

				block.after(container);
				block.remove();
			});
		}
	});
});
define('rebrush/components/checkout/advises',[
    'lodash',
    'jquery',
    'cartware_checkout',
    'cartware/components/_base',
    'cartware/jquery/bem'
], function(
    _,
    jQuery,
    CartwareCheckout,
    Component,
    BEM
) {

    return Component.create({
        blocks:	null,

        $init: function() {
            CartwareCheckout.prototype.updateProgress = _.wrap(CartwareCheckout.prototype.updateProgress, _.bind(function(fun, json) {
                fun(json);
                this.updateHeadline();
            }, this));

            this.updateHeadline();
        },

        updateHeadline: function() {
            var headlineBlock = BEM.block('page-title').filter(':first'),
                headline = headlineBlock.find('h1');

            var progressbarBlock = BEM.block('opcheckout-progress-bar'),
                currentStep = progressbarBlock.getElements('step').filter(':modifier(current)'),
                currentLabel = currentStep.find('.' + BEM.buildElementClassname('opcheckout-progress-bar', 'label'));

            headline.text(currentLabel.text());
        }
    });

});
define('rebrush/components/checkout/password/onpage-validator',[
    'lodash',
    'jquery',
    'cartware_checkout',
    'cartware/components/_base',
    'cartware/jquery/bem'
], function(
    _,
    $,
    CartwareCheckout,
    Component,
    BEM
) {
    var $document = $(document.body);

    return Component.create({
        registrationBlock:	null,
        rules: [
            {
                pattern: '[A-Z]',
                target: 'password-hint-upperCase'
            },
            {
                pattern: '[a-z]',
                target: 'password-hint-lowerCase'
            },
            {
                pattern: '[0-9]',
                target: 'password-hint-numericChar'
            },
            {
                pattern: '[\x21-\x2F\x3A-\x40\x5B-\x60\x7B-\x7E]',
                target: 'password-hint-specialChar'
            }
        ],

        $init: function() {
            this.registrationBlock = BEM.block('checkout-account-registration-form');
            this.bindEvents();
        },

        bindEvents: function() {
            $document.on('keyup', '.' + BEM.buildElementClassname('checkout-account-registration-form', 'password-input'),
                _.bind(this.validatePassword, this));
        },

        validatePassword: function() {
            var passwordInput = $('.' + BEM.buildElementClassname('checkout-account-registration-form', 'password-input')),
                password = passwordInput.val(),
                hintLength = $('.password-hint-length'),
                hintNames = $('.password-hint-noNames'),
                firstName = $('input[name="shipping[firstname]"]').val(),
                lastName = $('input[name="shipping[lastname]"]').val(),
                self = this,
                hasError = false;

            if (firstName || lastName) {
                var namesPattern = '^((?!' + (firstName || '') + (firstName && lastName ? '|' : '') + (lastName || '') + ').)*$';
                if (new RegExp(namesPattern).test(password)) {
                    self.toggleValidationHint(hintNames, 'success');
                } else {
                    self.toggleValidationHint(hintNames, 'error');
                    hasError = true;
                }
            }


            if (password.length >= 8) {
                this.toggleValidationHint(hintLength, 'success');
            } else {
                this.toggleValidationHint(hintLength, 'error');
                hasError = true;
            }

            this.rules.each(function(e) {
                var target = $('.' + e.target);
                if (new RegExp(e.pattern).test(password)) {
                    self.toggleValidationHint(target, 'success');
                } else {
                    self.toggleValidationHint(target, 'error');
                    hasError = true;
                }
            });

            if (password.length && hasError) {
                passwordInput.removeClass('validation-passed');
                passwordInput.addClass('validation-failed');
            } else {
                passwordInput.addClass('validation-passed');
                passwordInput.removeClass('validation-failed');
            }

            this.toggleConfirmPasswordContainer(password);
        },

        toggleValidationHint: function(e, state) {
            var icon = e.find('i:first');
            switch (state) {
                case 'success':
                    if (e.bemHasModifier('red')) {
                        e.bemRemoveModifier('red').bemAddModifier('green');
                    }
                    if (icon.hasClass('icon-message-error')) {
                        icon.removeClass('icon-message-error').addClass('icon-added-to-cart-check');
                    }
                    break;
                case 'error':
                    if (e.bemHasModifier('green')) {
                        e.bemRemoveModifier('green').bemAddModifier('red');
                    }
                    if (icon.hasClass('icon-added-to-cart-check')) {
                        icon.removeClass('icon-added-to-cart-check').addClass('icon-message-error');
                    }
                    break;
            }
        },

        toggleConfirmPasswordContainer: function(password) {
            var containerPasswordConfirm = $('.' + BEM.buildElementClassname('checkout-account-registration-form', 'confirm-password-container')),
                inputPasswordConfirm = $('input[name="register[password_confirm]"]'),
                inputCaptcha = $('input[name="captcha[code]"]'),
                inputPrivacyPolicyAgreement = $('input[data-agreement-code="has_accepted_privacy_policy"]'),
                captchaWrapper = $('.' + BEM.buildElementClassname('cartware-checkout', 'captcha-wrapper'));

            if (password.length > 0) {
                if (containerPasswordConfirm && containerPasswordConfirm.hasClass('hidden')) {
                    containerPasswordConfirm.removeClass('hidden');
                }
                if (captchaWrapper && captchaWrapper.hasClass('hidden')) {
                    captchaWrapper.removeClass('hidden');
                }
                if (inputPasswordConfirm && !inputPasswordConfirm.hasClass('validate-cpassword-checkout')) {
                    inputPasswordConfirm.addClass('validate-cpassword-checkout');
                }
                if (inputCaptcha && !inputCaptcha.hasClass('required-entry')) {
                    inputCaptcha.addClass('required-entry');
                }
                if (inputPrivacyPolicyAgreement && !inputPrivacyPolicyAgreement.hasClass('required-entry')) {
                    inputPrivacyPolicyAgreement.addClass('required-entry');
                }
            } else {
                if (containerPasswordConfirm && !containerPasswordConfirm.hasClass('hidden')) {
                    containerPasswordConfirm.addClass('hidden');
                }
                if (captchaWrapper && !captchaWrapper.hasClass('hidden')) {
                    captchaWrapper.addClass('hidden');
                }
                if (inputPasswordConfirm && inputPasswordConfirm.hasClass('validate-cpassword-checkout')) {
                    inputPasswordConfirm.removeClass('validate-cpassword-checkout');
                }
                if (inputCaptcha && inputCaptcha.hasClass('required-entry')) {
                    inputCaptcha.removeClass('required-entry');
                }
                if (inputPrivacyPolicyAgreement && inputPrivacyPolicyAgreement.hasClass('required-entry')) {
                    inputPrivacyPolicyAgreement.removeClass('required-entry');
                }
            }
        },
    });
});

define('rebrush/components/checkout/closing-loop',[
    'lodash',
    'jquery',
    'cartware_checkout',
    'cartware/components/_base',
    'cartware/components/Dispatcher',
    'cartware/jquery/bem',
    'gigaset/spinner',
], function(
    _,
    $,
    CartwareCheckout,
    Component,
    ComponentDispatcher,
    BEM,
    Spinner
) {
    var MODIFIER_HIDE = 'hide',
        $document = $(document.body);

    return Component.create({
        block:	null,
        showMoreCta: null,
        unfoldContent: null,

        $init: function() {
            this.block = BEM.block('closing-the-loop');
            this.showMoreCta = this.block.getElements('show-more-cta');

            this._bindEvents();
        },

        _bindEvents: function () {
            $document.on('click', '.' + BEM.buildElementClassname('closing-the-loop', 'show-more-cta'), _.bind(this.onClickShowMore, this));
            $document.on('click', '.' + BEM.buildElementClassname('closing-the-loop', 'checkbox'), _.bind(this.onClickCheckbox, this));
        },

        onClickShowMore: function() {
            this.triggerShowMoreContent();
        },
        
        onClickCheckbox: function () {
            var checkbox = $('.' + BEM.buildElementClassname('closing-the-loop', 'checkbox')),
                url = (!checkbox.is(':checked') ? checkbox.data('remove-url') : checkbox.data('addto-url'));

            this._sendAjaxRequest(url, null, _.bind(this.onClickCheckboxCallback, this));
        },

        onClickCheckboxCallback: function (response, status, xhr) {
            const cartTotalsTable = BEM.block('cart-totals');
            const checkoutReviewTotalsTable = $('#checkout-review-table').find('tfoot');
            const totalsResponse = response.totals || null;
            const priceLabel = response.price_label || null;

            if (!totalsResponse) {
                return;
            }

            let totalsTableElement = cartTotalsTable.length ? cartTotalsTable : checkoutReviewTotalsTable;

            if (totalsTableElement.length) {
                totalsTableElement.replaceWith(totalsResponse);
            }

            if (priceLabel) {
                $('.' + BEM.buildElementClassname('closing-the-loop', 'price')).replaceWith(priceLabel).css('font-weight', 600);
            }
        },

        triggerShowMoreContent: function () {
            var unfoldContent = $('.' + BEM.buildElementClassname('closing-the-loop', 'unfold-content'));

            if (unfoldContent.bemHasModifier(MODIFIER_HIDE)) {
                unfoldContent.show();
                unfoldContent.bemRemoveModifier(MODIFIER_HIDE);
            } else {
                unfoldContent.hide();
                unfoldContent.bemAddModifier(MODIFIER_HIDE);
            }
        },

        /**
         * Send ajax request
         *
         * @param url
         * @param data
         * @param callback
         * @param onSuccessCallback
         * @private
         */
        _sendAjaxRequest: function (url, data, callback, onSuccessCallback) {
            data = data || {};

            Spinner.add($document);

            // add isAjax param to avoid caching problems
            if (url.indexOf('isAjax=1') === -1 && typeof data === 'string' && data.indexOf('isAjax=1') === -1) {
                url += url.indexOf('?') === -1 ? '?isAjax=1' : '&isAjax=1';
            }

            url = (this.isSecure === true && url.indexOf('http://') === 0) ? url.replace('http://', 'https://') : url;

            $.ajax(url, {
                method: 'POST',
                data: data
            }).then(_.bind(function (response, status, xhr) {

                if (typeof callback === 'function') {
                    callback(response, status, xhr);
                }

                Spinner.remove();

                if ((response.error || response.success === false)) {
                    console.log(response.error);
                    alert(response.error || 'An error occured. Please try again later.');
                } else {
                    if (typeof onSuccessCallback === 'function') {
                        onSuccessCallback(response, status, xhr);
                    }
                }
            }, this));
        },
    });

});

define('text!shoptheme/templates/customer/address.html',[],function () { return '<div class="b_customer-address">\r\n\t<span class="b_customer-address__line b_customer-address__line--first"><%= firstname %> <%= lastname %></span>\r\n\t<% if ("string" === typeof company) { %>\r\n\t\t<span class="b_customer-address__line"><%= company %></span>\r\n\t<% } %>\r\n\t<span class="b_customer-address__line">\r\n\t\t<% if ("string" === typeof street) { %>\r\n\t\t\t<%= street %>\r\n\t\t<% } else { %>\r\n\t\t\t<%= street.join("<br>") %>\r\n\t\t<% } %>\r\n\t</span>\r\n\t<span class="b_customer-address__line">\r\n\t\t<% if ("undefined" !== typeof city) { %>\r\n\t\t\t<%= city %>,\r\n\t\t<% } %>\r\n\t\t<% if ("undefined" !== typeof region) { %>\r\n\t\t\t<%= region %>\r\n\t\t<% } %>\r\n\t\t<% if ("undefined" !== typeof postcode) { %>\r\n\t\t\t<%=postcode %>\r\n\t\t<% } %>\r\n\t</span>\r\n\t<% if ("string" === typeof country) { %>\r\n\t\t<span class="b_customer-address__line"><%= country %></span>\r\n\t<% } %>\r\n</div>';});

define('rebrush/components/checkout/address/qaddress',[
	'jquery',
	'lodash',
	'cartware_checkout',
	'cartware/components/_base',
	'text!shoptheme/templates/magnific-popup/default.html',
	'text!shoptheme/templates/customer/address.html',
	'cartware/jquery/bem',
	'json2',
	'magnific-popup',
], function(
	$,
	_,
	Checkout,
	Component,
	PopupHtml,
	AddressHtml,
	BEM
) {
	var popupTemplate = null,
		addressTemplate = null,
		messageTemplate = null;

	return Component.create({
		$init: function() {
			this._adviseShipping();
			this._adviseBilling();
		},

		_adviseShipping: function() {
			this._setupAdvise('shipping');
		},

		_adviseBilling: function() {
			this._setupAdvise('billing');
		},

		_setupAdvise: function(type) {
			var self = this;

			Checkout().retrieve(type, function(obj) {
				var onSave = obj.onSave;
				obj.onSave = function(transport) {
					var response = JSON.parse(transport.responseText);

					if (response.error && 'qaddress' in response) {
						$.magnificPopup.open({
							items: {
								src: self._renderPopup({
									title: Translator.translate('Address validation'),
									body: self._renderTemplate({
										message: response.message,
										address: response.qaddress ? self._renderAddress(response.qaddress) : ''
									})
								}),
								type: 'inline'
							},

							callbacks: {
								open: function() {
									var self = this;
									this.content.on('click', '[data-mfp-close]', function(e) {
										self.currentTarget = e.currentTarget;
										self.close();
									});
								},

								close: function() {
									this.block = BEM.block('qaddress-validation-result');
									let $currentTarget = this.currentTarget ? $(this.currentTarget) : this.block;
									let acceptButtonClicked = $currentTarget.hasClass('popup-correct-address-trigger');

									// If accept button was clicked, write the response address
									if (acceptButtonClicked) {
										self._writeAddress(type, response.qaddress);
										if (type === 'shipping') {
											shipping.save();
										}
										else if (type === 'billing') {
											billing.save();
										}
									}
								}
							}
						});

						return;
					}

					return onSave.apply(this, arguments);
				};
			});
		},

        _writeAddress: function(type, address) {
            _.forEach(address, function(value, key) {
                var node;

                if (key === 'address_type_ui') return;

                if (_.isArray(value)) {
                    _.forEach(value, function(val, i) {
                        if (key === 'street') {
                            // Remove all HTML tags including <br>
                            val = val.replace(/<[^>]*>/g, ' ').trim();

                            // Split street and house number if combined
                            if (value.length === 1) {
                                var parts = val.match(/^(.*?)(?:\s+(\d+\w*))?$/);
                                if (parts) {
                                    val = i === 0 ? parts[1] : (parts[2] || '');
                                }
                            }
                        }

                        node = $($('[name="' + type + '[' + key + '][]"]').get(i));
                        node.val(val);
                    });
                } else {
                    node = $('[name="' + type + '[' + key + ']"]');
                    node.val(value);
                }
            });
        },

		_renderPopup: function(data) {
			if (null === popupTemplate) {
				popupTemplate = _.template(PopupHtml);
			}

			return popupTemplate(data);
		},

		_renderAddress: function(data) {
			if (null === addressTemplate) {
				addressTemplate = _.template(AddressHtml);
			}

			return addressTemplate(data);
		},

		_renderTemplate: function(data) {
			const templateId = 'qaddress-popup-template';

			// Check if the template needs to be reloaded
			if (messageTemplate === null || messageTemplate.source !== templateId) {
				const templateContent = $(`#${templateId}`).html();
				messageTemplate = _.template(templateContent);
				messageTemplate.source = templateContent;
			}

			return this._renderData(messageTemplate(data), data);
		},

		_renderData: function(templateHtml, data) {
			let $popupContainer = $(templateHtml);
			$popupContainer.find('.message-container').html(data.message);

			// Check if the address is provided
			if (data.address) {
				$popupContainer.find('.address-container').html(data.address);
			} else {
				$popupContainer.find('.address-buttons')
					.css({'border-top': 'none', 'padding-top': '0'});
			}
			return $('<div />').append($popupContainer.clone()).html();
		}
	});
});
define('rebrush/components/checkout/reepay/checkout',[
    'lodash',
    'jquery',
    'cartware/components/_base',
    'cartware/components/Registry',
    'cartware/jquery/bem',
    'gigaset/spinner'
], function(
    _,
    $,
    Component,
    Registry,
    BEM,
    Spinner
) {
    return Component.create({
        blockIdentifier: 'reepay-payment',
        config: _.get(window, 'gigaset.checkout', {}),
        reepayContainerId: 'rp_container',
        reepay: null,

        $init: function() {
            this.mode = this.config.mode ? Number(this.config.mode) : null;
            this.modeMapping = this.config.mode_mapping || null;
            this.block = BEM.block('reepay-payment') || null;

            if (!this.block || !this.mode) {
                return;
            }

            this.paymentSessionId = this.block.data('session-id') ? window.atob(this.block.data('session-id')) : null;

            if (typeof this.paymentSessionId === 'undefined' || !this.paymentSessionId) {
                return;
            }

            this.initRepayObject();

            if (!this.reepay) {
                return;
            }

            this.addEventHandlers();
        },

        addEventHandlers: function() {
            switch (this.mode) {
                case 1: // embedded
                    this.reepay.addEventHandler(Reepay.Event.Accept, (data) => this.acceptHandler(data));
                    this.reepay.addEventHandler(Reepay.Event.Error, (data) => this.errorHandler(data));
                    this.reepay.addEventHandler(Reepay.Event.Cancel, (data) => this.cancelHandler(data));
                    this.reepay.addEventHandler(Reepay.Event.Close, (data) => this.closeHandler(data));

                    var cta = $('.' + BEM.buildElementClassname(this.blockIdentifier, 'cta', 'cancel'));
                    cta.on('click', _.bind(this.cancelBtnHandler , this));

                    break;
                case 2: // modal
                    this.reepay.addEventHandler(Reepay.Event.Accept, (data) => this.acceptHandler(data));
                    this.reepay.addEventHandler(Reepay.Event.Error, (data) => this.errorHandler(data));
                    this.reepay.addEventHandler(Reepay.Event.Cancel, (data) => this.cancelHandler(data));
                    this.reepay.addEventHandler(Reepay.Event.Close, (data) => this.closeHandler(data));

                    var cta = $('.' + BEM.buildElementClassname(this.blockIdentifier, 'cta', 'submit'));
                    cta.on('click', _.bind(this.submitBtnHandler , this));

                    break;
                case 3: // window
                    break;
                default:
                    break;
            }
        },

        errorHandler: function (data) {
            var url = this.config.url.error || '';

            if (!url) {
                return;
            }

            this._sendAjaxRequest(url, data, null, (response) => {
                if (response && response.redirect_url) {
                    window.location = response.redirect_url;
                }
            });
        },

        acceptHandler: function (data) {
            var url = this.config.url.accept || '';

            if (!url) {
                return;
            }

            if((data.error === undefined || !data.error || data.error === "error.session.INVOICE_ALREADY_PAID" || data.error === "invoice_already_settled" ) && data.invoice) {
                this._sendAjaxRequest(url, data, null, (response) => {
                    if (response && response.redirect_url) {
                        window.location = response.redirect_url;
                    }
                });
            }
        },

        cancelHandler: function (data) {
            var url = this.config.url.cancel || '';

            if (!url) {
                return;
            }

            this._sendAjaxRequest(url, data, null, (response) => {
                if (response && response.redirect_url) {
                    window.location = response.redirect_url;
                }
            });
        },

        closeHandler: function (data) {
            // not in use yet
            var url = this.config.url.close || '';
        },

        submitBtnHandler: function () {
            this.reepay.show(self.paymentSessionId);
        },

        cancelBtnHandler: function(e) {
            const cta = $(e.currentTarget);
            const orderId = this.block.data('order-id') ? window.atob(this.block.data('order-id')) : null;

            this.cancelHandler({
                id: this.paymentSessionId,
                invoice: orderId
            });
        },

        initRepayObject: function() {
            switch (this.mode) {
                case 1: // embedded
                    this.reepay = new Reepay.EmbeddedCheckout(this.paymentSessionId , this.reepayContainerId);
                    break;
                case 2: // modal
                    this.reepay = new Reepay.ModalCheckout(this.paymentSessionId);
                    break;
                case 3: // window
                    this.reepay = new Reepay.WindowCheckout(this.paymentSessionId);
                    break;
                default:
                    this.reepay = null;
                    break;
            }
        },

        /**
         * Send ajax request
         *
         * @param url
         * @param data
         * @param callback
         * @param onSuccessCallback
         * @private
         */
        _sendAjaxRequest: function (url, data, callback, onSuccessCallback) {
            Spinner.add();
            data = data || {};

            data._isAjax = 1;

            // add isAjax param to avoid caching problems
            if (url.indexOf('isAjax=1') === -1 && typeof data === 'string' && data.indexOf('isAjax=1') === -1) {
                url += url.indexOf('?') === -1 ? '?isAjax=1' : '&isAjax=1';
            }

            url = (this.isSecure === true && url.indexOf('http://') === 0) ? url.replace('http://', 'https://') : url;

            $.ajax(url, {
                method: 'POST',
                data: data
            }).then(_.bind(function (response, status, xhr) {
                Spinner.remove();

                // reponse callback
                if (typeof callback === 'function') {
                    callback(response, status, xhr);
                }

                // got error
                if (!response || status !== 'success') {
                    console.log('Ajax: error');
                    return;
                }

                // response callback on success
                if (typeof onSuccessCallback === 'function') {
                    onSuccessCallback(response, status, xhr);
                }
            }, this));
        },
    });

});

define('rebrush/components/checkout/packstation',[
    'jquery',
    'lodash',
    'cartware_checkout',
    'cartware/components/_base',
    'cartware/jquery/bem',
    'rebrush/components/checkout/packstation-validation'
], function (
    $,
    _,
    CartwareCheckout,
    Component,
    BEM,
    PackstationValidation
) {
    'use strict';

    /** -------------------------------
     *  Constants
     * ------------------------------- */
    var STREET_DHL_GROUP_SELECTOR = '.street-dhl';
    var PACKSTATION_TYPE = 'packingstation';

    var SELECTORS = {
        block: 'cartware-checkout',

        addressType: 'input[name="shipping[address_type_ui]"]',
        shippingAddressId: 'input[name="shipping_address_id"]',

        formNormal: '#co-shipping-form-normal',
        formPackstation: '#co-shipping-form-packstation',

        packstationStreet: `${STREET_DHL_GROUP_SELECTOR} #shipping\\:street0`,
        packstationDhlNumber: `${STREET_DHL_GROUP_SELECTOR} #shipping\\:street1`,
        packstationCustomerNumber: `${STREET_DHL_GROUP_SELECTOR} #shipping\\:street2`,

        packstationRadio: '#address_type_packingstation',
        selectedIsPackstation: '#selected_is_packstation',

        billingDifferent: '#billing\\:different_from_shipping',
        billingBlock: '#billing-address'
    };

    return Component.create({
        selectors: SELECTORS,

        $init() {
            this.isDebugMode = _.get(window, 'gigaset.featureflags.developer_mode', false);

            this.validation = new PackstationValidation({
                packstationCustomerNumber: SELECTORS.packstationCustomerNumber,
                packstationDhlNumber: SELECTORS.packstationDhlNumber
            });

            CartwareCheckout.ready(this.onCheckoutReady.bind(this));
        },

        /** ----------------------------
         *  Debug logger (dev mode only)
         * ----------------------------- */
        _debug: function () {
            if (!this.isDebugMode || !window.console) {
                return;
            }
            console.log.apply(console, arguments);
        },

        /** --------------
         *  Checkout ready
         * --------------- */
        onCheckoutReady() {
            this.block = BEM.block(this.selectors.block);

            this.cacheElements();
            this.bindEvents();
            this.setInitialState();
            this.registerValidation();

            this._debug('[Packstation.js] Checkout ready');

            this.syncPackstationState();
        },

        /** -------------------------------
         *  Cache DOM
         * ------------------------------- */
        cacheElements() {
            var s = this.selectors;

            this.$document = $(document);

            this.$addressType = this.block.find(s.addressType);
            this.$formNormal = this.block.find(s.formNormal);
            this.$formPackstation = this.block.find(s.formPackstation);

            this.$billingDifferent = $(s.billingDifferent);
            this.$billingBlock = $(s.billingBlock);
            this.$selectedIsPackstation = $(s.selectedIsPackstation);
        },

        /** -------------------------------
         *  Register validation rules
         * ------------------------------- */
        registerValidation: function () {
            var packstation = window.packstation || {};

            this.validation.register(
                packstation.rules || {},
                (packstation.country || 'DE').toLowerCase()
            );
        },

        /** --------------------
         *  Bind checkout events
         * --------------------- */
        bindEvents() {
            var self = this;

            this.$document.on('change', this.selectors.shippingAddressId, function () {
                var $input = $(this);
                var isNew = $input.data('checkout-address-action') === 'new';

                self._debug('[Packstation.js] Address changed', { isNew: isNew });

                if (isNew) {
                    self.updatePackstationFromNew();
                } else {
                    self.updatePackstationFromExisting();
                }
            });

            this.$addressType.on('change', function (e) {
                var $current = $(e.currentTarget);
                var isPackstation = $current.val() === PACKSTATION_TYPE;

                self._debug(
                    '[Packstation.js] Address type changed',
                    { isPackstation: isPackstation }
                );

                self.$addressType.prop('checked', false);
                $current.prop('checked', true);

                self.toggleForms(isPackstation);
                self.updatePackstationFromNew();
            });
        },

        /** -------------
         *  Initial state
         * -------------- */
        setInitialState: function () {
            this.toggleForms(
                this.$addressType.filter(':checked').val() === PACKSTATION_TYPE
            );
        },

        /** --------------------------------------
         *  Show / hide normal & packstation forms
         * --------------------------------------- */
        toggleForms: function (showPackstation) {
            if (showPackstation) {
                this.$formNormal
                    .addClass('hidden')
                    .find(':input')
                    .prop('disabled', true);

                this.$formPackstation
                    .removeClass('hidden')
                    .find(':input')
                    .prop('disabled', false);
            } else {
                this.$formNormal
                    .removeClass('hidden')
                    .find(':input')
                    .prop('disabled', false);

                this.$formPackstation
                    .addClass('hidden')
                    .find(':input')
                    .prop('disabled', true);
            }

            if (this.validation) {
                this.validation.toggleValidationClasses(showPackstation);
            }
        },

        /** -----------------------
         *  sync Pack Station State
         * ------------------------ */
        syncPackstationState: function () {
            if (this._hasExistingAddressSelected()) {
                this.updatePackstationFromExisting();
                return;
            }

            this.updatePackstationFromNew();
        },

        /** ------------------------------------
         *  Update state from "new address" form
         * ------------------------------------- */
        updatePackstationFromNew() {
            if (this._hasExistingAddressSelected()) {
                return;
            }

            var isPackstation = $(this.selectors.packstationRadio).is(':checked');

            this._debug(
                '[Packstation.js] Update from new address',
                { isPackstation: isPackstation }
            );

            this.$selectedIsPackstation.val(isPackstation ? 1 : 0);
            this.toggleBillingForPackstation(isPackstation);
        },

        /** -------------------------------------------
         *  Update state from existing customer address
         * -------------------------------------------- */
        updatePackstationFromExisting: function () {
            var $selected = $(this.selectors.shippingAddressId + ':checked');

            if (!$selected.length ||
                typeof $selected.data('is-packstation') === 'undefined') {
                return;
            }

            var isPackstation = parseInt($selected.data('is-packstation'), 10) || 0;

            this._debug(
                '[Packstation.js] Update from existing address',
                { isPackstation: isPackstation }
            );

            this.$selectedIsPackstation.val(isPackstation);
            this.toggleBillingForPackstation(!!isPackstation);
        },

        /** --------------------------------------------------
         *  Check if an existing customer address is selected
         * --------------------------------------------------- */
        _hasExistingAddressSelected: function () {
            var $selected = $(this.selectors.shippingAddressId + ':checked');

            return $selected.length &&
                typeof $selected.data('is-packstation') !== 'undefined';
        },

        /** -------------------------------
         *  Billing logic
         * ------------------------------- */
        toggleBillingForPackstation: function (isPackstation) {
            if (!this.$billingDifferent.length || !this.$billingBlock.length) {
                return;
            }

            var label = this.$billingDifferent.closest('.checkbox').find('label');

            if (isPackstation) {
                window.billing.setDifferentFromShipping(true);
                this.$billingBlock.show();

                this.$billingDifferent.prop({
                    checked: true,
                    disabled: true,
                    readOnly: true
                });

                label.addClass('checked');
            } else {
                window.billing.setDifferentFromShipping(false);
                this.$billingBlock.hide();

                this.$billingDifferent.prop({
                    checked: false,
                    disabled: false,
                    readOnly: false
                });

                label.removeClass('checked');
                this.$billingDifferent.removeClass('checked');
            }
        },
    });
});

define('rebrush/components/ui/anchor-navigation',[
	'jquery',
	'lodash',
	'cartware/components/_base',
	'cartware/jquery/bem',
	'json!shoptheme/etc/breakpoints.json',
	'enquire'
], function(
	$,
	_,
	Component,
	BEM,
	BreakPoints,
	enquire
) {

	var BLOCK_ANCHOR_NAVIGATION = 'anchor-navigation';
	var BLOCK_HEADLINES = 'heading-container';

	var SELECTOR_CONTAINER = '.b_main-container__col1-col-main';

	return Component.create({

		$init: function() {
			var self = this;

			this.container = $(SELECTOR_CONTAINER);
			this.headlines = this.container.find('h2');

			this.space = $('.b_hero-teaser__anchor-container');

			this.dom = this.buildDom();

			this.space.prepend(this.dom);

			this.listBlock = BEM.block(BLOCK_ANCHOR_NAVIGATION);
			this.list = this.listBlock.getElements('list');
			this.links = this.listBlock.getElements('link');

			this.crawl();
			this.bindEvents();
		},

		buildDom: function() {
			var domContainer 	= $('<div class="' + BEM.buildBlockClassname(BLOCK_ANCHOR_NAVIGATION) + '" />'),
				titleContainer 	= $('<div class="' + BEM.buildElementClassname(BLOCK_ANCHOR_NAVIGATION, 'title-container') + '" />'),
				listTitle 		= $('<strong class="' + BEM.buildElementClassname(BLOCK_ANCHOR_NAVIGATION, 'title') + '">' + Translator.translate('In this article:') + '</strong>'),
				listContainer 	= $('<ol class="' + BEM.buildElementClassname(BLOCK_ANCHOR_NAVIGATION, 'list') + '" />');

			return domContainer.append(titleContainer.append(listTitle)).append(listContainer);
		},

		crawl: function() {
			var self = this;

			this.container.find(this.headlines).each(function(i) {
				self.addItem($(this), i);
			});
		},

		addItem: function(item, i) {
			var cleanRegExp = new RegExp('^' + (i+1) + '\\.\\s*'),
				linkTitle = _.trim(item.text().replace(cleanRegExp, '')),
				listItem = $('<li class="' + BEM.buildElementClassname(BLOCK_ANCHOR_NAVIGATION, 'list-item') + '" />'),
			//link = $('<a href='#' title='' + linkTitle + '' class='' +  BEM.buildElementClassname(BLOCK_ANCHOR_NAVIGATION, 'link') + ''>' + linkTitle + '</a>');
				link = $('<a href="#" title="' + _.escape(linkTitle) + '" class="' +  BEM.buildElementClassname(BLOCK_ANCHOR_NAVIGATION, 'link') + '">' + _.escape(linkTitle) + '</a>');

			link.data('content-item', item);

			this.list.append(listItem.append(link));
		},

		bindEvents: function() {
			var self = this;
			this.list.on('click', '.' + BEM.buildElementClassname(BLOCK_ANCHOR_NAVIGATION, 'link'), function(e) {
				e.preventDefault();
				self.onLinkClick(e.target);
			});
		},

		onLinkClick: function(link) {
			var item = $(link).data('content-item');
			var position = item.offset().top;

			$('html, body').animate({
				scrollTop: position - 15
			}, 500);
		}


	});

});
define('shoptheme/components/paypal/express/review',[
	"jquery",
	"cartware/components/_base",
	"gigaset/spinner"
], function(
	$,
	Component,
	Spinner
) {
	return Component.create({
		init: function() {
			var checkoutReviewForm = new VarienForm('order_review_form'),
				shippingMethodForm = new VarienForm('shipping_method_form');
			
			var _initialize = OrderReviewController.prototype.initialize,
				__validateForm = OrderReviewController.prototype._validateForm;

			OrderReviewController.prototype._submitOrder = function(){
				if (this._canSubmitOrder && (this.reloadByShippingSelect || this._validateForm())) {
					this.form.submit();
					this._updateOrderSubmit(true);
					this._toggleButton(this.formSubmit, true, true);
					if (this._ubpdateOrderButton) {
						this._ubpdateOrderButton.addClassName('no-checkout');
						this._ubpdateOrderButton.setStyle({opacity:.5});
					}
					// if (this._pleaseWait) {
					// 	this._pleaseWait.show();
					// }
					Spinner.add();

					return;
				}
				this._updateOrderSubmit(true);
			};

			OrderReviewController.prototype.initialize = function(orderForm, orderFormSubmit, shippingSelect, shippingSubmitForm, shippingResultId, shippingSubmit) {
				// _initialize.apply(this, arguments);

				if (!orderForm) {
					return;
				}
				this.form = orderForm;
				if (orderFormSubmit) {
					this.formSubmit = orderFormSubmit;
					var submitOrder = this._submitOrder.bind(this);
					var onSubmitButtonClick = function() {
						if (shippingMethodForm.validator.validate() && checkoutReviewForm.validator.validate()) {
							submitOrder();
						}
					};
					Event.observe(orderFormSubmit, 'click', onSubmitButtonClick);
				}

				if (shippingSubmitForm) {
					this.reloadByShippingSelect = true;
					if (shippingSubmitForm && shippingSelect) {
						this.shippingSelect = shippingSelect;
						Event.observe(
							shippingSelect,
							'change',
							this._submitShipping.bindAsEventListener(this, shippingSubmitForm.action, shippingResultId)
						);
						this._updateOrderSubmit(false);
					} else {
						this._canSubmitOrder = true;
					}
				} else {
					Form.getElements(this.form).each(this._bindElementChange, this);

					if (shippingSelect && $(shippingSelect)) {
						this.shippingSelect = $(shippingSelect).id;
						this.shippingMethodsContainer = $(this.shippingSelect).up();
					} else {
						this.shippingSelect = shippingSelect;
					}
					this._updateOrderSubmit(false);
				}

				var self = this;
				$(this.shippingSelect).change(function(e){
					self.shippingSelect.blur();
					self._submitShipping(e, shippingSubmitForm.action, shippingResultId);
				});
			};

			OrderReviewController.prototype._submitShipping = function(event, url, resultId){
				this._updateOrderSubmit(true);
				// if (this._pleaseWait) {
				// 	this._pleaseWait.show();
				// }
				if ('' != this.shippingSelect.value) {
					Spinner.add();
					new Ajax.Updater(resultId, url, {
						parameters: {isAjax:true, shipping_method:this.shippingSelect.value},
						onComplete: function() {
							// if (this._pleaseWait) {
							// 	this._pleaseWait.hide();
							// }
							Spinner.remove();
						}.bind(this),
						onSuccess: this._onSubmitShippingSuccess.bind(this),
						evalScripts: true
					});
				}
			};

			OrderReviewController.prototype._toggleButton = function(button, disable, force){
				if (force !== true) {
					return;
				}

				button.disabled = disable;
				button.removeClassName('no-checkout');
				button.setStyle({opacity:1});
				if (disable) {
					button.addClassName('no-checkout');
					button.setStyle({opacity:.5});
				}
			};

			// Moved from template
			PayPalExpressAjax = new OrderReviewController($('#order_review_form').get(0), $('#review_button').get(0),
				$('#shipping_method').get(0), $('#shipping_method_form').get(0), 'details-reload'
			);
			PayPalExpressAjax.addPleaseWait($('#review-please-wait').get(0));
		}
	});
});
define('rebrush/components/blocks/image-comparer',[
    'jquery',
    'cartware/components/_base',
    'cartware/jquery/bem',
    'lodash'
], function(
    $,
    Component,
    BEM,
    _
) {
    var BLOCK_NAME = 'image-comparer';

    function Comparer(block) {
        this.block = BEM.block(block);
        this.$refs = {
            leftImage: this.block.getElements('left-image'),
            rightImage: this.block.getElements('right-image'),
            controls: this.block.getElements('controls'),
            container: this.block.getElements('container')
        };
        this.moveCallback = _.bind(this.updateSliderPosition, this);
        this.positionPct = 0.5;
        this.imageWidth = 0;
        this.hover = false;
        this.bindEvents();
    }

    Comparer.prototype = {
        bindEvents: function() {
            $(window).on('resize', _.debounce(_.bind(this.getAndSetImageWidth, this), 100));

            // for mobile
            this.$refs.container.on('touchstart', _.bind(this.startSliding, this));
            window.addEventListener && window.addEventListener('touchend', _.bind(this.finishSliding, this));

            // for desktop
            if (this.hover) {
                this.$refs.container.on('mouseenter', _.bind(this.startSliding, this));
                this.$refs.container.on('mouseleave', _.bind(this.finishSliding, this));
            } else {
                this.$refs.container.on('mousedown', _.bind(this.startSliding, this));
                $(window).on('mouseup', _.bind(this.finishSliding, this));
            }
        },

        getAndSetImageWidth: function() {
            this.imageWidth = this.$refs.rightImage.get(0).getBoundingClientRect().width;

            var leftImageStyle = {
                clip:
                    'rect(auto, ' +
                    this.imageWidth * this.positionPct +
                    'px, auto, auto)',
            };
            var controlsStyle = {
                cursor: !this.hover && 'ew-resize',
                'margin-left': this.imageWidth * this.positionPct + 'px'
            };

            this.$refs.leftImage.css(leftImageStyle);
            this.$refs.controls.css(controlsStyle);
        },
        startSliding: function(event) {
            // Prevent default behavior other than mobile scrolling
            if (!('touches' in event)) {
                event.preventDefault();
            }
            // Slide the image even if you just click or tap (not drag)
            this.updateSliderPosition(event);

            $(window).on('mousemove', this.moveCallback);
            window.addEventListener && window.addEventListener('touchmove', this.moveCallback);
        },
        finishSliding: function() {
            $(window).off('mousemove', this.moveCallback);
            window.addEventListener && window.removeEventListener('touchmove', this.moveCallback);
        },
        updateSliderPosition: function(event) {
            var e = event || window.event;

            // Calc Cursor Position from the left edge of the viewport
            var cursorXfromViewport = e.touches ? e.touches[0].pageX : e.pageX;

            // Calc Cursor Position from the left edge of the window (consider any page scrolling)
            var cursorXfromWindow = cursorXfromViewport - window.pageXOffset;

            // Calc Cursor Position from the left edge of the image
            var imagePosition = this.$refs.rightImage.get(0).getBoundingClientRect();
            var pos = cursorXfromWindow - imagePosition.left;

            // Set minimum and maximum values ​​to prevent the slider from overflowing
            var minPos = 0;
            var maxPos = this.imageWidth - 2;

            if (pos < minPos) {
                pos = minPos;
            }

            if (pos > maxPos) {
                pos = maxPos;
            }

            this.positionPct = pos / this.imageWidth;
            this.getAndSetImageWidth();
        }
    };

    return Component.create({
        $init: function() {
            BEM.block(BLOCK_NAME).each(function() {
                new Comparer(this);
            });
        }
    });
});
define('rebrush/components/blocks/landingpages/router/video',[
    'jquery',
    'cartware/components/_base',
    'cartware/jquery/bem',
    'lodash'
], function(
    $,
    Component,
    BEM,
    _
) {
    var BLOCK_NAME = 'lp-router-video';

    function Video(block) {
        this.block = BEM.block(block);
        this.$refs = {
            overlay: this.block.getElements('overlay'),
            video: this.block.getElements('video')
        };

        this.bindEvents();
    }

    Video.prototype = {
        bindEvents: function() {
            this.$refs.overlay.on('click', _.bind(this.play, this));
            this.$refs.video.on('ended', _.bind(this.reset, this));
            this.$refs.video.on('click', _.bind(this.pause, this));
        },

        play: function() {
            this.$refs.overlay.hide();
            this.$refs.video.get(0).play();
        },

        pause: function() {
            this.$refs.overlay.show();
            this.$refs.video.get(0).pause();
        },

        reset: function() {
            this.$refs.overlay.show();
        }
    };

    return Component.create({
        $init: function() {
            BEM.block(BLOCK_NAME).each(function() {
                new Video(this);
            });
        }
    });
});
define('rebrush/components/blocks/landingpages/router/app',[
    'jquery',
    'cartware/components/_base',
    'cartware/jquery/bem',
    'lodash',
    'scrollreveal'
], function(
    $,
    Component,
    BEM,
    _,
    ScrollReveal
) {
    var BLOCK_NAME = 'lp-router-app';

    function App(block) {
        this.block = BEM.block(block);
        this.elements = this.block.getElements('element');

        this.initScrollreveal();
    }

    App.prototype = {

        initScrollreveal: function() {
            if (this.elements.length) {
                ScrollReveal().reveal(this.elements, {
                    reset: false,
                    scale: 1,
                    opacity: 0
                });
            }

            this.block.css('opacity', 1);
        }

    };

    return Component.create({
        $init: function() {
            BEM.block(BLOCK_NAME).each(function() {
                new App(this);
            });
        }
    });
});
define('rebrush/components/product/view/wheretobuy',[
	'jquery',
	'lodash',
	'cartware/components/_base',
	'cartware/jquery/bem',
	'cartware/components/Registry',
	'cartware/components/Config',
	'modernizr',
	'elevatezoom',
	'cartware-slider',
	'magnific-popup'
], function(
	$,
	_,
	Component,
	BEM,
	Registry,
	Config
) {
	return Component.create({
		slotBlock:				null,
		currentBlock:			null,
		spConfig:				null,
		cachedBlocks:			null,
		stockNotificationBlock: null,
		onDestroyCallbacks: 	null,
		dividerHtml: 			null,

		constructor: function() {
			this.cachedBlocks = {};
			this.onDestroyCallbacks = [_.bind(function() {
				this.spConfig = null;
				this.cachedBlocks = null;
			}, this)];
		},

		$init: function() {
			this.dividerHtml = '<div class="' + BEM.buildElementClassname('gigaset-wheretobuy-addto', 'divider') + '">' +
				'<span class="' + BEM.buildElementClassname('gigaset-wheretobuy-addto', 'divider-label') + '">' + Translator.translate('or') + '</span></div>';
			this.initializeConfigurable();
			this.initializeSimple();
		},

		initializeConfigurable: function() {
			this.slotBlock = BEM.block('gigaset-wheretobuy-configurable-slot');

			Registry().retrieve('product.view.configurable.spConfig', _.bind(function(spConfig) {
				this.spConfig = spConfig;
				this.initializeSimpleObservation();
			}, this));
		},

		initializeSimple: function() {
			this.currentBlock = BEM.block('gigaset-wheretobuy-addto');
			var button = this.currentBlock.getElements('button');

			// this.rearrangeBlocks();

			// var onClick = _.bind(function(e) {
			// 	e.preventDefault();
			//
			// 	$.magnificPopup.open({
			// 		mainClass: 'mfp--where-to-buy test',
			// 		fixedContentPos: true,
			// 		items: {
			// 			src: button.attr('href'),
			// 			type: 'iframe',
			// 			mainClass: 'mfp--where-to-buy test',
			// 		},
			// 		callbacks: {
			// 			open: function () {
			// 				if ($.magnificPopup.instance.isOpen) {
			// 					console.log('into2');
			// 				}
			// 				var popup = $('.popup-body');
			// 				console.log(popup);
			// 			}
			// 		}
			// 	});
			// }, this);
			//
			// if ($.magnificPopup.instance.isOpen) {
			// 	console.log('into');
			// }

			var openPopup = _.bind(function(e) {
				this.openPopup(e, button);
			}, this);

			button.on('click', openPopup);

			// button.on('click', this.test());
			this.onDestroyCallbacks.push(_.bind(function() {
				button.off('click', openPopup);
			}, this));
		},

		openPopup: function(e, button) {
			e.preventDefault();

			$.magnificPopup.open({
				mainClass: 'mfp--where-to-buy',
				fixedContentPos: true,
				items: {
					src: button.attr('href'),
					type: 'iframe',
				}
			});

			if ($.magnificPopup.instance.isOpen) {
				var mfpContainer = $('.mfp-wrap');
				if (mfpContainer.length) {
					var classList = mfpContainer.get(0).classList;
					if (!classList.contains('mfp--where-to-buy')) {
						mfpContainer.addClass('mfp--where-to-buy');
					}
					if (classList.contains('container-size')) {
						mfpContainer.removeClass('container-size');
					}
				}
			}
		},

		rearrangeBlocks: function() {
			this.stockNotificationBlock = BEM.block('product-stocknotification');

			if (this.stockNotificationBlock.length && this.currentBlock.length) {
				var visibility = this.stockNotificationBlock.css('display');
				if (visibility !== 'none' && visibility !== 'hidden') {
					// this.currentBlock.append(this.dividerHtml);
					this.currentBlock.after(this.stockNotificationBlock);
				}
			}
		},

		initializeSimpleObservation: function() {
			var currentSimple = _.get(this.spConfig, '__currentGallerySimple');
			var _onChangeSimple = _.bind(function(e, simple) {
				this.changeSimple(simple);
			}, this);

			$(document).on('product/changeSimple', _onChangeSimple);

			this.onDestroyCallbacks.push(_.bind(function() {
				$(document).off('product/changeSimple', _onChangeSimple);
			}, this));

			if (currentSimple) {
				_onChangeSimple(null, currentSimple);
			}
		},

		changeSimple: function(simple) {
			var html = _.get(simple, 'gigaset_wheretobuy.addto', null),
				id = _.get(simple, 'id', 'undefined', null),
				block = _.get(this.cachedBlocks, id, null);

			this.slotBlock.empty();

			if (null !== html) {
				if (null === block) {
					block = $('<div />').html(html);
					_.set(this.cachedBlocks, id, block);
					this.slotBlock.append(block);
					this.initializeSimple();
				} else {
					this.slotBlock.append(block);
				}
			}
		},

		destructor: function() {
			this.spConfig = null;
		}
	});
});
/*!
 * jQuery Form Plugin
 * version: 4.3.0
 * Requires jQuery v1.7.2 or later
 * Project repository: https://github.com/jquery-form/form

 * Copyright 2017 Kevin Morris
 * Copyright 2006 M. Alsup

 * Dual licensed under the LGPL-2.1+ or MIT licenses
 * https://github.com/jquery-form/form#license

 * This library is free software; you can redistribute it and/or
 * modify it under the terms of the GNU Lesser General Public
 * License as published by the Free Software Foundation; either
 * version 2.1 of the License, or (at your option) any later version.
 * This library is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the GNU
 * Lesser General Public License for more details.
 */
/* global ActiveXObject */

/* eslint-disable */
(function (factory) {
	if (typeof define === 'function' && define.amd) {
		// AMD. Register as an anonymous module.
		define('jquery-form',['jquery'], factory);
	} else if (typeof module === 'object' && module.exports) {
		// Node/CommonJS
		module.exports = function( root, jQuery ) {
			if (typeof jQuery === 'undefined') {
				// require('jQuery') returns a factory that requires window to build a jQuery instance, we normalize how we use modules
				// that require this pattern but the window provided is a noop if it's defined (how jquery works)
				if (typeof window !== 'undefined') {
					jQuery = require('jquery');
				}
				else {
					jQuery = require('jquery')(root);
				}
			}
			factory(jQuery);
			return jQuery;
		};
	} else {
		// Browser globals
		factory(jQuery);
	}

}(function ($) {
/* eslint-enable */
	'use strict';

	/*
		Usage Note:
		-----------
		Do not use both ajaxSubmit and ajaxForm on the same form. These
		functions are mutually exclusive. Use ajaxSubmit if you want
		to bind your own submit handler to the form. For example,

		$(document).ready(function() {
			$('#myForm').on('submit', function(e) {
				e.preventDefault(); // <-- important
				$(this).ajaxSubmit({
					target: '#output'
				});
			});
		});

		Use ajaxForm when you want the plugin to manage all the event binding
		for you. For example,

		$(document).ready(function() {
			$('#myForm').ajaxForm({
				target: '#output'
			});
		});

		You can also use ajaxForm with delegation (requires jQuery v1.7+), so the
		form does not have to exist when you invoke ajaxForm:

		$('#myForm').ajaxForm({
			delegation: true,
			target: '#output'
		});

		When using ajaxForm, the ajaxSubmit function will be invoked for you
		at the appropriate time.
	*/

	var rCRLF = /\r?\n/g;

	/**
	 * Feature detection
	 */
	var feature = {};

	feature.fileapi = $('<input type="file">').get(0).files !== undefined;
	feature.formdata = (typeof window.FormData !== 'undefined');

	var hasProp = !!$.fn.prop;

	// attr2 uses prop when it can but checks the return type for
	// an expected string. This accounts for the case where a form
	// contains inputs with names like "action" or "method"; in those
	// cases "prop" returns the element
	$.fn.attr2 = function() {
		if (!hasProp) {
			return this.attr.apply(this, arguments);
		}

		var val = this.prop.apply(this, arguments);

		if ((val && val.jquery) || typeof val === 'string') {
			return val;
		}

		return this.attr.apply(this, arguments);
	};

	/**
	 * ajaxSubmit() provides a mechanism for immediately submitting
	 * an HTML form using AJAX.
	 *
	 * @param	{object|string}	options		jquery.form.js parameters or custom url for submission
	 * @param	{object}		data		extraData
	 * @param	{string}		dataType	ajax dataType
	 * @param	{function}		onSuccess	ajax success callback function
	 */
	$.fn.ajaxSubmit = function(options, data, dataType, onSuccess) {
		// fast fail if nothing selected (http://dev.jquery.com/ticket/2752)
		if (!this.length) {
			log('ajaxSubmit: skipping submit process - no element selected');

			return this;
		}

		/* eslint consistent-this: ["error", "$form"] */
		var method, action, url, isMsie, iframeSrc, $form = this;

		if (typeof options === 'function') {
			options = {success: options};

		} else if (typeof options === 'string' || (options === false && arguments.length > 0)) {
			options = {
				'url'      : options,
				'data'     : data,
				'dataType' : dataType
			};

			if (typeof onSuccess === 'function') {
				options.success = onSuccess;
			}

		} else if (typeof options === 'undefined') {
			options = {};
		}

		method = options.method || options.type || this.attr2('method');
		action = options.url || this.attr2('action');

		url = (typeof action === 'string') ? $.trim(action) : '';
		url = url || window.location.href || '';
		if (url) {
			// clean url (don't include hash vaue)
			url = (url.match(/^([^#]+)/) || [])[1];
		}
		// IE requires javascript:false in https, but this breaks chrome >83 and goes against spec.
		// Instead of using javascript:false always, let's only apply it for IE.
		isMsie = /(MSIE|Trident)/.test(navigator.userAgent || '');
		iframeSrc = (isMsie && /^https/i.test(window.location.href || '')) ? 'javascript:false' : 'about:blank'; // eslint-disable-line no-script-url

		options = $.extend(true, {
			url       : url,
			success   : $.ajaxSettings.success,
			type      : method || $.ajaxSettings.type,
			iframeSrc : iframeSrc
		}, options);

		// hook for manipulating the form data before it is extracted;
		// convenient for use with rich editors like tinyMCE or FCKEditor
		var veto = {};

		this.trigger('form-pre-serialize', [this, options, veto]);

		if (veto.veto) {
			log('ajaxSubmit: submit vetoed via form-pre-serialize trigger');

			return this;
		}

		// provide opportunity to alter form data before it is serialized
		if (options.beforeSerialize && options.beforeSerialize(this, options) === false) {
			log('ajaxSubmit: submit aborted via beforeSerialize callback');

			return this;
		}

		var traditional = options.traditional;

		if (typeof traditional === 'undefined') {
			traditional = $.ajaxSettings.traditional;
		}

		var elements = [];
		var qx, a = this.formToArray(options.semantic, elements, options.filtering);

		if (options.data) {
			var optionsData = $.isFunction(options.data) ? options.data(a) : options.data;

			options.extraData = optionsData;
			qx = $.param(optionsData, traditional);
		}

		// give pre-submit callback an opportunity to abort the submit
		if (options.beforeSubmit && options.beforeSubmit(a, this, options) === false) {
			log('ajaxSubmit: submit aborted via beforeSubmit callback');

			return this;
		}

		// fire vetoable 'validate' event
		this.trigger('form-submit-validate', [a, this, options, veto]);
		if (veto.veto) {
			log('ajaxSubmit: submit vetoed via form-submit-validate trigger');

			return this;
		}

		var q = $.param(a, traditional);

		if (qx) {
			q = (q ? (q + '&' + qx) : qx);
		}

		if (options.type.toUpperCase() === 'GET') {
			options.url += (options.url.indexOf('?') >= 0 ? '&' : '?') + q;
			options.data = null;	// data is null for 'get'
		} else {
			options.data = q;		// data is the query string for 'post'
		}

		var callbacks = [];

		if (options.resetForm) {
			callbacks.push(function() {
				$form.resetForm();
			});
		}

		if (options.clearForm) {
			callbacks.push(function() {
				$form.clearForm(options.includeHidden);
			});
		}

		// perform a load on the target only if dataType is not provided
		if (!options.dataType && options.target) {
			var oldSuccess = options.success || function(){};

			callbacks.push(function(data, textStatus, jqXHR) {
				var successArguments = arguments,
					fn = options.replaceTarget ? 'replaceWith' : 'html';

				$(options.target)[fn](data).each(function(){
					oldSuccess.apply(this, successArguments);
				});
			});

		} else if (options.success) {
			if ($.isArray(options.success)) {
				$.merge(callbacks, options.success);
			} else {
				callbacks.push(options.success);
			}
		}

		options.success = function(data, status, xhr) { // jQuery 1.4+ passes xhr as 3rd arg
			var context = options.context || this;		// jQuery 1.4+ supports scope context

			for (var i = 0, max = callbacks.length; i < max; i++) {
				callbacks[i].apply(context, [data, status, xhr || $form, $form]);
			}
		};

		if (options.error) {
			var oldError = options.error;

			options.error = function(xhr, status, error) {
				var context = options.context || this;

				oldError.apply(context, [xhr, status, error, $form]);
			};
		}

		if (options.complete) {
			var oldComplete = options.complete;

			options.complete = function(xhr, status) {
				var context = options.context || this;

				oldComplete.apply(context, [xhr, status, $form]);
			};
		}

		// are there files to upload?

		// [value] (issue #113), also see comment:
		// https://github.com/malsup/form/commit/588306aedba1de01388032d5f42a60159eea9228#commitcomment-2180219
		var fileInputs = $('input[type=file]:enabled', this).filter(function() {
			return $(this).val() !== '';
		});
		var hasFileInputs = fileInputs.length > 0;
		var mp = 'multipart/form-data';
		var multipart = ($form.attr('enctype') === mp || $form.attr('encoding') === mp);
		var fileAPI = feature.fileapi && feature.formdata;

		log('fileAPI :' + fileAPI);

		var shouldUseFrame = (hasFileInputs || multipart) && !fileAPI;
		var jqxhr;

		// options.iframe allows user to force iframe mode
		// 06-NOV-09: now defaulting to iframe mode if file input is detected
		if (options.iframe !== false && (options.iframe || shouldUseFrame)) {
			// hack to fix Safari hang (thanks to Tim Molendijk for this)
			// see: http://groups.google.com/group/jquery-dev/browse_thread/thread/36395b7ab510dd5d
			if (options.closeKeepAlive) {
				$.get(options.closeKeepAlive, function() {
					jqxhr = fileUploadIframe(a);
				});

			} else {
				jqxhr = fileUploadIframe(a);
			}

		} else if ((hasFileInputs || multipart) && fileAPI) {
			jqxhr = fileUploadXhr(a);

		} else {
			jqxhr = $.ajax(options);
		}

		$form.removeData('jqxhr').data('jqxhr', jqxhr);

		// clear element array
		for (var k = 0; k < elements.length; k++) {
			elements[k] = null;
		}

		// fire 'notify' event
		this.trigger('form-submit-notify', [this, options]);

		return this;

		// utility fn for deep serialization
		function deepSerialize(extraData) {
			var serialized = $.param(extraData, options.traditional).split('&');
			var len = serialized.length;
			var result = [];
			var i, part;

			for (i = 0; i < len; i++) {
				// #252; undo param space replacement
				serialized[i] = serialized[i].replace(/\+/g, ' ');
				part = serialized[i].split('=');
				// #278; use array instead of object storage, favoring array serializations
				result.push([decodeURIComponent(part[0]), decodeURIComponent(part[1])]);
			}

			return result;
		}

		// XMLHttpRequest Level 2 file uploads (big hat tip to francois2metz)
		function fileUploadXhr(a) {
			var formdata = new FormData();

			for (var i = 0; i < a.length; i++) {
				formdata.append(a[i].name, a[i].value);
			}

			if (options.extraData) {
				var serializedData = deepSerialize(options.extraData);

				for (i = 0; i < serializedData.length; i++) {
					if (serializedData[i]) {
						formdata.append(serializedData[i][0], serializedData[i][1]);
					}
				}
			}

			options.data = null;

			var s = $.extend(true, {}, $.ajaxSettings, options, {
				contentType : false,
				processData : false,
				cache       : false,
				type        : method || 'POST'
			});

			if (options.uploadProgress) {
				// workaround because jqXHR does not expose upload property
				s.xhr = function() {
					var xhr = $.ajaxSettings.xhr();

					if (xhr.upload) {
						xhr.upload.addEventListener('progress', function(event) {
							var percent = 0;
							var position = event.loaded || event.position;			/* event.position is deprecated */
							var total = event.total;

							if (event.lengthComputable) {
								percent = Math.ceil(position / total * 100);
							}

							options.uploadProgress(event, position, total, percent);
						}, false);
					}

					return xhr;
				};
			}

			s.data = null;

			var beforeSend = s.beforeSend;

			s.beforeSend = function(xhr, o) {
				// Send FormData() provided by user
				if (options.formData) {
					o.data = options.formData;
				} else {
					o.data = formdata;
				}

				if (beforeSend) {
					beforeSend.call(this, xhr, o);
				}
			};

			return $.ajax(s);
		}

		// private function for handling file uploads (hat tip to YAHOO!)
		function fileUploadIframe(a) {
			var form = $form[0], el, i, s, g, id, $io, io, xhr, sub, n, timedOut, timeoutHandle;
			var deferred = $.Deferred();

			// #341
			deferred.abort = function(status) {
				xhr.abort(status);
			};

			if (a) {
				// ensure that every serialized input is still enabled
				for (i = 0; i < elements.length; i++) {
					el = $(elements[i]);
					if (hasProp) {
						el.prop('disabled', false);
					} else {
						el.removeAttr('disabled');
					}
				}
			}

			s = $.extend(true, {}, $.ajaxSettings, options);
			s.context = s.context || s;
			id = 'jqFormIO' + new Date().getTime();
			var ownerDocument = form.ownerDocument;
			var $body = $form.closest('body');

			if (s.iframeTarget) {
				$io = $(s.iframeTarget, ownerDocument);
				n = $io.attr2('name');
				if (!n) {
					$io.attr2('name', id);
				} else {
					id = n;
				}

			} else {
				$io = $('<iframe name="' + id + '" src="' + s.iframeSrc + '" />', ownerDocument);
				$io.css({position: 'absolute', top: '-1000px', left: '-1000px'});
			}
			io = $io[0];


			xhr = { // mock object
				aborted               : 0,
				responseText          : null,
				responseXML           : null,
				status                : 0,
				statusText            : 'n/a',
				getAllResponseHeaders : function() {},
				getResponseHeader     : function() {},
				setRequestHeader      : function() {},
				abort                 : function(status) {
					var e = (status === 'timeout' ? 'timeout' : 'aborted');

					log('aborting upload... ' + e);
					this.aborted = 1;

					try { // #214, #257
						if (io.contentWindow.document.execCommand) {
							io.contentWindow.document.execCommand('Stop');
						}
					} catch (ignore) {}

					$io.attr('src', s.iframeSrc); // abort op in progress
					xhr.error = e;
					if (s.error) {
						s.error.call(s.context, xhr, e, status);
					}

					if (g) {
						$.event.trigger('ajaxError', [xhr, s, e]);
					}

					if (s.complete) {
						s.complete.call(s.context, xhr, e);
					}
				}
			};

			g = s.global;
			// trigger ajax global events so that activity/block indicators work like normal
			if (g && $.active++ === 0) {
				$.event.trigger('ajaxStart');
			}
			if (g) {
				$.event.trigger('ajaxSend', [xhr, s]);
			}

			if (s.beforeSend && s.beforeSend.call(s.context, xhr, s) === false) {
				if (s.global) {
					$.active--;
				}
				deferred.reject();

				return deferred;
			}

			if (xhr.aborted) {
				deferred.reject();

				return deferred;
			}

			// add submitting element to data if we know it
			sub = form.clk;
			if (sub) {
				n = sub.name;
				if (n && !sub.disabled) {
					s.extraData = s.extraData || {};
					s.extraData[n] = sub.value;
					if (sub.type === 'image') {
						s.extraData[n + '.x'] = form.clk_x;
						s.extraData[n + '.y'] = form.clk_y;
					}
				}
			}

			var CLIENT_TIMEOUT_ABORT = 1;
			var SERVER_ABORT = 2;

			function getDoc(frame) {
				/* it looks like contentWindow or contentDocument do not
				 * carry the protocol property in ie8, when running under ssl
				 * frame.document is the only valid response document, since
				 * the protocol is know but not on the other two objects. strange?
				 * "Same origin policy" http://en.wikipedia.org/wiki/Same_origin_policy
				 */

				var doc = null;

				// IE8 cascading access check
				try {
					if (frame.contentWindow) {
						doc = frame.contentWindow.document;
					}
				} catch (err) {
					// IE8 access denied under ssl & missing protocol
					log('cannot get iframe.contentWindow document: ' + err);
				}

				if (doc) { // successful getting content
					return doc;
				}

				try { // simply checking may throw in ie8 under ssl or mismatched protocol
					doc = frame.contentDocument ? frame.contentDocument : frame.document;
				} catch (err) {
					// last attempt
					log('cannot get iframe.contentDocument: ' + err);
					doc = frame.document;
				}

				return doc;
			}

			// Rails CSRF hack (thanks to Yvan Barthelemy)
			var csrf_token = $('meta[name=csrf-token]').attr('content');
			var csrf_param = $('meta[name=csrf-param]').attr('content');

			if (csrf_param && csrf_token) {
				s.extraData = s.extraData || {};
				s.extraData[csrf_param] = csrf_token;
			}

			// take a breath so that pending repaints get some cpu time before the upload starts
			function doSubmit() {
				// make sure form attrs are set
				var t = $form.attr2('target'),
					a = $form.attr2('action'),
					mp = 'multipart/form-data',
					et = $form.attr('enctype') || $form.attr('encoding') || mp;

				// update form attrs in IE friendly way
				form.setAttribute('target', id);
				if (!method || /post/i.test(method)) {
					form.setAttribute('method', 'POST');
				}
				if (a !== s.url) {
					form.setAttribute('action', s.url);
				}

				// ie borks in some cases when setting encoding
				if (!s.skipEncodingOverride && (!method || /post/i.test(method))) {
					$form.attr({
						encoding : 'multipart/form-data',
						enctype  : 'multipart/form-data'
					});
				}

				// support timout
				if (s.timeout) {
					timeoutHandle = setTimeout(function() {
						timedOut = true; cb(CLIENT_TIMEOUT_ABORT);
					}, s.timeout);
				}

				// look for server aborts
				function checkState() {
					try {
						var state = getDoc(io).readyState;

						log('state = ' + state);
						if (state && state.toLowerCase() === 'uninitialized') {
							setTimeout(checkState, 50);
						}

					} catch (e) {
						log('Server abort: ', e, ' (', e.name, ')');
						cb(SERVER_ABORT);				// eslint-disable-line callback-return
						if (timeoutHandle) {
							clearTimeout(timeoutHandle);
						}
						timeoutHandle = undefined;
					}
				}

				// add "extra" data to form if provided in options
				var extraInputs = [];

				try {
					if (s.extraData) {
						for (var n in s.extraData) {
							if (s.extraData.hasOwnProperty(n)) {
								// if using the $.param format that allows for multiple values with the same name
								if ($.isPlainObject(s.extraData[n]) && s.extraData[n].hasOwnProperty('name') && s.extraData[n].hasOwnProperty('value')) {
									extraInputs.push(
										$('<input type="hidden" name="' + s.extraData[n].name + '">', ownerDocument).val(s.extraData[n].value)
											.appendTo(form)[0]);
								} else {
									extraInputs.push(
										$('<input type="hidden" name="' + n + '">', ownerDocument).val(s.extraData[n])
											.appendTo(form)[0]);
								}
							}
						}
					}

					if (!s.iframeTarget) {
						// add iframe to doc and submit the form
						$io.appendTo($body);
					}

					if (io.attachEvent) {
						io.attachEvent('onload', cb);
					} else {
						io.addEventListener('load', cb, false);
					}

					setTimeout(checkState, 15);

					try {
						form.submit();

					} catch (err) {
						// just in case form has element with name/id of 'submit'
						var submitFn = document.createElement('form').submit;

						submitFn.apply(form);
					}

				} finally {
					// reset attrs and remove "extra" input elements
					form.setAttribute('action', a);
					form.setAttribute('enctype', et); // #380
					if (t) {
						form.setAttribute('target', t);
					} else {
						$form.removeAttr('target');
					}
					$(extraInputs).remove();
				}
			}

			if (s.forceSync) {
				doSubmit();
			} else {
				setTimeout(doSubmit, 10); // this lets dom updates render
			}

			var data, doc, domCheckCount = 50, callbackProcessed;

			function cb(e) {
				if (xhr.aborted || callbackProcessed) {
					return;
				}

				doc = getDoc(io);
				if (!doc) {
					log('cannot access response document');
					e = SERVER_ABORT;
				}
				if (e === CLIENT_TIMEOUT_ABORT && xhr) {
					xhr.abort('timeout');
					deferred.reject(xhr, 'timeout');

					return;

				}
				if (e === SERVER_ABORT && xhr) {
					xhr.abort('server abort');
					deferred.reject(xhr, 'error', 'server abort');

					return;
				}

				if (!doc || doc.location.href === s.iframeSrc) {
					// response not received yet
					if (!timedOut) {
						return;
					}
				}

				if (io.detachEvent) {
					io.detachEvent('onload', cb);
				} else {
					io.removeEventListener('load', cb, false);
				}

				var status = 'success', errMsg;

				try {
					if (timedOut) {
						throw 'timeout';
					}

					var isXml = s.dataType === 'xml' || doc.XMLDocument || $.isXMLDoc(doc);

					log('isXml=' + isXml);

					if (!isXml && window.opera && (doc.body === null || !doc.body.innerHTML)) {
						if (--domCheckCount) {
							// in some browsers (Opera) the iframe DOM is not always traversable when
							// the onload callback fires, so we loop a bit to accommodate
							log('requeing onLoad callback, DOM not available');
							setTimeout(cb, 250);

							return;
						}
						// let this fall through because server response could be an empty document
						// log('Could not access iframe DOM after mutiple tries.');
						// throw 'DOMException: not available';
					}

					// log('response detected');
					var docRoot = doc.body ? doc.body : doc.documentElement;

					xhr.responseText = docRoot ? docRoot.innerHTML : null;
					xhr.responseXML = doc.XMLDocument ? doc.XMLDocument : doc;
					if (isXml) {
						s.dataType = 'xml';
					}
					xhr.getResponseHeader = function(header){
						var headers = {'content-type': s.dataType};

						return headers[header.toLowerCase()];
					};
					// support for XHR 'status' & 'statusText' emulation :
					if (docRoot) {
						xhr.status = Number(docRoot.getAttribute('status')) || xhr.status;
						xhr.statusText = docRoot.getAttribute('statusText') || xhr.statusText;
					}

					var dt = (s.dataType || '').toLowerCase();
					var scr = /(json|script|text)/.test(dt);

					if (scr || s.textarea) {
						// see if user embedded response in textarea
						var ta = doc.getElementsByTagName('textarea')[0];

						if (ta) {
							xhr.responseText = ta.value;
							// support for XHR 'status' & 'statusText' emulation :
							xhr.status = Number(ta.getAttribute('status')) || xhr.status;
							xhr.statusText = ta.getAttribute('statusText') || xhr.statusText;

						} else if (scr) {
							// account for browsers injecting pre around json response
							var pre = doc.getElementsByTagName('pre')[0];
							var b = doc.getElementsByTagName('body')[0];

							if (pre) {
								xhr.responseText = pre.textContent ? pre.textContent : pre.innerText;
							} else if (b) {
								xhr.responseText = b.textContent ? b.textContent : b.innerText;
							}
						}

					} else if (dt === 'xml' && !xhr.responseXML && xhr.responseText) {
						xhr.responseXML = toXml(xhr.responseText);			// eslint-disable-line no-use-before-define
					}

					try {
						data = httpData(xhr, dt, s);						// eslint-disable-line no-use-before-define

					} catch (err) {
						status = 'parsererror';
						xhr.error = errMsg = (err || status);
					}

				} catch (err) {
					log('error caught: ', err);
					status = 'error';
					xhr.error = errMsg = (err || status);
				}

				if (xhr.aborted) {
					log('upload aborted');
					status = null;
				}

				if (xhr.status) { // we've set xhr.status
					status = ((xhr.status >= 200 && xhr.status < 300) || xhr.status === 304) ? 'success' : 'error';
				}

				// ordering of these callbacks/triggers is odd, but that's how $.ajax does it
				if (status === 'success') {
					if (s.success) {
						s.success.call(s.context, data, 'success', xhr);
					}

					deferred.resolve(xhr.responseText, 'success', xhr);

					if (g) {
						$.event.trigger('ajaxSuccess', [xhr, s]);
					}

				} else if (status) {
					if (typeof errMsg === 'undefined') {
						errMsg = xhr.statusText;
					}
					if (s.error) {
						s.error.call(s.context, xhr, status, errMsg);
					}
					deferred.reject(xhr, 'error', errMsg);
					if (g) {
						$.event.trigger('ajaxError', [xhr, s, errMsg]);
					}
				}

				if (g) {
					$.event.trigger('ajaxComplete', [xhr, s]);
				}

				if (g && !--$.active) {
					$.event.trigger('ajaxStop');
				}

				if (s.complete) {
					s.complete.call(s.context, xhr, status);
				}

				callbackProcessed = true;
				if (s.timeout) {
					clearTimeout(timeoutHandle);
				}

				// clean up
				setTimeout(function() {
					if (!s.iframeTarget) {
						$io.remove();
					} else { // adding else to clean up existing iframe response.
						$io.attr('src', s.iframeSrc);
					}
					xhr.responseXML = null;
				}, 100);
			}

			var toXml = $.parseXML || function(s, doc) { // use parseXML if available (jQuery 1.5+)
				if (window.ActiveXObject) {
					doc = new ActiveXObject('Microsoft.XMLDOM');
					doc.async = 'false';
					doc.loadXML(s);

				} else {
					doc = (new DOMParser()).parseFromString(s, 'text/xml');
				}

				return (doc && doc.documentElement && doc.documentElement.nodeName !== 'parsererror') ? doc : null;
			};
			var parseJSON = $.parseJSON || function(s) {
				/* jslint evil:true */
				return window['eval']('(' + s + ')');			// eslint-disable-line dot-notation
			};

			var httpData = function(xhr, type, s) { // mostly lifted from jq1.4.4

				var ct = xhr.getResponseHeader('content-type') || '',
					xml = ((type === 'xml' || !type) && ct.indexOf('xml') >= 0),
					data = xml ? xhr.responseXML : xhr.responseText;

				if (xml && data.documentElement.nodeName === 'parsererror') {
					if ($.error) {
						$.error('parsererror');
					}
				}
				if (s && s.dataFilter) {
					data = s.dataFilter(data, type);
				}
				if (typeof data === 'string') {
					if ((type === 'json' || !type) && ct.indexOf('json') >= 0) {
						data = parseJSON(data);
					} else if ((type === 'script' || !type) && ct.indexOf('javascript') >= 0) {
						$.globalEval(data);
					}
				}

				return data;
			};

			return deferred;
		}
	};

	/**
	 * ajaxForm() provides a mechanism for fully automating form submission.
	 *
	 * The advantages of using this method instead of ajaxSubmit() are:
	 *
	 * 1: This method will include coordinates for <input type="image"> elements (if the element
	 *	is used to submit the form).
	 * 2. This method will include the submit element's name/value data (for the element that was
	 *	used to submit the form).
	 * 3. This method binds the submit() method to the form for you.
	 *
	 * The options argument for ajaxForm works exactly as it does for ajaxSubmit. ajaxForm merely
	 * passes the options argument along after properly binding events for submit elements and
	 * the form itself.
	 */
	$.fn.ajaxForm = function(options, data, dataType, onSuccess) {
		if (typeof options === 'string' || (options === false && arguments.length > 0)) {
			options = {
				'url'      : options,
				'data'     : data,
				'dataType' : dataType
			};

			if (typeof onSuccess === 'function') {
				options.success = onSuccess;
			}
		}

		options = options || {};
		options.delegation = options.delegation && $.isFunction($.fn.on);

		// in jQuery 1.3+ we can fix mistakes with the ready state
		if (!options.delegation && this.length === 0) {
			var o = {s: this.selector, c: this.context};

			if (!$.isReady && o.s) {
				log('DOM not ready, queuing ajaxForm');
				$(function() {
					$(o.s, o.c).ajaxForm(options);
				});

				return this;
			}

			// is your DOM ready?  http://docs.jquery.com/Tutorials:Introducing_$(document).ready()
			log('terminating; zero elements found by selector' + ($.isReady ? '' : ' (DOM not ready)'));

			return this;
		}

		if (options.delegation) {
			$(document)
				.off('submit.form-plugin', this.selector, doAjaxSubmit)
				.off('click.form-plugin', this.selector, captureSubmittingElement)
				.on('submit.form-plugin', this.selector, options, doAjaxSubmit)
				.on('click.form-plugin', this.selector, options, captureSubmittingElement);

			return this;
		}

		if (options.beforeFormUnbind) {
			options.beforeFormUnbind(this, options);
		}

		return this.ajaxFormUnbind()
			.on('submit.form-plugin', options, doAjaxSubmit)
			.on('click.form-plugin', options, captureSubmittingElement);
	};

	// private event handlers
	function doAjaxSubmit(e) {
		/* jshint validthis:true */
		var options = e.data;

		if (!e.isDefaultPrevented()) { // if event has been canceled, don't proceed
			e.preventDefault();
			$(e.target).closest('form').ajaxSubmit(options); // #365
		}
	}

	function captureSubmittingElement(e) {
		/* jshint validthis:true */
		var target = e.target;
		var $el = $(target);

		if (!$el.is('[type=submit],[type=image]')) {
			// is this a child element of the submit el?  (ex: a span within a button)
			var t = $el.closest('[type=submit]');

			if (t.length === 0) {
				return;
			}
			target = t[0];
		}

		var form = target.form;

		form.clk = target;

		if (target.type === 'image') {
			if (typeof e.offsetX !== 'undefined') {
				form.clk_x = e.offsetX;
				form.clk_y = e.offsetY;

			} else if (typeof $.fn.offset === 'function') {
				var offset = $el.offset();

				form.clk_x = e.pageX - offset.left;
				form.clk_y = e.pageY - offset.top;

			} else {
				form.clk_x = e.pageX - target.offsetLeft;
				form.clk_y = e.pageY - target.offsetTop;
			}
		}
		// clear form vars
		setTimeout(function() {
			form.clk = form.clk_x = form.clk_y = null;
		}, 100);
	}


	// ajaxFormUnbind unbinds the event handlers that were bound by ajaxForm
	$.fn.ajaxFormUnbind = function() {
		return this.off('submit.form-plugin click.form-plugin');
	};

	/**
	 * formToArray() gathers form element data into an array of objects that can
	 * be passed to any of the following ajax functions: $.get, $.post, or load.
	 * Each object in the array has both a 'name' and 'value' property. An example of
	 * an array for a simple login form might be:
	 *
	 * [ { name: 'username', value: 'jresig' }, { name: 'password', value: 'secret' } ]
	 *
	 * It is this array that is passed to pre-submit callback functions provided to the
	 * ajaxSubmit() and ajaxForm() methods.
	 */
	$.fn.formToArray = function(semantic, elements, filtering) {
		var a = [];

		if (this.length === 0) {
			return a;
		}

		var form = this[0];
		var formId = this.attr('id');
		var els = (semantic || typeof form.elements === 'undefined') ? form.getElementsByTagName('*') : form.elements;
		var els2;

		if (els) {
			els = $.makeArray(els); // convert to standard array
		}

		// #386; account for inputs outside the form which use the 'form' attribute
		// FinesseRus: in non-IE browsers outside fields are already included in form.elements.
		if (formId && (semantic || /(Edge|Trident)\//.test(navigator.userAgent))) {
			els2 = $(':input[form="' + formId + '"]').get(); // hat tip @thet
			if (els2.length) {
				els = (els || []).concat(els2);
			}
		}

		if (!els || !els.length) {
			return a;
		}

		if ($.isFunction(filtering)) {
			els = $.map(els, filtering);
		}

		var i, j, n, v, el, max, jmax;

		for (i = 0, max = els.length; i < max; i++) {
			el = els[i];
			n = el.name;
			if (!n || el.disabled) {
				continue;
			}

			if (semantic && form.clk && el.type === 'image') {
				// handle image inputs on the fly when semantic == true
				if (form.clk === el) {
					a.push({name: n, value: $(el).val(), type: el.type});
					a.push({name: n + '.x', value: form.clk_x}, {name: n + '.y', value: form.clk_y});
				}
				continue;
			}

			v = $.fieldValue(el, true);
			if (v && v.constructor === Array) {
				if (elements) {
					elements.push(el);
				}
				for (j = 0, jmax = v.length; j < jmax; j++) {
					a.push({name: n, value: v[j]});
				}

			} else if (feature.fileapi && el.type === 'file') {
				if (elements) {
					elements.push(el);
				}

				var files = el.files;

				if (files.length) {
					for (j = 0; j < files.length; j++) {
						a.push({name: n, value: files[j], type: el.type});
					}
				} else {
					// #180
					a.push({name: n, value: '', type: el.type});
				}

			} else if (v !== null && typeof v !== 'undefined') {
				if (elements) {
					elements.push(el);
				}
				a.push({name: n, value: v, type: el.type, required: el.required});
			}
		}

		if (!semantic && form.clk) {
			// input type=='image' are not found in elements array! handle it here
			var $input = $(form.clk), input = $input[0];

			n = input.name;

			if (n && !input.disabled && input.type === 'image') {
				a.push({name: n, value: $input.val()});
				a.push({name: n + '.x', value: form.clk_x}, {name: n + '.y', value: form.clk_y});
			}
		}

		return a;
	};

	/**
	 * Serializes form data into a 'submittable' string. This method will return a string
	 * in the format: name1=value1&amp;name2=value2
	 */
	$.fn.formSerialize = function(semantic) {
		// hand off to jQuery.param for proper encoding
		return $.param(this.formToArray(semantic));
	};

	/**
	 * Serializes all field elements in the jQuery object into a query string.
	 * This method will return a string in the format: name1=value1&amp;name2=value2
	 */
	$.fn.fieldSerialize = function(successful) {
		var a = [];

		this.each(function() {
			var n = this.name;

			if (!n) {
				return;
			}

			var v = $.fieldValue(this, successful);

			if (v && v.constructor === Array) {
				for (var i = 0, max = v.length; i < max; i++) {
					a.push({name: n, value: v[i]});
				}

			} else if (v !== null && typeof v !== 'undefined') {
				a.push({name: this.name, value: v});
			}
		});

		// hand off to jQuery.param for proper encoding
		return $.param(a);
	};

	/**
	 * Returns the value(s) of the element in the matched set. For example, consider the following form:
	 *
	 *	<form><fieldset>
	 *		<input name="A" type="text">
	 *		<input name="A" type="text">
	 *		<input name="B" type="checkbox" value="B1">
	 *		<input name="B" type="checkbox" value="B2">
	 *		<input name="C" type="radio" value="C1">
	 *		<input name="C" type="radio" value="C2">
	 *	</fieldset></form>
	 *
	 *	var v = $('input[type=text]').fieldValue();
	 *	// if no values are entered into the text inputs
	 *	v === ['','']
	 *	// if values entered into the text inputs are 'foo' and 'bar'
	 *	v === ['foo','bar']
	 *
	 *	var v = $('input[type=checkbox]').fieldValue();
	 *	// if neither checkbox is checked
	 *	v === undefined
	 *	// if both checkboxes are checked
	 *	v === ['B1', 'B2']
	 *
	 *	var v = $('input[type=radio]').fieldValue();
	 *	// if neither radio is checked
	 *	v === undefined
	 *	// if first radio is checked
	 *	v === ['C1']
	 *
	 * The successful argument controls whether or not the field element must be 'successful'
	 * (per http://www.w3.org/TR/html4/interact/forms.html#successful-controls).
	 * The default value of the successful argument is true. If this value is false the value(s)
	 * for each element is returned.
	 *
	 * Note: This method *always* returns an array. If no valid value can be determined the
	 *	array will be empty, otherwise it will contain one or more values.
	 */
	$.fn.fieldValue = function(successful) {
		for (var val = [], i = 0, max = this.length; i < max; i++) {
			var el = this[i];
			var v = $.fieldValue(el, successful);

			if (v === null || typeof v === 'undefined' || (v.constructor === Array && !v.length)) {
				continue;
			}

			if (v.constructor === Array) {
				$.merge(val, v);
			} else {
				val.push(v);
			}
		}

		return val;
	};

	/**
	 * Returns the value of the field element.
	 */
	$.fieldValue = function(el, successful) {
		var n = el.name, t = el.type, tag = el.tagName.toLowerCase();

		if (typeof successful === 'undefined') {
			successful = true;
		}

		/* eslint-disable no-mixed-operators */
		if (successful && (!n || el.disabled || t === 'reset' || t === 'button' ||
			(t === 'checkbox' || t === 'radio') && !el.checked ||
			(t === 'submit' || t === 'image') && el.form && el.form.clk !== el ||
			tag === 'select' && el.selectedIndex === -1)) {
		/* eslint-enable no-mixed-operators */
			return null;
		}

		if (tag === 'select') {
			var index = el.selectedIndex;

			if (index < 0) {
				return null;
			}

			var a = [], ops = el.options;
			var one = (t === 'select-one');
			var max = (one ? index + 1 : ops.length);

			for (var i = (one ? index : 0); i < max; i++) {
				var op = ops[i];

				if (op.selected && !op.disabled) {
					var v = op.value;

					if (!v) { // extra pain for IE...
						v = (op.attributes && op.attributes.value && !(op.attributes.value.specified)) ? op.text : op.value;
					}

					if (one) {
						return v;
					}

					a.push(v);
				}
			}

			return a;
		}

		return $(el).val().replace(rCRLF, '\r\n');
	};

	/**
	 * Clears the form data. Takes the following actions on the form's input fields:
	 *  - input text fields will have their 'value' property set to the empty string
	 *  - select elements will have their 'selectedIndex' property set to -1
	 *  - checkbox and radio inputs will have their 'checked' property set to false
	 *  - inputs of type submit, button, reset, and hidden will *not* be effected
	 *  - button elements will *not* be effected
	 */
	$.fn.clearForm = function(includeHidden) {
		return this.each(function() {
			$('input,select,textarea', this).clearFields(includeHidden);
		});
	};

	/**
	 * Clears the selected form elements.
	 */
	$.fn.clearFields = $.fn.clearInputs = function(includeHidden) {
		var re = /^(?:color|date|datetime|email|month|number|password|range|search|tel|text|time|url|week)$/i; // 'hidden' is not in this list

		return this.each(function() {
			var t = this.type, tag = this.tagName.toLowerCase();

			if (re.test(t) || tag === 'textarea') {
				this.value = '';

			} else if (t === 'checkbox' || t === 'radio') {
				this.checked = false;

			} else if (tag === 'select') {
				this.selectedIndex = -1;

			} else if (t === 'file') {
				if (/MSIE/.test(navigator.userAgent)) {
					$(this).replaceWith($(this).clone(true));
				} else {
					$(this).val('');
				}

			} else if (includeHidden) {
				// includeHidden can be the value true, or it can be a selector string
				// indicating a special test; for example:
				// $('#myForm').clearForm('.special:hidden')
				// the above would clean hidden inputs that have the class of 'special'
				if ((includeHidden === true && /hidden/.test(t)) ||
					(typeof includeHidden === 'string' && $(this).is(includeHidden))) {
					this.value = '';
				}
			}
		});
	};


	/**
	 * Resets the form data or individual elements. Takes the following actions
	 * on the selected tags:
	 * - all fields within form elements will be reset to their original value
	 * - input / textarea / select fields will be reset to their original value
	 * - option / optgroup fields (for multi-selects) will defaulted individually
	 * - non-multiple options will find the right select to default
	 * - label elements will be searched against its 'for' attribute
	 * - all others will be searched for appropriate children to default
	 */
	$.fn.resetForm = function() {
		return this.each(function() {
			var el = $(this);
			var tag = this.tagName.toLowerCase();

			switch (tag) {
			case 'input':
				this.checked = this.defaultChecked;
				// fall through

			case 'textarea':
				this.value = this.defaultValue;

				return true;

			case 'option':
			case 'optgroup':
				var select = el.parents('select');

				if (select.length && select[0].multiple) {
					if (tag === 'option') {
						this.selected = this.defaultSelected;
					} else {
						el.find('option').resetForm();
					}
				} else {
					select.resetForm();
				}

				return true;

			case 'select':
				el.find('option').each(function(i) {				// eslint-disable-line consistent-return
					this.selected = this.defaultSelected;
					if (this.defaultSelected && !el[0].multiple) {
						el[0].selectedIndex = i;

						return false;
					}
				});

				return true;

			case 'label':
				var forEl = $(el.attr('for'));
				var list = el.find('input,select,textarea');

				if (forEl[0]) {
					list.unshift(forEl[0]);
				}

				list.resetForm();

				return true;

			case 'form':
				// guard against an input with the name of 'reset'
				// note that IE reports the reset function as an 'object'
				if (typeof this.reset === 'function' || (typeof this.reset === 'object' && !this.reset.nodeType)) {
					this.reset();
				}

				return true;

			default:
				el.find('form,input,label,select,textarea').resetForm();

				return true;
			}
		});
	};

	/**
	 * Enables or disables any matching elements.
	 */
	$.fn.enable = function(b) {
		if (typeof b === 'undefined') {
			b = true;
		}

		return this.each(function() {
			this.disabled = !b;
		});
	};

	/**
	 * Checks/unchecks any matching checkboxes or radio buttons and
	 * selects/deselects and matching option elements.
	 */
	$.fn.selected = function(select) {
		if (typeof select === 'undefined') {
			select = true;
		}

		return this.each(function() {
			var t = this.type;

			if (t === 'checkbox' || t === 'radio') {
				this.checked = select;

			} else if (this.tagName.toLowerCase() === 'option') {
				var $sel = $(this).parent('select');

				if (select && $sel[0] && $sel[0].type === 'select-one') {
					// deselect all other options
					$sel.find('option').selected(false);
				}

				this.selected = select;
			}
		});
	};

	// expose debug var
	$.fn.ajaxSubmit.debug = false;

	// helper fn for console logging
	function log() {
		if (!$.fn.ajaxSubmit.debug) {
			return;
		}

		var msg = '[jquery.form] ' + Array.prototype.join.call(arguments, '');

		if (window.console && window.console.log) {
			window.console.log(msg);

		} else if (window.opera && window.opera.postError) {
			window.opera.postError(msg);
		}
	}
}));

define('rebrush/components/gigaset_customprint/view',[
    'jquery',
    'lodash',
    'cartware/components/_base',
    'cartware/jquery/bem',
    'jquery-form',
    'magnific-popup'
], function(
    $,
    _,
    Component,
    BEM
) {
    var fabric;

    function ObserverImageLoaded(callback) {
        this.callback = callback;
    }

    ObserverImageLoaded.prototype = {
        callback: null,
        backgroundLoaded: false,
        overlayLoaded: false,

        checkFulfilled: function () {
            if (this.backgroundLoaded && this.overlayLoaded) {
                this.callback();
            }
        }
    };

    function View(dialogBlock) {
        this.dialogBlock = dialogBlock;
    }

    View.prototype = {
        dialogBlock: null,
        cta: null,
        optionInput: null,
        addToCart: null,
        canvas: null,
        stencilImage: null,
        baseImage: null,
        alertBox: null,
        fileExtensions: 'png|jpeg|jpg',
        globalScale: null,
        dimensions: null,
        thumbnail: false,
        localscale: 1,
        progressBar: null,
        previewElement: null,
        previewWrapper: null,
        data: null,
        tokenHash: null,

        initializeComponent: function() {
			let optionsBlock = this.dialogBlock.closest('.' + BEM.buildElementClassname('product-options','option-wrap')),
                popup = this.dialogBlock,
                self = this;

			this.cta = optionsBlock.find('.' + BEM.buildElementClassname('product-options','custom-print-open-configurator'));
			this.previewElement = optionsBlock.find('.' + BEM.buildElementClassname('product-options','custom-print-preview') + ' img');
			this.previewWrapper = this.previewElement.parent();
			this.optionInput = optionsBlock.find('.product-custom-option-print');
			this.dimensions = popup.data('dimensions');

			this.previewWrapper.find('.' + BEM.buildElementClassname('product-options','custom-print-preview-reset')).on('click', function (e) {
			    e.preventDefault();
			    self.reset();
            });

			this.optionInput.on('change', function () {
			    if (!$(this).val()) {
                    self.previewElement.attr('src', '');
                    self.previewWrapper.hide();
                    opConfig.reloadPrice();

                    if (!self.cta.hasClass('btn')) {
                        self.cta.addClass('btn');
                    }

                    if (!self.cta.hasClass('btn-primary')) {
                        self.cta.addClass('btn-primary');
                    }
                }
            });

			this.initPopup(popup);
		},

        initPopup: function (popup) {
            let self = this;
            function openCallback() {
                self.thumbnail = null;

                let canvas = this.contentContainer.find('canvas');

                self.stencilImage = this.contentContainer.find('img.image-stencil'),
                self.baseImage = this.contentContainer.find('img.image-base');

                self.progressBar = this.contentContainer.find('.progress-bar');
                self.alertBox = this.contentContainer.find('.alert');

                self.initCanvas(canvas[0], self.baseImage, self.stencilImage);
                self.initActions(this.contentContainer);
            }

            this.cta.on('click', function(event) {
                event.preventDefault();
                event.stopPropagation();

                // holding main popup window content in DOM element
                let hiddenPopupContainer = $('.data-essential-product-popup-holder');
                if(hiddenPopupContainer.length) {
                    hiddenPopupContainer.remove();
                }
                let mainPopup = $('.mfp-content .popup-wrap');

                if($.magnificPopup.instance.isOpen) {
                    setTimeout(function() {
                        openCallback.call($.magnificPopup.instance);
                    }, 0);
                }

                $.magnificPopup.open({
                    closeOnContentClick: false,
                    closeOnBgClick: false,
                    closeBtnInside: true,
                    items: [
                        {
                            type: 'inline',
                            src: popup.html(),
                        }
                    ],
                    callbacks: {
                        open: openCallback,
                        close: function () {
                            if (!self.thumbnail) {
                                return;
                            }

                            self.canvas = null;
                            self.thumbnail = null;
                        }
                    }
                });

                let containerPopup = $('<div class="data-essential-product-popup-holder" style="display: none"></div>');
                $('body').append(containerPopup);
                containerPopup.append(mainPopup);
            });

            $(window).on('resize', function () {
                self.resizeCanvas();
            });

			this.cta.removeAttr('disabled');
        },

        initCanvas: function (canvas, background, stencil) {
            let fCanvas = new fabric.Canvas(canvas),
                self = this;
            fCanvas.controlsAboveOverlay = true;

            if (!this.dimensions.fullscale) {
                let border = $('<div />');
                border.addClass('border');
                border.css({
                    width: this.dimensions.width + 2,
                    height: this.dimensions.height + 2,
                    left: this.dimensions.widthOffset - 1,
                    top: this.dimensions.heightOffset - 1
                });

                $(fCanvas.wrapperEl).append(border);
            }

            let callback = function () {
                self.originalWidth = fCanvas.getWidth();
                self.originalHeight = fCanvas.getHeight();

                self.resizeCanvas();
            };

            let observer = new ObserverImageLoaded(callback);

            fabric.Image.fromURL(background.attr('src'), function (image) {
                fCanvas.setBackgroundImage(image, fCanvas.renderAll.bind(fCanvas));
                fCanvas.setWidth(image.width);
                fCanvas.setHeight(image.height);

                observer.backgroundLoaded = true;
                observer.checkFulfilled();
            });

            fabric.Image.fromURL(stencil.attr('src'), function (image) {
                fCanvas.setOverlayImage(image, fCanvas.renderAll.bind(fCanvas));
                observer.overlayLoaded = true;
                observer.checkFulfilled();
            });

            if (this.optionInput.val()) {

                let inputValue = JSON.parse(this.optionInput.val());

                fabric.Image.fromURL(inputValue.baseUrl + inputValue.uploadImage, function(image) {

                    self.localScale = self.dimensions.width / image.width;

                    image.set('top', (self.dimensions.heightOffset + inputValue.offsetY) * self.globalScale);
                    image.set('left', (self.dimensions.widthOffset + inputValue.offsetX) * self.globalScale);
                    image.scale(self.globalScale * inputValue.scale * self.localScale);
                    image.setControlsVisibility({ml: false, mb: false, mr: false, mt: false, mtr: false});
                    image.lockScalingFlip = true;
                    image.transparentCorners = false;
                    image.name = 'customprint-image';

                    image.on('scaling', function () {
                        self.toogleAlert('');

                        let maxScale = self.data.maxScale * self.globalScale * self.localScale;

                        if (this.scaleX > maxScale) {
                            this.scaleX = maxScale;
                            this.scaleY = maxScale;

                            self.toogleAlert(Translator.translate('Image cannot be scaled larger for print.'));
                        }
                    });

                    self.thumbnail = image;
                    fCanvas.add(self.thumbnail);
                    fCanvas.setActiveObject(self.thumbnail);
                });
            }

			this.canvas = fCanvas;
        },

        initActions: function(container) {

            let self = this,
                uploadInput = container.find('input[type="file"]')[0],
                form = container.find('form'),
                saveButton = container.find('.' + BEM.buildElementClassname('print-dialog','print-file-input-save')),
                cancelButton = container.find('.' + BEM.buildElementClassname('print-dialog','print-file-input-cancel'));

            if (this.optionInput.val()) {
                saveButton.show();
                cancelButton.hide();
            }

            $(uploadInput).on('change', function () {
                $(this).siblings('span.filename-label').html(this.files[0].name);
            });

            container.on('click', '.' + BEM.buildElementClassname('print-dialog','print-file-input-submit'), function (event) {
                event.preventDefault();

                self.progressBar.animate(
                    {width: '0'},
                    {duration: 0}
                );

                self.toogleAlert('');

			    let file = uploadInput.files[0];

			    if (typeof file === 'undefined') {
			        self.toogleAlert(Translator.translate('Please select a file first.'));
			        return false;
                }

			    let fileType = file.type.split('/');

			    if (fileType[0] !== 'image' || !RegExp(self.fileExtensions).test(fileType[1])) {
			        let message = Translator.translate('File extension is not allowed. Allowed extensions are %s.');
			        message = message.replace('{0}', self.fileExtensions.replace('|', ', '));

			        self.toogleAlert(message);
			        return false;
                }


			    if (self.thumbnail) {
			        self.canvas.remove(self.thumbnail);
                }

                form.ajaxSubmit({
                    beforeSubmit: function () {
                        self.progressBar.width('50%');
                    },
                    uploadProgress: function (event, position, total, percentageComplete) {
                        self.progressBar.animate(
                            {width: percentageComplete + '%'},
                            {duration: 10}
                        );
                    },
                    success: function (response) {
                        self.tokenHash = response.tokenHash;
                        self.data = {
                            uploadImage: response.imageUrl,
                            tokenHash: response.tokenHash,
                            offsetX: 0,
                            offsetY: 0,
                            scale: 1,
                            maxScale: response.maxScale
                        };

                        saveButton.show();
                        cancelButton.hide();

                        fabric.Image.fromURL(response.baseUrl + response.imageUrl, function(image) {
                            self.localScale = self.dimensions.width / image.width;

                            let initialScale = self.data.maxScale < 1 ? self.data.maxScale : 1;

                            if (initialScale < 1) {
                                self.toogleAlert(Translator.translate('Image cannot be scaled larger for print.'));
                            }

                            image.set('top', self.dimensions.heightOffset * self.globalScale);
                            image.set('left', self.dimensions.widthOffset * self.globalScale);
                            image.scale(self.globalScale * self.localScale * initialScale);
                            image.setControlsVisibility({ml: false, mb: false, mr: false, mt: false, mtr: false});
                            image.lockScalingFlip = true;
                            image.transparentCorners = false;
                            image.name = 'customprint-image';

                            image.on('scaling', function () {
                                self.toogleAlert('');

                                let maxScale = self.data.maxScale * self.globalScale * self.localScale;

                                if (this.scaleX > maxScale) {
                                    this.scaleX = maxScale;
                                    this.scaleY = maxScale;

                                    self.toogleAlert(Translator.translate('Image cannot be scaled larger for print.'));
                                }
                            });

                            self.thumbnail = image;
                            self.canvas.add(self.thumbnail);
                            self.canvas.setActiveObject(self.thumbnail);
                        });
                    },
                    error: function (response) {
                        self.toogleAlert(response.responseJSON.message);

                        self.progressBar.animate(
                            {width: '0'},
                            {duration: 0}
                        );
                    }
                });
            });

            saveButton.on('click', function (event) {
                event.preventDefault();

                if (self.dimensions.forceFill) {

                    var printFrameWidth = self.dimensions.width;
                    var printFrameHeight = self.dimensions.height;

                    var userInputWidth = self.thumbnail.get('scaleX') * self.thumbnail.get('width');
                    var userInputHeight = self.thumbnail.get('scaleY') * self.thumbnail.get('height');

                    var userInputCordinates = self.thumbnail.get('oCoords');

                    var printFrameTopLeftY = self.dimensions.heightOffset;
                    var printFrameTopLeftX = self.dimensions.widthOffset;
                    var printFrameBottomRightY = printFrameTopLeftY + printFrameHeight;
                    var printFrameBottomRightX = printFrameTopLeftX + printFrameWidth;

                    var frameSizeFilled = userInputWidth > printFrameWidth && userInputHeight > printFrameHeight;
                    var yAxisFrameFilled = userInputCordinates.tl.y < printFrameTopLeftY && userInputCordinates.br.y > printFrameBottomRightY;
                    var xAxisFrameFilled = userInputCordinates.tl.x < printFrameTopLeftX && userInputCordinates.br.x > printFrameBottomRightX;

                    if (!frameSizeFilled || !yAxisFrameFilled || !xAxisFrameFilled) {
                        self.toogleAlert(Translator.translate('Please fill the complete print frame (orange borders) or use the passepartout print option instead.'));
                        return false;
                    }
                }

                if (self.thumbnail) {
                    self.data.offsetX = (self.thumbnail.get('left') / self.globalScale) - self.dimensions.widthOffset;
                    self.data.offsetY = (self.thumbnail.get('top') / self.globalScale) - self.dimensions.heightOffset;
                    self.data.scale = (self.thumbnail.get('scaleX') / self.globalScale / self.localScale);
                    self.data.canvasWidth = self.canvas.width;
                    self.data.canvasHeight = self.canvas.height;

                    $.ajax({
                        url: self.cta.data('generate-preview-action'),
                        data: {
                            hash: self.tokenHash,
                            data: self.data,
                        },
                        method: 'post',
                        success: function (response) {

                            let preview = response.preview;

                            self.data.preview = preview;
                            self.data.baseUrl = response.baseUrl;
                            self.previewElement.attr('src', response.baseUrl + preview);
                            self.previewWrapper.show();

                            self.optionInput.val(JSON.stringify(self.data));
                            opConfig && opConfig.reloadPrice();

                            self.cta.removeClass('btn');
                            self.cta.removeClass('btn-primary');
                        }
                    });
                }

                BEM.block('product-detail-add-to-cart').getElements('add-to-cart-button').attr('data-customprint-added', 1);

                if($('.catalog-category-view').length) {
                    self.backToMainPopup();
                } else {
                    $.magnificPopup.close();
                }
            });

            cancelButton.on('click', function (event) {
                event.preventDefault();
                if($('.catalog-category-view').length) {
                    self.backToMainPopup();
                } else {
                    $.magnificPopup.close();
                }
            });
        },

        backToMainPopup: function () {
            let hiddenPopupContainer = $('.data-essential-product-popup-holder');
            if(hiddenPopupContainer.length) {
                $.magnificPopup.open({
                    items: [
                        {
                            type: 'inline',
                            src: hiddenPopupContainer.children().first(),
                        }
                    ],
                });
                hiddenPopupContainer.remove();
            }
        },

        toogleAlert: function (message) {
            this.alertBox.html(message);

            if (message === '') {
                this.alertBox.addClass('empty');
                return;
            }

            this.alertBox.removeClass('empty');
        },

        resizeCanvas: function () {
            if (this.canvas === null) {
                return;
            }

            let width = window.innerWidth-50,
                canvasWidth = this.canvas.getWidth();

            if (canvasWidth > width) {
                this.globalScale = width / canvasWidth;

                let ratio = this.originalHeight / this.originalWidth;

                this.resizeCanvasTo(width, width * ratio);
            } else if (width >= this.originalWidth) {
                this.globalScale = 1;

                this.resizeCanvasTo(this.originalWidth, this.originalHeight);
            }
        },

        resizeCanvasTo: function (width, height) {
            this.canvas.setWidth(width);
            this.canvas.setHeight(height);

            let scale = width/this.originalWidth;

            this.canvas.backgroundImage.scale(scale);
            this.canvas.overlayImage.scale(scale);

            if (this.thumbnail) {
                this.canvas.remove(this.thumbnail);
                this.thumbnail = null;
            }

            let border = $(this.canvas.wrapperEl).find('.border');

            if (border.length > 0) {
                border.css({
                    width: this.dimensions.width * scale + 2,
                    height: this.dimensions.height * scale + 2,
                    left: this.dimensions.widthOffset * scale - 1,
                    top: this.dimensions.heightOffset * scale - 1
                });
            }

            this.canvas.calcOffset();
            this.canvas.renderAll();
        },

        reset: function () {
            this.data.preview = null;
            this.previewElement.attr('src', '');
            this.previewWrapper.hide();

            this.optionInput.val('');

            if (!this.cta.hasClass('btn')) {
                this.cta.addClass('btn');
            }
            if (!this.cta.hasClass('btn-primary')) {
                this.cta.addClass('btn-primary');
            }
        }
    };

    return Component.create({
        $init: function () {
            var dialogBlocks = $('script[data-identifier="print-dialog"]');

            if (!dialogBlocks.length) {
                return;
            }

            require(['fabric'], function (lib) {
                fabric = lib;
                fabric.Object.prototype.cornerColor = '#ff5200';

                dialogBlocks.each(function () {
                    (new View($(this))).initializeComponent();
                });
            });
        }
    });
});
define('rebrush/components/blocks/discount-code',[
    'jquery',
    'cartware/components/_base',
    'cartware/jquery/bem',
    'rebrush/system/layout/responsive-hub',
    'lodash',
    'rebrush/system/animation',
    'modernizr'
], function(
    $,
    Component,
    BEM,
    ResponsiveHub,
    _,
    Animation
) {
    var BLOCK_NAME = 'discount-code';

    var MODIFIER_OPEN       = 'open',
        MODIFIER_OPENING    = 'opening';

    var endPrecedingAnimation = $.noop;
    var isMobile = true;

    function DisountCodeBox(block) {
        this.block = BEM.block(block);
        this.trigger = this.block.getElements('trigger');
        this.content = this.block.getElements('content');
        this.init();
    }

    DisountCodeBox.prototype = {
        init: function() {
            this.bindEvents();
        },

        bindEvents: function() {
            var self = this;

            this.trigger.on('click', function(e) {
                e.preventDefault();
                self.toggle();
            });

            ResponsiveHub
                .on('change.mobile.enter', function() { isMobile = true; })
                .on('change.mobile.leave', function() { isMobile = false; });
        },

        toggle: function() {
            if (this.block.bemHasModifier(MODIFIER_OPEN)) {
                this.close();
            } else {
                this.open();
            }
        },

        open: function() {
            var targetHeight;
            var onAnimationEnd = _.bind(function() {
                endPrecedingAnimation = $.noop;
                this.content.css('height', '');
                this.block.bemRemoveModifier(MODIFIER_OPENING);
                this.block.bemAddModifier(MODIFIER_OPEN);
            }, this);

            this.block.bemAddModifier(MODIFIER_OPENING);
            if (!isMobile && Animation.supportAnimations) {
                endPrecedingAnimation();
                endPrecedingAnimation = onAnimationEnd;
                this.content.css('height', 'auto');
                targetHeight = this.content.height();
                this.content.css('height', '');
                _.defer(_.bind(function() {
                    this.content.one(Animation.transitionEndEventName, onAnimationEnd);
                    this.content.css('height', targetHeight);
                }, this));
            } else {
                onAnimationEnd();
            }

            this.block.find('[name="coupon_code"]').focus();
        },

        close: function() {
            var onAnimationEnd = _.bind(function() {
                endPrecedingAnimation = $.noop;
                this.content.css('height', '');
                this.content.css('opacity', '');
                this.block.bemRemoveModifier(MODIFIER_OPEN);
            }, this);

            if (!isMobile && Animation.supportAnimations) {
                endPrecedingAnimation();
                endPrecedingAnimation = onAnimationEnd;
                this.content.css('height', this.content.height());
                _.defer(_.bind(function() {
                    this.content.one(Animation.transitionEndEventName, onAnimationEnd);
                    this.content.css('height', 0);
                    this.content.css('opacity', 0);
                }, this));
            } else {
                onAnimationEnd();
            }
        }
    };

    return Component.create({
        $init: function() {
            BEM.block(BLOCK_NAME).each(function() {
                new DisountCodeBox(this);
            });
        }
    });
});
define('rebrush/components/tradein/tradein',[
    'jquery',
    'lodash',
    'cartware/components/_base',
    'cartware/jquery/bem'
], function(
    $,
    _,
    Component,
    BEM
) {
    return Component.create({
        $init: function () {

            this.block = BEM.block('checkout-tradein');
            this.deviceTypeCheckboxes = this.block.getElements('device-type-checkbox');
            this.couponCodeValue = this.block.getElements('coupon-title');

            this._initializeEvents();


        },
        _initializeEvents: function() {

            var self = this;
            this.deviceTypeCheckboxes.on('click', function(e) {
                self.couponCodeValue.show();
            });
        },

    });
});
define('rebrush/components/referral/progressbar',[
    'jquery',
    'lodash',
    'cartware/components/_base',
    'cartware/jquery/bem'
], function(
    $,
    _,
    Component,
    BEM
) {
    return Component.create({
        $init: function () {

            this.dashboardTabs = BEM.block('tabs');
            this.tabReferralCode = this.dashboardTabs.getElements('tab--referral-code');
            this.tabReferralRewards = this.dashboardTabs.getElements('tab--referral-rewards');
            this.progressbar = BEM.block('referral-progress-bar');

            this._setProgressBarStep(2);
            this._initializeEvents();
        },
        _initializeEvents: function() {
            var self = this;
            this.tabReferralRewards.on('click', function(e) {
                self._setProgressBarStep(3);
            });
            this.tabReferralCode.on('click', function(e) {
                self._setProgressBarStep(2);
            });
        },
        _setProgressBarStep: function(step) {

            const circleConnector = this.progressbar.getElements('circle-connector');

            const stepCircles = this.progressbar.getElements('circle');

            // const stepCircles = this.progressbar.getElements('circle');

            let currentActive = step ? step : 1;

            stepCircles.each(function (i, circle) {
                if (i < currentActive) {
                    // circle.addModifier('active');
                    if (!circle.className.includes('b_referral-progress-bar__circle--active')) {
                        circle.className = circle.className  + ' b_referral-progress-bar__circle--active';
                    }
                } else {
                    // circle.removeModifier('active');
                    circle.className = circle.className.replaceAll('b_referral-progress-bar__circle--active', '');
                }
            });

            const activeCircles = this.progressbar.getElements('circle--active');

            circleConnector.css('width', ((activeCircles.length - 1) / (stepCircles.length - 1)) * 100 + '%');
        }
    });
});
define('rebrush/components/blocks/gigaset/shipping/sales/order/shipment/return',[
    'jquery',
    'cartware/components/_base',
    'cartware/jquery/bem'
], function(
    $,
    Component,
    BEM
) {
    const BLOCK_NAME = 'gigaset-shipping-sales-order-shipment-return';

    function GigasetShippingSalesOrderShipmentReturn(block) {
        this.block = BEM.block(block);
        this.form = this.block.find('form');
        this.init();
    }

    GigasetShippingSalesOrderShipmentReturn.prototype = {
        init: function() {
            this.block.on('click', 'button[value^="view/"]', (e) => {
                e.preventDefault();

                const button = $(e.currentTarget);
                const intentInput = $(`<input type="hidden" name="${button.attr('name')}" value="${button.attr('value')}" />"`);

                const popup = window.open('', 'returnLabelPopup', 'width=600,height=400');

                this.form.append(intentInput);

                this.form.attr('target', 'returnLabelPopup');
                this.form.submit();

                this.form.attr('target', null);
                intentInput.remove();

                // const button = $(e.target);
                //
                // // Create a new window
                //
                //
                // // Create a form in the popup
                // const popupForm = document.createElement('form');
                // popupForm.method = 'POST';
                // popupForm.action = this.form.attr('action');
                //
                // // Copy all form inputs to the popup form
                // const originalForm = this.form.get(0);
                // const inputs = originalForm.querySelectorAll('input, select, textarea');
                // inputs.forEach(input => {
                //     const clonedInput = input.cloneNode(true);
                //     popupForm.appendChild(clonedInput);
                // });
                //
                // $(`<input type="hidden" name="${button.attr('name')}" value="${button.attr('name')}" />`).appendTo(popupForm);
                //
                // // Add the form to popup and submit it
                // popup.document.body.appendChild(popupForm);
                // // popupForm.submit();
            });
        }
    };

    return Component.create({
        $init: function() {
            BEM.block(BLOCK_NAME).each(function() {
                new GigasetShippingSalesOrderShipmentReturn(this);
            });
        }
    });
});
define('rebrush/components/customer/account/order/return/popup',[
    'jquery',
    'lodash',
    'cartware/components/_base',
    'cartware/jquery/bem',
    'magnific-popup'
], function (
    $,
    _,
    Component,
    BEM
) {
    return Component.create({
        returnPopupTriggerClass: BEM.buildElementClassname('order', 'order-return-popup-trigger'),
        returnPopupContentClass: BEM.buildElementClassname('order', 'order-return-popup'),

        $init: function () {
            this.initializeEvents();
        },

        initializeEvents: function () {
            $(document.body).on('click', '.' + this.returnPopupTriggerClass, _.bind(this.onClickReturnPopupTrigger, this));
        },

        onClickReturnPopupTrigger: function (e) {
            var element = $(e.currentTarget),
                orderId = element.data('order-id'),
                popupTemplate = $('.' + this.returnPopupContentClass + '[data-order-id="' + orderId + '"]');

            e.preventDefault();
            this.renderMagnificPopup(popupTemplate.find('.' + BEM.buildBlockClassname('order-return-popup')), null, null);
        },

        renderMagnificPopup: function (body, onOpenCallback, onCloseCallback) {
            $.magnificPopup.open({
                mainClass: null,
                items: {
                    src: body,
                    type: 'inline',
                },
                closeBtnInside: true,
                callbacks: {
                    open: function () {

                        if (typeof onOpenCallback === 'function') {
                            onOpenCallback();
                        }

                        if (typeof onCloseCallback === 'function') {
                            $.magnificPopup.instance.close = function() {
                                onCloseCallback();
                                // Call the original close method to close the popup
                                $.magnificPopup.proto.close.call(this);
                            };
                        }
                    }
                }
            });
        }
    });
});

/*!
 * pickadate.js v3.5.5, 2015/02/08
 * By Amsul, http://amsul.ca
 * Hosted on http://amsul.github.io/pickadate.js
 * Licensed under MIT
 */

(function ( factory ) {

    // AMD.
    if ( typeof define == 'function' && define.amd )
        define( 'picker', ['jquery'], factory )

    // Node.js/browserify.
    else if ( typeof exports == 'object' )
        module.exports = factory( require('jquery') )

    // Browser globals.
    else this.Picker = factory( jQuery )

}(function( $ ) {

var $window = $( window )
var $document = $( document )
var $html = $( document.documentElement )
var supportsTransitions = document.body.style.transition != null


/**
 * The picker constructor that creates a blank picker.
 */
function PickerConstructor( ELEMENT, NAME, COMPONENT, OPTIONS ) {

    // If there’s no element, return the picker constructor.
    if ( !ELEMENT ) return PickerConstructor


    var
        IS_DEFAULT_THEME = false,


        // The state of the picker.
        STATE = {
            id: ELEMENT.id || 'P' + Math.abs( ~~(Math.random() * new Date()) )
        },


        // Merge the defaults and options passed.
        SETTINGS = COMPONENT ? $.extend( true, {}, COMPONENT.defaults, OPTIONS ) : OPTIONS || {},


        // Merge the default classes with the settings classes.
        CLASSES = $.extend( {}, PickerConstructor.klasses(), SETTINGS.klass ),


        // The element node wrapper into a jQuery object.
        $ELEMENT = $( ELEMENT ),


        // Pseudo picker constructor.
        PickerInstance = function() {
            return this.start()
        },


        // The picker prototype.
        P = PickerInstance.prototype = {

            constructor: PickerInstance,

            $node: $ELEMENT,


            /**
             * Initialize everything
             */
            start: function() {

                // If it’s already started, do nothing.
                if ( STATE && STATE.start ) return P


                // Update the picker states.
                STATE.methods = {}
                STATE.start = true
                STATE.open = false
                STATE.type = ELEMENT.type


                // Confirm focus state, convert into text input to remove UA stylings,
                // and set as readonly to prevent keyboard popup.
                ELEMENT.autofocus = ELEMENT == getActiveElement()
                ELEMENT.readOnly = !SETTINGS.editable
                ELEMENT.id = ELEMENT.id || STATE.id
                if ( ELEMENT.type != 'text' ) {
                    ELEMENT.type = 'text'
                }


                // Create a new picker component with the settings.
                P.component = new COMPONENT(P, SETTINGS)


                // Create the picker root and then prepare it.
                P.$root = $( '<div class="' + CLASSES.picker + '" id="' + ELEMENT.id + '_root" />' )
                prepareElementRoot()


                // Create the picker holder and then prepare it.
                P.$holder = $( createWrappedComponent() ).appendTo( P.$root )
                prepareElementHolder()


                // If there’s a format for the hidden input element, create the element.
                if ( SETTINGS.formatSubmit ) {
                    prepareElementHidden()
                }


                // Prepare the input element.
                prepareElement()


                // Insert the hidden input as specified in the settings.
                if ( SETTINGS.containerHidden ) $( SETTINGS.containerHidden ).append( P._hidden )
                else $ELEMENT.after( P._hidden )


                // Insert the root as specified in the settings.
                if ( SETTINGS.container ) $( SETTINGS.container ).append( P.$root )
                else $ELEMENT.after( P.$root )


                // Bind the default component and settings events.
                P.on({
                    start: P.component.onStart,
                    render: P.component.onRender,
                    stop: P.component.onStop,
                    open: P.component.onOpen,
                    close: P.component.onClose,
                    set: P.component.onSet
                }).on({
                    start: SETTINGS.onStart,
                    render: SETTINGS.onRender,
                    stop: SETTINGS.onStop,
                    open: SETTINGS.onOpen,
                    close: SETTINGS.onClose,
                    set: SETTINGS.onSet
                })


                // Once we’re all set, check the theme in use.
                IS_DEFAULT_THEME = isUsingDefaultTheme( P.$holder[0] )


                // If the element has autofocus, open the picker.
                if ( ELEMENT.autofocus ) {
                    P.open()
                }


                // Trigger queued the “start” and “render” events.
                return P.trigger( 'start' ).trigger( 'render' )
            }, //start


            /**
             * Render a new picker
             */
            render: function( entireComponent ) {

                // Insert a new component holder in the root or box.
                if ( entireComponent ) {
                    P.$holder = createWrappedComponent()
                    P.$root.html( P.$holder )
                }
                else P.$root.find( '.' + CLASSES.box ).html( P.component.nodes( STATE.open ) )

                // Trigger the queued “render” events.
                return P.trigger( 'render' )
            }, //render


            /**
             * Destroy everything
             */
            stop: function() {

                // If it’s already stopped, do nothing.
                if ( !STATE.start ) return P

                // Then close the picker.
                P.close()

                // Remove the hidden field.
                if ( P._hidden ) {
                    P._hidden.parentNode.removeChild( P._hidden )
                }

                // Remove the root.
                P.$root.remove()

                // Remove the input class, remove the stored data, and unbind
                // the events (after a tick for IE - see `P.close`).
                $ELEMENT.removeClass( CLASSES.input ).removeData( NAME )
                setTimeout( function() {
                    $ELEMENT.off( '.' + STATE.id )
                }, 0)

                // Restore the element state
                ELEMENT.type = STATE.type
                ELEMENT.readOnly = false

                // Trigger the queued “stop” events.
                P.trigger( 'stop' )

                // Reset the picker states.
                STATE.methods = {}
                STATE.start = false

                return P
            }, //stop


            /**
             * Open up the picker
             */
            open: function( dontGiveFocus ) {

                // If it’s already open, do nothing.
                if ( STATE.open ) return P

                // Add the “active” class.
                $ELEMENT.addClass( CLASSES.active )
                aria( ELEMENT, 'expanded', true )

                // * A Firefox bug, when `html` has `overflow:hidden`, results in
                //   killing transitions :(. So add the “opened” state on the next tick.
                //   Bug: https://bugzilla.mozilla.org/show_bug.cgi?id=625289
                setTimeout( function() {

                    // Add the “opened” class to the picker root.
                    P.$root.addClass( CLASSES.opened )
                    aria( P.$root[0], 'hidden', false )

                }, 0 )

                // If we have to give focus, bind the element and doc events.
                if ( dontGiveFocus !== false ) {

                    // Set it as open.
                    STATE.open = true

                    // Prevent the page from scrolling.
                    if ( IS_DEFAULT_THEME ) {
                        $html.
                            css( 'overflow', 'hidden' ).
                            css( 'padding-right', '+=' + getScrollbarWidth() )
                    }

                    // Pass focus to the root element’s jQuery object.
                    focusPickerOnceOpened()

                    // Bind the document events.
                    $document.on( 'click.' + STATE.id + ' focusin.' + STATE.id, function( event ) {

                        var target = event.target

                        // If the target of the event is not the element, close the picker picker.
                        // * Don’t worry about clicks or focusins on the root because those don’t bubble up.
                        //   Also, for Firefox, a click on an `option` element bubbles up directly
                        //   to the doc. So make sure the target wasn't the doc.
                        // * In Firefox stopPropagation() doesn’t prevent right-click events from bubbling,
                        //   which causes the picker to unexpectedly close when right-clicking it. So make
                        //   sure the event wasn’t a right-click.
                        if ( target != ELEMENT && target != document && event.which != 3 ) {

                            // If the target was the holder that covers the screen,
                            // keep the element focused to maintain tabindex.
                            P.close( target === P.$holder[0] )
                        }

                    }).on( 'keydown.' + STATE.id, function( event ) {

                        var
                            // Get the keycode.
                            keycode = event.keyCode,

                            // Translate that to a selection change.
                            keycodeToMove = P.component.key[ keycode ],

                            // Grab the target.
                            target = event.target


                        // On escape, close the picker and give focus.
                        if ( keycode == 27 ) {
                            P.close( true )
                        }


                        // Check if there is a key movement or “enter” keypress on the element.
                        else if ( target == P.$holder[0] && ( keycodeToMove || keycode == 13 ) ) {

                            // Prevent the default action to stop page movement.
                            event.preventDefault()

                            // Trigger the key movement action.
                            if ( keycodeToMove ) {
                                PickerConstructor._.trigger( P.component.key.go, P, [ PickerConstructor._.trigger( keycodeToMove ) ] )
                            }

                            // On “enter”, if the highlighted item isn’t disabled, set the value and close.
                            else if ( !P.$root.find( '.' + CLASSES.highlighted ).hasClass( CLASSES.disabled ) ) {
                                P.set( 'select', P.component.item.highlight )
                                if ( SETTINGS.closeOnSelect ) {
                                    P.close( true )
                                }
                            }
                        }


                        // If the target is within the root and “enter” is pressed,
                        // prevent the default action and trigger a click on the target instead.
                        else if ( $.contains( P.$root[0], target ) && keycode == 13 ) {
                            event.preventDefault()
                            target.click()
                        }
                    })
                }

                // Trigger the queued “open” events.
                return P.trigger( 'open' )
            }, //open


            /**
             * Close the picker
             */
            close: function( giveFocus ) {

                // If we need to give focus, do it before changing states.
                if ( giveFocus ) {
                    if ( SETTINGS.editable ) {
                        ELEMENT.focus()
                    }
                    else {
                        // ....ah yes! It would’ve been incomplete without a crazy workaround for IE :|
                        // The focus is triggered *after* the close has completed - causing it
                        // to open again. So unbind and rebind the event at the next tick.
                        P.$holder.off( 'focus.toOpen' ).focus()
                        setTimeout( function() {
                            P.$holder.on( 'focus.toOpen', handleFocusToOpenEvent )
                        }, 0 )
                    }
                }

                // Remove the “active” class.
                $ELEMENT.removeClass( CLASSES.active )
                aria( ELEMENT, 'expanded', false )

                // * A Firefox bug, when `html` has `overflow:hidden`, results in
                //   killing transitions :(. So remove the “opened” state on the next tick.
                //   Bug: https://bugzilla.mozilla.org/show_bug.cgi?id=625289
                setTimeout( function() {

                    // Remove the “opened” and “focused” class from the picker root.
                    P.$root.removeClass( CLASSES.opened + ' ' + CLASSES.focused )
                    aria( P.$root[0], 'hidden', true )

                }, 0 )

                // If it’s already closed, do nothing more.
                if ( !STATE.open ) return P

                // Set it as closed.
                STATE.open = false

                // Allow the page to scroll.
                if ( IS_DEFAULT_THEME ) {
                    $html.
                        css( 'overflow', '' ).
                        css( 'padding-right', '-=' + getScrollbarWidth() )
                }

                // Unbind the document events.
                $document.off( '.' + STATE.id )

                // Trigger the queued “close” events.
                return P.trigger( 'close' )
            }, //close


            /**
             * Clear the values
             */
            clear: function( options ) {
                return P.set( 'clear', null, options )
            }, //clear


            /**
             * Set something
             */
            set: function( thing, value, options ) {

                var thingItem, thingValue,
                    thingIsObject = $.isPlainObject( thing ),
                    thingObject = thingIsObject ? thing : {}

                // Make sure we have usable options.
                options = thingIsObject && $.isPlainObject( value ) ? value : options || {}

                if ( thing ) {

                    // If the thing isn’t an object, make it one.
                    if ( !thingIsObject ) {
                        thingObject[ thing ] = value
                    }

                    // Go through the things of items to set.
                    for ( thingItem in thingObject ) {

                        // Grab the value of the thing.
                        thingValue = thingObject[ thingItem ]

                        // First, if the item exists and there’s a value, set it.
                        if ( thingItem in P.component.item ) {
                            if ( thingValue === undefined ) thingValue = null
                            P.component.set( thingItem, thingValue, options )
                        }

                        // Then, check to update the element value and broadcast a change.
                        if ( thingItem == 'select' || thingItem == 'clear' ) {
                            $ELEMENT.
                                val( thingItem == 'clear' ? '' : P.get( thingItem, SETTINGS.format ) ).
                                trigger( 'change' )
                        }
                    }

                    // Render a new picker.
                    P.render()
                }

                // When the method isn’t muted, trigger queued “set” events and pass the `thingObject`.
                return options.muted ? P : P.trigger( 'set', thingObject )
            }, //set


            /**
             * Get something
             */
            get: function( thing, format ) {

                // Make sure there’s something to get.
                thing = thing || 'value'

                // If a picker state exists, return that.
                if ( STATE[ thing ] != null ) {
                    return STATE[ thing ]
                }

                // Return the submission value, if that.
                if ( thing == 'valueSubmit' ) {
                    if ( P._hidden ) {
                        return P._hidden.value
                    }
                    thing = 'value'
                }

                // Return the value, if that.
                if ( thing == 'value' ) {
                    return ELEMENT.value
                }

                // Check if a component item exists, return that.
                if ( thing in P.component.item ) {
                    if ( typeof format == 'string' ) {
                        var thingValue = P.component.get( thing )
                        return thingValue ?
                            PickerConstructor._.trigger(
                                P.component.formats.toString,
                                P.component,
                                [ format, thingValue ]
                            ) : ''
                    }
                    return P.component.get( thing )
                }
            }, //get



            /**
             * Bind events on the things.
             */
            on: function( thing, method, internal ) {

                var thingName, thingMethod,
                    thingIsObject = $.isPlainObject( thing ),
                    thingObject = thingIsObject ? thing : {}

                if ( thing ) {

                    // If the thing isn’t an object, make it one.
                    if ( !thingIsObject ) {
                        thingObject[ thing ] = method
                    }

                    // Go through the things to bind to.
                    for ( thingName in thingObject ) {

                        // Grab the method of the thing.
                        thingMethod = thingObject[ thingName ]

                        // If it was an internal binding, prefix it.
                        if ( internal ) {
                            thingName = '_' + thingName
                        }

                        // Make sure the thing methods collection exists.
                        STATE.methods[ thingName ] = STATE.methods[ thingName ] || []

                        // Add the method to the relative method collection.
                        STATE.methods[ thingName ].push( thingMethod )
                    }
                }

                return P
            }, //on



            /**
             * Unbind events on the things.
             */
            off: function() {
                var i, thingName,
                    names = arguments;
                for ( i = 0, namesCount = names.length; i < namesCount; i += 1 ) {
                    thingName = names[i]
                    if ( thingName in STATE.methods ) {
                        delete STATE.methods[thingName]
                    }
                }
                return P
            },


            /**
             * Fire off method events.
             */
            trigger: function( name, data ) {
                var _trigger = function( name ) {
                    var methodList = STATE.methods[ name ]
                    if ( methodList ) {
                        methodList.map( function( method ) {
                            PickerConstructor._.trigger( method, P, [ data ] )
                        })
                    }
                }
                _trigger( '_' + name )
                _trigger( name )
                return P
            } //trigger
        } //PickerInstance.prototype


    /**
     * Wrap the picker holder components together.
     */
    function createWrappedComponent() {

        // Create a picker wrapper holder
        return PickerConstructor._.node( 'div',

            // Create a picker wrapper node
            PickerConstructor._.node( 'div',

                // Create a picker frame
                PickerConstructor._.node( 'div',

                    // Create a picker box node
                    PickerConstructor._.node( 'div',

                        // Create the components nodes.
                        P.component.nodes( STATE.open ),

                        // The picker box class
                        CLASSES.box
                    ),

                    // Picker wrap class
                    CLASSES.wrap
                ),

                // Picker frame class
                CLASSES.frame
            ),

            // Picker holder class
            CLASSES.holder,

            'tabindex="-1"'
        ) //endreturn
    } //createWrappedComponent



    /**
     * Prepare the input element with all bindings.
     */
    function prepareElement() {

        $ELEMENT.

            // Store the picker data by component name.
            data(NAME, P).

            // Add the “input” class name.
            addClass(CLASSES.input).

            // If there’s a `data-value`, update the value of the element.
            val( $ELEMENT.data('value') ?
                P.get('select', SETTINGS.format) :
                ELEMENT.value
            )


        // Only bind keydown events if the element isn’t editable.
        if ( !SETTINGS.editable ) {

            $ELEMENT.

                // On focus/click, open the picker.
                on( 'focus.' + STATE.id + ' click.' + STATE.id, function(event) {
                    event.preventDefault()
                    P.open()
                }).

                // Handle keyboard event based on the picker being opened or not.
                on( 'keydown.' + STATE.id, handleKeydownEvent )
        }


        // Update the aria attributes.
        aria(ELEMENT, {
            haspopup: true,
            expanded: false,
            readonly: false,
            owns: ELEMENT.id + '_root'
        })
    }


    /**
     * Prepare the root picker element with all bindings.
     */
    function prepareElementRoot() {
        aria( P.$root[0], 'hidden', true )
    }


     /**
      * Prepare the holder picker element with all bindings.
      */
    function prepareElementHolder() {

        P.$holder.

            on({

                // For iOS8.
                keydown: handleKeydownEvent,

                'focus.toOpen': handleFocusToOpenEvent,

                blur: function() {
                    // Remove the “target” class.
                    $ELEMENT.removeClass( CLASSES.target )
                },

                // When something within the holder is focused, stop from bubbling
                // to the doc and remove the “focused” state from the root.
                focusin: function( event ) {
                    P.$root.removeClass( CLASSES.focused )
                    event.stopPropagation()
                },

                // When something within the holder is clicked, stop it
                // from bubbling to the doc.
                'mousedown click': function( event ) {

                    var target = event.target

                    // Make sure the target isn’t the root holder so it can bubble up.
                    if ( target != P.$holder[0] ) {

                        event.stopPropagation()

                        // * For mousedown events, cancel the default action in order to
                        //   prevent cases where focus is shifted onto external elements
                        //   when using things like jQuery mobile or MagnificPopup (ref: #249 & #120).
                        //   Also, for Firefox, don’t prevent action on the `option` element.
                        if ( event.type == 'mousedown' && !$( target ).is( 'input, select, textarea, button, option' )) {

                            event.preventDefault()

                            // Re-focus onto the holder so that users can click away
                            // from elements focused within the picker.
                            P.$holder[0].focus()
                        }
                    }
                }

            }).

            // If there’s a click on an actionable element, carry out the actions.
            on( 'click', '[data-pick], [data-nav], [data-clear], [data-close]', function() {

                var $target = $( this ),
                    targetData = $target.data(),
                    targetDisabled = $target.hasClass( CLASSES.navDisabled ) || $target.hasClass( CLASSES.disabled ),

                    // * For IE, non-focusable elements can be active elements as well
                    //   (http://stackoverflow.com/a/2684561).
                    activeElement = getActiveElement()
                    activeElement = activeElement && ( activeElement.type || activeElement.href )

                // If it’s disabled or nothing inside is actively focused, re-focus the element.
                if ( targetDisabled || activeElement && !$.contains( P.$root[0], activeElement ) ) {
                    P.$holder[0].focus()
                }

                // If something is superficially changed, update the `highlight` based on the `nav`.
                if ( !targetDisabled && targetData.nav ) {
                    P.set( 'highlight', P.component.item.highlight, { nav: targetData.nav } )
                }

                // If something is picked, set `select` then close with focus.
                else if ( !targetDisabled && 'pick' in targetData ) {
                    P.set( 'select', targetData.pick )
                    if ( SETTINGS.closeOnSelect ) {
                        P.close( true )
                    }
                }

                // If a “clear” button is pressed, empty the values and close with focus.
                else if ( targetData.clear ) {
                    P.clear()
                    if ( SETTINGS.closeOnClear ) {
                        P.close( true )
                    }
                }

                else if ( targetData.close ) {
                    P.close( true )
                }

            }) //P.$holder

    }


     /**
      * Prepare the hidden input element along with all bindings.
      */
    function prepareElementHidden() {

        var name

        if ( SETTINGS.hiddenName === true ) {
            name = ELEMENT.name
            ELEMENT.name = ''
        }
        else {
            name = [
                typeof SETTINGS.hiddenPrefix == 'string' ? SETTINGS.hiddenPrefix : '',
                typeof SETTINGS.hiddenSuffix == 'string' ? SETTINGS.hiddenSuffix : '_submit'
            ]
            name = name[0] + ELEMENT.name + name[1]
        }

        P._hidden = $(
            '<input ' +
            'type=hidden ' +

            // Create the name using the original input’s with a prefix and suffix.
            'name="' + name + '"' +

            // If the element has a value, set the hidden value as well.
            (
                $ELEMENT.data('value') || ELEMENT.value ?
                    ' value="' + P.get('select', SETTINGS.formatSubmit) + '"' :
                    ''
            ) +
            '>'
        )[0]

        $ELEMENT.

            // If the value changes, update the hidden input with the correct format.
            on('change.' + STATE.id, function() {
                P._hidden.value = ELEMENT.value ?
                    P.get('select', SETTINGS.formatSubmit) :
                    ''
            })
    }


    // Wait for transitions to end before focusing the holder. Otherwise, while
    // using the `container` option, the view jumps to the container.
    function focusPickerOnceOpened() {

        if (IS_DEFAULT_THEME && supportsTransitions) {
            P.$holder.find('.' + CLASSES.frame).one('transitionend', function() {
                P.$holder[0].focus()
            })
        }
        else {
            P.$holder[0].focus()
        }
    }


    function handleFocusToOpenEvent(event) {

        // Stop the event from propagating to the doc.
        event.stopPropagation()

        // Add the “target” class.
        $ELEMENT.addClass( CLASSES.target )

        // Add the “focused” class to the root.
        P.$root.addClass( CLASSES.focused )

        // And then finally open the picker.
        P.open()
    }


    // For iOS8.
    function handleKeydownEvent( event ) {

        var keycode = event.keyCode,

            // Check if one of the delete keys was pressed.
            isKeycodeDelete = /^(8|46)$/.test(keycode)

        // For some reason IE clears the input value on “escape”.
        if ( keycode == 27 ) {
            P.close( true )
            return false
        }

        // Check if `space` or `delete` was pressed or the picker is closed with a key movement.
        if ( keycode == 32 || isKeycodeDelete || !STATE.open && P.component.key[keycode] ) {

            // Prevent it from moving the page and bubbling to doc.
            event.preventDefault()
            event.stopPropagation()

            // If `delete` was pressed, clear the values and close the picker.
            // Otherwise open the picker.
            if ( isKeycodeDelete ) { P.clear().close() }
            else { P.open() }
        }
    }


    // Return a new picker instance.
    return new PickerInstance()
} //PickerConstructor



/**
 * The default classes and prefix to use for the HTML classes.
 */
PickerConstructor.klasses = function( prefix ) {
    prefix = prefix || 'picker'
    return {

        picker: prefix,
        opened: prefix + '--opened',
        focused: prefix + '--focused',

        input: prefix + '__input',
        active: prefix + '__input--active',
        target: prefix + '__input--target',

        holder: prefix + '__holder',

        frame: prefix + '__frame',
        wrap: prefix + '__wrap',

        box: prefix + '__box'
    }
} //PickerConstructor.klasses



/**
 * Check if the default theme is being used.
 */
function isUsingDefaultTheme( element ) {

    var theme,
        prop = 'position'

    // For IE.
    if ( element.currentStyle ) {
        theme = element.currentStyle[prop]
    }

    // For normal browsers.
    else if ( window.getComputedStyle ) {
        theme = getComputedStyle( element )[prop]
    }

    return theme == 'fixed'
}



/**
 * Get the width of the browser’s scrollbar.
 * Taken from: https://github.com/VodkaBears/Remodal/blob/master/src/jquery.remodal.js
 */
function getScrollbarWidth() {

    if ( $html.height() <= $window.height() ) {
        return 0
    }

    var $outer = $( '<div style="visibility:hidden;width:100px" />' ).
        appendTo( 'body' )

    // Get the width without scrollbars.
    var widthWithoutScroll = $outer[0].offsetWidth

    // Force adding scrollbars.
    $outer.css( 'overflow', 'scroll' )

    // Add the inner div.
    var $inner = $( '<div style="width:100%" />' ).appendTo( $outer )

    // Get the width with scrollbars.
    var widthWithScroll = $inner[0].offsetWidth

    // Remove the divs.
    $outer.remove()

    // Return the difference between the widths.
    return widthWithoutScroll - widthWithScroll
}



/**
 * PickerConstructor helper methods.
 */
PickerConstructor._ = {

    /**
     * Create a group of nodes. Expects:
     * `
        {
            min:    {Integer},
            max:    {Integer},
            i:      {Integer},
            node:   {String},
            item:   {Function}
        }
     * `
     */
    group: function( groupObject ) {

        var
            // Scope for the looped object
            loopObjectScope,

            // Create the nodes list
            nodesList = '',

            // The counter starts from the `min`
            counter = PickerConstructor._.trigger( groupObject.min, groupObject )


        // Loop from the `min` to `max`, incrementing by `i`
        for ( ; counter <= PickerConstructor._.trigger( groupObject.max, groupObject, [ counter ] ); counter += groupObject.i ) {

            // Trigger the `item` function within scope of the object
            loopObjectScope = PickerConstructor._.trigger( groupObject.item, groupObject, [ counter ] )

            // Splice the subgroup and create nodes out of the sub nodes
            nodesList += PickerConstructor._.node(
                groupObject.node,
                loopObjectScope[ 0 ],   // the node
                loopObjectScope[ 1 ],   // the classes
                loopObjectScope[ 2 ]    // the attributes
            )
        }

        // Return the list of nodes
        return nodesList
    }, //group


    /**
     * Create a dom node string
     */
    node: function( wrapper, item, klass, attribute ) {

        // If the item is false-y, just return an empty string
        if ( !item ) return ''

        // If the item is an array, do a join
        item = $.isArray( item ) ? item.join( '' ) : item

        // Check for the class
        klass = klass ? ' class="' + klass + '"' : ''

        // Check for any attributes
        attribute = attribute ? ' ' + attribute : ''

        // Return the wrapped item
        return '<' + wrapper + klass + attribute + '>' + item + '</' + wrapper + '>'
    }, //node


    /**
     * Lead numbers below 10 with a zero.
     */
    lead: function( number ) {
        return ( number < 10 ? '0': '' ) + number
    },


    /**
     * Trigger a function otherwise return the value.
     */
    trigger: function( callback, scope, args ) {
        return typeof callback == 'function' ? callback.apply( scope, args || [] ) : callback
    },


    /**
     * If the second character is a digit, length is 2 otherwise 1.
     */
    digits: function( string ) {
        return ( /\d/ ).test( string[ 1 ] ) ? 2 : 1
    },


    /**
     * Tell if something is a date object.
     */
    isDate: function( value ) {
        return {}.toString.call( value ).indexOf( 'Date' ) > -1 && this.isInteger( value.getDate() )
    },


    /**
     * Tell if something is an integer.
     */
    isInteger: function( value ) {
        return {}.toString.call( value ).indexOf( 'Number' ) > -1 && value % 1 === 0
    },


    /**
     * Create ARIA attribute strings.
     */
    ariaAttr: ariaAttr
} //PickerConstructor._



/**
 * Extend the picker with a component and defaults.
 */
PickerConstructor.extend = function( name, Component ) {

    // Extend jQuery.
    $.fn[ name ] = function( options, action ) {

        // Grab the component data.
        var componentData = this.data( name )

        // If the picker is requested, return the data object.
        if ( options == 'picker' ) {
            return componentData
        }

        // If the component data exists and `options` is a string, carry out the action.
        if ( componentData && typeof options == 'string' ) {
            return PickerConstructor._.trigger( componentData[ options ], componentData, [ action ] )
        }

        // Otherwise go through each matched element and if the component
        // doesn’t exist, create a new picker using `this` element
        // and merging the defaults and options with a deep copy.
        return this.each( function() {
            var $this = $( this )
            if ( !$this.data( name ) ) {
                new PickerConstructor( this, name, Component, options )
            }
        })
    }

    // Set the defaults.
    $.fn[ name ].defaults = Component.defaults
} //PickerConstructor.extend



function aria(element, attribute, value) {
    if ( $.isPlainObject(attribute) ) {
        for ( var key in attribute ) {
            ariaSet(element, key, attribute[key])
        }
    }
    else {
        ariaSet(element, attribute, value)
    }
}
function ariaSet(element, attribute, value) {
    element.setAttribute(
        (attribute == 'role' ? '' : 'aria-') + attribute,
        value
    )
}
function ariaAttr(attribute, data) {
    if ( !$.isPlainObject(attribute) ) {
        attribute = { attribute: data }
    }
    data = ''
    for ( var key in attribute ) {
        var attr = (key == 'role' ? '' : 'aria-') + key,
            attrVal = attribute[key]
        data += attrVal == null ? '' : attr + '="' + attribute[key] + '"'
    }
    return data
}

// IE8 bug throws an error for activeElements within iframes.
function getActiveElement() {
    try {
        return document.activeElement
    } catch ( err ) { }
}



// Expose the picker constructor.
return PickerConstructor


}));




/*!
 * Date picker for pickadate.js v3.5.5
 * http://amsul.github.io/pickadate.js/date.htm
 */

(function ( factory ) {

    // AMD.
    if ( typeof define == 'function' && define.amd )
        define( 'picker-date',['picker', 'jquery'], factory )

    // Node.js/browserify.
    else if ( typeof exports == 'object' )
        module.exports = factory( require('./picker.js'), require('jquery') )

    // Browser globals.
    else factory( Picker, jQuery )

}(function( Picker, $ ) {


/**
 * Globals and constants
 */
var DAYS_IN_WEEK = 7,
    WEEKS_IN_CALENDAR = 6,
    _ = Picker._



/**
 * The date picker constructor
 */
function DatePicker( picker, settings ) {

    var calendar = this,
        element = picker.$node[ 0 ],
        elementValue = element.value,
        elementDataValue = picker.$node.data( 'value' ),
        valueString = elementDataValue || elementValue,
        formatString = elementDataValue ? settings.formatSubmit : settings.format,
        isRTL = function() {

            return element.currentStyle ?

                // For IE.
                element.currentStyle.direction == 'rtl' :

                // For normal browsers.
                getComputedStyle( picker.$root[0] ).direction == 'rtl'
        }

    calendar.settings = settings
    calendar.$node = picker.$node

    // The queue of methods that will be used to build item objects.
    calendar.queue = {
        min: 'measure create',
        max: 'measure create',
        now: 'now create',
        select: 'parse create validate',
        highlight: 'parse navigate create validate',
        view: 'parse create validate viewset',
        disable: 'deactivate',
        enable: 'activate'
    }

    // The component's item object.
    calendar.item = {}

    calendar.item.clear = null
    calendar.item.disable = ( settings.disable || [] ).slice( 0 )
    calendar.item.enable = -(function( collectionDisabled ) {
        return collectionDisabled[ 0 ] === true ? collectionDisabled.shift() : -1
    })( calendar.item.disable )

    calendar.
        set( 'min', settings.min ).
        set( 'max', settings.max ).
        set( 'now' )

    // When there’s a value, set the `select`, which in turn
    // also sets the `highlight` and `view`.
    if ( valueString ) {
        calendar.set( 'select', valueString, {
            format: formatString,
            defaultValue: true
        })
    }

    // If there’s no value, default to highlighting “today”.
    else {
        calendar.
            set( 'select', null ).
            set( 'highlight', calendar.item.now )
    }


    // The keycode to movement mapping.
    calendar.key = {
        40: 7, // Down
        38: -7, // Up
        39: function() { return isRTL() ? -1 : 1 }, // Right
        37: function() { return isRTL() ? 1 : -1 }, // Left
        go: function( timeChange ) {
            var highlightedObject = calendar.item.highlight,
                targetDate = new Date( highlightedObject.year, highlightedObject.month, highlightedObject.date + timeChange )
            calendar.set(
                'highlight',
                targetDate,
                { interval: timeChange }
            )
            this.render()
        }
    }


    // Bind some picker events.
    picker.
        on( 'render', function() {
            picker.$root.find( '.' + settings.klass.selectMonth ).on( 'change', function() {
                var value = this.value
                if ( value ) {
                    picker.set( 'highlight', [ picker.get( 'view' ).year, value, picker.get( 'highlight' ).date ] )
                    picker.$root.find( '.' + settings.klass.selectMonth ).trigger( 'focus' )
                }
            })
            picker.$root.find( '.' + settings.klass.selectYear ).on( 'change', function() {
                var value = this.value
                if ( value ) {
                    picker.set( 'highlight', [ value, picker.get( 'view' ).month, picker.get( 'highlight' ).date ] )
                    picker.$root.find( '.' + settings.klass.selectYear ).trigger( 'focus' )
                }
            })
        }, 1 ).
        on( 'open', function() {
            var includeToday = ''
            if ( calendar.disabled( calendar.get('now') ) ) {
                includeToday = ':not(.' + settings.klass.buttonToday + ')'
            }
            picker.$root.find( 'button' + includeToday + ', select' ).attr( 'disabled', false )
        }, 1 ).
        on( 'close', function() {
            picker.$root.find( 'button, select' ).attr( 'disabled', true )
        }, 1 )

} //DatePicker


/**
 * Set a datepicker item object.
 */
DatePicker.prototype.set = function( type, value, options ) {

    var calendar = this,
        calendarItem = calendar.item

    // If the value is `null` just set it immediately.
    if ( value === null ) {
        if ( type == 'clear' ) type = 'select'
        calendarItem[ type ] = value
        return calendar
    }

    // Otherwise go through the queue of methods, and invoke the functions.
    // Update this as the time unit, and set the final value as this item.
    // * In the case of `enable`, keep the queue but set `disable` instead.
    //   And in the case of `flip`, keep the queue but set `enable` instead.
    calendarItem[ ( type == 'enable' ? 'disable' : type == 'flip' ? 'enable' : type ) ] = calendar.queue[ type ].split( ' ' ).map( function( method ) {
        value = calendar[ method ]( type, value, options )
        return value
    }).pop()

    // Check if we need to cascade through more updates.
    if ( type == 'select' ) {
        calendar.set( 'highlight', calendarItem.select, options )
    }
    else if ( type == 'highlight' ) {
        calendar.set( 'view', calendarItem.highlight, options )
    }
    else if ( type.match( /^(flip|min|max|disable|enable)$/ ) ) {
        if ( calendarItem.select && calendar.disabled( calendarItem.select ) ) {
            calendar.set( 'select', calendarItem.select, options )
        }
        if ( calendarItem.highlight && calendar.disabled( calendarItem.highlight ) ) {
            calendar.set( 'highlight', calendarItem.highlight, options )
        }
    }

    return calendar
} //DatePicker.prototype.set


/**
 * Get a datepicker item object.
 */
DatePicker.prototype.get = function( type ) {
    return this.item[ type ]
} //DatePicker.prototype.get


/**
 * Create a picker date object.
 */
DatePicker.prototype.create = function( type, value, options ) {

    var isInfiniteValue,
        calendar = this

    // If there’s no value, use the type as the value.
    value = value === undefined ? type : value


    // If it’s infinity, update the value.
    if ( value == -Infinity || value == Infinity ) {
        isInfiniteValue = value
    }

    // If it’s an object, use the native date object.
    else if ( $.isPlainObject( value ) && _.isInteger( value.pick ) ) {
        value = value.obj
    }

    // If it’s an array, convert it into a date and make sure
    // that it’s a valid date – otherwise default to today.
    else if ( $.isArray( value ) ) {
        value = new Date( value[ 0 ], value[ 1 ], value[ 2 ] )
        value = _.isDate( value ) ? value : calendar.create().obj
    }

    // If it’s a number or date object, make a normalized date.
    else if ( _.isInteger( value ) || _.isDate( value ) ) {
        value = calendar.normalize( new Date( value ), options )
    }

    // If it’s a literal true or any other case, set it to now.
    else /*if ( value === true )*/ {
        value = calendar.now( type, value, options )
    }

    // Return the compiled object.
    return {
        year: isInfiniteValue || value.getFullYear(),
        month: isInfiniteValue || value.getMonth(),
        date: isInfiniteValue || value.getDate(),
        day: isInfiniteValue || value.getDay(),
        obj: isInfiniteValue || value,
        pick: isInfiniteValue || value.getTime()
    }
} //DatePicker.prototype.create


/**
 * Create a range limit object using an array, date object,
 * literal “true”, or integer relative to another time.
 */
DatePicker.prototype.createRange = function( from, to ) {

    var calendar = this,
        createDate = function( date ) {
            if ( date === true || $.isArray( date ) || _.isDate( date ) ) {
                return calendar.create( date )
            }
            return date
        }

    // Create objects if possible.
    if ( !_.isInteger( from ) ) {
        from = createDate( from )
    }
    if ( !_.isInteger( to ) ) {
        to = createDate( to )
    }

    // Create relative dates.
    if ( _.isInteger( from ) && $.isPlainObject( to ) ) {
        from = [ to.year, to.month, to.date + from ];
    }
    else if ( _.isInteger( to ) && $.isPlainObject( from ) ) {
        to = [ from.year, from.month, from.date + to ];
    }

    return {
        from: createDate( from ),
        to: createDate( to )
    }
} //DatePicker.prototype.createRange


/**
 * Check if a date unit falls within a date range object.
 */
DatePicker.prototype.withinRange = function( range, dateUnit ) {
    range = this.createRange(range.from, range.to)
    return dateUnit.pick >= range.from.pick && dateUnit.pick <= range.to.pick
}


/**
 * Check if two date range objects overlap.
 */
DatePicker.prototype.overlapRanges = function( one, two ) {

    var calendar = this

    // Convert the ranges into comparable dates.
    one = calendar.createRange( one.from, one.to )
    two = calendar.createRange( two.from, two.to )

    return calendar.withinRange( one, two.from ) || calendar.withinRange( one, two.to ) ||
        calendar.withinRange( two, one.from ) || calendar.withinRange( two, one.to )
}


/**
 * Get the date today.
 */
DatePicker.prototype.now = function( type, value, options ) {
    value = new Date()
    if ( options && options.rel ) {
        value.setDate( value.getDate() + options.rel )
    }
    return this.normalize( value, options )
}


/**
 * Navigate to next/prev month.
 */
DatePicker.prototype.navigate = function( type, value, options ) {

    var targetDateObject,
        targetYear,
        targetMonth,
        targetDate,
        isTargetArray = $.isArray( value ),
        isTargetObject = $.isPlainObject( value ),
        viewsetObject = this.item.view/*,
        safety = 100*/


    if ( isTargetArray || isTargetObject ) {

        if ( isTargetObject ) {
            targetYear = value.year
            targetMonth = value.month
            targetDate = value.date
        }
        else {
            targetYear = +value[0]
            targetMonth = +value[1]
            targetDate = +value[2]
        }

        // If we’re navigating months but the view is in a different
        // month, navigate to the view’s year and month.
        if ( options && options.nav && viewsetObject && viewsetObject.month !== targetMonth ) {
            targetYear = viewsetObject.year
            targetMonth = viewsetObject.month
        }

        // Figure out the expected target year and month.
        targetDateObject = new Date( targetYear, targetMonth + ( options && options.nav ? options.nav : 0 ), 1 )
        targetYear = targetDateObject.getFullYear()
        targetMonth = targetDateObject.getMonth()

        // If the month we’re going to doesn’t have enough days,
        // keep decreasing the date until we reach the month’s last date.
        while ( /*safety &&*/ new Date( targetYear, targetMonth, targetDate ).getMonth() !== targetMonth ) {
            targetDate -= 1
            /*safety -= 1
            if ( !safety ) {
                throw 'Fell into an infinite loop while navigating to ' + new Date( targetYear, targetMonth, targetDate ) + '.'
            }*/
        }

        value = [ targetYear, targetMonth, targetDate ]
    }

    return value
} //DatePicker.prototype.navigate


/**
 * Normalize a date by setting the hours to midnight.
 */
DatePicker.prototype.normalize = function( value/*, options*/ ) {
    value.setHours( 0, 0, 0, 0 )
    return value
}


/**
 * Measure the range of dates.
 */
DatePicker.prototype.measure = function( type, value/*, options*/ ) {

    var calendar = this

    // If it’s anything false-y, remove the limits.
    if ( !value ) {
        value = type == 'min' ? -Infinity : Infinity
    }

    // If it’s a string, parse it.
    else if ( typeof value == 'string' ) {
        value = calendar.parse( type, value )
    }

    // If it's an integer, get a date relative to today.
    else if ( _.isInteger( value ) ) {
        value = calendar.now( type, value, { rel: value } )
    }

    return value
} ///DatePicker.prototype.measure


/**
 * Create a viewset object based on navigation.
 */
DatePicker.prototype.viewset = function( type, dateObject/*, options*/ ) {
    return this.create([ dateObject.year, dateObject.month, 1 ])
}


/**
 * Validate a date as enabled and shift if needed.
 */
DatePicker.prototype.validate = function( type, dateObject, options ) {

    var calendar = this,

        // Keep a reference to the original date.
        originalDateObject = dateObject,

        // Make sure we have an interval.
        interval = options && options.interval ? options.interval : 1,

        // Check if the calendar enabled dates are inverted.
        isFlippedBase = calendar.item.enable === -1,

        // Check if we have any enabled dates after/before now.
        hasEnabledBeforeTarget, hasEnabledAfterTarget,

        // The min & max limits.
        minLimitObject = calendar.item.min,
        maxLimitObject = calendar.item.max,

        // Check if we’ve reached the limit during shifting.
        reachedMin, reachedMax,

        // Check if the calendar is inverted and at least one weekday is enabled.
        hasEnabledWeekdays = isFlippedBase && calendar.item.disable.filter( function( value ) {

            // If there’s a date, check where it is relative to the target.
            if ( $.isArray( value ) ) {
                var dateTime = calendar.create( value ).pick
                if ( dateTime < dateObject.pick ) hasEnabledBeforeTarget = true
                else if ( dateTime > dateObject.pick ) hasEnabledAfterTarget = true
            }

            // Return only integers for enabled weekdays.
            return _.isInteger( value )
        }).length/*,

        safety = 100*/



    // Cases to validate for:
    // [1] Not inverted and date disabled.
    // [2] Inverted and some dates enabled.
    // [3] Not inverted and out of range.
    //
    // Cases to **not** validate for:
    // • Navigating months.
    // • Not inverted and date enabled.
    // • Inverted and all dates disabled.
    // • ..and anything else.
    if ( !options || (!options.nav && !options.defaultValue) ) if (
        /* 1 */ ( !isFlippedBase && calendar.disabled( dateObject ) ) ||
        /* 2 */ ( isFlippedBase && calendar.disabled( dateObject ) && ( hasEnabledWeekdays || hasEnabledBeforeTarget || hasEnabledAfterTarget ) ) ||
        /* 3 */ ( !isFlippedBase && (dateObject.pick <= minLimitObject.pick || dateObject.pick >= maxLimitObject.pick) )
    ) {


        // When inverted, flip the direction if there aren’t any enabled weekdays
        // and there are no enabled dates in the direction of the interval.
        if ( isFlippedBase && !hasEnabledWeekdays && ( ( !hasEnabledAfterTarget && interval > 0 ) || ( !hasEnabledBeforeTarget && interval < 0 ) ) ) {
            interval *= -1
        }


        // Keep looping until we reach an enabled date.
        while ( /*safety &&*/ calendar.disabled( dateObject ) ) {

            /*safety -= 1
            if ( !safety ) {
                throw 'Fell into an infinite loop while validating ' + dateObject.obj + '.'
            }*/


            // If we’ve looped into the next/prev month with a large interval, return to the original date and flatten the interval.
            if ( Math.abs( interval ) > 1 && ( dateObject.month < originalDateObject.month || dateObject.month > originalDateObject.month ) ) {
                dateObject = originalDateObject
                interval = interval > 0 ? 1 : -1
            }


            // If we’ve reached the min/max limit, reverse the direction, flatten the interval and set it to the limit.
            if ( dateObject.pick <= minLimitObject.pick ) {
                reachedMin = true
                interval = 1
                dateObject = calendar.create([
                    minLimitObject.year,
                    minLimitObject.month,
                    minLimitObject.date + (dateObject.pick === minLimitObject.pick ? 0 : -1)
                ])
            }
            else if ( dateObject.pick >= maxLimitObject.pick ) {
                reachedMax = true
                interval = -1
                dateObject = calendar.create([
                    maxLimitObject.year,
                    maxLimitObject.month,
                    maxLimitObject.date + (dateObject.pick === maxLimitObject.pick ? 0 : 1)
                ])
            }


            // If we’ve reached both limits, just break out of the loop.
            if ( reachedMin && reachedMax ) {
                break
            }


            // Finally, create the shifted date using the interval and keep looping.
            dateObject = calendar.create([ dateObject.year, dateObject.month, dateObject.date + interval ])
        }

    } //endif


    // Return the date object settled on.
    return dateObject
} //DatePicker.prototype.validate


/**
 * Check if a date is disabled.
 */
DatePicker.prototype.disabled = function( dateToVerify ) {

    var
        calendar = this,

        // Filter through the disabled dates to check if this is one.
        isDisabledMatch = calendar.item.disable.filter( function( dateToDisable ) {

            // If the date is a number, match the weekday with 0index and `firstDay` check.
            if ( _.isInteger( dateToDisable ) ) {
                return dateToVerify.day === ( calendar.settings.firstDay ? dateToDisable : dateToDisable - 1 ) % 7
            }

            // If it’s an array or a native JS date, create and match the exact date.
            if ( $.isArray( dateToDisable ) || _.isDate( dateToDisable ) ) {
                return dateToVerify.pick === calendar.create( dateToDisable ).pick
            }

            // If it’s an object, match a date within the “from” and “to” range.
            if ( $.isPlainObject( dateToDisable ) ) {
                return calendar.withinRange( dateToDisable, dateToVerify )
            }
        })

    // If this date matches a disabled date, confirm it’s not inverted.
    isDisabledMatch = isDisabledMatch.length && !isDisabledMatch.filter(function( dateToDisable ) {
        return $.isArray( dateToDisable ) && dateToDisable[3] == 'inverted' ||
            $.isPlainObject( dateToDisable ) && dateToDisable.inverted
    }).length

    // Check the calendar “enabled” flag and respectively flip the
    // disabled state. Then also check if it’s beyond the min/max limits.
    return calendar.item.enable === -1 ? !isDisabledMatch : isDisabledMatch ||
        dateToVerify.pick < calendar.item.min.pick ||
        dateToVerify.pick > calendar.item.max.pick

} //DatePicker.prototype.disabled


/**
 * Parse a string into a usable type.
 */
DatePicker.prototype.parse = function( type, value, options ) {

    var calendar = this,
        parsingObject = {}

    // If it’s already parsed, we’re good.
    if ( !value || typeof value != 'string' ) {
        return value
    }

    // We need a `.format` to parse the value with.
    if ( !( options && options.format ) ) {
        options = options || {}
        options.format = calendar.settings.format
    }

    // Convert the format into an array and then map through it.
    calendar.formats.toArray( options.format ).map( function( label ) {

        var
            // Grab the formatting label.
            formattingLabel = calendar.formats[ label ],

            // The format length is from the formatting label function or the
            // label length without the escaping exclamation (!) mark.
            formatLength = formattingLabel ? _.trigger( formattingLabel, calendar, [ value, parsingObject ] ) : label.replace( /^!/, '' ).length

        // If there's a format label, split the value up to the format length.
        // Then add it to the parsing object with appropriate label.
        if ( formattingLabel ) {
            parsingObject[ label ] = value.substr( 0, formatLength )
        }

        // Update the value as the substring from format length to end.
        value = value.substr( formatLength )
    })

    // Compensate for month 0index.
    return [
        parsingObject.yyyy || parsingObject.yy,
        +( parsingObject.mm || parsingObject.m ) - 1,
        parsingObject.dd || parsingObject.d
    ]
} //DatePicker.prototype.parse


/**
 * Various formats to display the object in.
 */
DatePicker.prototype.formats = (function() {

    // Return the length of the first word in a collection.
    function getWordLengthFromCollection( string, collection, dateObject ) {

        // Grab the first word from the string.
        // Regex pattern from http://stackoverflow.com/q/150033
        var word = string.match( /[^\x00-\x7F]+|\w+/ )[ 0 ]

        // If there's no month index, add it to the date object
        if ( !dateObject.mm && !dateObject.m ) {
            dateObject.m = collection.indexOf( word ) + 1
        }

        // Return the length of the word.
        return word.length
    }

    // Get the length of the first word in a string.
    function getFirstWordLength( string ) {
        return string.match( /\w+/ )[ 0 ].length
    }

    return {

        d: function( string, dateObject ) {

            // If there's string, then get the digits length.
            // Otherwise return the selected date.
            return string ? _.digits( string ) : dateObject.date
        },
        dd: function( string, dateObject ) {

            // If there's a string, then the length is always 2.
            // Otherwise return the selected date with a leading zero.
            return string ? 2 : _.lead( dateObject.date )
        },
        ddd: function( string, dateObject ) {

            // If there's a string, then get the length of the first word.
            // Otherwise return the short selected weekday.
            return string ? getFirstWordLength( string ) : this.settings.weekdaysShort[ dateObject.day ]
        },
        dddd: function( string, dateObject ) {

            // If there's a string, then get the length of the first word.
            // Otherwise return the full selected weekday.
            return string ? getFirstWordLength( string ) : this.settings.weekdaysFull[ dateObject.day ]
        },
        m: function( string, dateObject ) {

            // If there's a string, then get the length of the digits
            // Otherwise return the selected month with 0index compensation.
            return string ? _.digits( string ) : dateObject.month + 1
        },
        mm: function( string, dateObject ) {

            // If there's a string, then the length is always 2.
            // Otherwise return the selected month with 0index and leading zero.
            return string ? 2 : _.lead( dateObject.month + 1 )
        },
        mmm: function( string, dateObject ) {

            var collection = this.settings.monthsShort

            // If there's a string, get length of the relevant month from the short
            // months collection. Otherwise return the selected month from that collection.
            return string ? getWordLengthFromCollection( string, collection, dateObject ) : collection[ dateObject.month ]
        },
        mmmm: function( string, dateObject ) {

            var collection = this.settings.monthsFull

            // If there's a string, get length of the relevant month from the full
            // months collection. Otherwise return the selected month from that collection.
            return string ? getWordLengthFromCollection( string, collection, dateObject ) : collection[ dateObject.month ]
        },
        yy: function( string, dateObject ) {

            // If there's a string, then the length is always 2.
            // Otherwise return the selected year by slicing out the first 2 digits.
            return string ? 2 : ( '' + dateObject.year ).slice( 2 )
        },
        yyyy: function( string, dateObject ) {

            // If there's a string, then the length is always 4.
            // Otherwise return the selected year.
            return string ? 4 : dateObject.year
        },

        // Create an array by splitting the formatting string passed.
        toArray: function( formatString ) { return formatString.split( /(d{1,4}|m{1,4}|y{4}|yy|!.)/g ) },

        // Format an object into a string using the formatting options.
        toString: function ( formatString, itemObject ) {
            var calendar = this
            return calendar.formats.toArray( formatString ).map( function( label ) {
                return _.trigger( calendar.formats[ label ], calendar, [ 0, itemObject ] ) || label.replace( /^!/, '' )
            }).join( '' )
        }
    }
})() //DatePicker.prototype.formats




/**
 * Check if two date units are the exact.
 */
DatePicker.prototype.isDateExact = function( one, two ) {

    var calendar = this

    // When we’re working with weekdays, do a direct comparison.
    if (
        ( _.isInteger( one ) && _.isInteger( two ) ) ||
        ( typeof one == 'boolean' && typeof two == 'boolean' )
     ) {
        return one === two
    }

    // When we’re working with date representations, compare the “pick” value.
    if (
        ( _.isDate( one ) || $.isArray( one ) ) &&
        ( _.isDate( two ) || $.isArray( two ) )
    ) {
        return calendar.create( one ).pick === calendar.create( two ).pick
    }

    // When we’re working with range objects, compare the “from” and “to”.
    if ( $.isPlainObject( one ) && $.isPlainObject( two ) ) {
        return calendar.isDateExact( one.from, two.from ) && calendar.isDateExact( one.to, two.to )
    }

    return false
}


/**
 * Check if two date units overlap.
 */
DatePicker.prototype.isDateOverlap = function( one, two ) {

    var calendar = this,
        firstDay = calendar.settings.firstDay ? 1 : 0

    // When we’re working with a weekday index, compare the days.
    if ( _.isInteger( one ) && ( _.isDate( two ) || $.isArray( two ) ) ) {
        one = one % 7 + firstDay
        return one === calendar.create( two ).day + 1
    }
    if ( _.isInteger( two ) && ( _.isDate( one ) || $.isArray( one ) ) ) {
        two = two % 7 + firstDay
        return two === calendar.create( one ).day + 1
    }

    // When we’re working with range objects, check if the ranges overlap.
    if ( $.isPlainObject( one ) && $.isPlainObject( two ) ) {
        return calendar.overlapRanges( one, two )
    }

    return false
}


/**
 * Flip the “enabled” state.
 */
DatePicker.prototype.flipEnable = function(val) {
    var itemObject = this.item
    itemObject.enable = val || (itemObject.enable == -1 ? 1 : -1)
}


/**
 * Mark a collection of dates as “disabled”.
 */
DatePicker.prototype.deactivate = function( type, datesToDisable ) {

    var calendar = this,
        disabledItems = calendar.item.disable.slice(0)


    // If we’re flipping, that’s all we need to do.
    if ( datesToDisable == 'flip' ) {
        calendar.flipEnable()
    }

    else if ( datesToDisable === false ) {
        calendar.flipEnable(1)
        disabledItems = []
    }

    else if ( datesToDisable === true ) {
        calendar.flipEnable(-1)
        disabledItems = []
    }

    // Otherwise go through the dates to disable.
    else {

        datesToDisable.map(function( unitToDisable ) {

            var matchFound

            // When we have disabled items, check for matches.
            // If something is matched, immediately break out.
            for ( var index = 0; index < disabledItems.length; index += 1 ) {
                if ( calendar.isDateExact( unitToDisable, disabledItems[index] ) ) {
                    matchFound = true
                    break
                }
            }

            // If nothing was found, add the validated unit to the collection.
            if ( !matchFound ) {
                if (
                    _.isInteger( unitToDisable ) ||
                    _.isDate( unitToDisable ) ||
                    $.isArray( unitToDisable ) ||
                    ( $.isPlainObject( unitToDisable ) && unitToDisable.from && unitToDisable.to )
                ) {
                    disabledItems.push( unitToDisable )
                }
            }
        })
    }

    // Return the updated collection.
    return disabledItems
} //DatePicker.prototype.deactivate


/**
 * Mark a collection of dates as “enabled”.
 */
DatePicker.prototype.activate = function( type, datesToEnable ) {

    var calendar = this,
        disabledItems = calendar.item.disable,
        disabledItemsCount = disabledItems.length

    // If we’re flipping, that’s all we need to do.
    if ( datesToEnable == 'flip' ) {
        calendar.flipEnable()
    }

    else if ( datesToEnable === true ) {
        calendar.flipEnable(1)
        disabledItems = []
    }

    else if ( datesToEnable === false ) {
        calendar.flipEnable(-1)
        disabledItems = []
    }

    // Otherwise go through the disabled dates.
    else {

        datesToEnable.map(function( unitToEnable ) {

            var matchFound,
                disabledUnit,
                index,
                isExactRange

            // Go through the disabled items and try to find a match.
            for ( index = 0; index < disabledItemsCount; index += 1 ) {

                disabledUnit = disabledItems[index]

                // When an exact match is found, remove it from the collection.
                if ( calendar.isDateExact( disabledUnit, unitToEnable ) ) {
                    matchFound = disabledItems[index] = null
                    isExactRange = true
                    break
                }

                // When an overlapped match is found, add the “inverted” state to it.
                else if ( calendar.isDateOverlap( disabledUnit, unitToEnable ) ) {
                    if ( $.isPlainObject( unitToEnable ) ) {
                        unitToEnable.inverted = true
                        matchFound = unitToEnable
                    }
                    else if ( $.isArray( unitToEnable ) ) {
                        matchFound = unitToEnable
                        if ( !matchFound[3] ) matchFound.push( 'inverted' )
                    }
                    else if ( _.isDate( unitToEnable ) ) {
                        matchFound = [ unitToEnable.getFullYear(), unitToEnable.getMonth(), unitToEnable.getDate(), 'inverted' ]
                    }
                    break
                }
            }

            // If a match was found, remove a previous duplicate entry.
            if ( matchFound ) for ( index = 0; index < disabledItemsCount; index += 1 ) {
                if ( calendar.isDateExact( disabledItems[index], unitToEnable ) ) {
                    disabledItems[index] = null
                    break
                }
            }

            // In the event that we’re dealing with an exact range of dates,
            // make sure there are no “inverted” dates because of it.
            if ( isExactRange ) for ( index = 0; index < disabledItemsCount; index += 1 ) {
                if ( calendar.isDateOverlap( disabledItems[index], unitToEnable ) ) {
                    disabledItems[index] = null
                    break
                }
            }

            // If something is still matched, add it into the collection.
            if ( matchFound ) {
                disabledItems.push( matchFound )
            }
        })
    }

    // Return the updated collection.
    return disabledItems.filter(function( val ) { return val != null })
} //DatePicker.prototype.activate


/**
 * Create a string for the nodes in the picker.
 */
DatePicker.prototype.nodes = function( isOpen ) {

    var
        calendar = this,
        settings = calendar.settings,
        calendarItem = calendar.item,
        nowObject = calendarItem.now,
        selectedObject = calendarItem.select,
        highlightedObject = calendarItem.highlight,
        viewsetObject = calendarItem.view,
        disabledCollection = calendarItem.disable,
        minLimitObject = calendarItem.min,
        maxLimitObject = calendarItem.max,


        // Create the calendar table head using a copy of weekday labels collection.
        // * We do a copy so we don't mutate the original array.
        tableHead = (function( collection, fullCollection ) {

            // If the first day should be Monday, move Sunday to the end.
            if ( settings.firstDay ) {
                collection.push( collection.shift() )
                fullCollection.push( fullCollection.shift() )
            }

            // Create and return the table head group.
            return _.node(
                'thead',
                _.node(
                    'tr',
                    _.group({
                        min: 0,
                        max: DAYS_IN_WEEK - 1,
                        i: 1,
                        node: 'th',
                        item: function( counter ) {
                            return [
                                collection[ counter ],
                                settings.klass.weekdays,
                                'scope=col title="' + fullCollection[ counter ] + '"'
                            ]
                        }
                    })
                )
            ) //endreturn
        })( ( settings.showWeekdaysFull ? settings.weekdaysFull : settings.weekdaysShort ).slice( 0 ), settings.weekdaysFull.slice( 0 ) ), //tableHead


        // Create the nav for next/prev month.
        createMonthNav = function( next ) {

            // Otherwise, return the created month tag.
            return _.node(
                'div',
                ' ',
                settings.klass[ 'nav' + ( next ? 'Next' : 'Prev' ) ] + (

                    // If the focused month is outside the range, disabled the button.
                    ( next && viewsetObject.year >= maxLimitObject.year && viewsetObject.month >= maxLimitObject.month ) ||
                    ( !next && viewsetObject.year <= minLimitObject.year && viewsetObject.month <= minLimitObject.month ) ?
                    ' ' + settings.klass.navDisabled : ''
                ),
                'data-nav=' + ( next || -1 ) + ' ' +
                _.ariaAttr({
                    role: 'button',
                    controls: calendar.$node[0].id + '_table'
                }) + ' ' +
                'title="' + (next ? settings.labelMonthNext : settings.labelMonthPrev ) + '"'
            ) //endreturn
        }, //createMonthNav


        // Create the month label.
        createMonthLabel = function() {

            var monthsCollection = settings.showMonthsShort ? settings.monthsShort : settings.monthsFull

            // If there are months to select, add a dropdown menu.
            if ( settings.selectMonths ) {

                return _.node( 'select',
                    _.group({
                        min: 0,
                        max: 11,
                        i: 1,
                        node: 'option',
                        item: function( loopedMonth ) {

                            return [

                                // The looped month and no classes.
                                monthsCollection[ loopedMonth ], 0,

                                // Set the value and selected index.
                                'value=' + loopedMonth +
                                ( viewsetObject.month == loopedMonth ? ' selected' : '' ) +
                                (
                                    (
                                        ( viewsetObject.year == minLimitObject.year && loopedMonth < minLimitObject.month ) ||
                                        ( viewsetObject.year == maxLimitObject.year && loopedMonth > maxLimitObject.month )
                                    ) ?
                                    ' disabled' : ''
                                )
                            ]
                        }
                    }),
                    settings.klass.selectMonth,
                    ( isOpen ? '' : 'disabled' ) + ' ' +
                    _.ariaAttr({ controls: calendar.$node[0].id + '_table' }) + ' ' +
                    'title="' + settings.labelMonthSelect + '"'
                )
            }

            // If there's a need for a month selector
            return _.node( 'div', monthsCollection[ viewsetObject.month ], settings.klass.month )
        }, //createMonthLabel


        // Create the year label.
        createYearLabel = function() {

            var focusedYear = viewsetObject.year,

            // If years selector is set to a literal "true", set it to 5. Otherwise
            // divide in half to get half before and half after focused year.
            numberYears = settings.selectYears === true ? 5 : ~~( settings.selectYears / 2 )

            // If there are years to select, add a dropdown menu.
            if ( numberYears ) {

                var
                    minYear = minLimitObject.year,
                    maxYear = maxLimitObject.year,
                    lowestYear = focusedYear - numberYears,
                    highestYear = focusedYear + numberYears

                // If the min year is greater than the lowest year, increase the highest year
                // by the difference and set the lowest year to the min year.
                if ( minYear > lowestYear ) {
                    highestYear += minYear - lowestYear
                    lowestYear = minYear
                }

                // If the max year is less than the highest year, decrease the lowest year
                // by the lower of the two: available and needed years. Then set the
                // highest year to the max year.
                if ( maxYear < highestYear ) {

                    var availableYears = lowestYear - minYear,
                        neededYears = highestYear - maxYear

                    lowestYear -= availableYears > neededYears ? neededYears : availableYears
                    highestYear = maxYear
                }

                return _.node( 'select',
                    _.group({
                        min: lowestYear,
                        max: highestYear,
                        i: 1,
                        node: 'option',
                        item: function( loopedYear ) {
                            return [

                                // The looped year and no classes.
                                loopedYear, 0,

                                // Set the value and selected index.
                                'value=' + loopedYear + ( focusedYear == loopedYear ? ' selected' : '' )
                            ]
                        }
                    }),
                    settings.klass.selectYear,
                    ( isOpen ? '' : 'disabled' ) + ' ' + _.ariaAttr({ controls: calendar.$node[0].id + '_table' }) + ' ' +
                    'title="' + settings.labelYearSelect + '"'
                )
            }

            // Otherwise just return the year focused
            return _.node( 'div', focusedYear, settings.klass.year )
        } //createYearLabel


    // Create and return the entire calendar.
    return _.node(
        'div',
        ( settings.selectYears ? createYearLabel() + createMonthLabel() : createMonthLabel() + createYearLabel() ) +
        createMonthNav() + createMonthNav( 1 ),
        settings.klass.header
    ) + _.node(
        'table',
        tableHead +
        _.node(
            'tbody',
            _.group({
                min: 0,
                max: WEEKS_IN_CALENDAR - 1,
                i: 1,
                node: 'tr',
                item: function( rowCounter ) {

                    // If Monday is the first day and the month starts on Sunday, shift the date back a week.
                    var shiftDateBy = settings.firstDay && calendar.create([ viewsetObject.year, viewsetObject.month, 1 ]).day === 0 ? -7 : 0

                    return [
                        _.group({
                            min: DAYS_IN_WEEK * rowCounter - viewsetObject.day + shiftDateBy + 1, // Add 1 for weekday 0index
                            max: function() {
                                return this.min + DAYS_IN_WEEK - 1
                            },
                            i: 1,
                            node: 'td',
                            item: function( targetDate ) {

                                // Convert the time date from a relative date to a target date.
                                targetDate = calendar.create([ viewsetObject.year, viewsetObject.month, targetDate + ( settings.firstDay ? 1 : 0 ) ])

                                var isSelected = selectedObject && selectedObject.pick == targetDate.pick,
                                    isHighlighted = highlightedObject && highlightedObject.pick == targetDate.pick,
                                    isDisabled = disabledCollection && calendar.disabled( targetDate ) || targetDate.pick < minLimitObject.pick || targetDate.pick > maxLimitObject.pick,
                                    formattedDate = _.trigger( calendar.formats.toString, calendar, [ settings.format, targetDate ] )

                                return [
                                    _.node(
                                        'div',
                                        targetDate.date,
                                        (function( klasses ) {

                                            // Add the `infocus` or `outfocus` classes based on month in view.
                                            klasses.push( viewsetObject.month == targetDate.month ? settings.klass.infocus : settings.klass.outfocus )

                                            // Add the `today` class if needed.
                                            if ( nowObject.pick == targetDate.pick ) {
                                                klasses.push( settings.klass.now )
                                            }

                                            // Add the `selected` class if something's selected and the time matches.
                                            if ( isSelected ) {
                                                klasses.push( settings.klass.selected )
                                            }

                                            // Add the `highlighted` class if something's highlighted and the time matches.
                                            if ( isHighlighted ) {
                                                klasses.push( settings.klass.highlighted )
                                            }

                                            // Add the `disabled` class if something's disabled and the object matches.
                                            if ( isDisabled ) {
                                                klasses.push( settings.klass.disabled )
                                            }

                                            return klasses.join( ' ' )
                                        })([ settings.klass.day ]),
                                        'data-pick=' + targetDate.pick + ' ' + _.ariaAttr({
                                            role: 'gridcell',
                                            label: formattedDate,
                                            selected: isSelected && calendar.$node.val() === formattedDate ? true : null,
                                            activedescendant: isHighlighted ? true : null,
                                            disabled: isDisabled ? true : null
                                        })
                                    ),
                                    '',
                                    _.ariaAttr({ role: 'presentation' })
                                ] //endreturn
                            }
                        })
                    ] //endreturn
                }
            })
        ),
        settings.klass.table,
        'id="' + calendar.$node[0].id + '_table' + '" ' + _.ariaAttr({
            role: 'grid',
            controls: calendar.$node[0].id,
            readonly: true
        })
    ) +

    // * For Firefox forms to submit, make sure to set the buttons’ `type` attributes as “button”.
    _.node(
        'div',
        _.node( 'button', settings.today, settings.klass.buttonToday,
            'type=button data-pick=' + nowObject.pick +
            ( isOpen && !calendar.disabled(nowObject) ? '' : ' disabled' ) + ' ' +
            _.ariaAttr({ controls: calendar.$node[0].id }) ) +
        _.node( 'button', settings.clear, settings.klass.buttonClear,
            'type=button data-clear=1' +
            ( isOpen ? '' : ' disabled' ) + ' ' +
            _.ariaAttr({ controls: calendar.$node[0].id }) ) +
        _.node('button', settings.close, settings.klass.buttonClose,
            'type=button data-close=true ' +
            ( isOpen ? '' : ' disabled' ) + ' ' +
            _.ariaAttr({ controls: calendar.$node[0].id }) ),
        settings.klass.footer
    ) //endreturn
} //DatePicker.prototype.nodes




/**
 * The date picker defaults.
 */
DatePicker.defaults = (function( prefix ) {

    return {

        // The title label to use for the month nav buttons
        labelMonthNext: 'Next month',
        labelMonthPrev: 'Previous month',

        // The title label to use for the dropdown selectors
        labelMonthSelect: 'Select a month',
        labelYearSelect: 'Select a year',

        // Months and weekdays
        monthsFull: [ 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December' ],
        monthsShort: [ 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec' ],
        weekdaysFull: [ 'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday' ],
        weekdaysShort: [ 'Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat' ],

        // Today and clear
        today: 'Today',
        clear: 'Clear',
        close: 'Close',

        // Picker close behavior
        closeOnSelect: true,
        closeOnClear: true,

        // The format to show on the `input` element
        format: 'd mmmm, yyyy',

        // Classes
        klass: {

            table: prefix + 'table',

            header: prefix + 'header',

            navPrev: prefix + 'nav--prev',
            navNext: prefix + 'nav--next',
            navDisabled: prefix + 'nav--disabled',

            month: prefix + 'month',
            year: prefix + 'year',

            selectMonth: prefix + 'select--month',
            selectYear: prefix + 'select--year',

            weekdays: prefix + 'weekday',

            day: prefix + 'day',
            disabled: prefix + 'day--disabled',
            selected: prefix + 'day--selected',
            highlighted: prefix + 'day--highlighted',
            now: prefix + 'day--today',
            infocus: prefix + 'day--infocus',
            outfocus: prefix + 'day--outfocus',

            footer: prefix + 'footer',

            buttonClear: prefix + 'button--clear',
            buttonToday: prefix + 'button--today',
            buttonClose: prefix + 'button--close'
        }
    }
})( Picker.klasses().picker + '__' )





/**
 * Extend the picker to add the date picker.
 */
Picker.extend( 'pickadate', DatePicker )


}));




define('gigaset_csp/components/product/register',[
	"jquery",
	"cartware/components/_base",
	"cartware/jquery/bem",
	"select2",
	"picker-date",
	"bootstrap-button"
], function(
	$,
	Component,
	BEM,
	S2
) {
	return Component.create({
		block: null,

		form:				null,
		varienForm:			null,
		submitButton:		null,
		producttypeSelect:	null,
		productSelect:		null,
		dateofpurchase:		null,

		options:			null,

		loading:			false,

		$init: function() {
			this.block = BEM.block("csp-product-register-form");
			this.form = this.block.getElements("form");
			this.submitButton = this.block.getElements("submit-button");
			this.varienForm = new VarienForm(this.form.get(0));

			this.options = this.block.data("config");

			this.producttypeSelect = this.block.getElements("producttype");

			this.producttypeSelect
				.select2()
				.on("change", $.proxy(this._onChangeProducttype, this));

			this.productSelect = this.block.getElements("product");
			this.productSelect.select2();

			this.dateofpurchase = this.block.getElements("dateofpurchase");
			this.dateofpurchase.pickadate({
				formatSubmit: 'yyyy/mm/dd'
			});

			this.form.on("submit", $.proxy(this._onFormSubmit, this));
		},

		setLoading: function(flag) {
			this.loading = flag;
			this.block[flag ? "addModifier" : "removeModifier"]("loading");
		},

		_onChangeProducttype: function() {
			var self = this,
				producttype = this.producttypeSelect.val();

			if (producttype.length === 0) {
				this._onProductsRetrieve({});
				return;
			}

			this.setLoading(true);
			this.productSelect.attr("disabled", "disabled");
			this.productSelect.html("");

			$
				.ajax(this.options.productOptionsUrl, {
					method: "GET",
					data: {
						producttype: producttype
					}
				})
				.done($.proxy(this._onProductsRetrieve, this))
				.always(function() {
					self.setLoading(false);
				});
		},

		_onProductsRetrieve: function(data) {
			var optionsHtml = [];

			$.each(data, function(key, value) {
				optionsHtml.push('<option value="' + key + '">' + value + '</option>');
			});

			this.productSelect.html(optionsHtml.join(""));

			if (optionsHtml.length) {
				this.productSelect.removeAttr("disabled");
			} else {
				this.productSelect.attr("disabled", "disabled");
			}

			this.productSelect.select2();
		},

		_onFormSubmit: function(e) {
			if (this.loading || !this.varienForm.validator.validate()) {
				e.preventDefault();
				return;
			}

			this.block.addModifier("submitting");
			this.submitButton.button("loading");
		}
	});
});
define('gigaset_csp/components/order/repair/form',[
	"jquery",
	"cartware/components/_base",
	"cartware/jquery/bem",
	"select2",
	"bootstrap-button"
], function(
	$,
	Component,
	BEM,
	S2
) {
	return Component.create({
		block: null,

		form:				null,
		varienForm:			null,
		submitButton:		null,
		productSelect:		null,

		config:				null,

		$init: function() {
			this.block = BEM.block("csp-repair-order-register-form");

			this.config = this.block.data("config");

			this.form = this.block.getElements("form");
			this.submitButton = this.block.getElements("submit-button");
			this.varienForm = new VarienForm(this.form.get(0));

			this.productSelect = this.block.getElements("product");

			this.productSelect.select2({
				templateResult: $.proxy(function(option) {
					if (!option.id) { return option.text; }

					var $option = $([
						'<span>',
							option.text,
							'<span class="select2-results__option-notice">',
								' (', Translator.translate("purchased:"), " ", this.config.purchaseDates[option.id], ')',
							'</span>',
						'</span>'
					].join(""));
					return $option;
				}, this)
			});

			this.form.on("submit", $.proxy(this._onFormSubmit, this));
		},

		_onFormSubmit: function(e) {
			if (!this.varienForm.validator.validate()) {
				e.preventDefault();
				return;
			}

			this.block.addModifier("submitting");
			this.submitButton.button("loading");
		}
	});
});
define('gigaset_csp/components/order/repair/form/description',[
	"jquery",
	"cartware/components/_base",
	"cartware/jquery/bem",
	"bootstrap-button"
], function(
	$,
	Component,
	BEM
) {
	return Component.create({
		block: null,

		faqRadios:			null,
		compatRadios:		null,
		solvedAloneRadios:	null,


		form:				null,
		varienForm:			null,
		submitButton:		null,

		config:				null,

		$init: function() {
			var self = this;

			this.block = BEM.block("csp-repair-order-register-form-description");

			this.faqRadios = this.block.getElements("lookup-faq");
			this.compatRadios = this.block.getElements("checked-compatibility");
			this.solvedAloneRadios = this.block.getElements("solved-alone");

			this.faqRadios.on("change", $.proxy(this._onRadioChange, this));
			this.compatRadios.on("change", $.proxy(this._onRadioChange, this));

			this.config = this.block.data("config");

			this.form = this.block.getElements("form");
			this.submitButton = this.block.getElements("submit-button");
			this.varienForm = new VarienForm(this.form.get(0));

			this.form.on("submit", $.proxy(this._onFormSubmit, this));
		},

		_onRadioChange: function(e) {
			var continueRadio = this.solvedAloneRadios.filter(function() { return this.value == 0; })
				, enable = true;

			this.faqRadios.each(function() {
				if (this.checked && this.value == 0) {
					enable = false;
					return false;
				}
			});

			this.compatRadios.each(function() {
				if (this.checked && this.value == 0) {
					enable = false;
					return false;
				}
			});

			if (enable) {
				continueRadio.removeAttr("disabled");
			} else {
				continueRadio.attr("disabled", "disabled");
				continueRadio.removeAttr("checked");
			}
		},

		_onFormSubmit: function(e) {
			if (!this.varienForm.validator.validate()) {
				e.preventDefault();
				return;
			}

			this.block.addModifier("submitting");
			this.submitButton.button("loading");
		}
	});
});
define('gigaset_csp/components/order/repair/form/address',[
	"jquery",
	"cartware/components/_base",
	"cartware/jquery/bem",
	"bootstrap-button"
], function(
	$,
	Component,
	BEM
) {
	return Component.create({
		block: null,

		addressChooserBlock:	null,

		form:				null,
		validator:			null,
		submitButton:		null,
		createAddress:		null,
		createAddressWrap:	null,

		config:				null,

		$init: function() {
			var self = this;

			this.block = BEM.block("csp-repair-order-register-form-address");
			this.addressChooserBlock = BEM.block("customer-address-chooser");

			this.config = this.block.data("config");

			this.form = this.block.getElements("form");
			this.submitButton = this.block.getElements("submit-button");
			this.validator = new Validation(this.form.get(0), {
				onSubmit: false
			});

			this.createAddressWrap = this.block.getElements("new-address");

			this.createAddress = this.block.getElements("create-delivery-address");
			this.createAddress.on("change", function() {
				if (this.checked) {
					if (this.value != 0) {
						self._showCreateAddressForm();
						self.addressChooserBlock.addModifier("disabled");
					} else {
						self._hideCreateAddressForm();
						self.addressChooserBlock.removeModifier("disabled");
					}
				}
			});

			this.form.on("submit", $.proxy(this._onFormSubmit, this));
		},

		_showCreateAddressForm: function() {
			this.createAddressWrap.css("display", "block");
		},

		_hideCreateAddressForm: function() {
			this.createAddressWrap.css("display", "none");
		},

		_onFormSubmit: function(e) {
			var validateForm = false;

			this.createAddress.each(function() {
				if (this.checked && this.value != 0) {
					validateForm = true;
					return false;
				}
			});

			if (validateForm && !this.validator.validate()) {
				e.preventDefault();
				return;
			}

			this.block.addModifier("submitting");
			this.submitButton.button("loading");
		}
	});
});
define('gigaset_csp/components/order/repair/form/reference',[
	"jquery",
	"cartware/components/_base",
	"cartware/jquery/bem",
	"bootstrap-button"
], function(
	$,
	Component,
	BEM
) {
	return Component.create({
		block: null,

		form:				null,
		varienForm:			null,
		submitButton:		null,

		config:				null,

		$init: function() {
			var self = this;

			this.block = BEM.block("csp-repair-order-register-form-reference");

			this.config = this.block.data("config");

			this.form = this.block.getElements("form");
			this.submitButton = this.block.getElements("submit-button");
			this.varienForm = new VarienForm(this.form.get(0));

			this.form.on("submit", $.proxy(this._onFormSubmit, this));
		},

		_showCreateAddressForm: function() {
			this.createAddressWrap.css("display", "block");
		},

		_hideCreateAddressForm: function() {
			this.createAddressWrap.css("display", "none");
		},

		_onFormSubmit: function(e) {
			if (!this.varienForm.validator.validate()) {
				e.preventDefault();
				return;
			}

			this.block.addModifier("submitting");
			this.submitButton.button("loading");
		}
	});
});
/*!
 * Time picker for pickadate.js v3.5.5
 * http://amsul.github.io/pickadate.js/time.htm
 */

(function ( factory ) {

    // AMD.
    if ( typeof define == 'function' && define.amd )
        define( 'picker-time',['picker', 'jquery'], factory )

    // Node.js/browserify.
    else if ( typeof exports == 'object' )
        module.exports = factory( require('./picker.js'), require('jquery') )

    // Browser globals.
    else factory( Picker, jQuery )

}(function( Picker, $ ) {


/**
 * Globals and constants
 */
var HOURS_IN_DAY = 24,
    MINUTES_IN_HOUR = 60,
    HOURS_TO_NOON = 12,
    MINUTES_IN_DAY = HOURS_IN_DAY * MINUTES_IN_HOUR,
    _ = Picker._



/**
 * The time picker constructor
 */
function TimePicker( picker, settings ) {

    var clock = this,
        elementValue = picker.$node[ 0 ].value,
        elementDataValue = picker.$node.data( 'value' ),
        valueString = elementDataValue || elementValue,
        formatString = elementDataValue ? settings.formatSubmit : settings.format

    clock.settings = settings
    clock.$node = picker.$node

    // The queue of methods that will be used to build item objects.
    clock.queue = {
        interval: 'i',
        min: 'measure create',
        max: 'measure create',
        now: 'now create',
        select: 'parse create validate',
        highlight: 'parse create validate',
        view: 'parse create validate',
        disable: 'deactivate',
        enable: 'activate'
    }

    // The component's item object.
    clock.item = {}

    clock.item.clear = null
    clock.item.interval = settings.interval || 30
    clock.item.disable = ( settings.disable || [] ).slice( 0 )
    clock.item.enable = -(function( collectionDisabled ) {
        return collectionDisabled[ 0 ] === true ? collectionDisabled.shift() : -1
    })( clock.item.disable )

    clock.
        set( 'min', settings.min ).
        set( 'max', settings.max ).
        set( 'now' )

    // When there’s a value, set the `select`, which in turn
    // also sets the `highlight` and `view`.
    if ( valueString ) {
        clock.set( 'select', valueString, {
            format: formatString
        })
    }

    // If there’s no value, default to highlighting “today”.
    else {
        clock.
            set( 'select', null ).
            set( 'highlight', clock.item.now )
    }

    // The keycode to movement mapping.
    clock.key = {
        40: 1, // Down
        38: -1, // Up
        39: 1, // Right
        37: -1, // Left
        go: function( timeChange ) {
            clock.set(
                'highlight',
                clock.item.highlight.pick + timeChange * clock.item.interval,
                { interval: timeChange * clock.item.interval }
            )
            this.render()
        }
    }


    // Bind some picker events.
    picker.
        on( 'render', function() {
            var $pickerHolder = picker.$root.children(),
                $viewset = $pickerHolder.find( '.' + settings.klass.viewset ),
                vendors = function( prop ) {
                    return ['webkit', 'moz', 'ms', 'o', ''].map(function( vendor ) {
                        return ( vendor ? '-' + vendor + '-' : '' ) + prop
                    })
                },
                animations = function( $el, state ) {
                    vendors( 'transform' ).map(function( prop ) {
                        $el.css( prop, state )
                    })
                    vendors( 'transition' ).map(function( prop ) {
                        $el.css( prop, state )
                    })
                }
            if ( $viewset.length ) {
                animations( $pickerHolder, 'none' )
                $pickerHolder[ 0 ].scrollTop = ~~$viewset.position().top - ( $viewset[ 0 ].clientHeight * 2 )
                animations( $pickerHolder, '' )
            }
        }, 1 ).
        on( 'open', function() {
            picker.$root.find( 'button' ).attr( 'disabled', false )
        }, 1 ).
        on( 'close', function() {
            picker.$root.find( 'button' ).attr( 'disabled', true )
        }, 1 )

} //TimePicker


/**
 * Set a timepicker item object.
 */
TimePicker.prototype.set = function( type, value, options ) {

    var clock = this,
        clockItem = clock.item

    // If the value is `null` just set it immediately.
    if ( value === null ) {
        if ( type == 'clear' ) type = 'select'
        clockItem[ type ] = value
        return clock
    }

    // Otherwise go through the queue of methods, and invoke the functions.
    // Update this as the time unit, and set the final value as this item.
    // * In the case of `enable`, keep the queue but set `disable` instead.
    //   And in the case of `flip`, keep the queue but set `enable` instead.
    clockItem[ ( type == 'enable' ? 'disable' : type == 'flip' ? 'enable' : type ) ] = clock.queue[ type ].split( ' ' ).map( function( method ) {
        value = clock[ method ]( type, value, options )
        return value
    }).pop()

    // Check if we need to cascade through more updates.
    if ( type == 'select' ) {
        clock.set( 'highlight', clockItem.select, options )
    }
    else if ( type == 'highlight' ) {
        clock.set( 'view', clockItem.highlight, options )
    }
    else if ( type == 'interval' ) {
        clock.
            set( 'min', clockItem.min, options ).
            set( 'max', clockItem.max, options )
    }
    else if ( type.match( /^(flip|min|max|disable|enable)$/ ) ) {
        if ( clockItem.select && clock.disabled( clockItem.select ) ) {
            clock.set( 'select', value, options )
        }
        if ( clockItem.highlight && clock.disabled( clockItem.highlight ) ) {
            clock.set( 'highlight', value, options )
        }
        if ( type == 'min' ) {
            clock.set( 'max', clockItem.max, options )
        }
    }

    return clock
} //TimePicker.prototype.set


/**
 * Get a timepicker item object.
 */
TimePicker.prototype.get = function( type ) {
    return this.item[ type ]
} //TimePicker.prototype.get


/**
 * Create a picker time object.
 */
TimePicker.prototype.create = function( type, value, options ) {

    var clock = this

    // If there’s no value, use the type as the value.
    value = value === undefined ? type : value

    // If it’s a date object, convert it into an array.
    if ( _.isDate( value ) ) {
        value = [ value.getHours(), value.getMinutes() ]
    }

    // If it’s an object, use the “pick” value.
    if ( $.isPlainObject( value ) && _.isInteger( value.pick ) ) {
        value = value.pick
    }

    // If it’s an array, convert it into minutes.
    else if ( $.isArray( value ) ) {
        value = +value[ 0 ] * MINUTES_IN_HOUR + (+value[ 1 ])
    }

    // If no valid value is passed, set it to “now”.
    else if ( !_.isInteger( value ) ) {
        value = clock.now( type, value, options )
    }

    // If we’re setting the max, make sure it’s greater than the min.
    if ( type == 'max' && value < clock.item.min.pick ) {
        value += MINUTES_IN_DAY
    }

    // If the value doesn’t fall directly on the interval,
    // add one interval to indicate it as “passed”.
    if ( type != 'min' && type != 'max' && (value - clock.item.min.pick) % clock.item.interval !== 0 ) {
        value += clock.item.interval
    }

    // Normalize it into a “reachable” interval.
    value = clock.normalize( type, value, options )

    // Return the compiled object.
    return {

        // Divide to get hours from minutes.
        hour: ~~( HOURS_IN_DAY + value / MINUTES_IN_HOUR ) % HOURS_IN_DAY,

        // The remainder is the minutes.
        mins: ( MINUTES_IN_HOUR + value % MINUTES_IN_HOUR ) % MINUTES_IN_HOUR,

        // The time in total minutes.
        time: ( MINUTES_IN_DAY + value ) % MINUTES_IN_DAY,

        // Reference to the “relative” value to pick.
        pick: value % MINUTES_IN_DAY
    }
} //TimePicker.prototype.create


/**
 * Create a range limit object using an array, date object,
 * literal “true”, or integer relative to another time.
 */
TimePicker.prototype.createRange = function( from, to ) {

    var clock = this,
        createTime = function( time ) {
            if ( time === true || $.isArray( time ) || _.isDate( time ) ) {
                return clock.create( time )
            }
            return time
        }

    // Create objects if possible.
    if ( !_.isInteger( from ) ) {
        from = createTime( from )
    }
    if ( !_.isInteger( to ) ) {
        to = createTime( to )
    }

    // Create relative times.
    if ( _.isInteger( from ) && $.isPlainObject( to ) ) {
        from = [ to.hour, to.mins + ( from * clock.settings.interval ) ];
    }
    else if ( _.isInteger( to ) && $.isPlainObject( from ) ) {
        to = [ from.hour, from.mins + ( to * clock.settings.interval ) ];
    }

    return {
        from: createTime( from ),
        to: createTime( to )
    }
} //TimePicker.prototype.createRange


/**
 * Check if a time unit falls within a time range object.
 */
TimePicker.prototype.withinRange = function( range, timeUnit ) {
    range = this.createRange(range.from, range.to)
    return timeUnit.pick >= range.from.pick && timeUnit.pick <= range.to.pick
}


/**
 * Check if two time range objects overlap.
 */
TimePicker.prototype.overlapRanges = function( one, two ) {

    var clock = this

    // Convert the ranges into comparable times.
    one = clock.createRange( one.from, one.to )
    two = clock.createRange( two.from, two.to )

    return clock.withinRange( one, two.from ) || clock.withinRange( one, two.to ) ||
        clock.withinRange( two, one.from ) || clock.withinRange( two, one.to )
}


/**
 * Get the time relative to now.
 */
TimePicker.prototype.now = function( type, value/*, options*/ ) {

    var interval = this.item.interval,
        date = new Date(),
        nowMinutes = date.getHours() * MINUTES_IN_HOUR + date.getMinutes(),
        isValueInteger = _.isInteger( value ),
        isBelowInterval

    // Make sure “now” falls within the interval range.
    nowMinutes -= nowMinutes % interval

    // Check if the difference is less than the interval itself.
    isBelowInterval = value < 0 && interval * value + nowMinutes <= -interval

    // Add an interval because the time has “passed”.
    nowMinutes += type == 'min' && isBelowInterval ? 0 : interval

    // If the value is a number, adjust by that many intervals.
    if ( isValueInteger ) {
        nowMinutes += interval * (
            isBelowInterval && type != 'max' ?
                value + 1 :
                value
            )
    }

    // Return the final calculation.
    return nowMinutes
} //TimePicker.prototype.now


/**
 * Normalize minutes to be “reachable” based on the min and interval.
 */
TimePicker.prototype.normalize = function( type, value/*, options*/ ) {

    var interval = this.item.interval,
        minTime = this.item.min && this.item.min.pick || 0

    // If setting min time, don’t shift anything.
    // Otherwise get the value and min difference and then
    // normalize the difference with the interval.
    value -= type == 'min' ? 0 : ( value - minTime ) % interval

    // Return the adjusted value.
    return value
} //TimePicker.prototype.normalize


/**
 * Measure the range of minutes.
 */
TimePicker.prototype.measure = function( type, value, options ) {

    var clock = this

    // If it’s anything false-y, set it to the default.
    if ( !value ) {
        value = type == 'min' ? [ 0, 0 ] : [ HOURS_IN_DAY - 1, MINUTES_IN_HOUR - 1 ]
    }

    // If it’s a string, parse it.
    if ( typeof value == 'string' ) {
        value = clock.parse( type, value )
    }

    // If it’s a literal true, or an integer, make it relative to now.
    else if ( value === true || _.isInteger( value ) ) {
        value = clock.now( type, value, options )
    }

    // If it’s an object already, just normalize it.
    else if ( $.isPlainObject( value ) && _.isInteger( value.pick ) ) {
        value = clock.normalize( type, value.pick, options )
    }

    return value
} ///TimePicker.prototype.measure


/**
 * Validate an object as enabled.
 */
TimePicker.prototype.validate = function( type, timeObject, options ) {

    var clock = this,
        interval = options && options.interval ? options.interval : clock.item.interval

    // Check if the object is disabled.
    if ( clock.disabled( timeObject ) ) {

        // Shift with the interval until we reach an enabled time.
        timeObject = clock.shift( timeObject, interval )
    }

    // Scope the object into range.
    timeObject = clock.scope( timeObject )

    // Do a second check to see if we landed on a disabled min/max.
    // In that case, shift using the opposite interval as before.
    if ( clock.disabled( timeObject ) ) {
        timeObject = clock.shift( timeObject, interval * -1 )
    }

    // Return the final object.
    return timeObject
} //TimePicker.prototype.validate


/**
 * Check if an object is disabled.
 */
TimePicker.prototype.disabled = function( timeToVerify ) {

    var clock = this,

        // Filter through the disabled times to check if this is one.
        isDisabledMatch = clock.item.disable.filter( function( timeToDisable ) {

            // If the time is a number, match the hours.
            if ( _.isInteger( timeToDisable ) ) {
                return timeToVerify.hour == timeToDisable
            }

            // If it’s an array, create the object and match the times.
            if ( $.isArray( timeToDisable ) || _.isDate( timeToDisable ) ) {
                return timeToVerify.pick == clock.create( timeToDisable ).pick
            }

            // If it’s an object, match a time within the “from” and “to” range.
            if ( $.isPlainObject( timeToDisable ) ) {
                return clock.withinRange( timeToDisable, timeToVerify )
            }
        })

    // If this time matches a disabled time, confirm it’s not inverted.
    isDisabledMatch = isDisabledMatch.length && !isDisabledMatch.filter(function( timeToDisable ) {
        return $.isArray( timeToDisable ) && timeToDisable[2] == 'inverted' ||
            $.isPlainObject( timeToDisable ) && timeToDisable.inverted
    }).length

    // If the clock is "enabled" flag is flipped, flip the condition.
    return clock.item.enable === -1 ? !isDisabledMatch : isDisabledMatch ||
        timeToVerify.pick < clock.item.min.pick ||
        timeToVerify.pick > clock.item.max.pick
} //TimePicker.prototype.disabled


/**
 * Shift an object by an interval until we reach an enabled object.
 */
TimePicker.prototype.shift = function( timeObject, interval ) {

    var clock = this,
        minLimit = clock.item.min.pick,
        maxLimit = clock.item.max.pick/*,
        safety = 1000*/

    interval = interval || clock.item.interval

    // Keep looping as long as the time is disabled.
    while ( /*safety &&*/ clock.disabled( timeObject ) ) {

        /*safety -= 1
        if ( !safety ) {
            throw 'Fell into an infinite loop while shifting to ' + timeObject.hour + ':' + timeObject.mins + '.'
        }*/

        // Increase/decrease the time by the interval and keep looping.
        timeObject = clock.create( timeObject.pick += interval )

        // If we've looped beyond the limits, break out of the loop.
        if ( timeObject.pick <= minLimit || timeObject.pick >= maxLimit ) {
            break
        }
    }

    // Return the final object.
    return timeObject
} //TimePicker.prototype.shift


/**
 * Scope an object to be within range of min and max.
 */
TimePicker.prototype.scope = function( timeObject ) {
    var minLimit = this.item.min.pick,
        maxLimit = this.item.max.pick
    return this.create( timeObject.pick > maxLimit ? maxLimit : timeObject.pick < minLimit ? minLimit : timeObject )
} //TimePicker.prototype.scope


/**
 * Parse a string into a usable type.
 */
TimePicker.prototype.parse = function( type, value, options ) {

    var hour, minutes, isPM, item, parseValue,
        clock = this,
        parsingObject = {}

    // If it’s already parsed, we’re good.
    if ( !value || typeof value != 'string' ) {
        return value
    }

    // We need a `.format` to parse the value with.
    if ( !( options && options.format ) ) {
        options = options || {}
        options.format = clock.settings.format
    }

    // Convert the format into an array and then map through it.
    clock.formats.toArray( options.format ).map( function( label ) {

        var
            substring,

            // Grab the formatting label.
            formattingLabel = clock.formats[ label ],

            // The format length is from the formatting label function or the
            // label length without the escaping exclamation (!) mark.
            formatLength = formattingLabel ?
                _.trigger( formattingLabel, clock, [ value, parsingObject ] ) :
                label.replace( /^!/, '' ).length

        // If there's a format label, split the value up to the format length.
        // Then add it to the parsing object with appropriate label.
        if ( formattingLabel ) {
            substring = value.substr( 0, formatLength )
            parsingObject[ label ] = substring.match(/^\d+$/) ? +substring : substring
        }

        // Update the time value as the substring from format length to end.
        value = value.substr( formatLength )
    })

    // Grab the hour and minutes from the parsing object.
    for ( item in parsingObject ) {
        parseValue = parsingObject[item]
        if ( _.isInteger(parseValue) ) {
            if ( item.match(/^(h|hh)$/i) ) {
                hour = parseValue
                if ( item == 'h' || item == 'hh' ) {
                    hour %= 12
                }
            }
            else if ( item == 'i' ) {
                minutes = parseValue
            }
        }
        else if ( item.match(/^a$/i) && parseValue.match(/^p/i) && ('h' in parsingObject || 'hh' in parsingObject) ) {
            isPM = true
        }
    }

    // Calculate it in minutes and return.
    return (isPM ? hour + 12 : hour) * MINUTES_IN_HOUR + minutes
} //TimePicker.prototype.parse


/**
 * Various formats to display the object in.
 */
TimePicker.prototype.formats = {

    h: function( string, timeObject ) {

        // If there's string, then get the digits length.
        // Otherwise return the selected hour in "standard" format.
        return string ? _.digits( string ) : timeObject.hour % HOURS_TO_NOON || HOURS_TO_NOON
    },
    hh: function( string, timeObject ) {

        // If there's a string, then the length is always 2.
        // Otherwise return the selected hour in "standard" format with a leading zero.
        return string ? 2 : _.lead( timeObject.hour % HOURS_TO_NOON || HOURS_TO_NOON )
    },
    H: function( string, timeObject ) {

        // If there's string, then get the digits length.
        // Otherwise return the selected hour in "military" format as a string.
        return string ? _.digits( string ) : '' + ( timeObject.hour % 24 )
    },
    HH: function( string, timeObject ) {

        // If there's string, then get the digits length.
        // Otherwise return the selected hour in "military" format with a leading zero.
        return string ? _.digits( string ) : _.lead( timeObject.hour % 24 )
    },
    i: function( string, timeObject ) {

        // If there's a string, then the length is always 2.
        // Otherwise return the selected minutes.
        return string ? 2 : _.lead( timeObject.mins )
    },
    a: function( string, timeObject ) {

        // If there's a string, then the length is always 4.
        // Otherwise check if it's more than "noon" and return either am/pm.
        return string ? 4 : MINUTES_IN_DAY / 2 > timeObject.time % MINUTES_IN_DAY ? 'a.m.' : 'p.m.'
    },
    A: function( string, timeObject ) {

        // If there's a string, then the length is always 2.
        // Otherwise check if it's more than "noon" and return either am/pm.
        return string ? 2 : MINUTES_IN_DAY / 2 > timeObject.time % MINUTES_IN_DAY ? 'AM' : 'PM'
    },

    // Create an array by splitting the formatting string passed.
    toArray: function( formatString ) { return formatString.split( /(h{1,2}|H{1,2}|i|a|A|!.)/g ) },

    // Format an object into a string using the formatting options.
    toString: function ( formatString, itemObject ) {
        var clock = this
        return clock.formats.toArray( formatString ).map( function( label ) {
            return _.trigger( clock.formats[ label ], clock, [ 0, itemObject ] ) || label.replace( /^!/, '' )
        }).join( '' )
    }
} //TimePicker.prototype.formats




/**
 * Check if two time units are the exact.
 */
TimePicker.prototype.isTimeExact = function( one, two ) {

    var clock = this

    // When we’re working with minutes, do a direct comparison.
    if (
        ( _.isInteger( one ) && _.isInteger( two ) ) ||
        ( typeof one == 'boolean' && typeof two == 'boolean' )
     ) {
        return one === two
    }

    // When we’re working with time representations, compare the “pick” value.
    if (
        ( _.isDate( one ) || $.isArray( one ) ) &&
        ( _.isDate( two ) || $.isArray( two ) )
    ) {
        return clock.create( one ).pick === clock.create( two ).pick
    }

    // When we’re working with range objects, compare the “from” and “to”.
    if ( $.isPlainObject( one ) && $.isPlainObject( two ) ) {
        return clock.isTimeExact( one.from, two.from ) && clock.isTimeExact( one.to, two.to )
    }

    return false
}


/**
 * Check if two time units overlap.
 */
TimePicker.prototype.isTimeOverlap = function( one, two ) {

    var clock = this

    // When we’re working with an integer, compare the hours.
    if ( _.isInteger( one ) && ( _.isDate( two ) || $.isArray( two ) ) ) {
        return one === clock.create( two ).hour
    }
    if ( _.isInteger( two ) && ( _.isDate( one ) || $.isArray( one ) ) ) {
        return two === clock.create( one ).hour
    }

    // When we’re working with range objects, check if the ranges overlap.
    if ( $.isPlainObject( one ) && $.isPlainObject( two ) ) {
        return clock.overlapRanges( one, two )
    }

    return false
}


/**
 * Flip the “enabled” state.
 */
TimePicker.prototype.flipEnable = function(val) {
    var itemObject = this.item
    itemObject.enable = val || (itemObject.enable == -1 ? 1 : -1)
}


/**
 * Mark a collection of times as “disabled”.
 */
TimePicker.prototype.deactivate = function( type, timesToDisable ) {

    var clock = this,
        disabledItems = clock.item.disable.slice(0)


    // If we’re flipping, that’s all we need to do.
    if ( timesToDisable == 'flip' ) {
        clock.flipEnable()
    }

    else if ( timesToDisable === false ) {
        clock.flipEnable(1)
        disabledItems = []
    }

    else if ( timesToDisable === true ) {
        clock.flipEnable(-1)
        disabledItems = []
    }

    // Otherwise go through the times to disable.
    else {

        timesToDisable.map(function( unitToDisable ) {

            var matchFound

            // When we have disabled items, check for matches.
            // If something is matched, immediately break out.
            for ( var index = 0; index < disabledItems.length; index += 1 ) {
                if ( clock.isTimeExact( unitToDisable, disabledItems[index] ) ) {
                    matchFound = true
                    break
                }
            }

            // If nothing was found, add the validated unit to the collection.
            if ( !matchFound ) {
                if (
                    _.isInteger( unitToDisable ) ||
                    _.isDate( unitToDisable ) ||
                    $.isArray( unitToDisable ) ||
                    ( $.isPlainObject( unitToDisable ) && unitToDisable.from && unitToDisable.to )
                ) {
                    disabledItems.push( unitToDisable )
                }
            }
        })
    }

    // Return the updated collection.
    return disabledItems
} //TimePicker.prototype.deactivate


/**
 * Mark a collection of times as “enabled”.
 */
TimePicker.prototype.activate = function( type, timesToEnable ) {

    var clock = this,
        disabledItems = clock.item.disable,
        disabledItemsCount = disabledItems.length

    // If we’re flipping, that’s all we need to do.
    if ( timesToEnable == 'flip' ) {
        clock.flipEnable()
    }

    else if ( timesToEnable === true ) {
        clock.flipEnable(1)
        disabledItems = []
    }

    else if ( timesToEnable === false ) {
        clock.flipEnable(-1)
        disabledItems = []
    }

    // Otherwise go through the disabled times.
    else {

        timesToEnable.map(function( unitToEnable ) {

            var matchFound,
                disabledUnit,
                index,
                isRangeMatched

            // Go through the disabled items and try to find a match.
            for ( index = 0; index < disabledItemsCount; index += 1 ) {

                disabledUnit = disabledItems[index]

                // When an exact match is found, remove it from the collection.
                if ( clock.isTimeExact( disabledUnit, unitToEnable ) ) {
                    matchFound = disabledItems[index] = null
                    isRangeMatched = true
                    break
                }

                // When an overlapped match is found, add the “inverted” state to it.
                else if ( clock.isTimeOverlap( disabledUnit, unitToEnable ) ) {
                    if ( $.isPlainObject( unitToEnable ) ) {
                        unitToEnable.inverted = true
                        matchFound = unitToEnable
                    }
                    else if ( $.isArray( unitToEnable ) ) {
                        matchFound = unitToEnable
                        if ( !matchFound[2] ) matchFound.push( 'inverted' )
                    }
                    else if ( _.isDate( unitToEnable ) ) {
                        matchFound = [ unitToEnable.getFullYear(), unitToEnable.getMonth(), unitToEnable.getDate(), 'inverted' ]
                    }
                    break
                }
            }

            // If a match was found, remove a previous duplicate entry.
            if ( matchFound ) for ( index = 0; index < disabledItemsCount; index += 1 ) {
                if ( clock.isTimeExact( disabledItems[index], unitToEnable ) ) {
                    disabledItems[index] = null
                    break
                }
            }

            // In the event that we’re dealing with an overlap of range times,
            // make sure there are no “inverted” times because of it.
            if ( isRangeMatched ) for ( index = 0; index < disabledItemsCount; index += 1 ) {
                if ( clock.isTimeOverlap( disabledItems[index], unitToEnable ) ) {
                    disabledItems[index] = null
                    break
                }
            }

            // If something is still matched, add it into the collection.
            if ( matchFound ) {
                disabledItems.push( matchFound )
            }
        })
    }

    // Return the updated collection.
    return disabledItems.filter(function( val ) { return val != null })
} //TimePicker.prototype.activate


/**
 * The division to use for the range intervals.
 */
TimePicker.prototype.i = function( type, value/*, options*/ ) {
    return _.isInteger( value ) && value > 0 ? value : this.item.interval
}


/**
 * Create a string for the nodes in the picker.
 */
TimePicker.prototype.nodes = function( isOpen ) {

    var
        clock = this,
        settings = clock.settings,
        selectedObject = clock.item.select,
        highlightedObject = clock.item.highlight,
        viewsetObject = clock.item.view,
        disabledCollection = clock.item.disable

    return _.node(
        'ul',
        _.group({
            min: clock.item.min.pick,
            max: clock.item.max.pick,
            i: clock.item.interval,
            node: 'li',
            item: function( loopedTime ) {
                loopedTime = clock.create( loopedTime )
                var timeMinutes = loopedTime.pick,
                    isSelected = selectedObject && selectedObject.pick == timeMinutes,
                    isHighlighted = highlightedObject && highlightedObject.pick == timeMinutes,
                    isDisabled = disabledCollection && clock.disabled( loopedTime ),
                    formattedTime = _.trigger( clock.formats.toString, clock, [ settings.format, loopedTime ] )
                return [
                    _.trigger( clock.formats.toString, clock, [ _.trigger( settings.formatLabel, clock, [ loopedTime ] ) || settings.format, loopedTime ] ),
                    (function( klasses ) {

                        if ( isSelected ) {
                            klasses.push( settings.klass.selected )
                        }

                        if ( isHighlighted ) {
                            klasses.push( settings.klass.highlighted )
                        }

                        if ( viewsetObject && viewsetObject.pick == timeMinutes ) {
                            klasses.push( settings.klass.viewset )
                        }

                        if ( isDisabled ) {
                            klasses.push( settings.klass.disabled )
                        }

                        return klasses.join( ' ' )
                    })( [ settings.klass.listItem ] ),
                    'data-pick=' + loopedTime.pick + ' ' + _.ariaAttr({
                        role: 'option',
                        label: formattedTime,
                        selected: isSelected && clock.$node.val() === formattedTime ? true : null,
                        activedescendant: isHighlighted ? true : null,
                        disabled: isDisabled ? true : null
                    })
                ]
            }
        }) +

        // * For Firefox forms to submit, make sure to set the button’s `type` attribute as “button”.
        _.node(
            'li',
            _.node(
                'button',
                settings.clear,
                settings.klass.buttonClear,
                'type=button data-clear=1' + ( isOpen ? '' : ' disabled' ) + ' ' +
                _.ariaAttr({ controls: clock.$node[0].id })
            ),
            '', _.ariaAttr({ role: 'presentation' })
        ),
        settings.klass.list,
        _.ariaAttr({ role: 'listbox', controls: clock.$node[0].id })
    )
} //TimePicker.prototype.nodes







/**
 * Extend the picker to add the component with the defaults.
 */
TimePicker.defaults = (function( prefix ) {

    return {

        // Clear
        clear: 'Clear',

        // The format to show on the `input` element
        format: 'h:i A',

        // The interval between each time
        interval: 30,

        // Picker close behavior
        closeOnSelect: true,
        closeOnClear: true,

        // Classes
        klass: {

            picker: prefix + ' ' + prefix + '--time',
            holder: prefix + '__holder',

            list: prefix + '__list',
            listItem: prefix + '__list-item',

            disabled: prefix + '__list-item--disabled',
            selected: prefix + '__list-item--selected',
            highlighted: prefix + '__list-item--highlighted',
            viewset: prefix + '__list-item--viewset',
            now: prefix + '__list-item--now',

            buttonClear: prefix + '__button--clear'
        }
    }
})( Picker.klasses().picker )





/**
 * Extend the picker to add the time picker.
 */
Picker.extend( 'pickatime', TimePicker )


}));




define('gigaset_csp/components/customer/request',[
	"jquery",
	"cartware/components/_base",
	"cartware/jquery/bem",
	"select2",
	"picker-date",
	"picker-time",
	"bootstrap-button"
], function(
	$,
	Component,
	BEM,
	S2
) {
	return Component.create({
		block: null,

		form:				null,
		varienForm:			null,
		submitButton:		null,

		countrySelect:		null,
		businessUnitSelect:	null,
		producttypeSelect:	null,
		noProducttypeInfo:	null,
		productSelect:		null,
		defectRadios:		null,

		dependDefectBlocks:	null,
		dependUnitBlocks: 	null,

		fault_date:			null,
		fault_time:			null,

		options:			null,

		loading:			false,

		productsLoaded:		false,
		defect:				false,

		$init: function() {
			this.block = BEM.block("csp-customer-request-form");
			this.form = this.block.getElements("form");
			this.submitButton = this.block.getElements("submit-button");
			this.varienForm = new VarienForm(this.form.get(0));

			this.options = this.block.data("config");

			this.countrySelect = this.block.getElements("country_id");
			this.countrySelect
				.on("change", $.proxy(this._onChangeCountry, this))
				.select2();

			this.businessUnitSelect = this.block.getElements("business_unit");
			this.businessUnitSelect
				// .select2()
				.on("change", $.proxy(this._onChangeBusinessUnit, this));

			this.producttypeSelect = this.block.getElements("product_type_id");
			this.producttypeSelect
				.select2()
				.on("change", $.proxy(this._onChangeProducttype, this));

			this.noProducttypeInfo = this.block.getElements("no_product_type_info");

			this.productSelect = this.block.getElements("product_id");
			//this.productSelect.select2();

			this.dependDefectBlocks = this.form.find("[data-depends-defect]");
			this.dependUnitBlocks = this.form.find("[data-depends-businessunit]");

			this.defectRadios = this.block.getElements("assumed_defect");
			this.defectRadios
				.on("change", $.proxy(this._onChangeDefect, this));

			this.countrySelect.change();
			this._onChangeDefect();

			// this.businessUnit.change();

			// this.applyDefectDependencies();
			// this.applyUnitDependencies();

			this.fault_date = this.block.getElements("fault_date");
			this.fault_date.pickadate({
				format: 'yyyy-mm-dd',
				formatSubmit: 'yyyy-mm-dd',
				clear: ''
			});

			this.fault_time = this.block.getElements("fault_time");
			this.fault_time.pickatime({
				format: 'HH:i',
				formatLabel: 'HH:i',
				formatSubmit: 'HH:i',
				clear: ''
			});

			this.form.on("submit", $.proxy(this._onFormSubmit, this));
		},

		setLoading: function(field, flag) {
			this.loading = flag;
			this.block[flag ? "addModifier" : "removeModifier"]("loading-" + field);
		},

		applyDefectDependencies: function() {
			if (this.defect) {
				this.dependDefectBlocks.show();
			} else {
				this.dependDefectBlocks.hide();
			}
		},

		applyUnitDependencies: function() {
			var unit = this.businessUnitSelect.val();
			this.dependUnitBlocks.each(function(){
				var blockUnit = $(this).data('depends-businessunit');
				if (unit && (blockUnit == unit || blockUnit == 0)) {
					$(this).show();
				} else {
					$(this).hide();
				}
			});
		},

		loadProducts: function() {
			if (!this.defect || this.productsLoaded) {
				return;
			}

			var self = this,
				producttype = this.producttypeSelect.val(),
				country = this.countrySelect.val();

			if (!producttype || producttype.length === 0) {
				this._onProductsRetrieve({});
				return;
			}

			var productsFieldName = this.productSelect.attr('name');

			this.setLoading(productsFieldName, true);
			this.productSelect.attr("disabled", "disabled");
			this.productSelect.html("");

			$.ajax(this.options.productOptionsUrl, {
				method: "GET",
				data: {
					producttype: producttype,
					country: country
				}
			})
				.done($.proxy(this._onProductsRetrieve, this))
				.always(function() {
					self.setLoading(productsFieldName, false);
				});
		},

		_onChangeDefect: function() {
			var self = this;
			this.defect = false;

			this.defectRadios.each(function(){
				if (this.checked && this.value == 1) {
					self.defect = true;
				}
			});

			this.applyDefectDependencies();
			this.loadProducts();
		},

		_onChangeCountry: function() {
			var self = this,
				country = this.countrySelect.val();

			// if (country.length && country) {
			// 	this.businessUnitSelect.removeAttr("disabled");
			// } else {
			// 	this.businessUnitSelect
			// 		.val('')
			// 		.attr("disabled", "disabled");
			// }

			this.businessUnitSelect.trigger('change');
		},

		_onChangeBusinessUnit: function() {
			var self = this,
				unit = this.businessUnitSelect.val(),
				country = this.countrySelect.val(),
				selectedOption = this.businessUnitSelect.find('option:selected');

			if (selectedOption) {
				var relocateUrl = selectedOption.data('relocate');
				if (relocateUrl) {
					var win = window.open(relocateUrl, '_blank');
					win.focus();
				}
			}

			this.applyUnitDependencies();

			if (!unit.length || !country.length) {
				this._onProducttypesRetrieve({});
				return;
			}

			var productTypesFieldName = this.producttypeSelect.attr('name');

			this.setLoading(productTypesFieldName, true);
			this.producttypeSelect.attr("disabled", "disabled");
			this.producttypeSelect.html("");

			$.ajax(this.options.productTypeOptionsUrl, {
				method: "GET",
				data: {
					business_unit: unit,
					country: country
				}
			})
				.done($.proxy(this._onProducttypesRetrieve, this))
				.always(function() {
					self.setLoading(productTypesFieldName, false);
				});
		},

		_onChangeProducttype: function() {
			this._onProductsRetrieve({});
			this.loadProducts();
		},

		_onProducttypesRetrieve: function(data) {
			var optionsHtml = [];

			$.each(data, function(key, value) {
				optionsHtml.push('<option value="' + key + '">' + value + '</option>');
			});

			this.producttypeSelect.html(optionsHtml.join(""));

			if (optionsHtml.length) {
				var guid = this.producttypeSelect.attr('value'),
					option = this.producttypeSelect.find('option[value="' + guid +'"]');

				this.producttypeSelect.removeAttr("disabled");
				this.noProducttypeInfo.hide();

				if (option) {
					this.producttypeSelect.val(guid);
					option.attr('selected', 'selected');
				}

				this._onChangeProducttype();
			} else {
				this.producttypeSelect.attr("disabled", "disabled");

				if (this.countrySelect.val() && this.businessUnitSelect.val()) {
					this.noProducttypeInfo.show();
				}

				this._onProductsRetrieve({});
			}

			this.producttypeSelect.select2();
		},

		_onProductsRetrieve: function(data) {
			var optionsHtml = [];

			$.each(data, function(key, value) {
				optionsHtml.push('<option value="' + key + '">' + value + '</option>');
			});

			this.productSelect.html(optionsHtml.join(""));

			if (optionsHtml.length) {
				this.productSelect.removeAttr("disabled");
				this.productsLoaded = true;
			} else {
				this.productSelect.attr("disabled", "disabled");
				this.productsLoaded = false;
			}

			this.productSelect.select2();
		},

		_onFormSubmit: function(e) {
			if (this.loading || !this.varienForm.validator.validate()) {
				e.preventDefault();
				return;
			}

			this.block.addModifier("submitting");
			this.submitButton.button("loading");
		}
	});
});
define('gigaset_csp/components/handsetbase/registration',[
	"jquery",
	"cartware/components/_base",
	"cartware/components/PubSubHub",
	"cartware/jquery/bem",
	"shoptheme/components/tracking",
	"magnific-popup"
], function(
	$,
	Component,
	PubSubHub,
	BEM
) {
	return Component.create({
		block: null,
		productData: null,

		currentHandset: null,
		currentBasestation: null,

		// 1x1 transparent gif
		fallbackImage: "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7",

		$init: function() {
			this.block = BEM.block("csp-registration");

			this.handsetSelect = this.block.getElements("handsets");
			this.handsetImage = this.block.getElements("handsetImage");
			this.handsetImageWrapper = this.block.getElements("image-wrapper-handset");
			this.handsetText = this.block.getElements("handsetText");
			this.handsetTextWrapper = this.block.getElements("text-wrapper-handset");
			this.handsetProductImage = this.block.getElements("productImageHandset");


			this.basestationSelect = this.block.getElements("basestations");
			this.basestationImage = this.block.getElements("basestationImage");
			this.basestationImageWrapper = this.block.getElements("image-wrapper-basesation");
			this.basestationText = this.block.getElements("basestationText");
			this.basestationTextWrapper = this.block.getElements("text-wrapper-basesation");


			this.basestationProductImage = this.block.getElements("productImageBasestation");

			this.popupLink = this.block.getElements("zoomLink");

			var productDataId = "productData_" + this.block.find("[data-products-id]").data("products-id");

			this.productData = productDataId in window ? window[productDataId] : {};
			this.bindEvents();

			this.parseUrl();
			this.manualPopup();
			this.setHandsetProductImage();
			this.setBasestationProductImage();

			$(window).on('hashchange', $.proxy(this.parseUrl, this));

            this.initAriaLabels();
		},

        initAriaLabels: function() {
            const selectors = [
                { id: '#select2-csp_handset_select-container', label: 'csp_handset_select_label' },
                { id: '#select2-csp_base_select-container', label: 'csp_base_select_label' }
            ];

            selectors.forEach(({ id, label }) => {
                this.waitForElement(id).then((e) => {
                    $(e).attr('aria-labelledby', label);
                });
            });
        },

        waitForElement: function(selector) {
            return new Promise(resolve => {
                if (document.querySelector(selector)) {
                    return resolve(document.querySelector(selector));
                }

                const observer = new MutationObserver(mutations => {
                    if (document.querySelector(selector)) {
                        resolve(document.querySelector(selector));
                        observer.disconnect();
                    }
                });

                observer.observe(document.body, {
                    childList: true,
                    subtree: true
                });
            });
        },

		manualPopup: function() {

			$(document).on("click", '.cc_container p a', function (e) {

				var link = $(e.currentTarget);
				if (link.hasClass('cc_btn')) {
					return;
				}
				e.preventDefault();


				link.prop('href', self._updateUrlParameter(link.prop('href'), 'isAjax', 1));

				link.magnificPopup({
					type: 'ajax',
					callbacks: {
						parseAjax: function (mfpResponse) {
							var html = mfpResponse.data.replace(/<script[^>]*>([\s\S]*?)<\/script>/gi, '');

							mfpResponse.data = self._renderPopup({
								body: html
							});
						}
					}
				}).magnificPopup('open');
			});

			this.popupLink.on('click', function(e) {
				e.preventDefault();

				var link = $(this).find('img').attr('src');

				$(this).magnificPopup({
					items: {
						src: link
					},
					type: 'image'
				}).magnificPopup('open');

			});


		},

		parseUrl: function() {
			var urlMatches = /#(.+)/.exec(document.location);

			if (urlMatches) {
				$.each(urlMatches[1].split("&"), $.proxy(function(i, fragment) {
					var parts = /(.+?)=(\d+)/.exec(fragment);

					if (!parts) return;

					if (parts[1] === "basestation") {
						this.setBasestationInternal(parts[2]);
					} else if (parts[1] === "handset") {
						this.setHandsetInternal(parts[2]);
					}
				}, this));
			}
		},

		bindEvents: function() {
			this.handsetSelect.on("change", $.proxy(function(e, preventLoop) {
				preventLoop || this.setHandset(this.handsetSelect.val());
			}, this));

			this.basestationSelect.on("change", $.proxy(function(e, preventLoop) {
				preventLoop|| this.setBasestation(this.basestationSelect.val());
			}, this));
		},

		setHandset: function(value) {
			this.setHandsetProductImage();

			this.setUrlHash("handset", value);
			return this;
		},

		setHandsetProductImage: function() {
			if (this.handsetSelect.val() && this.productData[this.handsetSelect.val()].image) {
				this.handsetProductImage.attr("src", this.productData[this.handsetSelect.val()].image);
			} else {
				this.handsetProductImage.attr("src", this.handsetProductImage.attr('data-default-src'));
			}
		},

		setBasestation: function(value) {
			this.setBasestationProductImage();

			this.setUrlHash("basestation", value);
			return this;
		},

		setBasestationProductImage: function() {
			if (this.basestationSelect.val() && this.productData[this.basestationSelect.val()].image) {
				this.basestationProductImage.attr("src", this.productData[this.basestationSelect.val()].image);
			} else {
				this.basestationProductImage.attr("src", this.basestationProductImage.attr('data-default-src'));
			}
		},

		setHandsetInternal: function(value) {
			this.handsetSelect.val(value).trigger("change", true);
			this.currentHandset = value in this.productData ? this.productData[value] : null;
			this.currentHandset.uid = value;
			this.onChange();
			return this;
		},

		setBasestationInternal: function(value) {
			this.basestationSelect.val(value).trigger("change", true);
			this.currentBasestation = value in this.productData ? this.productData[value] : null;
			this.currentBasestation.uid = value;
			this.onChange();
			return this;
		},

		onChange: function() {
			if (this.currentHandset) {
				if (this.currentHandset.mobile && this.currentHandset.mobile.type == 'file') {
					this.handsetTextWrapper.removeClass('active');
					this.handsetImageWrapper.addClass('active');
					this.handsetImage.attr("src", this.currentHandset.mobile.content ? this.currentHandset.mobile.content : this.fallbackImage);
				} else {
					this.handsetImageWrapper.removeClass('active');
					this.handsetTextWrapper.addClass('active');
					this.handsetText.html(this.currentHandset.mobile ? this.currentHandset.mobile.content : '');
				}
			}

			if (this.currentBasestation) {
				if (this.currentBasestation.base && this.currentBasestation.base.type == 'file') {
					this.basestationTextWrapper.removeClass('active');
					this.basestationImageWrapper.addClass('active');
					this.basestationImage.attr("src", this.currentBasestation.base.content ? this.currentBasestation.base.content : this.fallbackImage);
				} else {
					this.basestationImageWrapper.removeClass('active');
					this.basestationTextWrapper.addClass('active');
					this.basestationText.html(this.currentBasestation.base ? this.currentBasestation.base.content : '');
				}
			}

			if (this.currentHandset && this.currentBasestation) {
				this.registerProductCombination();
			}

			this.manualPopup();
		},

		setUrlHash: function(key, value) {
			var hash = window.location.hash,
				originalHash = hash,
				regex = new RegExp(key + "=\\d+");

			if (regex.match(hash)) {
				hash = hash.replace(regex, key + "=" + value);
			} else {
				hash += hash.substr(0, 1) === "#" ? "&" : "#";
				hash += key + "=" + value;
			}

			hash = hash.replace(/&$/, ""); // strip trailing "&"

			if (hash !== originalHash) {
				window.location.hash = hash;
			}
		},

		registerProductCombination: function () {
			PubSubHub.emit("csp.handsetbase.registration.view", {
				handset: this.currentHandset,
				basestation: this.currentBasestation
			});
		}
	});
});

define('gigaset_csp/components/compatibility/byproducts',[
	"jquery",
	"lodash",
	"cartware/components/_base",
	"cartware/components/PubSubHub",
	"cartware/jquery/bem",
	"gigaset/spinner",
	"shoptheme/components/tracking"
], function(
	$,
	_,
	Component,
	PubSubHub,
	BEM,
	Spinner
) {
	return Component.create({

		block: null,
		form: null,
		productOne: null,
		productTwo: null,
		productOneSelect: null,
		productOneImage: null,
		productTwoSelect: null,
		productTwoImage: null,
		productTypeWrapper: null,
		productOneTypeSelect: null,
		productOneTypeHidden: null,
		productOneType: null,
		productData: null,
		resultListContainer: null,
		resultListTemplate: null,
		xhrCache: null,

		$init: function() {
			this.block = BEM.block("csp-compatibility-byproducts");
			this.form = this.block.getElements("form");

			this.productOneSelect = this.block.getElements("product1");
			this.productTwoSelect = this.block.getElements("product2");

			this.productOneImage = this.block.getElements("product1-image");
			this.productTwoImage = this.block.getElements("product2-image");

			this.productOneImage.attr('data-default-src', this.productOneImage.attr('src'));

			this.productTypeWrapper = this.block.getElements("productTypeWrapper");
			this.productOneTypeSelect = this.block.getElements("product1-type");
			this.productOneTypeHidden = $('<input type="hidden" name="' + this.productOneTypeSelect.attr("name") + '" style="display: none;" />');
			this.productOneTypeHidden.insertAfter(this.productOneTypeSelect);
			this.productOneType = this.productOneTypeSelect.val();

			var productDataId = "productData_" + this.block.find("[data-products-id]").data("products-id");
			this.productData = productDataId in window ? window[productDataId] : {};

			this.resultListContainer = BEM.block("csp-compatibility-featureresult");
			this.resultListTemplate = _.template(this.block.find('[data-template="resultList"]').html());

			this.xhrCache = {};

			this.bindEvents();
			this.parseUrl();
			$(window).on('hashchange', $.proxy(this.parseUrl, this));
            this.initAriaLabels();
		},

        waitForElement: function(selector) {
            return new Promise(resolve => {
                if (document.querySelector(selector)) {
                    return resolve(document.querySelector(selector));
                }

                const observer = new MutationObserver(mutations => {
                    if (document.querySelector(selector)) {
                        resolve(document.querySelector(selector));
                        observer.disconnect();
                    }
                });

                observer.observe(document.body, {
                    childList: true,
                    subtree: true
                });
            });
        },

		parseUrl: function() {
			var urlMatches = /#(.+)/.exec(document.location);
			var change = false;

			if (urlMatches) {
				this.productTwo = null; // Reset productTwo

				$.each(urlMatches[1].split("&"), $.proxy(function(i, fragment) {
					var parts = /(.+?)=([^&=]+)/.exec(fragment);

					if (!parts) return;

					if (parts[1] === "product1") {
						change = true;
						this.setProductOneInternal(parts[2]);
					} else if (parts[1] === "product1Type") {
						change = true;
						this.setProductOneTypeInternal(parts[2]);
					} else if (parts[1] === "product2") {
						change = true;
						this.setProductTwoInternal(parts[2]);
					}
				}, this));

				if (change) {
					this.onChange();
				}
			}
		},

        initAriaLabels: function() {
            const selectors = [
                { id: '#select2-compatibility_product1_select-container', label: 'compatibility_product1_select_label' },
                { id: '#select2-compatibility_product2_select-container', label: 'compatibility_product2_select_label' }
            ];

            selectors.forEach(({ id, label }) => {
                this.waitForElement(id).then((e) => {
                    $(e).attr('aria-labelledby', label);
                });
            });
        },

		bindEvents: function() {
			this.productOneSelect.on("change", $.proxy(function(e, preventLoop) {
				if (this.productOneSelect.val() && this.productData[this.productOneSelect.val()].image) {
					this.productOneImage.attr("src", this.productData[this.productOneSelect.val()].image);
				} else {
					this.productOneImage.attr("src", this.productOneImage.attr('data-default-src'));
				}

				if (preventLoop) return;
				this.setProductOne(this.productOneSelect.val());
			}, this));

			this.productTwoSelect.on("change", $.proxy(function(e, preventLoop) {
				if (this.productTwoSelect.val() && this.productData[this.productTwoSelect.val()].image) {
					this.productTwoImage.attr("src", this.productData[this.productTwoSelect.val()].image);
				} else {
					this.productTwoImage.attr("src", this.productOneImage.attr('data-default-src'));
				}

				preventLoop|| this.setProductTwo(this.productTwoSelect.val());
			}, this));

			this.productOneTypeSelect.on("change", $.proxy(function(e, preventLoop) {
				var newValue = this.productOneTypeSelect.val();

				if (this.productOne && this.productOne.compatibleIds) {
					var productOneType = "basestation";
					var compatibleIds = this.productOne.compatibleIds;

					if ("basestations" in compatibleIds && compatibleIds.basestations.length) {
						if (newValue == "handset"
							|| (!("handsets" in compatibleIds) || !compatibleIds.handsets.length)
						) {
							productOneType = "handset";
						}
					}

					if (productOneType != newValue) {
						this.productOneTypeSelect.val(productOneType);
						return;
					}
				}

				preventLoop|| this.setProductOneType(newValue);
			}, this));
		},

		setProductOne: function(value) {
			this.setUrlHash({
				product1: value,
				product2: null,
				product1Type: null
			});
			return this;
		},

		setProductTwo: function(value) {
			this.setUrlHash({
				product2: value
			});
			return this;
		},

		setProductOneType: function(value) {
			this.setUrlHash({
				product1Type: value,
				product2: null
			});
			return this;
		},

		setProductOneTypeVisibility: function(prodTypeVisibility) {
			this.productOneTypeSelect.attr("disabled", prodTypeVisibility);
			var myElement = $('.b_csp-compatibility-byproducts__productTypeWrapper');
			myElement[prodTypeVisibility ? 'bemAddModifier' : 'bemRemoveModifier']("disabled");
		},

		setProductOneInternal: function(value) {
			this.productOneSelect.val(value).trigger("change", true);
			this.productOne = value in this.productData ? this.productData[value] : null;
			this.productOne.uid = value;

			var compatibleIds = this.productOne.compatibleIds;
			var enableSwitch = false;
			var switchType = false;

			if ("basestations" in compatibleIds && compatibleIds.basestations.length) {

				if (("handsets" in compatibleIds) && compatibleIds.handsets.length) {
					enableSwitch = true;
				} else {
					switchType = true;
					this.setProductOneTypeInternal("handset");
				}
			} else if (("handsets" in compatibleIds) && compatibleIds.handsets.length) {
				switchType = true;
				this.setProductOneTypeInternal("basestation");
			}

			if (!switchType) {
				this.setProductOneTypeInternal(this.productOneTypeSelect.val());
			}

			this.setProductOneTypeVisibility(!enableSwitch);

			return this;
		},

		setProductTwoInternal: function(value) {
			this.productTwoSelect.val(value).trigger("change", true);
			this.productTwo = value in this.productData ? this.productData[value] : null;
			this.productTwo.uid = value;
			return this;
		},

		setProductOneTypeInternal: function(value) {
			this.productOneTypeSelect.val(value).trigger("change", true);
			this.productOneTypeHidden.val(value);
			this.productOneType = value;

			this.fillProductTwoOptions();
			return this;
		},

		fillProductTwoOptions: function() {
			this.productTwoSelect.empty();

			var compatibleIds = this.productOne.compatibleIds.handsets;

			if (this.productOneType == "handset") {
				compatibleIds = this.productOne.compatibleIds.basestations;
			}

			_.forEach(compatibleIds, _.bind(function(compatibleId) {
				var compatibleProduct = compatibleId in this.productData ? this.productData[compatibleId]: null;
				if (!compatibleProduct) return;

				var option = $('<option value="' + compatibleProduct.uid + '">' + compatibleProduct.name + '</option>')
				option.appendTo(this.productTwoSelect);
			}, this));

			this.productTwoSelect.val("").trigger("change", true);
		},

		onChange: function() {
			this.resultListContainer.html("");

			if (this.productOne && this.productTwo) {
				var cacheKey = [this.productOne.uid, this.productTwo.uid].sort().join("-");

				if (cacheKey in this.xhrCache) {
					this.resultListContainer.html(this.xhrCache[cacheKey]);
				} else {
					this.setLoading(true);

					$.ajax({
						type: this.form.attr("method") || "POST",
						url: this.form.attr("action"),
						data: this.form.serialize()
					}).then(_.bind(function(results) {
						var html = this.resultListTemplate({
							results: results
						});

						this.setLoading(false);
						this.xhrCache[cacheKey] = html;
						this.resultListContainer.html(html);
					}, this));
				}

				this.registerProductCombination();
			}
		},

		setLoading: function(status) {
			// this.resultListContainer[status ? "addClass" : "removeClass"]("loading");
			Spinner[status ? "add" : "remove"](this.block);
		},

		setUrlHash: function(values) {
			var hash = window.location.hash,
				originalHash = hash;

			_.forEach(values, function(value, key) {
				var regex = new RegExp(key + "=[^&=]+");

				if (_.isArray(value)) {
					value = value.join(",");
				}

				if (regex.match(hash)) {
					if (null === value) {
						hash = hash.replace(regex, "");
					} else {
						hash = hash.replace(regex, key + "=" + value);
					}
				} else {
					if (value !== null) {
						hash += hash.substr(0, 1) === "#" ? "&" : "#";
						hash += key + "=" + value;
					}
				}
			});

			hash = hash.replace(/&$/, ""); // strip trailing "&"

			if (hash !== originalHash) {
				window.location.hash = hash;
			}
		},

		registerProductCombination: function () {
			PubSubHub.emit("csp.compatibility.byproducts.view", {
				productOne: this.productOne,
				productTwo: this.productTwo
			});
		}
	});
});

define('gigaset_csp/components/compatibility/byfeatures',[
	"jquery",
	"lodash",
	"cartware/components/_base",
	"cartware/components/PubSubHub",
	"cartware/jquery/bem",
	"gigaset/spinner",
	"shoptheme/components/tracking"
], function(
	$,
	_,
	Component,
	PubSubHub,
	BEM,
	Spinner
) {
	return Component.create({

		block: null,
		form: null,
		product: null,
		productSelect: null,
		functions: null,
		functionsSelect: null,
		productImage: null,
		productData: null,
		resultListContainer: null,
		resultListTemplate: null,
		xhrCache: null,

		modeWrapper: null,
		modeSelect: null,
		modeInput: null,
		mode: null,

		$init: function() {
			this.block = BEM.block("csp-compatibility-byfeatures");
			this.form = this.block.getElements("form");

			this.productSelect = this.block.getElements("productSelect");
			this.functionsSelect = this.block.getElements("functionsSelect");

			this.productImage = this.block.getElements("product-image");
			this.productImage.attr('data-default-src', this.productImage.attr('src'));

			var productDataId = "productData_" + this.block.find("[data-products-id]").data("products-id");
			this.productData = productDataId in window ? window[productDataId] : {};

			this.resultListContainer = BEM.block("csp-compatibility-productresult");
			this.resultListTemplate = _.template(this.block.find('[data-template="resultList"]').html());

			this.modeWrapper = this.block.getElements("mode-wrapper");
			this.modeSelect = this.block.getElements("modeSelect");
			this.modeInput = this.block.getElements("modeInput");

			this.xhrCache = {};

			this.bindEvents();
			this.parseUrl();
			this.setImage();
            this.initAriaLabels();
			$(window).on('hashchange', $.proxy(this.parseUrl, this));
		},

        waitForElement: function(selector) {
            return new Promise(resolve => {
                if (document.querySelector(selector)) {
                    return resolve(document.querySelector(selector));
                }

                const observer = new MutationObserver(mutations => {
                    if (document.querySelector(selector)) {
                        resolve(document.querySelector(selector));
                        observer.disconnect();
                    }
                });

                observer.observe(document.body, {
                    childList: true,
                    subtree: true
                });
            });
        },

		parseUrl: function() {
			var urlMatches = /#(.+)/.exec(document.location);
			var change = false;

			if (urlMatches) {
				$.each(urlMatches[1].split("&"), $.proxy(function(i, fragment) {
					var parts = /(.+?)=([^&=]+)/.exec(fragment);

					if (!parts) return;

					if (parts[1] === "product") {
						change = true;
						this.setProductInternal(parts[2]);
					} else if (parts[1] === "functions") {
						change = true;
						this.setFunctionsInternal((parts[2] || "").split(","));
					} else if (parts[1] === "mode") {
						change = true;
						this.setModeInternal(parts[2]);
					}
				}, this));

				if (change) {
					this.onChange();
				}
			}
		},

        initAriaLabels: function() {
            const selectors = [
                { id: '#select2-compatibility_feature_select-container', label: 'compatibility_feature_select_label' },
                { id: '#select2-compatibility_mode_select-container', label: 'compatibility_mode_select_label' },
                { id: '#select2-compatibility_functions_select-container', label: 'compatibility_functions_select_label' }
            ];

            selectors.forEach(({ id, label }) => {
                this.waitForElement(id).then((e) => {
                    $(e).attr('aria-labelledby', label);
                });
            });
        },

		bindEvents: function() {
			this.productSelect.on("change", $.proxy(function(e, preventLoop) {
				if (preventLoop) return;
				this.setProduct(this.productSelect.val());
			}, this));

			this.modeSelect.on("change", $.proxy(function(e, preventLoop) {
				preventLoop|| this.setMode(this.modeSelect.val());
			}, this));

			this.functionsSelect.on("change", $.proxy(function(e, preventLoop) {
				preventLoop|| this.setFunctions(this.functionsSelect.val());
			}, this));
		},

		setProduct: function(value) {
			this.setUrlHash({
				product: value
			});
			this.setImage();
			return this;
		},

		setImage: function() {
			if (this.productSelect.val() && this.productData[this.productSelect.val()].image) {
				this.productImage.attr("src", this.productData[this.productSelect.val()].image);
			} else {
				this.productImage.attr("src", this.productImage.attr('data-default-src'));
			}
		},

		setFunctions: function(value) {
			this.setUrlHash({
				functions: value
			});
			return this;
		},

		setMode: function(value) {
			this.setUrlHash({
				mode: value
			});
			return this;
		},

		setProductInternal: function(value) {
			this.productSelect.val(value).trigger("change", true);
			this.product = value in this.productData ? this.productData[value] : null;
			this.determineMode();
			return this;
		},

		setFunctionsInternal: function(value) {
			this.functionsSelect.val(value).trigger("change", true);
			this.functions = value.sort();
			return this;
		},

		setModeInternal: function(value) {
			this.modeSelect.val(value).trigger("change", true);
			this.modeInput.val(value);
			this.mode = value;
			return this;
		},

		setTypeVisibility: function(prodTypeVisibility) {
			var myElement = $('.b_csp-compatibility-byfeatures__mode-wrapper');
			myElement[prodTypeVisibility ? 'bemAddModifier' : 'bemRemoveModifier']("disabled");
		},

		determineMode: function() {
			if (this.product.base && this.product.handset) {
				// this.modeWrapper.show();
				this.setModeInternal("handset");
				this.modeSelect.attr("disabled", false);
				this.setTypeVisibility(false);
			} else {
				// this.modeWrapper.hide();

				if (this.product.handset) {
					this.setModeInternal("basestation");
				} else {
					// fallback
					this.setModeInternal("handset");
				}

				this.modeSelect.attr("disabled", true);
				this.setTypeVisibility(true);
			}

			this.modeSelect.trigger("change", true);
		},

		onChange: function() {
			this.resultListContainer.html("");

			if (this.product && this.functions && this.functions.length && this.mode) {
				var cacheKey = this.product.uid + "#" + this.mode + "#" + this.functions.sort().join("-");

				if (cacheKey in this.xhrCache) {
					this.resultListContainer.html(this.xhrCache[cacheKey]);
				} else {
					this.setLoading(true);

					$.ajax({
						type: this.form.attr("method") || "POST",
						url: this.form.attr("action"),
						data: this.form.serialize()
					}).then(_.bind(function(results) {
						var html = 'An error occurred';

						if ("undefined" !== typeof window.Translator && window.Translator.translate) {
							html = window.Translator.translate(html);
						}

						if ($.isArray(results)) {
							html = this.resultListTemplate({
								results: results
							});
						}

						this.setLoading(false);
						this.xhrCache[cacheKey] = html;
						this.resultListContainer.html(html);
					}, this));
				}

				this.registerProductCombination();
			}
		},

		setLoading: function(status) {
			// this.resultListContainer[status ? "addClass" : "removeClass"]("loading");
			Spinner[status ? "add" : "remove"](this.block);
		},

		setUrlHash: function(values) {
			var hash = window.location.hash,
				originalHash = hash;

			_.forEach(values, function(value, key) {
				var regex = new RegExp(key + "=[^&=]+");

				if (_.isArray(value)) {
					value = value.join(",");
				}

				if (regex.match(hash)) {
					if (null === value) {
						hash = hash.replace(regex, "");
					} else {
						hash = hash.replace(regex, key + "=" + value);
					}
				} else {
					if (value !== null) {
						hash += hash.substr(0, 1) === "#" ? "&" : "#";
						hash += key + "=" + value;
					}
				}
			});

			hash = hash.replace(/&$/, ""); // strip trailing "&"

			if (hash !== originalHash) {
				window.location.hash = hash;
			}
		},

		registerProductCombination: function () {
			var productLabel = this.productSelect.find("option:selected").text();
			var functionLabels = this.functionsSelect.find("option:selected").map(function() { return $(this).text(); }).get();


			PubSubHub.emit("csp.compatibility.byfeatures.view", {
				product: productLabel,
				functions: functionLabels
			});
		}
	});
});

define('gigaset_csp/components/support/detail/intro',[
	"jquery",
	"lodash",
	"cartware/components/_base",
	"cartware/components/Registry",
	"cartware/jquery/bem"
], function(
	$,
	_,
	Component,
	Registry,
	BEM
) {

	var BLOCK = "csp-support-detail-introduction";

	return Component.create({

		$init: function() {
			var self = this;
			this.block = BEM.block(BLOCK);

			Registry().retrieve("system.dispatcher", function(dispatcher) {
				var handles = dispatcher.getHandles();

				_.forEach({
					"faq":				"intro-faq",
					"downloads":		"intro-downloads",
					"accessories":		"intro-accessories",
					"compatibility":	"intro-compatibility",
					"registration":		"intro-registration",
					"contact":			"intro-contact",
					"videotutorials":	"intro-videotutorials",
					"repair":			"intro-repair"
				}, function(value, key) {
					var element = self.block.getElements(value);

					if (key === "accessories") {
						element.css("display", _.trim(self.block.getElements("block-accessories").text()) !== "" ? "" : "none");
					} else {
						element.css("display", _.indexOf(handles, "gigaset_csp_support_detail_" + key) >= 0 ? "" : "none");
					}
				});

				self.block.bemAddModifier("ready");
			});
		}

	});
});
define('gigaset_csp/components/support/detail/faq',[
	"jquery",
	"lodash",
	"cartware/components/_base",
	"cartware/components/PubSubHub",
	"cartware/jquery/bem",
	"text!shoptheme/templates/magnific-popup/default.html",
	"gigaset/spinner",
	"magnific-popup"
], function(
	$,
	_,
	Component,
	PubSubHub,
	BEM,
	PopupHtml
) {
	var BLOCK = "csp-support-detail-faq";

	var popupTemplate = _.template(PopupHtml);

	return Component.create({
		block: null,
		product: null,

		$init: function() {
			this.block = BEM.block(BLOCK);
			this.product = this.block.data("product");
			
			this.bindEvents();
		},

		bindEvents: function() {
			var self = this;
			var linkElements = [
				'div.' + BEM.buildElementClassname('csp-support-detail-faq-top-entries', 'link'),
				'div.' + BEM.buildElementClassname('csp-support-detail-faq-categorized', 'link'),
				'div.' + BEM.buildElementClassname('search-autocomplete-item', 'link'),
				'div.' + BEM.buildElementClassname('contentbridge-search-result', 'faq-link'),
				'#search-results-faqcontent ' + '.' + BEM.buildElementClassname('contentbridge-search-result', 'link')
			];

			$(document.body).on(
				"click",
				linkElements.join(","),
				function(e) {
					var link = $(this),
						href = link.data("href"),
						content;

					// Tracking call
					$.post(href);


					e.preventDefault();
					self.registerFaqView(href).then(function() {
						if (link.bemHasModifier("file")) {
							window.open(href, '_blank');
							return;
						}

						$.magnificPopup.open({
							items: {
								src: popupTemplate({
									title: link.text(),
									body: link.bemSiblings("content").html()
								}),
								type: 'inline'
							}
						});
					});
				}
			);
		},

		registerFaqView: function (faqUrl) {
			var deferred = $.Deferred();

			if (typeof faqUrl === "undefined" || faqUrl === "") {
				return deferred.resolve();
			}

			PubSubHub.emit("csp.support.detail.faq.view", {
				faqUrl: faqUrl,
				product: _.get(this, "product.name", null),
				deferred: deferred
			});

			return deferred.promise();
		}
	});
});
define('gigaset_csp/components/support/detail/faq/categories',[
	"jquery",
	"lodash",
	"cartware/components/_base",
	"cartware/jquery/bem"
], function(
	$,
	_,
	Component,
	BEM
) {
	var BLOCK = "csp-support-detail-faq-categorized";
	var ELEMENT_LIST_ITEM_CLASS = '.' + BEM.buildElementClassname(BLOCK, 'list-item');
	var ELEMENT_CATEGORY_TITLE_CLASS = '.' + BEM.buildElementClassname(BLOCK, 'category-title');
	var ELEMENT_ENTRY_LIST_CLASS = '.' + BEM.buildElementClassname(BLOCK, 'entry-list');

	return Component.create({
		block: null,
		product: null,

		$init: function() {
			this.block = BEM.block(BLOCK);

			this.bindEvents();
		},

		bindEvents: function() {
			this.block.on(
				"click", ELEMENT_CATEGORY_TITLE_CLASS, function(e) {
					var title = $(this),
						listItem = title.closest(ELEMENT_LIST_ITEM_CLASS),
						entryList = listItem.find(ELEMENT_ENTRY_LIST_CLASS);

					entryList.slideToggle();
				}
			);
		},

		registerFaqView: function (faqUrl) {
			PubSubHub.emit("csp.support.detail.faq.view", {
				faqUrl: faqUrl,
				product: this.product.name
			});
		}
	});
});
define('gigaset_csp/components/search/autocomplete',[
	"jquery",
	"cartware/components/_base",
	"cartware/jquery/bem"
], function(
	$,
	Component,
	BEM
) {
	return Component.create({

		$init: function() {
			var $body = $(document.body);
			var self = this;

			this.blocks = BEM.block("csp-search-field");

			this.blocks.each(function() {
				var block = BEM.block(this);
				var inputs = block.getElements("input");
				var autocomplete = block.getElements("autocomplete");
				var autocompleteUlLiItems = null;
				var quicklinksTemplate = block.getElements('quicklinks-template').html();
				var quicklinks = null;

				function getQuicklinks() {
					if (null === quicklinks) {
						quicklinks = $(quicklinksTemplate).hide().insertAfter(autocomplete);
					}

					return quicklinks;
				}

				inputs.each(function() {
					var inputNode = this,
						input = $(inputNode),
						form = this.form,
						originalName = this.name,
						suffix = '',
						currentIndex = -1,
						currentElem = null;

					if (gigaset != 'undifined' && gigaset.store_code) {
						suffix = '&store=' + gigaset.store_code;
					}

					var url = input.data("autocomplete-url") + suffix;

					this.name = "q";
					var searchForm = new Varien.searchForm(form, this, input.attr("placeholder"));
					searchForm.initAutocomplete(url, autocomplete.get(0));
					this.name = originalName;

					input.on("focus keyup blur", function(e) {
						var value = input.val();
						var quicklinks = getQuicklinks();
						var hide = !!value.length;

						if (e.type === "blur") {
							hide = true;
							currentIndex = -1;
						}

						if ($.contains(quicklinks.get(0), e.relatedTarget)) {
							hide = false;
						}

						quicklinks[hide ? "hide" : "show"]();

						if (!hide) {
							$body.on("click", function onClick(e) {
								if (e.toElement && !$.contains(block.get(0), e.toElement)) {
									quicklinks.hide();
									currentIndex = -1;
									$body.off("click", onClick);
								}
							});
						}
					});

					var onKeyPress = function (event) {
						if (this.active) {
							return;
						}

						return searchForm.autocompleter.onKeyPress.apply(this, arguments);
					};

					Event.stopObserving(inputNode, 'keydown');
					Event.observe(inputNode, 'keydown', onKeyPress.bindAsEventListener(searchForm.autocompleter));

					input.on('keydown', function(e) {
						autocompleteUlLiItems = autocomplete.find('ul.b_search-autocomplete__list>li:not(.b_search-autocomplete__list-item--section)');
						// if(this.active)
						switch(event.keyCode) {
							case Event.KEY_RETURN:
								selectEntry(currentIndex);
								Event.stop(event);
								currentIndex = -1;
							case Event.KEY_ESC:
								autocomplete.hide();
								quicklinks.hide();
								// this.active = false;
								Event.stop(event);
								return;
							case Event.KEY_LEFT:
							case Event.KEY_RIGHT:
								return;
							case Event.KEY_UP:
								markPrevious();
								Event.stop(event);
								return;
							case Event.KEY_DOWN:
								markNext();
								Event.stop(event);
								return;
						}

						function markPrevious() {
							currentIndex --;
							if (currentIndex < -1) {
								currentIndex = autocompleteUlLiItems.length - 1;
							}
							updateStyling();
						}

						function markNext() {
							currentIndex ++;
							if (currentIndex > autocompleteUlLiItems.length - 1) {
								currentIndex = -1;
							}
							updateStyling();
						}

						function updateStyling() {
							var elem = $(autocompleteUlLiItems.get(currentIndex));
							autocompleteUlLiItems.removeClass('b_search-autocomplete__list-item--active');
							if (currentIndex === -1) {
								input.focus();
								self.scrollIntoViewIfNeeded(input);
							} else {
								elem.addClass('b_search-autocomplete__list-item--active');
								self.scrollIntoViewIfNeeded(elem);
							}
						}

						function selectEntry(index) {
							if (index > -1) {
								var elem = $(autocompleteUlLiItems.get(currentIndex)),
									link = elem.find('.b_search-autocomplete-item__link');
								if (link.is('a')) {
									location.href = link.prop('href');
								} else {
									link.trigger('click');
								}
							} else {
								form.submit();
							}
						}
					});
				});
			});
		},

		scrollIntoViewIfNeeded: function($target) {
			if ($target.offset()) {
				if ($target.offset().top < jQuery(window).scrollTop()){
					//scroll up
					$('html,body').animate({scrollTop: $target.offset().top});
				}
				else if ($target.offset().top + $target.outerHeight() >
					$(window).scrollTop() + (
						window.innerHeight || document.documentElement.clientHeight
					)) {
					//scroll down
					$('html,body').animate({scrollTop: $target.offset().top -
							(window.innerHeight || document.documentElement.clientHeight)
							+ $target.outerHeight()}
					);
				}
			}
		},


	});
});
define('gigaset_csp/components/product/support',[
	"jquery",
	"lodash",
	"cartware/components/_base"
], function(
	$,
	_,
	Component
) {
	return Component.create({
		block:					null,
		current_url:			null,
		onChangeSimpleCallback:	null,

		constructor: function() {
			this.onChangeSimpleCallback = _.bind(function (e, simple) {
				this.changeSimple(simple);
			}, this);
		},

		$init: function () {
			this.initializeComponent();
		},

		initializeComponent: function () {
			var self = this;
			var currentSimple = _.get(window, 'spConfig.__currentSimple');
			var parentId = ('undefined' !== typeof CURRENT_PRODUCT) ? CURRENT_PRODUCT : null;

			this.block = $('#tab-support');
			this.block.hide();


			$(document).on('product/changeSimple', this.onChangeSimpleCallback);

			if (currentSimple) {
				this.changeSimple(currentSimple);
			} else if (parentId) {
				this.changeSimple({id: parentId});
			}

			this.block.on('click', function(e) {
				if (self.current_url) {
					window.open(self.current_url);
				}

				e.preventDefault();
			});
		},

		changeSimple: function (simple) {
			this.current_url = simple ? _.get(window, ['gigaset', 'support_url_map', simple.id]) : null;

			if (!this.current_url) {
				this.block.hide();
			} else {
				this.block.show();
			}
		},

		destructor: function() {
			$(document).off('product/changeSimple', this.onChangeSimpleCallback);
		}
	});
});
define('gigaset_csp/components/product/downloads',[
	"jquery",
	"lodash",
	"cartware/components/_base"
], function(
	$,
	_,
	Component
) {
	return Component.create({
		currentBlock:			null,
		onChangeSimpleCallback:	null,

		constructor: function() {
			this.onChangeSimpleCallback = _.bind(function (e, simple) {
				if (!simple) {
					return;
				}

				this.changeSimple(simple.id);
			}, this);
		},

		$init: function () {
			this.initializeComponent();
		},

		initializeComponent: function () {
			var currentSimple = _.get(window, 'spConfig.__currentSimple');
			var parentId = ('undefined' !== typeof CURRENT_PRODUCT) ? CURRENT_PRODUCT : null;

			$(document).on('product/changeSimple', this.onChangeSimpleCallback);

			if (currentSimple) {
				this.changeSimple(currentSimple.id);
			} else if (parentId) {
				this.changeSimple(parentId);
			}
		},

		changeSimple: function (simple) {
			var downloadBlocks = $('[data-downloads-id]');

			downloadBlocks.each(function (i, e) {
				var block = $(e);

				if (block.data('downloads-id') == simple) {
					block.addClass('active');
				} else {
					block.removeClass('active');
				}
			});
		},

		destructor: function() {
			$(document).off('product/changeSimple', this.onChangeSimpleCallback);
		}
	});
});
define('gigaset_csp/components/downloads/list',[
	'jquery',
	'lodash',
	'cartware/components/_base',
	'cartware/jquery/bem',
	'gigaset/spinner',
	'shoptheme/components/ui/tabs',
	'shoptheme/components/tracking'
], function(
	$,
	_,
	Component,
	BEM,
	Spinner,
	Tabs
) {
	return Component.create({

		tabs: null,
		activeTab: null,
		block: null,
		tabsClass: null,
		form: null,
		category: null,
		product: null,
		categorySelect: null,
		productSelect: null,
		tabInput: null,
		productData: null,
		resultListContainer: null,
		resultListTemplate: null,
		xhrCache: null,
		preventReload: false,

		$init: function() {
			this.block = BEM.block('csp-downloads');
			this.tabs = new Tabs();

			this.form = this.block.getElements('form');
			this.tabsClass = '.' + BEM.buildBlockClassname('tabs');

			this.categorySelect = this.block.getElements('category');
			this.productSelect = this.block.getElements('product');
			this.tabInput = this.block.getElements('active-tab');

			var productDataId = 'productData_' + this.block.find('[data-products-id]').data('products-id');
			this.productData = productDataId in window ? window[productDataId] : {};

			this.resultListContainer = BEM.block('csp-downloads-result');

			this.resultListTemplate = _.template(this.block.find('[data-template="resultList"]').html());

			this.xhrCache = {};

			this.bindEvents();
			this.parseUrl();
			$(window).on('hashchange', $.proxy(this.parseUrl, this));
            this.initAriaLabels();
		},

		bindEvents: function() {
			this.categorySelect.on('change', $.proxy(function(e, preventLoop) {
				preventLoop || this.setCategory(this.categorySelect.val());
			}, this));

			this.productSelect.on('change', $.proxy(function(e, preventLoop) {
				preventLoop || this.setProduct(this.productSelect.val());
			}, this));

			this.resultListContainer.on('click', $.proxy(function(e) {
				if ($(e.target).data('tab-id'))
					this.setTab($(e.target).data('tab-id'));
				else if ($(e.target.parentElement).data('tab-id')) {
					this.setTab($(e.target.parentElement).data('tab-id'));
				}
			}, this));
		},

        waitForElement: function(selector) {
            return new Promise(resolve => {
                if (document.querySelector(selector)) {
                    return resolve(document.querySelector(selector));
                }

                const observer = new MutationObserver(mutations => {
                    if (document.querySelector(selector)) {
                        resolve(document.querySelector(selector));
                        observer.disconnect();
                    }
                });

                observer.observe(document.body, {
                    childList: true,
                    subtree: true
                });
            });
        },

        initAriaLabels: function() {
            const selectors = [
                { id: '#select2-csp_category_select-container', label: 'csp_category_select_label' },
                { id: '#select2-csp_product_select-container', label: 'csp_product_select_label' }
            ];

            selectors.forEach(({ id, label }) => {
                this.waitForElement(id).then((e) => {
                    $(e).attr('aria-labelledby', label);
                });
            });
        },

		setCategory: function (value) {
			this.setUrlHash({
				category: value,
				product: null,
				tab: null
			});
		},

		setProduct: function (value) {
			this.setUrlHash({
				product: value
			});
		},

		setTab: function (value) {
			this.preventReload = true;

			this.setUrlHash({
				tab: value
			});
		},

		setCategoryInternal: function(value) {
			this.category = value in this.productData ? this.productData[value] : null;

			if (this.category) {
				this.categorySelect.val(value).trigger('change', true);
				this.fillProductOptions();
			}
		},

		setProductInternal: function (value) {
			if (!value || !this.category) {
				this.productSelect.val('').trigger('change', true);
				this.resultListContainer.html('');
			}

			this.product = _.find(this.category.products, {'uid': value});
			this.productSelect.val(this.product ? value : '').trigger('change', true);
		},

		setTabInternal: function (value) {
			this.activeTab = value;
			this.tabInput.val(value);
		},

		onChange: function() {
			this.resultListContainer.html('');

			if (this.category && this.product) {
				var cacheKey = [this.product.uid, this.category.uid].join("-");

				if (cacheKey in this.xhrCache) {
					this.resultListContainer.html(this.xhrCache[cacheKey]);
				} else {
					this.setLoading(true);

					$.ajax({
						type: this.form.attr('method') || 'POST',
						url: this.form.attr('action'),
						data: this.form.serialize()
					}).then(_.bind(function(results) {
						var html = this.resultListTemplate({
							results: results
						});

						this.setLoading(false);

						this.xhrCache[cacheKey] = html;

						this.resultListContainer.html(html);

						var tabs = this.resultListContainer.find(this.tabsClass);
						this.tabs.initAjax(tabs);
					}, this));
				}
			}

			this.block.trigger('gigaset.content.change');
		},

		fillProductOptions: function() {
			this.productSelect.empty();
			this.productSelect.prop('disabled', false);

			var products = this.category.products;

			_.forEach(products, _.bind(function(product) {
				var option = $('<option value="' + product.uid + '">' + product.name + '</option>');
				option.appendTo(this.productSelect);
			}, this));

			this.productSelect.val('').trigger('change', true);
		},

		parseUrl: function() {
			if (this.preventReload) {
				this.preventReload = false;
				return;
			}

			console.log('PARSE URL');

			var urlMatches = /#(.+)/.exec(document.location);
			var change = false;

			if (urlMatches) {
				this.product = null;
				this.tab = null;

				$.each(urlMatches[1].split('&'), $.proxy(function(i, fragment) {
					var parts = /(.+?)=([^&=]+)/.exec(fragment);

					if (!parts) {
						return;
					}

					if (parts[1] === 'category') {
						change = true;
						this.setCategoryInternal(parts[2]);
					} else if (parts[1] === 'product') {
						change = true;
						this.setProductInternal(parts[2]);
					} else if (parts[1] === 'tab') {
						this.setTabInternal(parts[2]);
					}
				}, this));

				if (change) {
					this.onChange();
				}
			}
		},

		setUrlHash: function(values) {
			var hash = window.location.hash,
				originalHash = hash;

			_.forEach(values, function(value, key) {
				var regex = new RegExp(key + '=[^&=]+');

				if (_.isArray(value)) {
					value = value.join(',');
				}

				if (regex.match(hash)) {
					if (null === value) {
						hash = hash.replace(regex, '');
					} else {
						hash = hash.replace(regex, key + '=' + value);
					}
				} else {
					if (value !== null) {
						hash += hash.substr(0, 1) === '#' ? '&' : '#';
						hash += key + '=' + value;
					}
				}
			});

			hash = hash.replace(/&$/, ''); // strip trailing "&"
			hash = hash.replace(/&&+/, '&');

			if (hash !== originalHash) {
				window.location.hash = hash;
			}
		},

		setLoading: function(status) {
			Spinner[status ? 'add' : 'remove'](this.block);
		}
	});
});

define('gigaset_contentbridge/classes/utils',[
	"jquery"
], function(
	$
) {
	var $$ = {};

	/**
	 * A utility log function.
	 *
	 */
	$$.log = function(m) {
		if (window && window.console && window.console.log) {
			window.console.log(arguments.length == 1 ? m : arguments);
		}
	};


	/**
	 * The function is indented for submitting a form query on the change event.
	 * It replaces the action attribute of the form with the correct hashed one
	 * stored in a link (<a>-tag) inside a hidden div tag.
	 * It requires the jquery library.
	 * @param elem
	 * @param linkIdPrefix
	 * @param val an optional parameter which can be used to override the value
	 * of the input/select element.
	 */
	$$.submit_query=function(elem,linkIdPrefix,val) {
		var form=$(elem).closest('form');
		val=val || $(elem).val();
		if (val!='') {
			var link=$('#'+linkIdPrefix+val);
			window.location.href =link.attr('href');
		}
	};


	/**
	 * The function is for switching select input elements in the registration
	 * customer care and showing the corresponding registration image.
	 * @param elem the select element that was switched
	 */
	$$.switch_product_types=function(elem) {
		var $elem=$(elem);
		var id=$elem.val();
		if (id) {
			var type=(gigaset._product_type || {})[id];
			var otype=(gigaset._opposite_of || {})[type];
			var $form=$elem.closest('form');
			var select=$form.attr('name');
			var oselect=(gigaset._opposite_of || {})[select];
			var $imgdiv=$('#'+select+'-image-div');
			$("div.giga_product_select").hide();
			var $div=$("#"+select+"-"+type);
			$("select",$div).val(id);
			$div.show();
			$("#"+oselect+"-"+otype).show();
			var img=$('#'+select+'-image');
			img.attr('src',$('#product_'+id).attr('data-imgurl'));
			$('h3',$imgdiv).text($elem.attr('data-no')+$("option:selected",$elem).text());
			$('img',$form).attr('src',$('#product_'+id).attr('data-thumbnail'));
			$imgdiv.show();
		}
	};



	/**
	 * The function creates a product search box with autocompletion.
	 * It requires the jquery-ui library.
	 * @param elem the input element we use
	 * @param products an object with a mapping for all products.
	 * It has to be of the form <code>{ 'product name' : 'product_id',....}</code>
	 * @param linkIdPrefix used for the submit_query call
	 */
	$$.product_search=function(elem,products,linkIdPrefix,appendTo) {
		elem=$(elem);
		var vals=$.map(products,function(val,key){return key;});
		elem.autocomplete({
			"appendTo":appendTo,
			"source":vals,
			"minLength":0,
			"select":function(ev,ui){
				var name=ui.item.value;
				$$.submit_query(elem,linkIdPrefix,products[name]);
			},
			"open":function(){
				$(this).autocomplete('widget').css('z-index',999);
				return false;
			}

		});
		elem.on('dblclick',function(ev){
			elem.autocomplete('search');
		});
	};


	/**
	 * This function redirect the browser to the correct cached url based on the 'product'-parameter
	 */
	$$.redirect_to_product=function(linkIdPrefix) {
		var productId=decodeURI((RegExp('[\?&]product=([^&]+)(&|$)').exec(location.search)||[,null])[1]);
		if (productId) {
			var url=$("#"+linkIdPrefix+productId).attr('href');
			if (url) {
				window.location=url;
			}
		}

	};


	/**
	 * This toggles the visibility of a dependent element by a mouse click.
	 */
	$$.toggle_element=function(trigger,elem) {
		$(trigger).click(function(ev) {
			$(elem,$(ev.currentTarget).parent()).toggle();
		});
	};

	/**
	 * The function is showing the correct compatibility table based on
	 * the second selected product
	 * @param elem the select element that was switched
	 */
	$$.change_compat_product2=function(elem,linkIdPrefix,allElems,imgIdPrefix) {
		var $elem=$(elem);
		var $form=$elem.closest('form');
		var id=$elem.val();
		var $cur;
		$(allElems).hide();
		$('img',$form).attr('src',$('#'+imgIdPrefix+id).attr('data-thumbnail'));
		$cur=$('#'+linkIdPrefix+id);
		$cur.show();
	};

	return $$;
});
define('gigaset_contentbridge/components/faq',[
	"jquery",
	"lodash",
	"cartware/components/_base",
	"gigaset_contentbridge/classes/utils",
	"text!shoptheme/templates/magnific-popup/default.html",
	"magnific-popup"
], function(
	$,
	_,
	Component,
	$$,
	PopupHtml
) {
	var popupTemplate = null;

	return Component.create({
		$init: function() {
			$$.toggle_element(".giga_toggle",".giga_to_toggle");
			//$('<div id="giga_dialog_wrapper" class="giga_dialog"/>').appendTo($("body"));
			this.show_faq(".giga_show_content","#giga_faq_dialog",".giga_rating_uri",
				"#rating_stars","#giga_dialog_wrapper");

			if ($("#giga_product_search").length>0) {
				//$$.product_search("#giga_product_search",$$._products,'faqs_',"#giga_dialog_wrapper");
				this.product_categories_menu(".giga_product_category");
				this.product_categories_menu(".giga_product_subcategory");
				//$$.redirect_to_product("faqs_");
			}
		},

		show_faq: function(elems, dialog, rating_uri, rating_stars_id, appendTo) {
			var self = this,
				showFAQLinks = $(elems);

			//$dialog.dialog({'autoOpen':false,'modal':true,
			//	'appendTo':appendTo,
			//	'dialogClass':'giga_dialog',
			//	'width':$dialog.attr('data-width'),
			//	'show':'slow','hide':'fast'});
			/* $elems.click(function(ev){
				var target = $(ev.currentTarget);
				var click_uri = $(".giga_faq_click",target.parent()).text();
				$.post(click_uri,{},function(){});
				$dialog.dialog("option","title",target.text());


				var content=$(".giga_faq_content",target.parent()).clone();
				$dialog.empty().append(content);

				var phpPath=$(rating_uri,target.parent()).text();
				$(".giga_faq_rating", $dialog).jRating({
					'bigStarsPath':'/typo3conf/ext/giga_util/Resources/Public/Icons/stars.png',
					'smallStarsPath':'/typo3conf/ext/giga_util/Resources/Public/Icons/small.png',
					'phpPath':phpPath,
					'length':rating_stars,
					'rateMax':rating_stars,
					'canRateAgain':true,
					'step':true
				});

				$dialog.dialog('open');
				return false;
			}); */

			showFAQLinks.on('click', function(e) {
				e.preventDefault();
				var link = $(this),
					faqContent = link.next();

				$.magnificPopup.open({
					items: {
						src: self._renderPopup({
							body: faqContent.html()
						}),
						type: 'inline'
					},
					callbacks: {
						open: function() {
							$('.giga_left', this.contentContainer).hide();

							$('a.giga_print', this.contentContainer).on('click', function(e) {
								e.preventDefault();
								window.print();
							});
						}
					}
				});
			});

		},
		product_categories_menu: function(elems) {
			var $elems=$(elems);

			$elems.each(function(idx,elem){
				var $elem=$(elem);
				$elem.hover(function(ev) {
					var parent=$elem.parent();
					var sub=$(ev.currentTarget).children(".giga_menu").first();
					sub.show();
					sub.width(parent.width());
					sub.position({of:parent,my:"left top",at:"left bottom"});
				},function(ev) {
					var sub=$(ev.currentTarget).children(".giga_menu").first();
					sub.hide();
				});
			});
		},
		_renderPopup: function(data) {
			if (null === popupTemplate) {
				popupTemplate = _.template(PopupHtml);
			}

			return popupTemplate(data);
		}
	});
});
define('gigaset_contentbridge/components/customer-service',[
	"jquery",
	"cartware/components/_base",
	"gigaset_contentbridge/classes/utils"
], function(
	$,
	Component,
	$$
) {
	var utils = $$;

	return Component.create({
		$init: function() {
			this._initOnChange();
		},

		_initOnChange: function() {
			var selects = $('.b_contentbridge').find('[onchange]');

			selects.each(function() {
				var select = $(this),
					instruction = select.attr("onchange").replace(/^gigaset\./, "utils.");

				select.removeAttr("onchange");

				select.on("change", function(e) {
					e.preventDefault();
					e.stopPropagation();

					console.log(instruction);
					eval(instruction);
				});
			});
		}
	});
});
define('gigaset_contentbridge/components/contact-form',[
	"jquery",
	"cartware/components/_base"
], function(
	$,
	Component
) {
	return Component.create({
		$init: function() {
			// Handle contact form submissions and category and sub-category relations
			$('.contact-form').each(function () {

				var $form = $(this),
					$categories = $form.find('.contact-categories'),
					$subCategories = $form.find('.contact-subcategories'),
					hasBothSelects = $categories.length > 0 && $subCategories.length > 0,
					hiddenClass = 'hidden',
					activeClass = 'dk_option_current';

				// Validate submission
				$form.submit(function () {

					var $allFields = $form.find('input, textarea, select'),
						$allCustomSelects = $form.find('.dk_container'),
						$requiredField = $form.find('.required-entry'),
						errorCount = 0;

					// Reset all failed fields
					$allFields.removeClass('validation-failed');
					// Reset all failed custom selects
					$allCustomSelects.removeClass('validation-failed');

					$requiredField.each(function () {

						var $thisField = $(this),
							isSelectElement = $thisField.is("select"),
							isEmailElement = $thisField.attr("type") == "email",
							expr = /^([\w\.\-]+)@([\w\-]+)((\.(\w){2,3})+)$/i;

						if (isSelectElement) {

							if ($thisField.val() == "VOID") {

								var identifier = $thisField.attr('data-identifier'),
									$customSelect = $form.find('#dk_container_' + identifier);

								if ($customSelect.length > 0) {

									$customSelect.addClass('validation-failed');
								}

								errorCount++;
							}
						} else if (isEmailElement) {

							if ($thisField.val() == "" || !expr.test($thisField.val())) {

								$thisField.addClass('validation-failed');
								errorCount++;
							}
						} else {

							if ($thisField.val() == "") {

								$thisField.addClass('validation-failed');
								errorCount++;
							}
						}

					});

					return errorCount == 0;
				});


				if (hasBothSelects) {

					// When $categories is changed, show corresponding optgroup within custom dropdown representing $subCategories
					$categories.on('change', function () {

						// Reset subcategories to first option
						$subCategories.find('option:first').attr('selected','selected');

						var $self = $(this),
							activeCategory = $self.val(),
							$activeSubCategory = $subCategories.find('option:selected'),
							activeSubCategoryText = $activeSubCategory.text(),
							$customMenu = $subCategories.siblings('.dk_container'),
							$customMenuActiveCategory = $customMenu.find('[data-dk-dropdown-value="' + activeSubCategoryText + '"]').parent('li');

						if ($customMenu.length) {

							$customMenu
								// Show/hide relevant subcategories
								.find('li[class^="cat-"]')
								.addClass(hiddenClass)
								.end()
								.find('.cat-' + activeCategory)
								.removeClass(hiddenClass)
								.end()
								// Update label
								.find('.dk_label')
								.text(activeSubCategoryText)
								.end()
								// Hide previously selected subcategory
								.find('.' + activeClass)
								.removeClass(activeClass)
								.end();

							// Set first subcategory as selected (style-wise)
							$customMenuActiveCategory.addClass(activeClass);
						}
					});
				}
			});
		}
	});
});
define('gigaset_contentbridge/components/news',[
	"jquery",
	"cartware/components/_base"
], function(
	$,
	Component
) {
	return Component.create({
		$init: function() {
			$(".news .article-back").on("click", function(e) {
				e.preventDefault();
				window.history.back();
			});
		}
	});
});
define('gigaset_contentbridge/components/manuals',[
	"jquery",
	"lodash",
	"cartware/components/_base",
	"gigaset_contentbridge/classes/utils",
	"text!shoptheme/templates/magnific-popup/default.html",
	"magnific-popup"
], function(
	$,
	_,
	Component,
	$$,
	PopupHtml
) {
	var popupTemplate = null;

	return Component.create({
		$init: function() {
			var self = this;

			$('.giga_disclaim.giga_download').on('click', function() {
				var disclaimer = $('#giga_download_disclaimer'),
					url = $(this).data('url');

				$.magnificPopup.open({
					items: {
						src: self._renderPopup({
							body: disclaimer.html()
						}),
						type: 'inline'
					},
					callbacks: {
						open: function () {
							$('button.giga_download', this.contentContainer).on('click', function(e) {
								e.preventDefault();
								window.open(url);
							});
							$('input.giga_agree', this.contentContainer).on('change', function() {
								if ($(this).is(':checked') === true) {
									$(this).parent().next('.download').show();
								} else {
									$(this).parent().next('.download').hide();
								}
							});
						}
					}
				});
			});
		},
		_renderPopup: function(data) {
			if (null === popupTemplate) {
				popupTemplate = _.template(PopupHtml);
			}

			return popupTemplate(data);
		}
	});
});
define('gigaset_contentbridge/components/slider',[
    "jquery",
    "lodash",
    "cartware/components/_base",
    "text!shoptheme/templates/magnific-popup/default.html",
    "magnific-popup"
], function(
    $,
    _,
    Component,
    PopupHtml
) {
    var _popupTemplate = _.template(PopupHtml);

    return Component.create({
        $init: function() {
            var self = this;

            $('.magnific-trigger').on('click', function(e) {
                e.preventDefault()

                var elem = $(this);
                var modalContent = $(elem.data('target')).html();

                if(modalContent) {
                    $.magnificPopup.open({
                        items: {
                            src: _popupTemplate({
                                body: modalContent
                            }),
                            type: 'inline'
                        }
                    });
                }
            });
        }
    });
});
define('gigaset_contentbridge/components/gallery',[
	"jquery",
	"cartware/components/_base"
], function(
	$,
	Component
) {
	return Component.create({
		$init: function () {
			// teaser-gallery
			$('.ext-teaser-gallery').each(function(){
				var $teaser = $(this),
					$galleryThumbs = $teaser.find('.gallery-thumbs a'),
					$galleryImg = $('<div/>', {'class':'gallery-image'}).appendTo($teaser);

				$galleryThumbs.click(function(ev) {
					var $el = $(this);
					$galleryThumbs.removeClass('current');
					$el.addClass('current');
					$galleryImg.fadeOut(function(){
						$galleryImg.empty();
						$('<img/>', {'src': $el.attr('href')}).appendTo($galleryImg);
						$galleryImg.fadeIn();
					});
					ev.preventDefault();
				});

				$galleryThumbs.first().trigger('click');
			});
		}
	});
});
(function webpackUniversalModuleDefinition(root, factory) {
	if(typeof exports === 'object' && typeof module === 'object')
		module.exports = factory();
	else if(typeof define === 'function' && define.amd)
		define('cleave',[], factory);
	else if(typeof exports === 'object')
		exports["Cleave"] = factory();
	else
		root["Cleave"] = factory();
})(this, function() {
return /******/ (function(modules) { // webpackBootstrap
/******/ 	// The module cache
/******/ 	var installedModules = {};

/******/ 	// The require function
/******/ 	function __webpack_require__(moduleId) {

/******/ 		// Check if module is in cache
/******/ 		if(installedModules[moduleId])
/******/ 			return installedModules[moduleId].exports;

/******/ 		// Create a new module (and put it into the cache)
/******/ 		var module = installedModules[moduleId] = {
/******/ 			exports: {},
/******/ 			id: moduleId,
/******/ 			loaded: false
/******/ 		};

/******/ 		// Execute the module function
/******/ 		modules[moduleId].call(module.exports, module, module.exports, __webpack_require__);

/******/ 		// Flag the module as loaded
/******/ 		module.loaded = true;

/******/ 		// Return the exports of the module
/******/ 		return module.exports;
/******/ 	}


/******/ 	// expose the modules object (__webpack_modules__)
/******/ 	__webpack_require__.m = modules;

/******/ 	// expose the module cache
/******/ 	__webpack_require__.c = installedModules;

/******/ 	// __webpack_public_path__
/******/ 	__webpack_require__.p = "";

/******/ 	// Load entry module and return exports
/******/ 	return __webpack_require__(0);
/******/ })
/************************************************************************/
/******/ ([
/* 0 */
/***/ (function(module, exports, __webpack_require__) {

	/* WEBPACK VAR INJECTION */(function(global) {'use strict';

	/**
	 * Construct a new Cleave instance by passing the configuration object
	 *
	 * @param {String | HTMLElement} element
	 * @param {Object} opts
	 */
	var Cleave = function (element, opts) {
	    var owner = this;
	    var hasMultipleElements = false;

	    if (typeof element === 'string') {
	        owner.element = document.querySelector(element);
	        hasMultipleElements = document.querySelectorAll(element).length > 1;
	    } else {
	      if (typeof element.length !== 'undefined' && element.length > 0) {
	        owner.element = element[0];
	        hasMultipleElements = element.length > 1;
	      } else {
	        owner.element = element;
	      }
	    }

	    if (!owner.element) {
	        throw new Error('[cleave.js] Please check the element');
	    }

	    if (hasMultipleElements) {
	      try {
	        // eslint-disable-next-line
	        console.warn('[cleave.js] Multiple input fields matched, cleave.js will only take the first one.');
	      } catch (e) {
	        // Old IE
	      }
	    }

	    opts.initValue = owner.element.value;

	    owner.properties = Cleave.DefaultProperties.assign({}, opts);

	    owner.init();
	};

	Cleave.prototype = {
	    init: function () {
	        var owner = this, pps = owner.properties;

	        // no need to use this lib
	        if (!pps.numeral && !pps.phone && !pps.creditCard && !pps.time && !pps.date && (pps.blocksLength === 0 && !pps.prefix)) {
	            owner.onInput(pps.initValue);

	            return;
	        }

	        pps.maxLength = Cleave.Util.getMaxLength(pps.blocks);

	        owner.isAndroid = Cleave.Util.isAndroid();
	        owner.lastInputValue = '';

	        owner.onChangeListener = owner.onChange.bind(owner);
	        owner.onKeyDownListener = owner.onKeyDown.bind(owner);
	        owner.onFocusListener = owner.onFocus.bind(owner);
	        owner.onCutListener = owner.onCut.bind(owner);
	        owner.onCopyListener = owner.onCopy.bind(owner);

	        owner.element.addEventListener('input', owner.onChangeListener);
	        owner.element.addEventListener('keydown', owner.onKeyDownListener);
	        owner.element.addEventListener('focus', owner.onFocusListener);
	        owner.element.addEventListener('cut', owner.onCutListener);
	        owner.element.addEventListener('copy', owner.onCopyListener);


	        owner.initPhoneFormatter();
	        owner.initDateFormatter();
	        owner.initTimeFormatter();
	        owner.initNumeralFormatter();

	        // avoid touch input field if value is null
	        // otherwise Firefox will add red box-shadow for <input required />
	        if (pps.initValue || (pps.prefix && !pps.noImmediatePrefix)) {
	            owner.onInput(pps.initValue);
	        }
	    },

	    initNumeralFormatter: function () {
	        var owner = this, pps = owner.properties;

	        if (!pps.numeral) {
	            return;
	        }

	        pps.numeralFormatter = new Cleave.NumeralFormatter(
	            pps.numeralDecimalMark,
	            pps.numeralIntegerScale,
	            pps.numeralDecimalScale,
	            pps.numeralThousandsGroupStyle,
	            pps.numeralPositiveOnly,
	            pps.stripLeadingZeroes,
	            pps.prefix,
	            pps.signBeforePrefix,
	            pps.delimiter
	        );
	    },

	    initTimeFormatter: function() {
	        var owner = this, pps = owner.properties;

	        if (!pps.time) {
	            return;
	        }

	        pps.timeFormatter = new Cleave.TimeFormatter(pps.timePattern, pps.timeFormat);
	        pps.blocks = pps.timeFormatter.getBlocks();
	        pps.blocksLength = pps.blocks.length;
	        pps.maxLength = Cleave.Util.getMaxLength(pps.blocks);
	    },

	    initDateFormatter: function () {
	        var owner = this, pps = owner.properties;

	        if (!pps.date) {
	            return;
	        }

	        pps.dateFormatter = new Cleave.DateFormatter(pps.datePattern, pps.dateMin, pps.dateMax);
	        pps.blocks = pps.dateFormatter.getBlocks();
	        pps.blocksLength = pps.blocks.length;
	        pps.maxLength = Cleave.Util.getMaxLength(pps.blocks);
	    },

	    initPhoneFormatter: function () {
	        var owner = this, pps = owner.properties;

	        if (!pps.phone) {
	            return;
	        }

	        // Cleave.AsYouTypeFormatter should be provided by
	        // external google closure lib
	        try {
	            pps.phoneFormatter = new Cleave.PhoneFormatter(
	                new pps.root.Cleave.AsYouTypeFormatter(pps.phoneRegionCode),
	                pps.delimiter
	            );
	        } catch (ex) {
	            throw new Error('[cleave.js] Please include phone-type-formatter.{country}.js lib');
	        }
	    },

	    onKeyDown: function (event) {
	        var owner = this, pps = owner.properties,
	            charCode = event.which || event.keyCode,
	            Util = Cleave.Util,
	            currentValue = owner.element.value;

	        // if we got any charCode === 8, this means, that this device correctly
	        // sends backspace keys in event, so we do not need to apply any hacks
	        owner.hasBackspaceSupport = owner.hasBackspaceSupport || charCode === 8;
	        if (!owner.hasBackspaceSupport
	          && Util.isAndroidBackspaceKeydown(owner.lastInputValue, currentValue)
	        ) {
	            charCode = 8;
	        }

	        owner.lastInputValue = currentValue;

	        // hit backspace when last character is delimiter
	        var postDelimiter = Util.getPostDelimiter(currentValue, pps.delimiter, pps.delimiters);
	        if (charCode === 8 && postDelimiter) {
	            pps.postDelimiterBackspace = postDelimiter;
	        } else {
	            pps.postDelimiterBackspace = false;
	        }
	    },

	    onChange: function () {
	        this.onInput(this.element.value);
	    },

	    onFocus: function () {
	        var owner = this,
	            pps = owner.properties;

	        Cleave.Util.fixPrefixCursor(owner.element, pps.prefix, pps.delimiter, pps.delimiters);
	    },

	    onCut: function (e) {
	        if (!Cleave.Util.checkFullSelection(this.element.value)) return;
	        this.copyClipboardData(e);
	        this.onInput('');
	    },

	    onCopy: function (e) {
	        if (!Cleave.Util.checkFullSelection(this.element.value)) return;
	        this.copyClipboardData(e);
	    },

	    copyClipboardData: function (e) {
	        var owner = this,
	            pps = owner.properties,
	            Util = Cleave.Util,
	            inputValue = owner.element.value,
	            textToCopy = '';

	        if (!pps.copyDelimiter) {
	            textToCopy = Util.stripDelimiters(inputValue, pps.delimiter, pps.delimiters);
	        } else {
	            textToCopy = inputValue;
	        }

	        try {
	            if (e.clipboardData) {
	                e.clipboardData.setData('Text', textToCopy);
	            } else {
	                window.clipboardData.setData('Text', textToCopy);
	            }

	            e.preventDefault();
	        } catch (ex) {
	            //  empty
	        }
	    },

	    onInput: function (value) {
	        var owner = this, pps = owner.properties,
	            Util = Cleave.Util;

	        // case 1: delete one more character "4"
	        // 1234*| -> hit backspace -> 123|
	        // case 2: last character is not delimiter which is:
	        // 12|34* -> hit backspace -> 1|34*
	        // note: no need to apply this for numeral mode
	        var postDelimiterAfter = Util.getPostDelimiter(value, pps.delimiter, pps.delimiters);
	        if (!pps.numeral && pps.postDelimiterBackspace && !postDelimiterAfter) {
	            value = Util.headStr(value, value.length - pps.postDelimiterBackspace.length);
	        }

	        // phone formatter
	        if (pps.phone) {
	            if (pps.prefix && (!pps.noImmediatePrefix || value.length)) {
	                pps.result = pps.prefix + pps.phoneFormatter.format(value).slice(pps.prefix.length);
	            } else {
	                pps.result = pps.phoneFormatter.format(value);
	            }
	            owner.updateValueState();

	            return;
	        }

	        // numeral formatter
	        if (pps.numeral) {
	            // Do not show prefix when noImmediatePrefix is specified
	            // This mostly because we need to show user the native input placeholder
	            if (pps.prefix && pps.noImmediatePrefix && value.length === 0) {
	                pps.result = '';
	            } else {
	                pps.result = pps.numeralFormatter.format(value);
	            }
	            owner.updateValueState();

	            return;
	        }

	        // date
	        if (pps.date) {
	            value = pps.dateFormatter.getValidatedDate(value);
	        }

	        // time
	        if (pps.time) {
	            value = pps.timeFormatter.getValidatedTime(value);
	        }

	        // strip delimiters
	        value = Util.stripDelimiters(value, pps.delimiter, pps.delimiters);

	        // strip prefix
	        // var strippedPreviousResult = Util.stripDelimiters(pps.result, pps.delimiter, pps.delimiters);
	        value = Util.getPrefixStrippedValue(value, pps.prefix, pps.prefixLength, pps.result, pps.delimiter, pps.delimiters);

	        // strip non-numeric characters
	        value = pps.numericOnly ? Util.strip(value, /[^\d]/g) : value;

	        // convert case
	        value = pps.uppercase ? value.toUpperCase() : value;
	        value = pps.lowercase ? value.toLowerCase() : value;

	        // prefix
	        if (pps.prefix && (!pps.noImmediatePrefix || value.length)) {
	            value = pps.prefix + value;

	            // no blocks specified, no need to do formatting
	            if (pps.blocksLength === 0) {
	                pps.result = value;
	                owner.updateValueState();

	                return;
	            }
	        }

	        // update credit card props
	        if (pps.creditCard) {
	            owner.updateCreditCardPropsByValue(value);
	        }

	        // strip over length characters
	        value = Util.headStr(value, pps.maxLength);

	        // apply blocks
	        pps.result = Util.getFormattedValue(
	            value,
	            pps.blocks, pps.blocksLength,
	            pps.delimiter, pps.delimiters, pps.delimiterLazyShow
	        );

	        owner.updateValueState();
	    },

	    updateCreditCardPropsByValue: function (value) {
	        var owner = this, pps = owner.properties,
	            Util = Cleave.Util,
	            creditCardInfo;

	        // At least one of the first 4 characters has changed
	        if (Util.headStr(pps.result, 4) === Util.headStr(value, 4)) {
	            return;
	        }

	        creditCardInfo = Cleave.CreditCardDetector.getInfo(value, pps.creditCardStrictMode);

	        pps.blocks = creditCardInfo.blocks;
	        pps.blocksLength = pps.blocks.length;
	        pps.maxLength = Util.getMaxLength(pps.blocks);

	        // credit card type changed
	        if (pps.creditCardType !== creditCardInfo.type) {
	            pps.creditCardType = creditCardInfo.type;

	            pps.onCreditCardTypeChanged.call(owner, pps.creditCardType);
	        }
	    },

	    updateValueState: function () {
	        var owner = this,
	            Util = Cleave.Util,
	            pps = owner.properties;

	        if (!owner.element) {
	            return;
	        }

	        var endPos = owner.element.selectionEnd;
	        var oldValue = owner.element.value;
	        var newValue = pps.result;

	        endPos = Util.getNextCursorPosition(endPos, oldValue, newValue, pps.delimiter, pps.delimiters);

	        // fix Android browser type="text" input field
	        // cursor not jumping issue
	        if (owner.isAndroid) {
	            window.setTimeout(function () {
	                owner.element.value = newValue;
	                Util.setSelection(owner.element, endPos, pps.document, false);
	                owner.callOnValueChanged();
	            }, 1);

	            return;
	        }

	        owner.element.value = newValue;
	        Util.setSelection(owner.element, endPos, pps.document, false);
	        owner.callOnValueChanged();
	    },

	    callOnValueChanged: function () {
	        var owner = this,
	            pps = owner.properties;

	        pps.onValueChanged.call(owner, {
	            target: {
	                value: pps.result,
	                rawValue: owner.getRawValue()
	            }
	        });
	    },

	    setPhoneRegionCode: function (phoneRegionCode) {
	        var owner = this, pps = owner.properties;

	        pps.phoneRegionCode = phoneRegionCode;
	        owner.initPhoneFormatter();
	        owner.onChange();
	    },

	    setRawValue: function (value) {
	        var owner = this, pps = owner.properties;

	        value = value !== undefined && value !== null ? value.toString() : '';

	        if (pps.numeral) {
	            value = value.replace('.', pps.numeralDecimalMark);
	        }

	        pps.postDelimiterBackspace = false;

	        owner.element.value = value;
	        owner.onInput(value);
	    },

	    getRawValue: function () {
	        var owner = this,
	            pps = owner.properties,
	            Util = Cleave.Util,
	            rawValue = owner.element.value;

	        if (pps.rawValueTrimPrefix) {
	            rawValue = Util.getPrefixStrippedValue(rawValue, pps.prefix, pps.prefixLength, pps.result, pps.delimiter, pps.delimiters);
	        }

	        if (pps.numeral) {
	            rawValue = pps.numeralFormatter.getRawValue(rawValue);
	        } else {
	            rawValue = Util.stripDelimiters(rawValue, pps.delimiter, pps.delimiters);
	        }

	        return rawValue;
	    },

	    getISOFormatDate: function () {
	        var owner = this,
	            pps = owner.properties;

	        return pps.date ? pps.dateFormatter.getISOFormatDate() : '';
	    },

	    getISOFormatTime: function () {
	        var owner = this,
	            pps = owner.properties;

	        return pps.time ? pps.timeFormatter.getISOFormatTime() : '';
	    },

	    getFormattedValue: function () {
	        return this.element.value;
	    },

	    destroy: function () {
	        var owner = this;

	        owner.element.removeEventListener('input', owner.onChangeListener);
	        owner.element.removeEventListener('keydown', owner.onKeyDownListener);
	        owner.element.removeEventListener('focus', owner.onFocusListener);
	        owner.element.removeEventListener('cut', owner.onCutListener);
	        owner.element.removeEventListener('copy', owner.onCopyListener);
	    },

	    toString: function () {
	        return '[Cleave Object]';
	    }
	};

	Cleave.NumeralFormatter = __webpack_require__(1);
	Cleave.DateFormatter = __webpack_require__(2);
	Cleave.TimeFormatter = __webpack_require__(3);
	Cleave.PhoneFormatter = __webpack_require__(4);
	Cleave.CreditCardDetector = __webpack_require__(5);
	Cleave.Util = __webpack_require__(6);
	Cleave.DefaultProperties = __webpack_require__(7);

	// for angular directive
	((typeof global === 'object' && global) ? global : window)['Cleave'] = Cleave;

	// CommonJS
	module.exports = Cleave;

	/* WEBPACK VAR INJECTION */}.call(exports, (function() { return this; }())))

/***/ }),
/* 1 */
/***/ (function(module, exports) {

	'use strict';

	var NumeralFormatter = function (numeralDecimalMark,
	                                 numeralIntegerScale,
	                                 numeralDecimalScale,
	                                 numeralThousandsGroupStyle,
	                                 numeralPositiveOnly,
	                                 stripLeadingZeroes,
	                                 prefix,
	                                 signBeforePrefix,
	                                 delimiter) {
	    var owner = this;

	    owner.numeralDecimalMark = numeralDecimalMark || '.';
	    owner.numeralIntegerScale = numeralIntegerScale > 0 ? numeralIntegerScale : 0;
	    owner.numeralDecimalScale = numeralDecimalScale >= 0 ? numeralDecimalScale : 2;
	    owner.numeralThousandsGroupStyle = numeralThousandsGroupStyle || NumeralFormatter.groupStyle.thousand;
	    owner.numeralPositiveOnly = !!numeralPositiveOnly;
	    owner.stripLeadingZeroes = stripLeadingZeroes !== false;
	    owner.prefix = (prefix || prefix === '') ? prefix : '';
	    owner.signBeforePrefix = !!signBeforePrefix;
	    owner.delimiter = (delimiter || delimiter === '') ? delimiter : ',';
	    owner.delimiterRE = delimiter ? new RegExp('\\' + delimiter, 'g') : '';
	};

	NumeralFormatter.groupStyle = {
	    thousand: 'thousand',
	    lakh:     'lakh',
	    wan:      'wan',
	    none:     'none'    
	};

	NumeralFormatter.prototype = {
	    getRawValue: function (value) {
	        return value.replace(this.delimiterRE, '').replace(this.numeralDecimalMark, '.');
	    },

	    format: function (value) {
	        var owner = this, parts, partSign, partSignAndPrefix, partInteger, partDecimal = '';

	        // strip alphabet letters
	        value = value.replace(/[A-Za-z]/g, '')
	            // replace the first decimal mark with reserved placeholder
	            .replace(owner.numeralDecimalMark, 'M')

	            // strip non numeric letters except minus and "M"
	            // this is to ensure prefix has been stripped
	            .replace(/[^\dM-]/g, '')

	            // replace the leading minus with reserved placeholder
	            .replace(/^\-/, 'N')

	            // strip the other minus sign (if present)
	            .replace(/\-/g, '')

	            // replace the minus sign (if present)
	            .replace('N', owner.numeralPositiveOnly ? '' : '-')

	            // replace decimal mark
	            .replace('M', owner.numeralDecimalMark);

	        // strip any leading zeros
	        if (owner.stripLeadingZeroes) {
	            value = value.replace(/^(-)?0+(?=\d)/, '$1');
	        }

	        partSign = value.slice(0, 1) === '-' ? '-' : '';
	        if (typeof owner.prefix != 'undefined') {
	            if (owner.signBeforePrefix) {
	                partSignAndPrefix = partSign + owner.prefix;
	            } else {
	                partSignAndPrefix = owner.prefix + partSign;
	            }
	        } else {
	            partSignAndPrefix = partSign;
	        }
	        
	        partInteger = value;

	        if (value.indexOf(owner.numeralDecimalMark) >= 0) {
	            parts = value.split(owner.numeralDecimalMark);
	            partInteger = parts[0];
	            partDecimal = owner.numeralDecimalMark + parts[1].slice(0, owner.numeralDecimalScale);
	        }

	        if(partSign === '-') {
	            partInteger = partInteger.slice(1);
	        }

	        if (owner.numeralIntegerScale > 0) {
	          partInteger = partInteger.slice(0, owner.numeralIntegerScale);
	        }

	        switch (owner.numeralThousandsGroupStyle) {
	        case NumeralFormatter.groupStyle.lakh:
	            partInteger = partInteger.replace(/(\d)(?=(\d\d)+\d$)/g, '$1' + owner.delimiter);

	            break;

	        case NumeralFormatter.groupStyle.wan:
	            partInteger = partInteger.replace(/(\d)(?=(\d{4})+$)/g, '$1' + owner.delimiter);

	            break;

	        case NumeralFormatter.groupStyle.thousand:
	            partInteger = partInteger.replace(/(\d)(?=(\d{3})+$)/g, '$1' + owner.delimiter);

	            break;
	        }

	        return partSignAndPrefix + partInteger.toString() + (owner.numeralDecimalScale > 0 ? partDecimal.toString() : '');
	    }
	};

	module.exports = NumeralFormatter;


/***/ }),
/* 2 */
/***/ (function(module, exports) {

	'use strict';

	var DateFormatter = function (datePattern, dateMin, dateMax) {
	    var owner = this;

	    owner.date = [];
	    owner.blocks = [];
	    owner.datePattern = datePattern;
	    owner.dateMin = dateMin
	      .split('-')
	      .reverse()
	      .map(function(x) {
	        return parseInt(x, 10);
	      });
	    if (owner.dateMin.length === 2) owner.dateMin.unshift(0);

	    owner.dateMax = dateMax
	      .split('-')
	      .reverse()
	      .map(function(x) {
	        return parseInt(x, 10);
	      });
	    if (owner.dateMax.length === 2) owner.dateMax.unshift(0);
	    
	    owner.initBlocks();
	};

	DateFormatter.prototype = {
	    initBlocks: function () {
	        var owner = this;
	        owner.datePattern.forEach(function (value) {
	            if (value === 'Y') {
	                owner.blocks.push(4);
	            } else {
	                owner.blocks.push(2);
	            }
	        });
	    },

	    getISOFormatDate: function () {
	        var owner = this,
	            date = owner.date;

	        return date[2] ? (
	            date[2] + '-' + owner.addLeadingZero(date[1]) + '-' + owner.addLeadingZero(date[0])
	        ) : '';
	    },

	    getBlocks: function () {
	        return this.blocks;
	    },

	    getValidatedDate: function (value) {
	        var owner = this, result = '';

	        value = value.replace(/[^\d]/g, '');

	        owner.blocks.forEach(function (length, index) {
	            if (value.length > 0) {
	                var sub = value.slice(0, length),
	                    sub0 = sub.slice(0, 1),
	                    rest = value.slice(length);

	                switch (owner.datePattern[index]) {
	                case 'd':
	                    if (sub === '00') {
	                        sub = '01';
	                    } else if (parseInt(sub0, 10) > 3) {
	                        sub = '0' + sub0;
	                    } else if (parseInt(sub, 10) > 31) {
	                        sub = '31';
	                    }

	                    break;

	                case 'm':
	                    if (sub === '00') {
	                        sub = '01';
	                    } else if (parseInt(sub0, 10) > 1) {
	                        sub = '0' + sub0;
	                    } else if (parseInt(sub, 10) > 12) {
	                        sub = '12';
	                    }

	                    break;
	                }

	                result += sub;

	                // update remaining string
	                value = rest;
	            }
	        });

	        return this.getFixedDateString(result);
	    },

	    getFixedDateString: function (value) {
	        var owner = this, datePattern = owner.datePattern, date = [],
	            dayIndex = 0, monthIndex = 0, yearIndex = 0,
	            dayStartIndex = 0, monthStartIndex = 0, yearStartIndex = 0,
	            day, month, year, fullYearDone = false;

	        // mm-dd || dd-mm
	        if (value.length === 4 && datePattern[0].toLowerCase() !== 'y' && datePattern[1].toLowerCase() !== 'y') {
	            dayStartIndex = datePattern[0] === 'd' ? 0 : 2;
	            monthStartIndex = 2 - dayStartIndex;
	            day = parseInt(value.slice(dayStartIndex, dayStartIndex + 2), 10);
	            month = parseInt(value.slice(monthStartIndex, monthStartIndex + 2), 10);

	            date = this.getFixedDate(day, month, 0);
	        }

	        // yyyy-mm-dd || yyyy-dd-mm || mm-dd-yyyy || dd-mm-yyyy || dd-yyyy-mm || mm-yyyy-dd
	        if (value.length === 8) {
	            datePattern.forEach(function (type, index) {
	                switch (type) {
	                case 'd':
	                    dayIndex = index;
	                    break;
	                case 'm':
	                    monthIndex = index;
	                    break;
	                default:
	                    yearIndex = index;
	                    break;
	                }
	            });

	            yearStartIndex = yearIndex * 2;
	            dayStartIndex = (dayIndex <= yearIndex) ? dayIndex * 2 : (dayIndex * 2 + 2);
	            monthStartIndex = (monthIndex <= yearIndex) ? monthIndex * 2 : (monthIndex * 2 + 2);

	            day = parseInt(value.slice(dayStartIndex, dayStartIndex + 2), 10);
	            month = parseInt(value.slice(monthStartIndex, monthStartIndex + 2), 10);
	            year = parseInt(value.slice(yearStartIndex, yearStartIndex + 4), 10);

	            fullYearDone = value.slice(yearStartIndex, yearStartIndex + 4).length === 4;

	            date = this.getFixedDate(day, month, year);
	        }

	        // mm-yy || yy-mm
	        if (value.length === 4 && (datePattern[0] === 'y' || datePattern[1] === 'y')) {
	            monthStartIndex = datePattern[0] === 'm' ? 0 : 2;
	            yearStartIndex = 2 - monthStartIndex;
	            month = parseInt(value.slice(monthStartIndex, monthStartIndex + 2), 10);
	            year = parseInt(value.slice(yearStartIndex, yearStartIndex + 2), 10);

	            fullYearDone = value.slice(yearStartIndex, yearStartIndex + 2).length === 2;

	            date = [0, month, year];
	        }

	        // mm-yyyy || yyyy-mm
	        if (value.length === 6 && (datePattern[0] === 'Y' || datePattern[1] === 'Y')) {
	            monthStartIndex = datePattern[0] === 'm' ? 0 : 4;
	            yearStartIndex = 2 - 0.5 * monthStartIndex;
	            month = parseInt(value.slice(monthStartIndex, monthStartIndex + 2), 10);
	            year = parseInt(value.slice(yearStartIndex, yearStartIndex + 4), 10);

	            fullYearDone = value.slice(yearStartIndex, yearStartIndex + 4).length === 4;

	            date = [0, month, year];
	        }

	        date = owner.getRangeFixedDate(date);
	        owner.date = date;

	        var result = date.length === 0 ? value : datePattern.reduce(function (previous, current) {
	            switch (current) {
	            case 'd':
	                return previous + (date[0] === 0 ? '' : owner.addLeadingZero(date[0]));
	            case 'm':
	                return previous + (date[1] === 0 ? '' : owner.addLeadingZero(date[1]));
	            case 'y':
	                return previous + (fullYearDone ? owner.addLeadingZeroForYear(date[2], false) : '');
	            case 'Y':
	                return previous + (fullYearDone ? owner.addLeadingZeroForYear(date[2], true) : '');
	            }
	        }, '');

	        return result;
	    },

	    getRangeFixedDate: function (date) {
	        var owner = this,
	            datePattern = owner.datePattern,
	            dateMin = owner.dateMin || [],
	            dateMax = owner.dateMax || [];

	        if (!date.length || (dateMin.length < 3 && dateMax.length < 3)) return date;

	        if (
	          datePattern.find(function(x) {
	            return x.toLowerCase() === 'y';
	          }) &&
	          date[2] === 0
	        ) return date;

	        if (dateMax.length && (dateMax[2] < date[2] || (
	          dateMax[2] === date[2] && (dateMax[1] < date[1] || (
	            dateMax[1] === date[1] && dateMax[0] < date[0]
	          ))
	        ))) return dateMax;

	        if (dateMin.length && (dateMin[2] > date[2] || (
	          dateMin[2] === date[2] && (dateMin[1] > date[1] || (
	            dateMin[1] === date[1] && dateMin[0] > date[0]
	          ))
	        ))) return dateMin;

	        return date;
	    },

	    getFixedDate: function (day, month, year) {
	        day = Math.min(day, 31);
	        month = Math.min(month, 12);
	        year = parseInt((year || 0), 10);

	        if ((month < 7 && month % 2 === 0) || (month > 8 && month % 2 === 1)) {
	            day = Math.min(day, month === 2 ? (this.isLeapYear(year) ? 29 : 28) : 30);
	        }

	        return [day, month, year];
	    },

	    isLeapYear: function (year) {
	        return ((year % 4 === 0) && (year % 100 !== 0)) || (year % 400 === 0);
	    },

	    addLeadingZero: function (number) {
	        return (number < 10 ? '0' : '') + number;
	    },

	    addLeadingZeroForYear: function (number, fullYearMode) {
	        if (fullYearMode) {
	            return (number < 10 ? '000' : (number < 100 ? '00' : (number < 1000 ? '0' : ''))) + number;
	        }

	        return (number < 10 ? '0' : '') + number;
	    }
	};

	module.exports = DateFormatter;


/***/ }),
/* 3 */
/***/ (function(module, exports) {

	'use strict';

	var TimeFormatter = function (timePattern, timeFormat) {
	    var owner = this;

	    owner.time = [];
	    owner.blocks = [];
	    owner.timePattern = timePattern;
	    owner.timeFormat = timeFormat;
	    owner.initBlocks();
	};

	TimeFormatter.prototype = {
	    initBlocks: function () {
	        var owner = this;
	        owner.timePattern.forEach(function () {
	            owner.blocks.push(2);
	        });
	    },

	    getISOFormatTime: function () {
	        var owner = this,
	            time = owner.time;

	        return time[2] ? (
	            owner.addLeadingZero(time[0]) + ':' + owner.addLeadingZero(time[1]) + ':' + owner.addLeadingZero(time[2])
	        ) : '';
	    },

	    getBlocks: function () {
	        return this.blocks;
	    },

	    getTimeFormatOptions: function () {
	        var owner = this;
	        if (String(owner.timeFormat) === '12') {
	            return {
	                maxHourFirstDigit: 1,
	                maxHours: 12,
	                maxMinutesFirstDigit: 5,
	                maxMinutes: 60
	            };
	        }

	        return {
	            maxHourFirstDigit: 2,
	            maxHours: 23,
	            maxMinutesFirstDigit: 5,
	            maxMinutes: 60
	        };
	    },

	    getValidatedTime: function (value) {
	        var owner = this, result = '';

	        value = value.replace(/[^\d]/g, '');

	        var timeFormatOptions = owner.getTimeFormatOptions();

	        owner.blocks.forEach(function (length, index) {
	            if (value.length > 0) {
	                var sub = value.slice(0, length),
	                    sub0 = sub.slice(0, 1),
	                    rest = value.slice(length);

	                switch (owner.timePattern[index]) {

	                case 'h':
	                    if (parseInt(sub0, 10) > timeFormatOptions.maxHourFirstDigit) {
	                        sub = '0' + sub0;
	                    } else if (parseInt(sub, 10) > timeFormatOptions.maxHours) {
	                        sub = timeFormatOptions.maxHours + '';
	                    }

	                    break;

	                case 'm':
	                case 's':
	                    if (parseInt(sub0, 10) > timeFormatOptions.maxMinutesFirstDigit) {
	                        sub = '0' + sub0;
	                    } else if (parseInt(sub, 10) > timeFormatOptions.maxMinutes) {
	                        sub = timeFormatOptions.maxMinutes + '';
	                    }
	                    break;
	                }

	                result += sub;

	                // update remaining string
	                value = rest;
	            }
	        });

	        return this.getFixedTimeString(result);
	    },

	    getFixedTimeString: function (value) {
	        var owner = this, timePattern = owner.timePattern, time = [],
	            secondIndex = 0, minuteIndex = 0, hourIndex = 0,
	            secondStartIndex = 0, minuteStartIndex = 0, hourStartIndex = 0,
	            second, minute, hour;

	        if (value.length === 6) {
	            timePattern.forEach(function (type, index) {
	                switch (type) {
	                case 's':
	                    secondIndex = index * 2;
	                    break;
	                case 'm':
	                    minuteIndex = index * 2;
	                    break;
	                case 'h':
	                    hourIndex = index * 2;
	                    break;
	                }
	            });

	            hourStartIndex = hourIndex;
	            minuteStartIndex = minuteIndex;
	            secondStartIndex = secondIndex;

	            second = parseInt(value.slice(secondStartIndex, secondStartIndex + 2), 10);
	            minute = parseInt(value.slice(minuteStartIndex, minuteStartIndex + 2), 10);
	            hour = parseInt(value.slice(hourStartIndex, hourStartIndex + 2), 10);

	            time = this.getFixedTime(hour, minute, second);
	        }

	        if (value.length === 4 && owner.timePattern.indexOf('s') < 0) {
	            timePattern.forEach(function (type, index) {
	                switch (type) {
	                case 'm':
	                    minuteIndex = index * 2;
	                    break;
	                case 'h':
	                    hourIndex = index * 2;
	                    break;
	                }
	            });

	            hourStartIndex = hourIndex;
	            minuteStartIndex = minuteIndex;

	            second = 0;
	            minute = parseInt(value.slice(minuteStartIndex, minuteStartIndex + 2), 10);
	            hour = parseInt(value.slice(hourStartIndex, hourStartIndex + 2), 10);

	            time = this.getFixedTime(hour, minute, second);
	        }

	        owner.time = time;

	        return time.length === 0 ? value : timePattern.reduce(function (previous, current) {
	            switch (current) {
	            case 's':
	                return previous + owner.addLeadingZero(time[2]);
	            case 'm':
	                return previous + owner.addLeadingZero(time[1]);
	            case 'h':
	                return previous + owner.addLeadingZero(time[0]);
	            }
	        }, '');
	    },

	    getFixedTime: function (hour, minute, second) {
	        second = Math.min(parseInt(second || 0, 10), 60);
	        minute = Math.min(minute, 60);
	        hour = Math.min(hour, 60);

	        return [hour, minute, second];
	    },

	    addLeadingZero: function (number) {
	        return (number < 10 ? '0' : '') + number;
	    }
	};

	module.exports = TimeFormatter;


/***/ }),
/* 4 */
/***/ (function(module, exports) {

	'use strict';

	var PhoneFormatter = function (formatter, delimiter) {
	    var owner = this;

	    owner.delimiter = (delimiter || delimiter === '') ? delimiter : ' ';
	    owner.delimiterRE = delimiter ? new RegExp('\\' + delimiter, 'g') : '';

	    owner.formatter = formatter;
	};

	PhoneFormatter.prototype = {
	    setFormatter: function (formatter) {
	        this.formatter = formatter;
	    },

	    format: function (phoneNumber) {
	        var owner = this;

	        owner.formatter.clear();

	        // only keep number and +
	        phoneNumber = phoneNumber.replace(/[^\d+]/g, '');

	        // strip non-leading +
	        phoneNumber = phoneNumber.replace(/^\+/, 'B').replace(/\+/g, '').replace('B', '+');

	        // strip delimiter
	        phoneNumber = phoneNumber.replace(owner.delimiterRE, '');

	        var result = '', current, validated = false;

	        for (var i = 0, iMax = phoneNumber.length; i < iMax; i++) {
	            current = owner.formatter.inputDigit(phoneNumber.charAt(i));

	            // has ()- or space inside
	            if (/[\s()-]/g.test(current)) {
	                result = current;

	                validated = true;
	            } else {
	                if (!validated) {
	                    result = current;
	                }
	                // else: over length input
	                // it turns to invalid number again
	            }
	        }

	        // strip ()
	        // e.g. US: 7161234567 returns (716) 123-4567
	        result = result.replace(/[()]/g, '');
	        // replace library delimiter with user customized delimiter
	        result = result.replace(/[\s-]/g, owner.delimiter);

	        return result;
	    }
	};

	module.exports = PhoneFormatter;


/***/ }),
/* 5 */
/***/ (function(module, exports) {

	'use strict';

	var CreditCardDetector = {
	    blocks: {
	        uatp:          [4, 5, 6],
	        amex:          [4, 6, 5],
	        diners:        [4, 6, 4],
	        discover:      [4, 4, 4, 4],
	        mastercard:    [4, 4, 4, 4],
	        dankort:       [4, 4, 4, 4],
	        instapayment:  [4, 4, 4, 4],
	        jcb15:         [4, 6, 5],
	        jcb:           [4, 4, 4, 4],
	        maestro:       [4, 4, 4, 4],
	        visa:          [4, 4, 4, 4],
	        mir:           [4, 4, 4, 4],
	        unionPay:      [4, 4, 4, 4],
	        general:       [4, 4, 4, 4]
	    },

	    re: {
	        // starts with 1; 15 digits, not starts with 1800 (jcb card)
	        uatp: /^(?!1800)1\d{0,14}/,

	        // starts with 34/37; 15 digits
	        amex: /^3[47]\d{0,13}/,

	        // starts with 6011/65/644-649; 16 digits
	        discover: /^(?:6011|65\d{0,2}|64[4-9]\d?)\d{0,12}/,

	        // starts with 300-305/309 or 36/38/39; 14 digits
	        diners: /^3(?:0([0-5]|9)|[689]\d?)\d{0,11}/,

	        // starts with 51-55/2221–2720; 16 digits
	        mastercard: /^(5[1-5]\d{0,2}|22[2-9]\d{0,1}|2[3-7]\d{0,2})\d{0,12}/,

	        // starts with 5019/4175/4571; 16 digits
	        dankort: /^(5019|4175|4571)\d{0,12}/,

	        // starts with 637-639; 16 digits
	        instapayment: /^63[7-9]\d{0,13}/,

	        // starts with 2131/1800; 15 digits
	        jcb15: /^(?:2131|1800)\d{0,11}/,

	        // starts with 2131/1800/35; 16 digits
	        jcb: /^(?:35\d{0,2})\d{0,12}/,

	        // starts with 50/56-58/6304/67; 16 digits
	        maestro: /^(?:5[0678]\d{0,2}|6304|67\d{0,2})\d{0,12}/,

	        // starts with 22; 16 digits
	        mir: /^220[0-4]\d{0,12}/,

	        // starts with 4; 16 digits
	        visa: /^4\d{0,15}/,

	        // starts with 62; 16 digits
	        unionPay: /^62\d{0,14}/
	    },

	    getStrictBlocks: function (block) {
	      var total = block.reduce(function (prev, current) {
	        return prev + current;
	      }, 0);

	      return block.concat(19 - total);
	    },

	    getInfo: function (value, strictMode) {
	        var blocks = CreditCardDetector.blocks,
	            re = CreditCardDetector.re;

	        // Some credit card can have up to 19 digits number.
	        // Set strictMode to true will remove the 16 max-length restrain,
	        // however, I never found any website validate card number like
	        // this, hence probably you don't want to enable this option.
	        strictMode = !!strictMode;

	        for (var key in re) {
	            if (re[key].test(value)) {
	                var matchedBlocks = blocks[key];
	                return {
	                    type: key,
	                    blocks: strictMode ? this.getStrictBlocks(matchedBlocks) : matchedBlocks
	                };
	            }
	        }

	        return {
	            type: 'unknown',
	            blocks: strictMode ? this.getStrictBlocks(blocks.general) : blocks.general
	        };
	    }
	};

	module.exports = CreditCardDetector;


/***/ }),
/* 6 */
/***/ (function(module, exports) {

	'use strict';

	var Util = {
	    noop: function () {
	    },

	    strip: function (value, re) {
	        return value.replace(re, '');
	    },

	    getPostDelimiter: function (value, delimiter, delimiters) {
	        // single delimiter
	        if (delimiters.length === 0) {
	            return value.slice(-delimiter.length) === delimiter ? delimiter : '';
	        }

	        // multiple delimiters
	        var matchedDelimiter = '';
	        delimiters.forEach(function (current) {
	            if (value.slice(-current.length) === current) {
	                matchedDelimiter = current;
	            }
	        });

	        return matchedDelimiter;
	    },

	    getDelimiterREByDelimiter: function (delimiter) {
	        return new RegExp(delimiter.replace(/([.?*+^$[\]\\(){}|-])/g, '\\$1'), 'g');
	    },

	    getNextCursorPosition: function (prevPos, oldValue, newValue, delimiter, delimiters) {
	      // If cursor was at the end of value, just place it back.
	      // Because new value could contain additional chars.
	      if (oldValue.length === prevPos) {
	          return newValue.length;
	      }

	      return prevPos + this.getPositionOffset(prevPos, oldValue, newValue, delimiter ,delimiters);
	    },

	    getPositionOffset: function (prevPos, oldValue, newValue, delimiter, delimiters) {
	        var oldRawValue, newRawValue, lengthOffset;

	        oldRawValue = this.stripDelimiters(oldValue.slice(0, prevPos), delimiter, delimiters);
	        newRawValue = this.stripDelimiters(newValue.slice(0, prevPos), delimiter, delimiters);
	        lengthOffset = oldRawValue.length - newRawValue.length;

	        return (lengthOffset !== 0) ? (lengthOffset / Math.abs(lengthOffset)) : 0;
	    },

	    stripDelimiters: function (value, delimiter, delimiters) {
	        var owner = this;

	        // single delimiter
	        if (delimiters.length === 0) {
	            var delimiterRE = delimiter ? owner.getDelimiterREByDelimiter(delimiter) : '';

	            return value.replace(delimiterRE, '');
	        }

	        // multiple delimiters
	        delimiters.forEach(function (current) {
	            current.split('').forEach(function (letter) {
	                value = value.replace(owner.getDelimiterREByDelimiter(letter), '');
	            });
	        });

	        return value;
	    },

	    headStr: function (str, length) {
	        return str.slice(0, length);
	    },

	    getMaxLength: function (blocks) {
	        return blocks.reduce(function (previous, current) {
	            return previous + current;
	        }, 0);
	    },

	    // strip prefix
	    // Before type  |   After type    |     Return value
	    // PEFIX-...    |   PEFIX-...     |     ''
	    // PREFIX-123   |   PEFIX-123     |     123
	    // PREFIX-123   |   PREFIX-23     |     23
	    // PREFIX-123   |   PREFIX-1234   |     1234
	    getPrefixStrippedValue: function (value, prefix, prefixLength, prevResult, delimiter, delimiters) {
	        // No prefix
	        if (prefixLength === 0) {
	          return value;
	        }

	        // Pre result has issue
	        // Revert to raw prefix
	        if (prevResult.slice(0, prefixLength) !== prefix) {
	          return '';
	        }

	        var prevValue = this.stripDelimiters(prevResult, delimiter, delimiters);

	        // New value has issue, someone typed in between prefix letters
	        // Revert to pre value
	        if (value.slice(0, prefixLength) !== prefix) {
	          return prevValue.slice(prefixLength);
	        }

	        // No issue, strip prefix for new value
	        return value.slice(prefixLength);
	    },

	    getFirstDiffIndex: function (prev, current) {
	        var index = 0;

	        while (prev.charAt(index) === current.charAt(index)) {
	            if (prev.charAt(index++) === '') {
	                return -1;
	            }
	        }

	        return index;
	    },

	    getFormattedValue: function (value, blocks, blocksLength, delimiter, delimiters, delimiterLazyShow) {
	        var result = '',
	            multipleDelimiters = delimiters.length > 0,
	            currentDelimiter;

	        // no options, normal input
	        if (blocksLength === 0) {
	            return value;
	        }

	        blocks.forEach(function (length, index) {
	            if (value.length > 0) {
	                var sub = value.slice(0, length),
	                    rest = value.slice(length);

	                if (multipleDelimiters) {
	                    currentDelimiter = delimiters[delimiterLazyShow ? (index - 1) : index] || currentDelimiter;
	                } else {
	                    currentDelimiter = delimiter;
	                }

	                if (delimiterLazyShow) {
	                    if (index > 0) {
	                        result += currentDelimiter;
	                    }

	                    result += sub;
	                } else {
	                    result += sub;

	                    if (sub.length === length && index < blocksLength - 1) {
	                        result += currentDelimiter;
	                    }
	                }

	                // update remaining string
	                value = rest;
	            }
	        });

	        return result;
	    },

	    // move cursor to the end
	    // the first time user focuses on an input with prefix
	    fixPrefixCursor: function (el, prefix, delimiter, delimiters) {
	        if (!el) {
	            return;
	        }

	        var val = el.value,
	            appendix = delimiter || (delimiters[0] || ' ');

	        if (!el.setSelectionRange || !prefix || (prefix.length + appendix.length) < val.length) {
	            return;
	        }

	        var len = val.length * 2;

	        // set timeout to avoid blink
	        setTimeout(function () {
	            el.setSelectionRange(len, len);
	        }, 1);
	    },

	    // Check if input field is fully selected
	    checkFullSelection: function(value) {
	      try {
	        var selection = window.getSelection() || document.getSelection() || {};
	        return selection.toString().length === value.length;
	      } catch (ex) {
	        // Ignore
	      }

	      return false;
	    },

	    setSelection: function (element, position, doc) {
	        if (element !== this.getActiveElement(doc)) {
	            return;
	        }

	        // cursor is already in the end
	        if (element && element.value.length <= position) {
	          return;
	        }

	        if (element.createTextRange) {
	            var range = element.createTextRange();

	            range.move('character', position);
	            range.select();
	        } else {
	            try {
	                element.setSelectionRange(position, position);
	            } catch (e) {
	                // eslint-disable-next-line
	                console.warn('The input element type does not support selection');
	            }
	        }
	    },

	    getActiveElement: function(parent) {
	        var activeElement = parent.activeElement;
	        if (activeElement && activeElement.shadowRoot) {
	            return this.getActiveElement(activeElement.shadowRoot);
	        }
	        return activeElement;
	    },

	    isAndroid: function () {
	        return navigator && /android/i.test(navigator.userAgent);
	    },

	    // On Android chrome, the keyup and keydown events
	    // always return key code 229 as a composition that
	    // buffers the user’s keystrokes
	    // see https://github.com/nosir/cleave.js/issues/147
	    isAndroidBackspaceKeydown: function (lastInputValue, currentInputValue) {
	        if (!this.isAndroid() || !lastInputValue || !currentInputValue) {
	            return false;
	        }

	        return currentInputValue === lastInputValue.slice(0, -1);
	    }
	};

	module.exports = Util;


/***/ }),
/* 7 */
/***/ (function(module, exports) {

	/* WEBPACK VAR INJECTION */(function(global) {'use strict';

	/**
	 * Props Assignment
	 *
	 * Separate this, so react module can share the usage
	 */
	var DefaultProperties = {
	    // Maybe change to object-assign
	    // for now just keep it as simple
	    assign: function (target, opts) {
	        target = target || {};
	        opts = opts || {};

	        // credit card
	        target.creditCard = !!opts.creditCard;
	        target.creditCardStrictMode = !!opts.creditCardStrictMode;
	        target.creditCardType = '';
	        target.onCreditCardTypeChanged = opts.onCreditCardTypeChanged || (function () {});

	        // phone
	        target.phone = !!opts.phone;
	        target.phoneRegionCode = opts.phoneRegionCode || 'AU';
	        target.phoneFormatter = {};

	        // time
	        target.time = !!opts.time;
	        target.timePattern = opts.timePattern || ['h', 'm', 's'];
	        target.timeFormat = opts.timeFormat || '24';
	        target.timeFormatter = {};

	        // date
	        target.date = !!opts.date;
	        target.datePattern = opts.datePattern || ['d', 'm', 'Y'];
	        target.dateMin = opts.dateMin || '';
	        target.dateMax = opts.dateMax || '';
	        target.dateFormatter = {};

	        // numeral
	        target.numeral = !!opts.numeral;
	        target.numeralIntegerScale = opts.numeralIntegerScale > 0 ? opts.numeralIntegerScale : 0;
	        target.numeralDecimalScale = opts.numeralDecimalScale >= 0 ? opts.numeralDecimalScale : 2;
	        target.numeralDecimalMark = opts.numeralDecimalMark || '.';
	        target.numeralThousandsGroupStyle = opts.numeralThousandsGroupStyle || 'thousand';
	        target.numeralPositiveOnly = !!opts.numeralPositiveOnly;
	        target.stripLeadingZeroes = opts.stripLeadingZeroes !== false;
	        target.signBeforePrefix = !!opts.signBeforePrefix;

	        // others
	        target.numericOnly = target.creditCard || target.date || !!opts.numericOnly;

	        target.uppercase = !!opts.uppercase;
	        target.lowercase = !!opts.lowercase;

	        target.prefix = (target.creditCard || target.date) ? '' : (opts.prefix || '');
	        target.noImmediatePrefix = !!opts.noImmediatePrefix;
	        target.prefixLength = target.prefix.length;
	        target.rawValueTrimPrefix = !!opts.rawValueTrimPrefix;
	        target.copyDelimiter = !!opts.copyDelimiter;

	        target.initValue = (opts.initValue !== undefined && opts.initValue !== null) ? opts.initValue.toString() : '';

	        target.delimiter =
	            (opts.delimiter || opts.delimiter === '') ? opts.delimiter :
	                (opts.date ? '/' :
	                    (opts.time ? ':' :
	                        (opts.numeral ? ',' :
	                            (opts.phone ? ' ' :
	                                ' '))));
	        target.delimiterLength = target.delimiter.length;
	        target.delimiterLazyShow = !!opts.delimiterLazyShow;
	        target.delimiters = opts.delimiters || [];

	        target.blocks = opts.blocks || [];
	        target.blocksLength = target.blocks.length;

	        target.root = (typeof global === 'object' && global) ? global : window;
	        target.document = opts.document || target.root.document;

	        target.maxLength = 0;

	        target.backspace = false;
	        target.result = '';

	        target.onValueChanged = opts.onValueChanged || (function () {});

	        return target;
	    }
	};

	module.exports = DefaultProperties;

	/* WEBPACK VAR INJECTION */}.call(exports, (function() { return this; }())))

/***/ })
/******/ ])
});
;
define('gigaset_shoppingcodes/components/form',[
	"jquery",
	"cartware/components/_base",
	"cleave"
], function(
	$,
	Component,
	Cleave
) {
	return Component.create({
		$init: function(){
			if ($('.input-code').length) {
				var cleave = new Cleave('.input-code', {
					delimiter: '-',
					blocks: [4, 4, 4, 4],
					uppercase: true
				});
			}
		}
	});
});
define('gigaset_stocknotification/components/product/view',[
	'jquery',
	'lodash',
	'cartware/components/_base',
	'cartware/jquery/bem',
	'gigaset/spinner',
	'text!shoptheme/templates/magnific-popup/default.html',
	'json2'
], function(
	$,
	_,
	Component,
	BEM,
	Spinner,
	PopupHtml
) {
	var popup = null;

	return Component.create({
		watchlistDialog:		null,
		emailInput:				null,
		attributeInput:			null,
		productIdInput:			null,
		parentProductId:		null,
		bundledOptions:			null,
		addToCart:				null,
		submitting:				false,

		constructor: function() {
			this.bundledOptions = [];
		},

		$init: function() {
			this.addToCart = BEM.block('product-detail-add-to-cart');
			this.watchlistDialog = BEM.block('product-stocknotification');
			this.productOptionsTechnical = BEM.block('product-options', $('.b_product-view__column-technical'));
			this.productCustomOptions = $('.b_product-options__custom-option-list-wrapper');
			this.emailInput = this.watchlistDialog.getElements('input');
			this.attributeInput = $('.watchlist-attributes');
			this.productIdInput = $('.watchlist-product-id');
			this.parentProductId = $('.watchlist-parent-product-id');
			this.cartButton = $('.btn-cart');
			this.addToBox = $('.add-to-box');
			this.isCatalogStore = this.watchlistDialog.data('is-catalog-store');
			this.productOptionsContainer = $('.product-options-bottom');

			this.watchlistDialog.insertAfter(this.addToBox);
			$(document).on('product/changeSimple', $.proxy(this._onSimpleChange, this));
			this.hub.on('product/changeSimple', $.proxy(this._onSimpleChangeCallback, this));
			var bundle = window.bundle;
			var opConfig = window.opConfig;
			var self = this;

			if (opConfig) {
				opConfig.reloadPrice = function() {
					var config = this.config;
					var skipIds = [];
					var self = this;
					this.selectedOptions = [];
					$$('body .product-custom-option').each(function(element){
						var optionId = 0;
						element.name.sub(/[0-9]+/, function(match){
							optionId = parseInt(match[0], 10);
						});
						if (config[optionId]) {
							var configOptions = config[optionId];
							var curConfig = {price: 0};
							if (element.type == 'checkbox' || element.type == 'radio') {
								if (element.checked) {
									if (typeof configOptions[element.getValue()] != 'undefined') {
										curConfig = configOptions[element.getValue()];

										if (!self.selectedOptions[optionId]) {
											self.selectedOptions[optionId] = [];
										}

										self.selectedOptions[optionId].push(element.getValue());
									}
								}
							} else if(element.hasClassName('datetime-picker') && !skipIds.include(optionId)) {
								dateSelected = true;
								$$('.product-custom-option[id^="options_' + optionId + '"]').each(function(dt){
									if (dt.getValue() == '') {
										dateSelected = false;
									}
								});
								if (dateSelected) {
									curConfig = configOptions;
									skipIds[optionId] = optionId;
								}
							} else if(element.type == 'select-one' || element.type == 'select-multiple') {
								if ('options' in element) {
									$A(element.options).each(function(selectOption){
										if ('selected' in selectOption && selectOption.selected) {
											if (typeof(configOptions[selectOption.value]) != 'undefined') {
												curConfig = configOptions[selectOption.value];

												if (!self.selectedOptions[optionId]) {
													self.selectedOptions[optionId] = [];
												}

												self.selectedOptions[optionId].push(selectOption.value);
											}
										}
									});
								}
							} else {
								if (element.getValue().strip() != '') {
									curConfig = configOptions;
								}
							}
							if(element.type == 'select-multiple' && ('options' in element)) {
								$A(element.options).each(function(selectOption) {
									if (('selected' in selectOption) && typeof(configOptions[selectOption.value]) != 'undefined') {
										if (selectOption.selected) {
											curConfig = configOptions[selectOption.value];
										} else {
											curConfig = {price: 0};
										}
										optionsPrice.addCustomPrices(optionId + '-' + selectOption.value, curConfig);
										optionsPrice.reload();
									}
								});
							} else {
								optionsPrice.addCustomPrices(element.id || optionId, curConfig);
								optionsPrice.reload();
							}
						}
					});
				};
			}

			if (bundle) {
				this.isBundleAvailable(bundle);

				var oldChangeSelection = bundle.changeSelection;
				bundle.changeSelection = function (o) {
					oldChangeSelection.apply(bundle, arguments);

					self.isBundleAvailable(bundle);

					//_.forEach(bundle.config.selected, function(value, key) {
					//	console.log(key);
					//	console.log(value);
					//});
				}
			}

			this.watchlistDialog.getElements('submit').on('click', _.bind(this.submit, this));
			this.emailInput.on('keypress', function(e) {
				if (e.charCode == 13) {
					e.preventDefault();
					self.submit();
				}
			});
		},

		submit: function(e) {
			var self = this;

			if (e) {
				e.preventDefault();
			}

			if (this.submitting) return;

			if (!Validation.validate(this.emailInput.get(0))) {
				return;
			}

			var form = $('<form>');

			form
				.css('display', 'none')
				.attr('action', this.watchlistDialog.data('action'))
				.attr('method', 'POST')
				.appendTo(document.body);

			if (window.bundle) {
				this.attributeInput.val(JSON.stringify({
					bundle_option: window.bundle.config.selected,
					options: window.opConfig ? window.opConfig.selectedOptions : []
				}));
			}

			this.watchlistDialog.clone().appendTo(form);

			Spinner.add();

			this.submitting = true;

			$.ajax(form.attr('action'), {
				method: form.attr('method'),
				data: form.serialize(),
				dataType: 'json'
			}).then(function (res) {
				var content = res.message;

				if ('error' in res) {
					content = res.error;
				}

				$.magnificPopup.open({
					items: {
						src: self._renderPopup({
							body: content
						})
					}
				});

				Spinner.remove();

				self.submitting = false;
			});
		},
		_onSimpleChangeCallback: function (simple) {
			this._onSimpleChange({}, simple);
		},
		_onSimpleChange: function(e, simple) {
			if (this.isCatalogStore || (simple && (simple.qty <= 0 || !simple.is_available))) {
				var attributes = {};

				if (simple.can_be_watched) {
					this.watchlistDialog.show();
				} else {
					this.watchlistDialog.hide();
				}

				this.addToCart.hide();

				if(this.productCustomOptions.length) {
					if (this.productOptionsTechnical.find('.b_product-options__attribute-list').length) {
						this.productCustomOptions.hide();
					} else {
						this.productOptionsTechnical.hide();
					}
				}

				$.each(simple.attributes, function (index, value) {
					attributes[value.attributeId] = value.value;
				});

				this.attributeInput.val(JSON.stringify({super_attribute: attributes}));
				this.productIdInput.val(simple.id);
			} else {
				this.watchlistDialog.hide();
				this.addToCart.show();

				if(this.productCustomOptions.length) {
					if (this.productOptionsTechnical.find('.b_product-options__attribute-list').length) {
						this.productCustomOptions.show();
					} else {
						this.productOptionsTechnical.show();
					}
				}
				this.attributeInput.val('');
				this.productIdInput.val(this.parentProductId.val());
			}
		},

		isBundleAvailable: function (bundle) {
			var available = true,
				display = this.watchlistDialog.is(':visible');

			_.forEach(bundle.config.selected, function(value, key) {
				var optionId = key;

				_.forEach(value, function(value, key) {
					if (!bundle.config.options[optionId].selections[value]['available']) {
						available = false;
					}
				});
			});

			if (!available) {
				// this.watchlistDialog.insertAfter(this.addToCart);
				this.watchlistDialog.show();
				this.addToCart.hide();
				this.cartButton.attr('disabled', 'disabled');
			} else {
				this.watchlistDialog.hide();
				this.addToCart.show();
				this.cartButton.removeAttr('disabled');
			}
		},

		_renderPopup: function(data) {
			if (null === popup) {
				popup = _.template(PopupHtml);
			}

			return popup(data);
		}
	});
});

define('gigaset_stocknotification/components/product/list',[
	"jquery",
	"lodash",
	"cartware/components/_base",
	"cartware/components/Dispatcher",
	"json!gigaset_stocknotification/handles/gigaset_stocknotification.json"
], function(
	$,
	_,
	Component,
	ComponentDispatcher,
	Handles
) {
	return Component.create({
		$init: function() {
			$(document).on("cartware_cart/modal/open", function() {
				new ComponentDispatcher({
					handles: ["catalog_product_view", "PRODUCT_TYPE_configurable"],
					apply: Handles
				});
			});
		}
	});
});


//# sourceMappingURL=gigaset.built.js.map